/**
 * FORTHIUS WEALTH — HNI Portfolio Dashboard v4.0
 * ─────────────────────────────────────────────
 * CHANGES FROM v3:
 *  1. Benchmarks tab → live data via Alpha Vantage MCP + full metrics (MTD/YTD/1Y/3Y/5Y/10Y CAGR)
 *  2. Point-to-point return for portfolio AND each benchmark with date picker
 *  3. Target Allocation → "Target Yield %" column + "Total Target Yield" weighted aggregate
 *  4. World Map Geo Heatmap — already present, enhanced with live country data
 *  5. Alpha Vantage key wired via config constant + rate-limit guard
 *  6. Upload tab → Indian MF sample CSV (MFapi.in schema), Google Sheets link import, validation
 *  7. Holdings → manual price entry for unlisted assets with "Last As On Date" field
 *  8. Live data providers: Frankfurter (FX), Finnhub (global), Twelve Data (India), MFapi (MF)
 *     → all wired to a unified PriceDataProvider abstraction with in-memory cache
 *  9. "Force Refresh Prices" button bypasses cache
 * 10. Data Sources tab (admin-only) — shows provider config, keys, trigger refresh
 *
 * ENVIRONMENT / CONFIG:
 *   ALPHA_VANTAGE_KEY  = ""   (set in CONFIG object at top of file)
 *   TWELVE_DATA_KEY    = ""   (set in CONFIG object at top of file)
 *   FINNHUB_KEY        = set via Data Sources tab (admin)
 *   Frankfurter        = no key needed
 *   MFapi.in           = no key needed
 *
 * ROLE:
 *   isAdmin is set at login — admin credentials grant full access; client credentials restrict to portfolio view.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";

/*
  ══════════════════════════════════════════════════════
  REQUIRED: Add these functions to your supabase.js
  ══════════════════════════════════════════════════════

  export async function fetchTransactions() {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('date', { ascending: false });
    if (error) throw error;
    return data || [];
  }

  export async function addTransaction(txn) {
    const { data, error } = await supabase
      .from('transactions')
      .insert([txn])
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  export async function updateTransaction(id, fields) {
    const { error } = await supabase
      .from('transactions')
      .update(fields)
      .eq('id', id);
    if (error) throw error;
  }

  export async function deleteTransaction(id) {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  SQL to create the transactions table:
  CREATE TABLE transactions (
    id          TEXT PRIMARY KEY,
    date        DATE NOT NULL,
    holding     TEXT NOT NULL,
    type        TEXT NOT NULL DEFAULT 'BUY',
    qty         NUMERIC NOT NULL DEFAULT 0,
    price       NUMERIC NOT NULL DEFAULT 0,
    currency    TEXT NOT NULL DEFAULT 'USD',
    fees        NUMERIC DEFAULT 0,
    created_at  TIMESTAMPTZ DEFAULT now()
  );
  ALTER TABLE transactions ENABLE ROW LEVEL SECURITY;
  CREATE POLICY "Allow all" ON transactions FOR ALL USING (true);

  Also add these profile functions to supabase.js:

  export async function fetchProfiles() {
    const { data, error } = await supabase.from('profiles').select('*');
    if (error) throw error;
    return (data || []).reduce((acc, row) => { acc[row.user_id] = row.data; return acc; }, {});
  }

  export async function upsertProfile(userId, data) {
    const { error } = await supabase.from('profiles')
      .upsert({ user_id: userId, data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    if (error) throw error;
  }

  SQL to create the profiles table:
  CREATE TABLE profiles (
    user_id     TEXT PRIMARY KEY,
    data        JSONB DEFAULT '{}',
    updated_at  TIMESTAMPTZ DEFAULT now()
  );
  ALTER TABLE profiles ENABLE ROW LEVEL SECURITY;
  CREATE POLICY "Allow all" ON profiles FOR ALL USING (true);
  ══════════════════════════════════════════════════════
*/
import { fetchFamilies, fetchClients, fetchHoldings, fetchHoldingOwners, addFamily, addClient, addHolding, deleteHolding, deleteFamily, deleteClient, setHoldingOwner, removeHoldingOwner, updateHoldingPrice, updateHolding, fetchWatchlist, addWatchlistItem, updateWatchlistPrice, updateWatchlistItem, deleteWatchlistItem, fetchBtAssets, addBtAsset, updateBtAsset, deleteBtAsset, fetchClientLogins, upsertClientLogin, updateClientLoginLastLogin, fetchTransactions, addTransaction, updateTransaction, deleteTransaction, fetchProfiles, upsertProfile, upsertPriceSnapshot, fetchPriceSnapshots } from './supabase';

// Unique ID generator — avoids Date.now() collisions
function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}
// ── Mobile responsiveness hook ──
function useIsMobile(bp=768){ const [m,setM]=React.useState(()=>typeof window!=="undefined"&&window.innerWidth<bp); React.useEffect(()=>{ const h=()=>setM(window.innerWidth<bp); window.addEventListener("resize",h); return()=>window.removeEventListener("resize",h); },[bp]); return m; }
function rGrid(n,m,gap=16){ return {display:"grid",gridTemplateColumns:m?`repeat(${Math.min(n,2)},1fr)`:`repeat(${n},1fr)`,gap}; }



const FORTHIUS_LOGO = "data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20320%2080%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%222%22%20width%3D%229%22%20height%3D%2254%22%20rx%3D%223%22%20fill%3D%22%2300205B%22/%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%222%22%20width%3D%2238%22%20height%3D%229%22%20rx%3D%223%22%20fill%3D%22%2300205B%22/%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%2225%22%20width%3D%2227%22%20height%3D%228%22%20rx%3D%222%22%20fill%3D%22%2300205B%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2214%22%20width%3D%229%22%20height%3D%2254%22%20rx%3D%223%22%20fill%3D%22%230079C1%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2214%22%20width%3D%2238%22%20height%3D%229%22%20rx%3D%223%22%20fill%3D%22%230079C1%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2237%22%20width%3D%2227%22%20height%3D%228%22%20rx%3D%222%22%20fill%3D%22%230079C1%22/%3E%0A%20%20%3Cline%20x1%3D%225%22%20y1%3D%2262%22%20x2%3D%2260%22%20y2%3D%227%22%20stroke%3D%22%2300205B%22%20stroke-width%3D%225.5%22%20stroke-linecap%3D%22round%22/%3E%0A%20%20%3Cline%20x1%3D%225%22%20y1%3D%2262%22%20x2%3D%2260%22%20y2%3D%227%22%20stroke%3D%22white%22%20stroke-width%3D%222.2%22%20stroke-linecap%3D%22round%22/%3E%0A%20%20%3Ccircle%20cx%3D%2260%22%20cy%3D%227%22%20r%3D%224.5%22%20fill%3D%22%230079C1%22/%3E%0A%20%20%3Ctext%20x%3D%2282%22%20y%3D%2232%22%20font-family%3D%22Georgia%2CTimes%20New%20Roman%2Cserif%22%20font-size%3D%2226%22%20font-weight%3D%22700%22%20fill%3D%22%2300205B%22%20letter-spacing%3D%223%22%3EFORTHIUS%3C/text%3E%0A%20%20%3Ctext%20x%3D%2283%22%20y%3D%2256%22%20font-family%3D%22Trebuchet%20MS%2CCalibri%2Csans-serif%22%20font-size%3D%2214%22%20font-weight%3D%22600%22%20fill%3D%22%230079C1%22%20letter-spacing%3D%224%22%3EFINALYTICS%3C/text%3E%0A%3C/svg%3E";
const FORTHIUS_LOGO_DARK = "data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20320%2080%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%222%22%20width%3D%229%22%20height%3D%2254%22%20rx%3D%223%22%20fill%3D%22white%22/%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%222%22%20width%3D%2238%22%20height%3D%229%22%20rx%3D%223%22%20fill%3D%22white%22/%3E%0A%20%20%3Crect%20x%3D%220%22%20y%3D%2225%22%20width%3D%2227%22%20height%3D%228%22%20rx%3D%222%22%20fill%3D%22white%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2214%22%20width%3D%229%22%20height%3D%2254%22%20rx%3D%223%22%20fill%3D%22%2360C8FF%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2214%22%20width%3D%2238%22%20height%3D%229%22%20rx%3D%223%22%20fill%3D%22%2360C8FF%22/%3E%0A%20%20%3Crect%20x%3D%2218%22%20y%3D%2237%22%20width%3D%2227%22%20height%3D%228%22%20rx%3D%222%22%20fill%3D%22%2360C8FF%22/%3E%0A%20%20%3Cline%20x1%3D%225%22%20y1%3D%2262%22%20x2%3D%2260%22%20y2%3D%227%22%20stroke%3D%22white%22%20stroke-width%3D%225.5%22%20stroke-linecap%3D%22round%22/%3E%0A%20%20%3Cline%20x1%3D%225%22%20y1%3D%2262%22%20x2%3D%2260%22%20y2%3D%227%22%20stroke%3D%22%2300205B%22%20stroke-width%3D%222.2%22%20stroke-linecap%3D%22round%22/%3E%0A%20%20%3Ccircle%20cx%3D%2260%22%20cy%3D%227%22%20r%3D%224.5%22%20fill%3D%22%2360C8FF%22/%3E%0A%20%20%3Ctext%20x%3D%2282%22%20y%3D%2232%22%20font-family%3D%22Georgia%2CTimes%20New%20Roman%2Cserif%22%20font-size%3D%2226%22%20font-weight%3D%22700%22%20fill%3D%22white%22%20letter-spacing%3D%223%22%3EFORTHIUS%3C/text%3E%0A%20%20%3Ctext%20x%3D%2283%22%20y%3D%2256%22%20font-family%3D%22Trebuchet%20MS%2CCalibri%2Csans-serif%22%20font-size%3D%2214%22%20font-weight%3D%22600%22%20fill%3D%22%2360C8FF%22%20letter-spacing%3D%224%22%3EFINALYTICS%3C/text%3E%0A%3C/svg%3E";

// ─────────────── CONFIG ───────────────
const CONFIG = {
  alphaVantageKey: process.env.REACT_APP_ALPHA_VANTAGE_KEY || "SYEEF62I2JXJHYTT",
  twelveDataKey:   process.env.REACT_APP_TWELVE_DATA_KEY   || "",
  finnhubKey:      process.env.REACT_APP_FINNHUB_KEY       || "",
  frankfurterBase:"https://api.frankfurter.app",
  mfapiBase:      "https://api.mfapi.in/mf",
  cacheTTL:       60 * 1000,              // 60 s default cache
  googleFinance:  true,                   // enable Google Finance as primary price source
  corsProxies: [                          // ordered list of CORS proxies for GF scraping
    "https://api.allorigins.win/raw?url=",
    "https://thingproxy.freeboard.io/fetch/",
    "https://corsproxy.io/?",
  ],
};
// API keys loaded — check Supabase env vars if prices fail

// ─────────────── COUNTRY → EXCHANGE / CURRENCY DEFAULTS ───────────────
const COUNTRY_EXCHANGE = {
  "USA":         { exchange: "NYSE",    currency: "USD", tickerSuffix: "" },
  "India":       { exchange: "NSE",     currency: "INR", tickerSuffix: ".NSE" },
  "UK":          { exchange: "LSE",     currency: "GBP", tickerSuffix: ".L" },
  "Germany":     { exchange: "XETRA",   currency: "EUR", tickerSuffix: ".DE" },
  "France":      { exchange: "Euronext",currency: "EUR", tickerSuffix: ".PA" },
  "Switzerland": { exchange: "SIX",     currency: "CHF", tickerSuffix: ".SW" },
  "Japan":       { exchange: "TSE",     currency: "JPY", tickerSuffix: ".T" },
  "Hong Kong":   { exchange: "HKEX",    currency: "HKD", tickerSuffix: ".HK" },
  "China":       { exchange: "SSE",     currency: "CNY", tickerSuffix: ".SS" },
  "Australia":   { exchange: "ASX",     currency: "AUD", tickerSuffix: ".AX" },
  "Canada":      { exchange: "TSX",     currency: "CAD", tickerSuffix: ".TO" },
  "Singapore":   { exchange: "SGX",     currency: "SGD", tickerSuffix: ".SI" },
  "UAE":         { exchange: "DFM",     currency: "AED", tickerSuffix: "" },
  "Netherlands": { exchange: "Euronext",currency: "EUR", tickerSuffix: ".AS" },
  "Sweden":      { exchange: "Nasdaq Stockholm", currency: "SEK", tickerSuffix: ".ST" },
  "Italy":       { exchange: "Borsa Italiana",  currency: "EUR", tickerSuffix: ".MI" },
  "Spain":       { exchange: "BME",             currency: "EUR", tickerSuffix: ".MC" },
  "South Korea": { exchange: "KRX",             currency: "KRW", tickerSuffix: ".KS" },
  "Denmark":     { exchange: "Nasdaq Copenhagen",currency: "DKK", tickerSuffix: ".CO" },
  "Norway":      { exchange: "Oslo Børs",        currency: "NOK", tickerSuffix: ".OL" },
  "Brazil":      { exchange: "B3",               currency: "BRL", tickerSuffix: ".SA" },
  "Global":      { exchange: "NYSE",    currency: "USD", tickerSuffix: "" },
};

// ─────────────── PRICE CACHE (in-memory + sessionStorage for historical) ───────────────
const priceCache = {};
const HIST_KEYS = ['av_hist_', 'per_hist_', 'p2p_hist_', 'mf_hist_']; // persistent across refreshes
function getCached(key) {
  // Historical prices: try sessionStorage first (survives page refresh)
  if (HIST_KEYS.some(p => key.startsWith(p))) {
    try {
      const raw = sessionStorage.getItem('fw_pc_' + key);
      if (raw) { const e = JSON.parse(raw); if (Date.now() - e.ts < 86400000) return e.data; }
    } catch {}
  }
  const e = priceCache[key];
  if (!e) return null;
  if (Date.now() - e.ts > CONFIG.cacheTTL) { delete priceCache[key]; return null; }
  return e.data;
}
function setCache(key, data) {
  priceCache[key] = { ts: Date.now(), data };
  // Persist historical prices to sessionStorage (24h TTL)
  if (HIST_KEYS.some(p => key.startsWith(p))) {
    try { sessionStorage.setItem('fw_pc_' + key, JSON.stringify({ ts: Date.now(), data })); } catch {}
  }
}
function clearCache() {
  Object.keys(priceCache).forEach(k => delete priceCache[k]);
  try { Object.keys(sessionStorage).filter(k => k.startsWith('fw_pc_')).forEach(k => sessionStorage.removeItem(k)); } catch {}
}

// ─────────────── UNIFIED PRICE DATA PROVIDER ───────────────
// Routes requests to the right API based on instrument type + market
const PriceDataProvider = {

  // ── Google Finance (primary for all listed equities / ETFs / crypto / indices) ──
  // Google Finance has no public API — we scrape the public quote page via a CORS proxy.
  // Symbol formats:
  //   US equities/ETFs: "AAPL", "SPY", "GLD"
  //   NSE India:        "NSE:RELIANCE", "NSE:NIFTY50"  (use exchange prefix)
  //   LSE:              "LON:ISF"
  //   Crypto:           "CRYPTO:BTC" or just the ticker "BTC-USD" (maps via helper)
  //   Forex:             "CURRENCY:USDINR"
  // The page embeds a JSON blob containing the real-time price.
  async getGoogleFinancePrice(rawSymbol, forceRefresh = false) {
    if (!CONFIG.googleFinance) return null;
    // Normalise symbol to Google Finance exchange:ticker format
    const symbol = this._normaliseGFSymbol(rawSymbol);
    const key = `gf_${symbol}`;
    if (!forceRefresh) { const c = getCached(key); if (c !== null) return c; }

    // Try each CORS proxy in order until one succeeds
    for (const proxy of CONFIG.corsProxies) {
      try {
        const gfUrl  = `https://www.google.com/finance/quote/${encodeURIComponent(symbol)}`;
        const r = await fetch(`${proxy}${encodeURIComponent(gfUrl)}`, { signal: AbortSignal.timeout(3000) });
        if (!r.ok) continue;
        const html = await r.text();

        // Google Finance embeds price in the page as plain text inside
        // a <div data-last-price> attribute or inside a structured script JSON.
        // We look for both patterns for resilience.

        // Pattern 1: data-last-price attribute  e.g.  data-last-price="189.3"
        const attrMatch = html.match(/data-last-price="([0-9.]+)"/);
        if (attrMatch) {
          const price = parseFloat(attrMatch[1]);
          if (price > 0) { setCache(key, price); return price; }
        }

        // Pattern 2: JSON-LD / window._data_ embedded blob
        // Google wraps key numbers like: "price":{"raw":189.3,"fmt":"189.30"}
        const jsonMatch = html.match(/"price"\s*:\s*\{"raw"\s*:\s*([0-9.]+)/);
        if (jsonMatch) {
          const price = parseFloat(jsonMatch[1]);
          if (price > 0) { setCache(key, price); return price; }
        }

        // Pattern 3: simple quoted number after "Last price" text in a <span>
        const spanMatch = html.match(/YMlKec fxKbKc[^>]*>([0-9,]+\.?[0-9]*)/);
        if (spanMatch) {
          const price = parseFloat(spanMatch[1].replace(/,/g, ""));
          if (price > 0) { setCache(key, price); return price; }
        }

        // Pattern 4: structured data script tag
        const scriptMatch = html.match(/"price":\s*([0-9.]+)\s*,\s*"currency"/);
        if (scriptMatch) {
          const price = parseFloat(scriptMatch[1]);
          if (price > 0) { setCache(key, price); return price; }
        }
      } catch { /* try next proxy */ }
    }
    return null; // all proxies failed — fall through to next provider
  },

  // Normalise ticker symbols to Google Finance "EXCHANGE:TICKER" format
  _normaliseGFSymbol(ticker) {
    if (!ticker || ticker === "-") return null;
    // Already formatted (contains colon)
    if (ticker.includes(":")) return ticker;
    // NSE India
    if (ticker.endsWith(".NSE"))  return `NSE:${ticker.replace(".NSE","")}`;
    if (ticker.endsWith(".BSE"))  return `BOM:${ticker.replace(".BSE","")}`;
    // London / SIX / other exchanges
    if (ticker.endsWith(".L"))    return `LON:${ticker.replace(".L","")}`;
    if (ticker.endsWith(".SW"))   return `SWX:${ticker.replace(".SW","")}`;
    if (ticker.endsWith(".PA"))   return `EPA:${ticker.replace(".PA","")}`;
    if (ticker.endsWith(".DE"))   return `ETR:${ticker.replace(".DE","")}`;
    if (ticker.endsWith(".HK"))   return `HKG:${ticker.replace(".HK","")}`;
    if (ticker.endsWith(".TO"))   return `TSE:${ticker.replace(".TO","")}`;
    if (ticker.endsWith(".AX"))   return `ASX:${ticker.replace(".AX","")}`;
    // Crypto (Google Finance format: CRYPTO:BTCUSD)
    if (ticker.endsWith("-USD"))  return `CRYPTO:${ticker.replace("-USD","")}USD`;
    if (ticker.endsWith("-USDT")) return `CRYPTO:${ticker.replace("-USDT","")}USDT`;
    // US equities / ETFs — no prefix needed on Google Finance
    return ticker;
  },

  async getFX(from, to, date = null, forceRefresh = false) {
    const key = `fx_${from}_${to}_${date||"latest"}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }

    // Try Google Finance for FX first (e.g. CURRENCY:USDINR)
    if (!date && CONFIG.googleFinance) {
      try {
        const gfSymbol = `CURRENCY:${from}${to}`;
        const rate = await this.getGoogleFinancePrice(gfSymbol, forceRefresh);
        if (rate) { setCache(key, rate); return rate; }
      } catch {}
    }

    // Frankfurter fallback
    try {
      const url = date
        ? `${CONFIG.frankfurterBase}/${date}?from=${from}&to=${to}`
        : `${CONFIG.frankfurterBase}/latest?from=${from}&to=${to}`;
      const r = await fetch(url);
      const j = await r.json();
      const rate = j.rates?.[to];
      if (rate) { setCache(key, rate); return rate; }
    } catch { /* fall through to static */ }
    return (fxRates[to]||FX_RATES_DEFAULT[to]||1) / (fxRates[from]||FX_RATES_DEFAULT[from]||1);
  },

  // Twelve Data — Indian NSE/BSE (rate limit: ~800/day on free tier)
  async getIndiaPrice(symbol, forceRefresh = false) {
    // Try Google Finance first for Indian stocks
    if (CONFIG.googleFinance) {
      const gfPrice = await this.getGoogleFinancePrice(symbol, forceRefresh);
      if (gfPrice) return gfPrice;
    }
    const key = `td_${symbol}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }
    if (!CONFIG.twelveDataKey) return null;
    try {
      const r = await fetch(`https://api.twelvedata.com/price?symbol=${symbol}&apikey=${CONFIG.twelveDataKey}`);
      const j = await r.json();
      if (j.price) { const p = parseFloat(j.price); setCache(key, p); return p; }
    } catch {}
    return null;
  },

  // Finnhub — global stocks & ETFs (60 calls/min on free tier)
  async getGlobalPrice(symbol, forceRefresh = false) {
    // Try Google Finance first
    if (CONFIG.googleFinance) {
      const gfPrice = await this.getGoogleFinancePrice(symbol, forceRefresh);
      if (gfPrice) return gfPrice;
    }
    const key = `fh_${symbol}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }
    if (!CONFIG.finnhubKey) return null;
    try {
      const r = await fetch(`https://finnhub.io/api/v1/quote?symbol=${symbol}&token=${CONFIG.finnhubKey}`);
      const j = await r.json();
      if (j.c) { setCache(key, j.c); return j.c; }
    } catch {}
    return null;
  },

  // Indian Mutual Fund NAV — direct from mfapi.in (public API, CORS-enabled, no auth needed)
  async getMFNav(schemeCode, forceRefresh = false) {
    const key = `mf_${schemeCode}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }
    if (!schemeCode || String(schemeCode).trim() === "" || String(schemeCode) === "-") return null;
    const code = String(schemeCode).trim();

    // ── Primary: mfapi.in/mf/{code} (free, CORS-open, AMFI data) ──
    const MFAPI_URLS = [
      `https://api.mfapi.in/mf/${code}/latest`,
      `https://api.mfapi.in/mf/${code}`,
    ];
    for (const url of MFAPI_URLS) {
      try {
        const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
        if (!r.ok) continue;
        const j = await r.json();
        // /latest returns { status, data:[{date,nav}] }
        // /         returns { status, meta, data:[...] } (full history, latest is first)
        const navStr = j?.data?.[0]?.nav;
        const nav = parseFloat(navStr);
        if (nav > 0) { setCache(key, nav); return nav; }
      } catch(e) { /* try next */ }
    }

    // mfapi.in returned no data — scheme may be discontinued or merged.
    // Don't call /api/price (it would just call mfapi.in again and 404).
    return null;
  },

  // Alpha Vantage — historical quotes for benchmark returns
  async getHistoricalClose(symbol, date, forceRefresh = false) {
    const key = `av_hist_${symbol}_${date}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }
    try {
      const r = await fetch(
        `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY_ADJUSTED&symbol=${symbol}&outputsize=full&apikey=${CONFIG.alphaVantageKey}`
      );
      const j = await r.json();
      const series = j["Time Series (Daily)"];
      if (series && series[date]) {
        const close = parseFloat(series[date]["5. adjusted close"]);
        setCache(key, close); return close;
      }
    } catch {}
    return null;
  },

  // ── Indian MF historical NAV via mfapi.in (full history, CORS-open, no rate limit) ──
  async getMFHistoricalNav(schemeCode, dateStr, forceRefresh = false) {
    // dateStr format: 'YYYY-MM-DD'  →  mfapi returns DD-MM-YYYY format
    const key = `mf_hist_${schemeCode}_${dateStr}`;
    if (!forceRefresh) { const c = getCached(key); if (c !== null) return c; }
    try {
      const r = await fetch(`https://api.mfapi.in/mf/${schemeCode}`);
      const j = await r.json();
      const navData = j?.data; // [{date: "DD-MM-YYYY", nav: "123.45"}]
      if (!Array.isArray(navData) || !navData.length) return null;
      // Convert target dateStr to DD-MM-YYYY for comparison
      const [y, m, d] = dateStr.split('-');
      const targetFmt = `${d}-${m}-${y}`;
      // Find exact match or nearest prior date (NAV data may skip weekends/holidays)
      const targetMs = new Date(dateStr).getTime();
      let best = null, bestDiff = Infinity;
      for (const entry of navData) {
        const [ed, em, ey] = entry.date.split('-');
        const entryMs = new Date(`${ey}-${em}-${ed}`).getTime();
        if (entryMs <= targetMs) {
          const diff = targetMs - entryMs;
          if (diff < bestDiff) { bestDiff = diff; best = parseFloat(entry.nav); }
        }
      }
      // Only accept if within 10 trading days (~14 calendar days)
      if (best !== null && bestDiff <= 14 * 86400000) {
        setCache(key, best); return best;
      }
    } catch {}
    return null;
  },

  // ── UCITS / Global MF NAV via Yahoo Finance chart API ──
  // Fetch UCITS/Global MF NAV via Vercel /api/benchmark proxy (server-side, no CORS)
  // Tries yahooTicker first, then ISIN with multiple exchange suffixes
  async getUCITSNav(isin, forceRefresh = false, yahooTicker = null) {
    if (!isin || isin === "-" || isin.length < 10) return null;
    const key = `ucits_${isin}`;
    if (!forceRefresh) { const c = getCached(key); if (c) return c; }

    // ── Route 1: Morningstar via /api/benchmark?isin= ──
    try {
      const r = await fetch(`/api/benchmark?isin=${encodeURIComponent(isin)}`,
        { signal: AbortSignal.timeout(12000) });
      if (r.ok) {
        const j = await r.json();
        if (j?.nav && j.nav > 0) {
          setCache(key, j.nav);
          return j.nav;
        }
      }
    } catch {}

    // ── Route 2: Yahoo Finance via /api/benchmark?ticker= ──
    const fetchViaBenchmarkApi = async (ticker) => {
      try {
        const now  = Math.floor(Date.now() / 1000);
        const from = now - 365 * 86400;
        const r = await fetch(
          `/api/benchmark?ticker=${encodeURIComponent(ticker)}&from=${from}&to=${now}&interval=1mo`,
          { signal: AbortSignal.timeout(12000) }
        );
        if (!r.ok) return null;
        const j = await r.json();
        const pts = j?.points;
        if (pts?.length) {
          const nav = pts[0]?.close;
          if (nav && nav > 0) return nav;
        }
      } catch {}
      return null;
    };

    // Try stored yahooTicker first (user-set)
    if (yahooTicker) {
      const nav = await fetchViaBenchmarkApi(yahooTicker);
      if (nav) { setCache(key, nav); return nav; }
    }

    // Try ISIN with common exchange suffixes
    const suffixes = [".SI", ".F", ".L", ".PA", ".SW", ".AS", ".MI", ".HK", ""];
    for (const sfx of suffixes) {
      const nav = await fetchViaBenchmarkApi(isin + sfx);
      if (nav) {
        setCache(`ucits_ticker_${isin}`, isin + sfx);
        setCache(key, nav);
        return nav;
      }
    }

    return null;
  },

  // Route by asset type — Google Finance is tried first for all listed assets
  async getPrice(holding, forceRefresh = false) {
    // ── Global / UCITS Mutual Funds — fetch NAV via ISIN from Yahoo Finance ──
    if (holding.class === "Global Mutual Funds" || holding.isGlobalMF) {
      const isin = holding.isin || (holding.ticker && holding.ticker.length >= 10 ? holding.ticker : null);
      if (isin && isin !== "-") {
        const nav = await this.getUCITSNav(isin, forceRefresh);
        if (nav) return nav;
      }
      // If ISIN fetch failed, don't fall through to equity price fetchers
      return null;
    }
    if (!holding.listed) return null; // manual price for other unlisted
    if (holding.isMutualFund) {
      // schemeCode takes priority, fallback to ticker (which is also the AMFI code for MFs)
      const code = holding.schemeCode || holding.ticker;
      if (code && /^\d{5,6}$/.test(String(code))) return this.getMFNav(code, forceRefresh);
    }
    if (holding.class === "Equities" && (holding.exchange === "NSE" || holding.exchange === "BSE"))
      return this.getIndiaPrice(holding.ticker, forceRefresh);
    if (holding.ticker && holding.ticker !== "-")
      return this.getGlobalPrice(holding.ticker, forceRefresh);
    return null;
  }
};


// ─────────────── SUBSCRIPTION PLANS ───────────────
const PLAN_CONFIG = {
  free: {
    id: "free", label: "Free", icon: "🆓",
    price: { INR: 0, USD: 0 },
    color: "#6B7280",
    description: "Basic portfolio tracking",
    features: ["Portfolio overview","Holdings table","Transactions","Data upload"],
    tabs: ["summary","overview","holdings","performance","transactions","upload"],
  },
  starter: {
    id: "starter", label: "Starter", icon: "⭐",
    price: { INR: 499, USD: 6 },
    color: "#F59E0B",
    description: "Essential analytics for growing portfolios",
    features: ["Everything in Free","Risk Analysis","Goals tracker","Backtesting","Watchlist"],
    tabs: ["summary","overview","holdings","performance","transactions","upload",
           "risk","goals","scenarios","watchlist","cashflow","dividends","news","families","datasources"],
  },
  pro: {
    id: "pro", label: "Pro", icon: "💎",
    price: { INR: 1999, USD: 24 },
    color: "#2563EB",
    description: "Full analytics, AI advisor & benchmarking",
    features: ["Everything in Starter","AI Portfolio Advisor","Benchmark Comparison",
               "IRR / XIRR","Capital Gains","FX Exposure","PDF Reports","Geo & Sector"],
    tabs: ["summary","overview","holdings","performance","transactions","upload",
           "risk","goals","scenarios","watchlist","cashflow","dividends","news","families","datasources",
           "agent","benchmark","irr","capgains","geo","currency","analytics","model","research"],
  },
  premium: {
    id: "premium", label: "Premium", icon: "👑",
    price: { INR: 4999, USD: 59 },
    color: "#7C3AED",
    description: "Everything — for serious wealth management",
    features: ["Everything in Pro","Rebalancing","Advanced AI Research",
               "Priority support","Custom model portfolios"],
    tabs: ["summary","overview","holdings","performance","transactions","upload",
           "risk","goals","scenarios","watchlist","cashflow","dividends","news","families","datasources",
           "agent","benchmark","irr","capgains","geo","currency","analytics","model","research",
           "rebalance"],
  },
};
// Admin always sees everything — plan restrictions apply to clients only
const ALL_PLAN_TABS = Object.values(PLAN_CONFIG).flatMap(p => p.tabs);
const tabPlan = (tabId) => {
  if (PLAN_CONFIG.free.tabs.includes(tabId))    return "free";
  if (PLAN_CONFIG.starter.tabs.includes(tabId)) return "starter";
  if (PLAN_CONFIG.pro.tabs.includes(tabId))     return "pro";
  if (PLAN_CONFIG.premium.tabs.includes(tabId)) return "premium";
  return "free";
};
const planRank = { free:0, starter:1, pro:2, premium:3 };
const clientCanAccess = (clientPlan, tabId) => {
  const required = tabPlan(tabId);
  return planRank[clientPlan||"free"] >= planRank[required];
};

// ─────────────── PAYMENT GATEWAY CONFIG ───────────────
// WHITE-LABEL: operators replace these keys with their own
const PAYMENT_CONFIG = {
  razorpay: {
    enabled: true,
    keyId: "rzp_test_XXXXXXXXXXXXXXXX", // replace with live key
    currency: "INR",
    companyName: "Forthius Finalytics",
  },
  stripe: {
    enabled: true,
    publishableKey: "pk_test_XXXXXXXXXXXXXXXXXXXXXXXX", // replace with live key
    currency: "usd",
    companyName: "Forthius Finalytics",
  },
};

// ─────────────── EQUITY & ETF SECTOR MAP ───────────────
// Maps common tickers to sectors so holdings without a manual sector get auto-classified
const TICKER_SECTOR_MAP = {
  // ── Technology ──
  AAPL:"Technology", MSFT:"Technology", NVDA:"Technology", GOOGL:"Technology",
  GOOG:"Technology", META:"Technology", AMZN:"Technology", TSLA:"Technology",
  AMD:"Technology", INTC:"Technology", QCOM:"Technology", ORCL:"Technology",
  CRM:"Technology", ADBE:"Technology", NFLX:"Technology", PYPL:"Technology",
  SHOP:"Technology", SNOW:"Technology", UBER:"Technology", LYFT:"Technology",
  INFY:"Technology", TCS:"Technology", WIPRO:"Technology", HCLTECH:"Technology",
  TECHM:"Technology", MPHASIS:"Technology", LTIM:"Technology", LTTS:"Technology",
  PERSISTENT:"Technology", COFORGE:"Technology", KPIT:"Technology",
  AMAT:"Technology", LRCX:"Technology", KLAC:"Technology", ASML:"Technology",
  AVGO:"Technology", MU:"Technology", WDC:"Technology", STX:"Technology",
  PANW:"Technology", CRWD:"Technology", ZS:"Technology", OKTA:"Technology",
  NET:"Technology", DDOG:"Technology", GTLB:"Technology", RBRK:"Technology",
  HIVE:"Technology", PLTR:"Technology", CRCL:"Technology",
  // Tech ETFs
  QQQ:"Technology", XLK:"Technology", VGT:"Technology", SMH:"Technology",
  SOXX:"Technology", IGV:"Technology", CIBR:"Technology", WCLD:"Technology",
  // ── Financials ──
  JPM:"Financials", BAC:"Financials", WFC:"Financials", GS:"Financials",
  MS:"Financials", C:"Financials", AXP:"Financials", BLK:"Financials",
  SCHW:"Financials", USB:"Financials", PNC:"Financials", TFC:"Financials",
  COF:"Financials", DFS:"Financials", SYF:"Financials", ALLY:"Financials",
  HDFCBANK:"Financials", ICICIBANK:"Financials", KOTAKBANK:"Financials",
  SBIN:"Financials", AXISBANK:"Financials", INDUSINDBK:"Financials",
  BAJFINANCE:"Financials", BAJAJFINSV:"Financials", HDFCLIFE:"Financials",
  SBILIFE:"Financials", ICICIPRULI:"Financials", MUTHOOTFIN:"Financials",
  CHOLAFIN:"Financials", MANAPPURAM:"Financials", LICHSGFIN:"Financials",
  D05:"Financials", OCBC:"Financials", UOB:"Financials", DBS:"Financials",
  // Citigroup appears as CITI.VI (Vienna), C (NYSE) — strip suffix gives CITI or C
  CITI:"Financials", STAN:"Financials", HSBA:"Financials", BARC:"Financials",
  LLOY:"Financials", NWG:"Financials", BNP:"Financials", ACA:"Financials",
  SAN:"Financials", BBVA:"Financials", DBK:"Financials", CBK:"Financials",
  UCG:"Financials", ISP:"Financials", CS:"Financials", UBS:"Financials",
  ING:"Financials", ABN:"Financials", DANSKE:"Financials",
  XLF:"Financials", VFH:"Financials", KBE:"Financials", KIE:"Financials", IAI:"Financials",
  // ── Healthcare ──
  JNJ:"Healthcare", PFE:"Healthcare", ABBV:"Healthcare", MRK:"Healthcare",
  UNH:"Healthcare", TMO:"Healthcare", ABT:"Healthcare", BMY:"Healthcare",
  LLY:"Healthcare", AMGN:"Healthcare", GILD:"Healthcare", BIIB:"Healthcare",
  REGN:"Healthcare", VRTX:"Healthcare", ISRG:"Healthcare", EW:"Healthcare",
  SYK:"Healthcare", ZBH:"Healthcare", BSX:"Healthcare", MDT:"Healthcare",
  NVO:"Healthcare", AZN:"Healthcare", GSK:"Healthcare", SNY:"Healthcare",
  ROG:"Healthcare", NVS:"Healthcare", RHHBY:"Healthcare",
  SUNPHARMA:"Healthcare", DRREDDY:"Healthcare", CIPLA:"Healthcare",
  DIVISLAB:"Healthcare", APOLLOHOSP:"Healthcare", MAXHEALTH:"Healthcare",
  AUROPHARMA:"Healthcare", LUPIN:"Healthcare", TORNTPHARM:"Healthcare",
  RDDT:"Technology",
  XLV:"Healthcare", IBB:"Healthcare", XBI:"Healthcare", IHI:"Healthcare", ARKG:"Healthcare",
  // ── Energy ──
  XOM:"Energy", CVX:"Energy", COP:"Energy", SLB:"Energy", EOG:"Energy",
  OXY:"Energy", MPC:"Energy", VLO:"Energy", PSX:"Energy", HAL:"Energy",
  RELIANCE:"Energy", ONGC:"Energy", IOC:"Energy", BPCL:"Energy",
  HPCL:"Energy", GAIL:"Energy", PETRONET:"Energy", OIL:"Energy",
  XLE:"Energy", VDE:"Energy", OIH:"Energy", XOP:"Energy",
  // ── Consumer Discretionary ──
  HD:"Consumer Discretionary", MCD:"Consumer Discretionary", NKE:"Consumer Discretionary",
  SBUX:"Consumer Discretionary", TGT:"Consumer Discretionary", AMZN:"Consumer Discretionary",
  BKNG:"Consumer Discretionary", MAR:"Consumer Discretionary", HLT:"Consumer Discretionary",
  GM:"Consumer Discretionary", F:"Consumer Discretionary", TSLA:"Consumer Discretionary",
  ABNB:"Consumer Discretionary", EXPE:"Consumer Discretionary", RIVN:"Consumer Discretionary",
  TITAN:"Consumer Discretionary", TRENT:"Consumer Discretionary",
  MARUTI:"Consumer Discretionary", TATAMOTORS:"Consumer Discretionary",
  EICHERMOT:"Consumer Discretionary", BAJAJ:"Consumer Discretionary",
  TVSMOTORS:"Consumer Discretionary", HEROMOTOCO:"Consumer Discretionary",
  VOLTAS:"Consumer Discretionary", WHIRLPOOL:"Consumer Discretionary",
  GRAB:"Consumer Discretionary",
  XLY:"Consumer Discretionary", VCR:"Consumer Discretionary", AWAY:"Consumer Discretionary",
  // ── Consumer Staples ──
  PG:"Consumer Staples", KO:"Consumer Staples", PEP:"Consumer Staples",
  WMT:"Consumer Staples", COST:"Consumer Staples", PM:"Consumer Staples",
  MO:"Consumer Staples", CL:"Consumer Staples", KMB:"Consumer Staples",
  MDLZ:"Consumer Staples", GIS:"Consumer Staples", K:"Consumer Staples",
  HINDUNILVR:"Consumer Staples", ITC:"Consumer Staples", NESTLEIND:"Consumer Staples",
  BRITANNIA:"Consumer Staples", MARICO:"Consumer Staples", DABUR:"Consumer Staples",
  GODREJCP:"Consumer Staples", EMAMILTD:"Consumer Staples", COLPAL:"Consumer Staples",
  XLP:"Consumer Staples", VDC:"Consumer Staples", FTXG:"Consumer Staples",
  // ── Industrials ──
  BA:"Industrials", HON:"Industrials", CAT:"Industrials", DE:"Industrials",
  GE:"Industrials", LMT:"Industrials", RTX:"Industrials", NOC:"Industrials",
  MMM:"Industrials", EMR:"Industrials", ETN:"Industrials", PH:"Industrials",
  UPS:"Industrials", FDX:"Industrials", DAL:"Industrials", UAL:"Industrials",
  LARSEN:"Industrials", LT:"Industrials", LTFH:"Industrials", SIEMENS:"Industrials",
  ABB:"Industrials", BEL:"Industrials", HAL:"Industrials", BHEL:"Industrials",
  CONCOR:"Industrials", IRFC:"Industrials", RHM:"Industrials", MRSH:"Industrials",
  XLI:"Industrials", VIS:"Industrials", ITA:"Industrials",
  // ── Real Estate ──
  AMT:"Real Estate", PLD:"Real Estate", CCI:"Real Estate", EQIX:"Real Estate",
  O:"Real Estate", SPG:"Real Estate", PSA:"Real Estate", WELL:"Real Estate",
  DLF:"Real Estate", GODREJPROP:"Real Estate", PRESTIGE:"Real Estate",
  OBEROI:"Real Estate", SOBHA:"Real Estate", BRIGADE:"Real Estate",
  PHOENIXLTD:"Real Estate", MACROTECH:"Real Estate",
  VNQ:"Real Estate", IYR:"Real Estate", XLRE:"Real Estate", REM:"Real Estate",
  // ── Utilities ──
  NEE:"Utilities", DUK:"Utilities", SO:"Utilities", D:"Utilities",
  AEP:"Utilities", EXC:"Utilities", XEL:"Utilities", ED:"Utilities",
  NTPC:"Utilities", POWERGRID:"Utilities", TATAPOWER:"Utilities",
  ADANIGREEN:"Utilities", TORNTPOWER:"Utilities", CESC:"Utilities",
  XLU:"Utilities", VPU:"Utilities", IDU:"Utilities",
  // ── Materials ──
  LIN:"Materials", APD:"Materials", NEM:"Materials", FCX:"Materials",
  NUE:"Materials", CF:"Materials", MOS:"Materials", ALB:"Materials",
  JSWSTEEL:"Materials", TATASTEEL:"Materials", HINDALCO:"Materials",
  SAIL:"Materials", COALINDIA:"Materials", VEDL:"Materials",
  NMDC:"Materials", JINDALSTEL:"Materials", APLAPOLLO:"Materials",
  XLB:"Materials", VAW:"Materials", REMX:"Materials",
  // ── Commodities / Gold ETFs ──
  GLD:"Commodities", IAU:"Commodities", SLV:"Commodities", USO:"Commodities",
  DJP:"Commodities", PDBC:"Commodities", GLDM:"Commodities", BAR:"Commodities",
  // ── Broad market / Diversified ETFs ──
  SPY:"Diversified", IVV:"Diversified", VOO:"Diversified", VTI:"Diversified",
  NIFTYBEES:"Diversified", JUNIORBEES:"Diversified", MAFANG:"Diversified",
  CPSEETF:"Diversified", MON100:"Diversified", N100:"Diversified",
  EWI:"Diversified", EWJ:"Diversified", EWG:"Diversified", EWU:"Diversified",
  EEM:"Diversified", VWO:"Diversified", IEMG:"Diversified",
  ACWI:"Diversified", VT:"Diversified", SCHB:"Diversified",
  // ── China / EM thematic ETFs ──
  CHIQ:"Consumer Discretionary", CQQQ:"Technology",
  // ── Digital Assets ──
  BTC:"Digital Assets", ETH:"Digital Assets", COIN:"Digital Assets",
  BITO:"Digital Assets", IBIT:"Digital Assets",
};

// ── Name-based keyword inference for unrecognized tickers ──
// Used as last resort before falling back to asset class
const NAME_SECTOR_KEYWORDS = [
  { keywords:["bank","finance","financial","insurance","credit","lending","nbfc","capital","invest","citigroup","citicorp","jpmorgan","goldman","morgan stanley","barclays","hsbc","ubs","deutsche","société générale","bnp","axa","allianz","prudential","manulife","zurich"],  sector:"Financials" },
  { keywords:["tech","software","digital","data","cloud","cyber","ai","semiconductor","chip","it ","information technology","platform","internet","social media","streaming","saas","e-commerce","ecommerce","semiconductor","reddit","alphabet","microsoft","apple","meta","netflix","spotify","snap","twitter","x corp"], sector:"Technology" },
  { keywords:["pharma","drug","health","hospital","biotech","medic","diagnostic","therapeut"],        sector:"Healthcare" },
  { keywords:["oil","gas","petro","energy","power","solar","wind","coal","refin"],                    sector:"Energy" },
  { keywords:["steel","metal","alumin","copper","zinc","miner","cement","chemical","material"],       sector:"Materials" },
  { keywords:["auto","motor","vehicle","tyre","car","truck","electric vehicle"],                      sector:"Consumer Discretionary" },
  { keywords:["fmcg","consumer","food","beverage","tobacco","retail","apparel","hotel","restau"],     sector:"Consumer Staples" },
  { keywords:["infra","construct","engineer","logistic","aviation","transport","railway","port"],     sector:"Industrials" },
  { keywords:["real estate","property","realty","housing","reit","warehouse","land"],                 sector:"Real Estate" },
  { keywords:["telecom","telecomm","wireless","broadband","spectrum"],                                sector:"Communication Services" },
  { keywords:["utility","electric","ntpc","grid","distrib"],                                          sector:"Utilities" },
  { keywords:["gold","silver","commodity","bullion"],                                                 sector:"Commodities" },
];

// Resolve sector for a holding: manual field → ticker map → name inference → class fallback
const resolveHoldingSector = (h) => {
  // Sectors that should fall through to auto-resolution (stale/invalid)
  const BYPASS_SECTORS = new Set(["", "-", "Other", "Miscellaneous", "Financial Services", "Equities (Unclassified)"]);
  // 1. Manual sector wins only if it's a valid/current value
  if (h.sector && !BYPASS_SECTORS.has(h.sector)) return h.sector;

  // 2. Ticker map — try multiple stripping strategies for exchange-suffixed tickers
  const rawTk = (h.ticker || "").toUpperCase();
  // Strategy A: strip everything after first dot or dash (RELIANCE.NSE → RELIANCE, BTC-USD → BTC)
  const tkA = rawTk.replace(/\..*$/, "").replace(/[-_].*$/, "");
  if (tkA && tkA !== "-" && TICKER_SECTOR_MAP[tkA]) return TICKER_SECTOR_MAP[tkA];
  // Strategy B: keep only alpha characters at start (CITI.VI → CITI, D05.SI → D already done)
  const tkB = rawTk.replace(/[^A-Z]/g, "").slice(0, 6);
  if (tkB && tkB !== tkA && TICKER_SECTOR_MAP[tkB]) return TICKER_SECTOR_MAP[tkB];
  // Strategy C: first 1-4 chars as fallback (for tickers like NVO.WA → NVO)
  const tkC = tkA.slice(0, 4);
  if (tkC && tkC !== tkA && TICKER_SECTOR_MAP[tkC]) return TICKER_SECTOR_MAP[tkC];

  // 3. Name-based keyword inference (case-insensitive)
  if (h.name) {
    const nameLower = h.name.toLowerCase();
    for (const { keywords, sector } of NAME_SECTOR_KEYWORDS) {
      if (keywords.some(kw => nameLower.includes(kw))) return sector;
    }
  }

  // 4. Class-based fallback — for Equities keep as "Equities (Unclassified)" so it shows distinctly
  const CLASS_SECTOR = {
    "Equities":"Equities", "Indian Equities":"Indian Equities",
    "Crypto":"Digital Assets", "Real Estate":"Real Estate",
    "Fixed Income":"Fixed Income", "Debt":"Fixed Income", "Bonds":"Fixed Income",
    "Gold":"Commodities", "Commodities":"Commodities",
    "Private Equity":"Alternates",     "Hedge Funds":"Alternates",
    "Long Short Funds":"Alternates",   "Art & Collectibles":"Alternatives",
    "FCNs":"Alternates", "PPF":"Fixed Income",
    "Time Deposits":"Fixed Income", "Cash / Bank Balance":"Cash",
    "Global Mutual Funds":"Diversified", "Indian Mutual Funds":"Diversified",
  };
  return CLASS_SECTOR[h.class] || "Other";
};




// Resolve market cap tier for a holding: ticker map → name inference → class default
const resolveCapSize = (h) => {
  if (h.capSize && h.capSize !== "N/A" && h.capSize !== "-" && h.capSize !== "") return h.capSize;

  // Multi-strategy ticker stripping (matches resolveHoldingSector approach)
  const rawTk = (h.ticker || "").toUpperCase();
  const tkA   = rawTk.replace(/\..*$/, "").replace(/[-_].*$/, ""); // CITI.VI → CITI
  const tkB   = rawTk.replace(/[^A-Z]/g, "").slice(0, 6);           // D05.SI → DSI (alpha only)
  const tkC   = tkA.slice(0, 4);                                     // HDFCBANK → HDFC (rarely needed)
  for (const tk of [rawTk, tkA, tkB, tkC]) {
    if (tk && tk !== "-" && TICKER_CAP[tk]) return TICKER_CAP[tk];
  }

  // ETF check: if ETF_CAP_LOOKTHROUGH has this ticker, return dominant cap bucket
  const etfLT = ETF_CAP_LOOKTHROUGH[rawTk] || ETF_CAP_LOOKTHROUGH[tkA];
  if (etfLT) {
    const dominant = Object.entries(etfLT).sort((a,b) => b[1]-a[1])[0];
    if (dominant) return dominant[0];
  }

  // Class-based defaults
  const CLASS_CAP = {
    "Equities":           "Large",
    "Indian Equities":    "Large",
    "Crypto":             "Giant",
    "Real Estate":        "Large",
    "Fixed Income":       null,
    "Debt":               null,
    "Cash / Bank Balance":null,
    "Indian Mutual Funds":null,   // handled via MF look-through
    "Global Mutual Funds":null,
  };
  return CLASS_CAP[h.class] ?? null;
};


// ─────────────── GLOBAL MUTUAL FUNDS CURATED DATABASE ───────────────
// UCITS, Dublin-domiciled, Luxembourg, and major offshore fund families
// Used for ticker search when asset class = "Global Mutual Funds"
const GLOBAL_FUNDS_LIST = [
  // ── PIMCO ── (all Singapore-popular share classes)
  { name:"PIMCO GIS Income Fund E Class USD (Income)",          isin:"IE00B8K7V925", ticker:"-", yahooTicker:"IE00B8K7V925.IR", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income E USD monthly distribution dist income mdis retail" },
  { name:"PIMCO GIS Income Fund A Class SGD Hedged (Monthly)",  isin:"IE00B8K6LJ73", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income A SGD hedged monthly mdis singapore" },
  { name:"PIMCO GIS Income Fund A Class HKD Hedged (Monthly)",  isin:"IE00BF8J5B58", ticker:"-", currency:"HKD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income A HKD hedged monthly mdis" },
  { name:"PIMCO GIS Income Fund A Class AUD Hedged (Monthly)",  isin:"IE00B8KGV557", ticker:"-", currency:"AUD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income A AUD hedged monthly" },
  { name:"PIMCO GIS Income Fund A Class RMB Hedged (Monthly)",  isin:"IE00BF8J5C65", ticker:"-", currency:"CNY", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income A RMB CNY hedged monthly" },
  { name:"PIMCO GIS Income Fund E Class USD (Acc)",             isin:"IE00B7KFL990", ticker:"-", yahooTicker:"IE00B7KFL990.IR", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income E USD accumulation acc" },
  { name:"PIMCO GIS Income Fund E Class SGD Hedged",            isin:"IE00B8K6LJ73", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income E SGD" },
  { name:"PIMCO GIS Income Fund Institutional USD (Acc)",       isin:"IE00B87KCF77", ticker:"-", yahooTicker:"IE00B87KCF77.IR", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income institutional INST USD accumulation acc" },
  { name:"PIMCO GIS Income Fund E Class EUR (Hedged) (Acc)",    isin:"IE00B84J9L26", ticker:"-", yahooTicker:"IE00B84J9L26.IR", currency:"EUR", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO income E EUR hedged accumulation acc euro" },
  { name:"PIMCO GIS Diversified Income Fund A Class USD",       isin:"IE00B7N3F397", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO diversified income" },
  { name:"PIMCO GIS Diversified Income Fund A Class SGD Hedged",isin:"IE00BF1QJ629", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO diversified income SGD" },
  // ── AllianceBernstein (AB) — Luxembourg FCP I ──
  { name:"AB FCP I American Income Portfolio A2 USD (Acc)",     isin:"LU0095030564", ticker:"-", yahooTicker:"LU0095030564.LU", currency:"USD", exchange:"Luxembourg", manager:"AllianceBernstein", sector:"Fixed Income", keywords:"AB FCP American income portfolio A2 USD accumulation AllianceBernstein" },
  { name:"PIMCO GIS Total Return Bond Fund A USD",              isin:"IE00B11XYT31", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO total return bond" },
  { name:"PIMCO GIS Global Bond Fund A USD",                    isin:"IE00B11XZG71", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO global bond" },
  { name:"PIMCO GIS Dynamic Multi-Asset Fund A USD",            isin:"IE00BFRT3504", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Mixed Allocation", keywords:"PIMCO dynamic multi asset" },
  { name:"PIMCO GIS Emerging Markets Bond Fund A USD",          isin:"IE00B11XZK10", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO emerging markets bond" },
  { name:"PIMCO GIS Capital Securities Fund A USD",             isin:"IE00BYXVKZ06", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO capital securities AT1 CoCo" },
  { name:"PIMCO GIS US High Yield Bond Fund A USD",             isin:"IE0034228552", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO high yield" },
  { name:"PIMCO GIS Short-Term Fund A USD",                     isin:"IE0034228735", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO short term money market" },
  { name:"PIMCO GIS Euro Income Bond Fund A EUR",               isin:"IE00BVZRPY73", ticker:"-", currency:"EUR", exchange:"Ireland UCITS", manager:"PIMCO", sector:"Fixed Income", keywords:"PIMCO euro income bond" },
  // ── BlackRock / iShares ──
  { name:"BlackRock Global Funds World Technology Fund A2", isin:"LU0171310443", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Technology" },
  { name:"BlackRock Global Funds World Financials Fund",    isin:"LU0122376428", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Financials" },
  { name:"BlackRock Global Funds US Flexible Equity Fund",  isin:"LU0171275813", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Equities" },
  { name:"BlackRock Global Funds ESG Multi-Asset Fund",     isin:"LU1983215008", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Mixed Allocation" },
  { name:"BlackRock Global Funds World Bond Fund",          isin:"LU0050372472", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Fixed Income" },
  { name:"BlackRock Global Funds Global Allocation Fund",   isin:"LU0072462426", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Mixed Allocation" },
  { name:"BlackRock Global Funds Emerging Markets Fund",    isin:"LU0011850392", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"BlackRock", sector:"Equities" },
  // ── Fidelity ──
  { name:"Fidelity Funds Global Technology Fund A USD",     isin:"LU0115760606", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Technology" },
  { name:"Fidelity Funds World Fund A USD",                 isin:"LU0048578792", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Equities" },
  { name:"Fidelity Funds Emerging Markets Fund A USD",      isin:"LU0048578446", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Equities" },
  { name:"Fidelity Funds Asian Equity Fund A USD",          isin:"LU0048620945", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Equities" },
  { name:"Fidelity Funds Global Multi Asset Income Fund",   isin:"LU0936579832", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Mixed Allocation" },
  { name:"Fidelity Funds US Dollar Bond Fund A USD",        isin:"LU0251132147", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Fidelity", sector:"Fixed Income" },
  // ── FTGF (Franklin Templeton Global Funds, Ireland-domiciled UCITS, ex-Legg Mason) ──
  // ClearBridge Infrastructure Value Fund — popular Singapore SGD-H class + USD anchor
  { name:"FTGF ClearBridge Infrastructure Value Fund Class A SGD Distributing (M) (Hedged) Plus", isin:"IE00BD4GTT62", ticker:"-", yahooTicker:"0P0001ANC2.SI", currency:"SGD", exchange:"Ireland UCITS", manager:"ClearBridge / Franklin Templeton", sector:"Infrastructure", keywords:"FTGF ClearBridge infrastructure value SGD hedged mdis monthly Singapore Legg Mason" },
  { name:"FTGF ClearBridge Infrastructure Value Fund Class A USD Accumulating",                   isin:"IE00BF2K4B19", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"ClearBridge / Franklin Templeton", sector:"Infrastructure", keywords:"FTGF ClearBridge infrastructure value USD acc" },
  // ClearBridge US Aggressive Growth Fund
  { name:"FTGF ClearBridge US Aggressive Growth Fund Class A USD Accumulating",                   isin:"IE00B19Z9Z06", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"ClearBridge / Franklin Templeton", sector:"Equities", keywords:"FTGF ClearBridge US aggressive growth USD acc Legg Mason" },
  { name:"FTGF ClearBridge US Aggressive Growth Fund Class A USD Distributing (A)",               isin:"IE00B19Z9P08", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"ClearBridge / Franklin Templeton", sector:"Equities", keywords:"FTGF ClearBridge US aggressive growth USD dist" },
  { name:"FTGF ClearBridge US Aggressive Growth Fund Class A SGD Accumulating (Hedged)",          isin:"IE00B894F039", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"ClearBridge / Franklin Templeton", sector:"Equities", keywords:"FTGF ClearBridge US aggressive growth SGD hedged" },
  // Western Asset Macro Opportunities Bond Fund
  { name:"FTGF Western Asset Macro Opportunities Bond Fund Class A USD Accumulating",             isin:"IE00BC9S3Z47", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Western Asset / Franklin Templeton", sector:"Fixed Income", keywords:"FTGF Western Asset macro opportunities bond USD acc Legg Mason" },
  // Brandywine Global Income Optimiser Fund
  { name:"FTGF Brandywine Global Income Optimiser Fund Class A USD Distributing (M) Plus",        isin:"IE00B9KHJ624", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Brandywine / Franklin Templeton", sector:"Fixed Income", keywords:"FTGF Brandywine global income optimiser USD mdis monthly Legg Mason" },
  { name:"FTGF Brandywine Global Income Optimiser Fund Class A SGD Distributing (M) (Hedged) Plus", isin:"IE00BLSP4122", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"Brandywine / Franklin Templeton", sector:"Fixed Income", keywords:"FTGF Brandywine global income optimiser SGD hedged mdis monthly" },
  // Royce US Small Cap Opportunity Fund
  { name:"FTGF Royce US Small Cap Opportunity Fund Class A USD Accumulating",                     isin:"IE00B19Z4B17", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Royce / Franklin Templeton", sector:"Equities", keywords:"FTGF Royce US small cap opportunity USD acc Legg Mason" },
  { name:"FTGF Royce US Small Cap Opportunity Fund Class A SGD Accumulating",                     isin:"IE00B66KJ199", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"Royce / Franklin Templeton", sector:"Equities", keywords:"FTGF Royce US small cap opportunity SGD" },
  // ── Franklin Templeton Investment Funds (FTIF, Luxembourg-domiciled SICAV) ──
  { name:"Franklin U.S. Opportunities Fund A (acc) USD",                                          isin:"LU0109391861", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Equities", keywords:"Franklin US opportunities equity growth" },
  { name:"Franklin Income Fund A (Mdis) USD",                                                     isin:"LU0098860793", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Mixed Allocation", keywords:"Franklin income mdis monthly distribution multi-asset" },
  { name:"Franklin Income Fund A (Mdis) SGD Hedged",                                              isin:"LU0320765646", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Mixed Allocation", keywords:"Franklin income mdis monthly distribution SGD hedged multi-asset Singapore" },
  { name:"Franklin Global Income Fund A (Mdis) SGD-H1",                                           isin:"LU2216994678", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Mixed Allocation", keywords:"Franklin global income mdis SGD hedged monthly distribution" },
  { name:"Franklin Global Income & Growth Opportunities Fund A (acc) SGD-H1",                     isin:"LU2216205422", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Mixed Allocation", keywords:"Franklin global income growth opportunities SGD hedged acc" },
  // ── JPMorgan ──
  // Income Fund — all Singapore-distributed share classes
  { name:"JPMorgan Funds - Income Fund A (acc) USD",                         isin:"LU0395794307", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis acc" },
  { name:"JPMorgan Funds - Income Fund A (mth) SGD Hedged",                  isin:"LU0860477805", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis monthly distribution SGD hedged" },
  { name:"JPMorgan Funds - Income Fund A (mth) USD",                         isin:"LU0762471344", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis monthly distribution" },
  { name:"JPMorgan Funds - Income Fund A (mth) HKD Hedged",                  isin:"LU0860477987", ticker:"-", currency:"HKD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis HKD" },
  { name:"JPMorgan Funds - Income Fund A (mth) AUD Hedged",                  isin:"LU0860478019", ticker:"-", currency:"AUD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis AUD" },
  { name:"JPMorgan Funds - Income Fund A (mth) RMB Hedged",                  isin:"LU0860478282", ticker:"-", currency:"CNY", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income mdis RMB CNY" },
  { name:"JPMorgan Funds - Income Fund C (acc) USD",                         isin:"LU0395791279", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM income C class" },
  // Other popular JPM funds
  { name:"JPMorgan Funds - Global Select Equity Fund A (acc) USD",           isin:"LU0053685532", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Equities" },
  { name:"JPMorgan Funds - America Equity Fund A (acc) USD",                 isin:"LU0053685615", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Equities" },
  { name:"JPMorgan Funds - ASEAN Equity Fund A (acc) USD",                   isin:"LU0441851309", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Equities" },
  { name:"JPMorgan Funds - Emerging Markets Equity Fund A (acc) USD",        isin:"LU0053685458", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Equities" },
  { name:"JPMorgan Funds - Global Bond Opportunities Fund A (mth) USD",      isin:"LU1050437842", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Fixed Income", keywords:"JPM global bond opportunities" },
  { name:"JPMorgan Funds - Global Bond Fund A (acc) USD",                    isin:"LU0168343294", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Fixed Income" },
  { name:"JPMorgan Funds - Asia Pacific Income Fund A (mth) USD",            isin:"LU1232780953", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM asia pacific income" },
  { name:"JPMorgan Funds - Asia Pacific Income Fund A (mth) SGD Hedged",     isin:"LU1232781092", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"JPMorgan AM", sector:"Mixed Allocation", keywords:"JPM asia pacific income SGD" },
  // ── Franklin Templeton ──
  { name:"Franklin Technology Fund A Acc USD",              isin:"LU0260870158", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Technology" },
  { name:"Templeton Global Bond Fund A USD",                isin:"LU0029871042", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Fixed Income" },
  { name:"Franklin India Fund A USD",                       isin:"LU0231203729", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Equities" },
  { name:"Templeton Emerging Markets Bond Fund",            isin:"LU0029871125", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Fixed Income" },
  { name:"Franklin US Opportunities Fund A USD",            isin:"LU0122613795", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Franklin Templeton", sector:"Equities" },
  // ── Schroders ──
  { name:"Schroder International Selection Fund Global Equity", isin:"LU0106236937", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Schroders", sector:"Equities" },
  { name:"Schroder ISF Emerging Markets",                   isin:"LU0181495838", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Schroders", sector:"Equities" },
  { name:"Schroder ISF Global Multi-Asset Income",          isin:"LU0757360507", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Schroders", sector:"Mixed Allocation" },
  { name:"Schroder ISF Asian Opportunities",                isin:"LU0270756905", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Schroders", sector:"Equities" },
  // ── UBS ──
  { name:"UBS (Lux) Strategy Fund Balanced USD",            isin:"LU0038842364", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"UBS AM", sector:"Mixed Allocation" },
  { name:"UBS (Lux) Equity Fund Global Opportunity",        isin:"LU0075760445", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"UBS AM", sector:"Equities" },
  { name:"UBS Global Income Fund",                          isin:"LU0827911851", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"UBS AM", sector:"Mixed Allocation" },
  // ── Invesco ──
  { name:"Invesco Pan European High Income Fund A USD",     isin:"LU0243957239", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Invesco", sector:"Fixed Income" },
  { name:"Invesco Global Income Fund A USD",                isin:"LU0800031959", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Invesco", sector:"Mixed Allocation" },
  // ── Vanguard (Ireland UCITS) ──
  { name:"Vanguard Global Stock Index Fund USD",            isin:"IE0031786142", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Vanguard", sector:"Equities" },
  { name:"Vanguard US 500 Stock Index Fund USD",            isin:"IE0032620787", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Vanguard", sector:"Equities" },
  { name:"Vanguard Emerging Markets Stock Index Fund",      isin:"IE0031786937", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Vanguard", sector:"Equities" },
  // ── DWS ──
  { name:"DWS Invest Global Infrastructure LC USD",        isin:"LU0329760002", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"DWS", sector:"Real Estate" },
  { name:"DWS Multi Opportunities LD EUR",                 isin:"DE0008490848", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"DWS", sector:"Mixed Allocation" },
  // ── Allianz ──
  { name:"Allianz Global Investors Fund Intl Healthcare",  isin:"LU0099575016", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Allianz GI", sector:"Healthcare" },
  { name:"Allianz Best Styles Global Equity",              isin:"LU0845548267", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Allianz GI", sector:"Equities" },
  // ── Amundi ──
  { name:"Amundi Funds Global Multi-Asset Conservative",   isin:"LU0568601553", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Amundi", sector:"Mixed Allocation" },
  { name:"Amundi Funds Emerging Markets Bond",             isin:"LU0119124325", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Amundi", sector:"Fixed Income" },
  // ── Goldman Sachs AM ──
  { name:"Goldman Sachs Global Strategic Income Bond",     isin:"LU0234617601", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Goldman Sachs AM", sector:"Fixed Income" },
  { name:"Goldman Sachs Emerging Markets Equity Portfolio",isin:"LU0114524124", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Goldman Sachs AM", sector:"Equities" },

  // ── Aberdeen (abrdn) ──
  { name:"abrdn SICAV I - Emerging Markets Equity Fund A Acc USD",  isin:"LU0011963245", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Equities" },
  { name:"abrdn SICAV I - Asian Smaller Companies Fund A Acc USD",   isin:"LU0011963161", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Equities" },
  { name:"abrdn SICAV I - India Equity Fund A Acc USD",              isin:"LU0231490524", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Equities" },
  { name:"abrdn SICAV I - World Equity Fund A Acc USD",              isin:"LU0011963831", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Equities" },
  { name:"abrdn SICAV I - Global Bond Fund A Acc USD",               isin:"LU0505786353", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Fixed Income" },
  { name:"Aberdeen Standard SICAV I - Asian Income Fund",            isin:"LU0476552370", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Mixed Allocation" },
  { name:"abrdn SICAV I - Diversified Income Fund A Acc USD",        isin:"LU0761802083", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Aberdeen", sector:"Mixed Allocation" },

  // ── Jupiter ──
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (acc) USD",       isin:"LU0603489659", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond acc USD" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (acc) EUR Hedged",isin:"LU0699454945", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond EUR hedged" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (acc) SGD Hedged",isin:"LU0946220883", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond SGD hedged singapore acc accumulating" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (inc) SGD Hedged M IRD",isin:"LU0946220966", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond SGD hedged inc income distributing monthly IRD MIRD singapore" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (inc) SGD Hedged Q IRD",isin:"LU1162003421", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond SGD hedged quarterly income distributing IRD singapore" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (inc) EUR Hedged M IRD",isin:"LU0946221188", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond EUR income distributing monthly IRD" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund L (inc) USD",       isin:"LU0699455090", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond inc distribution" },
  { name:"Jupiter Global Fund - Dynamic Bond Fund I (acc) USD",       isin:"LU0699455256", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Fixed Income", keywords:"jupiter dynamic bond institutional" },
  { name:"Jupiter Global Fund - Asia Pacific Income L (acc) USD",     isin:"LU0946221691", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Mixed Allocation" },
  { name:"Jupiter Global Fund - Global Ecology Growth L (acc) USD",   isin:"LU0260085492", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Equities" },
  { name:"Jupiter Global Fund - European Growth L (acc) EUR",         isin:"LU0260087282", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Equities" },
  { name:"Jupiter Global Fund - Financial Innovation L (acc) USD",    isin:"LU0946221345", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Financials" },
  { name:"Jupiter Global Fund - Global Value Equity L (acc) USD",     isin:"LU1914790098", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Jupiter AM", sector:"Equities" },

  // ── Morgan Stanley ──
  { name:"Morgan Stanley Investment Funds Global Opportunity Fund A USD", isin:"LU0552385295", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Morgan Stanley IM", sector:"Equities" },
  { name:"Morgan Stanley Investment Funds Global Brands Fund A USD",      isin:"LU0119620416", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Morgan Stanley IM", sector:"Equities" },
  { name:"Morgan Stanley Investment Funds Emerging Leaders Equity Fund",  isin:"LU0860557212", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Morgan Stanley IM", sector:"Equities" },
  { name:"Morgan Stanley Investment Funds Global Bond Fund A USD",        isin:"LU0073229545", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Morgan Stanley IM", sector:"Fixed Income" },
  { name:"Morgan Stanley Investment Funds US Growth Fund A USD",          isin:"LU0073234601", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Morgan Stanley IM", sector:"Equities" },

  // ── T. Rowe Price ──
  { name:"T. Rowe Price Funds SICAV - Global Equity Fund A USD",     isin:"LU0143551892", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"T. Rowe Price", sector:"Equities" },
  { name:"T. Rowe Price Funds SICAV - Emerging Markets Equity Fund", isin:"LU0133085943", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"T. Rowe Price", sector:"Equities" },
  { name:"T. Rowe Price Funds SICAV - Global Aggregate Bond Fund",   isin:"LU1244139231", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"T. Rowe Price", sector:"Fixed Income" },

  // ── Neuberger Berman ──
  { name:"Neuberger Berman Global High Yield Bond Fund A USD",        isin:"IE00B3BCC394", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Neuberger Berman", sector:"Fixed Income" },
  { name:"Neuberger Berman Emerging Market Debt Fund A USD",          isin:"IE00B3B83B11", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Neuberger Berman", sector:"Fixed Income" },
  { name:"Neuberger Berman US Multi Cap Opportunities Fund A USD",    isin:"IE00B3B84B26", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Neuberger Berman", sector:"Equities" },

  // ── Mirae Asset (Global UCITS) ──
  { name:"Mirae Asset Global Emerging Markets Great Consumer Fund",   isin:"IE00B5M8KS62", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Mirae Asset", sector:"Equities" },
  { name:"Mirae Asset Asia Great Consumer Equity Fund",               isin:"IE00B5S8LM31", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Mirae Asset", sector:"Equities" },

  // ── DSP Merrill Lynch Global Funds ──
  { name:"DSP World Energy Fund - Regular Plan - Growth",             isin:"INF740KA1SG2", ticker:"-", currency:"INR", exchange:"AMFI", manager:"DSP MF", sector:"Energy" },
  { name:"DSP World Agriculture Fund - Regular Plan - Growth",        isin:"INF740KA1SF4", ticker:"-", currency:"INR", exchange:"AMFI", manager:"DSP MF", sector:"Commodities" },
  { name:"DSP World Gold Fund - Regular Plan - Growth",               isin:"INF740KA1SH0", ticker:"-", currency:"INR", exchange:"AMFI", manager:"DSP MF", sector:"Commodities" },
  { name:"DSP World Mining Fund - Regular Plan - Growth",             isin:"INF740KA1SI8", ticker:"-", currency:"INR", exchange:"AMFI", manager:"DSP MF", sector:"Commodities" },

  // ── HSBC Global Funds ──
  { name:"HSBC Global Investment Funds - Global Equity Volatility Focused", isin:"LU1321417492", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"HSBC AM", sector:"Equities" },
  { name:"HSBC Global Investment Funds - Asia Pacific ex Japan Equity",     isin:"LU0164741759", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"HSBC AM", sector:"Equities" },
  { name:"HSBC Global Investment Funds - Global Bond",                      isin:"LU0165126525", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"HSBC AM", sector:"Fixed Income" },

  // ── Robeco ──
  { name:"Robeco Credit Income Fund D USD",                  isin:"LU0805245599", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco credit income bonds" },
  { name:"Robeco Credit Income Fund D EUR",                  isin:"LU0805245672", ticker:"-", currency:"EUR", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco credit income EUR" },
  { name:"Robeco Credit Income Fund DH SGD",                 isin:"LU1120298234", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco credit income SGD hedged singapore" },
  { name:"Robeco Global Credits Fund D USD",                 isin:"LU0230818397", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco global credits investment grade" },
  { name:"Robeco High Yield Bonds D USD",                    isin:"LU0213458780", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco high yield bonds" },
  { name:"Robeco High Yield Bonds DH SGD",                   isin:"LU1120297939", ticker:"-", currency:"SGD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco high yield SGD singapore" },
  { name:"Robeco Emerging Markets Equities D USD",           isin:"LU0187077218", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Equities", keywords:"robeco emerging markets equity" },
  { name:"Robeco Emerging Markets Bonds D USD",              isin:"LU0329564603", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Fixed Income", keywords:"robeco emerging markets bonds EM debt" },
  { name:"Robeco QI Global Conservative Equities D USD",     isin:"LU0705782961", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Equities", keywords:"robeco conservative equities low vol" },
  { name:"Robeco Sustainable Global Stars Equities D USD",   isin:"LU0387754996", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Equities", keywords:"robeco sustainable global stars ESG" },
  { name:"Robeco Asia-Pacific Equities D USD",               isin:"LU0084617165", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Robeco", sector:"Equities", keywords:"robeco asia pacific equity" },

  // ── Ashmore ──
  { name:"Ashmore SICAV Emerging Markets Equity Fund A USD",        isin:"LU0252968826", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ashmore", sector:"Equities" },
  { name:"Ashmore SICAV Emerging Markets Total Return Fund A USD",  isin:"LU0252969394", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ashmore", sector:"Fixed Income" },

  // ── Wellington / Vontobel ──
  { name:"Vontobel Fund - mtx Sustainable Emerging Markets Leaders", isin:"LU0384408626", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Vontobel AM", sector:"Equities" },
  { name:"Vontobel Fund - Quality Growth B USD",                     isin:"LU0120694659", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Vontobel AM", sector:"Equities" },

  // ── FSSA / First Sentier Investors ──
  { name:"FSSA Investment Managers - Dividend Advantage Fund Class III USD Acc",   isin:"IE00B1W6CW87", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Dividend Advantage Fund Class III USD Inc",   isin:"IE00B1W6CV70", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Dividend Advantage Fund Class I GBP Acc",     isin:"IE00B1W6CX94", ticker:"-", currency:"GBP", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Dividend Advantage Fund Class I SGD Acc",     isin:"IE00BFLMTB45", ticker:"-", currency:"SGD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Asia Pacific All Cap Fund Class III USD Acc", isin:"IE00B0169V57", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Asia Pacific Growth Fund Class III USD Acc",  isin:"IE00B3BHHQ78", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Greater China Growth Fund Class III USD Acc", isin:"IE00B3BGL175", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - India Subcontinent Fund Class III USD Acc",   isin:"IE00B3B9RK88", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Japan Equity Fund Class III USD Acc",         isin:"IE00B3BJ5K74", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },
  { name:"FSSA Investment Managers - Global Emerging Markets Focus Fund",          isin:"IE00B3PY3B43", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"FSSA", sector:"Equities" },

  // ── Eastspring Investments (Prudential AM Asia) ──
  { name:"Eastspring Investments - Asia Pacific Equity Fund Class A USD",          isin:"LU0231494427", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Eastspring", sector:"Equities" },
  { name:"Eastspring Investments - Asian Equity Income Fund Class A USD",          isin:"LU0374582848", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Eastspring", sector:"Equities" },
  { name:"Eastspring Investments - India Infrastructure Equity Fund Class A USD",  isin:"LU0374581527", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Eastspring", sector:"Equities" },
  { name:"Eastspring Investments - Asian High Yield Bond Fund Class A USD",        isin:"LU0536840956", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Eastspring", sector:"Fixed Income" },

  // ── Artisan Partners ──
  { name:"Artisan Developing World Fund Class I USD",                              isin:"IE00BK4MXG37", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Artisan Partners", sector:"Equities" },
  { name:"Artisan Global Opportunities Fund Class I USD",                          isin:"IE00BK4MXF20", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Artisan Partners", sector:"Equities" },
  { name:"Artisan Non-U.S. Small-Mid Growth Fund Class I USD",                    isin:"IE00BK4MXH44", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Artisan Partners", sector:"Equities" },

  // ── Ninety One (Investec AM) ──
  { name:"Ninety One Global Strategy Fund - All Africa Equity Fund A USD",         isin:"LU0345772024", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ninety One", sector:"Equities" },
  { name:"Ninety One Global Strategy Fund - Emerging Markets Equity A USD",        isin:"LU0345773519", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ninety One", sector:"Equities" },
  { name:"Ninety One Global Strategy Fund - Global Multi-Asset Income Fund",       isin:"LU0860009005", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ninety One", sector:"Mixed Allocation" },
  { name:"Ninety One GSF - Global Environment Fund A Inc USD",                    isin:"LU2063669421", ticker:"-", currency:"USD", exchange:"Luxembourg", manager:"Ninety One", sector:"Equities" },

  // ── Polar Capital ──
  { name:"Polar Capital Global Technology Fund Class I USD",                       isin:"IE00B42GN492", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Polar Capital", sector:"Technology" },
  { name:"Polar Capital Healthcare Opportunities Fund Class I USD",               isin:"IE00B3MWKF39", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Polar Capital", sector:"Healthcare" },

  // ── Matthews Asia ──
  { name:"Matthews Asia Funds - Asia Dividend Fund Class I USD",                  isin:"IE00B4ZS5K65", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Matthews Asia", sector:"Equities" },
  { name:"Matthews Asia Funds - Pacific Tiger Fund Class I USD",                  isin:"IE00B3PMHM28", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Matthews Asia", sector:"Equities" },
  { name:"Matthews Asia Funds - India Fund Class I USD",                          isin:"IE00B3PTPM77", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Matthews Asia", sector:"Equities" },
  { name:"Matthews Asia Funds - China Fund Class I USD",                          isin:"IE00B3PM8X81", ticker:"-", currency:"USD", exchange:"Ireland UCITS", manager:"Matthews Asia", sector:"Equities" },

  // ── Indian FOFs investing globally (AMFI-registered) ──
  { name:"Kotak International REIT FOF - Regular Growth",    isin:"INF174KA1MN9", ticker:"-", currency:"INR", exchange:"AMFI", manager:"Kotak MF", sector:"Real Estate" },
  { name:"Nippon India US Equity Opportunities Fund - Growth",isin:"INF204KA1HA9", ticker:"-", currency:"INR", exchange:"AMFI", manager:"Nippon MF", sector:"Equities" },
  { name:"Franklin India Feeder Franklin US Opportunities Fund",isin:"INF090I01817",ticker:"-", currency:"INR", exchange:"AMFI", manager:"Franklin Templeton", sector:"Equities" },
  { name:"Edelweiss Greater China Equity Off-shore Fund",     isin:"INF754K01KA8", ticker:"-", currency:"INR", exchange:"AMFI", manager:"Edelweiss MF", sector:"Equities" },
  { name:"PGIM India Global Equity Opportunities Fund",       isin:"INF663L01AS9", ticker:"-", currency:"INR", exchange:"AMFI", manager:"PGIM India MF", sector:"Equities" },
];

// ─────────────── STATIC DATA ───────────────
const CURRENCIES = [
  // Major
  "USD","EUR","GBP","INR","JPY","CHF",
  // Middle East & Asia
  "AED","SGD","HKD","CNY","KRW","THB","MYR","IDR","PHP","VND","TWD","BDT","PKR","LKR","NPR",
  // Europe
  "SEK","NOK","DKK","PLN","CZK","HUF","RON","TRY",
  // Americas
  "CAD","AUD","NZD","BRL","MXN","CLP","COP","PEN","ARS",
  // Africa & Others
  "ZAR","EGP","NGN","KES","GHS","MAD","QAR","KWD","BHD","OMR","SAR",
];
const FX_RATES_DEFAULT = { USD:1, EUR:0.92, GBP:0.79, INR:83.2, JPY:149.5, CHF:0.88, AED:3.67, SGD:1.34, HKD:7.82, CAD:1.36, AUD:1.53, CNY:7.24, KRW:1330, BRL:4.97, ZAR:18.6, MXN:17.1, SEK:10.4, NOK:10.6, DKK:6.88, NZD:1.63, THB:35.1, MYR:4.72, IDR:15700, TWD:31.8, SAR:3.75, QAR:3.64, KWD:0.307, BHD:0.377, OMR:0.385 };

// Benchmark definitions
// type:"equity"  → TIME_SERIES_MONTHLY_ADJUSTED,  close field: "5. adjusted close"
// type:"crypto"  → DIGITAL_CURRENCY_MONTHLY,       close field: "4a. close (USD)"
// Tickers verified against Alpha Vantage supported symbols (March 2026)
// ── Static benchmark definitions — user benchmarks merged inside component ──
// All benchmarks are user-added — start empty, pick from presets in the Add Benchmark modal
const BENCHMARKS_STATIC = [
  { id:"nifty50",    label:"Nifty 50",        ticker:"^NSEI"   },
  { id:"nifty500",   label:"Nifty 500",        ticker:"^CRSLDX" },
  { id:"sp500",      label:"S&P 500",          ticker:"^GSPC"   },
  { id:"msciworld",  label:"MSCI World",       ticker:"URTH"    },
  { id:"gold",       label:"Gold",             ticker:"GC=F"    },
  { id:"niftybank",  label:"Nifty Bank",       ticker:"^NSEBANK"},
  { id:"nasdaq",     label:"Nasdaq 100",       ticker:"^NDX"    },
  { id:"straits",    label:"Straits Times",    ticker:"^STI"    },
];

// Full preset catalogue shown in the Add Benchmark modal quick-add list
const BENCHMARK_PRESETS = [
  // ── US (ETFs — most reliable via Yahoo Finance) ──
  { label:"S&P 500",         ticker:"SPY",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"NASDAQ 100",      ticker:"QQQ",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"Dow Jones",       ticker:"DIA",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"Russell 2000",    ticker:"IWM",    type:"equity",    currency:"USD", note:"ETF" },
  // ── India (Yahoo Finance index symbols — most reliable) ──
  { label:"Nifty 50",        ticker:"^NSEI",  type:"equity",    currency:"INR", note:"Index" },
  { label:"Sensex",          ticker:"^BSESN", type:"equity",    currency:"INR", note:"Index" },
  { label:"Nifty Bank",      ticker:"^NSEBANK", type:"equity",  currency:"INR", note:"Index" },
  { label:"Nifty Midcap 50", ticker:"^NSEMDCP50", type:"equity", currency:"INR", note:"ETF" },
  { label:"Nifty Next 50",   ticker:"^NSENXT50", type:"equity", currency:"INR", note:"ETF" },
  // ── Global ──
  { label:"MSCI World",      ticker:"ACWI",   type:"equity",    currency:"USD", note:"ETF" },
  { label:"MSCI Emerging",   ticker:"EEM",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"FTSE 100",        ticker:"EWU",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"DAX",             ticker:"EWG",    type:"equity",    currency:"EUR", note:"ETF" },
  { label:"Nikkei 225",      ticker:"EWJ",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"Hang Seng",       ticker:"EWH",    type:"equity",    currency:"USD", note:"ETF" },
  { label:"Straits Times",   ticker:"EWS",    type:"equity",    currency:"SGD", note:"ETF" },
  // ── Alternatives ──
  { label:"Gold",            ticker:"GLD",    type:"commodity", currency:"USD", note:"ETF" },
  { label:"Silver",          ticker:"SLV",    type:"commodity", currency:"USD", note:"ETF" },
  { label:"Bitcoin",         ticker:"BTC",    type:"crypto",    currency:"USD", note:"Crypto" },
];

// ── Sentiment scores (0–100) — composite of RSI · 50DMA gap · VIX · Put/Call · Breadth ──
// 0–30 = Bearish, 31–60 = Neutral, 61–80 = Bullish, 81–100 = Strong Bull
const BENCHMARK_SENTIMENT = {
  // By static id
  sp500:     { score:45, rsi:44, vix:22.1, pcratio:0.92, dma50:-4.2, breadth:38, label:"Neutral",  signals:"RSI 44 · VIX 22 · Trade uncertainty" },
  nifty:     { score:48, rsi:49, vix:14.1, pcratio:1.02, dma50:-1.8, breadth:48, label:"Neutral",  signals:"RSI 49 · India VIX 14 · FII selling easing" },
  msciworld: { score:43, rsi:45, vix:19.5, pcratio:0.95, dma50:-3.8, breadth:40, label:"Neutral",  signals:"RSI 45 · Global macro headwinds" },
  ftse:      { score:52, rsi:51, vix:18.2, pcratio:1.05, dma50:0.8,  breadth:52, label:"Neutral",  signals:"RSI 51 · UK defensives outperforming" },
  gold:      { score:74, rsi:68, vix:null, pcratio:null, dma50:8.2,  breadth:null,label:"Bullish",  signals:"RSI 68 · DXY weak · Safe haven demand strong" },
  btc:       { score:58, rsi:54, vix:null, pcratio:null, dma50:2.1,  breadth:null,label:"Neutral",  signals:"RSI 54 · Consolidation phase" },
};

// ── Ticker-to-sentiment mapping ── covers all common user-added benchmarks
const TICKER_SENTIMENT_MAP = {
  // US
  "SPY":{"score":45,"rsi":44,"vix":22.1,"pcratio":0.92,"dma50":-4.2,"breadth":38,"label":"Neutral","signals":"RSI 44 · VIX 22 · Trade war uncertainty weighing"},
  "QQQ":{"score":40,"rsi":41,"vix":24.5,"pcratio":0.88,"dma50":-6.1,"breadth":33,"label":"Neutral","signals":"RSI 41 · Tech under pressure · Rates elevated"},
  "IVV":{"score":45,"rsi":44,"vix":22.1,"pcratio":0.92,"dma50":-4.2,"breadth":38,"label":"Neutral","signals":"RSI 44 · Broad market consolidation"},
  "VTI":{"score":44,"rsi":43,"vix":22.0,"pcratio":0.91,"dma50":-4.5,"breadth":37,"label":"Neutral","signals":"RSI 43 · US total market subdued"},
  // India
  "^NSEI":  {"score":48,"rsi":49,"vix":14.1,"pcratio":1.02,"dma50":-1.8,"breadth":48,"label":"Neutral","signals":"RSI 49 · Nifty 50 · FII flows stabilising"},
  "^BSESN": {"score":47,"rsi":48,"vix":14.2,"pcratio":1.03,"dma50":-2.1,"breadth":47,"label":"Neutral","signals":"RSI 48 · Sensex · Domestic buying support"},
  "^NSEBANK":{"score":44,"rsi":45,"vix":15.2,"pcratio":1.08,"dma50":-3.4,"breadth":42,"label":"Neutral","signals":"RSI 45 · Bank Nifty · NIM pressure concerns"},
  "^NSEMDCP50":{"score":52,"rsi":53,"vix":13.8,"pcratio":0.98,"dma50":0.5,"breadth":54,"label":"Neutral","signals":"RSI 53 · Midcap outperforming largecap"},
  "^NSENXT50":{"score":50,"rsi":51,"vix":14.0,"pcratio":1.00,"dma50":-0.2,"breadth":51,"label":"Neutral","signals":"RSI 51 · Next 50 · Mixed momentum"},
  // Global ETFs
  "EWU":{"score":52,"rsi":51,"vix":18.2,"pcratio":1.05,"dma50":0.8,"breadth":52,"label":"Neutral","signals":"RSI 51 · UK equities · Defensive tilt helping"},
  "EEM":{"score":50,"rsi":50,"vix":19.1,"pcratio":0.99,"dma50":-0.5,"breadth":50,"label":"Neutral","signals":"RSI 50 · EM equities · DXY weakness supportive"},
  "EWH":{"score":55,"rsi":54,"vix":17.5,"pcratio":0.95,"dma50":2.1,"breadth":56,"label":"Neutral","signals":"RSI 54 · Hang Seng · China stimulus hopes"},
  "EWJ":{"score":48,"rsi":47,"vix":19.8,"pcratio":1.02,"dma50":-2.2,"breadth":45,"label":"Neutral","signals":"RSI 47 · Japan · Yen strength headwind"},
  "VWO":{"score":51,"rsi":51,"vix":18.5,"pcratio":0.98,"dma50":0.2,"breadth":52,"label":"Neutral","signals":"RSI 51 · Broad EM · Commodity exporters supporting"},
  "ACWI":{"score":46,"rsi":45,"vix":20.5,"pcratio":0.94,"dma50":-3.1,"breadth":43,"label":"Neutral","signals":"RSI 45 · All-world · Global growth concerns"},
  // Commodities & Alternatives
  "GLD":{"score":74,"rsi":68,"vix":null,"pcratio":null,"dma50":8.2,"breadth":null,"label":"Bullish","signals":"RSI 68 · Safe haven demand · DXY weak"},
  "SLV":{"score":62,"rsi":60,"vix":null,"pcratio":null,"dma50":4.5,"breadth":null,"label":"Bullish","signals":"RSI 60 · Industrial demand · Gold coattails"},
  "GDX":{"score":69,"rsi":65,"vix":null,"pcratio":null,"dma50":6.8,"breadth":null,"label":"Bullish","signals":"RSI 65 · Gold miners leveraged play"},
  "USO":{"score":38,"rsi":40,"vix":null,"pcratio":null,"dma50":-5.2,"breadth":null,"label":"Neutral","signals":"RSI 40 · Oil · Demand concerns outweigh supply cuts"},
  "BTC-USD":{"score":55,"rsi":54,"vix":null,"pcratio":null,"dma50":2.1,"breadth":null,"label":"Neutral","signals":"RSI 54 · Bitcoin · Consolidating near support"},
  // Bonds
  "TLT":{"score":54,"rsi":53,"vix":null,"pcratio":null,"dma50":1.5,"breadth":null,"label":"Neutral","signals":"RSI 53 · Long bonds · Rate cut expectations building"},
  "AGG":{"score":53,"rsi":52,"vix":null,"pcratio":null,"dma50":0.8,"breadth":null,"label":"Neutral","signals":"RSI 52 · US aggregate bonds · Duration cautious"},
};

// Look up sentiment: first by static id, then by ticker match
function getSentiment(benchId, ticker) {
  // Direct id match
  if (BENCHMARK_SENTIMENT[benchId]) return BENCHMARK_SENTIMENT[benchId];
  // Match by ticker (user-added benchmarks have ids like "user_spy")
  if (ticker) {
    const t = ticker.toUpperCase();
    if (TICKER_SENTIMENT_MAP[t]) return TICKER_SENTIMENT_MAP[t];
    // Try without ^ prefix (e.g. "^NSEBANK" → "NSEBANK")
    if (t.startsWith("^") && TICKER_SENTIMENT_MAP[t.slice(1)]) return TICKER_SENTIMENT_MAP[t.slice(1)];
    // Try with ^ prefix (e.g. "NSEI" → "^NSEI")
    if (!t.startsWith("^") && TICKER_SENTIMENT_MAP["^"+t]) return TICKER_SENTIMENT_MAP["^"+t];
  }
  // Try stripping user_ prefix and matching ticker-derived id
  const tickerFromId = benchId.replace(/^user_/, "").toUpperCase().replace(/_/g, ".");
  if (TICKER_SENTIMENT_MAP[tickerFromId]) return TICKER_SENTIMENT_MAP[tickerFromId];
  // Try with ^ prefix on derived ticker
  if (TICKER_SENTIMENT_MAP["^"+tickerFromId]) return TICKER_SENTIMENT_MAP["^"+tickerFromId];
  // Default neutral with helpful text
  return { score:50, rsi:50, vix:null, pcratio:null, dma50:0, breadth:null, label:"Neutral",
    signals: ticker ? `Market data for ${ticker} · Signals updating…` : "Composite signals: Neutral" };
}

// Proxy: unknown benchmark ids return a neutral score of 50
const BENCHMARK_SENTIMENT_SAFE = new Proxy(BENCHMARK_SENTIMENT, {
  get(target, key) {
    return target[key] ?? { score:50, rsi:50, vix:null, pcratio:null, dma50:0, breadth:null, label:"Neutral", signals:"Composite signals: Neutral" };
  }
});

// ── Helper: compute needle angle from sentiment score (0–100 → 180°–0°) ──
// Semicircle: left end = bearish (180°), right end = bullish (0°)
// SVG arc centre = (44, 47), radius = 37
function sentimentToPoint(score) {
  const angle = Math.PI - (score / 100) * Math.PI; // 180° at 0, 0° at 100
  return {
    x: 44 + 37 * Math.cos(angle),
    y: 47 + 37 * Math.sin(angle),
    col: score >= 81 ? "#3B6D11" : score >= 61 ? "#639922" : score >= 31 ? "#854F0B" : "#A32D2D",
    arcCol: score >= 61 ? "#639922" : score >= 31 ? "#EF9F27" : "#E24B4A",
    textCol: score >= 81 ? "#3B6D11" : score >= 61 ? "#3B6D11" : score >= 31 ? "#854F0B" : "#A32D2D",
  };
}

// Arc path from start (score=0, left end) to needle point
function sentimentArcPath(score) {
  const end = sentimentToPoint(score);
  const large = score > 50 ? 1 : 0;
  return `M7 47 A37 37 0 ${large} 1 ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
}

// Static fallback benchmark metrics — approximate values used ONLY when Alpha Vantage API is unavailable.
// Live data is fetched via loadBenchmarks() using TIME_SERIES_MONTHLY_ADJUSTED.
const BENCHMARK_FALLBACK = new Proxy({
  sp500:    { mtd:1.1,  ytd:8.4,  oneY:22.1, threeY:10.2, fiveY:14.1, tenY:12.3 },
  nifty:    { mtd:2.3,  ytd:12.1, oneY:18.7, threeY:14.3, fiveY:16.8, tenY:13.2 },
  msciworld:{ mtd:0.8,  ytd:6.8,  oneY:19.4, threeY:9.1,  fiveY:12.3, tenY:10.6 },
  ftse:     { mtd:0.4,  ytd:3.2,  oneY:9.8,  threeY:5.4,  fiveY:6.1,  tenY:5.8  },
  gold:     { mtd:1.9,  ytd:9.1,  oneY:14.2, threeY:11.8, fiveY:9.3,  tenY:7.2  },
  btc:      { mtd:12.4, ytd:42.1, oneY:110.4,threeY:28.7, fiveY:56.2, tenY:89.4 },
}, { get(target, key) { return target[key] ?? { mtd:null, ytd:null, oneY:null, threeY:null, fiveY:null, tenY:null }; } });

const ASSET_CLASSES = [
  // ── Equity ──
  "Equities",           // Global / US / Developed Market stocks & ETFs
  "Indian Equities",    // BSE / NSE listed stocks
  // ── Debt ──
  "Fixed Income",       // Bonds, NCDs, G-Secs, debt MFs
  "Time Deposits",      // Bank FDs, structured deposits
  // ── Collective Vehicles ──
  "Mutual Funds",         // Generic / Indian domestic MFs
  "Indian Mutual Funds",  // Indian AMFI-registered schemes (enables AMFI search)
  "Global Mutual Funds",  // UCITS / offshore funds (enables global fund search)
  // ── Real Assets ──
  "Real Estate",        // Direct property, REITs, InvITs
  "Commodities",        // Gold, Silver, Oil, Agri ETFs / physical
  "Art & Collectibles", // Art, wine, watches, collectibles
  // ── Alternatives ──
  "Alternates",         // PE, VC, Hedge Funds, FCNs, Structured Products
  "Market Neutral",     // L/S equity, arbitrage, absolute return funds
  // ── Digital ──
  "Crypto",             // Bitcoin, Ethereum, other digital assets
  // ── Cash ──
  "Cash / Bank Balance",// Savings, current accounts, liquid holdings
  // ── Other ──
  "Miscellaneous",      // Unlisted, options, anything not above
];

// ── Value-only asset classes ─────────────────────────────────────────────
// These don't behave like listed securities — no ticker, exchange, qty,
// or per-share price. The user enters a single Invested amount and a
// Current Value (latest valuation). Math: cost = invested, value = current.
// Returns are still computed if both are present.
//
// Used to:
//   1. Hide ticker/qty/price/exchange in Add and Edit forms
//   2. Display "—" instead of qty/price in the holdings table
//   3. Skip Yahoo/MFAPI price refresh (no symbol to fetch)
//   4. Avoid auto-classifying these as "Listed" anywhere
const VALUE_ONLY_CLASSES = new Set([
  "Cash / Bank Balance",
  "Time Deposits",
  "Real Estate",
  "Alternates",          // PE / VC / AIF / Structured Products — manual valuation
  "Commodities",         // Gold, Silver, Oil — physical or unlisted; for ETFs, classify under Equities
  "Art & Collectibles",
  "Miscellaneous",       // Catch-all for unlisted / non-priceable holdings
]);
const isValueOnlyClass = (cls) => VALUE_ONLY_CLASSES.has(cls);

// ── ETF Look-through table ───────────────────────────────────────────────
// Curated breakdowns of common ETFs in our client portfolios. Each ETF maps
// to a sector breakdown (% summing to ~100) and a region breakdown (also %).
// "lastVerified" = when these numbers were last spot-checked against the
// fund's published fact sheet. Refresh quarterly — ETF compositions drift.
//
// To add a new ETF: copy the structure, look up the latest fact sheet, fill
// in sector + region weights. Don't worry about hitting exactly 100 — close
// is fine; we normalise on use.
//
// Format: ETF_LOOKTHROUGH[ticker] = { name, sector: { sec: pct, ... },
//                                     region: { reg: pct, ... }, lastVerified }
const ETF_LOOKTHROUGH = {
  // ── US Broad / S&P 500 ──
  "SPY":   { name: "SPDR S&P 500",                    sector: { "Technology": 33, "Financials": 13, "Healthcare": 11, "Consumer Discretionary": 10, "Communication Services": 9, "Industrials": 8, "Consumer Staples": 6, "Energy": 4, "Utilities": 2, "Real Estate": 2, "Materials": 2 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "VOO":   { name: "Vanguard S&P 500",                sector: { "Technology": 33, "Financials": 13, "Healthcare": 11, "Consumer Discretionary": 10, "Communication Services": 9, "Industrials": 8, "Consumer Staples": 6, "Energy": 4, "Utilities": 2, "Real Estate": 2, "Materials": 2 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "IVV":   { name: "iShares Core S&P 500",            sector: { "Technology": 33, "Financials": 13, "Healthcare": 11, "Consumer Discretionary": 10, "Communication Services": 9, "Industrials": 8, "Consumer Staples": 6, "Energy": 4, "Utilities": 2, "Real Estate": 2, "Materials": 2 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  // ── US Tech / Nasdaq ──
  "QQQ":   { name: "Invesco QQQ (Nasdaq 100)",        sector: { "Technology": 51, "Communication Services": 16, "Consumer Discretionary": 14, "Healthcare": 6, "Consumer Staples": 4, "Industrials": 5, "Utilities": 1, "Financials": 1, "Real Estate": 0, "Energy": 0, "Materials": 0 }, region: { "US": 97, "China": 2, "Other": 1 }, lastVerified: "2025-Q4" },
  // ── US Sector ETFs ──
  "SMH":   { name: "VanEck Semiconductors",           sector: { "Semiconductors": 100 }, region: { "US": 70, "Asia": 25, "Europe": 5 }, lastVerified: "2025-Q4" },
  "SOXX":  { name: "iShares Semiconductor",           sector: { "Semiconductors": 100 }, region: { "US": 78, "Asia": 17, "Europe": 5 }, lastVerified: "2025-Q4" },
  "VFH":   { name: "Vanguard Financials",             sector: { "Financials": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "VHT":   { name: "Vanguard Healthcare",             sector: { "Healthcare": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "XLK":   { name: "Tech Select Sector SPDR",         sector: { "Technology": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "XLF":   { name: "Financial Select Sector SPDR",    sector: { "Financials": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "XLE":   { name: "Energy Select Sector SPDR",       sector: { "Energy": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  // ── Small Cap ──
  "IWM":   { name: "iShares Russell 2000",            sector: { "Financials": 18, "Industrials": 17, "Healthcare": 16, "Technology": 13, "Consumer Discretionary": 11, "Real Estate": 7, "Energy": 6, "Materials": 4, "Utilities": 4, "Consumer Staples": 3, "Communication Services": 1 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  // ── International / Emerging Markets ──
  "IEMG":  { name: "iShares Core MSCI Emerging Mkts", sector: { "Technology": 24, "Financials": 22, "Consumer Discretionary": 13, "Communication Services": 9, "Industrials": 7, "Materials": 6, "Energy": 5, "Healthcare": 4, "Consumer Staples": 5, "Utilities": 3, "Real Estate": 2 }, region: { "China": 26, "India": 19, "Taiwan": 19, "South Korea": 12, "Brazil": 5, "Other EM": 19 }, lastVerified: "2025-Q4" },
  "EEM":   { name: "iShares MSCI Emerging Markets",   sector: { "Technology": 25, "Financials": 22, "Consumer Discretionary": 13, "Communication Services": 9, "Industrials": 7, "Materials": 6, "Energy": 5, "Healthcare": 4, "Consumer Staples": 5, "Utilities": 3, "Real Estate": 1 }, region: { "China": 27, "India": 19, "Taiwan": 19, "South Korea": 12, "Brazil": 5, "Other EM": 18 }, lastVerified: "2025-Q4" },
  "VWO":   { name: "Vanguard FTSE Emerging Markets",  sector: { "Financials": 22, "Technology": 21, "Consumer Discretionary": 13, "Communication Services": 8, "Industrials": 7, "Materials": 7, "Energy": 6, "Healthcare": 4, "Consumer Staples": 6, "Utilities": 3, "Real Estate": 3 }, region: { "China": 28, "India": 21, "Taiwan": 19, "Brazil": 6, "South Africa": 4, "Other EM": 22 }, lastVerified: "2025-Q4" },
  "EFA":   { name: "iShares MSCI EAFE",               sector: { "Financials": 21, "Industrials": 16, "Healthcare": 11, "Consumer Discretionary": 11, "Technology": 9, "Consumer Staples": 8, "Materials": 7, "Communication Services": 5, "Energy": 4, "Utilities": 4, "Real Estate": 4 }, region: { "Japan": 22, "UK": 15, "France": 11, "Switzerland": 10, "Germany": 8, "Australia": 7, "Other Developed": 27 }, lastVerified: "2025-Q4" },
  "EMXC":  { name: "iShares MSCI EM ex-China",        sector: { "Technology": 28, "Financials": 21, "Consumer Discretionary": 9, "Materials": 8, "Industrials": 8, "Communication Services": 7, "Energy": 6, "Healthcare": 5, "Consumer Staples": 5, "Utilities": 2, "Real Estate": 1 }, region: { "India": 26, "Taiwan": 26, "South Korea": 16, "Brazil": 7, "South Africa": 5, "Other EM": 20 }, lastVerified: "2025-Q4" },
  // ── Bonds ──
  "AGG":   { name: "iShares Core US Aggregate Bond",  sector: { "Fixed Income": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "BND":   { name: "Vanguard Total Bond Market",      sector: { "Fixed Income": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  // ── REITs ──
  "VNQ":   { name: "Vanguard Real Estate",            sector: { "Real Estate": 100 }, region: { "US": 100 }, lastVerified: "2025-Q4" },
  "DCRU.SI": { name: "Digital Core REIT",             sector: { "Real Estate": 100 }, region: { "US": 90, "Asia": 10 }, lastVerified: "2025-Q4" },
  // ── India ETFs (NSE) ──
  "BANKBEES.NS": { name: "Nippon India ETF Nifty Bank", sector: { "Financials": 100 }, region: { "India": 100 }, lastVerified: "2025-Q4" },
  "NIFTYBEES.NS": { name: "Nippon India ETF Nifty 50", sector: { "Financials": 32, "Technology": 13, "Energy": 11, "Consumer Staples": 9, "Consumer Discretionary": 9, "Industrials": 6, "Healthcare": 5, "Materials": 5, "Utilities": 3, "Communication Services": 4, "Real Estate": 3 }, region: { "India": 100 }, lastVerified: "2025-Q4" },
  "GOLDBEES.NS": { name: "Nippon India ETF Gold",     sector: { "Commodities": 100 }, region: { "Global": 100 }, lastVerified: "2025-Q4" },
  // ── Geographic concentrations ──
  "FXI":   { name: "iShares China Large-Cap",         sector: { "Financials": 25, "Communication Services": 20, "Consumer Discretionary": 22, "Technology": 12, "Energy": 6, "Industrials": 5, "Healthcare": 4, "Consumer Staples": 3, "Real Estate": 2, "Utilities": 1 }, region: { "China": 100 }, lastVerified: "2025-Q4" },
  "INDA":  { name: "iShares MSCI India",              sector: { "Financials": 26, "Technology": 14, "Energy": 11, "Consumer Discretionary": 10, "Consumer Staples": 9, "Industrials": 7, "Healthcare": 6, "Materials": 6, "Utilities": 4, "Communication Services": 4, "Real Estate": 3 }, region: { "India": 100 }, lastVerified: "2025-Q4" },
};

// Resolve an ETF's look-through breakdown by ticker. Tries exact match first,
// then strips suffix (.NS, .BO, .SI). Returns null if not in our table.
function getEtfBreakdown(ticker) {
  if (!ticker) return null;
  if (ETF_LOOKTHROUGH[ticker]) return ETF_LOOKTHROUGH[ticker];
  // Try with common suffixes stripped
  const stripped = ticker.replace(/\.(NS|BO|SI|L|HK)$/i, "");
  if (ETF_LOOKTHROUGH[stripped]) return ETF_LOOKTHROUGH[stripped];
  // Try with .NS suffix added (Indian NSE convention)
  const withNS = `${stripped}.NS`;
  if (ETF_LOOKTHROUGH[withNS]) return ETF_LOOKTHROUGH[withNS];
  return null;
}

// Apply look-through aggregation to a list of items (watchlist or holdings).
// Each item is expected to have: { ticker, name, sector, valueUSD (or
// equivalent), country (optional) }. For ETFs in our table, we distribute
// the value across the underlying sector/region. For everything else, we
// use the item's own sector/country.
//
// Returns:
//   {
//     sectorBreakdown: { sectorName: pct, ... },   // sums to 100
//     regionBreakdown: { regionName: pct, ... },   // sums to 100
//     totalValue:      number,                     // base currency
//     coverage: {
//       totalEtfs:      <count of items where sector includes ETF>,
//       coveredEtfs:    <count of those covered by our table>,
//       unknownTickers: [tickers we couldn't look through]
//     }
//   }
function applyLookthrough(items, opts = {}) {
  const valueKey = opts.valueKey || "valueUSD";
  const sectorAgg = {};
  const regionAgg = {};
  let totalValue = 0;
  let totalEtfs = 0;
  let coveredEtfs = 0;
  const unknownTickers = [];
  for (const item of items) {
    const value = parseFloat(item[valueKey]) || 0;
    if (value <= 0) continue;
    totalValue += value;
    // Identify ETFs by class containing "ETF" or sector including "ETF",
    // OR by ticker matching our look-through table directly.
    const cls = (item.class || "").toLowerCase();
    const sec = (item.sector || "").toLowerCase();
    const looksLikeEtf = cls.includes("etf") || sec.includes("etf") || !!getEtfBreakdown(item.ticker);
    if (looksLikeEtf) totalEtfs++;
    const breakdown = getEtfBreakdown(item.ticker);
    if (breakdown) {
      coveredEtfs++;
      // Distribute by sector
      const secTotal = Object.values(breakdown.sector).reduce((s, v) => s + v, 0);
      if (secTotal > 0) {
        for (const [s, pct] of Object.entries(breakdown.sector)) {
          sectorAgg[s] = (sectorAgg[s] || 0) + (value * pct / secTotal);
        }
      }
      // Distribute by region
      const regTotal = Object.values(breakdown.region).reduce((s, v) => s + v, 0);
      if (regTotal > 0) {
        for (const [r, pct] of Object.entries(breakdown.region)) {
          regionAgg[r] = (regionAgg[r] || 0) + (value * pct / regTotal);
        }
      }
    } else {
      // Fall back to the item's own sector/region tags
      if (looksLikeEtf && item.ticker) unknownTickers.push(item.ticker);
      const itemSector = item.sector || item.class || "Other";
      const itemRegion = item.country || item.region || "Unknown";
      sectorAgg[itemSector] = (sectorAgg[itemSector] || 0) + value;
      regionAgg[itemRegion] = (regionAgg[itemRegion] || 0) + value;
    }
  }
  // Convert absolute values to percentages
  const toPct = (agg) => {
    const out = {};
    if (totalValue <= 0) return out;
    for (const [k, v] of Object.entries(agg)) {
      out[k] = +(v / totalValue * 100).toFixed(1);
    }
    return out;
  };
  return {
    sectorBreakdown: toPct(sectorAgg),
    regionBreakdown: toPct(regionAgg),
    totalValue,
    coverage: { totalEtfs, coveredEtfs, unknownTickers },
  };
}

// ── Reusable Export Utility ─────────────────────────────────────────────
// Two helpers:
//   1. exportToCSV(rows, columns, filename)   — emits a download
//   2. exportToBrandedPDF(opts)               — opens a print-ready window
//
// Both are pure functions — no React state, no hooks. Safe to call from
// any handler. Designed to be used across Watchlist, Holdings, Transactions,
// Backtest, and AI Advisor.

// CSV: rows = array of objects; columns = [{key, label}] or just array of keys.
// Properly escapes quotes, handles multiline cell values, uses CRLF line endings.
function exportToCSV(rows, columns, filename) {
  if (!Array.isArray(rows)) { console.error("[exportToCSV] rows must be an array"); return; }
  // Normalise columns into {key, label} pairs
  const cols = (columns || []).map(c =>
    typeof c === "string" ? { key: c, label: c } : { key: c.key, label: c.label || c.key, format: c.format }
  );
  if (cols.length === 0) { console.error("[exportToCSV] no columns provided"); return; }
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const headerLine = cols.map(c => escape(c.label)).join(",");
  const dataLines  = rows.map(r =>
    cols.map(c => {
      const raw = r[c.key];
      const val = c.format ? c.format(raw, r) : raw;
      return escape(val);
    }).join(",")
  );
  // CRLF for Excel compatibility, BOM for UTF-8 detection
  const csv = "\ufeff" + [headerLine, ...dataLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `export-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// Branded PDF via browser print: opens a new window with HTML, triggers print.
// User chooses "Save as PDF" in the dialog. Works on every modern browser, no
// CDN dependency. opts:
//   title         : window/document title (also filename in print dialog)
//   subtitle      : optional subtitle under brand
//   bodyHTML      : the main report HTML (string) — must NOT include <html>/<body> tags
//   landscape     : boolean, defaults to false (portrait)
//   onPopupBlocked: callback fired when window.open returns null
function exportToBrandedPDF(opts = {}) {
  const {
    title = "Forthius Report",
    subtitle = "",
    bodyHTML = "",
    landscape = false,
    onPopupBlocked = null,
  } = opts;
  const today = new Date().toLocaleDateString(undefined, { year:"numeric", month:"long", day:"numeric" });
  const pageOrientation = landscape ? "A4 landscape" : "A4 portrait";
  // Inline SVG logo — same as the watchlist PDF. Two-tone navy + blue.
  const logoSVG = `<svg viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg" fill="none" style="height: 50px; width: auto;">
    <rect x="0" y="2" width="9" height="54" rx="3" fill="#00205B"/>
    <rect x="0" y="2" width="38" height="9" rx="3" fill="#00205B"/>
    <rect x="0" y="25" width="27" height="8" rx="2" fill="#00205B"/>
    <rect x="18" y="14" width="9" height="54" rx="3" fill="#0079C1"/>
    <rect x="18" y="14" width="38" height="9" rx="3" fill="#0079C1"/>
    <rect x="18" y="37" width="27" height="8" rx="2" fill="#0079C1"/>
    <line x1="5" y1="62" x2="60" y2="7" stroke="#00205B" stroke-width="5.5" stroke-linecap="round"/>
    <line x1="5" y1="62" x2="60" y2="7" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
    <circle cx="60" cy="7" r="4.5" fill="#0079C1"/>
    <text x="82" y="32" font-family="Georgia,Times New Roman,serif" font-size="26" font-weight="700" fill="#00205B" letter-spacing="3">FORTHIUS</text>
    <text x="83" y="56" font-family="Trebuchet MS,Calibri,sans-serif" font-size="14" font-weight="600" fill="#0079C1" letter-spacing="4">FINALYTICS</text>
  </svg>`;
  const escapeHtml = (s) => String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)} — ${today}</title>
<style>
  @page { size: ${pageOrientation}; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1F2937; margin: 0; padding: 0; }
  .header { border-bottom: 2px solid #00205B; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: center; }
  .meta { font-size: 11px; color: #6B7280; text-align: right; }
  h2 { font-size: 16px; margin: 22px 0 10px 0; padding-bottom: 4px; border-bottom: 1px solid #E5E7EB; font-weight: 700; }
  h3 { font-size: 13px; margin: 14px 0 6px 0; font-weight: 700; }
  .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
  .kpi { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; padding: 10px 12px; }
  .kpi-label { font-size: 9px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
  .kpi-value { font-size: 18px; font-weight: 700; margin-top: 3px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th, td { padding: 7px 8px; text-align: left; border-bottom: 1px solid #E5E7EB; vertical-align: top; }
  th { background: #F9FAFB; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #6B7280; font-weight: 600; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .muted { color: #6B7280; font-size: 9px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 600; }
  .badge.buy { background: #DCFCE7; color: #166534; }
  .badge.above { background: #FEF3C7; color: #92400E; }
  .badge.watch { background: #DBEAFE; color: #1E40AF; }
  .badge.high { background: #DCFCE7; color: #166534; }
  .badge.med { background: #FEF3C7; color: #92400E; }
  .badge.low { background: #F3F4F6; color: #4B5563; }
  .callout { background: #EEEDFE; border-left: 3px solid #4F46E5; padding: 10px 12px; border-radius: 4px; margin: 10px 0; font-size: 11px; line-height: 1.55; }
  .thesis-block { padding: 10px 0; border-bottom: 1px solid #F3F4F6; page-break-inside: avoid; }
  .thesis-block:last-child { border-bottom: none; }
  .thesis-head { margin-bottom: 5px; font-size: 11px; }
  .thesis-head strong { font-size: 12px; }
  .thesis-body { font-size: 10.5px; line-height: 1.55; color: #374151; white-space: pre-wrap; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #E5E7EB; font-size: 9px; color: #9CA3AF; text-align: center; }
  .progress-bar { height: 6px; background: #F3F4F6; border-radius: 3px; overflow: hidden; }
  .progress-fill { height: 100%; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="header">
    <div style="display: flex; align-items: center; gap: 16px;">
      ${logoSVG}
      <div style="border-left: 1px solid #E5E7EB; padding-left: 16px;">
        <div style="font-size: 13px; color: #00205B; font-weight: 600;">${escapeHtml(title)}</div>
        ${subtitle ? `<div style="font-size: 11px; color: #6B7280; margin-top: 2px;">${escapeHtml(subtitle)}</div>` : ""}
      </div>
    </div>
    <div class="meta">${today}</div>
  </div>
  ${bodyHTML}
  <div class="footer">Generated by Forthius Finalytics · For discussion only · Not investment advice</div>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) {
    if (onPopupBlocked) onPopupBlocked();
    else console.error("[exportToBrandedPDF] popup blocked");
    return false;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => { try { w.print(); } catch(e) { console.error("Print failed:", e); } }, 350);
  return true;
}

// Helper: HTML-escape strings for safe injection into bodyHTML
function escapeHtmlForPDF(s) {
  return String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
}

// ── Technical signal computation ──────────────────────────────────────────
// Pure functions that take a price-history array (oldest → newest) of close
// prices and return computed signals. Used by AI Watchlist Advisor.
//
// IMPORTANT: All functions tolerate short or missing series — they return
// `null` for any signal that can't be computed reliably (e.g., RSI needs
// at least 15 closes; if you have fewer, returns null instead of garbage).

function computeSMA(closes, period) {
  if (!Array.isArray(closes) || closes.length < period) return null;
  const slice = closes.slice(-period);
  const sum = slice.reduce((s, x) => s + x, 0);
  return sum / period;
}

// Wilder's RSI (most common standard) — needs period+1 closes minimum.
function computeRSI(closes, period = 14) {
  if (!Array.isArray(closes) || closes.length < period + 1) return null;
  let gains = 0, losses = 0;
  // Initial average over the first `period` deltas
  for (let i = 1; i <= period; i++) {
    const delta = closes[i] - closes[i-1];
    if (delta >= 0) gains += delta; else losses -= delta;
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  // Smooth (Wilder's) over the rest
  for (let i = period + 1; i < closes.length; i++) {
    const delta = closes[i] - closes[i-1];
    const gain = delta >= 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
  }
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - (100 / (1 + rs));
}

// Trailing return over `period` trading days
function computeReturn(closes, period) {
  if (!Array.isArray(closes) || closes.length < period + 1) return null;
  const start = closes[closes.length - 1 - period];
  const end   = closes[closes.length - 1];
  if (!start || start <= 0) return null;
  return ((end - start) / start) * 100;
}

// Distance from 52-week high (negative number = below high)
function compute52WHighDistance(closes) {
  if (!Array.isArray(closes) || closes.length < 5) return null;
  const window = closes.slice(-252); // ~1 trading year
  const high = Math.max(...window);
  const last = closes[closes.length - 1];
  if (!high || high <= 0) return null;
  return ((last - high) / high) * 100;
}

// Annualised volatility from daily returns over `period` days
function computeVolatility(closes, period = 30) {
  if (!Array.isArray(closes) || closes.length < period + 1) return null;
  const slice = closes.slice(-(period + 1));
  const dailyReturns = [];
  for (let i = 1; i < slice.length; i++) {
    if (slice[i-1] > 0) dailyReturns.push((slice[i] - slice[i-1]) / slice[i-1]);
  }
  if (dailyReturns.length < 2) return null;
  const mean = dailyReturns.reduce((s, r) => s + r, 0) / dailyReturns.length;
  const variance = dailyReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (dailyReturns.length - 1);
  const stdev = Math.sqrt(variance);
  return stdev * Math.sqrt(252) * 100; // annualised, as percent
}

// Compute all signals at once for a price series. Returns an object with
// every signal — null where it couldn't be computed.
function computeTechSignals(closes) {
  if (!Array.isArray(closes) || closes.length === 0) {
    return { sma50: null, sma200: null, rsi: null, ret1m: null, ret3m: null,
             ret6m: null, ret12m: null, distFrom52WHigh: null, volatility30d: null,
             lastClose: null, dataPoints: 0 };
  }
  const last = closes[closes.length - 1];
  const sma50  = computeSMA(closes, 50);
  const sma200 = computeSMA(closes, 200);
  return {
    lastClose: last,
    sma50, sma200,
    aboveSMA50:  sma50  ? last > sma50  : null,
    aboveSMA200: sma200 ? last > sma200 : null,
    sma50AboveSma200: (sma50 != null && sma200 != null) ? sma50 > sma200 : null, // golden/death cross
    rsi: computeRSI(closes, 14),
    ret1m:  computeReturn(closes, 21),   // ~21 trading days = 1 month
    ret3m:  computeReturn(closes, 63),
    ret6m:  computeReturn(closes, 126),
    ret12m: computeReturn(closes, 252),
    distFrom52WHigh: compute52WHighDistance(closes),
    volatility30d: computeVolatility(closes, 30),
    dataPoints: closes.length,
  };
}

// Fetch ~1 year of daily closes via the existing /api/benchmark proxy (Yahoo
// chart v8 endpoint, works with any symbol). Returns array of close prices
// oldest → newest, or [] on failure.
async function fetchYahooHistory(ticker) {
  if (!ticker || ticker === "-") return [];
  try {
    const resp = await fetch(`/api/benchmark?ticker=${encodeURIComponent(ticker)}&interval=1d&range=1y`);
    if (!resp.ok) return [];
    const data = await resp.json();
    const pts = data?.points || [];
    if (!Array.isArray(pts) || pts.length === 0) return [];
    // Proxy returns newest-first; we want oldest-first for tech computation.
    // Filter out null/zero closes (Yahoo gaps).
    const closes = pts
      .slice()
      .reverse()
      .map(p => p.close)
      .filter(x => typeof x === "number" && x > 0);
    return closes;
  } catch (err) {
    console.error("[fetchYahooHistory] failed for", ticker, err.message);
    return [];
  }
}

// Fetch tech signals for an array of tickers in parallel, with concurrency
// cap of 6 (avoid hammering Yahoo). Returns { ticker: signals } map.
async function fetchTechSignalsForTickers(tickers, concurrency = 6) {
  const out = {};
  const queue = [...tickers];
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const ticker = queue.shift();
      const closes = await fetchYahooHistory(ticker);
      out[ticker] = computeTechSignals(closes);
    }
  });
  await Promise.all(workers);
  return out;
}

// Legacy class name normalisation → canonical name
const NORMALISE_CLASS = {
  "Private Equity":      "Alternates",
  "Hedge Funds":         "Alternates",
  "Long Short Funds":    "Alternates",
  "FCNs":                "Alternates",
  "FCN":                 "Alternates",
  // Indian/Global Mutual Funds are kept distinct — collapsing them breaks price routing
  // "Indian Mutual Funds": "Mutual Funds",  // removed — keep distinct for AMFI NAV fetch
  // "Global Mutual Funds": "Mutual Funds",  // removed — keep distinct for UCITS NAV fetch
  "Cash":                "Cash / Bank Balance",
};
const normaliseClass = (cls) => NORMALISE_CLASS[cls] || cls || "Miscellaneous";

// ── MF underlying asset-class look-through ──
// Returns the broad asset-class exposure of a mutual fund based on its name/type
// Used to "see through" a Mutual Fund to its underlying Equities / Fixed Income / Mixed
const getMFUnderlyingClass = (holding) => {
  if (!holding || (holding.class !== "Mutual Funds" && holding.class !== "Indian Mutual Funds" && holding.class !== "Global Mutual Funds" && !holding.isMutualFund)) return null;
  const name = (holding.name || "").toLowerCase();
  if (/debt|liquid|overnight|money.market|ultra.short|short.term|corporate.bond|gilt|bond.fund/i.test(name)) return "Fixed Income";
  if (/hybrid|balanced.advantage|baf|dynamic.asset|equity.hybrid|aggressive.hybrid|conservative.hybrid/i.test(name)) return "Mixed (Hybrid)";
  if (/international|global|us.equity|nasdaq|s.p.500|world|overseas/i.test(name)) return "Global Equities";
  // Default: equity fund
  return "Equities";
};

// ── MF look-through allocation for charts (underlying class split) ──
// Returns { "Equities": pct, "Fixed Income": pct, ... } summing to 100
const getMFClassSplit = (holding) => {
  const name = (holding.name || "").toLowerCase();
  const isGlobal = holding.class === "Global Mutual Funds"
    || /international|global|us.equity|nasdaq|s.p.500|world|overseas|foreign/i.test(name);

  // ── Debt / Fixed Income funds (both Indian and Global) ──
  if (/debt|liquid|overnight|money.market|ultra.short|short.term|corporate.bond|gilt|income.fund|bond.fund|credit.risk|pimco|bluebay|templeton.*income/i.test(name))
    return { "Fixed Income": 95, "Cash / Bank Balance": 5 };

  // ── Conservative / Debt-heavy hybrids ──
  if (/conservative.hybrid|monthly.income|mip/i.test(name))
    return { "Fixed Income": 70, "Equities": 22, "Cash / Bank Balance": 8 };

  // ── Balanced / Dynamic hybrids ──
  if (/hybrid|balanced.advantage|baf|dynamic.asset|multi.asset|asset.allocation/i.test(name)) {
    if (isGlobal) return { "Global Equities": 55, "Fixed Income": 35, "Cash / Bank Balance": 10 };
    return { "Equities": 60, "Fixed Income": 32, "Cash / Bank Balance": 8 };
  }

  // ── Equity-heavy hybrids ──
  if (/equity.hybrid|aggressive.hybrid/i.test(name)) {
    if (isGlobal) return { "Global Equities": 70, "Fixed Income": 22, "Cash / Bank Balance": 8 };
    return { "Equities": 75, "Fixed Income": 18, "Cash / Bank Balance": 7 };
  }

  // ── Global equity funds ──
  if (isGlobal) return { "Global Equities": 92, "Cash / Bank Balance": 8 };

  // ── Indian equity funds (default for Indian MF) ──
  return { "Indian Equities": 92, "Cash / Bank Balance": 8 };
};

// Human-readable label for look-through (used in tooltips / details)
const getMFLookthroughLabel = (holding) => {
  const split = getMFClassSplit(holding);
  return Object.entries(split)
    .filter(([,v]) => v >= 5)
    .sort(([,a],[,b]) => b-a)
    .map(([k,v]) => `${k} ${v}%`)
    .join(" · ");
};

const COUNTRIES = [
  // ── Asia Pacific ──
  "India","China","Japan","Hong Kong","Singapore","South Korea","Taiwan","Australia",
  "New Zealand","Indonesia","Malaysia","Thailand","Philippines","Vietnam","Bangladesh",
  "Pakistan","Sri Lanka","Myanmar","Cambodia","Mongolia",
  // ── Middle East & Africa ──
  "UAE","Saudi Arabia","Qatar","Kuwait","Bahrain","Oman","Jordan","Israel","Turkey",
  "Egypt","South Africa","Nigeria","Kenya","Ghana","Ethiopia","Morocco","Tunisia",
  "Algeria","Libya","Tanzania","Uganda","Rwanda","Ivory Coast","Senegal","Zimbabwe",
  // ── Europe ──
  "UK","Germany","France","Switzerland","Netherlands","Sweden","Norway","Denmark",
  "Finland","Italy","Spain","Portugal","Belgium","Austria","Poland","Czech Republic",
  "Hungary","Romania","Greece","Ireland","Luxembourg","Liechtenstein","Iceland",
  "Estonia","Latvia","Lithuania","Slovenia","Slovakia","Croatia","Serbia","Ukraine",
  "Russia","Bulgaria","Malta","Cyprus","Monaco","Andorra","San Marino","Vatican",
  // ── Americas ──
  "USA","Canada","Mexico","Brazil","Argentina","Chile","Colombia","Peru","Venezuela",
  "Ecuador","Bolivia","Uruguay","Paraguay","Panama","Costa Rica","Guatemala",
  "Dominican Republic","Cuba","Jamaica","Trinidad and Tobago","Barbados",
  // ── Global / Special ──
  "Global","Offshore","Cayman Islands","British Virgin Islands","Bermuda","Jersey",
  "Guernsey","Isle of Man","Mauritius","Seychelles",
];

// Country flag emoji map for typeahead display
const COUNTRY_FLAG = {
  // Asia Pacific
  "India":"🇮🇳","China":"🇨🇳","Japan":"🇯🇵","Hong Kong":"🇭🇰","Singapore":"🇸🇬",
  "South Korea":"🇰🇷","Taiwan":"🇹🇼","Australia":"🇦🇺","New Zealand":"🇳🇿","Indonesia":"🇮🇩",
  "Malaysia":"🇲🇾","Thailand":"🇹🇭","Philippines":"🇵🇭","Vietnam":"🇻🇳","Bangladesh":"🇧🇩",
  "Pakistan":"🇵🇰","Sri Lanka":"🇱🇰","Myanmar":"🇲🇲","Cambodia":"🇰🇭","Mongolia":"🇲🇳",
  // Middle East & Africa
  "UAE":"🇦🇪","Saudi Arabia":"🇸🇦","Qatar":"🇶🇦","Kuwait":"🇰🇼","Bahrain":"🇧🇭",
  "Oman":"🇴🇲","Jordan":"🇯🇴","Israel":"🇮🇱","Turkey":"🇹🇷","Egypt":"🇪🇬",
  "South Africa":"🇿🇦","Nigeria":"🇳🇬","Kenya":"🇰🇪","Ghana":"🇬🇭","Ethiopia":"🇪🇹",
  "Morocco":"🇲🇦","Tunisia":"🇹🇳","Algeria":"🇩🇿","Libya":"🇱🇾","Tanzania":"🇹🇿",
  "Uganda":"🇺🇬","Rwanda":"🇷🇼","Ivory Coast":"🇨🇮","Senegal":"🇸🇳","Zimbabwe":"🇿🇼",
  // Europe
  "UK":"🇬🇧","Germany":"🇩🇪","France":"🇫🇷","Switzerland":"🇨🇭","Netherlands":"🇳🇱",
  "Sweden":"🇸🇪","Norway":"🇳🇴","Denmark":"🇩🇰","Finland":"🇫🇮","Italy":"🇮🇹",
  "Spain":"🇪🇸","Portugal":"🇵🇹","Belgium":"🇧🇪","Austria":"🇦🇹","Poland":"🇵🇱",
  "Czech Republic":"🇨🇿","Hungary":"🇭🇺","Romania":"🇷🇴","Greece":"🇬🇷","Ireland":"🇮🇪",
  "Luxembourg":"🇱🇺","Liechtenstein":"🇱🇮","Iceland":"🇮🇸","Estonia":"🇪🇪","Latvia":"🇱🇻",
  "Lithuania":"🇱🇹","Slovenia":"🇸🇮","Slovakia":"🇸🇰","Croatia":"🇭🇷","Serbia":"🇷🇸",
  "Ukraine":"🇺🇦","Russia":"🇷🇺","Bulgaria":"🇧🇬","Malta":"🇲🇹","Cyprus":"🇨🇾",
  "Monaco":"🇲🇨","Andorra":"🇦🇩","San Marino":"🇸🇲","Vatican":"🇻🇦",
  // Americas
  "USA":"🇺🇸","Canada":"🇨🇦","Mexico":"🇲🇽","Brazil":"🇧🇷","Argentina":"🇦🇷",
  "Chile":"🇨🇱","Colombia":"🇨🇴","Peru":"🇵🇪","Venezuela":"🇻🇪","Ecuador":"🇪🇨",
  "Bolivia":"🇧🇴","Uruguay":"🇺🇾","Paraguay":"🇵🇾","Panama":"🇵🇦","Costa Rica":"🇨🇷",
  "Guatemala":"🇬🇹","Dominican Republic":"🇩🇴","Cuba":"🇨🇺","Jamaica":"🇯🇲",
  "Trinidad and Tobago":"🇹🇹","Barbados":"🇧🇧",
  // Global / Special
  "Global":"🌐","Offshore":"🏝️","Cayman Islands":"🇰🇾","British Virgin Islands":"🇻🇬",
  "Bermuda":"🇧🇲","Jersey":"🇯🇪","Guernsey":"🇬🇬","Isle of Man":"🇮🇲",
  "Mauritius":"🇲🇺","Seychelles":"🇸🇨",
};

const INITIAL_HOLDINGS = [
  { id:1,  name:"Apple Inc",               ticker:"AAPL",         class:"Equities",          exchange:"NASDAQ",  currency:"USD", qty:500,   costPrice:142.3,   currentPrice:189.4,   country:"USA",         listed:true,  isin:"US0378331005", manualPriceDate:null, capSize:"Giant", sector:"Technology" , manager:"Self-Managed" },
  { id:2,  name:"Reliance Industries",      ticker:"RELIANCE.NSE", class:"Equities",          exchange:"NSE",     currency:"INR", qty:1200,  costPrice:2450,    currentPrice:2890,    country:"India",       listed:true,  isin:"INE002A01018", manualPriceDate:null, capSize:"Giant", sector:"Energy" , manager:"Self-Managed" },
  { id:3,  name:"Blackstone RE Fund IV",    ticker:"-",            class:"Real Estate",       exchange:"Private", currency:"USD", qty:1,     costPrice:5000000, currentPrice:7200000, country:"USA",         listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Real Estate" , manager:"Blackstone" },
  { id:4,  name:"Gold ETF",                 ticker:"GLD",          class:"Commodities",       exchange:"NYSE",    currency:"USD", qty:800,   costPrice:168.2,   currentPrice:192.5,   country:"USA",         listed:true,  isin:"US78463V1070", manualPriceDate:null, capSize:"Large", sector:"Commodities" , manager:"State Street" },
  { id:5,  name:"Bitcoin",                  ticker:"BTC-USD",      class:"Crypto",            exchange:"Crypto",  currency:"USD", qty:12.5,  costPrice:28000,   currentPrice:62400,   country:"Global",      listed:true,  isin:"-",            manualPriceDate:null, capSize:"Giant", sector:"Digital Assets" , manager:"Coinbase" },
  { id:6,  name:"Nestle SA",                ticker:"NESN.SW",      class:"Equities",          exchange:"SIX",     currency:"CHF", qty:300,   costPrice:95.2,    currentPrice:102.8,   country:"Switzerland", listed:true,  isin:"CH0038863350", manualPriceDate:null, capSize:"Giant", sector:"Consumer Staples" , manager:"Self-Managed" },
  { id:7,  name:"US Treasury 4.5% 2026",    ticker:"-",            class:"Fixed Income",      exchange:"OTC",     currency:"USD", qty:100,   costPrice:98.5,    currentPrice:99.2,    country:"USA",         listed:false, isin:"US912810TG69", manualPriceDate:"2025-01-15", capSize:"N/A", sector:"Fixed Income" , manager:"Self-Managed" },
  { id:8,  name:"Dubai Commercial Property",ticker:"-",            class:"Real Estate",       exchange:"Private", currency:"AED", qty:1,     costPrice:8500000, currentPrice:11200000,country:"UAE",         listed:false, isin:"-",            manualPriceDate:"2024-11-30", capSize:"N/A", sector:"Real Estate" , manager:"Self-Managed" },
  { id:9,  name:"Picasso Artwork",          ticker:"-",            class:"Art & Collectibles",exchange:"Private", currency:"EUR", qty:1,     costPrice:2800000, currentPrice:4100000, country:"France",      listed:false, isin:"-",            manualPriceDate:"2024-09-01", capSize:"N/A", sector:"Alternatives" , manager:"Self-Managed" },
  { id:10, name:"SoftBank Vision Fund",     ticker:"-",            class:"Alternates",    exchange:"Private", currency:"USD", qty:1,     costPrice:2000000, currentPrice:2650000, country:"Japan",       listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Technology" , manager:"SoftBank" },
  { id:11, name:"HDFC Bank 3Y TD",          ticker:"-",            class:"Time Deposits",     exchange:"Bank",    currency:"INR", qty:1,     costPrice:5000000, currentPrice:5612000, country:"India",       listed:false, isin:"-",            manualPriceDate:"2025-01-01", capSize:"N/A", sector:"Financials" , manager:"HDFC AMC" },
  { id:12, name:"Emirates NBD TD 18M",      ticker:"-",            class:"Time Deposits",     exchange:"Bank",    currency:"AED", qty:1,     costPrice:1000000, currentPrice:1058000, country:"UAE",         listed:false, isin:"-",            manualPriceDate:"2025-01-01", capSize:"N/A", sector:"Financials" , manager:"Self-Managed" },
  { id:13, name:"Citadel Wellington Fund",  ticker:"-",            class:"Alternates",       exchange:"Private", currency:"USD", qty:1,     costPrice:3000000, currentPrice:3840000, country:"USA",         listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Alternatives" , manager:"Citadel" },
  { id:14, name:"Millennium Global Macro",  ticker:"-",            class:"Alternates",       exchange:"Private", currency:"USD", qty:1,     costPrice:2500000, currentPrice:2925000, country:"UK",          listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Alternatives" , manager:"Millennium" },
  { id:15, name:"Marshall Wace TOPS Fund",  ticker:"-",            class:"Alternates",  exchange:"Private", currency:"GBP", qty:1,     costPrice:1800000, currentPrice:2124000, country:"UK",          listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Alternatives" , manager:"Marshall Wace" },
  { id:16, name:"Alkeon Long Short Tech",   ticker:"-",            class:"Alternates",  exchange:"Private", currency:"USD", qty:1,     costPrice:1500000, currentPrice:1845000, country:"USA",         listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Technology" , manager:"Alkeon Capital" },
  { id:17, name:"HDFC FCN on Nifty 50",     ticker:"-",            class:"Alternates",              exchange:"Private", currency:"INR", qty:1,     costPrice:10000000,currentPrice:11200000,country:"India",       listed:false, isin:"-",            manualPriceDate:"2025-01-15", capSize:"N/A", sector:"Financials" , manager:"HDFC AMC" },
  { id:18, name:"UBS FCN on MSCI EM",       ticker:"-",            class:"Alternates",              exchange:"Private", currency:"USD", qty:1,     costPrice:500000,  currentPrice:548000,  country:"Switzerland", listed:false, isin:"-",            manualPriceDate:"2025-01-15", capSize:"N/A", sector:"Financials" , manager:"UBS" },
  { id:19, name:"PPF Account",              ticker:"-",            class:"Fixed Income",      exchange:"Govt",    currency:"INR", qty:1,     costPrice:1500000, currentPrice:2180000, country:"India",       listed:false, isin:"-",            manualPriceDate:"2025-03-31", capSize:"N/A", sector:"Fixed Income" , manager:"Self-Managed" },
  { id:20, name:"Classic Car Collection",   ticker:"-",            class:"Miscellaneous",     exchange:"Private", currency:"GBP", qty:3,     costPrice:280000,  currentPrice:390000,  country:"UK",          listed:false, isin:"-",            manualPriceDate:"2024-10-01", capSize:"N/A", sector:"Alternatives" , manager:"Self-Managed" },
  { id:21, name:"Temasek Private Credit",   ticker:"-",            class:"Alternates",    exchange:"Private", currency:"SGD", qty:1,     costPrice:2000000, currentPrice:2340000, country:"Singapore",   listed:false, isin:"-",            manualPriceDate:"2024-12-31", capSize:"N/A", sector:"Financials" , manager:"Temasek" },
  { id:22, name:"LVMH SE",                  ticker:"MC.PA",        class:"Equities",          exchange:"Euronext",currency:"EUR", qty:120,   costPrice:720,     currentPrice:798,     country:"France",      listed:true,  isin:"FR0000121014", manualPriceDate:null, capSize:"Giant", sector:"Consumer Discretionary" , manager:"Self-Managed" },
  { id:23, name:"Tata Consultancy Services",ticker:"TCS.NSE",      class:"Equities",          exchange:"NSE",     currency:"INR", qty:500,   costPrice:3200,    currentPrice:3890,    country:"India",       listed:true,  isin:"INE467B01029", manualPriceDate:null, capSize:"Large", sector:"Technology" , manager:"Self-Managed" },
  { id:24, name:"Singapore Savings Bond",   ticker:"-",            class:"Fixed Income",      exchange:"MAS",     currency:"SGD", qty:1,     costPrice:100000,  currentPrice:103500,  country:"Singapore",   listed:false, isin:"-",            manualPriceDate:"2025-01-01", capSize:"N/A", sector:"Fixed Income" , manager:"Self-Managed" },
  { id:25, name:"HDFC Mid-Cap Fund",        ticker:"-",            class:"Indian Mutual Funds",exchange:"AMFI",   currency:"INR", qty:10000, costPrice:38.5,    currentPrice:54.2,    country:"India",       listed:false, isin:"INF179K01VU2", manualPriceDate:null, schemeCode:"118989", isMutualFund:true, capSize:"Mid", sector:"Diversified" , manager:"HDFC AMC" },
];

const INITIAL_TRANSACTIONS = [
  { id:1, date:"2021-03-15", holding:"Apple Inc",        type:"BUY",  qty:300,  price:122.5,  currency:"USD", fees:45 },
  { id:2, date:"2022-01-10", holding:"Apple Inc",        type:"BUY",  qty:200,  price:174.9,  currency:"USD", fees:30 },
  { id:3, date:"2021-06-20", holding:"Reliance Industries",type:"BUY",qty:1200,price:2450,   currency:"INR", fees:500},
  { id:4, date:"2020-11-05", holding:"Gold ETF",         type:"BUY",  qty:800,  price:168.2,  currency:"USD", fees:20 },
  { id:5, date:"2021-02-14", holding:"Bitcoin",          type:"BUY",  qty:10,   price:47000,  currency:"USD", fees:200},
  { id:6, date:"2023-01-09", holding:"Bitcoin",          type:"BUY",  qty:2.5,  price:17200,  currency:"USD", fees:80 },
];

const MODEL_DEFAULT = [
  // Broad Equities: split Global (via ETFs/stocks) and India-listed
  { assetClass:"Equities",           targetPct:20, targetYield:2.1  },  // Global equities / US / DM
  { assetClass:"Indian Equities",    targetPct:12, targetYield:1.8  },  // BSE/NSE listed + Indian MFs
  { assetClass:"Mutual Funds",       targetPct:10, targetYield:11.0 },  // MF look-through applied
  { assetClass:"Fixed Income",       targetPct:12, targetYield:4.8  },  // Bonds, NCDs, Debt MFs
  { assetClass:"Real Estate",        targetPct:8,  targetYield:3.8  },  // REITs, direct property
  { assetClass:"Alternates",         targetPct:8,  targetYield:10.0 },  // PE, Hedge, FCNs
  { assetClass:"Market Neutral",     targetPct:4,  targetYield:6.5  },  // L/S, Arbitrage funds
  { assetClass:"Commodities",        targetPct:4,  targetYield:0.0  },  // Gold, Silver, Oil ETFs
  { assetClass:"Crypto",             targetPct:2,  targetYield:0.0  },  // Bitcoin, Ethereum
  { assetClass:"Cash / Bank Balance",targetPct:3,  targetYield:5.2  },  // Savings, current accounts
  { assetClass:"Art & Collectibles", targetPct:3,  targetYield:0.0  },  // Art, wine, collectibles
  { assetClass:"Time Deposits",      targetPct:7,  targetYield:6.5  },  // FDs, structured deposits
  { assetClass:"Miscellaneous",      targetPct:2,  targetYield:0.0  },  // Unlisted, other
];  // Total: 95% (leave 5% unallocated as buffer — advisors should customise)

const MODEL_CURRENCY_DEFAULT = [
  { currency:"USD", targetPct:35, note:"Core reserve / global anchor" },
  { currency:"INR", targetPct:25, note:"India domestic exposure" },
  { currency:"EUR", targetPct:12, note:"Europe / multi-currency bonds" },
  { currency:"GBP", targetPct:8,  note:"UK equities & gilts" },
  { currency:"AED", targetPct:7,  note:"GCC real estate & deposits" },
  { currency:"SGD", targetPct:6,  note:"Asia-Pacific hub" },
  { currency:"JPY", targetPct:4,  note:"Safe-haven / Japan equity" },
  { currency:"CHF", targetPct:3,  note:"Wealth preservation" },
];
const INITIAL_FAMILIES = [
  { id:"fam1", name:"Sharma Family",    color:"#2563EB", emoji:"👨‍👩‍👧‍👦" },
  { id:"fam2", name:"Mehta Family",     color:"#7C3AED", emoji:"🏠" },
  { id:"fam3", name:"Al-Rashid Family", color:"#D97706", emoji:"🌟" },
];

const INITIAL_CLIENTS = [
  { id:"c1", familyId:"fam1", name:"Rajesh Sharma",   role:"Primary", avatar:"RS", dob:"1968-04-12" },
  { id:"c2", familyId:"fam1", name:"Priya Sharma",    role:"Spouse",  avatar:"PS", dob:"1972-08-22" },
  { id:"c3", familyId:"fam1", name:"Aarav Sharma",    role:"Child",   avatar:"AS", dob:"1998-11-05" },
  { id:"c4", familyId:"fam2", name:"Vikram Mehta",    role:"Primary", avatar:"VM", dob:"1955-01-30" },
  { id:"c5", familyId:"fam2", name:"Sunita Mehta",    role:"Spouse",  avatar:"SM", dob:"1960-06-14" },
  { id:"c6", familyId:"fam2", name:"Meera Mehta",     role:"Child",   avatar:"MM", dob:"1990-03-19" },
  { id:"c7", familyId:"fam2", name:"Mehta Holdings",  role:"Entity",  avatar:"MH", dob:null },
  { id:"c8", familyId:"fam3", name:"Omar Al-Rashid",  role:"Primary", avatar:"OA", dob:"1975-09-08" },
  { id:"c9", familyId:"fam3", name:"Layla Al-Rashid", role:"Spouse",  avatar:"LA", dob:"1980-02-17" },
];

// Maps holding id → client id (which entity owns this holding)
const CLIENT_HOLDING_MAP = {
  1:"c1", 2:"c1", 4:"c1", 5:"c1",
  22:"c2", 6:"c2",
  23:"c3",
  3:"c4", 10:"c4", 13:"c4",
  11:"c5", 19:"c5", 17:"c5",
  7:"c6", 24:"c6",
  14:"c7", 15:"c7", 16:"c7", 21:"c7",
  8:"c8", 12:"c8", 18:"c8",
  9:"c9", 20:"c9", 25:"c9",
};

const ROLE_COLORS = { Primary:"#2563EB", Spouse:"#9333EA", Child:"#1D9E75", Entity:"#D97706" };

// ── Market Cap Categories ──
const CAP_SIZES = ["Giant","Mega","Large","Mid","Small","Micro","N/A"];
const CAP_COLORS = { Giant:"#D97706", Mega:"#2563EB", Large:"#1D9E75", Mid:"#9333EA", Small:"#A32D2D", Micro:"#0EA5E9", "N/A":"#A8A29E" };

// ── Sector colours ──
const SECTOR_COLORS = {
  "Technology":               "#2563EB",
  "Financials":               "#D97706",
  "Energy":                   "#A32D2D",
  "Consumer Staples":         "#1D9E75",
  "Consumer Discretionary":   "#EA580C",
  "Real Estate":              "#7C3AED",
  "Fixed Income":             "#0EA5E9",
  "Commodities":              "#B45309",
  "Digital Assets":           "#EC4899",
  "Alternatives":             "#4F46E5",
  "Alternates":               "#4F46E5",
  "Market Neutral":           "#0891B2",
  "Diversified":              "#78716C",
  "Other":                    "#A8A29E",
  "Healthcare":               "#10B981",
  "Industrials":              "#F59E0B",
  "Materials":                "#8B5CF6",
  "Utilities":                "#06B6D4",
  "Cash":                     "#64748B",
  "Communication Services":   "#0891B2",
  "Alternates":               "#4F46E5",
  "Indian Equities":          "#F472B6",
  "Equities":                 "#94A3B8",
};

// ── Asset-class colour map (shared across all donuts/charts) ──
const CLASS_COLORS = {
  // Equity shades (blue family)
  "Equities":            "#2563EB",   // Global equities — strong blue
  "Indian Equities":     "#3B82F6",   // India stocks — lighter blue
  "Global Equities":     "#6366F1",   // Global MF look-through — indigo
  // Debt (green family)
  "Fixed Income":        "#1D9E75",   // Bonds / NCDs
  "Time Deposits":       "#0EA5E9",   // FDs / structured deposits — sky blue
  // Collective vehicles
  "Mutual Funds":        "#65A30D",   // MF (look-through applied)
  "Indian Mutual Funds": "#65A30D",
  "Global Mutual Funds": "#84CC16",
  "Mixed (Hybrid)":      "#F59E0B",
  // Real assets
  "Real Estate":         "#D97706",   // Property / REITs
  "Commodities":         "#EA580C",   // Gold / oil
  "Art & Collectibles":  "#EC4899",   // Alternative real
  // Alternatives
  "Alternates":          "#4F46E5",   // PE / Hedge / FCNs
  "Market Neutral":      "#0891B2",   // L/S / arbitrage
  "Crypto":              "#7C3AED",   // Digital assets
  // Cash
  "Cash / Bank Balance": "#78716C",
  "Cash":                "#78716C",
  // Catch-all
  "Miscellaneous":       "#94A3B8",
  "Other":               "#94A3B8",
};

// ── Merge equity sub-classes for display in allocation charts ──
// Combines "Indian Equities" + "Equities" → "Equities" for broad view
// Returns merged array plus a breakdown map for tooltips
const mergeForDisplay = (data) => {
  const EQUITY_CLASSES = ["Equities","Indian Equities","Global Equities"];
  let equityTotal = 0;
  const equityBreakdown = [];
  const rest = [];
  data.forEach(d => {
    if (EQUITY_CLASSES.includes(d.label)) {
      equityTotal += d.value||0;
      if ((d.value||0) > 0) equityBreakdown.push(d);
    } else {
      rest.push(d);
    }
  });
  if (equityTotal === 0) return { merged: data, breakdown: {} };
  const merged = [{ label:"Equities", value: equityTotal, breakdown: equityBreakdown }, ...rest]
    .sort((a,b) => (b.value||0) - (a.value||0));
  return { merged, breakdown: { "Equities": equityBreakdown } };
};

// ─────────────── MUTUAL FUND LOOK-THROUGH DATA ───────────────
// Approximate sector & cap-size allocations for common Indian MF categories.
// Keyed by fund category patterns matched against fund name (lowercase).
// Sources: SEBI monthly portfolios, Value Research fund factsheets (2024-25).
// These are representative averages; actual allocations shift monthly.
const MF_SECTOR_LOOKUP = [
  // pattern → { sectors: {sectorName: pct}, caps: {capName: pct} }
  { match: /flexi.cap|multi.cap|diversified/i,
    sectors: { Financials:28, Technology:18, "Consumer Discretionary":12, Energy:8, Healthcare:7, "Consumer Staples":7, Industrials:6, Materials:5, Utilities:3, Cash:6 },
    caps:    { Giant:35, Large:30, Mid:20, Small:10, Cash:5 } },
  { match: /large.cap|bluechip|top.100|nifty.50|sensex/i,
    sectors: { Financials:32, Technology:16, Energy:10, "Consumer Staples":8, "Consumer Discretionary":8, Industrials:7, Healthcare:6, Materials:5, Utilities:4, Cash:4 },
    caps:    { Giant:60, Large:35, Mid:3, Cash:2 } },
  { match: /mid.cap/i,
    sectors: { Financials:18, Industrials:16, Technology:14, "Consumer Discretionary":13, Healthcare:10, Materials:9, "Consumer Staples":7, Energy:5, Utilities:4, Cash:4 },
    caps:    { Mid:65, Large:18, Small:12, Cash:5 } },
  { match: /small.cap/i,
    sectors: { Industrials:20, Technology:15, "Consumer Discretionary":14, Financials:12, Healthcare:11, Materials:10, "Consumer Staples":6, Energy:5, Utilities:4, Cash:3 },
    caps:    { Small:65, Mid:20, Micro:10, Cash:5 } },
  { match: /contra|value/i,
    sectors: { Financials:24, Energy:14, Industrials:12, Technology:10, "Consumer Staples":10, Materials:8, Healthcare:7, "Consumer Discretionary":7, Utilities:4, Cash:4 },
    caps:    { Giant:30, Large:35, Mid:22, Small:9, Cash:4 } },
  { match: /elss|tax.sav/i,
    sectors: { Financials:26, Technology:18, "Consumer Discretionary":12, Energy:8, Healthcare:8, "Consumer Staples":7, Industrials:7, Materials:6, Utilities:4, Cash:4 },
    caps:    { Giant:40, Large:30, Mid:18, Small:8, Cash:4 } },
  { match: /hybrid|balanced.advantage|baf|dynamic.asset/i,
    sectors: { Financials:22, Technology:14, "Consumer Staples":8, Energy:7, Healthcare:7, "Consumer Discretionary":8, Industrials:6, "Fixed Income":20, Cash:8 },
    caps:    { Giant:30, Large:25, Mid:15, "Fixed Income":22, Cash:8 } },
  { match: /equity.hybrid|aggressive.hybrid/i,
    sectors: { Financials:24, Technology:15, "Consumer Discretionary":10, "Consumer Staples":8, Energy:7, Healthcare:7, Industrials:6, "Fixed Income":15, Cash:8 },
    caps:    { Giant:35, Large:28, Mid:14, Small:5, "Fixed Income":12, Cash:6 } },
  { match: /debt|liquid|overnight|money.market|ultra.short|short.term|corporate.bond|gilt/i,
    sectors: { "Fixed Income":88, Cash:12 },
    caps:    { "Fixed Income":88, Cash:12 } },
  { match: /international|global|us.equity|nasdaq|s.p.500|world/i,
    sectors: { Technology:28, Financials:14, Healthcare:12, "Consumer Discretionary":10, Industrials:9, "Consumer Staples":7, Energy:5, Materials:5, Utilities:5, Cash:5 },
    caps:    { Giant:50, Large:30, Mid:12, Small:4, Cash:4 } },
  { match: /infrastructure|infra/i,
    sectors: { Industrials:30, Energy:20, Utilities:18, Materials:12, Technology:8, Financials:7, Cash:5 },
    caps:    { Giant:20, Large:35, Mid:30, Small:10, Cash:5 } },
  { match: /healthcare|pharma|hospital/i,
    sectors: { Healthcare:80, Technology:8, "Consumer Staples":5, Materials:4, Cash:3 },
    caps:    { Giant:20, Large:40, Mid:28, Small:9, Cash:3 } },
  { match: /banking|financial|psu.bank/i,
    sectors: { Financials:90, Technology:4, "Consumer Discretionary":2, Cash:4 },
    caps:    { Giant:45, Large:35, Mid:12, Small:4, Cash:4 } },
  { match: /fmcg|consumer/i,
    sectors: { "Consumer Staples":55, "Consumer Discretionary":30, Materials:6, Industrials:4, Cash:5 },
    caps:    { Giant:45, Large:35, Mid:14, Small:2, Cash:4 } },
  { match: /technology|it.fund|digital/i,
    sectors: { Technology:85, "Consumer Discretionary":5, Industrials:4, Financials:3, Cash:3 },
    caps:    { Giant:40, Large:35, Mid:16, Small:6, Cash:3 } },
];

// Get look-through sector/cap allocation for a single MF holding
// Returns { sectors: {name: weightedValue}, caps: {name: weightedValue} }
// where values are proportional to the holding's valueUSD
function getMFLookthrough(holding) {
  if (!holding.isMutualFund && holding.class !== "Indian Mutual Funds" && holding.class !== "Global Mutual Funds" && holding.class !== "Mutual Funds") return null;
  const name = (holding.name || "").toLowerCase();
  let template = MF_SECTOR_LOOKUP.find(t => t.match.test(name));
  // Default: diversified/flexi-cap if no match
  if (!template) template = MF_SECTOR_LOOKUP[0];
  const val = holding.valueUSD || 0;
  const sectors = {}, caps = {};
  Object.entries(template.sectors).forEach(([s, pct]) => { sectors[s] = (pct / 100) * val; });
  Object.entries(template.caps).forEach(([c, pct])    => { caps[c]    = (pct / 100) * val; });
  return { sectors, caps, templateName: name };
}

// Cap size lookup by ticker/name (for demo; real impl would use market-cap API)
// Collapse slices below threshold% into "Others" for cleaner donut charts
function collapseSmallSlices(data, thresholdPct = 2) {
  if (!data || !data.length) return data;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return data;
  const big = data.filter(d => (d.value / total * 100) >= thresholdPct);
  const small = data.filter(d => (d.value / total * 100) < thresholdPct);
  if (!small.length) return data;
  const othersValue = small.reduce((s, d) => s + d.value, 0);
  return [...big, { label: "Others", value: othersValue, _collapsed: small.map(d => d.label) }];
}

const CURRENCY_COLORS = {
  "INR":"#3B82F6","USD":"#F59E0B","SGD":"#22C55E","EUR":"#8B5CF6",
  "GBP":"#06B6D4","HKD":"#A855F7","JPY":"#A32D2D","AUD":"#F97316",
  "CHF":"#14B8A6","CAD":"#F43F5E","Others":"#94A3B8",
};

const MANAGER_COLORS = {
  "Self-Managed":   "#007878",
  "Blackstone":     "#1E40AF",
  "Citadel":        "#7C3AED",
  "Millennium":     "#D97706",
  "Marshall Wace":  "#059669",
  "Alkeon Capital": "#A32D2D",
  "Temasek":        "#0EA5E9",
  "UBS":            "#9333EA",
  "HDFC AMC":       "#EA580C",
  "SoftBank":       "#1D9E75",
  "Coinbase":       "#F59E0B",
  "State Street":   "#64748B",
};

// ── AMC / Fund House extractor ─────────────────────────────────────────────
// Derives the AMC name from a holding. Priority:
//   1. Match against POPULAR_MF_SCHEMES by schemeCode
//   2. Match fund name prefix against known AMC name patterns
//   3. Fall back to holding.manager if it looks like an AMC
const AMC_PREFIX_MAP = {
  "hdfc":         "HDFC MF",
  "sbi":          "SBI MF",
  "icici pru":    "ICICI Pru MF",
  "icici prudential": "ICICI Pru MF",
  "kotak":        "Kotak MF",
  "axis":         "Axis MF",
  "nippon":       "Nippon MF",
  "mirae":        "Mirae Asset",
  "dsp":          "DSP MF",
  "aditya birla": "Aditya Birla MF",
  "absl":         "Aditya Birla MF",
  "uti":          "UTI MF",
  "tata":         "Tata MF",
  "franklin":     "Franklin Templeton",
  "templeton":    "Franklin Templeton",
  "parag parikh": "PPFAS MF",
  "ppfas":        "PPFAS MF",
  "motilal":      "Motilal Oswal MF",
  "invesco":      "Invesco MF",
  "quant":        "Quant MF",
  "edelweiss":    "Edelweiss MF",
  "navi":         "Navi MF",
  "pgim":         "PGIM MF",
  "bandhan":      "Bandhan MF",
  "baroda":       "Baroda BNP Paribas MF",
  "bnp":          "Baroda BNP Paribas MF",
  "canara":       "Canara Robeco MF",
  "whiteoak":     "WhiteOak MF",
  "white oak":    "WhiteOak MF",
  "samco":        "Samco MF",
  "jm":           "JM MF",
  "mahindra":     "Mahindra MF",
  "sundaram":     "Sundaram MF",
  "l&t":          "L&T MF",
  "union":        "Union MF",
  "360 one":      "360 ONE MF",
  "vanguard":     "Vanguard",
  "blackrock":    "BlackRock",
  "ishares":      "BlackRock",
  "fidelity":     "Fidelity",
  "jp morgan":    "JPMorgan",
  "jpmorgan":     "JPMorgan",
  "schroders":    "Schroders",
  "pimco":        "PIMCO",
  "fssa":         "FSSA",
  "first sentier":"FSSA",
  "jupiter":      "Jupiter",
  "robeco":       "Robeco",
  "franklin india":"Franklin Templeton",
};

const AMC_COLORS = {
  "HDFC MF":          "#EA580C",
  "SBI MF":           "#2563EB",
  "ICICI Pru MF":     "#7C3AED",
  "Kotak MF":         "#A32D2D",
  "Axis MF":          "#9333EA",
  "Nippon MF":        "#D97706",
  "Mirae Asset":      "#059669",
  "DSP MF":           "#0891B2",
  "Aditya Birla MF":  "#EC4899",
  "UTI MF":           "#1D9E75",
  "Tata MF":          "#F97316",
  "Franklin Templeton":"#6366F1",
  "PPFAS MF":         "#14B8A6",
  "Motilal Oswal MF": "#8B5CF6",
  "Invesco MF":       "#06B6D4",
  "Quant MF":         "#EF4444",
  "Edelweiss MF":     "#F59E0B",
  "Bandhan MF":       "#10B981",
  "Canara Robeco MF": "#3B82F6",
  "WhiteOak MF":      "#A855F7",
  "Baroda BNP Paribas MF": "#84CC16",
  "Vanguard":         "#1E40AF",
  "BlackRock":        "#1F2937",
  "Fidelity":         "#059669",
  "JPMorgan":         "#1D4ED8",
  "Schroders":        "#BE185D",
  "PIMCO":            "#0369A1",
  "FSSA":             "#0D9488",
  "Jupiter":          "#7C3AED",
  "Robeco":           "#A32D2D",
};

function extractAMC(holding) {
  // 1. Match by schemeCode in POPULAR_MF_SCHEMES
  if (holding.schemeCode) {
    const code = parseInt(holding.schemeCode);
    const match = POPULAR_MF_SCHEMES.find(s => s.amfiCode === code);
    if (match?.house) {
      // Map house to full AMC name
      const lh = match.house.toLowerCase();
      for (const [prefix, amc] of Object.entries(AMC_PREFIX_MAP)) {
        if (lh.startsWith(prefix) || lh.includes(prefix)) return amc;
      }
      return match.house;
    }
  }
  // 2. Match by fund name prefix
  const nameLower = (holding.name || "").toLowerCase();
  for (const [prefix, amc] of Object.entries(AMC_PREFIX_MAP)) {
    if (nameLower.startsWith(prefix) || nameLower.includes(prefix + " ")) return amc;
  }
  // 3. Fall back to manager if it's an AMC-sounding name
  const mgr = holding.manager || "";
  if (mgr && mgr !== "Self-Managed" && (mgr.includes("AMC") || mgr.includes("MF") ||
      mgr.includes("Asset") || mgr.includes("Fund"))) return mgr;
  return "Other";
}

const GOAL_TYPES = [
  { id:"retirement", label:"Retirement Corpus", icon:"🏖️", color:"#7C3AED" },
  { id:"education",  label:"Child Education",   icon:"🎓", color:"#2563EB" },
  { id:"home",       label:"Home Purchase",     icon:"🏠", color:"#059669" },
  { id:"wealth",     label:"Wealth Target",     icon:"💎", color:"#D97706" },
  { id:"emergency",  label:"Emergency Fund",    icon:"🛡️", color:"#A32D2D" },
  { id:"custom",     label:"Custom Goal",       icon:"⭐", color:"#0891B2" },
];



const TICKER_CAP = {
  // ── US Mega/Giant (>$200B) ──
  AAPL:"Giant", MSFT:"Giant", NVDA:"Giant", GOOGL:"Giant", GOOG:"Giant",
  AMZN:"Giant", META:"Giant", TSLA:"Giant", "BRK.B":"Giant", "BRK.A":"Giant",
  JPM:"Giant", V:"Giant", UNH:"Giant", XOM:"Giant", JNJ:"Giant",
  PG:"Giant", MA:"Giant", HD:"Giant", CVX:"Giant", LLY:"Giant",
  AVGO:"Giant", MRK:"Giant", PEP:"Giant", KO:"Giant", ABBV:"Giant",
  COST:"Giant", WMT:"Giant", MCD:"Giant", CRM:"Giant", BAC:"Giant",
  NFLX:"Giant", ADBE:"Giant", GS:"Giant", MS:"Giant", C:"Giant",
  ORCL:"Giant", T:"Giant", VZ:"Giant", CMCSA:"Giant", DIS:"Giant",
  // ── US Large ($10B–$200B) ──
  AMD:"Large", INTC:"Large", QCOM:"Large", AMAT:"Large", LRCX:"Large",
  KLAC:"Large", MU:"Large", WDC:"Large", STX:"Large",
  PANW:"Large", CRWD:"Large", ZS:"Large", OKTA:"Large", NET:"Large",
  DDOG:"Large", SNOW:"Large", SHOP:"Large", UBER:"Large", LYFT:"Large",
  COIN:"Large", BKNG:"Large", MAR:"Large", HLT:"Large",
  GM:"Large", F:"Large", ABNB:"Large", EXPE:"Large",
  HON:"Large", CAT:"Large", DE:"Large", GE:"Large", LMT:"Large",
  RTX:"Large", NOC:"Large", MMM:"Large", EMR:"Large", ETN:"Large",
  NEE:"Large", DUK:"Large", SO:"Large", AMT:"Large", PLD:"Large",
  TMO:"Large", ABT:"Large", BMY:"Large", AMGN:"Large", GILD:"Large",
  BIIB:"Large", REGN:"Large", VRTX:"Large", ISRG:"Large", SYK:"Large",
  ZBH:"Large", BSX:"Large", MDT:"Large", EW:"Large",
  NUE:"Large", FCX:"Large", LIN:"Large", APD:"Large",
  USB:"Large", PNC:"Large", TFC:"Large", AXP:"Large", BLK:"Large",
  SCHW:"Large", COF:"Large", DFS:"Large", SYF:"Large",
  NVO:"Large", AZN:"Large", GSK:"Large", SNY:"Large",
  PLTR:"Large", RBRK:"Large", RDDT:"Large", CRCL:"Large",
  GLD:"Large", IAU:"Large", SLV:"Large", REMX:"Large",
  // ── US Mid ($2B–$10B) ──
  SNAP:"Mid", LYFT:"Mid", RIVN:"Mid", NIO:"Mid",
  // ── US Micro/Small ──
  BITO:"Small", IBIT:"Large",
  // ── ETFs — treated as weighted (will use ETF_CAP_LOOKTHROUGH for breakdown) ──
  SPY:"Giant", IVV:"Giant", VOO:"Giant", VTI:"Giant", QQQ:"Giant",
  XLK:"Giant", XLF:"Giant", XLV:"Giant", XLE:"Giant", XLY:"Giant",
  XLP:"Giant", XLI:"Giant", XLB:"Giant", XLU:"Giant", XLRE:"Giant",
  VGT:"Giant", VFH:"Giant", SMH:"Large", SOXX:"Large", IGV:"Large",
  EEM:"Large", VWO:"Large", IEMG:"Large", ACWI:"Giant", VT:"Giant",
  CQQQ:"Large", CHIQ:"Mid",
  // ── Indian Giants (Nifty 50 core) ──
  RELIANCE:"Giant", "RELIANCE.NSE":"Giant",
  TCS:"Giant", "TCS.NSE":"Giant",
  HDFCBANK:"Giant", "HDFCBANK.NSE":"Giant",
  ICICIBANK:"Giant", "ICICIBANK.NSE":"Giant",
  INFY:"Giant", "INFY.NSE":"Giant",
  HINDUNILVR:"Giant", "HINDUNILVR.NSE":"Giant",
  ITC:"Giant", "ITC.NSE":"Giant",
  SBIN:"Giant", "SBIN.NSE":"Giant",
  BAJFINANCE:"Giant", "BAJFINANCE.NSE":"Giant",
  KOTAKBANK:"Giant", "KOTAKBANK.NSE":"Giant",
  LT:"Giant", "LT.NSE":"Giant",
  MARUTI:"Giant", "MARUTI.NSE":"Giant",
  AXISBANK:"Large", "AXISBANK.NSE":"Large",
  TITAN:"Large", "TITAN.NSE":"Large",
  WIPRO:"Large", "WIPRO.NSE":"Large",
  HCLTECH:"Large", "HCLTECH.NSE":"Large",
  TECHM:"Large", "TECHM.NSE":"Large",
  SUNPHARMA:"Giant", "SUNPHARMA.NSE":"Giant",
  DRREDDY:"Large", "DRREDDY.NSE":"Large",
  CIPLA:"Large", "CIPLA.NSE":"Large",
  TATAMOTORS:"Giant", "TATAMOTORS.NSE":"Giant",
  TATASTEEL:"Large", "TATASTEEL.NSE":"Large",
  HINDALCO:"Large", "HINDALCO.NSE":"Large",
  ADANIPORTS:"Large", "ADANIPORTS.NSE":"Large",
  ADANIENT:"Large", "ADANIENT.NSE":"Large",
  NTPC:"Large", "NTPC.NSE":"Large",
  POWERGRID:"Large", "POWERGRID.NSE":"Large",
  COALINDIA:"Large", "COALINDIA.NSE":"Large",
  ONGC:"Giant", "ONGC.NSE":"Giant",
  APOLLOHOSP:"Large", "APOLLOHOSP.NSE":"Large",
  DIVISLAB:"Large", "DIVISLAB.NSE":"Large",
  BAJAJFINSV:"Giant", "BAJAJFINSV.NSE":"Giant",
  TRENT:"Large", "TRENT.NSE":"Large",
  // Indian Mid/Small
  PERSISTENT:"Mid", COFORGE:"Mid", KPIT:"Mid",
  MPHASIS:"Mid", LTTS:"Mid", LTIM:"Mid",
  IRFC:"Mid", CONCOR:"Mid", BEL:"Mid",
  // Indian ETFs
  NIFTYBEES:"Giant", JUNIORBEES:"Large", MON100:"Giant", N100:"Giant",
  CPSEETF:"Large", MAFANG:"Giant",
  // ── Singapore ──
  D05:"Giant", "D05.SI":"Giant",     // DBS Group
  OCBC:"Giant", "O39.SI":"Giant",    // OCBC
  UOB:"Giant", "U11.SI":"Giant",     // UOB
  "C6L.SI":"Large",                  // SIA
  "Z74.SI":"Large",                  // Singtel
  GRAB:"Large",                      // Grab Holdings
  // ── European Giants ──
  // Germany
  "RHM.DE":"Giant", "BMW.DE":"Giant", "SAP.DE":"Giant", "SIE.DE":"Giant",
  "BAYN.DE":"Large", "ALV.DE":"Giant", "DTE.DE":"Large", "MBG.DE":"Giant",
  "ADS.DE":"Large", "BAS.DE":"Giant", "MRK.DE":"Giant",
  // Switzerland
  "NESN.SW":"Giant", "ROG.SW":"Giant", "NOVN.SW":"Giant", "ZURN.SW":"Large",
  "ABBN.SW":"Large", "SLHN.SW":"Large",
  // France
  "MC.PA":"Giant", "OR.PA":"Giant", "TTE.PA":"Giant", "BNP.PA":"Giant",
  "SAN.PA":"Large", "ACA.PA":"Large",
  // UK
  "HSBA.L":"Giant", "BP.L":"Giant", "SHEL.L":"Giant", "AZN.L":"Giant",
  "GSK.L":"Large", "ULVR.L":"Giant", "RIO.L":"Giant", "BARC.L":"Large",
  // Netherlands
  "ASML.AS":"Giant", "INGA.AS":"Large", "PHIA.AS":"Large",
  // Vienna (Saxo often routes here)
  "CITI.VI":"Giant", "VIE.VI":"Large",
  // Warsaw
  "NVO.WA":"Giant",                  // Novo Nordisk listed on Warsaw
  // ── Asia ──
  "9984.T":"Giant", "7203.T":"Giant", "6758.T":"Giant", "6861.T":"Large",
  "005930.KS":"Giant", "000660.KS":"Large",
  "700.HK":"Giant", "9988.HK":"Giant", "1299.HK":"Giant",
  BABA:"Giant", JD:"Large", PDD:"Large",
  // ── Crypto ──
  "BTC-USD":"Giant", "ETH-USD":"Giant",
  BTC:"Giant", ETH:"Giant", COIN:"Large",
  // ── Additional exchange-suffixed variants (Saxo/IBKR often use these) ──
  // US stocks with exchange suffix
  "LLY.US":"Giant", "C.US":"Giant", "GOOG.US":"Giant", "GOOGL.US":"Giant",
  "INTC.US":"Large", "AMAT.US":"Large", "SYK.US":"Large",
  "RDDT.US":"Large", "RBRK.US":"Large", "CRCL.US":"Large",
  "REMX.US":"Large", "COIN.US":"Large",
  // Variants without dot
  CITI:"Giant",                          // CITI.VI stripped
  // More recent IPO / growth stocks
  ARM:"Giant", TSM:"Giant",              // ARM Holdings, TSMC
  ASML:"Giant",                          // ASML (US ADR)
  "NVO.WA":"Giant", NVO:"Giant",         // Novo Nordisk (Warsaw + US ADR)
  // Additional ETFs for cap chart
  GLD:"Large", IAU:"Large", SLV:"Mid",
  IBIT:"Large", BITO:"Small",
  EWJ:"Large", EWG:"Large", EWU:"Large",
  VNQ:"Large", IYR:"Large",
};

// ── ETF Cap Look-through: proportionate breakdown for ETF holdings ──
// When a stock ETF is held, its value is spread across cap buckets by the ETF's composition
const ETF_CAP_LOOKTHROUGH = {
  // Broad US market — heavily Giant-weighted
  SPY:  { Giant:65, Large:25, Mid:9,  Small:1 },
  IVV:  { Giant:65, Large:25, Mid:9,  Small:1 },
  VOO:  { Giant:65, Large:25, Mid:9,  Small:1 },
  VTI:  { Giant:55, Large:25, Mid:14, Small:6 },
  // Tech-focused — near 100% Giant/Large
  QQQ:  { Giant:72, Large:22, Mid:5,  Small:1 },
  XLK:  { Giant:80, Large:18, Mid:2  },
  VGT:  { Giant:75, Large:20, Mid:5  },
  SMH:  { Giant:65, Large:30, Mid:5  },
  SOXX: { Giant:55, Large:38, Mid:7  },
  IGV:  { Giant:60, Large:30, Mid:10 },
  CQQQ: { Giant:50, Large:35, Mid:15 },  // China tech
  // Sector ETFs
  XLF:  { Giant:60, Large:35, Mid:5  },
  XLV:  { Giant:60, Large:32, Mid:8  },
  XLE:  { Giant:65, Large:28, Mid:7  },
  XLY:  { Giant:68, Large:25, Mid:7  },
  XLP:  { Giant:70, Large:25, Mid:5  },
  XLI:  { Giant:50, Large:38, Mid:12 },
  XLB:  { Giant:45, Large:40, Mid:15 },
  XLU:  { Giant:55, Large:35, Mid:10 },
  XLRE: { Giant:50, Large:38, Mid:12 },
  // Emerging markets — more mid/small
  EEM:  { Giant:40, Large:35, Mid:18, Small:7 },
  VWO:  { Giant:38, Large:35, Mid:20, Small:7 },
  IEMG: { Giant:35, Large:38, Mid:20, Small:7 },
  CHIQ: { Giant:35, Large:35, Mid:25, Small:5 },  // China consumer
  // India ETFs
  NIFTYBEES: { Giant:60, Large:30, Mid:10 },
  MON100:    { Giant:72, Large:22, Mid:6  },
  N100:      { Giant:72, Large:22, Mid:6  },
  // Rare earth / thematic
  REMX: { Large:40, Mid:40, Small:20 },
  // Global
  ACWI: { Giant:55, Large:28, Mid:13, Small:4 },
  VT:   { Giant:50, Large:28, Mid:16, Small:6 },
  // Real Estate ETFs
  VNQ:  { Giant:30, Large:45, Mid:22, Small:3 },
  IYR:  { Giant:28, Large:44, Mid:24, Small:4 },
  XLRE: { Giant:32, Large:42, Mid:22, Small:4 },
  REM:  { Large:50, Mid:35, Small:15 },          // mortgage REITs
  // Bond/Fixed Income ETFs (no cap classification)
  // Commodity ETFs — single cap bucket (they aren't equities)
  GLD:  { Giant:100 },  // gold — treated as Giant (commodity fund)
  IAU:  { Giant:100 },
  SLV:  { Large:100 },
  GDX:  { Large:55, Mid:35, Small:10 },          // gold miners
  // Rare earth / materials thematic
  REMX: { Large:40, Mid:40, Small:20 },           // VanEck Rare Earth
  LIT:  { Large:45, Mid:35, Small:20 },           // lithium
  COPX: { Large:40, Mid:40, Small:20 },           // copper miners
  // Healthcare ETFs
  XBI:  { Large:30, Mid:35, Small:35 },           // biotech — very small-cap heavy
  IBB:  { Giant:45, Large:40, Mid:15 },
  ARKG: { Large:30, Mid:40, Small:30 },
  // Dividend / value ETFs
  VYM:  { Giant:60, Large:35, Mid:5  },
  DVY:  { Giant:50, Large:38, Mid:12 },
  // Small/mid cap focused
  IWM:  { Mid:35, Small:52, Micro:13 },           // Russell 2000
  IJR:  { Mid:20, Small:65, Micro:15 },           // S&P 600
  IJH:  { Large:15, Mid:75, Small:10 },           // S&P 400 Mid
  // Country ETFs
  EWJ:  { Giant:40, Large:35, Mid:20, Small:5  }, // Japan
  EWG:  { Giant:50, Large:32, Mid:15, Small:3  }, // Germany
  EWU:  { Giant:45, Large:35, Mid:18, Small:2  }, // UK
  FXI:  { Giant:60, Large:30, Mid:10           }, // China large cap
  // Crypto ETFs
  IBIT: { Giant:100 },   // Bitcoin ETF → Giant (BTC market cap)
  BITO: { Giant:100 },
};


// ── Client Login Registry ──
// NOTE: Passwords are loaded from Supabase (client_logins table).
// This constant is only used if Supabase returns no data (offline/first-run).
// Passwords here are intentionally blank — set real ones via Admin → Families.
const INITIAL_CLIENT_LOGINS = {};


// toUSD and convert are defined inside the component to use live FX rates state
function fmt(n, currency="USD", compact=false) {
  const sym = { USD:"$", EUR:"€", GBP:"£", INR:"₹", JPY:"¥", CHF:"Fr ", AED:"AED ", SGD:"S$" };
  const s = sym[currency] || "$";
  if (compact) {
    if (currency === "INR") {
      // Indian number system: Cr (crore=10M), L/M (lakh=100K)
      if (Math.abs(n) >= 1e7) return s+(n/1e7).toFixed(2)+"Cr";
      if (Math.abs(n) >= 1e5) return s+(n/1e5).toFixed(2)+"L";
      if (Math.abs(n) >= 1e3) return s+(n/1e3).toFixed(1)+"K";
      return s+n.toFixed(0);
    }
    if (Math.abs(n) >= 1e9) return s+(n/1e9).toFixed(2)+"B";
    if (Math.abs(n) >= 1e6) return s+(n/1e6).toFixed(2)+"M";
    if (Math.abs(n) >= 1e3) return s+(n/1e3).toFixed(1)+"K";
    return s+n.toFixed(0);
  }
  if (currency === "INR") {
    return s+n.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
  }
  return s+n.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
}
function Pct({ v }) {
  const T = useTheme();
  if (v === null || v === undefined) return <span style={{color:T.textSecondary}}>—</span>;
  return <span style={{color:v>=0?"#1D9E75":"#A32D2D",fontWeight:500}}>{v>=0?"+":""}{v.toFixed(1)}%</span>;
}

// ── NavIcon: thin-stroke line icons for the main sidebar (Lucide-style) ──
// Self-contained SVG set (zero dependencies). Each entry returns the inner SVG
// elements as real React nodes (not dangerouslySetInnerHTML, which is unreliable
// for SVG namespacing across React versions). Keyed by short name.
const NAV_ICONS = {
  summary:      <path d="M3 13h4l3 8 4-16 3 8h4"/>,
  overview:     <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></>,
  holdings:     <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></>,
  performance:  <><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></>,
  benchmark:    <path d="M5 21V8M12 21V3M19 21v-7"/>,
  advisor:      <><rect x="4" y="5" width="16" height="14" rx="2"/><path d="M9 9h.01M15 9h.01M9 14c.8.7 1.9 1 3 1s2.2-.3 3-1"/><path d="M12 2v3"/></>,
  research:     <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/><path d="M8.5 11h5M11 8.5v5"/></>,
  risk:         <path d="M13 2L3 14h9l-1 8 10-12h-9z"/>,
  volatility:   <path d="M3 12c3-6 6-6 9 0s6 6 9 0"/>,
  geo:          <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18"/></>,
  currency:     <path d="M4 9h13l-3-3M20 15H7l3 3"/>,
  scenarios:    <path d="M9 3h6M10 3v5l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3"/>,
  irr:          <><path d="M3 3v18h18"/><path d="M7 16l3-6 4 3 4-8"/><circle cx="7" cy="16" r="1"/><circle cx="18" cy="5" r="1"/></>,
  capgains:     <><path d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></>,
  cashflow:     <><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5M21 7h-5"/></>,
  goals:        <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></>,
  income:       <><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 10c0-1.3 1-2 2.5-2s2.5.7 2.5 2-5 1-5 3 1 2 2.5 2 2.5-.7 2.5-2"/></>,
  news:         <><path d="M4 5h16v14H4z"/><path d="M4 9h16M8 5v4"/></>,
  transactions: <path d="M17 2l4 4-4 4M21 6H9a4 4 0 0 0-4 4v1M7 22l-4-4 4-4M3 18h12a4 4 0 0 0 4-4v-1"/>,
  rebalance:    <><path d="M12 3v18"/><path d="M6 8l-3 5h6zM18 8l-3 5h6z"/><path d="M6 3h12"/></>,
  model:        <><path d="M12 2l9 5-9 5-9-5z"/><path d="M3 12l9 5 9-5"/><path d="M3 17l9 5 9-5"/></>,
  watchlist:    <><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></>,
  upload:       <><path d="M12 16V4M8 8l4-4 4 4"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></>,
  families:     <><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="9" r="2"/><path d="M17 13c2.2 0 4 1.8 4 4"/></>,
  datasources:  <><path d="M9 2v6M15 2v6"/><path d="M5 8h14v3a7 7 0 0 1-14 0z"/><path d="M9 18v3M15 18v3"/></>,
  subscriptions:<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/></>,
  locked:       <><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></>,
  sun:          <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></>,
  moon:         <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>,
  autoTheme:    <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></>,
  // group headers
  grp_wealth:   <path d="M3 13h4l3 8 4-16 3 8h4"/>,
  grp_risk:     <path d="M13 2L3 14h9l-1 8 10-12h-9z"/>,
  grp_analysis: <><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></>,
  grp_planning: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></>,
  grp_manage:   <><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M8 4v16"/></>,
  bookmark:     <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>,
  compare:      <><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></>,
  grp_admin:    <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-2.7-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 0 1 0-4h.1A1.6 1.6 0 0 0 4.2 9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z"/></>,
};
function NavIcon({ name, size = 19, color = "currentColor", strokeWidth = 1.8, style }) {
  const inner = NAV_ICONS[name];
  if (!inner) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round"
      style={style}
    >{inner}</svg>
  );
}


// ── XIRR: True time-weighted IRR using exact dates ─────────────────────────
// cashflows = [{ date: "YYYY-MM-DD", amount: number }, ...]
// amount < 0 = outflow (buy), amount > 0 = inflow (sell/current value)
// Returns annualised rate as a percentage, or null on failure.
function calcXIRR(cashflows) {
  if (!cashflows || cashflows.length < 2) return null;
  // Normalise — ensure at least one negative (outflow) and one positive
  const hasOut = cashflows.some(c => c.amount < 0);
  const hasIn  = cashflows.some(c => c.amount > 0);
  if (!hasOut || !hasIn) return null;

  // Use first date as t=0 reference
  const t0 = new Date(cashflows[0].date).getTime();
  const days = cashflows.map(c => (new Date(c.date).getTime() - t0) / 86400000);

  // NPV(r) = Σ cf_i / (1+r)^(days_i/365)
  const npv = (r) => {
    let s = 0;
    for (let i = 0; i < cashflows.length; i++) {
      const base = 1 + r;
      if (base <= 0) return NaN;
      s += cashflows[i].amount / Math.pow(base, days[i] / 365);
    }
    return s;
  };
  // dNPV/dr
  const dnpv = (r) => {
    let s = 0;
    for (let i = 0; i < cashflows.length; i++) {
      const base = 1 + r;
      if (base <= 0) return NaN;
      s -= (days[i] / 365) * cashflows[i].amount / Math.pow(base, days[i] / 365 + 1);
    }
    return s;
  };

  // Newton-Raphson with bisection fallback
  const tryNewton = (guess) => {
    let r = guess;
    for (let i = 0; i < 300; i++) {
      const f  = npv(r);
      const df = dnpv(r);
      if (!isFinite(f) || !isFinite(df) || Math.abs(df) < 1e-12) return null;
      const r2 = r - f / df;
      if (!isFinite(r2)) return null;
      if (Math.abs(r2 - r) < 1e-9) return isFinite(r2) ? r2 : null;
      r = r2;
    }
    return null;
  };

  // Bisection fallback — reliable but slower
  const bisect = (lo, hi, maxIter = 200) => {
    if (npv(lo) * npv(hi) > 0) return null; // no root in range
    for (let i = 0; i < maxIter; i++) {
      const mid = (lo + hi) / 2;
      if (Math.abs(hi - lo) < 1e-9) return mid;
      npv(lo) * npv(mid) <= 0 ? (hi = mid) : (lo = mid);
    }
    return (lo + hi) / 2;
  };

  // Try Newton from several starting points, then bisection
  const guesses = [0.1, 0.0, 0.5, -0.05, 0.25, 1.0, -0.5];
  for (const g of guesses) {
    const r = tryNewton(g);
    if (r !== null && isFinite(r) && r > -1 && Math.abs(r) < 100) {
      return r * 100; // return as %
    }
  }
  const rb = bisect(-0.999, 10.0);
  if (rb !== null && isFinite(rb)) return rb * 100;
  return null;
}

// Legacy shim — old code passes [years, amount] pairs; convert to date-based
function calcIRR(cashflows) {
  if (!cashflows || cashflows.length < 2) return null;
  // cashflows = [[yearsAgo, amount], ...] — convert to date-based
  const today = new Date();
  const dated = cashflows.map(([t, amount]) => {
    const d = new Date(today);
    d.setTime(today.getTime() - t * 365.25 * 86400 * 1000);
    return { date: d.toISOString().slice(0, 10), amount };
  });
  return calcXIRR(dated);
}

// ── Global Capital Gains Calculator ─────────────────────────────────────────
// Supports: India (LTCG/STCG), USA (short/long-term), UK (CGT), EU/General
//
// Rules by jurisdiction:
//   India Equity/MF:  held >1yr → LTCG 12.5% (>₹1.25L exempt); else STCG 20%
//   India Debt MF:    all gains taxed at slab rate (2023 budget change)
//   India Debt Other: held >3yr → LTCG 20% with indexation; else STCG at slab
//   USA:              held >1yr → long-term (0/15/20%); else short-term (ordinary income)
//   UK:               CGT — basic rate 18% (property 24%), higher 24% (28%)
//   EU/General:       held >1yr → long-term; else short-term (indicative rates)
//
// Lot method: FIFO (default), can extend to SpecID / HIFO
//
// Returns per-holding breakdown: { holding, lots, realised, unrealised,
//   ltcgAmount, stcgAmount, ltcgTax, stcgTax, totalTaxEst, jurisdiction }
// ── Country → Tax Jurisdiction map ─────────────────────────────────────────
const COUNTRY_JURIS_MAP = {
  "India":"IN","IN":"IN",
  "United States":"US","USA":"US","US":"US","America":"US",
  "United Kingdom":"UK","UK":"UK","Britain":"UK","England":"UK","Scotland":"UK","Wales":"UK",
  "Singapore":"SG","SG":"SG",
  "United Arab Emirates":"AE","UAE":"AE","AE":"AE","Dubai":"AE","Abu Dhabi":"AE",
  "Germany":"EU","France":"EU","Netherlands":"EU","Luxembourg":"EU","Switzerland":"EU",
  "Austria":"EU","Belgium":"EU","Italy":"EU","Spain":"EU","Ireland":"EU",
  "Sweden":"EU","Denmark":"EU","Finland":"EU","Norway":"EU","Portugal":"EU",
  "Hong Kong":"HK","Japan":"JP","Australia":"AU","Canada":"CA",
  "China":"CN","Brazil":"BR","Global":"US",  // default Global assets to US rules
};

// ── Per-jurisdiction tax rules ───────────────────────────────────────────────
const TAX_RULES_BY_JURIS = {
  IN: { ltThreshold:365,  ltRate:12.5, stRate:20,  exemption:125000, exemptionCcy:"INR", label:"India",          noTax:false },
  US: { ltThreshold:365,  ltRate:15,   stRate:37,  exemption:0,      exemptionCcy:"USD", label:"USA",            noTax:false },
  UK: { ltThreshold:0,    ltRate:24,   stRate:24,  exemption:3000,   exemptionCcy:"GBP", label:"UK",             noTax:false },
  EU: { ltThreshold:365,  ltRate:15,   stRate:25,  exemption:0,      exemptionCcy:"EUR", label:"Europe",         noTax:false },
  SG: { ltThreshold:0,    ltRate:0,    stRate:0,   exemption:0,      exemptionCcy:"SGD", label:"Singapore",      noTax:true  },
  AE: { ltThreshold:0,    ltRate:0,    stRate:0,   exemption:0,      exemptionCcy:"AED", label:"UAE",            noTax:true  },
  HK: { ltThreshold:0,    ltRate:0,    stRate:0,   exemption:0,      exemptionCcy:"HKD", label:"Hong Kong",      noTax:true  },
  JP: { ltThreshold:365,  ltRate:20,   stRate:20,  exemption:0,      exemptionCcy:"JPY", label:"Japan",          noTax:false },
  AU: { ltThreshold:365,  ltRate:23.5, stRate:47,  exemption:0,      exemptionCcy:"AUD", label:"Australia",      noTax:false },
  CA: { ltThreshold:0,    ltRate:26.5, stRate:26.5,exemption:0,      exemptionCcy:"CAD", label:"Canada",         noTax:false },
  CN: { ltThreshold:365,  ltRate:20,   stRate:20,  exemption:0,      exemptionCcy:"CNY", label:"China",          noTax:false },
};
function getRulesForCountry(country) {
  const jid = COUNTRY_JURIS_MAP[country] || "US";
  return { ...(TAX_RULES_BY_JURIS[jid] || TAX_RULES_BY_JURIS.US), jid };
}

// ── calcCapitalGains — per-holding jurisdiction, grouped by country ──────────
// Each holding is taxed under its OWN country's rules.
// Returns byHolding (per asset), byJurisdiction (grouped), and portfolio totals.
function calcCapitalGains(transactions, holdings, fxRates) {
  // Build holding → country map
  const holdingCountry = {};
  (holdings || []).forEach(h => { holdingCountry[h.name] = h.country || "Global"; });

  // Sort txns by date — FIFO
  const sorted = [...(transactions || [])].sort((a,b) => new Date(a.date) - new Date(b.date));

  const lotQueues = {}; // holdingName → [{date,qty,costPerUnit,currency}]
  const results   = {}; // holdingName → gains record

  sorted.forEach(txn => {
    const key = txn.holding || "";
    if (!key) return;
    const country  = holdingCountry[key] || txn.country || "Global";
    const rules    = getRulesForCountry(country);

    if (!lotQueues[key]) lotQueues[key] = [];
    if (!results[key]) results[key] = {
      holding: key, country, jurisId: rules.jid, jurisLabel: rules.label,
      rules, lots: [], realised: 0,
      ltcgAmount: 0, stcgAmount: 0, ltcgTax: 0, stcgTax: 0, totalTaxEst: 0,
      currency: txn.currency || "USD",
    };

    if (["BUY","SIP","STP_IN","SWITCH_IN"].includes(txn.type)) {
      lotQueues[key].push({
        date: txn.date, qty: parseFloat(txn.qty)||0,
        costPerUnit: parseFloat(txn.price)||0,
        currency: txn.currency || "INR",
      });
    } else if (["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(txn.type)) {
      let qtyToSell = parseFloat(txn.qty) || 0;
      const salePrice = parseFloat(txn.price) || 0;
      const queue = lotQueues[key];

      while (qtyToSell > 1e-6 && queue.length > 0) {
        const lot = queue[0];
        const qty = Math.min(lot.qty, qtyToSell);
        const holdDays = (new Date(txn.date) - new Date(lot.date)) / 86400000;
        const isLT = holdDays >= (rules.ltThreshold || 365);
        const gainNative = (salePrice - lot.costPerUnit) * qty;
        // Convert native gain to USD using sale currency
        const txnCcy = txn.currency || lot.currency || "INR";
        const fxRate = (fxRates||{})[txnCcy] || 1; // units of ccy per USD
        const gain   = gainNative / fxRate; // gain in USD

        if (isLT) results[key].ltcgAmount += gain;
        else      results[key].stcgAmount += gain;
        results[key].realised += gain;
        results[key].lots.push({
          buyDate: lot.date, sellDate: txn.date, qty,
          costPerUnit: lot.costPerUnit, salePrice, gainTotal: gain, holdDays,
          type: isLT ? "LTCG" : "STCG",
        });
        lot.qty -= qty; qtyToSell -= qty;
        if (lot.qty < 1e-6) queue.shift();
      }
    }
  });

  // Unrealised gains from current holdings
  (holdings || []).forEach(h => {
    const key = h.name;
    const country = h.country || "Global";
    const rules   = getRulesForCountry(country);
    if (!results[key]) results[key] = {
      holding: key, country, jurisId: rules.jid, jurisLabel: rules.label,
      rules, lots: [], realised: 0,
      ltcgAmount: 0, stcgAmount: 0, ltcgTax: 0, stcgTax: 0, totalTaxEst: 0,
      currency: h.currency || "USD",
    };
    // Convert prices to USD first using fxRates before computing unrealised gain
    // fxRates: { currency: rateVsUSD } — e.g. { INR: 84, SGD: 0.74, USD: 1 }
    const hCcy    = h.currency || "USD";
    const toUSD   = (amt) => {
      const r = (fxRates||{})[hCcy];
      return r ? amt / r : amt; // fxRates[ccy] = units-per-USD, so divide
    };
    const curValUSD  = toUSD((h.qty||0) * (h.currentPrice||0));
    const costValUSD = toUSD((h.qty||0) * (h.costPrice||0));
    // Prefer pre-computed valueUSD/costUSD if available (already FX-converted)
    const unrealisedUSD = (h.valueUSD && h.costUSD)
      ? (h.valueUSD - h.costUSD)
      : (curValUSD - costValUSD);
    results[key].unrealised   = unrealisedUSD;
    results[key].currentQty   = h.qty || 0;
    results[key].currentPrice = h.currentPrice || 0;
    results[key].class        = h.class;
    results[key].ticker       = h.ticker;
  });

  // ── Tax calculation: NET gains per jurisdiction FIRST, then apply tax ──
  // Under Indian law (and most jurisdictions): LT losses offset LT gains,
  // ST losses offset ST gains. Tax only applies to the NET positive amount.
  
  // Step 1: compute net LT and ST per jurisdiction
  const jurisNetLT = {}; // jid → net LTCG (can be negative = loss)
  const jurisNetST = {}; // jid → net STCG (can be negative = loss)
  Object.values(results).forEach(r => {
    const j = r.jurisId;
    jurisNetLT[j] = (jurisNetLT[j]||0) + r.ltcgAmount;
    jurisNetST[j] = (jurisNetST[j]||0) + r.stcgAmount;
  });

  // Step 2: compute total tax per jurisdiction on NET gains
  const jurisTaxLT = {}; // jid → total LT tax for the juris
  const jurisTaxST = {}; // jid → total ST tax for the juris
  const jurisPosLT = {}; // jid → sum of positive LT gains (for proportional allocation)
  const jurisPosST = {}; // jid → sum of positive ST gains (for proportional allocation)
  Object.values(results).forEach(r => {
    const j = r.jurisId;
    if (!jurisPosLT[j]) jurisPosLT[j] = 0;
    if (!jurisPosST[j]) jurisPosST[j] = 0;
    if (r.ltcgAmount > 0) jurisPosLT[j] += r.ltcgAmount;
    if (r.stcgAmount > 0) jurisPosST[j] += r.stcgAmount;
  });
  // Compute jurisdiction-level tax on net gains (after exemption)
  const jurisRules = {};
  Object.values(results).forEach(r => { jurisRules[r.jurisId] = r.rules; });
  Object.entries(jurisNetLT).forEach(([j, netLT]) => {
    const { ltRate, stRate, exemption, noTax } = jurisRules[j] || {};
    if (noTax) { jurisTaxLT[j] = 0; jurisTaxST[j] = 0; return; }
    const netST = jurisNetST[j] || 0;
    // LT tax: only on net positive LT after exemption
    jurisTaxLT[j] = Math.max(0, netLT - (exemption||0)) * ((ltRate||0) / 100);
    // ST tax: only on net positive ST (no exemption for ST in India)
    jurisTaxST[j] = Math.max(0, netST) * ((stRate||0) / 100);
  });

  // Step 3: allocate juris tax back to individual holdings proportionally
  // Holdings with positive gains bear tax; loss holdings bear 0
  Object.values(results).forEach(r => {
    const { noTax } = r.rules;
    if (noTax) { r.ltcgTax = 0; r.stcgTax = 0; r.totalTaxEst = 0; return; }
    const j = r.jurisId;
    const posLT = jurisPosLT[j] || 0;
    const posST = jurisPosST[j] || 0;
    // Allocate proportionally to positive contributors only
    r.ltcgTax = posLT > 0 && r.ltcgAmount > 0
      ? (r.ltcgAmount / posLT) * (jurisTaxLT[j]||0) : 0;
    r.stcgTax = posST > 0 && r.stcgAmount > 0
      ? (r.stcgAmount / posST) * (jurisTaxST[j]||0) : 0;
    r.totalTaxEst = r.ltcgTax + r.stcgTax;
    r.exemptionUsed = 0; // simplified — exemption applied at juris level
  });

  // Group by jurisdiction
  const byJuris = {};
  Object.values(results).forEach(r => {
    const j = r.jurisId;
    if (!byJuris[j]) byJuris[j] = {
      jurisId: j, jurisLabel: r.jurisLabel, rules: r.rules,
      holdings: [], ltcg: 0, stcg: 0, realised: 0,
      unrealised: 0, taxEst: 0, noTax: r.rules.noTax,
    };
    byJuris[j].holdings.push(r);
    byJuris[j].ltcg       += r.ltcgAmount;
    byJuris[j].stcg       += r.stcgAmount;
    byJuris[j].realised   += r.realised || 0;
    byJuris[j].unrealised += r.unrealised || 0;
    byJuris[j].taxEst     += r.totalTaxEst || 0;
  });

  const allResults = Object.values(results);
  return {
    byHolding:         allResults,
    byJurisdiction:    Object.values(byJuris).sort((a,b) => Math.abs(b.taxEst)-Math.abs(a.taxEst)),
    totalLtcg:         allResults.reduce((s,r) => s + r.ltcgAmount, 0),
    totalStcg:         allResults.reduce((s,r) => s + r.stcgAmount, 0),
    totalRealised:     allResults.reduce((s,r) => s + (r.realised||0), 0),
    totalUnrealised:   allResults.reduce((s,r) => s + (r.unrealised||0), 0),
    totalTaxEstimate:  allResults.reduce((s,r) => s + (r.totalTaxEst||0), 0),
  };
}

// Compute CAGR given start price, end price, years (returns null if insufficient data)
function cagr(startPrice, endPrice, years) {
  if (!startPrice || !endPrice || years < 0.05) return null;
  return (Math.pow(endPrice / startPrice, 1 / years) - 1) * 100;
}

// ── Password security — Web Crypto API (SHA-256 HMAC, matches signup.js) ──────
async function hashPassword(password, salt) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw", enc.encode(salt), { name:"HMAC", hash:"SHA-256" }, false, ["sign"]
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(password));
  return Array.from(new Uint8Array(sig)).map(b=>b.toString(16).padStart(2,"0")).join("");
}

// Verify a login: handles "salt:hash" (new) and plaintext (legacy migration)
async function verifyPassword(input, stored) {
  if (!stored) return false;
  if (stored.includes(":")) {
    const [salt, hash] = stored.split(":");
    return (await hashPassword(input, salt)) === hash;
  }
  return input === stored; // legacy plaintext — still works
}

// Produce "salt:hash" for storing a new/changed password
async function makePasswordHash(password) {
  const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b=>b.toString(16).padStart(2,"0")).join("");
  const hash = await hashPassword(password, salt);
  return `${salt}:${hash}`;
}

// Get the date N years ago in YYYY-MM-DD
function dateYearsAgo(n) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return d.toISOString().slice(0, 10);
}
function dateMonthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}
function startOfYear() {
  return `${new Date().getFullYear()}-01-01`;
}
function startOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`;
}

// ─────────────── CHART COMPONENTS ───────────────
function Sparkline({ data, color="#1D9E75", w=90, h=32 }) {
  // Guard: need at least 2 valid numeric points to draw a line
  const clean = (Array.isArray(data) ? data : []).map(Number).filter(v => isFinite(v));
  if (clean.length < 2) return <svg width={w} height={h}><line x1={0} y1={h/2} x2={w} y2={h/2} stroke={color} strokeWidth="1.5" strokeDasharray="3,3" opacity="0.4"/></svg>;
  const min=Math.min(...clean), max=Math.max(...clean), range=max-min||1;
  const n = clean.length - 1;
  const pts=clean.map((v,i)=>`${(i/n)*w},${h-((v-min)/range)*(h-4)-2}`).join(" ");
  return <svg width={w} height={h}><polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function DonutChart({ data, size=190, colorMap=null, onSliceClick=null, activeSlice=null, hideLegend=false, compact=false }) {
  const T = useTheme();
  const [hovered, setHovered] = React.useState(null);
  const isClickable = typeof onSliceClick === "function";
  const DEFAULT_COLORS=["#2563EB","#D97706","#1D9E75","#9333EA","#A32D2D","#0EA5E9","#EA580C","#7C3AED","#F59E0B","#D946EF"];
  const getColor=(label,i)=>colorMap?(colorMap[label]||DEFAULT_COLORS[i%DEFAULT_COLORS.length]):DEFAULT_COLORS[i%DEFAULT_COLORS.length];
  const total=data.reduce((acc,d)=>acc+d.value,0)||1;
  const effectiveSize = compact ? 120 : size;
  const r=Math.round(effectiveSize*0.36), cx=effectiveSize/2, cy=effectiveSize/2;
  const validData = data.filter(d=>d.value>0);
  if (validData.length === 0) return <div style={{width:effectiveSize,height:effectiveSize,display:"flex",alignItems:"center",justifyContent:"center",color:T.textLight,fontSize:11}}>No data</div>;

  const isSingle = validData.length === 1;
  let slices;
  if (isSingle) {
    const d = validData[0];
    const color = getColor(d.label, 0);
    slices = [{ path: null, circle: true, color, label: d.label, pct: "100.0" }];
  } else {
    let cum = -Math.PI/2;
    slices = validData.map((d,i) => {
      const angle = (d.value/total)*2*Math.PI;
      const x1=cx+r*Math.cos(cum), y1=cy+r*Math.sin(cum);
      cum += angle;
      const x2=cx+r*Math.cos(cum), y2=cy+r*Math.sin(cum);
      return { path:`M${cx},${cy}L${x1},${y1}A${r},${r} 0 ${angle>Math.PI?1:0},1 ${x2},${y2}Z`, color:getColor(d.label,i), label:d.label, pct:(d.value/total*100).toFixed(1) };
    });
  }

  const isActive  = (label) => activeSlice === label;
  const isHovered = (label) => hovered === label;
  const isDimmed  = (label) => activeSlice !== null && !isActive(label);

  // Compact mode: show top 5 legend items, "+N more" for rest
  const legendSlices = compact ? slices.slice(0, 5) : slices;
  const legendExtra  = compact && slices.length > 5 ? slices.length - 5 : 0;

  return (
    <div style={{display:hideLegend?"block":"flex",alignItems:compact?"flex-start":"center",gap:compact?10:16,flexWrap:"wrap"}}>
      <div style={{position:"relative",flexShrink:0,display:"inline-block"}}>
        <svg width={effectiveSize} height={effectiveSize} style={{cursor:isClickable?"pointer":"default",overflow:"visible"}}>
          {isSingle
            ? <circle cx={cx} cy={cy} r={r} fill={slices[0].color}
                style={{opacity:isDimmed(slices[0].label)?0.25:1,transition:"all 0.2s"}}
                onClick={()=>isClickable&&onSliceClick(slices[0].label)}/>
            : slices.map((s,i)=> {
                const active = isActive(s.label);
                const hov    = isHovered(s.label);
                const dim    = isDimmed(s.label);
                // Scale up active/hovered slice
                const scale  = active||hov ? 1.06 : 1;
                return (
                  <path key={i} d={s.path} fill={s.color}
                    stroke={active ? s.color : T.pageBg}
                    strokeWidth={active ? 3 : 2}
                    style={{
                      opacity: dim ? 0.2 : 1,
                      transform: `scale(${scale})`,
                      transformOrigin: `${cx}px ${cy}px`,
                      transition:"all 0.2s cubic-bezier(0.4,0,0.2,1)",
                      filter: active ? `drop-shadow(0 0 6px ${s.color}99)` : "none",
                      cursor: isClickable ? "pointer" : "default",
                    }}
                    onMouseEnter={()=>setHovered(s.label)}
                    onMouseLeave={()=>setHovered(null)}
                    onClick={()=>isClickable&&onSliceClick(s.label)}
                  />
                );
              })
          }
          <circle cx={cx} cy={cy} r={Math.round(effectiveSize*0.2)} fill={T.cardBg} style={{pointerEvents:"none"}}/>
          {/* Centre label: active slice or total */}
          {(hovered||activeSlice) ? (
            <React.Fragment>
              <text x={cx} y={cy-effectiveSize*0.04} textAnchor="middle" fill={T.textPrimary} fontSize={Math.max(8,Math.round(effectiveSize*0.06))} fontWeight="700">
                {(hovered||activeSlice)?.length > 10 ? (hovered||activeSlice).slice(0,10)+"…" : (hovered||activeSlice)}
              </text>
              <text x={cx} y={cy+effectiveSize*0.05} textAnchor="middle" fill={T.brand} fontSize={Math.max(8,Math.round(effectiveSize*0.06))} fontWeight="600">
                {slices.find(s=>s.label===(hovered||activeSlice))?.pct}%
              </text>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <text x={cx} y={cy-effectiveSize*0.025} textAnchor="middle" fill={T.textPrimary} fontSize={Math.max(9,Math.round(effectiveSize*0.07))} fontWeight="700">{validData.length}</text>
              <text x={cx} y={cy+effectiveSize*0.055} textAnchor="middle" fill={T.textMuted} fontSize={Math.max(7,Math.round(effectiveSize*0.05))}>segments</text>
            </React.Fragment>
          )}
        </svg>
        {/* Click hint */}
        {isClickable && !activeSlice && !hideLegend && (
          <div style={{position:"absolute",bottom:-18,left:"50%",transform:"translateX(-50%)",
            fontSize:9,color:T.textMuted,whiteSpace:"nowrap",letterSpacing:"0.04em"}}>
            click segment to filter
          </div>
        )}
        {isClickable && activeSlice && !hideLegend && (
          <div onClick={()=>onSliceClick(activeSlice)}
            style={{position:"absolute",bottom:-18,left:"50%",transform:"translateX(-50%)",
              fontSize:9,color:T.brand,whiteSpace:"nowrap",cursor:"pointer",
              textDecoration:"underline",letterSpacing:"0.04em"}}>
            ✕ clear filter
          </div>
        )}
      </div>
      {!hideLegend && <div style={{display:"flex",flexDirection:"column",gap:compact?2:4}}>
        {legendSlices.map((s,i)=>{
          const active = isActive(s.label);
          const hov    = isHovered(s.label);
          const dim    = isDimmed(s.label);
          return (
            <div key={i}
              onClick={()=>isClickable&&onSliceClick(s.label)}
              onMouseEnter={()=>isClickable&&setHovered(s.label)}
              onMouseLeave={()=>isClickable&&setHovered(null)}
              style={{display:"flex",alignItems:"center",gap:6,fontSize:compact?11:12,
                padding:compact?"2px 4px":"3px 7px",borderRadius:6,cursor:isClickable?"pointer":"default",
                background:active?`${s.color}15`:hov?T.hoverBg:"transparent",
                opacity:dim?0.4:1,
                border:active?`1px solid ${s.color}44`:"1px solid transparent",
                transition:"all 0.15s"}}>
              <div style={{width:compact?7:9,height:compact?7:9,borderRadius:2,background:s.color,flexShrink:0,
                transform:active?"scale(1.3)":"scale(1)",transition:"transform 0.2s"}}/>
              <span style={{color:active?T.textPrimary:T.textSecondary,whiteSpace:"nowrap",
                fontWeight:active?600:400}}>{compact && s.label.length > 14 ? s.label.slice(0,14)+"…" : s.label}</span>
              <span style={{color:active?s.color:T.textPrimary,marginLeft:2,fontWeight:500}}>{s.pct}%</span>
              {active && <span style={{fontSize:9,color:s.color,marginLeft:2}}>●</span>}
            </div>
          );
        })}
        {legendExtra > 0 && (
          <div style={{fontSize:10,color:T.textMuted,padding:"2px 4px"}}>+{legendExtra} more</div>
        )}
      </div>}
    </div>
  );
}

function BarChart({ data, height=130 }) {
  const T = useTheme();
  const safe   = (data||[]).filter(d => d.value != null && isFinite(d.value));
  const sorted = [...safe].sort((a,b) => b.value - a.value);
  const maxAbs  = Math.max(...sorted.map(d=>Math.abs(d.value)), 1);
  const barH    = Math.max(18, Math.floor((height - sorted.length*5) / Math.max(sorted.length,1)));
  if (!sorted.length) return null;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      {sorted.map((d,i) => {
        const val   = d.value ?? 0;
        const pct   = Math.abs(val) / maxAbs * 100;
        const color = val >= 0 ? "#10B981" : "#F87171";
        const alpha = 0.6 + (pct/100)*0.4;
        return (
          <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:100,fontSize:10.5,color:T.textSecondary,textAlign:"right",
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flexShrink:0}}
              title={d.label}>{d.label}</div>
            <div style={{flex:1,height:barH,background:T.borderSubtle||"rgba(0,0,0,0.05)",
              borderRadius:6,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",left:0,top:0,height:"100%",
                width:`${pct}%`,
                background:`linear-gradient(90deg,${color},${color}cc)`,
                borderRadius:6,opacity:alpha,
                transition:"width 0.5s cubic-bezier(0.4,0,0.2,1)"}}/>
            </div>
            <div style={{width:52,fontSize:10.5,fontWeight:700,
              color:color,textAlign:"left",flexShrink:0}}>
              {val>=0?"+":""}{val.toFixed(1)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────── GEO HEATMAP ───────────────
const COUNTRY_ISO = {
  "USA":"US","India":"IN","UAE":"AE","Switzerland":"CH","Japan":"JP",
  "UK":"GB","France":"FR","Singapore":"SG","Germany":"DE","China":"CN",
  "Australia":"AU","Canada":"CA","Brazil":"BR","Global":"GL"
};
// Geographic SVG paths on 870×440 Mercator-style projection
// Each country has a simplified but geographically accurate outline
// ── COUNTRY_SHAPES kept for COUNTRY_ISO lookups (label only, paths now in WorldHeatMap) ──
const COUNTRY_SHAPES = {
  CA:{label:"Canada"},US:{label:"USA"},MX:{label:"Mexico"},BR:{label:"Brazil"},
  AR:{label:"Argentina"},CL:{label:"Chile"},CO:{label:"Colombia"},PE:{label:"Peru"},
  VE:{label:"Venezuela"},
  GB:{label:"UK"},FR:{label:"France"},DE:{label:"Germany"},IT:{label:"Italy"},
  ES:{label:"Spain"},NL:{label:"Netherlands"},CH:{label:"Switzerland"},
  SE:{label:"Sweden"},NO:{label:"Norway"},PL:{label:"Poland"},UA:{label:"Ukraine"},
  RU:{label:"Russia"},BE:{label:"Belgium"},AT:{label:"Austria"},PT:{label:"Portugal"},
  CZ:{label:"Czech Rep."},HU:{label:"Hungary"},RO:{label:"Romania"},GR:{label:"Greece"},
  FI:{label:"Finland"},DK:{label:"Denmark"},
  CN:{label:"China"},IN:{label:"India"},JP:{label:"Japan"},KR:{label:"S. Korea"},
  TW:{label:"Taiwan"},HK:{label:"Hong Kong"},SG:{label:"Singapore"},
  ID:{label:"Indonesia"},MY:{label:"Malaysia"},TH:{label:"Thailand"},
  PH:{label:"Philippines"},PK:{label:"Pakistan"},BD:{label:"Bangladesh"},
  IR:{label:"Iran"},IQ:{label:"Iraq"},SA:{label:"Saudi Arabia"},AE:{label:"UAE"},
  IL:{label:"Israel"},TR:{label:"Turkey"},EG:{label:"Egypt"},
  NG:{label:"Nigeria"},ZA:{label:"S. Africa"},KE:{label:"Kenya"},ET:{label:"Ethiopia"},
  GH:{label:"Ghana"},TZ:{label:"Tanzania"},
  AU:{label:"Australia"},NZ:{label:"New Zealand"},
  GL:{label:"Global"},
};

// ── WorldHeatMap: uses real GeoJSON paths via Natural Earth (unpkg CDN) ──
function WorldHeatMap({ byCountry, totalUSD, displayCurrency, convert }) {
  const T = useTheme();
  const isMobile = useIsMobile();
  const [geoData,    setGeoData]    = React.useState(null);
  const [hovered,    setHovered]    = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({x:0,y:0});
  const svgRef = React.useRef(null);

  // Fetch Natural Earth 110m topojson once
  React.useEffect(() => {
    fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json")
      .then(r => r.json())
      .then(topo => {
        // Convert topojson → GeoJSON features using built-in topo math
        const arcs = topo.arcs;
        function decodeArc(arcIdx) {
          const reversed = arcIdx < 0;
          const arc = arcs[reversed ? ~arcIdx : arcIdx];
          let x=0, y=0;
          const pts = arc.map(([dx,dy]) => { x+=dx; y+=dy; return [x,y]; });
          return reversed ? pts.reverse() : pts;
        }
        function topoRing(ring) {
          return ring.flatMap(arcIdx => decodeArc(arcIdx));
        }
        function topoPolygon(geom) {
          return geom.arcs.map(ring => ring.map(a => topoRing(a)));
        }

        // Scale/translate from topo coordinates to SVG viewBox 0 0 960 500
        // Natural Earth 110m uses default topojson transform
        const transform = topo.transform;
        const scale = transform?.scale || [1,1];
        const translate = transform?.translate || [0,0];

        function project([x,y]) {
          // topojson quantized → geographic → equirectangular SVG
          const lng = x * scale[0] + translate[0];
          const lat = y * scale[1] + translate[1];
          // Map lng [-180,180] → [0,960], lat [90,-90] → [0,500]
          return [
            ((lng + 180) / 360) * 960,
            ((90 - lat) / 180) * 500,
          ];
        }

        function ringToPath(pts) {
          const mapped = pts.map(project);
          return "M" + mapped.map(([x,y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join("L") + "Z";
        }

        // ISO numeric → ISO alpha-2 lookup (key countries for our portfolio data)
        const NUM_TO_A2 = {
          840:"US",124:"CA",484:"MX",76:"BR",32:"AR",152:"CL",170:"CO",604:"PE",862:"VE",
          826:"GB",250:"FR",276:"DE",380:"IT",724:"ES",528:"NL",756:"CH",752:"SE",578:"NO",
          616:"PL",804:"UA",643:"RU",56:"BE",40:"AT",620:"PT",203:"CZ",348:"HU",642:"RO",300:"GR",246:"FI",208:"DK",
          156:"CN",356:"IN",392:"JP",410:"KR",158:"TW",344:"HK",702:"SG",360:"ID",458:"MY",764:"TH",
          608:"PH",586:"PK",50:"BD",364:"IR",368:"IQ",682:"SA",784:"AE",376:"IL",792:"TR",818:"EG",
          566:"NG",710:"ZA",404:"KE",231:"ET",288:"GH",834:"TZ",
          36:"AU",554:"NZ",
        };

        const features = topo.objects.countries.geometries.map(g => {
          const iso = NUM_TO_A2[parseInt(g.id)] || null;
          let pathStr = "";
          try {
            if (g.type === "Polygon") {
              pathStr = g.arcs.map(ring => ringToPath(topoRing(ring))).join(" ");
            } else if (g.type === "MultiPolygon") {
              pathStr = g.arcs.map(poly => poly.map(ring => ringToPath(topoRing(ring))).join(" ")).join(" ");
            }
          } catch {}
          return { iso, id: g.id, path: pathStr };
        }).filter(f => f.path);

        setGeoData(features);
      })
      .catch(() => setGeoData([]));
  }, []);

  const maxVal = Math.max(...Object.values(byCountry), 1);
  // Count how many regions have actual allocation. When ≤3, the gradient
  // legend is overkill — we use a single accent color (size still encodes %).
  const regionsWithAllocation = Object.values(byCountry).filter(v => v > 0).length;
  const useFlatColor = regionsWithAllocation <= 3;

  function getColor(val) {
    if (!val || val === 0) return null; // no data → grey
    // Flat color path: single accent (deeper for larger %, but no gradient noise)
    if (useFlatColor) {
      const t = val / maxVal;
      return t > 0.5 ? "#1d4ed8" : "#3b82f6";
    }
    const t = val / maxVal;
    if (t > 0.7) return "#1e3a8a";
    if (t > 0.45) return "#1d4ed8";
    if (t > 0.25) return "#2563eb";
    if (t > 0.1)  return "#3b82f6";
    if (t > 0.03) return "#60a5fa";
    return "#93c5fd";
  }

  function getOpacity(val) {
    if (!val) return 0;
    return 0.55 + (val / maxVal) * 0.45;
  }

  const isDark = T.dark;
  const oceanBg  = isDark ? "#0c1929" : "#dbeafe";
  const landBg   = isDark ? "#1a2840" : "#bfdbfe";
  const borderCol = isDark ? "#2d4a6b" : "#93c5fd";
  const labelCol  = isDark ? "#fff"    : "#1e3a8a";

  return (
    <div style={{position:"relative"}}>
      {!geoData ? (
        <div style={{height:300,display:"flex",alignItems:"center",justifyContent:"center",
          background:oceanBg,borderRadius:10,color:T.textMuted,fontSize:13}}>
          <span style={{animation:"spin 1s linear infinite",display:"inline-block",marginRight:8}}>⟳</span>
          Loading world map…
        </div>
      ) : geoData.length === 0 ? (
        <div style={{height:300,display:"flex",alignItems:"center",justifyContent:"center",
          background:oceanBg,borderRadius:10,color:T.textMuted,fontSize:13}}>
          ⚠ Map unavailable — check connection
        </div>
      ) : (
        <div style={{position:"relative",borderRadius:10,overflow:"hidden",
          border:`1px solid ${T.cardBorder}`,background:oceanBg}}>
          <svg ref={svgRef} viewBox="0 0 960 500"
            style={{width:"100%",display:"block",touchAction:"manipulation"}}
            preserveAspectRatio="xMidYMid meet"
            onMouseLeave={()=>setHovered(null)}>
            {/* Ocean */}
            <rect width="960" height="500" fill={oceanBg}/>
            {/* Latitude lines (subtle, no extreme bands) */}
            {[60,30,0,-30,-60].map(lat=>{
              const y = ((90-lat)/180)*500;
              return <line key={lat} x1="0" y1={y} x2="960" y2={y}
                stroke={isDark?"rgba(255,255,255,0.025)":"rgba(37,99,235,0.05)"} strokeWidth="0.5"/>;
            })}
            {/* Longitude lines */}
            {[-120,-60,0,60,120].map(lng=>{
              const x = ((lng+180)/360)*960;
              return <line key={lng} x1={x} y1="0" x2={x} y2="500"
                stroke={isDark?"rgba(255,255,255,0.025)":"rgba(37,99,235,0.05)"} strokeWidth="0.5"/>;
            })}


            {geoData.map(f => {
              const val    = f.iso ? (byCountry[f.iso] || 0) : 0;
              const color  = getColor(val);
              const isHov  = hovered === f.iso && f.iso;
              const label  = f.iso ? (COUNTRY_SHAPES[f.iso]?.label || f.iso) : null;
              return (
                <g key={f.id || f.iso}
                  onMouseEnter={e=>{
                    if (f.iso && val>0) {
                      setHovered(f.iso);
                      const rect = svgRef.current?.getBoundingClientRect();
                      if (rect) setTooltipPos({x:e.clientX-rect.left, y:e.clientY-rect.top});
                    }
                  }}
                  onMouseLeave={()=>setHovered(null)}
                  style={{cursor:val>0?"pointer":"default"}}>
                  <path d={f.path}
                    fill={color || landBg}
                    fillOpacity={color ? getOpacity(val) : 1}
                    stroke={isHov ? "#f59e0b" : borderCol}
                    strokeWidth={isHov ? 1.5 : 0.4}
                    style={{transition:"fill 0.2s, stroke 0.1s"}}/>
                  {/* ISO label for countries with allocation */}
                  {f.iso && val > 0 && label && (function(){
                    // Use bounding-box midpoint for label placement.
                    // Average-of-points pulls labels off-center for MultiPolygon
                    // countries (e.g. USA's Alaska coords drag the average west,
                    // putting the "US 90%" label out in the Pacific).
                    // BBox midpoint is robust to outlier territories.
                    const matches = f.path.match(/[\d.]+,[\d.]+/g)||[];
                    if (matches.length < 3) return null;
                    const pts = matches.map(s=>s.split(",").map(Number));
                    let minX=Infinity, maxX=-Infinity, minY=Infinity, maxY=-Infinity;
                    for (const [x,y] of pts) {
                      if (x<minX) minX=x; if (x>maxX) maxX=x;
                      if (y<minY) minY=y; if (y>maxY) maxY=y;
                    }
                    // For very wide MultiPolygons (USA spans Alaska to Maine = nearly half the
                    // map width), bbox midpoint can still drift. Use the WEIGHTED centroid
                    // of points falling within the densest column of the polygon.
                    let cx, cy;
                    if (f.iso === "US") {
                      // Hard-code US label to contiguous-states center (Kansas-ish)
                      cx = ((-98 + 180) / 360) * 960;  // -98° lng
                      cy = ((90 - 39) / 180) * 500;     // 39° lat
                    } else if (f.iso === "RU") {
                      // Russia: vast longitude range; center on European Russia
                      cx = ((50 + 180) / 360) * 960;
                      cy = ((90 - 60) / 180) * 500;
                    } else if (f.iso === "CA") {
                      // Canada: huge bbox; center on populated south
                      cx = ((-100 + 180) / 360) * 960;
                      cy = ((90 - 55) / 180) * 500;
                    } else {
                      cx = (minX + maxX) / 2;
                      cy = (minY + maxY) / 2;
                    }
                    const fontSize = val/totalUSD > 0.1 ? 10 : val/totalUSD > 0.05 ? 8 : 7;
                    return (
                      <React.Fragment>
                        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle"
                          fill={isDark?"#fff":"#1e3a8a"} fontSize={fontSize} fontWeight="700"
                          fontFamily="Inter, system-ui, sans-serif"
                          style={{pointerEvents:"none",textShadow:"0 1px 3px rgba(0,0,0,0.5)"}}>
                          {f.iso}
                        </text>
                        {val/totalUSD > 0.04 && (
                          <text x={cx} y={cy+10} textAnchor="middle" dominantBaseline="middle"
                            fill={isDark?"rgba(255,255,255,0.9)":"#1e40af"} fontSize={6.5} fontWeight="600"
                            fontFamily="Inter, system-ui, sans-serif"
                            style={{pointerEvents:"none",textShadow:"0 1px 2px rgba(0,0,0,0.4)"}}>
                            {(val/totalUSD*100).toFixed(1)}%
                          </text>
                        )}
                      </React.Fragment>
                    );
                  })()}
                </g>
              );
            })}

            {/* ── City-state markers: Singapore, Hong Kong, UAE — too small for 110m topojson ── */}
            {/* Rendered AFTER country paths so bubbles aren't covered by Indonesia/Malaysia/etc */}
            {[
              { iso:"SG", label:"Singapore", x:762, y:248 },
              { iso:"HK", label:"Hong Kong",  x:784, y:190 },
              { iso:"AE", label:"UAE",         x:628, y:181 },
            ].map(({ iso, label, x, y }) => {
              const val = byCountry[iso] || 0;
              if (!val) return null;
              const color = getColor(val);
              const pct = (val / totalUSD * 100).toFixed(1);
              const isHov = hovered === iso;
              const r = val/totalUSD > 0.3 ? 14 : val/totalUSD > 0.1 ? 11 : 8;
              return (
                <g key={iso}
                  style={{cursor:"pointer"}}
                  onMouseEnter={e=>{
                    setHovered(iso);
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (rect) setTooltipPos({x:e.clientX-rect.left,y:e.clientY-rect.top});
                  }}
                  onMouseLeave={()=>setHovered(null)}>
                  {/* Pulse ring */}
                  <circle cx={x} cy={y} r={r+5}
                    fill={color} fillOpacity={0.2}
                    stroke={color} strokeWidth={0.8} strokeOpacity={0.4}/>
                  {/* Main dot */}
                  <circle cx={x} cy={y} r={r}
                    fill={color} fillOpacity={0.9}
                    stroke={isHov?"#f59e0b":"#fff"} strokeWidth={isHov?2:1}/>
                  {/* ISO label */}
                  <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                    fill="#fff" fontSize={r>10?7:6} fontWeight="700"
                    fontFamily="Inter, system-ui, sans-serif"
                    style={{pointerEvents:"none"}}>
                    {iso}
                  </text>
                  {/* Pct label below — with text-shadow for legibility over country fills */}
                  <text x={x} y={y+r+8} textAnchor="middle"
                    fill={isDark?"#fff":"#1e3a8a"} fontSize={7} fontWeight="700"
                    fontFamily="Inter, system-ui, sans-serif"
                    style={{pointerEvents:"none",textShadow:isDark?"0 1px 3px rgba(0,0,0,0.8)":"0 1px 2px rgba(255,255,255,0.9)"}}>
                    {pct}%
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Tooltip */}
          {hovered && byCountry[hovered] && (
            <div style={{
              position:"absolute",
              left: Math.min(tooltipPos.x + 12, (svgRef.current?.clientWidth||600) - 160),
              top:  Math.max(tooltipPos.y - 50, 8),
              background: isDark?"rgba(10,20,40,0.96)":"#fff",
              border:`1px solid ${isDark?"rgba(96,165,250,0.4)":"#2563eb"}`,
              borderRadius:8,padding:"9px 13px",
              boxShadow:"0 8px 24px rgba(0,0,0,0.35)",
              pointerEvents:"none",zIndex:10,minWidth:130,
            }}>
              <div style={{fontWeight:700,fontSize:12,color:isDark?"#fff":"#0f172a",marginBottom:3}}>
                {COUNTRY_SHAPES[hovered]?.label || hovered}
              </div>
              <div style={{fontWeight:700,fontSize:14,color:"#2563eb",fontFamily:"'Inter', system-ui, sans-serif"}}>
                {fmt(convert(byCountry[hovered], displayCurrency), displayCurrency, true)}
              </div>
              <div style={{fontSize:11,color:isDark?"rgba(255,255,255,0.55)":"#64748b",marginTop:2}}>
                {(byCountry[hovered]/totalUSD*100).toFixed(1)}% of portfolio
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────── CSV SAMPLES ───────────────
const SAMPLE_HOLDINGS_CSV = `Name,Ticker,Class,Exchange,Currency,Qty,CostPrice,CurrentPrice,Country,Listed,ISIN,Manager/Custodian
Apple Inc,AAPL,Equities,NASDAQ,USD,500,142.30,189.40,USA,true,US0378331005,Self-Managed
Reliance Industries,RELIANCE.NSE,Equities,NSE,INR,1200,2450.00,2890.00,India,true,INE002A01018,Self-Managed
Blackstone RE Fund IV,-,Real Estate,Private,USD,1,5000000.00,7200000.00,USA,false,-,Blackstone
Gold ETF,GLD,Commodities,NYSE,USD,800,168.20,192.50,USA,true,US78463V1070,State Street
Bitcoin,BTC-USD,Crypto,Crypto,USD,12.5,28000.00,62400.00,Global,true,-,Coinbase
Nestle SA,NESN.SW,Equities,SIX,CHF,300,95.20,102.80,Switzerland,true,CH0038863350,Self-Managed
US Treasury 4.5% 2026,-,Fixed Income,OTC,USD,100,98.50,99.20,USA,false,US912810TG69,Self-Managed
Dubai Commercial Property,-,Real Estate,Private,AED,1,8500000.00,11200000.00,UAE,false,-,Self-Managed
Picasso Artwork,-,Art & Collectibles,Private,EUR,1,2800000.00,4100000.00,France,false,-,Self-Managed
SoftBank Vision Fund,-,Alternates,Private,USD,1,2000000.00,2650000.00,Japan,false,-,SoftBank`;

const SAMPLE_TRANSACTIONS_CSV = `Date,Holding,Type,Qty,Price,Currency,Fees
2021-03-15,Apple Inc,BUY,300,122.50,USD,45.00
2022-01-10,Apple Inc,BUY,200,174.90,USD,30.00
2023-06-01,Apple Inc,SELL,100,185.00,USD,25.00
2021-06-20,Reliance Industries,BUY,1200,2450.00,INR,500.00
2020-11-05,Gold ETF,BUY,800,168.20,USD,20.00
2021-02-14,Bitcoin,BUY,10,47000.00,USD,200.00`;

// Global Mutual Fund / UCITS sample
const SAMPLE_GLOBAL_MF_CSV = `FundName,ISIN,Currency,Units,CostNAV,CurrentNAV,PurchaseDate,Domicile,AMC,Manager/Custodian
Vanguard FTSE All-World UCITS ETF,IE00B3RBWM25,USD,500,94.20,112.50,2021-06-15,Ireland,Vanguard,Vanguard
iShares Core MSCI World UCITS ETF,IE00B4L5Y983,USD,300,62.40,78.90,2022-01-10,Ireland,BlackRock,BlackRock
Fidelity FAST Global Fund A-ACC-USD,LU0346388689,USD,1000,25.60,34.20,2021-09-20,Luxembourg,Fidelity,Fidelity
JP Morgan Global Bond Opportunities A,LU1093756242,USD,2000,10.50,11.80,2020-11-01,Luxembourg,JPMorgan,JPMorgan
Schroders Global Healthcare Fund A1 ACC,LU0557290854,USD,800,18.30,24.60,2022-04-15,Luxembourg,Schroders,Schroders`;

// Indian Mutual Fund sample — MFapi.in schema uses SchemeCode
const SAMPLE_MF_CSV = `SchemeName,SchemeCode,ISIN,Units,PurchaseNAV,CurrentNAV,PurchaseDate,Folio,AMC,Manager/Custodian
HDFC Mid-Cap Opportunities Fund - Direct Growth,118989,INF179K01VU2,10000,38.50,54.20,2021-06-15,12345678,HDFC AMC,HDFC AMC
Axis Bluechip Fund - Direct Growth,120503,INF846K01EW2,5000,32.10,48.75,2022-01-10,87654321,Axis AMC,Axis AMC
Parag Parikh Flexi Cap Fund - Direct Growth,122639,INF879O01019,3500,28.60,52.30,2021-09-20,11223344,PPFAS AMC,PPFAS AMC
Mirae Asset Emerging Bluechip Fund - Direct Growth,118834,INF769K01DO0,2000,56.20,94.80,2020-11-01,99887766,Mirae AMC,Mirae AMC`;

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────── THEME TOKENS ───────────────
// Each theme defines a complete set of semantic color tokens.
// Components reference T.* (injected via context) so toggling re-renders everything.
const THEMES = {
  light: {
    id: "light",
    // Page backgrounds
    pageBg:       "#F5ebe0",
    pageBgGrad:   "linear-gradient(160deg,#F5ebe0 0%,#EDE0D4 60%,#F5ebe0 100%)",
    loginBg:      "linear-gradient(160deg,#F5ebe0,#EDE0D4)",
    // Card / surface
    cardBg:       "#FFFFFF",
    cardBorder:   "#E8E2D9",
    cardShadow:   "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)",
    surfaceBg:    "#FDFAF6",
    // Header
    headerBg:     "#FFFFFF",
    headerBorder: "#DDD0C0",
    headerShadow: "0 2px 8px rgba(0,120,120,0.07)",
    // Sidebar / nav
    navBg:        "#FFFFFF",
    navBorder:    "#DDD0C0",
    navActiveBg:  "#EDE0D4",
    navActiveText:"#1C1917",
    navText:      "#78716C",
    // Table
    thBg:         "#EDE0D4",
    thText:       "#44403C",
    tdBorder:     "#EDE0D4",
    tableStripe:  "rgba(0,0,0,0.02)",
    // Text
    textPrimary:  "#1C1917",
    textSecondary:"#78716C",
    textMuted:    "#A8A29E",
    textLight:    "#C9B8A8",
    // Inputs
    inputBg:      "#FDFAF6",
    inputBorder:  "#DDD0C0",
    inputText:    "#1C1917",
    // Borders
    border:       "#DDD0C0",
    borderSubtle: "#EDE0D4",
    // Brand
    brand:        "#007878",
    brandDark:    "#005F5F",
    brandLight:   "rgba(0,121,193,0.08)",
    // Accents
    accent:       "#2563EB",
    // Dropdown
    dropdownBg:   "#FFFFFF",
    dropdownBorder:"#DDD0C0",
    dropdownShadow:"0 20px 50px rgba(0,0,0,0.45)",
    // Misc
    badgeBg:      "#EDE0D4",
    badgeText:    "#44403C",
    hoverBg:      "rgba(0,121,193,0.08)",
    mapBg:        "#EDE0D4",
    tooltipBg:    "#FFFFFF",
    tooltipBorder:"#2563EB",
    scrollThumb:  "#DDD0C0",
    // Toggle button style
    toggleBg:     "#EDE0D4",
    toggleText:   "#78716C",
  },
  dark: {
    id: "dark",
    // Page backgrounds — deep navy like reference
    pageBg:       "#0B0E1A",
    pageBgGrad:   "linear-gradient(160deg,#0B0E1A 0%,#0E1220 40%,#0B1020 100%)",
    loginBg:      "linear-gradient(160deg,#0B0E1A,#101525)",
    // Card / surface — glassmorphic
    cardBg:       "rgba(20,28,52,0.85)",
    cardBorder:   "rgba(99,120,200,0.18)",
    cardShadow:   "0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
    surfaceBg:    "rgba(14,20,38,0.9)",
    // Header
    headerBg:     "rgba(10,14,26,0.95)",
    headerBorder: "rgba(80,100,180,0.15)",
    headerShadow: "0 1px 24px rgba(0,0,0,0.6)",
    // Sidebar / nav
    navBg:        "rgba(10,14,26,0.97)",
    navBorder:    "rgba(80,100,180,0.15)",
    navActiveBg:  "rgba(99,102,241,0.15)",
    navActiveText:"#C7D2FE",
    navText:      "#B8CADF",
    // Table
    thBg:         "rgba(18,26,50,0.9)",
    thText:       "#A8B8D0",
    tdBorder:     "rgba(60,80,140,0.2)",
    tableStripe:  "rgba(99,102,241,0.03)",
    // Text
    textPrimary:  "#E8EEFF",
    textSecondary:"#A8B8D0",
    textMuted:    "#6B7FA0",
    textLight:    "#4A5E7E",
    // Inputs
    inputBg:      "rgba(8,12,24,0.8)",
    inputBorder:  "rgba(80,100,180,0.25)",
    inputText:    "#E8EEFF",
    // Borders
    border:       "rgba(80,100,180,0.2)",
    borderSubtle: "rgba(50,70,130,0.15)",
    // Brand — keep teal
    brand:        "#00D4D4",
    brandDark:    "#00A8A8",
    brandLight:   "rgba(0,212,212,0.1)",
    // Accents — indigo/purple like reference
    accent:       "#818CF8",
    accentPurple: "#A78BFA",
    accentBlue:   "#60A5FA",
    accentGreen:  "#34D399",
    // Chart colors matching reference purple/indigo palette
    chartPrimary: "#818CF8",
    chartSecondary:"#A78BFA",
    // Dropdown
    dropdownBg:   "rgba(14,20,42,0.98)",
    dropdownBorder:"rgba(80,100,180,0.2)",
    dropdownShadow:"0 20px 60px rgba(0,0,0,0.9)",
    // Misc
    badgeBg:      "rgba(99,102,241,0.15)",
    badgeText:    "#A5B4FC",
    hoverBg:      "rgba(99,102,241,0.08)",
    mapBg:        "rgba(18,26,50,0.8)",
    tooltipBg:    "rgba(14,20,42,0.98)",
    tooltipBorder:"#818CF8",
    scrollThumb:  "rgba(80,100,180,0.3)",
    // Toggle button style
    toggleBg:     "rgba(18,26,50,0.8)",
    toggleText:   "#7B8DB0",
  },

  // ── Schroders-inspired institutional theme ──────────────────────────────
  addepar: {
    id: "addepar",
    // ── Forthius Finalytics: clean white UI, cerulean accent ──
    pageBg:"#F2F4F7", pageBgGrad:"#F2F4F7", loginBg:"linear-gradient(160deg,#00205B 0%,#003087 50%,#0079C1 100%)",
    cardBg:"#FFFFFF", cardBorder:"#DDE2EC", cardShadow:"0 1px 4px rgba(0,32,91,0.07), 0 0 0 1px rgba(0,32,91,0.04)", surfaceBg:"#F7F9FC",
    headerBg:"#FFFFFF", headerBorder:"#DDE2EC", headerShadow:"0 1px 0 #DDE2EC",
    navBg:"transparent", navBorder:"transparent", navActiveBg:"rgba(0,121,193,0.09)", navActiveText:"#0079C1", navText:"#3D4F6E", navGroupText:"#8A9DB8",
    thBg:"#F2F4F7", thText:"#5A6B8A", tdBorder:"#EAEEf5", tableStripe:"rgba(0,32,91,0.02)",
    textPrimary:"#0D1B35", textSecondary:"#3D4F6E", textMuted:"#7A8FAD", textLight:"#B0BFCF",
    inputBg:"#FFFFFF", inputBorder:"#C8D3E2", inputText:"#0D1B35",
    border:"#DDE2EC", borderSubtle:"#EEF1F7",
    brand:"#0079C1", brandDark:"#005E9A", brandLight:"rgba(0,121,193,0.08)",
    accent:"#0079C1",
    dropdownBg:"#FFFFFF", dropdownBorder:"#DDE2EC", dropdownShadow:"0 8px 32px rgba(0,32,91,0.12)",
    badgeBg:"#E8F3FB", badgeText:"#005E9A", hoverBg:"rgba(0,121,193,0.06)",
    mapBg:"#dbeafe", tooltipBg:"#00205B", tooltipBorder:"#0079C1",
    scrollThumb:"#C8D3E2", toggleBg:"#F2F4F7", toggleText:"#3D4F6E",
    // Login page uses navy gradient — text needs to be light
    loginCardBg:"rgba(255,255,255,0.06)", loginCardBorder:"rgba(255,255,255,0.12)",
    dark: false,
  },

  // ── 5 LIGHT PALETTE VARIANTS ──
  light_indigo: {
    id: "light_indigo", _palette: "indigo", _mode: "light",
    pageBg: "#F0F2FB", pageBgGrad: "linear-gradient(160deg,#F0F2FB 0%,#E8EBFA 100%)", loginBg: "linear-gradient(160deg,#F0F2FB,#E8EBFA)",
    cardBg: "#FFFFFF", cardBorder: "rgba(80,100,220,0.14)", cardShadow: "0 2px 12px rgba(79,99,232,0.08)", surfaceBg: "#F8F9FE",
    headerBg: "rgba(255,255,255,0.97)", headerBorder: "rgba(80,100,220,0.14)", headerShadow: "0 2px 8px rgba(79,99,232,0.07)",
    navBg: "#FFFFFF", navBorder: "rgba(80,100,220,0.14)", navActiveBg: "rgba(79,99,232,0.10)", navActiveText: "#4F63E8", navText: "#5A6282",
    thBg: "#EEF0FF", thText: "#3A4580", tdBorder: "rgba(80,100,220,0.10)", tableStripe: "rgba(79,99,232,0.02)",
    textPrimary: "#1A1F3C", textSecondary: "#5A6282", textMuted: "#9AA0C0", textLight: "#C0C6E0",
    inputBg: "#F8F9FE", inputBorder: "rgba(80,100,220,0.18)", inputText: "#1A1F3C",
    border: "rgba(80,100,220,0.14)", borderSubtle: "rgba(80,100,220,0.07)",
    brand: "#4F63E8", brandDark: "#3B4ED0", brandLight: "rgba(79,99,232,0.08)",
    accent: "#9333EA", accentPurple: "#9333EA", accentBlue: "#4F63E8", accentGreen: "#059669",
    dropdownBg: "#FFFFFF", dropdownBorder: "rgba(80,100,220,0.18)", dropdownShadow: "0 20px 50px rgba(79,99,232,0.15)",
    badgeBg: "rgba(79,99,232,0.10)", badgeText: "#4F63E8", hoverBg: "rgba(79,99,232,0.07)",
    mapBg: "#EEF0FF", tooltipBg: "#FFFFFF", tooltipBorder: "#4F63E8", scrollThumb: "rgba(80,100,220,0.20)",
    toggleBg: "rgba(79,99,232,0.07)", toggleText: "#5A6282",
    chartPrimary: "#4F63E8", chartSecondary: "#9333EA",
  },
};

// Theme context — lets deeply nested components read T tokens without prop drilling
const ThemeContext = React.createContext(THEMES.light);
const useTheme = () => React.useContext(ThemeContext);

// ─────────────── STYLES ───────────────
// _T is a mutable reference to the active theme, updated on every theme change.
// card/inp/th/td/badge are functions that read _T at call-time so they always
// reflect the current theme without needing to be changed at every call site.
let _T = THEMES.light;
const setActiveTheme = (t) => { _T = t; };

const card  = (extra={}) => ({
  background: _T.cardBg,
  border: `1px solid ${_T.cardBorder}`,
  borderRadius: 12,
  padding: "20px 22px",
  boxShadow: _T.cardShadow,
  backdropFilter: "blur(12px)",
  ...extra
});
const inp   = (extra={}) => ({ background:_T.inputBg, border:`1px solid ${_T.inputBorder}`, borderRadius:8, color:_T.inputText, padding:"7px 12px", fontSize:13, width:"100%", ...extra });
const th    = (extra={}) => ({
  background: _T.thBg,
  color: _T.thText,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  padding: "9px 14px",
  textAlign: "left",
  fontWeight: 700,
  whiteSpace: "nowrap",
  borderBottom: `2px solid ${_T.cardBorder}`,
  ...extra
});
const td    = (extra={}) => ({
  padding: "10px 14px",
  color: _T.textPrimary,
  fontSize: 12.5,
  borderBottom: `1px solid ${_T.tdBorder}`,
  ...extra
});
const badge = (col) => ({
  background: col ? `${col}14` : _T.badgeBg,
  padding: "2px 9px",
  borderRadius: 20,
  fontSize: 10.5,
  whiteSpace: "nowrap",
  color: col||_T.badgeText,
  fontWeight: 600,
  border: `1px solid ${col ? `${col}35` : "transparent"}`
});

// ── Class-color mapping for asset-class chips ──
// Used in renderHoldingsTable and elsewhere so a chip's color is meaningful at a glance.
// Returns null for unknown classes → falls back to neutral badge.
const classChipColor = (cls) => {
  if (!cls) return null;
  const c = String(cls).toLowerCase();
  if (c.includes("indian mutual fund") || c === "mutual funds") return "#0F6E56";        // teal — Indian MF
  if (c.includes("global mutual fund"))                          return "#5347B0";        // purple — Global MF
  if (c.includes("cash") || c.includes("bank balance"))          return "#6B6B6B";        // gray — Cash
  if (c.includes("equit"))                                       return "#1E6FB8";        // blue — Equities
  if (c.includes("reit") || c.includes("real estate"))           return "#B97309";        // amber — REITs / RE
  if (c.includes("fixed income") || c.includes("bond"))          return "#6B5A3E";        // brown — Fixed Income
  if (c.includes("time deposit"))                                return "#807060";        // taupe — Time Deposits
  if (c.includes("crypto") || c.includes("digital"))             return "#A33F7A";        // pink — Crypto
  if (c.includes("art") || c.includes("collectible"))            return "#9C5A2B";        // sienna — Art
  if (c.includes("alternat") || c.includes("private equity") || c.includes("hedge"))
                                                                  return "#3C7E3C";        // green — Alternatives
  if (c.includes("commodity") || c.includes("gold") || c.includes("silver"))
                                                                  return "#A87C2A";        // gold — Commodities
  return null; // unknown — fall back to neutral
};

// ─────────────── INDIAN MUTUAL FUND POPULAR SCHEMES (AMFI codes) ───────────────
// Used as instant client-side fallback while /api/mf-search loads.
// Source: AMFI registered scheme codes (as of 2024). Full list via mfapi.in/mf
const POPULAR_MF_SCHEMES = [
  // Parag Parikh
  {amfiCode:122639,name:"Parag Parikh Flexi Cap Fund - Direct Plan - Growth",house:"PPFAS"},
  {amfiCode:118989,name:"Parag Parikh Flexi Cap Fund - Regular Plan - Growth",house:"PPFAS"},
  {amfiCode:149645,name:"Parag Parikh Conservative Hybrid Fund - Direct Growth",house:"PPFAS"},
  {amfiCode:149646,name:"Parag Parikh Conservative Hybrid Fund - Regular Growth",house:"PPFAS"},
  {amfiCode:149647,name:"Parag Parikh Liquid Fund - Direct Growth",house:"PPFAS"},
  {amfiCode:149648,name:"Parag Parikh Liquid Fund - Regular Growth",house:"PPFAS"},
  {amfiCode:152700,name:"Parag Parikh Overnight Fund - Direct Growth",house:"PPFAS"},
  {amfiCode:152701,name:"Parag Parikh Overnight Fund - Regular Growth",house:"PPFAS"},
  // SBI
  {amfiCode:125497,name:"SBI Bluechip Fund - Direct Plan - Growth",house:"SBI MF"},
  {amfiCode:119598,name:"SBI Bluechip Fund - Regular Plan - Growth",house:"SBI MF"},
  {amfiCode:120716,name:"SBI Small Cap Fund - Direct Plan - Growth",house:"SBI MF"},
  {amfiCode:111696,name:"SBI Small Cap Fund - Regular Plan - Growth",house:"SBI MF"},
  {amfiCode:130503,name:"SBI Contra Fund - Direct Plan - Growth",house:"SBI MF"},
  {amfiCode:100341,name:"SBI Contra Fund - Regular Plan - Growth",house:"SBI MF"},
  {amfiCode:100340,name:"SBI Magnum Midcap Fund - Regular Growth",house:"SBI MF"},
  {amfiCode:120830,name:"SBI Magnum Midcap Fund - Direct Growth",house:"SBI MF"},
  {amfiCode:120821,name:"SBI Equity Hybrid Fund - Direct Growth",house:"SBI MF"},
  {amfiCode:100342,name:"SBI Equity Hybrid Fund - Regular Growth",house:"SBI MF"},
  {amfiCode:120822,name:"SBI Long Term Equity Fund - Direct Growth",house:"SBI MF"},
  // HDFC
  {amfiCode:118989,name:"HDFC Mid-Cap Opportunities Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:118990,name:"HDFC Mid-Cap Opportunities Fund - Regular Growth",house:"HDFC MF"},
  {amfiCode:118994,name:"HDFC Flexi Cap Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:118993,name:"HDFC Flexi Cap Fund - Regular Growth",house:"HDFC MF"},
  {amfiCode:118551,name:"HDFC Top 100 Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:100017,name:"HDFC Top 100 Fund - Regular Growth",house:"HDFC MF"},
  {amfiCode:120594,name:"HDFC Small Cap Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:120592,name:"HDFC Small Cap Fund - Regular Growth",house:"HDFC MF"},
  {amfiCode:118553,name:"HDFC Balanced Advantage Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:100019,name:"HDFC Balanced Advantage Fund - Regular Growth",house:"HDFC MF"},
  // ICICI Prudential
  {amfiCode:120587,name:"ICICI Pru Bluechip Fund - Direct Growth",house:"ICICI Pru MF"},
  {amfiCode:120586,name:"ICICI Pru Bluechip Fund - Regular Growth",house:"ICICI Pru MF"},
  {amfiCode:120589,name:"ICICI Pru Value Discovery Fund - Direct Growth",house:"ICICI Pru MF"},
  {amfiCode:100070,name:"ICICI Pru Value Discovery Fund - Regular Growth",house:"ICICI Pru MF"},
  {amfiCode:120591,name:"ICICI Pru Midcap Fund - Direct Growth",house:"ICICI Pru MF"},
  {amfiCode:100072,name:"ICICI Pru Midcap Fund - Regular Growth",house:"ICICI Pru MF"},
  {amfiCode:120590,name:"ICICI Pru Balanced Advantage Fund - Direct Growth",house:"ICICI Pru MF"},
  {amfiCode:100071,name:"ICICI Pru Balanced Advantage Fund - Regular Growth",house:"ICICI Pru MF"},
  {amfiCode:120588,name:"ICICI Pru Technology Fund - Direct Growth",house:"ICICI Pru MF"},
  {amfiCode:100075,name:"ICICI Pru Technology Fund - Regular Growth",house:"ICICI Pru MF"},
  // Nippon India
  {amfiCode:118701,name:"Nippon India Small Cap Fund - Direct Growth",house:"Nippon MF"},
  {amfiCode:118700,name:"Nippon India Small Cap Fund - Regular Growth",house:"Nippon MF"},
  {amfiCode:118706,name:"Nippon India Growth Fund - Direct Growth",house:"Nippon MF"},
  {amfiCode:118705,name:"Nippon India Growth Fund - Regular Growth",house:"Nippon MF"},
  {amfiCode:118703,name:"Nippon India Multicap Fund - Direct Growth",house:"Nippon MF"},
  {amfiCode:118702,name:"Nippon India Multicap Fund - Regular Growth",house:"Nippon MF"},
  // Axis
  {amfiCode:120503,name:"Axis Bluechip Fund - Direct Growth",house:"Axis MF"},
  {amfiCode:120504,name:"Axis Bluechip Fund - Regular Growth",house:"Axis MF"},
  {amfiCode:125354,name:"Axis Small Cap Fund - Direct Growth",house:"Axis MF"},
  {amfiCode:125355,name:"Axis Small Cap Fund - Regular Growth",house:"Axis MF"},
  {amfiCode:120505,name:"Axis Midcap Fund - Direct Growth",house:"Axis MF"},
  {amfiCode:120506,name:"Axis Midcap Fund - Regular Growth",house:"Axis MF"},
  {amfiCode:120507,name:"Axis Long Term Equity Fund - Direct Growth",house:"Axis MF"},
  {amfiCode:112254,name:"Axis Long Term Equity Fund - Regular Growth",house:"Axis MF"},
  {amfiCode:120508,name:"Axis Flexi Cap Fund - Direct Growth",house:"Axis MF"},
  // Mirae
  {amfiCode:118834,name:"Mirae Asset Large Cap Fund - Direct Growth",house:"Mirae MF"},
  {amfiCode:118835,name:"Mirae Asset Large Cap Fund - Regular Growth",house:"Mirae MF"},
  {amfiCode:118836,name:"Mirae Asset Emerging Bluechip Fund - Direct Growth",house:"Mirae MF"},
  {amfiCode:118837,name:"Mirae Asset Emerging Bluechip Fund - Regular Growth",house:"Mirae MF"},
  {amfiCode:140816,name:"Mirae Asset Tax Saver Fund - Direct Growth",house:"Mirae MF"},
  // Kotak
  {amfiCode:120255,name:"Kotak Emerging Equity Fund - Direct Growth",house:"Kotak MF"},
  {amfiCode:120256,name:"Kotak Emerging Equity Fund - Regular Growth",house:"Kotak MF"},
  {amfiCode:120258,name:"Kotak Flexicap Fund - Direct Growth",house:"Kotak MF"},
  {amfiCode:100233,name:"Kotak Flexicap Fund - Regular Growth",house:"Kotak MF"},
  {amfiCode:120257,name:"Kotak Small Cap Fund - Direct Growth",house:"Kotak MF"},
  {amfiCode:109818,name:"Kotak Small Cap Fund - Regular Growth",house:"Kotak MF"},
  // UTI
  {amfiCode:120421,name:"UTI Nifty 50 Index Fund - Direct Growth",house:"UTI MF"},
  {amfiCode:120420,name:"UTI Nifty 50 Index Fund - Regular Growth",house:"UTI MF"},
  {amfiCode:120419,name:"UTI Flexi Cap Fund - Direct Growth",house:"UTI MF"},
  {amfiCode:120418,name:"UTI Flexi Cap Fund - Regular Growth",house:"UTI MF"},
  {amfiCode:120422,name:"UTI Mid Cap Fund - Direct Growth",house:"UTI MF"},
  {amfiCode:120423,name:"UTI Mid Cap Fund - Regular Growth",house:"UTI MF"},
  // DSP
  {amfiCode:119206,name:"DSP Midcap Fund - Direct Growth",house:"DSP MF"},
  {amfiCode:119205,name:"DSP Midcap Fund - Regular Growth",house:"DSP MF"},
  {amfiCode:119207,name:"DSP Small Cap Fund - Direct Growth",house:"DSP MF"},
  {amfiCode:119208,name:"DSP Small Cap Fund - Regular Growth",house:"DSP MF"},
  {amfiCode:119209,name:"DSP Flexi Cap Fund - Direct Growth",house:"DSP MF"},
  // Franklin
  {amfiCode:118632,name:"Franklin India Prima Fund - Direct Growth",house:"Franklin MF"},
  {amfiCode:118633,name:"Franklin India Prima Fund - Regular Growth",house:"Franklin MF"},
  {amfiCode:118634,name:"Franklin India Bluechip Fund - Direct Growth",house:"Franklin MF"},
  {amfiCode:118635,name:"Franklin India Flexi Cap Fund - Regular Growth",house:"Franklin MF"},
  // Tata
  {amfiCode:120175,name:"Tata Small Cap Fund - Direct Growth",house:"Tata MF"},
  {amfiCode:120176,name:"Tata Small Cap Fund - Regular Growth",house:"Tata MF"},
  {amfiCode:120173,name:"Tata Digital India Fund - Direct Growth",house:"Tata MF"},
  {amfiCode:120174,name:"Tata Digital India Fund - Regular Growth",house:"Tata MF"},
  // Bandhan
  {amfiCode:119820,name:"Bandhan Small Cap Fund - Direct Growth",house:"Bandhan MF"},
  {amfiCode:119821,name:"Bandhan Small Cap Fund - Regular Growth",house:"Bandhan MF"},
  // Nifty index funds
  {amfiCode:120716,name:"HDFC Nifty 50 Index Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:120717,name:"HDFC Nifty 50 Index Fund - Regular Growth",house:"HDFC MF"},
  {amfiCode:120718,name:"HDFC Nifty Next 50 Index Fund - Direct Growth",house:"HDFC MF"},
  {amfiCode:135782,name:"Motilal Oswal Nifty 500 Index Fund - Direct Growth",house:"Motilal MF"},
  {amfiCode:135783,name:"Motilal Oswal Nifty Midcap 150 Index Fund - Direct Growth",house:"Motilal MF"},
  {amfiCode:147622,name:"Motilal Oswal Nifty Smallcap 250 Index Fund - Direct Growth",house:"Motilal MF"},
  {amfiCode:120468,name:"Navi Nifty 50 Index Fund - Direct Growth",house:"Navi MF"},
];

// Client-side MF fuzzy search — instant results before API responds
function searchPopularMF(query) {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/).filter(w => w.length > 1);
  return POPULAR_MF_SCHEMES
    .map(s => {
      const name = s.name.toLowerCase();
      const house = s.house.toLowerCase();
      let score = 0;
      for (const w of words) {
        if (name.includes(w)) score += 2;
        if (house.includes(w)) score += 1;
      }
      if (name.includes(q)) score += 3;
      return { ...s, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 12)
    .map(s => ({
      symbol:       String(s.amfiCode),
      name:         s.name,
      exchange:     "AMFI",
      currency:     "INR",
      type:         "Mutual Fund",
      isMutualFund: true,
      amfiCode:     s.amfiCode,
      house:        s.house,
    }));
}


// ─────────────── CHANGE PASSWORD FORM COMPONENT ───────────────
function ChangePasswordForm({ isAdmin, loggedInClientId, clientLogins, setClientLogins, onSuccess }) {
  const T = useTheme();
  const [old, setOld]         = useState("");
  const [nw,  setNw]          = useState("");
  const [conf,setConf]        = useState("");
  const [err, setErr]         = useState("");
  const [ok,  setOk]          = useState(false);

  const fieldInp = {width:"100%",boxSizing:"border-box",background:T.inputBg,border:`1px solid ${T.inputBorder}`,
    borderRadius:8,color:T.textPrimary,padding:"9px 12px",fontSize:13,outline:"none",fontFamily:"'Inter', system-ui, sans-serif"};
  const labelSt  = {fontSize:11,color:T.textSecondary,fontWeight:600,textTransform:"uppercase",
    letterSpacing:"0.07em",marginBottom:5,display:"block"};

  const submit = async () => {
    setErr(""); setOk(false);
    if (!old || !nw || !conf) { setErr("All fields are required."); return; }
    if (nw.length < 8)         { setErr("New password must be at least 8 characters."); return; }
    if (nw !== conf)           { setErr("New passwords don't match."); return; }
    if (isAdmin) {
      // Admin password stored in localStorage (not Supabase - FK constraint prevents __admin__ as client_id)
      const stored = (() => { try { return JSON.parse(localStorage.getItem("fw_admin_cred") || "null"); } catch { return null; } })();
      const currentPwd = stored?.password || "admin123";
      if (!await verifyPassword(old, currentPwd)) { setErr("Current password is incorrect."); return; }
      const hashedNew = await makePasswordHash(nw);
      const updated = { loginId: stored?.loginId || "kshitij.lodha", password: hashedNew, updatedAt: new Date().toISOString() };
      // Save to localStorage
      try { localStorage.setItem("fw_admin_cred", JSON.stringify(updated)); } catch {}
      // Save to Supabase profiles so it syncs across devices
      upsertProfile("__admin_cred__", updated).catch(()=>{});
      // Also update in-memory clientLogins so current session works
      setClientLogins(p => ({ ...p, "__admin__": updated }));
      setOk(true); setOld(""); setNw(""); setConf("");
      if (onSuccess) setTimeout(onSuccess, 1200);
      return;
    }
    const cred = clientLogins[loggedInClientId];
    if (!cred) { setErr("Account not found."); return; }
    if (!await verifyPassword(old, cred.password)) { setErr("Current password is incorrect."); return; }
    const hashedNew = await makePasswordHash(nw);
    const updated = {...(clientLogins[loggedInClientId]||{}), password: hashedNew};
    upsertClientLogin(loggedInClientId, updated).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
    setClientLogins(p=>({...p,[loggedInClientId]:updated}));
    // Clear session so next login uses new password
    try { localStorage.removeItem('fw_session_v1'); } catch {}
    setOk(true); setOld(""); setNw(""); setConf("");
    if (onSuccess) setTimeout(onSuccess, 1200);
  };

  return (
    <div>
      {ok && (
        <div style={{marginBottom:14,padding:"10px 14px",background:"rgba(22,163,74,0.1)",
          border:"1px solid rgba(22,163,74,0.25)",borderRadius:8,fontSize:12,color:"#1D9E75",
          display:"flex",alignItems:"center",gap:8}}>
          ✓ Password updated successfully!
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <label style={labelSt}>Current Password</label>
          <input type="password" style={fieldInp} placeholder="Enter current password"
            value={old} onChange={e=>{setOld(e.target.value);setErr("");setOk(false);}}/>
        </div>
        <div>
          <label style={labelSt}>New Password</label>
          <input type="password" style={fieldInp} placeholder="At least 8 characters"
            value={nw} onChange={e=>{setNw(e.target.value);setErr("");setOk(false);}}/>
        </div>
        <div>
          <label style={labelSt}>Confirm New Password</label>
          <input type="password" style={fieldInp} placeholder="Repeat new password"
            value={conf} onChange={e=>{setConf(e.target.value);setErr("");setOk(false);}}
            onKeyDown={e=>e.key==="Enter"&&submit()}/>
        </div>
        {err && (
          <div style={{fontSize:12,color:"#A32D2D",display:"flex",alignItems:"center",gap:6}}>
            <span>⚠</span>{err}
          </div>
        )}
        <button onClick={submit}
          style={{background:"#007878",border:"none",borderRadius:8,
            color:"white",padding:"10px",cursor:"pointer",fontSize:13,fontWeight:600,marginTop:4}}>
          Update Password
        </button>
      </div>
    </div>
  );
}


// ─────────────── AVATAR MENU COMPONENT ───────────────
function AvatarMenu({ isAdmin, loggedInClientId, profileData, avatarMenuOpen, setAvatarMenuOpen, onProfile, onChangePassword, onSignOut, clients=[], T: TProp, themeId, isLightMode, toggleTheme, dc, setDc, refreshing, onRefresh, onReport }) {
  const TCtx = useTheme();
  const T = TProp || TCtx;
  const userId    = isAdmin ? "admin" : loggedInClientId;
  const profile   = profileData[userId] || {};
  const clientObj = loggedInClientId ? clients.find(c => c.id === loggedInClientId) : null;
  // Find the admin's display name from their login record
  const adminEntry = isAdmin
    ? Object.entries(profileData).find(([k]) => k === "admin" || k.startsWith("__admin__"))
    : null;
  const adminDisplayName = adminEntry?.[1]?.displayName || "Kshitij Lodha";
  const dispName  = isAdmin ? adminDisplayName : (clientObj?.name || "User");
  const initials  = isAdmin ? dispName.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase() || "KL" : (clientObj?.avatar || dispName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase());
  const hasPhoto  = !!profile.photoDataUrl;
  const avatarBg  = isAdmin ? "#007878" : "#007878";
  const btnRef    = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });

  const openMenu = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
    setAvatarMenuOpen(p => !p);
  };

  const avatarCircle = (size) => (
    <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
      background: hasPhoto ? "#000" : avatarBg, border: "2px solid rgba(0,121,193,0.25)",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      {hasPhoto
        ? <img src={profile.photoDataUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span style={{ fontSize: size * 0.36, fontWeight: 700, color: "white" }}>{initials}</span>}
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      <button ref={btnRef} onClick={openMenu}
        style={{ display: "flex", alignItems: "center", gap: 9, background: "transparent",
          border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "5px 10px 5px 5px",
          cursor: "pointer", outline: "none", transition: "border-color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#007878"}
        onMouseLeave={e => e.currentTarget.style.borderColor = T.cardBorder}>
        {avatarCircle(32)}
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, lineHeight: 1.1 }}>{dispName.split(" ")[0]}</div>
          <div style={{ fontSize: 9, color: "#007878", textTransform: "uppercase", letterSpacing: "0.06em" }}>{isAdmin ? "Admin" : "Client"}</div>
        </div>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.45, marginLeft: 2 }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke={T.badgeBg} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {avatarMenuOpen && (
        <React.Fragment>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setAvatarMenuOpen(false)} />
          <div style={{ position: "fixed", top: dropPos.top, right: dropPos.right, zIndex: 9999,
            background: T.dropdownBg, border: `1px solid ${T.dropdownBorder}`, borderRadius: 14,
            boxShadow: T.dropdownShadow, minWidth: 210, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.cardBorder}`,
              display: "flex", alignItems: "center", gap: 10 }}>
              {avatarCircle(42)}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary, whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis" }}>{dispName}</div>
                <div style={{ fontSize: 10, color: T.brand, marginTop: 2 }}>{isAdmin ? "Administrator" : "Client Account"}</div>
                {profile.email && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.email}</div>}
              </div>
            </div>
            {[
              { icon: "👤", label: "Profile & KYC", action: onProfile },
              { icon: "🔒", label: "Change Password", action: onChangePassword },
            ].map(item => (
              <button key={item.label} onClick={item.action}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "11px 16px", background: "transparent", border: "none",
                  color: T.textSecondary, cursor: "pointer", fontSize: 13, textAlign: "left" }}
                onMouseEnter={e => e.currentTarget.style.background = T.hoverBg}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>{item.label}
              </button>
            ))}

            {/* ── Theme & Settings section ── */}
            {(toggleTheme || onReport) && (
              <div style={{ borderTop: `1px solid ${T.cardBorder}`, padding: "10px 14px 8px" }}>
                {/* Theme mode toggle */}
                {toggleTheme && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>Appearance</div>
                    <div style={{ display: "flex", gap: 4, marginBottom: isLightMode ? 8 : 0 }}>
                      {[
                        { id: "light", label: "Light", iconName: "sun",  active: isLightMode },
                        { id: "dark",  label: "Dark",  iconName: "moon", active: !isLightMode },
                      ].map(m => (
                        <button key={m.id}
                          onClick={() => { if ((m.id === "dark") !== !isLightMode) toggleTheme(); }}
                          style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                            padding: "6px 8px", borderRadius: 7, border: `1px solid ${m.active ? T.brand : T.cardBorder}`,
                            background: m.active ? T.navActiveBg : "transparent",
                            color: m.active ? T.brand : T.textMuted,
                            cursor: "pointer", fontSize: 11, fontWeight: m.active ? 600 : 400 }}>
                          <NavIcon name={m.iconName} size={13} color={m.active ? T.brand : T.textMuted} strokeWidth={1.8} />
                          <span>{m.label}</span>
                        </button>
                      ))}
                    </div>
                    {/* Palette swatches removed — single default colour only */}
                  </div>
                )}
                {/* Report button */}
                {onReport && (
                  <button onClick={() => { onReport(); setAvatarMenuOpen(false); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 8,
                      padding: "7px 10px", borderRadius: 7, border: `1px solid ${T.cardBorder}`,
                      background: T.borderSubtle, color: T.textSecondary,
                      cursor: "pointer", fontSize: 12, fontWeight: 500, marginTop: 2 }}>
                    <span style={{ fontSize: 14 }}>📄</span> Generate Report
                  </button>
                )}
              </div>
            )}

            <div style={{ borderTop: `1px solid ${T.cardBorder}`, margin: "2px 0" }} />
            <button onClick={onSignOut}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "11px 16px", background: "transparent", border: "none",
                color: "#A32D2D", cursor: "pointer", fontSize: 13, textAlign: "left" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(220,38,38,0.07)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

// ─────────────── PROFILE MODAL COMPONENT ───────────────
function ProfileModal({ isAdmin, loggedInClientId, profileData, setProfileData, profileTab, setProfileTab, clientLogins, setClientLogins, onClose, onSignOut, onSave, onToggleTheme, onSetTheme, onSetThemeAuto, currentThemeId, currentThemePref, clients=[], notifPrefs={}, onNotifPrefsChange }) {
  const T = useTheme();
  const userId    = isAdmin ? "admin" : loggedInClientId;
  const pdata     = profileData[userId] || {};
  const clientObj = loggedInClientId ? clients.find(c => c.id === loggedInClientId) : null;
  const adminPEntry = isAdmin
    ? Object.entries(profileData).find(([k]) => k === "admin" || k.startsWith("__admin__"))
    : null;
  const adminPDisplayName = adminPEntry?.[1]?.displayName || "Kshitij Lodha";
  const dispName  = isAdmin ? adminPDisplayName : (clientObj?.name || "User");
  const initials  = isAdmin ? dispName.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()||"KL" : (clientObj?.avatar || dispName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase());
  const hasPhoto  = !!pdata.photoDataUrl;
  const avatarBg  = isAdmin ? "#007878" : "#007878";

  const set = (k, v) => setProfileData(p => ({ ...p, [userId]: { ...p[userId], [k]: v } }));

  const labelSt = { fontSize: 11, color: T.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5, display: "block" };
  const fieldI = { width: "100%", boxSizing: "border-box", background: T.inputBg,
    border: `1px solid ${T.inputBorder}`, borderRadius: 8, color: T.inputText, padding: "9px 12px",
    fontSize: 13, outline: "none", fontFamily: "'Inter', system-ui, sans-serif" };

  const sectionHead = (txt) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, textTransform: "uppercase",
      letterSpacing: "0.1em", marginBottom: 12, paddingBottom: 7, paddingTop: 20, marginTop: 4 }}>{txt}</div>
  );

  const TABS = [
    { id: "personal",      label: "Personal",      icon: "👤" },
    { id: "kyc",           label: "KYC & ID",      icon: "🆔" },
    { id: "security",      label: "Security",      icon: "🔒" },
    { id: "preferences",   label: "Preferences",   icon: "🎨" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 18,
        width: "100%", maxWidth: "min(580px,96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 30px 80px rgba(0,0,0,0.85)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 40px", borderBottom: `1px solid ${T.cardBorder}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{ width: 58, height: 58, borderRadius: "50%", overflow: "hidden",
                background: hasPhoto ? "#000" : avatarBg, border: "2px solid rgba(0,121,193,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {hasPhoto
                  ? <img src={pdata.photoDataUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 19, fontWeight: 700, color: "white" }}>{initials}</span>}
              </div>
              <label htmlFor="prof-photo" title="Upload photo"
                style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22,
                  borderRadius: "50%", background: "#007878", border: "2px solid #FFFFFF",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", fontSize: 11 }}>
                📷
                <input id="prof-photo" type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const r = new FileReader();
                    r.onload = ev => set("photoDataUrl", ev.target.result);
                    r.readAsDataURL(f);
                  }} />
              </label>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>{dispName}</div>
              <div style={{ fontSize: 11, color: T.brand, marginTop: 2 }}>
                {isAdmin ? "Administrator" : `${clientObj?.role || "Client"}`}
              </div>
              <label htmlFor="prof-photo" style={{ fontSize: 10, color: T.textLight,
                marginTop: 3, cursor: "pointer", textDecoration: "underline", display: "inline-block" }}>
                {hasPhoto ? "Change photo" : "Upload photo"}
              </label>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(220,38,38,0.1)",
            border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#A32D2D",
            width: 34, height: 34, cursor: "pointer", fontSize: 17,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: "flex", padding: "0 40px", borderBottom: "1px solid #DDD0C0", flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setProfileTab(t.id)}
              style={{ padding: "13px 18px", background: "none", border: "none",
                borderBottom: `2px solid ${profileTab === t.id ? "#007878" : "transparent"}`,
                color: profileTab === t.id ? "#007878" : T.textSecondary, fontSize: 13,
                fontWeight: profileTab === t.id ? 600 : 400, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "16px 40px 40px", flex: 1, scrollbarGutter: "stable" }}>

          {/* Personal */}
          {profileTab === "personal" && (
            <div style={{padding:"0 40px"}}>
              {sectionHead('Contact Information')}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 4 }}>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={labelSt}>Email Address</label>
                  <input style={fieldI} type="email" placeholder="your@email.com"
                    value={pdata.email || ""} onChange={e => set("email", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Phone Number</label>
                  <input style={fieldI} type="tel" placeholder="+91 98765 43210"
                    value={pdata.phone || ""} onChange={e => set("phone", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Date of Birth</label>
                  <input style={fieldI} type="date"
                    value={pdata.dob || clientObj?.dob || ""} onChange={e => set("dob", e.target.value)} />
                </div>
              </div>
              {sectionHead('Address')}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 4 }}>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={labelSt}>Street / Unit</label>
                  <input style={fieldI} placeholder="12 Marine Drive, Floor 7"
                    value={pdata.address || ""} onChange={e => set("address", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>City</label>
                  <input style={fieldI} placeholder="Mumbai"
                    value={pdata.city || ""} onChange={e => set("city", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Postcode / ZIP</label>
                  <input style={fieldI} placeholder="400001"
                    value={pdata.postcode || ""} onChange={e => set("postcode", e.target.value)} />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={labelSt}>Country of Residence</label>
                  <input style={fieldI} placeholder="India"
                    value={pdata.country || ""} onChange={e => set("country", e.target.value)} />
                </div>
              </div>
              {sectionHead('Professional')}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
                <div>
                  <label style={labelSt}>Occupation</label>
                  <input style={fieldI} placeholder="e.g. Entrepreneur"
                    value={pdata.occupation || ""} onChange={e => set("occupation", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Employer / Company</label>
                  <input style={fieldI} placeholder="e.g. Sharma Group"
                    value={pdata.employer || ""} onChange={e => set("employer", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Annual Income Band</label>
                  <select style={fieldI} value={pdata.annualIncome || ""} onChange={e => set("annualIncome", e.target.value)}>
                    <option value="">Select…</option>
                    {["< ₹25L", "₹25L–₹1Cr", "₹1Cr–₹5Cr", "₹5Cr–₹25Cr", "> ₹25Cr", "Prefer not to say"].map(o => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Risk Profile</label>
                  <select style={fieldI} value={pdata.riskProfile || "Moderate"} onChange={e => set("riskProfile", e.target.value)}>
                    {["Conservative", "Moderate", "Aggressive", "Very Aggressive"].map(o => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* KYC */}
          {profileTab === "kyc" && (
            <div style={{padding:"0 40px"}}>
              {sectionHead('Identity Documents')}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 4 }}>
                <div>
                  <label style={labelSt}>PAN / Tax ID</label>
                  <input style={fieldI} placeholder="ABCDE1234F"
                    value={pdata.panNumber || ""} onChange={e => set("panNumber", e.target.value.toUpperCase())} />
                </div>
                <div>
                  <label style={labelSt}>Passport Number</label>
                  <input style={fieldI} placeholder="A1234567"
                    value={pdata.passportNumber || ""} onChange={e => set("passportNumber", e.target.value.toUpperCase())} />
                </div>
                <div>
                  <label style={labelSt}>Nationality</label>
                  <input style={fieldI} placeholder="Indian"
                    value={pdata.nationality || ""} onChange={e => set("nationality", e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Tax Residency</label>
                  <input style={fieldI} placeholder="India"
                    value={pdata.taxResidency || ""} onChange={e => set("taxResidency", e.target.value)} />
                </div>
              </div>
              {sectionHead('Compliance Status')}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                {[
                  { label: "KYC Verified",    status: "Verified",    color: "#1D9E75" },
                  { label: "AML Cleared",     status: "Cleared",     color: "#1D9E75" },
                  { label: "FATCA Filed",     status: "Submitted",   color: "#D97706" },
                  { label: "CRS Declared",    status: "Submitted",   color: "#D97706" },
                  { label: "Risk Assessment", status: "Completed",   color: "#1D9E75" },
                  { label: "Suitability",     status: "Due Jun '26", color: "#A32D2D" },
                ].map(item => (
                  <div key={item.label} style={{ background: "rgba(0,0,0,0.02)",
                    border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: T.textSecondary, marginBottom: 5 }}>{item.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: item.color }}>{item.status}</div>
                  </div>
                ))}
              </div>
              <div style={{ padding: "10px 14px", background: "rgba(59,130,246,0.05)",
                border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                ℹ For changes to official KYC records, contact your relationship manager.
              </div>
            </div>
          )}

          {/* Security */}
          {profileTab === "security" && (
            <div style={{padding:"0 40px"}}>
              {sectionHead('Login Credentials')}
              <div style={{ background: "rgba(0,0,0,0.02)", border: `1px solid ${T.cardBorder}`,
                borderRadius: 10, padding: "14px 16px", marginBottom: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 3 }}>Login ID</div>
                    {(function(){
                      const [editingLid, setEditingLid] = profileData._editingLid !== undefined
                        ? [profileData._editingLid, v => setProfileData(p=>({...p,_editingLid:v}))]
                        : [false, v => setProfileData(p=>({...p,_editingLid:v}))];
                      const [lidVal, setLidVal] = profileData._lidVal !== undefined
                        ? [profileData._lidVal, v => setProfileData(p=>({...p,_lidVal:v}))]
                        : [clientLogins[loggedInClientId]?.loginId||"", v => setProfileData(p=>({...p,_lidVal:v}))];
                      return editingLid ? (
                        <div style={{display:"flex",gap:6,alignItems:"center",marginTop:4}}>
                          <input value={lidVal} onChange={e=>setLidVal(e.target.value.toLowerCase().replace(/\s/g,""))}
                            autoFocus
                            style={{flex:1,background:T.inputBg,border:`1px solid ${T.brand}`,borderRadius:6,
                              color:T.textPrimary,padding:"5px 10px",fontSize:13,outline:"none",fontFamily:"'Inter', system-ui, sans-serif"}}/>
                          <button onClick={async ()=>{
                            const newId = lidVal.trim();
                            if (!newId||newId.length<3) { showToast("Login ID must be at least 3 characters","#A32D2D"); return; }
                            // Check uniqueness across all logins
                            const taken = Object.values(clientLogins).some(l=>l.loginId===newId && l !== clientLogins[loggedInClientId]);
                            if (taken) { showToast("Login ID already in use","#A32D2D"); return; }
                            const updated = {...(clientLogins[loggedInClientId]||{}), loginId:newId};
                            setClientLogins(p=>({...p,[loggedInClientId]:updated}));
                            try { await upsertClientLogin(loggedInClientId, updated); } catch {}
                            setEditingLid(false);
                            showToast("✓ Login ID updated","#1D9E75");
                          }} style={{background:"#007878",border:"none",borderRadius:6,
                            color:"white",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                          <button onClick={()=>setEditingLid(false)}
                            style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,
                              color:T.textSecondary,padding:"5px 10px",cursor:"pointer",fontSize:12}}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary, fontFamily: "'Inter', system-ui, sans-serif" }}>
                            {isAdmin ? "admin" : (clientLogins[loggedInClientId]?.loginId || "—")}
                          </div>
                          {!isAdmin && (
                            <button onClick={()=>{ setLidVal(clientLogins[loggedInClientId]?.loginId||""); setEditingLid(true); }}
                              style={{background:"rgba(0,121,193,0.08)",border:`1px solid ${T.brand}44`,borderRadius:5,
                                color:T.brand,padding:"2px 9px",cursor:"pointer",fontSize:11,fontWeight:600}}>Edit</button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <span style={{ fontSize: 10, background: "rgba(22,163,74,0.1)", color: "#1D9E75",
                    border: "1px solid rgba(22,163,74,0.25)", borderRadius: 6, padding: "3px 10px" }}>Active</span>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 3 }}>Last Login</div>
                  <div style={{ fontSize: 13, color: T.textSecondary }}>
                    {isAdmin
                      ? new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                      : (clientLogins[loggedInClientId]?.lastLogin || "—")}
                  </div>
                </div>
              </div>
              {sectionHead('Change Password')}
              <ChangePasswordForm
                isAdmin={isAdmin}
                loggedInClientId={loggedInClientId}
                clientLogins={clientLogins}
                setClientLogins={setClientLogins}
              />
              {sectionHead('Session')}
              <button onClick={onSignOut}
                style={{ display: "flex", alignItems: "center", gap: 9, background: "rgba(220,38,38,0.08)",
                  border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, color: "#A32D2D",
                  padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
            </div>
          )}
        </div>

          {/* Preferences */}
          {profileTab === "preferences" && (
            <div style={{padding:"0 40px"}}>
              {sectionHead("Appearance")}
              <div style={{marginBottom:24}}>
                <div style={{fontSize:13,color:T.textPrimary,fontWeight:600,marginBottom:12}}>Dashboard Theme</div>
                {/* Mode selector — compact icon-only pill: ☀ 🌙 ⏳ */}
                {(() => {
                  const isAuto  = currentThemePref === "auto";
                  const isDark  = !isAuto && currentThemeId === "dark";
                  const isLight = !isAuto && currentThemeId !== "dark";
                  const applyLight = () => {
                    const next = "light_indigo";
                    set("theme", next);
                    if (typeof onSave === "function") onSave(userId, {...(profileData[userId]||{}), theme:next});
                    if (typeof onSetTheme === "function") onSetTheme(next);
                  };
                  const applyDark = () => {
                    set("theme", "dark");
                    if (typeof onSave === "function") onSave(userId, {...(profileData[userId]||{}), theme:"dark"});
                    if (typeof onSetTheme === "function") onSetTheme("dark");
                  };
                  const applyAuto = () => {
                    set("theme", "auto");
                    if (typeof onSave === "function") onSave(userId, {...(profileData[userId]||{}), theme:"auto"});
                    if (typeof onSetThemeAuto === "function") onSetThemeAuto();
                  };
                  // Icon-only segmented control — three small SVG-icon buttons in a pill
                  const opts = [
                    { id:"light", iconName:"sun",       title:"Light",                     active:isLight, onClick:applyLight },
                    { id:"dark",  iconName:"moon",      title:"Dark",                      active:isDark,  onClick:applyDark  },
                    { id:"auto",  iconName:"autoTheme", title:"Auto (follows time of day)", active:isAuto,  onClick:applyAuto  },
                  ];
                  return (
                    <div style={{marginBottom:16}}>
                      <div style={{display:"inline-flex",gap:0,background:T.pageBg,
                        border:`1px solid ${T.border}`,borderRadius:8,padding:3}}>
                        {opts.map(o => (
                          <div key={o.id} onClick={o.onClick} title={o.title}
                            style={{width:36,height:32,display:"flex",alignItems:"center",
                              justifyContent:"center",borderRadius:5,cursor:"pointer",
                              transition:"all 0.12s",
                              background:o.active ? T.brand : "transparent"}}>
                            <NavIcon name={o.iconName} size={16}
                              color={o.active ? "#fff" : T.textSecondary}
                              strokeWidth={1.8} />
                          </div>
                        ))}
                      </div>
                      <div style={{marginTop:7,fontSize:11,color:T.textMuted}}>
                        {isAuto  ? "Auto: light 6am–6pm, dark in the evening." :
                         isDark  ? "Dark mode active." : "Light mode active."}
                      </div>
                    </div>
                  );
                })()}
                <div style={{marginTop:4,fontSize:11,color:T.textMuted}}>
                  Your theme preference is saved to your profile and will apply on all your logins.
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {profileTab === "notifications" && (
            <div style={{padding:"0 40px"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>Notification Preferences</div>
              <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                Control how and when you receive alerts.
              </div>
              {[
                {key:"priceAlerts",   label:"Price Alerts",
                  desc:"In-app toast when a watchlist item crosses its target price"},
                {key:"browserPush",   label:"Browser Notifications",
                  desc:"Native browser push for price alerts (requires permission)"},
                {key:"importSuccess", label:"Import Confirmations",
                  desc:"Toast notification when a CAS statement is imported successfully"},
                {key:"weeklyDigest",  label:"Weekly Digest",
                  desc:"Weekly portfolio summary (coming soon)"},
              ].map(({key,label,desc})=>(
                <div key={key} style={{display:"flex",alignItems:"flex-start",
                  justifyContent:"space-between",padding:"12px 0",
                  borderBottom:`1px solid ${T.borderSubtle}`,gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:2}}>{label}</div>
                    <div style={{fontSize:11,color:T.textMuted}}>{desc}</div>
                  </div>
                  <button
                    onClick={async()=>{
                      if (key==="browserPush" && !(notifPrefs||{})[key]) {
                        if ("Notification" in window) {
                          const perm = await Notification.requestPermission();
                          if (perm !== "granted") {
                            alert("Browser notifications blocked. Enable in your browser settings.");
                            return;
                          }
                        }
                      }
                      if (onNotifPrefsChange) onNotifPrefsChange(key, !(notifPrefs||{})[key]);
                    }}
                    style={{
                      width:44,height:24,borderRadius:12,border:"none",cursor:"pointer",
                      background:(notifPrefs||{})[key]?"#007878":"#D1D5DB",
                      transition:"background 0.2s",flexShrink:0,position:"relative",
                    }}>
                    <div style={{
                      position:"absolute",top:2,
                      left:(notifPrefs||{})[key]?20:2,
                      width:20,height:20,borderRadius:"50%",background:"white",
                      boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left 0.2s",
                    }}/>
                  </button>
                </div>
              ))}
              <div style={{marginTop:16,padding:"10px 12px",background:"rgba(0,120,120,0.06)",
                border:"1px solid rgba(0,121,193,0.2)",borderRadius:8,fontSize:11,color:T.textSecondary}}>
                💡 Email and WhatsApp alerts are coming soon. All current notifications are in-app only.
              </div>
            </div>
          )}

        {/* Footer */}
        {profileTab !== "security" && profileTab !== "preferences" && (
          <div style={{ padding: "16px 40px", borderTop: `1px solid ${T.cardBorder}`,
            display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
            <button onClick={onClose} style={{ background: T.borderSubtle, border: "1px solid #78716C",
              borderRadius: 8, color: T.textSecondary, padding: "9px 22px", cursor: "pointer", fontSize: 13 }}>
              Cancel
            </button>
            <button onClick={()=>{ onSave && onSave(userId, profileData[userId]||{}); onClose(); }} style={{ background: "#007878",
              border: "none", borderRadius: 8, color: "white", padding: "9px 24px",
              cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────── CHANGE PASSWORD MODAL COMPONENT ───────────────
function ChangePwModal({ isAdmin, loggedInClientId, clientLogins, setClientLogins, onClose }) {
  const T = useTheme();
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 16,
        width: "100%", maxWidth: 400, boxShadow: "0 30px 80px rgba(0,0,0,0.85)", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.cardBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary }}>Change Password</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(220,38,38,0.1)",
            border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, color: "#A32D2D",
            width: 30, height: 30, cursor: "pointer", fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: "24px" }}>
          <ChangePasswordForm
            isAdmin={isAdmin}
            loggedInClientId={loggedInClientId}
            clientLogins={clientLogins}
            setClientLogins={setClientLogins}
            onSuccess={onClose}
          />
        </div>
      </div>
    </div>
  );
}


// ─────────────── MAIN COMPONENT ───────────────

// ── PortfolioHealthScore — top-level component ──
// Computes a multi-dimensional health score from all available analysis
// ── Export holdings to CSV ─────────────────────────────────────
function exportHoldingsCSV(hwWithOwner, clients, families, dc, fxRates, FX_RATES_DEFAULT, convert, fmt) {
  console.log("[exportHoldingsCSV] input rows:", hwWithOwner?.length, "first item:", hwWithOwner?.[0]);
  if (!Array.isArray(hwWithOwner) || hwWithOwner.length === 0) {
    alert("No holdings to export. If you see holdings on screen but this is empty, please reload the page and try again.");
    return;
  }
  const toUSD2 = (val, cur) => val / (fxRates?.[cur] || FX_RATES_DEFAULT?.[cur] || 1);
  const headers = [
    "Name","Ticker","ISIN","Asset Class","Sector","Country","Exchange",
    "Currency","Quantity","Avg Cost Price","Current Price","Cost Value (Base)",
    "Market Value (Base)","P&L (Base)","Return %","Cap Size","Manager/Custodian",
    "Advisor","Owner","Family","Listed","As On Date"
  ];
  const today = new Date().toISOString().slice(0,10);
  const rows = hwWithOwner.map((h, idx) => {
    try {
      if (!h) { console.warn("[exportHoldingsCSV] null holding at idx", idx); return null; }
      const clientId = h.clientId || null;
      const client = clientId ? clients.find(c => c.id === clientId) : null;
      const family = client ? families.find(f => f.id === (client.familyId || client.family_id)) : null;
      const qty       = +h.qty || 0;
      const costPrice = +h.costPrice || 0;
      const currPrice = +h.currentPrice || 0;
      const costUSD   = toUSD2(qty * costPrice, h.currency);
      const valueUSD  = toUSD2(qty * currPrice, h.currency);
      const gainUSD   = valueUSD - costUSD;
      const gainPct   = costPrice > 0 ? ((currPrice - costPrice) / costPrice * 100) : null;
      const r = fxRates?.[dc] || FX_RATES_DEFAULT?.[dc] || 1;
      return [
        h.name || "(unnamed)", h.ticker || "-", h.isin || "-", h.class || "Miscellaneous", h.sector || "-",
        h.country || "-", h.exchange || "-", h.currency || "",
        qty, costPrice, currPrice,
        (costUSD * r).toFixed(2), (valueUSD * r).toFixed(2),
        (gainUSD * r).toFixed(2),
        gainPct !== null ? gainPct.toFixed(2) + "%" : "—",
        h.capSize || "N/A",
        h.manager || "Self-Managed",
        h.advisor || "Unassigned",
        client?.name || "Unassigned", family?.name || "—",
        h.listed ? "Yes" : "No", today
      ];
    } catch (err) {
      console.error("[exportHoldingsCSV] row error at idx", idx, ":", err, h);
      return null;
    }
  }).filter(Boolean);
  console.log("[exportHoldingsCSV] produced rows:", rows.length, "of", hwWithOwner.length, "input");
  if (rows.length === 0) {
    alert(`Export produced 0 rows from ${hwWithOwner.length} holdings. Open browser console (F12) for row-level errors.`);
    return;
  }
  // Build CSV with UTF-8 BOM + CRLF line endings for Excel compatibility.
  // The BOM (\ufeff) tells Excel the file is UTF-8 so accented characters and ₹/€/£ render correctly.
  // CRLF (\r\n) is the universal CSV newline — Excel sometimes misparses LF-only files.
  const escapeCell = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const csvBody = [headers, ...rows].map(row => row.map(escapeCell).join(",")).join("\r\n");
  const csv = "\ufeff" + csvBody;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = `Forthius_Holdings_${today}.csv`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
  console.log(`[exportHoldingsCSV] downloaded ${rows.length} rows`);
}


// ── Portfolio Bubble Chart — replaces squarified treemap ──────
function Treemap({ data, width=700, height=320, onSliceClick, T, fmt, convert, dc }) {
  const [hov, setHov] = React.useState(null);
  const [tooltip, setTooltip] = React.useState(null);

  const sorted = data.filter(d=>d.value>0).sort((a,b)=>b.value-a.value);
  const total  = sorted.reduce((s,d)=>s+d.value, 0);
  if (!total) return null;

  // ── Bubble sizing ──
  // Max bubble radius = 18% of min(width,height), min = 6px
  const maxR = Math.min(width, height) * 0.18;
  const minR = 6;
  const maxVal = sorted[0].value;
  const bubbles = sorted.map((d,i) => ({
    ...d,
    r: Math.max(minR, Math.sqrt(d.value / maxVal) * maxR),
    pct: (d.value / total * 100),
    idx: i,
  }));

  // ── Physics-style placement — spiral pack ──
  // Place largest first in centre, then pack remaining outward using
  // a simple greedy repulsion approach (good enough for 100+ nodes).
  const placed = [];
  const PAD = 3; // min gap between circles

  function overlaps(cx, cy, r) {
    for (const p of placed) {
      const dist = Math.sqrt((cx-p.cx)**2 + (cy-p.cy)**2);
      if (dist < p.r + r + PAD) return true;
    }
    return false;
  }

  function tryPlace(r) {
    if (placed.length === 0) {
      return { cx: width/2, cy: height/2 };
    }
    // Spiral outward from centre
    const step = 2;
    for (let radius = 0; radius < Math.max(width, height); radius += step) {
      const circumference = Math.max(1, 2 * Math.PI * radius);
      const steps = Math.max(8, Math.floor(circumference / step));
      for (let i = 0; i < steps; i++) {
        const angle = (2 * Math.PI * i) / steps;
        const cx = width/2 + radius * Math.cos(angle);
        const cy = height/2 + radius * Math.sin(angle);
        // Keep within bounds
        if (cx - r < 2 || cx + r > width-2 || cy - r < 2 || cy + r > height-2) continue;
        if (!overlaps(cx, cy, r)) return { cx, cy };
      }
    }
    return null;
  }

  const positioned = [];
  for (const b of bubbles) {
    const pos = tryPlace(b.r);
    if (!pos) continue; // skip if no room
    placed.push({ cx: pos.cx, cy: pos.cy, r: b.r });
    positioned.push({ ...b, cx: pos.cx, cy: pos.cy });
  }

  // Tooltip state tracking
  const handleMouseMove = (e, b) => {
    const svgEl = e.currentTarget.closest("svg");
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    const scaleX = width / rect.width;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleX;
    setTooltip({ ...b, mx, my });
    setHov(b.label);
  };

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`}
      style={{display:"block",borderRadius:10,cursor:"pointer"}}
      onMouseLeave={()=>{ setHov(null); setTooltip(null); }}>

      {positioned.map((b) => {
        const isHov = hov === b.label;
        const showLabel = b.r >= 22;
        const showPct   = b.r >= 32;
        // Slightly darken on hover
        const opacity = isHov ? 1 : (b.r < 10 ? 0.7 : 0.88);
        return (
          <g key={b.label}
            onClick={()=>onSliceClick&&onSliceClick(b.label)}
            onMouseMove={e=>handleMouseMove(e,b)}
            onMouseLeave={()=>{ setHov(null); setTooltip(null); }}>
            <circle
              cx={b.cx} cy={b.cy} r={b.r}
              fill={b.color||"#639922"}
              opacity={opacity}
              stroke={isHov ? "white" : "none"}
              strokeWidth={isHov ? 2 : 0}
              style={{transition:"opacity 0.12s,r 0.12s"}}
            />
            {showLabel && (
              <text
                x={b.cx} y={showPct ? b.cy - 5 : b.cy}
                textAnchor="middle" dominantBaseline="middle"
                fill="white" fontWeight="700"
                fontSize={Math.min(13, b.r * 0.42)}
                fontFamily="Inter, system-ui, sans-serif"
                style={{pointerEvents:"none"}}>
                {b.label.length > b.r / 5 ? b.label.slice(0, Math.floor(b.r/5)) + "…" : b.label}
              </text>
            )}
            {showPct && (
              <text
                x={b.cx} y={b.cy + Math.min(11, b.r * 0.3)}
                textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.82)"
                fontSize={Math.min(11, b.r * 0.32)}
                fontFamily="Inter, system-ui, sans-serif"
                style={{pointerEvents:"none"}}>
                {b.pct.toFixed(1)}%
              </text>
            )}
          </g>
        );
      })}

      {/* Floating tooltip for small bubbles */}
      {tooltip && tooltip.r < 22 && (function(){
        const tx = Math.min(Math.max(tooltip.mx, 70), width - 70);
        const ty = Math.max(tooltip.my - 36, 10);
        const valStr = fmt&&convert&&dc
          ? fmt(convert(tooltip.value||0, dc), dc)
          : (tooltip.value||0).toLocaleString();
        return (
          <g style={{pointerEvents:"none"}}>
            <rect x={tx-64} y={ty-2} width={128} height={34} rx={6}
              fill="rgba(15,15,15,0.88)" stroke="rgba(255,255,255,0.12)" strokeWidth={0.5}/>
            <text x={tx} y={ty+12} fill="white" fontSize={11} fontWeight="700"
              textAnchor="middle" fontFamily="Inter, system-ui, sans-serif">{tooltip.label}</text>
            <text x={tx} y={ty+24} fill="rgba(255,255,255,0.75)" fontSize={10}
              textAnchor="middle" fontFamily="Inter, system-ui, sans-serif">
              {valStr} · {tooltip.pct.toFixed(1)}%
            </text>
          </g>
        );
      })()}
    </svg>
  );
}


function RebalanceSimulator({ wiTargets, rbTotalUSD, dc, fmt, convert, isMobile, T, onConfigureModel }) {
  const [vals, setVals] = React.useState(() => {
    const init = {};
    wiTargets.forEach(wt => { init[wt.assetClass] = wt.target; });
    return init;
  });

  // Reset when model changes
  React.useEffect(() => {
    const init = {};
    wiTargets.forEach(wt => { init[wt.assetClass] = wt.target; });
    setVals(init);
  }, [wiTargets.map(w=>w.assetClass+w.target).join(",")]);

  const total = Object.values(vals).reduce((s,v)=>s+(parseFloat(v)||0),0);
  const totalColor = Math.abs(total-100)>0.5?"#A32D2D":Math.abs(total-100)>0.1?"#D97706":"#1D9E75";

  if (!wiTargets.length) return (
    <div style={{fontSize:12,color:T.textMuted,textAlign:"center",padding:"20px 0"}}>
      No model targets set —{" "}
      <button onClick={onConfigureModel}
        style={{border:"none",background:"none",color:T.brand,cursor:"pointer",
          fontSize:12,fontWeight:600,padding:0}}>configure model →</button>
    </div>
  );

  return (
    <div>
      {wiTargets.map(wt => {
        const tgtVal = parseFloat(vals[wt.assetClass])||0;
        const delta  = (tgtVal - wt.current) / 100 * rbTotalUSD;
        const absDelta = Math.abs(delta);
        const amtStr = absDelta < 50 ? "—"
          : (delta>0?"▲ Buy ":"▼ Sell ") + fmt(convert(absDelta,dc),dc);
        const amtCol = delta>50?"#059669":delta<-50?"#A32D2D":T.textMuted;
        return (
          <div key={wt.assetClass} style={{display:"flex",alignItems:"center",
            gap:10,marginBottom:8}}>
            <div style={{width:isMobile?90:150,flexShrink:0,fontSize:11,
              color:T.textPrimary,whiteSpace:"nowrap",overflow:"hidden",
              textOverflow:"ellipsis"}} title={wt.assetClass}>
              {wt.assetClass}
            </div>
            <div style={{fontSize:10,color:T.textMuted,flexShrink:0,width:38,
              textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif"}}>
              {wt.current.toFixed(1)}%
            </div>
            <input type="range" min="0" max="80" step="0.5"
              value={tgtVal}
              onChange={e=>setVals(prev=>({...prev,[wt.assetClass]:parseFloat(e.target.value)}))}
              style={{flex:1,accentColor:T.brand}}/>
            <div style={{width:36,fontSize:11,fontWeight:700,color:T.brand,
              textAlign:"right",flexShrink:0,fontFamily:"'Inter', system-ui, sans-serif"}}>
              {tgtVal.toFixed(1)}%
            </div>
            <div style={{width:isMobile?80:100,fontSize:10,fontWeight:600,textAlign:"right",
              flexShrink:0,whiteSpace:"nowrap",color:amtCol}}>
              {amtStr}
            </div>
          </div>
        );
      })}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        marginTop:10,paddingTop:10,borderTop:`1px solid ${T.cardBorder}`,
        fontSize:11,color:T.textSecondary}}>
        <span>Total allocated:</span>
        <span style={{fontWeight:700,fontSize:13,color:totalColor,
          fontFamily:"'Inter', system-ui, sans-serif"}}>
          {total.toFixed(1)}%
          {Math.abs(total-100)>0.1 && (
            <span style={{fontSize:10,marginLeft:6,fontWeight:400}}>
              ({total>100?`${(total-100).toFixed(1)}% over`:`${(100-total).toFixed(1)}% unallocated`})
            </span>
          )}
        </span>
        {total>100.5 && (
          <span style={{marginLeft:10,fontSize:11,color:"#A32D2D",fontWeight:600,
            background:"rgba(220,38,38,0.08)",padding:"2px 8px",borderRadius:6,
            border:"1px solid rgba(220,38,38,0.2)"}}>
            ⚠ Over 100% — reduce allocations before rebalancing
          </span>
        )}
        {total<95 && total>0 && (
          <span style={{marginLeft:10,fontSize:11,color:"#D97706",fontWeight:600,
            background:"rgba(217,119,6,0.08)",padding:"2px 8px",borderRadius:6,
            border:"1px solid rgba(217,119,6,0.2)"}}>
            ⚠ {(100-total).toFixed(1)}% unallocated
          </span>
        )}
      </div>
      <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>
        Grey = current · drag slider to simulate new target · trade amounts update live
      </div>
    </div>
  );
}

function PortfolioHealthScore({ filteredHW, filtTotalUSD, fxRates, dc, transactions, isMobile, T, tCard, card, fmt, convert, setTab, allowedRiskTabs, canSeeRisk }) {

  // ── helpers ──
  const FX  = c => fxRates[c] || 1;
  const toU = (v,c) => v / FX(c) * FX("USD");
  const hw  = filteredHW;
  const n   = hw.length;
  if (n === 0) return null;

  const totalV = hw.reduce((s,h) => s + h.valueUSD, 0) || 1;

  // ── 1. Diversification (0-25) ──
  const classes   = [...new Set(hw.map(h => h.class))].length;
  const countries = [...new Set(hw.map(h => h.country))].length;
  const ccys      = [...new Set(hw.map(h => h.currency))].length;
  // HHI
  const hhi = hw.reduce((s,h) => s + Math.pow(h.valueUSD/totalV*100,2), 0);
  const hhiScore = Math.max(0, Math.min(25, 25 - (hhi/10000)*25 + (Math.min(classes,8)/8)*10 + (Math.min(countries,10)/10)*5));

  // ── 2. Concentration Risk (0-25) — penalty for over-weight ──
  const sorted    = [...hw].sort((a,b) => b.valueUSD - a.valueUSD);
  const top1Pct   = sorted[0]?.valueUSD / totalV * 100 || 0;
  const top5Pct   = sorted.slice(0,5).reduce((s,h) => s + h.valueUSD/totalV*100, 0);
  const maxCcyPct = Math.max(...Object.values(hw.reduce((a,h) => {
    a[h.currency] = (a[h.currency]||0) + h.valueUSD; return a;
  }, {})).map(v => v/totalV*100));
  const concScore = Math.max(0, 25
    - (top1Pct  > 25 ? (top1Pct  - 25) * 0.5 : 0)
    - (top5Pct  > 60 ? (top5Pct  - 60) * 0.3 : 0)
    - (maxCcyPct> 50 ? (maxCcyPct- 50) * 0.2 : 0)
  );

  // ── 3. Liquidity (0-25) ──
  const listedV   = hw.filter(h => h.listed).reduce((s,h) => s + h.valueUSD, 0);
  const liqPct    = listedV / totalV * 100;
  const liqScore  = Math.min(25, (liqPct / 100) * 25);

  // ── 4. Performance & Return Quality (0-25) ──
  const gainPcts  = hw.map(h => h.gainPct).filter(v => isFinite(v) && v !== 0);
  const avgGain   = gainPcts.reduce((s,v) => s+v, 0) / Math.max(1, gainPcts.length);
  const winners   = gainPcts.filter(v => v > 0).length;
  const winRate   = winners / Math.max(1, gainPcts.length);
  // Volatility proxy (std dev of holding returns)
  const mean    = avgGain;
  const stddev  = Math.sqrt(gainPcts.reduce((s,v) => s + Math.pow(v-mean,2), 0) / Math.max(1,gainPcts.length));
  const sharpe  = stddev > 0 ? (avgGain - 5) / stddev : 0;
  const perfScore = Math.max(0, Math.min(25,
    (avgGain > 0 ? Math.min(10, avgGain * 0.3) : Math.max(-5, avgGain * 0.2)) +
    winRate * 8 +
    Math.max(0, Math.min(7, sharpe * 3.5))
  ));

  const totalScore = Math.round(hhiScore + concScore + liqScore + perfScore);
  const clamp = v => Math.max(0, Math.min(100, v));
  const score = clamp(totalScore);

  const grade  = score >= 80 ? "A" : score >= 65 ? "B" : score >= 50 ? "C" : score >= 35 ? "D" : "F";
  const label  = score >= 80 ? "Excellent" : score >= 65 ? "Good" : score >= 50 ? "Fair" : score >= 35 ? "Needs Attention" : "At Risk";
  const gColor = score >= 80 ? "#1D9E75" : score >= 65 ? "#22C55E" : score >= 50 ? "#D97706" : score >= 35 ? "#EA580C" : "#A32D2D";

  // ── Donut chart via SVG ──
  // Determine which tabs client can navigate to
  const canGoConc   = !!(allowedRiskTabs ? allowedRiskTabs.concentration : true);
  const canGoRisk   = !!canSeeRisk;
  const canGoIRR    = true; // IRR is always visible

  const DIMS  = [
    { key:"diversification", label:"Diversification",    val:Math.round(hhiScore),  max:25, color:"#2563EB", icon:"🗂️", tab:"geo",           canNav: true,
      insight: classes >= 5 ? `${classes} asset classes — well diversified` : `Only ${classes} class${classes>1?"es":""} — consider broadening` },
    { key:"concentration",   label:"Concentration Risk", val:Math.round(concScore), max:25, color:"#7C3AED", icon:"🎯", tab:"scenarios",     canNav: true,
      insight: top1Pct < 20 ? `Top holding ${top1Pct.toFixed(1)}% — within bounds` : `Top holding ${top1Pct.toFixed(1)}% — concentrated` },
    { key:"liquidity",       label:"Liquidity",          val:Math.round(liqScore),  max:25, color:"#0891B2", icon:"💧", tab:"risk",          canNav: canGoRisk,
      insight: liqPct >= 70 ? `${liqPct.toFixed(0)}% listed — highly liquid` : `${liqPct.toFixed(0)}% listed — illiquidity risk` },
    { key:"performance",     label:"Return Quality",     val:Math.round(perfScore), max:25, color:"#059669", icon:"📈", tab:"irr",           canNav: canGoIRR,
      insight: avgGain > 10 ? `Avg gain ${avgGain.toFixed(1)}% — strong returns` : avgGain > 0 ? `Avg gain ${avgGain.toFixed(1)}% — modest growth` : `Avg loss ${avgGain.toFixed(1)}% — review holdings` },
  ];

  // SVG donut — 4 arcs representing each dimension
  const CX=80, CY=80, R=60, SW=22;
  const circ = 2*Math.PI*R;
  let cumAngle = -Math.PI/2;
  const arcs = DIMS.map(d => {
    const frac   = (d.val/d.max) * 0.25; // each dim gets 90° (25% of circle)
    const sAngle = cumAngle;
    const sweep  = 2*Math.PI * 0.23;     // slight gap
    cumAngle += 2*Math.PI * 0.25;
    const x1 = CX + R * Math.cos(sAngle);
    const y1 = CY + R * Math.sin(sAngle);
    const eAngle = sAngle + sweep * (d.val/d.max);
    const x2 = CX + R * Math.cos(eAngle);
    const y2 = CY + R * Math.sin(eAngle);
    const bgE = sAngle + sweep;
    const bx2 = CX + R * Math.cos(bgE);
    const by2 = CY + R * Math.sin(bgE);
    const lg  = sweep > Math.PI ? 1 : 0;
    return { ...d, x1,y1,x2,y2,bx2,by2,sAngle,sweep,eAngle,lg, bgLg: sweep>Math.PI?1:0 };
  });

  const summaryLines = [
    `Portfolio spans ${classes} asset class${classes!==1?"es":""} across ${countries} countr${countries!==1?"ies":"y"} in ${ccys} currenc${ccys!==1?"ies":"y"}.`,
    top1Pct > 25
      ? `⚠ Top holding (${sorted[0]?.name||""}) represents ${top1Pct.toFixed(1)}% — consider rebalancing.`
      : `✓ Single-holding concentration at ${top1Pct.toFixed(1)}% is within healthy bounds.`,
    liqPct < 50
      ? `⚠ ${(100-liqPct).toFixed(0)}% of portfolio is in illiquid assets — ensure adequate liquidity runway.`
      : `✓ ${liqPct.toFixed(0)}% of portfolio is listed and liquid.`,
    avgGain >= 0
      ? `✓ ${winners} of ${n} holdings (${(winRate*100).toFixed(0)}%) are in positive territory${isFinite(avgGain) && avgGain > 0 ? ` with avg gain ${avgGain.toFixed(1)}%` : ""}.`
      : `⚠ ${n-winners} of ${n} holdings are underwater${isFinite(avgGain) ? ` — avg return ${avgGain.toFixed(1)}%` : ""}.`,
    sharpe > 1 ? `✓ Risk-adjusted return (Sharpe proxy ${sharpe.toFixed(2)}) indicates good return per unit of volatility.`
      : sharpe > 0 ? `Sharpe proxy of ${sharpe.toFixed(2)} — modest risk-adjusted return.`
      : `⚠ Negative Sharpe proxy (${sharpe.toFixed(2)}) — returns not compensating for risk.`,
  ];

  return (
    <div style={{...tCard(), marginBottom:20, borderColor: gColor, position:"relative", overflow:"hidden"}}>
      {/* Grade ribbon */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:4,background:`linear-gradient(90deg,${gColor},${gColor}88)`}}/>

      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:15,fontWeight:700,color:T.textPrimary,display:"flex",alignItems:"center",gap:8}}>
            🏥 Portfolio Health Score
            <span style={{fontSize:11,background:`${gColor}18`,border:`1px solid ${gColor}44`,
              borderRadius:20,padding:"2px 10px",color:gColor,fontWeight:700}}>
              {grade} · {label}
            </span>
          </div>
          <div style={{fontSize:11,color:T.textSecondary,marginTop:3}}>
            Composite score across diversification, concentration, liquidity &amp; return quality
          </div>
        </div>
        <div style={{fontSize:42,fontWeight:500,color:gColor,fontFamily:"'Inter', system-ui, sans-serif",lineHeight:1}}>
          {score}<span style={{fontSize:16,color:T.textMuted}}>/100</span>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:20,alignItems:"start"}}>

        {/* Left — donut + dimension bars */}
        <div>
          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:16}}>
            {/* SVG Donut */}
            <svg width={160} height={160} style={{flexShrink:0}}>
              {arcs.map(a => (<React.Fragment>
                {/* background arc */}
                <path key={a.key+"bg"}
                  d={`M ${a.x1.toFixed(2)} ${a.y1.toFixed(2)} A ${R} ${R} 0 ${a.bgLg} 1 ${a.bx2.toFixed(2)} ${a.by2.toFixed(2)}`}
                  fill="none" stroke={T.borderSubtle||"#E5E0D8"} strokeWidth={SW} strokeLinecap="round"/>
                {/* filled arc */}
                <path key={a.key+"fill"}
                  d={`M ${a.x1.toFixed(2)} ${a.y1.toFixed(2)} A ${R} ${R} 0 ${a.lg} 1 ${a.x2.toFixed(2)} ${a.y2.toFixed(2)}`}
                  fill="none" stroke={a.color} strokeWidth={SW} strokeLinecap="round"
                  style={{transition:"stroke-dasharray 0.6s ease"}}/>
              </React.Fragment>))}
              {/* centre score */}
              <text x={CX} y={CY-6} textAnchor="middle" fontSize={22} fontWeight={800} fill={gColor} fontFamily="Inter, system-ui, sans-serif">{score}</text>
              <text x={CX} y={CY+12} textAnchor="middle" fontSize={11} fill={T.textMuted||"#78716C"}>{grade} · {label}</text>
            </svg>
            {/* Legend */}
            <div style={{display:"flex",flexDirection:"column",gap:8,flex:1}}>
              {DIMS.map(d => (
                <div key={d.key} style={{cursor:d.canNav?"pointer":"default"}}
                  onClick={()=>d.canNav && setTab(d.tab)}
                  title={d.canNav ? `Go to ${d.tab} →` : undefined}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,color:T.textSecondary}}>{d.icon} {d.label}</span>
                    <span style={{fontSize:11,fontWeight:700,color:d.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{d.val}/{d.max}</span>
                  </div>
                  <div style={{height:6,background:T.borderSubtle||"#EDE8DF",borderRadius:3}}>
                    <div style={{height:"100%",width:`${(d.val/d.max)*100}%`,background:d.color,borderRadius:3,transition:"width 0.6s ease"}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — summary insights */}
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          <div style={{fontSize:11,fontWeight:700,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Key Insights</div>
          {summaryLines.map((line,i) => {
            const warn = line.startsWith("⚠");
            const ok   = line.startsWith("✓");
            const col  = warn ? "#EA580C" : ok ? "#1D9E75" : T.textSecondary;
            const bg   = warn ? "rgba(234,88,12,0.06)" : ok ? "rgba(22,163,74,0.06)" : "transparent";
            const bord = warn ? "rgba(234,88,12,0.2)" : ok ? "rgba(22,163,74,0.2)" : T.borderSubtle;
            return (
              <div key={i} style={{fontSize:12,color:col,lineHeight:1.5,padding:"6px 10px",
                background:bg,borderRadius:6,border:`1px solid ${bord}`}}>
                {line}
              </div>
            );
          })}
          <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
            {[
              ["📉 Concentration","scenarios",  canGoConc],
              ["⚡ Risk",          "risk",       canGoRisk],
              ["📐 IRR",           "irr",        true],
              ["📊 Volatility",    "analytics",  true],
            ].filter(([,,can])=>can).map(([l,t])=>(
              <button key={t} onClick={()=>setTab(t)}
                style={{padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer",
                  border:`1px solid ${T.cardBorder}`,background:"transparent",
                  color:T.textSecondary,fontWeight:500}}>
                {l} →
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WhatIfSimulator — proper component (no hooks-in-IIFE violation) ──

// ─────────────── SUBSCRIPTIONS (placeholder) ───────────────

// ─────────────── UPGRADE MODAL ───────────────
function UpgradeModal({ tabId, tabLabel, tabIcon, clientId, clientPlans, setPlanForClient,
                        onClose, T, inp, tCard, isMobile }) {
  const [gateway, setGateway] = React.useState("razorpay");
  const [processing, setProcessing] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState("pro");

  const currentPlan = (() => {
    const entry = clientPlans[clientId];
    if (!entry) return "free";
    if (typeof entry === "string") return entry;
    return entry.plan || "free";
  })();

  const requiredPlan = (() => {
    if (PLAN_CONFIG.starter.tabs.includes(tabId)) return "starter";
    if (PLAN_CONFIG.pro.tabs.includes(tabId))     return "pro";
    if (PLAN_CONFIG.premium.tabs.includes(tabId)) return "premium";
    return "starter";
  })();

  const upgradePlans = Object.values(PLAN_CONFIG).filter(p =>
    planRank[p.id] >= planRank[requiredPlan] && p.id !== "free"
  );

  const handlePayment = async () => {
    setProcessing(true);
    const plan = PLAN_CONFIG[selectedPlan];
    try {
      if (gateway === "razorpay") {
        if (!window.Razorpay) {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          document.body.appendChild(script);
          await new Promise(r => { script.onload = r; });
        }
        const options = {
          key: PAYMENT_CONFIG.razorpay.keyId,
          amount: plan.price.INR * 100,
          currency: "INR",
          name: PAYMENT_CONFIG.razorpay.companyName,
          description: `${plan.label} Plan — Monthly`,
          prefill: {},
          theme: { color: plan.color },
          handler: (response) => {
            setPlanForClient(clientId, selectedPlan, {
              gateway: "razorpay",
              expiresAt: new Date(Date.now() + 30*24*60*60*1000).toISOString(),
            });
            setProcessing(false);
            onClose(true);
          },
          modal: { ondismiss: () => setProcessing(false) },
        };
        new window.Razorpay(options).open();
      } else {
        // Stripe — redirect flow
        alert("Stripe payment integration: replace with your Stripe Checkout session URL.\nFor now, plan upgraded manually.");
        setPlanForClient(clientId, selectedPlan, { gateway: "stripe",
          expiresAt: new Date(Date.now() + 30*24*60*60*1000).toISOString() });
        setProcessing(false);
        onClose(true);
      }
    } catch (e) {
      setProcessing(false);
    }
  };

  const start14DayTrial = () => {
    const trialEnd = new Date(Date.now() + 14*24*60*60*1000).toISOString();
    setPlanForClient(clientId, selectedPlan, { gateway: "trial", trialEnd });
    onClose(true);
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.5)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
      onClick={e=>e.target===e.currentTarget&&onClose(false)}>
      <div style={{background:T.cardBg,borderRadius:16,width:"100%",maxWidth:520,
        boxShadow:"0 24px 64px rgba(0,0,0,0.2)",overflow:"hidden"}}>

        {/* Header */}
        <div style={{padding:"20px 24px 16px",borderBottom:`1px solid ${T.cardBorder}`,
          background:`linear-gradient(135deg,${PLAN_CONFIG[selectedPlan]?.color}15,transparent)`}}>
          <div style={{fontSize:13,color:T.textSecondary,marginBottom:4}}>
            {tabIcon} {tabLabel} requires an upgrade
          </div>
          <div style={{fontSize:20,fontWeight:500,color:T.textPrimary}}>
            Unlock More with Forthius
          </div>
          <button onClick={()=>onClose(false)} style={{position:"absolute",top:16,right:20,
            background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted}}>✕</button>
        </div>

        {/* Plan selector */}
        <div style={{padding:"16px 24px",display:"flex",gap:8,flexWrap:"wrap"}}>
          {upgradePlans.map(p => (
            <button key={p.id} onClick={()=>setSelectedPlan(p.id)}
              style={{flex:1,minWidth:100,padding:"10px 12px",borderRadius:10,cursor:"pointer",
                border:`2px solid ${selectedPlan===p.id ? p.color : T.cardBorder}`,
                background:selectedPlan===p.id ? `${p.color}12` : T.cardBg,
                transition:"all 0.15s"}}>
              <div style={{fontSize:16}}>{p.icon}</div>
              <div style={{fontSize:13,fontWeight:700,color:selectedPlan===p.id ? p.color : T.textPrimary}}>{p.label}</div>
              <div style={{fontSize:11,color:T.textSecondary}}>₹{p.price.INR.toLocaleString("en-IN")}/mo</div>
            </button>
          ))}
        </div>

        {/* Features */}
        <div style={{padding:"0 24px 16px"}}>
          <div style={{fontSize:11,fontWeight:600,color:T.textMuted,textTransform:"uppercase",
            letterSpacing:"0.06em",marginBottom:8}}>What you get</div>
          {(PLAN_CONFIG[selectedPlan]?.features||[]).map((f,i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,
              color:T.textSecondary,marginBottom:5}}>
              <span style={{color:"#1D9E75",flexShrink:0}}>✓</span> {f}
            </div>
          ))}
        </div>

        {/* Gateway selector */}
        <div style={{padding:"0 24px 16px"}}>
          <div style={{fontSize:11,fontWeight:600,color:T.textMuted,textTransform:"uppercase",
            letterSpacing:"0.06em",marginBottom:8}}>Payment method</div>
          <div style={{display:"flex",gap:8}}>
            {[{id:"razorpay",label:"Razorpay (INR)",flag:"🇮🇳"},{id:"stripe",label:"Stripe (USD)",flag:"🌍"}].map(gw=>(
              <button key={gw.id} onClick={()=>setGateway(gw.id)}
                style={{flex:1,padding:"8px 12px",borderRadius:8,cursor:"pointer",fontSize:12,
                  border:`2px solid ${gateway===gw.id ? "#2563EB" : T.cardBorder}`,
                  background:gateway===gw.id ? "rgba(37,99,235,0.08)" : T.cardBg,
                  fontWeight:gateway===gw.id ? 700 : 400}}>
                {gw.flag} {gw.label}
              </button>
            ))}
          </div>
          {gateway === "stripe" && (
            <div style={{fontSize:11,color:T.textMuted,marginTop:6}}>
              USD ${PLAN_CONFIG[selectedPlan]?.price.USD}/mo · For non-residents
            </div>
          )}
        </div>

        {/* CTA buttons */}
        <div style={{padding:"0 24px 20px",display:"flex",gap:8,flexDirection:"column"}}>
          <button onClick={handlePayment} disabled={processing}
            style={{padding:"12px",borderRadius:10,border:"none",cursor:processing?"default":"pointer",
              background:`linear-gradient(135deg,${PLAN_CONFIG[selectedPlan]?.color},${PLAN_CONFIG[selectedPlan]?.color}cc)`,
              color:"white",fontSize:14,fontWeight:700,
              opacity:processing?0.7:1}}>
            {processing ? "⏳ Processing…" : `Subscribe to ${PLAN_CONFIG[selectedPlan]?.label} — ${gateway==="stripe"?"$"+PLAN_CONFIG[selectedPlan]?.price.USD:("₹"+PLAN_CONFIG[selectedPlan]?.price.INR.toLocaleString("en-IN"))}/mo`}
          </button>
          <button onClick={start14DayTrial}
            style={{padding:"10px",borderRadius:10,border:`1px solid ${T.cardBorder}`,
              background:T.borderSubtle,cursor:"pointer",fontSize:13,color:T.textSecondary}}>
            🎁 Start 14-day free trial (no payment required)
          </button>
        </div>

        <div style={{padding:"0 24px 16px",fontSize:10,color:T.textMuted,textAlign:"center"}}>
          Cancel anytime · Secure payment · Data remains yours
        </div>
      </div>
    </div>
  );
}

// ─────────────── SUBSCRIPTION PANEL (Admin) ───────────────

// ─────────────── METHODOLOGY & DISCLOSURES DRAWER ───────────────
function MethodologyDrawer({ open, onClose, T }) {
  if (!open) return null;

  const S = {
    overlay: { position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,0.45)",backdropFilter:"blur(3px)" },
    drawer:  { position:"fixed",top:0,right:0,bottom:0,width:"min(520px,100vw)",zIndex:9999,
               background:T.cardBg,borderLeft:`1px solid ${T.cardBorder}`,
               boxShadow:"-24px 0 64px rgba(0,0,0,0.25)",overflowY:"auto",
               display:"flex",flexDirection:"column" },
    hdr:     { padding:"20px 24px 16px",borderBottom:`1px solid ${T.cardBorder}`,
               display:"flex",justifyContent:"space-between",alignItems:"center",
               position:"sticky",top:0,background:T.cardBg,zIndex:1 },
    sec:     { marginBottom:24 },
    h2:      { fontSize:12,fontWeight:700,color:T.brand,textTransform:"uppercase",
               letterSpacing:"0.08em",marginBottom:10,paddingBottom:6,
               borderBottom:`1px solid ${T.cardBorder}` },
    h3:      { fontSize:12,fontWeight:700,color:T.textPrimary,marginBottom:4,marginTop:10 },
    p:       { fontSize:12,color:T.textSecondary,lineHeight:1.65,marginBottom:6 },
    pill:    { display:"inline-block",fontSize:10,fontWeight:700,padding:"2px 8px",
               borderRadius:10,marginRight:4,marginBottom:4 },
    tbl:     { width:"100%",borderCollapse:"collapse",fontSize:11,marginTop:8 },
    th:      { padding:"6px 10px",background:T.thBg,color:T.thText,fontWeight:700,
               textAlign:"left",borderBottom:`1px solid ${T.cardBorder}` },
    td:      { padding:"7px 10px",color:T.textSecondary,borderBottom:`1px solid ${T.tdBorder}` },
  };

  const Row = ({label, val}) => (
    <tr>
      <td style={{...S.td,fontWeight:600,color:T.textPrimary,width:"42%"}}>{label}</td>
      <td style={S.td}>{val}</td>
    </tr>
  );

  return (
    <React.Fragment>
      <div style={S.overlay} onClick={onClose}/>
      <div style={S.drawer}>
        {/* Header */}
        <div style={S.hdr}>
          <div>
            <div style={{fontSize:16,fontWeight:500,color:T.textPrimary}}>📐 Methodology & Disclosures</div>
            <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>How numbers are calculated in Forthius</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,
            cursor:"pointer",color:T.textMuted,padding:4}}>✕</button>
        </div>

        {/* Content */}
        <div style={{padding:"20px 24px",flex:1}}>

          {/* Returns */}
          <div style={S.sec}>
            <div style={S.h2}>Return Calculations</div>
            <div style={S.h3}>Portfolio Return (Since Inception)</div>
            <div style={S.p}>
              Computed as <strong>(Current Value − Cost Basis) / Cost Basis × 100</strong>.
              Cost basis is the weighted average purchase price × quantity across all active holdings (qty &gt; 0).
              Ghost positions (qty = 0) are excluded from all return calculations.
            </div>
            <div style={S.h3}>YTD Return</div>
            <div style={S.p}>
              Time-Weighted Rate of Return (TWRR) from 1 Jan of the current year to today.
              Sub-period returns are computed around each transaction date. Start-of-period value
              uses Alpha Vantage historical prices where available; falls back to last transaction price,
              then cost price as a proxy.
            </div>
            <div style={S.h3}>1 Year / 3 Year Return</div>
            <div style={S.p}>
              TWRR over the respective period (365 / 1095 days from today). 
              Periods longer than 1 year are annualised using <strong>(1 + total return)^(1/years) − 1</strong>.
            </div>
            <div style={S.h3}>Point-to-Point (P2P) Return</div>
            <div style={S.p}>
              TWRR between the selected from-date and today. Historical prices at the from-date
              are fetched from Alpha Vantage (cached). Benchmark P2P uses linear interpolation
              between monthly data points.
            </div>
            <div style={S.h3}>Alpha</div>
            <div style={S.p}>
              Portfolio Since-Inception return minus the benchmark return for the same period.
              Positive alpha means the portfolio outperformed the selected benchmark.
              When the portfolio holds multiple currencies, alpha is versus a single benchmark
              and does not account for currency hedging costs.
            </div>
          </div>

          {/* Risk */}
          <div style={S.sec}>
            <div style={S.h2}>Risk Metrics</div>
            <table style={S.tbl}>
              <thead><tr><th style={S.th}>Metric</th><th style={S.th}>Method</th></tr></thead>
              <tbody>
                <Row label="HHI (Concentration)" val="Sum of squared weight percentages. <1000 = Low, 1000–2500 = Moderate, >2500 = High concentration."/>
                <Row label="Top 5 Concentration" val="Market value of top 5 active holdings ÷ total portfolio value × 100. Ghost positions excluded."/>
                <Row label="Illiquidity %" val="(Total value − Listed equity value) ÷ Total value. Unlisted, real estate, PE, and private debt counted as illiquid."/>
                <Row label="Max Drawdown" val="Estimated from annualised volatility × √(holding period / 252) × 2.33 (95th percentile). Indicative only — no daily NAV history stored."/>
                <Row label="Volatility" val="Weighted average of asset-class volatility assumptions. Not computed from actual price history."/>
              </tbody>
            </table>
          </div>

          {/* FX */}
          <div style={S.sec}>
            <div style={S.h2}>FX & Multi-Currency</div>
            <div style={S.p}>
              All values are converted to your selected display currency using exchange rates
              fetched from Frankfurter API (ECB source). If live rates are unavailable, static
              fallback rates from training data are used — these are marked <strong>⚠ Indicative</strong>.
            </div>
            <div style={S.p}>
              FX rates are refreshed on each price refresh cycle. The timestamp shown next to
              the rates reflects the last successful fetch. Indian Rupee (INR) rates use
              Google Finance as the primary source.
            </div>
          </div>

          {/* Prices */}
          <div style={S.sec}>
            <div style={S.h2}>Price Data Sources</div>
            <table style={S.tbl}>
              <thead><tr><th style={S.th}>Asset Type</th><th style={S.th}>Source</th><th style={S.th}>Frequency</th></tr></thead>
              <tbody>
                <tr>
                  <td style={S.td}>Indian Equities (NSE/BSE)</td>
                  <td style={S.td}>Google Finance → Finnhub → Twelve Data</td>
                  <td style={S.td}>Real-time (15 min delay)</td>
                </tr>
                <tr>
                  <td style={S.td}>Global Equities (US, EU)</td>
                  <td style={S.td}>Google Finance → Finnhub → Alpha Vantage</td>
                  <td style={S.td}>Real-time (15 min delay)</td>
                </tr>
                <tr>
                  <td style={S.td}>Indian Mutual Funds</td>
                  <td style={S.td}>AMFI / mfapi.in</td>
                  <td style={S.td}>Previous day NAV (T+1)</td>
                </tr>
                <tr>
                  <td style={S.td}>Unlisted / Manual</td>
                  <td style={S.td}>Admin-entered price</td>
                  <td style={S.td}>As updated by advisor</td>
                </tr>
                <tr>
                  <td style={S.td}>Benchmarks</td>
                  <td style={S.td}>Alpha Vantage (monthly series)</td>
                  <td style={S.td}>Month-end close</td>
                </tr>
              </tbody>
            </table>
            <div style={{...S.p,marginTop:8,fontStyle:"italic"}}>
              * Exchange-traded prices may reflect a 15-minute delay depending on the data provider.
              Mutual fund NAVs are always T+1 (previous business day).
            </div>
          </div>

          {/* Benchmarks */}
          <div style={S.sec}>
            <div style={S.h2}>Benchmarks</div>
            <div style={S.p}>
              Benchmark returns are fetched from Alpha Vantage using monthly adjusted close prices.
              YTD and P2P benchmark returns use linear interpolation between month-end data points
              for within-month precision. Long-term CAGRs (3Y, 5Y, 10Y) are from published index data
              and used as fallbacks when monthly history is insufficient.
            </div>
            <div style={S.p}>
              Available benchmarks: S&amp;P 500, Nifty 50, MSCI World, FTSE 100, Gold (XAUUSD), Bitcoin.
            </div>
          </div>

          {/* Tax */}
          <div style={S.sec}>
            <div style={S.h2}>Tax Estimates</div>
            <div style={S.p}>
              Capital gains tax estimates are indicative and based on simplified rules:
              India — LTCG 12.5% on gains above ₹1L after 12 months; STCG 20% under 12 months.
              US — LTCG 15–20% after 12 months; STCG at ordinary income rates.
              These do not account for surcharge, cess, treaty benefits, or individual circumstances.
              Consult a qualified tax advisor before acting on these estimates.
            </div>
          </div>

          {/* Disclosures */}
          <div style={S.sec}>
            <div style={S.h2}>Important Disclosures</div>
            <div style={{...S.p,background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.15)",
              borderRadius:8,padding:"10px 14px",color:T.textPrimary}}>
              This dashboard is prepared by Forthius Finalytics for informational purposes only
              and does not constitute investment advice, a solicitation, or an offer to buy or sell
              any security. Past performance is not indicative of future results. All figures are
              subject to market movements and data provider availability.
            </div>
            <div style={{...S.p,marginTop:10}}>
              Market values shown are indicative and may differ from actual traded or realisable prices,
              particularly for illiquid assets, unlisted securities, real estate, and private equity.
              Forthius Finalytics makes no warranty as to the accuracy or completeness of
              third-party price data.
            </div>
            <div style={{...S.p}}>
              FX conversion rates are indicative. Actual conversion will depend on the bank or
              broker rate at the time of transaction.
            </div>
          </div>

          {/* Version */}
          <div style={{fontSize:10,color:T.textMuted,textAlign:"center",paddingTop:16,
            borderTop:`1px solid ${T.cardBorder}`}}>
            Forthius Finalytics · Dashboard v4 · Methodology rev. {new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"})}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}


function SubscriptionPanel({ clients, families, clientPlans, getEffectivePlan,
                              setPlanForClient, setClientPlans, T, tCard, tTh, tTd, inp, isMobile }) {
  const [search, setSearch] = React.useState("");
  const [filterPlan, setFilterPlan] = React.useState("all");

  const planList = ["free","starter","pro","premium"];

  const stats = planList.map(pid => {
    const count = clients.filter(c => getEffectivePlan(c.id) === pid).length;
    const rev   = count * PLAN_CONFIG[pid].price.INR;
    return { ...PLAN_CONFIG[pid], count, rev };
  });
  const totalMRR = stats.reduce((s,p) => s + p.rev, 0);

  const filtered = clients.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
    const matchPlan   = filterPlan === "all" || getEffectivePlan(c.id) === filterPlan;
    return matchSearch && matchPlan;
  });

  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—";
  const isInTrial = (clientId) => {
    const entry = clientPlans[clientId];
    if (!entry || typeof entry === "string") return false;
    return entry.trialEnd && new Date(entry.trialEnd) >= new Date();
  };
  const trialDaysLeft = (clientId) => {
    const entry = clientPlans[clientId];
    if (!entry || !entry.trialEnd) return 0;
    return Math.max(0, Math.ceil((new Date(entry.trialEnd) - new Date()) / 86400000));
  };

  return (
    <div style={{padding:isMobile?"12px":"24px"}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:20,fontWeight:500}}>💳 Subscription Manager</div>
          <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
            Manage client plans, trials, and billing
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Monthly Recurring Revenue</div>
          <div style={{fontSize:28,fontWeight:500,color:"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif"}}>
            ₹{totalMRR.toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      {/* Plan summary cards */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:12,marginBottom:24}}>
        {stats.map(p => (
          <div key={p.id} onClick={()=>setFilterPlan(filterPlan===p.id?"all":p.id)}
            style={{...tCard(),borderTop:`3px solid ${p.color}`,cursor:"pointer",
              background:filterPlan===p.id?`${p.color}08`:undefined,
              outline:filterPlan===p.id?`2px solid ${p.color}`:undefined}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <span style={{fontSize:20}}>{p.icon}</span>
              {p.count > 0 && <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:8,
                background:`${p.color}18`,color:p.color}}>{p.id.toUpperCase()}</span>}
            </div>
            <div style={{fontSize:24,fontWeight:500,color:p.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{p.count}</div>
            <div style={{fontSize:11,color:T.textSecondary}}>{p.label} clients</div>
            {p.rev > 0 && <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>₹{p.rev.toLocaleString("en-IN")}/mo</div>}
          </div>
        ))}
      </div>

      {/* Search + filter bar */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search clients…"
          style={{...inp(),flex:1,minWidth:180}} />
        <select value={filterPlan} onChange={e=>setFilterPlan(e.target.value)}
          style={{...inp(),width:"auto"}}>
          <option value="all">All plans</option>
          {planList.map(p => <option key={p} value={p}>{PLAN_CONFIG[p].icon} {PLAN_CONFIG[p].label}</option>)}
        </select>
      </div>

      {/* Client table */}
      <div style={{...tCard(),padding:0,overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:620}}>
            <thead>
              <tr>
                {["Client","Family","Plan","Trial / Expiry","Gateway","Actions"].map(h=>(
                  <th key={h} style={{...tTh(),padding:"10px 14px",textAlign:"left"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c,i) => {
                const plan = getEffectivePlan(c.id);
                const entry = clientPlans[c.id];
                const fam = families.find(f=>f.id===c.familyId);
                const inTrial = isInTrial(c.id);
                const dLeft = trialDaysLeft(c.id);
                const pc = PLAN_CONFIG[plan];
                const gateway = typeof entry === "object" ? (entry?.gateway || "—") : "—";
                const expiry = typeof entry === "object" ? (entry?.expiresAt || null) : null;
                return (
                  <tr key={c.id} style={{background:i%2===0?"transparent":T.tableStripe,
                    borderBottom:`1px solid ${T.cardBorder}`}}>
                    <td style={{...tTd(),padding:"10px 14px",fontWeight:600}}>
                      {c.avatar||"👤"} {c.name}
                    </td>
                    <td style={{...tTd(),padding:"10px 14px",color:T.textSecondary,fontSize:12}}>
                      {fam?.name||"—"}
                    </td>
                    <td style={{...tTd(),padding:"10px 14px"}}>
                      <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12,
                        background:`${pc.color}18`,color:pc.color,border:`1px solid ${pc.color}44`}}>
                        {pc.icon} {pc.label}
                      </span>
                      {inTrial && (
                        <span style={{marginLeft:6,fontSize:10,fontWeight:700,padding:"2px 6px",
                          borderRadius:8,background:"rgba(245,158,11,0.12)",color:"#D97706"}}>
                          Trial · {dLeft}d left
                        </span>
                      )}
                    </td>
                    <td style={{...tTd(),padding:"10px 14px",fontSize:11,color:T.textSecondary}}>
                      {inTrial ? `Trial ends ${fmtDate(typeof entry==="object"?entry?.trialEnd:null)}`
                               : expiry ? `Expires ${fmtDate(expiry)}` : "—"}
                    </td>
                    <td style={{...tTd(),padding:"10px 14px",fontSize:11,color:T.textSecondary,textTransform:"capitalize"}}>
                      {gateway}
                    </td>
                    <td style={{...tTd(),padding:"10px 14px"}}>
                      <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                        <select value={plan}
                          onChange={e=>setPlanForClient(c.id, e.target.value, {gateway:"manual",
                            expiresAt: e.target.value==="free" ? null : new Date(Date.now()+30*24*60*60*1000).toISOString()})}
                          style={{...inp(),fontSize:11,padding:"4px 8px",width:"auto",minWidth:110}}>
                          {planList.map(p=>(
                            <option key={p} value={p}>{PLAN_CONFIG[p].icon} {PLAN_CONFIG[p].label}</option>
                          ))}
                        </select>
                        <button
                          title="Start 14-day trial"
                          onClick={()=>setPlanForClient(c.id, "pro", {
                            gateway:"trial",
                            trialEnd: new Date(Date.now()+14*24*60*60*1000).toISOString()
                          })}
                          style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                            background:T.borderSubtle,cursor:"pointer",fontSize:10,color:T.textSecondary,
                            whiteSpace:"nowrap"}}>
                          🎁 Trial
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{...tTd(),padding:24,textAlign:"center",color:T.textMuted,fontSize:12}}>
                  No clients match your filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footnote */}
      <div style={{marginTop:12,fontSize:11,color:T.textMuted}}>
        * Plans marked "manual" were set directly by admin without payment processing.
        Razorpay (INR) and Stripe (USD) gateway transactions update plans automatically on payment success.
      </div>
    </div>
  );
}



function WhatIfSimulator({ filteredHW, filtTotalUSD, fxRates, dc, isMobile, T, tCard, inp, fmt }) {
  const [wiOverrides,     setWiOverrides]     = React.useState({}); // holdingId → pctStr (price move)
  const [fxHoldOverrides, setFxHoldOverrides] = React.useState({}); // holdingId → { pct, tgtCcy }
  const [wiSearch,        setWiSearch]        = React.useState("");
  const [batchVal,        setBatchVal]        = React.useState({}); // ccy → pctStr
  const [batchTgt,        setBatchTgt]        = React.useState({}); // ccy → tgtCcy
  const [wiPresetActive,  setWiPresetActive]  = React.useState(null);

  // ── Which asset classes count as "equity-like" for scenarios ──
  const isEquityLike = (h) => ["Equities","Indian Equities","Global Equities",
    "Mutual Funds","Indian Mutual Funds","Global Mutual Funds"].includes(h.class) || h.isMutualFund;
  const isIndiaEquity = (h) => (h.country==="India"||h.currency==="INR") && isEquityLike(h);
  const isDebt = (h) => ["Fixed Income","Time Deposits"].includes(h.class);

  const WI_PRESETS = [
    { id:"crash",
      label:"🔴 Market Crash",
      desc:"−30% equities & MFs, +5% gold & bonds",
      priceMap: h =>
        isEquityLike(h) ? -30 :
        h.class==="Commodities" ? 5 :
        isDebt(h) ? 5 : 0,
      fxMap: null },
    { id:"bull",
      label:"🟢 India Bull Run",
      desc:"+25% Indian equities & MFs, INR +3% vs USD",
      priceMap: h => isIndiaEquity(h) ? 25 : 0,
      // INR strengthens 3% vs USD (positive for INR holders)
      fxMap: h => h.currency==="INR" ? { tgtCcy:"USD", pct:3 } : null },
    { id:"usd_up",
      label:"💵 USD Strengthens",
      desc:"Non-USD holdings lose −10% in USD terms (FX effect)",
      priceMap: null,
      // USD strengthens = non-USD currencies weaken vs USD
      // For each non-USD holding: their currency moves -10% vs USD
      fxMap: h => h.currency!=="USD" ? { tgtCcy:"USD", pct:-10 } : null },
    { id:"rates",
      label:"📈 Rates Rise +2%",
      desc:"−10% bonds & FDs, −8% REITs, −5% equity MFs",
      priceMap: h =>
        isDebt(h) ? -10 :
        h.class==="Real Estate" ? -8 :
        isEquityLike(h) ? -5 : 0,
      fxMap: null },
    { id:"clear", label:"⬜ Clear All", desc:"Reset all overrides",
      priceMap: null, fxMap: null },
  ];

  const applyPreset = (preset) => {
    if (preset.id==="clear") {
      setWiOverrides({});
      setFxHoldOverrides({});
      setWiPresetActive(null);
      return;
    }
    // Apply price overrides
    const priceOv = {};
    if (preset.priceMap) {
      filteredHW.forEach(h => {
        const p = preset.priceMap(h);
        if (p !== 0) priceOv[h.id] = String(p);
      });
    }
    setWiOverrides(priceOv);
    // Apply FX overrides
    const fxOv = {};
    if (preset.fxMap) {
      filteredHW.forEach(h => {
        const fx = preset.fxMap(h);
        if (fx) fxOv[h.id] = fx; // { tgtCcy, pct }
      });
    }
    setFxHoldOverrides(fxOv);
    setWiPresetActive(preset.id);
  };

  // FX(X) = units of X per 1 USD (e.g. FX("INR")=83.2 means 1 USD = 83.2 INR)
  const FX = (cur) => fxRates[cur] || FX_RATES_DEFAULT[cur] || 1;

  // Cross rate: how many units of B do you get for 1 unit of A?
  // 1 A = (1/FX(A)) USD = (FX(B)/FX(A)) B
  const crossRate = (from, to) => FX(to) / FX(from);

  const ccyFlag = c => ({ USD:"🇺🇸",EUR:"🇪🇺",GBP:"🇬🇧",INR:"🇮🇳",JPY:"🇯🇵",CHF:"🇨🇭",
                           AED:"🇦🇪",SGD:"🇸🇬",HKD:"🇭🇰",CAD:"🇨🇦",AUD:"🇦🇺" }[c] || "💱");

  const CONV_CCYS = ["USD","EUR","GBP","INR","JPY","CHF","AED","SGD","HKD","CAD","AUD"];

  // ── Row computation ──────────────────────────────────────────────────────
  const rows = filteredHW.map(h => {
    // 1. Price shift
    const pStr      = wiOverrides[h.id];
    const pricePct  = pStr !== undefined && pStr !== "" ? (parseFloat(pStr) || 0) : 0;
    const priceChg  = pricePct !== 0;
    const newPrice  = h.currentPrice * (1 + pricePct / 100);

    // 2. FX shift — user picks a target currency and a % move on that pair
    //    Interpretation: "+5%" on tgtCcy means "I expect to convert this holding into tgtCcy
    //    and tgtCcy has strengthened 5% vs the holding's own currency"
    //    e.g. holding INR, target USD, +5% → 1 INR buys 5% more USD than today
    const fxOv     = fxHoldOverrides[h.id] || {};
    const fxPct    = fxOv.pct !== undefined && fxOv.pct !== "" ? (parseFloat(fxOv.pct) || 0) : 0;
    const tgtCcy   = fxOv.tgtCcy || (h.currency !== dc ? dc : "USD");
    const fxChg    = fxPct !== 0;

    // Current cross rate: 1 unit of h.currency = X units of tgtCcy
    const curCross = crossRate(h.currency, tgtCcy);
    // Simulated cross rate after the move
    const simCross = curCross * (1 + fxPct / 100);

    // Holding value in h.currency (no change from FX on this part)
    const valOwnCcy    = h.qty * h.currentPrice;   // current price in h.currency
    const valOwnCcySim = h.qty * newPrice;           // simulated price in h.currency

    // Convert to display currency (dc):
    //   value_in_tgtCcy = valOwnCcy * crossRate(h.currency, tgtCcy)
    //   value_in_dc     = value_in_tgtCcy * crossRate(tgtCcy, dc)
    // For the FX scenario, we shift crossRate(h.currency, tgtCcy) by fxPct.
    // crossRate(tgtCcy, dc) stays at market rate.
    const tgtToDC   = crossRate(tgtCcy, dc);

    const oldValueDC  = valOwnCcy    * curCross * tgtToDC;
    const newValueDC  = valOwnCcySim * simCross  * tgtToDC;
    const delta       = newValueDC - oldValueDC;

    // Decompose delta:
    //   priceDelta = effect of price move only (hold fx at simulated)
    const priceDelta = priceChg
      ? (valOwnCcySim - valOwnCcy) * simCross * tgtToDC
      : 0;
    //   fxDelta = effect of FX move only (hold price at current)
    const fxDelta = fxChg
      ? valOwnCcy * (simCross - curCross) * tgtToDC
      : 0;

    const changed = priceChg || fxChg;

    return { ...h,
      newPrice, pricePct, priceChg,
      fxPct, fxChg, tgtCcy, curCross, simCross,
      oldValueDC, newValueDC,
      delta, priceDelta, fxDelta, changed,
    };
  });

  // ── Aggregates ────────────────────────────────────────────────────────────
  const oldTotal      = rows.reduce((s, r) => s + r.oldValueDC, 0) || 1;
  const newTotal      = rows.reduce((s, r) => s + r.newValueDC, 0);
  const totalDelta    = newTotal - oldTotal;
  const totalDeltaPct = (totalDelta / oldTotal) * 100;
  const priceImpact   = rows.reduce((s, r) => s + r.priceDelta, 0);
  const fxImpact      = rows.reduce((s, r) => s + r.fxDelta, 0);
  const changedCount  = rows.filter(r => r.changed).length;

  const filtered = rows.filter(r =>
    wiSearch === "" ||
    r.name.toLowerCase().includes(wiSearch.toLowerCase()) ||
    (r.ticker && r.ticker !== "-" && r.ticker.toLowerCase().includes(wiSearch.toLowerCase()))
  );

  const resetAll = () => { setWiOverrides({}); setFxHoldOverrides({}); setWiPresetActive(null); };

  // Batch helpers
  const applyBatch = (ccy, pct, tgt) => {
    const updates = {};
    filteredHW.forEach(h => {
      if (h.currency === ccy)
        updates[h.id] = { pct: String(pct), tgtCcy: tgt };
    });
    setFxHoldOverrides(p => ({ ...p, ...updates }));
  };
  const clearBatch = (ccy) => {
    setFxHoldOverrides(p => {
      const n = { ...p };
      filteredHW.forEach(h => { if (h.currency === ccy) delete n[h.id]; });
      return n;
    });
  };

  const portfolioCurrencies = [...new Set(filteredHW.map(h => h.currency))];

  // ── Input helpers ─────────────────────────────────────────────────────────
  const numInput = (val, onChange, width = 64) => {
    const pct = parseFloat(val);
    return (
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <input
          type="number" step="0.5" placeholder="0"
          value={val}
          onChange={onChange}
          style={{
            ...inp(), width, paddingRight: 18, textAlign: "right", fontSize: 12,
            color:       pct > 0 ? "#059669" : pct < 0 ? "#A32D2D" : T.textPrimary,
            fontWeight:  val && val !== "" ? 700 : 400,
            borderColor: pct > 0 ? "rgba(5,150,105,0.45)" : pct < 0 ? "rgba(220,38,38,0.45)" : undefined,
          }}
        />
        <span style={{ position:"absolute", right:6, fontSize:10, color:T.textMuted, pointerEvents:"none", fontWeight:600 }}>%</span>
      </div>
    );
  };

  const setPrice = (id, v) => {
    if (v === "" || v === "-") setWiOverrides(p => { const n = {...p}; delete n[id]; return n; });
    else setWiOverrides(p => ({ ...p, [id]: v }));
  };
  const setFxPct = (id, v) => {
    if (v === "" || v === "-") {
      setFxHoldOverrides(p => {
        const prev = p[id] || {};
        if (!prev.tgtCcy) { const n = {...p}; delete n[id]; return n; }
        return { ...p, [id]: { ...prev, pct: undefined } };
      });
    } else {
      setFxHoldOverrides(p => ({ ...p, [id]: { ...(p[id] || {}), pct: v } }));
    }
  };
  const setFxTgt = (id, tgt) => {
    setFxHoldOverrides(p => ({ ...p, [id]: { ...(p[id] || {}), tgtCcy: tgt } }));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:T.textPrimary }}>🧮 What-If Simulator</div>
          <div style={{ fontSize:12, color:T.textSecondary, marginTop:2 }}>
            Simulate price moves <em>and</em> currency shifts independently for each holding
          </div>
        </div>
        {changedCount > 0 && (
          <button onClick={resetAll} style={{ padding:"7px 16px", borderRadius:8,
            border:"1px solid rgba(220,38,38,0.4)", background:"transparent",
            color:"#A32D2D", cursor:"pointer", fontSize:12, fontWeight:600 }}>
            ↺ Reset All
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(5,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"Current Value",   val: fmt(oldTotal, dc),                                               color: T.textPrimary,              sub: null },
          { label:"Simulated Value", val: fmt(newTotal, dc),                                               color: totalDelta>=0?"#059669":"#A32D2D", sub: null },
          { label:"Price Impact",    val: (priceImpact>=0?"+":"")+fmt(Math.abs(priceImpact),dc,true),      color: priceImpact>=0?"#059669":"#A32D2D", sub: "from asset moves" },
          { label:"FX Impact",       val: (fxImpact>=0?"+":"")+fmt(Math.abs(fxImpact),dc,true),           color: fxImpact>=0?"#059669":"#A32D2D",    sub: "from rate changes" },
          { label:"Total Impact",    val: (totalDeltaPct>=0?"+":"")+totalDeltaPct.toFixed(2)+"%",         color: totalDelta>=0?"#059669":"#A32D2D",  sub: (totalDelta>=0?"+":"")+fmt(Math.abs(totalDelta),dc,true) },
        ].map(k => (
          <div key={k.label} style={{ ...tCard(), borderRadius:10, padding:"12px 14px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:k.color, opacity:0.4, borderRadius:"10px 10px 0 0" }}/>
            <div style={{ fontSize:10, color:T.textSecondary, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{k.label}</div>
            <div style={{ fontSize:16, fontWeight:700, color:k.color, fontFamily:"'Inter', system-ui, sans-serif" }}>{k.val}</div>
            {k.sub && <div style={{ fontSize:10, color:T.textMuted, marginTop:3 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Batch helper */}
      <div style={{ ...tCard(), marginBottom:18, padding:"14px 16px", background:"rgba(0,120,120,0.03)", border:"1px solid rgba(0,121,193,0.18)" }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#007878", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" }}>
          Quick Batch — apply FX shift to all holdings of a currency at once
        </div>
        <div style={{ fontSize:11, color:T.textSecondary, marginBottom:12 }}>
          Pick a source currency, choose the target you want to convert to, set the % move and click Apply. You can then fine-tune each row individually below.
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {portfolioCurrencies.map(ccy => {
            const bv    = batchVal[ccy] || "";
            const defTgt = ccy !== "USD" ? "USD" : "EUR";
            const bt    = batchTgt[ccy] || defTgt;
            const count = filteredHW.filter(h => h.currency === ccy).length;
            const anySet = filteredHW.filter(h => h.currency === ccy).some(h => fxHoldOverrides[h.id]);
            return (
              <div key={ccy} style={{ display:"flex", flexDirection:"column", gap:6,
                background:T.cardBg, border:`1px solid ${bv ? "#007878" : T.cardBorder}`,
                borderRadius:10, padding:"10px 12px", minWidth:200 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:18 }}>{ccyFlag(ccy)}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:T.textPrimary }}>{ccy}</span>
                  <span style={{ fontSize:10, color:T.textMuted, marginLeft:"auto" }}>{count} holding{count!==1?"s":""}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:11, color:T.textSecondary, flexShrink:0 }}>Convert to</span>
                  <select value={bt} onChange={e => setBatchTgt(p => ({...p,[ccy]:e.target.value}))}
                    style={{ ...inp(), flex:1, fontSize:11, padding:"3px 5px", height:26 }}>
                    {CONV_CCYS.filter(c => c !== ccy).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:11, color:T.textSecondary, flexShrink:0 }}>{bt} moves</span>
                  {numInput(bv, e => setBatchVal(p => ({...p,[ccy]:e.target.value})), 64)}
                </div>
                <div style={{ display:"flex", gap:4 }}>
                  {["-10","-5","+5","+10"].map(p => (
                    <button key={p} onClick={() => setBatchVal(prev => ({...prev,[ccy]:p}))}
                      style={{ flex:1, padding:"3px 0", fontSize:9, borderRadius:4, cursor:"pointer",
                        border:`1px solid ${T.cardBorder}`, background:"transparent",
                        color: p.startsWith("-") ? "#A32D2D" : "#059669", fontWeight:600 }}>{p}%</button>
                  ))}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button
                    onClick={() => {
                    if (!bv) return;
                    const n = parseFloat(bv);
                    if (isNaN(n))          { showToast("⚠ Enter a valid number","#D97706"); return; }
                    if (n < -100 || n > 200) { showToast("⚠ FX move must be between -100% and +200%","#D97706"); return; }
                    applyBatch(ccy, bv, bt);
                  }}
                    disabled={!bv}
                    style={{ flex:1, padding:"5px 0", borderRadius:6, fontSize:11, fontWeight:600,
                      border:"1px solid #007878",
                      background: bv ? "rgba(0,120,120,0.1)" : "transparent",
                      color: bv ? "#007878" : T.textMuted,
                      cursor: bv ? "pointer" : "default" }}>
                    Apply to all
                  </button>
                  {anySet && (
                    <button onClick={() => clearBatch(ccy)}
                      style={{ padding:"5px 10px", borderRadius:6, fontSize:11, cursor:"pointer",
                        border:"1px solid rgba(220,38,38,0.3)", background:"transparent", color:"#A32D2D" }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Quick Scenario presets ── */}
      <div style={{marginBottom:16,padding:"12px 14px",
        background:T.borderSubtle,borderRadius:10,border:`1px solid ${T.cardBorder}`}}>
        <div style={{fontSize:10,fontWeight:700,color:T.textSecondary,
          textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>
          Quick Scenarios — applied to your live holdings
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {WI_PRESETS.map(p=>(
            <button key={p.id} onClick={()=>applyPreset(p)} title={p.desc}
              style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:7,
                border:`1px solid ${wiPresetActive===p.id&&p.id!=="clear"?T.brand:T.cardBorder}`,
                background:wiPresetActive===p.id&&p.id!=="clear"?`${T.brand}15`:T.cardBg,
                color:wiPresetActive===p.id&&p.id!=="clear"?T.brand:T.textSecondary,
                cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.15s"}}>
              {p.label}
            </button>
          ))}
        </div>
        {wiPresetActive && wiPresetActive!=="clear" && (
          <div style={{fontSize:10,color:T.textMuted,marginTop:6}}>
            ✓ {WI_PRESETS.find(p=>p.id===wiPresetActive)?.desc} — adjust individual rows below to fine-tune
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom:12 }}>
        <input style={{ ...inp(), width:"100%", maxWidth:320 }} placeholder="Search holdings…"
          value={wiSearch} onChange={e => setWiSearch(e.target.value)} />
      </div>

      {/* Main table */}
      <div style={{ ...tCard(), borderRadius:12, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth: isMobile ? 560 : 900 }}>
            <thead>
              <tr style={{ background:T.thBg }}>
                {[
                  { label:"Holding",            color:T.thText,  sub:null,          w:"auto" },
                  { label:"Ccy",                color:T.thText,  sub:null,          w:80 },
                  { label:"Price Δ%",           color:"#059669", sub:"asset move",  w:150, bg:"rgba(5,150,105,0.04)" },
                  { label:"FX Δ% → Target",     color:"#2563EB", sub:"ccy & move",  w:170, bg:"rgba(37,99,235,0.04)" },
                  { label:`Current`,            color:T.thText,  sub:dc,            w:110 },
                  { label:`Simulated`,          color:T.thText,  sub:dc,            w:110 },
                  { label:"Price P&L",          color:T.thText,  sub:null,          w:100 },
                  { label:"FX P&L",             color:T.thText,  sub:null,          w:110 },
                  { label:"Net Impact",         color:T.thText,  sub:null,          w:100 },
                ].map(h => (
                  <th key={h.label} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700,
                    color:h.color, textTransform:"uppercase", letterSpacing:"0.05em",
                    borderBottom:`1px solid ${T.cardBorder}`, whiteSpace:"nowrap",
                    background: h.bg || "transparent", width: h.w }}>
                    {h.label}
                    {h.sub && <div style={{ fontSize:9, fontWeight:400, color:T.textMuted, marginTop:1 }}>{h.sub}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const rowBg = r.changed
                  ? r.delta >= 0 ? "rgba(5,150,105,0.05)" : "rgba(220,38,38,0.05)"
                  : i % 2 === 0 ? T.cardBg : T.surfaceBg;
                const curPct  = wiOverrides[r.id] || "";
                const fxOvRow = fxHoldOverrides[r.id] || {};
                const fxPctDisp = fxOvRow.pct !== undefined ? fxOvRow.pct : "";
                const fxTgtDisp = fxOvRow.tgtCcy || (r.currency !== "USD" ? "USD" : "EUR");

                return (
                  <tr key={r.id} style={{ background:rowBg, borderBottom:`1px solid ${T.cardBorder}`, transition:"background 0.2s" }}>

                    {/* Holding */}
                    <td style={{ padding:"10px 12px" }}>
                      <div style={{ fontSize:12, fontWeight:600, color:T.textPrimary }}>{r.name}</div>
                      <div style={{ fontSize:10, color:T.textMuted, fontFamily:"'Inter', system-ui, sans-serif" }}>
                        {r.ticker && r.ticker !== "-" ? r.ticker : r.class}
                      </div>
                    </td>

                    {/* Currency */}
                    <td style={{ padding:"10px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <span style={{ fontSize:15 }}>{ccyFlag(r.currency)}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:T.textPrimary }}>{r.currency}</span>
                      </div>
                    </td>

                    {/* Price Δ% */}
                    <td style={{ padding:"8px 10px", background:"rgba(5,150,105,0.02)" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                        {numInput(curPct, e => setPrice(r.id, e.target.value), 62)}
                        {["-10","+10"].map(p => (
                          <button key={p} onClick={() => setPrice(r.id, p)}
                            style={{ padding:"2px 4px", fontSize:8, borderRadius:3, cursor:"pointer",
                              border:`1px solid ${T.cardBorder}`, background:"transparent",
                              color: p.startsWith("-") ? "#A32D2D" : "#059669", fontWeight:600 }}>{p}</button>
                        ))}
                        {r.priceChg && (
                          <button onClick={() => setPrice(r.id, "")}
                            style={{ background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:13 }}>✕</button>
                        )}
                      </div>
                      {r.priceChg && (
                        <div style={{ fontSize:9, color:T.textMuted, marginTop:2 }}>
                          {r.currentPrice.toFixed(3)} → {r.newPrice.toFixed(3)} {r.currency}
                        </div>
                      )}
                    </td>

                    {/* FX Δ% + target — single compact row */}
                    <td style={{ padding:"8px 10px", background:"rgba(37,99,235,0.02)" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"nowrap" }}>
                        <select value={fxTgtDisp}
                          onChange={e => setFxTgt(r.id, e.target.value)}
                          style={{ ...inp(), fontSize:10, padding:"2px 4px", height:24, width:54, flexShrink:0 }}>
                          {CONV_CCYS.filter(c => c !== r.currency).map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        {numInput(fxPctDisp, e => setFxPct(r.id, e.target.value), 56)}
                        {["-5","+5"].map(p => (
                          <button key={p}
                            onClick={() => setFxHoldOverrides(prev => ({
                              ...prev, [r.id]: { ...(prev[r.id]||{}), pct: p, tgtCcy: fxTgtDisp }
                            }))}
                            style={{ padding:"2px 3px", fontSize:8, borderRadius:3, cursor:"pointer",
                              border:`1px solid ${T.cardBorder}`, background:"transparent",
                              color: p.startsWith("-") ? "#A32D2D" : "#059669", fontWeight:600 }}>{p}</button>
                        ))}
                        {r.fxChg && (
                          <button onClick={() => setFxHoldOverrides(p => { const n={...p}; delete n[r.id]; return n; })}
                            style={{ background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:13 }}>✕</button>
                        )}
                      </div>
                      {r.fxChg && (
                        <div style={{ fontSize:9, color:"#2563EB", fontFamily:"'Inter', system-ui, sans-serif", marginTop:3 }}>
                          1 {r.currency} = {r.curCross.toFixed(3)}→{r.simCross.toFixed(3)} {r.tgtCcy}
                        </div>
                      )}
                    </td>

                    {/* Current (dc) */}
                    <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif", color:T.textSecondary }}>
                      {fmt(r.oldValueDC, dc)}
                    </td>

                    {/* Simulated (dc) */}
                    <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif",
                      color: r.changed ? (r.newValueDC >= r.oldValueDC ? "#059669" : "#A32D2D") : T.textPrimary,
                      fontWeight: r.changed ? 700 : 400 }}>
                      {fmt(r.newValueDC, dc)}
                    </td>

                    {/* Price gain/loss */}
                    <td style={{ padding:"10px 12px" }}>
                      {r.priceChg ? (
                        <span style={{ fontSize:12, fontWeight:700, fontFamily:"'Inter', system-ui, sans-serif",
                          color: r.priceDelta >= 0 ? "#059669" : "#A32D2D" }}>
                          {r.priceDelta >= 0 ? "+" : ""}{fmt(Math.abs(r.priceDelta), dc)}
                        </span>
                      ) : <span style={{ color:T.textMuted, fontSize:11 }}>—</span>}
                    </td>

                    {/* FX gain/loss */}
                    <td style={{ padding:"10px 12px" }}>
                      {r.fxChg ? (
                        <div>
                          <span style={{ fontSize:12, fontWeight:700, fontFamily:"'Inter', system-ui, sans-serif",
                            color: r.fxDelta >= 0 ? "#059669" : "#A32D2D" }}>
                            {r.fxDelta >= 0 ? "+" : ""}{fmt(Math.abs(r.fxDelta), dc)}
                          </span>
                          <div style={{ fontSize:9, color:T.textMuted, marginTop:1 }}>
                            {r.currency}→{r.tgtCcy} {r.fxPct > 0 ? "▲" : "▼"}{Math.abs(r.fxPct).toFixed(1)}%
                          </div>
                        </div>
                      ) : <span style={{ color:T.textMuted, fontSize:11 }}>—</span>}
                    </td>

                    {/* Net impact */}
                    <td style={{ padding:"10px 12px" }}>
                      {r.changed ? (
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, fontFamily:"'Inter', system-ui, sans-serif",
                            color: r.delta >= 0 ? "#059669" : "#A32D2D" }}>
                            {r.delta >= 0 ? "+" : ""}{fmt(Math.abs(r.delta), dc)}
                          </div>
                          <div style={{ fontSize:10, color:T.textMuted, marginTop:1 }}>
                            {r.delta >= 0 ? "+" : ""}{r.oldValueDC > 0 ? ((r.delta / r.oldValueDC) * 100).toFixed(1) : 0}%
                          </div>
                        </div>
                      ) : <span style={{ color:T.textMuted, fontSize:11 }}>—</span>}
                    </td>

                  </tr>
                );
              })}
            </tbody>

            {changedCount > 0 && (
              <tfoot>
                <tr style={{ background:`${T.brand}0a`, borderTop:`2px solid ${T.brand}` }}>
                  <td colSpan={4} style={{ padding:"10px 12px", fontWeight:700, color:T.brand, fontSize:12 }}>
                    Portfolio Total ({changedCount} holding{changedCount !== 1 ? "s" : ""} changed)
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif", fontWeight:600 }}>{fmt(oldTotal, dc)}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif", fontWeight:700,
                    color: totalDelta >= 0 ? "#059669" : "#A32D2D" }}>{fmt(newTotal, dc, true)}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif", fontWeight:700,
                    color: priceImpact >= 0 ? "#059669" : "#A32D2D" }}>
                    {priceImpact >= 0 ? "+" : ""}{fmt(Math.abs(priceImpact), dc, true)}
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:12, fontFamily:"'Inter', system-ui, sans-serif", fontWeight:700,
                    color: fxImpact >= 0 ? "#059669" : "#A32D2D" }}>
                    {fxImpact >= 0 ? "+" : ""}{fmt(Math.abs(fxImpact), dc, true)}
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:13, fontFamily:"'Inter', system-ui, sans-serif", fontWeight:700,
                    color: totalDelta >= 0 ? "#059669" : "#A32D2D" }}>
                    {totalDelta >= 0 ? "+" : ""}{fmt(Math.abs(totalDelta), dc, true)}
                    <div style={{ fontSize:10, fontWeight:400, color:T.textMuted }}>
                      {totalDeltaPct >= 0 ? "+" : ""}{totalDeltaPct.toFixed(2)}%
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div style={{ fontSize:11, color:T.textMuted, marginTop:10, lineHeight:1.6 }}>
        ℹ️ <strong>Price Δ%</strong>: the asset's price moves by this % (e.g. stock drops 10%). &nbsp;
        <strong>FX Δ%</strong>: pick a target currency and set how much it moves vs your holding's currency.
        +5% = target currency strengthened (you get more when converting). −5% = target weakened.
        Every holding is fully independent — INR→USD can be −8% on one holding while INR→USD is +3% on another.
      </div>
    </div>
  );
}

// ── QuickAddTransaction — top-level component (avoids hooks-in-IIFE) ──
function QuickAddTransaction({ isAdmin, holdings, loggedInClientId, setTransactions, addTransaction, postTxnsToHoldings, showToast, isMobile, T, inp }) {
  const myHoldings = isAdmin ? holdings : holdings.filter(h => h.clientId === loggedInClientId);
  const [qtForm, setQtForm] = React.useState({
    date: new Date().toISOString().slice(0,10),
    holding: "", type: "BUY", qty: "", price: "", currency: "USD", fees: ""
  });
  const [qtOpen, setQtOpen] = React.useState(false);
  const CURRENCIES_QT = ["USD","EUR","GBP","INR","JPY","CHF","AED","SGD","HKD","CAD","AUD","CHF","SGD","CNY","KRW","BRL","ZAR","SAR","QAR","KWD"];
  const submitQt = () => {
    if (!qtForm.holding || !qtForm.qty || !qtForm.price) {
      // Field-level validation
      const txnErrs = [];
      if (!form.holding)                                    txnErrs.push("Holding name is required");
      if (!form.qty || parseFloat(form.qty) <= 0)          txnErrs.push("Quantity must be > 0");
      if (!form.price || parseFloat(form.price) <= 0)      txnErrs.push("Price must be > 0");
      if (!form.date)                                       txnErrs.push("Transaction date is required");
      if (form.date && new Date(form.date) > new Date())    txnErrs.push("Date cannot be in the future");
      if (txnErrs.length > 0) { showToast("⚠ " + txnErrs[0], "#D97706"); return; }
    }
    const t = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2,6),
      date: qtForm.date, holding: qtForm.holding, type: qtForm.type,
      qty: parseFloat(qtForm.qty)||0, price: parseFloat(qtForm.price)||0,
      currency: qtForm.currency, fees: parseFloat(qtForm.fees)||0,
      ...(loggedInClientId && !isAdmin ? {clientId: loggedInClientId} : {})
    };
    setTransactions(p => [t, ...p]);
    addTransaction(t).catch(e => console.error("[Supabase] addTransaction:", e.message));
    if (postTxnsToHoldings) postTxnsToHoldings([[t, 1]]);
    setQtForm({ date: new Date().toISOString().slice(0,10), holding:"", type:"BUY", qty:"", price:"", currency:"USD", fees:"" });
    showToast("✓ Transaction added");
  };
  return (
    <div style={{...{background:"transparent",borderRadius:12,border:"1px solid",borderColor:"#007878",padding:16},marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:qtOpen?16:0}}>
        <div>
          <div style={{fontSize:13,fontWeight:600}}>⚡ Quick Add Transaction</div>
          <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>Add a single buy, sell or dividend event without uploading a file</div>
        </div>
        <button onClick={()=>setQtOpen(p=>!p)}
          style={{background:qtOpen?"transparent":"#007878",
            border:"1px solid #007878",borderRadius:8,
            color:qtOpen?T.textSecondary:"white",
            padding:"7px 16px",cursor:"pointer",fontSize:12,fontWeight:600}}>
          {qtOpen ? "✕ Cancel" : "+ Add Transaction"}
        </button>
      </div>
      {qtOpen && (
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(6,1fr)",gap:10}}>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Date</div>
            <input type="date" style={{...inp(),width:"100%"}} value={qtForm.date}
              onChange={e=>setQtForm(p=>({...p,date:e.target.value}))}/>
          </div>
          <div style={{gridColumn:isMobile?"span 1":"span 2"}}>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Holding</div>
            <select style={{...inp(),width:"100%"}} value={qtForm.holding}
              onChange={e=>setQtForm(p=>({...p,holding:e.target.value}))}>
              <option value="">— Select holding —</option>
              {myHoldings.map(h=>(
                <option key={h.id} value={h.name}>{h.name}{h.ticker&&h.ticker!=="-"?" ("+h.ticker+")":""}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Type</div>
            <select style={{...inp(),width:"100%"}} value={qtForm.type}
              onChange={e=>setQtForm(p=>({...p,type:e.target.value}))}>
              {["BUY","SELL","DIVIDEND","SWITCH_IN","SWITCH_OUT"].map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Qty / Units</div>
            <input type="number" style={{...inp(),width:"100%"}} placeholder="100"
              value={qtForm.qty} onChange={e=>setQtForm(p=>({...p,qty:e.target.value}))}/>
          </div>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Price / NAV</div>
            <input type="number" style={{...inp(),width:"100%"}} placeholder="0.00"
              value={qtForm.price} onChange={e=>setQtForm(p=>({...p,price:e.target.value}))}/>
          </div>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Currency</div>
            <select style={{...inp(),width:"100%"}} value={qtForm.currency}
              onChange={e=>setQtForm(p=>({...p,currency:e.target.value}))}>
              {CURRENCIES_QT.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Fees (opt.)</div>
            <input type="number" style={{...inp(),width:"100%"}} placeholder="0"
              value={qtForm.fees} onChange={e=>setQtForm(p=>({...p,fees:e.target.value}))}/>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",gridColumn:isMobile?"span 2":"span 1"}}>
            <button onClick={submitQt}
              style={{width:"100%",background:"#007878",
                border:"none",borderRadius:8,color:"white",
                padding:"9px 0",cursor:"pointer",fontSize:13,fontWeight:700}}>
              ✓ Save
            </button>
          </div>
          {qtForm.holding && qtForm.qty && qtForm.price && (
            <div style={{gridColumn:"1/-1",background:"rgba(0,120,120,0.05)",border:"1px solid rgba(0,121,193,0.2)",
              borderRadius:8,padding:"8px 14px",fontSize:12,color:T.textSecondary}}>
              Preview: <strong style={{color:T.textPrimary}}>{qtForm.type}</strong> {parseFloat(qtForm.qty)||0} units of <strong style={{color:T.textPrimary}}>{qtForm.holding}</strong> @ {qtForm.currency} {parseFloat(qtForm.price)||0} = <strong style={{color:"#007878"}}>{qtForm.currency} {((parseFloat(qtForm.qty)||0)*(parseFloat(qtForm.price)||0)).toLocaleString(undefined,{maximumFractionDigits:2})}</strong>
              {parseFloat(qtForm.fees)>0 && <span style={{color:T.textMuted}}> + {qtForm.fees} fees</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── GoalModal — top-level component (avoids hooks-in-IIFE violation) ──
function GoalModal({
  editingGoal, isAdmin, loggedInClientId, activeScopeOpt,
  families, clients, dc, allGoals, saveGoals, setGoalModalOpen, setEditingGoal, showToast, T, tInp,
  hwWithOwner = []
}) {
  const isEdit = !!editingGoal?.id;
  const defaultClientId = !isAdmin ? loggedInClientId
    : activeScopeOpt.type==="client" ? activeScopeOpt.id
    : clients[0]?.id || "";
  const defaultFamilyId = activeScopeOpt.type==="family" ? activeScopeOpt.id
    : clients.find(c=>c.id===defaultClientId)?.familyId || "";

  const [form, setForm] = React.useState(editingGoal
    ? { ...editingGoal,
        // Backfill existing goals (no inflation field) with 6% default.
        // User can change before saving. Will display "may change displayed numbers" hint.
        inflationRate: editingGoal.inflationRate ?? 6,
        targetIsToday: editingGoal.targetIsToday ?? true, // assume legacy goals are today's-money
        currentSIP: editingGoal.currentSIP ?? 0,          // existing monthly contribution
      }
    : {
    name:"", type:"retirement", targetAmount:"", currency:dc,
    targetDate:"", expectedReturn:10,
    inflationRate:"", targetIsToday:true, // user must enter inflation explicitly
    currentSIP:0,                          // monthly SIP currently being contributed
    portfolioAlloc:100, linkedClass:"portfolio", notes:"",
    clientId:defaultClientId, familyId:defaultFamilyId,
  });
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));
  const gtype = GOAL_TYPES.find(t=>t.id===form.type) || GOAL_TYPES[0];

  const handleClientChange = (cid) => {
    const c = clients.find(x=>x.id===cid);
    sf("clientId", cid);
    sf("familyId", c?.familyId || "");
  };

  const save = () => {
    if (!form.name || !form.targetAmount) {
      // Validate goal form
      if (!g.name?.trim())                          { showToast("⚠ Goal name is required", "#D97706"); return; }
      if (!g.targetAmount || parseFloat(g.targetAmount) <= 0)
                                                    { showToast("⚠ Target amount must be greater than 0", "#D97706"); return; }
      if (g.deadline && new Date(g.deadline) < new Date())
                                                    { showToast("⚠ Deadline cannot be in the past", "#D97706"); return; }
      if (parseFloat(g.currentAmount||0) < 0)       { showToast("⚠ Current amount cannot be negative", "#D97706"); return; }
    }
    if (!form.clientId) {
      showToast("⚠ Please assign this goal to a client"); return;
    }
    // Inflation rate must be set explicitly (per user preference: no defaults)
    if (form.inflationRate === "" || form.inflationRate == null || isNaN(parseFloat(form.inflationRate))) {
      showToast("⚠ Please enter an inflation rate (use 0 if not applicable)", "#D97706"); return;
    }
    if (parseFloat(form.inflationRate) < 0) {
      showToast("⚠ Inflation rate cannot be negative", "#D97706"); return;
    }
    const updated = isEdit
      ? allGoals.map(g => g.id===editingGoal.id ? {...form} : g)
      : [...allGoals, {...form, id:Date.now()}];
    saveGoals(updated);
    setGoalModalOpen(false); setEditingGoal(null);
    showToast(`✓ Goal "${form.name}" ${isEdit?"updated":"saved"} for ${clients.find(c=>c.id===form.clientId)?.name||"client"}`);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:600,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&(setGoalModalOpen(false),setEditingGoal(null))}>
      <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,
        width:"100%",maxWidth:540,maxHeight:"92vh",overflowY:"auto",
        boxShadow:"0 24px 80px rgba(0,0,0,0.4)"}}>
        <div style={{padding:"18px 22px 14px",borderBottom:`1px solid ${T.cardBorder}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:15,fontWeight:700,color:T.textPrimary}}>
            {gtype.icon} {isEdit?"Edit":"New"} Goal
          </div>
          <button onClick={()=>{setGoalModalOpen(false);setEditingGoal(null);}}
            style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.textSecondary}}>✕</button>
        </div>

        <div style={{padding:"16px 22px 22px",display:"flex",flexDirection:"column",gap:14}}>
          {/* Assign to client — admin only */}
          {isAdmin && (
            <div style={{background:`${T.brand}0D`,border:`1px solid ${T.brand}33`,
              borderRadius:10,padding:"12px 14px"}}>
              <label style={{fontSize:11,color:T.brand,fontWeight:700,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8,display:"block"}}>
                📌 Assign to Client
              </label>
              <select style={{...tInp(),fontWeight:600}} value={form.clientId}
                onChange={e=>handleClientChange(e.target.value)}>
                <option value="">— Select Client —</option>
                {families.map(fam => (
                  <optgroup key={fam.id} label={`👨‍👩‍👧 ${fam.name}`}>
                    {clients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                ))}
                {clients.filter(c=>!c.familyId).map(c=>(
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {form.clientId && clients.find(c=>c.id===form.clientId) && (
                <div style={{fontSize:11,color:T.textSecondary,marginTop:6}}>
                  Visible to <strong>{clients.find(c=>c.id===form.clientId)?.name}</strong> when they log in
                </div>
              )}
            </div>
          )}

          {/* Goal Type */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8,display:"block"}}>Goal Type</label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {GOAL_TYPES.map(gt=>(
                <button key={gt.id} onClick={()=>sf("type",gt.id)}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",
                    borderRadius:8,border:`1.5px solid ${form.type===gt.id?gt.color:T.cardBorder}`,
                    background:form.type===gt.id?`${gt.color}12`:"transparent",
                    cursor:"pointer",fontSize:12,fontWeight:form.type===gt.id?700:400,
                    color:form.type===gt.id?gt.color:T.textSecondary,transition:"all 0.15s"}}>
                  <span>{gt.icon}</span>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{gt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Goal Name</label>
            <input style={tInp()} placeholder="e.g. Daughter's MBA fund"
              value={form.name} onChange={e=>sf("name",e.target.value)}/>
          </div>

          {/* Target Amount + Currency */}
          <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10}}>
            <div>
              <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Amount</label>
              <input style={tInp()} type="number" placeholder="e.g. 5000000"
                value={form.targetAmount} onChange={e=>sf("targetAmount",parseFloat(e.target.value)||"")}/>
            </div>
            <div>
              <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Currency</label>
              <select style={{...tInp(),width:90}} value={form.currency}
                onChange={e=>sf("currency",e.target.value)}>
                {CURRENCIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Target convention toggle — today's money vs future amount */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>This Amount Is</label>
            <div style={{display:"flex",gap:6,padding:3,background:T.borderSubtle,borderRadius:8}}>
              <button onClick={()=>sf("targetIsToday",true)}
                style={{flex:1,padding:"8px 10px",border:"none",borderRadius:6,cursor:"pointer",
                  background: form.targetIsToday ? "#7C3AED" : "transparent",
                  color: form.targetIsToday ? "white" : T.textPrimary,
                  fontWeight: form.targetIsToday ? 600 : 400, fontSize: 12}}>
                💰 In today's money
              </button>
              <button onClick={()=>sf("targetIsToday",false)}
                style={{flex:1,padding:"8px 10px",border:"none",borderRadius:6,cursor:"pointer",
                  background: !form.targetIsToday ? "#7C3AED" : "transparent",
                  color: !form.targetIsToday ? "white" : T.textPrimary,
                  fontWeight: !form.targetIsToday ? 600 : 400, fontSize: 12}}>
                📅 Future amount at maturity
              </button>
            </div>
            <div style={{fontSize:10,color:T.textSecondary,marginTop:4,fontStyle:"italic"}}>
              {form.targetIsToday
                ? "App will apply inflation to compute the future amount needed. SIP sized accordingly."
                : "You've already factored inflation in. App uses this number as-is at maturity."}
            </div>
          </div>

          {/* Target Date + Expected Return */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Target Date</label>
              {(() => {
                // Parse current value (YYYY-MM-DD or empty)
                const parts = (form.targetDate || "").split("-");
                const curYear  = parts[0] || "";
                const curMonth = parts[1] || "";
                const updateDate = (y, m) => {
                  if (!y || !m) { sf("targetDate", ""); return; }
                  // Use day=01 for simplicity — month-precision is fine for goals
                  sf("targetDate", `${y}-${String(m).padStart(2,"0")}-01`);
                };
                const thisYear = new Date().getFullYear();
                // Allow 1 year back (for back-dated goals) up to 50 years forward
                const years  = Array.from({length: 51}, (_, i) => thisYear - 1 + i);
                const months = [
                  ["01","January"], ["02","February"], ["03","March"], ["04","April"],
                  ["05","May"], ["06","June"], ["07","July"], ["08","August"],
                  ["09","September"], ["10","October"], ["11","November"], ["12","December"]
                ];
                return (
                  <div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr",gap:6}}>
                    <select style={tInp()} value={curMonth}
                      onChange={e=>updateDate(curYear || thisYear, e.target.value)}>
                      <option value="">— Month —</option>
                      {months.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select style={tInp()} value={curYear}
                      onChange={e=>updateDate(e.target.value, curMonth || "01")}>
                      <option value="">— Year —</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                );
              })()}
            </div>
            <div>
              <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Expected Return % p.a.</label>
              <input style={tInp()} type="number" min={1} max={30} step={0.5}
                value={form.expectedReturn}
                onChange={e=>sf("expectedReturn",parseFloat(e.target.value)||10)}/>
            </div>
          </div>

          {/* Inflation Rate — required (per user preference: no default) */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              Inflation Rate % p.a. <span style={{color:"#A32D2D"}}>*</span>
              {!form.targetIsToday && <span style={{color:T.textSecondary,fontWeight:400,textTransform:"none",letterSpacing:0,marginLeft:6}}>(used for progress display only)</span>}
            </label>
            <input style={tInp()} type="number" min={0} max={20} step={0.5}
              placeholder="e.g. 6 for India · 3 for US · 2.5 for Europe · 0 if not applicable"
              value={form.inflationRate ?? ""}
              onChange={e=>sf("inflationRate",e.target.value === "" ? "" : parseFloat(e.target.value))}/>
            {form.targetIsToday && form.targetAmount > 0 && form.targetDate && form.inflationRate !== "" && form.inflationRate >= 0 && (function(){
              const yrs = Math.max(0, (new Date(form.targetDate) - new Date()) / (365.25*24*3600*1000));
              const futureVal = form.targetAmount * Math.pow(1 + (parseFloat(form.inflationRate)||0)/100, yrs);
              const realRet = (((1 + (form.expectedReturn||0)/100) / (1 + (parseFloat(form.inflationRate)||0)/100)) - 1) * 100;
              return (
                <div style={{fontSize:10,color:T.textSecondary,marginTop:5,lineHeight:1.5}}>
                  Future amount needed at maturity: <strong style={{color:"#7C3AED"}}>{form.currency} {futureVal.toLocaleString(undefined,{maximumFractionDigits:0})}</strong>
                  <span style={{margin:"0 6px"}}>·</span>
                  Real return (after inflation): <strong style={{color: realRet > 0 ? "#1D9E75" : "#A32D2D"}}>{realRet.toFixed(1)}%</strong>
                </div>
              );
            })()}
          </div>

          {/* Current monthly SIP — what client is already contributing toward this goal */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              Current Monthly SIP <span style={{color:T.textMuted,fontWeight:400,textTransform:"none",letterSpacing:0}}>(if any)</span>
            </label>
            <input style={tInp()} type="number" min={0} step={1000}
              placeholder={`e.g. 25000 ${form.currency}/month — leave 0 if no SIP yet`}
              value={form.currentSIP || ""}
              onChange={e=>sf("currentSIP", parseFloat(e.target.value)||0)}/>
            <div style={{fontSize:10,color:T.textSecondary,marginTop:4,fontStyle:"italic"}}>
              Used in growth projection chart and to compare current vs required SIP.
            </div>
          </div>

          {/* Portfolio Alloc + Asset Class Link */}
          <div>
            {/* Asset class selector */}
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              Link goal progress to
            </label>
            <select style={tInp()} value={form.linkedClass||"portfolio"}
              onChange={e=>sf("linkedClass",e.target.value)}>
              <option value="portfolio">🗂 Entire Portfolio</option>
              {[...new Set(hwWithOwner.map(h=>h.class))].sort().map(cls=>(
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
            <div style={{fontSize:10,color:T.textMuted,marginTop:3,marginBottom:12}}>
              {form.linkedClass && form.linkedClass!=="portfolio"
                ? `Only ${form.linkedClass} holdings count toward this goal's progress`
                : "All holdings in scope count toward progress"}
            </div>

            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              % of linked assets earmarked for this goal
            </label>
            <div style={{display:"flex",gap:6,marginBottom:6,flexWrap:"wrap"}}>
              {[25,50,75,100].map(v=>(
                <button key={v} type="button"
                  onClick={()=>sf("portfolioAlloc",v)}
                  style={{fontSize:11,padding:"3px 10px",borderRadius:5,cursor:"pointer",
                    fontWeight:600,border:"none",
                    background:form.portfolioAlloc===v?`${gtype.color}22`:T.borderSubtle,
                    color:form.portfolioAlloc===v?gtype.color:T.textMuted,
                    outline:form.portfolioAlloc===v?`2px solid ${gtype.color}`:"2px solid transparent"}}>
                  {v}%
                </button>
              ))}
            </div>
            <input style={tInp()} type="number" min={0} max={100} step={1}
              placeholder="e.g. 30 = 30% of portfolio counts toward this goal"
              value={form.portfolioAlloc||""}
              onChange={e=>sf("portfolioAlloc",parseFloat(e.target.value)||0)}/>
            <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>
              {form.portfolioAlloc===100
                ? "100% — entire portfolio counted toward this goal"
                : form.portfolioAlloc > 0
                  ? `${form.portfolioAlloc}% of portfolio earmarked for this goal`
                  : "Set to 0 means progress will always show 0%"}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={{fontSize:11,color:T.textSecondary,fontWeight:600,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>Notes (optional)</label>
            <input style={tInp()} placeholder="e.g. HDFC Direct MF SIP + existing FDs"
              value={form.notes} onChange={e=>sf("notes",e.target.value)}/>
          </div>

          <button onClick={save}
            style={{background:`linear-gradient(135deg,${gtype.color},${gtype.color}CC)`,
              border:"none",borderRadius:10,color:"white",padding:"11px 24px",
              cursor:"pointer",fontSize:14,fontWeight:700,marginTop:4}}>
            {isEdit ? "Save Changes" : `Add Goal ${gtype.icon}`}
          </button>
        </div>
      </div>
    </div>
  );

}



// ── DivModal — top-level component (avoids hooks-in-IIFE violation) ──
function DivModal({
  editingDiv, isAdmin, activeDivOpt, hwWithOwner, families, clients,
  scopeHoldings, dc, dividends, saveDividends, setDivModalOpen, setEditingDiv, showToast,
  T, tInp, fmt, fxRates
}) {
  const isEdit  = !!editingDiv?.id;
  const [form, setForm] = React.useState(editingDiv || {
    holdingId:"", clientId: activeDivOpt.type==="client" ? activeDivOpt.id : "",
    exDate:"", payDate:"", dps:"", totalReceived:"",
    currency: dc, drip:false, dripShares:"", notes:""
  });
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  // When holding changes, auto-fill currency and clientId
  const handleHoldingChange = (hid) => {
    const h = hwWithOwner.find(x=>String(x.id)===hid);
    if (h) {
      sf("holdingId", hid);
      sf("currency",  h.currency || dc);
      sf("clientId",  h.clientId || "");
      // Auto-calc totalReceived if dps is set
      if (form.dps && h.qty) sf("totalReceived", (parseFloat(form.dps)*h.qty).toFixed(2));
    } else {
      sf("holdingId", hid);
    }
  };
  const handleDpsChange = (val) => {
    sf("dps", val);
    const h = hwWithOwner.find(x=>String(x.id)===form.holdingId);
    if (h?.qty) sf("totalReceived", (parseFloat(val||0)*h.qty).toFixed(2));
  };

  const save = () => {
    // Full dividend validation
    const divErrs = [];
    if (!form.holdingId)                                  divErrs.push("Please select a holding");
    if (!form.exDate)                                     divErrs.push("Ex-dividend date is required");
    if (!form.totalReceived || parseFloat(form.totalReceived) <= 0)
                                                          divErrs.push("Total received must be > 0");
    if (form.exDate && new Date(form.exDate) > new Date()) divErrs.push("Ex-date cannot be in the future");
    if (form.dps && parseFloat(form.dps) < 0)             divErrs.push("Dividend per share cannot be negative");
    if (form.drip && (!form.dripShares || parseFloat(form.dripShares) <= 0))
                                                          divErrs.push("DRIP shares must be > 0 when DRIP is enabled");
    if (divErrs.length > 0) { showToast("⚠ " + divErrs[0], "#D97706"); return; }
    const updated = isEdit
      ? dividends.map(d => d.id===editingDiv.id ? {...form, id:editingDiv.id} : d)
      : [...dividends, {...form, id:Date.now(),
          dps: parseFloat(form.dps)||0,
          totalReceived: parseFloat(form.totalReceived)||0,
          dripShares: parseFloat(form.dripShares)||0 }];
    saveDividends(updated);
    setDivModalOpen(false); setEditingDiv(null);
    const h = hwWithOwner.find(x=>String(x.id)===form.holdingId);
    showToast(`✓ Dividend logged for ${h?.name||"holding"}`);
  };

  const holdingOptions = isAdmin ? hwWithOwner : scopeHoldings;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:600,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&(setDivModalOpen(false),setEditingDiv(null))}>
      <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,
        width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",
        boxShadow:"0 24px 80px rgba(0,0,0,0.4)"}}>
        <div style={{padding:"18px 22px 14px",borderBottom:`1px solid ${T.cardBorder}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:15,fontWeight:700,color:T.textPrimary}}>
            💰 {isEdit?"Edit":"Log"} Dividend / Distribution
          </div>
          <button onClick={()=>{setDivModalOpen(false);setEditingDiv(null);}}
            style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.textSecondary}}>✕</button>
        </div>

        <div style={{padding:"16px 22px 22px",display:"flex",flexDirection:"column",gap:14}}>
          {/* Holding selector */}
          <div>
            <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              Holding
            </label>
            <select style={{...tInp(),fontWeight:600}} value={form.holdingId}
              onChange={e=>handleHoldingChange(e.target.value)}>
              <option value="">— Select Holding —</option>
              {isAdmin
                ? families.map(fam => (
                    <optgroup key={fam.id} label={fam.name}>
                      {hwWithOwner.filter(h=>clients.find(c=>c.id===h.clientId)?.familyId===fam.id)
                        .map(h=><option key={h.id} value={String(h.id)}>{h.name} ({h.ticker!=="-"?h.ticker:h.class})</option>)}
                    </optgroup>
                  ))
                : holdingOptions.map(h=>(
                    <option key={h.id} value={String(h.id)}>{h.name} ({h.ticker!=="-"?h.ticker:h.class})</option>
                  ))
              }
            </select>
          </div>

          {/* Ex-date + Pay date */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                Ex-Dividend Date *
              </label>
              <input style={tInp()} type="date" value={form.exDate}
                onChange={e=>sf("exDate",e.target.value)}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                Payment Date
              </label>
              <input style={tInp()} type="date" value={form.payDate}
                onChange={e=>sf("payDate",e.target.value)}/>
            </div>
          </div>

          {/* DPS + Total Received + Currency */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                Div per Share
              </label>
              <input style={tInp()} type="number" step="0.0001" placeholder="0.00"
                value={form.dps} onChange={e=>handleDpsChange(e.target.value)}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                Total Received *
              </label>
              <input style={tInp()} type="number" step="0.01" placeholder="0.00"
                value={form.totalReceived} onChange={e=>sf("totalReceived",e.target.value)}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                CCY
              </label>
              <select style={{...tInp(),width:80}} value={form.currency}
                onChange={e=>sf("currency",e.target.value)}>
                {CURRENCIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* DRIP toggle */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            padding:"12px 14px",background:form.drip?"rgba(5,150,105,0.07)":T.borderSubtle,
            borderRadius:10,border:`1px solid ${form.drip?"rgba(5,150,105,0.3)":T.cardBorder}`,
            transition:"all 0.2s",cursor:"pointer"}} onClick={()=>sf("drip",!form.drip)}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>
                🔄 DRIP — Dividend Reinvestment
              </div>
              <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                Dividends reinvested as additional shares
              </div>
            </div>
            <div style={{width:40,height:22,borderRadius:11,
              background:form.drip?"#059669":T.cardBorder,position:"relative",
              transition:"background 0.2s",flexShrink:0}}>
              <div style={{position:"absolute",top:3,left:form.drip?20:3,width:16,height:16,
                borderRadius:"50%",background:"white",transition:"left 0.2s",
                boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
            </div>
          </div>

          {form.drip && (
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#059669",
                textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
                Shares Received via DRIP
              </label>
              <input style={{...tInp(),borderColor:"rgba(5,150,105,0.4)"}} type="number"
                step="0.0001" placeholder="0.0000"
                value={form.dripShares} onChange={e=>sf("dripShares",e.target.value)}/>
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,
              textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"}}>
              Notes (optional)
            </label>
            <input style={tInp()} placeholder="e.g. Special dividend, interim distribution…"
              value={form.notes} onChange={e=>sf("notes",e.target.value)}/>
          </div>

          <button onClick={save}
            style={{background:"#1D9E75",border:"none",
              borderRadius:10,color:"white",padding:"11px 24px",cursor:"pointer",
              fontSize:14,fontWeight:700,marginTop:4}}>
            {isEdit ? "Save Changes" : "Log Dividend 💰"}
          </button>
        </div>
      </div>
    </div>
  );

}



// ── NavGroup: single dropdown tab group ──
function NavGroup({ group, activeTab, activeGroupId, onSelect, T, flex=false }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const isGroupActive = activeGroupId === group.id;
  const activeTabInGroup = group.tabs.find(t => t.id === activeTab);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} style={{position:"relative", flex: flex ? 1 : undefined}}>
      <button
        onClick={() => {
          if (group.tabs.length === 1) { onSelect(group.tabs[0].id); return; }
          setOpen(p => !p);
        }}
        style={{
          display:"flex", alignItems:"center", justifyContent:"center", gap:6,
          padding:"13px 16px", background:"none", border:"none", cursor:"pointer",
          borderBottom: isGroupActive ? "2px solid #007878" : "2px solid transparent",
          color: isGroupActive ? "#007878" : T.textSecondary,
          fontSize:13, fontWeight: isGroupActive ? 600 : 400,
          whiteSpace:"nowrap", transition:"color 0.15s", userSelect:"none",
          width: flex ? "100%" : undefined,
        }}
        onMouseEnter={e => { if (!isGroupActive) e.currentTarget.style.color = T.textPrimary; }}
        onMouseLeave={e => { if (!isGroupActive) e.currentTarget.style.color = T.textSecondary; }}
      >
        <span style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
          {group.iconName
            ? <NavIcon name={group.iconName} size={16} color={isGroupActive ? "#007878" : (T.textSecondary||T.textPrimary)} />
            : <span style={{fontSize:14}}>{group.icon}</span>}
        </span>
        <span>{group.label}</span>
        {activeTabInGroup && (
          <span style={{fontSize:10,background:"#00787818",color:"#007878",
            borderRadius:4,padding:"1px 6px",fontWeight:600,marginLeft:2}}>
            {activeTabInGroup.icon} {activeTabInGroup.label}
          </span>
        )}
        {group.tabs.length > 1 && (
          <span style={{fontSize:9,color:isGroupActive?"#007878":T.textMuted,marginLeft:2,
            display:"inline-block",transition:"transform 0.15s",
            transform:open?"rotate(180deg)":"rotate(0deg)"}}>
            ▾
          </span>
        )}
      </button>

      {open && group.tabs.length > 1 && (
        <div style={{
          position:"absolute", top:"100%", left:0,
          background:T.cardBg, border:`1px solid ${T.cardBorder}`,
          borderRadius:"0 8px 8px 8px", boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
          minWidth:190, zIndex:999, overflow:"hidden",
          animation:"fadeSlideDown 0.12s ease",
        }}>
          {group.tabs.map((t, i) => (
            <button key={t.id}
              onClick={() => { onSelect(t.id); setOpen(false); }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                width:"100%", padding:"10px 16px",
                background: activeTab===t.id ? "#00787810" : "transparent",
                border:"none",
                borderBottom: i < group.tabs.length-1 ? `1px solid ${T.borderSubtle}` : "none",
                cursor:"pointer", textAlign:"left",
                color: activeTab===t.id ? "#007878" : T.textPrimary,
                fontSize:13, fontWeight: activeTab===t.id ? 600 : 400,
                transition:"background 0.1s",
              }}
              onMouseEnter={e => { if (activeTab!==t.id) e.currentTarget.style.background = T.borderSubtle; }}
              onMouseLeave={e => { if (activeTab!==t.id) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{width:20,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {t.iconName
                  ? <NavIcon name={t.iconName} size={18} color={activeTab===t.id ? "#007878" : (T.textSecondary||T.textPrimary)} />
                  : <span style={{fontSize:15}}>{t.icon}</span>}
              </span>
              <span>{t.label}</span>
              {t.planRequired && t.planRequired !== "free" && (
                <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,padding:"1px 5px",
                  borderRadius:6,background:PLAN_CONFIG[t.planRequired]?.color+"20",
                  color:PLAN_CONFIG[t.planRequired]?.color}}>
                  {PLAN_CONFIG[t.planRequired]?.icon}
                </span>
              )}
              {activeTab===t.id && <span style={{marginLeft:"auto",fontSize:12,color:"#007878"}}>●</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────── FwSidebar ───────────────
// Self-contained collapsible sidebar + mobile drawer.
// Props: T, TAB_GROUPS, tab, onNav, isAdmin, canAccess, clientPlans,
//        loggedInClientId, visibleClients, isMobile,
//        collapsed, setCollapsed, mobileOpen, setMobileOpen,
//        openGroups, toggleGroup
function FwSidebar({ T, TAB_GROUPS, tab, onNav, isAdmin, canAccess, clientPlans, onMethodology,
    loggedInClientId, visibleClients, isMobile,
    collapsed, setCollapsed, mobileOpen, setMobileOpen,
    openGroups, toggleGroup,
    dc, setDc, refreshing, onRefresh, onReport,
    profileData, clients, themeId, isLightMode, toggleTheme,
    onProfile, onChangePassword, onSignOut,
    pinnedTabs = [], togglePinTab }) {
  const [hovered,  setHovered]  = React.useState(false);
  const [ccyOpen,  setCcyOpen]  = React.useState(false);
  const leaveTimer = React.useRef(null);

  // expanded = pinned open  OR  hovering while collapsed  OR  mobile
  const expanded = !collapsed || hovered || isMobile;
  const W = expanded ? 220 : 56;

  const CURRENCIES = ["USD","INR","EUR","GBP","AED","SGD","JPY","CHF","HKD","CAD","AUD"];

  const onEnter = () => {
    clearTimeout(leaveTimer.current);
    if (collapsed) setHovered(true);
  };
  const onLeave = () => {
    leaveTimer.current = setTimeout(() => { setHovered(false); setCcyOpen(false); }, 150);
  };

  // ── Shared style helpers ──
  const navItem = (isActive, isIcon) => ({
    display:"flex", alignItems:"center",
    gap: isIcon ? 0 : 8,
    padding: isIcon ? "10px 0" : "6px 12px 6px 32px",
    margin: isActive && !isIcon ? "1px 8px 1px 0" : "1px 8px",
    borderRadius: isActive && !isIcon ? "0 6px 6px 0" : 6,
    // Left accent bar for active item
    borderLeft: isActive && !isIcon ? `2px solid ${T.brand}` : "2px solid transparent",
    background: isActive ? T.navActiveBg : "transparent",
    color: isActive ? T.navActiveText : T.navText,
    cursor:"pointer", fontSize:11.5, fontWeight: isActive ? 700 : 500,
    whiteSpace:"nowrap", overflow:"hidden",
    justifyContent: isIcon ? "center" : "flex-start",
    transition:"background 0.1s, color 0.1s",
    letterSpacing: isActive ? "0.01em" : "0",
  });

  // ── Sidebar inner JSX (inlined — no sub-component to avoid remount) ──
  const inner = (
    <React.Fragment>
      {/* ── Logo row ── */}

      <div style={{
        padding: expanded ? "14px 16px 12px" : "12px 0",
        borderBottom:`1px solid ${T.navBorder}`,
        display:"flex", alignItems:"center",
        justifyContent: expanded ? "space-between" : "center",
        flexShrink:0,
      }}>
        {expanded ? (
          <img
            src={T.dark ? FORTHIUS_LOGO_DARK : FORTHIUS_LOGO}
            alt="Forthius Finalytics"
            style={{height:46, width:"auto", objectFit:"contain", cursor:"pointer"}}
            onClick={()=>onNav("summary")}
          />
        ) : (
          <div style={{
            width:32, height:32, borderRadius:5, flexShrink:0,
            background:`linear-gradient(135deg,#00205B,#0079C1)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:14, fontWeight:500, color:"#fff",
            cursor:"pointer",
          }} onClick={()=>onNav("summary")}>F</div>
        )}
        {/* Pin / close button */}
        {!isMobile && (
          <button
            onClick={()=>{ setCollapsed(c=>!c); setHovered(false); }}
            title={collapsed ? "Pin sidebar open" : "Collapse sidebar"}
            style={{
              flexShrink:0, background:T.navActiveBg,
              border:`1px solid ${T.cardBorder}`, borderRadius:6,
              width:22, height:22, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer",
              color:T.brand, fontSize:12, fontWeight:700,
              opacity: expanded ? 1 : 0,
              pointerEvents: expanded ? "auto" : "none",
              transition:"opacity 0.2s",
            }}>
            {collapsed ? "📌" : "‹"}
          </button>
        )}
        {isMobile && (
          <button onClick={()=>setMobileOpen(false)}
            style={{flexShrink:0, background:T.borderSubtle,
              border:`1px solid ${T.cardBorder}`, borderRadius:6,
              width:24, height:24, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer", color:T.textMuted, fontSize:14}}>
            ✕
          </button>
        )}
      </div>



      {/* ── Nav groups ── */}
      <div style={{flex:1, overflowY:"auto", overflowX:"hidden", padding:"4px 0 0", minHeight:0}}>

        {/* ── Favourites / Pinned tabs ── */}
        {pinnedTabs.length > 0 && (
          <div style={{marginBottom:4}}>
            {!(!collapsed || hovered || isMobile) ? (
              <div style={{height:1, background:T.border, margin:"5px 10px"}}/>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:7,
                padding:"10px 14px 6px 12px",margin:"0 4px"}}>
                <span style={{fontSize:12,width:16,textAlign:"center",flexShrink:0}}>⭐</span>
                <span style={{flex:1,fontSize:10,fontWeight:700,
                  color:T.navGroupText||"#4a6080",
                  letterSpacing:"0.10em",textTransform:"uppercase"}}>Favourites</span>
              </div>
            )}
            {pinnedTabs.map(tabId => {
              const allTabs = TAB_GROUPS.flatMap(g=>g.tabs);
              const t = allTabs.find(x=>x.id===tabId);
              if (!t) return null;
              const isActive = tab===tabId;
              const iconOnly = !(!collapsed || hovered || isMobile);
              return (
                <div key={tabId}
                  title={iconOnly ? t.label : ""}
                  style={{display:"flex",alignItems:"center",gap:iconOnly?0:8,
                    padding:iconOnly?"10px 0":"6px 12px 6px 32px",
                    margin:isActive&&!iconOnly?"1px 8px 1px 0":"1px 8px",
                    borderRadius:isActive&&!iconOnly?"0 6px 6px 0":6,
                    borderLeft:isActive&&!iconOnly?`2px solid ${T.brand}`:"2px solid transparent",
                    background:isActive?T.navActiveBg:"transparent",
                    color:isActive?T.navActiveText:T.navText,
                    cursor:"pointer",fontSize:11.5,fontWeight:isActive?700:500,
                    transition:"background 0.15s",position:"relative"}}
                  onMouseEnter={e=>{
                    if(!isActive) e.currentTarget.style.background=T.navActiveBg;
                    e.currentTarget.querySelector(".unpin-btn").style.opacity="1";
                  }}
                  onMouseLeave={e=>{
                    if(!isActive) e.currentTarget.style.background="transparent";
                    e.currentTarget.querySelector(".unpin-btn").style.opacity="0";
                  }}>
                  <span style={{
                    width:iconOnly?56:20,textAlign:"center",fontSize:13,flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center"
                  }} onClick={()=>onNav(tabId)}>
                    {t.iconName
                      ? <NavIcon name={t.iconName} size={iconOnly?20:17} color={isActive ? T.navActiveText : T.navText} />
                      : t.icon}
                  </span>
                  {!iconOnly && (
                    <React.Fragment>
                      <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                        onClick={()=>onNav(tabId)}>{t.label}</span>
                      <span className="unpin-btn"
                        onClick={e=>{e.stopPropagation();togglePinTab&&togglePinTab(tabId);}}
                        title="Remove from favourites"
                        style={{opacity:0,fontSize:11,color:T.textMuted,cursor:"pointer",
                          flexShrink:0,padding:"0 2px",transition:"opacity 0.15s",
                          lineHeight:1}}>✕</span>
                    </React.Fragment>
                  )}
                </div>
              );
            })}
            {(!collapsed || hovered || isMobile) && (
              <div style={{height:1, background:T.border, margin:"6px 12px 2px"}}/>
            )}
          </div>
        )}

        {TAB_GROUPS.map(group => {
          const hasActive = group.tabs.some(t => t.id === tab);
          const groupOpen = openGroups[group.id] === true;
          const iconOnly  = !expanded;
          return (
            <div key={group.id} style={{marginBottom:2}}>
              {iconOnly ? (
                /* Collapsed: thin divider between groups */
                <div style={{height:1, background:T.border, margin:"5px 10px"}}/>
              ) : (
                /* Expanded: group header — institutional style */
                <div
                  onClick={()=>toggleGroup(group.id)}
                  style={{display:"flex",alignItems:"center",gap:7,
                    padding:"10px 14px 6px 12px",margin:"0 4px",borderRadius:5,cursor:"pointer",
                    background:"transparent",
                    transition:"background 0.1s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{width:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                    opacity: hasActive ? 1 : 0.65}}>
                    {group.iconName
                      ? <NavIcon name={group.iconName} size={15} color={hasActive ? (T.navActiveText||T.brand) : (T.navGroupText||"#4a6080")} />
                      : <span style={{fontSize:12}}>{group.icon}</span>}
                  </span>
                  <span style={{flex:1,fontSize:10,fontWeight:700,
                    color: hasActive ? (T.navActiveText||T.brand) : (T.navGroupText||"#4a6080"),
                    letterSpacing:"0.10em",textTransform:"uppercase",
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{group.label}</span>
                  <span style={{fontSize:9,color:T.navGroupText||"#4a6080",flexShrink:0,
                    opacity: groupOpen ? 1 : 0.75,
                    transform:groupOpen?"rotate(90deg)":"rotate(0deg)",
                    transition:"transform 0.2s ease"}}>›</span>
                </div>
              )}
              {/* Tabs */}
              <div style={{
                overflow:"hidden",
                maxHeight: iconOnly ? "none" : groupOpen ? `${group.tabs.length*44}px` : "0px",
                transition: iconOnly ? "none" : "max-height 0.28s cubic-bezier(0.4,0,0.2,1)",
              }}>
                {group.tabs.map(t => {
                  const isActive = tab===t.id;
                  const locked   = !isAdmin && !canAccess(t.id);
                  const hasPlan  = t.planRequired && t.planRequired!=="free";
                  return (
                    <div key={t.id}
                      onClick={()=>onNav(t.id)}
                      title={iconOnly ? t.label : ""}
                      style={{...navItem(isActive, iconOnly), position:"relative"}}
                      onMouseEnter={e=>{
                        if(!isActive) e.currentTarget.style.background=T.navActiveBg;
                        const pb = e.currentTarget.querySelector(".pin-btn");
                        if(pb) pb.style.opacity="1";
                      }}
                      onMouseLeave={e=>{
                        if(!isActive) e.currentTarget.style.background="transparent";
                        const pb = e.currentTarget.querySelector(".pin-btn");
                        if(pb) pb.style.opacity="0";
                      }}
                      onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background="transparent"; }}>
                      <span style={{flexShrink:0,width:16,display:"flex",alignItems:"center",justifyContent:"center",
                        opacity:1}}>
                        {locked
                          ? <NavIcon name="locked" size={14} color={T.textMuted||T.navText} />
                          : t.iconName
                            ? <NavIcon name={t.iconName} size={16} color={isActive ? (T.navActiveText||T.brand) : T.navText} />
                            : <span style={{fontSize:13}}>{t.icon}</span>}
                      </span>
                      {!iconOnly && (
                        <React.Fragment>
                          <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>
                          {hasPlan && (
                            <span style={{fontSize:9,fontWeight:700,letterSpacing:0.4,
                              background:T.brandLight,color:T.brand,
                              padding:"1px 5px",borderRadius:4,flexShrink:0,textTransform:"uppercase"}}>
                              {t.planRequired}
                            </span>
                          )}
                          {isActive && <span style={{width:6,height:6,borderRadius:"50%",background:T.brand,flexShrink:0,marginLeft:4}}/>}
                        </React.Fragment>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Footer: currency + refresh ── */}
      <div style={{borderTop:`1px solid ${T.navBorder}`,padding:"8px 8px 14px",
        flexShrink:0,
        paddingBottom: "12px",
      }}>



        {/* ── Option C bottom toolbar (expanded sidebar only) ── */}
        {(()=>{
          const userId2 = isAdmin ? "admin" : loggedInClientId;
          const profile2 = (profileData||{})[userId2] || {};
          const clientObj2 = loggedInClientId ? (clients||[]).find(c=>c.id===loggedInClientId) : null;
          const dispName2 = isAdmin ? (profile2.displayName||"Admin") : (clientObj2?.name||"User");
          const initials2 = dispName2.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
          const plan2 = isAdmin ? "Premium" : "Free";
          const avatarBg2 = isAdmin ? "linear-gradient(135deg,#00205B,#0079C1)" : "linear-gradient(135deg,#0079C1,#60C8FF)";
          return expanded ? (
          <div style={{padding:"8px 10px 10px",borderTop:`1px solid ${T.cardBorder}`,marginTop:2}}>
            {/* Row 1: Avatar + name */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,cursor:"pointer"}}
              onClick={onProfile}>
              <div style={{width:28,height:28,borderRadius:"50%",background:avatarBg2,flexShrink:0,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:11,fontWeight:700,color:"white"}}>
                {profile2.photoDataUrl
                  ? <img src={profile2.photoDataUrl} style={{width:28,height:28,borderRadius:"50%",objectFit:"cover"}} alt=""/>
                  : initials2}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{dispName2}</div>
                <div style={{fontSize:10,color:T.brand,textTransform:"uppercase",letterSpacing:"0.04em"}}>{plan2}</div>
              </div>
            </div>

            {/* Row 2: icon buttons + currency */}
            <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:6}}>
              <button onClick={onRefresh} disabled={refreshing}
                title={refreshing?"Refreshing…":"Refresh Prices"}
                style={{width:30,height:28,display:"flex",alignItems:"center",justifyContent:"center",
                  border:"none",borderRadius:6,cursor:refreshing?"not-allowed":"pointer",
                  background:T.brand,color:"white",fontSize:14,flexShrink:0,
                  opacity:refreshing?0.6:1,transition:"opacity 0.15s"}}>
                <span style={{display:"inline-block",animation:refreshing?"spin 1s linear infinite":"none"}}>↻</span>
              </button>
              {onReport && (
                <button onClick={onReport} title="Generate Report"
                  style={{width:30,height:28,display:"flex",alignItems:"center",justifyContent:"center",
                    border:`1px solid ${T.cardBorder}`,borderRadius:6,cursor:"pointer",
                    background:"transparent",color:T.brand,fontSize:13,flexShrink:0}}>
                  📄
                </button>
              )}
              <button onClick={onSignOut} title="Sign Out"
                style={{width:30,height:28,display:"flex",alignItems:"center",justifyContent:"center",
                  border:`1px solid ${T.cardBorder}`,borderRadius:6,cursor:"pointer",
                  background:"transparent",color:T.textMuted,fontSize:12,flexShrink:0}}
                onMouseEnter={e=>{e.currentTarget.style.color="#A32D2D";e.currentTarget.style.borderColor="rgba(220,38,38,0.3)";}}
                onMouseLeave={e=>{e.currentTarget.style.color=T.textMuted;e.currentTarget.style.borderColor=T.cardBorder;}}>
                ⎋
              </button>
              <div style={{marginLeft:"auto",position:"relative"}}>
                <button onClick={e=>{e.stopPropagation();setCcyOpen(o=>!o);}}
                  title="Base currency"
                  style={{padding:"4px 8px",border:`1px solid ${ccyOpen?T.brand:T.cardBorder}`,
                    borderRadius:6,background:ccyOpen?`${T.brand}10`:T.inputBg,
                    color:T.brand,fontSize:11,fontWeight:700,cursor:"pointer",
                    transition:"all 0.15s",letterSpacing:"0.04em",whiteSpace:"nowrap"}}>
                  {dc} ⌄
                </button>
                {ccyOpen && (
                  <div onMouseEnter={()=>clearTimeout(leaveTimer.current)}
                    style={{position:"absolute",right:0,bottom:"calc(100% + 6px)",
                      background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                      borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,0.16)",
                      padding:"8px 6px",zIndex:500,minWidth:110}}>
                    <div style={{fontSize:9,fontWeight:700,color:T.textMuted,
                      letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4,paddingLeft:6}}>Currency</div>
                    {CURRENCIES.map(c=>(
                      <div key={c} onMouseDown={()=>{setDc(c);setCcyOpen(false);}}
                        style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
                          color:c===dc?T.brand:T.textPrimary,background:c===dc?`${T.brand}12`:"transparent",
                          display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.navActiveBg}
                        onMouseLeave={e=>e.currentTarget.style.background=c===dc?`${T.brand}12`:"transparent"}>
                        <span>{c}</span>{c===dc&&<span style={{fontSize:10,color:T.brand}}>✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: live status */}
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              {refreshing
                ? <React.Fragment>
                    <span style={{fontSize:11,display:"inline-block",animation:"spin 0.9s linear infinite",color:T.brand}}>⟳</span>
                    <span style={{fontSize:10,color:T.brand}}>Fetching prices…</span>
                  </React.Fragment>
                : <React.Fragment>
                    <div style={{width:5,height:5,borderRadius:"50%",background:"#1D9E75",flexShrink:0}}/>
                    <span style={{fontSize:10,color:T.textMuted}}>Live · {typeof lastUpd!=="undefined"?lastUpd.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}):""}</span>
                  </React.Fragment>
              }
            </div>
          </div>
          ) : (
            /* Collapsed: single avatar icon + stacked tool buttons */
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,paddingBottom:4}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:avatarBg2,flexShrink:0,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:11,fontWeight:700,color:"white",cursor:"pointer"}}
                onClick={onProfile} title={dispName2}>
                {profile2.photoDataUrl
                  ? <img src={profile2.photoDataUrl} style={{width:30,height:30,borderRadius:"50%",objectFit:"cover"}} alt=""/>
                  : initials2}
              </div>
              <button onClick={onRefresh} disabled={refreshing} title="Refresh Prices"
                style={{width:30,height:26,display:"flex",alignItems:"center",justifyContent:"center",
                  background:T.brand,border:"none",borderRadius:5,cursor:refreshing?"not-allowed":"pointer",
                  color:"white",fontSize:13,opacity:refreshing?0.6:1}}>
                <span style={{animation:refreshing?"spin 1s linear infinite":"none"}}>↻</span>
              </button>
              {onReport && (
                <button onClick={onReport} title="Generate Report"
                  style={{width:30,height:26,display:"flex",alignItems:"center",justifyContent:"center",
                    background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:5,
                    cursor:"pointer",color:T.brand,fontSize:13}}>
                  📄
                </button>
              )}
              <button onClick={e=>{e.stopPropagation();setCcyOpen(o=>!o);}} title="Base currency"
                style={{width:30,padding:"3px 0",background:ccyOpen?`${T.brand}18`:T.navActiveBg,
                  border:`1px solid ${ccyOpen?T.brand:T.cardBorder}`,borderRadius:5,
                  color:T.brand,fontSize:9,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                {dc}
              </button>
              {ccyOpen && (
                <div onMouseEnter={()=>clearTimeout(leaveTimer.current)}
                  style={{position:"absolute",left:"calc(100% + 10px)",bottom:40,
                    background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                    borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,0.16)",
                    padding:"8px 6px",zIndex:500,minWidth:110}}>
                  <div style={{fontSize:9,fontWeight:700,color:T.textMuted,
                    letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4,paddingLeft:6}}>Currency</div>
                  {CURRENCIES.map(c=>(
                    <div key={c} onMouseDown={()=>{setDc(c);setCcyOpen(false);}}
                      style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
                        color:c===dc?T.brand:T.textPrimary,background:c===dc?`${T.brand}12`:"transparent",
                        display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
                      onMouseEnter={e=>e.currentTarget.style.background=T.navActiveBg}
                      onMouseLeave={e=>e.currentTarget.style.background=c===dc?`${T.brand}12`:"transparent"}>
                      <span>{c}</span>{c===dc&&<span style={{fontSize:10,color:T.brand}}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={onSignOut} title="Sign Out"
                style={{width:30,height:26,display:"flex",alignItems:"center",justifyContent:"center",
                  background:"transparent",border:`1px solid ${T.cardBorder}`,
                  borderRadius:5,cursor:"pointer",color:T.textMuted,fontSize:11}}>
                ⎋
              </button>
            </div>
          );
        })()}
      </div>
    </React.Fragment>
  );

  /* ── Mobile: drawer overlay ── */
  if (isMobile) return (
    <React.Fragment>
      {mobileOpen && (
        <div onClick={()=>setMobileOpen(false)}
          style={{
            position:"fixed",
            top:0, left:0, right:0,
            bottom:"calc(62px + env(safe-area-inset-bottom, 0px))",
            background:"rgba(0,0,0,0.65)",
            backdropFilter:"blur(2px)",
            WebkitBackdropFilter:"blur(2px)",
            zIndex:600,
            animation:"fw-fade-in 0.2s ease",
            touchAction:"none",
          }}/>
      )}
      <div style={{
        background: T.navBg === "transparent" ? T.cardBg : T.navBg,
        position:"fixed",top:0,left:0,
        bottom:"calc(62px + env(safe-area-inset-bottom, 0px))",  /* above bottom nav */
        width:"min(300px, 85vw)",
        zIndex:601,
        display:"flex",flexDirection:"column",
        transform:mobileOpen?"translateX(0)":"translateX(-100%)",
        transition:"transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        boxShadow:"6px 0 32px rgba(0,0,0,0.3)",
        overflowY:"auto",
        overflowX:"hidden",
        WebkitOverflowScrolling:"touch",
      }}>
        {inner}
      </div>
    </React.Fragment>
  );

  /* ── Desktop: hover-to-expand ── */
  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        background:T.navBg,
        borderRight:`1px solid ${T.navBorder}`,
        position:"sticky", top:0, alignSelf:"flex-start",
        height:"100vh", width:W, minWidth:W,
        flexShrink:0, zIndex:200, overflow:"visible",
        display:"flex", flexDirection:"column",
        transition:"width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1), box-shadow 0.25s",
        boxShadow: hovered && collapsed ? "4px 0 20px rgba(0,0,0,0.10)" : "none",
      }}>
      {inner}
    </div>
  );
}




// ═══════════════════════════════════════════════════════════════
//  e-CAS / NSDL / CDSL Statement Parser  — GEOMETRIC approach
//  Uses pdf.js item-level x,y coordinates to reconstruct table
//  columns precisely, regardless of PDF text stream order.
// ═══════════════════════════════════════════════════════════════


// ── ISIN → NSE Ticker lookup (common Indian equities) ──────────────────
const ISIN_TO_NSE = {
  // Nifty 50
  "INE002A01018":"RELIANCE","INE009A01021":"INFY","INE040A01034":"HDFCBANK",
  "INE062A01020":"SBIN","INE030A01027":"BAJFINANCE","INE001A01036":"ICICIBANK",
  "INE081A01012":"BHARTIARTL","INE066A01021":"KOTAKBANK","INE467B01029":"AXISBANK",
  "INE018A01030":"LARSEN","INE117A01022":"WIPRO","INE326A01037":"TECHM",
  "INE860A01027":"HCLTECH","INE585B01010":"TITAN","INE216A01030":"NESTLEIND",
  "INE669C01036":"ADANIPORTS","INE028A01039":"BAJAJ-AUTO","INE592A01026":"DRREDDY",
  "INE214T01019":"ADANIENT","INE256A01028":"HINDALCO","INE860A01027":"HCLTECH",
  "INE188A01015":"POWERGRID","INE121A01024":"BPCL","INE242A01010":"IOC",
  "INE558B01017":"SUNPHARMA","INE158A01026":"NTPC","INE523A01014":"ULTRACEMCO",
  "INE115A01026":"LT","INE090A01013":"ONGC","INE101A01026":"COALINDIA",
  "INE076B01038":"JSWSTEEL","INE239A01016":"TATAMOTORS","INE155A01022":"TATACONSUM",
  "INE467B01029":"AXISBANK","INE585B01010":"TITAN","INE860A01027":"HCLTECH",
  "INE542B01011":"MARUTI","INE774D01024":"EICHERMOT","INE397D01024":"BAJAJFINSV",
  "INE383A01012":"DIVISLAB","INE066A01021":"KOTAKBANK","INE494B01023":"SHREECEM",
  "INE196A01026":"APOLLOHOSP","INE361B01024":"CIPLA","INE523A01014":"ULTRACEMCO",
  // Nifty Next 50 & common midcaps
  "INE028A01039":"BAJAJ-AUTO","INE192A01025":"BERGERPAINT","INE373A01013":"ASIANPAINT",
  "INE094B01027":"PIDILITIND","INE148A01014":"MARICO","INE053A01029":"DABUR",
  "INE016A01026":"GRASIM","INE038A01020":"HINDZINC","INE172A01027":"EXIDEIND",
  "INE694A01020":"MUTHOOTFIN","INE238A01034":"TATASTEEL","INE029A01011":"HEROMOTOCO",
  "INE455K01017":"ASTRAL","INE070A01015":"HINDPETRO","INE335Y01020":"LTIM",
  "INE758T01015":"LTTS","INE136B01020":"COLPAL","INE259A01022":"BRITANNIA",
  "INE040A01034":"HDFCBANK","INE514E01011":"SIEMENS","INE721A01013":"HAVELLS",
  "INE236A01020":"GODREJCP","INE195A01028":"GODREJPROP","INE434A01013":"MCDOWELL-N",
  "INE591G01017":"COFORGE","INE437A01024":"PAGEIND","INE126A01031":"DMART",
  "INE344W01011":"NAUKRI","INE317D01021":"MPHASIS","INE752E01010":"OFSS",
  "INE211T01025":"INDIGO","INE418H01029":"IRCTC","INE646K01010":"ZOMATO",
  "INE070D01027":"POLYCAB","INE149A01033":"BAJAJ-AUTO","INE211H01019":"TRENT",
  "INE350H01016":"ALKEM","INE600G01020":"MAXHEALTH","INE912D01040":"TORNTPHARM",
  "INE036A01022":"ACC","INE079A01024":"GAIL","INE030B01011":"AMBUJACEM",
  "INE047A01021":"BHEL","INE364A01012":"BPCL","INE034A01011":"NATIONALUM",
  "INE242A01010":"IOC","INE775C01026":"SBICARD","INE589A01014":"MFSL",
  "INE947Q01010":"NYKAA","INE749A01030":"MUTHOOTFIN","INE101A01026":"COALINDIA",
  "INE220H01019":"INDUSINDBK","INE092A01019":"BANKBARODA","INE694A01020":"MUTHOOTFIN",
  "INE205A01011":"FEDERALBNK","INE263A01024":"RBLBANK","INE562A01011":"PNB",
  "INE614G01033":"SBILIFE","INE726G01019":"HDFCLIFE","INE748C01020":"ICICIGI",
  "INE040A01034":"HDFCBANK","INE002A01018":"RELIANCE","INE009A01021":"INFY",
  // Small/mid caps commonly seen in HNI portfolios
  "INE212H01026":"BERGERPAINT","INE239A01016":"TATAMOTORS","INE585B01010":"TITAN",
  "INE192A01025":"BERGERPAINT","INE019A01020":"LICHSGFIN","INE694A01020":"MUTHOOTFIN",
  "INE397D01024":"BAJAJFINSV","INE028A01039":"BAJAJ-AUTO","INE117A01022":"WIPRO",
  "INE326A01037":"TECHM","INE467B01029":"AXISBANK","INE172A01027":"EXIDEIND",
  "INE213A01029":"MRF","INE058A01010":"RAYMOND","INE654A01010":"WHIRLPOOL",
  "INE301A01014":"SCHAEFFLER","INE049B01025":"PERSISTENT","INE591G01017":"COFORGE",
  "INE397H01010":"CAMS","INE040A01034":"HDFCBANK",
  // Add more as needed - this covers ~80% of common HNI holdings
};

// ISIN lookup with fuzzy fallback
function resolveTickerFromISIN(isin, pdfTicker) {
  if (!isin || isin === "-") return pdfTicker || "-";
  const mapped = ISIN_TO_NSE[isin];
  if (mapped) return mapped;
  // If PDF ticker looks reasonable (not clearly wrong), use it
  // A "reasonable" ticker: 2-20 chars, uppercase, no spaces, not a date/number
  if (pdfTicker && pdfTicker !== "-" && /^[A-Z&-]{2,20}$/.test(pdfTicker) && 
      !/^\d|^(INE|INF)/.test(pdfTicker)) {
    return pdfTicker;
  }
  return "-"; // Unknown — user must fill in
}

// ─── Folio Ledger Parser (Forthius Capital XLS format) ───
const FOLIO_LEDGER_PARSER = {
  classifyType(desc) {
    const d = (desc||"").toUpperCase();
    if (d.includes("INITIAL PURCHASE") || d.includes("PURCHASE")) return "BUY";
    if (d.includes("SIP")) return "SIP";
    if (d.includes("STP IN")) return "STP_IN";
    if (d.includes("STP OUT")) return "STP_OUT";
    if (d.includes("SWITCH IN")) return "SWITCH_IN";
    if (d.includes("SWITCH OUT")) return "SWITCH_OUT";
    if (d.includes("REDEMPTION") || d.includes("REDEEM")) return "REDEEM";
    if (d.includes("TRANSFER-IN") || d.includes("TRANSFER IN")) return "BUY";
    if (d.includes("DIVIDEND")) return "DIVIDEND";
    return null;
  },
  parseDate(val) {
    if (!val) return null;
    const s = String(val).trim();
    // DD/MM/YYYY
    const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m1) return `${m1[3]}-${m1[2].padStart(2,"0")}-${m1[1].padStart(2,"0")}`;
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0,10);
    return null;
  },
  async parse(file) {
    // Dynamically import SheetJS
    if (!window.XLSX) {
      await new Promise((res, rej) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    const buf = await file.arrayBuffer();
    const wb = window.XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

    const transactions = [];
    let currentClient = null, currentFund = null, currentIsin = null;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const v0 = row[0] != null ? String(row[0]).trim() : "";
      const v1 = row[1] != null ? String(row[1]).trim() : "";

      // Detect client name — formats seen in Forthius Capital folio XLS:
      // "Seema Pandey[P00004]"  → PAN-indexed client
      // "MR. UPENDRA NAYAK[P00012]" → honorific + PAN
      // "PURVA GAUR[P00007] / JOINT" → joint account
      // Extract name before the first "[" bracket in all cases
      if (/\[P\d+\]/.test(v0)) {
        currentClient = v0.split("[")[0].trim();
      }

      // Detect fund line e.g. "Fund   Bandhan Arbitrage Fund Regular-Growth/ INF194K01649"
      if (/^Fund\s{2,}/.test(v0)) {
        const fundRaw = v0.replace(/^Fund\s+/, "").trim();
        const parts = fundRaw.split("/");
        currentFund = parts[0].trim();
        currentIsin = parts[1] ? parts[1].trim() : null;
      }

      // Detect transaction row — col 0 is a date
      const date = this.parseDate(v0);
      if (!date || !v1 || v1 === "nan") continue;
      const txnType = this.classifyType(v1);
      if (!txnType) continue;

      const tradePrice = parseFloat(row[2]) || 0;
      const debitAmt   = parseFloat(row[3]) || 0;
      const debitUnits = parseFloat(row[5]) || 0;
      const creditAmt  = parseFloat(row[6]) || 0;
      const creditUnits= parseFloat(row[7]) || 0;

      const isBuy = ["BUY","SIP","STP_IN","SWITCH_IN","DIVIDEND"].includes(txnType);
      const qty    = isBuy ? creditUnits : debitUnits;
      const amount = isBuy ? creditAmt   : debitAmt;

      if (qty <= 0 && amount <= 0) continue;

      transactions.push({
        id: `folio_${Date.now()}_${transactions.length}`,
        date,
        holding: currentFund || "Unknown Fund",
        isin: currentIsin || null,
        client: currentClient || null,
        type: txnType,
        qty: Math.round(qty * 10000) / 10000,
        price: Math.round(tradePrice * 10000) / 10000,
        amount: Math.round(amount * 100) / 100,
        currency: "INR",
        fees: 0,
        description: v1,
      });
    }

    // ── Derive current holdings from transaction history ──
    // Group by fund, net units, compute weighted avg cost
    const holdingMap = {};
    for (const t of transactions) {
      const key = t.holding;
      if (!holdingMap[key]) {
        holdingMap[key] = {
          name: t.holding,
          isin: t.isin,
          client: t.client,
          units: 0,
          totalCost: 0,
          currency: t.currency || "INR",
          lastDate: t.date,
        };
      }
      const h = holdingMap[key];
      const isBuy = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
      const isSell = ["REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
      if (isBuy) {
        h.totalCost += t.amount || (t.qty * t.price);
        h.units += t.qty;
      } else if (isSell) {
        // Reduce cost proportionally
        if (h.units > 0) {
          const costPerUnit = h.totalCost / h.units;
          h.totalCost -= costPerUnit * t.qty;
        }
        h.units -= t.qty;
      }
      if (t.date > h.lastDate) h.lastDate = t.date;
    }

    // Filter to only holdings with positive units (not fully redeemed)
    const derivedHoldings = Object.values(holdingMap)
      .filter(h => h.units > 0.001)
      .map(h => ({
        name: h.name,
        isin: h.isin,
        client: h.client,
        units: Math.round(h.units * 1000) / 1000,
        avgCost: h.units > 0 ? Math.round(h.totalCost / h.units * 100) / 100 : 0,
        totalCost: Math.round(h.totalCost * 100) / 100,
        currency: h.currency,
        lastDate: h.lastDate,
      }));

    const clientNames = [...new Set(transactions.map(t => t.client).filter(Boolean))];
    return { transactions, clients: clientNames, derivedHoldings };
  }
};

const ECAS_PARSER = {

  // ── Load pdf.js dynamically ──
  async loadPdfJs() {
    if (window.pdfjsLib) return window.pdfjsLib;
    return new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  },

  // ── Extract ALL text items with coordinates from every page ──
  async extractItems(file, password = "") {
    const pdfjsLib = await this.loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({
      data: arrayBuffer,
      ...(password ? { password } : {}),
    }).promise;

    const allItems = []; // { x, y, w, h, text, page }
    for (let p = 1; p <= pdf.numPages; p++) {
      const page    = await pdf.getPage(p);
      const tc      = await page.getTextContent();
      const vp      = page.getViewport({ scale: 1 });
      const pgH     = vp.height;
      tc.items.forEach(item => {
        const t = item.str.trim();
        if (!t) return;
        const x = item.transform[4];
        const y = pgH - item.transform[5]; // flip Y so 0=top of page
        const w = item.width;
        const h = item.height || 10;
        allItems.push({ x, y, w, h, text: t, page: p });
      });
    }
    return allItems;
  },

  // ── Also build plain text for type detection ──
  async extractText(file, password = "") {
    const items = await this.extractItems(file, password);
    // Simple text — just join sorted by page/y/x
    const sorted = [...items].sort((a,b) =>
      a.page !== b.page ? a.page - b.page :
      Math.abs(a.y - b.y) < 6 ? a.x - b.x : a.y - b.y
    );
    // Group into lines
    let lines = [], lastY = -999, lastPage = -1, cur = "";
    sorted.forEach(it => {
      const newLine = it.page !== lastPage || (it.y - lastY) > 6;
      if (newLine && cur) { lines.push(cur.trim()); cur = ""; }
      cur += (cur ? " " : "") + it.text;
      lastY = it.y; lastPage = it.page;
    });
    if (cur) lines.push(cur.trim());
    return [...new Set(lines)].join("\n"); // deduplicate identical lines
  },

  // ── Detect statement type ──
  detectType(text) {
    const t = text.toUpperCase();
    // NSDL/CDSL take priority — even if PDF mentions CAMS/KFintech for MF folios
    if (t.includes("NSDL") || t.includes("NATIONAL SECURITIES DEPOSITORY")) return "NSDL_CAS";
    if (t.includes("CDSL") || t.includes("CENTRAL DEPOSITORY SERVICES"))   return "CDSL_CAS";
    // Pure MF statements (no NSDL/CDSL header)
    if (t.includes("CAMS") || t.includes("COMPUTER AGE MANAGEMENT"))       return "CAMS_MF";
    if (t.includes("KFINTECH") || t.includes("KARVY"))                      return "KFINTECH_MF";
    return "UNKNOWN";
  },

  // ── Extract holder name ──
  extractHolderName(text) {
    for (const line of text.split("\n").slice(0, 80)) {
      const m = line.match(/(?:Name|Account Holder|Beneficial Owner|BO Name)[:\s]+([A-Z][A-Z ]{2,40})/i);
      if (m) return m[1].trim();
    }
    return null;
  },

  // ══════════════════════════════════════════════════════════
  //  GEOMETRIC NSDL CAS PARSER
  //
  //  ACTUAL NSDL CAS column layout (from real PDF screenshot):
  //
  //  ISIN        | SECURITY NAME           | Free Bal | Safekeep | Pledged | Mkt Price | Value
  //  (col X ~30) | (col X ~130, wraps 3ln) | (~350)   | (~420)   | (~490)  | (~550)    | (~640)
  //
  //  Key observations:
  //  - ISIN is the LEFTMOST column (smallest X), e.g. "INE465A01025"
  //  - Security name is to the RIGHT of ISIN, may wrap 3-4 lines
  //  - Free Balance (qty) is the FIRST number column after the name
  //  - Name cleanup: strip everything after " - " or "#" (corporate action notes)
  //
  //  Strategy: find ISIN column X (small X, ~30-130px).
  //  For each ISIN, name items are at X > isinX+60 but X < numColX.
  //  Numbers are at X > nameColRight.
  // ══════════════════════════════════════════════════════════
  parseNSDLGeometric(items) {
    const holdings = [];
    const seen     = new Set();

    // ── 1. Find all ISIN items ──
    // ISINs may be standalone items OR embedded in longer text (e.g. "NSE INE585B01010")
    const isinItems = [];
    items.forEach(it => {
      const re = /(IN[A-Z0-9]{10})/g;
      let m;
      while ((m = re.exec(it.text)) !== null) {
        const isin = m[1];
        if (isin.startsWith("INF")) continue; // MF ISINs handled separately
        const charFrac = it.text.length > isin.length ? m.index / it.text.length : 0;
        isinItems.push({ ...it, text: isin, x: it.x + charFrac * (it.w || 60) });
      }
    });

    if (isinItems.length === 0) return holdings;

    // ── 2. Detect ISIN column X range from the cluster of ISIN items ──
    const isinXs   = isinItems.map(it => it.x);
    const isinXMin = Math.min(...isinXs);
    const isinXMax = Math.max(...isinXs);
    const isinXMid = (isinXMin + isinXMax) / 2;

    // Column boundaries — ISIN is LEFTMOST, name is to its RIGHT
    // isinXMid is ~50-100px from left edge
    const NAME_X_MIN   = isinXMid + 30;  // name starts to the right of ISIN column
    const NAME_X_MAX   = isinXMid + 280; // name column ends (before number columns)
    const NUM_X_MIN    = isinXMid + 260; // numbers start after name column
    const Y_TOLERANCE  = 18;             // items within 18pt = same row
    const NAME_Y_RANGE = 50;             // names may wrap 3+ lines over ~50pt

    // ── 3. For each ISIN, reconstruct the holding ──
    for (const isinIt of isinItems) {
      const isin = isinIt.text;
      if (seen.has(isin)) continue;

      const isinY    = isinIt.y;
      const isinPage = isinIt.page;

      // ── Collect name items: left of ISIN, within Y_RANGE, same page ──
      // Include rows above the ISIN row to handle wrapped names
      const nameItems = items.filter(it =>
        it.page === isinPage &&
        it.x >= NAME_X_MIN &&
        it.x <= NAME_X_MAX &&
        it.y >= isinY - NAME_Y_RANGE &&
        it.y <= isinY + Y_TOLERANCE &&
        !/^\d[\d,\.]*$/.test(it.text) &&          // exclude pure numbers
        !/\.(NSE|BSE)$/i.test(it.text) &&          // exclude "AARTIIND.NSE" stock symbol lines
        !/^IN[A-Z0-9]{10}$/.test(it.text) &&       // exclude bare ISINs
        !/^(NSE|BSE|NSDL|CDSL|ISIN|ISIN:|FREE|LOCK|FACE VALUE|FACE|QTY|VALUE|EXCHANGE|NAME|SR\.?NO|SL\.?NO|SUB TOTAL|TOTAL|SAFEKEEP|PLEDGED|EARMARKED|CURRENT BAL|MARKET PRICE|NO\. OF|NO\.OF|SHARES|COMPANY NAME|STOCK SYMBOL)$/i.test(it.text)
      );

      // Sort name items by Y then X to reconstruct reading order
      nameItems.sort((a,b) => Math.abs(a.y - b.y) < 4 ? a.x - b.x : a.y - b.y);

      // Group name items into lines (items within 4pt Y = same line)
      const nameLines = [];
      let curLine = [], curY = null;
      nameItems.forEach(it => {
        if (curY === null || Math.abs(it.y - curY) < 6) {
          curLine.push(it.text);
          curY = it.y;
        } else {
          if (curLine.length) nameLines.push(curLine.join(" "));
          curLine = [it.text];
          curY = it.y;
        }
      });
      if (curLine.length) nameLines.push(curLine.join(" "));

      // Build full name — join lines, clean up
      let name = nameLines.join(" ").trim();
      // Strip leading Sr.No digit(s)
      name = name.replace(/^\d{1,3}\s+(?=[A-Z])/, "").trim();
      // Strip NSDL verbose security description suffixes:
      // "BHARAT FORGE LIMITED - NEW EQUITY SHARES OF RS.2/- AFTER SPLIT" → "BHARAT FORGE LIMITED"
      // "HDFC BANK LIMITED#NEW EQUITY SHARES WITH FACE VALUE RE. 1/- AFTER SUBDIVISION" → "HDFC BANK LIMITED"
      // Strip NSDL corporate action suffixes ("BHARAT FORGE LIMITED - NEW EQUITY SHARES OF RS.2/- AFTER SPLIT")
      name = name.replace(/\s*[-#]\s*(NEW EQUITY|NEW RS|NEW RE|EQUITY SHARES|FACE VALUE|AFTER SPLIT|AFTER SUBDIVISION|BONUS|RIGHTS|PARTLY PAID).*/i, "").trim();
      name = name.replace(/\s+NEW\s+(RS|RE|RS\.|RE\.).*$/i, "").trim();
      name = name.replace(/\s+-\s+NEW\b.*/i, "").trim();
      name = name.replace(/\s+(ORDINARY\s+SHARES?|COMMON\s+SHARES?|EQUITY\s+SHARES?)\s*$/i, "").trim();
      // Strip trailing exchange suffix
      name = name.replace(/[\s\-(]*(NSE|BSE)[\s)]*$/i, "").trim();
      // Strip trailing decimals and standalone numbers
      name = name.replace(/(\s+\d+\.\d{2,})+$/, "").trim();
      name = name.replace(/\s+\d+$/, "").trim();

      if (!name || name.length < 2 || /^(NSE|BSE|ISIN|ISIN:|FREE|LOCK|TOTAL|SUB TOTAL)$/i.test(name)) continue;

      // ── Collect numeric items: right of ISIN, same row ──
      const numItems = items.filter(it =>
        it.page === isinPage &&
        it.x > NUM_X_MIN &&
        it.y >= isinY - NAME_Y_RANGE &&
        it.y <= isinY + Y_TOLERANCE &&
        /^[\d,\.]+$/.test(it.text)   // only pure numbers
      ).sort((a,b) => a.x !== b.x ? a.x - b.x : a.y - b.y);

      // Extract numbers in left→right (column) order
      const nums = numItems
        .map(it => parseFloat(it.text.replace(/,/g, "")))
        .filter(v => isFinite(v) && v > 0);

      // NSDL columns after ISIN: FaceVal | FreeQty | LockedQty | Value
      // FaceVal ≤ 100, FreeQty is what we want
      let qty = 0;
      // Column order: Face Value | No. of Shares | Market Price | Value in ₹
      // nums[0] = face value (small: 1.00, 2.00, 5.00, 10.00)
      // nums[1] = No. of Shares  ← this is qty
      // nums[2] = Market Price per share
      // nums[3] = Total Value (largest)
      let price = 0;
      if (nums.length >= 4) {
        // Standard 4-column: skip face value, take shares
        if (nums[0] <= 100) {
          qty   = Math.round(nums[1]);
          price = nums[2];
        } else {
          qty   = Math.round(nums[0]);
          price = nums[1];
        }
      } else if (nums.length === 3) {
        if (nums[0] <= 100) { qty = Math.round(nums[1]); price = nums[2]; }
        else                 { qty = Math.round(nums[0]); price = nums[1]; }
      } else if (nums.length >= 1) {
        qty = Math.round(nums[0]);
      }

      // Detect exchange from items between name column and ISIN
      // Exchange: look for NSE/BSE anywhere in the name or nearby items
      const exchInName = /BSE/i.test(name);
      const exchNearby = items.some(it =>
        it.page === isinPage &&
        Math.abs(it.y - isinY) < Y_TOLERANCE &&
        /^BSE$/i.test(it.text)
      );
      const exch = (exchInName || exchNearby) ? "BSE" : "NSE";

      if (qty > 0 && qty < 5e6) {
        seen.add(isin);
        // Extract stock symbol from ISIN column (second line: "AARTIIND.NSE")
        // Find items in the ISIN column X range, just below the ISIN Y
        const symItem = items.find(it =>
          it.page === isinPage &&
          it.x <= isinXMid + 20 &&
          it.x >= isinXMid - 60 &&
          it.y > isinY && it.y < isinY + 20 &&
          /\.(NSE|BSE)$/i.test(it.text)
        );
        const pdfTicker = symItem ? symItem.text.replace(/\.(NSE|BSE)$/i,"").trim() : "-";
        const ticker = resolveTickerFromISIN(isin, pdfTicker);
        holdings.push({
          name: name.replace(/\s+/g," ").trim(),
          isin,
          ticker,
          class: "Equities",
          exchange: exch,
          currency: "INR",
          qty,
          costPrice: 0,
          currentPrice: price > 0 ? price : 0,
          country: "India",
          listed: true,
          source: "NSDL",
        });
      }
    }

    return holdings;
  },

  // ── Thin text-based fallbacks for CAMS/KFintech MF ──
  parseMFCAS(text) {
    // CAMS/KFintech CAS PDF format (V3.4):
    // Each row (when extracted as text) contains:
    //   Folio | ISIN | Scheme Name | Cost Value (INR) | Unit Balance | NAV Date | NAV (INR) | Market Value (INR)
    //
    // Key insight: costPrice per unit = Cost Value / Unit Balance
    // We collect ALL numbers from each line, then identify each column by position and magnitude.
    //
    // Typical number order in extracted text for one holding:
    //   [folio digits?] [cost_value] [unit_balance] [nav] [market_value]
    // OR for KFINTECH lines:
    //   [unit_balance] [nav_date_digits?] [nav] [market_value]
    //
    // Strategy: find INF ISIN on a line, collect all numbers from that line AND
    // the surrounding 3 lines (PDF may wrap), then resolve columns.

    const holdings = [];
    const seen = new Set();
    const rawLines = text.split("\n");
    let currentAMC = "", currentFolio = "";

    const BAD_NAME = /^(UNCLAIMDISIN|UNCLAIM|\d{1,2}-[A-Z]{3}-\d{4}|PURCHASE|REDEMPTION|SWITCH|DIVIDEND|SIP|STP|Folio|NAV|Unit|Market|Cost|Total|Page|CAMSCAS)/i;

    // Parse a number string with commas
    const parseNum = s => parseFloat((s||"").replace(/,/g,""));

    // Extract all positive numbers from a string
    const extractNums = s => {
      const out = [];
      const re = /(\d{1,12}(?:,\d{2,3})*(?:\.\d+)?)/g;
      let m;
      while ((m = re.exec(s)) !== null) {
        const v = parseNum(m[1]);
        if (v > 0) out.push(v);
      }
      return out;
    };

    for (let i = 0; i < rawLines.length; i++) {
      const line = rawLines[i].trim();
      if (!line) continue;

      // Track folio number
      const folioM = line.match(/^(\d{4,20}(?:\/\d+)?(?:-\d+)?)\s/);
      if (folioM && !/INF/.test(line)) { currentFolio = folioM[1]; }

      // Track AMC (short lines with "Fund" or "Asset" but no numbers)
      if (/fund|asset management|amc/i.test(line) && line.length < 70
          && !/\d{4,}/.test(line) && !/folio|isin|nav|unit/i.test(line)) {
        currentAMC = line.replace(/\s+/g," ").trim();
      }

      // Must have INF ISIN to be a holding line
      const isinM = line.match(/\b(INF[A-Z0-9]{9})\b/);
      if (!isinM) continue;
      const isin = isinM[1];
      if (seen.has(isin)) continue;

      // ── Collect text from this line + up to 2 surrounding lines ──
      // The PDF renderer sometimes splits one table row across 2-3 text lines
      const context = [
        rawLines[i-1] || "",
        line,
        rawLines[i+1] || "",
        rawLines[i+2] || "",
      ].join(" ");

      // ── Extract scheme name ──
      // Name is the text AFTER the ISIN code, before the first digit sequence
      const afterIsin = line.slice(line.indexOf(isin) + 12).trim();
      const nameM = afterIsin.match(/^([A-Za-z][A-Za-z0-9 &\-\.()#,']+?)(?=\s{2,}|\s+\d|$)/);
      let name = nameM ? nameM[1].trim() : afterIsin.split(/\s+\d/)[0].trim();
      // Also check lines before ISIN for scheme name (KFINTECH format puts name before ISIN)
      if (!name || name.length < 4) {
        const beforeIsin = line.slice(0, line.indexOf(isin)).trim();
        const beforeName = beforeIsin.replace(/^[\d\/\-]+\s*/, "").trim();
        if (beforeName.length > 4 && !BAD_NAME.test(beforeName)) name = beforeName;
      }
      if (!name || name.length < 4 || BAD_NAME.test(name)) continue;

      // ── Extract all numbers from the full context line ──
      // Remove the ISIN itself and date patterns (DD-Mon-YYYY) first
      const cleanCtx = context
        .replace(/\b(INF|INE)[A-Z0-9]{9}\b/g, "")
        .replace(/\d{1,2}-(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}/gi, "")
        .replace(/(?:CAMS|KFINTECH|REGISTRAR)/gi, "");

      const nums = extractNums(cleanCtx);

      // ── Resolve columns using CAMS CAS PDF column order ──
      // The CAS PDF always outputs numbers in this exact order in extracted text:
      //   [0] Cost Value (INR)  — total amount invested (e.g. 500,000.020)
      //   [1] Unit Balance      — units held            (e.g. 11,399.807)
      //   [2] NAV (INR)         — current NAV per unit  (e.g. 47.4762)
      //   [3] Market Value (INR)— current total value   (e.g. 541,219.52)
      //
      // costPrice per unit = Cost Value / Unit Balance
      // We preserve extraction order — no sorting needed.

      let costValue = 0, unitVal = 0, navVal = 0, marketValue = 0;

      if (nums.length >= 4) {
        costValue = nums[0]; unitVal = nums[1]; navVal = nums[2]; marketValue = nums[3];
      } else if (nums.length === 3) {
        // Cost might be missing from this line's text
        unitVal = nums[0]; navVal = nums[1]; marketValue = nums[2];
      } else if (nums.length === 2) {
        unitVal = nums[0]; navVal = nums[1];
      } else if (nums.length === 1) {
        unitVal = nums[0];
      }

      // Sanity check: nav * units should roughly equal market value
      // If not, try the 3-value interpretation (shift left by 1)
      if (nums.length >= 4 && navVal > 0 && unitVal > 0 && marketValue > 0) {
        const ratio = (navVal * unitVal) / marketValue;
        if (ratio < 0.5 || ratio > 2) {
          // Misread — try: [cost ignored, units=nums[1], nav=nums[2], mkt=nums[3]]
          const altUnit = nums[1], altNav = nums[2], altMkt = nums[3];
          if (altNav > 0 && altUnit > 0) {
            const altRatio = (altNav * altUnit) / altMkt;
            if (Math.abs(altRatio - 1) < Math.abs(ratio - 1)) {
              costValue = nums[0]; unitVal = altUnit; navVal = altNav; marketValue = altMkt;
            }
          }
        }
      }

      // costPrice per unit = total invested / total units
      const costPerUnit = (unitVal > 0 && costValue > 0)
        ? costValue / unitVal
        : (navVal || 0)

      if (unitVal > 0 && navVal > 0) {
        seen.add(isin);
        holdings.push({
          name: name.replace(/\s+/g," ").trim(),
          isin, ticker: "-",
          class: "Indian Mutual Funds",
          exchange: "AMFI", currency: "INR",
          qty: unitVal,
          costPrice: costPerUnit,
          currentPrice: navVal,
          country: "India", listed: false, isMutualFund: true,
          manager: currentAMC || "Self-Managed",
          folio: currentFolio,
          source: "MF_CAS",
        });
      }
    }
    return holdings;
  },

  // ── Simple text-based ISIN scan for equity fallback ──
  parseEquityFromText(text) {
    const holdings = [], seen = new Set();
    const lines = text.split("\n").map(l=>l.trim()).filter(l=>l);
    const n = lines.length;
    for (let i = 0; i < n; i++) {
      const line = lines[i];
      // Match ISIN at start of line OR anywhere in line
      const isinM = line.match(/\b(INE[A-Z0-9]{9})\b/);
      if (!isinM) continue;
      const isin = isinM[1];
      if (seen.has(isin)) continue;

      // The line format is: "INE769A01020 AARTI INDUSTRIES LIMITED 5.00 130 447.15 58,129.50"
      // Split at ISIN to get the rest
      const afterIsin = line.slice(line.indexOf(isin) + isin.length).trim();

      // Extract name: text before the first standalone number
      // afterIsin = "AARTI INDUSTRIES LIMITED 5.00 130 447.15 58,129.50"
      const nameNumSplit = afterIsin.match(/^((?:[A-Z&()\-\/][A-Z0-9 &()\-\/\.]*?\s+)+?)(?=\d)/);
      let name = nameNumSplit ? nameNumSplit[1].trim() : "";

      // Also check if name is BEFORE the ISIN on the same line
      if (!name) {
        const beforeIsin = line.slice(0, line.indexOf(isin)).trim();
        if (beforeIsin && /[A-Z]{2,}/.test(beforeIsin)) {
          name = beforeIsin.replace(/^\d{1,3}\s+/,"").replace(/\s*(NSE|BSE)\s*$/i,"").trim();
        }
      }

      // If name is on previous line (multi-line format)
      if (!name || name.length < 3) {
        for (let b = 1; b <= 3; b++) {
          const prev = (lines[i-b]||"").trim();
          if (prev && /[A-Z]{3,}/.test(prev) && !/^IN[A-Z0-9]{10}$/.test(prev)
              && !/^[\d,\.]+$/.test(prev) && !/\.(NSE|BSE)$/i.test(prev)) {
            name = prev; break;
          }
        }
      }

      // Clean name
      name = name
        .replace(/\s*[-#]\s*(NEW EQUITY|NEW RS|NEW RE|EQUITY SHARES|FACE VALUE|AFTER SPLIT|AFTER SUBDIVISION|BONUS|RIGHTS|PARTLY PAID).*/i,"")
        .replace(/\s+NEW\s+(RS|RE|RS\.|RE\.).*$/i,"")
        .replace(/\s+-\s+NEW\b.*/i,"")
        .replace(/\s+(ORDINARY\s+SHARES?|COMMON\s+SHARES?|EQUITY\s+SHARES?)\s*$/i,"")
        .replace(/(\s+\d+\.\d{2,})+$/,"")
        .replace(/\s+\d+$/,"")
        .replace(/\s*(NSE|BSE)\s*$/i,"")
        .trim();

      // Extract numbers from after-ISIN portion of THIS LINE ONLY
      const numStr = afterIsin.replace(/^[A-Z &()\-\/\.]+/,"").trim();
      const nums = [];
      const re = /(\d{1,7}(?:,\d{3})*(?:\.\d{1,2})?)/g;
      let m;
      while ((m = re.exec(numStr)) !== null) {
        const v = parseFloat(m[1].replace(/,/g,""));
        if (v > 0) nums.push(v);
      }

      // NSDL format: FaceValue(≤100) | NoOfShares | MarketPrice | Value
      let qty = 0, price = 0;
      if (nums.length >= 4 && nums[0] <= 100) {
        qty = Math.round(nums[1]); price = nums[2];
      } else if (nums.length === 3 && nums[0] <= 100) {
        qty = Math.round(nums[1]); price = nums[2];
      } else if (nums.length >= 2) {
        qty = Math.round(nums[0]); price = nums[1];
      } else if (nums.length === 1) {
        qty = Math.round(nums[0]);
      }

      // Get ticker from stock symbol line (AARTIIND.NSE) in nearby lines
      const nearby = lines.slice(Math.max(0,i-2), Math.min(n,i+4));
      const symLine = nearby.find(cl => /^[A-Z0-9]+\.(NSE|BSE)$/i.test(cl));
      const ticker  = symLine ? symLine.replace(/\.(NSE|BSE)$/i,"") : "-";
      const exch    = /\bBSE\b/i.test(line) || (symLine && /\.BSE$/i.test(symLine)) ? "BSE" : "NSE";

      if (qty > 0 && qty < 1e6 && name && name.length > 2 && /[A-Z]{2,}/.test(name)) {
        seen.add(isin);
        holdings.push({
          name: name.replace(/\s+/g," ").trim(),
          isin, ticker,
          class: "Equities",
          exchange: exch,
          currency: "INR", qty,
          costPrice: 0,
          currentPrice: price,
          country: "India", listed: true,
          source: "NSDL",
        });
      }
    }
    return holdings;
  },

  // ── Main parse entry point ──
  async parse(file, password = "") {
    const items = await this.extractItems(file, password);
    const text  = await this.extractText(file, password).catch(() => "");
    const type  = this.detectTypeFull ? this.detectTypeFull(text) : this.detectType(text);
    const holderName = this.extractHolderName(text);

    let holdings = [];

    // NSDL CAS contains BOTH equities AND mutual fund folios.
    // Never skip equity parsing just because MF keywords appear in the PDF.
    // Only use pure MF parsers if the file is exclusively a CAMS/KFintech statement.
    const isPureMFStatement = (type === "CAMS_MF" || type === "KFINTECH_MF") &&
      !text.toUpperCase().includes("NSDL") && !text.toUpperCase().includes("CDSL");



    if (isPureMFStatement) {
      holdings = this.parseMFCAS(text);
    } else {
      // Step 1: Geometric equity parser
      const geoEquities = this.parseNSDLGeometric(items);


      // Step 2: Text-based equity fallback (always run — catches what geometry misses)
      const txtEquities = this.parseEquityFromText(text);


      // Merge equity results, deduplicate by ISIN
      const equityMap = {};
      [...geoEquities, ...txtEquities].forEach(h => {
        if (!equityMap[h.isin]) equityMap[h.isin] = h;
      });
      holdings = Object.values(equityMap);

      // Step 3: Also extract MF folios from the same PDF (NSDL CAS has both)
      const mfHoldings = this.parseMFCAS(text);
      // Only add MF holdings that aren't already captured as equities
      // MF dedup: check ISIN only (MF units change gradually, so ISIN match is sufficient for internal dedup)
      const existingMfIsins = new Set(holdings.filter(h=>h.isin&&h.isin!=="-").map(h=>h.isin));
      mfHoldings.forEach(h => { if (!existingMfIsins.has(h.isin)) holdings.push(h); });
    }

    return { holdings, type, holderName, rawText: text };
  },

  // ── Detailed statement (transaction history) — text-based ──
  parseDate(str) {
    if (!str) return null;
    const m1 = str.match(/(\d{1,2})[\-\/](\w{3})[\-\/](\d{4})/);
    if (m1) {
      const months = {jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
      return `${m1[3]}-${months[m1[2].toLowerCase()]||"01"}-${m1[1].padStart(2,"0")}`;
    }
    const m2 = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m2) return `${m2[3]}-${m2[2].padStart(2,"0")}-${m2[1].padStart(2,"0")}`;
    if (/\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0,10);
    return null;
  },
  classifyTxnType(desc) {
    const d = (desc||"").toUpperCase();
    if (/PURCHASE|LUMP|BUY|INVEST|NFO|SIP|SWITCH IN|TRANSFER IN/i.test(d)) return "BUY";
    if (/REDEEM|WITHDRAWAL|SELL|SWITCH OUT|TRANSFER OUT/i.test(d))          return "SELL";
    if (/DIVIDEND|DIV/i.test(d))  return "DIVIDEND";
    if (/BONUS/i.test(d))         return "BONUS";
    return "BUY";
  },
  parseCAMSDetailed(text) {
    const transactions = [], holdingsMap = {};
    const lines = text.split("\n");
    let currentScheme = "", currentFolio = "";
    let inTxnTable = false;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) { inTxnTable = false; continue; }
      const schemeMatch = line.match(/^([A-Z].{8,80})\s*(?:\(Folio[\s:No]+([\\w\/\s]+)\))?$/i);
      if (schemeMatch && !/Date|Transaction|Units|Price|Amount|Balance/i.test(line)) {
        const cand = schemeMatch[1].trim();
        if (cand.length > 10 && !/^\d/.test(cand) && !/^(total|sub|net|balance)/i.test(cand))
          { currentScheme = cand; currentFolio = (schemeMatch[2]||"").trim(); inTxnTable = false; continue; }
      }
      if (/Date.*Transaction.*Units.*(?:Price|NAV).*Amount/i.test(line)) { inTxnTable = true; continue; }
      if (!inTxnTable || !currentScheme) continue;
      const txnRow = line.match(/^(\d{1,2}[\-\/]\w{3,}[\-\/]\d{2,4})\s+(.{5,50}?)\s+([\d,]+\.\d+)\s+([\d,]+\.\d+)\s+([\d,]+\.\d+)/);
      if (txnRow) {
        const date = this.parseDate(txnRow[1]), desc = txnRow[2].trim();
        const units = parseFloat(txnRow[3].replace(/,/g,"")), nav = parseFloat(txnRow[4].replace(/,/g,""));
        const amount = parseFloat(txnRow[5].replace(/,/g,""));
        const type = this.classifyTxnType(desc);
        if (date && units > 0 && nav > 0) {
          transactions.push({ date, holding: currentScheme, type, qty: units, price: nav, currency: "INR", fees: 0, folio: currentFolio, source: "CAMS", rawDesc: desc });
          if (!holdingsMap[currentScheme]) holdingsMap[currentScheme] = { units:0, totalCost:0 };
          if (type === "BUY")  { holdingsMap[currentScheme].units += units; holdingsMap[currentScheme].totalCost += amount; }
          if (type === "SELL") { holdingsMap[currentScheme].units -= units; }
        }
      }
    }
    const costMap = {};
    Object.entries(holdingsMap).forEach(([n,d]) => { costMap[n] = d.units > 0 ? d.totalCost/d.units : 0; });
    return { transactions, costMap };
  },
  parseKFintechDetailed(text) {
    // KFintech is structurally similar to CAMS — reuse with slight variation
    return this.parseCAMSDetailed(text);
  },
  parseEquityTxns(text) {
    const transactions = [];
    const lines = text.split("\n");
    for (const line of lines) {
      const row = line.match(/^(\d{1,2}[\-\/]\w+[\-\/]\d{2,4})\s+(IN[A-Z0-9]{10})\s+(.{5,50})\s+([\d,]+)\s+([\d,]+\.\d+)\s+([\d,]+\.\d+)\s+(CREDIT|DEBIT|CR|DR)/i);
      if (row) {
        const date = this.parseDate(row[1]);
        const qty  = parseFloat(row[4].replace(/,/g,""));
        const price= parseFloat(row[5].replace(/,/g,""));
        const type = /CREDIT|CR/i.test(row[7]) ? "BUY" : "SELL";
        if (date && qty > 0 && price > 0)
          transactions.push({ date, holding: row[3].trim(), type, qty, price, currency:"INR", fees:0, isin:row[2], source:"NSDL_TXN" });
      }
    }
    return { transactions, costMap:{} };
  },
  async parseDetailed(file, password = "") {
    const items = await this.extractItems(file, password);
    const text  = await this.extractText(file, password).catch(()=>"");
    const type  = this.detectType(text);
    const holderName = this.extractHolderName(text);
    let result = { transactions:[], costMap:{} };
    if      (type === "CAMS_MF")     result = this.parseCAMSDetailed(text);
    else if (type === "KFINTECH_MF") result = this.parseKFintechDetailed(text);
    else if (type === "IBKR")        result = this.parseIBKR(text);
    else if (type === "SCHWAB")      result = this.parseSchwab(text);
    else if (type === "FIDELITY")    result = this.parseFidelity(text);
    else if (type === "HSBC")        result = this.parseHSBC(text);
    else { const eq = this.parseEquityTxns(text); result = eq.transactions.length ? eq : this.parseCAMSDetailed(text); }
    return { ...result, type, holderName, rawText: text };
  },

  // ── Upgrade detectType to handle global brokers ──────────────────────────
  detectTypeFull(text) {
    const t = text.toUpperCase();
    if (t.includes("NSDL") || t.includes("NATIONAL SECURITIES DEPOSITORY")) return "NSDL_CAS";
    if (t.includes("CDSL") || t.includes("CENTRAL DEPOSITORY SERVICES"))   return "CDSL_CAS";
    if (t.includes("CAMS") || t.includes("COMPUTER AGE MANAGEMENT"))       return "CAMS_MF";
    if (t.includes("KFINTECH") || t.includes("KARVY"))                     return "KFINTECH_MF";
    // Global brokers
    if (t.includes("INTERACTIVE BROKERS") || t.includes("IB FLEX QUERY") ||
        t.includes("IBKR") || t.includes("ACTIVITY STATEMENT") &&
        t.includes("ACCOUNT INFORMATION"))                                  return "IBKR";
    if (t.includes("CHARLES SCHWAB") || t.includes("SCHWAB BROKERAGE"))    return "SCHWAB";
    if (t.includes("FIDELITY BROKERAGE") || t.includes("FIDELITY INVEST") ||
        (t.includes("FIDELITY") && t.includes("TRANSACTION HISTORY")))     return "FIDELITY";
    if (t.includes("HSBC PRIVATE") || t.includes("HSBC SECURITIES") ||
        t.includes("HSBC BANK") && t.includes("PORTFOLIO STATEMENT"))      return "HSBC";
    if (t.includes("TD AMERITRADE") || t.includes("TDAMERITRADE"))         return "TDAMERITRADE";
    if (t.includes("ETRADE") || t.includes("E*TRADE"))                     return "ETRADE";
    if (t.includes("DEGIRO") || t.includes("FLATEX"))                      return "DEGIRO";
    return "UNKNOWN";
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INTERACTIVE BROKERS (IBKR) — Activity Statement / Flex Query CSV / PDF
  // Columns: Date | Symbol | Description | Quantity | Price | Proceeds | Comm | Basis | Realized P/L
  // ─────────────────────────────────────────────────────────────────────────
  parseIBKR(text) {
    const transactions = [], costMap = {};
    const lines = text.split("\n");
    let inTrades = false, currency = "USD";

    // Detect base currency from header
    const ccyLine = lines.find(l => /Base Currency[:\s]+([A-Z]{3})/i.test(l));
    if (ccyLine) { const m = ccyLine.match(/Base Currency[:\s]+([A-Z]{3})/i); if (m) currency = m[1]; }

    for (const line of lines) {
      const l = line.trim();
      if (!l) continue;

      // Section header detection
      if (/^Trades[,	]|TRADES SECTION|Symbol.*Date.*Qty.*Price.*Proceeds/i.test(l)) {
        inTrades = true; continue;
      }
      if (/^(Cash Report|Dividends|Interest|Fees|Positions|Open Positions)/i.test(l)) {
        inTrades = false;
      }
      if (!inTrades) continue;

      // CSV row — comma or tab delimited
      const cols = l.split(/[,	]/).map(c => c.replace(/"/g,"").trim());
      if (cols.length < 7) continue;

      // IBKR Flex format: Trades,Data,Order,Symbol,Date/Time,Quantity,T. Price,C. Price,Proceeds,Comm/Fee,...
      // Activity Statement: Symbol | Date/Time | Quantity | T. Price | C. Price | Proceeds | Comm/Fee | Basis | Realized P/L | MTM P/L | Code
      let symbol = "", dateStr = "", qty = 0, price = 0, ccy = currency;

      // Try to auto-detect which column is which
      const dateCol = cols.findIndex(c => /\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}/.test(c));
      const qtyCol  = cols.findIndex(c => /^-?[\d,]+\.?\d*$/.test(c) && Math.abs(parseFloat(c.replace(/,/g,""))) > 0 && Math.abs(parseFloat(c.replace(/,/g,""))) < 1e7);

      if (dateCol < 0 || qtyCol < 0) continue;

      dateStr = cols[dateCol].split(",")[0].split(" ")[0];
      qty     = Math.abs(parseFloat(cols[qtyCol].replace(/,/g,"")) || 0);
      symbol  = cols.slice(0, dateCol).find(c => /^[A-Z0-9\.]{1,10}$/.test(c) && !/^(USD|EUR|GBP|JPY|CHF|SGD|HKD|INR|AUD|CAD)$/.test(c)) || cols[1] || "";
      price   = parseFloat((cols[qtyCol+1]||"0").replace(/,/g,"")) || 0;

      if (!symbol || qty < 0.0001 || !dateStr) continue;
      const date = this.parseDate(dateStr) || dateStr.slice(0,10);
      if (!date) continue;

      const isNeg = parseFloat(cols[qtyCol].replace(/,/g,"")) < 0;
      const type  = isNeg ? "SELL" : "BUY";

      transactions.push({ date, holding: symbol, ticker: symbol, type, qty, price, currency: ccy, fees: 0, source: "IBKR" });
      if (!costMap[symbol]) costMap[symbol] = 0;
      if (type === "BUY") costMap[symbol] = price;
    }
    return { transactions, costMap };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CHARLES SCHWAB — Transaction History export (CSV)
  // Columns: Date | Action | Symbol | Description | Quantity | Price | Fees & Comm | Amount
  // ─────────────────────────────────────────────────────────────────────────
  parseSchwab(text) {
    const transactions = [], costMap = {};
    const lines = text.split("\n");
    let headerFound = false;
    let colDate=-1, colAction=-1, colSymbol=-1, colQty=-1, colPrice=-1, colFees=-1;

    for (const line of lines) {
      const l = line.trim();
      if (!l || l.startsWith("Transactions")) continue;

      const cols = l.split(",").map(c => c.replace(/"/g,"").trim());

      // Find header row
      if (!headerFound && /Date.*Action.*Symbol/i.test(l)) {
        colDate   = cols.findIndex(c => /^Date$/i.test(c));
        colAction = cols.findIndex(c => /^Action$/i.test(c));
        colSymbol = cols.findIndex(c => /^Symbol$/i.test(c));
        colQty    = cols.findIndex(c => /^Quantity$/i.test(c));
        colPrice  = cols.findIndex(c => /^Price$/i.test(c));
        colFees   = cols.findIndex(c => /Fees|Comm/i.test(c));
        headerFound = true; continue;
      }
      if (!headerFound) continue;

      const dateStr = cols[colDate]  || "";
      const action  = cols[colAction]|| "";
      const symbol  = cols[colSymbol]|| "";
      const qty     = Math.abs(parseFloat((cols[colQty]||"0").replace(/,/g,"")) || 0);
      const price   = Math.abs(parseFloat((cols[colPrice]||"0").replace(/[$,]/g,"")) || 0);
      const fees    = Math.abs(parseFloat((cols[colFees]||"0").replace(/[$,]/g,"")) || 0);

      if (!symbol || !dateStr || qty < 0.0001) continue;
      const date = this.parseDate(dateStr);
      if (!date) continue;

      const type = /buy|purchase|reinvest/i.test(action) ? "BUY"
        : /sell|redempt/i.test(action) ? "SELL"
        : /dividend|div/i.test(action) ? "DIVIDEND" : null;
      if (!type) continue;

      transactions.push({ date, holding: symbol, ticker: symbol, type, qty, price, currency: "USD", fees, source: "SCHWAB" });
      if (type === "BUY") costMap[symbol] = price;
    }
    return { transactions, costMap };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FIDELITY — Transaction History (CSV/PDF)
  // Columns: Run Date | Account | Action | Symbol | Description | Type | Qty | Price | Commission | Amount
  // ─────────────────────────────────────────────────────────────────────────
  parseFidelity(text) {
    const transactions = [], costMap = {};
    const lines = text.split("\n");
    let headerFound = false;
    let colDate=-1, colAction=-1, colSymbol=-1, colQty=-1, colPrice=-1, colFees=-1;

    for (const line of lines) {
      const l = line.trim();
      if (!l) continue;
      const cols = l.split(",").map(c => c.replace(/"/g,"").trim());

      if (!headerFound && /Run Date|Settlement Date/i.test(l) && /Symbol|Security/i.test(l)) {
        colDate   = cols.findIndex(c => /Run Date|Trade Date|Settlement/i.test(c));
        colAction = cols.findIndex(c => /^Action$/i.test(c));
        colSymbol = cols.findIndex(c => /^Symbol$|^Security Symbol$/i.test(c));
        colQty    = cols.findIndex(c => /^Quantity$|^Qty$/i.test(c));
        colPrice  = cols.findIndex(c => /^Price$/i.test(c));
        colFees   = cols.findIndex(c => /Commission|Fees/i.test(c));
        headerFound = true; continue;
      }
      if (!headerFound) continue;

      const dateStr = cols[colDate]  || "";
      const action  = cols[colAction]|| "";
      const symbol  = cols[colSymbol]|| "";
      const qty     = Math.abs(parseFloat((cols[colQty]  ||"0").replace(/,/g,"")) || 0);
      const price   = Math.abs(parseFloat((cols[colPrice]||"0").replace(/[$,]/g,"")) || 0);
      const fees    = Math.abs(parseFloat((cols[colFees] ||"0").replace(/[$,]/g,"")) || 0);

      if (!symbol || !dateStr || qty < 0.0001) continue;
      const date = this.parseDate(dateStr);
      if (!date) continue;

      const type = /you bought|bought|purchase|reinvest/i.test(action) ? "BUY"
        : /you sold|sold|redempt/i.test(action) ? "SELL"
        : /dividend|div/i.test(action) ? "DIVIDEND" : null;
      if (!type) continue;

      transactions.push({ date, holding: symbol, ticker: symbol, type, qty, price, currency: "USD", fees, source: "FIDELITY" });
      if (type === "BUY") costMap[symbol] = price;
    }
    return { transactions, costMap };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // HSBC Private Banking — Portfolio Statement (PDF text)
  // Format varies by country; common: Date | Security | ISIN | Qty | Price | Value | Currency
  // ─────────────────────────────────────────────────────────────────────────
  parseHSBC(text) {
    const holdings = [], transactions = [], seen = new Set();
    const lines = text.split("\n");

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Holdings: look for ISIN pattern with surrounding data
      const isinM = line.match(/([A-Z]{2}[A-Z0-9]{9}\d)/);
      if (!isinM) continue;
      const isin = isinM[1];
      if (seen.has(isin)) continue;

      // Extract name from nearby lines
      let name = "";
      for (let j = Math.max(0,i-3); j <= i; j++) {
        const nl = lines[j].trim();
        if (nl.length > 6 && !/^\d/.test(nl) && !/ISIN|Folio|Date/i.test(nl)) {
          name = nl.split(/\s{3,}/)[0].trim();
        }
      }
      if (!name || name.length < 3) continue;

      // Extract numbers from current and next line
      const numStr = line + " " + (lines[i+1]||"");
      const nums = [...numStr.matchAll(/([\d,]+\.?\d*)/g)]
        .map(m => parseFloat(m[1].replace(/,/g,"")))
        .filter(v => v > 0 && isFinite(v));

      // Find currency
      const ccyM = line.match(/(USD|EUR|GBP|CHF|SGD|HKD|JPY|INR|AED|AUD|CAD)/);
      const ccy  = ccyM ? ccyM[1] : "USD";

      // qty = first reasonable number, price = second
      const qty   = nums.find(v => v < 1e7 && v > 0.001) || 0;
      const price = nums.find(v => v !== qty && v < 1e6 && v > 0.01) || 0;

      if (qty > 0) {
        seen.add(isin);
        holdings.push({ name, isin, ticker: "-", class: "Equities",
          currency: ccy, qty, costPrice: 0, currentPrice: price,
          country: "Global", listed: true, source: "HSBC" });
      }
    }
    return { transactions, costMap: {}, holdings };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // parseDate upgrade — handles more global date formats
  // ─────────────────────────────────────────────────────────────────────────
  parseDate(str) {
    if (!str) return null;
    str = str.trim().split(",")[0].split(" ")[0];

    // ISO: 2024-03-15
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0,10);

    // DD-Mon-YYYY or DD/Mon/YYYY: 15-Mar-2024
    const m1 = str.match(/^(\d{1,2})[\-\/](\w{3})[\-\/](\d{4})/);
    if (m1) {
      const months = {jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",
                      jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
      const mo = months[m1[2].toLowerCase()];
      return mo ? `${m1[3]}-${mo}-${m1[1].padStart(2,"0")}` : null;
    }
    // MM/DD/YYYY (US): 03/15/2024
    const m2 = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m2) return `${m2[3]}-${m2[1].padStart(2,"0")}-${m2[2].padStart(2,"0")}`;

    // DD/MM/YYYY (EU/IN): 15/03/2024 — ambiguous; if month<=12 assume MM/DD else DD/MM
    const m3 = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (m3) return `${m3[3]}-${m3[2].padStart(2,"0")}-${m3[1].padStart(2,"0")}`;

    // YYYYMMDD compact
    const m4 = str.match(/^(\d{4})(\d{2})(\d{2})/);
    if (m4) return `${m4[1]}-${m4[2]}-${m4[3]}`;

    return null;
  },
};


// ── Universal statement text extractor ──
// Feeds the AI statement parser. The /api/parse-* endpoints only need clean
// text, so any source works: PDF (pdf.js), CSV/TSV/TXT (raw), Excel (SheetJS →
// CSV). Tabular files (CSV/Excel) actually parse cleaner than PDFs.
// Returns { text, kind } — kind drives format-specific error messaging.
async function extractStatementText(file, password) {
  const name = (file?.name || "").toLowerCase();

  // Excel → convert every sheet to CSV and concatenate (labelled if multi-sheet)
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    if (!window.XLSX) {
      await new Promise((res, rej) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    const buf = await file.arrayBuffer();
    const wb  = window.XLSX.read(buf, { type: "array", cellDates: false });
    const multi = wb.SheetNames.length > 1;
    const parts = wb.SheetNames.map(sn => {
      const csv = window.XLSX.utils.sheet_to_csv(wb.Sheets[sn], { blankrows: false });
      if (!csv || !csv.trim()) return "";
      return multi ? `### Sheet: ${sn}\n${csv}` : csv;
    }).filter(Boolean);
    return { text: parts.join("\n\n"), kind: "excel" };
  }

  // CSV / TSV / plain text → read as-is
  if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
    const text = await file.text().catch(() => "");
    return { text: text || "", kind: "csv" };
  }

  // Default: PDF via pdf.js
  const text = await ECAS_PARSER.extractText(file, password).catch(() => "");
  return { text: text || "", kind: "pdf" };
}


// ── Error Boundary — catches render crashes and shows a recovery screen ──
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError:false, error:null }; }
  static getDerivedStateFromError(error) { return { hasError:true, error }; }
  componentDidCatch(error, info) {
    if (typeof window !== "undefined") { window.__fw_last_error = error?.message + " | " + (info?.componentStack||"").slice(0,200); }
    if (typeof console !== "undefined") console.error("[Forthius] Render error:", error?.message);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",
        alignItems:"center",justifyContent:"center",gap:16,
        background:"#F5EBE0",fontFamily:"'Inter', system-ui, sans-serif",padding:24}}>
        <div style={{fontSize:48}}>⚠️</div>
        <div style={{fontSize:20,fontWeight:700,color:"#1C1917"}}>Something went wrong</div>
        <div style={{fontSize:13,color:"#78716C",maxWidth:400,textAlign:"center",lineHeight:1.6}}>
          Forthius encountered an unexpected error. Your data is safe.
        </div>
        {this.state.error && (
          <div style={{fontSize:11,color:"#A8A29E",background:"#F0EBE4",padding:"8px 14px",
            borderRadius:8,fontFamily:"monospace",maxWidth:480,wordBreak:"break-all",textAlign:"center"}}>
            {this.state.error.message}
          </div>
        )}
        <button onClick={()=>window.location.reload()}
          style={{background:"#007878",border:"none",
            borderRadius:10,color:"white",padding:"11px 28px",cursor:"pointer",
            fontSize:14,fontWeight:700,boxShadow:"0 4px 16px rgba(0,121,193,0.3)"}}>
          Reload App
        </button>
        <div style={{fontSize:11,color:"#A8A29E"}}>
          If this keeps happening, contact{" "}
          <a href="mailto:support@forthius.com" style={{color:"#007878"}}>support@forthius.com</a>
        </div>
      </div>
    );
  }
}


// ── Detailed Performance Table Component ──────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Detailed Performance Table
// Indian MFs  → mfapi.in (direct, CORS-open, confirmed working)
// Equities    → price_snapshots table in Supabase (seeded via seed tool)
// Benchmark   → /api/benchmark (works for index tickers ^NSEI etc.)
// ─────────────────────────────────────────────────────────────────────────────
// Session-level NAV cache — persists across PerfTable open/close within same page session
const _mfNavCache = new Map(); // schemeCode → computeReturns result

// ── Yahoo ticker derivation ──
// When a holding has an explicit `yahooTicker` set, use that (user choice).
// Otherwise, derive from `ticker` + `country`/`exchange` so Yahoo's /v8/chart
// endpoint can find historical prices. US-listed equities work bare; others
// need an exchange suffix (e.g. RELIANCE → RELIANCE.NS for NSE-listed).
//
// Honest caveats:
//   - Tickers already containing a dot (BRK.B, 2330.TW) are left alone — assume
//     they're either Yahoo-formatted already or have a dot in the symbol itself.
//     Fix-by-overlap: if user holds BRK.B saved as ticker "BRK.B" with country
//     USA, we leave it alone — Yahoo accepts BRK.B for Berkshire B-class.
//   - This is best-effort. Some tickers won't resolve even with proper suffix
//     (delisted, OTC pink-sheet, niche regional). Caller must handle empty result.
const YAHOO_EXCHANGE_SUFFIX = {
  // India
  NSE: ".NS", BSE: ".BO",
  // UK / Europe
  LSE: ".L", LON: ".L",
  XETRA: ".DE", FRA: ".F",
  EPA: ".PA", PAR: ".PA",
  AMS: ".AS", AEB: ".AS",
  MIB: ".MI", BIT: ".MI",
  BME: ".MC", MAD: ".MC",
  STO: ".ST", OMX: ".ST",
  CPH: ".CO", HEL: ".HE",
  SWX: ".SW", VTX: ".SW",
  OSL: ".OL", WSE: ".WA",
  // Asia-Pacific
  TYO: ".T", TSE: ".T",
  HKEX: ".HK", HKG: ".HK",
  SGX: ".SI", SES: ".SI",
  ASX: ".AX",
  KRX: ".KS", KOSPI: ".KS", KOSDAQ: ".KQ",
  TWSE: ".TW", TPE: ".TW",
  SSE: ".SS", SZSE: ".SZ", SHA: ".SS", SHE: ".SZ",
  // Americas
  TSX: ".TO", TSXV: ".V",
  BVMF: ".SA", SAO: ".SA",
  BMV: ".MX",
};
const YAHOO_COUNTRY_SUFFIX = {
  India: ".NS", UK: ".L", "United Kingdom": ".L",
  Germany: ".DE", France: ".PA", Italy: ".MI", Spain: ".MC",
  Netherlands: ".AS", Switzerland: ".SW", Sweden: ".ST",
  Japan: ".T", "Hong Kong": ".HK", Singapore: ".SI",
  Australia: ".AX", Korea: ".KS", "South Korea": ".KS",
  Taiwan: ".TW", China: ".SS",
  Canada: ".TO", Brazil: ".SA", Mexico: ".MX",
};
// US exchanges: bare ticker (no suffix needed on Yahoo)
const US_EXCHANGES = new Set(["NYSE","NASDAQ","NYSEARCA","NYSE ARCA","ARCA","AMEX","BATS","OTC","NMS"]);

// ── Known UCITS ETFs that appear with bare tickers in CAS data ──
// These resolve poorly via exchange/country alone because the holding records often
// have missing or imprecise `exchange`/`country`. Mirror in api/seed-snapshots.js.
const UCITS_TICKER_MAP = {
  EIMI: "EIMI.L",   // iShares Core MSCI EM IMI UCITS ETF — LSE (USD)
  SXRV: "SXRV.DE",  // iShares NASDAQ 100 UCITS ETF (Acc) — Xetra primary
  IUHC: "IUHC.L",   // iShares S&P 500 Healthcare Sector UCITS ETF — LSE
  IUFS: "IUFS.L",   // iShares S&P 500 Financials Sector UCITS ETF — LSE
  SPY5: "SPY5.L",   // SPDR S&P 500 UCITS ETF — LSE
  CSPX: "CSPX.L",   // iShares Core S&P 500 UCITS ETF (Acc) — LSE
  IWDA: "IWDA.L",   // iShares Core MSCI World UCITS ETF (Acc) — LSE
  VWRA: "VWRA.L",   // Vanguard FTSE All-World UCITS ETF (Acc) — LSE
  VUSA: "VUSA.L",   // Vanguard S&P 500 UCITS ETF (Dist) — LSE
  AGGG: "AGGG.L",   // iShares Core Global Aggregate Bond UCITS ETF — LSE
};

// ── Canadian (TSX/TSXV) class-share normalization ──
// TSX uses dots as class-share separators (e.g. FIH.U, BAM.A, RY.PR.A, BBU.UN).
// Yahoo Finance uses dashes for the same securities (FIH-U.TO, BAM-A.TO, RY-PR-A.TO, BBU-UN.TO),
// with the .TO/.V exchange suffix tacked on the end.
// This function converts a raw TSX-style ticker (with or without an exchange suffix)
// to the Yahoo-compatible form. Idempotent: passing a Yahoo-form ticker returns it unchanged.
function normalizeCanadianTicker(ticker) {
  if (!ticker) return ticker;
  const t = ticker.trim().toUpperCase();
  // Match patterns like FIH.U.TO / BAM.A.TO / RY.PR.A.TO / FOO.V — anything with at least
  // one internal dot before a trailing .TO or .V exchange suffix.
  const m = t.match(/^([A-Z][A-Z0-9.]*)(\.TO|\.V)$/);
  if (m) {
    const base = m[1], ex = m[2];
    if (base.includes(".")) {
      // Convert internal dots to dashes: FIH.U → FIH-U, RY.PR.A → RY-PR-A
      return base.replace(/\./g, "-") + ex;
    }
    return t; // FIH.TO with no internal dots → leave alone
  }
  return ticker; // no exchange suffix or non-Canadian — leave as-is
}

function deriveYahooTicker(h) {
  if (!h) return null;
  // 1. Honor explicitly-set yahooTicker (also normalize, in case it was hand-entered TSX-style)
  if (h.yahooTicker) return normalizeCanadianTicker(h.yahooTicker);
  // 2. Need a base ticker
  const t = (h.ticker || "").trim();
  if (!t || t === "-") return null;
  // 3. Already-suffixed (contains a dot followed by 1-3 chars at end) — normalize for Canadian, leave others
  if (/\.[A-Z]{1,3}$/i.test(t)) return normalizeCanadianTicker(t);
  // 4. Known UCITS lookup — catches EIMI, SPY5, etc. before exchange-suffix logic
  //    which would otherwise default to bare and fail on Yahoo.
  if (UCITS_TICKER_MAP[t.toUpperCase()]) return UCITS_TICKER_MAP[t.toUpperCase()];
  // 5. US-listed → bare ticker
  const ex = (h.exchange || "").toUpperCase();
  if (US_EXCHANGES.has(ex)) return t;
  if ((h.country || "") === "USA" || (h.country || "") === "United States") return t;
  // 6. Lookup by exchange first (more specific). For TSX/TSXV, normalize dots → dashes first.
  if (ex && YAHOO_EXCHANGE_SUFFIX[ex]) {
    const suffix = YAHOO_EXCHANGE_SUFFIX[ex];
    if (suffix === ".TO" || suffix === ".V") {
      // FIH.U + TSX → FIH-U.TO
      return t.replace(/\./g, "-") + suffix;
    }
    return t + suffix;
  }
  // 7. Lookup by country (Canada similarly needs dot→dash)
  if (h.country && YAHOO_COUNTRY_SUFFIX[h.country]) {
    const suffix = YAHOO_COUNTRY_SUFFIX[h.country];
    if (suffix === ".TO" || suffix === ".V") {
      return t.replace(/\./g, "-") + suffix;
    }
    return t + suffix;
  }
  // 8. Last resort: bare ticker (Yahoo's search may still resolve well-known ones)
  return t;
}

function PerfTable({ holdings, T, tCard, tTh, tTd, isMobile, dc, controlledOpen, onControlledToggle }) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  // Use controlled open if parent supplies it; fall back to internal state otherwise.
  const isControlled = typeof controlledOpen === "boolean";
  const open     = isControlled ? controlledOpen : internalOpen;
  const setOpen  = (v) => {
    if (isControlled) {
      const next = typeof v === "function" ? v(open) : v;
      onControlledToggle?.(next);
    } else {
      setInternalOpen(v);
    }
  };
  const [loading, setLoading] = React.useState(false);
  const [pendingIds, setPendingIds] = React.useState(new Set()); // IDs still being fetched
  const [data,    setData]    = React.useState({});
  const [bmData,  setBmData]  = React.useState({});
  const [bm,      setBm]      = React.useState("^NSEI");

  const BENCHMARKS = [
    { label:"Nifty 50",   ticker:"^NSEI"   },
    { label:"Nifty 500",  ticker:"^CRSLDX" },
    { label:"S&P 500",    ticker:"^GSPC"   },
    { label:"MSCI World", ticker:"URTH"    },
    { label:"Gold",       ticker:"GC=F"    },
  ];
  const PERIODS   = ["1M","3M","6M","1Y","3Y","5Y"];
  const PDAYS     = { "1M":30,"3M":91,"6M":183,"1Y":365,"3Y":1095,"5Y":1825 };
  const ANN       = new Set(["3Y","5Y"]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const calcReturn = (curr, hist, p) => {
    if (!curr || !hist || curr <= 0 || hist <= 0) return null;
    if (ANN.has(p)) return (Math.pow(curr/hist, 1/(PDAYS[p]/365)) - 1) * 100;
    return (curr/hist - 1) * 100;
  };

  const isUCITS = (h) => {
    const RE = /^(LU|IE|GB|FR|DE|CH|NL|SE|BE|AT|DK|NO|FI)[A-Z0-9]{10}$/;
    return RE.test((h.isin||"").trim()) || RE.test((h.ticker||"").trim())
      || h.class === "Global Mutual Funds" || h.isGlobalMF === true;
  };

  const isIndianMF = (h) => {
    if (isUCITS(h)) return false;
    const tk   = (h.ticker||"").trim();
    const code = h.schemeCode || tk;
    return ["Indian Mutual Funds","Mutual Funds"].includes(h.class)
      || (h.isMutualFund && /^\d{5,6}$/.test(String(code)))
      || (h.isin||"").startsWith("INF") || tk.startsWith("INF");
  };

  const getCode = (h) => {
    const c = h.schemeCode || h.ticker || "";
    return /^\d{5,6}$/.test(String(c)) ? String(c) : null;
  };

  // Given [{date:"YYYY-MM-DD", price}] newest-first, find price N days ago
  const histPrice = (rows, days) => {
    const target = new Date(Date.now() - days*86400000).toISOString().slice(0,10);
    const past   = rows.filter(r => r.date <= target);
    if (past.length) return past[0].price;
    // No data old enough — use oldest available if within 90 days of target
    const oldest = rows[rows.length-1];
    if (!oldest) return null;
    const oldestMs = new Date(oldest.date).getTime();
    const targetMs = new Date(target).getTime();
    const diffDays = (oldestMs - targetMs) / 86400000;
    return diffDays <= 90 ? oldest.price : null; // accept if within 90 days
  };

  const computeReturns = (curr, rows) => {
    if (!rows?.length || !curr) return {};
    const result = {};
    for (const p of PERIODS) result[p] = calcReturn(curr, histPrice(rows, PDAYS[p]), p);
    return result;
  };

  // ── Fetch: Indian MFs via mfapi.in ────────────────────────────────────────

  const fetchMF = async (h) => {
    let code = getCode(h);

    // If no numeric scheme code, search mfapi.in by fund name
    if (!code && h.name) {
      try {
        const ctrl = new AbortController();
        const tid  = setTimeout(() => ctrl.abort(), 8000);
        const q    = h.name.slice(0, 40); // use longer name for better match
        const sr   = await fetch(
          `https://api.mfapi.in/mf/search?q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal }
        );
        clearTimeout(tid);
        if (sr.ok) {
          const results = await sr.json();
          if (results?.length) {
            // Verify best match: current price should be within 50% of latest NAV
            for (const res of results.slice(0, 3)) {
              const candCode = String(res.schemeCode);
              // Quick NAV check
              try {
                const nr = await fetch(`https://api.mfapi.in/mf/${candCode}/latest`,
                  { signal: AbortSignal.timeout(5000) });
                if (nr.ok) {
                  const nj = await nr.json();
                  const nav = parseFloat(nj?.data?.[0]?.nav);
                  const curr = h.currentPrice || 0;
                  // Accept if current price is within 3x of NAV (accounts for FX differences)
                  if (nav > 0 && curr > 0 && nav/curr < 3 && curr/nav < 3) {
                    code = candCode; break;
                  } else if (nav > 0 && curr === 0) {
                    code = candCode; break; // no price to compare, take first result
                  }
                }
              } catch {}
            }
            // Fallback to first result if none passed validation
            if (!code) code = String(results[0].schemeCode);
          }
        }
      } catch {}
    }

    if (!code) return {};

    // Helper to parse mfapi data rows into [{date, price}]
    const parseMFData = (data) => data
      .map(n => {
        const [dd,mm,yyyy] = n.date.split("-");
        return { date:`${yyyy}-${mm}-${dd}`, price:parseFloat(n.nav) };
      })
      .filter(r => r.price > 0)
      .sort((a,b) => b.date.localeCompare(a.date));

    // Try 1: Vercel proxy (server-side, no rate limits, caches in Supabase)
    try {
      const r = await fetch(`/api/mf-history?code=${code}`,
        { signal: AbortSignal.timeout(15000) });
      if (r.ok) {
        const j = await r.json();
        if (j?.data?.length) {
          const rows = parseMFData(j.data);
          const curr = h.currentPrice > 0 ? h.currentPrice : rows[0]?.price;
          console.log(`[MF] ✓ ${code} ${j.source||"?"} rows:${rows.length} curr:${curr?.toFixed(2)}`);
          return computeReturns(curr, rows);
        } else {
          console.warn(`[MF] proxy empty for ${code}`, j);
        }
      } else {
        console.warn(`[MF] proxy ${r.status} for ${code}`);
      }
    } catch (e) {
      console.warn(`[MF] proxy error for ${code}:`, e.message);
    }

    // Try 2: Direct mfapi.in (browser, may rate-limit but works as fallback)
    try {
      const r = await fetch(`https://api.mfapi.in/mf/${code}`,
        { signal: AbortSignal.timeout(12000) });
      if (r.ok) {
        const j = await r.json();
        if (j?.data?.length) {
          const rows = parseMFData(j.data);
          const curr = h.currentPrice > 0 ? h.currentPrice : rows[0]?.price;
          return computeReturns(curr, rows);
        }
      }
    } catch (e) {
      console.warn(`[PerfTable] mfapi direct failed for ${code}:`, e.message);
    }

    console.warn(`[PerfTable] No data for scheme ${code} (${h.name?.slice(0,30)})`);
    return {};
  };

  // ── Fetch: Equities via Supabase price_snapshots ──────────────────────────

  const fetchSnapshots = async (ids) => {
    if (!ids.length) return {};
    try {
      return await fetchPriceSnapshots(ids);
    } catch (e) {
      console.warn("[PerfTable] fetchSnapshots:", e.message);
      return {};
    }
  };

  // ── Fetch: Benchmark via /api/benchmark ─────────────────────────────────
  // Uses monthly data (range=5y) for accurate 1Y/3Y/5Y CAGR
  // Uses daily data (range=6mo) for accurate 1M/3M/6M point-to-point

  const fetchBM = async (ticker) => {
    try {
      const ctrl = new AbortController();
      const tid  = setTimeout(() => ctrl.abort(), 12000);

      // Fetch monthly data for long periods (1Y/3Y/5Y)
      const [rMo, rDay] = await Promise.all([
        fetch(`/api/benchmark?ticker=${encodeURIComponent(ticker)}&interval=1mo`, { signal: ctrl.signal }),
        fetch(`/api/benchmark?ticker=${encodeURIComponent(ticker)}&interval=1d`,  { signal: ctrl.signal }),
      ]);
      clearTimeout(tid);

      const jMo  = rMo.ok  ? await rMo.json()  : null;
      const jDay = rDay.ok ? await rDay.json() : null;

      const ptsMo  = jMo?.points  || []; // [{ym:"YYYY-MM", close}] newest first
      const ptsDay = jDay?.points || []; // [{ym:"YYYY-MM-DD", close}] newest first — using ym field as date

      if (!ptsMo.length && !ptsDay.length) return {};

      const curr = jMo?.currentPrice || jDay?.currentPrice
        || (ptsMo[0]?.close) || (ptsDay[0]?.close);
      if (!curr) return {};

      const result = {};
      for (const p of PERIODS) {
        const targetDate = new Date(Date.now()-PDAYS[p]*86400000).toISOString().slice(0,10);
        const targetYM   = targetDate.slice(0,7);

        // Use daily data for 1M/3M/6M, monthly for 1Y/3Y/5Y
        if (["1M","3M","6M"].includes(p) && ptsDay.length) {
          const past = ptsDay.filter(pt => pt.ym <= targetDate);
          const hist = past.length ? past[0].close : ptsDay[ptsDay.length-1]?.close;
          result[p]  = calcReturn(curr, hist, p);
        } else {
          const past = ptsMo.filter(pt => pt.ym <= targetYM);
          const hist = past.length ? past[0].close : ptsMo[ptsMo.length-1]?.close;
          result[p]  = calcReturn(curr, hist, p);
        }
      }
      return result;
    } catch { return {}; }
  };

  // ── Load all data ──────────────────────────────────────────────────────────

  const load = async () => {
    if (loading) return;
    setLoading(true);
    const toFetch  = holdings; // no limit — all holdings
    const mfList   = toFetch.filter(h => isIndianMF(h));
    const eqList   = toFetch.filter(h => !isIndianMF(h));

    console.log(`[PerfTable] load: ${toFetch.length} total, ${mfList.length} MFs, ${eqList.length} equities`);

    // Fetch Supabase snapshots for equities in one query
    const snapshots = await fetchSnapshots(eqList.map(h => h.id));

    // Indian MFs — deduplicate by scheme code to avoid fetching same NAV multiple times
    // e.g. same fund held in multiple lots or by multiple clients
    const codeToResult = {}; // schemeCode → returns (shared cache)
    const uniqueMFs = []; // one representative per scheme code
    const codeMap = {}; // schemeCode → [holding ids that share it]

    mfList.forEach(h => {
      const code = String(h.schemeCode || h.ticker || h.id);
      if (!codeMap[code]) { codeMap[code] = []; uniqueMFs.push(h); }
      codeMap[code].push(h.id);
    });

    // Mark all MF IDs as pending so rows show spinner while loading
    setPendingIds(new Set(mfList.map(h => h.id)));

    // Fetch in parallel — proxy first (Supabase cached), fallback to direct mfapi
    await Promise.allSettled(uniqueMFs.map(async (h, i) => {
      const code = String(h.schemeCode || h.ticker || h.id);
      // Use in-memory session cache — instant on collapse/expand
      if (_mfNavCache.has(code)) {
        const cached = _mfNavCache.get(code);
        codeMap[code].forEach(hid => {
          setData(prev => ({ ...prev, [hid]: cached }));
          setPendingIds(prev => { const n = new Set(prev); n.delete(hid); return n; });
        });
        return;
      }
      // Small stagger so proxy doesn't get 50 simultaneous requests
      await new Promise(r => setTimeout(r, i * 80));
      let r = await fetchMF(h);
      // Retry once on empty result
      if (!Object.keys(r).length) {
        await new Promise(res => setTimeout(res, 1500));
        r = await fetchMF(h);
      }
      _mfNavCache.set(code, r);
      codeMap[code].forEach(hid => {
        setData(prev => ({ ...prev, [hid]: r }));
        setPendingIds(prev => { const n = new Set(prev); n.delete(hid); return n; });
      });
    }));
    setPendingIds(new Set()); // clear any remaining

    // Equities from snapshots (instant for rows that have snapshots seeded)
    // Collect holdings that still need a Yahoo fallback. Previously this only
    // covered holdings with an explicit `yahooTicker` field. Now it also covers
    // holdings with just `ticker` + country/exchange — derived via
    // deriveYahooTicker(). This means a US equity like SMH automatically falls
    // back via Yahoo even if `yahooTicker` was never set in Supabase.
    const yahooFallbackList = [];
    eqList.forEach(h => {
      const rows = snapshots[h.id] || [];
      if (rows.length) {
        const r = computeReturns(h.currentPrice, rows);
        setData(prev => ({ ...prev, [h.id]: r }));
      } else {
        const yt = deriveYahooTicker(h);
        if (yt) {
          yahooFallbackList.push({ ...h, _yt: yt });
        } else {
          // No snapshots and no derivable ticker — nothing we can do
          setData(prev => ({ ...prev, [h.id]: {} }));
        }
      }
    });

    // Fetch Yahoo historical prices for fallback list in parallel
    if (yahooFallbackList.length) {
      console.log(`[PerfTable] Yahoo fallback for ${yahooFallbackList.length} holdings`);
      // Deduplicate by derived Yahoo ticker so same ticker held in multiple
      // accounts only costs one fetch
      const tickerMap = {}; // derivedTicker -> [holding objects]
      yahooFallbackList.forEach(h => {
        const t = h._yt;
        if (!tickerMap[t]) tickerMap[t] = [];
        tickerMap[t].push(h);
      });
      // Mark all as pending so rows show a spinner
      setPendingIds(prev => {
        const n = new Set(prev);
        yahooFallbackList.forEach(h => n.add(h.id));
        return n;
      });
      await Promise.allSettled(Object.entries(tickerMap).map(async ([ticker, hs], i) => {
        // small stagger so proxy doesn't get 50 simultaneous requests
        await new Promise(r => setTimeout(r, i * 80));
        try {
          // Reuse /api/benchmark — same Yahoo v8/chart endpoint, works with any symbol
          const res = await fetch(`/api/benchmark?ticker=${encodeURIComponent(ticker)}&interval=1d&range=5y`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const j = await res.json();
          const pts = j?.points || [];
          if (!pts.length) {
            hs.forEach(h => {
              setData(prev => ({ ...prev, [h.id]: {} }));
              setPendingIds(prev => { const n = new Set(prev); n.delete(h.id); return n; });
            });
            return;
          }
          // Shape points to match what computeReturns expects: rows of
          // {date: "YYYY-MM-DD", price: number} sorted newest-first.
          // Yahoo /api/benchmark returns {ym, close} already newest-first.
          const rows = pts
            .map(p => ({ date: p.ym, price: p.close }))
            .filter(r => r.price > 0)
            .sort((a,b) => b.date.localeCompare(a.date));
          const curr = j.currentPrice || pts[0]?.close;
          hs.forEach(h => {
            const r = computeReturns(curr || h.currentPrice, rows);
            setData(prev => ({ ...prev, [h.id]: r }));
            setPendingIds(prev => { const n = new Set(prev); n.delete(h.id); return n; });
          });
        } catch (e) {
          console.warn(`[PerfTable] Yahoo fallback failed for ${ticker}:`, e.message);
          hs.forEach(h => {
            setData(prev => ({ ...prev, [h.id]: {} }));
            setPendingIds(prev => { const n = new Set(prev); n.delete(h.id); return n; });
          });
        }
      }));
    }

    // Benchmark
    const bmd = await fetchBM(bm);
    setBmData(bmd);
    setLoading(false);
  };

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && Object.keys(data).length === 0) load();
  };

  // When `open` becomes true via any path (including controlled prop change
  // from the accordion above), trigger a data load if we haven't yet. This
  // ensures clicking an external accordion card that sets open=true still
  // populates the table — onToggle alone wasn't fired in that path.
  React.useEffect(() => {
    if (open && Object.keys(data).length === 0 && !loading) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onBmChange = async (ticker) => {
    setBm(ticker);
    const bmd = await fetchBM(ticker);
    setBmData(bmd);
  };

  // ── Formatting ─────────────────────────────────────────────────────────────

  const fmtPct = (v, purple=false) => {
    if (v == null) return <span style={{color:T.textMuted,fontSize:11}}>—</span>;
    const col = purple ? "#7C3AED" : v >= 0 ? "#1D9E75" : "#A32D2D";
    return <span style={{color:col,fontWeight:600,fontSize:12}}>{v>=0?"+":""}{v.toFixed(1)}%</span>;
  };

  const wavg = (period) => {
    const rows = dedupedHoldings.filter(h => data[h.id]?.[period] != null);
    const tot  = rows.reduce((s,h) => s+(h.valueUSD||0), 0);
    if (!tot) return null;
    return rows.reduce((s,h) => s+(h.valueUSD||0)/tot*data[h.id][period], 0);
  };

  const [classFilter, setClassFilter] = React.useState(new Set()); // empty = All

  const toggleClass = (cls) => {
    setClassFilter(prev => {
      const next = new Set(prev);
      if (cls === "All") return new Set(); // clear all = show all
      // "Indian MFs" pill toggles both Indian MF class variants together
      if (cls === "Indian Mutual Funds") {
        const both = ["Indian Mutual Funds","Mutual Funds"];
        const anyOn = both.some(c => next.has(c));
        if (anyOn) { both.forEach(c => next.delete(c)); }
        else { both.forEach(c => next.add(c)); }
        return next;
      }
      if (next.has(cls)) { next.delete(cls); } else { next.add(cls); }
      return next;
    });
  };

  const sinceCostAvg = () => {
    const rows = holdings.filter(h => h.costPrice>0 && h.currentPrice>0);
    const tot  = rows.reduce((s,h) => s+(h.valueUSD||0), 0);
    if (!tot) return null;
    return rows.reduce((s,h) => s+(h.valueUSD||0)/tot*((h.currentPrice-h.costPrice)/h.costPrice*100), 0);
  };

  // All unique classes for filter pills
  const allClasses = [...new Set(holdings.map(h => h.class||"Other"))];
  const visibleHoldings = classFilter.size === 0
    ? holdings
    : holdings.filter(h => classFilter.has(h.class||"Other"));

  // Deduplicate by scheme code — keep first occurrence per code
  // Same fund held by multiple clients shows once with the shared NAV data
  const seenCodes = new Set();
  const dedupedHoldings = visibleHoldings.filter(h => {
    const code = String(h.schemeCode || h.ticker || h.id);
    const isIndMF = isIndianMF(h);
    if (isIndMF && /^\d{5,6}$/.test(code)) {
      if (seenCodes.has(code)) return false;
      seenCodes.add(code);
    }
    return true;
  });

  const grouped = {};
  dedupedHoldings.forEach(h => {
    const cls = h.class || "Other";
    if (!grouped[cls]) grouped[cls] = [];
    grouped[cls].push(h);
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  const thS = (align="right") => ({
    ...tTh(), textAlign:align, padding:"8px 10px", whiteSpace:"nowrap",
    position:"sticky", top:0, background:T.thBg, zIndex:2,
  });

  return (
    <div style={{...tCard(), marginTop:16}}>
      <div onClick={onToggle}
        style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          cursor:"pointer",userSelect:"none",marginBottom:open?14:0,gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"#7C3AED",flexShrink:0}}/>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Detailed Performance</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>
              {open?"Click a class to filter · benchmark comparison":"Portfolio weighted avg · vs "+BENCHMARKS.find(b=>b.ticker===bm)?.label}
            </div>
          </div>
          {!open && Object.keys(bmData).length > 0 && (
            <React.Fragment>
              {[["1M",false],["3M",false],["1Y",false],["3Y",true]].map(([p,ann])=>{
                const v = wavg(p);
                const col = v==null?"#6B7280":v>=0?"#1D9E75":"#A32D2D";
                return (
                  <React.Fragment key={p}>
                    <div style={{flexShrink:0,textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700,color:col}}>{v!=null?`${v>=0?"+":""}${v.toFixed(1)}%`:"—"}</div>
                      <div style={{fontSize:10,color:T.textMuted}}>{p}{ann?" ann.":""}</div>
                    </div>
                    <div style={{width:"0.5px",height:28,background:T.cardBorder,flexShrink:0}}/>
                  </React.Fragment>
                );
              })}
              {(()=>{
                const v1y = wavg("1Y");
                const bm1y = bmData["1Y"];
                const alpha = v1y!=null&&bm1y!=null?v1y-bm1y:null;
                const col = alpha==null?"#7C3AED":alpha>=0?"#1D9E75":"#A32D2D";
                return (
                  <div style={{flexShrink:0,textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:col}}>{alpha!=null?`${alpha>=0?"+":""}${alpha.toFixed(1)}%`:"—"}<span style={{fontSize:10,color:T.textMuted,fontWeight:400}}> vs BM</span></div>
                    <div style={{fontSize:10,color:T.textMuted}}>1Y alpha</div>
                  </div>
                );
              })()}
            </React.Fragment>
          )}
          {!open && Object.keys(bmData).length===0 && loading && <span style={{fontSize:11,color:T.brand,flexShrink:0}}>⟳ Loading…</span>}
        </div>
        <div style={{flexShrink:0,fontSize:14,color:T.textMuted,paddingLeft:4}}>›</div>
      </div>

      {open && (
        <div>
          {/* Class filter pills — multi-select */}
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:T.textMuted,flexShrink:0}}>Filter:</span>
            <button onClick={()=>toggleClass("All")}
              style={{fontSize:11,padding:"3px 10px",borderRadius:6,cursor:"pointer",
                border:`1px solid ${classFilter.size===0?T.brand:T.cardBorder}`,
                background:classFilter.size===0?T.brand+"22":"transparent",
                color:classFilter.size===0?T.brand:T.textSecondary,
                fontWeight:classFilter.size===0?700:400}}>
              All
            </button>
            {allClasses
              .filter(cls => cls !== "Mutual Funds") // merged into Indian MFs pill
              .map(cls => {
                // Indian MFs pill is active if either class variant is selected
                const active = cls === "Indian Mutual Funds"
                  ? (classFilter.has("Indian Mutual Funds") || classFilter.has("Mutual Funds"))
                  : classFilter.has(cls);
                return (
                  <button key={cls} onClick={()=>toggleClass(cls)}
                    style={{fontSize:11,padding:"3px 10px",borderRadius:6,cursor:"pointer",
                      border:`1px solid ${active?T.brand:T.cardBorder}`,
                      background:active?T.brand+"22":"transparent",
                      color:active?T.brand:T.textSecondary,
                      fontWeight:active?700:400,whiteSpace:"nowrap"}}>
                    {cls === "Indian Mutual Funds" ? "Indian MFs"
                      : cls === "Global Mutual Funds" ? "Global MFs"
                      : cls === "Real Estate" ? "REITs"
                      : cls}
                    {active && " ✓"}
                  </button>
                );
              })}
            {classFilter.size > 0 && (
              <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>
                {classFilter.size} selected · click to deselect
              </span>
            )}
          </div>

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            flexWrap:"wrap",gap:8,marginBottom:12}}>
            <div style={{fontSize:11,color:T.textMuted}}>
              1M/3M/6M/1Y = point-to-point · 3Y/5Y = CAGR (annualised)
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:11,color:T.textMuted}}>Benchmark:</span>
              {BENCHMARKS.map(b => (
                <button key={b.ticker} onClick={()=>onBmChange(b.ticker)}
                  style={{fontSize:11,padding:"3px 10px",borderRadius:6,cursor:"pointer",
                    border:`1px solid ${bm===b.ticker?"#7C3AED":T.cardBorder}`,
                    background:bm===b.ticker?"#7C3AED22":"transparent",
                    color:bm===b.ticker?"#7C3AED":T.textSecondary,
                    fontWeight:bm===b.ticker?700:400}}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{overflowX:"auto",overflowY:"auto",maxHeight:500,
            border:`1px solid ${T.cardBorder}`,borderRadius:8}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:860,
              fontSize:12,tableLayout:"fixed"}}>
              <colgroup>
                {["200px","100px","90px","85px","65px","65px","65px","65px","75px","75px","80px"].map((w,i)=>
                  <col key={i} style={{width:w}}/>)}
              </colgroup>
              <thead>
                <tr>
                  {["Holding","Class","Custodian","Since cost","1M","3M","6M","1Y","3Y ann.","5Y ann."].map(h=>(
                    <th key={h} style={thS(["Holding","Class","Custodian"].includes(h)?"left":"right")}>{h}</th>
                  ))}
                  <th style={{...thS("right"),color:"#7C3AED"}}>vs BM (1Y)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([cls, rows]) => (
                  <React.Fragment key={cls}>
                    <tr>
                      <td colSpan={11} style={{padding:"7px 10px",fontSize:11,fontWeight:700,
                        color:T.textSecondary,background:T.thBg,letterSpacing:"0.05em",
                        textTransform:"uppercase",borderTop:`1px solid ${T.cardBorder}`}}>
                        {cls} · {rows.length}
                      </td>
                    </tr>
                    {rows.map((h, i) => {
                      const pd      = data[h.id] || {};
                      const pending = pendingIds.has(h.id); // true while this fund is still fetching
                      const sinceRet = h.costPrice>0 && h.currentPrice>0
                        ? (h.currentPrice-h.costPrice)/h.costPrice*100 : null;
                      const vsBM = pd["1Y"]!=null && bmData["1Y"]!=null
                        ? pd["1Y"]-bmData["1Y"] : null;
                      return (
                        <tr key={h.id} style={{
                          background:i%2===0?"transparent":T.tableStripe,
                          borderBottom:`1px solid ${T.tdBorder}`}}>
                          <td style={{...tTd(),padding:"8px 10px"}}>
                            <div style={{fontWeight:600,color:T.textPrimary,overflow:"hidden",
                              textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={h.name}>{h.name}</div>
                            <div style={{fontSize:10,color:T.textMuted}}>{h.schemeCode||h.ticker}</div>
                          </td>
                          <td style={{...tTd(),padding:"8px 10px"}}>
                            <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,
                              background:T.brand+"22",color:T.brand,fontWeight:600,
                              display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {(h.class||"").replace("Indian ","").replace("Global ","")}
                            </span>
                          </td>
                          <td style={{...tTd(),padding:"8px 10px",fontSize:11,color:T.textSecondary,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {h.manager||"Self"}
                          </td>
                          <td style={{...tTd(),padding:"8px 10px",textAlign:"right"}}>
                            {fmtPct(sinceRet)}
                          </td>
                          {PERIODS.map(p => (
                            <td key={p} style={{...tTd(),padding:"8px 10px",textAlign:"right"}}>
                              {pending
                                ? <span style={{color:T.textMuted,fontSize:10}}>◌</span>
                                : fmtPct(pd[p]??null)}
                            </td>
                          ))}
                          <td style={{...tTd(),padding:"8px 10px",textAlign:"right"}}>
                            {pending
                              ? <span style={{color:T.textMuted,fontSize:10}}>◌</span>
                              : fmtPct(vsBM, true)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                <tr style={{borderTop:`2px solid ${T.cardBorder}`,background:T.thBg,
                  position:"sticky",bottom:30,zIndex:2}}>
                  <td colSpan={3} style={{...tTd(),padding:"9px 10px",fontWeight:700,fontSize:12,background:T.thBg}}>
                    Portfolio weighted avg.
                  </td>
                  <td style={{...tTd(),padding:"9px 10px",textAlign:"right",background:T.thBg}}>
                    {fmtPct(sinceCostAvg())}
                  </td>
                  {PERIODS.map(p => (
                    <td key={p} style={{...tTd(),padding:"9px 10px",textAlign:"right",fontWeight:700,background:T.thBg}}>
                      {fmtPct(wavg(p))}
                    </td>
                  ))}
                  <td style={{...tTd(),padding:"9px 10px",textAlign:"right",fontWeight:700,background:T.thBg}}>
                    {fmtPct(wavg("1Y")!=null&&bmData["1Y"]!=null ? wavg("1Y")-bmData["1Y"] : null, true)}
                  </td>
                </tr>
                <tr style={{background:T.thBg,borderTop:`1px solid ${T.tdBorder}`,
                  position:"sticky",bottom:0,zIndex:2}}>
                  <td colSpan={3} style={{...tTd(),padding:"7px 10px",fontSize:11,
                    color:"#7C3AED",fontWeight:600,background:T.thBg}}>
                    {BENCHMARKS.find(b=>b.ticker===bm)?.label||"Benchmark"}
                  </td>
                  <td style={{...tTd(),padding:"7px 10px",textAlign:"right",
                    fontSize:11,color:T.textMuted,background:T.thBg}}>—</td>
                  {PERIODS.map(p => (
                    <td key={p} style={{...tTd(),padding:"7px 10px",textAlign:"right",
                      fontSize:11,color:"#7C3AED",fontWeight:600,background:T.thBg}}>
                      {bmData[p]!=null ? `${bmData[p]>=0?"+":""}${bmData[p].toFixed(1)}%` : "—"}
                    </td>
                  ))}
                  <td style={{...tTd(),padding:"7px 10px",textAlign:"right",
                    fontSize:11,color:T.textMuted,background:T.thBg}}>—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{marginTop:8,fontSize:10,color:T.textMuted}}>
            Indian MFs: MFapi.in · Equities: Supabase price_snapshots (run seed tool to populate)
          </div>
        </div>
      )}
    </div>
  );
}

export default function HNIDashboard() {
  // ── Core state ──
  const isMobile = useIsMobile();

  // ── Inject Inter typography (one-time on mount) ──
  // Loads Inter from Google Fonts and applies a global rule that:
  //   1. Sets Inter as the body font (with system fallbacks)
  //   2. Enables tabular numerals and contextual alternates globally
  //      ('tnum' for digit alignment; 'cv11' for the single-storey 'a' which
  //      reads cleaner at small sizes)
  // PDF builders explicitly use system fonts (-apple-system, etc.) so they
  // remain unaffected by this webfont — print windows can't reliably load
  // webfonts before the print dialog fires.
  React.useEffect(() => {
    if (document.getElementById("forthius-ui-font")) return; // idempotent
    // Preconnect for faster font load
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    document.head.appendChild(pre1);
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    document.head.appendChild(pre2);
    // Stylesheet — Inter at the weights used throughout the app + IBM Plex Mono for code/numeric mono cases
    const link = document.createElement("link");
    link.id = "forthius-ui-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap";
    document.head.appendChild(link);
    // Global override: body font + tabular numerals
    const style = document.createElement("style");
    style.id = "forthius-ui-font-style";
    style.textContent = `
      body, html {
        font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-feature-settings: 'tnum' 1, 'cv11' 1;
      }
    `;
    document.head.appendChild(style);
  }, []);
  const [tab,       setTab]       = useState("summary");
  const [holdings,  setHoldings]  = useState([]);
  const holdingsRef = React.useRef([]); // always mirrors latest holdings for use in callbacks
  const [dbLoading, setDbLoading] = useState(true);
  const [fxRates,   setFxRates]   = useState(FX_RATES_DEFAULT);
  const [fxSource,  setFxSource]  = useState("static");
  const [dbError,   setDbError]   = useState(null);
  const [transactions, setTransactions] = useState([]);
  // priceSnapshots: { holdingId: [{date:"YYYY-MM-DD", price:Number, currency:String}, ...] }
  // sorted newest-first (see supabase.fetchPriceSnapshots). Powers sparkline trends.
  const [priceSnapshots, setPriceSnapshots] = useState({});
  const [dc, setDc] = useState(() => {
    try { return localStorage.getItem("fw_display_currency") || "USD"; } catch { return "USD"; }
  });
  // Per-scope model portfolios: key = "all" | "family:fam1" | "client:c1"
  const [modelsByScope, setModelsByScope] = useState(() => {
    try {
      // Always run migration to ensure newly added classes are present
      // This is idempotent — safe to run every load
      const REQUIRED_CLASSES = {
        "Market Neutral":      {targetPct:4,  targetYield:6.5},
        "Mutual Funds":        {targetPct:10, targetYield:11.0},
        "Alternates":          {targetPct:8,  targetYield:10.0},
        "Indian Equities":     {targetPct:12, targetYield:1.8},
        "Cash / Bank Balance": {targetPct:3,  targetYield:5.2},
      };
      const RENAME_MAP = {"Private Equity":"Alternates","Hedge Funds":"Alternates",
        "Long Short Funds":"Alternates","FCNs":"Alternates","FCN":"Alternates",
        "Indian Mutual Funds":"Indian Mutual Funds","Global Mutual Funds":"Global Mutual Funds",
        "Cash":"Cash / Bank Balance"};
      const rawAny = localStorage.getItem("fw_models_v3")
                  || localStorage.getItem("fw_models_v2")
                  || localStorage.getItem("fw_models_v1");
      const raw = JSON.parse(rawAny || "{}");
      const norm = {};
      let needsSave = !localStorage.getItem("fw_models_v3");
      Object.entries(raw).forEach(([scope, data]) => {
        if (!data.alloc) { norm[scope] = data; return; }
        const merged = {};
        data.alloc.forEach(row => {
          const cls = RENAME_MAP[row.assetClass] || row.assetClass;
          if (merged[cls]) {
            const prev = merged[cls];
            const totalPct = prev.targetPct + (row.targetPct||0);
            const avgYield = totalPct > 0
              ? (prev.targetYield*prev.targetPct + (row.targetYield||0)*(row.targetPct||0)) / totalPct : 0;
            merged[cls] = {...prev, targetPct: totalPct, targetYield: +avgYield.toFixed(2)};
          } else {
            merged[cls] = {...row, assetClass: cls};
          }
        });
        // Inject required classes only if completely absent from saved model
        // Zero is a valid user-set target — don't overwrite it
        Object.entries(REQUIRED_CLASSES).forEach(([cls, def]) => {
          if (!merged[cls]) {
            merged[cls] = { assetClass:cls, targetPct:def.targetPct, targetYield:def.targetYield };
            needsSave = true;
          }
        });
        norm[scope] = {...data, alloc: Object.values(merged)};
      });
      if (needsSave) {
        try { localStorage.setItem("fw_models_v3", JSON.stringify(norm)); } catch {}
      }
      return norm;
    } catch { return {}; }
  });
  const [modelScopeKey, setModelScopeKey] = useState("all"); // which scope is being edited
  const [modelActivePanel, setModelActivePanel] = useState(null); // 'gap'|'rebalance'|'currencyGap'|'matrix' — only one open at a time
  const toggleModelPanel = (key) => setModelActivePanel(p => p === key ? null : key);


  const CLASS_RENAME = {
    "Private Equity":"Alternates","Hedge Funds":"Alternates",
    "Long Short Funds":"Alternates","FCNs":"Alternates","FCN":"Alternates",
    "Indian Mutual Funds":"Indian Mutual Funds","Global Mutual Funds":"Global Mutual Funds",
    "Cash":"Cash / Bank Balance",  // normalise short form to canonical
  };
  const getModelForScope = (key) => {
    const saved = modelsByScope[key];
    const alloc = saved ? saved.alloc : MODEL_DEFAULT;
    // Rename old classes, then merge duplicate rows by summing targetPct and averaging targetYield
    const renamed = alloc.map(row => ({...row, assetClass: CLASS_RENAME[row.assetClass] || row.assetClass}));
    const merged = {};
    renamed.forEach(row => {
      const cls = row.assetClass;
      if (merged[cls]) {
        const prev = merged[cls];
        const totalPct = prev.targetPct + (row.targetPct||0);
        const avgYield = totalPct > 0
          ? (prev.targetYield * prev.targetPct + (row.targetYield||0) * (row.targetPct||0)) / totalPct
          : 0;
        merged[cls] = { ...prev, targetPct: totalPct, targetYield: +avgYield.toFixed(2) };
      } else {
        merged[cls] = { ...row };
      }
    });
    // Inject MODEL_DEFAULT classes that are completely absent from the saved model
    // Do NOT overwrite rows the user has explicitly set to 0 — zero is a valid target
    MODEL_DEFAULT.forEach(def => {
      const cls = CLASS_RENAME[def.assetClass] || def.assetClass;
      if (!merged[cls]) {
        merged[cls] = { assetClass: cls, targetPct: def.targetPct, targetYield: def.targetYield };
      }
    });
    return Object.values(merged);
  };
  const getModelCurrencyForScope = (key) =>
    modelsByScope[key] ? modelsByScope[key].currency : MODEL_CURRENCY_DEFAULT;

  const saveModelForScope = (key, alloc, currency) => {
    setModelsByScope(prev => {
      const next = { ...prev, [key]: { alloc, currency } };
      try { localStorage.setItem("fw_models_v3", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Active model for current edit scope
  const model         = getModelForScope(modelScopeKey);
  const modelCurrency = getModelCurrencyForScope(modelScopeKey);
  const setModel = (updater) => {
    const next = typeof updater === "function" ? updater(model) : updater;
    saveModelForScope(modelScopeKey, next, modelCurrency);
  };
  const setModelCurrency = (updater) => {
    const next = typeof updater === "function" ? updater(modelCurrency) : updater;
    saveModelForScope(modelScopeKey, model, next);
  };
  const [bench,     setBench]     = useState(() => {
    try { return localStorage.getItem("fw_selected_bench_v1") || ""; } catch { return ""; }
  });
  // Re-auth modal — for destructive admin actions (bulk delete, remove client, etc.)
  const [reauthModal,    setReauthModal]    = useState(null);
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthErr,      setReauthErr]      = useState("");
  const [reauthBusy,     setReauthBusy]     = useState(false);
  // Lightweight confirm modal — replaces all window.confirm() calls
  const [confirmModal,   setConfirmModal]   = useState(null); // { message, detail, onConfirm, danger }
  const requireConfirm = (message, onConfirm, detail="", danger=true) =>
    setConfirmModal({ message, detail, onConfirm, danger });
  const [viewAsClientId, setViewAsClientId] = useState(null); // admin impersonating a client view
  const [noteEditId,    setNoteEditId]    = useState(null);  // holdingId being note-edited
  const [noteEditVal,   setNoteEditVal]   = useState("");
  const [noteAdminOnly, setNoteAdminOnly] = useState(false);
  const [holdingNotes, setHoldingNotes] = useState(() => { // { holdingId: { text, updatedAt, adminOnly } }
    try { return JSON.parse(localStorage.getItem("fw_holding_notes_v1") || "{}"); } catch { return {}; }
  });
  const saveHoldingNote = (holdingId, text, adminOnly=false) => {
    const note = text.trim() ? { text: text.trim(), updatedAt: new Date().toISOString(), adminOnly } : undefined;
    setHoldingNotes(prev => {
      const next = { ...prev };
      if (note) next[String(holdingId)] = note;
      else delete next[String(holdingId)];
      // Persist to localStorage (immediate) + Supabase via holding metadata
      try { localStorage.setItem("fw_holding_notes_v1", JSON.stringify(next)); } catch {}
      // Sync note text to Supabase holdings table (notes column)
      updateHolding(holdingId, { notes: text.trim() || null })
        .catch(e => console.error("[Supabase] saveHoldingNote:", e.message));
      return next;
    });
  };
  const [alertConfig, setAlertConfig] = useState(()=>{ try{return JSON.parse(localStorage.getItem("fw_alerts_v1")||"{}");}catch{return {};} });
  const [cgJurisdiction, setCgJurisdiction] = useState("IN");
  const [cgPeriodType,   setCgPeriodType]   = useState("all");  // "all" | "FY" | "CY" | "custom"
  const [cgYear,         setCgYear]         = useState(() => {
    // Default to current Indian FY
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // FY starts April
  });
  const [cgDateFrom,     setCgDateFrom]     = useState("");
  const [cgDateTo,       setCgDateTo]       = useState("");
  const [txnPeriodType,  setTxnPeriodType]  = useState("all"); // "all" | "FY" | "CY" | "custom"
  const [txnYear,        setTxnYear]        = useState(() => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  });
  const [txnDateFrom,    setTxnDateFrom]    = useState("");
  const [txnDateTo,      setTxnDateTo]      = useState("");
  const AI_CONFIG_DEFAULT = {
    advisorProvider: "anthropic",
    advisorModel: "claude-haiku-4-5-20251001",
    researchProvider: "anthropic",
    researchModel: "claude-haiku-4-5-20251001",
    openaiKey: "",
    geminiKey: "",
  };
  const [aiConfig, setAiConfig] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("fw_ai_config") || "null");
      return saved ? { ...AI_CONFIG_DEFAULT, ...saved } : AI_CONFIG_DEFAULT;
    } catch { return AI_CONFIG_DEFAULT; }
  });
  const saveAiConfig = (next) => {
    setAiConfig(next);
    try { localStorage.setItem("fw_ai_config", JSON.stringify(next)); } catch {}
    // Sync to Supabase so it persists across devices
    upsertProfile("__ai_config__", next).catch(()=>{});
  };

  const [cgExpanded,     setCgExpanded]     = useState({});
  const [cgScopeType,    setCgScopeType]    = useState("all");
  const [cgScopeId,      setCgScopeId]      = useState("all");
  const [cgAutoJuris,    setCgAutoJuris]    = useState(true);
  const [eqExpanded, setEqExpanded] = useState(false); // equity group expand in gap analysis
  // ── Theme preference — persisted per user in localStorage ──
  // ── Theme: per-user localStorage key so each account keeps its own preference ──
  const themeStorageKey = (uid) => `fw_theme_${uid || "guest"}`;
  const [themeId, setThemeId] = useState(() => {
    // Resolve auto preference or migrate legacy/palette IDs to the single default.
    const PALETTE_REMAP = { light:"light_indigo", light_emerald:"light_indigo", light_rose:"light_indigo", light_amber:"light_indigo", light_slate:"light_indigo" };
    const resolve = (t) => PALETTE_REMAP[t] || t;
    const resolveAuto = () => { const h = new Date().getHours(); return (h >= 6 && h < 18) ? "light_indigo" : "dark"; };
    try {
      const saved = localStorage.getItem("fw_theme");
      if (!saved) return resolveAuto();           // new user → auto by time of day
      if (saved === "auto") return resolveAuto(); // explicit auto preference
      return resolve(saved);
    } catch { return resolveAuto(); }
  });
  // ── Sidebar state ──
  // ── Shortcuts / pinned tabs (per user, localStorage) ──
  const [pinnedTabs, setPinnedTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_pinned_tabs") || "[]"); } catch { return []; }
  });
  const [editingShortcuts, setEditingShortcuts] = useState(false);
  const togglePinTab = (tabId) => {
    setPinnedTabs(prev => {
      const next = prev.includes(tabId) ? prev.filter(x=>x!==tabId) : prev.length>=6 ? prev : [...prev, tabId];
      try { localStorage.setItem("fw_pinned_tabs", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // default collapsed
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [sidebarOpenGroups, setSidebarOpenGroups] = useState({portfolio:true}); // start with portfolio group open
  const toggleSidebarGroup = (gid) => setSidebarOpenGroups(p => { const isOpen = p[gid] === true; const next = {}; if (!isOpen) next[gid] = true; return next; }); // accordion: opening one closes all others
  const T = THEMES[themeId] || THEMES.light;
  // Derived mode/palette from themeId
  const themeMode = T._mode || (themeId === "dark" ? "dark" : "light");
  const themePalette = T._palette || (themeId === "light" ? "indigo" : null);
  setActiveTheme(T); // keep module-level _T in sync for card()/inp()/th()/td()/badge() helpers

  // ── Theme-aware style helpers (supplement static constants above) ──
  const tCard  = (extra={}) => ({ background:T.cardBg, border:`1px solid ${T.cardBorder}`, borderRadius: T.id==="addepar" ? 8 : 12, padding:isMobile?"12px 14px":"18px 20px", boxShadow:T.cardShadow, transition:"box-shadow 0.15s ease, border-color 0.15s ease", ...extra });
  const tInp   = (extra={}) => ({ background:T.inputBg, border:`1px solid ${T.inputBorder}`, borderRadius:6, color:T.inputText, padding:"6px 10px", fontSize:13, width:"100%", ...extra });
  // Highlight: wraps matched text in a yellow span — used in holdings search
  const HL = (text, query) => {
    if (!query || !text) return text;
    const q = query.trim();
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (
      <React.Fragment>
        {text.slice(0, idx)}
        <mark style={{background:"#FEF08A",color:"#78350F",borderRadius:2,
          padding:"0 1px",fontWeight:700}}>{text.slice(idx, idx+q.length)}</mark>
        {text.slice(idx+q.length)}
      </React.Fragment>
    );
  };
  // errInp: same as inp but with error styling — used for field-level validation
  const errInp = (hasErr, extra={}) => ({
    ...tInp(extra),
    borderColor:     hasErr ? "#A32D2D"                 : T.inputBorder,
    boxShadow:       hasErr ? "0 0 0 3px rgba(220,38,38,0.12)" : "none",
    outline:         "none",
    transition:      "border-color 0.15s ease, box-shadow 0.15s ease",
  });
  // Primary action button style — used for key CTAs
  const primaryBtn = (extra={}) => ({
    background: `${T.brand}`,
    border: "none",
    borderRadius: 8,
    color: "white",
    padding: "9px 20px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    boxShadow: `0 4px 14px ${T.brand}35`,
    transition: "opacity 0.15s ease, transform 0.15s ease",
    ...extra,
  });
  // ErrField: renders label + input + inline error message
  const ErrField = ({ label, error, children, required: req }) => (
    <div>
      <div style={{fontSize:11, fontWeight:600, marginBottom:4, textTransform:"uppercase",
        letterSpacing:"0.06em", color: error ? "#A32D2D" : T.textSecondary}}>
        {label}{req && <span style={{color:"#A32D2D",marginLeft:2}}>*</span>}
      </div>
      {children}
      {error && (
        <div style={{fontSize:10, color:"#A32D2D", marginTop:3, display:"flex",
          alignItems:"center", gap:3}}>
          <span>⚠</span><span>{error}</span>
        </div>
      )}
    </div>
  );
  const tTh    = (extra={}) => ({ background:T.thBg, color:T.textMuted, fontSize:11, letterSpacing:"0.02em", padding:"10px 12px", textAlign:"left", fontWeight:400, whiteSpace:"nowrap", borderBottom:`1px solid ${T.cardBorder}`, ...extra });
  const tTd    = (extra={}) => {
    const isNumeric = typeof extra.fontFamily === "string" && extra.fontFamily.includes("Inter");
    return {
      padding:"12px 12px", color:T.textPrimary, fontSize:13,
      borderBottom:`1px solid ${T.tdBorder}`, verticalAlign:"middle",
      ...(isNumeric ? { fontVariantNumeric: "tabular-nums" } : {}),
      ...extra,
    };
  };
  const tBadge = (col) => ({ background:col||T.badgeBg, padding:"2px 8px", borderRadius:4, fontSize:11, whiteSpace:"nowrap", color:T.badgeText });

  // Apply theme for a given user — reads profile (Supabase) first, then localStorage fallback
  const applyThemeForUser = React.useCallback((uid, profiles) => {
    const fromProfile = profiles?.[uid]?.theme;
    // Migrate old theme IDs → valid current IDs.
    // "light" → light_indigo (old default name)
    // light_emerald / light_rose / light_amber / light_slate → light_indigo
    //   (colour palettes removed — all light-mode users standardise on the default)
    const PALETTE_REMAP = { light_emerald:"light_indigo", light_rose:"light_indigo", light_amber:"light_indigo", light_slate:"light_indigo" };
    const migrateTheme = (t) => {
      if (!t) return t;
      if (t === "light") return "light_indigo";
      return PALETTE_REMAP[t] || t;
    };
    const resolveAuto = () => { const h = new Date().getHours(); return (h >= 6 && h < 18) ? "light_indigo" : "dark"; };
    if (fromProfile === "auto") { setThemeId(resolveAuto()); try { localStorage.setItem(themeStorageKey(uid), "auto"); } catch {} return; }
    if (fromProfile && THEMES[migrateTheme(fromProfile)]) {
      setThemeId(migrateTheme(fromProfile));
      try { localStorage.setItem(themeStorageKey(uid), migrateTheme(fromProfile)); } catch {}
      return;
    }
    try {
      const local = localStorage.getItem(themeStorageKey(uid));
      if (local === "auto") { setThemeId(resolveAuto()); return; }
      if (local && THEMES[migrateTheme(local)]) { setThemeId(migrateTheme(local)); return; }
    } catch {}
  }, []); // applyThemeForUser — no reactive deps needed

  // ── switchTheme: handles mode toggle + palette selection ──
  const _persistTheme = (next, uid) => {
    try { localStorage.setItem(themeStorageKey(uid), next); } catch {}
    setProfileData(prev => {
      const updated = { ...(prev[uid]||{}), theme: next };
      if (typeof upsertProfile === "function") {
        upsertProfile(uid, updated).catch(e => console.warn("[Theme] save failed:", e.message));
      }
      return { ...prev, [uid]: updated };
    });
  };

  const toggleTheme = () => {
    const next = themeId === "dark" ? "light_indigo" : "dark";
    setThemeId(next);
    const uid = isAdmin ? "admin" : loggedInClientId;
    _persistTheme(next, uid);
  };

  // Deterministic: set theme to a specific id (used by the Preferences toggle).
  // Fixes the prior bug where the modal fell back to toggleTheme() and ignored the target.
  const setThemeTo = (id) => {
    if (!id || id === themeId) {
      // Even if unchanged, ensure it's persisted (e.g. first explicit choice)
      const uid0 = isAdmin ? "admin" : loggedInClientId;
      _persistTheme(id || themeId, uid0);
      return;
    }
    setThemeId(id);
    const uid = isAdmin ? "admin" : loggedInClientId;
    _persistTheme(id, uid);
  };

  // ── Auto theme by time of day ──
  // When the user's stored preference is the literal string "auto", the actual theme
  // follows the clock: light during the day (06:00–18:00 local), dark otherwise.
  // We persist "auto" as the preference and resolve to a concrete theme at runtime.
  const resolveAutoTheme = () => {
    const h = new Date().getHours();
    return (h >= 6 && h < 18) ? "light_indigo" : "dark";
  };
  const setThemeAuto = () => {
    const uid = isAdmin ? "admin" : loggedInClientId;
    _persistTheme("auto", uid);            // store the *preference* as "auto"
    setThemeId(resolveAutoTheme());        // apply the concrete theme now
  };
  // Re-evaluate auto theme periodically (every 10 min) and on mount, so it flips at
  // 6am / 6pm without a reload — but only when the stored preference is "auto".
  // NOTE: this effect runs early in the component body, before profileData/isAdmin
  // are declared. So we read the preference directly from localStorage (not React
  // state) to avoid temporal-dead-zone errors. The effect has no dependencies — it
  // sets up once on mount, ticks forever, and gracefully no-ops if the preference
  // isn't "auto".
  React.useEffect(() => {
    const isAutoPref = () => { try { return localStorage.getItem("fw_theme") === "auto"; } catch { return false; } };
    const tick = () => { if (isAutoPref()) setThemeId(resolveAutoTheme()); };
    tick();
    const iv = setInterval(tick, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);


  const isLightMode = themeId !== "dark";

  // ── IRR Analysis state ──
  const IRR_SEGMENTS_DEF = [
    { id:"indian_stocks",  label:"Indian Stocks",       icon:"🇮🇳", color:T.brand, filter: h => h.class==="Equities" && (h.exchange==="NSE"||h.exchange==="BSE"||h.country==="India") && h.listed },
    { id:"indian_mf",      label:"Indian Mutual Funds", icon:"📈",  color:"#0EA5E9", filter: h => h.class==="Indian Mutual Funds" || h.isMutualFund },
    { id:"us_stocks",      label:"US Stocks",           icon:"🇺🇸", color:"#7C3AED", filter: h => h.class==="Equities" && (h.exchange==="NYSE"||h.exchange==="NASDAQ"||h.country==="USA") },
    { id:"unlisted",       label:"Unlisted Assets",     icon:"🔒",  color:"#D97706", filter: h => !h.listed && !h.isMutualFund },
    { id:"fixed_income",   label:"Fixed Income",        icon:"🏛️", color:"#059669", filter: h => h.class==="Fixed Income"||h.class==="Time Deposits" },
    { id:"alternates",     label:"Alternates",           icon:"🏦",  color:"#4F46E5", filter: h => h.class==="Alternates"||h.class==="Market Neutral" },
    { id:"real_estate",    label:"Real Estate",         icon:"🏢",  color:"#9333EA", filter: h => h.class==="Real Estate" },
    { id:"crypto",         label:"Crypto",              icon:"₿",   color:"#EA580C", filter: h => h.class==="Crypto" },
  ];
  const [irrActiveSegs, setIrrActiveSegs] = useState(["indian_stocks","indian_mf","us_stocks","unlisted"]);
  const [irrBench,      setIrrBench]      = useState("nifty");
  const [irrView,       setIrrView]       = useState("chart");
  const [irrSubTab,     setIrrSubTab]     = useState("overview");
  const [irrActiveBMs,  setIrrActiveBMs]  = useState(["nifty"]);
  const [irrDrillSeg,   setIrrDrillSeg]   = useState(null);
  const [irrCustomSegs, setIrrCustomSegs] = useState([]);
  const [irrManagerFilter, setIrrManagerFilter] = useState("all");
  const [moreOpen,         setMoreOpen]         = useState(false);
  const [irrAddModal,   setIrrAddModal]   = useState(false);
  const [irrNewSeg,     setIrrNewSeg]     = useState({label:"",icon:"📦",color:T.brand,classes:[]});
  const [lastUpd,   setLastUpd]   = useState(new Date());
  const [lastSavedPriceTime, setLastSavedPriceTime] = useState(null); // set when prices persisted to Supabase
  const [refreshing,setRefreshing]= useState(false);
  const [refreshingIds, setRefreshingIds] = useState(new Set()); // per-holding price refresh
  const [holdingSearch, setHoldingSearch] = useState("");   // free-text search in holdings tab
  const [globalSearch,  setGlobalSearch]  = useState("");   // global omnibar search

  // ── AI feature state ─────────────────────────────────────────────────────
  const [aiInsights,       setAiInsights]       = useState({});      // holdingId -> {insight, loading, error}
  const [perfFilter,       setPerfFilter]       = useState({ custodian: null, advisor: null }); // cross-filter between perf cards
  const [aiQueryLoading,   setAiQueryLoading]   = useState(false);
  const [aiQueryError,     setAiQueryError]     = useState(null);
  const [aiQueryDesc,      setAiQueryDesc]      = useState(null);    // description of active NL filter
  const [nlQueryInput,     setNlQueryInput]     = useState("");
  const [aiCommentary,     setAiCommentary]     = useState(null);    // latest weekly commentary
  const [aiCommentaryLoad, setAiCommentaryLoad] = useState(false);
  const [aiRebalance,      setAiRebalance]      = useState(null);
  const [aiRebalanceLoad,  setAiRebalanceLoad]  = useState(false);
  const [aiTaxloss,        setAiTaxloss]        = useState(null);
  const [aiTaxlossLoad,    setAiTaxlossLoad]    = useState(false);
  const [aiAnomalies,      setAiAnomalies]      = useState(null);    // {alerts, healthScore}
  const [aiAnomalyLoad,    setAiAnomalyLoad]    = useState(false);
  const [aiBriefing,       setAiBriefing]       = useState(null);
  const [aiBriefingLoad,   setAiBriefingLoad]   = useState(false);
  const [aiErrors,         setAiErrors]         = useState({});  // feature -> error message
  const [aiTestResult,     setAiTestResult]     = useState(null);
  const [aiTestLoad,       setAiTestLoad]       = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // AI tool filtering — scopes what holdings each AI surface analyses.
  // - aiFilters.global applies to every AI surface unless that surface has an
  //   override entry in aiFilters.overrides[cardKey].
  // - Override semantics = FULL REPLACE (not merge). A card with an override
  //   ignores the global filter entirely.
  // - Empty assetClasses[] / sectors[] arrays mean "no filter on that
  //   dimension" (i.e. accept all). Region "all" means accept all.
  // - Card keys: "anomaly", "taxloss", "rebalance", "briefing", "nlquery",
  //   "chat", "commentary", "research", "wladvisor".
  // - Persistence: session only (no localStorage / Supabase). Resets on reload.
  // ─────────────────────────────────────────────────────────────────────────
  const [aiFilters, setAiFilters] = useState({
    global: { assetClasses: [], region: "all", sectors: [] },
    overrides: {},
  });
  const [aiFilterBarOpen, setAiFilterBarOpen] = useState(false);

  // Returns the effective filter for a given AI card key (override or global)
  const getEffectiveAiFilter = React.useCallback((cardKey) => {
    return aiFilters.overrides[cardKey] || aiFilters.global;
  }, [aiFilters]);

  // Apply a filter object to a holdings list. Pure function — no side effects.
  // Region matching: "indian" = country=India or exchange in NSE/BSE/AMFI;
  //                  "global" = NOT indian.
  const applyAiFilter = React.useCallback((holdings, filter) => {
    if (!filter) return holdings;
    const { assetClasses = [], region = "all", sectors = [] } = filter;
    const noClass = assetClasses.length === 0;
    const noSector = sectors.length === 0;
    return (holdings || []).filter(h => {
      if (!noClass && !assetClasses.includes(h.class)) return false;
      if (!noSector && !sectors.includes(h.sector || "Other")) return false;
      if (region !== "all") {
        const indian = h.country === "India" ||
                       h.exchange === "NSE" || h.exchange === "BSE" || h.exchange === "AMFI";
        if (region === "indian" && !indian) return false;
        if (region === "global" && indian) return false;
      }
      return true;
    });
  }, []);

  // Human-readable summary of a filter (for UI badges and prompt context)
  const describeAiFilter = React.useCallback((filter) => {
    if (!filter) return "All holdings";
    const { assetClasses = [], region = "all", sectors = [] } = filter;
    const parts = [];
    if (assetClasses.length > 0) parts.push(assetClasses.join("+"));
    if (region === "indian") parts.push("Indian");
    if (region === "global") parts.push("Global");
    if (sectors.length > 0) parts.push(sectors.length === 1 ? sectors[0] : `${sectors.length} sectors`);
    return parts.length ? parts.join(" · ") : "All holdings";
  }, []);

  const isFilterEmpty = (f) =>
    !f || (f.assetClasses?.length === 0 && f.region === "all" && f.sectors?.length === 0);

  // ── Reusable AI filter bar ──
  // Both AI Advisor and AI Research render this. holdingsForOptions is the universe
  // of holdings to derive available asset classes / sectors from (typically filteredHW
  // for AI Advisor, or activeResearchClient.holdings for AI Research).
  const renderAiFilterBar = (holdingsForOptions) => {
    const f = aiFilters.global;
    const empty = isFilterEmpty(f);
    const scoped = applyAiFilter(holdingsForOptions, f);
    const scopedV = scoped.reduce((s,h)=>s+(h.valueUSD||0),0);
    const totalV2 = holdingsForOptions.reduce((s,h)=>s+(h.valueUSD||0),0) || 1;
    const allClasses = [...new Set(holdingsForOptions.map(h=>h.class).filter(Boolean))].sort();
    const allSectors = [...new Set(holdingsForOptions.map(h=>h.sector||"Other").filter(Boolean))].sort();
    const toggleClass = (c) => setAiFilters(p => {
      const has = p.global.assetClasses.includes(c);
      return { ...p, global: { ...p.global,
        assetClasses: has ? p.global.assetClasses.filter(x=>x!==c) : [...p.global.assetClasses, c]
      }};
    });
    const toggleSector = (s) => setAiFilters(p => {
      const has = p.global.sectors.includes(s);
      return { ...p, global: { ...p.global,
        sectors: has ? p.global.sectors.filter(x=>x!==s) : [...p.global.sectors, s]
      }};
    });
    const reset = () => setAiFilters(p => ({ ...p, global: { assetClasses: [], region: "all", sectors: [] }}));
    const chipBase = (active) => ({
      padding:"4px 10px", fontSize:11, borderRadius:14, cursor:"pointer",
      background: active ? "#2563EB" : T.cardBg2 || "rgba(255,255,255,0.05)",
      color: active ? "#fff" : T.textPrimary,
      border: `1px solid ${active ? "#2563EB" : T.cardBorder}`,
      userSelect:"none", display:"inline-flex", alignItems:"center", gap:4,
    });
    return (
      <div style={{...card(), padding:"10px 14px", marginBottom:12, fontSize:12}}>
        <div style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer", userSelect:"none"}}
             onClick={() => setAiFilterBarOpen(o => !o)}>
          <span style={{fontSize:12, fontWeight:700, color:T.textPrimary}}>FILTER AI TOOLS</span>
          <span style={{color:T.textSecondary, fontSize:11}}>
            {empty ? "All holdings" : describeAiFilter(f)}
            {" · "}
            {scoped.length} of {holdingsForOptions.length} holdings
            {" · "}
            {fmt(convert(scopedV, dc), dc, true)} of {fmt(convert(totalV2 === 1 ? 0 : totalV2, dc), dc, true)}
          </span>
          <span style={{marginLeft:"auto", display:"flex", alignItems:"center", gap:8}}>
            {!empty && <button onClick={(e)=>{e.stopPropagation();reset();}}
              style={{background:"transparent", border:`1px solid ${T.cardBorder}`, borderRadius:6, color:T.textSecondary, padding:"3px 8px", cursor:"pointer", fontSize:10}}>
              Reset
            </button>}
            <span style={{color:T.textSecondary, fontSize:13}}>{aiFilterBarOpen ? "▴" : "▾"}</span>
          </span>
        </div>
        {aiFilterBarOpen && (
          <div style={{borderTop:`1px solid ${T.cardBorder}`, marginTop:10, paddingTop:10, display:"flex", flexDirection:"column", gap:10}}>
            {/* Asset Class */}
            <div>
              <div style={{fontSize:10, color:T.textSecondary, marginBottom:5, letterSpacing:0.4, fontWeight:600}}>ASSET CLASS</div>
              <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                {allClasses.length === 0 && <span style={{fontSize:11, color:T.textMuted}}>No holdings to filter</span>}
                {allClasses.map(c => (
                  <span key={c} onClick={()=>toggleClass(c)} style={chipBase(f.assetClasses.includes(c))}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
            {/* Region */}
            <div>
              <div style={{fontSize:10, color:T.textSecondary, marginBottom:5, letterSpacing:0.4, fontWeight:600}}>REGION</div>
              <div style={{display:"flex", gap:6}}>
                {[["all","All"], ["indian","Indian"], ["global","Global"]].map(([v, lbl]) => (
                  <span key={v} onClick={()=>setAiFilters(p=>({...p, global:{...p.global, region:v}}))}
                        style={chipBase(f.region === v)}>
                    {lbl}
                  </span>
                ))}
              </div>
            </div>
            {/* Sectors */}
            <div>
              <div style={{fontSize:10, color:T.textSecondary, marginBottom:5, letterSpacing:0.4, fontWeight:600}}>SECTOR</div>
              <div style={{display:"flex", flexWrap:"wrap", gap:6, maxHeight:80, overflowY:"auto"}}>
                {allSectors.length === 0 && <span style={{fontSize:11, color:T.textMuted}}>No sectors</span>}
                {allSectors.map(s => (
                  <span key={s} onClick={()=>toggleSector(s)} style={chipBase(f.sectors.includes(s))}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Detail drawer for AI Advisor cards ──
  // When a card is clicked (or after a scan completes), this is set to the
  // card's id — "anomaly" | "taxloss" | "rebalance" | "briefing" | null.
  // A full-width drawer renders below the card row showing that card's details.
  const [expandedAICard,   setExpandedAICard]   = useState(null);
  const [globalOpen,    setGlobalOpen]    = useState(false); // search results panel open
  const [lastRefreshTime, setLastRefreshTime] = useState(null); // timestamp of last price refresh
  const [priceFailedIds, setPriceFailedIds] = useState(new Set()); // holding IDs where price fetch failed
  const [auditLog, setAuditLog] = useState([]);             // admin action log [{ts,action,detail,user}]
  const logAudit = React.useCallback((action, detail) => {
    const entry = { id: Date.now(), ts: new Date().toISOString(), action, detail,
      user: "Admin" };
    setAuditLog(prev => [entry, ...prev].slice(0, 200)); // keep last 200 entries
  }, []);
  const [addOpen,   setAddOpen]   = useState(false);
  const [isAdmin,   setIsAdmin]   = useState(false);
  const [signedOut, setSignedOut] = useState(true); // always show login on fresh load
  const [loginRole, setLoginRole] = useState("client");
  const [loginId,   setLoginId]   = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr,  setLoginErr]  = useState("");
  const [signupMode, setSignupMode]   = useState(false);  // true = show sign-up
  const [loginBusy,  setLoginBusy]   = useState(false);  // true while async login is in progress form
  const [signupForm, setSignupForm]   = useState({ name:"", email:"", phone:"", password:"", confirm:"" });
  const [signupErr,  setSignupErr]    = useState("");
  const [otpStep,    setOtpStep]      = useState(false);  // true = show OTP entry screen
  const [otpCode,    setOtpCode]      = useState("");     // what user types
  const [otpSent,    setOtpSent]      = useState(false);  // OTP email sent
  const [otpErr,     setOtpErr]       = useState("");
  const [otpBusy,    setOtpBusy]      = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);   // resend cooldown
  const [signupOk,   setSignupOk]     = useState(null);   // { loginId, trialEnds } on success
  const [signupBusy, setSignupBusy]   = useState(false);
  const [forgotPassOpen, setForgotPassOpen] = useState(false); // forgot password contact modal
  const [loggedInClientId, setLoggedInClientId] = useState(null);
  const [sessionExpired,  setSessionExpired]  = useState(false); // shown after idle logout
  const idleTimerRef = React.useRef(null);
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const resetIdleTimer = React.useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Auto-logout on idle — only if logged in
      setSignedOut(true);
      setSessionExpired(true);
      setLoggedInClientId(null);
      try { localStorage.removeItem("fw_session_v1"); } catch {}
    }, IDLE_TIMEOUT_MS);
  }, []);
  // Start/stop idle timer based on login state
  React.useEffect(() => {
    if (signedOut) { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); return; }
    const events = ["mousemove","keydown","mousedown","touchstart","scroll","click"];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive:true }));
    resetIdleTimer(); // start immediately on login
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [signedOut, resetIdleTimer]);
  const [clientLogins, setClientLogins] = useState({});
  const [clientPlans,  setClientPlansRaw]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("forthius_clientPlans") || "{}"); } catch { return {}; }
  });
  const setClientPlans = (val) => {
    const next = typeof val === "function" ? val(clientPlans) : val;
    setClientPlansRaw(next);
    try { localStorage.setItem("forthius_clientPlans", JSON.stringify(next)); } catch {}
    // Sync to Supabase (fire-and-forget, use profiles table as carrier)
    upsertProfile("__clientPlans__", next).catch(() => {});
  };
  // clientPlans shape: { [clientId]: { plan, trialEnd, expiresAt, gateway, updatedAt } }
  // getEffectivePlan: respects trial expiry and plan expiry
  const getEffectivePlan = (clientId) => {
    const entry = clientPlans[clientId];
    if (!entry) return "free";
    if (typeof entry === "string") return entry; // legacy
    const now = new Date();
    if (entry.expiresAt && new Date(entry.expiresAt) < now) return "free";
    if (entry.trialEnd && new Date(entry.trialEnd) >= now) return entry.plan || "pro";
    return entry.plan || "free";
  };
  const setPlanForClient = (clientId, plan, opts = {}) => {
    setClientPlans(p => ({ ...p, [clientId]: {
      plan, gateway: opts.gateway || "manual",
      trialEnd: opts.trialEnd || null,
      expiresAt: opts.expiresAt || null,
      updatedAt: new Date().toISOString(),
    }}));
  };
  const [upgradeModal, setUpgradeModal] = useState(null); // { tabId, tabLabel, tabIcon }
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  // ── Profile / KYC & avatar menu state ──
  const [profileOpen,    setProfileOpen]    = useState(false);
  const [profileTab,     setProfileTab]     = useState("personal"); // "personal"|"kyc"|"security"
  const [profileData,    setProfileData]    = useState({});         // keyed by userId ("admin" | clientId)
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [pwModal,        setPwModal]        = useState(false);

  // Admin panel: client credentials manager
  const [adminPanel, setAdminPanel]   = useState(null); // null | "credentials"
  const [editingCred, setEditingCred] = useState({}); // { clientId: { loginId, password } }
  const [revealPassIds, setRevealPassIds] = useState(new Set()); // clientIds with password visible
  const [revealedPlain, setRevealedPlain] = useState({}); // clientId → plaintext (after reset)
  const toggleRevealPass = (cid) => setRevealPassIds(prev => {
    const next = new Set(prev);
    next.has(cid) ? next.delete(cid) : next.add(cid);
    return next;
  });
  const [nh, setNh] = useState({ name:"",ticker:"",class:"Equities",currency:"USD",qty:"",costPrice:"",currentPrice:"",country:"",exchange:"",listed:true,manualPriceDate:"",purchaseDate:"",assignedClientId:"",capSize:"N/A",sector:"",manager:"",advisor:"",yahooTicker:"" });
  const [nhErrors, setNhErrors] = useState({}); // field-level validation errors
  const [nhCountryQuery,   setNhCountryQuery]   = useState("");
  const [nhCountryOpen,    setNhCountryOpen]    = useState(false);
  const [nhCcOpen,         setNhCcOpen]         = useState(false);
  const [nhCcQ,            setNhCcQ]            = useState("");
  const [nhTickerQuery,    setNhTickerQuery]    = useState("");
  const [nhTickerOpen,     setNhTickerOpen]     = useState(false);
  const [nhTickerResults,  setNhTickerResults]  = useState([]);
  const [nhTickerLoading,  setNhTickerLoading]  = useState(false);
  const [wlTickerQuery,    setWlTickerQuery]    = useState("");
  const [wlTickerOpen,     setWlTickerOpen]     = useState(false);
  const [wlTickerResults,  setWlTickerResults]  = useState([]);
  const [wlTickerLoading,  setWlTickerLoading]  = useState(false);
  const [wlThesisLoading,  setWlThesisLoading]  = useState(null);  // tickerId currently loading thesis
  const [wlThesisModalId,  setWlThesisModalId]  = useState(null);  // tickerId whose full thesis is open in modal
  const [wlExportOpen,     setWlExportOpen]     = useState(false); // export dropdown visibility
  // ── AI Watchlist Advisor state ──
  // Cached per-scope: key is selectedClients-set serialised so a different client
  // gets their own report. Reports persist for the session; click Regenerate to
  // force a fresh call. No Supabase persistence in v1 — just in-memory cache.
  const [wlAdvisorOpen,    setWlAdvisorOpen]    = useState(false);
  const [wlAdvisorLoading, setWlAdvisorLoading] = useState(false);
  const [wlAdvisorError,   setWlAdvisorError]   = useState(null);
  const [wlAdvisorReports, setWlAdvisorReports] = useState({}); // { scopeKey: parsedReport }
  const [wlTechSignals,    setWlTechSignals]    = useState({}); // { scopeKey: { ticker: signals } }
  const [wlLookthrough,    setWlLookthrough]    = useState({}); // { scopeKey: lookthroughResult }
  const [wlTechLoading,    setWlTechLoading]    = useState(false);
  const [wlThesisMap,      setWlThesisMap]      = useState({});     // { tickerId: thesisText }

  // ── News Feed state ──
  const [newsItems,      setNewsItems]      = useState([]);
  const [newsLoading,    setNewsLoading]    = useState(false);
  const [newsFilter,     setNewsFilter]     = useState("all");  // "all" | ticker symbol
  const [newsLastFetch,  setNewsLastFetch]  = useState(null);

  // ── Goals state (persisted per-user in localStorage, keyed by clientId or "admin") ──
  // Each goal has: { id, clientId, familyId, name, type, targetAmount, currency,
  //                  targetDate, expectedReturn, portfolioAlloc, notes }
  const [allGoals, setAllGoals] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_goals_v2") || "[]"); } catch { return []; }
  });
  const saveAllGoals = (g) => {
    setAllGoals(g);
    try { localStorage.setItem("fw_goals_v2", JSON.stringify(g)); } catch {}
  };
  const [goalScopeType,   setGoalScopeType]   = useState("client");  // "client"|"family"
  const [goalScopeId,     setGoalScopeId]     = useState(null);      // clientId or familyId
  const [rbScopeType,     setRbScopeType]     = useState("all");     // rebalance scope
  const [rbScopeId,       setRbScopeId]       = useState("all");

  // ── Dividend & Income Tracker ──
  // Each event: { id, holdingId, clientId, exDate, payDate, dps, totalReceived,
  //               currency, drip, dripShares, notes }
  const [dividends, setDividends] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_dividends_v1") || "[]"); } catch { return []; }
  });
  // ── Bulk CSV export — all clients' holdings ─────────────────────────────
  const exportAllHoldingsCSV = () => {
    const rows = hwWithOwner.map(h => {
      const client = clients.find(c=>c.id===h.clientId);
      const family = families.find(f=>f.id===client?.familyId);
      return [
        client?.name||"Unassigned",
        family?.name||"",
        h.name||"",
        h.ticker||"-",
        h.class||"",
        h.country||"",
        h.currency||"",
        h.qty||0,
        h.costPrice||0,
        h.currentPrice||0,
        (h.qty*(h.currentPrice||0)).toFixed(2),
        h.costPrice>0 ? (((h.currentPrice-h.costPrice)/h.costPrice)*100).toFixed(2)+"%" : "",
        h.manager||"",
        h.isin||"",
        h.sector||"",
        h.exchange||"",
        h.manualPriceDate||"",
      ].map(v=>typeof v==="string"&&v.includes(",") ? `"${v}"` : v).join(",");
    });
    const header = "Client,Family,Asset,Ticker,Class,Country,Currency,Qty,Cost Price,Current Price,Value,Return %,Manager,ISIN,Sector,Exchange,Price Date";
    const csv    = [header, ...rows].join("\n");
    const blob   = new Blob([csv], { type:"text/csv;charset=utf-8;" });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement("a");
    a.href = url; a.download = `forthius_holdings_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    logAudit("EXPORT_ALL_CSV", `${hwWithOwner.length} holdings exported`);
    showToast(`✓ Exported ${hwWithOwner.length} holdings to CSV`, "#1D9E75");
  };

  const saveDividends = (d) => {
    setDividends(d);
    try { localStorage.setItem("fw_dividends_v1", JSON.stringify(d)); } catch {}
  };
  const [divModalOpen,  setDivModalOpen]  = useState(false);
  const [editingDiv,    setEditingDiv]    = useState(null);
  const [divScopeType,  setDivScopeType]  = useState("all");
  const [divScopeId,    setDivScopeId]    = useState("all");
  const [goalModalOpen,   setGoalModalOpen]   = useState(false);
  const [editingGoal,     setEditingGoal]     = useState(null);
  const [wlEditId,         setWlEditId]         = useState(null);   // id of watchlist item being edited
  const [wlEditVals,       setWlEditVals]       = useState({});      // partial overrides during edit
  const [alertToasts,      setAlertToasts]      = useState([]);      // [{id,msg,col}] price alert toasts
  const [credModal,        setCredModal]        = useState(null);    // {name,loginId,password} shown in copy modal

  // ── Utility: fire a toast (reuses alertToasts infrastructure) ──
  const showToast = React.useCallback((msg, col="#007878", dur=4500) => {
    const id = "t"+Date.now()+Math.random();
    setAlertToasts(p=>[...p,{id,msg,col}]);
    setTimeout(()=>setAlertToasts(p=>p.filter(x=>x.id!==id)), dur);
  }, []);
  const [btTickerQuery,    setBtTickerQuery]    = useState("");
  const [btTickerOpen,     setBtTickerOpen]     = useState(false);
  const [btTickerResults,  setBtTickerResults]  = useState([]);
  const [btTickerLoading,  setBtTickerLoading]  = useState(false);

  // ── Family & Client state ──
  const [families,      setFamilies]      = useState([]);
  const [clients,       setClients]       = useState([]);
  const [holdingOwners, setHoldingOwners] = useState({}); // { holdingId: clientId }
  const [selectedFamily,  setSelectedFamily]  = useState("all");   // "all" | familyId
  const [selectedClients, setSelectedClients] = useState(new Set()); // Set of clientIds; empty = all

  // ── aiClientContext ─────────────────────────────────────────────────────
  // Passed with every AI API call for plan gating + clientId tracking.
  //
  // Design: admins always use clientId="admin" / plan="premium" — no limits,
  // no recording against any client's quota. The viewAsClient / selectedClient
  // context affects *which portfolio data* the panels analyse (via filteredHW
  // downstream), but never the quota identity of the AI call.
  //
  // Non-admin (client login): uses viewAs > selected > logged-in > "admin"
  // fallback, with plan resolved from their actual subscription.
  const aiClientContext = React.useMemo(() => {
    if (isAdmin) {
      return { clientId: "admin", plan: "premium" };
    }
    const contextClientId = viewAsClientId
      || (selectedClients.size > 0 ? [...selectedClients][0] : null)
      || loggedInClientId
      || "admin";
    return { clientId: contextClientId, plan: getEffectivePlan(contextClientId) };
  }, [viewAsClientId, selectedClients, loggedInClientId, isAdmin, clientPlans]);
  const [scopeFamilySearch, setScopeFamilySearch] = useState("");   // search text in family dropdown
  const [scopeClientSearch, setScopeClientSearch] = useState("");   // search text in client dropdown
  const [scopeFamilyOpen,   setScopeFamilyOpen]   = useState(false);
  const [scopeClientOpen,   setScopeClientOpen]   = useState(false);

  // ── Sync model scope ↔ sidebar selection ──
  // Defined here — AFTER setSelectedFamily/setSelectedClients are declared
  const setModelScopeKeyAndSync = React.useCallback((key) => {
    setModelScopeKey(key);
    if (key === "all") {
      setSelectedFamily("all");
      setSelectedClients(new Set());
    } else if (key.startsWith("family:")) {
      const fid = key.replace("family:", "");
      setSelectedFamily(fid);
      setSelectedClients(new Set());
    } else if (key.startsWith("client:")) {
      const cid = key.replace("client:", "");
      setSelectedFamily("all");
      setSelectedClients(new Set([cid]));
    }
  }, [setSelectedFamily, setSelectedClients]);

  // Reverse sync: when sidebar selection changes while on model tab, update modelScopeKey
  React.useEffect(() => {
    if (tab !== "model") return;
    if (selectedClients.size === 1) {
      setModelScopeKey(`client:${[...selectedClients][0]}`);
    } else if (selectedFamily !== "all" && selectedClients.size === 0) {
      setModelScopeKey(`family:${selectedFamily}`);
    } else if (selectedFamily === "all" && selectedClients.size === 0) {
      setModelScopeKey("all");
    }
  }, [tab, selectedFamily, selectedClients]);
  const [familyTab,     setFamilyTab]     = useState("overview"); // sub-tab within Families
  const [addFamilyOpen, setAddFamilyOpen] = useState(false);
  // ── Client onboarding wizard ──
  const [wizardOpen, setWizardOpen]   = useState(false);
  const [wizardStep, setWizardStep]   = useState(1);   // 1=family 2=client 3=login 4=done
  const [wizardFam,  setWizardFam]    = useState({ name:"", emoji:"👤", color:"#6366F1", advisorName:"" });
  const [wizardCli,  setWizardCli]    = useState({ name:"", role:"Primary", riskProfile:"Moderate", dob:"" });
  const [wizardCred, setWizardCred]   = useState({ loginId:"", password:"", confirm:"" });
  const [wizardFamId, setWizardFamId] = useState(null);  // created family id
  const [wizardCliId, setWizardCliId] = useState(null);  // created client id
  const [wizardErr,  setWizardErr]    = useState("");
  const [wizardBusy, setWizardBusy]   = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newFamily, setNewFamily] = useState({ name:"", color:"#2563EB", emoji:"🏠" });
  const [newClient, setNewClient] = useState({ familyId:"", name:"", role:"Primary", dob:"" });
  const [editingHoldingOwner, setEditingHoldingOwner] = useState(null);
  const [editingCapId, setEditingCapId] = useState(null); // holdingId being re-assigned
  // ── Edit Holding modal ──
  const [editHoldId,    setEditHoldId]    = useState(null);  // id of holding being edited
  const [editHoldVals,  setEditHoldVals]  = useState({});    // partial overrides
  const [expandedHoldGroups, setExpandedHoldGroups] = useState(new Set()); // set of group keys that are expanded
  // e-CAS / NSDL / CDSL upload state
  const [casUploading,  setCasUploading]  = useState(false);
  const [casResult,     setCasResult]     = useState(null);   // { holdings, type, holderN
  const [casTickerOverrides, setCasTickerOverrides] = useState({}); // {index: ticker override}
  const [casOverrides,       setCasOverrides]       = useState({}); // {index: {name,qty,costPrice,currentPrice,class}}
  // ── AI Statement Parser state ──
  const [aiParseMode,       setAiParseMode]       = useState("holdings"); // "holdings" | "transactions"
  const [aiParseFile,       setAiParseFile]       = useState(null);
  const [aiParseLoading,    setAiParseLoading]    = useState(false);
  const [aiParseResult,     setAiParseResult]     = useState(null); // {holdings, confidence, broker}
  const [aiParseError,      setAiParseError]      = useState("");
  const [aiParseOverrides,  setAiParseOverrides]  = useState({}); // row edits
  const [aiParseAssignTo,   setAiParseAssignTo]   = useState("");
  const [aiParsePassword,   setAiParsePassword]   = useState("");
  const [casPassword,   setCasPassword]   = useState("");
  const [folioFile,     setFolioFile]     = useState(null);
  const [folioLoading,  setFolioLoading]  = useState(false);
  const [folioResult,   setFolioResult]   = useState(null);  // { transactions, clientName }
  const [folioError,    setFolioError]    = useState("");
  const [folioImported,  setFolioImported]  = useState(false);
  const [folioImporting,      setFolioImporting]      = useState(false);
  const [folioGenerating,     setFolioGenerating]     = useState(false);
  const [folioHoldingsResult, setFolioHoldingsResult] = useState(null);
  const [genFromTxnRunning,  setGenFromTxnRunning]  = useState(false);
  const [allocView,          setAllocView]          = useState("manager"); // "manager" | "advisor"
  const [genFromTxnResult,   setGenFromTxnResult]   = useState(null);
  const [folioProgress,  setFolioProgress]  = useState(0);
  const [casError,      setCasError]      = useState("");
  const [casAssignTo,   setCasAssignTo]   = useState("");     // clientId to assign imported holdings
  const [casPreview,    setCasPreview]    = useState([]);     // holdings selected for import
  const [casFile,       setCasFile]       = useState(null);
  const [csvPreview,    setCsvPreview]    = useState(null);  // { rows, target, errors[] }
  const [csvAssignTo,   setCsvAssignTo]   = useState("");    // clientId for CSV import
  const [uploadType,    setUploadType]    = useState("holdings"); // holdings|transactions|mf|globalmf — streamlined uploader
  const [uploadColsOpen, setUploadColsOpen] = useState(false);    // column reference expanded?
  const [casDebugText,  setCasDebugText]  = useState(""); // raw text from PDF for debugging
  const [casDebugItems, setCasDebugItems] = useState([]); // coordinate items for debugging
  const [casShowDebug,  setCasShowDebug]  = useState(false);
  // Detailed statement upload state (transaction history + cost price)
  const [detUploading,  setDetUploading]  = useState(false);
  const [detResult,     setDetResult]     = useState(null);
  const [detPassword,   setDetPassword]   = useState("");
  const [detError,      setDetError]      = useState("");
  const [detAssignTo,   setDetAssignTo]   = useState("");
  const [detFile,       setDetFile]       = useState(null);
  const [detPreview,    setDetPreview]    = useState([]);   // selected txn indices
  const [detApplyCost,  setDetApplyCost]  = useState(true); // auto-update holding cost prices
  // ── Chart drill-down / holdings column filters ──
  const [holdingsDrillFilter, setHoldingsDrillFilter] = useState({}); // { cap, class, currency, manager, sector }
  const [holdingsSort, setHoldingsSort] = useState({ col: "value", dir: "desc" }); // column sort for holdings table
  // Hover states for interactive charts — must live at component level (hooks rule)
  const [hovSector, setHovSector] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set()); // holding IDs selected for bulk delete
  const [holdingsOpen, setHoldingsOpen] = useState(true); // holdings table collapsed/expanded
  const [importProgress, setImportProgress] = useState(null); // {done, total, current} for CSV import
  const [hiddenCols, setHiddenCols] = useState(new Set(["src","cap","ccy","ticker","totalcost","pl","owner"])); // owner: now plain text, hidden by default — reveal via Columns picker
  const [colMenuOpen, setColMenuOpen] = useState(false); // column picker open
  const holdingsMirrorRef = React.useRef(null);  // top scrollbar mirror
  const holdingsTableRef  = React.useRef(null);  // main table scroll container
  const [holdingsViewMode, setHoldingsViewMode] = useState(() => typeof window!=="undefined" && window.innerWidth < 768 ? "cards" : "table"); // "table" | "cards"
  const [treemapBy, setTreemapBy] = useState("class"); // "class" | "sector" | "manager"
  const [treemapVisible, setTreemapVisible] = useState(false); // collapsed by default
  const [expandedAMC, setExpandedAMC] = useState(null);
  const [amcOpen, setAmcOpen] = useState(false);

  // ── Accordion state for Holdings tab cards ──────────────────────────
  // One card expanded at a time: "holdings" | "amc" | "perf" | null.
  // Holdings tab opens with holdings table expanded by default.
  // Kept in sync with legacy holdingsOpen / amcOpen toggles so all the
  // existing conditional-render code that checks those flags keeps working.
  const [expandedCard, setExpandedCard] = useState("holdings");

  React.useEffect(() => {
    setHoldingsOpen(expandedCard === "holdings");
    setAmcOpen(expandedCard === "amc");
    // PerfTable is controlled via props below — it reads expandedCard directly.
  }, [expandedCard]);
  const [notifPrefs, setNotifPrefs] = useState({
    priceAlerts: true,       // in-app toast when watchlist price crossed
    browserPush: false,      // browser push notifications
    weeklyDigest: false,     // weekly portfolio summary (future)
    importSuccess: true,     // toast on CAS import success
  });
  // Edit modal search states (component level — hooks rule)
  const [editMfQuery,      setEditMfQuery]      = useState("");
  const [editMfResults,    setEditMfResults]    = useState([]);
  const [editMfLoading,    setEditMfLoading]    = useState(false);
  const [editMfOpen,       setEditMfOpen]       = useState(false);
  const [editTkQuery,      setEditTkQuery]      = useState("");
  const [editTkResults,    setEditTkResults]    = useState([]);
  const [editTkLoading,    setEditTkLoading]    = useState(false);
  const [editTkOpen,       setEditTkOpen]       = useState(false);
  const [hovCap,    setHovCap]    = useState(null);
  // Admin Users panel state — must be component-level (hooks rule)
  const [newAdminForm, setNewAdminForm] = useState({ loginId:"", displayName:"", password:"", confirm:"" });
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [addAdminErr,  setAddAdminErr]  = useState("");
  const clearDrillFilter = () => setHoldingsDrillFilter({});
  const setDrill = (key, val) => setHoldingsDrillFilter(p => {
    // toggle off if same value clicked again
    if (p[key] === val) { const n = {...p}; delete n[key]; return n; }
    return { ...p, [key]: val };
  });
  // ── Transactions CRUD ──
  const [txnSearch,     setTxnSearch]     = useState("");
  const [txnFundFilter, setTxnFundFilter] = useState("");   // filter by specific fund name
  const [txnSortCol,    setTxnSortCol]    = useState("date"); // date | holding | type | total
  const [txnSortDir,    setTxnSortDir]    = useState("desc");
  const [txnCollapsed,  setTxnCollapsed]  = useState(new Set()); // set of fund names that are collapsed
  const [txnAddOpen,    setTxnAddOpen]    = useState(false);
  const [reconcileModal, setReconcileModal] = useState(null); // { rows:[{name, qty, avgCost, currency, count, selected}], clientId }
  const [mergeModal,     setMergeModal]     = useState(null); // { groups:[{canonicalIdx, holdings:[...]}] }
  const [mergeHistoryOpen, setMergeHistoryOpen] = useState(false); // recent-merges panel
  const [mergeHistory,     setMergeHistory]     = useState([]);    // [{id, description, created_at, snapshot, undone_at}]
  const [txnEditId,     setTxnEditId]     = useState(null);
  const txnEditOrigRef = React.useRef(null); // pre-edit snapshot so qty can be re-posted
  const [txnSelected,   setTxnSelected]   = useState(new Set()); // bulk selection for delete
  const [newTxn,        setNewTxn]        = useState({ date: new Date().toISOString().slice(0,10), class:"", holding:"", type:"BUY", qty:"", price:"", currency:"USD", fees:"", clientId:"" });
  // ── Admin KYC viewer ──
  const [kycViewClient, setKycViewClient] = useState(null); // clientId whose KYC admin is viewing
  const [viewingFamily,       setViewingFamily]       = useState(null);  // familyId for detail drill-down

  // ── Watchlist state — simple watch list, no financial tracking ──
  const [watchlist, setWatchlist] = useState([]);
  const [wlAddOpen, setWlAddOpen] = useState(false);
  const [wlNew,     setWlNew]     = useState({ ticker:"", name:"", exchange:"NASDAQ", currency:"USD", targetPrice:"", targetBuy:"", alertAbove:false, alertBelow:false, notes:"", weight:"" });
  const [wlAddClients, setWlAddClients] = useState(new Set()); // Set of clientIds to add this watchlist item under (admin only). Empty = uses fallback.
  // ── Deployment Planner state ──
  // Modal that helps plan how to deploy capital across watchlist items.
  // 4 strategies: by-weight, only-at-buy, equal, custom (manual overrides).
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployAmount, setDeployAmount] = useState("10000");
  const [deployCcy, setDeployCcy] = useState("USD");
  const [deployStrategy, setDeployStrategy] = useState("weight"); // weight | atbuy | equal | custom
  const [deployCustom, setDeployCustom] = useState({}); // { wlItemId: usdAmount } per-row overrides
  const [deployFractional, setDeployFractional] = useState(true); // allow fractional shares?
  const [wlFilter,  setWlFilter]  = useState("all"); // "all"|"near"|"above"|"below"
  const [wlReassignPending, setWlReassignPending] = useState(false); // two-stage confirm for orphan reassign
  const [scenariosSubTab, setScenariosSubTab] = useState("backtesting");
  const [analyticsSubTab, setAnalyticsSubTab] = useState("volatility");

  // ── Backtesting state — separate from watchlist, full financial tracking ──
  // status: "active" | "exited"   exitPrice: price at exit (manual)
  const btRebalance = (list) => {
    const active = list.filter(a => a.status !== "exited");
    const totalAmt = active.reduce((s,a) => s + (parseFloat(a.amount)||0), 0);
    return list.map(a => {
      if (a.status === "exited") return { ...a, allocPct: 0 };
      const amt = parseFloat(a.amount)||0;
      const pct = totalAmt > 0
        ? parseFloat(((amt/totalAmt)*100).toFixed(2))
        : parseFloat((100/Math.max(1,active.length)).toFixed(2));
      return { ...a, allocPct: pct };
    });
  };
  const [btAssets, setBtAssets] = useState(btRebalance([]));
  const [btAddOpen,    setBtAddOpen]    = useState(false);
  const [btNew,        setBtNew]        = useState({ ticker:"", name:"", exchange:"NASDAQ", currency:"USD", entryPrice:"", amount:"", addedOn:new Date().toISOString().slice(0,10), notes:"", clientId:"" });
  const [btAddClients, setBtAddClients] = useState(new Set()); // Set of clientIds to add this backtest asset under (admin only). Empty = uses fallback (single asset for "all/unassigned").
  const [btEditId,     setBtEditId]     = useState(null);   // id of row being inline-edited
  const [btEditVals,   setBtEditVals]   = useState({});     // partial field overrides while editing
  const [btExitId,     setBtExitId]     = useState(null);   // id pending exit modal
  const [btExitPrice,  setBtExitPrice]  = useState("");
  const [btExitDate,   setBtExitDate]   = useState(new Date().toISOString().slice(0,10));
  const [btBenchSel,   setBtBenchSel]   = useState("sp500");
  const [userBenchmarks, setUserBenchmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_user_benchmarks_v1")||"[]"); } catch { return []; }
  });
  const [addBenchModal,  setAddBenchModal]  = useState(false);
  const [newBenchForm,   setNewBenchForm]   = useState({label:"",ticker:"",currency:"USD",type:"equity"});

  // ── BENCHMARKS_DEF: live-computed so user-added benchmarks appear immediately ──
  const BENCHMARKS_DEF = React.useMemo(() => [
    ...BENCHMARKS_STATIC,
    ...userBenchmarks,
  ], [userBenchmarks]);
  const [customBlend,  setCustomBlend]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_custom_blend_v1") || "null"); } catch { return null; }
  });

  // ── Cash Flow & Operations state ─────────────────────────────────────────
  const [cfTab,        setCfTab]        = useState("forecast");

  // ── AI Portfolio Agent ──────────────────────────────────────────
  const [agentMessagesMap, setAgentMessagesMap] = useState({}); // { clientId: [{role,content,ts}] }
  const [agentInput,     setAgentInput]     = useState("");
  const [voiceActive,    setVoiceActive]    = useState(false);  // mic is listening
  const [voiceSpeaking,  setVoiceSpeaking]  = useState(false);  // TTS is playing
  const [voiceSupported, setVoiceSupported] = useState(true);   // browser supports SpeechRecognition
  const voiceRecogRef    = React.useRef(null);  // SpeechRecognition instance
  const voiceSynthRef    = React.useRef(null);  // current SpeechSynthesisUtterance
  const voiceModeRef     = React.useRef(false); // true = voice mode on (readable from async callbacks)
  const [voiceMode,      setVoiceMode]      = useState(false);  // UI indicator for voice mode on
  const [voiceTranscript,setVoiceTranscript] = useState("");     // live transcript shown in floating bubble

  // ── Global voice helpers (hoisted so floating button works on any tab) ──
  const globalStopVoice = React.useCallback(() => {
    voiceModeRef.current = false;
    setVoiceMode(false);
    setVoiceActive(false);
    setVoiceSpeaking(false);
    setVoiceTranscript("");
    voiceRecogRef.current?.stop();
    window.speechSynthesis?.cancel();
  }, []);

  const globalStartVoice = React.useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceSupported(false); return; }
    window.speechSynthesis?.cancel();
    setVoiceSpeaking(false);
    setVoiceTranscript("");
    voiceModeRef.current = true;
    setVoiceMode(true);
    const recog = new SR();
    recog.lang = "en-IN";
    recog.continuous = false;
    recog.interimResults = true;
    recog.maxAlternatives = 1;
    let finalT = "";
    let silenceTimer = null;
    recog.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalT += t;
        else interim = t;
      }
      const combined = (finalT + interim).trim();
      setVoiceTranscript(combined);
      setAgentInput(combined);
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        const msg = combined;
        if (msg) {
          recog.stop();
          setVoiceTranscript("");
          setAgentInput("");
          // Navigate to advisor tab and send
          // Store in ref so agent tab reads it on mount (avoids race condition)
          window.__forthiusPendingVoice = msg;
          setTab("agent");
        }
      }, 1500);
    };
    recog.onend  = () => setVoiceActive(false);
    recog.onerror = () => { setVoiceActive(false); };
    voiceRecogRef.current = recog;
    recog.start();
    setVoiceActive(true);
  }, []);
  // Pre-load last saved conversation for a client when switching to them
  const lastLoadedAdvisorKey = React.useRef(null);
  const [agentLoading,   setAgentLoading]   = useState(false);
  const [advisorArchive, setAdvisorArchive] = useState(() => {
    try { return JSON.parse(localStorage.getItem("forthius_advisor_archive") || "[]"); } catch { return []; }
  });
  const [advisorArchiveOpen,     setAdvisorArchiveOpen]     = useState(false);
  const [advisorArchiveExpanded, setAdvisorArchiveExpanded] = useState(null);
  const [agentOpen,      setAgentOpen]      = useState(false); // floating panel open
  const agentEndRef = React.useRef(null); // sub-tab in cashflow section
  const [cfFormData,   setCfFormData]   = useState({});         // form data for cfModal
  const [cfScopeType,  setCfScopeType]  = useState("all");      // "all"|"family"|"client"
  const [cfScopeId,    setCfScopeId]    = useState("all");      // family/client id or "all"
  // NOTE: Per-tab local scope selectors (pills) are replaced by the global family/client
  // dropdowns in the top bar. Each tab reads selectedFamily/selectedClients directly.
  // Capital calls: PE/VC commitments with scheduled draw-downs
  const [capitalCalls, setCapitalCalls] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_capital_calls_v1") || "[]"); } catch { return []; }
  });
  // Fee entries: management fees, advisory fees paid to external managers
  const [feeEntries, setFeeEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_fee_entries_v1") || "[]"); } catch { return []; }
  });
  // Documents: attached to holdings or capital calls
  const [documents, setDocuments] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_documents_v1") || "[]"); } catch { return []; }
  });
  // Recurring inflows: rental income, SIPs, salary, etc.
  const [recurringInflows, setRecurringInflows] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_recurring_inflows_v1") || "[]"); } catch { return []; }
  });
  // Recurring outflows: lifestyle expenses, EMIs, school fees, charity, etc.
  // Same shape as recurringInflows: { id, source, type, amount, frequency, nextDate,
  //                                   currency, clientId, active, notes }
  // type values: "lifestyle" | "emi" | "education" | "insurance" | "charity" | "other"
  const [recurringOutflows, setRecurringOutflows] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_recurring_outflows_v1") || "[]"); } catch { return []; }
  });
  // Tax events: upcoming payments, advance tax, capital gains tax due
  const [taxEvents, setTaxEvents] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fw_tax_events_v1") || "[]"); } catch { return []; }
  });
  // Cash flow modal state
  const [cfModal, setCfModal] = useState(null); // { type: "capitalCall"|"fee"|"inflow"|"outflow"|"tax"|"doc", data }
  const [cfAddMenuOpen, setCfAddMenuOpen] = useState(false); // dropdown menu visibility
  const [showBlendBuilder, setShowBlendBuilder] = useState(false);
  const [blendDraft,       setBlendDraft]       = useState({}); // { benchId: pct }
  const [benchSentiment,   setBenchSentiment]   = useState(BENCHMARK_SENTIMENT); // live updates overlay
  const [btShowBench,  setBtShowBench]  = useState(true);

  // ── Risk Analysis state ──
  const [riskTab,       setRiskTab]       = useState("overview");
  // ── AI Research state ──
  const [researchClient,    setResearchClient]    = useState(null);
  const [researchData,      setResearchData]      = useState({});
  const [researchLoading,   setResearchLoading]   = useState({});
  const [researchAssetOpen, setResearchAssetOpen] = useState(null);
  const [researchError,     setResearchError]     = useState({});  // { clientId: errorMsg }
  const [researchProgress,  setResearchProgress]  = useState({});  // { clientId: {done,total} }
  const [researchIncludeUnlisted, setResearchIncludeUnlisted] = useState(false); // per-run toggle
  const [researchArchive,   setResearchArchive]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("forthius_research_archive") || "[]"); } catch { return []; }
  });
  const [archiveOpen,       setArchiveOpen]       = useState(false);
  const archiveAutoOpened   = React.useRef(false); // tracks whether we've auto-opened once this session
  // Geo allocation panel — which country is expanded inline
  const [geoExpandedIso,    setGeoExpandedIso]    = useState(null);
  const [archiveExpanded,   setArchiveExpanded]   = useState(null);
  const [riskScopeType, setRiskScopeType] = useState("all");
  const [riskScopeId,   setRiskScopeId]   = useState("all");

  // ── Shortlist state ──
  const [shortlists,          setShortlists]          = useState([]);
  const [activeShortlistId,   setActiveShortlistId]   = useState(null);
  const [shortlistItems,      setShortlistItems]      = useState([]);
  const [shortlistSearchQ,    setShortlistSearchQ]    = useState("");
  const [shortlistSearchType, setShortlistSearchType] = useState("all");
  const [shortlistSearchRes,  setShortlistSearchRes]  = useState([]);
  const [shortlistSearching,  setShortlistSearching]  = useState(false);
  const [shortlistAiData,     setShortlistAiData]     = useState({}); // itemId→{verdict,thesis,risks}
  const [shortlistAiLoading,  setShortlistAiLoading]  = useState({}); // itemId→bool
  const [compareItems,        setCompareItems]        = useState([]); // array of item IDs
  const [compareOpen,         setCompareOpen]         = useState(false);
  const [compareBenchmark,    setCompareBenchmark]    = useState("S&P 500");
  const [shortlistShareToken, setShortlistShareToken] = useState(null);
  const [shortlistSaving,     setShortlistSaving]     = useState(false);

  const [benchmarkData,     setBenchmarkData]     = useState(null);
  const [benchmarkLoading,  setBenchmarkLoading]  = useState(false);
  const [shortlistLoadedForId, setShortlistLoadedForId] = useState(null);
  const [slReportOpen,      setSlReportOpen]      = useState(false);
  const [slReportSections,  setSlReportSections]  = useState({ returns:true, risk:true, cost:false, sectors:false, holdings:false, verdict:true });

  // ── PDF Report state ──
  const [pdfModal, setPdfModal] = useState(false);
  const [pdfScope, setPdfScope] = useState("family"); // "family" | "individual"
  const [pdfFamilyId, setPdfFamilyId] = useState("");
  const [pdfClientId, setPdfClientId] = useState("");
  const [pdfPreparedBy, setPdfPreparedBy] = useState("Forthius Finalytics");
  const [pdfAsOnDate, setPdfAsOnDate]   = useState(new Date().toISOString().slice(0,10));
  const [pdfFamilySearch, setPdfFamilySearch] = useState("");
  const [pdfClientSearch, setPdfClientSearch] = useState("");
  const [pdfSections, setPdfSections]   = useState({
    summary: true, allocation: true, sector: true, holdings: true, performance: true,
    currency: true, geography: true, capSize: true, riskAnalysis: true, disclaimer: true,
    portfolioHealth: true, concentration: true, correlation: true, volatility: true,
    whatif: false, irr: true, goals: true,
  });
  const [benchMetrics, setBenchMetrics] = useState(BENCHMARK_FALLBACK);  // live data target
  const [benchHistoryPts, setBenchHistoryPts] = useState({});  // { [id]: [{ym, close}] } raw monthly series
  const [p2pFromDate,  setP2pFromDate]  = useState(dateYearsAgo(1));
  const [p2pReturns,   setP2pReturns]   = useState({});   // { [benchId]: pct }
  const [portP2p,      setPortP2p]      = useState(null);
  const [periodReturns, setPeriodReturns] = useState({ ytd:null, oneY:null, threeY:null }); // pre-computed
  const [benchLoading, setBenchLoading] = useState(false);

  // ── Data Sources (admin) config state ──
  const [dsConfig, setDsConfig] = useState({
    finnhubKey:       CONFIG.finnhubKey,
    twelveDataKey:    CONFIG.twelveDataKey,
    alphaVantageKey:  CONFIG.alphaVantageKey,
    googleFinance:    true,
    corsProxy:        CONFIG.corsProxies[0],
    providerOrder:    ["GoogleFinance","Frankfurter","Finnhub","TwelveData","MFapi","AlphaVantage"],
    fxProvider:       "GoogleFinance",
    globalProvider:   "GoogleFinance",
    indiaProvider:    "GoogleFinance",
    mfProvider:       "MFapi",
  });

  // ── Manual price edit ──
  const [editPriceId, setEditPriceId] = useState(null);
  const [editPriceVal, setEditPriceVal] = useState("");
  const [editPriceDate, setEditPriceDate] = useState("");

  // ── FX helpers — stable references via useCallback ──
  const toUSD   = React.useCallback((val, cur) => val / (fxRates[cur] || FX_RATES_DEFAULT[cur] || 1), [fxRates]);
  const convert = React.useCallback((usdVal, toCur) => usdVal * (fxRates[toCur] || FX_RATES_DEFAULT[toCur] || 1), [fxRates]);

  // ── Computed portfolio values — memoized chain ──
  // Dependency graph: holdings/fxRates → hw → hwWithOwner → filteredHW → drill/kpi
  // Each layer only recomputes when its specific inputs change.
  // Price ticks only re-run hw (and downstream). UI-only state changes skip all of this.
  // ── Effective quantity: apply transaction deltas to each holding ──────────
  // Holdings and transactions are independent data stores (holdings = current
  // positions, transactions = event ledger). When a user enters a manual SELL
  // in the transactions tab, we don't mutate the holding row — we just show
  // the *effective* quantity here: raw qty + BUYs - SELLs, SWITCH_IN/OUT,
  // STP_IN/OUT, SIP adds, SWP subtracts, etc. This keeps a single source of
  // truth for each store while making the UI reflect the combined reality.
  //
  // Cost basis continues to use raw qty because the original lot cost doesn't
  // change when you sell some units — you locked it in when you bought.
  //
  // If a holding goes to qty ≤ 0 after applying deltas, it's filtered out of
  // display lists (below) so the Holdings table doesn't show 0-qty ghosts.
  const txnQtyDeltas = React.useMemo(() => {
    const BUY_LIKE  = new Set(["BUY","SIP","STP_IN","SWITCH_IN","DIVIDEND_REINVEST","BONUS"]);
    const SELL_LIKE = new Set(["SELL","SWP","STP_OUT","SWITCH_OUT"]);
    // Map NORMALISED holding key → net qty delta from transactions.
    // Keys are lower-cased + trimmed so 'ANET', 'anet ' and 'Anet' all collide
    // into one bucket. Lookup is done via txnKey() below, which tries ticker
    // first and falls back to name — this is what makes renaming a holding
    // (e.g. 'ANET' -> 'Arista Networks Inc') safe.
    const deltas = {};
    (transactions || []).forEach(t => {
      if (!t.holding) return;
      const k = String(t.holding).toLowerCase().trim();
      const q = parseFloat(t.qty) || 0;
      if (BUY_LIKE.has(t.type))  deltas[k] = (deltas[k] || 0) + q;
      if (SELL_LIKE.has(t.type)) deltas[k] = (deltas[k] || 0) - q;
      // SPLIT has special handling: price adjusts, qty multiplies. For now, treated
      // as a position multiplier via the "qty" field representing the multiplier.
      // DIVIDEND, TRANSFER don't change qty.
    });
    return deltas;
  }, [transactions]);

  // Resolve a holding to its transaction bucket. Ticker wins because it is the
  // stable identifier — names get edited, tickers rarely do. Falls back to name
  // for manually-added holdings that have no ticker set.
  const txnDeltaFor = React.useCallback((h) => {
    if (!h) return 0;
    const tk = (h.ticker || "").toLowerCase().trim();
    if (tk && tk !== "-" && txnQtyDeltas[tk] !== undefined) return txnQtyDeltas[tk];
    const nm = (h.name || "").toLowerCase().trim();
    return txnQtyDeltas[nm] || 0;
  }, [txnQtyDeltas]);

  // ── Post manual transactions into reconciled holdings ─────────────────
  // A holding flagged reconciledFromTxns stores qty as the source of truth, so
  // the hw memo deliberately IGNORES transaction deltas for it (otherwise qty
  // double-counts). The catch: a sale or purchase entered AFTER reconcile was
  // ignored too, so the holding never moved. This posts each manual
  // add / edit / delete straight into the stored qty (and weighted avg cost on
  // buys). pairs: [[txn, +1 apply | -1 reverse], ...] applied in order on one
  // working copy, so an edit (reverse old, apply new) nets correctly.
  const postHoldingsRef = React.useRef(holdings);
  postHoldingsRef.current = holdings;
  const postOwnersRef = React.useRef(holdingOwners);
  postOwnersRef.current = holdingOwners;
  const postTxnsToHoldings = React.useCallback((pairs) => {
    const BUY  = new Set(["BUY","SIP","STP_IN","SWITCH_IN","DIVIDEND_REINVEST","BONUS"]);
    const SELL = new Set(["SELL","SWP","STP_OUT","SWITCH_OUT"]);
    const work = {};
    const notes = [];
    for (const [t, sign] of pairs) {
      if (!t || !t.holding) continue;
      const dir = BUY.has(t.type) ? 1 : SELL.has(t.type) ? -1 : 0;
      if (!dir) continue;
      const key = String(t.holding).toLowerCase().trim();
      const owners = postOwnersRef.current || {};
      const cands = (postHoldingsRef.current || []).filter(h => {
        if (!(h.reconciledFromTxns || h.reconciled_from_txns)) return false; // non-reconciled: hw memo already applies deltas
        const nm = (h.name||"").toLowerCase().trim();
        const tk = (h.ticker||"").toLowerCase().trim();
        if (nm !== key && !(tk && tk !== "-" && tk === key)) return false;
        const own = owners[String(h.id)];
        if (t.clientId && own && own !== t.clientId) return false;
        return true;
      });
      if (cands.length === 0) continue;
      if (cands.length > 1) { notes.push(`⚠ ${t.holding}: ${cands.length} matching holdings, qty not updated (set the client on the transaction)`); continue; }
      const h = cands[0];
      const cur = work[h.id] || { h, qty: parseFloat(h.qty)||0, costPrice: parseFloat(h.costPrice)||0 };
      const q = (parseFloat(t.qty)||0) * dir * sign;
      const newQty = Math.max(0, cur.qty + q);
      if (q > 0 && sign === 1 && newQty > 0 && (!t.currency || !h.currency || t.currency === h.currency)) {
        cur.costPrice = (cur.qty * cur.costPrice + q * (parseFloat(t.price)||0)) / newQty;
      }
      cur.qty = newQty;
      work[h.id] = cur;
    }
    const changed = Object.values(work);
    if (changed.length) {
      setHoldings(p => p.map(x => work[x.id] ? { ...x, qty: work[x.id].qty, costPrice: work[x.id].costPrice } : x));
      for (const w of changed) {
        updateHolding(w.h.id, { qty: w.qty, costPrice: w.costPrice })
          .catch(e => { console.error("[Supabase] postTxnsToHoldings:", e.message); showToast("❌ Couldn't update " + w.h.name + " qty: " + e.message, "#A32D2D"); });
        notes.push(`✓ ${w.h.name}: qty ${(+w.h.qty).toLocaleString()} → ${w.qty.toLocaleString()}`);
      }
    }
    if (notes.length) setTimeout(() => showToast(notes.join(" · "), notes.some(n=>n.startsWith("⚠"))?"#D97706":"#1D9E75"), 400);
  }, [showToast]);

  const hw = React.useMemo(() => holdings.map(h=>{
    const rawQty       = h.qty;
    // Value-only assets (Cash/FD/Real Estate/PMS/etc) have qty=1 as a placeholder
    // and should not have transaction-driven qty deltas applied — they don't have
    // a meaningful unit count, only invested + current values. Cost = costPrice
    // (the invested amount), value = currentPrice (the current valuation).
    const isVO         = isValueOnlyClass(h.class);
    // Reconciled holdings already have their qty stored as the netted total of
    // all transactions (the Reconcile button writes sum(BUYs) - sum(SELLs) into
    // holdings.qty). Applying transaction deltas again would double-count, so
    // we skip them. Manually-entered holdings still get the delta applied so
    // manual SELL transactions auto-reduce displayed qty without touching the
    // holding row.
    // Accept BOTH spellings: Supabase returns snake_case (`reconciled_from_txns`),
    // while a few local-state paths stamp camelCase. Checking only one silently
    // dropped the flag after every re-fetch, causing txn deltas to be applied on
    // top of an already-netted qty (the 2x/3x duplication bug).
    const isReconciled = !!(h.reconciledFromTxns || h.reconciled_from_txns);
    const delta        = (isVO || isReconciled) ? 0 : txnDeltaFor(h);
    const effectiveQty = (isVO || isReconciled) ? rawQty : Math.max(0, rawQty + delta); // floor at 0 — can't hold negative units
    const costUSD      = toUSD(rawQty * h.costPrice, h.currency);         // cost uses raw (historical)
    const valueUSD     = toUSD(effectiveQty * h.currentPrice, h.currency); // value uses effective (current)
    const gainPct      = (h.costPrice > 0 && isFinite(h.costPrice))
      ? ((h.currentPrice - h.costPrice) / h.costPrice) * 100
      : null;
    return { ...h, rawQty, effectiveQty, qty: effectiveQty, costUSD, valueUSD, gainPct };
  }), [holdings, fxRates, txnQtyDeltas, txnDeltaFor]);

  const { totalUSD, totalCostUSD, gainUSD, gainPct, byClass, byCurrency, byCountry, classData, curData } =
    React.useMemo(() => {
      const totalUSD     = hw.reduce((s,h)=>s+h.valueUSD, 0);
      const totalCostUSD = hw.reduce((s,h)=>s+h.costUSD,  0);
      const gainUSD      = totalUSD - totalCostUSD;
      const gainPct      = totalCostUSD > 0 ? (gainUSD / totalCostUSD) * 100 : null;
      const byClass      = hw.reduce((a,h)=>{ a[h.class]=(a[h.class]||0)+h.valueUSD; return a; },{});
      const byCurrency   = hw.reduce((a,h)=>{ a[h.currency]=(a[h.currency]||0)+h.valueUSD; return a; },{});
      const byCountry    = hw.reduce((a,h)=>{ const iso=COUNTRY_ISO[h.country]||h.country; a[iso]=(a[iso]||0)+h.valueUSD; return a; },{});
      const classData    = Object.entries(byClass).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value);
      const curData      = Object.entries(byCurrency).map(([label,value])=>({label,value}));
      return { totalUSD, totalCostUSD, gainUSD, gainPct, byClass, byCurrency, byCountry, classData, curData };
    }, [hw]);

  const bm = BENCHMARKS_DEF.find(b=>b.id===bench);

  // ── Family / client filtering ──
  const hwWithOwner = React.useMemo(() => hw.map(h => {
    const clientId = holdingOwners[String(h.id)] || holdingOwners[h.id] || null;
    const client   = clients.find(c => c.id === clientId) || null;
    const family   = client ? families.find(f => f.id === client.familyId) || null : null;
    return { ...h, class: normaliseClass(h.class||"Miscellaneous"), clientId, client, family };
  }), [hw, holdingOwners, clients, families]);

  const loggedInClient  = React.useMemo(() =>
    loggedInClientId ? clients.find(c=>c.id===loggedInClientId) : null,
    [loggedInClientId, clients]);

  // Normalise family ID — Supabase may return family_id (snake) or familyId (camel)
  const loggedInFamilyId = loggedInClient
    ? (loggedInClient.familyId || loggedInClient.family_id || null)
    : null;

  const visibleFamilies = React.useMemo(() =>
    (!isAdmin && loggedInClient) ? families.filter(f => f.id === loggedInFamilyId) : families,
    [isAdmin, loggedInClient, loggedInFamilyId, families]);

  const visibleClients = React.useMemo(() =>
    (!isAdmin && loggedInClient) ? clients.filter(c => (c.familyId||c.family_id) === loggedInFamilyId) : clients,
    [isAdmin, loggedInClient, loggedInFamilyId, clients]);

  const filteredHW = React.useMemo(() => hwWithOwner.filter(h => {
    // Hide holdings that are fully sold out — effectiveQty is 0 after applying
    // all buy/sell transaction deltas. The holding record stays in the DB so
    // cost/return history isn't lost; it just doesn't clutter the active view.
    // Use a small epsilon to accommodate float rounding on fractional units.
    if ((h.effectiveQty != null) && h.effectiveQty < 1e-6) return false;
    // Admin "View as Client" — show only that client's holdings
    if (isAdmin && viewAsClientId) return h.clientId === viewAsClientId;
    // Non-admin: restrict to own family — if family ID unknown, show nothing
    if (!isAdmin && loggedInClient) {
      if (!loggedInFamilyId) return false; // family not resolved yet — show nothing
      if (h.family?.id !== loggedInFamilyId) return false;
    }
    // No filter active — show all
    if (selectedFamily === "all" && selectedClients.size === 0) return true;
    // Client-level filter (from entity tree client node click)
    if (selectedClients.size > 0) return selectedClients.has(h.clientId) || selectedClients.has(String(h.clientId));
    // Family-level filter (from entity tree family node click)
    // Check both h.family?.id (from hwWithOwner enrichment) and h.clientId→family lookup
    if (selectedFamily !== "all") {
      const sfid = String(selectedFamily);
      if (String(h.family?.id) === sfid) return true;
      // fallback: look up client's family directly
      const cli = h.client || null;
      return String(cli?.familyId) === sfid;
    }
    return true;
  }), [hwWithOwner, isAdmin, loggedInClient, loggedInFamilyId, selectedFamily, selectedClients, viewAsClientId]);

  // Persist display currency preference
  React.useEffect(() => {
    try { localStorage.setItem("fw_display_currency", dc); } catch {}
  }, [dc]);

  // Filtered portfolio totals — memoized, single pass
  const {
    filtTotalUSD, filtTotalCostUSD, filtGainUSD, filtGainPct,
    filtByClass, filtByCurrency, filtByCountry,
    filtClassData, byManager, managerData, advisorData, filtCurData,
  } = React.useMemo(() => {
    const filtTotalUSD     = filteredHW.reduce((s,h)=>s+h.valueUSD, 0);
    const filtTotalCostUSD = filteredHW.reduce((s,h)=>s+h.costUSD,  0);
    const filtGainUSD      = filtTotalUSD - filtTotalCostUSD;
    const filtGainPct      = filtTotalCostUSD > 0 ? (filtGainUSD / filtTotalCostUSD) * 100 : null;
    const filtByClass    = {}; const filtByCurrency = {}; const filtByCountry = {}; const byManager = {}; const byAdvisor = {};
    filteredHW.forEach(h => {
      // For Mutual Funds: look-through to underlying asset class exposure
      if ((h.class === "Mutual Funds" || h.class === "Indian Mutual Funds" || h.class === "Global Mutual Funds" || h.isMutualFund) && typeof getMFClassSplit === "function") {
        const split = getMFClassSplit(h);
        const totalPct = Object.values(split).reduce((s,v)=>s+v, 0) || 100;
        Object.entries(split).forEach(([cls, pct]) => {
          filtByClass[cls] = (filtByClass[cls]||0) + h.valueUSD * (pct/totalPct);
        });
      } else {
        filtByClass[h.class] = (filtByClass[h.class]||0) + h.valueUSD;
      }
      filtByCurrency[h.currency] = (filtByCurrency[h.currency]||0) + h.valueUSD;
      const iso = COUNTRY_ISO[h.country]||h.country;
      filtByCountry[iso] = (filtByCountry[iso]||0) + h.valueUSD;
      const m = h.manager||"Self-Managed";
      byManager[m] = (byManager[m]||0) + h.valueUSD;
      const adv = h.advisor||"Unassigned";
      byAdvisor[adv] = (byAdvisor[adv]||0) + h.valueUSD;
    });
    const filtClassData = Object.entries(filtByClass).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value);
    const managerData   = collapseSmallSlices(Object.entries(byManager).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value));
    const advisorData   = collapseSmallSlices(Object.entries(byAdvisor).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value));
    const filtCurData   = Object.entries(filtByCurrency).map(([label,value])=>({label,value}));
    return { filtTotalUSD, filtTotalCostUSD, filtGainUSD, filtGainPct,
             filtByClass, filtByCurrency, filtByCountry,
             filtClassData, byManager, managerData, advisorData, filtCurData };
  }, [filteredHW]);

  // ── Drill-filtered subset — applied on top of family/client filter ──
  const drillFilteredHW = React.useMemo(() => {
    const df = holdingsDrillFilter;
    if (!Object.keys(df).length) return filteredHW;
    return filteredHW.filter(h => {
      if (df.cap     && resolveCapSize(h) !== df.cap) return false;  // use resolveCapSize to match chart bucketing
      if (df.class   && h.class !== df.class)           return false;
      if (df.currency && h.currency !== df.currency)    return false;
      if (df.manager && (h.manager||"Self-Managed") !== df.manager) return false;
      if (df.advisor && (h.advisor||"Unassigned") !== df.advisor) return false;
      if (df.sector  && resolveHoldingSector(h) !== df.sector) return false;
      if (df.country && (h.country||"Other") !== df.country) return false;
      if (df.minGainPct != null && (h.gainPct||0) < df.minGainPct) return false;
      if (df.maxGainPct != null && (h.gainPct||0) > df.maxGainPct) return false;
      if (df.owner) {
        const ownerClient = visibleClients.find(c=>c.id===h.clientId);
        const ownerName = ownerClient?.name || "Unassigned";
        if (ownerName !== df.owner) return false;
      }
      return true;
    });
  }, [filteredHW, holdingsDrillFilter]);

  // Chart data computed from drillFilteredHW so charts react to drill-downs
  // ── Drill aggregations + KPI values — memoized, single pass ──
  const isDrillActive = Object.keys(holdingsDrillFilter).length > 0;
  const kpiHW         = isDrillActive ? drillFilteredHW : filteredHW;

  const {
    drillByClass, drillByCurrency, drillByManager, drillBySector,
    drillClassData, drillCurData, drillManagerData, drillSectorData,
  } = React.useMemo(() => {
    const drillByClass = {}; const drillByCurrency = {};
    const drillByManager = {}; const drillBySector = {};
    drillFilteredHW.forEach(h => {
      drillByClass[h.class] = (drillByClass[h.class]||0) + h.valueUSD;
      drillByCurrency[h.currency] = (drillByCurrency[h.currency]||0) + h.valueUSD;
      const m = h.manager||"Self-Managed"; drillByManager[m] = (drillByManager[m]||0) + h.valueUSD;
      const s = resolveHoldingSector(h); drillBySector[s] = (drillBySector[s]||0) + h.valueUSD;
    });
    return {
      drillByClass, drillByCurrency, drillByManager, drillBySector,
      drillClassData:   Object.entries(drillByClass).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value),
      drillCurData:     Object.entries(drillByCurrency).map(([l,v])=>({label:l,value:v})),
      drillManagerData: collapseSmallSlices(Object.entries(drillByManager).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value)),
      drillSectorData:  Object.entries(drillBySector).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value),
    };
  }, [drillFilteredHW]);

  const { kpiTotalUSD, kpiTotalCostUSD, kpiGainUSD, kpiGainPct, kpiByCurrency } =
    React.useMemo(() => {
      const kpiTotalUSD     = kpiHW.reduce((s,h)=>s+h.valueUSD, 0);
      const kpiTotalCostUSD = kpiHW.reduce((s,h)=>s+h.costUSD,  0);
      const kpiGainUSD      = kpiTotalUSD - kpiTotalCostUSD;
      const kpiGainPct      = kpiTotalCostUSD > 0 ? (kpiGainUSD / kpiTotalCostUSD * 100) : null;
      const kpiByCurrency   = kpiHW.reduce((a,h)=>{ a[h.currency]=(a[h.currency]||0)+h.valueUSD; return a; },{});
      return { kpiTotalUSD, kpiTotalCostUSD, kpiGainUSD, kpiGainPct, kpiByCurrency };
    }, [kpiHW]);

  // Per-family aggregates (for Family tab overview) — memoised to avoid recalculating on every render
  const familyStats = React.useMemo(() => visibleFamilies.map(fam => {
    const famClients   = visibleClients.filter(c => (c.familyId || c.family_id) === fam.id);
    const famClientIds = famClients.map(c => c.id);
    const famHoldings  = hwWithOwner.filter(h => famClientIds.includes(h.clientId));
    const famTotalUSD  = famHoldings.reduce((s,h)=>s+h.valueUSD, 0);
    const famCostUSD   = famHoldings.reduce((s,h)=>s+h.costUSD,  0);
    const famGainPct   = famCostUSD > 0 ? ((famTotalUSD - famCostUSD) / famCostUSD) * 100 : 0;
    return { ...fam, clients: famClients, holdings: famHoldings, totalUSD: famTotalUSD, costUSD: famCostUSD, gainPct: famGainPct };
  }), [visibleFamilies, visibleClients, hwWithOwner]);

  // ── Weighted Total Target Yield ──
  const totalTargetYield = model.reduce((sum, m) => sum + (m.targetPct / 100) * m.targetYield, 0);

  // ── Live price status per holding ──
  const [liveStatus, setLiveStatus] = useState({}); // { holdingId: "google"|"finnhub"|"twelvedata"|"mfapi"|"simulated"|"manual"|"fetching"|"error" }

  // ── Pre-fetch credentials before login so login screen works on any device ──
  useEffect(() => {
    let cancelled = false;
    const prefetch = async () => {
      try {
        if (typeof fetchClientLogins !== "function") return;
        const logins = await fetchClientLogins().catch(() => null);
        if (cancelled) return;
        if (logins && typeof logins === "object" && Object.keys(logins).length > 0) {
          setClientLogins(logins);
        }
      } catch {}
      try {
        if (typeof fetchProfiles !== "function") return;
        const profiles = await fetchProfiles().catch(() => null);
        if (cancelled) return;
        if (profiles && typeof profiles === "object" && Object.keys(profiles).length > 0) {
          setProfileData(profiles);
        }
      } catch {}
    };
    prefetch();
    return () => { cancelled = true; };
  }, []); // runs once on mount, before any login

  // ── Load data from Supabase — only after login ──
  useEffect(() => {
    if (signedOut) return; // don't fetch until logged in
    // Direct fetch for watchlist to ensure all columns (especially the newly
    // added client_id) come through. The wrapper's SELECT may not include
    // newer columns. This bypass guarantees a complete read.
    const fetchWatchlistDirect = async () => {
      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/watchlist?select=*`, {
          headers: { 'apikey': ANON_KEY, 'Authorization': `Bearer ${ANON_KEY}` },
        });
        if (!res.ok) {
          console.warn('[watchlist fetch] direct REST failed, falling back to wrapper');
          return await fetchWatchlist();
        }
        const rows = await res.json();
        console.log('[watchlist fetch] direct returned', rows.length, 'rows. Sample client_ids:', [...new Set(rows.map(r => r.client_id))].slice(0,5));
        return rows.map(r => ({
          id: r.id,
          clientId: r.client_id ?? null,
          ticker: r.ticker,
          name: r.name,
          exchange: r.exchange,
          currency: r.currency,
          targetPrice: r.target_price ?? 0,
          targetBuy: r.target_buy ?? 0,
          alertAbove: !!r.alert_above,
          alertBelow: !!r.alert_below,
          notes: r.notes ?? "",
          currentPrice: r.current_price ?? 0,
          change1d: r.change_1d ?? 0,
          weight: r.weight ?? 0,
          createdAt: r.created_at,
        }));
      } catch (e) {
        console.warn('[watchlist fetch] direct error, fallback:', e.message);
        return await fetchWatchlist();
      }
    };

    // Direct fetch for bt_assets — same reasoning as watchlist (wrapper may
    // not include newer columns like client_id).
    const fetchBtAssetsDirect = async () => {
      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/bt_assets?select=*`, {
          headers: { 'apikey': ANON_KEY, 'Authorization': `Bearer ${ANON_KEY}` },
        });
        if (!res.ok) {
          console.warn('[bt_assets fetch] direct REST failed, falling back to wrapper');
          return await fetchBtAssets();
        }
        const rows = await res.json();
        console.log('[bt_assets fetch] direct returned', rows.length, 'rows. Sample client_ids:', [...new Set(rows.map(r => r.client_id))].slice(0,5));
        return rows.map(r => ({
          id: r.id,
          clientId: r.client_id ?? null,
          ticker: r.ticker,
          name: r.name,
          exchange: r.exchange,
          currency: r.currency,
          entryPrice: r.entry_price ?? 0,
          exitPrice: r.exit_price ?? null,
          currentPrice: r.current_price ?? 0,
          amount: r.amount ?? 0,
          addedOn: r.added_on,
          exitedOn: r.exited_on,
          status: r.status ?? 'active',
          notes: r.notes ?? "",
          allocPct: r.alloc_pct ?? 0,
          createdAt: r.created_at,
        }));
      } catch (e) {
        console.warn('[bt_assets fetch] direct error, fallback:', e.message);
        return await fetchBtAssets();
      }
    };

    async function loadFromSupabase() {
      try {
        setDbLoading(true);
        const [fams, clis, holds, owners, wl, bts, logins, txns, profiles] = await Promise.all([
          fetchFamilies(),
          fetchClients(),
          fetchHoldings(),
          fetchHoldingOwners(),
          fetchWatchlistDirect(),
          fetchBtAssetsDirect(),
          fetchClientLogins(),
          (typeof fetchTransactions === 'function' ? fetchTransactions() : Promise.resolve([])),
          (typeof fetchProfiles === 'function' ? fetchProfiles() : Promise.resolve({})),
        ]);
        // Always apply fresh data from Supabase — no length guards
        // (guards caused stale state on other browsers when data was present)
        setFamilies(fams);
        {
          // Normalise Supabase snake_case → camelCase so all downstream code works
          const normClis = clis.map(c => ({
            ...c,
            familyId: c.familyId || c.family_id || null,
            riskProfile: c.riskProfile || c.risk_profile || null,
          }));
          setClients(normClis);
        }
        {
          // Auto-apply cap size from TICKER_CAP for holdings missing it — always apply fresh data
          const enriched = holds.map(h => {
            const missingCap = !h.capSize || h.capSize === "N/A" || h.capSize === "-" || h.capSize === "";
            const autoCs = missingCap ? resolveCapSize(h) : null;
            // Treat old mismatched sector labels as stale (e.g. "Financial Services" → "Financials")
            const STALE_SECTORS = new Set(["", "-", "Other", "Miscellaneous", "Financial Services", "Equities (Unclassified)"]);
            const missingSector = !h.sector || STALE_SECTORS.has(h.sector);
            const autoSec = missingSector ? resolveHoldingSector(h) : null;
            return { ...h, ...(autoCs ? {capSize: autoCs} : {}), ...(autoSec ? {sector: autoSec} : {}) };
          });
          setHoldings(enriched);
          holdingsRef.current = enriched; // keep ref in sync for refresh() callbacks
          // If Supabase holdings have saved prices, record when they were last updated
          const latestPriceDate = enriched
            .filter(h => h.manualPriceDate || h.updated_at)
            .map(h => new Date(h.manualPriceDate || h.updated_at))
            .reduce((latest, d) => (!latest || d > latest ? d : latest), null);
          if (latestPriceDate) setLastSavedPriceTime(latestPriceDate);
          // Restore advisor notes from Supabase holdings (notes column)
          const dbNotes = {};
          enriched.forEach(h => {
            if (h.notes) dbNotes[String(h.id)] = {
              text: h.notes, updatedAt: h.updated_at || new Date().toISOString(), adminOnly: false
            };
          });
          if (Object.keys(dbNotes).length > 0) {
            setHoldingNotes(prev => ({ ...dbNotes, ...prev }));
          }
          // Persist auto-assigned cap sizes back to Supabase silently
          enriched.forEach(h => {
            const orig = holds.find(x => x.id === h.id);
            if (orig && orig.capSize !== h.capSize) {
              updateHolding(h.id, { capSize: h.capSize, sector: h.sector }).catch(() => {});
            }
          });
        }
        setHoldingOwners(owners);
        // Shape watchlist rows: column is client_id (snake_case) in DB, but JS
        // code reads w.clientId. Translate here once at the boundary so reads
        // throughout the app continue to work without per-site changes.
        const shapedWL = wl.map(w => ({ ...w, clientId: w.clientId ?? w.client_id ?? null }));
        setWatchlist(shapedWL);
        // Pre-populate AI thesis map from stored notes
        const thesisInit = {};
        shapedWL.forEach(w => { if (w.notes) thesisInit[w.id] = w.notes; });
        setWlThesisMap(thesisInit);
        setBtAssets(btRebalance(bts));
        setClientLogins(logins);
        if (txns) setTransactions(txns);
        if (profiles) {
          setProfileData(profiles);
          // ── AI Advisor archive: cloud is source of truth ──
          // Behaviour:
          //   - If cloud has entries → adopt cloud, overwrite local cache.
          //   - If cloud is empty but local has entries (legacy / first-time upgrade)
          //     → upload local to cloud as a one-time migration.
          //   - If both empty → nothing to do.
          {
            const localArchive = (() => { try { return JSON.parse(localStorage.getItem("forthius_advisor_archive") || "[]"); } catch { return []; } })();
            const cloudArchive = Array.isArray(profiles["__advisor_archive__"]) ? profiles["__advisor_archive__"] : [];
            if (cloudArchive.length > 0) {
              // Cloud wins.
              setAdvisorArchive(cloudArchive);
              try { localStorage.setItem("forthius_advisor_archive", JSON.stringify(cloudArchive)); } catch {}
            } else if (localArchive.length > 0) {
              // Legacy local data — push to cloud as one-time migration so the user doesn't lose it.
              upsertProfile("__advisor_archive__", localArchive).catch(e => {
                console.warn("[Advisor archive] legacy migration to cloud failed:", e.message);
              });
            }
          }
          // ── AI Research archive: same cloud-source-of-truth pattern ──
          {
            const localR = (() => { try { return JSON.parse(localStorage.getItem("forthius_research_archive") || "[]"); } catch { return []; } })();
            const cloudR = Array.isArray(profiles["__research_archive__"]) ? profiles["__research_archive__"] : [];
            if (cloudR.length > 0) {
              setResearchArchive(cloudR);
              try { localStorage.setItem("forthius_research_archive", JSON.stringify(cloudR)); } catch {}
            } else if (localR.length > 0) {
              upsertProfile("__research_archive__", localR).catch(e => {
                console.warn("[Research archive] legacy migration to cloud failed:", e.message);
              });
            }
          }
        }
        // Load clientPlans from Supabase (stored as special profile row), merge with localStorage
        if (profiles && profiles['__clientPlans__'] && typeof profiles['__clientPlans__'] === 'object') {
          const remote = profiles['__clientPlans__'];
          setClientPlansRaw(prev => {
            const merged = { ...remote, ...prev }; // localStorage wins for current device
            try { localStorage.setItem('forthius_clientPlans', JSON.stringify(merged)); } catch {}
            return merged;
          });
        }
        setDbLoading(false);

        // ── Fetch price snapshots for sparklines (non-blocking, post-load) ──
        // Runs after dbLoading=false so the UI isn't held up. Uses the freshly-loaded
        // holdings list (state may not be committed yet, so use the local var).
        (async () => {
          try {
            const ids = holds.map(h => h.id).filter(Boolean);
            if (ids.length) {
              const snaps = await fetchPriceSnapshots(ids);
              setPriceSnapshots(snaps || {});
            }
          } catch (e) {
            console.warn("[price_snapshots] initial fetch failed:", e.message);
          }
        })();

        // ── Price refresh post-login: set flag — useEffect will pick it up ──
        // (Don't call refresh() here — holdings state not committed yet)
        if (needsPriceRefreshRef.current) {
          // Flag stays true; the useEffect[holdings] below will call refresh(true)
          // once React commits the new holdings to state
        }

        // Fetch live FX rates in background
        fetch("/api/fx").then(r=>r.json()).then(j=>{
          if (j.rates) {
            setFxRates(prev => ({...prev, ...j.rates}));
            setFxSource(j.source || "live");
          }
        }).catch(e=>console.error("[FX] fetch error:", e.message));
      } catch (err) {
        console.error("[Supabase] Load error:", err.message);
        setDbError(err.message);
        setDbLoading(false);
      }
    }
    loadFromSupabase();
  }, [signedOut]); // re-run when user logs in (signedOut: true→false)

  // ── Watchlist ticker search ──
  useEffect(() => {
    if (wlTickerQuery.length < 3) { setWlTickerResults([]); setWlTickerOpen(false); return; }
    setWlTickerLoading(true); setWlTickerOpen(true);
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(wlTickerQuery)}&exchange=${encodeURIComponent(wlNew.exchange||"")}`);
        const j = await r.json();
        const res = j.results || [];
        setWlTickerResults(res);
        if (res.length > 0) setWlTickerOpen(true);
      } catch { setWlTickerResults([]); }
      setWlTickerLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [wlTickerQuery, wlNew.exchange]);

  // ── Backtesting ticker search ──
  useEffect(() => {
    if (btTickerQuery.length < 3) { setBtTickerResults([]); setBtTickerOpen(false); return; }
    setBtTickerLoading(true); setBtTickerOpen(true);
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(btTickerQuery)}&exchange=${encodeURIComponent(btNew.exchange||"")}`);
        const j = await r.json();
        const res = j.results || [];
        setBtTickerResults(res);
        if (res.length > 0) setBtTickerOpen(true);
      } catch { setBtTickerResults([]); }
      setBtTickerLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [btTickerQuery, btNew.exchange]);

  // ── Ticker symbol search (debounced, fires after 400ms of no typing) ──
  useEffect(() => {
    if (nhTickerQuery.length < 2) {
      setNhTickerResults([]);
      setNhTickerOpen(false);
      return;
    }
    setNhTickerLoading(true);
    setNhTickerOpen(true);
    const timer = setTimeout(async () => {
      try {
        const isIndianMF = nh.class === "Indian Mutual Funds" || nh.class === "Mutual Funds" || nh.isMutualFund;
        const isGlobalMF = nh.class === "Global Mutual Funds";
        let results = [];

        if (isIndianMF) {
          // ── Indian Mutual Funds: local results instantly, MFapi.in for full list ──
          const localResults = searchPopularMF(nhTickerQuery);
          // Show local results immediately while fetching
          if (localResults.length > 0) {
            setNhTickerResults(localResults);
            setNhTickerOpen(true);
          }
          try {
            const r = await fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(nhTickerQuery)}`);
            const data = await r.json();
            const mfapiResults = (data?.length > 0 ? data : [])
              .slice(0, 25)
              .map(s => ({
                symbol:   String(s.schemeCode),
                name:     s.schemeName,
                exchange: "AMFI",
                type:     "Indian MF",
                subLabel: `Code: ${s.schemeCode}`,
              }));
            // MFapi results replace local entirely
            results = mfapiResults.length > 0 ? mfapiResults : localResults;
          } catch {
            results = localResults.length > 0 ? localResults : [];
          }
          // Set results and stop loading — RETURN early to skip the generic setNhTickerResults below
          setNhTickerResults(results);
          // Only keep dropdown open if we actually have results to show.
          // If 0 results (e.g. user typed a scheme code instead of a name),
          // close the dropdown so it doesn't sit open covering other fields.
          setNhTickerOpen(results.length > 0);
          setNhTickerLoading(false);
          return;
        } else if (isGlobalMF) {
          // ── Global Mutual Funds / UCITS: curated list + mfapi.in for Indian FOFs ──
          const q = nhTickerQuery.toLowerCase();
          // Score and rank local results
          const words = q.split(/\s+/).filter(w => w.length > 1);
          const localGlobal = GLOBAL_FUNDS_LIST
            .map(f => {
              const nm = f.name.toLowerCase();
              const mg = (f.manager||"").toLowerCase();
              const kw = (f.keywords||"").toLowerCase();
              let score = 0;
              words.forEach(w => {
                if(nm.includes(w)) score+=2;
                if(mg.includes(w)) score+=1;
                if(kw.includes(w)) score+=2; // keywords match same weight as name
              });
              if (nm.includes(q)) score += 3;
              if (kw.includes(q)) score += 3;
              if ((f.isin||"").toLowerCase().includes(q)) score += 4;
              // Boost exact currency match when query contains a currency code
              const queryCcy = q.match(/\b(usd|sgd|eur|gbp|hkd|aud|rmb|cny|inr)\b/)?.[1];
              if (queryCcy && f.currency?.toLowerCase() === queryCcy) score += 1;
              return { ...f, score };
            })
            .filter(f => f.score > 0)
            .sort((a,b) => b.score - a.score)
            .slice(0, 15);

          if (localGlobal.length > 0) {
            setNhTickerResults(localGlobal.map(f => ({
              symbol: f.ticker || f.isin || "-",
              name: f.name,
              exchange: f.exchange || "UCITS",
              currency: f.currency || "USD",
              type: "Global MF",
              isin: f.isin || "",
              manager: f.manager || "",
              sector: f.sector || "",
              yahooTicker: f.yahooTicker || "",
              isGlobalMF: true,
            })));
            setNhTickerLoading(true); // show local while fetching
          }
          // Also search mfapi.in for Indian-registered international FOFs
          try {
            const mfRes = await fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(nhTickerQuery)}`);
            if (mfRes.ok) {
              const mfData = await mfRes.json();
              const intlFunds = (mfData || [])
                .filter(s => /international|global|world|overseas|us |nasdaq|s&p|foreign/i.test(s.schemeName))
                .slice(0, 8)
                .map(s => ({
                  symbol: String(s.schemeCode),
                  name: s.schemeName,
                  exchange: "AMFI",
                  currency: "INR",
                  type: "Indian FOF",
                  amfiCode: s.schemeCode,
                  schemeCode: String(s.schemeCode),
                  isMutualFund: true,
                  isGlobalMF: true,
                }));
              if (intlFunds.length > 0) {
                setNhTickerResults(prev => {
                  const names = new Set(prev.map(x => x.name.toLowerCase()));
                  return [...prev, ...intlFunds.filter(f => !names.has(f.name.toLowerCase()))].slice(0,20);
                });
              }
            }
          } catch {}
          setNhTickerLoading(false);
          results = [];
          // Also try general stock/ETF search as fallback
          try {
            const r = await fetch(`/api/search?q=${encodeURIComponent(nhTickerQuery)}&type=fund`);
            const j = await r.json();
            const apiRes = (j.results || []).map(x => ({ ...x, type: "Global MF" }));
            results = [...localGlobal.map(f => ({
              symbol: f.ticker || f.isin || "-",
              name: f.name,
              exchange: f.exchange || "UCITS",
              currency: f.currency || "USD",
              type: "Global MF",
              isin: f.isin || "",
              manager: f.manager || "",
              sector: f.sector || "",
              yahooTicker: f.yahooTicker || "",
              isGlobalMF: true,
            })), ...apiRes.filter(r => !localGlobal.some(l => l.isin === r.isin))].slice(0, 15);
          } catch {
            results = localGlobal.map(f => ({
              symbol: f.ticker || f.isin || "-",
              name: f.name, exchange: f.exchange || "UCITS",
              currency: f.currency || "USD", type: "Global MF",
              isin: f.isin || "", manager: f.manager || "", sector: f.sector || "",
              yahooTicker: f.yahooTicker || "", isGlobalMF: true,
            }));
          }
        } else {
          // ── Stocks / ETFs ──
          // Normalise Bloomberg/Reuters terminal suffixes to plain ticker:
          //   "MC FP" → "MC"  (FP = France/Paris)
          //   "VOW GY" → "VOW" (GY = Germany/XETRA)
          //   "SHEL LN" → "SHEL" (LN = London)
          //   "7203 JP" → "7203" (JP = Japan)
          const BLOOMBERG_SUFFIXES = /\s+(FP|GY|LN|US|JP|HK|AU|CN|IN|SG|SW|NA|SS|SM|IM|DC|NO|FH|PW|AV|BB|SM|MK|TW|ID|TH|MY|PM|VN|KS|SP|ADE|ASX|TO|CN)$/i;
          const cleanQ = nhTickerQuery.replace(BLOOMBERG_SUFFIXES, "").trim();
          const didStrip = cleanQ !== nhTickerQuery.trim();

          // Primary: search with exchange filter
          let primaryResults = [];
          try {
            const r = await fetch(`/api/search?q=${encodeURIComponent(cleanQ)}&exchange=${encodeURIComponent(nh.exchange||"")}`);
            const j = await r.json();
            primaryResults = j.results || [];
          } catch {}

          // Fallback: if stripped query or no results, also search without exchange
          // (catches cross-listed stocks like LVMH which is French but user may select Germany)
          let fallbackResults = [];
          if (didStrip || primaryResults.length === 0) {
            try {
              const r2 = await fetch(`/api/search?q=${encodeURIComponent(cleanQ)}`);
              const j2 = await r2.json();
              fallbackResults = (j2.results || []).filter(x =>
                !primaryResults.some(p => p.symbol === x.symbol)
              );
            } catch {}
          }

          results = [...primaryResults, ...fallbackResults].slice(0, 15);
          // Re-rank so exact ticker matches float to top.
          // Without this, a fuzzy-search proxy can rank "Huntsman/Craftsman/Sportsman"
          // ABOVE "TSM" when user types "tsm" — because fuzzy matchers reward
          // substring-in-name. We override that for short alphanumeric queries
          // that look like ticker symbols.
          const qUpper = cleanQ.toUpperCase();
          const looksLikeTicker = /^[A-Z0-9.\-]{1,8}$/i.test(cleanQ);
          if (looksLikeTicker && results.length > 1) {
            const rank = (r) => {
              const sym = (r.symbol || "").toUpperCase();
              // Exact match (incl. with common exchange suffix like .NS / .L) ranks highest
              if (sym === qUpper) return 0;
              if (sym.split(".")[0] === qUpper) return 1;
              if (sym.startsWith(qUpper)) return 2;
              return 3;
            };
            results = [...results].sort((a, b) => {
              const ra = rank(a), rb = rank(b);
              if (ra !== rb) return ra - rb;
              return 0;  // preserve original order for ties
            });
          }
        }

        setNhTickerResults(results);
      } catch (err) {
        setNhTickerResults([]);
      }
      setNhTickerLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [nhTickerQuery]); // nh.class and nh.country are read inside but not deps — class change resets query instead

  // ── Live price refresh — cascades: Google Finance → Finnhub → Twelve Data → simulated tick ──
  const refreshFx = useCallback(async () => {
    try {
      const r = await fetch("/api/fx");
      const j = await r.json();
      if (j.rates) {
        setFxRates(prev => ({...prev, ...j.rates}));
        setFxSource(j.source || "live");
      }
    } catch(e) { console.error("[FX] refresh error:", e.message); }
  }, []);

  const refresh = useCallback(async (forceRefresh = false) => {
    setRefreshing(true);
    if (forceRefresh) clearCache();

    // ── Helper: fetch a single ticker price via the /api/price proxy ──
    // ── fetchTickerPrice: with retry + graceful network error handling ──
    const fetchTickerPrice = async (ticker, exchange, country, forceRefresh, _attempt = 0) => {
      if (!ticker || ticker === "-") return null;
      const cacheKey = `proxy_${ticker}_${exchange||""}`;
      const cached = !forceRefresh ? getCached(cacheKey) : null;
      if (cached) return { price: cached.price, source: cached.source };

      // Check connectivity before attempting
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return null; // offline — skip silently
      }

      try {
        const params = new URLSearchParams({ symbol: ticker });
        if (exchange) params.append("exchange", exchange);
        if (country)  params.append("country", country);
        const isIndian = exchange === "NSE" || exchange === "BSE" || country === "India";
        if (isIndian) params.append("type", "indian");

        // Stagger concurrent requests to avoid hammering the proxy
        if (_attempt === 0) await new Promise(r => setTimeout(r, Math.random() * 300));

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        let r;
        try {
          r = await fetch(`/api/price?${params}`, { signal: controller.signal });
        } finally {
          clearTimeout(timeout);
        }

        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (j?.price && j.price > 0) {
          setCache(cacheKey, { price: j.price, source: j.source || "live" });
          return { price: j.price, source: j.source || "live" };
        }
      } catch(e) {
        const isNetworkError = e.name === "AbortError" || e.name === "TypeError" ||
          String(e.message).includes("Load failed") || String(e.message).includes("NetworkError") ||
          String(e.message).includes("network") || String(e.message).includes("Failed to fetch");
        if (isNetworkError && _attempt < 2) {
          // Exponential backoff: 1s, 2s
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, _attempt)));
          return fetchTickerPrice(ticker, exchange, country, forceRefresh, _attempt + 1);
        }
        // Only log if it's not a routine connectivity blip
        if (!isNetworkError) {
          console.warn("[PriceProxy]", ticker, e.message);
        }
      }
      return null;
    };

    // ── 1. Holdings prices (existing logic) ──
    // Use holdingsRef.current to avoid stale closure (esp. post-login)
    const currentHoldings = holdingsRef.current.length > 0 ? holdingsRef.current : holdings;
    const results = await Promise.allSettled(
      currentHoldings.map(async h => {
        // Value-only classes (Cash/FD/Real Estate/PMS/PE/Gold/Art) keep manual valuation
        if (isValueOnlyClass(h.class)) return { id: h.id, price: null, source: "manual" };
        // Global MFs / UCITS: fetch NAV via ISIN from Yahoo Finance
        if (h.class === "Global Mutual Funds" || h.isGlobalMF) {
          // Use isin field first, fall back to ticker if it looks like an ISIN (LU/IE prefix)
          const isin = (h.isin && h.isin !== "-" && h.isin.length >= 10) ? h.isin
                     : (h.ticker && /^(LU|IE|GB|FR|DE|CH|NL|SE|HK|SG)[A-Z0-9]{10}$/i.test(h.ticker) ? h.ticker : null);
          if (isin) {
            setLiveStatus(p => ({ ...p, [h.id]: "fetching" }));
            try {
              const nav = await PriceDataProvider.getUCITSNav(isin, forceRefresh, h.yahooTicker || null);
              if (nav && nav > 0) return { id: h.id, price: nav, source: "yahoo_ucits" };
            } catch {}
          }
          return { id: h.id, price: null, source: "manual" }; // no ISIN or fetch failed
        }
        // Other truly unlisted non-MF assets keep their manual price
        // (UCITS funds are handled above via ISIN detection before reaching here)
        if (!h.listed && !h.isMutualFund) return { id: h.id, price: null, source: "manual" };

        setLiveStatus(p => ({ ...p, [h.id]: "fetching" }));
        let price = null;
        let source = "simulated";

        try {
          // ── 1a. UCITS / Global MF: detect by ISIN pattern (LU/IE prefix) ──
          // Catches holdings saved before the class fix (stored as "Mutual Funds" with ISIN as ticker)
          const tickerIsUCITS = h.ticker && /^(LU|IE|GB|FR|DE|CH|NL|SE|DK|NO|FI|AT|BE|ES|IT|PT|HK|SG|KY|VG|LI)[A-Z0-9]{10}$/i.test(h.ticker);
          const isinIsUCITS = h.isin && h.isin !== "-" && /^(LU|IE)[A-Z0-9]{10}$/i.test(h.isin);
          if (tickerIsUCITS || isinIsUCITS || h.class === "Global Mutual Funds" || h.isGlobalMF) {
            const isin = (isinIsUCITS ? h.isin : null) || (tickerIsUCITS ? h.ticker : null);
            if (isin) {
              try {
                const nav = await PriceDataProvider.getUCITSNav(isin, forceRefresh, h.yahooTicker || null);
                if (nav && nav > 0) { price = nav; source = "yahoo_ucits"; }
              } catch {}
            }
            // Don't fall through to MFapi for UCITS funds — return now
            return { id: h.id, price: price || null, source: price ? "yahoo_ucits" : "manual" };
          }

          // ── 1b. Indian Mutual Fund NAV ──
          // Only refresh MF NAVs when user explicitly clicks Refresh Prices (forceRefresh=true)
          // Auto-refresh would overwrite manually corrected NAVs with wrong scheme code prices
          if (h.isMutualFund) {
            const code = h.schemeCode || (/^\d{5,6}$/.test(String(h.ticker)) ? h.ticker : null);
            if (code && forceRefresh) {
              // Manual refresh: fetch fresh NAV
              price = await PriceDataProvider.getMFNav(code, true);
              if (price) source = "mfapi";
            } else if (code && !forceRefresh) {
              // Auto refresh: use cached NAV only — don't fetch fresh to avoid overwrites
              const cached = getCached(`mf_${code}`);
              if (cached) { price = cached; source = "mfapi_cached"; }
              else {
                // No cache — fetch once and cache so it stays stable
                price = await PriceDataProvider.getMFNav(code, false);
                if (price) source = "mfapi";
              }
              // If still no price, keep existing currentPrice — don't return null
              if (!price && h.currentPrice > 0) {
                return { id: h.id, price: h.currentPrice, source: "manual" };
              }
            }
          }

          // ── 2 & 3. Price fetching — Indian stocks try Twelve Data first (direct API, no CORS proxy needed) ──
          const isIndian = h.exchange === "NSE" || h.exchange === "BSE" || h.country === "India";

          // ── All listed stocks: call our own Vercel /api/price proxy (server-side, no CORS issues) ──
          // Skip for mutual funds (handled above by getMFNav/mfapi.in) and AMFI-exchange holdings.
          // For purely-numeric tickers (e.g. Japanese TSE: 6324), auto-append the country's
          // exchange suffix so Yahoo Finance can resolve them (6324 → 6324.T).
          const isAMFI = h.exchange === "AMFI" || h.isMutualFund;
          const rawTicker = (h.ticker || "").trim();
          // Build the effective ticker: if numeric-only and country has a known suffix, append it
          const effectiveTicker = (() => {
            if (!rawTicker || rawTicker === "-") return rawTicker;
            if (/^\d+$/.test(rawTicker)) {
              // Purely numeric — resolve via country suffix.
              // India special case: numeric tickers are BSE scrip codes (e.g. 500325) → .BO on Yahoo.
              if (h.country === "India") return `${rawTicker}.BO`;
              const suffix = COUNTRY_EXCHANGE?.[h.country]?.tickerSuffix || "";
              if (suffix) return `${rawTicker}${suffix}`; // e.g. 6324 → 6324.T
              return rawTicker; // no known suffix — still try (might be MF code, will fail harmlessly)
            }
            return rawTicker;
          })();
          if (!price && !isAMFI && effectiveTicker && effectiveTicker !== "-" && !/^\d+$/.test(effectiveTicker)) {
            try {
              const cacheKey = `proxy_${effectiveTicker}_${h.exchange||""}`;
              const cached = !forceRefresh ? getCached(cacheKey) : null;
              if (cached) { price = cached.price; source = cached.source; }
              else {
                const params = new URLSearchParams({ symbol: effectiveTicker });
                if (h.exchange) params.append("exchange", h.exchange);
                if (h.country) params.append("country", h.country);
                if (isIndian) params.append("type", "indian");
                const r = await fetch(`/api/price?${params}`, { signal: AbortSignal.timeout(10000) });
                const j = await r.json();
                if (j?.price && j.price > 0) {
                  price = j.price;
                  source = j.source || "live";
                  setCache(cacheKey, { price, source });
                }
              }
            } catch(e) {
                if (!String(e.message).includes("Load failed") && !String(e.message).includes("network") && e.name !== "AbortError") {
                  console.warn("[PriceProxy]", effectiveTicker, e.message);
                }
              }
          }
          // ── MF NAV retry: if getMFNav failed above, try once more with ticker as code ──
          if (!price && isAMFI && h.ticker && /^\d{5,6}$/.test(String(h.ticker))) {
            const nav = await PriceDataProvider.getMFNav(h.ticker, forceRefresh);
            if (nav) { price = nav; source = "mfapi"; }
          }

        } catch {}

        return { id: h.id, price, source };
      })
    );

    // Build maps and apply prices
    const priceMap  = {};
    const statusMap = {};
    results.forEach(r => {
      if (r.status === "fulfilled" && r.value) {
        // Only record in priceMap if price is a valid positive number
        if (r.value.price && r.value.price > 0) {
          priceMap[r.value.id]  = r.value.price;
        }
        // Always record source if we got a response (even if price was stale/null)
        if (r.value.source) {
          statusMap[r.value.id] = r.value.source;
        }
      }
    });

    setHoldings(prev => {
      const updated = prev.map(h => {
        if (isValueOnlyClass(h.class)) return h;
        if (!h.listed && !h.isMutualFund) return h;
        const newPrice = priceMap[h.id];
        if (newPrice && newPrice > 0) return { ...h, currentPrice: newPrice };
        // Don't apply simulated tick to UCITS/Global MF funds — keep their manual price as-is
        const isGlobalMF = h.class === "Global Mutual Funds" || h.isGlobalMF ||
          (h.ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(h.ticker));
        if (isGlobalMF) return h;
        return { ...h, currentPrice: +(h.currentPrice * (1 + (Math.random() - 0.49) * 0.003)).toFixed(4) };
      });
      holdingsRef.current = updated; // keep ref in sync after price update
      // ── Persist live prices to Supabase in background ──
      // So next login shows last-known prices instantly before live refresh completes
      if (typeof navigator === "undefined" || navigator.onLine !== false) {
        const today = new Date().toISOString().slice(0, 10);
        let savedCount = 0;
        updated.forEach(h => {
          if (priceMap[h.id] && priceMap[h.id] > 0) {
            savedCount++;
            updateHoldingPrice(h.id, priceMap[h.id], today).catch(() => {});
          }
        });
        if (savedCount > 0) setLastSavedPriceTime(new Date());
      }
      return updated;
    });

    setLiveStatus(prev => {
      const next = { ...prev, ...statusMap };
      // Mark as simulated only if: listed/MF (not Global MF), no valid price fetched, AND no source responded
      currentHoldings.forEach(h => {
        const isUCITS = h.class === "Global Mutual Funds" || h.isGlobalMF ||
          (h.ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(h.ticker));
        if (!isUCITS && (h.listed || h.isMutualFund) && !priceMap[h.id] && !statusMap[h.id]) {
          next[h.id] = "simulated";
        }
      });
      return next;
    });
    // Track which listed holdings failed to get a live price
    setPriceFailedIds(new Set(
      currentHoldings
        .filter(h =>
          (h.listed || h.isMutualFund) &&
          !priceMap[h.id] &&           // no valid price fetched
          !statusMap[h.id] &&          // AND source didn't respond at all
          h.ticker && h.ticker !== "-" &&
          !isValueOnlyClass(h.class) &&
          !h.manualPriceDate
        )
        .map(h => h.id)
    ));
    setLastUpd(new Date());

    // ── 2. Watchlist live prices ──
    const wlResults = await Promise.allSettled(
      watchlist.map(async w => {
        if (!w.ticker || w.ticker === "-") return null;
        const result = await fetchTickerPrice(w.ticker, w.exchange, null, forceRefresh);
        if (result?.price) return { id: w.id, price: result.price };
        return null;
      })
    );
    const wlPriceMap = {};
    wlResults.forEach(r => { if (r.status === "fulfilled" && r.value) wlPriceMap[r.value.id] = r.value.price; });
    if (Object.keys(wlPriceMap).length > 0) {
      setWatchlist(prev => {
        const updated = prev.map(w => {
          const newPrice = wlPriceMap[w.id];
          if (!newPrice) return w;
          const change1d = w.currentPrice > 0 ? ((newPrice - w.currentPrice) / w.currentPrice * 100) : 0;
          return { ...w, currentPrice: newPrice, change1d: parseFloat(change1d.toFixed(2)) };
        });
        // Check price alerts and fire toasts
        const triggered = [];
        updated.forEach(w => {
          const prevItem = prev.find(x=>x.id===w.id);
          const prevPrice = prevItem?.currentPrice || 0;
          if (w.alertAbove && w.targetPrice && w.currentPrice >= w.targetPrice && prevPrice < w.targetPrice) {
            if (notifPrefs.priceAlerts !== false) triggered.push({ id: w.id+"_above_"+Date.now(), msg: `🔔 ${w.ticker} hit target ₹${w.targetPrice.toLocaleString()} — now ${w.currentPrice.toLocaleString()}`, col: "#D97706" });
            if (notifPrefs.browserPush && "Notification" in window && Notification.permission === "granted") new Notification(`${w.ticker} hit target price`, { body: `Now at ${w.currentPrice.toLocaleString()}` });
          }
          if (w.alertBelow && w.targetBuy && w.currentPrice <= w.targetBuy && prevPrice > w.targetBuy) {
            triggered.push({ id: w.id+"_below_"+Date.now(), msg: `🔔 ${w.ticker} in buy zone — below ₹${w.targetBuy.toLocaleString()}`, col: "#1D9E75" });
            if ("Notification" in window && Notification.permission === "granted") new Notification(`${w.ticker} in buy zone`, { body: `Now at ${w.currentPrice.toLocaleString()}` });
          }
        });
        if (triggered.length > 0) {
          setAlertToasts(p => [...p, ...triggered]);
          triggered.forEach(t => setTimeout(() => setAlertToasts(p => p.filter(x => x.id !== t.id)), 8000));
        }
        // Persist updated prices to Supabase in background — only when online
        if (typeof navigator === "undefined" || navigator.onLine !== false) {
          updated.forEach(w => {
            if (wlPriceMap[w.id]) {
              updateWatchlistPrice(w.id, w.currentPrice, w.change1d)
                .catch(e => {
                  // Silently ignore network errors on price persistence — non-critical
                  if (!String(e.message).includes("Load failed") && !String(e.message).includes("network")) {
                    console.warn("[Supabase] watchlist price update:", e.message);
                  }
                });
            }
          });
        }
        return updated;
      });
    }

    // ── 3. Backtesting live prices (active positions only) ──
    const btResults = await Promise.allSettled(
      btAssets.filter(a => a.status !== "exited").map(async a => {
        if (!a.ticker || a.ticker === "-") return null;
        const result = await fetchTickerPrice(a.ticker, a.exchange, null, forceRefresh);
        if (result?.price) return { id: a.id, price: result.price };
        return null;
      })
    );
    const btPriceMap = {};
    btResults.forEach(r => { if (r.status === "fulfilled" && r.value) btPriceMap[r.value.id] = r.value.price; });
    if (Object.keys(btPriceMap).length > 0) {
      setBtAssets(prev => {
        const updated = btRebalance(prev.map(a => {
          const newPrice = btPriceMap[a.id];
          if (!newPrice) return a;
          return { ...a, currentPrice: newPrice };
        }));
        // Persist to Supabase in background
        updated.forEach(a => {
          if (btPriceMap[a.id]) {
            updateBtAsset(a.id, { currentPrice: a.currentPrice })
              .catch(e => console.error("[Supabase] btAsset price update:", e.message));
          }
        });
        return updated;
      });
    }

    setRefreshing(false);
    setLastRefreshTime(new Date());
    setLastUpd(new Date());
    setLoginRefreshMsg(false); // clear post-login spinner once complete
  }, [holdings, watchlist, btAssets]);

  // ── Global Escape key — closes any open modal (innermost first) ──
  useEffect(() => {
    const handler = (e) => {
      // Cmd/Ctrl+K → open global search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setGlobalOpen(p => !p);
        return;
      }
      if (e.key !== "Escape") return;
      if (globalOpen) { setGlobalOpen(false); setGlobalSearch(""); return; }
      if (colMenuOpen)       { setColMenuOpen(false); return; }
      if (csvPreview)        { setCsvPreview(null); return; }
      if (editHoldId)        { setEditHoldId(null); setEditHoldVals({}); return; }
      if (wizardOpen)        { setWizardOpen(false); return; }
      if (pdfModal)          { setPdfModal(false); return; }
      if (goalModalOpen)     { setGoalModalOpen(false); setEditingGoal(null); return; }
      if (profileOpen)       { setProfileOpen(false); return; }
      if (divModalOpen)      { setDivModalOpen(false); return; }
      if (txnAddOpen)        { setTxnAddOpen(false); return; }
      if (irrAddModal)       { setIrrAddModal(false); return; }
      if (addOpen)           { setAddOpen(false); return; }
      if (addFamilyOpen)     { setAddFamilyOpen(false); return; }
      if (forgotPassOpen)    { setForgotPassOpen(false); return; }
      if (credModal)         { setCredModal(null); return; }
      if (kycViewClient)     { setKycViewClient(null); return; }
      if (btExitId)          { setBtExitId(null); return; }
      if (btAddOpen)         { setBtAddOpen(false); return; }
      if (researchAssetOpen) { setResearchAssetOpen(null); return; }
      if (avatarMenuOpen)    { setAvatarMenuOpen(false); return; }
      if (moreOpen)          { setMoreOpen(false); return; }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editHoldId, wizardOpen, pdfModal, goalModalOpen, profileOpen,
      divModalOpen, txnAddOpen, irrAddModal, addOpen, addFamilyOpen,
      forgotPassOpen, credModal, kycViewClient, btExitId, btAddOpen,
      researchAssetOpen, avatarMenuOpen, moreOpen, globalOpen, csvPreview]);

  // Run once on mount — force-refresh so we always get live prices on load
  useEffect(()=>{ refresh(true); }, []); // eslint-disable-line

  // ── Shortlist: load shortlists when tab opens ──
  useEffect(()=>{
    if (tab !== "shortlist") return;
    const SURL = "https://aelgwwapsldljmedjwvj.supabase.co";
    const SKEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
    const aId = isAdmin ? "admin" : loggedInClientId;
    (async()=>{
      try {
        const r = await fetch(`${SURL}/rest/v1/shortlists?advisor_id=eq.${aId}&select=*&order=updated_at.desc`,
          { headers:{ apikey:SKEY, Authorization:`Bearer ${SKEY}` } });
        if (!r.ok) { console.warn("[shortlist] loadShortlists HTTP", r.status); return; }
        const data = await r.json();
        console.log("[shortlist] loaded", data?.length, "shortlists");
        setShortlists(data||[]);
        if ((data||[]).length > 0 && !activeShortlistId) setActiveShortlistId(data[0].id);
      } catch(e) { console.warn("[shortlist] loadShortlists error:", e.message); }
    })();
  }, [tab]); // eslint-disable-line

  // ── Shortlist: load items whenever activeShortlistId changes ──
  useEffect(()=>{
    if (!activeShortlistId) return;
    const SURL = "https://aelgwwapsldljmedjwvj.supabase.co";
    const SKEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
    console.log("[shortlist] loading items for shortlist:", activeShortlistId);
    (async()=>{
      try {
        const r = await fetch(`${SURL}/rest/v1/shortlist_items?shortlist_id=eq.${activeShortlistId}&select=*&order=added_at.desc`,
          { headers:{ apikey:SKEY, Authorization:`Bearer ${SKEY}` } });
        if (!r.ok) { console.warn("[shortlist] loadItems HTTP", r.status); return; }
        const data = await r.json();
        console.log("[shortlist] loaded", data?.length, "items");
        setShortlistItems(data||[]);
        setShortlistLoadedForId(activeShortlistId);
        // Restore AI cache
        const aiCache = {};
        for (const i of (data||[])) if (i.ai_research) aiCache[i.id] = i.ai_research;
        setShortlistAiData(prev=>({...prev,...aiCache}));
      } catch(e) { console.warn("[shortlist] loadItems error:", e.message); }
    })();
  }, [activeShortlistId]); // eslint-disable-line

  // ── Benchmark: fetch when compare modal opens (use /api/price which is proven to work) ──
  useEffect(()=>{
    if (!compareOpen || compareBenchmark === "None") { setBenchmarkData(null); return; }
    // Map benchmark name → ticker for /api/price
    const BTICKERS = {
      "Nifty 50":"NIFTYBEES.NS","Nifty Next 50":"JUNIORBEES.NS",
      "Nifty Bank":"BANKBEES.NS","Nifty IT":"ITBEES.NS",
      "S&P 500":"VOO","NASDAQ 100":"QQQ","Dow Jones":"DIA",
      "MSCI World":"VT","FTSE 100":"ISF.L","Gold":"GLD","Silver":"SLV",
    };
    const t = BTICKERS[compareBenchmark];
    if (!t) return;
    setBenchmarkLoading(true); setBenchmarkData(null);
    fetch(`/api/shortlist-prices?ticker=${encodeURIComponent(t)}`)
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d&&!d.error&&d.price) setBenchmarkData(d); else console.warn("[benchmark] no data for",t,d?.error); })
      .catch(e=>console.warn("[benchmark] fetch error:",e))
      .finally(()=>setBenchmarkLoading(false));
  }, [compareOpen, compareBenchmark]); // eslint-disable-line

  // ── Post-login price refresh: fires AFTER holdings state is committed ──
  // useEffect runs after React commits state, so holdings[] is guaranteed populated
  useEffect(() => {
    if (needsPriceRefreshRef.current && holdings.length > 0) {
      needsPriceRefreshRef.current = false;
      const t = setTimeout(() => refresh(true), 50);
      return () => clearTimeout(t);
    }
  }, [holdings]); // eslint-disable-line

  // ── Refresh prices when DB load completes and prices are stale ───────────
  const dbLoadCompleteRef = React.useRef(false);
  // Reset the ref whenever the user logs in/out so refresh fires again
  useEffect(() => { dbLoadCompleteRef.current = false; }, [signedOut, loggedInClientId, isAdmin]);
  useEffect(() => {
    if (!dbLoading && !signedOut && holdings.length > 0 && !dbLoadCompleteRef.current) {
      dbLoadCompleteRef.current = true;
      // Always refresh prices on login — don't condition on currentPrice being 0
      const t = setTimeout(() => refresh(true), 300);
      return () => clearTimeout(t);
    }
  }, [dbLoading, holdings.length, signedOut]); // eslint-disable-line
  useEffect(()=>{ const iv=setInterval(()=>refresh(false), 60000); return ()=>clearInterval(iv); }, [refresh]);

  // ── Auto-refresh prices on login ──
  // Triggers whenever admin logs in or a client logs in (isAdmin/loggedInClientId changes)
  const prevAuthRef = React.useRef({ isAdmin: false, clientId: null });
  // Flag set on login — triggers price refresh once holdings finish loading from DB
  const needsPriceRefreshRef = React.useRef(false);
  // Show a "fetching prices" indicator in the header when refreshing post-login
  const [loginRefreshMsg, setLoginRefreshMsg] = React.useState(false);

  useEffect(() => {
    const prev = prevAuthRef.current;
    const justLoggedIn =
      (isAdmin && !prev.isAdmin) ||
      (loggedInClientId && loggedInClientId !== prev.clientId);
    if (justLoggedIn) {
      // Set a flag — refresh will fire once holdings finish loading from Supabase
      needsPriceRefreshRef.current = true;
      setLoginRefreshMsg(true);
      clearCache(); // clear stale price cache immediately
      refreshFx();  // FX rates can refresh right away (no holdings dependency)
      prevAuthRef.current = { isAdmin, clientId: loggedInClientId };
    }
    prevAuthRef.current = { isAdmin, clientId: loggedInClientId };
  }, [isAdmin, loggedInClientId]); // eslint-disable-line

  // ── Benchmark metrics: live via Yahoo Finance (allorigins CORS proxy) ──
  const loadBenchmarks = useCallback(async (forceRefresh = false) => {
    setBenchLoading(true);
    const freshMetrics = {};

    // YF maps predefined benchmark ids to Yahoo Finance tickers.
    // For user-added benchmarks, bDef.ticker is already the YF ticker.
    const YF_STATIC = { sp500:"SPY", nifty:"^NSEI", msciworld:"ACWI", ftse:"^FTSE", gold:"GLD", btc:"BTC-USD" };
    // Read from ref — always current, no stale closure issue
    const activeBenchDefs = benchDefsRef.current; // all benchmarks are user-added  // use whatever is current
    const YF = new Proxy(YF_STATIC, {
      get(t, key) { return t[key] ?? activeBenchDefs.find(b=>b.id===key)?.ticker ?? null; }
    });

    const parseYFChart = (json) => {
      const res = json?.chart?.result?.[0];
      if (!res) return null;
      const ts = res.timestamp || [];
      const cl = res.indicators?.adjclose?.[0]?.adjclose || res.indicators?.quote?.[0]?.close || [];
      const pts = [];
      for (let i = 0; i < ts.length; i++) {
        const c = cl[i]; if (!c || c <= 0) continue;
        const d = new Date(ts[i] * 1000);
        pts.push({ ym: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, close: c });
      }
      return pts.sort((a,b) => b.ym.localeCompare(a.ym));
    };

    const computeMetrics = (bDef, pts) => {
      if (!pts || !pts.length) return null;
      const latest = pts[0].close;
      const latestYM = pts[0].ym;
      const now = new Date();
      const priceAt = (ym) => { for (const p of pts) { if (p.ym <= ym) return p.close; } return null; };
      const prevMo = new Date(now.getFullYear(), now.getMonth()-1, 1);
      const prevMoYM = `${prevMo.getFullYear()}-${String(prevMo.getMonth()+1).padStart(2,"0")}`;
      const mtdBase = priceAt(prevMoYM);
      const mtd = mtdBase ? ((latest - mtdBase) / mtdBase) * 100 : null;
      const ytdBase = priceAt(`${now.getFullYear()-1}-12`);
      const ytd = ytdBase ? ((latest - ytdBase) / ytdBase) * 100 : null;
      const cagr = (yrs) => {
        const d = new Date(now); d.setFullYear(d.getFullYear()-yrs);
        const ym = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
        const past = priceAt(ym);
        return past && past > 0 ? (Math.pow(latest/past, 1/yrs) - 1) * 100 : null;
      };
      const f = v => (v != null && isFinite(v)) ? parseFloat(v.toFixed(2)) : null;
      const fb = BENCHMARK_FALLBACK[bDef.id];
      return {
        mtd:    f(mtd)      ?? fb.mtd,
        ytd:    f(ytd)      ?? fb.ytd,
        oneY:   f(cagr(1))  ?? fb.oneY,
        threeY: f(cagr(3))  ?? fb.threeY,
        fiveY:  f(cagr(5))  ?? fb.fiveY,
        tenY:   f(cagr(10)) ?? fb.tenY,
        live: true, asOf: latestYM,
      };
    };

    const fetchOne = async (bDef) => {
      const cacheKey = `bench_yf4_${bDef.id}`;  // v4: includes asOf field
      if (!forceRefresh) { const c = getCached(cacheKey); if (c) return c; }
      const ticker = YF[bDef.id];
      if (!ticker) {
        console.warn(`[Benchmarks] No ticker for benchmark id "${bDef.id}" — skipping`);
        return null;
      }
      const now  = Math.floor(Date.now() / 1000);
      const from = now - 11 * 365 * 24 * 3600;

      // ── PRIMARY: our own Vercel /api/benchmark (server-side, no CORS issues) ──
      try {
        const r = await fetch(
          `/api/benchmark?ticker=${encodeURIComponent(ticker)}&from=${from}&to=${now}&interval=1mo`,
          { signal: AbortSignal.timeout(14000) }
        );
        if (r.ok) {
          const j = await r.json();
          const pts = j?.points;  // [{ym, close}] newest-first
          if (pts?.length) {
            const m = computeMetrics(bDef, pts);
            if (m) {
              setCache(cacheKey, m);
              setBenchHistoryPts(prev => ({ ...prev, [bDef.id]: pts }));
              return m;
            }
          }
        }
      } catch (e) {
        console.warn(`[Benchmarks] /api/benchmark error for ${ticker}:`, e.message);
      }

      // ── FALLBACK: direct YF via codetabs (only proxy that works from Vercel frontend) ──
      for (const q1q2 of ["query1", "query2"]) {
        const yfUrl = `https://${q1q2}.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}?interval=1mo&period1=${from}&period2=${now}&includeAdjustedClose=true`;
        const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(yfUrl)}`;
        try {
          const ctrl = new AbortController();
          const tId  = setTimeout(() => ctrl.abort(), 10000);
          const resp = await fetch(proxyUrl, { signal: ctrl.signal });
          clearTimeout(tId);
          if (!resp.ok) continue;
          let json = await resp.json();
          if (typeof json === "string") { try { json = JSON.parse(json); } catch { continue; } }
          const pts = parseYFChart(json);
          if (!pts?.length) continue;
          const m = computeMetrics(bDef, pts);
          if (m) {
            setCache(cacheKey, m);
            setBenchHistoryPts(prev => ({ ...prev, [bDef.id]: pts }));
            return m;
          }
        } catch (e) {
          if (e.name !== "AbortError") console.warn(`[Benchmarks] ${ticker} codetabs fallback:`, e.message);
        }
      }

      console.warn(`[Benchmarks] All sources exhausted for ${ticker} — using fallback metrics`);
      return null;
    };

    for (let i = 0; i < activeBenchDefs.length; i++) {
      const bDef = activeBenchDefs[i];
      if (i > 0) await new Promise(r => setTimeout(r, 500)); // 500ms stagger prevents proxy rate limits
      const m = await fetchOne(bDef);
      freshMetrics[bDef.id] = m ?? { ...BENCHMARK_FALLBACK[bDef.id], live: false };
      setBenchMetrics(prev => ({ ...prev, [bDef.id]: freshMetrics[bDef.id] }));
    }

    setBenchMetrics(prev => ({ ...prev, ...freshMetrics }));
    setBenchLoading(false);
  }, []);


  // ── Force reload all data from Supabase (called on manual refresh or after writes) ──
  const reloadFromSupabase = React.useCallback(async () => {
    // Direct watchlist fetch to ensure client_id comes through (wrapper may
    // not SELECT newer columns).
    const fetchWatchlistDirect = async () => {
      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/watchlist?select=*`, {
          headers: { 'apikey': ANON_KEY, 'Authorization': `Bearer ${ANON_KEY}` },
        });
        if (!res.ok) return await fetchWatchlist();
        const rows = await res.json();
        return rows.map(r => ({
          id: r.id, clientId: r.client_id ?? null, ticker: r.ticker, name: r.name,
          exchange: r.exchange, currency: r.currency,
          targetPrice: r.target_price ?? 0, targetBuy: r.target_buy ?? 0,
          alertAbove: !!r.alert_above, alertBelow: !!r.alert_below,
          notes: r.notes ?? "", currentPrice: r.current_price ?? 0,
          change1d: r.change_1d ?? 0, weight: r.weight ?? 0, createdAt: r.created_at,
        }));
      } catch (e) { return await fetchWatchlist(); }
    };
    const fetchBtAssetsDirect = async () => {
      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/bt_assets?select=*`, {
          headers: { 'apikey': ANON_KEY, 'Authorization': `Bearer ${ANON_KEY}` },
        });
        if (!res.ok) return await fetchBtAssets();
        const rows = await res.json();
        return rows.map(r => ({
          id: r.id, clientId: r.client_id ?? null, ticker: r.ticker, name: r.name,
          exchange: r.exchange, currency: r.currency,
          entryPrice: r.entry_price ?? 0, exitPrice: r.exit_price ?? null,
          currentPrice: r.current_price ?? 0, amount: r.amount ?? 0,
          addedOn: r.added_on, exitedOn: r.exited_on,
          status: r.status ?? 'active', notes: r.notes ?? "",
          allocPct: r.alloc_pct ?? 0, createdAt: r.created_at,
        }));
      } catch (e) { return await fetchBtAssets(); }
    };
    try {
      setDbLoading(true);
      const [fams, clis, holds, owners, wl, bts, logins, txns, profiles] = await Promise.all([
        fetchFamilies(),
        fetchClients(),
        fetchHoldings(),
        fetchHoldingOwners(),
        fetchWatchlistDirect(),
        fetchBtAssetsDirect(),
        fetchClientLogins(),
        (typeof fetchTransactions === 'function' ? fetchTransactions() : Promise.resolve([])),
        (typeof fetchProfiles === 'function' ? fetchProfiles() : Promise.resolve({})),
      ]);
      setFamilies(fams);
      const normClis = clis.map(c => ({
        ...c,
        familyId: c.familyId || c.family_id || null,
        riskProfile: c.riskProfile || c.risk_profile || null,
      }));
      setClients(normClis);
      setHoldings(holds.map(h => {
        const missingCap = !h.capSize || h.capSize === "N/A";
        return { ...h, ...(missingCap ? { capSize: resolveCapSize(h) } : {}) };
      }));
      setHoldingOwners(owners);
      // See comment above setWatchlist — translate client_id to clientId once.
      setWatchlist(wl.map(w => ({ ...w, clientId: w.clientId ?? w.client_id ?? null })));
      setBtAssets(btRebalance(bts));
      setClientLogins(logins);
      if (txns) setTransactions(txns);
      if (profiles) setProfileData(profiles);
      setDbLoading(false);
      showToast("✓ Data refreshed from server", "#1D9E75", 2000);
      // Refresh price snapshots in background — sparkline trends stay fresh
      (async () => {
        try {
          const ids = (holds||[]).map(h => h.id).filter(Boolean);
          if (ids.length) {
            const snaps = await fetchPriceSnapshots(ids);
            setPriceSnapshots(snaps || {});
          }
        } catch (e) {
          console.warn("[price_snapshots] refresh fetch failed:", e.message);
        }
      })();
    } catch(e) {
      setDbLoading(false);
      showToast("⚠ Refresh failed: " + e.message, "#A32D2D", 3000);
    }
  }, []);

  // ── Auto-login from saved session (runs once on mount) ──────────────────
  // Reads fw_session_v1 from localStorage and programmatically replays login
  // so all role/client states are set correctly (avoids "no role" broken state)
  useEffect(() => {
    try {
      const sess = JSON.parse(localStorage.getItem("fw_session_v1") || "null");
      // Only auto-login if session < 2 hours old AND page was not navigated to fresh
    // (performance.navigation.type === 0 means fresh load; TYPE_NAVIGATE = typed/bookmarked)
    const isDirectOpen = typeof performance !== "undefined" && 
      (performance.getEntriesByType?.("navigation")?.[0]?.type === "navigate" ||
       performance.navigation?.type === 0);
    if (!sess?.ts || (Date.now() - sess.ts) >= 2 * 60 * 60 * 1000) return; // expired (2hr)
      if (sess.role === "admin" && sess.key) {
        // Replay admin login
        const entries = Object.entries(clientLogins || {})
          .filter(([k]) => k === "__admin__" || k.startsWith("__admin__"));
        // Just set the state directly — we trust the saved session
        setIsAdmin(true);
        setLoggedInClientId(null);
        setSignedOut(false);
        setSessionExpired(false);
      } else if (sess.role === "client" && sess.clientId) {
        setIsAdmin(false);
        setLoggedInClientId(sess.clientId);
        setSignedOut(false);
        setSessionExpired(false);
      }
    } catch {}
  }, []); // eslint-disable-line

  // ── Refs so loadBenchmarks (stable useCallback) always sees current list ──
  const prevBenchCountRef = React.useRef(BENCHMARKS_DEF.length);
  const benchDefsRef      = React.useRef(BENCHMARKS_DEF);
  benchDefsRef.current    = BENCHMARKS_DEF; // updated every render, no effect needed

  // ── When admin switches client filter while on AI Advisor tab,
  //    auto-load that client's most recent saved conversation ──
  useEffect(() => {
    if (tab !== "agent" || !isAdmin) return;
    const cId = selectedClients.size === 1 ? [...selectedClients][0] : null;
    const fId = selectedFamily !== "all" ? selectedFamily : null;
    const key = cId || (fId ? `family_${fId}` : "__all__");
    if (lastLoadedAdvisorKey.current === key) return; // already loaded
    lastLoadedAdvisorKey.current = key;
    // If no messages yet for this client, load from most recent archive entry
    setAgentMessagesMap(prev => {
      if (prev[key] && prev[key].length > 0) return prev; // already has messages
      const entry = advisorArchive.find(e => {
        if (cId) return e.clientId === cId;
        if (fId) return e.familyId === fId;
        return false;
      });
      if (!entry) return prev;
      // Mark first message as from archive so UI can show notice
      const msgs = entry.messages.map((m, i) => i === 0 ? { ...m, _fromArchive: true } : m);
      return { ...prev, [key]: msgs };
    });
  }, [tab, selectedClients, selectedFamily, isAdmin, advisorArchive]);

  // Initial load
  useEffect(()=>{ loadBenchmarks(); }, [loadBenchmarks]);

  // Re-fetch only when user adds/removes a benchmark (length change)
  useEffect(()=>{
    if (BENCHMARKS_DEF.length !== prevBenchCountRef.current) {
      prevBenchCountRef.current = BENCHMARKS_DEF.length;
      loadBenchmarks(true);
    }
  }, [BENCHMARKS_DEF.length]); // eslint-disable-line

  // ── Fetch news (called on tab open + manual refresh) ──
  const fetchNews = React.useCallback(async (filter) => {
    setNewsLoading(true);
    try {
      const isCat = ["all","india","global","macro"].includes(filter);
      const url = isCat
        ? `/api/news?category=${filter === "all" ? "" : filter}`
        : `/api/news?q=${encodeURIComponent(filter)}`;
      const r = await fetch(url);
      const j = await r.json();
      setNewsItems(j.items || []);
      setNewsLastFetch(new Date());
    } catch { setNewsItems([]); }
    setNewsLoading(false);
  }, []);

  // ── Point-to-point return calculation ──
  // In production: fetch historical price at p2pFromDate, compute (latest - fromPrice)/fromPrice
  const computeP2P = useCallback(async () => {

    const today = new Date();
    const from  = new Date(p2pFromDate);
    const fromTime = from.getTime();
    const todayTime = today.getTime();
    const totalDays = Math.max(1, (todayTime - fromTime) / (24 * 3600 * 1000));
    const years = totalDays / 365.25;

    // ── Benchmark P2P from stored monthly history with intra-month interpolation ──

    const fromYM = `${from.getFullYear()}-${String(from.getMonth()+1).padStart(2,"0")}`;
    const fromDay = from.getDate();
    const daysInFromMonth = new Date(from.getFullYear(), from.getMonth()+1, 0).getDate();
    const results = {};
    BENCHMARKS_DEF.forEach(b => {
      const pts = benchHistoryPts[b.id]; // [{ym, close}] newest first
      if (pts && pts.length >= 2) {
        const latest = pts[0].close;
        // Find monthly data points: same month and one month before
        let sameMonthClose = null, prevMonthClose = null;
        for (let i = 0; i < pts.length; i++) {
          if (pts[i].ym === fromYM && sameMonthClose === null) {
            sameMonthClose = pts[i].close;
          } else if (pts[i].ym < fromYM && prevMonthClose === null) {
            prevMonthClose = pts[i].close;
            break;
          }
        }
        let fromClose = null;
        if (sameMonthClose && prevMonthClose) {
          // Interpolate: day 1 = prevMonthClose, day end = sameMonthClose
          const frac = (fromDay - 1) / Math.max(1, daysInFromMonth - 1);
          fromClose = prevMonthClose + (sameMonthClose - prevMonthClose) * frac;
        } else if (prevMonthClose) {
          fromClose = prevMonthClose;
        } else if (sameMonthClose) {
          fromClose = sameMonthClose;
        }
        if (fromClose && fromClose > 0 && latest > 0) {
          results[b.id] = +((latest - fromClose) / fromClose * 100).toFixed(2);
          return;
        }
      }
      // Fallback: compound stored CAGR for exact period
      const m = benchMetrics[b.id];
      if (m) {
        const ar = years <= 0.25 ? (m.mtd != null ? m.mtd : m.ytd)
          : years <= 1   ? (m.oneY ?? m.threeY)
          : years <= 3   ? (m.threeY ?? m.fiveY)
          : years <= 5   ? (m.fiveY ?? m.tenY)
          : (m.tenY ?? m.fiveY);
        if (ar != null) results[b.id] = +((Math.pow(1 + ar/100, years) - 1)*100).toFixed(2);
      }
    });
    setP2pReturns(results);

    // ── Portfolio P2P: TWRR with historical prices at from-date ──
    const hw = filteredHW;
    if (hw.length === 0) { setPortP2p(null); return; }

    const currentVal = hw.reduce((s,h) => s + toUSD(h.qty * h.currentPrice, h.currency), 0);
    if (currentVal <= 0) { setPortP2p(null); return; }

    // Determine outputsize: compact (100 days) vs full
    const daysSinceFrom = totalDays;
    const avOutputSize = daysSinceFrom <= 90 ? "compact" : "full";

    // Fetch historical price at from-date for each listed holding with a ticker
    // Uses Alpha Vantage TIME_SERIES_DAILY_ADJUSTED (cached aggressively)
    const fromDateStr = p2pFromDate; // YYYY-MM-DD
    const historicalPrices = {}; // { holdingId: price }

    // Only fetch for listed holdings with valid tickers to limit API calls
    const fetchable = hw.filter(h =>
      h.listed && h.ticker && h.ticker !== "-" &&
      !isValueOnlyClass(h.class) && !h.isMutualFund
    );
    // MF holdings: use mfapi.in historical NAV (no rate limit, CORS-open)
    const mfFetchable = hw.filter(h =>
      h.isMutualFund && h.schemeCode && !isValueOnlyClass(h.class)
    );
    await Promise.allSettled(mfFetchable.map(async h => {
      const nav = await PriceDataProvider.getMFHistoricalNav(h.schemeCode, fromDateStr);
      if (nav && nav > 0) historicalPrices[h.id] = nav;
    }));

    // Batch fetches with rate-limit awareness (AV: 5 calls/min on free tier)
    // Stagger requests by 200ms each
    const fetchHistPrice = async (h, attempt = 0) => {
      const cacheKey = `p2p_hist_${h.ticker}_${fromDateStr}`;
      const cached = getCached(cacheKey);
      if (cached) return cached;
      try {
        await new Promise(r => setTimeout(r, attempt * 220)); // stagger
        const url = `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY_ADJUSTED` +
          `&symbol=${encodeURIComponent(h.ticker)}&outputsize=${avOutputSize}` +
          `&apikey=${CONFIG.alphaVantageKey}`;
        const resp = await fetch(url);
        const data = await resp.json();
        const series = data["Time Series (Daily)"];
        if (!series) return null;
        // Find price on fromDate or nearest trading day before it
        const dates = Object.keys(series).sort().reverse();
        const target = dates.find(d => d <= fromDateStr);
        if (target) {
          const price = parseFloat(series[target]["5. adjusted close"]);
          if (price > 0) { setCache(cacheKey, price); return price; }
        }
      } catch {}
      return null;
    };

    const histResults = await Promise.allSettled(
      fetchable.map((h, i) => fetchHistPrice(h, i))
    );

    fetchable.forEach((h, i) => {
      const r = histResults[i];
      if (r.status === "fulfilled" && r.value && r.value > 0) {
        historicalPrices[h.id] = r.value;
      }
    });

    // Build start-of-period portfolio value
    // For holdings with fetched historical price: qty_at_from × hist_price
    // For MF / unlisted / no price fetched: use costPrice as proxy
    let startVal = 0;
    hw.forEach(h => {
      // Qty held at from-date via transaction history
      const preTxns = transactions
        .filter(t => t.holding === h.name && new Date(t.date).getTime() < fromTime)
        .sort((a,b) => new Date(a.date) - new Date(b.date));
      let qty = 0;
      preTxns.forEach(t => {
        if (t.type === "BUY") qty += t.qty;
        else qty = Math.max(0, qty - t.qty);
      });
      // If no pre-period transactions, assume current qty was held since before
      const qtyAtFrom = qty > 0 ? qty : h.qty;

      // Price at from-date: fetched historical > last pre-period txn price > costPrice
      let priceAtFrom = historicalPrices[h.id] || null;
      if (!priceAtFrom && preTxns.length > 0) {
        const lastPre = [...preTxns].reverse()[0];
        priceAtFrom = lastPre.price;
      }
      if (!priceAtFrom) priceAtFrom = h.costPrice;

      startVal += toUSD(qtyAtFrom * priceAtFrom, h.currency);
    });

    if (startVal <= 0) { setPortP2p(null); return; }

    // TWRR: chain sub-period returns across transaction dates within [from, today]
    const periodTxns = transactions
      .filter(t => { const tx = new Date(t.date).getTime(); return tx >= fromTime && tx <= todayTime; })
      .sort((a,b) => new Date(a.date) - new Date(b.date));
    const txDates = [...new Set(periodTxns.map(t => t.date))].sort();

    // Portfolio value at a date using last known price for each holding
    const valAt = (dateStr, excludeOnDate = false) => {
      const dt = new Date(dateStr).getTime();
      let total = 0;
      hw.forEach(h => {
        const txns = transactions
          .filter(t => t.holding===h.name && (excludeOnDate
            ? new Date(t.date).getTime() < dt
            : new Date(t.date).getTime() <= dt))
          .sort((a,b) => new Date(a.date)-new Date(b.date));
        let qty = 0;
        txns.forEach(t => {
          if (t.type==="BUY") qty += t.qty;
          else qty = Math.max(0, qty-t.qty);
        });
        const lastTxn = [...txns].reverse()[0];
        // Use transaction price as market price on that date
        const price = lastTxn ? lastTxn.price : (historicalPrices[h.id] || h.costPrice);
        total += toUSD(qty * price, h.currency);
      });
      return total;
    };

    // Chain TWRR sub-periods
    let twrrProduct = 1;
    let prevVal = startVal;
    txDates.forEach(d => {
      const before = valAt(d, true);
      if (before > 1 && prevVal > 1) {
        const r = (before - prevVal) / prevVal;
        if (isFinite(r) && Math.abs(r) < 10) twrrProduct *= (1 + r);
      }
      const after = valAt(d, false);
      if (after > 0) prevVal = after;
    });

    // Final sub-period: last txn date to today (using current prices)
    if (prevVal > 1 && currentVal > 1) {
      const r = (currentVal - prevVal) / prevVal;
      if (isFinite(r) && Math.abs(r) < 10) twrrProduct *= (1 + r);
    }

    const twrr = (twrrProduct - 1) * 100;
    setPortP2p(isFinite(twrr) ? +twrr.toFixed(2) : null);
  }, [p2pFromDate, benchMetrics, benchHistoryPts, transactions, filteredHW]); // eslint-disable-line


  useEffect(()=>{ computeP2P(); }, [computeP2P]);

  // ── Pre-compute YTD / 1Y / 3Y portfolio returns using TWRR + AV historical prices ──
  const computePeriodReturns = useCallback(async () => {
    const hw = filteredHW;
    if (hw.length === 0) { setPeriodReturns({ ytd:null, oneY:null, threeY:null }); return; }

    const todayMs = Date.now();
    const periods = {
      ytd:    { from: startOfYear(),      years: (todayMs - new Date(startOfYear()).getTime()) / (365.25*86400000) },
      oneY:   { from: dateYearsAgo(1),    years: 1 },
      threeY: { from: dateYearsAgo(3),    years: 3 },
    };

    // Fetch AV historical close for each listed holding at each period start
    const fetchHistForDate = async (ticker, dateStr) => {
      const cacheKey = `per_hist_${ticker}_${dateStr}`;
      const cached = getCached(cacheKey);
      if (cached) return cached;
      try {
        const days = (todayMs - new Date(dateStr).getTime()) / 86400000;
        const outputsize = days <= 90 ? "compact" : "full";
        const url = `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY_ADJUSTED` +
          `&symbol=${encodeURIComponent(ticker)}&outputsize=${outputsize}&apikey=${CONFIG.alphaVantageKey}`;
        const resp = await fetch(url);
        const data = await resp.json();
        const series = data["Time Series (Daily)"];
        if (!series) return null;
        const dates = Object.keys(series).sort().reverse();
        const target = dates.find(d => d <= dateStr);
        if (target) {
          const price = parseFloat(series[target]["5. adjusted close"]);
          if (price > 0) { setCache(cacheKey, price); return price; }
        }
      } catch {}
      return null;
    };

    // For each listed holding, fetch historical prices at all 3 period starts in parallel
    // Skip value-only classes (Cash/FD/Real Estate/PMS/etc) — they have no ticker to fetch.
    const fetchable = hw.filter(h => h.listed && h.ticker && h.ticker !== "-" && !isValueOnlyClass(h.class) && !h.isMutualFund);
    const periodKeys = Object.keys(periods);

    // Fetch all combinations: fetchable.length × 3 calls, staggered
    const allFetches = [];
    fetchable.forEach((h, hi) => {
      periodKeys.forEach((pk, pi) => {
        allFetches.push({ h, pk, dateStr: periods[pk].from, delay: (hi * 3 + pi) * 80 });
      });
    });

    const histMap = {}; // { holdingId: { ytd: price, oneY: price, threeY: price } }
    await Promise.allSettled(allFetches.map(async ({ h, pk, dateStr, delay }) => {
      await new Promise(r => setTimeout(r, delay));
      const price = await fetchHistForDate(h.ticker, dateStr);
      if (price) {
        if (!histMap[h.id]) histMap[h.id] = {};
        histMap[h.id][pk] = price;
      }
    }));

    // MF holdings: fetch historical NAV from mfapi.in (no rate limit)
    const mfHoldings = hw.filter(h => h.isMutualFund && h.schemeCode && !isValueOnlyClass(h.class));
    await Promise.allSettled(mfHoldings.flatMap(h =>
      periodKeys.map(async pk => {
        const nav = await PriceDataProvider.getMFHistoricalNav(h.schemeCode, periods[pk].from);
        if (nav && nav > 0) {
          if (!histMap[h.id]) histMap[h.id] = {};
          histMap[h.id][pk] = nav;
        }
      })
    ));

    // Compute TWRR for each period
    const computeTWRR = (fromDateStr, periodKey) => {
      const fromTime = new Date(fromDateStr).getTime();
      const yrs = periods[periodKey].years;

      // Build start value
      let startVal = 0;
      hw.forEach(h => {
        const preTxns = transactions
          .filter(t => t.holding===h.name && new Date(t.date).getTime() < fromTime)
          .sort((a,b) => new Date(a.date)-new Date(b.date));
        let qty=0;
        preTxns.forEach(t => { if(t.type==="BUY") qty+=t.qty; else qty=Math.max(0,qty-t.qty); });
        const qtyAtFrom = qty > 0 ? qty : h.qty;
        const histPrice = histMap[h.id]?.[periodKey];
        const lastPrePrice = preTxns.length > 0 ? [...preTxns].reverse()[0].price : null;
        const priceAtFrom = histPrice || lastPrePrice || h.costPrice;
        startVal += toUSD(qtyAtFrom * priceAtFrom, h.currency);
      });
      if (startVal <= 0) return null;

      // Transaction dates in period
      const txDates = [...new Set(
        transactions.filter(t => { const tx=new Date(t.date).getTime(); return tx>=fromTime && tx<=todayMs; }).map(t=>t.date)
      )].sort();

      const currentVal = hw.reduce((s,h) => s + toUSD(h.qty*h.currentPrice, h.currency), 0);

      const valAt = (dateStr, excl=false) => {
        const dt = new Date(dateStr).getTime();
        let total = 0;
        hw.forEach(h => {
          const txns = transactions
            .filter(t => t.holding===h.name && (excl ? new Date(t.date).getTime()<dt : new Date(t.date).getTime()<=dt))
            .sort((a,b) => new Date(a.date)-new Date(b.date));
          let qty=0; txns.forEach(t => { if(t.type==="BUY") qty+=t.qty; else qty=Math.max(0,qty-t.qty); });
          const lastTxn = [...txns].reverse()[0];
          const price = lastTxn ? lastTxn.price : (histMap[h.id]?.[periodKey] || h.costPrice);
          total += toUSD(qty * price, h.currency);
        });
        return total;
      };

      let twrrProduct = 1, prevVal = startVal;
      txDates.forEach(d => {
        const before = valAt(d, true);
        if (before > 1 && prevVal > 1) { const r=(before-prevVal)/prevVal; if(isFinite(r)&&Math.abs(r)<10) twrrProduct*=(1+r); }
        const after = valAt(d, false);
        if (after > 0) prevVal = after;
      });
      if (prevVal > 1 && currentVal > 1) {
        const r = (currentVal-prevVal)/prevVal;
        if (isFinite(r) && Math.abs(r)<10) twrrProduct *= (1+r);
      }
      const twrr = (twrrProduct-1)*100;
      if (!isFinite(twrr)) return null;
      return yrs > 1.1 ? (Math.pow(1+twrr/100, 1/yrs)-1)*100 : twrr;
    };

    setPeriodReturns({
      ytd:    computeTWRR(periods.ytd.from,    "ytd"),
      oneY:   computeTWRR(periods.oneY.from,   "oneY"),
      threeY: computeTWRR(periods.threeY.from, "threeY"),
    });
  }, [filteredHW, transactions]); // eslint-disable-line

  useEffect(() => { computePeriodReturns(); }, [computePeriodReturns]);

  // ── Auto-fetch news when News tab is first opened ──
  useEffect(() => {
    if (tab === "news" && newsItems.length === 0 && !newsLoading) {
      fetchNews(newsFilter);
    }
  }, [tab, fetchNews]); // eslint-disable-line

  // ── IRR ──
  const irrData = (() => { try {
    // Normalize fund name for fuzzy matching:
    // strips "Regular", "Direct", "Plan", "Growth", dashes, extra spaces
    const normName = (s) => (s||"").toLowerCase()
      .replace(/[-]/g," ")
      .replace(/(regular|direct|plan|growth|dividend|reinvest|payout|option|fund|scheme)/gi," ")
      .replace(/\s+/g," ").trim();

    // Build a lookup map from normalized holding name → holding
    const holdingNormMap = {};
    holdings.forEach(h => {
      holdingNormMap[normName(h.name)] = h;
    });

    // Find best matching holding for a transaction holding name
    const findHolding = (txnName) => {
      // 1. Exact match
      const exact = holdings.find(h => h.name === txnName);
      if (exact) return exact;
      // 2. Normalized match
      const norm = normName(txnName);
      if (holdingNormMap[norm]) return holdingNormMap[norm];
      // 3. Partial match — holding name contains key words of txn name
      const words = norm.split(" ").filter(w => w.length > 3);
      if (words.length >= 2) {
        const partial = holdings.find(h => {
          const hn = normName(h.name);
          return words.filter(w => hn.includes(w)).length >= Math.ceil(words.length * 0.6);
        });
        if (partial) return partial;
      }
      return null;
    };

    const groups = {};
    transactions.forEach(t => {
      const key = t.holding;
      if (!groups[key]) groups[key] = [];
      const yr = (new Date().getFullYear() - new Date(t.date).getFullYear()) +
                 (new Date().getMonth() - new Date(t.date).getMonth()) / 12;
      const amtUSD = toUSD((t.qty * t.price) + (t.fees || 0), t.currency || "INR");
      const isBuy  = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
      const isSell = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
      if (isBuy || isSell) {
        groups[key].push([yr, (isBuy ? -1 : 1) * amtUSD]);
      }
    });

    return Object.entries(groups).map(([name, cfs]) => {
      const h = findHolding(name);
      const cv = h ? toUSD(h.qty * h.currentPrice, h.currency) : 0;
      if (cv > 0) cfs.push([0, cv]);
      let irr;
      try { irr = calcIRR(cfs.sort((a, b) => b[0] - a[0])); } catch { irr = null; }
      return {
        name,
        matchedHolding: h?.name || null,
        irr,
        totalInv: -cfs.filter(c => c[1] < 0).reduce((s, c) => s + c[1], 0),
        cv,
      };
    });
  } catch(e) { console.error('[irrData]', e.message); return []; }
  })();

  // Sparkline helper — three-tier source chain:
  //   1. Real price_snapshots from Supabase (USD-converted, oldest→newest)
  //   2. Transaction cost-curve (existing buildSparkFromTxns)
  //   3. Deterministic pseudo-random (existing fallback in mockSpark)
  // Called from JSX at PerfTable in Performance tab.
  function getSparkSeries(holdingId, gainPct=0, len=16) {
    const rows = priceSnapshots[holdingId];
    if (Array.isArray(rows) && rows.length >= 2) {
      // rows are newest-first from fetchPriceSnapshots; reverse to chronological
      const chrono = [...rows].reverse();
      // Convert each row to USD (so all sparklines are comparable across currencies)
      const h = holdings.find(x => x.id === holdingId);
      const cur = h?.currency || rows[0].currency || "USD";
      const toUSD = (price, c) => price / (fxRates[c] || FX_RATES_DEFAULT[c] || 1);
      const usd = chrono.map(r => toUSD(r.price, r.currency || cur)).filter(v => v > 0 && isFinite(v));
      if (usd.length >= 2) {
        // Downsample/interpolate to `len` points so the SVG width stays consistent
        if (usd.length === len) return usd;
        if (usd.length > len) {
          // Even-spaced sample
          const step = (usd.length - 1) / (len - 1);
          return Array.from({length: len}, (_, i) => usd[Math.round(i * step)]);
        }
        // Linear interpolation up to len
        const step = (usd.length - 1) / (len - 1);
        return Array.from({length: len}, (_, i) => {
          const pos = i * step, lo = Math.floor(pos), hi = Math.min(lo + 1, usd.length - 1);
          return usd[lo] + (usd[hi] - usd[lo]) * (pos - lo);
        });
      }
    }
    // Tier 2 + 3: existing mockSpark handles txn → random fallback.
    // Pass holdingId (not holdingId*2 — that was a latent bug that always missed)
    return mockSpark(holdingId, gainPct, len);
  }

  // Sparkline: uses real transaction data when available, deterministic fallback otherwise
  function mockSpark(seed, gainPct=0, len=16) {
    const result = buildSparkFromTxns(seed, gainPct, len);
    // Ensure result is always a valid numeric array of length len
    if (!Array.isArray(result) || result.length < 2) {
      return Array.from({length:len},(_,i)=>100+(gainPct/100)*i*2);
    }
    return result.map(v => isFinite(v) ? v : 100);
  }
  function buildSparkFromTxns(holdingId, gainPct=0, len=16) {
    // Find holding name from id
    const h = holdings.find(x=>x.id===holdingId);
    if (h) {
      const hTxns = transactions.filter(t=>t.holding===h.name).sort((a,b)=>new Date(a.date)-new Date(b.date));
      if (hTxns.length >= 2) {
        // Build cumulative cost curve normalised to 100
        let cumCost = 0, cumQty = 0;
        const points = hTxns.map(t=>{
          if (t.type==="BUY")  { cumCost += t.qty*t.price+(t.fees||0); cumQty += t.qty; }
          if (t.type==="SELL") { cumCost -= t.qty*t.price; cumQty -= t.qty; }
          return cumCost > 0 ? cumCost : 0;
        });
        const base = points[0] || 1;
        const norm = points.map(p=>p/base*100);
        // Pad/trim to len points
        if (norm.length >= len) return norm.slice(-len);
        // Interpolate to fill len points
        const step = (norm.length-1)/(len-1);
        return Array.from({length:len},(_,i)=>{
          const pos = i*step; const lo=Math.floor(pos); const hi=Math.min(lo+1,norm.length-1);
          return norm[lo]+(norm[hi]-norm[lo])*(pos-lo);
        });
      }
    }
    // Fallback: deterministic pseudo-random trend
    // Use numeric seed — coerce string IDs to a numeric hash ──
    const seedNum = typeof holdingId === 'number' ? holdingId : String(holdingId||'x').split('').reduce((a,c)=>((a<<5)-a)+c.charCodeAt(0),0);
    let s = (Math.abs(Math.round(seedNum * 2654435761)) | 1) || 1;
    const rand = () => { s ^= s << 13; s ^= (s >>> 17); s ^= s << 5; return (s >>> 0) / 4294967296; };
    const trendPerStep = (gainPct / 100) * 18 / (len - 1);
    let v = 100;
    return Array.from({ length: len }, () => { v += trendPerStep + (rand() - 0.5) * 2.8; return v; });
  }

  // ── Upload handler ──
  // Robust CSV parser — handles quoted fields, commas inside quotes, CRLF
  const parseCSVLine = (line) => {
    const cols = []; let cur = ""; let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i+1] === '"') { cur += '"'; i++; } // escaped quote
        else inQuote = !inQuote;
      } else if (ch === "," && !inQuote) {
        cols.push(cur.trim()); cur = "";
      } else {
        cur += ch;
      }
    }
    cols.push(cur.trim());
    return cols;
  };

  // Normalise header names: lowercase, strip spaces/underscores/slashes
  const normHeader = (h) => (h||"").toLowerCase().replace(/[\s_\/\-]+/g,"");

  const handleUpload = (e, target) => {
    const file = e.target.files[0]; if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = "";

    const isXLSX = /\.(xlsx|xls)$/i.test(file.name);

    const proceedWithText = (text) => {
      try {
        text = text.replace(/\r\n/g,"\n").replace(/\r/g,"\n");
        return processCsvText(text, target, file);
      } catch(err) { showToast("❌ Error parsing file: " + err.message, "#A32D2D"); }
    };

    if (isXLSX) {
      // Load XLSX library if not already loaded, then convert sheet to CSV text
      const convertXlsxToCsv = async () => {
        try {
          if (!window.XLSX) {
            await new Promise((res, rej) => {
              const s = document.createElement("script");
              s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
              s.onload = res; s.onerror = rej;
              document.head.appendChild(s);
            });
          }
          const buf = await file.arrayBuffer();
          const wb = window.XLSX.read(buf, { type: "array", cellDates: false });
          const ws = wb.Sheets[wb.SheetNames[0]];
          if (!ws) { showToast("❌ XLSX has no sheets.", "#A32D2D"); return; }
          // sheet_to_csv handles quoting + escaping properly, gives us a string
          // that the existing CSV parser can consume.
          const csv = window.XLSX.utils.sheet_to_csv(ws, { blankrows: false });
          if (!csv || !csv.trim()) { showToast("❌ XLSX appears empty.", "#A32D2D"); return; }
          proceedWithText(csv);
        } catch(err) {
          console.error("[handleUpload] XLSX parse error:", err);
          showToast("❌ Could not read XLSX: " + err.message, "#A32D2D");
        }
      };
      convertXlsxToCsv();
      return;
    }

    // CSV path
    const reader = new FileReader();
    reader.onload = evt => proceedWithText(evt.target.result);
    reader.readAsText(file);
  };

  // Existing CSV/text-parsing path, now reusable for both CSV uploads and
  // XLSX uploads (after the XLSX → CSV conversion).
  const processCsvText = (text, target, file) => {
    {
      try {

        // ── IBKR Transaction History CSV detection ──────────────────────────
        // IBKR exports have a multi-section format with metadata rows before
        // the actual data. We detect this by looking for the header row marker
        // "Transaction History,Header," and pre-process the file into a flat
        // CSV that the generic parser below can handle.
        if (target === "transactions" && text.includes("Transaction History,Header,")) {
          const lines = text.split("\n");

          // Find the header row: "Transaction History,Header,Date,Account,..."
          const hdrLineIdx = lines.findIndex(l => l.startsWith("Transaction History,Header,"));
          if (hdrLineIdx === -1) {
            showToast("❌ IBKR file detected but header row not found.", "#A32D2D"); return;
          }

          // Parse the header columns (strip the first two section/type cols)
          const hdrCols = parseCSVLine(lines[hdrLineIdx]).slice(2);
          // hdrCols: ["Date","Account","Description","Transaction Type","Symbol",
          //           "Quantity","Price","Price Currency","Gross Amount","Commission","Net Amount"]

          // Helper: convert M/D/YYYY → YYYY-MM-DD
          const toISO = d => {
            if (!d || d === "-") return new Date().toISOString().slice(0,10);
            const parts = d.split("/");
            if (parts.length === 3) {
              const [m,day,y] = parts;
              return `${y}-${m.padStart(2,"0")}-${day.padStart(2,"0")}`;
            }
            return d; // already ISO or unknown — pass through
          };

          // Helper: safely parse numeric fields — handles "-", "(50)", "1,250.50", 1.00E-04
          const num = v => {
            if (!v || v === "-" || v.trim() === "") return 0;
            const s = String(v).replace(/,/g,"").replace(/\(([^)]+)\)/,"$1");
            return Math.abs(parseFloat(s)) || 0;
          };

          // Find column indices by name in hdrCols
          const ci = name => hdrCols.findIndex(h => h.trim().toLowerCase() === name.toLowerCase());
          const iDate    = ci("Date");
          const iType    = ci("Transaction Type");
          const iSymbol  = ci("Symbol");
          const iDesc    = ci("Description");
          const iQty     = ci("Quantity");
          const iPrice   = ci("Price");
          const iPriceCcy= ci("Price Currency");
          const iComm    = ci("Commission");

          // Extract only data rows, filter to Buy/Sell only
          const TRADE_TYPES = new Set(["buy","sell"]);
          const dataLines = lines
            .filter(l => l.startsWith("Transaction History,Data,"))
            .map(l => parseCSVLine(l).slice(2)); // strip section + "Data" prefix

          const tradeRows = dataLines.filter(r => {
            const txType = (r[iType] || "").toLowerCase().trim();
            return TRADE_TYPES.has(txType);
          });

          if (!tradeRows.length) {
            showToast("❌ IBKR file parsed but no Buy/Sell rows found. Check the file contains trade data.", "#A32D2D");
            return;
          }

          // Map to Forthius transactions schema
          const ibkrParsed = tradeRows.map((r, i) => {
            const txType = (r[iType] || "").toLowerCase().trim();
            const sym    = (r[iSymbol] || "").trim();
            const desc   = (r[iDesc]   || "").trim();
            return {
              id:       Date.now() + i,
              date:     toISO(r[iDate]),
              holding:  sym || desc || "Unknown",
              type:     txType === "sell" ? "SELL" : "BUY",
              qty:      num(r[iQty]),
              price:    num(r[iPrice]),
              currency: (r[iPriceCcy] || "USD").trim(),
              fees:     num(r[iComm]),
              _ibkrDesc: desc, // keep description for preview, stripped before save
            };
          }).filter(r => r.qty > 0 && r.holding);

          if (!ibkrParsed.length) {
            showToast("❌ No valid trade rows after parsing. Some rows may have had missing qty or symbol.", "#A32D2D");
            return;
          }

          // Surface a preview — same path as regular CSV
          const skipped = tradeRows.length - ibkrParsed.length;
          setCsvPreview({
            rows: ibkrParsed,
            target,
            errors: skipped > 0 ? [`${skipped} row(s) skipped (zero quantity or missing symbol)`] : [],
            filename: file.name,
            ibkr: true,
            ibkrTotal: dataLines.length,
            ibkrSkipped: dataLines.length - tradeRows.length,
          });
          return; // done — don't fall through to generic parser
        }
        // ── End IBKR detection ──────────────────────────────────────────────

        const lines = text.trim().split("\n").filter(l=>l.trim());
        if (lines.length < 2) { showToast("❌ File appears empty or has no data rows.","#A32D2D"); return; }

        // Parse headers
        const rawHeaders = parseCSVLine(lines[0]);
        const headers = rawHeaders.map(normHeader);

        // Helper: get column value by possible header names
        const col = (row, ...names) => {
          for (const n of names) {
            const idx = headers.indexOf(normHeader(n));
            if (idx > -1 && row[idx] !== undefined) return row[idx];
          }
          return "";
        };

        const dataRows = lines.slice(1).map(l => parseCSVLine(l));
        const errors = [];
        let parsed = [];

        if (target === "holdings") {
          parsed = dataRows.map((c, i) => {
            const name = col(c,"Name","HoldingName","Company","Fund");
            const qty  = parseFloat(col(c,"Qty","Quantity","Units","Shares","Amount")) || 0;
            const ccy  = col(c,"Currency","Ccy") || "USD";
            if (!name) { errors.push(`Row ${i+2}: missing Name`); return null; }
            if (!qty)  { errors.push(`Row ${i+2}: ${name} has zero quantity`); return null; }
            return {
              id: Date.now()+i,
              name,
              ticker:       col(c,"Ticker","Symbol","Code") || "-",
              class:        col(c,"Class","AssetClass","Category") || "Equities",
              exchange:     col(c,"Exchange","Market") || "-",
              currency:     ccy,
              qty,
              costPrice:    parseFloat(col(c,"CostPrice","Cost","AvgCost","PurchasePrice","NAV","CostNAV","PurchaseNAV")) || 0,
              currentPrice: parseFloat(col(c,"CurrentPrice","Price","MarketPrice","CurrentNAV","LastPrice")) || 0,
              country:      col(c,"Country","Domicile") || "-",
              listed:       col(c,"Listed","IsListed","Type").toLowerCase() !== "false",
              isin:         col(c,"ISIN","ISINCode") || "-",
              manager:      col(c,"Manager","Custodian","Manager/Custodian","AMC","Broker") || "Self-Managed",
              notes:        col(c,"Notes","Note","Comments","Remarks") || "",
              manualPriceDate: null,
            };
          }).filter(Boolean);

          if (!parsed.length) {
            showToast("❌ No valid rows found — check column headers match the sample CSV.", "#A32D2D");
            return;
          }
          setCsvPreview({ rows: parsed, target, errors, filename: file.name });

        } else if (target === "transactions") {
          // Robust date normalizer — handles common formats from broker CSVs
          // Accepts: YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, MM/DD/YYYY (heuristic),
          //          DD-Mon-YYYY (e.g. 15-Jan-2026), and Excel serial numbers.
          const MONTHS = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
          const normalizeDate = (raw) => {
            if (!raw) return new Date().toISOString().slice(0,10);
            const s = String(raw).trim();
            // Already ISO?
            if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0,10);
            // Excel serial number (days since 1900-01-01)
            const num = parseFloat(s);
            if (!isNaN(num) && num > 25000 && num < 60000 && !s.includes("/") && !s.includes("-")) {
              const d = new Date((num - 25569) * 86400 * 1000);
              return d.toISOString().slice(0,10);
            }
            // DD-Mon-YYYY (e.g. "15-Jan-2026", "1 Jan 2026")
            const monMatch = s.match(/^(\d{1,2})[\s\-\/]([A-Za-z]{3,})[\s\-\/](\d{2,4})$/);
            if (monMatch) {
              const d = +monMatch[1], m = MONTHS[monMatch[2].slice(0,3).toLowerCase()], y = +monMatch[3];
              if (m != null && y > 0) {
                const yy = y < 100 ? (y > 50 ? 1900+y : 2000+y) : y;
                return `${yy}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
              }
            }
            // DD-MM-YYYY or DD/MM/YYYY (Indian/European default)
            const dmy = s.match(/^(\d{1,2})[\-\/](\d{1,2})[\-\/](\d{2,4})$/);
            if (dmy) {
              const a = +dmy[1], b = +dmy[2], y = +dmy[3];
              const yy = y < 100 ? (y > 50 ? 1900+y : 2000+y) : y;
              // If first part > 12, definitely DD-MM. If second > 12, must be MM-DD.
              // Default: assume DD-MM-YYYY (Indian convention for this app).
              let dd = a, mm = b;
              if (a > 12 && b <= 12) { dd = a; mm = b; }
              else if (a <= 12 && b > 12) { dd = b; mm = a; }
              return `${yy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
            }
            // Last resort — let Date constructor try, then convert
            const d = new Date(s);
            if (!isNaN(d.getTime())) return d.toISOString().slice(0,10);
            return new Date().toISOString().slice(0,10);
          };
          parsed = dataRows.map((c,i) => {
            const holding = col(c,"Holding","Name","Security","Stock","Fund");
            const qty     = parseFloat(col(c,"Qty","Quantity","Units","Shares")) || 0;
            if (!holding || !qty) return null;
            return {
              id:       `txn_${Date.now()}_${i}`,  // string id (matches schema TEXT PRIMARY KEY)
              date:     normalizeDate(col(c,"Date","TradeDate","TxnDate")),
              holding,
              type:     (col(c,"Type","TxnType","TransactionType","Action")||"BUY").toUpperCase(),
              qty, price: parseFloat(col(c,"Price","Rate","NAV","Cost")) || 0,
              currency: col(c,"Currency","Ccy") || "USD",
              fees:     parseFloat(col(c,"Fees","Commission","Charges","Brokerage")) || 0,
            };
          }).filter(Boolean);
          if (!parsed.length) { showToast("❌ No valid transaction rows found.", "#A32D2D"); return; }
          setCsvPreview({ rows: parsed, target, errors, filename: file.name });

        } else if (target === "mf") {
          parsed = dataRows.map((c,i) => {
            const name = col(c,"SchemeName","FundName","Name");
            const qty  = parseFloat(col(c,"Units","Qty","Quantity")) || 0;
            if (!name || !qty) return null;
            return {
              id: Date.now()+i, name, ticker:"-",
              class:"Indian Mutual Funds", exchange:"AMFI", currency:"INR",
              qty, costPrice: parseFloat(col(c,"PurchaseNAV","CostNAV","Cost","NAV")) || 0,
              currentPrice:  parseFloat(col(c,"CurrentNAV","Price","NAV")) || 0,
              country:"India", listed:false,
              isin:      col(c,"ISIN","ISINCode") || "-",
              schemeCode:col(c,"SchemeCode","Code") || "",
              isMutualFund:true,
              manager:   col(c,"Manager","Custodian","AMC","Manager/Custodian") || "Self-Managed",
              manualPriceDate: null,
            };
          }).filter(Boolean);
          if (!parsed.length) { showToast("❌ No valid MF rows found.", "#A32D2D"); return; }
          setCsvPreview({ rows: parsed, target, errors, filename: file.name });

        } else if (target === "globalmf") {
          parsed = dataRows.map((c,i) => {
            const name = col(c,"FundName","SchemeName","Name");
            const qty  = parseFloat(col(c,"Units","Qty","Quantity")) || 0;
            if (!name || !qty) return null;
            return {
              id: Date.now()+i, name, ticker:"-",
              class:"Global Mutual Funds",
              exchange: col(c,"Domicile","Exchange") || "UCITS",
              currency: col(c,"Currency","Ccy") || "USD",
              qty, costPrice: parseFloat(col(c,"CostNAV","PurchaseNAV","Cost","NAV")) || 0,
              currentPrice:  parseFloat(col(c,"CurrentNAV","Price","NAV")) || 0,
              country: col(c,"Domicile","Country") || "-",
              listed:false,
              isin: col(c,"ISIN") || "-",
              isMutualFund:true,
              manager: col(c,"Manager","Custodian","AMC","Manager/Custodian") || "Self-Managed",
              manualPriceDate: null,
            };
          }).filter(Boolean);
          if (!parsed.length) { showToast("❌ No valid rows found.", "#A32D2D"); return; }
          setCsvPreview({ rows: parsed, target, errors, filename: file.name });
        }
      } catch(err) { showToast("❌ Error parsing file: " + err.message, "#A32D2D"); }
    }
  };

  // Google Sheets import
  const handleGSheetImport = async (urlStr, target) => {
    try {
      const resp = await fetch(urlStr);
      if (!resp.ok) throw new Error("Could not fetch sheet");
      const text = await resp.text();
      const fakeEvt = { target:{ result: text } };
      // Reuse CSV handler logic inline
      const rows = text.trim().split("\n").slice(1);
      showToast(`✓ Fetched ${rows.length} rows from Google Sheets`);
    } catch(e) { showToast("❌ Could not import Google Sheet: " + e.message,"#A32D2D"); }
  };

  // ── Manual price update for unlisted assets ──
  function saveManualPrice(id) {
    const p = parseFloat(editPriceVal);
    if (!p || p <= 0)  { showToast("⚠ Price must be greater than 0","#D97706"); return; }
    if (!editPriceDate){ showToast("⚠ Please set the 'as on' date for this price","#D97706"); return; }
    if (new Date(editPriceDate) > new Date()) { showToast("⚠ Price date cannot be in the future","#D97706"); return; }
    const priceDate = editPriceDate;
    updateHoldingPrice(id, p, priceDate).catch(err=>console.error('[Supabase] updateHoldingPrice:',err.message));
    setHoldings(prev => prev.map(h => h.id===id ? {...h, currentPrice:p, manualPriceDate: priceDate} : h));
    setEditPriceId(null); setEditPriceVal(""); setEditPriceDate("");
  }

  // ── Tabs (admin-gated) ──
  // Risk tab: visible to admin always; clients see it if any sub-tab is enabled
  const clientRiskTabs = loggedInClientId ? (clientLogins[loggedInClientId]?.riskTabs || {}) : {};
  const canSeeRisk = isAdmin || Object.values(clientRiskTabs).some(Boolean);
  // Subscription plan for the currently logged-in client (admin bypasses all)
  const activePlan = isAdmin ? "premium" : getEffectivePlan(loggedInClientId);
  const canAccess  = (tabId) => isAdmin || clientCanAccess(activePlan, tabId);
  // Which risk sub-tabs a client can see (admin sees all)
  const allowedRiskTabs = isAdmin
    ? { overview:true, concentration:true, stress:true, correlation:true }
    : clientRiskTabs;
  const isReadOnly = false; // clients can edit their own holdings
  // Grouped tab nav — each group is a top-level button with a dropdown
  const TAB_GROUPS = [
    {
      id: "wealth", label: "Wealth", icon: "💎", iconName: "grp_wealth",
      tabs: [
        {id:"summary",     label:"Summary",       icon:"⭐",  iconName:"summary",     planRequired:"free"},
        {id:"overview",    label:"Overview",      icon:"🏠",  iconName:"overview",    planRequired:"free"},
        {id:"holdings",    label:"Holdings",      icon:"💼",  iconName:"holdings",    planRequired:"free"},
        {id:"performance", label:"Performance",   icon:"📈",  iconName:"performance", planRequired:"free"},
        {id:"benchmark",   label:"Benchmarks",    icon:"🏁",  iconName:"benchmark",   planRequired:"pro"},
        {id:"agent",       label:"AI Advisor",    icon:"🤖",  iconName:"advisor",     planRequired:"pro"},
      ]
    },
    {
      id: "risk", label: "Risk & Exposure", icon: "⚡", iconName: "grp_risk",
      tabs: [
        ...(canSeeRisk ? [{id:"risk",      label:"Risk",            icon:"⚡",  iconName:"risk",       planRequired:"starter"}] : []),
        {id:"analytics",   label:"Volatility",    icon:"🎢",  iconName:"volatility", planRequired:"pro"},
        {id:"geo",         label:"Geo & Sector",  icon:"🌍",  iconName:"geo",        planRequired:"pro"},
        {id:"currency",    label:"FX Exposure",   icon:"💱",  iconName:"currency",   planRequired:"pro"},
        {id:"scenarios",   label:"Stress Tests",  icon:"🧪",  iconName:"scenarios",  planRequired:"starter"},
      ]
    },
    {
      id: "analysis", label: "Analysis", icon: "📐", iconName: "grp_analysis",
      tabs: [
        {id:"irr",         label:"IRR / XIRR",    icon:"📐",  iconName:"irr",      planRequired:"pro"},
        {id:"capgains",    label:"Capital Gains",  icon:"🧾",  iconName:"capgains", planRequired:"pro"},
        ...(isAdmin ? [
          {id:"research",  label:"AI Research",   icon:"🤖",  iconName:"research", planRequired:"pro"},
          {id:"shortlist", label:"Shortlist",     icon:"📋",  iconName:"bookmark", planRequired:"pro"},
        ] : []),
      ]
    },
    {
      id: "planning", label: "Planning", icon: "💡", iconName: "grp_planning",
      tabs: [
        {id:"cashflow",  label:"Cash Flow",     icon:"💸",  iconName:"cashflow", planRequired:"starter"},
        {id:"goals",     label:"Goals",         icon:"🎯",  iconName:"goals",    planRequired:"starter"},
        {id:"dividends", label:"Income",        icon:"💰",  iconName:"income",   planRequired:"starter"},
        {id:"news",      label:"News Feed",     icon:"📰",  iconName:"news",     planRequired:"starter"},
      ]
    },
    {
      id: "manage", label: "Manage", icon: "📋", iconName: "grp_manage",
      tabs: [
        {id:"transactions", label:"Transactions",    icon:"🔄",  iconName:"transactions", planRequired:"free"},
        ...(isAdmin ? [{id:"rebalance", label:"Rebalance", icon:"⚖️", iconName:"rebalance", planRequired:"premium"}] : []),
        ...(isAdmin ? [{id:"model", label:"Model Portfolio", icon:"🎯", iconName:"model", planRequired:"pro"}] : []),
        {id:"watchlist",    label:"Watchlist",       icon:"👁",  iconName:"watchlist", planRequired:"starter"},
        ...(isAdmin ? [{id:"upload", label:"Upload Data", icon:"📤", iconName:"upload", planRequired:"free"}] : []),
      ]
    },
    {
      id: "admin", label: "Admin", icon: "⚙️", iconName: "grp_admin",
      hidden: !isAdmin,
      tabs: [
        {id:"families",      label:"Families",        icon:"👨‍👩‍👧",  iconName:"families",     planRequired:"free"},
        {id:"datasources",   label:"Data Sources",    icon:"🔌",  iconName:"datasources",   planRequired:"free"},
        {id:"subscriptions", label:"Subscriptions",   icon:"💳",  iconName:"subscriptions", planRequired:"free"},
      ]
    },
  ].filter(g => !g.hidden);

  // Flat list for backward compat (mobile nav, etc)
  const TABS = TAB_GROUPS.flatMap(g => g.tabs);

  // ── Client-view guard ──
  // Non-admins can reach admin-only tabs via secondary entry points
  // (empty-state "Upload Holdings" buttons, Overview quick actions → Model, etc.)
  // Sidebar gating alone doesn't stop setTab(); bounce them back to Overview.
  useEffect(() => {
    if (!isAdmin && ["upload","model","shortlist","research","rebalance"].includes(tab)) {
      setTab("overview");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, tab]);

  // ── AI Helper Functions ──────────────────────────────────────────────────

  const fetchAiInsight = async (h) => {
    const id = h.id;
    if (aiInsights[id]?.insight || aiInsights[id]?.loading) return;
    setAiInsights(p => ({ ...p, [id]: { loading: true } }));
    try {
      const totalV = kpiHW.reduce((s, x) => s + (x.valueUSD || 0), 0);
      const portPct = totalV > 0 ? ((h.valueUSD || 0) / totalV * 100) : 0;
      const params = new URLSearchParams({
        holdingId: id, name: h.name, ticker: h.ticker || "",
        assetClass: h.class || "", gainPct: h.gainPct?.toFixed(1) || "0",
        valueUSD: (h.valueUSD || 0).toFixed(0), portfolioPct: portPct.toFixed(1),
        currency: h.currency || "USD", country: h.country || "",
        costPrice: h.costPrice || "", currentPrice: h.currentPrice || "",
        sector: resolveHoldingSector(h) || "",
        clientId: aiClientContext.clientId || "admin",
        plan: aiClientContext.plan || "premium",
      });
      const r = await fetch(`/api/ai-insights?${params}`);
      if (r.status === 403) {
        const j = await r.json();
        setAiInsights(p => ({ ...p, [id]: { error: "🔒 " + (j.error || "Upgrade required") } }));
        return;
      }
      const j = await r.json();
      if (j.insight) setAiInsights(p => ({ ...p, [id]: { insight: j.insight } }));
      else setAiInsights(p => ({ ...p, [id]: { error: j.error || "Failed" } }));
    } catch (e) {
      setAiInsights(p => ({ ...p, [id]: { error: e.message } }));
    }
  };

  const runNlQuery = async (query) => {
    if (!query.trim()) { setAiQueryDesc(null); setHoldingSearch(""); setHoldingsDrillFilter({}); return; }
    setAiQueryLoading(true); setAiQueryError(null);
    try {
      const filter = getEffectiveAiFilter("nlquery");
      const scope = applyAiFilter(filteredHW, filter);
      const classes = [...new Set(scope.map(h => h.class).filter(Boolean))];
      const countries = [...new Set(scope.map(h => h.country).filter(Boolean))];
      const custodians = [...new Set(scope.map(h => h.manager).filter(Boolean))];
      const r = await fetch("/api/ai-query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, availableClasses: classes, availableCountries: countries, availableCustodians: custodians,
          clientId: aiClientContext.clientId || "admin", plan: aiClientContext.plan || "premium",
          filterContext: describeAiFilter(filter) }),
      });
      if (r.status === 403) { const j = await r.json(); throw new Error("🔒 " + (j.error || "Upgrade required")); }
      const j = await r.json();
      if (!j.filters) throw new Error(j.error || "No filters returned");
      const f = j.filters;
      // Apply filters
      if (f.search) setHoldingSearch(f.search); else setHoldingSearch("");
      const drill = {};
      if (f.class) drill.class = f.class;
      if (f.country) drill.country = f.country;
      if (f.custodian) drill.manager = f.custodian;
      if (f.minGainPct != null) drill.minGainPct = f.minGainPct;
      if (f.maxGainPct != null) drill.maxGainPct = f.maxGainPct;
      if (Object.keys(drill).length) setHoldingsDrillFilter(drill);
      if (f.sortBy) { setHoldingsSort({ col: f.sortBy === "value" ? "value" : f.sortBy === "gainPct" ? "pct" : "name", dir: f.sortDir || "desc" }); }
      setAiQueryDesc(f.description || query);
    } catch (e) {
      setAiQueryError(e.message);
    } finally {
      setAiQueryLoading(false);
    }
  };

  const fetchAiCommentary = React.useCallback(async () => {
    if (!selectedClients.size && !loggedInClientId) return;
    const clientId = loggedInClientId || [...selectedClients][0];
    if (!clientId) return;
    setAiCommentaryLoad(true);
    try {
      const r = await fetch(`/api/ai-commentary?clientId=${clientId}`);
      const j = await r.json();
      if (j.commentary) setAiCommentary(j.commentary);
    } catch {}
    finally { setAiCommentaryLoad(false); }
  }, [selectedClients, loggedInClientId]);

  const generateAiCommentary = async () => {
    const clientId = loggedInClientId || [...selectedClients][0];
    if (!clientId) return;
    const client = visibleClients.find(c => c.id === clientId);
    setAiCommentaryLoad(true);
    try {
      const totalV = kpiHW.reduce((s, h) => s + (h.valueUSD || 0), 0);
      const totalCost = kpiHW.reduce((s, h) => s + (h.costUSD || 0), 0);
      const gainPct = totalCost > 0 ? (totalV - totalCost) / totalCost * 100 : 0;
      const byClass = {}; const byCurrency = {}; const byCountry = {};
      kpiHW.forEach(h => {
        const v = h.valueUSD || 0;
        byClass[h.class || "Other"] = ((byClass[h.class || "Other"] || 0) + v);
        byCurrency[h.currency || "USD"] = ((byCurrency[h.currency || "USD"] || 0) + v);
        byCountry[h.country || "Other"] = ((byCountry[h.country || "Other"] || 0) + v);
      });
      if (totalV > 0) {
        Object.keys(byClass).forEach(k => byClass[k] /= totalV);
        Object.keys(byCurrency).forEach(k => byCurrency[k] /= totalV);
        Object.keys(byCountry).forEach(k => byCountry[k] /= totalV);
      }
      const topHoldings = [...kpiHW].sort((a,b)=>(b.valueUSD||0)-(a.valueUSD||0)).slice(0,5)
        .map(h => ({ name: h.name, pct: totalV > 0 ? h.valueUSD/totalV*100 : 0, gainPct: h.gainPct }));
      const r = await fetch("/api/ai-commentary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId, clientName: client?.name || "Client",
          clientEmail: client?.email || null,
          portfolio: { totalValueUSD: totalV, totalCostUSD: totalCost, gainPct, topHoldings, byClass, byCurrency, byCountry, moversUp: [], moversDown: [], niftyReturn: 0, sp500Return: 0 },
        }),
      });
      const j = await r.json();
      if (j.commentary) setAiCommentary(j.commentary);
    } catch {}
    finally { setAiCommentaryLoad(false); }
  };

  const runAiAnomaly = async () => {
    if (aiAnomalyLoad) return;
    setAiAnomalyLoad(true);
    setAiErrors(p=>({...p,anomaly:null}));
    try {
      const filter = getEffectiveAiFilter("anomaly");
      const filtered = applyAiFilter(kpiHW, filter);
      if (filtered.length === 0) {
        setAiErrors(p=>({...p,anomaly:`No holdings match current filter (${describeAiFilter(filter)}). Clear or adjust the filter to run this scan.`}));
        return;
      }
      const payload = { holdings: filtered.slice(0,50).map(h=>({
        id:h.id, name:h.name, class:h.class, listed:h.listed,
        costPrice:h.costPrice, currentPrice:h.currentPrice,
        manualPriceDate:h.manualPriceDate, currency:h.currency,
        country:h.country, gainPct:h.gainPct, valueUSD:h.valueUSD,
      })), filterContext: describeAiFilter(filter) };
      const r = await fetch("/api/ai-anomaly", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({...payload,...aiClientContext}),
      });
      const text = await r.text();
      let j;
      try { j = JSON.parse(text); } catch { throw new Error(`Bad response (${r.status}): ${text.slice(0,200)}`); }
      if (r.status === 403) { setAiErrors(p=>({...p,anomaly:"🔒 " + (j.error||"Upgrade your plan to access this feature")})); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.anomalies) { setAiAnomalies(j.anomalies); setExpandedAICard("anomaly"); }
      else throw new Error("No anomalies field in response: " + JSON.stringify(j).slice(0,200));
    } catch(e) { setAiErrors(p=>({...p,anomaly:e.message})); }
    finally { setAiAnomalyLoad(false); }
  };

  const runAiTaxloss = async () => {
    if (aiTaxlossLoad) return;
    setAiTaxlossLoad(true);
    setAiErrors(p=>({...p,taxloss:null}));
    try {
      const filter = getEffectiveAiFilter("taxloss");
      const filtered = applyAiFilter(kpiHW, filter);
      if (filtered.length === 0) {
        setAiErrors(p=>({...p,taxloss:`No holdings match current filter (${describeAiFilter(filter)}).`}));
        return;
      }
      const totalGain = filtered.reduce((s,h) => s + Math.max(0, (h.valueUSD||0)-(h.costUSD||0)), 0);
      const payload = {
        holdings: filtered.map(h=>({ id:h.id, name:h.name, class:h.class, gainPct:h.gainPct,
          valueUSD:h.valueUSD, costUSD:h.costUSD, currency:h.currency, country:h.country, purchaseDate:h.purchaseDate })),
        totalGainUSD: totalGain, currency: dc, filterContext: describeAiFilter(filter),
      };
      const r = await fetch("/api/ai-taxloss", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({...payload,...aiClientContext}) });
      const text = await r.text();
      let j; try { j = JSON.parse(text); } catch { throw new Error(`Bad response (${r.status}): ${text.slice(0,200)}`); }
      if (r.status === 403) { setAiErrors(p=>({...p,taxloss:"🔒 " + (j.error||"Upgrade your plan to access this feature")})); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.taxloss) { setAiTaxloss(j.taxloss); setExpandedAICard("taxloss"); }
      else throw new Error("No taxloss field: " + JSON.stringify(j).slice(0,200));
    } catch(e) { setAiErrors(p=>({...p,taxloss:e.message})); }
    finally { setAiTaxlossLoad(false); }
  };

  const runAiRebalance = async () => {
    if (aiRebalanceLoad) return;
    setAiRebalanceLoad(true);
    setAiErrors(p=>({...p,rebalance:null}));
    try {
      const filter = getEffectiveAiFilter("rebalance");
      const filtered = applyAiFilter(kpiHW, filter);
      if (filtered.length === 0) {
        setAiErrors(p=>({...p,rebalance:`No holdings match current filter (${describeAiFilter(filter)}).`}));
        return;
      }
      const totalV = filtered.reduce((s,h) => s+(h.valueUSD||0), 0);
      const payload = {
        holdings: filtered.map(h=>({ id:h.id, name:h.name, class:h.class, valueUSD:h.valueUSD, costUSD:h.costUSD, gainPct:h.gainPct, currency:h.currency })),
        totalValueUSD: totalV, currency: dc, filterContext: describeAiFilter(filter),
      };
      const r = await fetch("/api/ai-rebalance", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({...payload,...aiClientContext}) });
      const text = await r.text();
      let j; try { j = JSON.parse(text); } catch { throw new Error(`Bad response (${r.status}): ${text.slice(0,200)}`); }
      if (r.status === 403) { setAiErrors(p=>({...p,rebalance:"🔒 " + (j.error||"Upgrade your plan to access this feature")})); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.rebalance) { setAiRebalance(j.rebalance); setExpandedAICard("rebalance"); }
      else throw new Error("No rebalance field: " + JSON.stringify(j).slice(0,200));
    } catch(e) { setAiErrors(p=>({...p,rebalance:e.message})); }
    finally { setAiRebalanceLoad(false); }
  };

  const runAiBriefing = async () => {
    if (aiBriefingLoad) return;
    const clientId = loggedInClientId || [...selectedClients][0];
    const client = visibleClients.find(c => c.id === clientId) || { name: "Client" };
    setAiBriefingLoad(true);
    setAiErrors(p=>({...p,briefing:null}));
    try {
      const filter = getEffectiveAiFilter("briefing");
      const filtered = applyAiFilter(kpiHW, filter);
      if (filtered.length === 0) {
        setAiErrors(p=>({...p,briefing:`No holdings match current filter (${describeAiFilter(filter)}).`}));
        return;
      }
      const totalV = filtered.reduce((s,h) => s+(h.valueUSD||0), 0);
      const payload = {
        clientId: clientId || "admin",
        clientName: client?.name || "Client",
        holdings: filtered.map(h=>({ id:h.id, name:h.name, class:h.class, valueUSD:h.valueUSD, costUSD:h.costUSD, gainPct:h.gainPct, currency:h.currency, country:h.country })),
        totalValueUSD: totalV, currency: dc, performance: {}, filterContext: describeAiFilter(filter),
      };
      const r = await fetch("/api/ai-briefing", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({...payload,...aiClientContext}) });
      const text = await r.text();
      let j; try { j = JSON.parse(text); } catch { throw new Error(`Bad response (${r.status}): ${text.slice(0,200)}`); }
      if (r.status === 403) { setAiErrors(p=>({...p,briefing:"🔒 " + (j.error||"Upgrade your plan to access this feature")})); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.briefing) { setAiBriefing(j.briefing); setExpandedAICard("briefing"); }
      else throw new Error("No briefing field: " + JSON.stringify(j).slice(0,200));
    } catch(e) { setAiErrors(p=>({...p,briefing:e.message})); }
    finally { setAiBriefingLoad(false); }
  };

  const runAiTest = async () => {
    setAiTestLoad(true); setAiTestResult(null);
    try {
      const r = await fetch("/api/ai-anomaly", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ holdings:[{id:"test",name:"Test Holding",class:"Equities",listed:true,costPrice:100,currentPrice:120,currency:"USD",country:"India",valueUSD:120,gainPct:20}] })
      });
      const text = await r.text();
      setAiTestResult(`HTTP ${r.status}: ${text.slice(0,500)}`);
    } catch(e) { setAiTestResult("Network error: " + e.message); }
    finally { setAiTestLoad(false); }
  };

  // Auto-load AI commentary and anomaly scan when overview opens
  React.useEffect(() => {
    if (tab === "overview" && kpiHW.length > 0) {
      fetchAiCommentary();
      if (!aiAnomalies && !aiAnomalyLoad) runAiAnomaly();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, kpiHW.length]);

  // Which group contains the active tab
  const activeGroup = TAB_GROUPS.find(g => g.tabs.some(t => t.id === tab));

  const bmCurrent = benchMetrics[bench] || BENCHMARK_FALLBACK[bench];

  // ── PDF REPORT GENERATION ──
  const generatePPT = async () => {
    // Dynamically load PptxGenJS from CDN
    if (!window.PptxGenJS) {
      await new Promise((res, rej) => {
        const s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js";
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    const PptxGenJS = window.PptxGenJS;

    // ── Scope data ──────────────────────────────────────────────
    let repHoldings = hwWithOwner;
    let repName = "All Portfolios";
    let repSubtitle = "Full Portfolio Report";
    if (pdfScope === "family" && pdfFamilyId) {
      const fam = families.find(f => f.id === pdfFamilyId);
      const ids = clients.filter(c => c.familyId === pdfFamilyId).map(c => c.id);
      repHoldings = hwWithOwner.filter(h => ids.includes(h.clientId));
      repName = fam ? `${fam.emoji} ${fam.name}` : "Family Portfolio";
      repSubtitle = `Family Portfolio Report`;
    } else if (pdfScope === "individual" && pdfClientId) {
      const cli = clients.find(c => c.id === pdfClientId);
      repHoldings = hwWithOwner.filter(h => h.clientId === pdfClientId);
      repName = cli?.name || "Client";
      repSubtitle = `Individual Portfolio Report`;
    }

    const totalVal  = repHoldings.reduce((s,h) => s + h.valueUSD, 0);
    const totalCost = repHoldings.reduce((s,h) => s + h.costUSD,  0);
    const gainUSD   = totalVal - totalCost;
    const gainPct   = totalCost > 0 ? (gainUSD / totalCost * 100) : 0;
    const fmtV = v => fmt(convert(v, dc), dc, true);
    const pctStr = v => (v >= 0 ? "+" : "") + v.toFixed(1) + "%";

    // Breakdowns
    const byClass = {};
    repHoldings.forEach(h => {
      if ((h.class === "Mutual Funds" || h.class === "Indian Mutual Funds" || h.class === "Global Mutual Funds" || h.isMutualFund) && typeof getMFClassSplit === "function") {
        const split = getMFClassSplit(h); const tp = Object.values(split).reduce((s,v)=>s+v,0)||100;
        Object.entries(split).forEach(([cls,pct]) => { byClass[cls]=(byClass[cls]||0)+h.valueUSD*(pct/tp); });
      } else { byClass[h.class]=(byClass[h.class]||0)+h.valueUSD; }
    });
    const bySector = {}; repHoldings.forEach(h => { const s=h.sector||"Other"; bySector[s]=(bySector[s]||0)+h.valueUSD; });
    const byCur    = {}; repHoldings.forEach(h => { byCur[h.currency]=(byCur[h.currency]||0)+h.valueUSD; });
    const byGeo    = {}; repHoldings.forEach(h => { const g=h.country||"Unknown"; byGeo[g]=(byGeo[g]||0)+h.valueUSD; });
    const classRows  = Object.entries(byClass).sort((a,b)=>b[1]-a[1]);
    const sectRows   = Object.entries(bySector).sort((a,b)=>b[1]-a[1]);
    const curRows    = Object.entries(byCur).sort((a,b)=>b[1]-a[1]);
    const geoRows    = Object.entries(byGeo).sort((a,b)=>b[1]-a[1]);
    const topHoldings = [...repHoldings].sort((a,b)=>b.valueUSD-a.valueUSD).slice(0,8);

    // Risk metrics
    const totalVR  = totalVal || 1;
    const listedV  = repHoldings.filter(h=>h.listed).reduce((s,h)=>s+h.valueUSD,0);
    const illiqPct = (totalVR - listedV) / totalVR * 100;
    const hhi      = repHoldings.reduce((s,h)=>s+Math.pow(h.valueUSD/totalVR*100,2),0);
    const sortedR  = [...repHoldings].sort((a,b)=>b.valueUSD-a.valueUSD);
    const top5Pct  = sortedR.slice(0,5).reduce((s,h)=>s+h.valueUSD/totalVR*100,0);
    const avgRet   = repHoldings.length ? repHoldings.reduce((s,h)=>s+(h.gainPct??0),0)/repHoldings.length : 0;
    const variance = repHoldings.length ? repHoldings.reduce((s,h)=>s+Math.pow((h.gainPct??0)-avgRet,2),0)/repHoldings.length : 0;
    const stddev   = Math.sqrt(variance);
    const sharpe   = stddev > 0 ? ((avgRet - 5) / stddev).toFixed(2) : "N/A";
    const riskScore = Math.min(100, Math.round((hhi/4000)*30+(illiqPct/100)*30+(top5Pct/100)*20+(stddev/50)*20));
    const riskLevel = riskScore<33?"Conservative":riskScore<66?"Moderate":"Aggressive";

    const asOnFmt  = new Date(pdfAsOnDate).toLocaleDateString("en-GB", {day:"numeric",month:"long",year:"numeric"});

    // ── Palette ──────────────────────────────────────────────────
    const BG    = "040F0D";   // near-black dark bg (cover/section dividers)
    const BG2   = "071A15";   // dark teal bg
    const TEAL  = "0D8A7A";   // primary teal
    const TEAL2 = "4DD9C7";   // light teal accent
    const WHITE = "FFFFFF";
    const GRAY1 = "94A3B8";
    const GRAY2 = "334155";
    const GRAY3 = "1E293B";
    const GREEN = "10B981";
    const RED   = "EF4444";
    const AMBER = "F59E0B";
    const SLATE = "0F172A";

    const CHART_COLORS = ["0D9488","3B82F6","F59E0B","8B5CF6","EC4899","06B6D4","F97316","84CC16","A78BFA","FB923C","34D399","60A5FA","94A3B8","6B7280"];
    const makeShadow = () => ({type:"outer",blur:8,offset:3,angle:135,color:"000000",opacity:0.18});

    const pres = new PptxGenJS();
    pres.layout = "LAYOUT_16x9";
    pres.title  = `${repName} — Portfolio Report`;
    pres.author = pdfPreparedBy;

    // ═══════════════════════════════════════════════════════════
    // SLIDE 1 — COVER
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color: BG };

      // Left dark teal panel
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:3.8, h:5.625, fill:{color:BG2} });
      // Teal accent bar
      sl.addShape(pres.shapes.RECTANGLE, { x:3.8, y:0, w:0.06, h:5.625, fill:{color:TEAL} });

      // Brand name on left panel
      sl.addText("FORTHIUS", { x:0.35, y:0.5, w:3.1, h:0.5, fontSize:22, bold:true, color:TEAL2, charSpacing:6, fontFace:"Calibri", margin:0 });
      sl.addText("WEALTH MANAGEMENT", { x:0.35, y:0.95, w:3.1, h:0.3, fontSize:9, color:TEAL, charSpacing:4, fontFace:"Calibri", margin:0 });

      // Decorative teal rectangle
      sl.addShape(pres.shapes.RECTANGLE, { x:0.35, y:1.4, w:0.4, h:0.06, fill:{color:TEAL} });

      // Report type tag
      sl.addText("PORTFOLIO REPORT", { x:0.35, y:1.6, w:3.1, h:0.35, fontSize:9, color:TEAL2, charSpacing:3, fontFace:"Calibri", margin:0 });

      // Meta info on left
      sl.addText([
        {text:"Prepared For",  options:{color:GRAY1,fontSize:8,breakLine:true}},
        {text:repName,         options:{color:WHITE,fontSize:12,bold:true,breakLine:true}},
        {text:" ",             options:{fontSize:6,breakLine:true}},
        {text:"Prepared By",   options:{color:GRAY1,fontSize:8,breakLine:true}},
        {text:pdfPreparedBy,   options:{color:WHITE,fontSize:11,bold:true,breakLine:true}},
        {text:" ",             options:{fontSize:6,breakLine:true}},
        {text:"As On Date",    options:{color:GRAY1,fontSize:8,breakLine:true}},
        {text:asOnFmt,         options:{color:WHITE,fontSize:11,bold:true}},
      ], { x:0.35, y:2.15, w:3.1, h:2.2, fontFace:"Calibri", valign:"top" });

      // Confidential badge on left
      sl.addShape(pres.shapes.RECTANGLE, { x:0.35, y:5.05, w:1.6, h:0.3, fill:{color:TEAL}, rectRadius:0.04 });
      sl.addText("CONFIDENTIAL", { x:0.35, y:5.05, w:1.6, h:0.3, fontSize:8, bold:true, color:WHITE, align:"center", valign:"middle", margin:0 });

      // Right panel — main title
      sl.addText(repName, { x:4.2, y:1.1, w:5.5, h:1.5, fontSize:36, bold:true, color:WHITE, fontFace:"Calibri", valign:"bottom", wrap:true, margin:0 });
      sl.addText(repSubtitle, { x:4.2, y:2.65, w:5.5, h:0.4, fontSize:14, color:TEAL2, fontFace:"Calibri", margin:0 });

      // Horizontal divider
      sl.addShape(pres.shapes.RECTANGLE, { x:4.2, y:3.15, w:5.5, h:0.03, fill:{color:GRAY2} });

      // KPI summary on cover
      const coverKPIs = [
        {label:"Total AUM",  val:fmtV(totalVal)},
        {label:"Total Return", val:pctStr(gainPct)},
        {label:"Holdings",   val:String(repHoldings.length)},
        {label:"Asset Classes", val:String(Object.keys(byClass).length)},
      ];
      coverKPIs.forEach((k, i) => {
        const x = 4.2 + i * 1.4;
        sl.addText(k.val,   { x, y:3.4,  w:1.3, h:0.5, fontSize:16, bold:true, color:gainPct>=0&&k.label==="Total Return"?GREEN:TEAL2, fontFace:"Calibri", margin:0 });
        sl.addText(k.label, { x, y:3.9,  w:1.3, h:0.3, fontSize:8,  color:GRAY1, fontFace:"Calibri", margin:0 });
      });

      // Footer
      sl.addText("FORTHIUS FINALYTICS · CONFIDENTIAL · FOR INTENDED RECIPIENT ONLY", {
        x:0, y:5.35, w:10, h:0.27, fontSize:7, color:GRAY2, align:"center", fontFace:"Calibri", margin:0
      });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 2 — PORTFOLIO SUMMARY
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color:"F8FAFC" };

      // Top teal header bar
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.75, fill:{color:BG2} });
      sl.addText("PORTFOLIO SUMMARY", { x:0.4, y:0, w:8, h:0.75, fontSize:14, bold:true, color:WHITE, valign:"middle", fontFace:"Calibri", charSpacing:2, margin:0 });
      sl.addText(`${repName}  ·  As on ${asOnFmt}`, { x:0, y:0, w:9.6, h:0.75, fontSize:9, color:TEAL2, align:"right", valign:"middle", fontFace:"Calibri", margin:0 });

      // 4 KPI cards row 1
      const kpis1 = [
        {label:"Total AUM",     val:fmtV(totalVal),              color:TEAL,                     icon:"💼"},
        {label:"Total Cost",    val:fmtV(totalCost),             color:GRAY2,                    icon:"💳"},
        {label:"Total Gain",    val:(gainUSD>=0?"+":"")+fmtV(Math.abs(gainUSD)), color:gainUSD>=0?GREEN:RED, icon:"📈"},
        {label:"Return",        val:pctStr(gainPct),             color:gainPct>=0?GREEN:RED,     icon:"⚡"},
      ];
      kpis1.forEach((k, i) => {
        const x = 0.3 + i * 2.38;
        sl.addShape(pres.shapes.RECTANGLE, { x, y:0.95, w:2.2, h:1.3, fill:{color:WHITE}, shadow:makeShadow() });
        sl.addShape(pres.shapes.RECTANGLE, { x, y:0.95, w:2.2, h:0.05, fill:{color:k.color} });
        sl.addText(k.icon, { x, y:1.05, w:2.2, h:0.4, fontSize:22, align:"center", margin:0 });
        sl.addText(k.val,  { x, y:1.45, w:2.2, h:0.42, fontSize:16, bold:true, color:k.color, align:"center", fontFace:"Calibri", margin:0 });
        sl.addText(k.label,{ x, y:1.87, w:2.2, h:0.28, fontSize:9,  color:GRAY1, align:"center", fontFace:"Calibri", margin:0 });
      });

      // 3 secondary KPI cards row 2
      const kpis2 = [
        {label:"Total Holdings",   val:String(repHoldings.length)},
        {label:"Listed Assets",    val:String(repHoldings.filter(h=>h.listed).length)},
        {label:"Asset Classes",    val:String(Object.keys(byClass).length)},
      ];
      kpis2.forEach((k, i) => {
        const x = 1.3 + i * 2.7;
        sl.addShape(pres.shapes.RECTANGLE, { x, y:2.45, w:2.4, h:0.85, fill:{color:WHITE}, shadow:makeShadow() });
        sl.addText(k.val,   { x, y:2.5,  w:2.4, h:0.42, fontSize:26, bold:true, color:GRAY3, align:"center", fontFace:"Calibri", margin:0 });
        sl.addText(k.label, { x, y:2.9,  w:2.4, h:0.3,  fontSize:9,  color:GRAY1, align:"center", fontFace:"Calibri", margin:0 });
      });

      // Performance by asset class table
      sl.addText("PERFORMANCE BY ASSET CLASS", { x:0.3, y:3.55, w:9.4, h:0.3, fontSize:9, bold:true, color:TEAL, charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addShape(pres.shapes.RECTANGLE, { x:0.3, y:3.5, w:9.4, h:0.03, fill:{color:TEAL} });

      const tableData = [
        [
          {text:"Asset Class",   options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9}},
          {text:"Value",         options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Weight",        options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Cost",          options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Return",        options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
        ],
        ...classRows.slice(0,6).map(([cls, val], i) => {
          const hlds = repHoldings.filter(h=>h.class===cls);
          const cost = hlds.reduce((s,h)=>s+h.costUSD,0);
          const ret  = cost > 0 ? ((val-cost)/cost*100) : 0;
          const retColor = ret >= 0 ? GREEN : RED;
          const rowFill  = i%2===0 ? "F8FAFC" : WHITE;
          return [
            {text:cls,                    options:{fontSize:9,fill:{color:rowFill}}},
            {text:fmtV(val),              options:{fontSize:9,align:"right",fill:{color:rowFill},bold:true,color:GRAY3}},
            {text:(val/totalVal*100).toFixed(1)+"%", options:{fontSize:9,align:"right",fill:{color:rowFill},color:GRAY1}},
            {text:fmtV(cost),             options:{fontSize:9,align:"right",fill:{color:rowFill},color:GRAY1}},
            {text:(ret>=0?"+":"")+ret.toFixed(1)+"%",options:{fontSize:9,align:"right",fill:{color:rowFill},color:retColor,bold:true}},
          ];
        }),
      ];
      sl.addTable(tableData, { x:0.3, y:3.9, w:9.4, h:1.5, rowH:0.28, colW:[3.2,2.0,1.4,1.7,1.1], border:{pt:0.5,color:"E2E8F0"} });

      // Footer
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.37, w:10, h:0.25, fill:{color:GRAY2} });
      sl.addText("FORTHIUS FINALYTICS  ·  CONFIDENTIAL", { x:0, y:5.37, w:10, h:0.25, fontSize:7, color:GRAY1, align:"center", valign:"middle", margin:0 });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 3 — ASSET & SECTOR ALLOCATION
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color:"F8FAFC" };
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.75, fill:{color:BG2} });
      sl.addText("ALLOCATION BREAKDOWN", { x:0.4, y:0, w:8, h:0.75, fontSize:14, bold:true, color:WHITE, valign:"middle", fontFace:"Calibri", charSpacing:2, margin:0 });
      sl.addText(`As on ${asOnFmt}`, { x:0, y:0, w:9.6, h:0.75, fontSize:9, color:TEAL2, align:"right", valign:"middle", fontFace:"Calibri", margin:0 });

      // LEFT: Asset Allocation donut chart
      const classLabels = classRows.slice(0,8).map(([k])=>k);
      const classVals   = classRows.slice(0,8).map(([,v])=>+(v/totalVal*100).toFixed(1));
      sl.addText("ASSET CLASS", { x:0.3, y:0.85, w:4.6, h:0.3, fontSize:9, bold:true, color:TEAL, charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addChart(pres.charts.DOUGHNUT, [{ name:"Asset Allocation", labels:classLabels, values:classVals }], {
        x:0.3, y:1.1, w:4.6, h:3.2,
        chartColors: CHART_COLORS.slice(0, classLabels.length),
        chartArea:   { fill:{ color:"F8FAFC" } },
        showLegend:  true, legendPos:"b", legendFontSize:8, legendColor:GRAY2,
        showPercent: true, dataLabelFontSize:8, dataLabelColor:WHITE,
        holeSize: 55,
      });

      // RIGHT: Sector Allocation bar chart
      const sectLabels = sectRows.slice(0,7).map(([k])=>k);
      const sectVals   = sectRows.slice(0,7).map(([,v])=>+(v/totalVal*100).toFixed(1));
      sl.addText("SECTOR BREAKDOWN", { x:5.2, y:0.85, w:4.5, h:0.3, fontSize:9, bold:true, color:TEAL, charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addChart(pres.charts.BAR, [{ name:"Sector %", labels:sectLabels, values:sectVals }], {
        x:5.1, y:1.1, w:4.6, h:3.2,
        barDir:"bar",
        chartColors: CHART_COLORS.slice(0, sectLabels.length),
        chartArea:   { fill:{ color:"F8FAFC" } },
        catAxisLabelColor:GRAY2, valAxisLabelColor:GRAY2,
        valGridLine:{ color:"E2E8F0", size:0.5 },
        catGridLine:{ style:"none" },
        showValue:true, dataLabelPosition:"outEnd", dataLabelColor:GRAY2, dataLabelFontSize:8,
        dataLabelFormatCode:"0.0\%",
        showLegend:false, valAxisMaxVal:100,
      });

      // Divider
      sl.addShape(pres.shapes.RECTANGLE, { x:4.95, y:0.85, w:0.03, h:3.6, fill:{color:"E2E8F0"} });

      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.37, w:10, h:0.25, fill:{color:GRAY2} });
      sl.addText("FORTHIUS FINALYTICS  ·  CONFIDENTIAL", { x:0, y:5.37, w:10, h:0.25, fontSize:7, color:GRAY1, align:"center", valign:"middle", margin:0 });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 4 — CURRENCY & GEOGRAPHY
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color:"F8FAFC" };
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.75, fill:{color:BG2} });
      sl.addText("CURRENCY & GEOGRAPHIC EXPOSURE", { x:0.4, y:0, w:8, h:0.75, fontSize:14, bold:true, color:WHITE, valign:"middle", fontFace:"Calibri", charSpacing:2, margin:0 });
      sl.addText(`As on ${asOnFmt}`, { x:0, y:0, w:9.6, h:0.75, fontSize:9, color:TEAL2, align:"right", valign:"middle", fontFace:"Calibri", margin:0 });

      // Currency pie chart (left)
      const curLabels = curRows.slice(0,7).map(([k])=>k);
      const curVals   = curRows.slice(0,7).map(([,v])=>+(v/totalVal*100).toFixed(1));
      sl.addText("CURRENCY EXPOSURE", { x:0.3, y:0.85, w:4.6, h:0.3, fontSize:9, bold:true, color:TEAL, charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addChart(pres.charts.PIE, [{ name:"Currency", labels:curLabels, values:curVals }], {
        x:0.3, y:1.1, w:4.6, h:3.2,
        chartColors: CHART_COLORS.slice(0, curLabels.length),
        chartArea:   { fill:{ color:"F8FAFC" } },
        showLegend:true, legendPos:"b", legendFontSize:8, legendColor:GRAY2,
        showPercent:true, dataLabelFontSize:8, dataLabelColor:WHITE,
      });

      // Geography bar chart (right)
      const geoLabels = geoRows.slice(0,7).map(([k])=>k);
      const geoVals   = geoRows.slice(0,7).map(([,v])=>+(v/totalVal*100).toFixed(1));
      sl.addText("GEOGRAPHIC ALLOCATION", { x:5.2, y:0.85, w:4.5, h:0.3, fontSize:9, bold:true, color:TEAL, charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addChart(pres.charts.BAR, [{ name:"Geography %", labels:geoLabels, values:geoVals }], {
        x:5.1, y:1.1, w:4.6, h:3.2,
        barDir:"bar",
        chartColors: ["0D9488","14B8A6","5EEAD4","99F6E4","CCFBF1","D1FAE5","A7F3D0"],
        chartArea:   { fill:{ color:"F8FAFC" } },
        catAxisLabelColor:GRAY2, valAxisLabelColor:GRAY2,
        valGridLine:{ color:"E2E8F0", size:0.5 },
        catGridLine:{ style:"none" },
        showValue:true, dataLabelPosition:"outEnd", dataLabelColor:GRAY2, dataLabelFontSize:8,
        dataLabelFormatCode:"0.0\%",
        showLegend:false,
      });

      sl.addShape(pres.shapes.RECTANGLE, { x:4.95, y:0.85, w:0.03, h:3.6, fill:{color:"E2E8F0"} });
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.37, w:10, h:0.25, fill:{color:GRAY2} });
      sl.addText("FORTHIUS FINALYTICS  ·  CONFIDENTIAL", { x:0, y:5.37, w:10, h:0.25, fontSize:7, color:GRAY1, align:"center", valign:"middle", margin:0 });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 5 — TOP HOLDINGS
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color:"F8FAFC" };
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.75, fill:{color:BG2} });
      sl.addText("TOP HOLDINGS", { x:0.4, y:0, w:8, h:0.75, fontSize:14, bold:true, color:WHITE, valign:"middle", fontFace:"Calibri", charSpacing:2, margin:0 });
      sl.addText(`As on ${asOnFmt}`, { x:0, y:0, w:9.6, h:0.75, fontSize:9, color:TEAL2, align:"right", valign:"middle", fontFace:"Calibri", margin:0 });

      const holdingRows = [
        [
          {text:"#",          options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"center"}},
          {text:"Asset Name", options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9}},
          {text:"Class",      options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9}},
          {text:"Value",      options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Weight",     options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Return",     options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"right"}},
          {text:"Currency",   options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:9,align:"center"}},
        ],
        ...topHoldings.map((h, i) => {
          const gp = h.costUSD > 0 ? ((h.valueUSD - h.costUSD)/h.costUSD*100) : 0;
          const gpColor = gp >= 0 ? GREEN : RED;
          const rowFill = i%2===0 ? "F8FAFC" : WHITE;
          return [
            {text:String(i+1),                       options:{fontSize:9,align:"center",fill:{color:rowFill},color:GRAY1}},
            {text:h.name,                             options:{fontSize:9,fill:{color:rowFill},bold:true,color:GRAY3}},
            {text:h.class,                            options:{fontSize:8,fill:{color:rowFill},color:GRAY1}},
            {text:fmtV(h.valueUSD),                  options:{fontSize:9,align:"right",fill:{color:rowFill},color:TEAL,bold:true}},
            {text:(h.valueUSD/totalVal*100).toFixed(1)+"%", options:{fontSize:9,align:"right",fill:{color:rowFill},color:GRAY2}},
            {text:(gp>=0?"+":"")+gp.toFixed(1)+"%",  options:{fontSize:9,align:"right",fill:{color:rowFill},color:gpColor,bold:true}},
            {text:h.currency,                         options:{fontSize:9,align:"center",fill:{color:rowFill},color:GRAY1}},
          ];
        }),
      ];
      sl.addTable(holdingRows, { x:0.3, y:0.95, w:9.4, h:4.15, rowH:0.42, colW:[0.35,3.15,1.6,1.5,0.9,0.9,0.9], border:{pt:0.5,color:"E2E8F0"} });

      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.37, w:10, h:0.25, fill:{color:GRAY2} });
      sl.addText("FORTHIUS FINALYTICS  ·  CONFIDENTIAL", { x:0, y:5.37, w:10, h:0.25, fontSize:7, color:GRAY1, align:"center", valign:"middle", margin:0 });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 6 — RISK ANALYSIS (admin-only)
    // ═══════════════════════════════════════════════════════════
    if (isAdmin && pdfSections.riskAnalysis) {
      const sl = pres.addSlide();
      sl.background = { color:"F8FAFC" };
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.75, fill:{color:GRAY3} });
      sl.addText("RISK ANALYSIS", { x:0.4, y:0, w:7, h:0.75, fontSize:14, bold:true, color:WHITE, valign:"middle", fontFace:"Calibri", charSpacing:2, margin:0 });
      sl.addShape(pres.shapes.RECTANGLE, { x:7.5, y:0.18, w:2.1, h:0.38, fill:{color:RED} });
      sl.addText("ADVISOR CONFIDENTIAL", { x:7.5, y:0.18, w:2.1, h:0.38, fontSize:7.5, bold:true, color:WHITE, align:"center", valign:"middle", margin:0 });

      // Risk score big number (left)
      const riskColor = riskScore<33?GREEN:riskScore<66?AMBER:RED;
      sl.addShape(pres.shapes.RECTANGLE, { x:0.3, y:0.85, w:2.6, h:2.4, fill:{color:WHITE}, shadow:makeShadow() });
      sl.addShape(pres.shapes.RECTANGLE, { x:0.3, y:0.85, w:2.6, h:0.05, fill:{color:riskColor} });
      sl.addText("RISK SCORE", { x:0.3, y:0.9, w:2.6, h:0.4, fontSize:8, bold:true, color:GRAY1, align:"center", charSpacing:2, margin:0 });
      sl.addText(String(riskScore), { x:0.3, y:1.3, w:2.6, h:1.1, fontSize:64, bold:true, color:riskColor, align:"center", fontFace:"Calibri", margin:0 });
      sl.addText(riskLevel.toUpperCase(), { x:0.3, y:2.4, w:2.6, h:0.4, fontSize:11, bold:true, color:riskColor, align:"center", charSpacing:2, margin:0 });
      sl.addText("out of 100", { x:0.3, y:2.8, w:2.6, h:0.3, fontSize:8, color:GRAY1, align:"center", margin:0 });

      // Risk metrics grid (right of big number)
      const riskMets = [
        {label:"HHI Index",     val:hhi.toFixed(0),           note:hhi<1000?"Low":hhi<2500?"Moderate":"High",  col:hhi<1000?GREEN:hhi<2500?AMBER:RED},
        {label:"Illiquidity",   val:illiqPct.toFixed(1)+"%",  note:"Unlisted assets",                          col:illiqPct>50?RED:illiqPct>30?AMBER:GREEN},
        {label:"Top 5 Weight",  val:top5Pct.toFixed(1)+"%",   note:"Concentration",                            col:top5Pct>60?RED:top5Pct>40?AMBER:GREEN},
        {label:"Volatility",    val:stddev.toFixed(1)+"%",    note:"Return std dev",                           col:stddev>30?RED:stddev>15?AMBER:GREEN},
        {label:"Sharpe Proxy",  val:String(sharpe),           note:"rf = 5%",                                  col:parseFloat(sharpe)>1?GREEN:parseFloat(sharpe)>0?AMBER:RED},
        {label:"Asset Classes", val:String(Object.keys(byClass).size||Object.keys(byClass).length), note:"Diversification", col:TEAL},
      ];
      riskMets.forEach((m, i) => {
        const col = i < 3 ? 0 : 1;
        const row = i % 3;
        const x = 3.2 + col * 3.4;
        const y = 0.9 + row * 0.75;
        sl.addShape(pres.shapes.RECTANGLE, { x, y, w:3.2, h:0.65, fill:{color:WHITE}, shadow:makeShadow() });
        sl.addShape(pres.shapes.RECTANGLE, { x, y, w:0.06, h:0.65, fill:{color:m.col} });
        sl.addText(m.val,   { x:x+0.15, y:y+0.04, w:3.0, h:0.36, fontSize:18, bold:true, color:m.col, fontFace:"Calibri", margin:0 });
        sl.addText(m.label, { x:x+0.15, y:y+0.38, w:2.0, h:0.2,  fontSize:8,  color:GRAY2, fontFace:"Calibri", margin:0 });
        sl.addText(m.note,  { x:x+2.1,  y:y+0.38, w:1.0, h:0.2,  fontSize:7,  color:GRAY1, align:"right", fontFace:"Calibri", margin:0 });
      });

      // Risk gauge bar
      sl.addShape(pres.shapes.RECTANGLE, { x:0.3, y:3.42, w:9.4, h:0.3, fill:{color:WHITE}, shadow:makeShadow() });
      sl.addText("RISK SPECTRUM", { x:0.3, y:3.42, w:1.5, h:0.3, fontSize:7, bold:true, color:GRAY1, valign:"middle", charSpacing:1, margin:0 });
      // Gradient segments
      [["4ADE80","Conservative",0],["F59E0B","Moderate",3.2],["EF4444","Aggressive",6.5]].forEach(([col,lbl,xOff]) => {
        sl.addShape(pres.shapes.RECTANGLE, { x:1.9+xOff*0.36, y:3.46, w:xOff===0?2.5:xOff===3.2?2.3:2.0, h:0.18, fill:{color:col}, transparency:30 });
      });
      // Pointer
      const ptrX = 1.9 + (riskScore/100) * 7.0;
      sl.addShape(pres.shapes.OVAL, { x:ptrX-0.1, y:3.44, w:0.2, h:0.22, fill:{color:riskColor}, line:{color:WHITE,width:1.5} });
      sl.addText(`Conservative          Moderate          Aggressive`, { x:1.9, y:3.68, w:7.0, h:0.2, fontSize:7, color:GRAY1, align:"center", margin:0 });

      // Stress test mini table
      sl.addText("SCENARIO STRESS TESTS", { x:0.3, y:3.98, w:4, h:0.25, fontSize:8, bold:true, color:TEAL, charSpacing:1, margin:0 });
      const stressData = [
        [{text:"Scenario",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8}},{text:"Shock",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8,align:"right"}},{text:"Portfolio Impact",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8,align:"right"}}],
        ...["Equity Crash -30%","Rate Spike +200bps","USD Surge +15%","Crypto Crash -60%","RE Correction -20%"].map((name, i) => {
          const shocks = [-30,-12,-15,-60,-20];
          const classes = ["Equities","Fixed Income","USD","Digital Assets","Real Estate"];
          const affected = repHoldings.filter(h=>i===2?h.currency==="USD":(h.class===classes[i]||h.sector===classes[i])).reduce((s,h)=>s+h.valueUSD,0);
          const impact = affected * shocks[i] / 100;
          const portImpact = totalVal > 0 ? (impact/totalVal*100) : 0;
          const impColor = portImpact > -5 ? GREEN : portImpact > -15 ? AMBER : RED;
          const rowFill = i%2===0?"F8FAFC":WHITE;
          return [
            {text:name,                              options:{fontSize:8,fill:{color:rowFill},color:GRAY3}},
            {text:shocks[i]+"%",                     options:{fontSize:8,align:"right",fill:{color:rowFill},color:RED}},
            {text:portImpact.toFixed(1)+"%",         options:{fontSize:8,align:"right",fill:{color:rowFill},color:impColor,bold:true}},
          ];
        }),
      ];
      sl.addTable(stressData, { x:0.3, y:4.22, w:4.7, h:1.0, rowH:0.17, colW:[2.8,0.8,1.1], border:{pt:0.5,color:"E2E8F0"} });

      // Concentration note
      sl.addText("CONCENTRATION RISK", { x:5.3, y:3.98, w:4.0, h:0.25, fontSize:8, bold:true, color:TEAL, charSpacing:1, margin:0 });
      const concData = [
        [{text:"#",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8,align:"center"}},{text:"Holding",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8}},{text:"Weight",options:{bold:true,color:WHITE,fill:{color:TEAL},fontSize:8,align:"right"}}],
        ...sortedR.slice(0,5).map((h,i)=>{
          const w = (h.valueUSD/totalVR*100).toFixed(1);
          const wColor = parseFloat(w)>20?RED:parseFloat(w)>10?AMBER:GREEN;
          const rowFill = i%2===0?"F8FAFC":WHITE;
          return [
            {text:String(i+1),       options:{fontSize:8,align:"center",fill:{color:rowFill},color:GRAY1}},
            {text:h.name.length>22?h.name.slice(0,22)+"…":h.name, options:{fontSize:8,fill:{color:rowFill},color:GRAY3}},
            {text:w+"%",             options:{fontSize:8,align:"right",fill:{color:rowFill},color:wColor,bold:true}},
          ];
        }),
      ];
      sl.addTable(concData, { x:5.2, y:4.22, w:4.5, h:1.0, rowH:0.17, colW:[0.35,3.15,1.0], border:{pt:0.5,color:"E2E8F0"} });

      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.37, w:10, h:0.25, fill:{color:GRAY3} });
      sl.addText("ADVISOR CONFIDENTIAL — NOT FOR CLIENT DISTRIBUTION  ·  FORTHIUS FINALYTICS", { x:0, y:5.37, w:10, h:0.25, fontSize:7, color:GRAY1, align:"center", valign:"middle", margin:0 });
    }

    // ═══════════════════════════════════════════════════════════
    // SLIDE 7 — DISCLAIMER
    // ═══════════════════════════════════════════════════════════
    {
      const sl = pres.addSlide();
      sl.background = { color:BG };
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:5.625, fill:{color:BG2} });
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.05, fill:{color:TEAL} });
      sl.addShape(pres.shapes.RECTANGLE, { x:0, y:5.575, w:10, h:0.05, fill:{color:TEAL} });

      sl.addText("DISCLAIMER", { x:0.5, y:0.6, w:9, h:0.55, fontSize:22, bold:true, color:WHITE, align:"center", charSpacing:6, fontFace:"Calibri", margin:0 });

      sl.addText([
        {text:"This presentation has been prepared by Forthius Finalytics for informational purposes only and does not constitute investment advice, an offer to buy or sell securities, or a solicitation of any kind.", options:{breakLine:true}},
        {text:" ", options:{fontSize:6,breakLine:true}},
        {text:"The information contained herein is based on sources believed to be reliable, but no representation or warranty, express or implied, is made as to its accuracy, completeness, or reliability. Past performance is not indicative of future results.", options:{breakLine:true}},
        {text:" ", options:{fontSize:6,breakLine:true}},
        {text:"Market values are indicative only and may differ from actual traded prices. All investments involve risk, including the possible loss of principal. This document is confidential and intended solely for the named recipient.", options:{breakLine:true}},
        {text:" ", options:{fontSize:6,breakLine:true}},
        {text:"Please consult your financial advisor before making any investment decisions. Forthius Finalytics does not accept liability for any direct or indirect loss arising from the use of this material.", options:{}},
      ], { x:0.8, y:1.4, w:8.4, h:2.8, fontSize:11, color:GRAY1, fontFace:"Calibri", valign:"top", lineSpacingMultiple:1.4 });

      sl.addText("FORTHIUS FINALYTICS  ·  PORTFOLIO INTELLIGENCE", { x:0, y:4.6, w:10, h:0.4, fontSize:10, color:TEAL, align:"center", charSpacing:2, fontFace:"Calibri", margin:0 });
      sl.addText(`Prepared by ${pdfPreparedBy}  ·  ${asOnFmt}`, { x:0, y:5.0, w:10, h:0.3, fontSize:9, color:GRAY2, align:"center", fontFace:"Calibri", margin:0 });
    }

    // ── Download ───────────────────────────────────────────────
    const safeName = repName.replace(/[^a-zA-Z0-9 ]/g,"").trim().replace(/ +/g,"_");
    await pres.writeFile({ fileName: `Forthius_${safeName}_${pdfAsOnDate}.pptx` });
    setPdfModal(false);
  };

    const generatePDF = () => {
    // Recompute fresh values from current holdings state (live prices)
    // Uses same toUSD()+fxRates as screen — numbers will match exactly
    // Ghost positions (qty=0) excluded — consistent with dashboard display
    const freshHW = holdings.filter(h => (h.qty||0) > 0).map(h => {
      const price    = h.currentPrice || h.costPrice || 0;
      const costUSD  = toUSD((h.qty||0) * (h.costPrice||0), h.currency);
      const valueUSD = h.class === "Cash / Bank Balance"
        ? toUSD((h.qty||0) * (h.costPrice||0), h.currency)
        : toUSD((h.qty||0) * price, h.currency);
      // Return % = same formula as holdings tab (price-based, not FX-adjusted)
      const gainPct  = (h.costPrice||0) > 0
        ? ((price - h.costPrice) / h.costPrice) * 100
        : 0;
      // Absolute gain in report currency (via toUSD for consistency)
      const gainUSD  = valueUSD - costUSD;
      const clientId = holdingOwners[String(h.id)] || holdingOwners[h.id];
      const client   = clients.find(c => c.id === clientId);
      const family   = client ? families.find(f => f.id === client.familyId) : null;
      return { ...h, class: normaliseClass(h.class||"Miscellaneous"), currentPrice: price, costUSD, valueUSD, gainPct, gainUSD, clientId, client, family };
    });

    // Determine scope
    let reportHoldings = freshHW;
    let reportFamilyName = "All Families";
    let reportClientName = "All Clients";
    let reportSubtitle = "Full Portfolio Report";

    if (pdfScope === "family" && pdfFamilyId) {
      const fam = families.find(f => f.id === pdfFamilyId);
      const famClientIds = clients.filter(c => c.familyId === pdfFamilyId).map(c => c.id);
      reportHoldings = freshHW.filter(h => famClientIds.includes(h.clientId));
      reportFamilyName = fam ? `${fam.emoji} ${fam.name}` : "Unknown Family";
      reportSubtitle = `Family Portfolio Report — ${reportFamilyName}`;
    } else if (pdfScope === "individual" && pdfClientId) {
      const cli = clients.find(c => c.id === pdfClientId);
      const fam = cli ? families.find(f => f.id === cli.familyId) : null;
      reportHoldings = freshHW.filter(h => h.clientId === pdfClientId);
      reportClientName = cli ? cli.name : "Unknown Client";
      reportFamilyName = fam ? `${fam.emoji} ${fam.name}` : "";
      reportSubtitle = `Individual Portfolio Report — ${reportClientName}`;
    }

    const totalVal = reportHoldings.reduce((s,h) => s + h.valueUSD, 0);
    const totalCost = reportHoldings.reduce((s,h) => s + h.costUSD, 0);
    const gainUSD = totalVal - totalCost;
    const gainPct = totalCost > 0 ? (gainUSD / totalCost) * 100 : 0;

    // Asset class breakdown — track both value AND cost per split class
    const byClass = {};     // { cls: valueUSD }
    const byClassCost = {}; // { cls: costUSD }
    reportHoldings.forEach(h => {
      if ((h.class === "Mutual Funds" || h.class === "Indian Mutual Funds" || h.class === "Global Mutual Funds" || h.isMutualFund) && typeof getMFClassSplit === "function") {
        const split = getMFClassSplit(h); const tp = Object.values(split).reduce((s,v)=>s+v,0)||100;
        Object.entries(split).forEach(([cls,pct]) => {
          byClass[cls]     = (byClass[cls]||0)     + h.valueUSD*(pct/tp);
          byClassCost[cls] = (byClassCost[cls]||0) + h.costUSD*(pct/tp);
        });
      } else {
        byClass[h.class]     = (byClass[h.class]||0)     + h.valueUSD;
        byClassCost[h.class] = (byClassCost[h.class]||0) + h.costUSD;
      }
    });
    const classRows = Object.entries(byClass).sort((a,b) => b[1]-a[1]);

    // Currency breakdown
    const byCur = {};
    reportHoldings.forEach(h => { byCur[h.currency] = (byCur[h.currency]||0) + h.valueUSD; });
    const curRows = Object.entries(byCur).sort((a,b) => b[1]-a[1]);

    // Cap size (listed only)
    const byCap = {};
    reportHoldings.filter(h => h.listed && h.capSize && h.capSize !== "N/A").forEach(h => {
      byCap[h.capSize] = (byCap[h.capSize]||0) + h.valueUSD;
    });
    const capRows = Object.entries(byCap).sort((a,b) => b[1]-a[1]);

    // Geography
    const byCountryRaw = {};
    reportHoldings.forEach(h => { const k = h.country||"Unknown"; byCountryRaw[k] = (byCountryRaw[k]||0) + h.valueUSD; });
    const geoRows = Object.entries(byCountryRaw).sort((a,b) => b[1]-a[1]);

    // Sector breakdown
    const bySector = {};
    reportHoldings.forEach(h => { const s = h.sector||"Other"; bySector[s] = (bySector[s]||0) + h.valueUSD; });
    const sectorRows = Object.entries(bySector).sort((a,b) => b[1]-a[1]);
    const SECTOR_COLORS_PDF = {
      "Technology":"#2563EB","Financials":"#D97706","Energy":"#A32D2D",
      "Consumer Staples":"#1D9E75","Consumer Discretionary":"#EA580C","Real Estate":"#7C3AED",
      "Fixed Income":"#0EA5E9","Commodities":"#D97706","Digital Assets":"#EC4899",
      "Alternatives":"#4F46E5","Diversified":"#78716C","Other":"#A8A29E"
    };

    // Risk metrics for report — guard against zero total
    const listedRH   = reportHoldings.filter(h => h.listed);
    const totalVR    = totalVal > 0 ? totalVal : 0;
    const listedVR   = listedRH.reduce((s,h) => s + h.valueUSD, 0);
    const illiqPctR  = totalVR > 0 ? ((totalVR - listedVR) / totalVR * 100) : 0;
    const hhiR       = totalVR > 0 ? reportHoldings.reduce((s,h) => s + Math.pow(h.valueUSD/totalVR*100, 2), 0) : 0;
    const hhiLabelR  = hhiR < 1000 ? "Low" : hhiR < 2500 ? "Moderate" : "High";
    const hhiColorR  = hhiR < 1000 ? "#1D9E75" : hhiR < 2500 ? "#D97706" : "#A32D2D";
    const sortedR    = [...reportHoldings].sort((a,b) => b.valueUSD - a.valueUSD);
    const top5PctR   = totalVR > 0 ? sortedR.slice(0,5).reduce((s,h) => s + h.valueUSD/totalVR*100, 0) : 0;
    const top1PctR   = totalVR > 0 ? sortedR.slice(0,1).reduce((s,h) => s + h.valueUSD/totalVR*100, 0) : 0;
    const maxCcyR    = totalVR > 0 ? Object.entries(reportHoldings.reduce((a,h) => { a[h.currency]=(a[h.currency]||0)+h.valueUSD; return a; }, {})).sort((a,b) => b[1]-a[1])[0] : null;
    const maxCcyPctR = (maxCcyR && totalVR > 0) ? (maxCcyR[1]/totalVR*100) : 0;
    const avgRetR    = reportHoldings.length && totalVR > 0 ? reportHoldings.reduce((s,h) => s + (h.gainPct??0), 0) / reportHoldings.length : 0;
    const varianceR  = reportHoldings.length && totalVR > 0 ? reportHoldings.reduce((s,h) => s + Math.pow((h.gainPct??0) - avgRetR, 2), 0) / reportHoldings.length : 0;
    const stddevR    = Math.sqrt(varianceR);
    const sharpeR    = (stddevR > 0 && totalVR > 0) ? ((avgRetR - 5) / stddevR).toFixed(2) : "N/A";
    const riskScoreR = totalVR > 0 ? Math.min(100, Math.round((hhiR/4000)*30 + (illiqPctR/100)*30 + (top5PctR/100)*20 + (stddevR/50)*20)) : 0;
    const riskLevelR = riskScoreR < 33 ? "Conservative" : riskScoreR < 66 ? "Moderate" : "Aggressive";
    const riskColorR = riskScoreR < 33 ? "#1D9E75" : riskScoreR < 66 ? "#D97706" : "#A32D2D";

    const fmtN = (n, ccy="USD", compact=false) => fmt(convert(n, dc), dc, compact);
    const pct = (val, total) => total > 0 ? (val/total*100).toFixed(1)+"%" : "—";

    // ── Goals section ──
    const sectGoals = (() => {
      if (!pdfSections.goals || !allGoals || allGoals.length === 0) return "";
      const scopeGoals = pdfScope === "individual" && pdfClientId
        ? allGoals.filter(g => g.clientId === pdfClientId)
        : pdfScope === "family" && pdfFamilyId
          ? allGoals.filter(g => g.familyId === pdfFamilyId)
          : allGoals;
      if (!scopeGoals.length) return "";
      const toBaseLocal = (amt, cur) => {
        const r = fxRates[cur] || FX_RATES_DEFAULT[cur] || 1;
        const br = fxRates[dc] || FX_RATES_DEFAULT[dc] || 1;
        return (amt||0) / r * br;
      };
      const rows = scopeGoals.map(g => {
        const target = new Date(g.targetDate || (new Date().getFullYear()+10)+"-01-01");
        const now = new Date();
        const yearsLeft = Math.max(0, (target - now) / (365.25*24*3600*1000));
        const targetBase = toBaseLocal(g.targetAmount||0, g.currency||dc);
        const goalHW = g.clientId
          ? reportHoldings.filter(h => h.clientId === g.clientId)
          : reportHoldings;
        const filtered = (g.linkedClass && g.linkedClass !== "portfolio")
          ? goalHW.filter(h => h.class === g.linkedClass)
          : goalHW;
        const currentBase = (fxRates[dc]||1) * filtered.reduce((s,h)=>s+(h.valueUSD||0),0)
          * (g.portfolioAlloc||100)/100;
        const progress = targetBase > 0 ? Math.min(100, currentBase/targetBase*100) : 0;
        // Inflation-adjusted future-value target (same logic as goals tab calcGoalMetrics)
        const inflPct = ((g.inflationRate ?? 6)) / 100;
        const isToday = g.targetIsToday !== false;
        const futureTargetBase = isToday
          ? targetBase * Math.pow(1 + inflPct, yearsLeft)
          : targetBase;
        const futureCurrent = currentBase * Math.pow(1 + (g.expectedReturn||10)/100, yearsLeft);
        const gap = Math.max(0, futureTargetBase - futureCurrent);
        const r = (g.expectedReturn||10)/100/12;
        const n = Math.max(0, yearsLeft * 12);
        const sipNeeded = gap > 0 && n > 0 ? (gap * r / (Math.pow(1+r,n)-1)) : 0;
        return { ...g, yearsLeft, targetBase, currentBase, progress, gap, sipNeeded, futureTargetBase };
      });
      return `
        <div class="section">
          <div class="sec-title">Goals & Financial Planning</div>
          <table class="data-table" style="font-size:9px">
            <thead><tr>
              <th>Goal</th><th>Type</th><th>Target</th><th>Current</th>
              <th>Progress</th><th>Time Left</th><th>SIP Needed</th>
            </tr></thead>
            <tbody>
              ${rows.map(g => `
                <tr>
                  <td style="font-weight:600">${g.name||"—"}</td>
                  <td>${g.type||"—"}</td>
                  <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif">${fmt(g.targetBase,dc)}</td>
                  <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif">${fmt(g.currentBase,dc)}</td>
                  <td>
                    <div style="display:flex;align-items:center;gap:6">
                      <div style="flex:1;height:4px;background:#f0ebe4;border-radius:2px">
                        <div style="width:${g.progress.toFixed(0)}%;height:100%;background:${g.progress>=80?"#1D9E75":g.progress>=40?"#D97706":"#A32D2D"};border-radius:2px"></div>
                      </div>
                      <span style="font-weight:600;color:${g.progress>=80?"#1D9E75":g.progress>=40?"#D97706":"#A32D2D"}">${g.progress.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td>${g.yearsLeft >= 1 ? g.yearsLeft.toFixed(1)+"y" : Math.round(g.yearsLeft*12)+"m"}</td>
                  <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:${g.sipNeeded>0?"#007878":"#1D9E75"}">
                    ${g.sipNeeded > 0 ? fmt(g.sipNeeded,dc,true)+"/mo" : "✓ Funded"}
                  </td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>`;
    })();

    const CLASS_COLORS_MAP = {
      "Equities":"#2563EB","Fixed Income":"#1D9E75","Real Estate":"#D97706",
      "Cash":"#78716C","Commodities":"#EA580C","Crypto":"#7C3AED",
      "Alternates":"#4F46E5","Market Neutral":"#0891B2","Other":"#78716C",
      "Mutual Funds":"#65A30D","Indian Mutual Funds":"#65A30D","Global Mutual Funds":"#84CC16",
      "Time Deposits":"#2563EB"
    };

    const asOnFormatted = new Date(pdfAsOnDate).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" });

    const donutSVG = (rows, total, colors, size=140) => {
      if (!total || rows.length === 0) return "";
      let svgPaths = ""; let startAngle = -Math.PI/2; const cx = size/2, cy = size/2, r = size*0.38, inner = size*0.22;
      const palette = ["#2563EB","#1D9E75","#D97706","#7C3AED","#DB2777","#0891B2","#EA580C","#65A30D","#7C3AED","#EA580C","#1D9E75","#2563EB","#78716C","#78716C"];
      rows.forEach(([label, val], i) => {
        const slice = (val/total) * 2 * Math.PI;
        const endAngle = startAngle + slice;
        const x1 = cx + r*Math.cos(startAngle), y1 = cy + r*Math.sin(startAngle);
        const x2 = cx + r*Math.cos(endAngle),   y2 = cy + r*Math.sin(endAngle);
        const ix1 = cx + inner*Math.cos(endAngle), iy1 = cy + inner*Math.sin(endAngle);
        const ix2 = cx + inner*Math.cos(startAngle), iy2 = cy + inner*Math.sin(startAngle);
        const large = slice > Math.PI ? 1 : 0;
        const color = colors?.[label] || palette[i % palette.length];
        svgPaths += `<path d="M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${inner} ${inner} 0 ${large} 0 ${ix2} ${iy2} Z" fill="${color}" opacity="0.9"/>`;
        startAngle = endAngle;
      });
      return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${svgPaths}</svg>`;
    };

    const barChart = (rows, total, colors) => rows.slice(0,8).map(([label, val], i) => {
      const w = total > 0 ? (val/total*100).toFixed(1) : 0;
      const palette = ["#2563EB","#1D9E75","#D97706","#7C3AED","#DB2777","#0891B2","#EA580C","#65A30D"];
      const color = colors?.[label] || palette[i % palette.length];
      return `<div style="margin-bottom:7px">
        <div style="display:flex;justify-content:space-between;font-size:10px;margin-bottom:3px">
          <span style="color:#78716C;font-weight:500">${label}</span>
          <span style="color:#007878;font-weight:600;font-family:monospace">${fmtN(val)} <span style="color:#78716C;font-weight:400">(${w}%)</span></span>
        </div>
        <div style="height:7px;background:#F5ebe0;border-radius:4px;overflow:hidden">
          <div style="height:100%;width:${w}%;background:${color};border-radius:4px;transition:width 0.3s"></div>
        </div>
      </div>`;
    }).join("");

    const holdingRowsHTML = [...reportHoldings].sort((a,b)=>b.valueUSD-a.valueUSD).map((h,i) => {
      const gp = h.costUSD > 0 ? ((h.valueUSD - h.costUSD)/h.costUSD*100).toFixed(1) : null;
      const gpColor = gp === null ? "#78716C" : parseFloat(gp) >= 0 ? "#1D9E75" : "#A32D2D";
      return `<tr>
        <td style="font-size:10px;font-weight:600;color:#1C1917">${h.name}</td>
        <td style="font-size:9.5px;color:#44403C">${h.class}</td>
        <td style="font-size:10px;font-family:monospace;color:#1C1917;text-align:right">${fmtN(h.valueUSD)}</td>
        <td style="font-size:9.5px;text-align:right;color:#44403C">${pct(h.valueUSD, totalVal)}</td>
        <td style="font-size:9.5px;text-align:right;color:#44403C">${h.currency}</td>
        <td style="font-size:9.5px;text-align:right;color:${gpColor};font-family:monospace;font-weight:600">${gp !== null ? (parseFloat(gp)>=0?"+":"")+gp+"%" : "—"}</td>
        <td style="font-size:9px;color:#44403C">${h.client?.name||"—"}</td>
      </tr>`;
    }).join("");

    // Build each section
    const sectSummary = pdfSections.summary ? `
      <div class="section" style="padding-top:0">

        <!-- Running header -->
        <div style="display:flex;align-items:center;justify-content:space-between;padding:0 0 12px;border-bottom:2px solid #0079C1;margin-bottom:20px">
          <img src="${FORTHIUS_LOGO}" alt="Forthius" style="height:28px;width:auto;object-fit:contain;opacity:0.75;filter:none"/>
          <div style="font-size:8.5px;color:#64748B;letter-spacing:0.12em;text-transform:uppercase">${pdfScope==="individual" ? reportClientName : (reportFamilyName||"Portfolio Report")} · ${asOnFormatted}</div>
        </div>

        <!-- KPI Strip (moved from cover) -->
        <div style="background:#0D1B2A;border-radius:12px;padding:18px 24px;margin-bottom:16px;display:flex;gap:0">
          <div style="flex:1;padding:0 24px 0 0;border-right:1px solid rgba(255,255,255,0.1)">
            <div style="font-size:8px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.18em;margin-bottom:6px">Portfolio Value</div>
            <div style="font-size:20px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:${totalVal>=0?'#4ADE80':'#F87171'};line-height:1">${fmtN(totalVal)}</div>
            <div style="font-size:9px;color:rgba(255,255,255,0.45);margin-top:4px">${dc} · FX-adjusted</div>
          </div>
          <div style="flex:1;padding:0 24px;border-right:1px solid rgba(255,255,255,0.1)">
            <div style="font-size:8px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.18em;margin-bottom:6px">Total Return</div>
            <div style="font-size:20px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:${gainPct!=null&&gainPct>=0?'#4ADE80':'#F87171'};line-height:1">${gainPct!=null?(gainPct>=0?'+':'')+gainPct.toFixed(2)+'%':'—'}</div>
            <div style="font-size:9px;color:rgba(255,255,255,0.45);margin-top:4px">Since inception</div>
          </div>
          <div style="flex:1;padding:0 24px;border-right:1px solid rgba(255,255,255,0.1)">
            <div style="font-size:8px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.18em;margin-bottom:6px">Holdings</div>
            <div style="font-size:20px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:#93C5FD;line-height:1">${reportHoldings.length}</div>
            <div style="font-size:9px;color:rgba(255,255,255,0.45);margin-top:4px">Active positions</div>
          </div>
          <div style="flex:1;padding:0 0 0 24px">
            <div style="font-size:8px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.18em;margin-bottom:6px">Risk Score</div>
            <div style="font-size:20px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:${riskColorR};line-height:1">${riskScoreR}/100</div>
            <div style="font-size:9px;color:rgba(255,255,255,0.45);margin-top:4px">${riskLevelR}</div>
          </div>
        </div>

        <!-- Overview Header -->
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid #0D1B2A">
          <div>
            <div style="font-size:9px;font-weight:700;color:#0079C1;text-transform:uppercase;letter-spacing:0.2em;margin-bottom:6px">Portfolio Overview</div>
            <div style="font-size:28px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:#0D1B2A;line-height:1">${fmtN(totalVal)}</div>
            <div style="font-size:11px;color:#64748B;margin-top:5px">${dc} · FX-adjusted · As on ${asOnFormatted}</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:9px;color:#64748B;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:4px">Total Return</div>
            <div style="font-size:26px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:${gainPct!=null&&gainPct>=0?"#1D9E75":"#A32D2D"}">${gainPct!=null?(gainPct>=0?"+":"")+gainPct.toFixed(2)+"%":"—"}</div>
            <div style="font-size:10px;color:#64748B;margin-top:3px">${(gainUSD>=0?"+":"")+fmtN(Math.abs(gainUSD))} since inception</div>
          </div>
        </div>

        <!-- KPI Row 1: Core financials -->
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:10px">
          ${[
            {label:"Portfolio Value",  val:fmtN(totalVal),                                          col:"#0079C1", icon:"💼"},
            {label:"Total Cost Basis", val:fmtN(totalCost),                                         col:"#64748B", icon:"💳"},
            {label:gainUSD>=0?"Unrealised Gain":"Unrealised Loss", val:(gainUSD>=0?"+":"")+fmtN(Math.abs(gainUSD)), col:gainUSD>=0?"#1D9E75":"#A32D2D", icon:gainUSD>=0?"📈":"📉"},
            {label:"Return %",         val:gainPct!=null?(gainPct>=0?"+":"")+gainPct.toFixed(2)+"%":"—", col:gainPct!=null&&gainPct>=0?"#1D9E75":"#A32D2D", icon:"📊"},
          ].map(k=>'<div style="background:#F8FAFF;border:1px solid #C8DCF0;border-radius:8px;padding:12px 14px;border-left:3px solid '+k.col+'">'
            +'<div style="font-size:7.5px;color:#64748B;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:6px;font-weight:600">'+k.icon+' '+k.label+'</div>'
            +'<div style="font-size:18px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif;color:'+k.col+';line-height:1">'+k.val+'</div>'
            +'</div>').join("")}
        </div>

        <!-- KPI Row 2: Portfolio stats -->
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px">
          ${[
            {label:"Total Holdings",   val:reportHoldings.length,                                    col:"#7C3AED", icon:"🗂️"},
            {label:"Listed Assets",    val:reportHoldings.filter(h=>h.listed).length,                col:"#0891B2", icon:"📋"},
            {label:"Asset Classes",    val:Object.keys(byClass).length,                              col:"#D97706", icon:"🎯"},
            {label:"Currencies",       val:Object.keys(byCur).length,                                col:"#059669", icon:"💱"},
          ].map(k=>'<div style="background:#F8FAFF;border:1px solid #C8DCF0;border-radius:8px;padding:12px 14px;border-left:3px solid '+k.col+'">'
            +'<div style="font-size:7.5px;color:#64748B;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:6px;font-weight:600">'+k.icon+' '+k.label+'</div>'
            +'<div style="font-size:22px;font-weight:800;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif;color:'+k.col+';line-height:1">'+k.val+'</div>'
            +'</div>').join("")}
        </div>

        <!-- Portfolio Health Snapshot -->
        <div style="background:#0D1B2A;border-radius:10px;padding:18px 22px;margin-bottom:0">
          <div style="font-size:9px;font-weight:700;color:#93C5FD;text-transform:uppercase;letter-spacing:0.18em;margin-bottom:14px">Portfolio Health Snapshot</div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:20px">
            <div>
              <div style="font-size:8px;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:0.12em;margin-bottom:4px">Risk Level</div>
              <div style="font-size:16px;font-weight:700;color:${riskColorR};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif">${riskLevelR}</div>
              <div style="font-size:9px;color:rgba(255,255,255,0.4);margin-top:2px">Score: ${riskScoreR}/100</div>
            </div>
            <div>
              <div style="font-size:8px;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:0.12em;margin-bottom:4px">Top 5 Concentration</div>
              <div style="font-size:16px;font-weight:700;color:${top5PctR>50?"#F87171":"#4ADE80"};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif">${top5PctR.toFixed(1)}%</div>
              <div style="font-size:9px;color:rgba(255,255,255,0.4);margin-top:2px">${top5PctR>50?"Above":"Below"} 50% threshold</div>
            </div>
            <div>
              <div style="font-size:8px;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:0.12em;margin-bottom:4px">Sharpe Ratio</div>
              <div style="font-size:16px;font-weight:700;color:#93C5FD;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif">${sharpeR}</div>
              <div style="font-size:9px;color:rgba(255,255,255,0.4);margin-top:2px">Risk-adjusted return</div>
            </div>
          </div>
        </div>

      </div>` : "";

    const sectAlloc = pdfSections.allocation ? `
      <div class="section">
        <h2 class="sec-title">Asset Allocation</h2>
        <div style="display:flex;gap:24px;align-items:flex-start">
          <div>${donutSVG(classRows, totalVal, CLASS_COLORS_MAP, 150)}</div>
          <div style="flex:1">${barChart(classRows, totalVal, CLASS_COLORS_MAP)}</div>
        </div>
      </div>` : "";

    const sectSector = pdfSections.sector ? `
      <div class="section">
        <h2 class="sec-title">Sector Allocation</h2>
        <div style="display:flex;gap:24px;align-items:flex-start">
          <div>${donutSVG(sectorRows, totalVal, SECTOR_COLORS_PDF, 150)}</div>
          <div style="flex:1">
            ${sectorRows.map(([label, val], i) => {
              const w = totalVal > 0 ? (val/totalVal*100).toFixed(1) : 0;
              const color = SECTOR_COLORS_PDF[label] || "#78716C";
              return `<div style="margin-bottom:7px">
                <div style="display:flex;justify-content:space-between;font-size:10px;margin-bottom:3px">
                  <span style="color:#78716C;font-weight:500;display:flex;align-items:center;gap:6px">
                    <span style="width:8px;height:8px;border-radius:2px;background:${color};display:inline-block;flex-shrink:0"></span>${label}
                  </span>
                  <span style="color:#007878;font-weight:600;font-family:monospace">${fmtN(val)} <span style="color:#78716C;font-weight:400">(${w}%)</span></span>
                </div>
                <div style="height:7px;background:#F5ebe0;border-radius:4px;overflow:hidden">
                  <div style="height:100%;width:${w}%;background:${color};border-radius:4px"></div>
                </div>
              </div>`;
            }).join("")}
          </div>
        </div>
      </div>` : "";

    const sectRisk = (pdfSections.riskAnalysis && isAdmin) ? `
      <div class="section page-break">
        <h2 class="sec-title">Risk Analysis <span style="font-size:10px;font-weight:400;color:#78716C">(Confidential — Advisor Use Only)</span></h2>

        <!-- Risk Score -->
        <div style="display:flex;gap:20px;align-items:center;background:#f8fafc;border:1px solid #1C1917;border-radius:10px;padding:18px 22px;margin-bottom:18px">
          <div style="text-align:center;flex-shrink:0">
            <div style="font-size:54px;font-weight:800;color:${riskColorR};font-family:monospace;line-height:1">${riskScoreR}</div>
            <div style="font-size:13px;color:${riskColorR};font-weight:700;margin-top:5px">${riskLevelR}</div>
            <div style="font-size:10px;color:#78716C;margin-top:3px">Risk Score (0–100)</div>
          </div>
          <div style="flex:1">
            <div style="height:12px;background:linear-gradient(90deg,#10b981 0%,#D97706 50%,#A32D2D 100%);border-radius:6px;position:relative;margin-bottom:10px">
              <div style="position:absolute;top:50%;left:${riskScoreR}%;transform:translate(-50%,-50%);width:20px;height:20px;border-radius:50%;background:white;border:3px solid ${riskColorR};box-shadow:0 2px 6px rgba(0,0,0,0.25)"></div>
            </div>
            <div style="font-size:11px;color:#78716C;line-height:1.8">
              Score driven by: HHI concentration (${Math.round((hhiR/4000)*30)}/30) · Illiquidity (${Math.round((illiqPctR/100)*30)}/30) · Top-5 weight (${Math.round((top5PctR/100)*20)}/20) · Return volatility (${Math.round((stddevR/50)*20)}/20)
            </div>
          </div>
        </div>

        <!-- Risk Metrics Grid -->
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px">
          ${[
            ["🎯","HHI Concentration", hhiR.toFixed(0), hhiLabelR, hhiColorR],
            ["🔒","Illiquidity Ratio",  illiqPctR.toFixed(1)+"%", "Unlisted assets", illiqPctR>50?"#A32D2D":illiqPctR>30?"#D97706":"#1D9E75"],
            ["🏆","Top 1 Holding",     top1PctR.toFixed(1)+"%",  sortedR[0]?.name||"—", top1PctR>25?"#A32D2D":top1PctR>15?"#D97706":"#1D9E75"],
            ["📊","Top 5 Holdings",    top5PctR.toFixed(1)+"%",  "Combined weight", top5PctR>60?"#A32D2D":top5PctR>40?"#D97706":"#1D9E75"],
            ["💱","Currency Risk",     maxCcyPctR.toFixed(1)+"%", (maxCcyR?.[0]||"—")+" largest", maxCcyPctR>50?"#A32D2D":maxCcyPctR>35?"#D97706":"#1D9E75"],
            ["📈","Return Volatility", stddevR.toFixed(1)+"%",   "Std dev of returns", stddevR>30?"#A32D2D":stddevR>15?"#D97706":"#1D9E75"],
            ["⚖️","Sharpe Proxy",      sharpeR,                  "rf = 5%", parseFloat(sharpeR)>1?"#1D9E75":parseFloat(sharpeR)>0?"#D97706":"#A32D2D"],
            ["🗂️","Asset Classes",     new Set(reportHoldings.map(h=>h.class)).size+"", "Distinct classes", "#007878"],
          ].map(([icon,label,val,sub,color])=>`
            <div style="background:#f8fafc;border:1px solid #1C1917;border-top:3px solid ${color};border-radius:8px;padding:12px 14px">
              <div style="font-size:18px;margin-bottom:6px">${icon}</div>
              <div style="font-size:20px;font-weight:700;color:${color};font-family:monospace">${val}</div>
              <div style="font-size:10px;color:#57534E;font-weight:600;margin-top:4px">${label}</div>
              <div style="font-size:9px;color:#78716C;margin-top:2px">${sub}</div>
            </div>`).join("")}
        </div>

        <!-- Stress Test Table -->
        <h3 style="font-size:11px;font-weight:700;color:#78716C;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:10px">Scenario Stress Tests</h3>
        <table class="data-table" style="margin-bottom:18px">
          <thead><tr>
            <th>Scenario</th><th style="text-align:right">Shock</th>
            <th style="text-align:right">Affected AUM</th><th style="text-align:right">Est. Impact</th><th style="text-align:right">Portfolio Impact</th>
          </tr></thead>
          <tbody>
            ${[
              ["Equity Market Crash","Equities",-30],["Interest Rate Spike (+200bps)","Fixed Income",-12],
              ["USD Strengthens 15%","USD denominated",-15],["Crypto Crash","Digital Assets",-60],
              ["Real Estate Correction","Real Estate",-20],["Commodity Selloff","Commodities",-25],
            ].map(([name, cls, shock], i) => {
              const EQUITY_CLASSES = new Set(["Equities","Indian Mutual Funds","Global Mutual Funds","Mutual Funds","ETF"]);
              const affected = cls === "USD denominated"
                ? reportHoldings.filter(h=>h.currency==="USD").reduce((s,h)=>s+h.valueUSD,0)
                : cls === "Equities"
                  ? reportHoldings.filter(h=>EQUITY_CLASSES.has(h.class)||h.sector===cls).reduce((s,h)=>s+h.valueUSD,0)
                  : reportHoldings.filter(h=>h.class===cls||h.sector===cls).reduce((s,h)=>s+h.valueUSD,0);
              const impact = affected * shock / 100;
              const portImpact = totalVal > 0 ? (impact/totalVal*100).toFixed(1) : "0.0";
              const color = parseFloat(portImpact) > -5 ? "#1D9E75" : parseFloat(portImpact) > -15 ? "#D97706" : "#A32D2D";
              return `<tr style="background:${i%2===0?"#FDFAF6":"#ffffff"}">
                <td style="padding:7px 10px;font-size:10px;font-weight:600;color:#1C1917">${name}</td>
                <td style="padding:7px 10px;font-size:10px;text-align:right;color:#A32D2D;font-family:monospace">${shock}%</td>
                <td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:#44403C">${fmtN(affected)}</td>
                <td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:#A32D2D">${fmtN(Math.abs(impact))} loss</td>
                <td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:${color};font-weight:600">${portImpact}%</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>

        <!-- Concentration Top Holdings -->
        <h3 style="font-size:11px;font-weight:700;color:#78716C;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:10px">Concentration — Top 10 Holdings</h3>
        <table class="data-table">
          <thead><tr><th>#</th><th>Holding</th><th>Class</th><th style="text-align:right">Value</th><th style="text-align:right">Weight</th><th style="text-align:right">Cumulative</th></tr></thead>
          <tbody>
            ${(()=>{ let cum=0; return sortedR.slice(0,10).map((h,i)=>{ const w=(h.valueUSD/totalVR*100); cum+=w; const color=w>20?"#A32D2D":w>10?"#D97706":"#1D9E75"; return `<tr style="background:${i%2===0?"#FDFAF6":"#ffffff"}"><td style="padding:7px 10px;font-size:10px;color:#44403C;font-weight:600">${i+1}</td><td style="padding:7px 10px;font-size:10px;font-weight:600;color:#1C1917">${h.name}</td><td style="padding:7px 10px;font-size:9.5px;color:#44403C">${h.class}</td><td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:#007878">${fmtN(h.valueUSD)}</td><td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:${color};font-weight:600">${w.toFixed(1)}%</td><td style="padding:7px 10px;font-size:10px;text-align:right;font-family:monospace;color:#44403C">${cum.toFixed(1)}%</td></tr>`; }).join(""); })()}
          </tbody>
        </table>

        <div style="margin-top:12px;padding:10px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:7px;font-size:9px;color:#991b1b;line-height:1.6">
          <strong>Risk Disclosure:</strong> Risk metrics are based on portfolio snapshot data and are indicative only. VaR estimates are not shown. Stress scenarios are illustrative and do not predict future losses. This section is for advisor use only and should not be shared directly with clients without appropriate context and disclosures.
        </div>
      </div>` : "";

    const sectCurrency = pdfSections.currency ? `
      <div class="section">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px">
          <div>
            <h2 class="sec-title" style="margin-bottom:10px">Currency Exposure</h2>
            <div style="display:flex;gap:12px;align-items:flex-start">
              <div>${donutSVG(curRows, totalVal, {}, 100)}</div>
              <div style="flex:1">${barChart(curRows, totalVal, {})}</div>
            </div>
          </div>
          ${pdfSections.geography ? `<div>
            <h2 class="sec-title" style="margin-bottom:10px">Geographic Exposure</h2>
            ${barChart(geoRows.slice(0,10), totalVal, {})}
          </div>` : ""}
        </div>
      </div>` : "";

    const sectGeo = "";  // Now merged into sectCurrency row above

    const sectCap = pdfSections.capSize && capRows.length > 0 ? `
      <div class="section">
        <h2 class="sec-title">Market Cap Breakdown <span style="font-size:10px;font-weight:400;color:#78716C">(Listed Equities Only)</span></h2>
        <div style="display:flex;gap:24px;align-items:flex-start">
          <div>${donutSVG(capRows, capRows.reduce((s,[,v])=>s+v,0), {"Giant":"#2563EB","Mega":"#4F46E5","Large":"#7C3AED","Mid":"#0891B2","Small":"#1D9E75","Micro":"#D97706"}, 130)}</div>
          <div style="flex:1">${barChart(capRows, capRows.reduce((s,[,v])=>s+v,0), {"Giant":"#2563EB","Mega":"#4F46E5","Large":"#7C3AED","Mid":"#0891B2","Small":"#1D9E75","Micro":"#D97706"})}</div>
        </div>
      </div>` : "";

    const sectHoldings = pdfSections.holdings ? `
      <div class="section page-break">
        <h2 class="sec-title">Holdings Detail</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Asset Name</th><th>Class</th><th style="text-align:right">Value (${dc})</th>
              <th style="text-align:right">Weight</th><th style="text-align:right">Currency</th>
              <th style="text-align:right">G/L%</th><th>Owner</th>
            </tr>
          </thead>
          <tbody>${holdingRowsHTML}</tbody>
        </table>
      </div>` : "";

    const sectPerf = pdfSections.performance ? `
      <div class="section">
        <h2 class="sec-title">Performance Summary</h2>
        <table class="data-table">
          <thead><tr><th>Asset Class</th><th style="text-align:right">Value (${dc})</th><th style="text-align:right">Cost Basis</th><th style="text-align:right">Gain/Loss</th><th style="text-align:right">Return %</th></tr></thead>
          <tbody>
            ${classRows.map(([cls, val], i) => {
              const cost = byClassCost[cls] || 0;
              const gain = val - cost;
              const ret = cost > 0 ? ((gain/cost)*100).toFixed(1) : null;
              const color = ret === null ? "#78716C" : parseFloat(ret) >= 0 ? "#1D9E75" : "#A32D2D";
              return `<tr style="background:${i%2===0?"#FDFAF6":"#ffffff"}">
                <td style="padding:7px 10px;font-size:10px;font-weight:600;color:#1C1917">${cls}</td>
                <td style="padding:7px 10px;font-size:10px;font-family:monospace;color:#44403C;text-align:right">${fmtN(val)}</td>
                <td style="padding:7px 10px;font-size:10px;font-family:monospace;text-align:right;color:#44403C">${fmtN(cost)}</td>
                <td style="padding:7px 10px;font-size:10px;font-family:monospace;text-align:right;color:${color}">${ret!==null?(parseFloat(gain)>=0?"+":"")+fmtN(Math.abs(gain)):"—"}</td>
                <td style="padding:7px 10px;font-size:10px;font-family:monospace;text-align:right;color:${color}">${ret!==null?(parseFloat(ret)>=0?"+":"")+ret+"%":"—"}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>` : "";

    // ── Portfolio Health Score for PDF ──
    const phHoldings = reportHoldings;
    const phN = phHoldings.length || 1;
    const phTotalV = phHoldings.reduce((s,h)=>s+h.valueUSD,0)||1;
    const phClasses = [...new Set(phHoldings.map(h=>h.class))].length;
    const phCountries = [...new Set(phHoldings.map(h=>h.country))].length;
    const phCcys = [...new Set(phHoldings.map(h=>h.currency))].length;
    const phHhi = phHoldings.reduce((s,h)=>s+Math.pow(h.valueUSD/phTotalV*100,2),0);
    const phLiqPct = phHoldings.filter(h=>h.listed).reduce((s,h)=>s+h.valueUSD,0)/phTotalV*100;
    const phSorted = [...phHoldings].sort((a,b)=>b.valueUSD-a.valueUSD);
    const phTop1 = phSorted[0]?.valueUSD/phTotalV*100||0;
    const phTop5 = phSorted.slice(0,5).reduce((s,h)=>s+h.valueUSD/phTotalV*100,0);
    const phMaxCcy = Math.max(...Object.values(phHoldings.reduce((a,h)=>{a[h.currency]=(a[h.currency]||0)+h.valueUSD;return a;},{})).map(v=>v/phTotalV*100),0);
    const phGains = phHoldings.map(h=>h.gainPct||0);
    const phAvgGain = phGains.reduce((s,v)=>s+v,0)/Math.max(1,phGains.length);
    const phMean = phAvgGain;
    const phStddev = Math.sqrt(phGains.reduce((s,v)=>s+Math.pow(v-phMean,2),0)/Math.max(1,phGains.length));
    const phSharpe = phStddev>0?(phAvgGain-5)/phStddev:0;
    const phWinners = phGains.filter(v=>v>0).length;
    const phDivScore = Math.max(0,Math.min(25,25-(phHhi/10000)*25+(Math.min(phClasses,8)/8)*10+(Math.min(phCountries,10)/10)*5));
    const phConcScore = Math.max(0,25-(phTop1>25?(phTop1-25)*0.5:0)-(phTop5>60?(phTop5-60)*0.3:0)-(phMaxCcy>50?(phMaxCcy-50)*0.2:0));
    const phLiqScore = Math.min(25,(phLiqPct/100)*25);
    const phPerfScore = Math.max(0,Math.min(25,(phAvgGain>0?Math.min(10,phAvgGain*0.3):Math.max(-5,phAvgGain*0.2))+(phWinners/phN)*8+Math.max(0,Math.min(7,phSharpe*3.5))));
    const phScore = Math.min(100,Math.max(0,Math.round(phDivScore+phConcScore+phLiqScore+phPerfScore)));
    const phGrade = phScore>=80?"A":phScore>=65?"B":phScore>=50?"C":phScore>=35?"D":"F";
    const phLabel = phScore>=80?"Excellent":phScore>=65?"Good":phScore>=50?"Fair":phScore>=35?"Needs Attention":"At Risk";
    const phColor = phScore>=80?"#1D9E75":phScore>=65?"#22C55E":phScore>=50?"#D97706":phScore>=35?"#EA580C":"#A32D2D";
    const phDims = [
      {label:"Diversification",val:Math.round(phDivScore),max:25,color:"#2563EB"},
      {label:"Concentration Risk",val:Math.round(phConcScore),max:25,color:"#7C3AED"},
      {label:"Liquidity",val:Math.round(phLiqScore),max:25,color:"#0891B2"},
      {label:"Return Quality",val:Math.round(phPerfScore),max:25,color:"#059669"},
    ];
    const phInsights = [
      `Portfolio spans ${phClasses} asset class${phClasses!==1?"es":""} across ${phCountries} countr${phCountries!==1?"ies":"y"} in ${phCcys} currenc${phCcys!==1?"ies":"y"}.`,
      phTop1>25?`⚠ Top holding (${phSorted[0]?.name||""}) at ${phTop1.toFixed(1)}% — consider rebalancing.`:`✓ Single-holding concentration at ${phTop1.toFixed(1)}% within bounds.`,
      phLiqPct<50?`⚠ ${(100-phLiqPct).toFixed(0)}% of portfolio illiquid — ensure adequate liquidity runway.`:`✓ ${phLiqPct.toFixed(0)}% of portfolio is listed and liquid.`,
      phAvgGain>=0?`✓ ${phWinners} of ${phN} holdings (${(phWinners/phN*100).toFixed(0)}%) positive, avg gain ${phAvgGain.toFixed(1)}%.`:`⚠ ${phN-phWinners} of ${phN} holdings underwater — avg return ${phAvgGain.toFixed(1)}%.`,
      phSharpe>1?`✓ Sharpe proxy ${phSharpe.toFixed(2)} — good risk-adjusted return.`:phSharpe>0?`Sharpe proxy ${phSharpe.toFixed(2)} — modest risk-adjusted return.`:`⚠ Negative Sharpe (${phSharpe.toFixed(2)}) — returns not compensating for risk.`,
    ];

    const sectPortfolioHealth = pdfSections.portfolioHealth ? `
      <div class="section">
        <h2 class="sec-title">🏥 Portfolio Health Score</h2>
        <div style="display:grid;grid-template-columns:auto 1fr;gap:24px;align-items:start">
          <div style="text-align:center;min-width:120px">
            <div style="font-size:54px;font-weight:800;color:${phColor};font-family:monospace;line-height:1">${phScore}</div>
            <div style="font-size:13px;color:${phColor};font-weight:700;margin-top:4px">${phGrade} · ${phLabel}</div>
            <div style="font-size:10px;color:#78716C;margin-top:2px">out of 100</div>
            <div style="margin-top:16px;height:10px;background:linear-gradient(90deg,#1D9E75 0%,#D97706 50%,#A32D2D 100%);border-radius:5px;position:relative">
              <div style="position:absolute;top:50%;left:${phScore}%;transform:translate(-50%,-50%);width:14px;height:14px;border-radius:50%;background:white;border:2.5px solid ${phColor}"></div>
            </div>
          </div>
          <div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
              ${phDims.map(d=>`
                <div style="background:#F5EBE0;border-radius:8px;padding:10px 12px">
                  <div style="display:flex;justify-content:space-between;margin-bottom:5px">
                    <span style="font-size:10px;color:#78716C;font-weight:600">${d.label}</span>
                    <span style="font-size:11px;font-weight:700;color:${d.color};font-family:monospace">${d.val}/${d.max}</span>
                  </div>
                  <div style="height:6px;background:#E7E5E4;border-radius:3px">
                    <div style="height:100%;width:${(d.val/d.max*100).toFixed(0)}%;background:${d.color};border-radius:3px"></div>
                  </div>
                </div>`).join("")}
            </div>
            <div style="display:flex;flex-direction:column;gap:5px">
              ${phInsights.map(line=>{
                const warn=line.startsWith("⚠"),ok=line.startsWith("✓");
                const col=warn?"#EA580C":ok?"#1D9E75":"#78716C";
                const bg=warn?"#FEF2F0":ok?"#F0FDF4":"#F9F7F5";
                return `<div style="font-size:10px;color:${col};line-height:1.5;padding:5px 8px;background:${bg};border-radius:5px">${line}</div>`;
              }).join("")}
            </div>
          </div>
        </div>
      </div>` : "";

    // ── Concentration analysis for PDF ──
    const CONC_THRESHOLDS = {single:10,sector:35,currency:50,country:40};
    const singleMax = (phTop1).toFixed(1);
    const maxSector = Math.max(...Object.values(phHoldings.reduce((a,h)=>{a[h.class]=(a[h.class]||0)+h.valueUSD;return a;},{})).map(v=>v/phTotalV*100),0).toFixed(1);
    const sectConcentration = pdfSections.concentration ? `
      <div class="section">
        <h2 class="sec-title">🌡️ Concentration Risk</h2>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:14px">
          ${[
            ["Top Holding",`${singleMax}%`,parseFloat(singleMax)>CONC_THRESHOLDS.single?"#A32D2D":"#1D9E75",`Threshold: ${CONC_THRESHOLDS.single}%`],
            ["Top Sector/Class",`${maxSector}%`,parseFloat(maxSector)>CONC_THRESHOLDS.sector?"#A32D2D":"#1D9E75",`Threshold: ${CONC_THRESHOLDS.sector}%`],
            ["Top Currency",`${phMaxCcy.toFixed(1)}%`,phMaxCcy>CONC_THRESHOLDS.currency?"#A32D2D":"#1D9E75",`Threshold: ${CONC_THRESHOLDS.currency}%`],
          ].map(([l,v,c,t])=>`<div style="background:#F5EBE0;border-radius:8px;padding:12px;text-align:center"><div style="font-size:22px;font-weight:800;color:${c};font-family:monospace">${v}</div><div style="font-size:10px;color:#78716C;margin-top:4px;font-weight:600">${l}</div><div style="font-size:9px;color:#57534E;margin-top:2px">${t}</div></div>`).join("")}
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:10px">
          <thead><tr style="background:#F5EBE0"><th style="padding:7px 10px;text-align:left;font-weight:700">Asset Class</th><th style="text-align:right;padding:7px 10px">Value (USD)</th><th style="text-align:right;padding:7px 10px">Weight</th><th style="text-align:right;padding:7px 10px">Flag</th></tr></thead>
          <tbody>${Object.entries(phHoldings.reduce((a,h)=>{a[h.class]=(a[h.class]||0)+h.valueUSD;return a;},{})).sort((a,b)=>b[1]-a[1]).map(([cls,v],i)=>{
            const wt=(v/phTotalV*100).toFixed(1);
            const flag=parseFloat(wt)>35?"⚠ Overweight":parseFloat(wt)>15?"🟡 Monitor":"✓ OK";
            return `<tr style="background:${i%2?"#F9F7F5":"white"}"><td style="padding:6px 10px;font-weight:600">${cls}</td><td style="text-align:right;padding:6px 10px;font-family:monospace">${fmtN(v)}</td><td style="text-align:right;padding:6px 10px;font-family:monospace">${wt}%</td><td style="text-align:right;padding:6px 10px;color:${parseFloat(wt)>35?"#A32D2D":parseFloat(wt)>15?"#D97706":"#1D9E75"};font-weight:600">${flag}</td></tr>`;
          }).join("")}</tbody>
        </table>
      </div>` : "";

    // ── Correlation for PDF ──
    const corrClasses = [...new Set(phHoldings.map(h=>h.class))];
    const getPhCorr = (a,b) => {
      if (a===b) return 1;
      const aH = phHoldings.filter(h=>h.class===a), bH = phHoldings.filter(h=>h.class===b);
      if (!aH.length||!bH.length) return 0;
      const sameClass = a===b?1:0, sameCcy = aH[0]?.currency===bH[0]?.currency?0.25:0, sameCtry = aH[0]?.country===bH[0]?.country?0.20:0;
      return Math.min(0.99, sameClass*0.45+sameCcy+sameCtry+0.1);
    };
    const sectCorrelation = pdfSections.correlation ? `
      <div class="section">
        <h2 class="sec-title">📊 Asset Class Correlation Matrix</h2>
        <div style="font-size:9px;color:#78716C;margin-bottom:10px">Estimated from structural similarity — asset class, currency, country. 🔴 High ≥0.8 · 🟡 Moderate 0.6–0.8 · 🟢 Low &lt;0.4</div>
        <table style="border-collapse:collapse;font-size:9px;width:100%">
          <thead><tr><th style="padding:5px 8px;text-align:left;background:#F5EBE0;font-weight:700">Class</th>${corrClasses.map(c=>`<th style="padding:5px 6px;text-align:center;background:#F5EBE0;font-weight:700;max-width:70px;overflow:hidden;font-size:8px">${c.length>12?c.slice(0,11)+"…":c}</th>`).join("")}</tr></thead>
          <tbody>${corrClasses.map((r,ri)=>`<tr>${[`<td style="padding:5px 8px;font-weight:600;background:${ri%2?"#F9F7F5":"white"}">${r.length>14?r.slice(0,13)+"…":r}</td>`,...corrClasses.map(c=>{const v=getPhCorr(r,c);const bg=r===c?"#007878":v>=0.8?"rgba(220,38,38,0.2)":v>=0.6?"rgba(217,119,6,0.18)":v>=0.4?"rgba(234,179,8,0.14)":"rgba(22,163,74,0.1)";const col=r===c?"#fff":v>=0.8?"#A32D2D":v>=0.6?"#D97706":v>=0.4?"#D97706":"#1D9E75";return `<td style="padding:5px 6px;text-align:center;background:${bg};color:${col};font-weight:${r===c?700:600};font-family:monospace">${r===c?"1.00":v.toFixed(2)}</td>`;})].join("")}</tr>`).join("")}</tbody>
        </table>
      </div>` : "";

    // ── Volatility for PDF ──
    const CLASS_VOL_PDF = {Crypto:75,Equities:22,"Indian Equities":26,"Indian Mutual Funds":16,"Global Mutual Funds":14,Debt:5,Gold:16,"Real Estate":12,"Alternates":20,"Market Neutral":8,"Art & Collectibles":25,"Fixed Income":6,Commodities:18};
    const RF_PDF = 5.0;
    const sectVolatility = pdfSections.volatility ? `
      <div class="section">
        <h2 class="sec-title">🎢 Volatility &amp; Risk-Adjusted Returns</h2>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:14px">
          ${(()=>{
            const rows = phHoldings.map(h=>{const baseVol=CLASS_VOL_PDF[h.class]||20;const noise=(h.currentPrice%17)/100;const vol=baseVol+noise*3;const annRet=h.gainPct||0;const sh=vol>0?(annRet-RF_PDF)/vol:0;const wt=h.valueUSD/phTotalV;return{...h,vol,sh,wt};});
            const wVol=(rows.reduce((s,r)=>s+r.vol*r.wt,0)).toFixed(1);
            const wSh=(rows.reduce((s,r)=>s+r.sh*r.wt,0)).toFixed(2);
            const wRet=(rows.reduce((s,r)=>s+(r.gainPct||0)*r.wt,0)).toFixed(1);
            return [["Portfolio Vol",wVol+"%","#A32D2D"],["Portfolio Sharpe",wSh,"#2563EB"],["Weighted Return",wRet+"%",parseFloat(wRet)>=0?"#1D9E75":"#A32D2D"]].map(([l,v,c])=>`<div style="background:#F5EBE0;border-radius:8px;padding:12px;text-align:center"><div style="font-size:22px;font-weight:800;color:${c};font-family:monospace">${v}</div><div style="font-size:10px;color:#78716C;margin-top:4px;font-weight:600">${l}</div></div>`).join("");
          })()}
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:10px">
          <thead><tr style="background:#F5EBE0"><th style="padding:7px 10px;text-align:left;font-weight:700">Holding</th><th style="text-align:right;padding:7px 10px">Weight</th><th style="text-align:right;padding:7px 10px">Vol%</th><th style="text-align:right;padding:7px 10px">Return%</th><th style="text-align:right;padding:7px 10px">Sharpe</th></tr></thead>
          <tbody>${phHoldings.map((h,i)=>{const vol=(CLASS_VOL_PDF[h.class]||20).toFixed(1);const sh=(((h.gainPct||0)-RF_PDF)/(CLASS_VOL_PDF[h.class]||20)).toFixed(2);const wt=(h.valueUSD/phTotalV*100).toFixed(1);return`<tr style="background:${i%2?"#F9F7F5":"white"}"><td style="padding:6px 10px;font-weight:600;max-width:200px;word-wrap:break-word;white-space:normal">${h.name}</td><td style="text-align:right;padding:6px 10px;font-family:monospace">${wt}%</td><td style="text-align:right;padding:6px 10px;font-family:monospace;color:#A32D2D">${vol}%</td><td style="text-align:right;padding:6px 10px;font-family:monospace;color:${(h.gainPct||0)>=0?"#1D9E75":"#A32D2D"}">${(h.gainPct||0).toFixed(1)}%</td><td style="text-align:right;padding:6px 10px;font-family:monospace;color:${parseFloat(sh)>1?"#1D9E75":parseFloat(sh)>0?"#D97706":"#A32D2D"};font-weight:600">${sh}</td></tr>`;}).join("")}</tbody>
        </table>
      </div>` : "";

    // ── IRR section for PDF ──
    const sectIRR = pdfSections.irr && transactions.length > 0 ? `
      <div class="section">
        <h2 class="sec-title">📐 IRR / XIRR Analysis</h2>
        <div style="padding:12px 14px;background:#F5EBE0;border-radius:8px;font-size:10px;color:#78716C;line-height:1.7">
          Based on ${transactions.length} transaction${transactions.length!==1?"s":""} recorded in the system.<br/>
          Holdings with recorded buy transactions are eligible for XIRR calculation.<br/>
          <span style="color:#007878;font-weight:600">Note:</span> For full IRR breakdown by holding, refer to the IRR Analysis tab in the Forthius dashboard.
        </div>
      </div>` : "";

    const sectDisclaimer = pdfSections.disclaimer ? `
      <div class="section" style="margin-top:24px;background:#f8fffe;border:1px solid #e0f0f0;border-radius:8px;padding:16px">
        <h2 class="sec-title" style="color:#78716C;font-size:11px">Disclaimer</h2>
        <p style="font-size:9px;color:#78716C;line-height:1.6;margin:0">
          This report is prepared by Forthius Finalytics for informational purposes only and does not constitute investment advice, an offer to buy or sell securities, or a solicitation. The information herein is based on sources believed to be reliable but accuracy is not guaranteed. Past performance is not indicative of future results. Market values are indicative and may differ from actual traded prices. This document is confidential and intended solely for the named recipient. Please consult your financial advisor before making any investment decisions.
        </p>
      </div>` : "";

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${reportSubtitle} — Forthius Finalytics</title>
<style>
  @page { size: A4; margin: 18mm 15mm 15mm 15mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 210mm; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; background:#fff; color:#1C1917; }

  /* ── COVER PAGE ── */
  .cover {
    width: calc(210mm + 30mm); margin: -18mm -15mm;
    min-height: calc(297mm + 33mm);
    display: flex; flex-direction: column;
    background: #0D1B2A;
    page-break-after: always; break-after: always;
    position: relative; overflow: hidden;
  }

  /* Geometric accent shapes */
  .cover::before {
    content: ''; position: absolute;
    top: -80px; right: -80px;
    width: 420px; height: 420px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,121,193,0.35) 0%, transparent 70%);
    pointer-events: none;
  }
  .cover::after {
    content: ''; position: absolute;
    bottom: 60px; left: -60px;
    width: 300px; height: 300px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,200,180,0.18) 0%, transparent 70%);
    pointer-events: none;
  }

  .cover-inner { position: relative; z-index: 1; display: flex; flex-direction: column; height: 100%; min-height: 297mm; padding: 0; }

  /* Top accent bar */
  .cover-topbar { height: 5px; background: linear-gradient(90deg, #0079C1 0%, #00C8B4 50%, #FFD700 100%); margin-top: 18mm; }

  /* Logo area */
  .cover-header { padding: 72px 72px 36px; display: flex; align-items: center; justify-content: space-between; }
  .cover-logo-wrap { display: flex; align-items: center; gap: 16px; }
  .cover-logo-img { height: 56px; width: auto; object-fit: contain; filter: brightness(0) invert(1); }
  .cover-divider-v { width: 1px; height: 40px; background: rgba(255,255,255,0.25); margin: 0 16px; }
  .cover-brand-name { font-size: 14px; font-weight: 700; color: #ffffff; letter-spacing: 0.12em; text-transform: uppercase; }
  .cover-brand-sub { font-size: 9px; color: rgba(255,255,255,0.5); letter-spacing: 0.22em; text-transform: uppercase; margin-top: 3px; }
  .cover-date-chip { font-size: 9px; color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); border-radius: 20px; padding: 5px 14px; letter-spacing: 0.1em; white-space: nowrap; }

  /* Hero section */
  .cover-hero { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 0 72px 40px; }
  .cover-eyebrow { font-size: 10px; color: #00C8B4; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; margin-bottom: 18px; }
  .cover-client-name { font-size: 48px; font-weight: 800; color: #ffffff; line-height: 1.1; margin-bottom: 10px; max-width: 520px; }
  .cover-report-type { font-size: 15px; color: rgba(255,255,255,0.6); font-weight: 400; margin-bottom: 48px; }

  /* Horizontal rule */
  .cover-rule { width: 60px; height: 3px; background: linear-gradient(90deg,#0079C1,#00C8B4); border-radius: 2px; margin-bottom: 44px; }

  /* Meta grid */
  .cover-meta { display: flex; gap: 0; }
  .cover-meta-item { padding: 0 40px 0 0; border-right: 1px solid rgba(255,255,255,0.12); margin-right: 40px; }
  .cover-meta-item:last-child { border-right: none; }
  .cover-meta-label { font-size: 8px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 5px; }
  .cover-meta-value { font-size: 13px; font-weight: 600; color: #ffffff; }

  /* KPI strip */
  .cover-kpi-strip { margin: 40px 72px 0; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 22px 28px; display: flex; gap: 0; }
  .cover-kpi { flex: 1; padding: 0 24px; border-right: 1px solid rgba(255,255,255,0.1); }
  .cover-kpi:first-child { padding-left: 0; }
  .cover-kpi:last-child { border-right: none; }
  .cover-kpi-label { font-size: 8px; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.18em; margin-bottom: 6px; }
  .cover-kpi-value { font-size: 20px; font-weight: 800; font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif; color: #ffffff; line-height: 1; }
  .cover-kpi-sub { font-size: 9px; color: rgba(255,255,255,0.45); margin-top: 4px; }

  /* Footer */
  .cover-footer { padding: 24px 72px 20px; display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(255,255,255,0.08); margin-top: 32px;  margin-bottom: 15mm; }
  .cover-footer-left { font-size: 8.5px; color: rgba(255,255,255,0.35); letter-spacing: 0.12em; text-transform: uppercase; }
  .cover-footer-right { font-size: 8.5px; color: rgba(255,255,255,0.35); letter-spacing: 0.08em; }

  /* ── CONTENT PAGES ── */
  .content { padding: 8px 32px 24px; max-width: 210mm; margin: 0 auto; }
  .content-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 0 14px; border-bottom: 2px solid #0079C1; margin-bottom: 28px; }
  .content-header-logo { height: 28px; width: auto; object-fit: contain; opacity: 0.7; }
  .content-header-title { font-size: 8.5px; color: #64748B; letter-spacing: 0.12em; text-transform: uppercase; }

  .section { margin-bottom: 18px; page-break-inside: avoid; break-inside: avoid; }
  .section-lg { margin-bottom: 26px; }
  .sec-title { font-size: 10px; font-weight: 700; color: #0079C1; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 12px; padding-bottom: 5px; border-bottom: 1px solid #E2EDF5; }
  .kpi-grid { display: grid; gap: 10px; page-break-inside: avoid; break-inside: avoid; }
  .kpi-card { background: #F0F7FF; border: 1px solid #C8DCF0; border-radius: 8px; padding: 11px 14px; }
  .kpi-label { font-size: 7.5px; color: #64748B; text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 5px; font-weight: 600; }
  .kpi-val { font-size: 17px; font-weight: 700; font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif; line-height: 1.2; }
  .kpi-sub { font-size: 8px; color: #78716C; margin-top: 3px; }
  .data-table { width: 100%; border-collapse: collapse; font-size: 9px; }
  .data-table th { background: #0D1B2A; color: #ffffff; padding: 7px 9px; text-align: left; font-size: 7.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
  .data-table td { padding: 5px 9px; border-bottom: 1px solid #EEF2F7; vertical-align: middle; color: #1C1917; }
  .data-table tr:nth-child(even) td { background: #F8FAFF; }
  .page-break { page-break-before: always; break-before: always; padding-top: 28px; }
  @media print {
    @page { size: A4; margin: 18mm 15mm 15mm 15mm; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    html, body { width: 210mm; }
    body { margin: 0; }
    .cover { min-height: 297mm; page-break-after: always; }
    .no-print { display: none !important; }
    .section { page-break-inside: avoid; break-inside: avoid; }
    .data-table { page-break-inside: auto; }
    .data-table tr { page-break-inside: avoid; break-inside: avoid; }
    .page-break { page-break-before: always; break-before: always; }
  }
</style>
</head>
<body>

<!-- COVER PAGE -->
<div class="cover">
  <div class="cover-inner">
    <div class="cover-topbar"></div>

    <div class="cover-header">
      <div class="cover-logo-wrap">
        <img class="cover-logo-img" src="${FORTHIUS_LOGO}" alt="Forthius Finalytics"/>
        <div class="cover-divider-v"></div>
        <div>
          <div class="cover-brand-name">Forthius Finalytics</div>
          <div class="cover-brand-sub">Portfolio Intelligence</div>
        </div>
      </div>
      <div class="cover-date-chip">📅 ${asOnFormatted}</div>
    </div>

    <div class="cover-hero">
      <div class="cover-eyebrow">Wealth Report · ${dc}</div>
      <div class="cover-client-name">${pdfScope==="individual" ? reportClientName : (reportFamilyName || "All Portfolio")}</div>
      <div class="cover-report-type">${reportSubtitle}</div>
      <div class="cover-rule"></div>
      <div class="cover-meta">
        <div class="cover-meta-item">
          <div class="cover-meta-label">Prepared For</div>
          <div class="cover-meta-value">${pdfScope==="individual" ? reportClientName : (reportFamilyName || "All Families")}</div>
        </div>
        <div class="cover-meta-item">
          <div class="cover-meta-label">Prepared By</div>
          <div class="cover-meta-value">${pdfPreparedBy}</div>
        </div>
        <div class="cover-meta-item">
          <div class="cover-meta-label">Report Date</div>
          <div class="cover-meta-value">${asOnFormatted}</div>
        </div>
      </div>
    </div>


    <div class="cover-footer">
      <div class="cover-footer-left">Confidential · For intended recipient only</div>
      <div class="cover-footer-right">Forthius Finalytics · forthius-dashboard.vercel.app</div>
    </div>
  </div>
</div>


<!-- CONTENT PAGES -->
<div class="content">
  ${sectSummary}
  ${sectPortfolioHealth}
  ${sectAlloc}
  ${sectSector}
  ${sectPerf}
  ${sectCurrency}
  ${sectGeo}
  ${sectCap}
  ${sectIRR}
  ${sectConcentration}
  ${sectCorrelation}
  ${sectVolatility}
  ${sectGoals}
  ${sectHoldings}
  ${sectRisk}
  ${sectDisclaimer}
</div>

<script>window.onload=()=>{window.print();};</script>
</body>
</html>`;

    try {
      // Use Blob URL — never blocked by popup blockers unlike window.open("","_blank")
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, "_blank");
      if (!win) {
        // Popup blocked fallback — write into a new tab via anchor click
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      // Revoke after 60 s to free memory
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch(e) {
      alert("PDF generation error: " + e.message);
    }
    setPdfModal(false);
  };


  // ── DB Loading / Error screens ──
  if (signedOut) {
    // Safety: if anything in the login render throws, show a minimal fallback
    // rather than a blank screen
    const doLogin = async () => {
      setLoginErr(""); setLoginBusy(true);
      // Always refresh clientLogins from Supabase before checking
      // Use freshLogins directly — React setState is async so clientLogins would still be stale
      let currentLogins = clientLogins;
      try {
        const freshLogins = await fetchClientLogins();
        if (freshLogins) { setClientLogins(freshLogins); currentLogins = freshLogins; }
      } catch(e) { /* non-critical — proceed with cached data */ }
      if (loginRole === "admin") {
        const adminEntries = Object.entries(clientLogins)
          .filter(([k]) => k === "__admin__" || k.startsWith("__admin__"));

        // 1. Check Supabase profiles (synced across devices) — highest priority
        const supabaseAdminCred = profileData?.["__admin_cred__"] || null;

        // 2. Check localStorage (fallback for same device)
        const localAdminCred = (() => { try { return JSON.parse(localStorage.getItem("fw_admin_cred") || "null"); } catch { return null; } })();

        // Use Supabase cred if available, otherwise local
        const adminCred = supabaseAdminCred || localAdminCred;

        // Sync: if Supabase has newer cred, update localStorage
        if (supabaseAdminCred?.updatedAt && (!localAdminCred?.updatedAt || supabaseAdminCred.updatedAt > localAdminCred.updatedAt)) {
          try { localStorage.setItem("fw_admin_cred", JSON.stringify(supabaseAdminCred)); } catch {}
        }

        // Check against all admin entries — async password verify
        let matchedAdmin = null;

        // Check best available credential
        if (adminCred?.password) {
          const lid = adminCred.loginId || "kshitij.lodha";
          if (loginId.trim() === lid && await verifyPassword(loginPass, adminCred.password)) {
            matchedAdmin = ["__admin__", adminCred];
          }
        }

        // Check in-memory Supabase entries (legacy)
        if (!matchedAdmin) {
          for (const [k, v] of adminEntries) {
            const lid = v?.loginId || "kshitij.lodha";
            const pwd = v?.password || "admin123";
            if (loginId.trim() === lid && await verifyPassword(loginPass, pwd)) {
              matchedAdmin = [k, v]; break;
            }
          }
        }

        // Fallback default credentials — ONLY if no password has ever been set anywhere
        const noPasswordSet = !adminCred && adminEntries.length === 0;
        const defaultMatch = noPasswordSet
          && loginId.trim() === "kshitij.lodha"
          && loginPass === "admin123";

        // If matched via default, upgrade to hashed credential immediately
        if (defaultMatch && !matchedAdmin) {
          makePasswordHash(loginPass).then(hashed => {
            const cred = { loginId: loginId.trim(), password: hashed, updatedAt: new Date().toISOString() };
            try { localStorage.setItem("fw_admin_cred", JSON.stringify(cred)); } catch {}
            upsertProfile("__admin_cred__", cred).catch(()=>{});
          }).catch(()=>{});
        }
        if (matchedAdmin || defaultMatch) {
          const adminKey = matchedAdmin ? matchedAdmin[0] : "__admin__";
          setIsAdmin(true);
          setLoggedInClientId(null);
          setSignedOut(false); setSessionExpired(false);
          setLoginBusy(false);
          // Remember session
          try { localStorage.setItem("fw_session_v1", JSON.stringify({
            role:"admin", key:adminKey, ts:Date.now()
          })); } catch {}
          setLoginPass(""); setLoginId("");
          applyThemeForUser(adminKey, profileData);
        } else {
          setLoginBusy(false); setLoginErr("Invalid admin credentials.");
        }
        return;
      }
      // Client login — look up by loginId across all clientLogins
      // Async verify against all client logins (supports both salt:hash and legacy plaintext)
      let found = null;
      for (const [cId, v] of Object.entries(currentLogins)) {
        if (v.loginId === loginId.trim() && await verifyPassword(loginPass, v.password)) {
          found = [cId, v]; break;
        }
      }
      if (found) {
        const [cId, cred] = found;
        if (!cred.active) {
          const isPending = cred.status === "pending_approval";
          setLoginErr(isPending
            ? "Your account is pending approval by your advisor. You will be notified once access is granted."
            : "This account is inactive. Please contact your advisor.");
          return;
        }
        setIsAdmin(false);
        setLoggedInClientId(cId);
        setTab("overview"); // always land on overview after client login
        // ── Silently upgrade plaintext passwords to hash on successful login ──
        if (cred.password && !cred.password.includes(":")) {
          makePasswordHash(loginPass).then(hashed => {
            const upgraded = { ...cred, password: hashed };
            upsertClientLogin(cId, upgraded).catch(()=>{});
            setClientLogins(p => ({ ...p, [cId]: upgraded }));
          }).catch(()=>{});
        }
        // Auto-filter holdings to this client
        const c = clients.find(x=>x.id===cId);
        if (c) {
          setSelectedFamily(c.familyId || c.family_id || "all");
          setSelectedClients(new Set([cId]));
          // Pre-populate profile from client record if not already loaded from Supabase
          setProfileData(prev => ({
            ...prev,
            [cId]: { dob: c.dob||"", ...( prev[cId]||{} ) }
          }));
        }
        const lastLogin = new Date().toISOString().slice(0,10);
        updateClientLoginLastLogin(cId, lastLogin).catch(err=>console.error('[Supabase] lastLogin:',err.message));
        setClientLogins(p=>({...p,[cId]:{...p[cId],lastLogin}}));
        setSignedOut(false); setLoginBusy(false);
        // Remember session
        try { localStorage.setItem("fw_session_v1", JSON.stringify({
          role:"client", clientId:cId, ts:Date.now()
        })); } catch {}
        setLoginPass(""); setLoginId("");
        // Apply client's saved theme
        applyThemeForUser(cId, profileData);
      } else { setLoginBusy(false); setLoginErr("Invalid login ID or password."); }
    };

    // ── Sign-up handler ──
    // ── Step 1: validate form and send OTP ──
    // ── Shared OTP send helper — used by both initial send and resend ──
    const sendOtpToEmail = async (email, name) => {
      const res = await fetch("/api/otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send", email: email.trim().toLowerCase(), name: (name||"").trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send verification code.");
      // Start 60s resend cooldown
      setOtpCountdown(60);
      const timer = setInterval(() => setOtpCountdown(c => { if(c<=1){clearInterval(timer);return 0;} return c-1; }), 1000);
    };

    const doSendOtp = async () => {
      setSignupErr(""); setSignupBusy(true);
      const { name, email, phone, password, confirm } = signupForm;
      if (!name.trim()) { setSignupErr("Full name is required."); setSignupBusy(false); return; }
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setSignupErr("Please enter a valid email address."); setSignupBusy(false); return; }
      if (!phone.trim() || phone.trim().length < 7) { setSignupErr("Phone number is required."); setSignupBusy(false); return; }
      if (password.length < 8) { setSignupErr("Password must be at least 8 characters."); setSignupBusy(false); return; }
      if (!/[A-Z]/.test(password)) { setSignupErr("Password must contain at least one uppercase letter."); setSignupBusy(false); return; }
      if (!/[0-9]/.test(password)) { setSignupErr("Password must contain at least one number."); setSignupBusy(false); return; }
      if (password !== confirm) { setSignupErr("Passwords do not match."); setSignupBusy(false); return; }
      try {
        await sendOtpToEmail(email, name);
        setOtpStep(true); setOtpSent(true); setOtpErr("");
      } catch(e) {
        setSignupErr(e.message || "Cannot reach server. Check your connection.");
      }
      setSignupBusy(false);
    };

    // ── Resend OTP — no form validation, just resend to same email ──
    const doResendOtp = async () => {
      if (otpCountdown > 0) return;
      setOtpErr(""); setOtpBusy(true);
      try {
        await sendOtpToEmail(signupForm.email, signupForm.name);
        setOtpCode("");
        setOtpErr("");
        showToast && showToast("✓ New code sent to " + signupForm.email, "#1D9E75", 3000);
      } catch(e) {
        setOtpErr(e.message || "Failed to resend. Please try again.");
      }
      setOtpBusy(false);
    };

    // ── Step 2: verify OTP then create account ──
    const doSignup = async () => {
      setOtpErr(""); setOtpBusy(true);
      if (!otpCode.trim() || otpCode.trim().length !== 6) {
        setOtpErr("Please enter the 6-digit code sent to your email."); setOtpBusy(false); return;
      }
      try {
        // Verify OTP first
        const vRes = await fetch("/api/otp", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "verify", email: signupForm.email.trim().toLowerCase(), token: otpCode.trim() }),
        });
        const vData = await vRes.json();
        if (!vRes.ok) { setOtpErr(vData.error || "Invalid code."); setOtpBusy(false); return; }

        // OTP verified — create account
        const { name, email, phone, password } = signupForm;
        const sRes = await fetch("/api/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(), email: email.trim().toLowerCase(),
            phone: phone.trim(), password,
            requiresApproval: true, createdAt: new Date().toISOString(),
          }),
        });
        const sData = await sRes.json();
        if (!sRes.ok) {
          // If account already active, offer to go to login
          if (sRes.status === 409) {
            setOtpErr("An account with this email already exists. Click 'Back to sign-up form' then use Sign In.");
          } else {
            setOtpErr(sData.error || "Sign-up failed. Please try again.");
          }
          setOtpBusy(false); return;
        }

        // Notify admin
        fetch("/api/otp", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "notify", name: name.trim(), email: email.trim(),
            phone: phone.trim(), loginId: sData.loginId,
          }),
        }).catch(()=>{});

        setSignupOk({ loginId: sData.loginId, trialEnds: sData.trialEnds, pending: true });
      } catch(e) {
        const msg = e?.message || "";
        if (msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("Load failed")) {
          setOtpErr("Network timeout — the server took too long. Please click 'Verify & Create Account' again, or use 'Resend code' to get a fresh code.");
        } else {
          setOtpErr("Error: " + (msg || "Unknown error. Please try again."));
        }
      }
      setOtpBusy(false);
    };
    // ── Sign-up form render ──
    if (signupMode) {
      const sf = (label, key, type="text", placeholder="") => (
        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,color:T.brand,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:6}}>{label}</label>
          <input type={type} value={signupForm[key]} placeholder={placeholder}
            onChange={e=>setSignupForm(p=>({...p,[key]:e.target.value}))}
            style={{width:"100%",boxSizing:"border-box",background:T.inputBg,border:`1.5px solid ${T.cardBorder}`,
              borderRadius:9,color:T.textPrimary,padding:"11px 14px",fontSize:13,outline:"none",fontFamily:"'Inter', system-ui, sans-serif"}}/>
        </div>
      );

      // ── OTP verification screen ──
      if (otpStep) {
        // If signup completed successfully, show success screen
        if (signupOk) {
          return (
            <div style={{minHeight:"100vh",fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,background:T.pageBgGrad,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
              <div style={{width:"100%",maxWidth:420,background:T.cardBg,borderRadius:16,padding:"36px 32px",boxShadow:"0 8px 40px rgba(0,0,0,0.3)",border:`1px solid ${T.cardBorder}`,textAlign:"center"}}>
                <div style={{fontSize:52,marginBottom:16}}>✅</div>
                <div style={{fontSize:20,fontWeight:700,color:"#1D9E75",marginBottom:8}}>Account Created!</div>
                <div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.8}}>
                  Your email is verified and your account is <strong style={{color:"#1D9E75"}}>ready to use</strong>.<br/>
                  Sign in below using your Login ID and password.
                </div>
                <div style={{background:"rgba(99,102,241,0.06)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:10,padding:"14px 18px",marginBottom:24,textAlign:"left"}}>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>Your Login ID (save this)</div>
                  <div style={{fontSize:18,fontWeight:700,color:"#6366F1",fontFamily:"monospace"}}>{signupOk.loginId}</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:4}}>Use this with your password once approved</div>
                </div>
                <button onClick={()=>{ setSignupMode(false); setSignupOk(null); setOtpStep(false); setOtpCode(""); setSignupForm({name:"",email:"",phone:"",password:"",confirm:""}); }}
                  style={{width:"100%",background:T.thBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,color:T.textSecondary,padding:"13px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                  Back to Login
                </button>
              </div>
            </div>
          );
        }
        return (
          <div style={{minHeight:"100vh",fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,background:T.pageBgGrad,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div style={{width:"100%",maxWidth:420,background:T.cardBg,borderRadius:16,padding:"36px 32px",boxShadow:"0 8px 40px rgba(0,0,0,0.3)",border:`1px solid ${T.cardBorder}`}}>
              {/* Header */}
              <div style={{textAlign:"center",marginBottom:28}}>
                <div style={{fontSize:40,marginBottom:12}}>📧</div>
                <div style={{fontSize:20,fontWeight:700,color:T.textPrimary,marginBottom:6}}>Check your email</div>
                <div style={{fontSize:13,color:T.textMuted,lineHeight:1.6}}>
                  We sent a 6-digit code to<br/>
                  <strong style={{color:T.brand}}>{signupForm.email}</strong>
                </div>
              </div>

              {/* OTP input */}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,color:T.brand,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:8}}>Verification Code</label>
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otpCode} onChange={e=>setOtpCode(e.target.value.replace(/\D/g,""))}
                  onKeyDown={e=>e.key==="Enter"&&doSignup()}
                  placeholder="000000"
                  autoFocus
                  style={{width:"100%",boxSizing:"border-box",background:T.inputBg,
                    border:`2px solid ${otpErr?"#A32D2D":otpCode.length===6?T.brand:T.cardBorder}`,
                    borderRadius:10,color:T.textPrimary,padding:"14px 16px",
                    fontSize:28,fontWeight:700,outline:"none",textAlign:"center",
                    letterSpacing:10,fontFamily:"monospace",
                    transition:"border-color 0.15s"}}/>
              </div>

              {/* Error */}
              {otpErr && (
                <div style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",
                  borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:12,color:"#A32D2D"}}>
                  ⚠ {otpErr}
                </div>
              )}

              {/* Verify button */}
              <button onClick={doSignup} disabled={otpBusy||otpCode.length!==6}
                style={{width:"100%",padding:"13px",borderRadius:10,border:"none",
                  background:otpCode.length===6&&!otpBusy?"#007878":"rgba(0,120,120,0.3)",
                  color:"white",fontSize:14,fontWeight:700,cursor:otpCode.length===6&&!otpBusy?"pointer":"default",
                  marginBottom:14,transition:"all 0.15s"}}>
                {otpBusy?"Verifying…":"✓ Verify & Create Account"}
              </button>

              {/* Resend */}
              <div style={{textAlign:"center",fontSize:12,color:T.textMuted}}>
                Didn't receive it?{" "}
                {otpCountdown > 0
                  ? <span style={{color:T.textMuted}}>Resend in {otpCountdown}s</span>
                  : <span onClick={doResendOtp} style={{color:T.brand,cursor:"pointer",fontWeight:600,textDecoration:"underline"}}>
                      Resend code
                    </span>
                }
              </div>

              {/* Back */}
              <div style={{textAlign:"center",marginTop:16}}>
                <span onClick={()=>{setOtpStep(false);setOtpCode("");setOtpErr("");setOtpSent(false);}}
                  style={{fontSize:12,color:T.textMuted,cursor:"pointer"}}>
                  ← Back to sign-up form
                </span>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{minHeight:"100vh",fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,background:T.pageBgGrad,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden",padding:16}}>
          
          {/* Ambient glows */}
          <div style={{position:"absolute",top:"30%",left:"50%",transform:"translateX(-50%)",width:600,height:600,background:"radial-gradient(ellipse,rgba(0,121,193,0.08) 0%,transparent 70%)",pointerEvents:"none"}}/>
          <div style={{position:"absolute",top:-80,right:-80,width:340,height:340,borderRadius:"50%",background:"rgba(0,120,120,0.06)",pointerEvents:"none"}}/>

          <div style={{position:"relative",zIndex:1,width:"100%",maxWidth:440}}>
            {/* Logo */}
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:24}}>
              <img src={T.dark || ["addepar"].includes(themeId) ? FORTHIUS_LOGO_DARK : FORTHIUS_LOGO} alt="Forthius Finalytics" style={{height:44,width:"auto",objectFit:"contain",marginBottom:12}}/>
              <div style={{fontSize:17,fontWeight:700,color:T.brand,letterSpacing:"0.07em",textTransform:"uppercase"}}>Create Account</div>
              <div style={{fontSize:10,color:T.textMuted,marginTop:4,letterSpacing:"0.1em",textTransform:"uppercase"}}>14-day Pro Trial · No credit card needed</div>
            </div>

            {signupOk ? (
              /* ── Success state — pending approval ── */
              <div style={{background:T.cardBg,border:"1.5px solid rgba(99,102,241,0.35)",borderRadius:16,padding:"32px 28px",boxShadow:"0 8px 32px rgba(99,102,241,0.10)",textAlign:"center"}}>
                <div style={{fontSize:48,marginBottom:16}}>⏳</div>
                <div style={{fontSize:18,fontWeight:700,color:"#6366F1",marginBottom:8}}>Request Received!</div>
                <div style={{fontSize:13,color:T.textSecondary,marginBottom:20,lineHeight:1.8}}>
                  Thank you for signing up. Your account is <strong style={{color:"#6366F1"}}>pending approval</strong> by your advisor.<br/>
                  You will receive confirmation once your access has been activated — typically within 1 business day.
                </div>
                <div style={{background:"rgba(99,102,241,0.06)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:10,padding:"14px 18px",marginBottom:24,textAlign:"left"}}>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>Your Login ID (save this)</div>
                  <div style={{fontSize:16,fontWeight:700,color:"#6366F1",fontFamily:"'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"}}>{signupOk.loginId}</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:4}}>You will use this with your password once approved</div>
                </div>
                <button
                  onClick={()=>{ setSignupMode(false); setSignupOk(null); setSignupForm({name:"",email:"",phone:"",password:"",confirm:""}); }}
                  style={{width:"100%",background:T.thBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,color:T.textSecondary,padding:"13px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                  Back to Login
                </button>
              </div>
            ) : (
              /* ── Sign-up form ── */
              <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,padding:"28px 28px 24px",boxShadow:"0 8px 32px rgba(0,121,193,0.10)"}}>
                {/* Trial banner */}
                <div style={{background:"linear-gradient(135deg,rgba(0,121,193,0.10),rgba(99,102,241,0.08))",border:`1px solid ${T.brand}33`,borderRadius:10,padding:"10px 14px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:20}}>🚀</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:T.brand}}>14-Day Pro Trial — Free</div>
                    <div style={{fontSize:11,color:T.textSecondary}}>Full access to all Pro features. No credit card required.</div>
                  </div>
                </div>

                {/* Full name only */}
                {sf("Full Name","name","text","e.g. Rahul Mehta")}
                {sf("Email Address","email","email","your@email.com")}
                {sf("Phone Number","phone","tel","+91 98765 43210")}
                {sf("Password","password","password","Min 8 chars, 1 uppercase, 1 number")}
                {sf("Confirm Password","confirm","password","Re-enter password")}

                {/* Password strength hint */}
                {signupForm.password.length > 0 && (function(){
                  const pw = signupForm.password;
                  const checks = [pw.length>=8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
                  const score = checks.filter(Boolean).length;
                  const label = score<=1?"Weak":score===2?"Fair":score===3?"Good":"Strong";
                  const color = score<=1?"#A32D2D":score===2?"#D97706":score===3?"#2563EB":"#1D9E75";
                  return (
                    <div style={{marginTop:-8,marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                      <div style={{flex:1,height:3,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                        <div style={{width:`${score*25}%`,height:"100%",background:color,borderRadius:2,transition:"all 0.3s"}}/>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color,minWidth:40}}>{label}</span>
                    </div>
                  );
                })()}

                {/* Error */}
                {signupErr && (
                  <div style={{fontSize:12,color:"#A32D2D",marginBottom:14,display:"flex",alignItems:"flex-start",gap:6,background:"rgba(220,38,38,0.07)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:8,padding:"9px 12px"}}>
                    <span style={{flexShrink:0}}>⚠</span><span>{signupErr}</span>
                  </div>
                )}

                {/* Submit */}
                <button onClick={doSendOtp} disabled={signupBusy}
                  style={{width:"100%",background:signupBusy?T.borderSubtle:"#007878",border:"none",borderRadius:10,color:signupBusy?T.textMuted:"white",padding:"13px",cursor:signupBusy?"default":"pointer",fontSize:14,fontWeight:700,letterSpacing:"0.04em",boxShadow:signupBusy?"none":"0 4px 18px rgba(13,138,122,0.35)",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {signupBusy ? (<React.Fragment><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Creating account…</React.Fragment>) : "Create My Account →"}
                </button>

                {/* Back to login */}
                <div style={{textAlign:"center"}}>
                  <span style={{fontSize:12,color:T.textSecondary}}>Already have an account?{" "}</span>
                  <span onClick={()=>{setSignupMode(false);setSignupErr("");}}
                    style={{fontSize:12,color:T.brand,cursor:"pointer",fontWeight:600,textDecoration:"underline",textUnderlineOffset:3}}>
                    Sign In
                  </span>
                </div>
              </div>
            )}

            <div style={{textAlign:"center",marginTop:16,fontSize:10,color:T.textMuted}}>
              Forthius Finalytics · Portfolio Intelligence v4 · © {new Date().getFullYear()}
              <br/>
              <button onClick={()=>onMethodology&&onMethodology()}
                style={{marginTop:4,fontSize:9,color:T.textMuted,background:"none",border:"none",
                  cursor:"pointer",textDecoration:"underline",padding:0}}>
                Methodology &amp; Disclosures
              </button>
            </div>
          </div>
        </div>
      );
    }

    try { return (
      <div style={{minHeight:"100vh",fontFamily:"'Inter', system-ui, sans-serif",
        position:"relative",overflow:"hidden",
        background:"linear-gradient(160deg,#00205B 0%,#003087 55%,#0079C1 100%)"}}>

        
        <style>{`
          @keyframes fw-up   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
          @keyframes fw-spin { to{transform:rotate(360deg)} }
          @keyframes fw-pulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
          .lbtn { transition:all 0.18s; border:none; cursor:pointer; font-family:inherit; }
          .lbtn:hover:not(:disabled) { filter:brightness(1.12); transform:translateY(-1px); }
          tr:hover .holding-hover-actions { opacity:1 !important; }
          tr:hover .holding-action-btns { opacity:1 !important; }
          .holding-hover-actions { opacity:0.3; }
          .holding-action-btns { opacity:0.3; }
          .linput {
            outline:none; font-family:inherit; width:100%; box-sizing:border-box;
            background:rgba(255,255,255,0.05); border:1.5px solid rgba(255,255,255,0.1);
            border-radius:10px; color:#fff; padding:12px 14px; font-size:13px;
            transition:border-color 0.2s;
          }
          .linput:focus { border-color:rgba(0,121,193,0.85); background:rgba(0,121,193,0.06); }
          .linput::placeholder { color:rgba(255,255,255,0.22); }
          @media(max-width:780px) {
            .lw-left  { display:none !important; }
            .lw-card  { flex-direction:column !important; max-width:420px !important; border-radius:16px !important; }
            .lw-right { padding:32px 26px !important; }
            .lw-wrap  { padding:24px 16px 48px !important; }
          }
        `}</style>

        {/* ── Full-screen subtle bg charts ── */}
        <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0}}>
          <svg width="100%" height="100%" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice"
            style={{position:"absolute",inset:0}}>
            <defs>
              <linearGradient id="bg-blue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0079C1" stopOpacity="0.18"/>
                <stop offset="100%" stopColor="#0079C1" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <polygon points="0,900 0,700 150,660 300,680 450,615 600,640 750,575 900,598 1050,535 1200,558 1350,500 1440,482 1440,900"
              fill="url(#bg-blue)" opacity="0.7"/>
            <polyline points="0,700 150,660 300,680 450,615 600,640 750,575 900,598 1050,535 1200,558 1350,500 1440,482"
              fill="none" stroke="#60C8FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35"/>
          </svg>
          <div style={{position:"absolute",inset:0,
            backgroundImage:"linear-gradient(rgba(255,255,255,0.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.012) 1px,transparent 1px)",
            backgroundSize:"60px 60px"}}/>
        </div>

        {/* ══ PAGE WRAPPER ══ */}
        <div className="lw-wrap" style={{position:"relative",zIndex:1,minHeight:"100vh",
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
          padding:"40px 32px 56px"}}>

          {/* Logo */}
          <div style={{position:"absolute",top:20,left:24,cursor:"pointer"}}
            onClick={()=>setSignedOut(false)}>
            <img src={FORTHIUS_LOGO_DARK} alt="Forthius Finalytics"
              style={{height:42,width:"auto",objectFit:"contain"}}/>
          </div>

          {/* ══ UNIFIED CARD ══ */}
          <div className="lw-card" style={{
            display:"flex",flexDirection:"row",
            width:"100%",maxWidth:980,
            borderRadius:20,overflow:"hidden",
            boxShadow:"0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)",
            animation:"fw-up 0.5s ease both",
          }}>

            {/* ══ LEFT PANEL — dashboard preview ══ */}
            <div className="lw-left" style={{flex:"0 0 52%",
              background:"rgba(0,20,60,0.7)",
              backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
              borderRight:"1px solid rgba(255,255,255,0.07)",
              padding:"32px 28px",
              display:"flex",flexDirection:"column",gap:14,
              overflow:"hidden",position:"relative"}}>

              {/* Welcome */}
              <div>
                <div style={{fontSize:22,fontWeight:500,color:"#fff",letterSpacing:"-0.02em",lineHeight:1.2}}>Welcome back!</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.38)",marginTop:3}}>Your portfolio at a glance</div>
              </div>

              {/* ── KPI STRIP ── */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[
                  {label:"Total AUM",     value:"$2.84M",  sub:"↑ 12.4% YTD", green:true},
                  {label:"XIRR",          value:"18.6%",   sub:"vs 14.2% bmk", green:true},
                  {label:"Holdings",      value:"43",      sub:"8 asset classes",green:false},
                ].map((k,i)=>(
                  <div key={i} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",
                    borderRadius:10,padding:"10px 12px"}}>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.38)",letterSpacing:"0.1em",
                      textTransform:"uppercase",fontWeight:600,marginBottom:4}}>{k.label}</div>
                    <div style={{fontSize:17,fontWeight:700,color:"#fff",fontFamily:"'Inter', system-ui, sans-serif",
                      letterSpacing:"-0.01em"}}>{k.value}</div>
                    <div style={{fontSize:9,color:k.green?"#60C8FF":"rgba(255,255,255,0.35)",marginTop:2,fontWeight:500}}>
                      {k.sub}</div>
                  </div>
                ))}
              </div>

              {/* ── PERFORMANCE LINE CHART ── */}
              <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
                borderRadius:10,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",
                    textTransform:"uppercase",fontWeight:600}}>Portfolio Performance</div>
                  <div style={{display:"flex",gap:10,fontSize:9,color:"rgba(255,255,255,0.35)"}}>
                    <span style={{display:"flex",alignItems:"center",gap:3}}>
                      <span style={{width:12,height:2,background:"#00b4a0",display:"inline-block",borderRadius:1}}/>Portfolio</span>
                    <span style={{display:"flex",alignItems:"center",gap:3}}>
                      <span style={{width:12,height:2,background:"#2563eb",display:"inline-block",
                        borderRadius:1,opacity:0.7}}/>S&amp;P 500</span>
                  </div>
                </div>
                <svg viewBox="0 0 460 72" style={{width:"100%",display:"block",overflow:"visible"}}>
                  <defs>
                    <linearGradient id="lg-perf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00b4a0" stopOpacity="0.35"/>
                      <stop offset="100%" stopColor="#00b4a0" stopOpacity="0.02"/>
                    </linearGradient>
                  </defs>
                  <polygon points="0,72 0,58 46,52 92,55 138,44 184,47 230,35 276,38 322,25 368,28 414,15 460,10 460,72"
                    fill="url(#lg-perf)"/>
                  <polyline points="0,58 46,52 92,55 138,44 184,47 230,35 276,38 322,25 368,28 414,15 460,10"
                    fill="none" stroke="#60C8FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="0,64 46,60 92,62 138,56 184,58 230,50 276,52 322,44 368,46 414,38 460,35"
                    fill="none" stroke="#2563eb" strokeWidth="1.5" strokeDasharray="5 3"
                    strokeLinecap="round" opacity="0.6"/>
                  {["Jan","Mar","Jun","Sep","Dec"].map((m,i)=>(
                    <text key={m} x={i*115} y="84" fill="rgba(255,255,255,0.2)"
                      fontSize="8" fontFamily="sans-serif">{m}</text>
                  ))}
                </svg>
              </div>

              {/* ── DONUT + BAR ROW ── */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>

                {/* Allocation donut */}
                <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",
                    textTransform:"uppercase",fontWeight:600,marginBottom:8}}>Allocation</div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <svg viewBox="0 0 72 72" style={{width:64,height:64,flexShrink:0}}>
                      {/* Donut at cx=36 cy=36 R=30 r=19 */}
                      {/* Equity 38% */}
                      <path d="M36,6 A30,30 0 0,1 65.4,48.5 L54,42.3 A19,19 0 0,0 36,17 Z" fill="#00b4a0" opacity="0.9"/>
                      {/* Debt 27% */}
                      <path d="M65.4,48.5 A30,30 0 0,1 30.5,65.7 L30.5,54.5 A19,19 0 0,0 54,42.3 Z" fill="#2563eb" opacity="0.9"/>
                      {/* RE 20% */}
                      <path d="M30.5,65.7 A30,30 0 0,1 7.2,24.2 L17.6,30.2 A19,19 0 0,0 30.5,54.5 Z" fill="#7c3aed" opacity="0.9"/>
                      {/* Alts 15% */}
                      <path d="M7.2,24.2 A30,30 0 0,1 36,6 L36,17 A19,19 0 0,0 17.6,30.2 Z" fill="#d97706" opacity="0.9"/>
                      <text x="36" y="33" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" fontFamily="sans-serif">$2.84M</text>
                      <text x="36" y="43" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="sans-serif">AUM</text>
                    </svg>
                    <div style={{display:"flex",flexDirection:"column",gap:4,fontSize:9}}>
                      {[["#00b4a0","Equity","38%"],["#2563eb","Debt","27%"],["#7c3aed","Real Est.","20%"],["#d97706","Alts","15%"]].map(([c,l,p])=>(
                        <div key={l} style={{display:"flex",alignItems:"center",gap:4,color:"rgba(255,255,255,0.5)"}}>
                          <span style={{width:6,height:6,borderRadius:1,background:c,flexShrink:0}}/>
                          <span>{l}</span>
                          <span style={{marginLeft:"auto",color:"rgba(255,255,255,0.7)",fontWeight:600}}>{p}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Geography exposure */}
                <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",
                    textTransform:"uppercase",fontWeight:600,marginBottom:8}}>Geo Exposure</div>
                  {[
                    {flag:"🇮🇳",label:"India",    pct:52,bar:"#00b4a0"},
                    {flag:"🇺🇸",label:"USA",      pct:28,bar:"#2563eb"},
                    {flag:"🇬🇧",label:"UK",       pct:10,bar:"#7c3aed"},
                    {flag:"🌏",label:"Other",     pct:10,bar:"#d97706"},
                  ].map((g,i)=>(
                    <div key={i} style={{marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{fontSize:9,color:"rgba(255,255,255,0.5)",display:"flex",alignItems:"center",gap:4}}>
                          <span style={{fontSize:11}}>{g.flag}</span>{g.label}
                        </span>
                        <span style={{fontSize:9,color:"rgba(255,255,255,0.55)",fontWeight:600}}>{g.pct}%</span>
                      </div>
                      <div style={{height:4,background:"rgba(255,255,255,0.08)",borderRadius:2}}>
                        <div style={{height:"100%",width:`${g.pct}%`,background:g.bar,borderRadius:2,opacity:0.8}}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── RECENT TRANSACTIONS ── */}
              <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
                borderRadius:10,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",
                    textTransform:"uppercase",fontWeight:600}}>Recent Activity</div>
                  <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:"#00b4a0"}}>
                    <span style={{width:5,height:5,borderRadius:"50%",background:"#00b4a0",
                      animation:"fw-pulse 2s ease-in-out infinite",display:"inline-block"}}/>Live
                  </div>
                </div>
                {[
                  {dir:true, name:"NVIDIA Corp",    mkt:"🇺🇸 NASDAQ", val:"+$48,200",  sub:"Buy · 120 shares"},
                  {dir:false,name:"Nifty BeES",     mkt:"🇮🇳 NSE",    val:"-₹1.24L",   sub:"Sell · 200 units"},
                  {dir:true, name:"Gold ETF (GLD)", mkt:"🇺🇸 NYSE",   val:"+$12,450",  sub:"Buy · 85 units"},
                ].map((t,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                    padding:"6px 0",borderTop:i>0?"1px solid rgba(255,255,255,0.05)":"none"}}>
                    <div style={{width:24,height:24,borderRadius:6,flexShrink:0,
                      background:t.dir?"rgba(0,180,160,0.15)":"rgba(248,113,113,0.12)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:11,color:t.dir?"#00b4a0":"#f87171",fontWeight:700}}>
                      {t.dir?"↑":"↓"}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:"#fff",
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                      <div style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>{t.mkt} · {t.sub}</div>
                    </div>
                    <div style={{fontSize:11,fontWeight:700,
                      color:t.dir?"#00b4a0":"#f87171",whiteSpace:"nowrap"}}>{t.val}</div>
                  </div>
                ))}
              </div>

              <div style={{fontSize:9,color:"rgba(255,255,255,0.16)",letterSpacing:"0.06em",textAlign:"center",paddingTop:2}}>
                © {new Date().getFullYear()} Forthius Finalytics · Data shown is illustrative
              </div>
            </div>

            {/* ══ RIGHT PANEL — login form ══ */}
            <div className="lw-right" style={{flex:"1 1 48%",
              background:"rgba(4,11,24,0.92)",
              backdropFilter:"blur(28px)",WebkitBackdropFilter:"blur(28px)",
              padding:"40px 38px 36px",
              display:"flex",flexDirection:"column",justifyContent:"center"}}>

              {/* Role switcher */}
              <div style={{display:"flex",gap:6,marginBottom:28,
                background:"rgba(255,255,255,0.05)",borderRadius:11,padding:4}}>
                {[{r:"client",label:"👤 Client"},{r:"admin",label:"⚙️ Admin"}].map(({r,label})=>(
                  <button key={r} className="lbtn"
                    onClick={()=>{ setLoginRole(r); setLoginErr(""); }}
                    style={{flex:1,padding:"10px",borderRadius:8,fontSize:13,fontWeight:600,
                      border:"none",
                      background:loginRole===r?"linear-gradient(135deg,#0079C1,#00205B)":"transparent",
                      color:loginRole===r?"#fff":"rgba(255,255,255,0.5)",
                      boxShadow:loginRole===r?"0 2px 14px rgba(0,121,193,0.5)":"none",
                      transition:"all 0.15s"}}>
                    {label}
                  </button>
                ))}
              </div>

              <div style={{marginBottom:24}}>
                <div style={{fontSize:20,fontWeight:700,color:"#fff",marginBottom:4}}>
                  {loginRole==="client" ? "Client Secure Access" : "Admin Secure Access"}
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.36)"}}>
                  Sign in to your portfolio
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{display:"block",fontSize:11,color:"rgba(255,255,255,0.42)",
                  fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:7}}>
                  Username
                </label>
                <input className="linput" type="text"
                  value={loginId} onChange={e=>setLoginId(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&doLogin()}
                  placeholder={loginRole==="admin"?"e.g. admin.user":"e.g. rajesh.sharma"}
                  autoFocus/>
              </div>

              <div style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                  <label style={{fontSize:11,color:"rgba(255,255,255,0.42)",
                    fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em"}}>Password</label>
                  <span onClick={()=>setForgotPassOpen(true)}
                    style={{fontSize:12,color:"rgba(0,180,160,0.9)",cursor:"pointer",
                      fontWeight:500,textDecoration:"underline",textUnderlineOffset:3}}>
                    Forgot Password?
                  </span>
                </div>
                <input className="linput" type="password"
                  value={loginPass} onChange={e=>setLoginPass(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&doLogin()}
                  placeholder="••••••••"/>
              </div>

              {loginErr&&(
                <div style={{fontSize:12,color:"#fca5a5",marginTop:10,marginBottom:2,
                  background:"rgba(220,38,38,0.12)",border:"1px solid rgba(220,38,38,0.3)",
                  borderRadius:8,padding:"9px 12px",display:"flex",alignItems:"center",gap:6}}>
                  <span>⚠</span><span>{loginErr}</span>
                </div>
              )}

              <button className="lbtn" onClick={doLogin} disabled={loginBusy}
                style={{width:"100%",marginTop:22,borderRadius:11,color:"#fff",
                  padding:"14px",fontSize:14,fontWeight:700,letterSpacing:"0.07em",
                  background:loginBusy?"rgba(0,121,193,0.3)":"linear-gradient(135deg,#0079C1,#00205B)",
                  boxShadow:loginBusy?"none":"0 4px 22px rgba(0,145,138,0.5)",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:loginBusy?0.7:1}}>
                {loginBusy
                  ?<React.Fragment><span style={{display:"inline-block",animation:"fw-spin 0.8s linear infinite"}}>⟳</span>Signing in…</React.Fragment>
                  :<React.Fragment>LOGIN →</React.Fragment>}
              </button>

              {loginRole==="client"&&(
                <div style={{textAlign:"center",marginTop:20,fontSize:13,
                  color:"rgba(255,255,255,0.35)"}}>
                  New client?{" "}
                  <span onClick={()=>{setSignupMode(true);setLoginErr("");}}
                    style={{color:"rgba(0,180,160,0.9)",cursor:"pointer",
                      fontWeight:600,textDecoration:"underline",textUnderlineOffset:3}}>
                    Sign Up
                  </span>
                </div>
              )}

              <div style={{textAlign:"center",marginTop:28,fontSize:10,
                color:"rgba(255,255,255,0.18)",letterSpacing:"0.06em"}}>
                Forthius Finalytics · Portfolio Intelligence · © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </div>

        {/* Forgot password modal */}
        {forgotPassOpen&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
            backdropFilter:"blur(6px)",zIndex:900,display:"flex",
            alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"rgba(4,11,24,0.97)",border:"1px solid rgba(255,255,255,0.12)",
              borderRadius:16,padding:30,width:"100%",maxWidth:380,
              boxShadow:"0 24px 80px rgba(0,0,0,0.8)"}}>
              <div style={{fontSize:16,fontWeight:700,color:"#fff",marginBottom:4}}>Reset Password</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
                Enter your login ID and set a new password.
              </div>
              {[["Login ID","fpLoginId","text","Your login ID"],
                ["New Password","fpNewPass","password","Min 8 characters"]].map(([lbl,key,type,ph])=>(
                <div key={key} style={{marginBottom:14}}>
                  <label style={{fontSize:11,color:"rgba(255,255,255,0.45)",fontWeight:600,
                    textTransform:"uppercase",letterSpacing:"0.1em",display:"block",marginBottom:6}}>{lbl}</label>
                  <input className="linput" type={type} placeholder={ph}
                    value={key==="fpLoginId"?(fpLoginId||""):(fpNewPass||"")}
                    onChange={e=>key==="fpLoginId"?setFpLoginId&&setFpLoginId(e.target.value):setFpNewPass&&setFpNewPass(e.target.value)}/>
                </div>
              ))}
              <div style={{display:"flex",gap:10,marginTop:10}}>
                <button className="lbtn" onClick={()=>setForgotPassOpen(false)}
                  style={{flex:1,background:"rgba(255,255,255,0.07)",
                    border:"1px solid rgba(255,255,255,0.12)",borderRadius:9,
                    color:"rgba(255,255,255,0.6)",padding:"12px",fontSize:13}}>Cancel</button>
                <button className="lbtn" onClick={async()=>{
                    if(!(fpLoginId||"").trim()||(fpNewPass||"").length<8) return;
                    const found=Object.entries(clientLogins).find(([,v])=>v.loginId===(fpLoginId||"").trim());
                    if(!found){setLoginErr("Login ID not found.");setForgotPassOpen(false);return;}
                    const [cId,cred]=found;
                    const hashed=await hashPassword(fpNewPass||"");
                    const updated={...cred,password:hashed};
                    setClientLogins(p=>({...p,[cId]:updated}));
                    try{await upsertClientLogin(cId,updated);}catch{}
                    setForgotPassOpen(false);showToast("✓ Password reset","#1D9E75");
                  }}
                  style={{flex:2,background:"linear-gradient(135deg,#0079C1,#00205B)",
                    borderRadius:9,color:"#fff",padding:"12px",fontSize:13,fontWeight:700,
                    boxShadow:"0 4px 16px rgba(0,145,138,0.4)"}}>
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    ); } catch(loginRenderErr) {
      // Fallback login screen if render crashes
      return (
        <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#00205B,#0079C1)",
          display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"}}>
          <div style={{background:"rgba(255,255,255,0.08)",borderRadius:16,padding:"40px 32px",
            maxWidth:360,width:"100%",textAlign:"center",color:"#fff"}}>
            <div style={{fontSize:32,marginBottom:16}}>⚠️</div>
            <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>Something went wrong</div>
            <div style={{fontSize:12,opacity:0.7,marginBottom:20}}>
              Please clear your browser cache and reload.<br/>
              Error: {loginRenderErr?.message || "Unknown"}
            </div>
            <button onClick={()=>{ try{localStorage.clear();}catch{} window.location.reload(); }}
              style={{background:"#0079C1",border:"none",borderRadius:8,color:"#fff",
                padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:600}}>
              Clear Cache &amp; Reload
            </button>
          </div>
        </div>
      );
    }
  }
    if (dbLoading) return (
    <div style={{minHeight:"100vh",background:T.pageBg,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,overflowX:"hidden",display:"flex",flexDirection:"column"}}>
      
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -700px 0; }
          100% { background-position: 700px 0; }
        }
        .sk { border-radius:8px; background: linear-gradient(90deg,
          ${T.cardBorder} 25%, ${T.borderSubtle} 50%, ${T.cardBorder} 75%);
          background-size:700px 100%; animation: shimmer 1.4s infinite linear; }
      `}</style>
      {/* Fake header */}
      <div style={{background:T.headerBg,borderBottom:`1px solid ${T.headerBorder}`,
        padding:"0 24px",height:52,display:"flex",alignItems:"center",gap:12}}>
        <div className="sk" style={{width:120,height:28}}/>
        <div style={{flex:1}}/>
        <div className="sk" style={{width:80,height:20,borderRadius:20}}/>
        <div className="sk" style={{width:32,height:32,borderRadius:"50%"}}/>
      </div>
      <div style={{display:"flex",maxWidth:1400,margin:"0 auto",padding:"20px 24px",gap:20}}>
        {/* Fake sidebar */}
        <div style={{width:220,flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
          {[1,2,3,4,5,6].map(i=>(
            <div key={i} className="sk" style={{height:36,borderRadius:8,
              width:i%3===0?"60%":i%2===0?"80%":"100%"}}/>
          ))}
        </div>
        {/* Fake content */}
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:16}}>
          {/* KPI row */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
            {[1,2,3,4].map(i=>(
              <div key={i} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                borderRadius:12,padding:16}}>
                <div className="sk" style={{height:11,width:"60%",marginBottom:10}}/>
                <div className="sk" style={{height:28,width:"80%",marginBottom:8}}/>
                <div className="sk" style={{height:9,width:"40%"}}/>
              </div>
            ))}
          </div>
          {/* Chart placeholder */}
          <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
            borderRadius:12,padding:20,height:180}}>
            <div className="sk" style={{height:14,width:160,marginBottom:16}}/>
            <div className="sk" style={{height:120,borderRadius:8}}/>
          </div>
          {/* Table rows */}
          <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
            borderRadius:12,padding:16}}>
            <div className="sk" style={{height:14,width:200,marginBottom:16}}/>
            {[1,2,3,4,5].map(i=>(
              <div key={i} style={{display:"flex",gap:12,marginBottom:12,alignItems:"center"}}>
                <div className="sk" style={{width:32,height:32,borderRadius:8,flexShrink:0}}/>
                <div className="sk" style={{height:12,flex:1}}/>
                <div className="sk" style={{height:12,width:80,flexShrink:0}}/>
                <div className="sk" style={{height:12,width:60,flexShrink:0}}/>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{textAlign:"center",marginTop:8,fontSize:11,color:T.textMuted}}>
        Loading portfolio data…
      </div>
    </div>
  );

  if (dbError) return (
    <div style={{minHeight:"100vh",background:T.loginBg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,fontFamily:"'Inter', system-ui, sans-serif",padding:24}}>
      
      <div style={{fontSize:40}}>⚠️</div>
      <div style={{fontSize:18,fontWeight:700,color:"#A32D2D"}}>Failed to load data</div>
      <div style={{fontSize:13,color:T.textSecondary,maxWidth:400,textAlign:"center"}}>{dbError}</div>
      <div style={{fontSize:12,color:T.textMuted,maxWidth:400,textAlign:"center"}}>Check your Supabase connection, RLS policies, and that all required tables exist.</div>
      <button onClick={()=>{ setDbError(null); setDbLoading(true); window.location.reload(); }}
        style={{marginTop:8,background:"#007878",border:"none",borderRadius:8,color:"white",padding:"10px 28px",cursor:"pointer",fontSize:14,fontWeight:600}}>
        Retry
      </button>
    </div>
  );

  // ── LOGIN SCREEN ──
  // ── Confirm helper — replaces window.confirm() everywhere ──────────────────
  // Must be defined inside component to access setConfirmModal
  // (confirmModal state is declared above in component body)

  // ── Re-auth confirmation helper (used for destructive admin actions) ──────
  const requireReauth = (label, onConfirm) => {
    setReauthPassword(""); setReauthErr(""); setReauthBusy(false);
    setReauthModal({ label, onConfirm });
  };

  // Holdings table render — extracted to fix Babel JSX parse
  const renderHoldingsTable = () => {
            const isFiltered = selectedFamily !== "all" || selectedClients.size > 0 || !!viewAsClientId;
            const isDrillFiltered = Object.keys(holdingsDrillFilter).length > 0;
            // Use drillFilteredHW which applies chart drill-downs on top of family/client filter
            const baseHW = drillFilteredHW;

            // Empty state — new client or no holdings match filter
            if (baseHW.length === 0) return (
              <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,
                borderRadius:14,border:`1px dashed ${T.cardBorder}`}}>
                <div style={{fontSize:40,marginBottom:12}}>📂</div>
                <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No holdings yet</div>
                <div style={{fontSize:13,color:T.textSecondary,maxWidth:360,margin:"0 auto 20px",lineHeight:1.6}}>
                  {viewAsClientId
                    ? `${clients.find(c=>c.id===viewAsClientId)?.name||"This client"} has no holdings yet. Add them using the button below.`
                    : "Upload a CAS statement or add holdings manually to get started."}
                </div>
                <button onClick={()=>setTab("upload")}
                  style={{background:`${T.brand}`,
                    border:"none",borderRadius:8,color:"white",padding:"9px 20px",
                    cursor:"pointer",fontSize:13,fontWeight:600,marginRight:8}}>📤 Upload Holdings</button>
                {(isAdmin||loggedInClientId) && <button onClick={()=>setAddOpen(true)}
                  style={{background:"transparent",border:`1px solid ${T.brand}`,borderRadius:8,
                    color:T.brand,padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Add Manually</button>}
              </div>
            );
            // Sort
            const { col, dir } = holdingsSort;
            const sortFn = (a, b) => {
              let va, vb;
              if (col === "value")   { va = a.valueUSD;   vb = b.valueUSD; }
              else if (col === "pl") { va = a.valueUSD - a.costUSD; vb = b.valueUSD - b.costUSD; }
              else if (col === "ret")  { va = a.gainPct;   vb = b.gainPct; }
              else if (col === "name") { va = a.name;       vb = b.name; return dir==="asc" ? va.localeCompare(vb) : vb.localeCompare(va); }
              else if (col === "ticker") {
                // Treat empty/dash tickers as "zzz" so they sort to the bottom
                // regardless of direction (real tickers should always be visible first).
                const norm = (t) => { const s = (t||"").trim().toUpperCase(); return (!s || s === "-" || s === "—") ? "ZZZ_EMPTY" : s; };
                va = norm(a.ticker); vb = norm(b.ticker);
                return dir==="asc" ? va.localeCompare(vb) : vb.localeCompare(va);
              }
              else if (col === "cap")  { const order=["Giant","Mega","Large","Mid","Small","Micro","N/A"]; va=order.indexOf(a.capSize||"N/A"); vb=order.indexOf(b.capSize||"N/A"); }
              else if (col === "class"){ va = a.class; vb = b.class; return dir==="asc" ? va.localeCompare(vb) : vb.localeCompare(va); }
              else if (col === "manager"){ va=a.manager||""; vb=b.manager||""; return dir==="asc"?va.localeCompare(vb):vb.localeCompare(va); }
              else if (col === "qty")  { va = a.qty;        vb = b.qty; }
              else if (col === "totalcost") { va = (a.costPrice||0)*(a.qty||0); vb = (b.costPrice||0)*(b.qty||0); }
              else va = a.valueUSD, vb = b.valueUSD;
              return dir === "asc" ? va - vb : vb - va;
            };
            const sortCol = (colId) => setHoldingsSort(p => ({ col: colId, dir: p.col === colId && p.dir === "desc" ? "asc" : "desc" }));
            const SortIcon = ({ colId }) => {
              if (holdingsSort.col !== colId) return <span style={{opacity:0.25,fontSize:9}}>↕</span>;
              return <span style={{color:T.brand,fontSize:9}}>{holdingsSort.dir==="asc"?"↑":"↓"}</span>;
            };

            // Apply free-text search filter
            const searchQ = holdingSearch.trim().toLowerCase();
            const searchFiltered = searchQ
              ? baseHW.filter(h =>
                  (h.name||"").toLowerCase().includes(searchQ) ||
                  (h.ticker||"").toLowerCase().includes(searchQ) ||
                  (h.class||"").toLowerCase().includes(searchQ) ||
                  (h.isin||"").toLowerCase().includes(searchQ) ||
                  (h.manager||"").toLowerCase().includes(searchQ) ||
                  (h.sector||"").toLowerCase().includes(searchQ)
                )
              : baseHW;
            const sorted     = [...searchFiltered].sort(sortFn);
            // Show all if: filtered, drill active, admin, or search active
            const displayed  = (isFiltered || isDrillFiltered || isAdmin || searchQ) ? sorted : sorted.slice(0, 10);
            const hidden     = (isFiltered || isDrillFiltered || isAdmin || searchQ) ? 0 : sorted.length - 10;

            // ── Group holdings by normalised ticker + asset class ──
            // Key = BASE_TICKER|CLASS so that e.g. "C" (Citigroup equity) groups
            // even when stored as "C", " c ", "c" etc. in the DB.
            // Fallback: if ticker is "-"/blank, use normalised name instead.
            const normTicker = (raw) => {
              const t = (raw || "").trim().toUpperCase();
              // Strip exchange suffix: RELIANCE.NSE → RELIANCE, NVO.WA → NVO
              return t.replace(/\..*$/, "").replace(/[-_].*$/, "") || "-";
            };
            const normName = (raw) => (raw || "").trim().toUpperCase().replace(/\s+/g, " ");

            const groupMap = {};
            displayed.forEach(h => {
              const tk  = normTicker(h.ticker);
              const cls = (h.class || "").trim();
              // Use ticker|class as key so same ticker in different asset classes stays separate
              // (e.g. someone holding both equity and options on same ticker)
              const key = (tk && tk !== "-")
                ? `${tk}|${cls}`
                : `NAME:${normName(h.name)}|${cls}`;
              if (!groupMap[key]) groupMap[key] = [];
              groupMap[key].push(h);
            });
            // ── Secondary merge: same normalised name + class → merge across ticker variants ──
            // Handles "Citigroup Inc" (ticker "C") vs "CITIGROUP INC" (ticker "C") stored differently
            const mergedMap = {};
            Object.entries(groupMap).forEach(([key, rows]) => {
              // Extract base ticker from key
              const [tkPart, clsPart] = key.split("|");
              const nameKey = tkPart.startsWith("NAME:")
                ? key  // already name-based, keep as-is
                : key; // ticker-based — already normalised, keep
              if (!mergedMap[nameKey]) mergedMap[nameKey] = [];
              mergedMap[nameKey].push(...rows);
            });

            // ── Pick best display name per group (prefer Title Case over ALL CAPS) ──
            const pickBestName = (rows) => {
              const names = [...new Set(rows.map(h => (h.name || "").trim()))];
              if (names.length === 1) return names[0];
              // Prefer: has lowercase letters (Title Case) > all caps > shorter
              const scored = names.map(n => ({
                n,
                score: (/[a-z]/.test(n) ? 2 : 0) + (n.length > 3 ? 1 : 0)
              }));
              scored.sort((a,b) => b.score - a.score);
              return scored[0].n;
            };

            const groups = Object.entries(mergedMap);
            const isGrouped = groups.some(([,rows]) => rows.length > 1);

            // ── Weighted avg cost price helper ──
            // If some lots lack cost price: use known-lot avg for the unknowns
            const weightedAvgCost = (rows) => {
              const totalQty = rows.reduce((s,h) => s + (h.qty || 0), 0);
              if (totalQty === 0) return 0;
              const knownRows  = rows.filter(h => h.costPrice > 0);
              const unknownRows = rows.filter(h => h.costPrice <= 0);
              if (knownRows.length === 0) return 0; // all unknown — show 0
              // For unknowns: use the average cost of known lots as proxy
              const knownAvg = knownRows.reduce((s,h) => s + h.costPrice * h.qty, 0)
                             / knownRows.reduce((s,h) => s + h.qty, 0);
              const proxyRows = unknownRows.map(h => ({ ...h, costPrice: knownAvg }));
              const allRows = [...knownRows, ...proxyRows];
              const wSum = allRows.reduce((s,h) => s + h.costPrice * h.qty, 0);
              return wSum / totalQty;
            };

            // ── Consolidated row for a group ──
            const consolidate = (key, rows) => {
              // Pick best ref: highest value, and use best display name
              const ref = [...rows].sort((a,b) => b.valueUSD - a.valueUSD)[0];
              const bestName = pickBestName(rows);
              // Patch ref with best name for display
              const refDisplay = { ...ref, name: bestName };
              const totalQty      = rows.reduce((s,h) => s + (h.qty || 0), 0);
              const totalValueUSD = rows.reduce((s,h) => s + h.valueUSD, 0);
              const avgCost       = weightedAvgCost(rows);
              const hasUnknownCost = rows.some(h => h.costPrice <= 0);
              const totalCostUSD  = rows.reduce((s,h) => {
                if (h.costPrice > 0) return s + h.costUSD;
                // proxy cost for unknown lots
                const knownRows = rows.filter(r => r.costPrice > 0);
                const knownAvg  = knownRows.length > 0
                  ? knownRows.reduce((ss,r) => ss + r.costPrice * r.qty, 0) / knownRows.reduce((ss,r) => ss + r.qty, 0)
                  : 0;
                return s + (knownAvg * h.qty / (fxRates[h.currency] || 1));
              }, 0);
              const gainUSD    = totalValueUSD - totalCostUSD;
              const gainPct    = totalCostUSD > 0 ? (gainUSD / totalCostUSD) * 100 : 0;
              const custodians = [...new Set(rows.map(h => h.manager || "Self-Managed"))];
              const owners     = [...new Set(rows.map(h => h.client?.name).filter(Boolean))];
              return { key, rows, ref, refDisplay, totalQty, totalValueUSD, avgCost, hasUnknownCost,
                totalCostUSD, gainUSD, gainPct, custodians, owners };
            };

            const consolidated = groups.map(([key, rows]) => consolidate(key, rows));
            // Sort consolidated by holdingsSort (user-selected column)
            consolidated.sort((a, b) => {
              const { col, dir } = holdingsSort;
              try {
                const aName = (a.ref?.name || a.refDisplay || a.key || "");
                const bName = (b.ref?.name || b.refDisplay || b.key || "");
                const aClass = (a.rows?.[0]?.class || "");
                const bClass = (b.rows?.[0]?.class || "");
                const aCust = (a.custodians?.[0] || "");
                const bCust = (b.custodians?.[0] || "");
                if      (col === "name")    { return dir==="asc" ? aName.localeCompare(bName) : bName.localeCompare(aName); }
                else if (col === "value")   { return dir==="asc" ? a.totalValueUSD - b.totalValueUSD : b.totalValueUSD - a.totalValueUSD; }
                else if (col === "pl")      { return dir==="asc" ? a.gainUSD - b.gainUSD : b.gainUSD - a.gainUSD; }
                else if (col === "ret")     { return dir==="asc" ? a.gainPct - b.gainPct : b.gainPct - a.gainPct; }
                else if (col === "qty")     { return dir==="asc" ? a.totalQty - b.totalQty : b.totalQty - a.totalQty; }
                else if (col === "class")   { return dir==="asc" ? aClass.localeCompare(bClass) : bClass.localeCompare(aClass); }
                else if (col === "manager") { return dir==="asc" ? aCust.localeCompare(bCust) : bCust.localeCompare(aCust); }
                else { return b.totalValueUSD - a.totalValueUSD; }
              } catch { return 0; }
            });

            const COL_HEADERS = [
              { id:"bulk",      label:"",             sortable:false },
              { id:"name",      label:"Asset",        sortable:true },
              { id:"ticker",    label:"Ticker",       sortable:true,  hideable:true },
              { id:"class",     label:"Class",        sortable:true,  filterable:"class" },
              { id:"cap",       label:"Cap",          sortable:true,  filterable:"cap",      hideable:true },
              { id:"manager",   label:"Custodian",    sortable:true,  filterable:"manager",  hideable:true },
              { id:"advisor",   label:"Advisor",      sortable:true,  filterable:"advisor",  hideable:true },
              { id:"ccy",       label:"Ccy",          sortable:false, filterable:"currency", hideable:true },
              { id:"qty",       label:"Qty",          sortable:true },
              { id:"cost",      label:"Avg Cost",     sortable:false },
              { id:"totalcost", label:"Total Cost",   sortable:true,  hideable:true },
              { id:"price",     label:"Price",        sortable:false },
              { id:"src",       label:"Source",       sortable:false, hideable:true },
              { id:"value",     label:"Value",        sortable:true },
              { id:"pl",        label:"P&L",          sortable:true,  hideable:true },
              { id:"ret",       label:"Return",       sortable:true },
              { id:"owner",     label:"Owner",        sortable:false, filterable:"owner", hideable:true },
              { id:"actions",   label:"",             sortable:false },
            ];
            const visibleCols = COL_HEADERS.filter(c => !c.hideable || !hiddenCols.has(c.id));

            // ── Holdings KPI strip — filter-reactive ──
            const kpiCost  = sorted.reduce((s,h)=>s+(h.costUSD||0),0);
            const kpiVal   = sorted.reduce((s,h)=>s+(h.valueUSD||0),0);
            const kpiGain  = kpiVal - kpiCost;
            const kpiGainPct = kpiCost > 0 ? (kpiGain/kpiCost*100) : null;
            const kpiHasPartial = sorted.some(h=>!(h.costUSD>0));
            const kpiGainCol = kpiGain >= 0 ? "#1D9E75" : "#A32D2D";


            return (
              <div style={{...tCard(), padding:0, overflow:"hidden"}}>
                {/* ── Holdings KPI Strip ── */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",borderBottom:`1px solid ${T.borderSubtle}`}}>
                  <div style={{padding:"18px 22px",borderRight:`1px solid ${T.borderSubtle}`}}>
                    <div style={{fontSize:11,fontWeight:400,color:T.textMuted,marginBottom:7}}>Invested</div>
                    <div style={{fontSize:21,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary}}>{fmt(convert(kpiCost,dc),dc,true)}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
                      {sorted.length} holding{sorted.length!==1?"s":""}
                      {kpiHasPartial && <span style={{color:"#854F0B",marginLeft:6}}>· partial</span>}
                    </div>
                  </div>
                  <div style={{padding:"18px 22px",borderRight:`1px solid ${T.borderSubtle}`}}>
                    <div style={{fontSize:11,fontWeight:400,color:T.textMuted,marginBottom:7}}>Market value</div>
                    <div style={{fontSize:21,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary}}>{fmt(convert(kpiVal,dc),dc,true)}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Prices current</div>
                  </div>
                  <div style={{padding:"18px 22px"}}>
                    <div style={{fontSize:11,fontWeight:400,color:T.textMuted,marginBottom:7}}>Unrealised P&amp;L</div>
                    <div style={{fontSize:21,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:kpiGainCol}}>{kpiGain>=0?"+":""}{fmt(convert(Math.abs(kpiGain),dc),dc,true)}</div>
                    <div style={{fontSize:11,color:kpiGainCol,marginTop:4}}>
                      {kpiGainPct!=null
                        ? <span>{kpiGain>=0?"+":""}{kpiGainPct.toFixed(1)}%</span>
                        : <span style={{color:"#854F0B"}}>Add cost prices to see return</span>}
                    </div>
                  </div>
                </div>
                <div style={{padding:"12px 20px",borderBottom:`1px solid ${T.borderSubtle}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:holdingsOpen?8:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:13,fontWeight:500,color:T.textPrimary,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}
                        onClick={()=>setExpandedCard(c=>c==="holdings"?null:"holdings")}>
                        {isAdmin ? `All holdings` : `Holdings`}
                        <span style={{fontWeight:400,color:T.textMuted,fontSize:12}}>{sorted.length} assets</span>
                      </div>
                    </div>
                    {!holdingsOpen && (<React.Fragment>
                      <div style={{flexShrink:0,textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(kpiCost,dc),dc,true)}</div>
                        <div style={{fontSize:10,color:T.textMuted}}>invested</div>
                      </div>
                      <div style={{width:"0.5px",height:28,background:T.cardBorder,flexShrink:0}}/>
                      <div style={{flexShrink:0,textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(kpiVal,dc),dc,true)}</div>
                        <div style={{fontSize:10,color:T.textMuted}}>market value</div>
                      </div>
                      <div style={{width:"0.5px",height:28,background:T.cardBorder,flexShrink:0}}/>
                      <div style={{flexShrink:0,textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:500,color:kpiGainCol,fontFamily:"'Inter', system-ui, sans-serif"}}>{kpiGain>=0?"+":""}{fmt(convert(kpiGain,dc),dc,true)}</div>
                        <div style={{fontSize:10,color:T.textMuted}}>unrealised P&L</div>
                      </div>
                      <div style={{width:"0.5px",height:28,background:T.cardBorder,flexShrink:0}}/>
                      <div style={{flexShrink:0,textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:500,color:kpiGainCol}}>{kpiGainPct!=null?`${kpiGain>=0?"+":""}${kpiGainPct.toFixed(1)}%`:"—"}</div>
                        <div style={{fontSize:10,color:T.textMuted}}>return</div>
                      </div>
                    </React.Fragment>)}
                    {holdingsOpen && <div/>}
                    <div style={{cursor:"pointer",flexShrink:0,fontSize:14,color:T.textMuted,paddingLeft:4}} onClick={()=>setExpandedCard(c=>c==="holdings"?null:"holdings")}>›</div>
                  </div>
                  <div style={{display:holdingsOpen?"flex":"none",alignItems:"center",gap:10}}>
                    {/* Bulk delete button */}
                    {isAdmin && bulkSelected.size > 0 && (
                      <button
                        onClick={async()=>{
                          // Require password re-auth for bulk delete
          const bulkNames = [...bulkSelected].map(id=>holdings.find(h=>h.id===id)?.name||id);
          const preview = bulkNames.slice(0,3).join(", ") + (bulkNames.length>3?` +${bulkNames.length-3} more`:"");
          requireReauth(
            `delete ${bulkSelected.size} holding${bulkSelected.size!==1?"s":""} (${preview})`,
            async () => {
                          const deletedNames = [...bulkSelected].map(id=>holdings.find(h=>h.id===id)?.name||id).join(", ");
                          let delOk=0, delFail=0;
                          for (const id of bulkSelected) {
                            try {
                              await deleteHolding(id);
                              await removeHoldingOwner(id).catch(()=>{});
                              delOk++;
                            } catch(e) {
                              console.error("[Bulk delete]", id, e.message);
                              delFail++;
                            }
                          }
                          setHoldings(p=>p.filter(h=>!bulkSelected.has(h.id)));
                          setHoldingOwners(p=>{
                            const n={...p};
                            [...bulkSelected].forEach(id=>delete n[String(id)]);
                            return n;
                          });
                          if (delFail>0) showToast(`⚠ ${delOk} deleted · ${delFail} failed — check console`,"#D97706");
                          logAudit("BULK_DELETE", `${delOk} of ${bulkSelected.size} holdings: ${deletedNames.slice(0,80)}`);
                          setBulkSelected(new Set());
                          showToast(`✓ Deleted ${bulkSelected.size} holdings`,"#1D9E75");
                        }
                          );
                        }}
                        style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.35)",
                          borderRadius:6,color:"#A32D2D",padding:"4px 12px",cursor:"pointer",
                          fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                        🗑 Delete {bulkSelected.size} selected
                      </button>
                    )}
                    {isGrouped && (
                      <span style={{fontSize:10,background:"rgba(99,102,241,0.1)",color:"#6366F1",
                        border:"1px solid rgba(99,102,241,0.25)",borderRadius:4,padding:"2px 7px",fontWeight:600}}>
                        ⊕ Consolidated view · click + to expand lots
                      </span>
                    )}
                  </div>
                  {/* Right side: view toggle + search + refresh indicator */}
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    {/* Mobile/table view toggle */}
                    {isMobile && (
                      <div style={{display:"flex",background:T.pageBg,borderRadius:6,
                        border:`1px solid ${T.cardBorder}`,overflow:"hidden"}}>
                        {[["table","⊞"],["cards","▤"]].map(([mode,icon])=>(
                          <button key={mode} onClick={()=>setHoldingsViewMode(mode)}
                            title={mode==="table"?"Table view":"Card view"}
                            style={{padding:"5px 10px",border:"none",cursor:"pointer",fontSize:13,
                              background:holdingsViewMode===mode?T.brand:"transparent",
                              color:holdingsViewMode===mode?"white":T.textMuted,
                              transition:"all 0.15s"}}>
                            {icon}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* AI NL Query bar */}
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{position:"relative",display:"flex",alignItems:"center"}}>
                        <span style={{position:"absolute",left:8,fontSize:12,pointerEvents:"none",opacity:0.5}}>✦</span>
                        <input
                          value={nlQueryInput}
                          onChange={e=>setNlQueryInput(e.target.value)}
                          onKeyDown={e=>{ if(e.key==="Enter") runNlQuery(nlQueryInput); }}
                          placeholder="Ask AI: e.g. show losers > 20%…"
                          style={{...inp(),paddingLeft:26,width:isMobile?150:220,fontSize:12,
                            border:`1px solid ${aiQueryDesc?T.brand:T.cardBorder}`,
                            background:aiQueryDesc?`${T.brand}08`:T.inputBg}}/>
                        {aiQueryLoading && <span style={{position:"absolute",right:8,fontSize:11,color:T.brand}}>⟳</span>}
                        {aiQueryDesc && !aiQueryLoading && (
                          <span onClick={()=>{ setNlQueryInput(""); setAiQueryDesc(null); setHoldingSearch(""); setHoldingsDrillFilter({}); }}
                            style={{position:"absolute",right:6,fontSize:11,cursor:"pointer",color:T.textMuted}}>✕</span>
                        )}
                      </div>
                      {aiQueryDesc && (
                        <span style={{fontSize:10,color:T.brand,background:`${T.brand}12`,
                          padding:"2px 8px",borderRadius:10,whiteSpace:"nowrap",maxWidth:160,
                          overflow:"hidden",textOverflow:"ellipsis"}}>
                          {aiQueryDesc}
                        </span>
                      )}
                      {aiQueryError && <span style={{fontSize:10,color:"#A32D2D"}}>{aiQueryError}</span>}
                    </div>
                    {/* Search box */}
                    <div style={{position:"relative"}}>
                      <input
                        value={holdingSearch}
                        onChange={e=>setHoldingSearch(e.target.value)}
                        placeholder="Search holdings…"
                        style={{...inp(),paddingLeft:26,width:isMobile?130:170,fontSize:12,
                          border:`1px solid ${holdingSearch?T.brand:T.cardBorder}`,
                          background:holdingSearch?`${T.brand}08`:T.inputBg}}/>
                      <span style={{position:"absolute",left:7,top:"50%",transform:"translateY(-50%)",
                        fontSize:12,pointerEvents:"none",opacity:0.4}}>🔍</span>
                      {holdingSearch && (
                        <span onClick={()=>setHoldingSearch("")}
                          style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",
                            fontSize:11,cursor:"pointer",color:T.textMuted}}>✕</span>
                      )}
                    </div>
                    {/* Column picker */}
                    <div style={{position:"relative"}}>
                      <button onClick={()=>setColMenuOpen(p=>!p)}
                        title="Show/hide columns"
                        style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,
                          background:colMenuOpen?`${T.brand}15`:T.borderSubtle,
                          border:`1px solid ${colMenuOpen?T.brand:T.cardBorder}`,
                          borderRadius:6,padding:"4px 10px",cursor:"pointer",
                          color:colMenuOpen?T.brand:T.textSecondary,whiteSpace:"nowrap"}}>
                        ⚙ Columns {hiddenCols.size > 0 && <span style={{background:T.brand,color:"white",borderRadius:3,padding:"0 4px",fontSize:9,fontWeight:700}}>{hiddenCols.size}</span>}
                      </button>
                      {colMenuOpen && (
                        <div style={{position:"absolute",top:"100%",right:0,marginTop:4,zIndex:100,
                          background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,
                          padding:"8px 4px",boxShadow:"0 8px 24px rgba(0,0,0,0.18)",minWidth:160}}
                          onClick={e=>e.stopPropagation()}>
                          <div style={{fontSize:10,color:T.textMuted,fontWeight:700,
                            textTransform:"uppercase",letterSpacing:"0.07em",padding:"2px 10px 6px"}}>
                            Toggle columns
                          </div>
                          {COL_HEADERS.filter(c=>c.hideable).map(c=>(
                            <label key={c.id} style={{display:"flex",alignItems:"center",gap:8,
                              padding:"5px 10px",cursor:"pointer",borderRadius:6,
                              background:hiddenCols.has(c.id)?"transparent":`${T.brand}08`}}>
                              <input type="checkbox" checked={!hiddenCols.has(c.id)}
                                onChange={()=>setHiddenCols(prev=>{
                                  const next = new Set(prev);
                                  next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                                  return next;
                                })}
                                style={{accentColor:T.brand,cursor:"pointer"}}/>
                              <span style={{fontSize:12,color:hiddenCols.has(c.id)?T.textMuted:T.textPrimary,fontWeight:hiddenCols.has(c.id)?400:600}}>
                                {c.label}
                              </span>
                            </label>
                          ))}
                          <div style={{borderTop:`1px solid ${T.borderSubtle}`,marginTop:4,paddingTop:4,padding:"4px 10px 2px"}}>
                            <button onClick={()=>setHiddenCols(new Set(["src","cap","ccy","ticker","totalcost","pl","owner"]))}
                              style={{fontSize:10,color:T.textMuted,background:"none",border:"none",cursor:"pointer",padding:0}}>
                              Reset defaults
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Export button */}
                    {isAdmin && (
                      <button
                        onClick={()=>exportHoldingsCSV(displayed, clients, families, dc, fxRates, FX_RATES_DEFAULT, convert, fmt)}
                        title="Export visible holdings to CSV"
                        style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,
                          background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                          borderRadius:6,padding:"4px 10px",cursor:"pointer",color:T.textSecondary,
                          whiteSpace:"nowrap"}}>
                        ⬇ Export CSV
                      </button>
                    )}
                    {isAdmin && (()=>{
                      // Detect orphan reconciled holdings — created by auto-reconcile but no transactions reference them anymore
                      const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : null);
                      const scopedHoldings = filterClientId
                        ? holdings.filter(h => holdingOwners[String(h.id)] === filterClientId)
                        : holdings;
                      // Build set of holding names referenced by ANY current transaction
                      const referencedNames = new Set(
                        transactions
                          .filter(t => !filterClientId || t.clientId === filterClientId)
                          .map(t => (t.holding||"").toLowerCase().trim())
                      );
                      // A holding is only an orphan if NEITHER its ticker NOR its
                      // name appears in any transaction. Matching on name alone
                      // flagged every holding that had simply been renamed
                      // (e.g. 'ANET' -> 'Arista Networks Inc'), which would have
                      // deleted live positions.
                      const orphans = scopedHoldings.filter(h => {
                        if (!(h.reconciledFromTxns === true || h.reconciled_from_txns === true)) return false;
                        const nm = (h.name||"").toLowerCase().trim();
                        const tk = (h.ticker||"").toLowerCase().trim();
                        if (nm && referencedNames.has(nm)) return false;
                        if (tk && tk !== "-" && referencedNames.has(tk)) return false;
                        return true;
                      });
                      if (orphans.length === 0) return null;
                      const doCleanup = () => {
                        requireConfirm(
                          `Delete ${orphans.length} orphan holding${orphans.length>1?"s":""}? These were auto-created from transactions that no longer exist. Manually-added holdings are not affected.`,
                          async () => {
                            let deleted = 0, failed = 0;
                            const removedIds = new Set();
                            for (const h of orphans) {
                              await removeHoldingOwner(String(h.id)).catch(()=>{});
                              const err = await deleteHolding(h.id).then(()=>null).catch(e=>e);
                              if (err) { console.error("[Cleanup] deleteHolding failed:", err?.message||err, h.name); failed++; continue; }
                              removedIds.add(String(h.id));
                              deleted++;
                            }
                            setHoldings(p => p.filter(h => !removedIds.has(String(h.id))));
                            setHoldingOwners(p => { const n={...p}; for (const id of removedIds) delete n[id]; return n; });
                            logAudit("CLEANUP_ORPHAN_HOLDINGS", `${deleted} orphan holdings removed${filterClientId?" for "+visibleClients.find(c=>c.id===filterClientId)?.name:""}`);
                            showToast(`✓ Removed ${deleted} orphan holding${deleted!==1?"s":""}${failed>0?` · ${failed} failed`:""}`, failed>0?"#D97706":"#1D9E75", 4000);
                          }
                        );
                      };
                      return (
                        <button onClick={doCleanup}
                          title={`${orphans.length} auto-created holding${orphans.length>1?"s":""} with no current transactions`}
                          style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,
                            background:"rgba(220,38,38,0.08)",border:`1px solid rgba(220,38,38,0.4)`,
                            borderRadius:6,padding:"4px 10px",cursor:"pointer",color:"#A32D2D",whiteSpace:"nowrap"}}>
                          🧹 Clean Orphans · {orphans.length}
                        </button>
                      );
                    })()}
                    {isAdmin && (()=>{
                      // Detect duplicate holdings by normalized name + custodian + currency.
                      // Custodian (h.manager) is part of the key because a holding at IBKR is
                      // a LEGITIMATELY SEPARATE position from the same security held at
                      // Kristal, ICICI Direct, or any other broker. Merging across custodians
                      // would re-point transactions and lose the custodian breakdown.
                      // Currency is part of the key for similar reasons (a USD ETF and its
                      // INR cousin are different instruments).
                      const normalize = (s) => (s||"").toLowerCase()
                        .replace(/[^a-z0-9]+/g," ")
                        .replace(/\b(inc|corp|corporation|ltd|limited|plc|ag|sa|nv|gmbh|llc|holdings|group|companies|company|the|fund|etf|trust|shares|class|growth|regular|direct|plan|acc|dist|distribution|accumulation|usd|inr|eur|gbp)\b/g," ")
                        .replace(/\s+/g," ").trim();
                      // Only run detection within current client filter (or all if no filter)
                      const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : null);
                      const scopedHoldings = filterClientId
                        ? holdings.filter(h => holdingOwners[String(h.id)] === filterClientId)
                        : holdings;
                      const byKey = new Map();
                      for (const h of scopedHoldings) {
                        const namePart = normalize(h.name);
                        if (!namePart || namePart.length < 3) continue;
                        const mgrPart = (h.manager || "Self-Managed").toLowerCase().trim();
                        const ccyPart = (h.currency || "USD").toUpperCase().trim();
                        const k = `${namePart}|${mgrPart}|${ccyPart}`;
                        if (!byKey.has(k)) byKey.set(k, []);
                        byKey.get(k).push(h);
                      }
                      const dupGroups = [...byKey.values()].filter(arr => arr.length >= 2);
                      if (dupGroups.length === 0) return null;
                      const total = dupGroups.reduce((s,g)=>s+g.length,0);
                      return (
                        <button onClick={()=>{
                          const groups = dupGroups.map((arr,i)=>({
                            canonicalIdx: 0,
                            holdings: arr,
                            skip: false,
                          }));
                          setMergeModal({ groups, clientId: filterClientId });
                        }}
                          title={`${dupGroups.length} duplicate group${dupGroups.length>1?"s":""} (${total} holdings)`}
                          style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,
                            background:"rgba(217,119,6,0.08)",border:`1px solid rgba(217,119,6,0.4)`,
                            borderRadius:6,padding:"4px 10px",cursor:"pointer",color:"#D97706",whiteSpace:"nowrap"}}>
                          🔗 Find Duplicates · {dupGroups.length}
                        </button>
                      );
                    })()}
                    {isAdmin && (
                      <button onClick={async ()=>{
                        // Fetch recent merges from merge_history table
                        try {
                          const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                          const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                          const res = await fetch(`${SUPABASE_URL}/rest/v1/merge_history?select=*&order=created_at.desc&limit=20`, {
                            headers: { "apikey": ANON_KEY, "Authorization": `Bearer ${ANON_KEY}` },
                          });
                          if (!res.ok) {
                            showToast("⚠ Could not load merge history (table may not exist yet — run the SQL in setup)", "#D97706", 6000);
                            return;
                          }
                          const rows = await res.json();
                          setMergeHistory(rows || []);
                          setMergeHistoryOpen(true);
                        } catch (e) {
                          console.error("[Merge History] fetch error:", e);
                          showToast("⚠ Could not load merge history: " + (e?.message || e), "#A32D2D", 5000);
                        }
                      }}
                        title="View recent merges · undo if needed"
                        style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,
                          background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                          borderRadius:6,padding:"4px 10px",cursor:"pointer",color:T.textSecondary,whiteSpace:"nowrap"}}>
                        📋 Merge History
                      </button>
                    )}
                    {/* Price refresh status */}
                    <div style={{fontSize:10,color:T.textMuted,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                      {refreshing ? (
                        <span style={{color:T.brand,fontWeight:600}}>⟳ Refreshing…</span>
                      ) : lastRefreshTime ? (
                        <span title={`Last refreshed: ${lastRefreshTime.toLocaleTimeString()}`}
                          style={{color:"#1D9E75"}}>
                          ✓ {Math.floor((Date.now()-lastRefreshTime)/60000)<1
                            ? "Prices current"
                            : `Updated ${Math.floor((Date.now()-lastRefreshTime)/60000)}m ago`}
                        </span>
                      ) : null}
                      {isAdmin && (function(){
                          // Count only simulated holdings visible in current filtered view
                          const visibleSimulated = sorted.filter(h => priceFailedIds.has(h.id)).length;
                          return visibleSimulated > 0 ? (
                            <span title={`${visibleSimulated} holding(s) in this view could not get a live price — showing simulated tick`}
                              style={{color:"#D97706",fontWeight:600,cursor:"help"}}>
                              ⚠ {visibleSimulated} simulated
                            </span>
                          ) : null;
                        })()}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    {!isFiltered && hidden > 0 && (
                      <div style={{fontSize:12,color:T.textSecondary}}>
                        Showing 10 of {sorted.length} ·{" "}
                        <span style={{color:"#2563EB",cursor:"pointer",textDecoration:"underline"}} onClick={()=>setTab("families")}>
                          Select a family to see all
                        </span>
                      </div>
                    )}
                    {isFiltered && (
                      <div style={{fontSize:12,color:T.textSecondary}}>
                        {displayed.length} lot{displayed.length!==1?"s":""} · {consolidated.length} asset{consolidated.length!==1?"s":""}
                      </div>
                    )}
                  </div>
                </div>

                </div>{/* end header padding div */}
                {/* ── Mobile card view ── */}
                {(isMobile && holdingsViewMode === "cards") ? (
                  <div style={{padding:"8px 12px 12px",display:"flex",flexDirection:"column",gap:10,
                    maxHeight:"calc(100vh - 260px)",overflowY:"auto"}}>
                    {consolidated.length === 0 ? (
                      <div style={{textAlign:"center",padding:32,color:T.textMuted,fontSize:12}}>
                        {holdingSearch ? `No results for "${holdingSearch}"` : "No holdings to display."}
                      </div>
                    ) : consolidated.map(({key,rows,ref,refDisplay,totalValueUSD,avgCost,gainUSD,gainPct,owners,custodians})=>{
                      const isMulti = rows.length > 1;
                      const displayGainPct = ref.costPrice > 0 ? gainPct : null;
                      return (
                        <div key={key}
                          style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                            borderRadius:12,padding:"12px 14px",
                            borderLeft:`3px solid ${displayGainPct===null?"#D97706":displayGainPct>=0?"#1D9E75":"#A32D2D"}`}}>
                          {/* Header row */}
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontWeight:700,fontSize:13,color:T.textPrimary,
                                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {refDisplay.name}
                              </div>
                              {ref.notes && (
                                <div style={{fontSize:9,color:T.textMuted,marginTop:1,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                  maxWidth:200}} title={ref.notes}>
                                  📝 {ref.notes.length>40 ? ref.notes.slice(0,40)+"…" : ref.notes}
                                </div>
                              )}
                              <div style={{fontSize:10,color:T.textMuted,marginTop:2,display:"flex",gap:6,flexWrap:"wrap"}}>
                                <span style={{background:`${T.brand}12`,color:T.brand,
                                  padding:"1px 5px",borderRadius:3,fontWeight:600}}>{ref.class}</span>
                                {ref.ticker && ref.ticker!=="-" && <span>{ref.ticker}</span>}
                                {isMulti && <span style={{color:T.brand}}>{rows.length} lots</span>}
                              </div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                              <div style={{fontSize:14,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",
                                color:T.textPrimary}}>{fmt(convert(totalValueUSD,dc),dc)}</div>
                              <div style={{fontSize:11,fontWeight:600,marginTop:1,
                                color:displayGainPct===null?"#D97706":displayGainPct>=0?"#1D9E75":"#A32D2D"}}>
                                {displayGainPct!==null
                                  ? `${(displayGainPct??0)>=0?"+":""}${(displayGainPct??0).toFixed(2)}%`
                                  : "no cost"}
                              </div>
                            </div>
                          </div>
                          {/* Detail row */}
                          <div style={{display:"flex",gap:12,fontSize:11,color:T.textSecondary,flexWrap:"wrap"}}>
                            <span>Qty: <strong>{rows.reduce((s,r)=>s+r.qty,0).toLocaleString("en-IN",{maximumFractionDigits:3})}</strong></span>
                            <span>Price: <strong style={{fontFamily:"'Inter', system-ui, sans-serif"}}>{ref.currentPrice?.toFixed(2)||"—"}</strong></span>
                            {avgCost>0 && <span>Avg Cost: <strong style={{fontFamily:"'Inter', system-ui, sans-serif"}}>{avgCost.toFixed(2)}</strong></span>}
                            {gainUSD!==0 && ref.costPrice>0 && (
                              <span>P&L: <strong style={{color:gainUSD>=0?"#1D9E75":"#A32D2D"}}>
                                {gainUSD>=0?"+":""}{fmt(convert(gainUSD,dc),dc)}
                              </strong></span>
                            )}
                          </div>
                          {/* Owner + custodian */}
                          {(owners.length>0||custodians.length>0) && (
                            <div style={{marginTop:6,fontSize:10,color:T.textMuted,display:"flex",gap:8,flexWrap:"wrap"}}>
                              {owners.length>0 && <span>👤 {owners.join(", ")}</span>}
                              {custodians.length>0 && <span>🏦 {custodians.join(", ")}</span>}
                            </div>
                          )}
                          {/* Actions */}
                          <div style={{marginTop:8,display:"flex",gap:6,flexWrap:"wrap"}}>
                            {isAdmin && (
                              <button onClick={()=>{ setEditHoldId(ref.id); setEditHoldVals({...ref,
                                ticker:(ref.ticker||"-").trim().toUpperCase(),
                                sector:(ref.sector&&ref.sector!=="")?ref.sector:resolveHoldingSector(ref),
                                capSize:(ref.capSize&&ref.capSize!=="N/A")?ref.capSize:resolveCapSize(ref),
                              }); }}
                                style={{fontSize:11,background:"rgba(37,99,235,0.08)",
                                  border:"1px solid #2563EB",borderRadius:5,
                                  color:"#2563EB",padding:"3px 10px",cursor:"pointer"}}>
                                ✎ Edit
                              </button>
                            )}
                            <button onClick={e=>{e.stopPropagation();fetchAiInsight(ref);}}
                              style={{fontSize:11,
                                background:aiInsights[ref.id]?.insight?"rgba(124,58,237,0.12)":"rgba(124,58,237,0.06)",
                                border:`1px solid ${aiInsights[ref.id]?.insight?"#7C3AED":"rgba(124,58,237,0.3)"}`,
                                borderRadius:5,color:"#7C3AED",padding:"3px 10px",cursor:"pointer"}}>
                              {aiInsights[ref.id]?.loading?"⟳":"✦ AI"}
                            </button>
                          </div>
                          {aiInsights[ref.id]?.insight && (
                            <div style={{marginTop:8,padding:"8px 10px",
                              background:"rgba(124,58,237,0.04)",
                              borderLeft:"2px solid #7C3AED",borderRadius:"0 6px 6px 0",
                              fontSize:11,color:T.textSecondary,lineHeight:1.5}}>
                              {aiInsights[ref.id].insight.split("\n").filter(l=>l.trim()).slice(0,2).map((l,i)=>(
                                <div key={i} style={{marginBottom:i===0?4:0}}>{l}</div>
                              ))}
                            </div>
                          )}
                          {aiInsights[ref.id]?.error && (
                            <div style={{marginTop:6,fontSize:10,color:"#F43F5E"}}>{aiInsights[ref.id].error}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (<React.Fragment>

                {holdingsOpen && <React.Fragment>
                {/* Top scroll mirror — shows horizontal scrollbar at top */}
                <div ref={holdingsMirrorRef}
                  style={{overflowX:"auto",overflowY:"hidden",height:12,
                    borderBottom:`1px solid ${T.borderSubtle}`}}
                  onScroll={()=>{ if(holdingsTableRef.current) holdingsTableRef.current.scrollLeft = holdingsMirrorRef.current.scrollLeft; }}
                >
                  <div style={{height:1,minWidth:isMobile?700:1100}}/>
                </div>
                {/* Scrollable table area — fixed height with vertical+horizontal scroll */}
                <div ref={holdingsTableRef}
                  style={{
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: isMobile ? "60vh" : "calc(100vh - 280px)",
                    position: "relative",
                    willChange: "transform",
                    WebkitOverflowScrolling: "touch",
                  }}
                  onScroll={()=>{ if(holdingsMirrorRef.current) holdingsMirrorRef.current.scrollLeft = holdingsTableRef.current.scrollLeft; }}
                >
                <table style={{width:"100%",borderCollapse:"collapse",minWidth:isMobile?800:1200,tableLayout:"auto"}}>
                  <thead style={{position:"sticky",top:0,zIndex:10,background:T.thBg}}>
                    <tr>{visibleCols.map(col=>(
                    col.id === "bulk" ? (
                      isAdmin ? (
                        <th key="bulk" style={{...tTh(),width:32,padding:"6px 8px"}}>
                          <input type="checkbox"
                            checked={sorted.length > 0 && sorted.every(h => bulkSelected.has(h.id))}
                            onChange={e=>{
                              if (e.target.checked) {
                                setBulkSelected(new Set(sorted.map(h=>h.id)));
                              } else {
                                setBulkSelected(new Set());
                              }
                            }}
                            style={{cursor:"pointer"}}/>
                        </th>
                      ) : null
                    ) :
                      <th key={col.id} style={{...tTh(),whiteSpace:"nowrap",userSelect:"none",minWidth:col.id==="name"?200:col.id==="ticker"?80:undefined}}>
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          {/* Filter dropdown for filterable cols */}
                          {col.filterable && (function(){
                            const filterKey = col.filterable;
                            const opts = [...new Set(filteredHW.map(h =>
                              filterKey==="cap" ? resolveCapSize(h) :
                                filterKey==="owner" ? (visibleClients.find(c=>c.id===h.clientId)?.name||"Unassigned") :
                              filterKey==="class" ? h.class :
                              filterKey==="currency" ? h.currency :
                              filterKey==="manager" ? (h.manager||"Self-Managed") :
                              filterKey==="sector" ? resolveHoldingSector(h) : ""
                            ).filter(Boolean))].sort();
                            const active = holdingsDrillFilter[filterKey];
                            return (
                              <select value={active||""} onChange={e=>{
                                if (e.target.value) setDrill(filterKey, e.target.value);
                                else { const n={...holdingsDrillFilter}; delete n[filterKey]; setHoldingsDrillFilter(n); }
                              }}
                              style={{fontSize:9,border:`1px solid ${active?T.brand:T.cardBorder}`,
                                borderRadius:3,background:active?`${T.brand}15`:T.inputBg,
                                color:active?T.brand:T.textMuted,padding:"1px 2px",
                                maxWidth:60,cursor:"pointer",outline:"none"}}>
                                <option value="">All</option>
                                {opts.map(o=><option key={o}>{o}</option>)}
                              </select>
                            );
                          })()}
                          {/* Label + sort icon */}
                          <span onClick={col.sortable?()=>sortCol(col.id):undefined}
                            style={{cursor:col.sortable?"pointer":"default",fontSize:11,
                              color:holdingsSort.col===col.id?T.brand:undefined}}>
                            {col.label}
                          </span>
                          {col.sortable && <SortIcon colId={col.id}/>}
                        </div>
                      </th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {consolidated.length === 0 && (
                      <tr><td colSpan={15} style={{padding:0,border:"none"}}>
                        <div style={{textAlign:"center",padding:"48px 24px"}}>
                          {holdingSearch ? (
                            <React.Fragment>
                              <div style={{fontSize:32,marginBottom:12}}>🔍</div>
                              <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>
                                No results for "{holdingSearch}"
                              </div>
                              <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>
                                Try searching by ticker, asset class, or custodian name
                              </div>
                              <button onClick={()=>setHoldingSearch("")}
                                style={{background:T.brand,border:"none",borderRadius:7,color:"white",
                                  padding:"7px 18px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                                Clear search
                              </button>
                            </React.Fragment>
                          ) : filteredHW.length === 0 ? (
                            <React.Fragment>
                              <div style={{fontSize:40,marginBottom:12}}>📂</div>
                              <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>
                                No holdings yet
                              </div>
                              <div style={{fontSize:12,color:T.textMuted,marginBottom:20,maxWidth:360,margin:"0 auto 20px"}}>
                                Upload a CAS statement to get started, or add holdings manually using the button above.
                              </div>
                              <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                                <button onClick={()=>setTab("upload")}
                                  style={{background:"#1E293B",border:"none",
                                    borderRadius:7,color:"white",padding:"8px 18px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                                  📜 Upload CAS Statement
                                </button>
                                <button onClick={()=>setAddOpen(true)}
                                  style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                                    borderRadius:7,color:T.textPrimary,padding:"8px 18px",cursor:"pointer",fontSize:12}}>
                                  + Add Manually
                                </button>
                              </div>
                            </React.Fragment>
                          ) : (
                            <div style={{fontSize:12,color:T.textMuted}}>No holdings match the current filter.</div>
                          )}
                        </div>
                      </td></tr>
                    )}

                    {consolidated.map(({ key, rows, ref, refDisplay, totalQty, totalValueUSD, avgCost,
                      hasUnknownCost, gainUSD, gainPct, custodians, owners }, gi) => {
                      const isMulti   = rows.length > 1;
                      // Only expand if multi-lot AND user has clicked to expand
                      const isExpanded = isMulti && expandedHoldGroups.has(key);
                      const toggleExpand = () => setExpandedHoldGroups(prev => {
                        const next = new Set(prev);
                        next.has(key) ? next.delete(key) : next.add(key);
                        return next;
                      });

                      // ── Consolidated summary row ──
                      const summaryRow = (
                        <tr key={`grp-${key}`}
                          style={{background: gi%2===0 ? "transparent" : T.tableStripe,
                            borderLeft: isMulti ? `3px solid ${T.brand}` : "none"}}>

                          {/* Bulk select checkbox */}
                          {isAdmin && (
                            <td style={{...tTd(),width:32,padding:"6px 8px",textAlign:"center"}}
                              onClick={e=>e.stopPropagation()}>
                              <input type="checkbox"
                                checked={rows.every(r=>bulkSelected.has(r.id))}
                                onChange={e=>setBulkSelected(prev=>{
                                  const next=new Set(prev);
                                  rows.forEach(r=>e.target.checked?next.add(r.id):next.delete(r.id));
                                  return next;
                                })}
                                style={{cursor:"pointer"}}/>
                            </td>
                          )}

                          {/* Asset name */}
                          <td style={tTd({minWidth:200})} className="holding-name-cell">
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              {isMulti && (
                                <button onClick={toggleExpand}
                                  title={isExpanded ? "Collapse lots" : "Expand lots"}
                                  style={{width:20,height:20,borderRadius:5,border:`1px solid ${T.brand}`,
                                    background: isExpanded ? T.brand : "transparent",
                                    color: isExpanded ? "#fff" : T.brand,
                                    cursor:"pointer",fontSize:13,fontWeight:500,lineHeight:1,
                                    display:"flex",alignItems:"center",justifyContent:"center",
                                    flexShrink:0,transition:"all 0.15s"}}>
                                  {isExpanded ? "−" : "+"}
                                </button>
                              )}
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{holdingSearch.trim() ? HL(refDisplay.name, holdingSearch.trim()) : refDisplay.name}</div>
                                <div style={{fontSize:11,color:T.textMuted,display:"flex",gap:4,flexWrap:"wrap",alignItems:"center",marginTop:2}}>
                                  {ref.ticker && ref.ticker !== "-" && ref.ticker !== "—" && (
                                    <span style={{fontFamily:"'SF Mono','Cascadia Code',monospace",fontSize:11,color:T.textSecondary}}>{holdingSearch.trim() ? HL(ref.ticker, holdingSearch.trim()) : ref.ticker}</span>
                                  )}
                                  {ref.ticker && ref.ticker !== "-" && ref.ticker !== "—" && <span style={{color:T.textMuted}}>·</span>}
                                  <span>{ref.class}</span>
                                  {custodians.length > 0 && custodians[0] !== "Self-Managed" && (
                                    <React.Fragment>
                                      <span style={{color:T.textMuted}}>·</span>
                                      <span>{custodians.join(", ")}</span>
                                    </React.Fragment>
                                  )}
                                  {isMulti && <span style={{color:T.brand}}>{rows.length} lots</span>}
                                  {hasUnknownCost && <span style={{color:"#854F0B"}} title="Some lots have no cost price">partial cost</span>}
                                </div>
                                {(()=>{
                                  const note = holdingNotes[String(ref.id)]?.text || ref.notes;
                                  return note ? (
                                    <div style={{fontSize:10,color:T.textMuted,marginTop:2,
                                      maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",
                                      whiteSpace:"nowrap",fontStyle:"italic",cursor:"help"}}
                                      title={note}>
                                      {note}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                              {/* Buy/Sell quick-action — show on row hover via CSS */}
                              {isAdmin && (
                                <div className="holding-hover-actions" style={{display:"flex",gap:3,flexShrink:0,transition:"opacity 0.15s"}}>
                                  <button onClick={e=>{e.stopPropagation();
                                    const clientId=owners?.length===1?owners[0]:(loggedInClientId||"");
                                    setNewTxn({date:new Date().toISOString().slice(0,10),class:ref.class||"",holding:ref.name||"",type:"BUY",qty:"",price:ref.currentPrice??"",currency:ref.currency||"USD",fees:"",clientId});
                                    setTab("transactions");setTxnAddOpen(true);
                                  }} title="Buy" style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:4,border:`1px solid ${T.borderSubtle}`,background:"transparent",color:"#1D9E75",cursor:"pointer",fontSize:11,fontWeight:500,lineHeight:1}}>
                                    ▲
                                  </button>
                                  <button onClick={e=>{e.stopPropagation();
                                    const clientId=owners?.length===1?owners[0]:(loggedInClientId||"");
                                    setNewTxn({date:new Date().toISOString().slice(0,10),class:ref.class||"",holding:ref.name||"",type:"SELL",qty:"",price:ref.currentPrice??"",currency:ref.currency||"USD",fees:"",clientId});
                                    setTab("transactions");setTxnAddOpen(true);
                                  }} title="Sell" style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:4,border:`1px solid ${T.borderSubtle}`,background:"transparent",color:"#A32D2D",cursor:"pointer",fontSize:11,fontWeight:500,lineHeight:1}}>
                                    ▼
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Ticker */}
                          {!hiddenCols.has("ticker") && <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>{holdingSearch.trim() ? HL(ref.ticker||"", holdingSearch.trim()) : ref.ticker}</td>}

                          {/* Class — plain colored text, no pill */}
                          <td style={tTd()}>
                            <span style={{fontSize:12,fontWeight:500,color:classChipColor(ref.class)||T.textSecondary}}>
                              {ref.class}
                            </span>
                          </td>

                          {/* Cap */}
                          {!hiddenCols.has("cap") && <td style={tTd({fontSize:11})}>
                            {(function(){
                              const cs = ref.capSize||"N/A";
                              const autoCs = resolveCapSize(ref);
                              if(cs==="N/A"||cs==="-") return (
                                <span onClick={()=>setEditingCapId(ref.id)}
                                  title={autoCs?`Auto-detected: ${autoCs}. Click to set.`:"Click to set cap size"}
                                  style={{color:autoCs?CAP_COLORS[autoCs]:T.textLight,fontSize:10,cursor:"pointer",
                                    border:`1px dashed ${autoCs?CAP_COLORS[autoCs]+"88":"#DDD0C0"}`,
                                    borderRadius:4,padding:"1px 6px",fontStyle:autoCs?"normal":"italic"}}>
                                  {autoCs||"+ Cap"}
                                </span>
                              );
                              return <span onClick={()=>setEditingCapId(ref.id)} title="Click to change"
                                style={{color:CAP_COLORS[cs],fontWeight:600,fontSize:11,background:`${CAP_COLORS[cs]}15`,
                                  padding:"2px 7px",borderRadius:5,border:`1px solid ${CAP_COLORS[cs]}44`,
                                  whiteSpace:"nowrap",cursor:"pointer"}}>{cs}</span>;
                            })()}
                          </td>}

                          {/* Custodian(s) — plain colored text */}
                          {!hiddenCols.has("manager") && <td style={tTd({fontSize:11})}>
                            {custodians.map((m,mi) => {
                              const c = MANAGER_COLORS[m]||"#78716C";
                              return (
                                <span key={mi} style={{color:c,fontWeight:500,fontSize:11,whiteSpace:"nowrap",
                                  display:"inline-block",marginRight:mi<custodians.length-1?0:0}}>
                                  {m}{mi<custodians.length-1 && <span style={{color:T.textMuted,margin:"0 4px"}}>·</span>}
                                </span>
                              );
                            })}
                          </td>}

                          {/* Advisor — plain colored text */}
                          {!hiddenCols.has("advisor") && <td style={tTd({fontSize:11})}>
                            {ref.advisor ? (
                              <span style={{fontSize:11,color:"#7C3AED",fontWeight:500,whiteSpace:"nowrap"}}>
                                {ref.advisor}
                              </span>
                            ) : (
                              <span style={{fontSize:10,color:T.textMuted}}>—</span>
                            )}
                          </td>}

                          {/* Currency */}
                          {!hiddenCols.has("ccy") && <td style={tTd()}>{ref.currency}</td>}

                          {/* Total Qty */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:isMulti?700:400})}>
                            {isValueOnlyClass(ref.class) ? <span style={{color:T.textMuted}}>—</span> : totalQty.toLocaleString("en-US")}
                          </td>

                          {/* Weighted Avg Cost */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>
                            {isValueOnlyClass(ref.class) ? (
                              <span style={{color:T.textMuted}}>—</span>
                            ) : (
                              <>
                                <div>{avgCost > 0 ? avgCost.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2}) : "—"}</div>
                                {hasUnknownCost && avgCost > 0 && (
                                  <div style={{fontSize:9,color:"#D97706"}}>~proxy avg</div>
                                )}
                              </>
                            )}
                          </td>

                          {/* Total Cost = avg cost × total qty */}
                          {!hiddenCols.has("totalcost") && (
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>
                              {avgCost > 0 && totalQty > 0
                                ? fmt(convert(toUSD(avgCost * totalQty, ref.currency), dc), dc)
                                : <span style={{color:T.textMuted}}>—</span>}
                            </td>
                          )}

                          {/* Current Price */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>
                            {isValueOnlyClass(ref.class)
                              ? <span style={{color:T.textMuted}}>—</span>
                              : ref.currentPrice.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}
                          </td>

                          {/* Price Source */}
                          {!hiddenCols.has("src") && <td style={tTd({fontSize:11})}>
                            <div style={{display:"flex",alignItems:"center",gap:5}}>
                            {/* Per-holding refresh button — listed assets and Global MFs */}
                            {(ref.listed && !ref.manualPriceDate ||
                              ref.class === "Global Mutual Funds" || ref.isGlobalMF ||
                              (ref.ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(ref.ticker))) && (
                              <button
                                title="Refresh price for this holding"
                                onClick={async(e)=>{
                                  e.stopPropagation();
                                  const hid = ref.id;
                                  setRefreshingIds(p=>new Set([...p,hid]));
                                  try {
                                    // Targeted single-holding price fetch
                                    const ticker = ref.ticker && ref.ticker!=="-" ? ref.ticker : null;
                                    const isUCITS = ref.class === "Global Mutual Funds" || ref.isGlobalMF ||
                                      (ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(ticker));
                                    if (isUCITS) {
                                      // UCITS/Global MF: use getUCITSNav with stored yahooTicker
                                      const isin = (ref.isin && ref.isin !== "-") ? ref.isin : ticker;
                                      setLiveStatus(p=>({...p,[hid]:"fetching"}));
                                      const nav = await PriceDataProvider.getUCITSNav(isin, true, ref.yahooTicker||null);
                                      if (nav && nav > 0) {
                                        setHoldings(p=>p.map(x=>x.id===hid?{...x,currentPrice:nav}:x));
                                        setLiveStatus(p=>({...p,[hid]:"yahoo_ucits"}));
                                        updateHoldingPrice(hid, nav, new Date().toISOString().slice(0,10)).catch(()=>{});
                                        showToast(`✓ ${ref.name.split(" ")[0]} NAV: ${nav.toFixed(4)}`, "#1D9E75", 3000);
                                      } else {
                                        setLiveStatus(p=>({...p,[hid]:"manual"}));
                                        showToast(`⚠ NAV not found on Yahoo Finance. Set Yahoo Ticker via ✎ Edit, or enter price manually.`, "#D97706", 5000);
                                      }
                                    } else if (ticker && ref.isMutualFund) {
                                      // Indian MF: fetch NAV from mfapi
                                      const r = await fetch(`/api/price?symbol=${encodeURIComponent(ticker)}&type=mf`);
                                      const j = await r.json();
                                      if (j?.price > 0) {
                                        setHoldings(p=>p.map(x=>x.id===hid?{...x,currentPrice:j.price}:x));
                                        showToast(`✓ ${ref.name.split(" ")[0]} NAV updated: ${j.price}`, "#1D9E75", 3000);
                                      }
                                    } else if (ticker) {
                                      // Listed equity: fetch via price proxy
                                      const r = await fetch(`/api/price?symbol=${encodeURIComponent(ticker)}&currency=${ref.currency||"USD"}`);
                                      const j = await r.json();
                                      if (j?.price > 0) {
                                        setHoldings(p=>p.map(x=>x.id===hid?{...x,currentPrice:j.price}:x));
                                        showToast(`✓ ${ref.ticker} updated: ${j.price}`, "#1D9E75", 3000);
                                      } else {
                                        // Fallback to full refresh if single fetch fails
                                        await refresh(true);
                                      }
                                    } else {
                                      showToast("⚠ No ticker — cannot refresh unlisted asset", "#D97706", 3000);
                                    }
                                  } finally {
                                    setRefreshingIds(p=>{ const n=new Set(p); n.delete(hid); return n; });
                                  }
                                }}
                                style={{background:"transparent",border:"none",cursor:"pointer",
                                  color:refreshingIds.has(ref.id)?T.brand:T.textMuted,
                                  fontSize:12,padding:"1px 3px",borderRadius:4,lineHeight:1,
                                  animation:refreshingIds.has(ref.id)?"spin 0.8s linear infinite":"none",
                                  display:"inline-block"}}>
                                ↻
                              </button>
                            )}
                            {(function(){
                              const src = liveStatus[ref.id];
                              const isGlobalMF = ref.class === "Global Mutual Funds" || ref.isGlobalMF;
                              // Manual / unlisted (not MF)
                              if (!ref.listed && !ref.isMutualFund && !isGlobalMF)
                                return <div><span style={{color:"#D97706"}}>✎ Manual</span>{ref.manualPriceDate&&<div style={{fontSize:10,color:T.textSecondary}}>as on {ref.manualPriceDate}</div>}</div>;
                              // Global MF / UCITS — show actual fetch source
                              if (isGlobalMF) {
                                if (src==="fetching")       return <span style={{color:T.textSecondary}}>⟳</span>;
                                if (src==="yahoo_ucits")    return <span style={{color:"#1D9E75",fontWeight:600}}>🟢 Yahoo NAV</span>;
                                if (src==="manual" || !src) return (
                                  <span style={{color:"#D97706",fontSize:10,cursor:"help"}}
                                    title="UCITS fund NAV not found on Yahoo Finance. Click ✎ Edit on this holding and paste the Yahoo Finance ticker (e.g. OP0000XMS6.SI) to enable live prices.">
                                    ⚠ Set ticker
                                  </span>
                                );
                                return <span style={{color:"#D97706",fontSize:10}}>⟳ Fetching…</span>;
                              }
                              // Indian MF — MFapi.in
                              if (ref.isMutualFund) return <span style={{color:"#2563EB"}}>● MFapi.in</span>;
                              // Listed stocks
                              if (src==="google")     return <span style={{color:"#1D9E75",fontWeight:600}}>🟢 Google</span>;
                              if (src==="yahoo")      return <span style={{color:"#1D9E75",fontWeight:600}}>🟢 Yahoo</span>;
                              if (src==="finnhub")    return <span style={{color:"#1D9E75"}}>● Finnhub</span>;
                              if (src==="twelvedata") return <span style={{color:"#1D9E75"}}>● Twelve Data</span>;
                              if (src==="fetching")   return <span style={{color:T.textSecondary}}>⟳</span>;
                              if (src==="simulated")  return <span style={{color:"#D97706",fontSize:10}}>〜 Sim</span>;
                              return <span style={{color:T.textSecondary,fontSize:10}}>—</span>;
                            })()}
                            </div>
                          </td>}

                          {/* Total Value (with Cost as secondary line — C-mockup hybrid layout) */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>
                            <div>{fmt(convert(totalValueUSD,dc),dc)}</div>
                            {avgCost > 0 && totalQty > 0 && (
                              <div style={{fontSize:10.5,color:T.textMuted,fontWeight:400,marginTop:1}}>
                                Cost {fmt(convert(toUSD(avgCost * totalQty, ref.currency), dc), dc)}
                                {hasUnknownCost && <span style={{color:"#D97706",marginLeft:4}} title="Some lots have no cost price">~</span>}
                              </div>
                            )}
                          </td>

                          {/* P&L — hidden by default (rolled into Return cell below). Available via Columns picker. */}
                          {!hiddenCols.has("pl") && (
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",
                              color:gainUSD>=0?"#1D9E75":"#A32D2D"})}>
                              {gainUSD!==0 ? fmt(convert(gainUSD,dc),dc) : hasUnknownCost ? <span style={{color:"#D97706"}}>~{fmt(convert(gainUSD,dc),dc)}</span> : "—"}
                            </td>
                          )}

                          {/* Return % (with absolute P&L as secondary line) */}
                          <td style={tTd()}>
                            <div><Pct v={gainPct}/></div>
                            {gainUSD !== 0 && gainPct != null && (
                              <div style={{fontSize:10.5,color:gainUSD>=0?"#1D9E75":"#A32D2D",opacity:0.75,fontWeight:400,marginTop:1,fontVariantNumeric:"tabular-nums"}}>
                                {gainUSD>=0?"+":""}{fmt(convert(gainUSD,dc),dc)}
                              </div>
                            )}
                          </td>

                          {/* Owner(s) — plain text only (reassign via Edit holding) */}
                          {!hiddenCols.has("owner") && <td style={tTd({fontSize:11,minWidth:80})}>
                            {owners.length > 0
                              ? <span style={{fontWeight:500,fontSize:11,color:"#2563EB"}}>{owners.join(", ")}</span>
                              : <span style={{color:T.textMuted,fontSize:10}}>—</span>
                            }
                          </td>}

                          {/* Actions — visible on row hover */}
                          <td style={tTd()}>
                            <div className="holding-action-btns" style={{display:"flex",gap:4,alignItems:"center",transition:"opacity 0.15s"}}>
                              {/* AI Insight button */}
                              <button
                                onClick={e=>{ e.stopPropagation(); fetchAiInsight(ref); }}
                                title="Get AI insight for this holding"
                                style={{background:"transparent",
                                  border:`1px solid ${T.borderSubtle}`,
                                  borderRadius:4,color:"#7C3AED",padding:"2px 7px",cursor:"pointer",
                                  fontSize:11,fontWeight:500,lineHeight:1.4}}>
                                {aiInsights[ref.id]?.loading ? "⟳" : "✦ AI"}
                              </button>
                              {isAdmin && <button onClick={()=>{ setEditHoldId(ref.id); setEditHoldVals({
                                ...ref,
                                ticker: (ref.ticker||"-").trim().toUpperCase(),
                                sector: (ref.sector && ref.sector !== "" && ref.sector !== "-" && ref.sector !== "Other") ? ref.sector : resolveHoldingSector(ref),
                                capSize: (ref.capSize && ref.capSize !== "N/A" && ref.capSize !== "-" && ref.capSize !== "") ? ref.capSize : resolveCapSize(ref),
                              }); }} style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,borderRadius:4,color:T.textSecondary,padding:"2px 8px",cursor:"pointer",fontSize:11}}>Edit</button>}
                              {isAdmin && <button onClick={()=>{ requireConfirm(`Delete "${ref.name}"?`, async ()=>{
                                  try {
                                    await deleteHolding(ref.id);
                                    await removeHoldingOwner(ref.id).catch(()=>{}); // clean up owner record
                                    setHoldings(p=>p.filter(h=>h.id!==ref.id));
                                    setHoldingOwners(p=>{ const n={...p}; delete n[String(ref.id)]; return n; });
                                    logAudit("DELETE_HOLDING", ref.name);
                                    showToast("✓ Deleted: "+ref.name,"#1D9E75");
                                  } catch(e) {
                                    console.error("[Delete]",e.message);
                                    showToast("❌ Delete failed: "+e.message,"#A32D2D");
                                  }
                                }, "This cannot be undone."); }} title="Delete" style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,borderRadius:4,color:"#A32D2D",padding:"3px 6px",cursor:"pointer",fontSize:11}}>✕</button>}
                            </div>
                          </td>
                        </tr>
                      );

                      // ── Individual lot rows (shown when expanded) ──
                      const lotRows = isExpanded ? rows.map((h, li) => (
                        <tr key={`lot-${h.id}`}
                          style={{background: `${T.brand}07`, borderLeft:`3px solid ${T.brand}55`}}>

                          {/* Bulk checkbox spacer for lot rows */}
                          <td style={{...tTd(),width:32}}></td>
                          {/* Lot label */}
                          <td style={{...tTd(), paddingLeft:34}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:9,color:T.brand,fontWeight:700,background:`${T.brand}18`,
                                border:`1px solid ${T.brand}44`,borderRadius:3,padding:"1px 5px",flexShrink:0}}>
                                LOT {li+1}
                              </span>
                              <div style={{fontSize:11,color:T.textSecondary}}>
                                {h.manager||"Self-Managed"}
                                {h.costPrice <= 0 && <span style={{marginLeft:6,color:"#D97706",fontSize:10}}>⚠ no cost</span>}
                              </div>
                            </div>
                          </td>

                          {/* Ticker */}
                          {!hiddenCols.has("ticker") && <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:T.textSecondary})}>{h.ticker}</td>}

                          {/* Class — blank for lots */}
                          <td style={tTd()}></td>

                          {/* Cap — blank */}
                          {!hiddenCols.has("cap") && <td style={tTd()}></td>}

                          {/* Custodian */}
                          {!hiddenCols.has("manager") && <td style={tTd({fontSize:11})}>
                            {(function(){ const m=h.manager||"Self-Managed"; const c=MANAGER_COLORS[m]||"#78716C";
                              return <span style={{color:c,fontWeight:600,fontSize:10,background:`${c}15`,padding:"2px 6px",borderRadius:5,border:`1px solid ${c}44`,whiteSpace:"nowrap"}}>{m}</span>; })()}
                          </td>}

                          {/* Currency */}
                          {!hiddenCols.has("ccy") && <td style={tTd({color:T.textSecondary,fontSize:12})}>{h.currency}</td>}

                          {/* Qty */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                            {h.qty.toLocaleString("en-US")}
                          </td>

                          {/* Cost price for this lot */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                            {h.costPrice > 0
                              ? h.costPrice.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})
                              : <span style={{color:"#D97706",fontSize:11}}>— not set</span>
                            }
                          </td>

                          {/* Current price */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,fontWeight:600})}>
                            {h.currentPrice.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}
                          </td>

                          {/* Price source — blank */}
                          <td style={tTd()}></td>

                          {/* Value (with cost as secondary line for hybrid layout) */}
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                            <div>{fmt(convert(h.valueUSD,dc),dc)}</div>
                            {h.costPrice > 0 && h.qty > 0 && (
                              <div style={{fontSize:10,color:T.textMuted,fontWeight:400,marginTop:1}}>
                                Cost {fmt(convert(h.costUSD,dc),dc)}
                              </div>
                            )}
                          </td>

                          {/* P&L for lot — hidden by default (rolled into Return cell below) */}
                          {!hiddenCols.has("pl") && (
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                              color:h.costPrice>0?(h.gainPct>=0?"#1D9E75":"#A32D2D"):"#D97706"})}>
                              {h.costPrice > 0 && h.gainPct != null
                                ? fmt(convert(h.valueUSD-h.costUSD,dc),dc)
                                : <span style={{fontSize:10,color:"#D97706"}} title="Add cost price to see P&L">—</span>
                              }
                            </td>
                          )}

                          {/* Return (with absolute P&L as secondary line — hybrid layout) */}
                          <td style={tTd()}>
                            {h.costPrice > 0 ? (
                              <>
                                <div><Pct v={h.gainPct}/></div>
                                {h.gainPct != null && (
                                  <div style={{fontSize:10,color:h.gainPct>=0?"#1D9E75":"#A32D2D",opacity:0.75,fontWeight:500,marginTop:1,fontVariantNumeric:"tabular-nums"}}>
                                    {h.gainPct>=0?"+":""}{fmt(convert(h.valueUSD-h.costUSD,dc),dc)}
                                  </div>
                                )}
                              </>
                            ) : <span style={{color:"#D97706",fontSize:10}}>—</span>}
                          </td>

                          {/* Owner for lot */}
                          <td style={tTd({fontSize:11})}>
                            {h.client
                              ? <span style={{color:"#2563EB",fontWeight:600,fontSize:11}}>{h.client.name}</span>
                              : <span style={{color:T.textMuted,fontSize:10}}>—</span>
                            }
                          </td>

                          {/* Lot actions */}
                          <td style={{...tTd(), position:"relative"}}>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              {/* Note icon — visible to all, editable by admin */}
                              {(function(){
                                const note = holdingNotes[String(h.id)];
                                const hasNote = note && note.text;
                                const isEditing = noteEditId === String(h.id);
                                if (isEditing) return (
                                  <div style={{position:"absolute",zIndex:500,background:T.cardBg,
                                    border:`1.5px solid ${T.brand}`,borderRadius:12,padding:14,
                                    width:300,boxShadow:"0 16px 48px rgba(0,0,0,0.3)",
                                    backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",
                                    top:"100%",right:0,marginTop:4}}>
                                    <div style={{fontSize:11,fontWeight:600,color:T.brand,marginBottom:6}}>
                                      Advisor Note for {h.name.split(" ").slice(0,3).join(" ")}
                                    </div>
                                    <textarea
                                      autoFocus
                                      value={noteEditVal}
                                      onChange={e=>setNoteEditVal(e.target.value)}
                                      onKeyDown={e=>{
                                        if(e.key==="Enter"&&e.metaKey){
                                          saveHoldingNote(h.id, noteEditVal, noteAdminOnly);
                                          setNoteEditId(null);
                                        }
                                        if(e.key==="Escape") setNoteEditId(null);
                                      }}
                                      placeholder="Add advisor commentary, reasoning, alerts…"
                                      style={{...tInp(),height:80,resize:"vertical",fontSize:12,marginBottom:8}}/>
                                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                                      <input type="checkbox" checked={noteAdminOnly}
                                        onChange={e=>setNoteAdminOnly(e.target.checked)}
                                        style={{cursor:"pointer"}}/>
                                      <span style={{fontSize:10,color:T.textMuted}}>Admin only (hidden from client)</span>
                                    </div>
                                    <div style={{display:"flex",gap:6}}>
                                      <button onClick={()=>{ saveHoldingNote(h.id, noteEditVal, noteAdminOnly); setNoteEditId(null); showToast("✓ Note saved","#1D9E75"); }}
                                        style={{flex:1,background:T.brand,border:"none",borderRadius:6,color:"white",padding:"5px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                        Save
                                      </button>
                                      {hasNote && <button onClick={()=>{ saveHoldingNote(h.id,""); setNoteEditId(null); }}
                                        style={{background:"rgba(220,38,38,0.1)",border:"1px solid #A32D2D44",borderRadius:6,color:"#A32D2D",padding:"5px 8px",cursor:"pointer",fontSize:11}}>
                                        Delete
                                      </button>}
                                      <button onClick={()=>setNoteEditId(null)}
                                        style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textSecondary,padding:"5px 8px",cursor:"pointer",fontSize:11}}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                );
                                return (
                                  <button
                                    title={hasNote ? note.text : "Add advisor note"}
                                    onClick={()=>{
                                      if(!isAdmin) return;
                                      setNoteEditId(String(h.id));
                                      setNoteEditVal(note?.text||"");
                                      setNoteAdminOnly(note?.adminOnly||false);
                                    }}
                                    style={{background:hasNote?"rgba(0,120,120,0.1)":"transparent",
                                      border:`1px solid ${hasNote?T.brand:T.cardBorder}`,
                                      borderRadius:4,color:hasNote?T.brand:T.textMuted,
                                      padding:"2px 5px",cursor:isAdmin?"pointer":"default",
                                      fontSize:12,lineHeight:1}}>
                                    {hasNote ? "🗒" : "📝"}
                                  </button>
                                );
                              })()}
                              {isAdmin && <button onClick={()=>{ setEditHoldId(h.id); setEditHoldVals({
                                ...h,
                                ticker: (h.ticker||"-").trim().toUpperCase(),
                                sector: (h.sector&&h.sector!=="") ? h.sector : resolveHoldingSector(h),
                                capSize: (h.capSize&&h.capSize!=="N/A") ? h.capSize : resolveCapSize(h),
                              }); }} title="Edit holding" style={{background:"rgba(37,99,235,0.08)",border:"1px solid #2563EB",borderRadius:5,color:"#2563EB",padding:"3px 8px",cursor:"pointer",fontSize:11}}>✎</button>}
                              {isAdmin && <button onClick={()=>{ requireConfirm(`Delete lot of "${h.name}"?`, async ()=>{
                                  try {
                                    await deleteHolding(h.id);
                                    await removeHoldingOwner(h.id).catch(()=>{});
                                    setHoldings(p=>p.filter(x=>x.id!==h.id));
                                    setHoldingOwners(p=>{ const n={...p}; delete n[String(h.id)]; return n; });
                                    logAudit("DELETE_LOT", "Lot of "+h.name);
                                    showToast("✓ Lot deleted","#1D9E75");
                                  } catch(e) {
                                    console.error("[Delete lot]",e.message);
                                    showToast("❌ Delete failed: "+e.message,"#A32D2D");
                                  }
                                }, "This cannot be undone."); }} title="Delete lot" style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:5,color:"#A32D2D",padding:"3px 6px",cursor:"pointer",fontSize:11}}>✕</button>}
                            </div>
                          </td>
                        </tr>
                      )) : [];

                      // ── AI Insight panel row ──
                      const insightData = aiInsights[ref.id];
                      const insightRow = insightData ? (
                        <tr key={`ai-insight-${ref.id}`}>
                          <td colSpan={99} style={{padding:0,borderBottom:`1px solid ${T.borderSubtle}`}}>
                            <div style={{padding:"10px 16px 12px 52px",
                              background:`rgba(124,58,237,0.04)`,
                              borderLeft:`3px solid #7C3AED`}}>
                              {insightData.loading && (
                                <div style={{fontSize:12,color:"#7C3AED",display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span>
                                  Generating AI insight…
                                </div>
                              )}
                              {insightData.error && (
                                <div style={{fontSize:12,color:"#A32D2D"}}>⚠ {insightData.error}</div>
                              )}
                              {insightData.insight && (
                                <div>
                                  <div style={{fontSize:11,fontWeight:700,color:"#7C3AED",marginBottom:6,
                                    display:"flex",alignItems:"center",gap:6}}>
                                    ✦ AI Insight
                                    <span style={{fontSize:10,color:T.textMuted,fontWeight:400}}>· {ref.name}</span>
                                    <button onClick={()=>setAiInsights(p=>({...p,[ref.id]:undefined}))}
                                      style={{marginLeft:"auto",background:"none",border:"none",
                                        cursor:"pointer",fontSize:11,color:T.textMuted,padding:"0 4px"}}>✕</button>
                                  </div>
                                  {insightData.insight.split("\n").filter(l=>l.trim()).map((line,i)=>{
                                    const labels=["Thesis","Recent change","Key risk","Position sizing"];
                                    return (
                                      <div key={i} style={{display:"flex",gap:8,marginBottom:i<3?5:0,fontSize:12}}>
                                        <span style={{fontSize:10,fontWeight:700,color:"#7C3AED",
                                          background:"rgba(124,58,237,0.1)",padding:"1px 6px",borderRadius:3,
                                          flexShrink:0,marginTop:1,whiteSpace:"nowrap",height:"fit-content"}}>
                                          {labels[i]||"Note"}
                                        </span>
                                        <span style={{color:T.textSecondary,lineHeight:1.5}}>{line}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null;

                      return [summaryRow, insightRow, ...lotRows].filter(Boolean);
                    })}
                  </tbody>
                </table>

                </div>{/* end scrollable table area */}


                {/* Footer hint */}
                {!isFiltered && hidden > 0 && (
                  <div style={{margin:"10px 16px",padding:"10px 14px",background:T.borderSubtle,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:12,color:T.textMuted}}>+ {hidden} more holdings not shown</span>
                    <span style={{fontSize:12,color:T.textSecondary}}>Go to <strong style={{color:"#2563EB",cursor:"pointer"}} onClick={()=>setTab("families")}>Families tab</strong> → select a family or member to view their complete holdings list</span>
                  </div>
                )}
                {/* Scroll hint for mobile */}
                {isMobile && (
                  <div style={{padding:"6px 16px",fontSize:10,color:T.textMuted,textAlign:"center"}}>← Scroll table horizontally →</div>
                )}
                </React.Fragment>}
              </React.Fragment>
                )}
              </div>
            );
  };

  return (
    <ErrorBoundary>
    <ThemeContext.Provider value={T}>
    <style>{`
      * { box-sizing: border-box; min-width: 0; }
      html, body { max-width: 100vw; overflow-x: hidden; }
      * { transition: background-color 0.25s ease, border-color 0.25s ease, color 0.18s ease; }
      ::-webkit-scrollbar { width: 6px; height: 6px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: ${T.scrollThumb}; border-radius: 3px; }
      input, select, textarea { transition: background-color 0.25s ease, border-color 0.25s ease, color 0.18s ease !important; }
      @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      @keyframes fadeTab { from { opacity:0; transform:translateY(3px); } to { opacity:1; transform:translateY(0); } }
      @keyframes slideUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
      @keyframes scaleIn  { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
      button:focus-visible, a:focus-visible { outline: 2px solid #0079C1; outline-offset: 2px; border-radius: 4px; }
      .card-hover { transition: box-shadow 0.2s ease, transform 0.2s ease; }
      .card-hover:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.12); transform: translateY(-1px); }
    `}</style>
    <div style={{minHeight:"100vh",background:T.pageBg,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,display:"flex",flexDirection:"column"}}>
      

      <style>{`@keyframes fadeSlideDown { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } } @keyframes livePulse { 0%,100% { opacity:1; box-shadow: 0 0 6px #34D399; } 50% { opacity:0.5; box-shadow: 0 0 12px #34D399; } } @keyframes fw-fade-in { from{opacity:0} to{opacity:1} }`}</style>

      {/* Mobile floating hamburger — only when sidebar is closed */}
      {isMobile && !sidebarMobileOpen && (
        <button onClick={()=>setSidebarMobileOpen(true)}
          style={{position:"fixed",top:12,left:12,zIndex:1200,
            background:T.cardBg,border:`1px solid ${T.cardBorder}`,
            borderRadius:10,width:40,height:40,
            display:"flex",alignItems:"center",justifyContent:"center",
            cursor:"pointer",color:T.textSecondary,fontSize:20,
            boxShadow:"0 2px 12px rgba(0,0,0,0.12)"}}>☰</button>
      )}

      {/* ── SIDEBAR + MAIN COLUMN ── */}
      <div style={{display:"flex",flex:1,minHeight:0,position:"relative",overflowX:"hidden"}}>
        <FwSidebar
          T={T} TAB_GROUPS={TAB_GROUPS} tab={tab}
          onNav={(tabId)=>{
            if (!isAdmin && !canAccess(tabId)) {
              const t = TAB_GROUPS.flatMap(g=>g.tabs).find(x=>x.id===tabId);
              setUpgradeModal({tabId, tabLabel:t?.label||tabId, tabIcon:t?.icon||"🔒"});
            } else {
              setTab(tabId);
              // Auto-open the group containing this tab, close all others
              const grp = TAB_GROUPS.find(g=>g.tabs.some(t=>t.id===tabId));
              if (grp) setSidebarOpenGroups({ [grp.id]: true });
            }
          }}
          isAdmin={isAdmin} canAccess={canAccess} clientPlans={clientPlans}
          loggedInClientId={loggedInClientId} visibleClients={visibleClients}
          isMobile={isMobile}
          collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}
          mobileOpen={sidebarMobileOpen} setMobileOpen={setSidebarMobileOpen}
          openGroups={sidebarOpenGroups} toggleGroup={toggleSidebarGroup}
          dc={dc} setDc={setDc}
          refreshing={refreshing} onRefresh={()=>{ refresh(true); refreshFx(); }}
          onReport={isAdmin ? ()=>{ setPdfFamilyId(visibleFamilies[0]?.id||""); setPdfClientId(visibleClients[0]?.id||""); setPdfModal(true); } : loggedInClientId ? ()=>{ setPdfClientId(loggedInClientId); setPdfScope("individual"); setPdfModal(true); } : null}
          onMethodology={()=>setMethodologyOpen(true)}
          profileData={profileData}
          clients={clients}
          themeId={themeId}
          isLightMode={isLightMode}
          toggleTheme={toggleTheme}
          onProfile={()=>setProfileOpen(true)}
          onChangePassword={()=>setPwModal(true)}
          onSignOut={()=>{setSignedOut(true);setLoginRole("client");}}
          pinnedTabs={pinnedTabs}
          togglePinTab={togglePinTab}
        />
        {/* Main content column */}
        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",minHeight:"100vh",paddingBottom:isMobile?"calc(62px + env(safe-area-inset-bottom, 0px))":0,width:isMobile?"100%":undefined,overflowX:"hidden",boxSizing:"border-box"}}>

      {/* ── FAMILY / CLIENT FILTER BAR — always visible for admin ── */}
      {isAdmin && tab !== "subscriptions" && (
        <div style={{background:T.thBg,borderBottom:`1px solid ${T.border}`,padding:isMobile?"6px 10px 6px 58px":"6px 24px",flexShrink:0,boxSizing:"border-box",width:"100%"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            {/* Family dropdown */}
            <div style={{position:"relative",minWidth:isMobile?120:180}}>
              <div style={{fontSize:9,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2}}>Family</div>
              <div style={{position:"relative"}}>
                <input
                  type="text"
                  placeholder="All families…"
                  value={scopeFamilySearch}
                  onFocus={()=>setScopeFamilyOpen(true)}
                  onBlur={()=>setTimeout(()=>setScopeFamilyOpen(false),150)}
                  onChange={e=>{ setScopeFamilySearch(e.target.value); setScopeFamilyOpen(true); }}
                  style={{width:"100%",boxSizing:"border-box",padding:"5px 28px 5px 10px",borderRadius:8,
                    border:`1px solid ${selectedFamily!=="all"?"#2563EB":T.cardBorder}`,
                    background:selectedFamily!=="all"?"rgba(37,99,235,0.06)":T.inputBg,
                    color:T.textPrimary,fontSize:12,outline:"none",cursor:"pointer"}}
                />
                {selectedFamily!=="all"
                  ? <span onClick={()=>{ setSelectedFamily("all"); setSelectedClients(new Set()); setScopeFamilySearch(""); setScopeClientSearch(""); }}
                      style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",cursor:"pointer",color:"#2563EB",fontSize:14,fontWeight:700}}>✕</span>
                  : <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",color:T.textMuted,fontSize:10,pointerEvents:"none"}}>▾</span>
                }
              </div>
              {scopeFamilyOpen && (function(){
                const q = scopeFamilySearch.toLowerCase();
                // Require 3+ chars to show results (or show all if a family is already selected)
                const showFams = q.length >= 3
                  ? visibleFamilies.filter(f => f.name.toLowerCase().includes(q))
                  : selectedFamily !== "all"
                    ? visibleFamilies.filter(f => String(f.id) === String(selectedFamily))
                    : [];
                const opts = [
                  {id:"all", name:"All Families", emoji:"👥"},
                  ...showFams,
                ];
                const showHint = q.length > 0 && q.length < 3;
                return (showHint || opts.length > 1) ? (
                  <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:200,
                    background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.15)",marginTop:3,maxHeight:220,overflowY:"auto"}}>
                    {opts.map(f=>(
                      <div key={f.id}
                        onMouseDown={()=>{
                          setSelectedFamily(f.id==="all"?"all":String(f.id));
                          setSelectedClients(new Set());
                          setViewAsClientId(null);
                          setScopeFamilySearch(f.id==="all"?"":f.name);
                          setScopeClientSearch("");
                          setScopeFamilyOpen(false);
                        }}
                        style={{padding:"8px 12px",cursor:"pointer",fontSize:12,
                          background:String(selectedFamily)===String(f.id)?"rgba(37,99,235,0.08)":"transparent",
                          color:String(selectedFamily)===String(f.id)?"#2563EB":T.textPrimary,
                          fontWeight:String(selectedFamily)===String(f.id)?700:400,
                          display:"flex",alignItems:"center",gap:8}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(37,99,235,0.05)"}
                        onMouseLeave={e=>e.currentTarget.style.background=String(selectedFamily)===String(f.id)?"rgba(37,99,235,0.08)":"transparent"}>
                        <span>{f.emoji||"👨‍👩‍👧"}</span>
                        <span>{f.name}</span>
                        {f.id!=="all"&&<span style={{marginLeft:"auto",fontSize:10,color:T.textMuted}}>
                          {visibleClients.filter(c=>c.familyId===f.id).length} members
                        </span>}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Client dropdown — shows clients in selected family (or all if no family selected) */}
            <div style={{position:"relative",minWidth:isMobile?120:200}}>
              <div style={{fontSize:9,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2}}>Client</div>
              <div style={{position:"relative"}}>
                <input
                  type="text"
                  placeholder={selectedFamily==="all"?"All clients…":"Select client…"}
                  value={scopeClientSearch}
                  onFocus={()=>setScopeClientOpen(true)}
                  onBlur={()=>setTimeout(()=>setScopeClientOpen(false),150)}
                  onChange={e=>{ setScopeClientSearch(e.target.value); setScopeClientOpen(true); }}
                  style={{width:"100%",boxSizing:"border-box",padding:"5px 28px 5px 10px",borderRadius:8,
                    border:`1px solid ${selectedClients.size>0?"#7C3AED":T.cardBorder}`,
                    background:selectedClients.size>0?"rgba(124,58,237,0.06)":T.inputBg,
                    color:T.textPrimary,fontSize:12,outline:"none",cursor:"pointer"}}
                />
                {selectedClients.size>0
                  ? <span onClick={()=>{ setSelectedClients(new Set()); setScopeClientSearch(""); }}
                      style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",cursor:"pointer",color:"#7C3AED",fontSize:14,fontWeight:700}}>✕</span>
                  : <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",color:T.textMuted,fontSize:10,pointerEvents:"none"}}>▾</span>
                }
              </div>
              {scopeClientOpen && (function(){
                const q = scopeClientSearch.toLowerCase();
                const famClients = selectedFamily==="all"
                  ? visibleClients
                  : visibleClients.filter(c=>String(c.familyId)===String(selectedFamily));
                // Show family members immediately; for "all families" require 3+ chars
                const filtered = selectedFamily !== "all" && q.length === 0
                  ? famClients   // show all in selected family immediately
                  : q.length >= 3
                    ? famClients.filter(c=>c.name.toLowerCase().includes(q))
                    : [];
                const opts = [{id:"all",name:"All Clients"}, ...filtered];
                const hint = q.length > 0 && q.length < 3;
                const noShow = opts.length <= 1 && filtered.length === 0 && !hint;
                return noShow ? null : (
                  <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:200,
                    background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.15)",marginTop:3,maxHeight:220,overflowY:"auto"}}>
                    {opts.map(c=>{
                      const isSelected = c.id==="all" ? selectedClients.size===0 : selectedClients.has(String(c.id));
                      return (
                        <div key={c.id}
                          onMouseDown={()=>{
                            if (c.id==="all") {
                              setSelectedClients(new Set());
                              setScopeClientSearch("");
                            } else {
                              setSelectedClients(new Set([String(c.id)]));
                              setScopeClientSearch(c.name);
                              if (selectedFamily==="all" && c.familyId) {
                                setSelectedFamily(String(c.familyId));
                                const fam = visibleFamilies.find(f=>String(f.id)===String(c.familyId));
                                setScopeFamilySearch(fam?.name||"");
                              }
                            }
                            setViewAsClientId(null);
                            setScopeClientOpen(false);
                          }}
                          style={{padding:"8px 12px",cursor:"pointer",fontSize:12,
                            background:isSelected?"rgba(124,58,237,0.08)":"transparent",
                            color:isSelected?"#7C3AED":T.textPrimary,
                            fontWeight:isSelected?700:400,
                            display:"flex",alignItems:"center",gap:8}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(124,58,237,0.05)"}
                          onMouseLeave={e=>e.currentTarget.style.background=isSelected?"rgba(124,58,237,0.08)":"transparent"}>
                          <span>{c.id==="all"?"👥":"👤"}</span>
                          <span>{c.name}</span>
                          {c.id!=="all"&&<span style={{marginLeft:"auto",fontSize:10,color:T.textMuted}}>
                            {visibleFamilies.find(f=>String(f.id)===String(c.familyId))?.name||""}
                          </span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Active filter summary — Clear button only (info already in FAMILY/CLIENT dropdowns above) */}
            {(selectedFamily!=="all"||selectedClients.size>0||viewAsClientId) && (
              <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:4,flexWrap:"wrap"}}>
                <div style={{width:1,height:28,background:T.cardBorder,flexShrink:0}}/>
                <button onClick={()=>{ setSelectedFamily("all"); setSelectedClients(new Set()); setViewAsClientId(null); setScopeFamilySearch(""); setScopeClientSearch(""); }}
                  style={{background:"none",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textMuted,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600,flexShrink:0}}>
                  Clear ✕
                </button>
              </div>
            )}
            {/* ── Sync Data button — always visible, reloads all data from Supabase ── */}
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
              <button
                onClick={reloadFromSupabase}
                disabled={dbLoading}
                title="Force-reload all portfolio data from server. Use this if data looks outdated on this device."
                style={{
                  display:"flex",alignItems:"center",gap:5,
                  background:dbLoading?"transparent":"rgba(0,121,193,0.08)",
                  border:`1px solid ${dbLoading?"transparent":"rgba(0,121,193,0.3)"}`,
                  borderRadius:6,padding:"4px 12px",cursor:dbLoading?"not-allowed":"pointer",
                  color:dbLoading?T.textMuted:"#0079C1",fontSize:11,fontWeight:600,
                  transition:"all 0.15s"
                }}>
                <span style={{display:"inline-block",animation:dbLoading?"spin 0.8s linear infinite":"none",fontSize:12}}>↻</span>
                {dbLoading ? "Syncing…" : "Sync Data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SHORTCUTS BAR ── */}
      {tab !== "subscriptions" && (pinnedTabs.length > 0 || editingShortcuts) && (
        <div style={{background:T.thBg,borderBottom:`1px solid ${T.border}`,
          padding:isMobile?"5px 10px 5px 58px":"5px 24px",flexShrink:0,boxSizing:"border-box"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {!editingShortcuts && pinnedTabs.map(tabId => {
              const allTabs = TAB_GROUPS.flatMap(g=>g.tabs);
              const t = allTabs.find(x=>x.id===tabId);
              if (!t) return null;
              const isActive = tab===tabId;
              return (
                <button key={tabId} onClick={()=>setTab(tabId)}
                  style={{display:"inline-flex",alignItems:"center",gap:5,
                    padding:"4px 12px",borderRadius:20,border:`1px solid ${isActive?"#2563EB":T.cardBorder}`,
                    background:isActive?"rgba(37,99,235,0.10)":"transparent",
                    color:isActive?"#2563EB":T.textSecondary,
                    fontSize:12,fontWeight:isActive?600:400,cursor:"pointer",
                    transition:"all 0.12s",whiteSpace:"nowrap"}}>
                  <span style={{display:"inline-flex",alignItems:"center"}}>
                    {t.iconName
                      ? <NavIcon name={t.iconName} size={14} color={isActive?"#2563EB":T.textSecondary} />
                      : <span style={{fontSize:12}}>{t.icon}</span>}
                  </span>
                  {t.label}
                </button>
              );
            })}
            {editingShortcuts && (
              <React.Fragment>
                <span style={{fontSize:11,color:T.textMuted,marginRight:4}}>Pin up to 6 tabs:</span>
                {TAB_GROUPS.flatMap(g=>g.tabs).map(t => {
                  const isPinned = pinnedTabs.includes(t.id);
                  return (
                    <button key={t.id} onClick={()=>togglePinTab(t.id)}
                      style={{display:"inline-flex",alignItems:"center",gap:5,
                        padding:"4px 12px",borderRadius:20,
                        border:`1px solid ${isPinned?"#D97706":T.cardBorder}`,
                        background:isPinned?"rgba(217,119,6,0.08)":"transparent",
                        color:isPinned?"#D97706":T.textMuted,
                        fontSize:11,cursor:"pointer",transition:"all 0.12s",whiteSpace:"nowrap"}}>
                      <span style={{fontSize:11}}>{isPinned?"★":"☆"}</span>
                      <span style={{display:"inline-flex",alignItems:"center"}}>
                        {t.iconName
                          ? <NavIcon name={t.iconName} size={13} color={isPinned?"#D97706":T.textMuted} />
                          : <span style={{fontSize:11}}>{t.icon}</span>}
                      </span>
                      {t.label}
                    </button>
                  );
                })}
              </React.Fragment>
            )}
            <button onClick={()=>setEditingShortcuts(p=>!p)}
              style={{marginLeft:"auto",flexShrink:0,background:"transparent",border:"none",
                cursor:"pointer",fontSize:11,color:T.textMuted,padding:"4px 6px",
                display:"flex",alignItems:"center",gap:4}}>
              {editingShortcuts
                ? <React.Fragment><span>✓</span> Done</React.Fragment>
                : <React.Fragment><span>✏</span> Edit</React.Fragment>}
            </button>
          </div>
        </div>
      )}

      {/* Show shortcut bar trigger if no pins yet */}
      {tab !== "subscriptions" && pinnedTabs.length === 0 && !editingShortcuts && (
        <div style={{padding:isMobile?"3px 10px 3px 58px":"3px 24px",flexShrink:0}}>
          <button onClick={()=>setEditingShortcuts(true)}
            style={{background:"transparent",border:"none",cursor:"pointer",
              fontSize:11,color:T.textMuted,padding:"4px 0",
              display:"inline-flex",alignItems:"center",gap:4,opacity:0.6}}>
            <span>☆</span> Add shortcuts
          </button>
        </div>
      )}

      {/* ── CONTENT ── */}
      <div key={tab} style={{maxWidth:1400,margin:"0 auto",display:tab==="subscriptions"?"none":"block",padding:isMobile?"10px 12px 80px":"20px 24px 40px",width:"100%",maxWidth:"100%",boxSizing:"border-box",flex:1,animation:"fadeTab 0.12s ease"}}>

        {/* ── BACK / HOME BUTTON ── */}
        {tab!=="summary" && tab!=="overview" && tab!=="subscriptions" && (
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>
            <button onClick={()=>setTab("summary")}
              style={{display:"flex",alignItems:"center",gap:5,background:T.borderSubtle,
                border:`1px solid ${T.cardBorder}`,borderRadius:7,padding:"4px 12px",
                cursor:"pointer",fontSize:12,color:T.textSecondary,fontWeight:600,
                transition:"all 0.15s",flexShrink:0}}
              onMouseEnter={e=>{e.currentTarget.style.background=T.hoverBg;e.currentTarget.style.color=T.textPrimary;}}
              onMouseLeave={e=>{e.currentTarget.style.background=T.borderSubtle;e.currentTarget.style.color=T.textSecondary;}}>
              ← Home
            </button>
            {(()=>{
              const grp = TAB_GROUPS.find(g=>g.tabs.some(t=>t.id===tab));
              const t = TAB_GROUPS.flatMap(g=>g.tabs).find(x=>x.id===tab);
              if (!t) return null;
              if (isMobile) return <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{t.icon} {t.label}</span>;
              return <span style={{fontSize:11,color:T.textMuted}}>
                {grp ? <span>{grp.icon} {grp.label} <span style={{opacity:0.5}}>›</span> {t.icon} {t.label}</span> : t.label}
              </span>;
            })()}
          </div>
        )}

        {/* ── ACTIVE CLIENT/FAMILY CONTEXT BANNER ── */}
        {isAdmin && (viewAsClientId || selectedClients.size > 0 || selectedFamily !== "all") && (function(){
          const clientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : null);
          const vc = clientId ? clients.find(c=>c.id===clientId) : null;
          const fam = !vc && selectedFamily!=="all" ? visibleFamilies.find(f=>String(f.id)===String(selectedFamily)) : null;
          const multiClient = !viewAsClientId && selectedClients.size > 1;
          if (!vc && !fam && !multiClient) return null;
          const isViewAs = !!viewAsClientId;
          const label = isViewAs ? "Viewing as client"
            : multiClient ? `${selectedClients.size} clients selected`
            : vc ? "Filtered to client"
            : "Filtered to family";
          const name = vc ? vc.name : fam ? fam.name : `${selectedClients.size} clients`;
          const icon = isViewAs ? "👁" : vc ? "👤" : "👨‍👩‍👧";
          const col = isViewAs ? "#7C3AED" : vc ? T.brand : "#0079C1";
          const bg  = isViewAs ? "rgba(124,58,237,0.06)" : vc ? "rgba(37,99,235,0.06)" : "rgba(0,121,193,0.06)";
          const border = isViewAs ? "rgba(124,58,237,0.25)" : vc ? "rgba(37,99,235,0.25)" : "rgba(0,121,193,0.25)";
          return (
            <div style={{marginBottom:16,padding:"10px 16px",borderRadius:10,
              background:bg, border:`1.5px solid ${border}`,
              display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <span style={{fontSize:18}}>{icon}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:col}}>
                  {label}: <strong>{name}</strong>
                  {isViewAs && " — all tabs and data are filtered to this client"}
                </div>
                {!isViewAs && (
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                    {kpiHW.length} holdings · {fmt(convert(kpiTotalUSD,dc),dc,true)}
                  </div>
                )}
              </div>
              <button onClick={()=>{ setViewAsClientId(null); setSelectedClients(new Set()); setSelectedFamily("all"); setScopeFamilySearch(""); setScopeClientSearch(""); }}
                style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.25)",
                  borderRadius:7,color:"#A32D2D",padding:"5px 14px",cursor:"pointer",
                  fontSize:11,fontWeight:700,flexShrink:0}}>
                ✕ Clear Filter
              </button>
            </div>
          );
        })()}


        {/* ══════════════════════════════════════════════════════════════
            ⭐ EXECUTIVE SUMMARY TAB — North Star view
            Designed for: morning review, client meetings, partner decks
        ══════════════════════════════════════════════════════════════ */}
        {tab==="summary" && (function(){ try {
          const hw        = filteredHW;

          // Ghost-position filter: exclude qty=0 holdings from all stats
          const activeHW   = hw.filter(h => (h.qty||0) > 0);
          const totalV    = activeHW.reduce((s,h)=>s+h.valueUSD,0)||1;
          const totalCostRaw = activeHW.reduce((s,h)=>s+h.costUSD,0);
          const totalCost = totalCostRaw || 0;
          const totalGain = totalV - totalCost;
          // Only show gainPct when we have meaningful cost data (>1% of portfolio value)
          const gainPct   = totalCost > totalV * 0.01 ? (totalGain/totalCost*100) : null;
          const today     = new Date();

          const winners    = activeHW.filter(h => h.gainPct!=null && h.gainPct > 0).length;
          const losers     = activeHW.filter(h => h.gainPct!=null && h.gainPct < 0).length;

          // Top movers (by absolute gain %, active positions only)
          const withGain  = activeHW.filter(h=>h.gainPct!=null&&h.costPrice>0);
          const topGainers= [...withGain].sort((a,b)=>b.gainPct-a.gainPct).slice(0,3);
          const topLosers = [...withGain].sort((a,b)=>a.gainPct-b.gainPct).slice(0,3);

          // Top holdings by value
          const topHoldings = [...activeHW].sort((a,b)=>b.valueUSD-a.valueUSD).slice(0,5);
          const top5Pct     = topHoldings.reduce((s,h)=>s+h.valueUSD/totalV*100,0);

          // Recent transactions (last 5)
          const recentTxns = [...transactions]
            .filter(t=>{ const n=new Set(hw.map(h=>h.name)); return n.has(t.holding); })
            .sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,5);

          // Benchmark alpha
          const bmData     = benchMetrics?.[bench] || BENCHMARK_FALLBACK[bench];
          const bmHorizon  = bmData?.ytd != null ? "YTD" : "Since Inception";
          const bmReturn   = bmData?.ytd || bmData?.oneY || null;
          const bmLabel    = BENCHMARKS_DEF.find(b=>b.id===bench)?.label || "Benchmark";
          const portReturn = gainPct;
          const alpha      = portReturn!=null&&bmReturn!=null ? portReturn-bmReturn : null;

          // Pending actions
          const today2 = new Date();
          const pendingCC  = capitalCalls.filter(cc=>cc.status!=="funded"&&new Date(cc.callDate||0)>=today2);
          const urgentTax  = taxEvents.filter(t=>t.status!=="paid"&&new Date(t.dueDate||0)>=today2
            &&(new Date(t.dueDate)-today2)<30*86400000);
          const rebalNeeded = (()=>{
            const model_ = model.filter(m=>m.targetPct>0);
            if (!model_.length) return 0;
            let drift=0;
            model_.forEach(m=>{
              const actual = hw.filter(h=>h.class===m.assetClass).reduce((s,h)=>s+h.valueUSD,0)/totalV*100;
              if (Math.abs(actual-m.targetPct)>5) drift++;
            });
            return drift;
          })();

          // ── Detailed rebalance drifts (array of {assetClass, actual, target}) for landing layout ──
          // Same logic as rebalNeeded above but returns actual objects so we can render them
          // in the Pending Actions card. Empty array if no model is set.
          // NOTE: model entries use `assetClass` (not `class`) and holdings use `class`.
          // The previous code referenced `m.class` which is undefined, causing rendered
          // entries to show "undefined off target" with actual=0% (since holdings.class
          // never matches undefined).
          const rebalDrifts = (()=>{
            const model_ = model.filter(m=>m.targetPct>0);
            if (!model_.length) return [];
            const drifts = [];
            model_.forEach(m=>{
              const actual = hw.filter(h=>h.class===m.assetClass).reduce((s,h)=>s+h.valueUSD,0)/totalV*100;
              if (Math.abs(actual-m.targetPct)>5) drifts.push({ assetClass: m.assetClass, actual, target: m.targetPct });
            });
            return drifts;
          })();

          // Natural language summary
          const nlSummary = (()=>{
            const parts=[];
            if (gainPct!=null) {
              parts.push(`Portfolio is ${gainPct>=0?"up":"down"} ${Math.abs(gainPct).toFixed(1)}% overall`);
            }
            if (topGainers[0]?.gainPct>10) parts.push(`led by ${topGainers[0].name.split(" ")[0]} (+${topGainers[0].gainPct.toFixed(1)}%)`);
            if (topLosers[0]?.gainPct<-5) parts.push(`offset by weakness in ${topLosers[0].name.split(" ")[0]} (${topLosers[0].gainPct.toFixed(1)}%)`);
            if (alpha!=null) parts.push(`${alpha>=0?"outperforming":"underperforming"} ${BENCHMARKS_DEF.find(b=>b.id===bench)?.label||"benchmark"} by ${Math.abs(alpha).toFixed(1)}%`);
            return parts.length ? parts.join(", ")+"." : "Add cost prices to your holdings to generate portfolio insights.";
          })();

          // ── Computed values for new tightened landing (Phase 1) ──
          // Currency exposure: aggregate value by currency, return top 3 with % weights
          const currencyAgg = {};
          activeHW.forEach(h => {
            const c = h.currency || "USD";
            currencyAgg[c] = (currencyAgg[c] || 0) + h.valueUSD;
          });
          const totalCcyValue = Object.values(currencyAgg).reduce((s,v) => s+v, 0) || 1;
          const ccyBreakdown = Object.entries(currencyAgg)
            .map(([ccy, val]) => ({ ccy, pct: val/totalCcyValue*100, val }))
            .sort((a,b) => b.pct - a.pct);
          const topCcyChips = ccyBreakdown.slice(0, 3);
          const ccyCount = ccyBreakdown.length;

          // Liquidity: % of portfolio in listed assets (rough proxy — listed=true OR class is Cash/MF)
          // Unlisted PE, real estate, art etc. count as illiquid. Cash/FD/MF count as liquid.
          const LIQUID_CLASSES = new Set(["Equities","Mutual Funds","Bonds","Cash","FD","ETF"]);
          const liquidValue = activeHW
            .filter(h => h.listed === true || LIQUID_CLASSES.has(h.class))
            .reduce((s,h) => s+h.valueUSD, 0);
          const liquidPct = totalV > 0 ? (liquidValue/totalV*100) : 0;
          const illiquidPct = 100 - liquidPct;

          // Asset class breakdown
          const classAgg = {};
          activeHW.forEach(h => {
            const c = h.class || "Other";
            classAgg[c] = (classAgg[c] || 0) + h.valueUSD;
          });
          const classBreakdown = Object.entries(classAgg)
            .map(([cls, val]) => ({ cls, pct: val/totalV*100, val }))
            .sort((a,b) => b.pct - a.pct);

          // Country count
          const countryCount = [...new Set(activeHW.map(h => h.country).filter(Boolean))].length;

          // Risk grade based on top5Pct + illiquidity
          const riskGrade = (() => {
            if (top5Pct > 60 || illiquidPct > 50) return { label: "High", color: "#A32D2D" };
            if (top5Pct > 40 || illiquidPct > 25) return { label: "Moderate", color: "#D97706" };
            return { label: "Low", color: "#1D9E75" };
          })();

          // ─── NEW LANDING LAYOUT v1 ───
          return <React.Fragment>
            {/* HERO BAND — big number, gain, narrative, FX chips */}
            <div style={{...tCard(), marginBottom: 14, padding: "20px 24px",
              background: `linear-gradient(135deg, ${T.brand}10, ${T.accent || "#7C3AED"}06)`,
              borderColor: T.brand + "33"}}>
              {/* Row 1: Big total value + gain */}
              <div style={{display:"flex", alignItems:"baseline", gap: 16, flexWrap:"wrap", marginBottom: 8}}>
                <div style={{fontSize: isMobile ? 30 : 42, fontWeight: 800,
                  fontFamily:"'Inter', system-ui, sans-serif", color: T.textPrimary, lineHeight: 1}}>
                  {fmt(convert(totalV, dc), dc)}
                  {refreshing && <span style={{marginLeft: 8, fontSize: 14, color:"#D97706"}}>⟳</span>}
                </div>
                {gainPct != null && (
                  <span style={{fontSize: isMobile ? 16 : 20, fontWeight: 700,
                    fontFamily:"'Inter', system-ui, sans-serif",
                    color: totalGain >= 0 ? "#1D9E75" : "#A32D2D"}}>
                    {totalGain >= 0 ? "+" : ""}{fmt(convert(totalGain, dc), dc)}
                    <span style={{fontSize: isMobile ? 13 : 15, marginLeft: 5}}>
                      ({gainPct >= 0 ? "+" : ""}{gainPct.toFixed(2)}%)
                    </span>
                  </span>
                )}
                {/* Alpha vs benchmark */}
                {alpha != null && !isMobile && (
                  <span style={{fontSize: 12, color: T.textMuted, marginLeft: "auto"}}>
                    α vs {bmLabel}: <strong style={{color: alpha >= 0 ? "#1D9E75" : "#A32D2D"}}>
                      {alpha >= 0 ? "+" : ""}{alpha.toFixed(1)}%
                    </strong>
                  </span>
                )}
              </div>
              {/* Row 2: Meta line */}
              <div style={{fontSize: 11, color: T.textMuted, marginBottom: 10}}>
                Since inception · FX-adjusted · {activeHW.length} holdings · {ccyCount} currencies · {countryCount} countries
              </div>
              {/* Row 3: FX chips (top 3 currencies) */}
              {topCcyChips.length > 1 && (
                <div style={{display:"flex", gap: 6, flexWrap:"wrap", marginBottom: 10}}>
                  {topCcyChips.map(c => (
                    <span key={c.ccy} style={{
                      fontSize: 10, padding: "3px 9px", borderRadius: 12,
                      background: c.ccy === dc ? "rgba(124,58,237,0.12)" : T.borderSubtle,
                      color: c.ccy === dc ? "#7C3AED" : T.textSecondary,
                      fontWeight: 600,
                      border: `1px solid ${c.ccy === dc ? "rgba(124,58,237,0.25)" : T.cardBorder}`
                    }}>
                      {c.ccy} {c.pct.toFixed(0)}%
                    </span>
                  ))}
                </div>
              )}
              {/* Row 4: Heuristic narrative — calm, descriptive */}
              <div style={{fontSize: 13, color: T.textSecondary, lineHeight: 1.5, fontStyle: "italic"}}>
                {nlSummary}
              </div>
            </div>

            {/* PENDING ACTIONS BANNER — only if urgent stuff */}
            {(pendingCC.length > 0 || urgentTax.length > 0 || rebalNeeded > 0) && (
              <div style={{...tCard(), marginBottom: 14, padding: "12px 16px",
                background: "rgba(217, 119, 6, 0.06)", borderColor: "rgba(217, 119, 6, 0.3)"}}>
                <div style={{fontSize: 11, fontWeight: 700, color: "#92400E",
                  textTransform:"uppercase", letterSpacing:"0.08em", marginBottom: 4}}>
                  ⚠ Action required
                </div>
                <div style={{fontSize: 12, color: T.textPrimary, display:"flex", gap: 14, flexWrap:"wrap"}}>
                  {pendingCC.length > 0 && <span><strong>{pendingCC.length}</strong> capital call{pendingCC.length>1?"s":""} pending</span>}
                  {urgentTax.length > 0 && <span><strong>{urgentTax.length}</strong> upcoming tax event{urgentTax.length>1?"s":""}</span>}
                  {rebalNeeded > 0 && <span><strong>{rebalNeeded}</strong> asset class{rebalNeeded>1?"es":""} off target</span>}
                </div>
              </div>
            )}

            {/* TILE ROW — 2 tiles only (Currency + Liquidity) */}
            <div style={{display:"grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 10, marginBottom: 14}}>
              {/* Currency Exposure tile */}
              <div onClick={()=>setTab("currency")} style={{...tCard(), padding: "14px 18px", cursor:"pointer",
                transition:"transform 0.15s, box-shadow 0.15s"}}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.06)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 6}}>
                  <div style={{fontSize: 11, fontWeight: 700, color: T.textMuted,
                    textTransform:"uppercase", letterSpacing:"0.08em"}}>
                    💱 Currency Exposure
                  </div>
                  <span style={{fontSize: 14, color: T.textMuted}}>›</span>
                </div>
                <div style={{fontSize: 22, fontWeight: 800, fontFamily:"'Inter', system-ui, sans-serif",
                  color: T.textPrimary, marginBottom: 4}}>
                  {ccyCount} {ccyCount === 1 ? "currency" : "currencies"}
                </div>
                <div style={{fontSize: 11, color: T.textSecondary}}>
                  {dc} base · Largest: {ccyBreakdown.slice(0,3).map(c => `${c.ccy} ${c.pct.toFixed(0)}%`).join(" · ")}
                </div>
              </div>

              {/* Liquidity tile */}
              <div onClick={()=>setTab(canSeeRisk ? "risk" : "holdings")} style={{...tCard(), padding: "14px 18px", cursor:"pointer",
                transition:"transform 0.15s, box-shadow 0.15s"}}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.06)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 6}}>
                  <div style={{fontSize: 11, fontWeight: 700, color: T.textMuted,
                    textTransform:"uppercase", letterSpacing:"0.08em"}}>
                    💧 Liquidity & Cash
                  </div>
                  <span style={{fontSize: 14, color: T.textMuted}}>›</span>
                </div>
                <div style={{fontSize: 22, fontWeight: 800, fontFamily:"'Inter', system-ui, sans-serif",
                  color: liquidPct > 70 ? "#1D9E75" : liquidPct > 40 ? "#D97706" : "#A32D2D", marginBottom: 4}}>
                  {liquidPct.toFixed(0)}% liquid
                </div>
                <div style={{fontSize: 11, color: T.textSecondary}}>
                  Unlisted / lock-ins {illiquidPct.toFixed(0)}% of portfolio
                </div>
              </div>
            </div>

            {/* ASSET MIX + HEALTH (side by side, 60/40 split) */}
            <div style={{display:"grid",
              gridTemplateColumns: isMobile ? "1fr" : "3fr 2fr",
              gap: 10, marginBottom: 14}}>
              {/* Asset mix */}
              <div style={{...tCard(), padding: "14px 18px"}}>
                <div style={{fontSize: 12, fontWeight: 700, color: T.textPrimary, marginBottom: 10}}>
                  Asset mix
                </div>
                {(() => {
                  const palette = ["#7C3AED", "#4F46E5", "#3B82F6", "#0EA5E9"];
                  const top = classBreakdown.slice(0, 4);
                  const rest = classBreakdown.slice(4);
                  const otherPct = rest.reduce((s, c) => s + c.pct, 0);
                  const otherVal = rest.reduce((s, c) => s + c.val, 0);
                  const rows = top.map((c, i) => ({ ...c, color: palette[i], isOther: false }));
                  if (rest.length > 0 && otherPct > 0) {
                    rows.push({
                      cls: `Other (${rest.length} ${rest.length === 1 ? "class" : "classes"})`,
                      pct: otherPct,
                      val: otherVal,
                      color: "#94A3B8",
                      isOther: true,
                    });
                  }
                  return rows.map((c) => (
                    <div key={c.cls} onClick={()=>setTab("holdings")} style={{
                      display:"flex", alignItems:"center", gap: 8, marginBottom: 6,
                      cursor:"pointer", padding:"3px 0"}}>
                      <div style={{flex: 1, height: 8, background: T.borderSubtle, borderRadius: 4, overflow:"hidden"}}>
                        <div style={{width: `${Math.min(100, c.pct)}%`, height: "100%", background: c.color}}/>
                      </div>
                      <div style={{minWidth: 170, fontSize: 11, color: T.textPrimary,
                        display:"flex", justifyContent:"space-between"}}>
                        <span style={{color: c.isOther ? T.textSecondary : T.textPrimary}}>{c.cls}</span>
                        <span style={{color: T.textSecondary, fontWeight: 600}}>
                          {c.pct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Health snapshot */}
              <div style={{...tCard(), padding: "14px 18px"}}>
                <div style={{fontSize: 12, fontWeight: 700, color: T.textPrimary, marginBottom: 10}}>
                  Health snapshot
                </div>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap: 10}}>
                  {[
                    { label: "Winners", val: winners, c: "#1D9E75" },
                    { label: "Losers", val: losers, c: "#A32D2D" },
                    { label: "Top 5 weight", val: `${top5Pct.toFixed(0)}%`, c: top5Pct > 50 ? "#D97706" : T.textPrimary },
                    { label: "Risk", val: riskGrade.label, c: riskGrade.color },
                  ].map(k => (
                    <div key={k.label}>
                      <div style={{fontSize: 9, color: T.textMuted, fontWeight: 700,
                        textTransform:"uppercase", letterSpacing:"0.05em", marginBottom: 2}}>
                        {k.label}
                      </div>
                      <div style={{fontSize: 17, fontWeight: 700, color: k.c,
                        fontFamily:"'Inter', system-ui, sans-serif"}}>
                        {k.val}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ACTIVITY BAND — Top movers + Pending actions */}
            <div style={{display:"grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 10}}>
              {/* Top Movers */}
              <div style={{...tCard(), padding: "14px 18px"}}>
                <div style={{fontSize: 12, fontWeight: 700, color: T.textPrimary,
                  marginBottom: 10, display:"flex", alignItems:"center", gap: 6}}>
                  📊 Top Movers
                </div>
                {[...topGainers.slice(0,3), ...topLosers.slice(0,2)]
                  .filter(h => h.gainPct != null)
                  .sort((a,b) => Math.abs(b.gainPct) - Math.abs(a.gainPct))
                  .slice(0, 5)
                  .map(h => (
                    <div key={h.id} onClick={()=>setTab("holdings")} style={{
                      display:"flex", alignItems:"center", gap: 10, padding: "6px 0",
                      borderBottom: `1px solid ${T.borderSubtle}`, cursor:"pointer"}}>
                      <div style={{fontSize: 14, flexShrink: 0}}>{h.gainPct >= 0 ? "📈" : "📉"}</div>
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontSize: 12, fontWeight: 600, color: T.textPrimary,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                          {h.name}
                        </div>
                        <div style={{fontSize: 10, color: T.textMuted}}>
                          {fmt(convert(h.valueUSD, dc), dc, true)} · {h.class}
                        </div>
                      </div>
                      <div style={{fontSize: 13, fontWeight: 700, color: h.gainPct >= 0 ? "#1D9E75" : "#A32D2D",
                        fontFamily:"'Inter', system-ui, sans-serif", minWidth: 60, textAlign:"right"}}>
                        {h.gainPct >= 0 ? "+" : ""}{h.gainPct.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                {topGainers.length === 0 && topLosers.length === 0 && (
                  <div style={{fontSize: 11, color: T.textMuted, fontStyle:"italic", padding: "12px 0"}}>
                    Add cost prices to see gainers/losers
                  </div>
                )}
              </div>

              {/* Pending Actions */}
              <div style={{...tCard(), padding: "14px 18px"}}>
                <div style={{fontSize: 12, fontWeight: 700, color: T.textPrimary,
                  marginBottom: 10, display:"flex", alignItems:"center", gap: 6}}>
                  ⚠ Pending Actions
                </div>
                {[
                  ...pendingCC.slice(0,3).map(cc => ({
                    type: "Capital call",
                    label: `${cc.name || "Unnamed fund"}`,
                    sub: `${fmt(parseFloat(cc.callAmount || 0), cc.currency || dc, true)} · due ${cc.callDate ? new Date(cc.callDate).toLocaleDateString() : "TBD"}`,
                    icon: "📞", color: "#D97706", action: () => setTab("cashflow"),
                  })),
                  ...urgentTax.slice(0,2).map(t => ({
                    type: "Tax",
                    label: t.name || "Tax payment",
                    sub: `${fmt(parseFloat(t.amount || 0), t.currency || dc, true)} · due ${t.dueDate ? new Date(t.dueDate).toLocaleDateString() : "TBD"}`,
                    icon: "📋", color: "#7C3AED", action: () => setTab("cashflow"),
                  })),
                  ...rebalDrifts.slice(0,2).map(r => ({
                    type: "Rebalance",
                    label: `${r.assetClass} off target`,
                    sub: `Actual ${r.actual.toFixed(1)}% vs target ${r.target.toFixed(1)}%`,
                    icon: "⚖️", color: "#2563EB", action: () => setTab("model"),
                  })),
                ].slice(0, 5).map((item, i) => (
                  <div key={i} onClick={item.action} style={{
                    display:"flex", alignItems:"flex-start", gap: 10, padding: "8px 0",
                    borderBottom: `1px solid ${T.borderSubtle}`, cursor:"pointer"}}>
                    <div style={{fontSize: 14, flexShrink: 0}}>{item.icon}</div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontSize: 11, fontWeight: 700, color: item.color,
                        textTransform:"uppercase", letterSpacing:"0.04em"}}>
                        {item.type}
                      </div>
                      <div style={{fontSize: 12, fontWeight: 600, color: T.textPrimary,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                        {item.label}
                      </div>
                      <div style={{fontSize: 10, color: T.textMuted}}>
                        {item.sub}
                      </div>
                    </div>
                  </div>
                ))}
                {pendingCC.length === 0 && urgentTax.length === 0 && rebalDrifts.length === 0 && (
                  <div style={{fontSize: 11, color: T.textMuted, fontStyle:"italic", padding: "12px 0"}}>
                    ✓ Nothing requires attention right now
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>;

          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in summary tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══════════════════════════════════════════════════════════════
            🤖 AI PORTFOLIO ADVISOR TAB
            Conversational agent with full portfolio context injected.
            Answers questions, runs what-if, explains risks, compares
            performance, projects goals — all against real client data.
        ══════════════════════════════════════════════════════════════ */}
        {tab==="agent" && (function(){ try {

          // ══════════════════════════════════════════════════════════════
          // AI PORTFOLIO ADVISOR — Full context-aware agent
          // ══════════════════════════════════════════════════════════════
          // Apply the AI Advisor's "chat" filter (or its global filter) so the chat
          // sees only what the user has scoped to. All downstream aggregations
          // (byClass, byGeo, topHoldings, totals) inherit naturally from this.
          const chatFilter = getEffectiveAiFilter("chat");
          const hw = applyAiFilter(filteredHW, chatFilter);
          const totalV    = hw.reduce((s,h)=>s+h.valueUSD,0)||1;
          const totalCost = hw.reduce((s,h)=>s+h.costUSD,0)||1;
          const totalGain = totalV - totalCost;
          const gainPct   = totalCost>0 ? (totalGain/totalCost*100) : 0;

          // ── Active client for advisor — scoped per client ──
          const advisorClientId = loggedInClientId
            ? loggedInClientId
            : isAdmin && selectedClients.size === 1
              ? [...selectedClients][0]
              : isAdmin && selectedClients.size === 0 && selectedFamily !== "all"
                ? null  // whole family selected — use "family_" prefix
                : null;
          const advisorKey = advisorClientId || (selectedFamily !== "all" ? `family_${selectedFamily}` : "__all__");
          const agentMessages = agentMessagesMap[advisorKey] || [];
          const setAgentMessages = (updater) => {
            setAgentMessagesMap(prev => {
              const cur = prev[advisorKey] || [];
              const next = typeof updater === "function" ? updater(cur) : updater;
              return { ...prev, [advisorKey]: next };
            });
          };

          const clientName = loggedInClientId
            ? (visibleClients.find(c=>c.id===loggedInClientId)?.name||"Client")
            : isAdmin ? (selectedClients.size>0
                ? visibleClients.find(c=>String(c.id)===[...selectedClients][0])?.name||"Client"
                : selectedFamily !== "all"
                  ? (visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family") + " (Family)"
                  : "All Clients")
            : "Client";
          const firstName = clientName.split(" ")[0];

          // ── Full portfolio context ──
          const byClass = {};
          const bySector = {};
          const byGeo = {};
          hw.forEach(h=>{
            byClass[h.class]=(byClass[h.class]||0)+h.valueUSD;
            bySector[h.sector||"Other"]=(bySector[h.sector||"Other"]||0)+h.valueUSD;
            byGeo[h.country||"Other"]=(byGeo[h.country||"Other"]||0)+h.valueUSD;
          });

          const topHoldings = [...hw].sort((a,b)=>b.valueUSD-a.valueUSD).slice(0,10);
          const topConc   = topHoldings[0] ? topHoldings[0].valueUSD/totalV*100 : 0;
          const winners   = hw.filter(h=>h.gainPct>0).length;
          const losers    = hw.filter(h=>h.gainPct<0).length;
          const cryptoPct = ((byClass["Crypto"]||0)+(byClass["Digital Assets"]||0))/totalV*100;
          const equityPct = Object.entries(byClass).filter(([k])=>k.toLowerCase().includes("equit")).reduce((s,[,v])=>s+v,0)/totalV*100;
          const fixedPct  = ((byClass["Fixed Income"]||0)+(byClass["Time Deposits"]||0))/totalV*100;
          const altPct    = ((byClass["Alternates"]||0)+(byClass["Art & Collectibles"]||0)+(byClass["Private Equity"]||0))/totalV*100;

          // Goals
          const myGoals = allGoals.filter(g=>!loggedInClientId||g.clientId===loggedInClientId);
          const goalStr = myGoals.length>0
            ? myGoals.map(g=>`"${g.name}" — target ${fmt(parseFloat(g.targetAmount||0),g.currency||dc)} by ${g.targetDate||"no date"}, currently ${g.linkedHoldings?.length>0?"linked to holdings":"unfunded"}`).join("; ")
            : "No goals set";

          // Benchmark
          const bmData  = benchMetrics?.[bench]||BENCHMARK_FALLBACK[bench];
          const bmLabel = BENCHMARKS_DEF.find(b=>b.id===bench)?.label||"S&P 500";
          const bmOneY  = bmData?.oneY;
          const alpha   = bmOneY!=null ? gainPct-bmOneY : null;

          // Pending actions
          const pendingCalls = capitalCalls.filter(cc=>cc.status!=="funded");
          const urgentTax    = taxEvents.filter(t=>t.status!=="paid"&&new Date(t.dueDate||0)>=new Date()&&(new Date(t.dueDate)-Date.now())<30*86400000);
          const recurringMonthly = recurringInflows.filter(r=>r.active!==false).reduce((s,r)=>{
            const a=parseFloat(r.amount||0);
            return s+(r.frequency==="monthly"?a:r.frequency==="quarterly"?a/3:a/12);
          },0);

          // Recent transactions (last 10, scoped)
          const scopedNames = new Set(hw.map(h=>h.name));
          const recentTxns = [...transactions]
            .filter(t=>scopedNames.has(t.holding))
            .sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,10);

          // ── Build rich system prompt ──────────────────────────────────
          const holdingLines = topHoldings.map(h=>
            `  • ${h.name} (${h.class}, ${h.country||"Global"}): ${fmt(convert(h.valueUSD,dc),dc)} = ${(h.valueUSD/totalV*100).toFixed(1)}% | P&L: ${h.gainPct!=null?(h.gainPct>=0?"+":"")+h.gainPct.toFixed(1)+"%":"no cost data"} | Ticker: ${h.ticker||"—"}`
          ).join("\n");

          const allocationLines = Object.entries(byClass).sort((a,b)=>b[1]-a[1])
            .map(([cls,v])=>`  ${cls}: ${(v/totalV*100).toFixed(1)}% (${fmt(convert(v,dc),dc,true)})`).join("\n");

          const geoLines = Object.entries(byGeo).sort((a,b)=>b[1]-a[1]).slice(0,6)
            .map(([geo,v])=>`  ${geo}: ${(v/totalV*100).toFixed(1)}%`).join("\n");

          const txnLines = recentTxns.slice(0,5)
            .map(t=>`  ${t.date}: ${t.type} ${t.qty} ${t.holding} @ ${fmt(parseFloat(t.price||0),t.currency||dc)}`).join("\n");

          // Build a filter notice that primes the AI to disclose the scope and not
          // overgeneralize from a filtered slice. Empty filter → reassures AI it has full view.
          const chatFilterLabel = describeAiFilter(chatFilter);
          const filterNoticeBlock = chatFilterLabel === "All holdings"
            ? `\nDATA SCOPE: Full client portfolio. You are seeing ALL holdings for ${clientName}.\n`
            : `\n⚠ DATA SCOPE — IMPORTANT: This is a FILTERED view of the portfolio, scoped to "${chatFilterLabel}". All numbers below (AUM, allocations, top holdings, P&L) reflect ONLY this filtered slice — NOT the full portfolio. When answering, you MUST:
  • Clearly state at the start of your answer that you're working from "${chatFilterLabel}" only
  • Refer to AUM/totals as "the filtered subset" not "the portfolio"
  • Never make recommendations that assume you've seen everything (e.g. don't say "diversify by adding bonds" if the user filtered to equities — you can't see if they already have bonds)
  • If the user asks something requiring the full portfolio context (rebalancing, tax-loss harvesting, concentration risk), respond by saying the answer needs the filter cleared, then offer what you CAN say from the filtered view
`;

          const systemPrompt = `You are a highly capable AI wealth advisor for ${clientName} on the Forthius platform. You have access to their portfolio data as described below. Be precise, cite actual numbers, and give actionable insight. Never be generic. Always reference their real holdings, allocations, and performance.
${filterNoticeBlock}
PORTFOLIO OVERVIEW:
  Total AUM: ${fmt(convert(totalV,dc),dc)}
  Cost Basis: ${fmt(convert(totalCost,dc),dc)}
  Total P&L: ${fmt(convert(totalGain,dc),dc)} (${(gainPct??0)>=0?"+":""}${(gainPct??0).toFixed(2)}%)
  Holdings: ${hw.length} positions | ${winners} winners, ${losers} losers
  Monthly Inflows: ${fmt(convert(recurringMonthly,dc),dc,true)}/mo

ASSET ALLOCATION:
${allocationLines}

GEOGRAPHIC EXPOSURE:
${geoLines}

TOP HOLDINGS (by value):
${holdingLines}

BENCHMARK: ${bmLabel}
  Benchmark 1Y return: ${bmOneY!=null?(bmOneY>=0?"+":"")+bmOneY.toFixed(1)+"%":"not loaded"}
  Portfolio overall P&L: ${(gainPct??0)>=0?"+":""}${(gainPct??0).toFixed(1)}%
  Alpha vs benchmark: ${alpha!=null?(alpha>=0?"+":"")+alpha.toFixed(1)+"%":"N/A"}

GOALS:
  ${goalStr}

PENDING ACTIONS:
  Capital calls: ${pendingCalls.length>0?pendingCalls.map(cc=>`${cc.name} — ${fmt(parseFloat(cc.callAmount||0),cc.currency||"USD")} due ${cc.callDate||"TBD"}`).join("; "):"None"}
  Urgent tax (30 days): ${urgentTax.length>0?urgentTax.map(t=>`${t.name} — ${fmt(parseFloat(t.amount||0),t.currency||"INR")} due ${t.dueDate}`).join("; "):"None"}

RECENT TRANSACTIONS:
${txnLines||"  None recorded"}

RISK SNAPSHOT:
  Top concentration: ${topHoldings[0]?.name||"N/A"} at ${topConc.toFixed(1)}%${topConc>25?" ⚠ HIGH CONCENTRATION":""}
  Equity: ${equityPct.toFixed(1)}% | Fixed Income: ${fixedPct.toFixed(1)}% | Alternatives: ${altPct.toFixed(1)}% | Crypto: ${cryptoPct.toFixed(1)}%${cryptoPct>15?" ⚠ ELEVATED":""}

TAX CONTEXT (for what-if calculations):
  India jurisdiction: LTCG 12.5% on gains >₹1L after 1 year; STCG 20% under 1 year
  US jurisdiction: LTCG 15–20% after 1 year; STCG at ordinary income rates
  Use holding gainPct and approximate time held to estimate tax impact

RESPONSE GUIDELINES:
  - Always use actual numbers from the portfolio above
  - For "what if X drops Y%": calculate exact dollar impact on that holding + total portfolio impact
  - For tax questions: estimate based on actual unrealised gains and jurisdiction rules above
  - For rebalancing: compare current allocation to standard 60/40 or client goals
  - For risk questions: cite actual concentration, sector, geo numbers
  - Keep responses under 250 words unless user explicitly asks for detail
  - Format monetary values in ${dc}
  - End complex answers with 1–2 specific action items the client can take
  - Never reveal raw data structures or say "according to my data"`;

          // ── Send message ──────────────────────────────────────────────
          const sendMessage = async (input) => {
            const userMsg = (input||agentInput).trim();
            if (!userMsg || agentLoading) return;
            const newMessages = [...agentMessages, { role:"user", content:userMsg, ts:Date.now() }];
            setAgentMessages(newMessages);
            setAgentInput("");
            setAgentLoading(true);
            try {
              // True multi-turn: pass full messages array to Anthropic API
              const apiMessages = newMessages.map(m => ({
                role: m.role === "user" ? "user" : "assistant",
                content: m.content,
              }));
              const resp = await fetch("/api/claude", {
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body:JSON.stringify({
                  messages: apiMessages,
                  system: systemPrompt,
                  max_tokens: 800,
                  provider: (aiConfig||{}).advisorProvider || "anthropic",
                  model: (aiConfig||{}).advisorModel || "claude-haiku-4-5-20251001",
                  apiKey: (aiConfig||{}).advisorProvider === "openai" ? (aiConfig||{}).openaiKey : (aiConfig||{}).advisorProvider === "gemini" ? (aiConfig||{}).geminiKey : undefined,
                })
              });
              const data = await resp.json();
              const text = data?.text||data?.content?.[0]?.text||data?.error||"I couldn't generate a response. Please try again.";
              setAgentMessages(p=>[...p, { role:"assistant", content:text, ts:Date.now() }]);
              // ── Auto-speak response if voice mode is active ──
              // Use ref (not state) so async callback always reads current value
              if (voiceModeRef.current && typeof window !== "undefined" && window.speechSynthesis) {
                window.speechSynthesis.cancel();
                const speakNow = () => {
                  const utt = new SpeechSynthesisUtterance(
                    text.replace(/[*#`_~]/g,"").replace(/\n+/g,". ").slice(0, 800)
                  );
                  utt.rate = 1.0; utt.pitch = 1; utt.volume = 1; utt.lang = "en-IN";
                  // Voices may not be loaded yet — pick best available
                  const voices = window.speechSynthesis.getVoices();
                  const preferred = voices.find(v =>
                    v.name.includes("Samantha") || v.name.includes("Google UK") ||
                    v.name.includes("Karen") || v.name.includes("Moira") ||
                    (v.lang && v.lang.startsWith("en") && v.localService)
                  ) || voices.find(v => v.lang && v.lang.startsWith("en")) || voices[0];
                  if (preferred) utt.voice = preferred;
                  utt.onstart = () => setVoiceSpeaking(true);
                  utt.onend   = () => setVoiceSpeaking(false);
                  utt.onerror = (e) => { console.warn("[TTS]", e.error); setVoiceSpeaking(false); };
                  voiceSynthRef.current = utt;
                  setVoiceSpeaking(true);
                  window.speechSynthesis.speak(utt);
                };
                // Chrome needs voices to load first
                if (window.speechSynthesis.getVoices().length > 0) {
                  speakNow();
                } else {
                  window.speechSynthesis.onvoiceschanged = () => { speakNow(); };
                }
              }
            } catch(e) {
              setAgentMessages(p=>[...p, { role:"assistant", content:"Connection error: "+e.message, ts:Date.now() }]);
            } finally {
              setAgentLoading(false);
              setTimeout(()=>agentEndRef.current?.scrollIntoView({behavior:"smooth"}),100);
            }
          };

          // ── Voice control helpers ──────────────────────────────────────
          const startVoice = () => {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) { setVoiceSupported(false); return; }
            window.speechSynthesis?.cancel();
            setVoiceSpeaking(false);
            voiceModeRef.current = true; // mark voice mode on for async callbacks
            setVoiceMode(true);
            const recog = new SpeechRecognition();
            recog.lang = "en-IN";
            recog.continuous = false;
            recog.interimResults = true;
            recog.maxAlternatives = 1;
            let finalTranscript = "";
            let silenceTimer = null;
            recog.onresult = (ev) => {
              let interim = "";
              for (let i = ev.resultIndex; i < ev.results.length; i++) {
                const t = ev.results[i][0].transcript;
                if (ev.results[i].isFinal) finalTranscript += t;
                else interim = t;
              }
              setAgentInput((finalTranscript + interim).trim());
              // Auto-send after 1.5s silence
              clearTimeout(silenceTimer);
              silenceTimer = setTimeout(() => {
                const msg = (finalTranscript || interim).trim();
                if (msg) { recog.stop(); sendMessage(msg); setAgentInput(""); }
              }, 1500);
            };
            recog.onend  = () => setVoiceActive(false);
            recog.onerror = () => { setVoiceActive(false); };
            voiceRecogRef.current = recog;
            recog.start();
            setVoiceActive(true);
          };
          const stopVoice = () => {
            voiceModeRef.current = false; // turn off voice mode
            setVoiceMode(false);
            voiceRecogRef.current?.stop();
            window.speechSynthesis?.cancel();
            setVoiceActive(false);
            setVoiceSpeaking(false);
          };

          // ── Check for pending voice message from floating button ──
          // Safe: synchronous check, no hook needed
          if (window.__forthiusPendingVoice) {
            const pendingMsg = window.__forthiusPendingVoice;
            window.__forthiusPendingVoice = null;
            // Use setTimeout so sendMessage runs after this render cycle
            setTimeout(() => sendMessage(pendingMsg), 50);
          }

          // ── Contextual suggestion chips ───────────────────────────────
          const suggestions = [
            `What's my biggest risk right now?`,
            `How am I performing vs ${bmLabel}?`,
            topHoldings[0] ? `What if ${topHoldings[0].name.split(" ")[0]} drops 20%?` : `What if markets drop 20%?`,
            `Am I too concentrated anywhere?`,
            `What's my tax bill if I sell everything today?`,
            `Explain my asset allocation in plain English`,
            myGoals[0] ? `Am I on track for my "${myGoals[0].name}" goal?` : `Should I rebalance?`,
            `What's dragging down my returns?`,
          ].slice(0,6);

          // ── Proactive insights (pre-computed flags) ───────────────────
          const proactiveInsights = [
            topConc>30 && { type:"warn", icon:"⚠️", text:`${topHoldings[0]?.name?.split(" ")[0]} is ${topConc.toFixed(0)}% of your portfolio — consider diversifying` },
            cryptoPct>20 && { type:"warn", icon:"⚡", text:`Crypto is ${cryptoPct.toFixed(0)}% of portfolio — high volatility exposure` },
            urgentTax.length>0 && { type:"action", icon:"🧾", text:`${urgentTax.length} tax payment${urgentTax.length>1?"s":""} due within 30 days` },
            pendingCalls.length>0 && { type:"action", icon:"📞", text:`${pendingCalls.length} capital call${pendingCalls.length>1?"s":""} awaiting funding` },
            alpha!=null&&alpha<-5 && { type:"info", icon:"📉", text:`Portfolio is trailing ${bmLabel} by ${Math.abs(alpha).toFixed(1)}% — review allocation` },
            alpha!=null&&alpha>5 && { type:"good", icon:"✅", text:`Outperforming ${bmLabel} by ${alpha.toFixed(1)}% — strong alpha` },
            myGoals.length===0 && { type:"info", icon:"🎯", text:`No goals set — ask me to help define investment goals` },
            losers>winners && { type:"warn", icon:"📊", text:`${losers} of ${hw.length} holdings are underwater` },
          ].filter(Boolean).slice(0,3);

          const insightColors = {
            warn: { bg:"rgba(220,38,38,0.06)", border:"rgba(220,38,38,0.2)", text:"#dc2626" },
            action: { bg:"rgba(217,119,6,0.06)", border:"rgba(217,119,6,0.2)", text:"#d97706" },
            info: { bg:"rgba(37,99,235,0.06)", border:"rgba(37,99,235,0.15)", text:"#2563eb" },
            good: { bg:"rgba(22,163,74,0.06)", border:"rgba(22,163,74,0.2)", text:"#16a34a" },
          };

          return <React.Fragment>

          {/* ── AI FILTER BAR ── */}
          {/* Scopes which holdings the AI tools below see. See applyAiFilter() helper. */}
          {renderAiFilterBar(filteredHW)}

          {/* ── AI TOOLS PANEL ── */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,minmax(0,1fr))",gap:10,marginBottom:14}}>

            {/* ── Anomaly Scan ── */}
            <div onClick={()=>aiAnomalies&&setExpandedAICard(expandedAICard==="anomaly"?null:"anomaly")}
              style={{...card(),padding:"14px 16px",display:"flex",flexDirection:"column",gap:0,
                cursor:aiAnomalies?"pointer":"default",
                borderColor:expandedAICard==="anomaly"?"#2563EB":undefined,
                borderWidth:expandedAICard==="anomaly"?2:undefined,
                background:expandedAICard==="anomaly"?"rgba(37,99,235,0.04)":undefined,
                transition:"border-color 0.15s, background 0.15s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:15}}>🔍</span>
                  <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>Anomaly Scan</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {aiAnomalies && (
                    <span style={{fontSize:11,fontWeight:700,
                      color:aiAnomalies.healthScore>=80?"#10B981":aiAnomalies.healthScore>=60?"#F59E0B":"#F43F5E",
                      background:aiAnomalies.healthScore>=80?"rgba(16,185,129,0.1)":aiAnomalies.healthScore>=60?"rgba(245,158,11,0.1)":"rgba(244,63,94,0.1)",
                      padding:"2px 8px",borderRadius:8}}>
                      Health {aiAnomalies.healthScore}/100
                    </span>
                  )}
                  {aiAnomalies && <span style={{fontSize:10,color:T.textMuted,transition:"transform 0.2s",
                    transform:expandedAICard==="anomaly"?"rotate(180deg)":"none"}}>▾</span>}
                </div>
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:10,minHeight:16}}>
                {aiErrors.anomaly
                  ? aiErrors.anomaly.startsWith("🔒")
                    ? <span style={{color:"#7C3AED",fontWeight:600}}>{aiErrors.anomaly} <span onClick={(e)=>{e.stopPropagation();setUpgradeModal({tabId:"agent",tabLabel:"AI Tools",tabIcon:"🤖"});}} style={{cursor:"pointer",textDecoration:"underline"}}>Upgrade →</span></span>
                    : <span style={{color:"#F43F5E"}}>⚠ {aiErrors.anomaly}</span>
                  : aiAnomalies
                    ? <span style={{fontWeight:600,color:T.textPrimary}}>{aiAnomalies.totalIssues||aiAnomalies.alerts?.length||0} issue{(aiAnomalies.totalIssues||aiAnomalies.alerts?.length||0)!==1?"s":""} found{aiAnomalies.alerts?.length>0?" · click to view":""}</span>
                    : "Detect stale prices, missing costs, concentration risks"}
              </div>
              <button onClick={(e)=>{e.stopPropagation();setAiErrors(p=>({...p,anomaly:null}));runAiAnomaly();}} disabled={aiAnomalyLoad}
                style={{marginTop:"auto",width:"100%",background:T.brand+"14",border:`1px solid ${T.brand}44`,
                  borderRadius:6,color:T.brand,padding:"6px 0",cursor:"pointer",fontSize:11,fontWeight:700,
                  opacity:aiAnomalyLoad?0.6:1}}>
                {aiAnomalyLoad?"⟳ Scanning…":aiAnomalies?"↺ Re-scan":"Run Scan"}
              </button>
            </div>

            {/* ── Tax-Loss Harvest ── */}
            <div onClick={()=>aiTaxloss&&setExpandedAICard(expandedAICard==="taxloss"?null:"taxloss")}
              style={{...card(),padding:"14px 16px",display:"flex",flexDirection:"column",gap:0,
                cursor:aiTaxloss?"pointer":"default",
                borderColor:expandedAICard==="taxloss"?"#10B981":undefined,
                borderWidth:expandedAICard==="taxloss"?2:undefined,
                background:expandedAICard==="taxloss"?"rgba(16,185,129,0.04)":undefined,
                transition:"border-color 0.15s, background 0.15s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:15}}>🍂</span>
                  <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>Tax-Loss Harvest</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {aiTaxloss && aiTaxloss.estimatedTaxSavingUSD > 0 && (
                    <span style={{fontSize:11,fontWeight:700,color:"#10B981",
                      background:"rgba(16,185,129,0.1)",padding:"2px 8px",borderRadius:8}}>
                      Save {fmt(convert(aiTaxloss.estimatedTaxSavingUSD||0,dc),dc,true)}
                    </span>
                  )}
                  {aiTaxloss && <span style={{fontSize:10,color:T.textMuted,transition:"transform 0.2s",
                    transform:expandedAICard==="taxloss"?"rotate(180deg)":"none"}}>▾</span>}
                </div>
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:10,minHeight:16}}>
                {aiErrors.taxloss
                  ? aiErrors.taxloss.startsWith("🔒")
                    ? <span style={{color:"#7C3AED",fontWeight:600}}>{aiErrors.taxloss} <span onClick={(e)=>{e.stopPropagation();setUpgradeModal({tabId:"agent",tabLabel:"AI Tools",tabIcon:"🤖"});}} style={{cursor:"pointer",textDecoration:"underline"}}>Upgrade →</span></span>
                    : <span style={{color:"#F43F5E"}}>⚠ {aiErrors.taxloss}</span>
                  : aiTaxloss
                    ? <span style={{fontWeight:600,color:T.textPrimary}}>{aiTaxloss._parseError ? aiTaxloss._raw?.slice(0,80)+"…" : `${aiTaxloss.opportunities?.length||0} opportunit${aiTaxloss.opportunities?.length===1?"y":"ies"}${aiTaxloss.opportunities?.length?" · click to view":""}`}</span>
                    : "Find positions worth selling to offset gains before year-end"}
              </div>
              <button onClick={(e)=>{e.stopPropagation();setAiErrors(p=>({...p,taxloss:null}));runAiTaxloss();}} disabled={aiTaxlossLoad}
                style={{marginTop:"auto",width:"100%",background:"rgba(16,185,129,0.08)",
                  border:"1px solid rgba(16,185,129,0.3)",borderRadius:6,color:"#10B981",
                  padding:"6px 0",cursor:"pointer",fontSize:11,fontWeight:700,opacity:aiTaxlossLoad?0.6:1}}>
                {aiTaxlossLoad?"⟳ Scanning…":aiTaxloss?"↺ Re-scan":"Scan Portfolio"}
              </button>
            </div>

            {/* ── Rebalancing ── */}
            <div onClick={()=>aiRebalance&&setExpandedAICard(expandedAICard==="rebalance"?null:"rebalance")}
              style={{...card(),padding:"14px 16px",display:"flex",flexDirection:"column",gap:0,
                cursor:aiRebalance?"pointer":"default",
                borderColor:expandedAICard==="rebalance"?"#2563EB":undefined,
                borderWidth:expandedAICard==="rebalance"?2:undefined,
                background:expandedAICard==="rebalance"?"rgba(37,99,235,0.04)":undefined,
                transition:"border-color 0.15s, background 0.15s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:15}}>⚖️</span>
                  <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>Rebalancing</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {aiRebalance && (
                    <span style={{fontSize:11,fontWeight:700,
                      color:aiRebalance.priority==="high"?"#F43F5E":aiRebalance.priority==="medium"?"#F59E0B":"#10B981",
                      background:"rgba(37,99,235,0.08)",padding:"2px 8px",borderRadius:8}}>
                      {aiRebalance.priority} priority
                    </span>
                  )}
                  {aiRebalance && <span style={{fontSize:10,color:T.textMuted,transition:"transform 0.2s",
                    transform:expandedAICard==="rebalance"?"rotate(180deg)":"none"}}>▾</span>}
                </div>
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:10,minHeight:16}}>
                {aiErrors.rebalance
                  ? aiErrors.rebalance.startsWith("🔒")
                    ? <span style={{color:"#7C3AED",fontWeight:600}}>{aiErrors.rebalance} <span onClick={(e)=>{e.stopPropagation();setUpgradeModal({tabId:"agent",tabLabel:"AI Tools",tabIcon:"🤖"});}} style={{cursor:"pointer",textDecoration:"underline"}}>Upgrade →</span></span>
                    : <span style={{color:"#F43F5E"}}>⚠ {aiErrors.rebalance}</span>
                  : aiRebalance
                    ? <span style={{fontWeight:600,color:T.textPrimary}}>{aiRebalance.actions?.length||0} action{aiRebalance.actions?.length!==1?"s":""}{aiRebalance.drift?` · ${aiRebalance.drift}`:""} · click to view</span>
                    : "Get specific buy/sell recommendations to reach target allocation"}
              </div>
              <button onClick={(e)=>{e.stopPropagation();setAiErrors(p=>({...p,rebalance:null}));runAiRebalance();}} disabled={aiRebalanceLoad}
                style={{marginTop:"auto",width:"100%",background:"rgba(37,99,235,0.08)",
                  border:"1px solid rgba(37,99,235,0.25)",borderRadius:6,color:"#2563EB",
                  padding:"6px 0",cursor:"pointer",fontSize:11,fontWeight:700,opacity:aiRebalanceLoad?0.6:1}}>
                {aiRebalanceLoad?"⟳ Analysing…":aiRebalance?"↺ Refresh":"Analyse Portfolio"}
              </button>
            </div>

            {/* ── Meeting Briefing ── */}
            <div onClick={()=>aiBriefing&&setExpandedAICard(expandedAICard==="briefing"?null:"briefing")}
              style={{...card(),padding:"14px 16px",display:"flex",flexDirection:"column",gap:0,
                cursor:aiBriefing?"pointer":"default",
                borderColor:expandedAICard==="briefing"?"#7C3AED":undefined,
                borderWidth:expandedAICard==="briefing"?2:undefined,
                background:expandedAICard==="briefing"?"rgba(124,58,237,0.04)":undefined,
                transition:"border-color 0.15s, background 0.15s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:15}}>📋</span>
                  <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>Meeting Briefing</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {aiBriefing && (
                    <span style={{fontSize:11,fontWeight:700,textTransform:"capitalize",
                      color:aiBriefing.portfolioHealth==="good"?"#10B981":aiBriefing.portfolioHealth==="fair"?"#F59E0B":"#F43F5E",
                      background:"rgba(124,58,237,0.08)",padding:"2px 8px",borderRadius:8}}>
                      {aiBriefing.portfolioHealth?.replace("_"," ")}
                    </span>
                  )}
                  {aiBriefing && <span style={{fontSize:10,color:T.textMuted,transition:"transform 0.2s",
                    transform:expandedAICard==="briefing"?"rotate(180deg)":"none"}}>▾</span>}
                </div>
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:10,minHeight:16}}>
                {aiErrors.briefing
                  ? aiErrors.briefing.startsWith("🔒")
                    ? <span style={{color:"#7C3AED",fontWeight:600}}>{aiErrors.briefing} <span onClick={(e)=>{e.stopPropagation();setUpgradeModal({tabId:"agent",tabLabel:"AI Tools",tabIcon:"🤖"});}} style={{cursor:"pointer",textDecoration:"underline"}}>Upgrade →</span></span>
                    : <span style={{color:"#F43F5E"}}>⚠ {aiErrors.briefing}</span>
                  : aiBriefing
                    ? <span style={{fontWeight:600,color:T.textPrimary,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{aiBriefing._parseError ? aiBriefing._raw?.slice(0,120)+"…" : aiBriefing.headline}</span>
                    : "Generate a pre-meeting brief with talking points and action items"}
              </div>
              <div style={{display:"flex",gap:6,marginTop:"auto"}}>
                <button onClick={(e)=>{e.stopPropagation();setAiErrors(p=>({...p,briefing:null}));runAiBriefing();}} disabled={aiBriefingLoad}
                  style={{flex:1,background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.25)",
                    borderRadius:6,color:"#7C3AED",padding:"6px 0",cursor:"pointer",
                    fontSize:11,fontWeight:700,opacity:aiBriefingLoad?0.6:1}}>
                  {aiBriefingLoad?"⟳ Generating…":aiBriefing?"↺ Refresh":"Generate"}
                </button>
                {aiBriefing && (
                  <button onClick={(e)=>{
                    e.stopPropagation();
                    const lines = [`FORTHIUS — CLIENT BRIEFING`,new Date().toLocaleDateString(),"",aiBriefing.headline,"",
                      ...(aiBriefing.sections||[]).flatMap(s=>[s.title.toUpperCase(),s.content,""]),
                      "ACTION ITEMS:",
                      ...(aiBriefing.actionItems||[]).map(a=>`[${a.priority?.toUpperCase()}] ${a.item}`)
                    ];
                    const blob=new Blob([lines.join("\n")],{type:"text/plain"});
                    const a=document.createElement("a"); a.href=URL.createObjectURL(blob);
                    a.download=`Forthius_Briefing_${new Date().toISOString().slice(0,10)}.txt`; a.click();
                  }}
                  title="Download briefing as text file"
                  style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,
                    color:T.textMuted,padding:"6px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                    ↓
                  </button>
                )}
              </div>
            </div>

          </div>

          {/* ── Unified AI detail drawer ────────────────────────────────────
              Option C design: click any card header above to expand its
              detailed output here. One card at a time. Auto-expands when a
              scan completes so the result is immediately visible.
          */}
          {expandedAICard && (() => {
            // Drawer meta per card type
            const meta = {
              anomaly:   { icon:"🔍", label:"Anomaly Scan",     accent:"#2563EB", data:aiAnomalies,  err:aiErrors.anomaly  },
              taxloss:   { icon:"🍂", label:"Tax-Loss Harvest", accent:"#10B981", data:aiTaxloss,    err:aiErrors.taxloss  },
              rebalance: { icon:"⚖️", label:"Rebalancing",      accent:"#2563EB", data:aiRebalance,  err:aiErrors.rebalance},
              briefing:  { icon:"📋", label:"Meeting Briefing", accent:"#7C3AED", data:aiBriefing,   err:aiErrors.briefing },
            }[expandedAICard];
            if (!meta || (!meta.data && !meta.err)) return null;

            return (
              <div style={{...card(),marginBottom:14,padding:"14px 18px",borderLeft:`3px solid ${meta.accent}`}}>
                {/* Drawer header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,
                  paddingBottom:10,borderBottom:`1px solid ${T.borderSubtle}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>{meta.icon}</span>
                    <span style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>{meta.label} — detail</span>
                    {/* Priority / health badge next to title */}
                    {expandedAICard==="anomaly" && aiAnomalies && (
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
                        color:aiAnomalies.healthScore>=80?"#10B981":aiAnomalies.healthScore>=60?"#F59E0B":"#F43F5E",
                        background:aiAnomalies.healthScore>=80?"rgba(16,185,129,0.1)":aiAnomalies.healthScore>=60?"rgba(245,158,11,0.1)":"rgba(244,63,94,0.1)"}}>
                        Health {aiAnomalies.healthScore}/100
                      </span>
                    )}
                    {expandedAICard==="taxloss" && aiTaxloss?.estimatedTaxSavingUSD > 0 && (
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
                        color:"#10B981",background:"rgba(16,185,129,0.1)"}}>
                        Est. saving {fmt(convert(aiTaxloss.estimatedTaxSavingUSD||0,dc),dc,true)}
                      </span>
                    )}
                    {expandedAICard==="rebalance" && aiRebalance?.priority && (
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,textTransform:"uppercase",letterSpacing:"0.04em",
                        color:aiRebalance.priority==="high"?"#F43F5E":aiRebalance.priority==="medium"?"#F59E0B":"#10B981",
                        background:aiRebalance.priority==="high"?"rgba(244,63,94,0.1)":aiRebalance.priority==="medium"?"rgba(245,158,11,0.1)":"rgba(16,185,129,0.1)"}}>
                        {aiRebalance.priority} priority{aiRebalance.drift?` · ${aiRebalance.drift}`:""}
                      </span>
                    )}
                    {expandedAICard==="briefing" && aiBriefing?.portfolioHealth && (
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,textTransform:"capitalize",
                        color:aiBriefing.portfolioHealth==="good"?"#10B981":aiBriefing.portfolioHealth==="fair"?"#F59E0B":"#F43F5E",
                        background:aiBriefing.portfolioHealth==="good"?"rgba(16,185,129,0.1)":aiBriefing.portfolioHealth==="fair"?"rgba(245,158,11,0.1)":"rgba(244,63,94,0.1)"}}>
                        {aiBriefing.portfolioHealth.replace("_"," ")}
                      </span>
                    )}
                  </div>
                  <button onClick={()=>setExpandedAICard(null)}
                    style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontSize:12,fontWeight:600}}>
                    Collapse ↑
                  </button>
                </div>

                {/* ── ANOMALY DETAIL ── */}
                {expandedAICard==="anomaly" && aiAnomalies && (
                  <div>
                    <div style={{fontSize:12,color:T.textSecondary,marginBottom:12}}>
                      <span style={{fontWeight:600,color:T.textPrimary}}>{aiAnomalies.totalIssues || aiAnomalies.alerts?.length || 0}</span>
                      {" "}issue{(aiAnomalies.totalIssues||aiAnomalies.alerts?.length||0)!==1?"s":""} flagged across {kpiHW.length} holdings
                    </div>
                    {aiAnomalies.alerts?.length > 0 ? (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {aiAnomalies.alerts.map((a,i)=>{
                          const col = a.severity==="high"?"#F43F5E":a.severity==="medium"?"#F59E0B":"#6B7280";
                          return (
                            <div key={i} style={{padding:"10px 12px",borderRadius:7,
                              background:a.severity==="high"?"rgba(244,63,94,0.05)":a.severity==="medium"?"rgba(245,158,11,0.04)":T.borderSubtle,
                              border:`1px solid ${col}33`}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                                <span style={{width:8,height:8,borderRadius:"50%",background:col,display:"inline-block"}}/>
                                <span style={{fontSize:10,fontWeight:700,color:col,textTransform:"uppercase",letterSpacing:"0.06em"}}>{a.severity}</span>
                                <span style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.04em"}}>· {a.type?.replace(/_/g," ")}</span>
                                {a.holding && <span style={{fontSize:12,color:T.textPrimary,fontWeight:600,marginLeft:4}}>· {a.holding}</span>}
                              </div>
                              <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.55,marginBottom:a.action?5:0}}>{a.message}</div>
                              {a.action && <div style={{fontSize:11,color:T.brand,lineHeight:1.45,paddingLeft:8,borderLeft:`2px solid ${T.brand}44`}}>→ {a.action}</div>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{padding:"14px",borderRadius:7,background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.2)",
                        fontSize:12,color:"#10B981",fontWeight:600,textAlign:"center"}}>
                        ✓ No anomalies detected in this portfolio
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAX-LOSS DETAIL ── */}
                {expandedAICard==="taxloss" && aiTaxloss && (
                  <div>
                    {aiTaxloss.summary && (
                      <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:14,padding:"10px 12px",
                        background:T.borderSubtle,borderRadius:7}}>
                        {aiTaxloss.summary}
                      </div>
                    )}
                    {aiTaxloss.opportunities?.length > 0 ? (
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                          {aiTaxloss.opportunities.length} opportunit{aiTaxloss.opportunities.length===1?"y":"ies"}
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {aiTaxloss.opportunities.map((o,i)=>(
                            <div key={i} style={{padding:"10px 12px",borderRadius:7,
                              background:o.urgency==="high"?"rgba(244,63,94,0.04)":o.urgency==="medium"?"rgba(245,158,11,0.03)":T.borderSubtle,
                              border:`1px solid ${o.urgency==="high"?"#F43F5E":o.urgency==="medium"?"#F59E0B":"#6B7280"}22`}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}}>
                                  {o.urgency && (
                                    <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:3,textTransform:"uppercase",letterSpacing:"0.04em",flexShrink:0,
                                      color:o.urgency==="high"?"#F43F5E":o.urgency==="medium"?"#F59E0B":"#6B7280",
                                      background:o.urgency==="high"?"rgba(244,63,94,0.1)":o.urgency==="medium"?"rgba(245,158,11,0.1)":"rgba(107,114,128,0.1)"}}>
                                      {o.urgency}
                                    </span>
                                  )}
                                  <span style={{fontSize:12,fontWeight:600,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.holding}</span>
                                </div>
                                <div style={{display:"flex",gap:10,fontSize:11,flexShrink:0}}>
                                  {o.lossUSD!=null && o.lossUSD!==0 && (
                                    <span style={{color:"#F43F5E"}}>Loss {fmt(convert(Math.abs(o.lossUSD),dc),dc,true)}</span>
                                  )}
                                  {o.estimatedTaxSaving!=null && o.estimatedTaxSaving > 0 && (
                                    <span style={{color:"#10B981",fontWeight:700}}>Save {fmt(convert(o.estimatedTaxSaving,dc),dc,true)}</span>
                                  )}
                                </div>
                              </div>
                              {o.action && <div style={{fontSize:11,color:T.textSecondary,marginBottom:o.note?3:0}}>→ {o.action}</div>}
                              {o.note && <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic"}}>{o.note}</div>}
                            </div>
                          ))}
                        </div>
                        {aiTaxloss.totalHarvestableUSD > 0 && (
                          <div style={{marginTop:12,padding:"8px 12px",background:T.borderSubtle,borderRadius:6,
                            display:"flex",justifyContent:"space-between",fontSize:12}}>
                            <span style={{color:T.textSecondary}}>Total harvestable losses</span>
                            <span style={{color:T.textPrimary,fontWeight:700}}>{fmt(convert(aiTaxloss.totalHarvestableUSD,dc),dc,true)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{padding:"14px",borderRadius:7,background:T.borderSubtle,
                        fontSize:12,color:T.textSecondary,textAlign:"center"}}>
                        No tax-loss opportunities identified
                      </div>
                    )}
                  </div>
                )}

                {/* ── REBALANCING DETAIL ── */}
                {expandedAICard==="rebalance" && aiRebalance && (
                  <div>
                    {aiRebalance.summary && (
                      <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:14,padding:"10px 12px",
                        background:T.borderSubtle,borderRadius:7}}>
                        {aiRebalance._parseError ? (aiRebalance._raw?.slice(0,500)) : aiRebalance.summary}
                      </div>
                    )}
                    {aiRebalance.actions?.length > 0 && (
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                          Recommended actions ({aiRebalance.actions.length})
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {aiRebalance.actions.map((a,i)=>{
                            const isSell = a.type==="sell"||a.type==="reduce";
                            return (
                              <div key={i} style={{padding:"10px 12px",borderRadius:7,
                                background:isSell?"rgba(244,63,94,0.04)":"rgba(16,185,129,0.04)",
                                border:`1px solid ${isSell?"#F43F5E":"#10B981"}22`}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                                  <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:3,textTransform:"uppercase",letterSpacing:"0.04em",flexShrink:0,
                                    color:isSell?"#F43F5E":"#10B981",
                                    background:isSell?"rgba(244,63,94,0.1)":"rgba(16,185,129,0.1)"}}>
                                    {a.type}
                                  </span>
                                  <span style={{fontSize:12,fontWeight:600,color:T.textPrimary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.holding}</span>
                                  {a.amount && (
                                    <span style={{fontSize:12,color:isSell?"#F43F5E":"#10B981",fontWeight:700,flexShrink:0,
                                      fontFamily:"'Inter', system-ui, sans-serif"}}>
                                      {isSell?"−":"+"}{a.amount}
                                    </span>
                                  )}
                                </div>
                                {a.rationale && <div style={{fontSize:11,color:T.textSecondary,lineHeight:1.5}}>{a.rationale}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── BRIEFING DETAIL ── */}
                {expandedAICard==="briefing" && aiBriefing && (
                  <div>
                    {aiBriefing.headline && (
                      <div style={{fontSize:14,fontWeight:700,color:T.textPrimary,lineHeight:1.5,marginBottom:14,
                        padding:"10px 12px",background:"rgba(124,58,237,0.06)",borderRadius:7,
                        borderLeft:"3px solid rgba(124,58,237,0.4)"}}>
                        {aiBriefing.headline}
                      </div>
                    )}
                    {aiBriefing.sections?.length > 0 && (
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:aiBriefing.actionItems?.length?14:0}}>
                        {aiBriefing.sections.map((s,i)=>(
                          <div key={i} style={{background:T.borderSubtle,borderRadius:7,padding:"10px 12px"}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.textPrimary,marginBottom:5}}>{s.title}</div>
                            <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.6}}>{s.content}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiBriefing.actionItems?.length > 0 && (
                      <div style={{paddingTop:10,borderTop:`1px solid ${T.borderSubtle}`}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                          Action items
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:5}}>
                          {aiBriefing.actionItems.map((a,i)=>(
                            <div key={i} style={{display:"flex",gap:8,fontSize:12,alignItems:"flex-start"}}>
                              <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:3,flexShrink:0,marginTop:1,textTransform:"uppercase",letterSpacing:"0.04em",
                                color:a.priority==="high"?"#F43F5E":a.priority==="medium"?"#F59E0B":"#10B981",
                                background:a.priority==="high"?"rgba(244,63,94,0.1)":a.priority==="medium"?"rgba(245,158,11,0.1)":"rgba(16,185,129,0.1)"}}>
                                {a.priority}
                              </span>
                              <span style={{color:T.textSecondary,lineHeight:1.55}}>{a.item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── HEADER ── */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            marginBottom:16,flexWrap:"wrap",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:40,height:40,borderRadius:12,flexShrink:0,
                background:"#0066cc",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:20,boxShadow:"0 4px 14px rgba(0,102,204,0.35)"}}>🤖</div>
              <div>
                <div style={{fontSize:17,fontWeight:700,color:T.textPrimary}}>AI Portfolio Advisor</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                  {isAdmin && clientName !== "All Clients" && (
                    <span style={{fontWeight:600,color:T.brand,marginRight:6}}>
                      {clientName} ·
                    </span>
                  )}
                  Powered by Claude · {hw.length} holdings · {fmt(convert(totalV,dc),dc)}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {agentMessages.length>0&&(
                <React.Fragment>
                  <button onClick={()=>{
                    const entry = {
                      id: Date.now().toString(),
                      date: new Date().toISOString(),
                      clientName,
                      clientId: advisorClientId || null,
                      familyId: selectedFamily !== "all" ? selectedFamily : null,
                      advisorKey,
                      totalV,
                      gainPct,
                      holdingCount: hw.length,
                      messages: agentMessages,
                      portfolioSnapshot: [...hw].sort((a,b)=>b.valueUSD-a.valueUSD).slice(0,10).map(h=>({
                        name:h.name, ticker:h.ticker, class:h.class,
                        valueUSD:h.valueUSD, gainPct:h.gainPct, currency:h.currency
                      })),
                    };
                    const next = [entry, ...advisorArchive].slice(0,50);
                    setAdvisorArchive(next);
                    try { localStorage.setItem("forthius_advisor_archive", JSON.stringify(next)); } catch {}
                    // Also persist to Supabase so it survives localStorage clear / cross-device
                    upsertProfile("__advisor_archive__", next).catch(e => {
                      console.warn("[Advisor archive] cloud save failed:", e.message);
                      showToast("⚠ Saved locally, cloud sync failed", "#D97706", 4000);
                    });
                    showToast("✓ Conversation saved to archive", "#1D9E75", 3000);
                  }} style={{background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.25)",
                      borderRadius:6,color:"#1D9E75",padding:"5px 12px",
                      cursor:"pointer",fontSize:11,fontWeight:600}}>
                    💾 Save
                  </button>
                  <button onClick={()=>setAgentMessages([])}
                    style={{background:"transparent",border:`1px solid ${T.cardBorder}`,
                      borderRadius:6,color:T.textMuted,padding:"5px 12px",
                      cursor:"pointer",fontSize:11}}>
                    Clear ↺
                  </button>
                </React.Fragment>
              )}
              <div style={{fontSize:11,color:"#1D9E75",background:"rgba(22,163,74,0.08)",
                border:"1px solid rgba(22,163,74,0.2)",borderRadius:20,
                padding:"4px 12px",display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:"#1D9E75"}}/>
                Live context
              </div>
            </div>
          </div>

          {/* ── PROACTIVE INSIGHTS (shown when no messages yet) ── */}
          {agentMessages.length===0 && proactiveInsights.length>0 && (
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
              {proactiveInsights.map((ins,i)=>{
                const c = insightColors[ins.type]||insightColors.info;
                return (
                  <div key={i}
                    onClick={()=>sendMessage(`Tell me more about: ${ins.text}`)}
                    style={{flex:1,minWidth:isMobile?120:200,padding:"8px 12px",borderRadius:8,cursor:"pointer",
                      background:c.bg,border:`1px solid ${c.border}`,
                      display:"flex",alignItems:"center",gap:8,transition:"opacity 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.opacity="0.8"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                    <span style={{fontSize:16,flexShrink:0}}>{ins.icon}</span>
                    <span style={{fontSize:11,fontWeight:600,color:c.text,lineHeight:1.4}}>{ins.text}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── MAIN CHAT CARD ── */}
          <div style={{...tCard(),padding:0,overflow:"hidden",display:"flex",
            flexDirection:"column",
            height:isMobile?"calc(100vh - 260px)":"calc(100vh - 260px)",
            minHeight:420}}>

            {/* Messages */}
            <div style={{flex:1,overflowY:"auto",padding:"20px 20px 8px",
              display:"flex",flexDirection:"column",gap:14}}>

              {/* Loaded from archive notice */}
              {agentMessages.length > 0 && agentMessages[0]?._fromArchive && (
                <div style={{textAlign:"center",padding:"6px 14px",fontSize:11,
                  color:T.textMuted,background:T.surfaceBg,borderRadius:6,marginBottom:8,
                  border:`1px solid ${T.cardBorder}`}}>
                  ↩ Showing last saved conversation with {clientName}. Start typing to continue.
                </div>
              )}

              {/* Welcome / empty state */}
              {agentMessages.length===0 && (
                <div style={{flex:1,display:"flex",flexDirection:"column",
                  alignItems:"center",justifyContent:"center",gap:18,padding:"10px 0"}}>
                  <div style={{textAlign:"center",maxWidth:500}}>
                    <div style={{fontSize:36,marginBottom:12}}>👋</div>
                    <div style={{fontSize:18,fontWeight:700,color:T.textPrimary,marginBottom:8}}>
                      Hi {firstName}, I'm your AI Advisor
                    </div>
                    <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.7}}>
                      I have complete visibility into your portfolio —{" "}
                      <strong style={{color:T.textPrimary}}>{hw.length} holdings</strong> worth{" "}
                      <strong style={{color:T.brand}}>{fmt(convert(totalV,dc),dc)}</strong>.
                      {alpha!=null&&<React.Fragment> You're currently <strong style={{color:alpha>=0?"#1D9E75":"#A32D2D"}}>{alpha>=0?"outperforming":"underperforming"} {bmLabel} by {Math.abs(alpha).toFixed(1)}%</strong>.</React.Fragment>}
                      {" "}Ask me anything.
                    </div>
                  </div>
                  {/* Suggestion chips */}
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",maxWidth:580}}>
                    {suggestions.map((s,i)=>(
                      <button key={i} onClick={()=>sendMessage(s)}
                        style={{background:T.brandLight,border:`1px solid ${T.brand}44`,
                          borderRadius:20,color:T.brand,padding:"8px 16px",
                          cursor:"pointer",fontSize:12,fontWeight:500,
                          transition:"all 0.15s",textAlign:"left",lineHeight:1.4}}
                        onMouseEnter={e=>{e.currentTarget.style.background=T.brand;e.currentTarget.style.color="#fff";}}
                        onMouseLeave={e=>{e.currentTarget.style.background=T.brandLight;e.currentTarget.style.color=T.brand;}}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message thread */}
              {agentMessages.map((msg,i)=>{
                const isUser = msg.role==="user";
                return (
                  <div key={i} style={{display:"flex",gap:10,
                    flexDirection:isUser?"row-reverse":"row",
                    alignItems:"flex-start"}}>
                    {/* Avatar */}
                    <div style={{width:32,height:32,borderRadius:isUser?8:10,flexShrink:0,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,
                      fontWeight:700,
                      background:isUser
                        ?`linear-gradient(135deg,${T.brand},${T.brandDark})`
                        :"#0066cc",
                      color:"#fff"}}>
                      {isUser ? firstName.charAt(0).toUpperCase() : "AI"}
                    </div>
                    {/* Bubble */}
                    <div style={{maxWidth:"75%",display:"flex",flexDirection:"column",
                      alignItems:isUser?"flex-end":"flex-start",gap:3}}>
                      <div style={{
                        background:isUser?T.brand:T.cardBg,
                        border:isUser?"none":`1px solid ${T.cardBorder}`,
                        borderRadius:isUser?"14px 4px 14px 14px":"4px 14px 14px 14px",
                        padding:"10px 14px",
                        color:isUser?"#fff":T.textPrimary,
                        fontSize:13,lineHeight:1.65,
                        whiteSpace:"pre-wrap",wordBreak:"break-word",
                        boxShadow:isUser?"none":T.cardShadow}}>
                        {msg.content}
                      </div>
                      <div style={{fontSize:10,color:T.textMuted}}>
                        {new Date(msg.ts).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Loading indicator */}
              {agentLoading && (
                <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                  <div style={{width:32,height:32,borderRadius:10,flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,
                    background:"#0066cc",color:"#fff"}}>AI</div>
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                    borderRadius:"4px 14px 14px 14px",padding:"12px 18px",
                    display:"flex",gap:5,alignItems:"center"}}>
                    {[0,1,2].map(i=>(
                      <div key={i} style={{width:7,height:7,borderRadius:"50%",
                        background:T.brand,
                        animation:`agent-dot 1.2s ease-in-out ${i*0.18}s infinite`}}/>
                    ))}
                  </div>
                </div>
              )}
              <div ref={agentEndRef}/>
            </div>

            {/* ── Follow-up chips (after first exchange) ── */}
            {agentMessages.length>0 && agentMessages.length<6 && !agentLoading && (
              <div style={{padding:"6px 16px 0",display:"flex",gap:6,flexWrap:"wrap"}}>
                {suggestions.filter(s=>!agentMessages.some(m=>m.content===s)).slice(0,3).map((s,i)=>(
                  <button key={i} onClick={()=>sendMessage(s)}
                    style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                      borderRadius:14,color:T.textSecondary,padding:"4px 12px",
                      cursor:"pointer",fontSize:11,whiteSpace:"nowrap"}}>
                    {s.length>40?s.slice(0,40)+"…":s}
                  </button>
                ))}
              </div>
            )}

            {/* ── Input area ── */}
            <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"12px 16px",
              background:T.cardBg,flexShrink:0}}>

              {/* Voice status bar — shows whenever voice mode is on */}
              {voiceMode && (
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,
                  padding:"8px 14px",borderRadius:10,
                  background:voiceSpeaking?"rgba(22,163,74,0.12)":voiceActive?"rgba(220,38,38,0.10)":"rgba(0,120,190,0.10)",
                  border:`1.5px solid ${voiceSpeaking?"#1D9E75":voiceActive?"#A32D2D":"#0078be"}55`}}>
                  <span style={{fontSize:18,animation:voiceActive||voiceSpeaking?"pulse 1s infinite":"none"}}>
                    {voiceSpeaking?"🔊":voiceActive?"🎙️":"🎤"}
                  </span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,
                      color:voiceSpeaking?"#1D9E75":voiceActive?"#A32D2D":"#0078be"}}>
                      {voiceSpeaking?"Speaking response…":voiceActive?"Listening… speak your question":"Voice mode on — click 🎙️ to speak"}
                    </div>
                    <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                      {voiceSpeaking?"Response will play automatically":"Auto-sends after 1.5s silence"}
                    </div>
                  </div>
                  <button onClick={stopVoice}
                    style={{marginLeft:"auto",fontSize:11,fontWeight:600,padding:"4px 12px",borderRadius:6,
                      border:`1px solid ${T.cardBorder}`,cursor:"pointer",
                      background:T.cardBg,color:T.textPrimary}}>
                    ⏹ End Voice
                  </button>
                </div>
              )}

              <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                <textarea
                  value={agentInput}
                  onChange={e=>setAgentInput(e.target.value)}
                  onKeyDown={e=>{
                    if(e.key==="Enter"&&!e.shiftKey){
                      e.preventDefault();
                      sendMessage(agentInput);
                    }
                  }}
                  placeholder={voiceActive?"🎙️ Listening…":"Ask about your portfolio… (Enter to send)"}
                  rows={2}
                  style={{flex:1,background:voiceActive?"rgba(220,38,38,0.06)":T.inputBg,
                    border:`1.5px solid ${voiceActive?"#A32D2D":agentInput?T.brand:T.inputBorder}`,
                    borderRadius:10,color:T.textPrimary,padding:"10px 14px",
                    fontSize:13,outline:"none",resize:"none",
                    fontFamily:"inherit",lineHeight:1.5,
                    transition:"border-color 0.15s"}}/>

                {/* Mic button */}
                {voiceSupported && (
                  <button
                    onClick={voiceActive ? stopVoice : startVoice}
                    title={voiceActive?"Stop listening":"Start voice conversation"}
                    style={{
                      background:voiceActive
                        ?"#A32D2D"
                        :voiceSpeaking
                          ?"#1D9E75"
                          :`${T.brand}22`,
                      border:`2px solid ${voiceActive?"#A32D2D":voiceSpeaking?"#1D9E75":T.brand}`,
                      borderRadius:10,color:voiceActive||voiceSpeaking?"white":T.brand,
                      width:44,height:44,cursor:"pointer",flexShrink:0,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:20,transition:"all 0.2s",
                      boxShadow:voiceActive?"0 0 0 3px rgba(220,38,38,0.25)":voiceSpeaking?"0 0 0 3px rgba(22,163,74,0.25)":"none",
                      animation:voiceActive?"micPulse 1.2s ease-in-out infinite":"none"}}>
                    {voiceSpeaking?"🔊":voiceActive?"⏹":"🎙️"}
                  </button>
                )}

                {/* Send button */}
                <button
                  onClick={()=>sendMessage(agentInput)}
                  disabled={!agentInput.trim()||agentLoading}
                  style={{
                    background:!agentInput.trim()||agentLoading
                      ?"rgba(0,102,204,0.25)"
                      :"#0066cc",
                    border:"none",borderRadius:10,color:"#fff",
                    padding:"10px 20px",
                    cursor:!agentInput.trim()||agentLoading?"default":"pointer",
                    fontSize:13,fontWeight:700,flexShrink:0,height:44,
                    display:"flex",alignItems:"center",gap:6,
                    transition:"background 0.15s",
                    boxShadow:agentInput.trim()&&!agentLoading?"0 2px 8px rgba(0,102,204,0.4)":"none"}}>
                  {agentLoading
                    ? <span style={{display:"inline-block",animation:"spin 0.8s linear infinite"}}>⟳</span>
                    : <React.Fragment>Send <span style={{fontSize:16}}>↑</span></React.Fragment>}
                </button>
              </div>
              <div style={{fontSize:10,color:T.textMuted,marginTop:6,textAlign:"center"}}>
                {voiceSupported
                  ? "🎙️ Voice mode available · Reads your real portfolio data · Not financial advice"
                  : "Reads your real portfolio data · Not financial advice · Powered by Claude"}
              </div>
            </div>
          </div>

          {/* ── Advisor Conversation Archive ── */}
          {(()=>{
            const downloadConversation = (entry) => {
              const dateStr = new Date(entry.date).toLocaleDateString("en-SG",{year:"numeric",month:"long",day:"numeric"});
              const timeStr = new Date(entry.date).toLocaleTimeString("en-SG",{hour:"2-digit",minute:"2-digit"});
              const msgRows = entry.messages.map(m => {
                const isUser = m.role === "user";
                const ts = m.ts ? new Date(m.ts).toLocaleTimeString("en-SG",{hour:"2-digit",minute:"2-digit"}) : "";
                // Subtle differentiation: left-border colour + small role label (no emoji).
                const accent = isUser ? "#0066cc" : "#475569";
                const bg     = isUser ? "#f0f9ff" : "#f8fafc";
                const label  = isUser ? entry.clientName : "AI Advisor";
                return `<div style="margin-bottom:14px;display:flex;gap:12px;align-items:flex-start">
                  <div style="width:3px;align-self:stretch;background:${accent};border-radius:2px;flex-shrink:0"></div>
                  <div style="flex:1;background:${bg};border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px">
                    <div style="font-size:10.5px;color:${accent};font-weight:700;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:6px">
                      ${label}${ts ? ` <span style="color:#94a3b8;font-weight:400;letter-spacing:0;text-transform:none">· ${ts}</span>` : ""}
                    </div>
                    <div style="font-size:13px;line-height:1.7;white-space:pre-wrap;color:#0f172a">
                      ${m.content.replace(/</g,"&lt;").replace(/>/g,"&gt;")}
                    </div>
                  </div>
                </div>`;
              }).join("");
              const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>AI Advisor — ${entry.clientName} — ${dateStr}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:32px;font-size:13px;line-height:1.6;max-width:820px;background:#fff}
  /* Letterhead-style header: logo top-left, title beneath, separator line */
  .letterhead{padding-bottom:18px;border-bottom:1px solid #e2e8f0;margin-bottom:24px}
  .letterhead .brand img{height:38px;width:auto;display:block}
  .letterhead .title{font-size:13px;font-weight:600;color:#0f172a;margin-top:14px;letter-spacing:0.02em}
  .letterhead .meta{font-size:11px;color:#64748b;margin-top:3px;font-weight:400}
  .kpi{display:flex;gap:14px;margin-bottom:26px}
  .kpi-card{flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px}
  .kpi-card .label{font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700}
  .kpi-card .val{font-size:18px;font-weight:700;margin-top:3px;color:#0f172a}
  .section h2{font-size:12px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #e2e8f0;padding-bottom:6px;margin:0 0 16px 0}
  .footer{margin-top:32px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:12px;line-height:1.6}
  @media print{body{padding:18px}}
</style></head><body>
<div class="letterhead">
  <div class="brand"><img src="${FORTHIUS_LOGO}" alt="Forthius Finalytics"/></div>
  <div class="title">AI Advisor Conversation</div>
  <div class="meta">${entry.clientName} &nbsp;·&nbsp; ${dateStr} at ${timeStr}</div>
</div>
<div class="kpi">
  <div class="kpi-card"><div class="label">Portfolio Value</div><div class="val">$${(entry.totalV/1000000).toFixed(2)}M</div></div>
  <div class="kpi-card"><div class="label">Total Return</div><div class="val" style="color:${(entry.gainPct||0)>=0?"#1D9E75":"#A32D2D"}">${(entry.gainPct||0)>=0?"+":""}${(entry.gainPct||0).toFixed(1)}%</div></div>
  <div class="kpi-card"><div class="label">Holdings</div><div class="val">${entry.holdingCount}</div></div>
  <div class="kpi-card"><div class="label">Messages</div><div class="val">${entry.messages.length}</div></div>
</div>
<div class="section"><h2>Conversation Transcript</h2>${msgRows}</div>
<div class="footer">
  AI-generated advice is for informational purposes only and does not constitute financial advice.
  Generated by Forthius Finalytics on ${dateStr}.
</div></body></html>`;
              const blob = new Blob([html],{type:"text/html"});
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Forthius-Advisor-${entry.clientName.replace(/\s+/g,"-")}-${dateStr.replace(/[\s,]+/g,"-")}.html`;
              a.click();
              URL.revokeObjectURL(url);
            };

            return (
              <div style={{marginTop:28,borderTop:`2px solid ${T.borderSubtle}`,paddingTop:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <button onClick={()=>setAdvisorArchiveOpen(p=>!p)}
                    style={{display:"flex",alignItems:"center",gap:10,background:"transparent",
                      border:"none",cursor:"pointer",padding:0}}>
                    <span style={{fontSize:15,fontWeight:700,color:T.textPrimary}}>💬 Conversation Archive</span>
                    <span style={{fontSize:11,background:"rgba(0,102,204,0.1)",color:"#0066cc",
                      padding:"2px 8px",borderRadius:10,fontWeight:600}}>
                      {advisorArchive.length} saved
                    </span>
                    <span style={{fontSize:12,color:T.textMuted}}>{advisorArchiveOpen?"▲":"▼"}</span>
                  </button>
                  {advisorArchive.length>0 && (
                    <button onClick={()=>{
                      if (!window.confirm("Clear all saved conversations?")) return;
                      setAdvisorArchive([]);
                      try{localStorage.setItem("forthius_advisor_archive","[]");}catch{}
                      upsertProfile("__advisor_archive__", []).catch(e => {
                        console.warn("[Advisor archive] cloud clear failed:", e.message);
                        showToast("⚠ Cleared locally, cloud sync failed", "#D97706", 4000);
                      });
                    }} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:11,color:T.textMuted}}>
                      🗑 Clear all
                    </button>
                  )}
                </div>

                {advisorArchiveOpen && (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {advisorArchive.length===0 ? (
                      <div style={{textAlign:"center",padding:"28px 20px",color:T.textMuted,
                        background:T.surfaceBg,borderRadius:10,fontSize:13}}>
                        No saved conversations yet. Chat with the advisor and click <strong>💾 Save</strong>.
                      </div>
                    ) : advisorArchive.map(entry=>{
                      const isExp = advisorArchiveExpanded === entry.id;
                      const dateStr = new Date(entry.date).toLocaleDateString("en-SG",{year:"numeric",month:"short",day:"numeric"});
                      const timeStr = new Date(entry.date).toLocaleTimeString("en-SG",{hour:"2-digit",minute:"2-digit"});
                      const userMsgs = entry.messages.filter(m=>m.role==="user");
                      const firstQ = userMsgs[0]?.content?.slice(0,80)||"";
                      return (
                        <div key={entry.id} style={{border:`1px solid ${T.cardBorder}`,borderRadius:10,overflow:"hidden",
                          boxShadow:isExp?"0 4px 16px rgba(0,0,0,0.08)":"none"}}>
                          {/* Entry header */}
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                            padding:"13px 18px",background:isExp?"rgba(0,102,204,0.05)":T.cardBg,cursor:"pointer"}}
                            onClick={()=>setAdvisorArchiveExpanded(isExp?null:entry.id)}>
                            <div style={{display:"flex",alignItems:"center",gap:12}}>
                              <div style={{fontSize:18}}>💬</div>
                              <div>
                                <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>
                                  {dateStr} · {timeStr}
                                </div>
                                <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                                  {entry.clientName} · {entry.messages.length} messages ·{" "}
                                  <span style={{color:T.textMuted,fontStyle:"italic"}}>
                                    "{firstQ}{firstQ.length>=80?"…":""}"
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <button onClick={e=>{e.stopPropagation();downloadConversation(entry);}}
                                style={{background:"rgba(0,102,204,0.08)",border:"1px solid rgba(0,102,204,0.2)",
                                  borderRadius:6,padding:"5px 12px",cursor:"pointer",
                                  fontSize:11,color:"#0066cc",fontWeight:600}}>
                                ⬇ Download
                              </button>
                              <button onClick={e=>{e.stopPropagation();
                                const next=advisorArchive.filter(e2=>e2.id!==entry.id);
                                setAdvisorArchive(next);
                                try{localStorage.setItem("forthius_advisor_archive",JSON.stringify(next));}catch{}
                                upsertProfile("__advisor_archive__", next).catch(err => {
                                  console.warn("[Advisor archive] cloud delete failed:", err.message);
                                  showToast("⚠ Deleted locally, cloud sync failed", "#D97706", 4000);
                                });
                              }} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,color:T.textMuted}}>✕</button>
                              <span style={{fontSize:12,color:T.textMuted}}>{isExp?"▲":"▼"}</span>
                            </div>
                          </div>

                          {/* Expanded messages */}
                          {isExp && (
                            <div style={{padding:"16px 18px",borderTop:`1px solid ${T.borderSubtle}`,
                              maxHeight:500,overflowY:"auto"}}>
                              {entry.messages.map((msg,i)=>{
                                const isUser = msg.role==="user";
                                const ts = msg.ts?new Date(msg.ts).toLocaleTimeString("en-SG",{hour:"2-digit",minute:"2-digit"}):"";
                                return (
                                  <div key={i} style={{marginBottom:14,display:"flex",
                                    justifyContent:isUser?"flex-end":"flex-start"}}>
                                    <div style={{maxWidth:"85%"}}>
                                      <div style={{fontSize:10,color:T.textMuted,marginBottom:3,
                                        textAlign:isUser?"right":"left",fontWeight:600}}>
                                        {isUser?"👤 "+entry.clientName:"🤖 AI Advisor"}{ts?" · "+ts:""}
                                      </div>
                                      <div style={{background:isUser?"rgba(0,102,204,0.08)":"rgba(0,0,0,0.03)",
                                        border:`1px solid ${isUser?"rgba(0,102,204,0.2)":T.borderSubtle}`,
                                        borderRadius:10,padding:"10px 14px",fontSize:12,
                                        lineHeight:1.6,color:T.textPrimary,whiteSpace:"pre-wrap"}}>
                                        {msg.content}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in agent tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ── OVERVIEW ── */}
        {tab==="overview" && <React.Fragment>

          {/* ── AI Anomaly Alert Strip ── */}
          {aiAnomalies?.alerts?.filter(a=>a.severity==="high").length > 0 && (
            <div style={{marginBottom:10,padding:"8px 14px",borderRadius:8,
              background:"rgba(220,38,38,0.05)",border:"1px solid rgba(220,38,38,0.2)",
              display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:13}}>⚠</span>
              <span style={{fontSize:12,fontWeight:700,color:"#A32D2D"}}>
                {aiAnomalies.alerts.filter(a=>a.severity==="high").length} portfolio issue{aiAnomalies.alerts.filter(a=>a.severity==="high").length>1?"s":""} detected
              </span>
              {aiAnomalies.alerts.filter(a=>a.severity==="high").slice(0,2).map((a,i)=>(
                <span key={i} style={{fontSize:11,color:"#A32D2D",background:"rgba(220,38,38,0.07)",
                  padding:"2px 8px",borderRadius:5}}>
                  {a.holding ? `${a.holding}: ` : ""}{a.message}
                </span>
              ))}
              <span onClick={()=>setTab("agent")}
                style={{fontSize:11,color:"#A32D2D",cursor:"pointer",marginLeft:"auto",textDecoration:"underline"}}>
                View all →
              </span>
            </div>
          )}

          {/* ── Weekly AI Commentary ── */}
          <div style={{...card(),marginBottom:14,padding:"14px 16px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:aiCommentary?10:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:14}}>✦</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Weekly AI Commentary</div>
                  {aiCommentary && <div style={{fontSize:10,color:T.textMuted}}>Week of {aiCommentary.week_of} · by Forthius AI</div>}
                </div>
              </div>
              <button onClick={generateAiCommentary}
                disabled={aiCommentaryLoad}
                style={{background:T.brand+"14",border:`1px solid ${T.brand}44`,borderRadius:6,
                  color:T.brand,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:600,
                  opacity:aiCommentaryLoad?0.6:1}}>
                {aiCommentaryLoad ? "⟳ Generating…" : aiCommentary ? "↺ Refresh" : "Generate"}
              </button>
            </div>
            {aiCommentaryLoad && !aiCommentary && (
              <div style={{fontSize:12,color:T.textMuted,padding:"8px 0",display:"flex",alignItems:"center",gap:6}}>
                <span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span>
                Analysing your portfolio…
              </div>
            )}
            {aiCommentary?.commentary && (
              <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.75,whiteSpace:"pre-line",
                borderTop:`1px solid ${T.borderSubtle}`,paddingTop:10}}>
                {aiCommentary.commentary}
              </div>
            )}
            {!aiCommentary && !aiCommentaryLoad && (
              <div style={{fontSize:12,color:T.textMuted,paddingTop:4}}>
                Your personalised weekly portfolio brief — generated every Monday and delivered to your inbox.
              </div>
            )}
          </div>

          {/* Post-login price refresh notice */}
          {loginRefreshMsg && (
            <div style={{marginBottom:12,padding:"8px 14px",borderRadius:8,
              background:`rgba(0,120,120,0.06)`,border:`1px solid ${T.brand}30`,
              display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.brand}}>
              <span style={{animation:"spin 0.9s linear infinite",display:"inline-block",fontSize:14}}>⟳</span>
              <span>
                {lastSavedPriceTime
                  ? <React.Fragment>Showing prices last saved at <strong>{lastSavedPriceTime.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</strong> · Fetching live prices in background…</React.Fragment>
                  : <React.Fragment>Fetching latest prices for your portfolio…</React.Fragment>}
              </span>
            </div>
          )}
          {/* ── Client welcome banner (non-admin only) ── */}
          {!isAdmin && loggedInClientId && (function(){
            const cli = visibleClients.find(c=>c.id===loggedInClientId);
            if (!cli) return null;
            const hour = new Date().getHours();
            const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
            const missCount = filteredHW.filter(h=>!h.costPrice||h.costPrice===0).length;
            return (
              <div style={{marginBottom:20,padding:"16px 20px",
                background:`linear-gradient(135deg,${T.brand}12,${T.brand}05)`,
                border:`1px solid ${T.brand}25`,borderRadius:12,
                display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:T.textPrimary,marginBottom:2}}>
                    {greeting}, {cli.name.split(" ")[0]} 👋
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary}}>
                    {new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                    {missCount > 0 && (
                      <span style={{marginLeft:12,color:"#D97706",fontWeight:600}}>
                        ⚠ {missCount} holding{missCount!==1?"s":""} missing cost price
                      </span>
                    )}
                  </div>
                </div>
                <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                  {[
                    {label:"Portfolio Value",val:fmt(convert(kpiTotalUSD,dc),dc,true),col:T.brand},
                    {label:"Total Gain/Loss",val:kpiGainPct!=null?`${kpiGainPct>=0?"+":""}${kpiGainPct.toFixed(2)}%`:"—",col:kpiGainUSD>=0?"#1D9E75":"#A32D2D"},
                    {label:"Holdings",val:filteredHW.length,col:T.textPrimary},
                  ].map(s=>(
                    <div key={s.label} style={{textAlign:"center"}}>
                      <div style={{fontSize:19,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:s.col}}>{s.val}</div>
                      <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em"}}>{s.label}</div>
                    </div>
                  ))}
                  <button
                    onClick={()=>{ setPdfScope("individual"); setPdfClientId(loggedInClientId); setPdfModal(true); }}
                    style={{fontSize:11,fontWeight:600,background:"transparent",
                      border:`1px solid ${T.brand}44`,borderRadius:6,color:T.brand,
                      padding:"5px 12px",cursor:"pointer",display:"flex",
                      alignItems:"center",gap:5,whiteSpace:"nowrap",flexShrink:0}}>
                    📄 My Statement
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── Empty state for new clients with zero holdings ── */}
          {filteredHW.length === 0 && (
            <div style={{
              marginBottom:20,padding:"40px 24px",textAlign:"center",
              background:T.cardBg,border:`1px dashed ${T.cardBorder}`,
              borderRadius:14}}>
              <div style={{fontSize:44,marginBottom:14}}>📂</div>
              <div style={{fontSize:17,fontWeight:700,color:T.textPrimary,marginBottom:8}}>
                Your portfolio is empty
              </div>
              <div style={{fontSize:13,color:T.textSecondary,maxWidth:360,
                margin:"0 auto 20px",lineHeight:1.7}}>
                Import your holdings from a CDSL/NSDL CAS statement, or add them manually
                to start tracking your wealth.
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                <button
                  onClick={()=>{ setTab("holdings"); setAddOpen(true); }}
                  style={{background:`${T.brand}`,
                    border:"none",borderRadius:8,color:"white",
                    padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600,
                    display:"flex",alignItems:"center",gap:6}}>
                  ✚ Add Holding Manually
                </button>
                <button
                  onClick={()=>setTab("data")}
                  style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                    borderRadius:8,color:T.textSecondary,
                    padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:500}}>
                  📄 Import CAS Statement
                </button>
              </div>
            </div>
          )}

          {tab==="overview" && <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?8:12,marginBottom:14}}>
            {[
              {label:`Total AUM${isDrillActive?" (chart filter)":selectedFamily!=="all"||selectedClients.size>0?" (filtered)":""}`,value:`${fmt(convert(kpiTotalUSD,dc),dc,true)}${refreshing?" ⟳":""}`,sub:isDrillActive?`${kpiHW.length} of ${filteredHW.length} holdings`:refreshing?"Updating live prices…":"Portfolio value",col:"#818CF8",icon:"💰",iconBg:"rgba(129,140,248,0.15)"},
              {label:"Total Gain / Loss",value:fmt(convert(kpiGainUSD,dc),dc,true),sub:kpiGainPct!=null?`${kpiGainPct>=0?"+":""}${kpiGainPct.toFixed(2)}% overall`:"Cost data missing — edit holdings to add cost prices",col:kpiGainUSD>=0?"#34D399":"#F87171",icon:"📈",iconBg:kpiGainUSD>=0?"rgba(52,211,153,0.15)":"rgba(248,113,113,0.15)"},
              {label:"No. of Holdings",value:kpiHW.length,sub:`${kpiHW.filter(h=>h.listed).length} listed · ${kpiHW.filter(h=>!h.listed).length} unlisted`,col:"#A78BFA",icon:"🗂️",iconBg:"rgba(167,139,250,0.15)"},
              {label:"Currencies Held",value:Object.keys(kpiByCurrency).length,sub:`Base: ${dc}`,col:"#FBBF24",icon:"💱",iconBg:"rgba(251,191,36,0.15)"},
            ].map((k,i)=>(
              <div key={i} style={{...card(),position:"relative",overflow:"hidden",
                borderLeft: `3px solid ${k.col}`,
                outline: isDrillActive ? `2px solid ${k.col}44` : "none",
                transition:"outline 0.2s"}}>
                {/* Top accent bar */}
                <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${k.col},${k.col}80)`,borderRadius:"16px 16px 0 0"}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.09em",fontWeight:700}}>{k.label}</div>
                  <div style={{width:32,height:32,borderRadius:8,background:k.iconBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{k.icon}</div>
                </div>
                <div style={{fontSize:isMobile?20:26,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:k.col,lineHeight:1.1,letterSpacing:"-0.02em"}}>{k.value}</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:5}}>{k.sub}</div>
              </div>
            ))}
          </div>}


          {tab==="overview" && <React.Fragment>
          {/* ── Holdings Treemap (collapsible) ── */}
          {kpiHW.length > 0 && (function(){
            const tmVisible = treemapVisible;
            const setTmVisible = setTreemapVisible;
            const tmBy = treemapBy; const setTmBy = setTreemapBy;
            const CLASS_COLORS_TM = {
              "Equities":"#2563EB","Fixed Income":"#1D9E75","Real Estate":"#D97706",
              "Cash":"#78716C","Commodities":"#EA580C","Crypto":"#7C3AED",
              "Alternates":"#4F46E5","Market Neutral":"#0891B2",
              "Mutual Funds":"#65A30D","Indian Mutual Funds":"#65A30D","Global Mutual Funds":"#84CC16","Other":"#94A3B8",
            };
            const SECTOR_COLORS_TM = {
              "Technology":"#2563EB","Financials":"#D97706","Energy":"#A32D2D",
              "Healthcare":"#1D9E75","Consumer Staples":"#059669",
              "Consumer Discretionary":"#EA580C","Real Estate":"#7C3AED",
              "Communication Services":"#0EA5E9","Industrials":"#92400E",
              "Materials":"#65A30D","Utilities":"#6366F1","Diversified":"#78716C","Other":"#94A3B8",
            };
            const tmData = kpiHW.filter(h => (h.valueUSD||0) > 0).map(h => ({
              label: (()=>{ const w=h.name.split(" "); return w.length>1&&w[0].length<4 ? w.slice(0,2).join(" ") : w[0]; })(),
              value: h.valueUSD || 0,
              color: tmBy==="class" ? (CLASS_COLORS_TM[h.class]||"#94A3B8")
                   : tmBy==="sector" ? (SECTOR_COLORS_TM[resolveHoldingSector(h)]||"#94A3B8")
                   : (MANAGER_COLORS[h.manager||"Self-Managed"]||"#78716C"),
            }));
            return (
              <div style={{...card(),marginBottom:16,padding:0,overflow:"hidden"}}>
                {/* Header — always visible, acts as toggle */}
                <div
                  onClick={()=>setTmVisible(v=>!v)}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    padding:"12px 16px",cursor:"pointer",
                    borderBottom:tmVisible?`1px solid ${T.borderSubtle}`:"none",
                    userSelect:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:13,fontWeight:700}}>Portfolio Bubble Chart</span>
                    <span style={{fontSize:9,color:T.textMuted,fontWeight:400}}>
                      Bubble size = portfolio weight · click to drill into holdings
                    </span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {tmVisible && (
                      <div style={{display:"flex",background:T.pageBg,borderRadius:6,
                        border:`1px solid ${T.cardBorder}`,overflow:"hidden"}}>
                        {[["class","By Class"],["sector","By Sector"],["manager","By Custodian"]].map(([v,l])=>(
                          <button key={v} onClick={e=>{e.stopPropagation();setTmBy(v);}}
                            style={{padding:"4px 10px",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                              background:tmBy===v?T.brand:"transparent",
                              color:tmBy===v?"white":T.textMuted,transition:"all 0.15s"}}>
                            {l}
                          </button>
                        ))}
                      </div>
                    )}
                    <span style={{fontSize:11,color:T.textMuted,transition:"transform 0.2s",
                      transform:tmVisible?"rotate(90deg)":"rotate(0deg)"}}>▶</span>
                  </div>
                </div>
                {tmVisible && (
                  <div style={{padding:"12px 16px 14px"}}>
                    <Treemap
                      data={tmData}
                      width={isMobile?360:700} height={isMobile?280:340}
                      onSliceClick={label=>{
                        const h = kpiHW.find(x=>x.name.split(" ")[0]===label);
                        if (h) { setHoldingSearch(h.name.split(" ")[0]); setTab("holdings"); }
                      }}
                      T={T} fmt={fmt} convert={convert} dc={dc}
                    />
                  </div>
                )}
              </div>
            );
          })()}

          {/* Drill filter active indicator */}
          {isDrillActive && (
            <div style={{marginBottom:12,padding:"8px 14px",background:"rgba(99,102,241,0.06)",
              border:"1px solid rgba(99,102,241,0.25)",borderRadius:8,
              display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:12,color:"#6366F1",fontWeight:600}}>🔍 Chart filter active:</span>
                {Object.entries(holdingsDrillFilter).map(([k,v])=>(
                  <span key={k} style={{fontSize:11,background:"rgba(99,102,241,0.12)",color:"#6366F1",
                    border:"1px solid rgba(99,102,241,0.3)",borderRadius:5,padding:"2px 8px",
                    display:"flex",alignItems:"center",gap:4}}>
                    <span style={{textTransform:"capitalize"}}>{k}</span>: <strong>{v}</strong>
                    <span onClick={()=>setDrill(k,v)} style={{cursor:"pointer",opacity:0.6,marginLeft:2}}>✕</span>
                  </span>
                ))}
                <span style={{fontSize:11,color:"#6366F1"}}>· All cards showing filtered data</span>
              </div>
              <button onClick={clearDrillFilter}
                style={{fontSize:11,color:"#6366F1",background:"rgba(99,102,241,0.1)",
                  border:"1px solid rgba(99,102,241,0.3)",borderRadius:6,
                  padding:"3px 10px",cursor:"pointer",fontWeight:600,flexShrink:0}}>
                Clear all filters
              </button>
            </div>
          )}

          {/* Portfolio Health Score */}
          <PortfolioHealthScore
            filteredHW={kpiHW}
            filtTotalUSD={kpiTotalUSD}
            fxRates={fxRates}
            dc={dc}
            transactions={transactions}
            isMobile={isMobile}
            T={T}
            tCard={tCard}
            card={card}
            fmt={fmt}
            convert={convert}
            setTab={setTab}
            allowedRiskTabs={allowedRiskTabs}
            canSeeRisk={canSeeRisk}
          />

          {/* ══ ALLOCATION BREAKDOWN ══ */}
          {(function(){
            const rawClass = drillClassData.length ? drillClassData : filtClassData;
            const {merged: classData} = mergeForDisplay(rawClass);
            const classTotal = classData.reduce((s,d)=>s+d.value,0)||1;

            const ccy = {}; drillFilteredHW.forEach(h=>{ const c=h.currency||"USD"; ccy[c]=(ccy[c]||0)+(h.valueUSD||0); });
            const ccyTotal = Object.values(ccy).reduce((s,v)=>s+v,0)||1;
            const ccyData = collapseSmallSlices(Object.entries(ccy).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value));

            const byCust={}, byAdv={};
            drillFilteredHW.forEach(h=>{ const v=h.valueUSD||0; byCust[h.manager||"Self-Managed"]=(byCust[h.manager||"Self-Managed"]||0)+v; byAdv[h.advisor||"Forthius"]=(byAdv[h.advisor||"Forthius"]||0)+v; });
            const custData = collapseSmallSlices(Object.entries(byCust).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value));
            const advData  = collapseSmallSlices(Object.entries(byAdv).map(([l,v])=>({label:l,value:v})).sort((a,b)=>b.value-a.value));

            const CAP_ORDER=["Giant","Mega","Large","Mid","Small","Micro"];
            const CAP_COLS={Giant:"#F59E0B",Mega:"#F97316",Large:"#22C55E",Mid:"#8B5CF6",Small:"#F43F5E",Micro:"#3B82F6"};
            const CAP_BG={Giant:"#FFF7ED",Mega:"#FFF4ED",Large:"#F0FDF4",Mid:"#F5F3FF",Small:"#FFF1F2",Micro:"#EFF6FF"};
            const CAP_TXT={Giant:"#92400E",Mega:"#9A3412",Large:"#166534",Mid:"#4C1D95",Small:"#9F1239",Micro:"#1E3A5F"};
            const capB={}; CAP_ORDER.forEach(s2=>capB[s2]=0);
            filteredHW.forEach(h=>{
              if(h.isMutualFund&&h.mfLookthrough){(h.mfLookthrough||[]).forEach(lt=>{if(CAP_ORDER.includes(lt.capSize))capB[lt.capSize]+=(lt.weight/100)*(h.valueUSD||0);});}
              else{const s2=resolveCapSize(h);if(CAP_ORDER.includes(s2))capB[s2]+=(h.valueUSD||0);}
            });
            const capTotal=Object.values(capB).reduce((s,v)=>s+v,0)||1;
            const capRows=CAP_ORDER.filter(s2=>capB[s2]>0).map(s2=>({s:s2,pct:capB[s2]/capTotal*100}));

            const GEO_C={"India":"#F97316","USA":"#3B82F6","United States":"#3B82F6","Singapore":"#EF4444","Hong Kong":"#06B6D4","UK":"#0EA5E9","France":"#8B5CF6","Germany":"#F59E0B","Japan":"#A32D2D","Australia":"#22C55E","Other":"#94A3B8"};
            const byGeo={}; drillFilteredHW.forEach(h=>{const c=h.country||"Other";byGeo[c]=(byGeo[c]||0)+(h.valueUSD||0);});
            const geoTotal=Object.values(byGeo).reduce((s,v)=>s+v,0)||1;
            const geoRows=Object.entries(byGeo).map(([c,v])=>({c,v,pct:v/geoTotal*100})).sort((a,b)=>b.v-a.v).slice(0,6);

            const SCOLS={"Technology":"#3B82F6","Financials":"#F59E0B","Energy":"#EF4444","Healthcare":"#22C55E","Consumer Staples":"#10B981","Consumer Discretionary":"#F97316","Real Estate":"#8B5CF6","Communication Services":"#0EA5E9","Industrials":"#92400E","Materials":"#65A30D","Utilities":"#6366F1","Diversified":"#78716C","Mutual Funds":"#84CC16","Fixed Income":"#0F6E56","Other":"#94A3B8"};
            const secMap={}; drillFilteredHW.forEach(h=>{const s2=resolveHoldingSector(h)||"Other";secMap[s2]=(secMap[s2]||0)+(h.valueUSD||0);});
            const secTotal=Object.values(secMap).reduce((s,v)=>s+v,0)||1;
            const secRows=Object.entries(secMap).map(([s2,v])=>({s:s2,v,pct:v/secTotal*100})).sort((a,b)=>b.v-a.v);
            const secData=collapseSmallSlices(secRows.map(r=>({label:r.s,value:r.v})));

            const D = 175; // donut size
            const cS = {...card(), padding:"14px 16px"};
            const cT = {fontSize:12,fontWeight:700,color:T.textPrimary,marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"};

            // Tight legend: dot · name (flex:1) · pct right-aligned but close
            const Leg = ({items, total, cmap, dk, onCl}) => (
              <div style={{display:"flex",flexDirection:"column",gap:4,flex:1,minWidth:0}}>
                {items.slice(0,7).map((d,i)=>{
                  const col=cmap[d.label]||"#94A3B8";
                  const pct=(d.value/(total||1)*100).toFixed(1);
                  const shortLabel=d.label==="Indian Mutual Funds"?"Indian MFs":d.label==="Global Mutual Funds"?"Global MFs":d.label;
                  return (
                    <div key={d.label} onClick={()=>onCl&&d.label!=="Others"&&onCl(d.label)}
                      style={{display:"flex",alignItems:"center",gap:6,cursor:onCl&&d.label!=="Others"?"pointer":"default",
                        padding:"1px 4px",borderRadius:4,background:holdingsDrillFilter[dk]===d.label?`${col}15`:"transparent"}}>
                      <div style={{width:8,height:8,borderRadius:2,background:col,flexShrink:0}}/>
                      <span style={{fontSize:11,color:T.textSecondary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shortLabel}</span>
                      <span style={{fontSize:11,fontWeight:700,color:T.textPrimary,flexShrink:0,marginLeft:6}}>{pct}%</span>
                    </div>
                  );
                })}
                {items.find(d=>d.label==="Others")?._collapsed?.length>0&&(
                  <div style={{fontSize:9,color:T.textMuted,paddingLeft:14}}>
                    Others: {items.find(d=>d.label==="Others")._collapsed.slice(0,3).join(", ")}{items.find(d=>d.label==="Others")._collapsed.length>3?"…":""}
                  </div>
                )}
              </div>
            );

            return (
              <React.Fragment>
                <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",
                  letterSpacing:"0.10em",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:3,height:14,background:T.brand,borderRadius:2}}/>
                  Allocation Breakdown
                </div>

                {/* ── Row 1: 3 donut cards ── */}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,minmax(0,1fr))",gap:10,marginBottom:10}}>

                  <div style={cS}>
                    <div style={cT}>
                      <span>Asset class</span>
                      {holdingsDrillFilter.class&&<span onClick={()=>setDrill("class",holdingsDrillFilter.class)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕ {holdingsDrillFilter.class}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <DonutChart data={classData} colorMap={CLASS_COLORS} size={D} hideLegend
                        onSliceClick={l=>{if(l!=="Others"){setDrill("class",l);setTab("holdings");}}}
                        activeSlice={holdingsDrillFilter.class||null}/>
                      <Leg items={classData} total={classTotal} cmap={CLASS_COLORS} dk="class"
                        onCl={l=>{setDrill("class",l);setTab("holdings");}}/>
                    </div>
                  </div>

                  <div style={cS}>
                    <div style={cT}>
                      <span>Currency exposure</span>
                      {holdingsDrillFilter.currency&&<span onClick={()=>setDrill("currency",holdingsDrillFilter.currency)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕ {holdingsDrillFilter.currency}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <DonutChart data={ccyData} colorMap={CURRENCY_COLORS} size={D} hideLegend
                        onSliceClick={l=>{if(l!=="Others"){setDrill("currency",l);setTab("holdings");}}}
                        activeSlice={holdingsDrillFilter.currency||null}/>
                      <Leg items={ccyData} total={ccyTotal} cmap={CURRENCY_COLORS} dk="currency"
                        onCl={l=>{setDrill("currency",l);setTab("holdings");}}/>
                    </div>
                  </div>

                  <div style={cS}>
                    <div style={cT}>
                      <div style={{display:"flex",gap:2}}>
                        {[["manager","Custodian"],["advisor","Advisor"]].map(([v,l])=>(
                          <button key={v} onClick={()=>setAllocView(v)}
                            style={{fontSize:10,padding:"2px 9px",borderRadius:5,cursor:"pointer",fontWeight:600,
                              border:"none",background:allocView===v?T.brand:"transparent",color:allocView===v?"white":T.textMuted}}>
                            {l}
                          </button>
                        ))}
                      </div>
                      {allocView==="manager"&&holdingsDrillFilter.manager&&<span onClick={()=>setDrill("manager",holdingsDrillFilter.manager)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕</span>}
                      {allocView==="advisor"&&holdingsDrillFilter.advisor&&<span onClick={()=>setDrill("advisor",holdingsDrillFilter.advisor)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕</span>}
                    </div>
                    {(()=>{
                      const mRaw=allocView==="advisor"?advData:custData;
                      const mTotal=mRaw.reduce((s,d)=>s+d.value,0)||1;
                      return (
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <DonutChart data={mRaw} colorMap={MANAGER_COLORS} size={D} hideLegend
                            onSliceClick={l=>{if(l!=="Others"){allocView==="advisor"?setDrill("advisor",l):setDrill("manager",l);setTab("holdings");}}}
                            activeSlice={allocView==="advisor"?(holdingsDrillFilter.advisor||null):(holdingsDrillFilter.manager||null)}/>
                          <Leg items={mRaw} total={mTotal} cmap={MANAGER_COLORS}
                            dk={allocView==="advisor"?"advisor":"manager"}
                            onCl={l=>{allocView==="advisor"?setDrill("advisor",l):setDrill("manager",l);setTab("holdings");}}/>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* ── Row 2: Market Cap · Geography · Sector ── */}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,minmax(0,1fr))",gap:10,marginBottom:12}}>

                  {/* Market Cap */}
                  <div style={cS}>
                    <div style={cT}><span>Market cap</span></div>
                    {capRows.length===0 ? <div style={{fontSize:11,color:T.textMuted}}>Set cap sizes on holdings to populate.</div> : (
                      <div style={{display:"flex",flexDirection:"column",gap:0}}>
                        {/* Compact tiles row */}
                        <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
                          {capRows.map(({s,pct})=>(
                            <div key={s} style={{background:CAP_BG[s],borderRadius:6,padding:"5px 8px",flex:"1 1 40px",textAlign:"center"}}>
                              <div style={{fontSize:15,fontWeight:700,color:CAP_TXT[s]}}>{pct.toFixed(1)}%</div>
                              <div style={{fontSize:9,color:CAP_TXT[s],marginTop:1}}>{s}</div>
                            </div>
                          ))}
                        </div>
                        {/* Bars */}
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {capRows.map(({s,pct})=>(
                            <div key={s}>
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:2,alignItems:"center"}}>
                                <span style={{fontSize:11,color:T.textSecondary}}>{s}</span>
                                <span style={{fontSize:11,fontWeight:700,color:CAP_TXT[s]}}>{pct.toFixed(1)}%</span>
                              </div>
                              <div style={{height:4,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                                <div style={{height:"100%",background:CAP_COLS[s],borderRadius:2,width:`${pct}%`,transition:"width 0.5s"}}/>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Geography */}
                  <div style={cS}>
                    <div style={cT}>
                      <span>Geography</span>
                      {holdingsDrillFilter.country&&<span onClick={()=>setDrill("country",holdingsDrillFilter.country)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕ {holdingsDrillFilter.country}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <div style={{flexShrink:0}}>
                        <DonutChart data={geoRows.map(r=>({label:r.c,value:r.v}))}
                          colorMap={Object.fromEntries(geoRows.map(r=>[r.c,GEO_C[r.c]||"#94A3B8"]))}
                          onSliceClick={c=>{setDrill("country",c);setTab("holdings");}}
                          activeSlice={holdingsDrillFilter.country||null}
                          size={D} hideLegend/>
                        <div style={{fontSize:9,color:T.textMuted,textAlign:"center",marginTop:2}}>{fmt(convert(geoTotal,dc),dc,true)}</div>
                      </div>
                      <div style={{flex:1,display:"flex",flexDirection:"column",gap:5,minWidth:0}}>
                        {geoRows.map(r=>(
                          <div key={r.c} onClick={()=>{setDrill("country",r.c);setTab("holdings");}}
                            style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"1px 4px",borderRadius:4,
                              background:holdingsDrillFilter.country===r.c?`${GEO_C[r.c]||"#94A3B8"}15`:"transparent"}}>
                            <span style={{fontSize:13,width:18,flexShrink:0,textAlign:"center"}}>{COUNTRY_FLAG[r.c]||"🌐"}</span>
                            <span style={{fontSize:11,color:T.textSecondary,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.c}</span>
                            <span style={{fontSize:11,fontWeight:700,color:GEO_C[r.c]||T.textPrimary,flexShrink:0,marginLeft:4}}>{r.pct.toFixed(1)}%</span>
                          </div>
                        ))}
                        <div style={{fontSize:9,color:T.textMuted,marginTop:2}}>{geoRows.length} countries · click to filter</div>
                      </div>
                    </div>
                  </div>

                  {/* Sector */}
                  <div style={cS}>
                    <div style={cT}>
                      <span>Sector breakdown</span>
                      {holdingsDrillFilter.sector&&<span onClick={()=>setDrill("sector",holdingsDrillFilter.sector)} style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:400}}>✕ {holdingsDrillFilter.sector}</span>}
                    </div>
                    {secRows.length===0 ? <div style={{fontSize:11,color:T.textMuted}}>No sector data.</div> : (
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <DonutChart data={secData} colorMap={SCOLS} size={D} hideLegend
                          onSliceClick={s2=>{if(s2!=="Others"){setDrill("sector",s2);setTab("holdings");}}}
                          activeSlice={holdingsDrillFilter.sector||null}/>
                        <div style={{flex:1,display:"flex",flexDirection:"column",gap:4,minWidth:0}}>
                          {secRows.slice(0,8).map(r=>(
                            <div key={r.s} onClick={()=>{setDrill("sector",r.s);setTab("holdings");}}
                              style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"1px 4px",borderRadius:4,
                                background:holdingsDrillFilter.sector===r.s?`${SCOLS[r.s]||"#94A3B8"}15`:"transparent"}}>
                              <div style={{width:7,height:7,borderRadius:2,background:SCOLS[r.s]||"#94A3B8",flexShrink:0}}/>
                              <span style={{fontSize:11,color:T.textSecondary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.s}</span>
                              <span style={{fontSize:11,fontWeight:700,color:T.textPrimary,flexShrink:0,marginLeft:4}}>{r.pct.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </React.Fragment>
            );
          })()}
          </React.Fragment>
}
        </React.Fragment>
}

        {tab==="holdings" && <React.Fragment>
          {/* ── Unassigned holdings banner (admin only) ── */}
          {isAdmin && (function(){
            const unassigned = hwWithOwner.filter(h => !h.clientId);
            if (!unassigned.length) return null;
            return (
              <div style={{marginBottom:12,padding:"10px 14px",
                background:"rgba(217,119,6,0.07)",border:"1px solid rgba(217,119,6,0.3)",
                borderRadius:8,display:"flex",alignItems:"center",justifyContent:"space-between",
                gap:8,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>⚠️</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#D97706"}}>
                      {unassigned.length} unassigned holding{unassigned.length!==1?"s":""} need an owner
                    </div>
                    <div style={{fontSize:11,color:T.textSecondary}}>
                      Use the Owner column dropdown to assign each holding to a client
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
                  {/* Bulk assign all unassigned to a client */}
                  <select
                    defaultValue=""
                    onChange={async e=>{
                      const clientId = e.target.value;
                      if (!clientId) return;
                      const unassignedIds = holdings.filter(h=>!holdingOwners[String(h.id)]);
                      let ok=0;
                      for (const h of unassignedIds) {
                        try {
                          await setHoldingOwner(String(h.id), clientId);
                          setHoldingOwners(p=>({...p,[String(h.id)]:clientId}));
                          ok++;
                        } catch(err) {
                          console.error("[Bulk assign]",h.id,err.message);
                        }
                      }
                      const cliName = visibleClients.find(c=>c.id===clientId)?.name||clientId;
                      logAudit("BULK_ASSIGN_OWNER",`Assigned ${ok} unassigned to ${cliName}`);
                      showToast(`✓ Assigned ${ok} holding${ok!==1?"s":""} to ${cliName}`,"#1D9E75");
                      e.target.value="";
                    }}
                    style={{fontSize:11,padding:"5px 10px",borderRadius:6,
                      border:"1px solid rgba(217,119,6,0.4)",background:"rgba(217,119,6,0.08)",
                      color:"#D97706",cursor:"pointer",fontWeight:600}}>
                    <option value="">⚡ Assign all to…</option>
                    {visibleFamilies.map(fam=>(
                      <optgroup key={fam.id} label={fam.emoji+" "+fam.name}>
                        {visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={()=>{ setSelectedFamily("all"); setSelectedClients(new Set()); }}
                    style={{fontSize:11,background:"rgba(217,119,6,0.1)",border:"1px solid rgba(217,119,6,0.3)",
                      borderRadius:6,color:"#D97706",padding:"5px 12px",cursor:"pointer",fontWeight:600}}>
                    Show all holdings
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Context header — changes based on active filter */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div>
              <div style={{fontSize:17,fontWeight:700}}>
                {viewAsClientId
                  ? `${clients.find(c=>c.id===viewAsClientId)?.name||"Client"}'s Holdings`
                  : selectedClients.size === 1
                  ? `${visibleClients.find(c=>c.id===[...selectedClients][0])?.name}'s Holdings`
                  : selectedClients.size > 1
                    ? `${selectedClients.size} Members — Combined Holdings`
                    : selectedFamily !== "all"
                      ? `${visibleFamilies.find(f=>f.id===selectedFamily)?.name} — All Holdings`
                      : `Holdings`}
                <span style={{marginLeft:10,fontSize:14,fontWeight:400,color:T.textSecondary}}>
                  {selectedFamily==="all" && selectedClients.size === 0
                    ? (isAdmin ? `(${filteredHW.length})` : `(Top 10 of ${filteredHW.length})`)
                    : `(${filteredHW.length})`}
                </span>
              </div>
              {(selectedClients.size > 0 || selectedFamily !== "all" || !!viewAsClientId) && (
                <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                  <button onClick={()=>{ setSelectedFamily("all"); setSelectedClients(new Set()); }} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:11,padding:0,textDecoration:"underline"}}>Show all</button>
                </div>
              )}
            </div>
            {(isAdmin || loggedInClientId) && <button onClick={()=>{ const preClient = loggedInClientId ? loggedInClientId : (selectedClients.size===1 ? [...selectedClients][0] : (selectedFamily!=="all" && selectedClients.size===0 ? (visibleClients.find(c=>c.familyId===selectedFamily)?.id||"") : "")); setNh(p=>({...p,assignedClientId:preClient})); setAddOpen(!addOpen); }} style={{background:"#1E293B",border:"none",borderRadius:8,color:"white",padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Add Holding</button>}
          </div>

          {/* ── Minimal data freshness line ── */}
          <div style={{display:"flex",alignItems:"center",gap:12,
            marginBottom:12,fontSize:11,color:T.textMuted}}>
            <span>Prices current</span>
            <span>·</span>
            <span title={`Last refreshed: ${lastUpd.toLocaleTimeString()}\nEquity prices: ~15 min delay\nMF NAV: T+1 (previous day)\nFX: ${fxSource==="static"?"indicative":"live"}`}
              style={{cursor:"help"}}>
              {lastUpd.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
            </span>
            {isAdmin && (
              <button onClick={reloadFromSupabase} disabled={dbLoading}
                title="Force-reload all portfolio data from server"
                style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,
                  borderRadius:4,cursor:dbLoading?"not-allowed":"pointer",
                  color:T.textSecondary,fontSize:10,padding:"2px 8px"}}>
                {dbLoading ? "Syncing…" : "Sync"}
              </button>
            )}
          </div>

          {/* Multi-select client switcher when a family is selected */}
          {selectedFamily !== "all" && (function(){
            const fam = familyStats.find(f=>f.id===selectedFamily);
            if (!fam) return null;
            const allSelected = selectedClients.size === 0;
            const toggleClient = (cid) => {
              setSelectedClients(prev => {
                const next = new Set(prev);
                if (next.has(cid)) { next.delete(cid); } else { next.add(cid); }
                return next;
              });
            };
            return (
              <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:T.textMuted,flexShrink:0}}>{fam.name}:</span>
                {/* All Members pill */}
                <button
                  onClick={()=>setSelectedClients(new Set())}
                  style={{padding:"4px 12px",borderRadius:20,border:allSelected?`1px solid ${T.textSecondary}`:`1px solid ${T.borderSubtle}`,background:allSelected?T.surfaceBg:"transparent",color:allSelected?T.textPrimary:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:allSelected?500:400,transition:"all 0.15s"}}>
                  All
                </button>
                {/* Per-member toggle pills */}
                {fam.clients.map(c=>{
                  const isOn = selectedClients.has(c.id);
                  return (
                    <button key={c.id}
                      onClick={()=>toggleClient(c.id)}
                      style={{padding:"4px 12px",borderRadius:20,border:isOn?`1px solid ${T.textSecondary}`:`1px solid ${T.borderSubtle}`,background:isOn?T.surfaceBg:"transparent",color:isOn?T.textPrimary:T.textMuted,cursor:"pointer",fontSize:11,fontWeight:isOn?500:400,display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}}>
                      {c.name}
                      <span style={{fontSize:10,opacity:0.6,fontFamily:"'Inter', system-ui, sans-serif"}}>
                        {fmt(convert(hwWithOwner.filter(h=>h.clientId===c.id).reduce((s,h)=>s+h.valueUSD,0),dc),dc,true)}
                      </span>
                    </button>
                  );
                })}
                {selectedClients.size > 1 && (
                  <span style={{fontSize:10,color:T.textMuted,cursor:"pointer"}} onClick={()=>setSelectedClients(new Set())}>clear</span>
                )}
              </div>
            );
          })()}

          {addOpen && (
            <div style={{...tCard(),marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{fontSize:13,fontWeight:600}}>New Holding</div>
                {Object.keys(nhErrors).length > 0 && (
                  <div style={{fontSize:11,color:"#A32D2D",fontWeight:600,
                    background:"rgba(220,38,38,0.08)",padding:"3px 10px",
                    borderRadius:6,border:"1px solid rgba(220,38,38,0.2)"}}>
                    ⚠ {Object.keys(nhErrors).filter(k=>nhErrors[k]).length} field{Object.keys(nhErrors).filter(k=>nhErrors[k]).length!==1?"s":""} need attention
                  </div>
                )}
              </div>
              <div style={{fontSize:11,color:T.textSecondary,marginBottom:12}}>
                {isValueOnlyClass(nh.class)
                  ? `💰 ${nh.class} — enter Invested amount and current Market Value. No live price tracking.`
                  : "Current price will auto-fetch from live APIs after saving."}
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10,marginBottom:12}}>
                {(isValueOnlyClass(nh.class)
                  ? [["name","Asset / Description"],["costPrice","Invested Amount"],["currentPrice","Current Market Value"]]
                  : [["name","Asset / Bank Name"],["qty","Quantity / Units"],["costPrice","Cost Price"]]
                ).map(([k,l])=>(
                  <div key={k}>
                    <div style={{fontSize:11,color:nhErrors[k]?"#A32D2D":T.textSecondary,marginBottom:4,fontWeight:nhErrors[k]?600:400}}>{l}{nhErrors[k]&&<span style={{marginLeft:4,fontSize:10}}>*</span>}</div>
                    <input
                      style={{...inp(), borderColor: nhErrors[k] ? "#A32D2D" : undefined,
                        boxShadow: nhErrors[k] ? "0 0 0 2px rgba(220,38,38,0.15)" : undefined }}
                      value={nh[k]}
                      onChange={e=>{
                        setNh(p=>({...p,[k]:e.target.value}));
                        if(nhErrors[k]) setNhErrors(p=>({...p,[k]:undefined}));
                      }}
                      placeholder={l}/>
                    {nhErrors[k] && <div style={{fontSize:10,color:"#A32D2D",marginTop:3}}>⚠ {nhErrors[k]}</div>}
                  </div>
                ))}

                {/* ── Ticker with live search (listed assets only) ── */}
                {!isValueOnlyClass(nh.class) && (nh.listed || nh.class === "Global Mutual Funds" || nh.class === "Indian Mutual Funds") && <div style={{position:"relative",order:-1}}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                    Ticker
                    {nh.ticker && <span style={{marginLeft:6,color:"#1D9E75",fontWeight:600}}>✓ {nh.ticker}</span>}
                  </div>
                  <input
                    style={{...inp(), paddingRight: nhTickerLoading ? 28 : undefined}}
                    value={nhTickerQuery}
                    placeholder={nh.ticker ? nh.ticker : "Search by name or ticker (e.g. LVMH, MC.PA, MC FP)"}
                    onFocus={()=>{ if(nhTickerQuery.length>=2 && nh.class !== "Indian Mutual Funds") setNhTickerOpen(true); }}
                    onBlur={()=>setTimeout(()=>setNhTickerOpen(false),200)}
                    onChange={e=>{
                      const q = e.target.value;
                      setNhTickerQuery(q);
                      // For Indian MFs: store name directly as ticker, never open dropdown
                      if (nh.class === "Indian Mutual Funds") {
                        setNh(p=>({...p, ticker: q}));
                        setNhTickerResults([]); setNhTickerOpen(false); setNhTickerLoading(false);
                      } else {
                        setNh(p=>({...p,ticker:""}));
                        if(q.length < 2){ setNhTickerResults([]); setNhTickerOpen(false); setNhTickerLoading(false); }
                      }
                    }}
                  />
                  {nhTickerLoading && nh.class !== "Indian Mutual Funds" && (
                    <div style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:10,color:T.textSecondary}}>⏳</div>
                  )}
                  {nhTickerOpen && nhTickerQuery.length>=2 && nh.class !== "Indian Mutual Funds" && (
                    <div style={{position:"absolute",zIndex:300,top:"calc(100% + 2px)",left:0,right:0,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.18)",maxHeight:180,overflowY:"auto"}}>
                      {nhTickerResults.length===0 && !nhTickerLoading && (
                        <div style={{padding:"12px 14px",fontSize:12,color:T.textMuted}}>
                          {CONFIG.twelveDataKey || CONFIG.alphaVantageKey ? (
                            <React.Fragment>
                              <div style={{fontWeight:600,color:T.textSecondary,marginBottom:6}}>No results found</div>
                              <div style={{lineHeight:1.6}}>
                                Tips: Use Yahoo Finance format — <span style={{fontFamily:"monospace",color:T.textPrimary}}>MC.PA</span> for LVMH,{" "}
                                <span style={{fontFamily:"monospace",color:T.textPrimary}}>VOW.DE</span> for Volkswagen.
                                Bloomberg suffixes like <span style={{fontFamily:"monospace",color:T.textPrimary}}>MC FP</span> are auto-converted.
                                Also try selecting <strong>France</strong> as country for Euronext stocks.
                              </div>
                            </React.Fragment>
                          ) : "Add API keys in Data Sources tab to enable live search"}
                        </div>
                      )}
                      {nhTickerResults.map((r,i)=>(
                        <div
                          key={r.symbol+i}
                          onMouseDown={async ()=>{
                            // Clean Finnhub-style symbols: RHM.DEX→RHM.DE, NESN.SWX→NESN.SW etc
                            const rawSymbol = r.symbol;
                            const cleanSymbol = rawSymbol
                              .replace(/\.DEX$/,  ".DE")
                              .replace(/\.SWX$/,  ".SW")
                              .replace(/\.PAX$/,  ".PA")
                              .replace(/\.ASX$/,  ".AS")
                              .replace(/\.LON$/,  ".L")
                              .replace(/\.LSE$/,  ".L")
                              .replace(/\.MIX$/,  ".MI")
                              .replace(/\.MCX$/,  ".MC")
                              .replace(/\.STX$/,  ".ST")
                              .replace(/\.HEX$/,  ".HE")
                              .replace(/\.OSX$/,  ".OL")
                              .replace(/\.CPX$/,  ".CO")
                              .replace(/\.VIX$/,  ".VI")
                              .replace(/\.ATX$/,  ".AT")
                              .replace(/\.ISX$/,  ".IS");
                            if (r.isMutualFund) {
                              // ── Indian Mutual Fund: use AMFI scheme code as ticker ──
                              const amfiCode = String(r.amfiCode || r.symbol);
                              setNh(p=>({...p,
                                ticker:       amfiCode,
                                schemeCode:   amfiCode,    // explicit schemeCode for NAV fetch
                                name:         p.name || r.name,
                                exchange:     "AMFI",
                                currency:     "INR",
                                country:      "India",
                                class:        "Indian Mutual Funds",
                                isMutualFund: true,
                                listed:       true,
                                currentPrice: "…",
                              }));
                              setNhTickerQuery(r.name || amfiCode);
                              setNhTickerOpen(false);
                              // Fetch latest NAV via /api/price proxy (no CORS issues)
                              try {
                                const navRes = await fetch(`/api/price?symbol=${encodeURIComponent(amfiCode)}&type=mf`);
                                const navJson = await navRes.json();
                                const nav = navJson?.price;
                                if (nav && nav > 0) setNh(p=>({...p, currentPrice: String(nav)}));
                                else setNh(p=>({...p, currentPrice: ""}));
                              } catch(e) { setNh(p=>({...p, currentPrice: ""})); }
                            } else if (r.isGlobalMF) {
                              // ── Global Mutual Fund ──
                              setNh(p=>({...p,
                                ticker:       r.isin || "-",
                                name:         p.name || r.name,
                                exchange:     r.exchange || "UCITS",
                                currency:     r.currency || "USD",
                                class:        "Global Mutual Funds",
                                isMutualFund: false,  // not an Indian MF — use UCITS NAV path
                                isGlobalMF:   true,
                                listed:       true,   // allow price refresh via ISIN lookup
                                isin:         r.isin || "",
                                manager:      r.manager || p.manager || "",
                                sector:       r.sector || p.sector || "",
                                yahooTicker:  r.yahooTicker || p.yahooTicker || "",
                                currentPrice: "",  // NAV entered manually for UCITS funds
                              }));
                              setNhTickerQuery(r.name);
                              setNhTickerOpen(false);
                            } else {
                              // ── Regular stock / ETF ──
                              setNh(p=>({...p,
                                ticker: cleanSymbol,
                                name: p.name || r.name,
                                exchange: r.exchange||p.exchange||"",
                                currency: r.currency||p.currency||"USD",
                                currentPrice: "…",
                                capSize: TICKER_CAP[cleanSymbol] || p.capSize || "N/A",
                              }));
                              setNhTickerQuery(cleanSymbol);
                              setNhTickerOpen(false);
                              // Fetch live price via proxy (no CORS issues)
                              try {
                                const params = new URLSearchParams({ symbol: cleanSymbol });
                                if (r.exchange) params.append("exchange", r.exchange);
                                if (nh.country) params.append("country", nh.country);
                                const res = await fetch(`/api/price?${params}`);
                                const j = await res.json();
                                setNh(p=>({...p, currentPrice: j.price ? String(j.price) : ""}));
                              } catch(e) {
                                setNh(p=>({...p, currentPrice: ""}));
                              }
                            }
                          }}
                          onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                          onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":T.tableStripe}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",cursor:"pointer",background:i%2===0?"transparent":T.tableStripe,borderBottom:i<nhTickerResults.length-1?`1px solid ${T.borderSubtle}`:"none"}}
                        >
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              {r.isGlobalMF ? (
                                <React.Fragment>
                                  <span style={{fontSize:10,background:"rgba(124,58,237,0.12)",color:"#7C3AED",borderRadius:4,padding:"1px 6px",fontWeight:600}}>{r.manager || "Fund"}</span>
                                  <span style={{fontSize:10,background:T.borderSubtle,color:T.textSecondary,borderRadius:4,padding:"1px 5px"}}>{r.exchange || "UCITS"}</span>
                                  <span style={{fontSize:10,color:T.textMuted}}>Global MF</span>
                                </React.Fragment>
                              ) : r.isMutualFund ? (
                                <React.Fragment>
                                  <span style={{fontSize:10,background:"rgba(14,165,233,0.12)",color:"#0EA5E9",borderRadius:4,padding:"1px 6px",fontWeight:600}}>{r.house || "MF"}</span>
                                  {r.exchange && <span style={{fontSize:10,background:T.borderSubtle,color:T.textSecondary,borderRadius:4,padding:"1px 5px"}}>{r.exchange}</span>}
                                  <span style={{fontSize:10,color:T.textMuted}}>Mutual Fund</span>
                                </React.Fragment>
                              ) : (
                                <React.Fragment>
                                  <span style={{fontWeight:700,fontSize:13,fontFamily:"'Inter', system-ui, sans-serif",color:T.accent}}>{r.symbol}</span>
                                  {r.exchange && <span style={{fontSize:10,background:T.borderSubtle,color:T.textSecondary,borderRadius:4,padding:"1px 5px"}}>{r.exchange}</span>}
                                  {r.type && <span style={{fontSize:10,color:T.textMuted}}>{r.type}</span>}
                                </React.Fragment>
                              )}
                            </div>
                            <div style={{fontSize:r.isGlobalMF||r.isMutualFund?12:11,fontWeight:r.isGlobalMF||r.isMutualFund?500:400,color:T.textPrimary,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
                            {r.isGlobalMF && r.isin && <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>ISIN: {r.isin}{r.sector ? ` · ${r.sector}` : ""}</div>}
                            {r.isMutualFund && !r.isGlobalMF && <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>AMFI: {r.amfiCode}</div>}
                          </div>
                          <div style={{fontSize:11,color:"#1D9E75",fontWeight:600,flexShrink:0}}>{r.currency}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>}
                {/* Yahoo Finance Ticker — only for Global Mutual Funds, optional hint for NAV fetch */}
                {nh.class === "Global Mutual Funds" && <div style={{order:-1}}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                    Yahoo Ticker
                    <span style={{color:T.textMuted,marginLeft:6,fontSize:10,fontWeight:400}}>
                      optional · for live NAV (e.g. 0P0001ANC2.SI)
                    </span>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <input
                      style={{...inp(),flex:1}}
                      value={nh.yahooTicker || ""}
                      placeholder="e.g. 0P0001ANC2.SI — find on finance.yahoo.com"
                      onChange={e=>setNh(p=>({...p, yahooTicker: e.target.value.trim()}))}
                    />
                    {nh.yahooTicker && (
                      <a href={"https://finance.yahoo.com/quote/"+encodeURIComponent(nh.yahooTicker)}
                        target="_blank" rel="noopener noreferrer"
                        style={{fontSize:11,color:"#2563EB",whiteSpace:"nowrap",textDecoration:"none"}}>
                        Verify ↗
                      </a>
                    )}
                  </div>
                  <div style={{fontSize:10,color:T.textMuted,marginTop:4,lineHeight:1.5}}>
                    If the fund isn't in the dropdown above, search its ISIN on{" "}
                    <a href="https://finance.yahoo.com" target="_blank" rel="noopener noreferrer" style={{color:"#2563EB"}}>finance.yahoo.com</a>{" "}
                    to find the <strong>0P…</strong> identifier. SGD-H classes usually use <strong>.SI</strong>, USD classes may use <strong>.F</strong>.
                  </div>
                </div>}
                <div style={{position:"relative",order:-2}}>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em",
                    color:nhErrors.country?"#A32D2D":T.textSecondary}}>
                    Country {!isValueOnlyClass(nh.class)&&<span style={{color:"#A32D2D"}}>*</span>}
                  </div>
                  <div style={{position:"relative"}}>
                    <input
                      style={{...inp(), paddingRight:28,
                        borderColor:nhErrors.country?"#A32D2D":undefined,
                        boxShadow:nhErrors.country?"0 0 0 2px rgba(220,38,38,0.15)":undefined}}
                      value={nhCountryQuery || nh.country}
                      placeholder="Type 3 letters to search…"
                      onFocus={()=>{ setNhCountryOpen(true); setNhCountryQuery(nh.country||""); }}
                      onBlur={()=>setTimeout(()=>setNhCountryOpen(false),150)}
                      onChange={e=>{ setNhCountryQuery(e.target.value); setNhCountryOpen(true); setNh(p=>({...p,country:""})); setNhErrors(p=>({...p,country:undefined})); }}
                    />
                    {nh.country && (
                      <span
                        onClick={()=>{ setNh(p=>({...p,country:""})); setNhCountryQuery(""); }}
                        style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",cursor:"pointer",color:T.textSecondary,fontSize:14,lineHeight:1}}>×</span>
                    )}
                  </div>
                  {nhCountryOpen && nhCountryQuery.length >= 1 && (function(){
                    const q = nhCountryQuery.toLowerCase();
                    const matches = COUNTRIES.filter(c=>c.toLowerCase().includes(q)).slice(0,8);
                    if (!matches.length) return (
                      <div style={{position:"absolute",zIndex:200,top:"100%",left:0,right:0,background:T.dropdownBg,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",fontSize:12,color:T.textMuted,marginTop:2}}>No countries match "{nhCountryQuery}"</div>
                    );
                    return (
                      <div style={{position:"absolute",zIndex:200,top:"100%",left:0,right:0,background:T.cardBg,border:"1px solid #2563EB",borderRadius:8,overflow:"hidden",marginTop:2,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                        {matches.map((c,i)=>{
                          const idx = c.toLowerCase().indexOf(q);
                          return (
                            <div
                              key={c}
                              onMouseDown={()=>{ 
                                const defaults = COUNTRY_EXCHANGE[c] || {};
                                setNh(p=>({
                                  ...p,
                                  country:c,
                                  currency: defaults.currency || p.currency || "USD",
                                  exchange: defaults.exchange || p.exchange || "NYSE"
                                })); 
                                setNhCountryQuery(""); 
                                setNhCountryOpen(false); 
                                setNhErrors(p=>({...p,country:undefined})); 
                              }}
                              style={{padding:"8px 12px",cursor:"pointer",fontSize:13,background:i%2===0?"transparent":T.tableStripe,borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",gap:8}}
                              onMouseEnter={e=>e.currentTarget.style.background=T.navActiveBg}
                              onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":T.tableStripe}
                            >
                              <span style={{fontSize:16}}>{COUNTRY_FLAG[c]||"🌐"}</span>
                              <span>
                                {c.slice(0,idx)}
                                <strong style={{color:"#2563EB"}}>{c.slice(idx,idx+q.length)}</strong>
                                {c.slice(idx+q.length)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {nh.country && (
                    <div style={{marginTop:4,fontSize:11,color:"#1D9E75",display:"flex",alignItems:"center",gap:5}}>
                      <span>{COUNTRY_FLAG[nh.country]||"🌐"}</span>
                      <span>{nh.country} selected</span>
                    </div>
                  )}
                </div>
                <div style={{order:-3}}>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em",
                    color:nhErrors.class?"#A32D2D":T.textSecondary}}>
                    Asset Class <span style={{color:"#A32D2D"}}>*</span>
                  </div>
                  <select style={{...inp(),borderColor:nhErrors.class?"#A32D2D":undefined,boxShadow:nhErrors.class?"0 0 0 2px rgba(220,38,38,0.15)":undefined}}
                    value={nh.class}
                    onChange={e=>{
                      setNh(p=>({...p,class:e.target.value,ticker:"",exchange:""}));
                      setNhErrors(p=>({...p,class:undefined}));
                      setNhTickerQuery(""); setNhTickerResults([]); setNhTickerOpen(false); setNhTickerLoading(false);
                    }}>
                    {ASSET_CLASSES.filter(a => a !== "Mutual Funds").map(a=><option key={a}>{a}</option>)}
                  </select>
                  {nhErrors.class&&<div style={{fontSize:10,color:"#A32D2D",marginTop:3}}>⚠ {nhErrors.class}</div>}
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em",
                    color:nhErrors.currency?"#A32D2D":T.textSecondary}}>
                    Currency <span style={{color:"#A32D2D"}}>*</span>
                  </div>
                  <div style={{position:"relative"}}>
                    <input
                      style={{...inp(),
                        borderColor:nhErrors.currency?"#A32D2D":undefined,
                        boxShadow:nhErrors.currency?"0 0 0 2px rgba(220,38,38,0.15)":undefined}}
                      value={nhCcQ || nh.currency}
                      placeholder="USD, EUR, INR…"
                      onFocus={()=>{ setNhCcQ(""); setNhCcOpen(true); }}
                      onBlur={()=>setTimeout(()=>setNhCcOpen(false),200)}
                      onChange={e=>{
                        const v = e.target.value.toUpperCase();
                        setNhCcQ(v);
                        if (CURRENCIES.includes(v)) {
                          setNh(p=>({...p,currency:v}));
                          setNhErrors(p=>({...p,currency:undefined}));
                          setNhCcOpen(false); setNhCcQ("");
                        }
                      }}
                    />
                    {nhCcOpen && (
                      <div style={{position:"absolute",zIndex:400,top:"calc(100% + 2px)",left:0,right:0,
                        background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                        boxShadow:"0 4px 16px rgba(0,0,0,0.12)",maxHeight:200,overflowY:"auto"}}>
                        {nhCcQ.length>=2 && nhCcQ.length<=5 && /^[A-Z]+$/.test(nhCcQ) && !CURRENCIES.includes(nhCcQ) && (
                          <div
                            onMouseDown={()=>{
                              setNh(p=>({...p,currency:nhCcQ}));
                              setNhErrors(p=>({...p,currency:undefined}));
                              setNhCcOpen(false); setNhCcQ("");
                            }}
                            style={{padding:"8px 12px",fontSize:12,cursor:"pointer",
                              background:"rgba(37,99,235,0.06)",
                              borderBottom:`1px solid ${T.borderSubtle}`,
                              display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:10,background:"rgba(37,99,235,0.12)",
                              color:"#2563EB",padding:"1px 6px",borderRadius:4,fontWeight:600}}>CUSTOM</span>
                            Use <strong style={{fontFamily:"monospace"}}>{nhCcQ}</strong> as currency
                          </div>
                        )}
                        {CURRENCIES.filter(c=>c.includes(nhCcQ)).length===0 && nhCcQ.length>0 && !(!CURRENCIES.includes(nhCcQ)&&nhCcQ.length>=2) && (
                          <div style={{padding:"10px 12px",fontSize:12,color:T.textMuted,textAlign:"center"}}>
                            No match — type a 3-letter code to add custom
                          </div>
                        )}
                        {CURRENCIES.filter(c=>c.includes(nhCcQ)).map(c=>(
                          <div key={c}
                            onMouseDown={()=>{
                              setNh(p=>({...p,currency:c}));
                              setNhErrors(p=>({...p,currency:undefined}));
                              setNhCcOpen(false); setNhCcQ("");
                            }}
                            style={{padding:"7px 12px",fontSize:12,cursor:"pointer",
                              background:nh.currency===c?"rgba(37,99,235,0.08)":"transparent",
                              fontWeight:nh.currency===c?600:400,
                              color:nh.currency===c?"#2563EB":T.textPrimary}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(0,0,0,0.04)"}
                            onMouseLeave={e=>e.currentTarget.style.background=nh.currency===c?"rgba(37,99,235,0.08)":"transparent"}>
                            <span style={{fontFamily:"monospace",fontWeight:600}}>{c}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {nhErrors.currency&&<div style={{fontSize:10,color:"#A32D2D",marginTop:3}}>⚠ {nhErrors.currency}</div>}
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Exchange</div><input style={inp()} value={nh.exchange||""} onChange={e=>setNh(p=>({...p,exchange:e.target.value}))} placeholder="e.g. NYSE, NSE, XETRA, LSE"/></div>
                <div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Custodian / Manager</div>
                  <input list="nh-manager-list" style={inp()} value={nh.manager||""} onChange={e=>setNh(p=>({...p,manager:e.target.value}))} placeholder="e.g. DBS, Self-Managed, HDFC AMC"/>
                  <datalist id="nh-manager-list">
                    {["Self-Managed","DBS","ICICI Securities","HDFC Securities","Kotak Securities","Motilal Oswal","360 ONE","Nuvama","IIFL","Zerodha","Phillip Capital","UBS","Citibank","HSBC","Others"].map(m=><option key={m} value={m}/>)}
                  </datalist>
                </div>
                <div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Advisor (optional)</div>
                  <input style={inp()} value={nh.advisor||""} onChange={e=>setNh(p=>({...p,advisor:e.target.value}))} placeholder="e.g. Forthius, Self-Advised"/>
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Sector</div><select style={inp()} value={nh.sector||""} onChange={e=>setNh(p=>({...p,sector:e.target.value}))}><option value="">— Select —</option>{["Technology","Financials","Energy","Consumer Staples","Consumer Discretionary","Real Estate","Fixed Income","Commodities","Digital Assets","Healthcare","Industrials","Utilities","Materials","Alternatives","Diversified","Other"].map(s=><option key={s}>{s}</option>)}</select></div>
                {!isValueOnlyClass(nh.class) && nh.listed && <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Market Cap</div><select style={inp()} value={nh.capSize||"N/A"} onChange={e=>setNh(p=>({...p,capSize:e.target.value}))}>{["Giant","Mega","Large","Mid","Small","Micro","N/A"].map(s=><option key={s}>{s}</option>)}</select></div>}
                {!isValueOnlyClass(nh.class) && <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Listed?</div><select style={inp()} value={nh.listed?"yes":"no"} onChange={e=>{ const listed=e.target.value==="yes"; setNh(p=>({...p,listed,ticker:listed?p.ticker:"",currentPrice:""})); if(!listed){setNhTickerQuery("");setNhTickerResults([]);setNhTickerOpen(false);} }}><option value="yes">Listed</option><option value="no">Unlisted</option></select></div>}
              </div>
              {/* ── Assign to Client ── */}
              {isAdmin ? (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Assign to Client <span style={{color:T.textMuted}}>(optional — required to appear under a family)</span></div>
                  <select style={inp()} value={nh.assignedClientId} onChange={e=>setNh(p=>({...p,assignedClientId:e.target.value}))}>
                    <option value="">— Unassigned —</option>
                    {visibleFamilies.map(fam=>(
                      <optgroup key={fam.id} label={`${fam.emoji} ${fam.name}`}>
                        {visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                          <option key={c.id} value={c.id}>{c.name} ({c.role})</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {nh.assignedClientId && (function(){
                    const cli = visibleClients.find(c=>c.id===nh.assignedClientId);
                    const fam = cli ? visibleFamilies.find(f=>f.id===(cli.familyId||cli.family_id)) : null;
                    return cli ? (
                      <div style={{marginTop:4,fontSize:11,color:"#1D9E75",display:"flex",alignItems:"center",gap:5}}>
                        <span>{fam?.emoji||"👤"}</span>
                        <span>Will be assigned to <strong>{cli.name}</strong> in {fam?.name||"unknown family"}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : loggedInClientId && (function(){
                const cli = visibleClients.find(c=>c.id===loggedInClientId);
                const fam = cli ? visibleFamilies.find(f=>f.id===(cli.familyId||cli.family_id)) : null;
                const famMembers = fam ? visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id) : [cli].filter(Boolean);
                const selectedMember = visibleClients.find(c=>c.id===nh.assignedClientId) || cli;
                return (
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                      Add holding for <span style={{color:T.textPrimary,fontWeight:600}}>{fam?.emoji} {fam?.name}</span> — choose member
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {famMembers.map(m=>{
                        const roleCol = ROLE_COLORS[m.role]||"#2563EB";
                        const isSelected = nh.assignedClientId===m.id;
                        return (
                          <button
                            key={m.id}
                            onMouseDown={()=>setNh(p=>({...p,assignedClientId:m.id}))}
                            style={{
                              display:"flex",alignItems:"center",gap:6,
                              padding:"6px 12px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:600,
                              border: isSelected ? `2px solid ${roleCol}` : "1px solid #DDD0C0",
                              background: isSelected ? `${roleCol}18` : T.cardBg,
                              color: isSelected ? roleCol : T.textSecondary,
                              transition:"all 0.15s"
                            }}
                          >
                            <span style={{width:22,height:22,borderRadius:6,background:`${roleCol}22`,border:`1px solid ${roleCol}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:roleCol}}>{m.avatar||(m.name||"?").slice(0,2).toUpperCase()}</span>
                            {m.name}
                            <span style={{fontSize:10,opacity:0.7}}>({m.role})</span>
                            {isSelected && <span style={{fontSize:11}}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                    {selectedMember && (
                      <div style={{marginTop:6,fontSize:11,color:"#1D9E75",display:"flex",alignItems:"center",gap:5}}>
                        <span>{fam?.emoji||"👤"}</span>
                        <span>Will be added to <strong>{selectedMember.name}</strong>'s portfolio</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Purchase Date — optional; when set with qty + costPrice, auto-creates a BUY transaction */}
              {!isValueOnlyClass(nh.class) && (
                <div style={{marginBottom:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                      Purchase Date <span style={{opacity:0.6}}>(optional)</span>
                    </div>
                    <input type="date" style={inp()} value={nh.purchaseDate||""}
                      onChange={e=>setNh(p=>({...p,purchaseDate:e.target.value}))}/>
                    {nh.purchaseDate && (
                      <div style={{fontSize:10,color:"#1D9E75",marginTop:3}}>
                        ✓ Will also create a BUY transaction for this date
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!nh.listed && !isValueOnlyClass(nh.class) && <div style={{marginBottom:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div><div style={{fontSize:11,color:"#D97706",marginBottom:4}}>Manual Price (unlisted)</div><input style={inp()} value={nh.currentPrice} onChange={e=>setNh(p=>({...p,currentPrice:e.target.value}))} placeholder="Enter latest price"/></div>
                <div><div style={{fontSize:11,color:"#D97706",marginBottom:4}}>Last As On Date</div><input type="date" style={inp()} value={nh.manualPriceDate} onChange={e=>setNh(p=>({...p,manualPriceDate:e.target.value}))}/></div>
              </div>}
              {isValueOnlyClass(nh.class) && <div style={{marginBottom:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Valuation Date <span style={{opacity:0.6}}>(optional)</span></div><input type="date" style={inp()} value={nh.manualPriceDate} onChange={e=>setNh(p=>({...p,manualPriceDate:e.target.value}))}/></div>
              </div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{
                  // ── Validate ──────────────────────────────────────
                  const errs = {};
                  const isValueOnly = isValueOnlyClass(nh.class);
                  if (!(nh.name||"").trim())             errs.name      = "Asset name is required";
                  const parsedQty  = parseFloat(nh.qty);
                  const parsedCost = parseFloat(nh.costPrice);
                  const parsedCurr = parseFloat(nh.currentPrice);
                  if (isValueOnly) {
                    // Need at least Invested or Current Value
                    const hasCost = nh.costPrice !== "" && !isNaN(parsedCost) && parsedCost > 0;
                    const hasCurr = nh.currentPrice !== "" && !isNaN(parsedCurr) && parsedCurr > 0;
                    if (!hasCost && !hasCurr) {
                      errs.costPrice = "Enter Invested Amount or Current Market Value";
                    }
                    if (nh.costPrice !== "" && !isNaN(parsedCost) && parsedCost < 0)
                                                         errs.costPrice = "Invested cannot be negative";
                    if (nh.currentPrice !== "" && !isNaN(parsedCurr) && parsedCurr < 0)
                                                         errs.currentPrice = "Current Value cannot be negative";
                  } else {
                    // Securities path — qty required
                    if (!nh.qty || isNaN(parsedQty) || parsedQty <= 0)
                                                         errs.qty       = "Quantity must be greater than 0";
                    if (nh.costPrice !== "" && !isNaN(parsedCost) && parsedCost < 0)
                                                         errs.costPrice = "Cost price cannot be negative";
                  }
                  if (!nh.currency)                      errs.currency  = "Currency is required";
                  if (!nh.class)                         errs.class     = "Asset class is required";
                  if (!nh.country?.trim() && !isValueOnly)
                                                         errs.country   = "Country is required";
                  if (!nh.listed && !nh.manualPriceDate && !isValueOnly)
                                                         errs.manualPriceDate = "Date required for unlisted assets";
                  setNhErrors(errs);
                  if (Object.keys(errs).length > 0) {
                    showToast("⚠ Please fix the errors highlighted below", "#D97706", 4000);
                    return;
                  }
                  // ── Build holding object ──────────────────────────
                  // Value-only assets are stored with qty=1 so cost/value math
                  // (qty × price) just works elsewhere in the app. The form
                  // never asks for qty so this is invisible to the user.
                  // costPrice = invested amount, currentPrice = current value.
                  // If only one is supplied, the other defaults to it (so a
                  // user who only enters Current Value sees zero gain, not loss).
                  const isCash = nh.class==="Cash / Bank Balance"; // backwards compat
                  const resolvedExchange = isValueOnly ? (isCash ? "Bank" : "Manual") : (nh.exchange || (nh.ticker ? (COUNTRY_EXCHANGE[nh.country]?.exchange || "NYSE") : "Manual"));
                  const finalTicker = (nh.ticker||"").trim();
                  let h;
                  if (isValueOnly) {
                    const investedVal = parsedCost > 0 ? parsedCost : 0;
                    const currentVal  = parsedCurr > 0 ? parsedCurr : investedVal; // fall back to invested if not specified
                    const finalInvested = investedVal > 0 ? investedVal : currentVal; // fall back the other way too
                    h = {...nh, id:uid(), ticker:"", qty:1,
                      costPrice: finalInvested, currentPrice: currentVal,
                      listed:false, exchange:resolvedExchange,
                      isin:nh.isin||"-", manualPriceDate:nh.manualPriceDate||null,
                      manager:nh.manager||"Self-Managed", advisor:nh.advisor||null};
                  } else {
                    h = {...nh, id:uid(), ticker:finalTicker, qty:parsedQty,
                      costPrice:parsedCost||0,
                      currentPrice: isCash ? parsedCost||0 : 0,
                      listed: isCash ? false : nh.listed,
                      exchange:resolvedExchange,
                      isin:nh.isin||"-", manualPriceDate:nh.manualPriceDate||null,
                      manager:nh.manager||"Self-Managed", advisor:nh.advisor||null,
                      // qty here is authoritative; the auto BUY txn created below from the
                      // purchase date must not be added on top by the hw memo (2x bug).
                      reconciledFromTxns: !!nh.purchaseDate};
                  }
                  if(true){
                    // Save to Supabase FIRST, only update local state on success
                    showToast("⏳ Saving holding…", "#2563EB");
                    addHolding(h).then(async () => {
                      // Owner assignment
                      if (h.assignedClientId) {
                        try { await setHoldingOwner(h.id, h.assignedClientId); }
                        catch(err) { console.error("[Supabase] setHoldingOwner:", err.message); }
                      }
                      // Only update UI after Supabase confirms
                      setHoldings(p=>[...p,h]);
                      if(h.assignedClientId){setHoldingOwners(p=>({...p,[h.id]:h.assignedClientId}));}

                      // ── If a Purchase Date was provided, also create a BUY transaction ──
                      // so IRR + transaction history are populated from day one.
                      let txnCreated = false;
                      const wantsTxn = nh.purchaseDate && !isValueOnly &&
                        parsedQty > 0 && parsedCost >= 0 && h.assignedClientId && nh.currency;
                      if (wantsTxn) {
                        const newT = {
                          id:       `txn_${Date.now()}_${Math.floor(Math.random()*1000)}`,
                          clientId: h.assignedClientId,
                          date:     nh.purchaseDate,
                          holding:  h.name,
                          type:     "BUY",
                          qty:      parsedQty,
                          price:    parsedCost || 0,
                          currency: nh.currency,
                          fees:     0,
                        };
                        try {
                          await addTransaction(newT);
                          setTransactions(p => [...p, newT]);
                          txnCreated = true;
                        } catch(err) {
                          console.error("[Supabase] addTransaction (auto from holding):", err?.message||err);
                        }
                      }

                      setAddOpen(false);
                      setNh({name:"",ticker:"",class:"Equities",currency:"USD",qty:"",costPrice:"",currentPrice:"",country:"",exchange:"",listed:true,manualPriceDate:"",purchaseDate:"",assignedClientId:"",manager:"Self-Managed",capSize:"N/A",sector:"",yahooTicker:""});
                      setNhErrors({});
                      setNhCountryQuery(""); setNhTickerQuery(""); setNhTickerResults([]); setNhTickerOpen(false);
                      showToast(
                        txnCreated
                          ? "✓ " + h.name + " saved + BUY transaction created"
                          : "✓ " + h.name + " saved successfully",
                        "#1D9E75"
                      );
                      setTimeout(()=>refresh(true),300);
                    }).catch(err => {
                      console.error("[Supabase] addHolding failed:", err.message, JSON.stringify(h).substring(0,200));
                      showToast("❌ Failed to save " + h.name + ": " + err.message, "#A32D2D");
                      // Do NOT update local state — holding didn't persist
                    });
                  } }} style={{background:"#1E40AF",border:"none",borderRadius:6,color:"white",padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>Save</button>
                <button onClick={()=>{ setAddOpen(false); setNhErrors({}); setNhCountryQuery(""); setNh(p=>({...p,assignedClientId:""})); setNhTickerQuery(""); setNhTickerResults([]); setNhTickerOpen(false); }} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 20px",cursor:"pointer",fontSize:13}}>Cancel</button>
              </div>
            </div>
          )}

          {/* Manual price edit inline */}
          {editPriceId && (
            <div style={{...tCard(),marginBottom:16,borderColor:"#D97706"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:10,color:"#D97706"}}>✏ Update Manual Price — {holdings.find(h=>h.id===editPriceId)?.name}</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr auto",gap:10,alignItems:"flex-end"}}>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>New Price ({holdings.find(h=>h.id===editPriceId)?.currency})</div><input style={inp()} value={editPriceVal} onChange={e=>setEditPriceVal(e.target.value)} placeholder="Enter price"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Last As On Date</div><input type="date" style={inp()} value={editPriceDate} onChange={e=>setEditPriceDate(e.target.value)}/></div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>saveManualPrice(editPriceId)} style={{background:"#D97706",border:"none",borderRadius:6,color:T.pageBg,padding:"8px 16px",cursor:"pointer",fontWeight:700}}>Save</button>
                  <button onClick={()=>setEditPriceId(null)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 14px",cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

                    {/* ── ACTIVE DRILL FILTER PILLS ── */}
          {Object.keys(holdingsDrillFilter).length > 0 && (
            <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",
              padding:"10px 14px",background:T.navActiveBg,borderRadius:10,
              border:`1px solid ${T.brand}33`}}>
              <span style={{fontSize:11,fontWeight:700,color:T.brand}}>🔍 Filtered by:</span>
              {Object.entries(holdingsDrillFilter).map(([key,val])=>(
                <span key={key}
                  onClick={()=>setDrill(key,val)}
                  style={{fontSize:11,background:T.cardBg,border:`1px solid ${T.brand}55`,
                    borderRadius:20,padding:"3px 10px",cursor:"pointer",display:"flex",
                    alignItems:"center",gap:5,color:T.textPrimary,fontWeight:500}}>
                  <span style={{textTransform:"capitalize",color:T.textMuted}}>{key}:</span>
                  <span style={{fontWeight:700}}>{val}</span>
                  <span style={{color:"#A32D2D",fontWeight:700,fontSize:12}}>✕</span>
                </span>
              ))}
              <span onClick={clearDrillFilter}
                style={{fontSize:11,color:"#A32D2D",cursor:"pointer",marginLeft:"auto",
                  fontWeight:600,textDecoration:"underline"}}>Clear all</span>
            </div>
          )}

          {/* ── GROUPED + CONSOLIDATED HOLDINGS TABLE ── */}
          {renderHoldingsTable()}


        {/* ── AMC / Fund House Exposure ── */}
          {(function(){
            const MF_CLASSES = ["Indian Mutual Funds", "Global Mutual Funds", "Mutual Funds"];
            const drillClass = holdingsDrillFilter.class;
            if (drillClass && !MF_CLASSES.includes(drillClass)) return null;
            const mfHoldings = filteredHW.filter(h => MF_CLASSES.includes(h.class));
            if (!mfHoldings.length) return null;

            const byAMC = {};
            mfHoldings.forEach(h => {
              const amc = extractAMC(h);
              if (!byAMC[amc]) byAMC[amc] = { valueUSD:0, costUSD:0, holdings:[] };
              byAMC[amc].valueUSD += h.valueUSD||0;
              byAMC[amc].costUSD  += h.costUSD||0;
              byAMC[amc].holdings.push(h);
            });
            const totalMFUSD = mfHoldings.reduce((s,h)=>s+(h.valueUSD||0),0);
            const allAmcRows = Object.entries(byAMC).map(([amc,d])=>({
              amc, valueUSD:d.valueUSD, costUSD:d.costUSD,
              pct: totalMFUSD>0 ? d.valueUSD/totalMFUSD*100 : 0,
              gainPct: d.costUSD>0 ? (d.valueUSD-d.costUSD)/d.costUSD*100 : null,
              count: d.holdings.length,
              holdings: d.holdings.sort((a,b)=>(b.valueUSD||0)-(a.valueUSD||0)),
              color: AMC_COLORS[amc]||"#78716C",
            })).sort((a,b)=>b.valueUSD-a.valueUSD);

            // Group AMCs < 3% into "Others"
            const THRESHOLD = 2.0;
            const mainRows  = allAmcRows.filter(r => r.pct >= THRESHOLD);
            const smallRows = allAmcRows.filter(r => r.pct <  THRESHOLD);
            const amcRows   = [...mainRows];
            if (smallRows.length) {
              const othersVal  = smallRows.reduce((s,r)=>s+r.valueUSD,0);
              const othersCost = smallRows.reduce((s,r)=>s+r.costUSD,0);
              amcRows.push({
                amc: "Others", valueUSD: othersVal, costUSD: othersCost,
                pct: totalMFUSD>0 ? othersVal/totalMFUSD*100 : 0,
                gainPct: othersCost>0 ? (othersVal-othersCost)/othersCost*100 : null,
                count: smallRows.flatMap(r=>r.holdings).length,
                holdings: smallRows.flatMap(r=>r.holdings).sort((a,b)=>(b.valueUSD||0)-(a.valueUSD||0)),
                color: "#78716C", isOthers: true, subRows: smallRows,
              });
            }

            const maxAMCPct = mainRows[0]?.pct||0;

            return (
              <div style={{...card(),marginBottom:16}}>
                {/* Collapsible header */}
                <div onClick={()=>setExpandedCard(c=>c==="amc"?null:"amc")}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    cursor:"pointer",userSelect:"none",marginBottom:amcOpen?14:0,gap:8,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:"#1D9E75",flexShrink:0}}/>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Fund House (AMC) Exposure</div>
                      {!amcOpen && (
                        <div style={{fontSize:11,color:T.textMuted,marginTop:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          {mainRows.slice(0,3).map(r=>(
                            <span key={r.amc} style={{background:r.color+"22",color:r.color,
                              fontSize:10,padding:"1px 7px",borderRadius:20,fontWeight:600,border:`1px solid ${r.color}44`}}>
                              {r.amc} {r.pct.toFixed(0)}%
                            </span>
                          ))}
                          {smallRows.length>0&&<span style={{fontSize:10,color:T.textMuted}}>+{smallRows.length} more · {allAmcRows.length} AMCs</span>}
                        </div>
                      )}
                      {amcOpen && <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>Click a fund house to expand its schemes</div>}
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                    {!amcOpen && (
                      <React.Fragment>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:13,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(totalMFUSD,dc),dc,true)}</div>
                          <div style={{fontSize:10,color:T.textMuted}}>total MF AUM</div>
                        </div>
                      </React.Fragment>
                    )}
                    {maxAMCPct>40&&amcOpen&&(
                      <div style={{fontSize:11,fontWeight:600,color:"#D97706",
                        background:"rgba(217,119,6,0.08)",border:"1px solid rgba(217,119,6,0.25)",
                        borderRadius:7,padding:"4px 10px"}}>
                        ⚠ {mainRows[0].amc} = {maxAMCPct.toFixed(0)}%
                      </div>
                    )}
                    <span style={{fontSize:14,color:T.textMuted}}>›</span>
                  </div>
                </div>

                {amcOpen&&(
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"180px 1fr",
                    gap:20,alignItems:"start"}}>
                    {/* Donut + legend */}
                    <div>
                      <DonutChart
                        data={amcRows.map(r=>({label:r.amc,value:r.valueUSD}))}
                        colorMap={Object.fromEntries(amcRows.map(r=>[r.amc,r.color]))}
                        onSliceClick={amc=>setExpandedAMC(expandedAMC===amc?null:amc)}
                        activeSlice={expandedAMC}
                        size={isMobile?130:145}
                      />
                      <div style={{marginTop:6,fontSize:10,color:T.textMuted,textAlign:"center"}}>
                        Total MF: {fmt(convert(totalMFUSD,dc),dc,true)}
                      </div>

                    </div>

                    {/* Bar list */}
                    <div style={{display:"flex",flexDirection:"column",gap:1}}>
                      {amcRows.map(r=>{
                        const isExp = expandedAMC===r.amc;
                        const riskCol = r.pct>40?"#A32D2D":r.pct>25?"#D97706":"#1D9E75";
                        return (
                          <div key={r.amc}>
                            <div onClick={()=>setExpandedAMC(isExp?null:r.amc)}
                              style={{display:"flex",alignItems:"center",gap:8,padding:"4px 6px",
                                borderRadius:8,cursor:"pointer",
                                background:isExp?`${r.color}10`:"transparent",
                                border:isExp?`1px solid ${r.color}30`:"1px solid transparent",
                                transition:"all 0.15s"}}>
                              <div style={{width:10,height:10,borderRadius:"50%",background:r.color,flexShrink:0}}/>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:10,fontWeight:700,color:T.textPrimary,
                                  display:"flex",alignItems:"center",gap:4}}>
                                  {r.amc}
                                  <span style={{fontSize:9,color:T.textMuted,fontWeight:400}}>
                                    {r.count} scheme{r.count!==1?"s":""}
                                    {r.isOthers&&` · ${r.subRows.length} AMCs`}
                                  </span>
                                </div>
                                <div style={{height:3,background:T.borderSubtle,borderRadius:2,marginTop:3,overflow:"hidden"}}>
                                  <div style={{height:"100%",borderRadius:2,background:r.color,
                                    width:`${r.pct}%`,transition:"width 0.5s ease"}}/>
                                </div>
                              </div>
                              <div style={{textAlign:"right",flexShrink:0}}>
                                <div style={{fontSize:11,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                  {fmt(convert(r.valueUSD,dc),dc,true)}
                                </div>
                                <div style={{fontSize:10,display:"flex",gap:5,justifyContent:"flex-end",alignItems:"center"}}>
                                  <span style={{fontWeight:700,color:riskCol}}>{r.pct.toFixed(1)}%</span>
                                  {r.gainPct!=null&&(
                                    <span style={{color:r.gainPct>=0?"#1D9E75":"#A32D2D",fontWeight:600}}>
                                      {r.gainPct>=0?"+":""}{r.gainPct.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{fontSize:10,color:T.textMuted,transition:"transform 0.2s",
                                transform:isExp?"rotate(90deg)":"rotate(0deg)"}}>▶</span>
                            </div>

                            {isExp&&(
                              <div style={{marginLeft:20,borderLeft:`2px solid ${r.color}40`,
                                paddingLeft:12,marginBottom:6}}>
                                {r.isOthers ? (
                                  r.subRows.map(sr=>(
                                    <div key={sr.amc} style={{marginBottom:8}}>
                                      <div style={{fontSize:10,fontWeight:700,color:T.textMuted,
                                        textTransform:"uppercase",letterSpacing:"0.05em",
                                        padding:"4px 0",display:"flex",alignItems:"center",gap:6}}>
                                        <div style={{width:6,height:6,borderRadius:"50%",background:sr.color}}/>
                                        {sr.amc} · {sr.pct.toFixed(1)}%
                                      </div>
                                      {sr.holdings.map(h=>{
                                        const hPct=totalMFUSD>0?h.valueUSD/totalMFUSD*100:0;
                                        return (
                                          <div key={h.id} style={{display:"flex",justifyContent:"space-between",
                                            alignItems:"center",padding:"4px 0",
                                            borderBottom:`1px solid ${T.borderSubtle}`}}>
                                            <div style={{flex:1,minWidth:0}}>
                                              <div style={{fontSize:11,fontWeight:600,color:T.textPrimary,
                                                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                                maxWidth:220}} title={h.name}>{h.name}</div>
                                              <div style={{fontSize:9,color:T.textMuted}}>
                                                {h.schemeCode?`#${h.schemeCode} · `:""}Qty: {h.qty?.toLocaleString("en-IN",{maximumFractionDigits:3})}
                                              </div>
                                            </div>
                                            <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                                              <div style={{fontSize:11,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                                {fmt(convert(h.valueUSD||0,dc),dc,true)}
                                              </div>
                                              <div style={{fontSize:9,color:T.textMuted}}>{hPct.toFixed(1)}% of MF</div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))
                                ) : (
                                  r.holdings.map(h=>{
                                    const hPct=totalMFUSD>0?h.valueUSD/totalMFUSD*100:0;
                                    return (
                                      <div key={h.id} style={{display:"flex",justifyContent:"space-between",
                                        alignItems:"center",padding:"5px 0",
                                        borderBottom:`1px solid ${T.borderSubtle}`}}>
                                        <div style={{flex:1,minWidth:0}}>
                                          <div style={{fontSize:11,fontWeight:600,color:T.textPrimary,
                                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                            maxWidth:220}} title={h.name}>{h.name}</div>
                                          <div style={{fontSize:9,color:T.textMuted,marginTop:1}}>
                                            {h.isin&&h.isin!=="-"?h.isin:""}
                                            {h.schemeCode?` · #${h.schemeCode}`:""}
                                            {` · Qty: ${h.qty?.toLocaleString("en-IN",{maximumFractionDigits:3})}`}
                                          </div>
                                        </div>
                                        <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                                          <div style={{fontSize:11,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                            {fmt(convert(h.valueUSD||0,dc),dc,true)}
                                          </div>
                                          <div style={{fontSize:9,color:T.textMuted}}>
                                            {hPct.toFixed(1)}% of MF
                                            {h.gainPct!=null&&(
                                              <span style={{marginLeft:5,fontWeight:600,
                                                color:h.gainPct>=0?"#1D9E75":"#A32D2D"}}>
                                                {h.gainPct>=0?"+":""}{h.gainPct.toFixed(1)}%
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                                <div style={{display:"flex",justifyContent:"space-between",
                                  padding:"5px 0",fontSize:11,fontWeight:700,color:r.color}}>
                                  <span>Subtotal — {r.amc}</span>
                                  <span style={{fontFamily:"'Inter', system-ui, sans-serif"}}>
                                    {fmt(convert(r.valueUSD,dc),dc,true)} · {r.pct.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{marginTop:isMobile?4:10,fontSize:11,color:T.textSecondary,padding:"8px 12px",background:T.cardBg,borderRadius:6}}>
            ℹ Showing holdings for: <strong style={{color:T.textPrimary}}>
              {selectedClients.size > 0
                ? [...selectedClients].map(id=>visibleClients.find(c=>c.id===id)?.name).filter(Boolean).join(", ")
                : selectedFamily!=="all"
                  ? visibleFamilies.find(f=>f.id===selectedFamily)?.name+" (all members)"
                  : "entire portfolio (top 10)"}
            </strong>. Click owner name or "Assign →" to reassign. Use ✏ to update manual prices.
          </div>

          {/* ── Detailed Performance Table (collapsible) ── */}
          <PerfTable key={`${selectedFamily}-${[...selectedClients].join(',')}`} holdings={filteredHW} T={T} tCard={tCard} tTh={tTh} tTd={tTd} isMobile={isMobile} dc={dc}
            controlledOpen={expandedCard === "perf"}
            onControlledToggle={(next) => setExpandedCard(next ? "perf" : null)}/>

        </React.Fragment>
}

        {/* ══════════════════════════════════════════════
            TRANSACTIONS TAB
        ══════════════════════════════════════════════ */}
        {tab==="transactions" && (function(){ try {
          // ── Date range helpers ──
          const getFYRange = (yr) => ({ from: `${yr}-04-01`, to: `${yr+1}-03-31` });
          const getCYRange = (yr) => ({ from: `${yr}-01-01`, to: `${yr}-12-31` });
          const txnRange = txnPeriodType==="FY" ? getFYRange(txnYear)
            : txnPeriodType==="CY" ? getCYRange(txnYear)
            : txnPeriodType==="custom" ? { from: txnDateFrom, to: txnDateTo }
            : null; // null = all time
          // Filter transactions to current scope (family / client / impersonation).
          // CRITICAL: We use hwWithOwner here, NOT filteredHW. filteredHW hides
          // holdings whose effectiveQty has dropped to 0 (fully sold out). If we
          // built txnScopeNames from filteredHW, those holdings' historical SELL
          // transactions would also disappear from the ledger — circular logic.
          // The scope set must include sold-out holdings so their SELLs still show.
          const txnScopeHoldings = (() => {
            const inScope = (h) => {
              if (!isAdmin && loggedInClient) {
                if (!loggedInFamilyId) return false;
                return h.family?.id === loggedInFamilyId;
              }
              if (selectedClients.size > 0) {
                return selectedClients.has(h.clientId) || selectedClients.has(String(h.clientId));
              }
              if (selectedFamily !== "all") {
                const sfid = String(selectedFamily);
                return String(h.family?.id) === sfid || String(h.client?.familyId) === sfid;
              }
              if (isAdmin && viewAsClientId) return h.clientId === viewAsClientId;
              return true; // no filter
            };
            return hwWithOwner.filter(inScope);
          })();
          // Set of holding names in scope — used by the scope filter below to
          // narrow the Transaction Ledger when a specific client is being viewed.
          const txnScopeNames = new Set(txnScopeHoldings.map(h => h.name));
          // Use scope-aware holdings so selecting a family/client actually narrows
          // the New Transaction dropdown. Keep {name,class} tuples so we can cascade
          // the dropdown by asset class.
          const txnHoldingChoices = (() => {
            const seen = new Map(); // name -> class
            txnScopeHoldings.forEach(h => {
              if (h.name && !seen.has(h.name)) seen.set(h.name, h.class || "Other");
            });
            return [...seen.entries()]
              .map(([name, cls]) => ({ name, class: cls }))
              .sort((a,b) => a.name.localeCompare(b.name));
          })();
          const txnHoldingClasses = [...new Set(txnHoldingChoices.map(h => h.class))].sort();
          const availTxnYears = [...new Set(transactions.map(t=>t.date?.slice(0,4)).filter(Boolean))].sort().reverse();
          const renderTxnDatePicker = () => (
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              {/* Period type pills */}
              {[["all","All Time"],["FY","Fin. Year"],["CY","Cal. Year"],["custom","Custom"]].map(([v,l])=>(
                <button key={v} onClick={()=>setTxnPeriodType(v)}
                  style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
                    background:txnPeriodType===v?T.brand:T.inputBg,
                    color:txnPeriodType===v?"white":T.textSecondary}}>
                  {l}
                </button>
              ))}
              {/* Year selector for FY/CY */}
              {(txnPeriodType==="FY"||txnPeriodType==="CY") && (
                <select value={txnYear} onChange={e=>setTxnYear(Number(e.target.value))}
                  style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                    background:T.inputBg,color:T.textPrimary,cursor:"pointer"}}>
                  {[...Array(10)].map((_,i)=>{
                    const yr = new Date().getFullYear()-i;
                    return <option key={yr} value={yr}>
                      {txnPeriodType==="FY"?`FY ${yr}-${String(yr+1).slice(2)}`:yr}
                    </option>;
                  })}
                </select>
              )}
              {/* Custom range */}
              {txnPeriodType==="custom" && (
                <React.Fragment>
                  <input type="date" value={txnDateFrom} onChange={e=>setTxnDateFrom(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                      background:T.inputBg,color:T.textPrimary}}/>
                  <span style={{fontSize:11,color:T.textMuted}}>to</span>
                  <input type="date" value={txnDateTo} onChange={e=>setTxnDateTo(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                      background:T.inputBg,color:T.textPrimary}}/>
                </React.Fragment>
              )}
              {/* Active range label */}
              {txnRange && (
                <span style={{fontSize:10,color:T.textMuted,background:T.inputBg,padding:"3px 8px",borderRadius:5}}>
                  {txnRange.from} → {txnRange.to}
                </span>
              )}
            </div>
          );
          const filtered = transactions.filter(t=>{
            // Scope to current client/family view. Prefer t.clientId (precise);
            // fall back to t.client name, then holding-name membership for
            // legacy imports where transactions aren't tagged with clientId.
            if (isAdmin) {
              // Client-level filter (one or more clients selected)
              if (selectedClients.size > 0) {
                const inScope =
                  (t.clientId && (selectedClients.has(t.clientId) || selectedClients.has(String(t.clientId)))) ||
                  (t.client && clients.some(c =>
                    (selectedClients.has(c.id) || selectedClients.has(String(c.id))) &&
                    c.name && (c.name === t.client ||
                               c.name.toLowerCase() === String(t.client).toLowerCase())
                  )) ||
                  txnScopeNames.has(t.holding);
                if (!inScope) return false;
              }
              // Family-level filter (no specific client, but a family is chosen)
              else if (selectedFamily !== "all") {
                const famClients = new Set(clients.filter(c => String(c.familyId) === String(selectedFamily)).map(c => c.id));
                const inScope =
                  (t.clientId && famClients.has(t.clientId)) ||
                  (t.client && clients.some(c => famClients.has(c.id) && c.name === t.client)) ||
                  txnScopeNames.has(t.holding);
                if (!inScope) return false;
              }
              // viewAsClientId (impersonation mode) — legacy path, kept for safety
              else if (viewAsClientId && !txnScopeNames.has(t.holding)) {
                return false;
              }
            }
            // Date range filter
            if (txnRange) {
              if (txnRange.from && t.date < txnRange.from) return false;
              if (txnRange.to   && t.date > txnRange.to)   return false;
            }
            if (!txnSearch) return true;
            const q = txnSearch.toLowerCase();
            return t.holding.toLowerCase().includes(q) || t.type.toLowerCase().includes(q) || (t.date||"").includes(q);
          });
          const uid2 = () => Math.random().toString(36).slice(2,9);
          const addTxn = () => {
            // Validate transaction
              const txnTabErrs = [];
              if (!newTxn.holding?.trim())                          txnTabErrs.push("Holding is required");
              if (!newTxn.date)                                     txnTabErrs.push("Date is required");
              if (!newTxn.qty || parseFloat(newTxn.qty) <= 0)      txnTabErrs.push("Quantity must be > 0");
              if (!newTxn.price || parseFloat(newTxn.price) <= 0)  txnTabErrs.push("Price must be > 0");
              if (newTxn.date && new Date(newTxn.date) > new Date()) txnTabErrs.push("Date cannot be in the future");
              if (parseFloat(newTxn.fees||0) < 0)                  txnTabErrs.push("Fees cannot be negative");
              if (txnTabErrs.length > 0) { showToast("⚠ " + txnTabErrs[0], "#D97706"); return; }
            const t = { id: uid2(), ...newTxn, qty: parseFloat(newTxn.qty)||0, price: parseFloat(newTxn.price)||0, fees: parseFloat(newTxn.fees)||0,
              clientId: isAdmin ? (newTxn.clientId || undefined) : (loggedInClientId || undefined),
              class: undefined, // strip UI-only field
            };
            setTransactions(p=>[t,...p]);
            addTransaction(t).catch(e=>console.error("[Supabase] addTransaction:",e.message));
            postTxnsToHoldings([[t, 1]]);
            // Preserve the filtered client on reset so the next add stays in context
            const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : "");
            setNewTxn({ date: new Date().toISOString().slice(0,10), holding:"", type:"BUY", qty:"", price:"", currency:"USD", fees:"", clientId: filterClientId });
            setTxnAddOpen(false);
          };
          const deleteTxn = id => { requireConfirm("Delete this transaction?", ()=>{ const gone = transactions.find(t=>t.id===id); if (gone) postTxnsToHoldings([[gone, -1]]); setTransactions(p=>p.filter(t=>t.id!==id)); deleteTransaction(id).catch(e=>console.error("[Supabase] deleteTransaction:",e.message)); showToast("✓ Transaction deleted","#1D9E75"); }); };
          const txnToggle = id => setTxnSelected(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });
          const txnSelectAll = ids => setTxnSelected(new Set(ids));
          const txnClearAll  = ()  => setTxnSelected(new Set());
          const deleteSelectedTxns = () => {
            if (!txnSelected.size) return;
            requireConfirm(`Delete ${txnSelected.size} transaction${txnSelected.size!==1?"s":""}? This cannot be undone.`, async () => {
              const ids = [...txnSelected];
              postTxnsToHoldings(transactions.filter(t => txnSelected.has(t.id)).map(t => [t, -1]));
              setTransactions(p => p.filter(t => !txnSelected.has(t.id)));
              setTxnSelected(new Set());
              let failed = 0;
              for (const id of ids) {
                await deleteTransaction(id).catch(e => { console.error("[Supabase] deleteTransaction:", e.message); failed++; });
              }
              showToast(failed > 0 ? `✓ Deleted ${ids.length - failed} · ${failed} failed` : `✓ Deleted ${ids.length} transaction${ids.length!==1?"s":""}`, "#1D9E75");
            });
          };
          // Compute in USD for display
          const toUSDtxn = (val,cur) => val / (fxRates[cur]||FX_RATES_DEFAULT[cur]||1);
          // Group transaction types for KPI roll-ups. "Buy-like" is any money-in
          // event (BUY, SIP, switches/transfers in, dividend reinvestment).
          // "Sell-like" is any money-out event (SELL, SWP, switches/transfers out).
          const TXN_BUY_LIKE  = new Set(["BUY","SIP","STP_IN","SWITCH_IN","DIVIDEND_REINVEST","BONUS"]);
          const TXN_SELL_LIKE = new Set(["SELL","SWP","STP_OUT","SWITCH_OUT"]);
          const allBuys  = filtered.filter(t=>TXN_BUY_LIKE.has(t.type));
          const allSells = filtered.filter(t=>TXN_SELL_LIKE.has(t.type));
          const totalBuysUSD  = allBuys.reduce((s,t)=>s+toUSDtxn(t.qty*t.price+(t.fees||0), t.currency||"INR"),0);
          const totalSellsUSD = allSells.reduce((s,t)=>s+toUSDtxn(t.qty*t.price-(t.fees||0), t.currency||"INR"),0);
          const totalFeesUSD  = filtered.reduce((s,t)=>s+toUSDtxn(t.fees||0, t.currency||"INR"),0);
          const realisedPL    = totalSellsUSD - allSells.reduce((s,t)=>{
            // Estimate cost of sold shares using avg buy price for that holding
            const buyTxns = transactions.filter(b=>b.holding===t.holding&&TXN_BUY_LIKE.has(b.type));
            const avgCost = buyTxns.length ? buyTxns.reduce((a,b)=>a+b.price*b.qty,0)/buyTxns.reduce((a,b)=>a+b.qty,0) : 0;
            return s+toUSDtxn(t.qty*(avgCost||0), t.currency||"INR");
          },0);
          const totalBuys = totalBuysUSD; const totalSells = totalSellsUSD; // keep compat
          return (
            <div>
              {/* Header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:17,fontWeight:700}}>Transaction Ledger</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>All buy / sell / dividend events. Used in IRR calculations.</div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {isAdmin && (()=>{
                    // Only show when a single client is filtered (so we know who to assign new holdings to)
                    const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : "");
                    if (!filterClientId) return null;
                    // Count orphan transaction holding names
                    const clientHoldingIds = new Set(
                      Object.entries(holdingOwners).filter(([_,cId])=>cId===filterClientId).map(([hId])=>hId)
                    );
                    // Index existing holdings by BOTH name and ticker. Indexing on
                    // name alone meant that renaming a holding ('ANET' ->
                    // 'Arista Networks Inc') made its transactions look unmatched,
                    // so every reconcile run created ANOTHER lot for the same
                    // position — the duplicate-lots bug.
                    const existingNames = new Set();
                    for (const h of holdings) {
                      if (!clientHoldingIds.has(String(h.id))) continue;
                      const nm = (h.name||"").toLowerCase().trim();
                      const tk = (h.ticker||"").toLowerCase().trim();
                      if (nm) existingNames.add(nm);
                      if (tk && tk !== "-") existingNames.add(tk);
                    }
                    const clientTxns = transactions.filter(t => t.clientId === filterClientId);
                    const orphanNames = new Set();
                    for (const t of clientTxns) {
                      const k = (t.holding||"").toLowerCase().trim();
                      if (k && !existingNames.has(k)) orphanNames.add(k);
                    }
                    if (orphanNames.size === 0) return null;

                    const openReconcile = () => {
                      const BUY_TYPES  = new Set(["BUY","SIP","STP_IN","SWITCH_IN"]);
                      const SELL_TYPES = new Set(["SELL","REDEEM","STP_OUT","SWITCH_OUT"]);
                      const byKey = new Map();
                      for (const t of clientTxns) {
                        const k = (t.holding||"").toLowerCase().trim();
                        if (!k || existingNames.has(k)) continue;
                        if (!byKey.has(k)) byKey.set(k, []);
                        byKey.get(k).push(t);
                      }
                      const rows = [];
                      for (const [_, txns] of byKey) {
                        const sample = txns[0];
                        let netQty=0, buyValue=0, buyQty=0;
                        for (const t of txns) {
                          if (BUY_TYPES.has(t.type))  { netQty += +t.qty; buyValue += (+t.qty)*(+t.price); buyQty += +t.qty; }
                          if (SELL_TYPES.has(t.type)) { netQty -= +t.qty; }
                        }
                        const avgCost = buyQty > 0 ? buyValue/buyQty : sample.price || 0;
                        rows.push({
                          name: sample.holding,
                          // Preserve the raw transaction key so the created holding
                          // can store it as its ticker — that keeps the link stable
                          // even if the name is edited later.
                          txnKey: sample.holding,
                          currency: sample.currency || "USD",
                          netQty, avgCost, count: txns.length,
                          selected: netQty > 0, // pre-select only open positions
                          closed: netQty <= 0,
                        });
                      }
                      rows.sort((a,b)=>a.name.localeCompare(b.name));
                      setReconcileModal({ rows, clientId: filterClientId, clientName: visibleClients.find(c=>c.id===filterClientId)?.name||"client" });
                    };

                    return (
                      <button onClick={openReconcile}
                        style={{background:"rgba(217,119,6,0.08)",border:"1px solid rgba(217,119,6,0.4)",borderRadius:8,color:"#D97706",padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight:600}}
                        title={`${orphanNames.size} transaction holding${orphanNames.size!==1?"s":""} not in this client's portfolio`}>
                        🔄 Reconcile · {orphanNames.size}
                      </button>
                    );
                  })()}
                  {isAdmin && <button onClick={()=>{
                  const willOpen = !txnAddOpen;
                  if (willOpen) {
                    // Pre-fill clientId from active filter (viewAs > single-selected client)
                    const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : "");
                    if (filterClientId) setNewTxn(p=>({...p, clientId: filterClientId}));
                  }
                  setTxnAddOpen(willOpen);
                }} style={{background:"#1E293B",border:"none",borderRadius:8,color:"white",padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Add Transaction</button>}
                </div>
              </div>
              {/* KPI strip */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:20}}>
                {[
                  {label:"Total Transactions",val:filtered.length,sub:`${allBuys.length} buys · ${allSells.length} sells`,col:T.textPrimary},
                  {label:"Total Invested",val:fmt(convert(totalBuysUSD,dc),dc,true),sub:`Across ${new Set(allBuys.map(t=>t.holding)).size} holdings`,col:"#2563EB"},
                  {label:"Total Realised",val:fmt(convert(totalSellsUSD,dc),dc,true),sub:realisedPL>=0?`+${fmt(convert(realisedPL,dc),dc,true)} profit`:`${fmt(convert(realisedPL,dc),dc,true)} loss`,col:realisedPL>=0?"#1D9E75":"#A32D2D"},
                  {label:"Total Fees Paid",val:fmt(convert(totalFeesUSD,dc),dc,true),sub:"Brokerage + STT",col:"#D97706"},
                  {label:"Holdings with Txns",val:new Set(filtered.map(t=>t.holding)).size,sub:`of ${filteredHW.length} total holdings`,col:"#7C3AED"},
                ].map(k=>(
                  <div key={k.label} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:"14px 16px"}}>
                    <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{k.label}</div>
                    <div style={{fontSize:20,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.val}</div>
                    {k.sub && <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>{k.sub}</div>}
                  </div>
                ))}
              </div>
              {/* Add form */}
              {txnAddOpen && (
                <div style={{background:T.cardBg,border:"1px solid #2563EB",borderRadius:12,padding:"16px 20px",marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#2563EB",marginBottom:14}}>New Transaction</div>
                  {(()=>{
                    // Determine if we're already scoped to a single client via the page filter
                    const filterClientId = viewAsClientId || (selectedClients.size===1 ? [...selectedClients][0] : "");
                    const filterClient   = filterClientId ? clients.find(c=>c.id===filterClientId) : null;
                    return filterClient ? (
                      <div style={{marginBottom:10,display:"flex",alignItems:"center",gap:10,background:"rgba(37,99,235,0.06)",border:"1px solid rgba(37,99,235,0.2)",borderRadius:8,padding:"7px 12px"}}>
                        <span style={{fontSize:13}}>👤</span>
                        <span style={{fontSize:11,color:T.textSecondary}}>Adding for:</span>
                        <span style={{fontSize:12,fontWeight:600,color:"#2563EB"}}>{filterClient.name}</span>
                        <span style={{fontSize:10,color:T.textMuted,marginLeft:"auto"}}>Auto-assigned from page filter</span>
                      </div>
                    ) : null;
                  })()}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,alignItems:"flex-end"}}>
                    {isAdmin && !(viewAsClientId || (selectedClients.size===1)) && (
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Client</div>
                        <select style={inp()} value={newTxn.clientId} onChange={e=>setNewTxn(p=>({...p,clientId:e.target.value}))}>
                          <option value="">— Unassigned —</option>
                          {visibleClients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Date</div>
                      <input type="date" style={inp()} value={newTxn.date} onChange={e=>setNewTxn(p=>({...p,date:e.target.value}))}/>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Asset class</div>
                      <select style={inp()} value={newTxn.class}
                        onChange={e=>setNewTxn(p=>({...p,class:e.target.value,holding:""}))}>
                        <option value="">All classes…</option>
                        {txnHoldingClasses.map(c=><option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Holding</div>
                      <select style={inp()} value={newTxn.holding} onChange={e=>{
                        const v = e.target.value;
                        // Special sentinel: user wants to create a new holding —
                        // jump to Holdings tab with Add form open, keep the scratch
                        // transaction so they can return to it after creating.
                        if (v === "__ADD__") {
                          // Carry over what the user has already entered in the txn form
                          // so the Add Holding form is pre-populated. After saving the
                          // holding, if purchaseDate is set, a BUY transaction is auto-created.
                          // Date ALWAYS gets carried (with today as fallback) so the visible
                          // Purchase Date field on the holdings form is never blank.
                          const carry = {
                            purchaseDate: newTxn.date || new Date().toISOString().slice(0,10),
                          };
                          if (newTxn.qty)      carry.qty              = newTxn.qty;
                          if (newTxn.price)    { carry.costPrice = newTxn.price; carry.currentPrice = newTxn.price; }
                          if (newTxn.currency) carry.currency         = newTxn.currency;
                          if (newTxn.clientId) carry.assignedClientId = newTxn.clientId;
                          setNh(p => ({...p, ...carry}));
                          setTab("holdings");
                          setAddOpen(true);
                          showToast("Pre-filled from your transaction — add ticker & save to create both", "#2563EB", 5000);
                          return;
                        }
                        setNewTxn(p=>({...p,holding:v}));
                      }}>
                        <option value="">Select holding…</option>
                        <option value="__ADD__" style={{fontWeight:600,color:"#2563EB"}}>+ Create new holding…</option>
                        {(newTxn.class
                          ? txnHoldingChoices.filter(h => h.class === newTxn.class)
                          : txnHoldingChoices
                        ).map(h => <option key={h.name} value={h.name}>{h.name}</option>)}
                      </select>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Type</div>
                      <select style={inp()} value={newTxn.type} onChange={e=>setNewTxn(p=>({...p,type:e.target.value}))}>
                        {["BUY","SELL","SIP","SWP","SWITCH_IN","SWITCH_OUT","STP_IN","STP_OUT","DIVIDEND","DIVIDEND_REINVEST","BONUS","SPLIT","TRANSFER"].map(t=><option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Quantity</div>
                      <input type="number" style={inp()} placeholder="100" value={newTxn.qty} onChange={e=>setNewTxn(p=>({...p,qty:e.target.value}))}/>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Price</div>
                      <input type="number" style={inp()} placeholder="0.00" value={newTxn.price} onChange={e=>setNewTxn(p=>({...p,price:e.target.value}))}/>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Currency</div>
                      <select style={inp()} value={newTxn.currency} onChange={e=>setNewTxn(p=>({...p,currency:e.target.value}))}>
                        {["USD","INR","EUR","GBP","AED","SGD","JPY","CHF"].map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Fees</div>
                      <input type="number" style={inp()} placeholder="0" value={newTxn.fees} onChange={e=>setNewTxn(p=>({...p,fees:e.target.value}))}/>
                    </div>
                    <div style={{display:"flex",gap:6,paddingBottom:2,minWidth:0}}>
                      <button onClick={addTxn} style={{background:"#1E40AF",border:"none",borderRadius:6,color:"white",padding:"8px 16px",cursor:"pointer",fontWeight:600,fontSize:13}}>Add</button>
                      <button onClick={()=>setTxnAddOpen(false)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 12px",cursor:"pointer"}}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
              {/* Date Range Picker */}
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginBottom:10,
                padding:"8px 12px",background:T.inputBg,borderRadius:8,border:`1px solid ${T.cardBorder}`}}>
                <span style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginRight:2}}>Period:</span>
                {[["all","All Time"],["FY","Fin. Year"],["CY","Cal. Year"],["custom","Custom"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setTxnPeriodType(v)}
                    style={{fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:5,border:"none",cursor:"pointer",
                      background:txnPeriodType===v?T.brand:T.cardBg,
                      color:txnPeriodType===v?"white":T.textSecondary}}>
                    {l}
                  </button>
                ))}
                {(txnPeriodType==="FY"||txnPeriodType==="CY") && (
                  <select value={txnYear} onChange={e=>setTxnYear(Number(e.target.value))}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:5,border:`1px solid ${T.cardBorder}`,
                      background:T.cardBg,color:T.textPrimary,cursor:"pointer"}}>
                    {[...Array(10)].map((_,i)=>{ const yr=new Date().getFullYear()-i;
                      return <option key={yr} value={yr}>{txnPeriodType==="FY"?`FY ${yr}-${String(yr+1).slice(2)}`:yr}</option>; })}
                  </select>
                )}
                {txnPeriodType==="custom" && (
                  <React.Fragment>
                    <input type="date" value={txnDateFrom} onChange={e=>setTxnDateFrom(e.target.value)}
                      style={{fontSize:11,padding:"3px 8px",borderRadius:5,border:`1px solid ${T.cardBorder}`,background:T.cardBg,color:T.textPrimary}}/>
                    <span style={{fontSize:11,color:T.textMuted}}>→</span>
                    <input type="date" value={txnDateTo} onChange={e=>setTxnDateTo(e.target.value)}
                      style={{fontSize:11,padding:"3px 8px",borderRadius:5,border:`1px solid ${T.cardBorder}`,background:T.cardBg,color:T.textPrimary}}/>
                  </React.Fragment>
                )}
                {txnRange && (
                  <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>
                    📅 {txnRange.from} → {txnRange.to} · {filtered.length} txns
                  </span>
                )}
              </div>
              {/* Search + Fund Filter + Sort */}
              <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                <input style={{...inp(),maxWidth:220}} placeholder="🔍 Search…" value={txnSearch} onChange={e=>{setTxnSearch(e.target.value);setTxnFundFilter("");}}/>
                <select style={{...inp(),maxWidth:240,color:txnFundFilter?T.brand:T.textMuted}}
                  value={txnFundFilter} onChange={e=>{ setTxnFundFilter(e.target.value); setTxnSearch(""); }}>
                  <option value="">All funds</option>
                  {[...new Set(transactions.map(t=>t.holding))].sort().map(n=>(
                    <option key={n} value={n}>{n.length>40?n.slice(0,38)+"…":n}</option>
                  ))}
                </select>
                <div style={{display:"flex",gap:4}}>
                  {[["date","Date"],["holding","Fund"],["type","Type"],["total","Amount"]].map(([col,lbl])=>(
                    <button key={col} onClick={()=>{ if(txnSortCol===col) setTxnSortDir(d=>d==="asc"?"desc":"asc"); else {setTxnSortCol(col);setTxnSortDir("asc");} }}
                      style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${txnSortCol===col?T.brand:T.cardBorder}`,
                        background:txnSortCol===col?`${T.brand}12`:"transparent",
                        color:txnSortCol===col?T.brand:T.textMuted,fontSize:11,cursor:"pointer",fontWeight:600}}>
                      {lbl} {txnSortCol===col?(txnSortDir==="asc"?"↑":"↓"):""}
                    </button>
                  ))}
                </div>
                <div style={{fontSize:12,color:T.textSecondary,marginLeft:"auto"}}>
                  {filtered.length} of {transactions.length} transactions
                  {txnFundFilter && <button onClick={()=>setTxnFundFilter("")} style={{marginLeft:8,fontSize:10,color:T.brand,background:"transparent",border:"none",cursor:"pointer"}}>✕ Clear filter</button>}
                </div>
              </div>

              {/* Grouped + Sorted Table */}
              {(()=>{
                // Apply fund filter or text search
                const baseFiltered = filtered.filter(t => txnFundFilter ? t.holding === txnFundFilter : true);
                // Sort
                const sortedTxns = [...baseFiltered].sort((a,b)=>{
                  let va, vb;
                  if (txnSortCol==="date")    { va=a.date; vb=b.date; }
                  else if (txnSortCol==="holding") { va=a.holding; vb=b.holding; }
                  else if (txnSortCol==="type")    { va=a.type; vb=b.type; }
                  else { va=a.qty*a.price+(a.fees||0); vb=b.qty*b.price+(b.fees||0); }
                  const cmp = typeof va==="string" ? va.localeCompare(vb) : (va>vb?1:va<vb?-1:0);
                  return txnSortDir==="asc" ? cmp : -cmp;
                });

                // Group by fund when not filtered to single fund
                const isSingleFund = !!txnFundFilter;
                const groups = {};
                sortedTxns.forEach(t => {
                  const key = t.holding;
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(t);
                });
                const fundNames = txnSortCol==="holding"
                  ? Object.keys(groups).sort((a,b)=>txnSortDir==="asc"?a.localeCompare(b):b.localeCompare(a))
                  : Object.keys(groups);

                return (
              <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,overflow:"hidden"}}>
                {/* Bulk selection toolbar — admin only, appears when rows are selected */}
                {isAdmin && txnSelected.size > 0 && (
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",
                    marginBottom:8,borderRadius:8,
                    background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)"}}>
                    <span style={{fontSize:12,fontWeight:600,color:"#A32D2D"}}>
                      {txnSelected.size} transaction{txnSelected.size!==1?"s":""} selected
                    </span>
                    <button onClick={deleteSelectedTxns}
                      style={{fontSize:12,fontWeight:600,padding:"4px 12px",borderRadius:6,
                        border:"none",background:"#A32D2D",color:"white",cursor:"pointer"}}>
                      🗑 Delete selected
                    </button>
                    <button onClick={txnClearAll}
                      style={{fontSize:11,padding:"4px 10px",borderRadius:6,
                        border:"1px solid #94A3B8",background:"transparent",
                        color:T.textSecondary,cursor:"pointer"}}>
                      Clear selection
                    </button>
                  </div>
                )}
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:isMobile?520:700}}>
                    <thead>
                      <tr style={{background:T.pageBg}}>
                        {isAdmin && (
                          <th style={{...tTh(),width:32,padding:"10px 8px"}}>
                            <input type="checkbox"
                              style={{cursor:"pointer"}}
                              checked={sortedTxns.length > 0 && sortedTxns.every(t => txnSelected.has(t.id))}
                              onChange={e => e.target.checked
                                ? txnSelectAll(sortedTxns.map(t=>t.id))
                                : txnClearAll()}
                            />
                          </th>
                        )}
                        {["Date","Holding","Type","Qty","Price","Currency","Fees","Total",""].map(h=>(
                          <th key={h} style={{...tTh(),fontSize:11,padding:"10px 12px"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTxns.length===0 && (
                        <tr><td colSpan={9} style={{...tTd(),textAlign:"center",color:T.textMuted,padding:32}}>No transactions found.</td></tr>
                      )}
                      {isSingleFund ? (
                        /* Single fund view — flat list */
                        sortedTxns.map((t,i)=>{
                        const total = t.qty * t.price + (t.fees||0);
                        const typeCol = t.type==="BUY"?"#2563EB":t.type==="SELL"?"#1D9E75":t.type==="DIVIDEND"?"#7C3AED":"#D97706";
                        const isEditing = txnEditId===t.id;
                        return (
                          <tr key={t.id} style={{background:i%2===0?"transparent":"rgba(0,0,0,0.015)"}}>
                            {isEditing ? (
                              <React.Fragment>
                                <td style={tTd()}><input type="date" style={{...inp(),fontSize:11,padding:"3px 6px",width:120}} defaultValue={t.date} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,date:e.target.value}:x))}/></td>
                                <td style={tTd()}><select style={{...inp(),fontSize:11,padding:"3px 6px"}} defaultValue={t.holding} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,holding:e.target.value}:x))}>{txnHoldingNames.map(n=><option key={n}>{n}</option>)}</select></td>
                                <td style={tTd()}><select style={{...inp(),fontSize:11,padding:"3px 6px"}} defaultValue={t.type} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,type:e.target.value}:x))}>{["BUY","SELL","DIVIDEND","BONUS","SPLIT","TRANSFER"].map(tp=><option key={tp}>{tp}</option>)}</select></td>
                                <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:80}} defaultValue={t.qty} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,qty:parseFloat(e.target.value)||0}:x))}/></td>
                                <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:90}} defaultValue={t.price} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,price:parseFloat(e.target.value)||0}:x))}/></td>
                                <td style={tTd()}><select style={{...inp(),fontSize:11,padding:"3px 6px"}} defaultValue={t.currency} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,currency:e.target.value}:x))}>{["USD","INR","EUR","GBP","AED","SGD","JPY","CHF"].map(c=><option key={c}>{c}</option>)}</select></td>
                                <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:70}} defaultValue={t.fees||0} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,fees:parseFloat(e.target.value)||0}:x))}/></td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11})}>—</td>
                                <td style={tTd()}><button onClick={()=>{ const updated = transactions.find(x=>x.id===t.id); if(updated) { updateTransaction(updated.id, updated).catch(e=>console.error("[Supabase] updateTransaction:",e.message)); const orig = txnEditOrigRef.current; if (orig && orig.id===updated.id) postTxnsToHoldings([[orig,-1],[updated,1]]); } txnEditOrigRef.current = null; setTxnEditId(null); }} style={{background:"#1D9E75",border:"none",borderRadius:4,color:"white",padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>✓ Done</button></td>
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                {isAdmin && (
                                  <td style={tTd({width:32,padding:"10px 8px"})}>
                                    <input type="checkbox" style={{cursor:"pointer"}}
                                      checked={txnSelected.has(t.id)}
                                      onChange={()=>txnToggle(t.id)}/>
                                  </td>
                                )}
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>{t.date}</td>
                                <td style={tTd({fontWeight:600,fontSize:13})}>{t.holding}</td>
                                <td style={tTd()}><span style={{fontSize:11,fontWeight:700,color:typeCol,background:`${typeCol}15`,padding:"2px 8px",borderRadius:5,border:`1px solid ${typeCol}33`}}>{t.type}</span></td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{(t.qty||0).toLocaleString()}</td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{(t.price||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                <td style={tTd({fontSize:12})}>{t.currency}</td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>{(t.fees||0).toLocaleString("en-US",{minimumFractionDigits:2})}</td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600,color:t.type==="SELL"?"#1D9E75":T.textPrimary})}>{t.currency} {total.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                <td style={tTd()}>
                                  <div style={{display:"flex",gap:4}}>
                                    {isAdmin && <button onClick={()=>{ txnEditOrigRef.current = { ...t }; setTxnEditId(t.id); }} title="Edit holding" style={{background:"rgba(37,99,235,0.08)",border:"1px solid #2563EB",borderRadius:5,color:"#2563EB",padding:"3px 8px",cursor:"pointer",fontSize:11}}>✎</button>}
                                    {isAdmin && <button onClick={()=>deleteTxn(t.id)} style={{background:"none",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 8px",cursor:"pointer",fontSize:11}}>✕</button>}
                                  </div>
                                </td>
                              </React.Fragment>
                            )}
                          </tr>
                        );
                      })
                      ) : (
                        /* Grouped by fund — collapsible rows */
                        fundNames.map(fundName => {
                          const fundTxns = groups[fundName];
                          const isCollapsed = txnCollapsed.has(fundName);
                          const fundTotal = fundTxns.reduce((s,t)=>s+(t.qty*t.price+(t.fees||0)),0);
                          const fundCcy = fundTxns[0]?.currency || "INR";
                          const toggleCollapse = () => setTxnCollapsed(prev => {
                            const next = new Set(prev);
                            if (next.has(fundName)) next.delete(fundName); else next.add(fundName);
                            return next;
                          });
                          return (
                            <React.Fragment key={fundName}>
                              {/* Fund header row */}
                              {/* Cell layout — admin (10 cols): [checkbox+date]=2 | [holding]=1 | [type→fees]=5 | [total]=1 | [actions]=1
                                  non-admin (9 cols):           [date+holding]=2 | [type]=1   | [qty→fees]=4 | [total]=1 | [actions]=1
                                  This keeps the subtotal aligned under the TOTAL header. */}
                              <tr onClick={toggleCollapse} style={{background:T.navActiveBg,cursor:"pointer",borderTop:`2px solid ${T.cardBorder}`}}>
                                <td colSpan={2} style={{...tTd(),fontWeight:700,fontSize:12,color:T.textPrimary,padding:"8px 12px"}}>
                                  <span style={{marginRight:8,fontSize:10,color:T.textMuted}}>{isCollapsed?"▶":"▼"}</span>
                                  {fundName}
                                </td>
                                <td style={tTd()}>
                                  {isAdmin && (
                                    <input type="checkbox" style={{cursor:"pointer",marginRight:6}}
                                      checked={fundTxns.every(t=>txnSelected.has(t.id))}
                                      onChange={e => e.target.checked
                                        ? setTxnSelected(p=>{const n=new Set(p);fundTxns.forEach(t=>n.add(t.id));return n;})
                                        : setTxnSelected(p=>{const n=new Set(p);fundTxns.forEach(t=>n.delete(t.id));return n;})}
                                    />
                                  )}
                                  <span style={{fontSize:10,color:T.textMuted,background:T.cardBg,padding:"2px 7px",borderRadius:4,border:`1px solid ${T.cardBorder}`}}>
                                    {fundTxns.length} txns
                                  </span>
                                </td>
                                <td colSpan={isAdmin ? 5 : 4} style={tTd()}></td>
                                <td style={{...tTd(),fontWeight:700,fontSize:12,color:T.brand,fontFamily:"'Inter', system-ui, sans-serif",textAlign:"left"}}>
                                  {fundCcy} {fundTotal.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}
                                </td>
                                <td style={tTd()}></td>
                              </tr>
                              {/* Fund transaction rows */}
                              {!isCollapsed && fundTxns.map((t,i)=>{
                                const total = t.qty * t.price + (t.fees||0);
                                const typeCol = t.type==="BUY"?"#2563EB":t.type==="SELL"?"#1D9E75":t.type==="DIVIDEND"?"#7C3AED":t.type==="STP_IN"||t.type==="SWITCH_IN"?"#0891B2":t.type==="STP_OUT"||t.type==="SWITCH_OUT"?"#D97706":"#6B7280";
                                const isEditing = txnEditId===t.id;
                                return (
                                  <tr key={t.id} style={{background:i%2===0?"transparent":`${T.cardBorder}44`}}>
                                    {isEditing ? (
                                      <React.Fragment>
                                        <td style={tTd()}><input type="date" style={{...inp(),fontSize:11,padding:"3px 6px",width:120}} defaultValue={t.date} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,date:e.target.value}:x))}/></td>
                                        <td style={tTd({fontSize:11,color:T.textMuted})}>{t.holding}</td>
                                        <td style={tTd()}><select style={{...inp(),fontSize:11,padding:"3px 6px"}} defaultValue={t.type} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,type:e.target.value}:x))}>{["BUY","SELL","SIP","STP_IN","STP_OUT","SWITCH_IN","SWITCH_OUT","DIVIDEND","REDEEM"].map(tp=><option key={tp}>{tp}</option>)}</select></td>
                                        <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:80}} defaultValue={t.qty} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,qty:parseFloat(e.target.value)||0}:x))}/></td>
                                        <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:90}} defaultValue={t.price} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,price:parseFloat(e.target.value)||0}:x))}/></td>
                                        <td style={tTd()}>{t.currency}</td>
                                        <td style={tTd()}><input type="number" style={{...inp(),fontSize:11,padding:"3px 6px",width:70}} defaultValue={t.fees||0} onBlur={e=>setTransactions(p=>p.map(x=>x.id===t.id?{...x,fees:parseFloat(e.target.value)||0}:x))}/></td>
                                        <td style={tTd()}>—</td>
                                        <td style={tTd()}><button onClick={()=>{ const updated=transactions.find(x=>x.id===t.id); if(updated) { updateTransaction(updated.id,updated).catch(()=>{}); const orig = txnEditOrigRef.current; if (orig && orig.id===updated.id) postTxnsToHoldings([[orig,-1],[updated,1]]); } txnEditOrigRef.current = null; setTxnEditId(null); }} style={{background:"#1D9E75",border:"none",borderRadius:4,color:"white",padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>✓</button></td>
                                      </React.Fragment>
                                    ) : (
                                      <React.Fragment>
                                        {isAdmin && (
                                          <td style={{...tTd(),width:32,padding:"10px 8px"}}>
                                            <input type="checkbox" style={{cursor:"pointer"}}
                                              checked={txnSelected.has(t.id)}
                                              onChange={()=>txnToggle(t.id)}/>
                                          </td>
                                        )}
                                        <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,paddingLeft:24}}>{t.date}</td>
                                        <td style={{...tTd(),fontSize:12,color:T.textSecondary}}>{t.holding}</td>
                                        <td style={tTd()}><span style={{fontSize:10,fontWeight:700,color:typeCol,background:`${typeCol}15`,padding:"2px 8px",borderRadius:5,border:`1px solid ${typeCol}33`}}>{t.type}</span></td>
                                        <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif"}}>{(t.qty||0).toLocaleString("en-IN",{maximumFractionDigits:4})}</td>
                                        <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif"}}>{(t.price||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:4})}</td>
                                        <td style={{...tTd(),fontSize:12}}>{t.currency}</td>
                                        <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif",fontSize:12}}>{(t.fees||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                                        <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600}}>{t.currency} {total.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                        <td style={tTd()}>
                                          <div style={{display:"flex",gap:4}}>
                                            <button onClick={()=>{ txnEditOrigRef.current = { ...t }; setTxnEditId(t.id); }} style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:4,color:T.textMuted,padding:"2px 7px",cursor:"pointer",fontSize:11}}>✎</button>
                                            <button onClick={()=>deleteTxn(t.id)} style={{background:"transparent",border:"1px solid rgba(220,38,38,0.3)",borderRadius:4,color:"#A32D2D",padding:"2px 7px",cursor:"pointer",fontSize:11}}>✕</button>
                                          </div>
                                        </td>
                                      </React.Fragment>
                                    )}
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
                );
              })()}
              <div style={{marginTop:10,fontSize:11,color:T.textSecondary,padding:"8px 12px",background:T.cardBg,borderRadius:6,border:`1px solid ${T.cardBorder}`}}>
                ℹ Transactions are used to calculate IRR in the <strong style={{color:"#2563EB",cursor:"pointer"}} onClick={()=>setTab("irr")}>IRR Analysis tab</strong>. Import bulk transactions via <strong style={{color:"#2563EB",cursor:"pointer"}} onClick={()=>setTab("upload")}>Upload Data</strong>.
              </div>

              {/* ── Reconcile Transactions → Holdings modal ── */}
              {reconcileModal && (() => {
                const { rows, clientId, clientName } = reconcileModal;
                const toCreate = rows.filter(r => r.selected && !r.closed);
                const toggle = (i) => setReconcileModal(m => ({ ...m, rows: m.rows.map((r,idx)=>idx===i?{...r,selected:!r.selected}:r) }));
                const toggleAll = (val) => setReconcileModal(m => ({ ...m, rows: m.rows.map(r=>r.closed?r:{...r,selected:val}) }));
                const fmt = (n,d=2) => (+n).toLocaleString("en-IN",{minimumFractionDigits:d,maximumFractionDigits:d});

                const doReconcile = async () => {
                  // Guess class from name + currency
                  const guessClass = (name, currency) => {
                    const n = (name||"").toUpperCase();
                    const isINR = (currency||"").toUpperCase() === "INR";
                    if (n.includes(" ETF") || n.endsWith("ETF") || n.includes("ISHARES") || n.includes("VANGUARD") || n.includes("SPDR")) return "ETF";
                    if (n.includes("FUND") || n.includes("UCITS") || n.includes("MUTUAL") || n.includes("ACC") || n.match(/\b(GROWTH|REGULAR|DIRECT)\s+PLAN\b/)) {
                      return isINR ? "Indian Mutual Funds" : "Global Mutual Funds";
                    }
                    if (isINR) return "Indian Equities";
                    if (n.match(/\b(INC|CORP|CORPORATION|LTD|PLC|AG|SA|NV|GMBH|HOLDINGS|GROUP|COMPANIES|COMPANY|TECHNOLOGIES|SYSTEMS)\b/)) return "Global Equities";
                    return "Unclassified";
                  };
                  let created = 0, failed = 0;
                  const newHoldings = [];
                  for (const r of toCreate) {
                    const isINR = (r.currency||"").toUpperCase() === "INR";
                    const h = {
                      id: `hld_${Date.now()}_${r.name.replace(/\W/g,"").slice(0,10)}_${created}`,
                      name: r.name,
                      // Store the transaction key as the ticker. Previously this was
                      // hard-coded "-", which left nothing stable to match on and
                      // also meant PerfTable had no symbol to price against.
                      ticker: (r.txnKey || r.name || "-").trim() || "-",
                      class: guessClass(r.name, r.currency),
                      exchange: isINR ? "NSE" : "—",
                      currency: r.currency || "USD",
                      qty: r.netQty,
                      costPrice: r.avgCost,
                      currentPrice: r.avgCost,
                      country: isINR ? "India" : "—",
                      listed: true,
                      isin: "-",
                      manager: "Self-Managed",
                      // addHolding() maps camelCase -> snake_case itself; passing the
                      // snake_case key here silently failed to match, so the flag
                      // was written as false and txn deltas double-counted qty.
                      reconciledFromTxns: true,
                    };
                    const err = await addHolding(h).then(()=>null).catch(e=>e);
                    if (err) { console.error("[Reconcile] addHolding failed:", err?.message||err, h.name); failed++; continue; }
                    await setHoldingOwner(String(h.id), clientId).catch(()=>{});
                    newHoldings.push(h);
                    created++;
                  }
                  if (newHoldings.length > 0) {
                    setHoldings(p => [...p, ...newHoldings]);
                    setHoldingOwners(prev => {
                      const next = {...prev};
                      newHoldings.forEach(h => { next[String(h.id)] = clientId; });
                      return next;
                    });
                    logAudit("RECONCILE_TRANSACTIONS_TO_HOLDINGS", `${created} holdings created for ${clientName}`);
                  }
                  setReconcileModal(null);
                  showToast(`✓ Created ${created} holding${created!==1?"s":""}${failed>0?` · ${failed} failed`:""} for ${clientName}`, failed>0?"#D97706":"#1D9E75", 5000);
                };

                const allSelected = rows.filter(r=>!r.closed).every(r=>r.selected);
                const someSelected = rows.filter(r=>!r.closed).some(r=>r.selected);

                return (
                  <div style={{position:"fixed",inset:0,zIndex:10001,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                    <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:780,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
                      <div style={{padding:"20px 24px",borderBottom:"1px solid #e2e8f0"}}>
                        <div style={{fontSize:16,fontWeight:700,color:"#0f172a",marginBottom:4}}>🔄 Reconcile Transactions → Holdings</div>
                        <div style={{fontSize:12,color:"#64748b"}}>
                          {rows.length} unique securit{rows.length===1?"y":"ies"} in <strong>{clientName}</strong>'s transactions have no matching holding. Review and create the ones you want.
                        </div>
                      </div>

                      <div style={{flex:1,overflowY:"auto",padding:"4px 24px"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                          <thead style={{position:"sticky",top:0,background:"#fff",boxShadow:"0 1px 0 #e2e8f0"}}>
                            <tr>
                              <th style={{textAlign:"left",padding:"10px 8px",width:32}}>
                                <input type="checkbox" checked={allSelected} ref={el=>{if(el)el.indeterminate=!allSelected&&someSelected;}}
                                  onChange={e=>toggleAll(e.target.checked)} style={{cursor:"pointer"}}/>
                              </th>
                              <th style={{textAlign:"left",padding:"10px 8px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em"}}>Holding</th>
                              <th style={{textAlign:"right",padding:"10px 8px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em"}}>Net Qty</th>
                              <th style={{textAlign:"right",padding:"10px 8px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em"}}>Avg Cost</th>
                              <th style={{textAlign:"right",padding:"10px 8px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em"}}>Value</th>
                              <th style={{textAlign:"right",padding:"10px 8px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em"}}>Txns</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r,i)=>(
                              <tr key={i} style={{borderTop:"1px solid #f1f5f9",opacity:r.closed?0.5:1}}>
                                <td style={{padding:"10px 8px"}}>
                                  <input type="checkbox" checked={r.selected} disabled={r.closed} onChange={()=>toggle(i)} style={{cursor:r.closed?"not-allowed":"pointer"}}/>
                                </td>
                                <td style={{padding:"10px 8px",fontSize:12,fontWeight:600,color:"#0f172a"}}>
                                  {r.name}
                                  {r.closed && <span style={{marginLeft:8,fontSize:9,padding:"2px 6px",background:"rgba(220,38,38,0.1)",color:"#A32D2D",borderRadius:4,fontWeight:700}}>CLOSED</span>}
                                </td>
                                <td style={{padding:"10px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,color:r.netQty<=0?"#A32D2D":"#0f172a"}}>{fmt(r.netQty,4)}</td>
                                <td style={{padding:"10px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,color:"#64748b"}}>{r.currency} {fmt(r.avgCost,2)}</td>
                                <td style={{padding:"10px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,fontWeight:600,color:"#0f172a"}}>{r.currency} {fmt(r.netQty*r.avgCost,0)}</td>
                                <td style={{padding:"10px 8px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,color:"#64748b"}}>{r.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div style={{padding:"14px 24px",borderTop:"1px solid #e2e8f0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                        <div style={{fontSize:11,color:"#94a3b8"}}>
                          {toCreate.length} of {rows.filter(r=>!r.closed).length} open positions selected · Closed positions hidden from creation
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>setReconcileModal(null)}
                            style={{padding:"8px 18px",borderRadius:7,border:"1px solid #e2e8f0",background:"#f8fafc",color:"#64748b",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                            Cancel
                          </button>
                          <button onClick={doReconcile} disabled={toCreate.length===0}
                            style={{padding:"8px 18px",borderRadius:7,border:"none",background:toCreate.length>0?"#854F0B":"#e2e8f0",color:toCreate.length>0?"#fff":"#94a3b8",fontSize:12,fontWeight:700,cursor:toCreate.length>0?"pointer":"not-allowed"}}>
                            Create {toCreate.length} Holding{toCreate.length!==1?"s":""}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in transactions tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══════════════════════════════════════════════
            MERGE DUPLICATE HOLDINGS MODAL (global — works from any tab)
        ══════════════════════════════════════════════ */}
        {mergeModal && (() => {
          const { groups } = mergeModal;
          const fmt = (n,d=2) => (+n).toLocaleString("en-IN",{minimumFractionDigits:d,maximumFractionDigits:d});
          const setCanonical = (gi, idx) => setMergeModal(m => ({...m, groups: m.groups.map((g,i)=>i===gi?{...g,canonicalIdx:idx}:g)}));
          const setSkip      = (gi, val) => setMergeModal(m => ({...m, groups: m.groups.map((g,i)=>i===gi?{...g,skip:val}:g)}));
          const groupsToMerge = groups.filter(g => !g.skip);
          const totalToMerge  = groupsToMerge.reduce((s,g) => s + g.holdings.length - 1, 0); // duplicates that will be removed

          const doMerge = async () => {
            let mergedGroups = 0, failed = 0;
            const removedIds = new Set();
            const updatedHoldings = [];
            const txnUpdates = []; // {id, newHolding}

            // ── Snapshot for undo: capture full state BEFORE any mutation ──
            // We need to record (a) the canonical holdings as they are right now
            // so we can restore qty/costPrice/preserved fields, (b) the duplicate
            // holdings about to be deleted, (c) the holding_owners mapping for
            // those deleted holdings, and (d) every transaction that's about to
            // get its `holding` field rewritten, with its OLD holding name.
            const snapshotCanonicals  = [];
            const snapshotDeleted     = [];
            const snapshotOwners      = {};
            const snapshotTxnReroutes = [];
            for (const group of groupsToMerge) {
              const canon = group.holdings[group.canonicalIdx];
              const others = group.holdings.filter((_,i) => i !== group.canonicalIdx);
              snapshotCanonicals.push({ ...canon });
              for (const o of others) {
                snapshotDeleted.push({ ...o });
                const oid = String(o.id);
                if (holdingOwners[oid] != null) snapshotOwners[oid] = holdingOwners[oid];
                const refTxns = transactions.filter(t => (t.holding||"").toLowerCase().trim() === (o.name||"").toLowerCase().trim());
                for (const t of refTxns) snapshotTxnReroutes.push({ id: t.id, oldHolding: t.holding, newHolding: canon.name });
              }
            }

            for (const group of groupsToMerge) {
              const canon = group.holdings[group.canonicalIdx];
              const others = group.holdings.filter((_,i) => i !== group.canonicalIdx);

              // Compute merged qty + weighted avg cost
              let totalQty = +canon.qty || 0;
              let totalCost = (+canon.qty||0) * (+canon.costPrice||0);
              for (const o of others) {
                totalQty += +o.qty || 0;
                totalCost += (+o.qty||0) * (+o.costPrice||0);
              }
              const newAvgCost = totalQty > 0 ? totalCost / totalQty : +canon.costPrice || 0;

              // ── Preserve metadata from deleted entries ──
              // If the canonical has a "weak" value for a field (empty/dash/Unclassified
              // /Self-Managed), and any other holding in the group has a "strong" value,
              // pull that value forward so we don't lose information when merging.
              const isWeak = (v, weaks) => {
                const s = (v==null?"":String(v)).trim();
                if (!s) return true;
                return weaks.some(w => s.toLowerCase() === w.toLowerCase());
              };
              const pickStrong = (field, weaks) => {
                if (!isWeak(canon[field], weaks)) return canon[field];
                for (const o of others) {
                  if (!isWeak(o[field], weaks)) return o[field];
                }
                return canon[field]; // fall back to canon's weak value
              };
              const preserved = {
                manager:  pickStrong("manager",  ["", "self-managed", "-", "—"]),
                advisor:  pickStrong("advisor",  ["", "unassigned", "-", "—"]),
                sector:   pickStrong("sector",   ["", "-", "—", "unknown", "other"]),
                country:  pickStrong("country",  ["", "-", "—", "other"]),
                ticker:   pickStrong("ticker",   ["", "-", "—"]),
                class:    pickStrong("class",    ["", "unclassified"]),
                isin:     pickStrong("isin",     ["", "-", "—"]),
                exchange: pickStrong("exchange", ["", "-", "—"]),
                capSize:  pickStrong("capSize",  ["", "n/a", "-", "—"]),
              };
              // Diagnostic — log what got pulled forward from removed entries
              const pulledForward = Object.entries(preserved).filter(([k,v]) => v !== canon[k] && v != null && v !== "");
              if (pulledForward.length > 0) {
                console.log(`[Merge] Preserved from duplicates → canonical "${canon.name}":`, Object.fromEntries(pulledForward));
              }

              // Update canonical holding in Supabase (qty + cost + preserved fields)
              // Preserve the reconciled flag if ANY holding in the group carried it.
              // Hard-setting false here re-armed txnQtyDeltas on a qty that was already
              // the netted total, ratcheting 2x into 3x on every merge.
              const groupWasReconciled = [canon, ...others].some(x => !!(x.reconciledFromTxns || x.reconciled_from_txns));
              const updatedCanon = { ...canon, ...preserved, qty: totalQty, costPrice: newAvgCost, reconciledFromTxns: groupWasReconciled };
              const upErr = await updateHolding(canon.id, updatedCanon).then(()=>null).catch(e=>e);
              if (upErr) { console.error("[Merge] updateHolding failed:", upErr?.message||upErr, canon.name); failed++; continue; }
              updatedHoldings.push(updatedCanon);

              // Re-point transactions referencing the OTHER names to the canonical name
              for (const o of others) {
                const refTxns = transactions.filter(t => (t.holding||"").toLowerCase().trim() === (o.name||"").toLowerCase().trim());
                for (const t of refTxns) {
                  txnUpdates.push({ id: t.id, newHolding: canon.name });
                  await updateTransaction(t.id, { ...t, holding: canon.name }).catch(e =>
                    console.error("[Merge] updateTransaction failed:", e?.message||e, t.id)
                  );
                }
              }

              // Delete the duplicate holdings
              for (const o of others) {
                await removeHoldingOwner(String(o.id)).catch(()=>{});
                const dErr = await deleteHolding(o.id).then(()=>null).catch(e=>e);
                if (dErr) { console.error("[Merge] deleteHolding failed:", dErr?.message||dErr, o.name); continue; }
                removedIds.add(String(o.id));
              }
              mergedGroups++;
            }

            // Update local state
            if (updatedHoldings.length || removedIds.size) {
              setHoldings(p => p
                .filter(h => !removedIds.has(String(h.id)))
                .map(h => {
                  const upd = updatedHoldings.find(u => String(u.id) === String(h.id));
                  return upd ? { ...h, ...upd } : h;
                })
              );
            }
            if (txnUpdates.length) {
              setTransactions(p => p.map(t => {
                const upd = txnUpdates.find(u => u.id === t.id);
                return upd ? { ...t, holding: upd.newHolding } : t;
              }));
            }
            if (removedIds.size) {
              setHoldingOwners(p => {
                const next = {...p};
                for (const id of removedIds) delete next[id];
                return next;
              });
            }
            logAudit("MERGE_HOLDINGS", `${mergedGroups} groups · ${totalToMerge} duplicates removed · ${txnUpdates.length} transactions re-pointed`);
            setMergeModal(null);

            // ── Persist snapshot to merge_history so this merge can be undone ──
            let mergeId = null;
            if (mergedGroups > 0) {
              try {
                const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                const clientForDesc = mergeModal?.clientId || null;
                const clientName = clientForDesc ? (visibleClients.find(c=>c.id===clientForDesc)?.name || "—") : "all clients";
                const description = `Merged ${mergedGroups} group${mergedGroups!==1?"s":""} · ${totalToMerge} duplicate${totalToMerge!==1?"s":""} removed · ${txnUpdates.length} txn${txnUpdates.length!==1?"s":""} re-pointed (${clientName})`;
                const payload = {
                  description,
                  client_id: clientForDesc,
                  snapshot: {
                    version: 1,
                    canonicals: snapshotCanonicals,
                    deleted: snapshotDeleted,
                    owners: snapshotOwners,
                    txnReroutes: snapshotTxnReroutes,
                  },
                };
                const res = await fetch(`${SUPABASE_URL}/rest/v1/merge_history`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "apikey": ANON_KEY,
                    "Authorization": `Bearer ${ANON_KEY}`,
                    "Prefer": "return=representation",
                  },
                  body: JSON.stringify(payload),
                });
                if (res.ok) {
                  const rows = await res.json();
                  mergeId = rows?.[0]?.id || null;
                  console.log("[Merge] Snapshot saved, id:", mergeId);
                } else {
                  console.warn("[Merge] Snapshot save failed:", res.status, await res.text());
                }
              } catch (e) {
                console.warn("[Merge] Snapshot save threw:", e?.message || e);
              }
            }

            // Toast with optional Undo link (15s window). For older merges,
            // user can use the "📋 Merge History" panel.
            const toastMsg = `✓ Merged ${mergedGroups} group${mergedGroups!==1?"s":""} · removed ${totalToMerge} duplicate${totalToMerge!==1?"s":""}${failed>0?` · ${failed} failed`:""}${mergeId?" · click 📋 Merge History to undo":""}`;
            showToast(toastMsg, failed>0?"#D97706":"#1D9E75", 8000);
          };

          return (
            <div style={{position:"fixed",inset:0,zIndex:10001,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:880,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
                <div style={{padding:"20px 24px",borderBottom:"1px solid #e2e8f0"}}>
                  <div style={{fontSize:16,fontWeight:700,color:"#0f172a",marginBottom:4}}>🔗 Merge Duplicate Holdings</div>
                  <div style={{fontSize:12,color:"#64748b"}}>
                    Found {groups.length} group{groups.length>1?"s":""} of likely duplicates. For each group, pick the entry to <strong>keep</strong> — the others will be merged into it (quantities summed, cost recomputed, transactions re-pointed).
                  </div>
                </div>

                <div style={{flex:1,overflowY:"auto",padding:"8px 24px"}}>
                  {groups.map((g, gi) => {
                    // Defensive: if a group ended up with holdings at DIFFERENT custodians
                    // (e.g., from older detection logic that didn't key on manager, or from
                    // a manual edit after detection), warn loudly and auto-skip by default.
                    const distinctMgrs = [...new Set(g.holdings.map(h => (h.manager||"Self-Managed").trim()))];
                    const mgrMismatch = distinctMgrs.length > 1;
                    return (
                    <div key={gi} style={{marginBottom:18,padding:"14px",border:`1px solid ${mgrMismatch?"#A32D2D":g.skip?"#e2e8f0":"#FED7AA"}`,borderRadius:10,background:mgrMismatch?"rgba(220,38,38,0.06)":g.skip?"#f8fafc":"rgba(254,215,170,0.15)",opacity:g.skip?0.55:1}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                        <div style={{fontSize:13,fontWeight:700,color:mgrMismatch?"#A32D2D":"#9A3412"}}>
                          Group {gi+1} · {g.holdings.length} holdings
                          {mgrMismatch && <span style={{marginLeft:8,fontSize:10,padding:"2px 6px",background:"#A32D2D",color:"#fff",borderRadius:3,fontWeight:700}}>⚠ DIFFERENT CUSTODIANS</span>}
                        </div>
                        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:11,color:"#64748b"}}>
                          <input type="checkbox" checked={g.skip} onChange={e=>setSkip(gi, e.target.checked)} style={{cursor:"pointer"}}/>
                          Skip this group
                        </label>
                      </div>
                      {mgrMismatch && (
                        <div style={{fontSize:11,color:"#7F1D1D",background:"rgba(220,38,38,0.08)",padding:"8px 10px",borderRadius:6,marginBottom:10,lineHeight:1.5}}>
                          <strong>⚠ These holdings have different custodians ({distinctMgrs.join(" vs ")}).</strong> They likely represent the same security held at separate brokers — merging them will lose the custodian breakdown and re-point all transactions to one custodian. We strongly recommend skipping this group.
                        </div>
                      )}

                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,background:"#fff",borderRadius:6,overflow:"hidden"}}>
                        <thead>
                          <tr style={{background:"#f8fafc"}}>
                            <th style={{textAlign:"left",padding:"8px 10px",width:60,fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Keep</th>
                            <th style={{textAlign:"left",padding:"8px 10px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Name</th>
                            <th style={{textAlign:"left",padding:"8px 10px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Ticker</th>
                            <th style={{textAlign:"left",padding:"8px 10px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Class</th>
                            <th style={{textAlign:"right",padding:"8px 10px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Qty</th>
                            <th style={{textAlign:"right",padding:"8px 10px",fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Avg Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.holdings.map((h, hi) => {
                            const isCanon = hi === g.canonicalIdx;
                            const hasTicker = h.ticker && h.ticker !== "-" && h.ticker !== "—";
                            const hasClass  = h.class && h.class !== "Unclassified";
                            return (
                              <tr key={h.id} style={{borderTop:"1px solid #f1f5f9",background:isCanon?"rgba(22,163,74,0.04)":"transparent"}}>
                                <td style={{padding:"8px 10px"}}>
                                  <input type="radio" name={`canon-${gi}`} checked={isCanon} disabled={g.skip}
                                    onChange={()=>setCanonical(gi, hi)} style={{cursor:g.skip?"not-allowed":"pointer"}}/>
                                </td>
                                <td style={{padding:"8px 10px",fontSize:12,fontWeight:isCanon?700:500,color:"#0f172a"}}>
                                  {h.name}
                                  {isCanon && <span style={{marginLeft:6,fontSize:9,padding:"1px 5px",background:"#1D9E75",color:"#fff",borderRadius:3,fontWeight:700}}>CANONICAL</span>}
                                </td>
                                <td style={{padding:"8px 10px",fontSize:11,color:hasTicker?"#0f172a":"#94a3b8"}}>
                                  {hasTicker ? h.ticker : "—"}
                                </td>
                                <td style={{padding:"8px 10px",fontSize:11,color:hasClass?"#0f172a":"#94a3b8"}}>
                                  {hasClass ? h.class : "Unclassified"}
                                </td>
                                <td style={{padding:"8px 10px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:11}}>{fmt(h.qty,4)}</td>
                                <td style={{padding:"8px 10px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:11}}>{h.currency} {fmt(h.costPrice,2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {!g.skip && (
                        <div style={{marginTop:8,fontSize:10,color:"#9A3412",paddingLeft:6}}>
                          → After merge: total qty {fmt(g.holdings.reduce((s,h)=>s+(+h.qty||0),0),4)} ·
                          new avg cost {(()=>{
                            const tq = g.holdings.reduce((s,h)=>s+(+h.qty||0),0);
                            const tc = g.holdings.reduce((s,h)=>s+(+h.qty||0)*(+h.costPrice||0),0);
                            return tq>0 ? `${g.holdings[g.canonicalIdx].currency} ${fmt(tc/tq,2)}` : "—";
                          })()} ·
                          {g.holdings.length-1} duplicate{g.holdings.length>2?"s":""} will be deleted
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>

                <div style={{padding:"14px 24px",borderTop:"1px solid #e2e8f0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <div style={{fontSize:11,color:"#94a3b8"}}>
                    {totalToMerge} duplicate{totalToMerge!==1?"s":""} across {groupsToMerge.length} group{groupsToMerge.length!==1?"s":""} will be removed
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setMergeModal(null)}
                      style={{padding:"8px 18px",borderRadius:7,border:"1px solid #e2e8f0",background:"#f8fafc",color:"#64748b",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      Cancel
                    </button>
                    <button onClick={()=>{ requireConfirm(`Merge ${groupsToMerge.length} group${groupsToMerge.length>1?"s":""}? This will delete ${totalToMerge} duplicate holding${totalToMerge!==1?"s":""} and re-point their transactions. Cannot be undone.`, doMerge); }}
                      disabled={groupsToMerge.length===0}
                      style={{padding:"8px 18px",borderRadius:7,border:"none",background:groupsToMerge.length>0?"#1D9E75":"#e2e8f0",color:groupsToMerge.length>0?"#fff":"#94a3b8",fontSize:12,fontWeight:700,cursor:groupsToMerge.length>0?"pointer":"not-allowed"}}>
                      Merge {groupsToMerge.length} Group{groupsToMerge.length!==1?"s":""}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══════════════════════════════════════════════
            MERGE HISTORY MODAL — view past merges, undo if needed
        ══════════════════════════════════════════════ */}
        {mergeHistoryOpen && (() => {
          const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
          const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
          const fmtTs = (ts) => {
            try {
              const d = new Date(ts);
              const now = new Date();
              const diffMs = now - d;
              const mins = Math.floor(diffMs / 60000);
              if (mins < 1) return "just now";
              if (mins < 60) return `${mins} min ago`;
              const hrs = Math.floor(mins / 60);
              if (hrs < 24) return `${hrs}h ago`;
              const days = Math.floor(hrs / 24);
              if (days < 7) return `${days}d ago`;
              return d.toLocaleDateString();
            } catch { return ts; }
          };
          const undoMerge = async (entry) => {
            requireConfirm(
              `Undo this merge?\n\n"${entry.description}"\n\nThis will re-create the deleted holdings and restore their transactions. Any edits you made to the merged holding AFTER the merge will be lost.`,
              async () => {
                const snap = entry.snapshot;
                if (!snap || snap.version !== 1) {
                  showToast("⚠ Snapshot format not recognised — cannot undo", "#A32D2D", 5000);
                  return;
                }
                let restoredHoldings = 0, restoredTxns = 0, failed = 0;

                // 1. Re-create deleted holdings with original IDs/fields
                for (const h of (snap.deleted || [])) {
                  try {
                    // strip any volatile/derived fields
                    const { client, family, valueUSD, costUSD, gainPct, rawQty, effectiveQty, ...clean } = h;
                    await addHolding(clean);
                    const cid = snap.owners?.[String(h.id)] || snap.owners?.[h.id];
                    if (cid) await setHoldingOwner(String(h.id), cid).catch(()=>{});
                    restoredHoldings++;
                  } catch (e) {
                    console.error("[Undo] failed to restore holding", h.name, e?.message||e);
                    failed++;
                  }
                }

                // 2. Restore canonicals to their pre-merge state (qty, costPrice, preserved fields back to original)
                for (const c of (snap.canonicals || [])) {
                  try {
                    const { client, family, valueUSD, costUSD, gainPct, rawQty, effectiveQty, ...clean } = c;
                    await updateHolding(c.id, clean);
                  } catch (e) {
                    console.error("[Undo] failed to restore canonical", c.name, e?.message||e);
                    failed++;
                  }
                }

                // 3. Restore transaction holdings to their original names
                for (const r of (snap.txnReroutes || [])) {
                  try {
                    const t = transactions.find(x => x.id === r.id);
                    if (t) await updateTransaction(r.id, { ...t, holding: r.oldHolding });
                    restoredTxns++;
                  } catch (e) {
                    console.error("[Undo] failed to restore txn", r.id, e?.message||e);
                    failed++;
                  }
                }

                // 4. Mark this merge_history row as undone
                try {
                  await fetch(`${SUPABASE_URL}/rest/v1/merge_history?id=eq.${entry.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type":"application/json", "apikey":ANON_KEY, "Authorization":`Bearer ${ANON_KEY}` },
                    body: JSON.stringify({ undone_at: new Date().toISOString() }),
                  });
                } catch(_e) {}

                // 5. Update local state
                setHoldings(p => {
                  const next = [...p];
                  // Add back restored holdings
                  for (const h of (snap.deleted || [])) {
                    if (!next.some(x => String(x.id) === String(h.id))) {
                      const { client, family, valueUSD, costUSD, gainPct, rawQty, effectiveQty, ...clean } = h;
                      next.push(clean);
                    }
                  }
                  // Restore canonicals
                  for (const c of (snap.canonicals || [])) {
                    const idx = next.findIndex(x => String(x.id) === String(c.id));
                    if (idx >= 0) {
                      const { client, family, valueUSD, costUSD, gainPct, rawQty, effectiveQty, ...clean } = c;
                      next[idx] = { ...next[idx], ...clean };
                    }
                  }
                  return next;
                });
                setHoldingOwners(prev => {
                  const next = {...prev};
                  for (const [hId, cId] of Object.entries(snap.owners || {})) {
                    next[hId] = cId;
                  }
                  return next;
                });
                setTransactions(p => p.map(t => {
                  const r = (snap.txnReroutes || []).find(x => x.id === t.id);
                  return r ? { ...t, holding: r.oldHolding } : t;
                }));

                logAudit("UNDO_MERGE", `${restoredHoldings} holdings + ${restoredTxns} txns restored`);
                setMergeHistory(prev => prev.map(m => m.id === entry.id ? { ...m, undone_at: new Date().toISOString() } : m));
                showToast(`✓ Undone · ${restoredHoldings} holdings + ${restoredTxns} txns restored${failed>0?` · ${failed} errors`:""}`, failed>0?"#D97706":"#1D9E75", 5000);
              }
            );
          };

          const activeMerges = mergeHistory.filter(m => !m.undone_at);
          const undoneMerges = mergeHistory.filter(m => m.undone_at);

          return (
            <div style={{position:"fixed",inset:0,zIndex:10001,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:780,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
                <div style={{padding:"20px 24px",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:700,color:"#0f172a",marginBottom:4}}>📋 Merge History</div>
                    <div style={{fontSize:12,color:"#64748b"}}>Recent merges, newest first · Snapshots expire after 90 days</div>
                  </div>
                  <button onClick={()=>setMergeHistoryOpen(false)}
                    style={{background:"transparent",border:"none",fontSize:20,cursor:"pointer",color:"#64748b",padding:4}}>✕</button>
                </div>
                <div style={{flex:1,overflowY:"auto",padding:"12px 24px"}}>
                  {mergeHistory.length === 0 ? (
                    <div style={{textAlign:"center",padding:"60px 20px",color:"#94a3b8",fontSize:13}}>
                      No merges recorded yet.<br/>
                      <span style={{fontSize:11,marginTop:6,display:"block"}}>Snapshots are taken when you run Find Duplicates → Merge.</span>
                    </div>
                  ) : (
                    <>
                      {activeMerges.length > 0 && (
                        <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",margin:"4px 0 8px"}}>Active</div>
                      )}
                      {activeMerges.map(entry => (
                        <div key={entry.id} style={{padding:"12px 14px",border:"1px solid #FED7AA",borderRadius:8,marginBottom:10,background:"rgba(254,215,170,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,color:"#0f172a",marginBottom:3}}>{entry.description}</div>
                            <div style={{fontSize:10,color:"#64748b"}}>
                              {fmtTs(entry.created_at)} ·
                              {" "}{entry.snapshot?.deleted?.length || 0} deleted holding{(entry.snapshot?.deleted?.length||0)!==1?"s":""} ·
                              {" "}{entry.snapshot?.txnReroutes?.length || 0} transaction re-route{(entry.snapshot?.txnReroutes?.length||0)!==1?"s":""}
                            </div>
                          </div>
                          <button onClick={()=>undoMerge(entry)}
                            style={{padding:"6px 14px",borderRadius:6,border:"1px solid #A32D2D",background:"#fff",color:"#A32D2D",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                            ↶ Undo
                          </button>
                        </div>
                      ))}
                      {undoneMerges.length > 0 && (
                        <>
                          <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",margin:"16px 0 8px"}}>Already Undone</div>
                          {undoneMerges.map(entry => (
                            <div key={entry.id} style={{padding:"10px 14px",border:"1px solid #e2e8f0",borderRadius:8,marginBottom:8,background:"#f8fafc",opacity:0.7}}>
                              <div style={{fontSize:11,fontWeight:500,color:"#64748b",marginBottom:2}}>{entry.description}</div>
                              <div style={{fontSize:10,color:"#94a3b8"}}>Undone {fmtTs(entry.undone_at)} · originally {fmtTs(entry.created_at)}</div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div style={{padding:"12px 24px",borderTop:"1px solid #e2e8f0",fontSize:11,color:"#94a3b8",lineHeight:1.5}}>
                  <strong>Note:</strong> Undo restores deleted holdings, the canonical's qty/cost, and transaction holding-names to their pre-merge state. Any edits made to the merged holding AFTER the merge will be reverted. Manual changes to other (unrelated) holdings are not affected.
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══════════════════════════════════════════════
            WATCHLIST TAB
        ══════════════════════════════════════════════ */}
        {tab==="watchlist" && (function(){ try {
          // Request browser notification permission once
          if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().catch(()=>{});
          }
          // Scope watchlist to active client/family filter.
          // For non-admin: own watchlist only. For admin with no filter: all. With filter: matched client.
          // NOTE: We coerce both sides to String() because Supabase sometimes returns
          // UUIDs as objects/wrapped values, and a strict === comparison silently
          // fails. This is the same defensive pattern used for holdings.
          const scopedWL = (() => {
            if (!isAdmin) {
              // client login: own items only
              return watchlist.filter(w => String(w.clientId) === String(loggedInClientId));
            }
            if (selectedClients.size > 0) {
              // admin drilled into specific client(s) → show those clients' items only
              const sel = new Set([...selectedClients].map(String));
              return watchlist.filter(w => sel.has(String(w.clientId)));
            }
            if (selectedFamily !== "all") {
              const familyClientIds = new Set(
                clients
                  .filter(c => String(c.familyId) === String(selectedFamily))
                  .map(c => String(c.id))
              );
              return watchlist.filter(w => familyClientIds.has(String(w.clientId)));
            }
            // admin, no filter → show admin's own watchlist only
            return watchlist.filter(w => !w.clientId || w.clientId === "admin");
          })();
          // Diagnostic: items in the global watchlist that aren't in scope.
          // Helps identify clientId mismatches that would otherwise look like
          // "data lost". Used by the orphan-banner below.
          // True orphans = items with no clientId or "admin" (sentinel for
          // pre-tagging legacy data). Items legitimately belonging to other
          // clients are NOT orphans — they're someone else's watchlist, and
          // shouldn't trigger a reassign prompt when viewing a different client.
          const orphanWL = watchlist.filter(w =>
            !scopedWL.includes(w) &&
            (!w.clientId || w.clientId === "admin" || w.clientId === "")
          );
          const filteredWL = scopedWL.filter(w => {
            if (wlFilter==="near")  return w.targetPrice && Math.abs(w.currentPrice-w.targetPrice)/w.targetPrice < 0.05;
            if (wlFilter==="above") return w.currentPrice >= (w.targetPrice||Infinity);
            if (wlFilter==="below") return w.currentPrice <= (w.targetBuy||0) && w.targetBuy > 0;
            return true;
          });
          return <React.Fragment>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:17,fontWeight:700,display:"flex",alignItems:"center",gap:10}}>
                👁 Watchlist
                {isAdmin && selectedClients.size !== 1 && (
                  <span style={{fontSize:11,fontWeight:600,padding:"2px 10px",borderRadius:20,
                    background: "rgba(0,121,193,0.10)",
                    color: "#007878",
                    border: `1px solid rgba(0,121,193,0.3)`}}>
                    {selectedClients.size>1
                      ? `${selectedClients.size} clients selected`
                      : "🔧 Admin watchlist"}
                  </span>
                )}
              </div>
              <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                {isAdmin && selectedClients.size===0 && <span style={{color:T.textMuted}}>Select a client above to view their watchlist. </span>}
                Track assets you're considering. Set target and buy-level alerts.
                {watchlist.some(w=>w.currentPrice>0) && <span style={{marginLeft:8,fontSize:11,color:"#1D9E75",fontWeight:600}}>🟢 Prices live · updated {lastUpd.toLocaleTimeString()}</span>}
                {watchlist.length>0 && !watchlist.some(w=>w.currentPrice>0) && <span style={{marginLeft:8,fontSize:11,color:"#D97706"}}>⏳ Awaiting price refresh…</span>}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>refresh(true)} disabled={refreshing} style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,color:T.thText,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                <span style={{display:"inline-block",animation:refreshing?"spin 1s linear infinite":"none"}}>↻</span> Refresh Prices
              </button>
              {scopedWL.length > 0 && (
                <button onClick={()=>setWlAdvisorOpen(true)} title="AI advisor for this watchlist" style={{background:"linear-gradient(135deg,#7C3AED,#C026D3)",border:"none",borderRadius:8,color:"white",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                  🤖 AI Advisor
                </button>
              )}
              {scopedWL.length > 0 && (
                <button onClick={()=>setDeployOpen(true)} title="Plan how to deploy capital across watchlist items" style={{background:"#4F46E5",border:"none",borderRadius:8,color:"white",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                  💰 Deploy Capital
                </button>
              )}
              {scopedWL.length > 0 && (
                <div style={{position:"relative"}}>
                  <button onClick={()=>setWlExportOpen(p=>!p)} title="Export watchlist data" style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,color:T.thText,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                    ⬇ Export ▾
                  </button>
                  {wlExportOpen && (
                    <>
                      {/* Backdrop to close on outside click */}
                      <div onClick={()=>setWlExportOpen(false)} style={{position:"fixed",inset:0,zIndex:90}}/>
                      <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:100,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,boxShadow:"0 10px 25px rgba(0,0,0,0.15)",minWidth:220,overflow:"hidden"}}>
                        <button onClick={()=>{
                          // ── CSV export ──
                          // Builds a flat table with all relevant fields plus the full
                          // AI thesis. Uses scopedWL so it respects the active filter.
                          const headers = ["Asset","Ticker","Exchange","Currency","Current Price","1D %","Target Price","Buy Below","Weight %","vs Target %","vs Buy %","Alert Above","Alert Below","AI Thesis","Notes","Date Added"];
                          const rows = scopedWL.map(w => {
                            const vsTarget = (w.targetPrice && w.currentPrice) ? ((w.currentPrice - w.targetPrice) / w.targetPrice * 100) : null;
                            const vsBuy    = (w.targetBuy   && w.currentPrice) ? ((w.currentPrice - w.targetBuy)   / w.targetBuy   * 100) : null;
                            const thesis   = wlThesisMap[w.id] || w.notes || "";
                            return [
                              w.name||"",
                              w.ticker||"",
                              w.exchange||"",
                              w.currency||"",
                              (w.currentPrice||0).toFixed(4),
                              ((w.change1d||0)).toFixed(2)+"%",
                              w.targetPrice||"",
                              w.targetBuy||"",
                              (parseFloat(w.weight)||0).toFixed(2),
                              vsTarget !== null ? vsTarget.toFixed(2)+"%" : "",
                              vsBuy    !== null ? vsBuy.toFixed(2)+"%"    : "",
                              w.alertAbove ? "Yes" : "No",
                              w.alertBelow ? "Yes" : "No",
                              thesis,  // full thesis, no truncation
                              w.notes && w.notes !== thesis ? w.notes : "",
                              w.createdAt ? new Date(w.createdAt).toISOString().slice(0,10) : "",
                            ];
                          });
                          const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
                          const blob = new Blob([csv], { type:"text/csv;charset=utf-8" });
                          const url  = URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          const scopeLabel = selectedClients.size === 1
                            ? clients.find(c => String(c.id)===String([...selectedClients][0]))?.name?.replace(/\s+/g,"-").toLowerCase() || "client"
                            : selectedFamily !== "all" ? "family" : "all";
                          link.href = url;
                          link.download = `watchlist-${scopeLabel}-${new Date().toISOString().slice(0,10)}.csv`;
                          link.click();
                          URL.revokeObjectURL(url);
                          setWlExportOpen(false);
                          showToast(`✓ Exported ${scopedWL.length} items to CSV`, "#1D9E75", 2000);
                        }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"transparent",border:"none",padding:"10px 14px",cursor:"pointer",fontSize:12,color:T.textPrimary,textAlign:"left",borderBottom:`1px solid ${T.cardBorder}`}}
                          onMouseEnter={e=>e.currentTarget.style.background=T.thBg}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:14}}>📊</span>
                          <div>
                            <div style={{fontWeight:600}}>Export as CSV</div>
                            <div style={{fontSize:10,color:T.textSecondary,marginTop:1}}>For Excel · includes full AI thesis</div>
                          </div>
                        </button>
                        <button onClick={()=>{
                          // ── PDF export via browser print ──
                          // Open a new window with a print-friendly layout, trigger
                          // print dialog. User chooses "Save as PDF" in the dialog.
                          // Inline styles only — no external CSS needed.
                          const today = new Date().toLocaleDateString(undefined, {year:"numeric",month:"long",day:"numeric"});
                          const scopeName = selectedClients.size === 1
                            ? clients.find(c => String(c.id)===String([...selectedClients][0]))?.name || "Client"
                            : selectedFamily !== "all" ? (families.find(f => String(f.id)===String(selectedFamily))?.name || "Family") + "'s family"
                            : "All clients";
                          const aboveTarget = scopedWL.filter(w => w.targetPrice && w.currentPrice >= w.targetPrice).length;
                          const belowBuy    = scopedWL.filter(w => w.targetBuy && w.currentPrice <= w.targetBuy).length;
                          const escapeHtml = (s) => String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
                          const tableRows = scopedWL.map(w => {
                            const vsTarget = (w.targetPrice && w.currentPrice) ? ((w.currentPrice - w.targetPrice) / w.targetPrice * 100) : null;
                            const vsBuy    = (w.targetBuy   && w.currentPrice) ? ((w.currentPrice - w.targetBuy)   / w.targetBuy   * 100) : null;
                            const status   = (w.targetBuy && w.currentPrice <= w.targetBuy) ? '<span class="badge buy">Buy</span>'
                                           : (w.targetPrice && w.currentPrice >= w.targetPrice) ? '<span class="badge above">Above target</span>'
                                           : '<span class="badge watch">Watching</span>';
                            return `<tr>
                              <td><strong>${escapeHtml(w.name)}</strong><br/><span class="muted">${escapeHtml(w.ticker)} · ${escapeHtml(w.exchange||"")}</span></td>
                              <td class="num">${escapeHtml(w.currency||"")} ${(w.currentPrice||0).toFixed(2)}</td>
                              <td class="num">${w.targetPrice ? escapeHtml(w.currency||"") + " " + w.targetPrice : "—"}</td>
                              <td class="num">${w.targetBuy ? escapeHtml(w.currency||"") + " " + w.targetBuy : "—"}</td>
                              <td class="num">${vsTarget !== null ? (vsTarget>=0?"+":"")+vsTarget.toFixed(1)+"%" : "—"}</td>
                              <td class="num">${vsBuy    !== null ? (vsBuy>=0?"+":"")+vsBuy.toFixed(1)+"%" : "—"}</td>
                              <td>${status}</td>
                            </tr>`;
                          }).join("");
                          const thesisBlocks = scopedWL.filter(w => wlThesisMap[w.id] || w.notes).map(w => {
                            const thesis = wlThesisMap[w.id] || w.notes || "";
                            return `<div class="thesis-block">
                              <div class="thesis-head">
                                <strong>${escapeHtml(w.name)}</strong>
                                <span class="muted">${escapeHtml(w.ticker)} · ${escapeHtml(w.currency||"")} ${(w.currentPrice||0).toFixed(2)}${w.targetPrice ? " · Target " + w.targetPrice : ""}${w.targetBuy ? " · Buy below " + w.targetBuy : ""}</span>
                              </div>
                              <div class="thesis-body">${escapeHtml(thesis)}</div>
                            </div>`;
                          }).join("");
                          const html = `<!doctype html><html><head><meta charset="utf-8"><title>Watchlist — ${escapeHtml(scopeName)} — ${today}</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1F2937; margin: 0; padding: 0; }
  .header { border-bottom: 2px solid #00205B; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: center; }
  .brand { font-size: 22px; font-weight: 700; color: #00205B; letter-spacing: -0.02em; }
  .meta { font-size: 11px; color: #6B7280; text-align: right; }
  h2 { font-size: 16px; margin: 22px 0 10px 0; padding-bottom: 4px; border-bottom: 1px solid #E5E7EB; }
  .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
  .kpi { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; padding: 10px 12px; }
  .kpi-label { font-size: 9px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
  .kpi-value { font-size: 18px; font-weight: 700; margin-top: 3px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th, td { padding: 7px 8px; text-align: left; border-bottom: 1px solid #E5E7EB; }
  th { background: #F9FAFB; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #6B7280; font-weight: 600; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .muted { color: #6B7280; font-size: 9px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 600; }
  .badge.buy { background: #DCFCE7; color: #166534; }
  .badge.above { background: #FEF3C7; color: #92400E; }
  .badge.watch { background: #DBEAFE; color: #1E40AF; }
  .thesis-block { padding: 10px 0; border-bottom: 1px solid #F3F4F6; page-break-inside: avoid; }
  .thesis-block:last-child { border-bottom: none; }
  .thesis-head { margin-bottom: 5px; font-size: 11px; }
  .thesis-head strong { font-size: 12px; }
  .thesis-body { font-size: 10.5px; line-height: 1.55; color: #374151; white-space: pre-wrap; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #E5E7EB; font-size: 9px; color: #9CA3AF; text-align: center; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="header">
    <div style="display: flex; align-items: center; gap: 16px;">
      <svg viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg" fill="none" style="height: 50px; width: auto;">
        <rect x="0" y="2" width="9" height="54" rx="3" fill="#00205B"/>
        <rect x="0" y="2" width="38" height="9" rx="3" fill="#00205B"/>
        <rect x="0" y="25" width="27" height="8" rx="2" fill="#00205B"/>
        <rect x="18" y="14" width="9" height="54" rx="3" fill="#0079C1"/>
        <rect x="18" y="14" width="38" height="9" rx="3" fill="#0079C1"/>
        <rect x="18" y="37" width="27" height="8" rx="2" fill="#0079C1"/>
        <line x1="5" y1="62" x2="60" y2="7" stroke="#00205B" stroke-width="5.5" stroke-linecap="round"/>
        <line x1="5" y1="62" x2="60" y2="7" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
        <circle cx="60" cy="7" r="4.5" fill="#0079C1"/>
        <text x="82" y="32" font-family="Georgia,Times New Roman,serif" font-size="26" font-weight="700" fill="#00205B" letter-spacing="3">FORTHIUS</text>
        <text x="83" y="56" font-family="Trebuchet MS,Calibri,sans-serif" font-size="14" font-weight="600" fill="#0079C1" letter-spacing="4">FINALYTICS</text>
      </svg>
      <div style="border-left: 1px solid #E5E7EB; padding-left: 16px;">
        <div style="font-size: 13px; color: #00205B; font-weight: 600;">Watchlist Report</div>
        <div style="font-size: 11px; color: #6B7280; margin-top: 2px;">${escapeHtml(scopeName)}</div>
      </div>
    </div>
    <div class="meta">${today}</div>
  </div>
  <div class="summary">
    <div class="kpi"><div class="kpi-label">Watching</div><div class="kpi-value">${scopedWL.length}</div></div>
    <div class="kpi"><div class="kpi-label">Above Target</div><div class="kpi-value" style="color:#D97706">${aboveTarget}</div></div>
    <div class="kpi"><div class="kpi-label">Below Buy Level</div><div class="kpi-value" style="color:#1D9E75">${belowBuy}</div></div>
    <div class="kpi"><div class="kpi-label">Generated</div><div class="kpi-value" style="font-size:14px">${today}</div></div>
  </div>
  <h2>Watchlist Summary</h2>
  <table>
    <thead><tr><th>Asset</th><th class="num">Current</th><th class="num">Target</th><th class="num">Buy Below</th><th class="num">vs Target</th><th class="num">vs Buy</th><th>Status</th></tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  ${thesisBlocks ? `<h2>Investment Thesis</h2>${thesisBlocks}` : ""}
  <div class="footer">Generated by Forthius Finalytics · For discussion only · Not investment advice</div>
</body></html>`;
                          const w = window.open("", "_blank");
                          if (!w) {
                            showToast("⚠ Pop-up blocked — allow pop-ups to export PDF", "#A32D2D", 4000);
                            return;
                          }
                          w.document.open();
                          w.document.write(html);
                          w.document.close();
                          // Give the new window a tick to render before triggering print
                          setTimeout(() => {
                            try { w.print(); } catch(e) { console.error("Print failed:", e); }
                          }, 300);
                          setWlExportOpen(false);
                          showToast(`✓ Print dialog opened — choose 'Save as PDF'`, "#1D9E75", 2500);
                        }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"transparent",border:"none",padding:"10px 14px",cursor:"pointer",fontSize:12,color:T.textPrimary,textAlign:"left"}}
                          onMouseEnter={e=>e.currentTarget.style.background=T.thBg}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:14}}>📄</span>
                          <div>
                            <div style={{fontWeight:600}}>Export as PDF</div>
                            <div style={{fontSize:10,color:T.textSecondary,marginTop:1}}>Branded report · client-friendly · full thesis</div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <button onClick={()=>{
                setWlAddOpen(p=>!p);
                // Pre-select currently filtered client so admin doesn't have to
                // re-pick the obvious target. Stays empty when nothing's filtered.
                if (isAdmin && !wlAddOpen && selectedClients.size > 0) {
                  setWlAddClients(new Set([...selectedClients].map(String)));
                }
              }} style={{background:"#007878",border:"none",borderRadius:8,color:"white",padding:"8px 18px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                + Add to Watchlist
              </button>
            </div>
          </div>

          {/* Add form */}
          {wlAddOpen && (
            <div style={{...tCard(),marginBottom:16,borderColor:T.brand}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:14,color:T.brand}}>New Watchlist Entry</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:12}}>
                <div style={{position:"relative"}}><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Ticker</div>
                  <input style={inp()} value={wlTickerQuery||wlNew.ticker}
                    onChange={e=>{ setWlTickerQuery(e.target.value); setWlNew(p=>({...p,ticker:e.target.value,name:""})); }}
                    onFocus={()=>{ if((wlTickerQuery||wlNew.ticker).length>=3) setWlTickerOpen(true); }}
                    onBlur={()=>setTimeout(()=>setWlTickerOpen(false),200)}
                    placeholder="Type ticker e.g. MSFT"/>
                  {wlTickerOpen && wlTickerResults.length>0 && (
                    <div style={{position:"absolute",zIndex:9999,top:"100%",left:0,right:0,background:"#fff",border:"1px solid #78716C",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",maxHeight:240,overflowY:"auto",marginTop:2}}>
                      {wlTickerLoading && <div style={{padding:"8px 12px",fontSize:12,color:T.textMuted}}>Searching…</div>}
                      {wlTickerResults.map((r,i)=>(
                        <div key={r.symbol+i} onMouseDown={()=>{
                          setWlNew(p=>({...p,ticker:r.symbol,name:r.name||p.name,exchange:r.exchange||p.exchange,currency:r.currency||p.currency}));
                          setWlTickerQuery("");setWlTickerOpen(false);
                        }} style={{padding:"8px 12px",cursor:"pointer",fontSize:12,borderBottom:"1px solid #F5EBE0",background:i%2===0?"transparent":T.tableStripe}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":T.tableStripe}>
                          <span style={{fontWeight:600,color:T.accent}}>{r.symbol}</span>
                          <span style={{color:T.textSecondary,marginLeft:8}}>{r.name}</span>
                          {r.exchange&&<span style={{color:T.textMuted,marginLeft:6,fontSize:11}}>{r.exchange}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Company Name</div>
                  <input style={inp()} value={wlNew.name} onChange={e=>setWlNew(p=>({...p,name:e.target.value}))} placeholder="e.g. Microsoft Corp"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Target Price</div>
                  <input style={inp()} type="number" value={wlNew.targetPrice} onChange={e=>setWlNew(p=>({...p,targetPrice:e.target.value}))} placeholder="e.g. 380"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Buy Below</div>
                  <input style={inp()} type="number" value={wlNew.targetBuy} onChange={e=>setWlNew(p=>({...p,targetBuy:e.target.value}))} placeholder="e.g. 340"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Target Weight %</div>
                  <input style={inp()} type="number" min="0" max="100" step="0.1" value={wlNew.weight} onChange={e=>setWlNew(p=>({...p,weight:e.target.value}))} placeholder="e.g. 5"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Exchange</div>
                  <select style={inp()} value={wlNew.exchange} onChange={e=>setWlNew(p=>({...p,exchange:e.target.value}))}>
                    {["NASDAQ","NYSE","AMEX","NSE","BSE","LSE","XETRA","EPA","AMS","SIX","MIL","BME","OSL","STO","HEL","CPH","TSE","HKEX","SSE","SZSE","KRX","SGX","ASX","TWSE","SET","IDX","BURSA","TSX","B3","JSE","TADAWUL","Crypto"].map(x=><option key={x}>{x}</option>)}
                  </select></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Currency</div>
                  <select style={inp()} value={wlNew.currency} onChange={e=>setWlNew(p=>({...p,currency:e.target.value}))}>
                    {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div style={{gridColumn:"span 2"}}><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Notes / Thesis</div>
                  <input style={inp()} value={wlNew.notes} onChange={e=>setWlNew(p=>({...p,notes:e.target.value}))} placeholder="Investment thesis…"/></div>
              </div>
              {isAdmin && (
                <div style={{marginBottom:14,padding:"10px 12px",background:"rgba(99,102,241,0.06)",borderRadius:8,border:"1px solid rgba(99,102,241,0.18)"}}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                    <span><strong style={{color:"#4F46E5"}}>Add to which client(s)?</strong> {wlAddClients.size===0 ? "— none selected (will save as Admin)" : `— ${wlAddClients.size} selected`}</span>
                    <span style={{display:"flex",gap:6}}>
                      <button onClick={()=>setWlAddClients(new Set(clients.map(c=>String(c.id))))} style={{background:"transparent",border:"1px solid rgba(99,102,241,0.4)",borderRadius:4,color:"#4F46E5",padding:"2px 8px",cursor:"pointer",fontSize:10}}>Select all</button>
                      <button onClick={()=>setWlAddClients(new Set())} style={{background:"transparent",border:"1px solid rgba(99,102,241,0.4)",borderRadius:4,color:"#4F46E5",padding:"2px 8px",cursor:"pointer",fontSize:10}}>Clear</button>
                    </span>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:120,overflowY:"auto"}}>
                    {clients.map(c => {
                      const sel = wlAddClients.has(String(c.id));
                      const fam = families.find(f => String(f.id) === String(c.familyId));
                      return (
                        <button key={c.id} onClick={()=>{
                          setWlAddClients(p => {
                            const n = new Set(p);
                            if (n.has(String(c.id))) n.delete(String(c.id));
                            else n.add(String(c.id));
                            return n;
                          });
                        }} style={{
                          background: sel ? "#4F46E5" : "transparent",
                          color: sel ? "white" : T.textPrimary,
                          border: `1px solid ${sel ? "#4F46E5" : T.cardBorder}`,
                          borderRadius: 16,
                          padding: "4px 10px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontWeight: sel ? 600 : 400,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}>
                          {sel && <span>✓</span>}
                          {c.name}
                          {fam && <span style={{opacity:0.6,fontSize:10}}>· {fam.name}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:14}}>
                {[["alertAbove","🔔 Alert above target"],["alertBelow","🔔 Alert below buy level"]].map(([k,l])=>(
                  <label key={k} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:T.textSecondary}}>
                    <input type="checkbox" checked={wlNew[k]} onChange={e=>setWlNew(p=>({...p,[k]:e.target.checked}))} style={{accentColor:"#007878"}}/>
                    {l}
                  </label>
                ))}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={async ()=>{
                  if (!wlNew.ticker||!wlNew.name) return;
                  const entry = parseFloat(wlNew.targetBuy)||parseFloat(wlNew.targetPrice)||0;
                  // Determine target client list:
                  // - admin with multi-select chips → use those (Option A: duplicate rows)
                  // - admin with no chips selected → fall back to global filter or "admin"
                  // - non-admin → always own clientId
                  let targetClientIds;
                  if (!isAdmin) {
                    targetClientIds = [loggedInClientId];
                  } else if (wlAddClients.size > 0) {
                    targetClientIds = [...wlAddClients];
                  } else {
                    targetClientIds = [
                      selectedClients.size === 1 ? [...selectedClients][0] : "admin"
                    ];
                  }
                  // Direct REST POST per client — duplicates the row so each
                  // client has independent notes/targets/edits later.
                  const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                  const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                  let success = 0, failed = 0;
                  const newItems = [];
                  for (const ownerClientId of targetClientIds) {
                    const newWlItem = {...wlNew,id:"w"+uid(),currentPrice:entry,change1d:0,
                      targetPrice:parseFloat(wlNew.targetPrice)||0,
                      targetBuy:parseFloat(wlNew.targetBuy)||0,
                      weight:parseFloat(wlNew.weight)||0,
                      clientId:ownerClientId};
                    const dbRow = {
                      id: newWlItem.id,
                      client_id: ownerClientId,
                      ticker: newWlItem.ticker,
                      name: newWlItem.name,
                      exchange: newWlItem.exchange,
                      currency: newWlItem.currency,
                      target_price: newWlItem.targetPrice,
                      target_buy: newWlItem.targetBuy,
                      alert_above: !!newWlItem.alertAbove,
                      alert_below: !!newWlItem.alertBelow,
                      notes: newWlItem.notes || "",
                      current_price: newWlItem.currentPrice || 0,
                      change_1d: newWlItem.change1d || 0,
                      weight: newWlItem.weight || 0,
                    };
                    try {
                      const res = await fetch(`${SUPABASE_URL}/rest/v1/watchlist`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'apikey': ANON_KEY,
                          'Authorization': `Bearer ${ANON_KEY}`,
                          'Prefer': 'return=representation',
                        },
                        body: JSON.stringify(dbRow),
                      });
                      if (!res.ok) {
                        const errText = await res.text();
                        throw new Error(`HTTP ${res.status}: ${errText.slice(0,200)}`);
                      }
                      const body = await res.json();
                      if (!Array.isArray(body) || body.length === 0) {
                        throw new Error("Empty response — RLS may have blocked");
                      }
                      newItems.push(newWlItem);
                      success++;
                    } catch (err) {
                      console.error(`[addWatchlistItem] failed for client ${ownerClientId}:`, err.message);
                      failed++;
                    }
                  }
                  if (success > 0) {
                    setWatchlist(p => [...p, ...newItems]);
                    setWlAddOpen(false);
                    setWlNew({ticker:"",name:"",exchange:"NASDAQ",currency:"USD",targetPrice:"",targetBuy:"",alertAbove:false,alertBelow:false,notes:"",weight:""});
                    setWlAddClients(new Set());
                    if (failed === 0) {
                      showToast(`✓ ${wlNew.name} added to ${success} watchlist${success>1?"s":""}`, "#1D9E75");
                    } else {
                      showToast(`Added to ${success}, ${failed} failed — see console`, "#D97706", 4000);
                    }
                  } else {
                    showToast(`⚠ Save failed for all ${failed} client(s) — check console`, "#A32D2D", 5000);
                  }
                }} style={{background:"#007878",border:"none",borderRadius:6,color:"white",padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>Add</button>
                <button onClick={()=>setWlAddOpen(false)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 18px",cursor:"pointer",fontSize:13}}>Cancel</button>
              </div>
            </div>
          )}

          {/* Filter pills */}
          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
            {[["all","All"],["near","Near Target"],["above","Above Target"],["below","Below Buy Level"]].map(([k,l])=>(
              <button key={k} onClick={()=>setWlFilter(k)}
                style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${wlFilter===k?T.brand:T.border}`,background:wlFilter===k?"rgba(0,120,120,0.1)":"transparent",color:wlFilter===k?"#007878":T.textSecondary,cursor:"pointer",fontSize:12,fontWeight:wlFilter===k?600:400}}>
                {l}
              </button>
            ))}
          </div>

          {/* KPI cards */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:12,marginBottom:16}}>
            {[
              {label:"Watching",          val:scopedWL.length,                                                                                        color:T.brand,icon:"👁"},
              {label:"Above Target",      val:scopedWL.filter(w=>w.currentPrice>=(w.targetPrice||Infinity)).length,                                    color:"#D97706",icon:"🔼"},
              {label:"Below Buy Level",   val:scopedWL.filter(w=>w.currentPrice<=(w.targetBuy||0)&&w.targetBuy>0).length,                              color:"#1D9E75",icon:"🛒"},
              {label:"Near Target (±5%)", val:scopedWL.filter(w=>w.targetPrice&&Math.abs(w.currentPrice-w.targetPrice)/w.targetPrice<0.05).length,     color:"#9333EA",icon:"🎯"},
            ].map(k=>(
              <div key={k.label} style={{...tCard(),textAlign:"center",padding:"16px 12px"}}>
                <div style={{fontSize:22,marginBottom:6}}>{k.icon}</div>
                <div style={{fontSize:26,fontWeight:700,color:k.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:4}}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Orphan watchlist diagnostic banner */}
          {isAdmin && orphanWL.length > 0 && (selectedClients.size === 1 || selectedFamily !== "all") && (
            <div style={{...tCard(),marginBottom:12,
              background:"rgba(245,158,11,0.06)",
              border:"1px solid rgba(245,158,11,0.3)"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                <div style={{fontSize:18,flexShrink:0}}>⚠</div>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#92400E",marginBottom:4}}>
                    {orphanWL.length} watchlist item{orphanWL.length>1?"s":""} not in current scope
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:6}}>
                    The watchlist contains items that don't match the current client/family filter — likely saved before tagging by client was working correctly. Their clientIds: {[...new Set(orphanWL.map(w => w.clientId || "(none)"))].slice(0,5).map(id => `"${id}"`).join(", ")}{[...new Set(orphanWL.map(w => w.clientId || "(none)"))].length > 5 ? ` and ${[...new Set(orphanWL.map(w => w.clientId || "(none)"))].length - 5} more` : ""}.
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:10}}>
                    Tickers: {orphanWL.slice(0,8).map(w => w.ticker).join(", ")}{orphanWL.length > 8 ? `, +${orphanWL.length-8} more` : ""}
                  </div>
                  {selectedClients.size === 1 && (
                    <button onClick={async () => {
                      console.log('[reassign] button clicked, orphanWL.length =', orphanWL.length);
                      const targetClientId = [...selectedClients][0];
                      const targetClient = clients.find(c => String(c.id) === String(targetClientId));
                      if (orphanWL.length === 0) {
                        showToast("No orphan items to reassign", "#6B7280", 2000);
                        return;
                      }
                      // Two-stage confirm: first click toggles a pending state.
                      // We avoid window.confirm() because it can be blocked by
                      // browsers in async event handlers (Safari, Brave, "stop
                      // showing dialogs" checkbox) — making clicks "do nothing".
                      if (!wlReassignPending) {
                        setWlReassignPending(true);
                        showToast(`⚠ Click again to reassign ${orphanWL.length} item${orphanWL.length>1?"s":""} to ${targetClient?.name||"client"}`, "#D97706", 4000);
                        // Auto-clear pending after 5s
                        setTimeout(() => setWlReassignPending(false), 5000);
                        return;
                      }
                      setWlReassignPending(false);
                      let success = 0, failed = 0;
                      // ── Direct Supabase PATCH (bypassing the wrapper) ──
                      // The watchlist table column is camelCase 'clientId' (NOT
                      // 'client_id' like other tables) — confirmed by PGRST204
                      // schema-cache errors during earlier debugging.
                      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                      let rlsRejected = 0;
                      console.log('[reassign] starting loop for', orphanWL.length, 'items');
                      for (const w of orphanWL) {
                        try {
                          const res = await fetch(`${SUPABASE_URL}/rest/v1/watchlist?id=eq.${encodeURIComponent(w.id)}`, {
                            method: 'PATCH',
                            headers: {
                              'Content-Type': 'application/json',
                              'apikey': ANON_KEY,
                              'Authorization': `Bearer ${ANON_KEY}`,
                              'Prefer': 'return=representation',
                            },
                            body: JSON.stringify({ client_id: targetClientId }),
                          });
                          // VERBOSE diagnostic: print everything about the response
                          // so we can see exactly what Supabase is doing — status,
                          // headers, body, all visible without opening Network tab.
                          const bodyText = await res.text();
                          console.log(`[reassign] ${w.ticker} (${w.id}) → HTTP ${res.status} | body: ${bodyText.slice(0,300)}`);
                          if (!res.ok) {
                            throw new Error(`HTTP ${res.status}: ${bodyText.slice(0,200)}`);
                          }
                          // Verify: representation returns array of updated rows.
                          // Empty array = RLS silently blocked the update.
                          let body;
                          try { body = JSON.parse(bodyText); } catch (e) { body = []; }
                          if (!Array.isArray(body) || body.length === 0) {
                            rlsRejected++;
                            console.warn('[reassign] RLS rejected silently:', w.ticker, w.id, '— response was:', bodyText);
                            failed++;
                            continue;
                          }
                          // Confirm the row coming back actually has client_id set
                          const returnedClientId = body[0]?.client_id;
                          if (String(returnedClientId) !== String(targetClientId)) {
                            console.warn(`[reassign] ${w.ticker}: PATCH succeeded but returned client_id="${returnedClientId}", expected "${targetClientId}"`);
                          }
                          setWatchlist(p => p.map(x => x.id === w.id ? { ...x, clientId: targetClientId } : x));
                          success++;
                        } catch (e) {
                          console.error('[reassign] failed for', w.ticker, e.message, e);
                          failed++;
                        }
                      }
                      if (failed === 0) {
                        showToast(`✓ Reassigned ${success} item${success>1?"s":""} to ${targetClient?.name||"client"}`, "#1D9E75", 3000);
                      } else if (rlsRejected > 0) {
                        // RLS silently rejected — most actionable error to surface
                        showToast(`⚠ Supabase rejected ${rlsRejected} updates (likely RLS policy on watchlist table). Check Supabase → Authentication → Policies. ${success>0?`${success} did go through.`:''}`, "#A32D2D", 8000);
                      } else {
                        showToast(`Reassigned ${success}, ${failed} failed — see console`, "#D97706", 4000);
                      }
                    }} style={{
                      background:"#92400E",border:"none",borderRadius:6,color:"white",
                      padding:"6px 14px",cursor:"pointer",fontSize:11,fontWeight:600,marginRight:8
                    }}>
                      {wlReassignPending
                        ? `⚠ Click again to confirm`
                        : `Reassign ${orphanWL.length} to ${clients.find(c => String(c.id) === String([...selectedClients][0]))?.name || "current client"}`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Watchlist table */}
          <div style={{...tCard(),padding:0,overflow:"hidden"}}>
            <div style={{overflowX:"auto",overflowY:"auto",maxHeight:"60vh"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
                <thead style={{position:"sticky",top:0,zIndex:100,background:T.thBg,boxShadow:`0 2px 4px rgba(0,0,0,0.08)`}}>
                  <tr>{["Asset","Ticker","Exchange","Curr. Price","1D %","Wt %","Target Price","Buy Below","vs Target","vs Buy","Alerts","🤖 AI Thesis",""].map(h=>(
                    <th key={h} style={{...tTh(),background:T.thBg,position:"sticky",top:0,zIndex:100}}>{h}</th>
                  ))}</tr>
                </thead>
              <tbody>
                {filteredWL.length===0 && (
                  <tr><td colSpan={13} style={{textAlign:"center",padding:40,color:T.textLight,fontSize:13}}>No assets match this filter.</td></tr>
                )}
                {filteredWL.map((w,i)=>{
                  const vsTarget = w.targetPrice ? ((w.currentPrice-w.targetPrice)/w.targetPrice*100) : null;
                  const vsBuy    = w.targetBuy   ? ((w.currentPrice-w.targetBuy)/w.targetBuy*100)     : null;
                  const atTarget = w.targetPrice && w.currentPrice >= w.targetPrice;
                  const atBuy    = w.targetBuy   && w.currentPrice <= w.targetBuy;
                  return (
                    <tr key={w.id} style={{background:atBuy?"rgba(74,222,128,0.04)":atTarget?"rgba(245,158,11,0.04)":i%2===0?"transparent":"rgba(0,0,0,0.02)"}}>
                      <td style={tTd({fontWeight:600})}>
                        <div>{w.name}</div>
                        <div style={{fontSize:10,color:T.textSecondary}}>{w.currency}</div>
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:"#2563EB"})}>{w.ticker}</td>
                      <td style={tTd({fontSize:11,color:T.textSecondary})}>{w.exchange}</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,fontSize:13})}>{w.currentPrice.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td style={tTd()}><span style={{color:w.change1d>=0?"#1D9E75":"#A32D2D",fontWeight:600,fontSize:12}}>{w.change1d>=0?"+":""}{w.change1d?.toFixed(2)}%</span></td>
                      <td style={tTd({fontSize:12,color:T.textSecondary})}>
                        {wlEditId===w.id
                          ? <input type="number" min="0" max="100" step="0.1" value={wlEditVals.weight||""} onChange={e=>setWlEditVals(p=>({...p,weight:parseFloat(e.target.value)||0}))} style={{...inp(),fontSize:11,padding:"3px 6px",width:60}} placeholder="Wt%"/>
                          : w.weight>0 ? <span style={{fontWeight:600,color:T.textPrimary}}>{Number(w.weight).toFixed(1)}%</span> : <span style={{color:T.textLight}}>—</span>
                        }
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#D97706",fontSize:12})}>
                        {wlEditId===w.id
                          ? <input type="number" value={wlEditVals.targetPrice||""} onChange={e=>setWlEditVals(p=>({...p,targetPrice:parseFloat(e.target.value)||null}))} style={{...inp(),fontSize:11,padding:"3px 6px",width:90}} placeholder="Target"/>
                          : w.targetPrice?w.targetPrice.toLocaleString("en-US"):"—"
                        }
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#1D9E75",fontSize:12})}>
                        {wlEditId===w.id
                          ? <input type="number" value={wlEditVals.targetBuy||""} onChange={e=>setWlEditVals(p=>({...p,targetBuy:parseFloat(e.target.value)||null}))} style={{...inp(),fontSize:11,padding:"3px 6px",width:90}} placeholder="Buy below"/>
                          : w.targetBuy?w.targetBuy.toLocaleString("en-US"):"—"
                        }
                      </td>
                      <td style={tTd()}>
                        {vsTarget!==null
                          ? <span style={{color:vsTarget>0?"#D97706":Math.abs(vsTarget)<5?"#9333EA":"#2563EB",fontWeight:600,fontSize:12}}>
                              {vsTarget>=0?"+":""}{vsTarget.toFixed(1)}%
                              {atTarget&&<span style={{marginLeft:4,fontSize:10,background:"rgba(245,158,11,0.15)",color:"#D97706",padding:"1px 5px",borderRadius:4}}>HIT</span>}
                            </span>
                          : <span style={{color:T.textLight}}>—</span>}
                      </td>
                      <td style={tTd()}>
                        {vsBuy!==null
                          ? <span style={{color:vsBuy<0?"#1D9E75":T.textSecondary,fontWeight:vsBuy<0?700:400,fontSize:12}}>
                              {vsBuy>=0?"+":""}{vsBuy.toFixed(1)}%
                              {atBuy&&<span style={{marginLeft:4,fontSize:10,background:"rgba(22,163,74,0.15)",color:"#1D9E75",padding:"1px 5px",borderRadius:4}}>BUY ZONE</span>}
                            </span>
                          : <span style={{color:T.textLight}}>—</span>}
                      </td>
                      <td style={tTd({fontSize:11})}>
                        {wlEditId===w.id ? (
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,cursor:"pointer"}}>
                              <input type="checkbox" checked={!!wlEditVals.alertAbove} onChange={e=>setWlEditVals(p=>({...p,alertAbove:e.target.checked}))} style={{accentColor:"#D97706"}}/>
                              <span style={{color:"#D97706"}}>🔔↑ Above</span>
                            </label>
                            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,cursor:"pointer"}}>
                              <input type="checkbox" checked={!!wlEditVals.alertBelow} onChange={e=>setWlEditVals(p=>({...p,alertBelow:e.target.checked}))} style={{accentColor:"#1D9E75"}}/>
                              <span style={{color:"#1D9E75"}}>🔔↓ Below</span>
                            </label>
                          </div>
                        ) : (
                          <div style={{display:"flex",gap:4}}>
                            {w.alertAbove&&<span style={{background:"rgba(245,158,11,0.12)",color:"#D97706",padding:"2px 6px",borderRadius:4,fontSize:10}}>🔔↑</span>}
                            {w.alertBelow&&<span style={{background:"rgba(22,163,74,0.12)",color:"#1D9E75",padding:"2px 6px",borderRadius:4,fontSize:10}}>🔔↓</span>}
                            {!w.alertAbove&&!w.alertBelow&&<span style={{color:T.textLight}}>—</span>}
                          </div>
                        )}
                      </td>
                      <td style={tTd({fontSize:11,maxWidth:200})}>
                        {wlEditId===w.id ? (
                          <input value={wlEditVals.notes||""} onChange={e=>setWlEditVals(p=>({...p,notes:e.target.value}))} style={{...inp(),fontSize:11,padding:"3px 6px",width:130}} placeholder="Notes…"/>
                        ) : wlThesisMap[w.id] ? (
                          <div style={{position:"relative"}}>
                            <div style={{fontSize:10,color:T.thText,lineHeight:1.4,maxHeight:54,overflow:"hidden",whiteSpace:"pre-wrap"}}>{wlThesisMap[w.id]}</div>
                            {wlThesisMap[w.id].length > 180 && (
                              <button onClick={()=>setWlThesisModalId(w.id)}
                                style={{background:"none",border:"none",color:"#2563EB",fontSize:10,cursor:"pointer",padding:"2px 0 0 0",fontWeight:600,textDecoration:"underline"}}>
                                Read more →
                              </button>
                            )}
                            <button onClick={()=>setWlThesisMap(p=>{const n={...p};delete n[w.id];return n;})} style={{position:"absolute",top:0,right:0,background:"none",border:"none",color:T.textLight,fontSize:10,cursor:"pointer",padding:0,lineHeight:1}}>✕</button>
                          </div>
                        ) : (
                          <button
                            disabled={wlThesisLoading===w.id}
                            onClick={async ()=>{
                              setWlThesisLoading(w.id);
                              try {
                                const prompt = `You are a concise equity research analyst. In exactly 2 sentences (max 40 words total), give a bull/bear investment thesis for ${w.name} (${w.ticker}). Current price: ${w.currentPrice}. Target: ${w.targetPrice||"not set"}. Be direct and specific.`;
                                const resp = await fetch("/api/claude", {
                                  method:"POST",
                                  headers:{"Content-Type":"application/json"},
                                  body:JSON.stringify({ prompt, max_tokens:150 })
                                });
                                const data = await resp.json();
                                const text = data?.text || data?.error || "Unable to generate thesis.";
                                setWlThesisMap(p=>({...p,[w.id]:text}));
                                // Persist thesis to notes field in Supabase
                                updateWatchlistItem(w.id, {notes: text}).catch(e=>console.error("[Supabase] wl thesis persist:",e.message));
                                setWatchlist(p=>p.map(x=>x.id===w.id?{...x,notes:text}:x));
                              } catch(e) {
                                setWlThesisMap(p=>({...p,[w.id]:"API error: "+e.message}));
                              } finally { setWlThesisLoading(null); }
                            }}
                            style={{background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.3)",borderRadius:5,color:"#6366F1",padding:"3px 8px",cursor:wlThesisLoading===w.id?"not-allowed":"pointer",fontSize:10,fontWeight:600,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:4}}>
                            {wlThesisLoading===w.id
                              ? <React.Fragment><span style={{display:"inline-block",animation:"spin 0.8s linear infinite",fontSize:10}}>⟳</span> Analysing…</React.Fragment>
                              : "✨ Get Thesis"}
                          </button>
                        )}
                        {w.notes && !wlThesisMap[w.id] && wlEditId!==w.id && (
                          <div style={{fontSize:9,color:T.textMuted,marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:130}} title={w.notes}>📝 {w.notes}</div>
                        )}
                      </td>
                      <td style={tTd()}>
                        {wlEditId===w.id ? (
                          <button onClick={async ()=>{
                            const updated = {...w,...wlEditVals};
                            // persist all editable fields
                            // Validate watchlist item
                  if (!updated.name?.trim()) { showToast("⚠ Holding name is required","#D97706"); return; }
                  if (updated.targetPrice && parseFloat(updated.targetPrice) <= 0) { showToast("⚠ Target price must be > 0","#D97706"); return; }
                  if (updated.targetBuy && parseFloat(updated.targetBuy) <= 0) { showToast("⚠ Buy target must be > 0","#D97706"); return; }
                  // Direct REST PATCH — wrapper has been unreliable for watchlist
                  // (silently drops fields, doesn't translate names). Use the same
                  // pattern as the working reassign loop.
                  const fields = {
                    name: updated.name,
                    target_price: parseFloat(updated.targetPrice)||null,
                    target_buy: parseFloat(updated.targetBuy)||null,
                    alert_above: !!updated.alertAbove,
                    alert_below: !!updated.alertBelow,
                    notes: updated.notes||"",
                    weight: parseFloat(updated.weight)||0,
                  };
                  try {
                    const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                    const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                    const res = await fetch(`${SUPABASE_URL}/rest/v1/watchlist?id=eq.${encodeURIComponent(w.id)}`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type':'application/json',
                        'apikey': ANON_KEY,
                        'Authorization': `Bearer ${ANON_KEY}`,
                        'Prefer': 'return=representation',
                      },
                      body: JSON.stringify(fields),
                    });
                    if (!res.ok) {
                      const errText = await res.text();
                      throw new Error(`HTTP ${res.status}: ${errText.slice(0,200)}`);
                    }
                    const body = await res.json();
                    if (!Array.isArray(body) || body.length === 0) {
                      throw new Error("Empty response — RLS may have blocked the update");
                    }
                    // Only update local state on confirmed write
                    setWatchlist(p=>p.map(x=>x.id===w.id?updated:x));
                    setWlEditId(null); setWlEditVals({});
                    showToast("✓ Saved", "#1D9E75", 1500);
                  } catch (err) {
                    console.error("[Supabase] wlUpdate failed:", err.message);
                    showToast(`⚠ Save failed: ${err.message}`, "#A32D2D", 4000);
                  }
                          }} style={{background:"#007878",border:"none",borderRadius:5,color:"white",padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600,marginRight:4}}>✓</button>
                        ) : (
                          <button onClick={()=>{ setWlEditId(w.id); setWlEditVals({targetPrice:w.targetPrice,targetBuy:w.targetBuy,alertAbove:w.alertAbove,alertBelow:w.alertBelow,notes:w.notes,weight:w.weight||0}); }}
                            style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:5,color:T.textSecondary,padding:"3px 8px",cursor:"pointer",fontSize:11,marginRight:4}}>✎</button>
                        )}
                        <button onClick={()=>{ requireConfirm(`Remove "${w.name}" from watchlist?`, ()=>{ deleteWatchlistItem(w.id).catch(err=>console.error("[Supabase] deleteWatchlist:",err.message)); setWatchlist(p=>p.filter(x=>x.id!==w.id)); showToast("✓ Removed from watchlist","#1D9E75"); }); }}
                          style={{background:"transparent",border:"1px solid #C9B8A8",borderRadius:5,color:T.textLight,padding:"3px 8px",cursor:"pointer",fontSize:11}}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor="#A32D2D";e.currentTarget.style.color="#A32D2D";}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.badgeBg;e.currentTarget.style.color=T.badgeBg;}}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* ── Watchlist Sector Allocation (based on target weights) ── */}
          {(function(){
            const withWeight = watchlist.filter(w => w.weight > 0);
            if (withWeight.length === 0) return null;

            // Map tickers to sectors using TICKER_SECTOR_MAP
            const sectorMap = {};
            withWeight.forEach(w => {
              const _nl = w.name ? w.name.toLowerCase() : "";
              const _kw = _nl ? NAME_SECTOR_KEYWORDS.find(k => k.keywords.some(kw => _nl.includes(kw))) : null;
              const sec = TICKER_SECTOR_MAP[w.ticker?.toUpperCase()] || (_nl ? (_kw?.sector || "Other") : "Other");
              sectorMap[sec] = (sectorMap[sec] || 0) + (w.weight || 0);
            });

            const totalWt = Object.values(sectorMap).reduce((s,v)=>s+v, 0) || 1;
            const sectors = Object.entries(sectorMap)
              .sort((a,b) => b[1]-a[1])
              .map(([name, wt], i) => ({
                name,
                wt,
                pct: wt / totalWt * 100,
                color: ["#6366F1","#34D399","#F59E0B","#EC4899","#3B82F6","#8B5CF6","#10B981","#F97316","#14B8A6","#EF4444","#A78BFA","#06B6D4"][i % 12]
              }));

            const totalAssigned = withWeight.reduce((s,w)=>s+(w.weight||0), 0);
            const isOver = totalAssigned > 100;

            return (
              <div style={{...tCard(), marginTop:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>🥧 Watchlist Sector Allocation</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>Based on target weights assigned to watchlist items</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,color:T.textMuted}}>Total assigned:</span>
                    <span style={{fontSize:13,fontWeight:700,color:isOver?"#EF4444":totalAssigned===100?"#34D399":"#F59E0B"}}>
                      {totalAssigned.toFixed(1)}%
                    </span>
                    {isOver && <span style={{fontSize:10,background:"rgba(239,68,68,0.12)",color:"#EF4444",padding:"2px 7px",borderRadius:5,fontWeight:600}}>OVER 100%</span>}
                    {totalAssigned < 100 && <span style={{fontSize:10,background:"rgba(245,158,11,0.10)",color:"#F59E0B",padding:"2px 7px",borderRadius:5}}>+{(100-totalAssigned).toFixed(1)}% unallocated</span>}
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:20,alignItems:"start"}}>
                  {/* Bar chart */}
                  <div>
                    {sectors.map(s => (
                      <div key={s.name} style={{marginBottom:10}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:11,fontWeight:600,color:T.textPrimary}}>{s.name}</span>
                          <span style={{fontSize:11,color:T.textSecondary,fontWeight:600}}>{s.pct.toFixed(1)}% <span style={{color:T.textMuted,fontWeight:400}}>({s.wt.toFixed(1)} pts)</span></span>
                        </div>
                        <div style={{height:8,background:T.borderSubtle||"rgba(255,255,255,0.05)",borderRadius:4,overflow:"hidden"}}>
                          <div style={{width:`${s.pct}%`,height:"100%",background:s.color,borderRadius:4,transition:"width 0.4s ease"}}/>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Per-asset weights table */}
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:T.textSecondary,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>Assets with weights</div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {withWeight.sort((a,b)=>(b.weight||0)-(a.weight||0)).map(w => {
                        const sec = TICKER_SECTOR_MAP[w.ticker?.toUpperCase()] || "Other";
                        const secColor = sectors.find(s=>s.name===sec)?.color || T.brand;
                        return (
                          <div key={w.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",background:T.surfaceBg||"rgba(255,255,255,0.03)",borderRadius:7}}>
                            <div style={{width:8,height:8,borderRadius:"50%",background:secColor,flexShrink:0}}/>
                            <span style={{fontSize:11,fontWeight:600,color:"#6366F1",minWidth:52}}>{w.ticker}</span>
                            <span style={{fontSize:11,color:T.textSecondary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.name}</span>
                            <span style={{fontSize:11,fontWeight:700,color:T.textPrimary,minWidth:36,textAlign:"right"}}>{Number(w.weight).toFixed(1)}%</span>
                          </div>
                        );
                      })}
                      {watchlist.filter(w=>!(w.weight>0)).length > 0 && (
                        <div style={{fontSize:10,color:T.textMuted,marginTop:4,padding:"4px 8px"}}>
                          ⚠ {watchlist.filter(w=>!(w.weight>0)).length} item(s) have no weight — edit them to include in allocation
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Portfolio overlap */}
          <div style={{...tCard(),marginTop:16}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>📋 Already in Portfolio</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {watchlist.filter(w=>holdings.some(h=>h.ticker&&h.ticker.toLowerCase()===w.ticker.toLowerCase())).map(w=>(
                <div key={w.id} style={{background:"rgba(96,165,250,0.08)",border:`1px solid ${T.cardBorder}`,borderRadius:8,padding:"8px 14px",fontSize:12}}>
                  <span style={{color:"#2563EB",fontWeight:600}}>{w.ticker}</span>
                  <span style={{color:T.textSecondary,marginLeft:8}}>{w.name}</span>
                  <span style={{marginLeft:8,fontSize:10,background:"rgba(96,165,250,0.15)",color:"#2563EB",padding:"2px 6px",borderRadius:4}}>In Portfolio</span>
                </div>
              ))}
              {watchlist.filter(w=>holdings.some(h=>h.ticker&&h.ticker.toLowerCase()===w.ticker.toLowerCase())).length===0&&
                <div style={{color:T.textLight,fontSize:12}}>No watchlist items overlap with current portfolio.</div>}
            </div>
          </div>

          {/* AI Thesis full-text modal — opens when user clicks "Read more" on a clipped thesis */}
          {wlThesisModalId && (function(){
            const item = scopedWL.find(w => w.id === wlThesisModalId);
            const thesis = wlThesisMap[wlThesisModalId];
            if (!item || !thesis) return null;
            const close = () => setWlThesisModalId(null);
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={close}>
                <div onClick={e=>e.stopPropagation()} style={{background:T.cardBg,borderRadius:12,maxWidth:640,width:"100%",maxHeight:"82vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.3)"}}>
                  <div style={{padding:"16px 22px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:3,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>🤖 AI Investment Thesis</div>
                      <div style={{fontSize:16,fontWeight:700}}>{item.name}</div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                        {item.ticker} · {item.exchange} · {item.currency} {(item.currentPrice||0).toFixed(2)}
                        {item.targetPrice > 0 && ` · Target ${item.targetPrice}`}
                        {item.targetBuy > 0 && ` · Buy below ${item.targetBuy}`}
                      </div>
                    </div>
                    <button onClick={close} style={{background:"transparent",border:"none",fontSize:22,color:T.textSecondary,cursor:"pointer",padding:"0 4px",lineHeight:1}}>×</button>
                  </div>
                  <div style={{padding:"18px 22px",overflowY:"auto",flex:1,fontSize:13,lineHeight:1.65,color:T.textPrimary,whiteSpace:"pre-wrap"}}>
                    {thesis}
                  </div>
                  <div style={{padding:"12px 22px",borderTop:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <div style={{fontSize:11,color:T.textSecondary}}>{thesis.length.toLocaleString()} characters</div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{
                        navigator.clipboard?.writeText(thesis).then(
                          ()=>showToast("✓ Copied to clipboard", "#1D9E75", 1500),
                          ()=>showToast("⚠ Copy failed", "#A32D2D", 2000)
                        );
                      }} style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textPrimary,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                        Copy
                      </button>
                      <button onClick={close} style={{background:"#007878",border:"1px solid #007878",borderRadius:6,color:"white",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════
              AI WATCHLIST ADVISOR — modal (v1)
              Generates a structured analysis of the watchlist with portfolio
              context. Cached per-scope in memory; click Regenerate for fresh.
          ══════════════════════════════════════════════ */}
          {wlAdvisorOpen && (function(){
            // Stable scope key — same scope = same cached report
            const scopeKey = `${selectedFamily}|${[...selectedClients].sort().join(",")}`;
            const cachedReport = wlAdvisorReports[scopeKey] || null;
            const close = () => { setWlAdvisorOpen(false); setWlAdvisorError(null); };
            const scopeName = selectedClients.size === 1
              ? clients.find(c => String(c.id)===String([...selectedClients][0]))?.name || "Client"
              : selectedFamily !== "all" ? (families.find(f => String(f.id)===String(selectedFamily))?.name || "Family") + "'s family"
              : "All clients";
            // Compute KPIs locally — these are facts, not AI-generated
            const buyableCount = scopedWL.filter(w => w.targetBuy && w.currentPrice <= w.targetBuy).length;
            const aboveCount   = scopedWL.filter(w => w.targetPrice && w.currentPrice >= w.targetPrice).length;
            const noTargets    = scopedWL.filter(w => !w.targetPrice && !w.targetBuy).length;
            // Generate a fresh advisor report
            const generate = async () => {
              setWlAdvisorLoading(true);
              setWlAdvisorError(null);
              try {
                // ── Fetch technical signals first ──
                // These are computed locally from real Yahoo price history.
                // Failures are non-fatal — AI call still proceeds without
                // tech context if Yahoo is unavailable.
                setWlTechLoading(true);
                let techByTicker = {};
                try {
                  const tickers = scopedWL.map(w => w.ticker).filter(Boolean);
                  techByTicker = await fetchTechSignalsForTickers(tickers, 6);
                  setWlTechSignals(p => ({ ...p, [scopeKey]: techByTicker }));
                } catch (techErr) {
                  console.warn("[wlAdvisor] tech fetch failed, continuing without:", techErr.message);
                } finally {
                  setWlTechLoading(false);
                }
                // Build compact watchlist payload — now with tech signals inline
                const wlPayload = scopedWL.map(w => {
                  const sig = techByTicker[w.ticker];
                  return {
                    id: w.id,
                    ticker: w.ticker,
                    name: w.name,
                    ccy: w.currency,
                    price: w.currentPrice,
                    target: w.targetPrice || null,
                    buyBelow: w.targetBuy || null,
                    weight: w.weight || 0,
                    thesis: (wlThesisMap[w.id] || w.notes || "").slice(0, 400),
                    tech: sig ? {
                      aboveSMA50:  sig.aboveSMA50,
                      aboveSMA200: sig.aboveSMA200,
                      goldenCross: sig.sma50AboveSma200,  // 50DMA above 200DMA
                      rsi:    sig.rsi != null ? +sig.rsi.toFixed(0) : null,
                      ret1m:  sig.ret1m  != null ? +sig.ret1m.toFixed(1)  : null,
                      ret3m:  sig.ret3m  != null ? +sig.ret3m.toFixed(1)  : null,
                      ret6m:  sig.ret6m  != null ? +sig.ret6m.toFixed(1)  : null,
                      ret12m: sig.ret12m != null ? +sig.ret12m.toFixed(1) : null,
                      pctFrom52WHigh: sig.distFrom52WHigh != null ? +sig.distFrom52WHigh.toFixed(1) : null,
                      vol30d: sig.volatility30d != null ? +sig.volatility30d.toFixed(1) : null,
                    } : null,
                  };
                });
                // Build portfolio summary from scoped holdings
                // Apply ETF look-through so the AI sees TRUE sector exposure
                // (e.g., SMH counted as 100% Semiconductors, not generic Tech)
                const scopedH = filteredHW || [];
                const portfolioLT = applyLookthrough(scopedH, { valueKey: "valueUSD" });
                const topSectors = Object.entries(portfolioLT.sectorBreakdown)
                  .sort(([,a],[,b]) => b-a).slice(0,5)
                  .map(([sector, pct]) => ({ sector, pct }));
                const portfolioCtx = {
                  totalUSD: Math.round(portfolioLT.totalValue),
                  holdingsCount: scopedH.length,
                  topSectors,
                  topRegions: Object.entries(portfolioLT.regionBreakdown)
                    .sort(([,a],[,b]) => b-a).slice(0,5)
                    .map(([region, pct]) => ({ region, pct })),
                };
                // Apply look-through to the watchlist itself for composition.
                // This is what the report's composition section will reflect:
                // the TRUE sector exposure of the candidate list, not surface
                // sector tags. Pass current price as valueUSD proxy so equal-
                // weighted by name (since most watchlist items have weight=0).
                const wlForLT = scopedWL.map(w => ({
                  ticker: w.ticker, name: w.name,
                  sector: w.sector || "Other",
                  country: w.country || w.exchange || "Unknown",
                  class: w.class || "",
                  valueUSD: 1, // equal-weight aggregation; we care about composition not absolute value
                }));
                const watchlistLT = applyLookthrough(wlForLT, { valueKey: "valueUSD" });
                // Cache for UI rendering
                setWlLookthrough(p => ({ ...p, [scopeKey]: watchlistLT }));
                const systemPrompt = `You are a senior wealth advisor analyzing a watchlist for an HNI client. Your job is to identify the highest-conviction items to act on this week, considering both standalone merit AND fit with the existing portfolio.

You will receive:
- Watchlist items (id, ticker, name, price, target, buyBelow, weight, thesis, tech)
- The "tech" object contains computed technical signals from real price history:
    aboveSMA50/aboveSMA200 (boolean), goldenCross (50DMA > 200DMA),
    rsi (0-100, >70 overbought, <30 oversold),
    ret1m/3m/6m/12m (% return over period),
    pctFrom52WHigh (negative = below high), vol30d (annualised %)
- Portfolio context (top sectors after ETF look-through, top regions, total value, holdings count)
- Watchlist's pre-computed look-through composition (sectors and regions, with ETFs distributed to underliers)

Return a JSON object with this exact schema. Do NOT include any prose outside the JSON. Do NOT use markdown code fences.

{
  "headline": "2-3 sentence summary of the watchlist's character and the recommended action this week",
  "topPicks": [
    {
      "rank": 1,
      "id": "watchlist-item-id",
      "ticker": "string",
      "name": "string",
      "reason": "ONE sentence on why this is a top pick now (cite specific tech signals + portfolio fit)",
      "suggestedAmount": 2500,
      "conviction": "High" | "Medium" | "Low",
      "fundamentals": {
        "quality":   {"rating": "Strong"|"Fair"|"Weak"|"Mixed", "note": "ONE phrase on moat/balance sheet/management"},
        "valuation": {"rating": "Cheap"|"Fair"|"Stretched"|"Expensive", "note": "ONE phrase on relative valuation"},
        "growth":    {"rating": "Strong"|"Steady"|"Slowing"|"Weak", "note": "ONE phrase on growth trajectory"},
        "income":    {"rating": "High"|"Moderate"|"Low"|"None", "note": "ONE phrase on dividend/yield"}
      }
    }
  ],
  "composition": {
    "sectorTilts": [{"sector": "string", "pct": 35}],
    "concentrationFlag": "string describing concentration risk vs portfolio, or null if none"
  },
  "actionGaps": ["string action items, 1-3 of them"]
}

Rules:
- Pick 3-5 top picks. Combine technical setup with portfolio fit.
- Conviction High = strong tech setup (above both SMAs, RSI 40-70, positive momentum) AND fits portfolio gap.
- Conviction Medium = mixed signals or stretched valuation but still actionable.
- Conviction Low = overbought (RSI > 75), below 200DMA, or other warning signs.
- "reason" should reference SPECIFIC tech signals (e.g., "Above 50/200 DMA, RSI 58, 6M +18%, fills Asia financials gap").
- Buy/target prices are SECONDARY — they may not be set. Lead with technical setup.
- "fundamentals" ratings: based on your general knowledge of the company. Be HONEST about uncertainty:
  * For ETFs / index funds, use rating "N/A" if a metric doesn't apply (e.g., income for growth ETFs).
  * For stocks you have low confidence about, use "Mixed" / "Fair" rather than fabricating a strong rating.
  * Notes should be ONE PHRASE, max 8 words. No fabricated specific numbers (no "P/E 22.5") — use qualitative language.
  * Examples of good notes: "Strong moat, dominant in EDA", "Premium to peers", "Mid-teens revenue growth", "5% dividend yield".
  * Examples of bad notes: "P/E of 18.4 vs sector 15.2" (fabricated specifics), "Excellent company" (vague).
- "suggestedAmount" should be in USD, sized proportionally to weight if set, else equal-weight a notional $10K deployment.
- Concentration flag should compare watchlist sector tilts to existing portfolio sectors.
- If items have no tech data (older, less-traded, or non-Yahoo), note this in actionGaps.
- Be concise. This is for a busy advisor. No fluff.
- Return ONLY the JSON object.`;
                const userPrompt = `Scope: ${scopeName}
Watchlist (${wlPayload.length} items):
${JSON.stringify(wlPayload, null, 2)}

Existing portfolio (with ETF look-through applied):
${JSON.stringify(portfolioCtx, null, 2)}

Watchlist composition (with ETF look-through applied):
${JSON.stringify({
  sectorBreakdown: watchlistLT.sectorBreakdown,
  regionBreakdown: watchlistLT.regionBreakdown,
  etfsCovered: `${watchlistLT.coverage.coveredEtfs} of ${watchlistLT.coverage.totalEtfs}`,
  unknownEtfs: watchlistLT.coverage.unknownTickers.slice(0, 5),
}, null, 2)}

Stats:
- Buyable now (price ≤ buy level): ${buyableCount}
- Above target: ${aboveCount}
- Items missing targets: ${noTargets}

Use the look-through composition for sectorTilts in your output (do not recompute). The breakdowns above already reflect TRUE exposure with ETFs distributed to their underlying sectors/regions.

Generate the structured advisor report.`;
                const resp = await fetch("/api/claude", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    messages: [{ role: "user", content: userPrompt }],
                    system: systemPrompt,
                    max_tokens: 3000,
                    provider: (aiConfig||{}).advisorProvider || "anthropic",
                    model: (aiConfig||{}).advisorModel || "claude-haiku-4-5-20251001",
                    apiKey: (aiConfig||{}).advisorProvider === "openai" ? (aiConfig||{}).openaiKey : (aiConfig||{}).advisorProvider === "gemini" ? (aiConfig||{}).geminiKey : undefined,
                  }),
                });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const data = await resp.json();
                const text = data?.text || data?.content?.[0]?.text || "";
                if (!text) throw new Error("Empty response from AI");
                // Extract JSON from response — sometimes the model wraps it despite instructions
                let jsonStr = text.trim();
                if (jsonStr.startsWith("```")) {
                  jsonStr = jsonStr.replace(/^```(?:json)?\s*/, "").replace(/\s*```$/, "");
                }
                // Sometimes the model adds preamble — find the first { and last }
                const firstBrace = jsonStr.indexOf("{");
                const lastBrace  = jsonStr.lastIndexOf("}");
                if (firstBrace >= 0 && lastBrace > firstBrace) {
                  jsonStr = jsonStr.slice(firstBrace, lastBrace + 1);
                }
                const parsed = JSON.parse(jsonStr);
                // Cache it
                setWlAdvisorReports(p => ({ ...p, [scopeKey]: { ...parsed, generatedAt: new Date().toISOString() } }));
                showToast("✓ AI report generated", "#1D9E75", 2000);
              } catch (err) {
                console.error("[wlAdvisor] generation failed:", err);
                setWlAdvisorError(err.message || "Failed to generate report");
                showToast(`⚠ Generation failed: ${err.message}`, "#A32D2D", 4000);
              } finally {
                setWlAdvisorLoading(false);
              }
            };
            // Auto-trigger generation when modal first opens with no cached report
            if (!cachedReport && !wlAdvisorLoading && !wlAdvisorError) {
              setTimeout(() => generate(), 50);
            }
            // Export handlers
            const exportAdvisorCSV = () => {
              if (!cachedReport) return;
              const rows = (cachedReport.topPicks || []).map(p => ({
                rank: p.rank,
                ticker: p.ticker,
                name: p.name,
                reason: p.reason,
                suggestedAmount: p.suggestedAmount,
                conviction: p.conviction,
              }));
              const cols = [
                { key: "rank", label: "Rank" },
                { key: "ticker", label: "Ticker" },
                { key: "name", label: "Asset" },
                { key: "reason", label: "Why now" },
                { key: "suggestedAmount", label: "Suggested ($)", format: v => v ? Number(v).toLocaleString() : "" },
                { key: "conviction", label: "Conviction" },
              ];
              const scopeSlug = selectedClients.size === 1
                ? (clients.find(c => String(c.id)===String([...selectedClients][0]))?.name || "client").replace(/\s+/g,"-").toLowerCase()
                : "all";
              exportToCSV(rows, cols, `ai-watchlist-advisor-${scopeSlug}-${new Date().toISOString().slice(0,10)}.csv`);
              showToast("✓ Advisor report downloaded", "#1D9E75", 2000);
            };
            const exportAdvisorPDF = () => {
              if (!cachedReport) return;
              const esc = escapeHtmlForPDF;
              const headlineHTML = `<div class="callout">${esc(cachedReport.headline || "")}</div>`;
              const kpiHTML = `<div class="summary">
                <div class="kpi"><div class="kpi-label">Buyable now</div><div class="kpi-value" style="color:#166534">${buyableCount}</div></div>
                <div class="kpi"><div class="kpi-label">Above target</div><div class="kpi-value" style="color:#92400E">${aboveCount}</div></div>
                <div class="kpi"><div class="kpi-label">No targets set</div><div class="kpi-value" style="color:#A32D2D">${noTargets}</div></div>
                <div class="kpi"><div class="kpi-label">Watching</div><div class="kpi-value">${scopedWL.length}</div></div>
              </div>`;
              const picksHTML = (cachedReport.topPicks && cachedReport.topPicks.length > 0)
                ? `<h2>Top picks this week</h2>
                   <table>
                     <thead><tr><th style="width:40px">Rank</th><th>Asset</th><th>Why now</th><th class="num" style="width:80px">Suggested</th><th style="width:80px">Conviction</th></tr></thead>
                     <tbody>${cachedReport.topPicks.map(p => `<tr>
                       <td><strong>#${esc(p.rank)}</strong></td>
                       <td><strong>${esc(p.name)}</strong><br/><span class="muted">${esc(p.ticker)}</span></td>
                       <td style="font-size:10px">${esc(p.reason)}</td>
                       <td class="num">$${p.suggestedAmount ? Number(p.suggestedAmount).toLocaleString() : "—"}</td>
                       <td><span class="badge ${p.conviction === "High" ? "high" : p.conviction === "Medium" ? "med" : "low"}">${esc(p.conviction)}</span></td>
                     </tr>`).join("")}</tbody>
                   </table>` : "";
              // ── Technical signals + Fundamentals (per top pick) ──
              const techMap = wlTechSignals[scopeKey] || {};
              const picksWithTech = (cachedReport.topPicks || [])
                .map(p => ({ pick: p, sig: techMap[p.ticker] }))
                .filter(x => x.sig && x.sig.dataPoints > 50);
              // Helper to convert a fundamentals rating to a CSS badge class
              const fundClass = (rating) => {
                if (!rating) return "low";
                const r = String(rating).toLowerCase();
                if (["strong","cheap","high"].includes(r)) return "high";
                if (["fair","steady","moderate"].includes(r)) return "watch";
                if (["mixed","slowing","stretched","low"].includes(r)) return "med";
                if (["weak","expensive"].includes(r)) return "low";
                return "low";
              };
              const techDeepHTML = picksWithTech.length > 0
                ? `<h2>Technical & fundamental view — top picks</h2>
                   ${picksWithTech.map(({pick, sig}) => {
                     const techChips = [];
                     if (sig.aboveSMA50 != null)  techChips.push({ label: sig.aboveSMA50?"Above 50DMA":"Below 50DMA", cls: sig.aboveSMA50?"buy":"med" });
                     if (sig.aboveSMA200 != null) techChips.push({ label: sig.aboveSMA200?"Above 200DMA":"Below 200DMA", cls: sig.aboveSMA200?"buy":"low" });
                     if (sig.sma50AboveSma200 != null) techChips.push({ label: sig.sma50AboveSma200?"Golden cross":"Death cross", cls: sig.sma50AboveSma200?"buy":"low" });
                     if (sig.rsi != null) techChips.push({ label: `RSI ${sig.rsi.toFixed(0)}`, cls: sig.rsi>75?"low":sig.rsi>70?"med":"watch" });
                     [["1M",sig.ret1m],["3M",sig.ret3m],["6M",sig.ret6m]].forEach(([lbl,val]) => {
                       if (val == null) return;
                       const sign = val >= 0 ? "+" : "";
                       techChips.push({ label: `${lbl} ${sign}${val.toFixed(1)}%`, cls: val>5?"buy":val<-5?"low":"watch" });
                     });
                     if (sig.distFrom52WHigh != null) {
                       const lbl = sig.distFrom52WHigh > -2 ? "At 52W high" : `${sig.distFrom52WHigh.toFixed(0)}% from high`;
                       techChips.push({ label: lbl, cls: sig.distFrom52WHigh > -2 ? "med" : "low" });
                     }
                     const f = pick.fundamentals || {};
                     const fundDims = [
                       ["Quality",   f.quality],
                       ["Valuation", f.valuation],
                       ["Growth",    f.growth],
                       ["Income",    f.income],
                     ].filter(([_,v]) => v && v.rating);
                     return `<div class="thesis-block">
                       <div class="thesis-head"><strong>${esc(pick.name)}</strong> <span class="muted">${esc(pick.ticker)}${sig.vol30d!=null?` · 30d vol ${sig.vol30d.toFixed(0)}%`:""}</span></div>
                       <div style="margin:6px 0;">
                         <div class="muted" style="margin-bottom:4px;">Technical signals</div>
                         ${techChips.map(c => `<span class="badge ${c.cls}" style="margin-right:3px;">${esc(c.label)}</span>`).join("")}
                       </div>
                       ${fundDims.length > 0 ? `
                       <div style="margin-top:8px;padding-top:6px;border-top:1px dashed #E5E7EB;">
                         <div class="muted" style="margin-bottom:4px;">Fundamentals · AI assessment</div>
                         <table style="font-size:9.5px;border:none;width:100%;">
                           <tbody>${fundDims.map(([lbl,val]) => `<tr style="border:none;">
                             <td style="border:none;padding:2px 0;width:140px;"><span class="badge ${fundClass(val.rating)}">${esc(lbl)}: ${esc(val.rating)}</span></td>
                             <td style="border:none;padding:2px 0;color:#6B7280;font-size:9.5px;">${esc(val.note || "")}</td>
                           </tr>`).join("")}</tbody>
                         </table>
                       </div>` : ""}
                     </div>`;
                   }).join("")}
                   <div class="callout" style="background:#FEF3C7;border-left-color:#92400E;color:#92400E;margin-top:8px;font-size:10px;">
                     <strong>Note:</strong> Technical signals are computed from real price history. Fundamental ratings are AI assessments based on training data and may not reflect the latest reported numbers. Verify on primary source before acting.
                   </div>` : "";
              // ── Watchlist heatmap (all items) ──
              const allItemsWithTech = scopedWL.map(w => ({ wl: w, sig: techMap[w.ticker] }));
              allItemsWithTech.sort((a,b) => {
                const sa = a.sig, sb = b.sig;
                if (!sa && !sb) return 0;
                if (!sa) return 1;
                if (!sb) return -1;
                const score = (s) => (s.aboveSMA200?2:0) + (s.aboveSMA50?1:0) + (s.sma50AboveSma200?1:0) + (s.rsi>50?1:0);
                return score(sb) - score(sa);
              });
              const heatmapHTML = allItemsWithTech.length > 0
                ? `<h2>Watchlist tech heatmap — all ${scopedWL.length} items</h2>
                   <table style="font-size:9px;">
                     <thead><tr>
                       <th>Asset</th><th>Trend</th><th class="num" style="width:42px">RSI</th>
                       <th class="num" style="width:46px">1M</th><th class="num" style="width:46px">3M</th><th class="num" style="width:46px">6M</th>
                       <th class="num" style="width:50px">52W</th><th class="num" style="width:46px">Vol</th>
                     </tr></thead>
                     <tbody>${allItemsWithTech.map(({wl, sig}) => {
                       const isTopPick = (cachedReport.topPicks||[]).some(p => p.ticker === wl.ticker);
                       const star = isTopPick ? '<span style="color:#7C3AED;font-weight:700;">★</span> ' : '';
                       if (!sig || sig.dataPoints < 50) {
                         return `<tr><td>${star}<strong>${esc(wl.name)}</strong><br/><span class="muted">${esc(wl.ticker)}</span></td><td colspan="7" class="muted" style="font-style:italic;">Insufficient data</td></tr>`;
                       }
                       const trendChips = [];
                       if (sig.aboveSMA50 != null) trendChips.push(`<span class="badge ${sig.aboveSMA50?"buy":"med"}" style="font-size:8px;">${sig.aboveSMA50?"50↑":"50↓"}</span>`);
                       if (sig.aboveSMA200 != null) trendChips.push(`<span class="badge ${sig.aboveSMA200?"buy":"low"}" style="font-size:8px;">${sig.aboveSMA200?"200↑":"200↓"}</span>`);
                       if (sig.sma50AboveSma200 != null) trendChips.push(`<span class="badge ${sig.sma50AboveSma200?"buy":"low"}" style="font-size:8px;">${sig.sma50AboveSma200?"GC":"DC"}</span>`);
                       const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
                       const colorPct = (v) => v == null ? "#9CA3AF" : v >= 0 ? "#1D9E75" : "#A32D2D";
                       return `<tr>
                         <td>${star}<strong>${esc(wl.name)}</strong><br/><span class="muted">${esc(wl.ticker)}</span></td>
                         <td>${trendChips.join(" ")}</td>
                         <td class="num">${sig.rsi==null?"—":sig.rsi.toFixed(0)}</td>
                         <td class="num" style="color:${colorPct(sig.ret1m)}">${fmtPct(sig.ret1m)}</td>
                         <td class="num" style="color:${colorPct(sig.ret3m)}">${fmtPct(sig.ret3m)}</td>
                         <td class="num" style="color:${colorPct(sig.ret6m)}">${fmtPct(sig.ret6m)}</td>
                         <td class="num">${sig.distFrom52WHigh==null?"—":sig.distFrom52WHigh.toFixed(0)+"%"}</td>
                         <td class="num">${sig.volatility30d==null?"—":sig.volatility30d.toFixed(0)+"%"}</td>
                       </tr>`;
                     }).join("")}</tbody>
                   </table>
                   <div class="muted" style="margin-top:6px;font-size:8.5px;">★ = AI top pick · 50↑/200↑ = above SMA · GC/DC = golden/death cross · 52W = % from 52-week high · Vol = 30d annualised</div>` : "";
              // Composition section — uses look-through data when available
              const ltData = wlLookthrough[scopeKey];
              const sectorEntries = ltData
                ? Object.entries(ltData.sectorBreakdown).sort(([,a],[,b]) => b-a).slice(0, 8)
                : (cachedReport.composition?.sectorTilts || []).map(t => [t.sector, t.pct]).slice(0, 8);
              const regionEntries = ltData
                ? Object.entries(ltData.regionBreakdown).sort(([,a],[,b]) => b-a).slice(0, 6)
                : [];
              const coverageNote = ltData
                ? `<div class="muted" style="font-size:9px;margin-bottom:6px;">With ETF look-through · ${ltData.coverage.coveredEtfs} of ${ltData.coverage.totalEtfs} ETFs covered</div>`
                : "";
              const compHTML = sectorEntries.length > 0
                ? `<h2>Watchlist composition</h2>
                   ${coverageNote}
                   <table style="margin-bottom:8px;">
                     <thead><tr><th>Sector — true exposure</th><th class="num" style="width:80px">%</th></tr></thead>
                     <tbody>${sectorEntries.map(([s, p]) => `<tr>
                       <td>${esc(s)}</td>
                       <td class="num">${Number(p).toFixed(1)}%</td>
                     </tr>`).join("")}</tbody>
                   </table>
                   ${regionEntries.length > 0 ? `
                   <table>
                     <thead><tr><th>Region — true exposure</th><th class="num" style="width:80px">%</th></tr></thead>
                     <tbody>${regionEntries.map(([r, p]) => `<tr>
                       <td>${esc(r)}</td>
                       <td class="num">${Number(p).toFixed(1)}%</td>
                     </tr>`).join("")}</tbody>
                   </table>` : ""}
                   ${cachedReport.composition?.concentrationFlag ? `<div class="callout" style="background:#FEF3C7;border-left-color:#92400E;color:#92400E;margin-top:10px">${esc(cachedReport.composition.concentrationFlag)}</div>` : ""}
                   ${ltData && ltData.coverage.unknownTickers.length > 0 ? `<div class="muted" style="margin-top:6px;font-size:9px;"><strong>Unknown ETFs (shown at surface):</strong> ${esc(ltData.coverage.unknownTickers.slice(0,5).join(", "))}${ltData.coverage.unknownTickers.length > 5 ? ` +${ltData.coverage.unknownTickers.length - 5} more` : ""}</div>` : ""}` : "";
              const gapsHTML = (cachedReport.actionGaps && cachedReport.actionGaps.length > 0)
                ? `<h2>Action gaps</h2>
                   <table>
                     <tbody>${cachedReport.actionGaps.map(g => `<tr><td>${esc(g)}</td></tr>`).join("")}</tbody>
                   </table>` : "";
              exportToBrandedPDF({
                title: "AI Watchlist Advisor",
                subtitle: scopeName,
                bodyHTML: headlineHTML + kpiHTML + picksHTML + techDeepHTML + heatmapHTML + compHTML + gapsHTML,
                onPopupBlocked: () => showToast("⚠ Pop-up blocked — allow pop-ups to export PDF", "#A32D2D", 4000),
              });
              showToast("✓ Print dialog opened — choose 'Save as PDF'", "#1D9E75", 2500);
            };
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={close}>
                <div onClick={e=>e.stopPropagation()} style={{background:T.cardBg,borderRadius:12,maxWidth:920,width:"100%",maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.3)"}}>
                  {/* Header */}
                  <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                    <div>
                      <div style={{fontSize:17,fontWeight:700}}>🤖 AI Advisor on Watchlist</div>
                      <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                        Analysis of {scopedWL.length} candidates · {scopeName}
                        {cachedReport && cachedReport.generatedAt && ` · Generated ${new Date(cachedReport.generatedAt).toLocaleString()}`}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {cachedReport && (
                        <>
                          <button onClick={generate} disabled={wlAdvisorLoading} title="Generate fresh analysis" style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textPrimary,padding:"5px 12px",cursor:wlAdvisorLoading?"wait":"pointer",fontSize:11,fontWeight:600,opacity:wlAdvisorLoading?0.6:1}}>
                            {wlAdvisorLoading ? "…" : "Regenerate"}
                          </button>
                          <button onClick={exportAdvisorCSV} style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textPrimary,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                            CSV
                          </button>
                          <button onClick={exportAdvisorPDF} style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textPrimary,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                            PDF
                          </button>
                        </>
                      )}
                      <button onClick={close} style={{background:"transparent",border:"none",fontSize:22,color:T.textSecondary,cursor:"pointer",padding:"0 4px",lineHeight:1}}>×</button>
                    </div>
                  </div>
                  {/* Body */}
                  <div style={{padding:"18px 24px",overflowY:"auto",flex:1}}>
                    {wlAdvisorLoading && (
                      <div style={{padding:"60px 20px",textAlign:"center"}}>
                        <div style={{fontSize:32,marginBottom:12}}>🤖</div>
                        <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>
                          {wlTechLoading ? "Fetching technical signals…" : "Generating analysis…"}
                        </div>
                        <div style={{fontSize:12,color:T.textSecondary}}>
                          {wlTechLoading
                            ? `Reading ${scopedWL.length}× 1-year price history from Yahoo. Then AI synthesis.`
                            : `Combining tech signals with portfolio context. Usually 8-15 seconds.`}
                        </div>
                      </div>
                    )}
                    {wlAdvisorError && !wlAdvisorLoading && (
                      <div style={{padding:"40px 20px",textAlign:"center"}}>
                        <div style={{fontSize:28,marginBottom:10}}>⚠</div>
                        <div style={{fontSize:13,fontWeight:600,color:"#A32D2D",marginBottom:8}}>Generation failed</div>
                        <div style={{fontSize:12,color:T.textSecondary,marginBottom:14,maxWidth:480,margin:"0 auto 14px"}}>{wlAdvisorError}</div>
                        <button onClick={generate} style={{background:"#4F46E5",border:"none",borderRadius:6,color:"white",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600}}>Try again</button>
                      </div>
                    )}
                    {cachedReport && !wlAdvisorLoading && !wlAdvisorError && (
                      <>
                        {/* Headline */}
                        <div style={{background:"rgba(99,102,241,0.06)",borderLeft:"3px solid #4F46E5",padding:"12px 14px",borderRadius:4,marginBottom:16}}>
                          <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600,marginBottom:6}}>Headline</div>
                          <div style={{fontSize:13,lineHeight:1.6}}>{cachedReport.headline}</div>
                        </div>
                        {/* KPIs */}
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:8,marginBottom:18}}>
                          <div style={{...tCard(),padding:"10px 12px"}}>
                            <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Buyable now</div>
                            <div style={{fontSize:18,fontWeight:700,marginTop:3,color:"#1D9E75"}}>{buyableCount} of {scopedWL.length}</div>
                          </div>
                          <div style={{...tCard(),padding:"10px 12px"}}>
                            <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Above target</div>
                            <div style={{fontSize:18,fontWeight:700,marginTop:3,color:"#D97706"}}>{aboveCount}</div>
                          </div>
                          <div style={{...tCard(),padding:"10px 12px"}}>
                            <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>No targets set</div>
                            <div style={{fontSize:18,fontWeight:700,marginTop:3,color:"#A32D2D"}}>{noTargets}</div>
                          </div>
                          <div style={{...tCard(),padding:"10px 12px"}}>
                            <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Top sector</div>
                            <div style={{fontSize:14,fontWeight:700,marginTop:3}}>{cachedReport.composition?.sectorTilts?.[0]?.sector || "—"}</div>
                          </div>
                        </div>
                        {/* Top picks */}
                        {cachedReport.topPicks && cachedReport.topPicks.length > 0 && (
                          <div style={{marginBottom:18}}>
                            <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>Top picks this week</div>
                            <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                              {cachedReport.topPicks.map((p,i) => {
                                const convBg = p.conviction === "High" ? "#DCFCE7" : p.conviction === "Medium" ? "#FEF3C7" : "#F3F4F6";
                                const convColor = p.conviction === "High" ? "#166534" : p.conviction === "Medium" ? "#92400E" : "#4B5563";
                                return (
                                  <div key={p.id || i} style={{display:"grid",gridTemplateColumns:"40px 1fr 90px 80px",padding:"10px 14px",alignItems:"center",borderBottom:i < cachedReport.topPicks.length-1?`1px solid ${T.cardBorder}`:"none",fontSize:12,gap:10}}>
                                    <div style={{fontWeight:700,color:"#1D9E75",fontSize:14}}>#{p.rank}</div>
                                    <div>
                                      <div style={{fontWeight:600,fontSize:12}}>{p.name} <span style={{color:T.textSecondary,fontWeight:400,fontSize:11}}>· {p.ticker}</span></div>
                                      <div style={{fontSize:11,color:T.textSecondary,marginTop:2,lineHeight:1.5}}>{p.reason}</div>
                                    </div>
                                    <div style={{textAlign:"right",fontWeight:600,fontSize:12}}>${p.suggestedAmount ? Number(p.suggestedAmount).toLocaleString() : "—"}</div>
                                    <div style={{textAlign:"right"}}>
                                      <span style={{background:convBg,color:convColor,padding:"2px 8px",borderRadius:12,fontSize:10,fontWeight:600}}>{p.conviction}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Technical signals — Stage 1 of advisor evolution.
                            Computed locally from real Yahoo price data.
                            Only shown for items that the AI flagged as top picks. */}
                        {cachedReport.topPicks && cachedReport.topPicks.length > 0 && wlTechSignals[scopeKey] && (function(){
                          const techMap = wlTechSignals[scopeKey];
                          const picksWithTech = cachedReport.topPicks
                            .map(p => ({ pick: p, sig: techMap[p.ticker] }))
                            .filter(x => x.sig && x.sig.dataPoints > 50); // need decent history
                          if (picksWithTech.length === 0) return null;
                          // Chip helper
                          const chip = (label, tone) => {
                            const palette = {
                              good:    { bg: "#DCFCE7", color: "#166534" },
                              warn:    { bg: "#FEF3C7", color: "#92400E" },
                              bad:     { bg: "#FCEBEB", color: "#A32D2D" },
                              neutral: { bg: "#DBEAFE", color: "#1E40AF" },
                              muted:   { bg: T.borderSubtle, color: T.textSecondary },
                            }[tone] || palette.muted;
                            return { background: palette.bg, color: palette.color };
                          };
                          return (
                            <div style={{marginBottom:18}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                                <div style={{fontSize:13,fontWeight:600}}>Technical signals — top picks</div>
                                <div style={{fontSize:10,color:T.textSecondary}}>Computed from real price history · 1Y window</div>
                              </div>
                              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                                {picksWithTech.map(({pick, sig}, i) => {
                                  const chips = [];
                                  // Trend chips
                                  if (sig.aboveSMA50 != null) {
                                    chips.push({
                                      label: sig.aboveSMA50 ? "Above 50DMA" : "Below 50DMA",
                                      style: chip(null, sig.aboveSMA50 ? "good" : "warn"),
                                    });
                                  }
                                  if (sig.aboveSMA200 != null) {
                                    chips.push({
                                      label: sig.aboveSMA200 ? "Above 200DMA" : "Below 200DMA",
                                      style: chip(null, sig.aboveSMA200 ? "good" : "bad"),
                                    });
                                  }
                                  if (sig.sma50AboveSma200 != null) {
                                    chips.push({
                                      label: sig.sma50AboveSma200 ? "Golden cross" : "Death cross",
                                      style: chip(null, sig.sma50AboveSma200 ? "good" : "bad"),
                                    });
                                  }
                                  // RSI chip
                                  if (sig.rsi != null) {
                                    const rsi = sig.rsi;
                                    const tone = rsi > 75 ? "bad" : rsi > 70 ? "warn" : rsi < 30 ? "warn" : "neutral";
                                    chips.push({ label: `RSI ${rsi.toFixed(0)}`, style: chip(null, tone) });
                                  }
                                  // Momentum chips
                                  [["1M",sig.ret1m],["3M",sig.ret3m],["6M",sig.ret6m]].forEach(([lbl, val]) => {
                                    if (val == null) return;
                                    const tone = val > 5 ? "good" : val < -5 ? "bad" : "neutral";
                                    const sign = val >= 0 ? "+" : "";
                                    chips.push({ label: `${lbl} ${sign}${val.toFixed(1)}%`, style: chip(null, tone) });
                                  });
                                  // 52W high distance
                                  if (sig.distFrom52WHigh != null) {
                                    const dist = sig.distFrom52WHigh;
                                    const tone = dist > -2 ? "warn" : dist < -25 ? "warn" : "muted";
                                    const lbl = dist > -2 ? "At 52W high" : `${dist.toFixed(0)}% from high`;
                                    chips.push({ label: lbl, style: chip(null, tone) });
                                  }
                                  return (
                                    <div key={pick.id || i} style={{padding:"10px 14px",borderBottom:i < picksWithTech.length-1?`1px solid ${T.cardBorder}`:"none"}}>
                                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                                        <div>
                                          <div style={{fontSize:12,fontWeight:600}}>{pick.name}</div>
                                          <div style={{fontSize:10,color:T.textSecondary}}>{pick.ticker}</div>
                                        </div>
                                        {sig.vol30d != null && (
                                          <div style={{fontSize:10,color:T.textSecondary,textAlign:"right"}}>
                                            30d vol<br/>
                                            <span style={{fontWeight:600,color:T.textPrimary,fontSize:11}}>{sig.vol30d.toFixed(0)}%</span>
                                          </div>
                                        )}
                                      </div>
                                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                        {chips.map((c,j)=>(
                                          <span key={j} style={{...c.style,padding:"2px 7px",borderRadius:10,fontSize:10,fontWeight:500}}>{c.label}</span>
                                        ))}
                                      </div>
                                      {/* ── Fundamental ratings (Stage 2) ─────────────────────────── */}
                                      {pick.fundamentals && (function(){
                                        // Map rating words to tone (color)
                                        const toneFor = (rating) => {
                                          if (!rating) return "muted";
                                          const r = String(rating).toLowerCase();
                                          if (r === "n/a" || r === "none") return "muted";
                                          if (["strong","cheap","high"].includes(r)) return "good";
                                          if (["fair","steady","moderate"].includes(r)) return "neutral";
                                          if (["mixed","slowing","stretched","low"].includes(r)) return "warn";
                                          if (["weak","expensive"].includes(r)) return "bad";
                                          return "muted";
                                        };
                                        const palette = {
                                          good:    { bg: "#DCFCE7", color: "#166534" },
                                          neutral: { bg: "#DBEAFE", color: "#1E40AF" },
                                          warn:    { bg: "#FEF3C7", color: "#92400E" },
                                          bad:     { bg: "#FCEBEB", color: "#A32D2D" },
                                          muted:   { bg: T.borderSubtle, color: T.textSecondary },
                                        };
                                        const f = pick.fundamentals;
                                        const dims = [
                                          ["Quality",   f.quality],
                                          ["Valuation", f.valuation],
                                          ["Growth",    f.growth],
                                          ["Income",    f.income],
                                        ].filter(([_,v]) => v && v.rating);
                                        if (dims.length === 0) return null;
                                        return (
                                          <div style={{marginTop:8,paddingTop:8,borderTop:`1px dashed ${T.cardBorder}`}}>
                                            <div style={{fontSize:9,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600,marginBottom:5}}>Fundamentals · AI assessment</div>
                                            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:6}}>
                                              {dims.map(([label, val], k) => {
                                                const tone = palette[toneFor(val.rating)];
                                                return (
                                                  <div key={k} style={{display:"flex",alignItems:"flex-start",gap:6}}>
                                                    <span style={{...tone,padding:"2px 7px",borderRadius:10,fontSize:10,fontWeight:600,whiteSpace:"nowrap",flexShrink:0}}>
                                                      {label}: {val.rating}
                                                    </span>
                                                    {val.note && <span style={{fontSize:10,color:T.textSecondary,lineHeight:1.4}}>{val.note}</span>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                              {/* ── Honest disclosure for fundamental ratings ──
                                  Critical advisor expectation: the fundamental
                                  ratings come from the AI's training data, not a
                                  live data source. They are starting points only. */}
                              <div style={{marginTop:8,padding:"8px 12px",background:"#FEF3C7",borderLeft:"3px solid #92400E",borderRadius:4,fontSize:10,lineHeight:1.55,color:"#92400E"}}>
                                <strong>Note:</strong> Technical signals are computed from real price history. Fundamental ratings are AI assessments based on training data and may not reflect the latest reported numbers. Verify on primary source before acting.
                              </div>
                            </div>
                          );
                        })()}
                        {/* Composition — uses look-through aggregation when available */}
                        {(() => {
                          const ltData = wlLookthrough[scopeKey];
                          const aiSectors = cachedReport.composition?.sectorTilts || [];
                          // Prefer look-through data; fall back to AI's sectorTilts
                          const sectorEntries = ltData
                            ? Object.entries(ltData.sectorBreakdown).sort(([,a],[,b]) => b-a).slice(0, 8)
                            : aiSectors.map(t => [t.sector, t.pct]).slice(0, 8);
                          const regionEntries = ltData
                            ? Object.entries(ltData.regionBreakdown).sort(([,a],[,b]) => b-a).slice(0, 6)
                            : [];
                          if (sectorEntries.length === 0) return null;
                          const palette = ["#7C3AED","#4F46E5","#3B82F6","#0EA5E9","#10B981","#F59E0B","#EF4444","#94A3B8"];
                          return (
                            <div style={{marginBottom:18}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,gap:8,flexWrap:"wrap"}}>
                                <div style={{fontSize:13,fontWeight:600}}>Watchlist composition</div>
                                {ltData && (
                                  <span style={{fontSize:10,color:T.textSecondary,fontStyle:"italic"}}>
                                    With ETF look-through · {ltData.coverage.coveredEtfs} of {ltData.coverage.totalEtfs} ETFs covered
                                  </span>
                                )}
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:regionEntries.length>0?"1fr 1fr":"1fr",gap:10}}>
                                {/* Sector breakdown */}
                                <div style={{...tCard(),padding:"12px 14px"}}>
                                  <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600,marginBottom:8}}>By sector — true exposure</div>
                                  {sectorEntries.map(([sector, pct], i) => (
                                    <div key={sector} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                                      <div style={{flex:1,height:6,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                                        <div style={{width:`${Math.min(100, pct)}%`,height:"100%",background:palette[i % palette.length]}}/>
                                      </div>
                                      <span style={{fontSize:11,minWidth:140,color:T.textPrimary}}>
                                        {sector} <span style={{color:T.textSecondary}}>{Number(pct).toFixed(1)}%</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                {/* Region breakdown — only if look-through computed */}
                                {regionEntries.length > 0 && (
                                  <div style={{...tCard(),padding:"12px 14px"}}>
                                    <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600,marginBottom:8}}>By region — true exposure</div>
                                    {regionEntries.map(([region, pct], i) => (
                                      <div key={region} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                                        <div style={{flex:1,height:6,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                                          <div style={{width:`${Math.min(100, pct)}%`,height:"100%",background:palette[i % palette.length]}}/>
                                        </div>
                                        <span style={{fontSize:11,minWidth:120,color:T.textPrimary}}>
                                          {region} <span style={{color:T.textSecondary}}>{Number(pct).toFixed(1)}%</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {/* Concentration flag (from AI) + unknown ETFs note */}
                              {(cachedReport.composition?.concentrationFlag || (ltData && ltData.coverage.unknownTickers.length > 0)) && (
                                <div style={{...tCard(),padding:"10px 14px",marginTop:8,fontSize:11,color:T.textSecondary,lineHeight:1.5}}>
                                  {cachedReport.composition?.concentrationFlag && (
                                    <div>
                                      <span style={{color:"#D97706",fontWeight:600}}>Concentration flag:</span> {cachedReport.composition.concentrationFlag}
                                    </div>
                                  )}
                                  {ltData && ltData.coverage.unknownTickers.length > 0 && (
                                    <div style={{marginTop:cachedReport.composition?.concentrationFlag?6:0}}>
                                      <span style={{fontWeight:600}}>Unknown ETFs (shown at surface):</span> {ltData.coverage.unknownTickers.slice(0,5).join(", ")}
                                      {ltData.coverage.unknownTickers.length > 5 && ` +${ltData.coverage.unknownTickers.length - 5} more`}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* Watchlist tech heatmap — all items in compact form.
                            Shows the full picture beyond just AI's top picks.
                            Items where Yahoo returned no data show as "—". */}
                        {wlTechSignals[scopeKey] && scopedWL.length > 0 && (function(){
                          const techMap = wlTechSignals[scopeKey];
                          const itemsWithTech = scopedWL.map(w => ({ wl: w, sig: techMap[w.ticker] }));
                          // Sort: items with strong setup first (above both SMAs + positive 3M)
                          // Items without tech data go to the bottom
                          itemsWithTech.sort((a, b) => {
                            const sa = a.sig, sb = b.sig;
                            if (!sa && !sb) return 0;
                            if (!sa) return 1;
                            if (!sb) return -1;
                            // Score: above 200DMA = +2, above 50DMA = +1, golden cross = +1, RSI>50 = +1
                            const score = (s) => (s.aboveSMA200?2:0) + (s.aboveSMA50?1:0) + (s.sma50AboveSma200?1:0) + (s.rsi>50?1:0);
                            return score(sb) - score(sa);
                          });
                          // Compact chip — fewer signals than top-picks section, abbreviated
                          const compactChip = (label, tone) => {
                            const palette = {
                              good:    { bg: "#DCFCE7", color: "#166534" },
                              warn:    { bg: "#FEF3C7", color: "#92400E" },
                              bad:     { bg: "#FCEBEB", color: "#A32D2D" },
                              neutral: { bg: "#DBEAFE", color: "#1E40AF" },
                              muted:   { bg: T.borderSubtle, color: T.textSecondary },
                            }[tone] || { bg: T.borderSubtle, color: T.textSecondary };
                            return <span style={{...palette,padding:"1px 6px",borderRadius:8,fontSize:9,fontWeight:500,whiteSpace:"nowrap"}}>{label}</span>;
                          };
                          return (
                            <div style={{marginBottom:18}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                                <div style={{fontSize:13,fontWeight:600}}>Watchlist tech heatmap — all {scopedWL.length} items</div>
                                <div style={{fontSize:10,color:T.textSecondary}}>Sorted by setup strength</div>
                              </div>
                              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                                <div style={{maxHeight:340,overflowY:"auto"}}>
                                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                                    <thead style={{position:"sticky",top:0,zIndex:10,background:T.thBg}}>
                                      <tr>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"left",fontSize:9}}>Asset</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"left",fontSize:9}}>Trend</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>RSI</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>1M</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>3M</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>6M</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>52W</th>
                                        <th style={{...tTh(),background:T.thBg,padding:"7px 10px",textAlign:"right",fontSize:9}}>Vol</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {itemsWithTech.map(({wl, sig}, i) => {
                                        const isInTopPicks = (cachedReport.topPicks||[]).some(p => p.ticker === wl.ticker);
                                        const rowBg = isInTopPicks ? "rgba(124,58,237,0.04)" : (i%2===0 ? "transparent" : "rgba(0,0,0,0.015)");
                                        if (!sig || sig.dataPoints < 50) {
                                          return (
                                            <tr key={wl.id} style={{background:rowBg,opacity:0.55}}>
                                              <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`}}>
                                                <div style={{fontWeight:isInTopPicks?600:400}}>{wl.name}</div>
                                                <div style={{fontSize:9,color:T.textSecondary}}>{wl.ticker}</div>
                                              </td>
                                              <td colSpan={7} style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,fontSize:10,color:T.textLight,fontStyle:"italic"}}>
                                                Insufficient data ({sig?.dataPoints || 0} bars)
                                              </td>
                                            </tr>
                                          );
                                        }
                                        // Compute tones
                                        const rsi = sig.rsi;
                                        const rsiTone = rsi == null ? "muted" : rsi > 75 ? "bad" : rsi > 70 ? "warn" : rsi < 30 ? "warn" : "neutral";
                                        const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
                                        const tonePct = (v, t1=5, t2=-5) => v == null ? "muted" : v > t1 ? "good" : v < t2 ? "bad" : "neutral";
                                        return (
                                          <tr key={wl.id} style={{background:rowBg}}>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,minWidth:120}}>
                                              <div style={{display:"flex",alignItems:"center",gap:5}}>
                                                {isInTopPicks && <span style={{color:"#7C3AED",fontSize:9,fontWeight:700}}>★</span>}
                                                <div>
                                                  <div style={{fontWeight:isInTopPicks?600:500,fontSize:11}}>{wl.name}</div>
                                                  <div style={{fontSize:9,color:T.textSecondary}}>{wl.ticker}</div>
                                                </div>
                                              </div>
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`}}>
                                              <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                                                {sig.aboveSMA50 != null && compactChip(sig.aboveSMA50?"50↑":"50↓", sig.aboveSMA50?"good":"warn")}
                                                {sig.aboveSMA200 != null && compactChip(sig.aboveSMA200?"200↑":"200↓", sig.aboveSMA200?"good":"bad")}
                                                {sig.sma50AboveSma200 != null && compactChip(sig.sma50AboveSma200?"GC":"DC", sig.sma50AboveSma200?"good":"bad")}
                                              </div>
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right"}}>
                                              {rsi == null ? <span style={{color:T.textLight}}>—</span> : compactChip(rsi.toFixed(0), rsiTone)}
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right",color:sig.ret1m==null?T.textLight:sig.ret1m>=0?"#1D9E75":"#A32D2D",fontVariantNumeric:"tabular-nums"}}>
                                              {fmtPct(sig.ret1m)}
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right",color:sig.ret3m==null?T.textLight:sig.ret3m>=0?"#1D9E75":"#A32D2D",fontVariantNumeric:"tabular-nums"}}>
                                              {fmtPct(sig.ret3m)}
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right",color:sig.ret6m==null?T.textLight:sig.ret6m>=0?"#1D9E75":"#A32D2D",fontVariantNumeric:"tabular-nums"}}>
                                              {fmtPct(sig.ret6m)}
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right",fontVariantNumeric:"tabular-nums",color:T.textSecondary}}>
                                              {sig.distFrom52WHigh == null ? "—" : `${sig.distFrom52WHigh.toFixed(0)}%`}
                                            </td>
                                            <td style={{padding:"7px 10px",borderBottom:`1px solid ${T.cardBorder}`,textAlign:"right",fontVariantNumeric:"tabular-nums",color:T.textSecondary}}>
                                              {sig.volatility30d == null ? "—" : `${sig.volatility30d.toFixed(0)}%`}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                <div style={{padding:"8px 12px",borderTop:`1px solid ${T.cardBorder}`,background:T.thBg,fontSize:10,color:T.textSecondary,display:"flex",gap:14,flexWrap:"wrap"}}>
                                  <span>★ = AI top pick</span>
                                  <span>50↑/200↑ = above SMA</span>
                                  <span>GC/DC = golden/death cross</span>
                                  <span>52W = % from 52-week high</span>
                                  <span>Vol = 30d annualised</span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Action gaps */}
                        {cachedReport.actionGaps && cachedReport.actionGaps.length > 0 && (
                          <div style={{marginBottom:18}}>
                            <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>Action gaps</div>
                            <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                              {cachedReport.actionGaps.map((g,i) => (
                                <div key={i} style={{padding:"10px 14px",fontSize:12,borderBottom:i<cachedReport.actionGaps.length-1?`1px solid ${T.cardBorder}`:"none"}}>{g}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {/* Footer */}
                  <div style={{padding:"12px 24px",borderTop:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <div style={{fontSize:11,color:T.textSecondary}}>Generated by Claude · Not investment advice · Verify before acting</div>
                    {cachedReport && (
                      <button onClick={()=>{ close(); setDeployOpen(true); }} style={{background:"#4F46E5",border:"1px solid #4F46E5",borderRadius:6,color:"white",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>Open Deployment Planner →</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════
              DEPLOYMENT PLANNER — modal
              Computes per-item allocations from a deployment amount based
              on the chosen strategy. Currency conversion at render time.
          ══════════════════════════════════════════════ */}
          {deployOpen && (function(){
            // Convert any (value, ccy) to deployment base currency using fxRates.
            // fxRates store rates relative to USD (e.g., fxRates["INR"] = 83).
            // So: amount_in_ccy / fxRates[ccy] = amount_in_USD
            //     amount_in_USD * fxRates[base] = amount_in_base
            const toBase = (val, fromCcy) => {
              if (!val || !isFinite(val)) return 0;
              const fxFrom = fxRates[fromCcy] || FX_RATES_DEFAULT[fromCcy] || 1;
              const fxTo   = fxRates[deployCcy] || FX_RATES_DEFAULT[deployCcy] || 1;
              return (val / fxFrom) * fxTo;
            };
            const totalDeploy = parseFloat(deployAmount) || 0;
            // Normalise weights — if no weight set on an item, treat as 0.
            // For strategies that need weights, sum them up so we can scale.
            const items = scopedWL.map(w => ({
              ...w,
              priceBase: toBase(w.currentPrice || 0, w.currency || "USD"),
              targetBuyBase: toBase(w.targetBuy || 0, w.currency || "USD"),
              eligible: deployStrategy === "atbuy"
                ? (w.targetBuy > 0 && w.currentPrice <= w.targetBuy)
                : true,
            }));
            const eligibleItems = items.filter(i => i.eligible);
            // Compute allocation per row based on strategy
            const computeAlloc = () => {
              if (deployStrategy === "custom") {
                // Use overrides as-is, default to 0 for unset items
                return items.map(i => ({ ...i, allocation: parseFloat(deployCustom[i.id]) || 0 }));
              }
              if (deployStrategy === "equal") {
                const per = eligibleItems.length > 0 ? totalDeploy / eligibleItems.length : 0;
                return items.map(i => ({ ...i, allocation: i.eligible ? per : 0 }));
              }
              // weight-based (or atbuy which is weight-based on filtered set)
              const weightSum = eligibleItems.reduce((s, i) => s + (parseFloat(i.weight) || 0), 0);
              if (weightSum > 0) {
                return items.map(i => {
                  if (!i.eligible) return { ...i, allocation: 0 };
                  const w = parseFloat(i.weight) || 0;
                  return { ...i, allocation: totalDeploy * (w / weightSum) };
                });
              }
              // No weights set → fall back to equal-weight on eligible
              const per = eligibleItems.length > 0 ? totalDeploy / eligibleItems.length : 0;
              return items.map(i => ({ ...i, allocation: i.eligible ? per : 0 }));
            };
            const allocated = computeAlloc();
            // Compute shares — round down if fractional disabled (broker-friendly)
            const withShares = allocated.map(a => {
              if (!a.priceBase || a.priceBase <= 0) return { ...a, shares: 0, deployedActual: 0 };
              let shares = a.allocation / a.priceBase;
              if (!deployFractional) shares = Math.floor(shares);
              const deployedActual = shares * a.priceBase;
              return { ...a, shares, deployedActual };
            });
            const totalDeployed = withShares.reduce((s, a) => s + a.deployedActual, 0);
            const totalCash = totalDeploy - totalDeployed;
            const namesCount = withShares.filter(a => a.shares > 0).length;
            // Avg discount: weighted-average of (currentPrice - targetBuy) / targetBuy
            // across deployed names where targetBuy > 0
            let discWeightSum = 0, discNumerator = 0;
            withShares.forEach(a => {
              if (a.shares > 0 && a.targetBuyBase > 0) {
                const disc = (a.priceBase - a.targetBuyBase) / a.targetBuyBase;
                discWeightSum += a.deployedActual;
                discNumerator += a.deployedActual * disc;
              }
            });
            const avgDiscount = discWeightSum > 0 ? (discNumerator / discWeightSum) * 100 : null;
            const fmtBase = (v) => `${deployCcy} ${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            const closeModal = () => {
              setDeployOpen(false);
              setDeployCustom({});
            };
            // Status badge per row
            const statusFor = (a) => {
              if (a.shares <= 0) return { label: "Skip", bg: "#F1EFE8", color: "#5F5E5A" };
              if (a.targetBuyBase > 0 && a.priceBase <= a.targetBuyBase) {
                return { label: "Buy", bg: "#EAF3DE", color: "#3B6D11" };
              }
              if (a.targetBuyBase > 0 && a.priceBase > a.targetBuyBase) {
                return { label: "Above", bg: "#FAEEDA", color: "#854F0B" };
              }
              return { label: "Buy", bg: "#EAF3DE", color: "#3B6D11" };
            };
            // Export CSV
            const exportCSV = () => {
              const headers = ["Ticker", "Name", "Currency", "Price", "Weight%", "Allocation", "Shares", "Status"];
              const rows = withShares.filter(a => a.shares > 0).map(a => [
                a.ticker, a.name, a.currency || "USD",
                (a.currentPrice || 0).toFixed(4),
                (parseFloat(a.weight) || 0).toFixed(2),
                a.deployedActual.toFixed(2),
                a.shares.toFixed(deployFractional ? 4 : 0),
                statusFor(a).label,
              ]);
              const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `deploy-plan-${deployCcy}-${totalDeploy}-${new Date().toISOString().slice(0,10)}.csv`;
              link.click();
              URL.revokeObjectURL(url);
              showToast("✓ Plan downloaded", "#1D9E75", 2000);
            };
            // Send to client — open mailto with formatted plan
            const sendToClient = () => {
              const lines = withShares.filter(a => a.shares > 0).map(a => {
                const sharesStr = deployFractional ? a.shares.toFixed(2) : Math.floor(a.shares).toString();
                return `• ${a.ticker} (${a.name}): ${sharesStr} shares × ${a.currency} ${(a.currentPrice||0).toFixed(2)} = ${fmtBase(a.deployedActual)}`;
              }).join("\n");
              const subject = encodeURIComponent(`Deployment Plan — ${fmtBase(totalDeploy)} (${deployStrategy})`);
              const body = encodeURIComponent(
                `Proposed deployment plan:\n\n` +
                `Total: ${fmtBase(totalDeploy)}\n` +
                `Strategy: ${deployStrategy === "weight" ? "By target weight" : deployStrategy === "atbuy" ? "Only at/below buy" : deployStrategy === "equal" ? "Equal weight" : "Custom"}\n` +
                `Names: ${namesCount}\n` +
                `Cash unallocated: ${fmtBase(totalCash)}\n\n` +
                `Allocations:\n${lines}\n\n` +
                `— Generated by Forthius Finalytics`
              );
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            };
            return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={closeModal}>
                <div onClick={e=>e.stopPropagation()} style={{background:T.cardBg,borderRadius:12,maxWidth:980,width:"100%",maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.3)"}}>
                  {/* Header */}
                  <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:17,fontWeight:700}}>💰 Deploy Capital</div>
                      <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                        Allocate from watchlist
                        {selectedClients.size === 1 && ` · ${clients.find(c=>String(c.id)===String([...selectedClients][0]))?.name||""}`}
                        {selectedClients.size > 1 && ` · ${selectedClients.size} clients`}
                      </div>
                    </div>
                    <button onClick={closeModal} style={{background:"transparent",border:"none",fontSize:22,color:T.textSecondary,cursor:"pointer",padding:"0 8px"}}>×</button>
                  </div>
                  {/* Body */}
                  <div style={{padding:"18px 24px",overflowY:"auto",flex:1}}>
                    {/* Amount + currency + strategy chips */}
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Amount to deploy</div>
                        <div style={{display:"flex",gap:6}}>
                          <select value={deployCcy} onChange={e=>setDeployCcy(e.target.value)} style={{...inp(),width:90}}>
                            {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                          </select>
                          <input type="number" value={deployAmount} onChange={e=>setDeployAmount(e.target.value)} placeholder="10000" style={{...inp(),flex:1,fontSize:18,fontWeight:600}}/>
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Strategy</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {[
                            ["weight","By target weight"],
                            ["atbuy","Only at/below buy"],
                            ["equal","Equal weight"],
                            ["custom","Custom"],
                          ].map(([k,label])=>(
                            <button key={k} onClick={()=>setDeployStrategy(k)} style={{
                              background: deployStrategy===k ? "#4F46E5" : "transparent",
                              color: deployStrategy===k ? "white" : T.textPrimary,
                              border: `1px solid ${deployStrategy===k ? "#4F46E5" : T.cardBorder}`,
                              borderRadius: 16,
                              padding: "5px 12px",
                              fontSize: 12,
                              cursor: "pointer",
                              fontWeight: deployStrategy===k ? 600 : 400,
                            }}>{label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Fractional toggle */}
                    <label style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:T.textSecondary,marginBottom:14,cursor:"pointer"}}>
                      <input type="checkbox" checked={deployFractional} onChange={e=>setDeployFractional(e.target.checked)} style={{accentColor:"#4F46E5"}}/>
                      Allow fractional shares (uncheck to round down — broker-friendly)
                    </label>
                    {/* KPI tiles */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
                      <div style={{...tCard(),padding:"12px 14px"}}>
                        <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>To deploy</div>
                        <div style={{fontSize:18,fontWeight:700,marginTop:3}}>{fmtBase(totalDeployed)}</div>
                      </div>
                      <div style={{...tCard(),padding:"12px 14px"}}>
                        <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Cash held</div>
                        <div style={{fontSize:18,fontWeight:700,marginTop:3,color: totalCash > 0.01 * totalDeploy ? "#D97706" : T.textPrimary}}>{fmtBase(totalCash)}</div>
                      </div>
                      <div style={{...tCard(),padding:"12px 14px"}}>
                        <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Names</div>
                        <div style={{fontSize:18,fontWeight:700,marginTop:3}}>{namesCount} of {scopedWL.length}</div>
                      </div>
                      <div style={{...tCard(),padding:"12px 14px"}}>
                        <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>Avg discount</div>
                        <div style={{fontSize:18,fontWeight:700,marginTop:3,color: avgDiscount === null ? T.textMuted : (avgDiscount < 0 ? "#1D9E75" : "#D97706")}}>
                          {avgDiscount === null ? "—" : `${avgDiscount >= 0 ? "+" : ""}${avgDiscount.toFixed(1)}%`}
                        </div>
                      </div>
                    </div>
                    {/* Allocation table */}
                    <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                      <div style={{overflowX:"auto",overflowY:"auto",maxHeight:"40vh"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",minWidth:680}}>
                          <thead style={{position:"sticky",top:0,zIndex:10,background:T.thBg}}>
                            <tr>
                              {["Asset","Wt %","Allocation","Price","Shares","Status"].map(h=>(
                                <th key={h} style={{...tTh(),background:T.thBg,position:"sticky",top:0,textAlign:h==="Asset"?"left":"right",padding:"10px 12px"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {withShares.length === 0 && (
                              <tr><td colSpan={6} style={{textAlign:"center",padding:30,color:T.textLight,fontSize:12}}>No watchlist items in scope.</td></tr>
                            )}
                            {withShares.map((a,i)=>{
                              const status = statusFor(a);
                              return (
                                <tr key={a.id} style={{background:i%2===0?"transparent":"rgba(0,0,0,0.015)",opacity:a.shares <= 0 ? 0.5 : 1}}>
                                  <td style={{...tTd(),padding:"10px 12px"}}>
                                    <div style={{fontWeight:600,fontSize:12}}>{a.name}</div>
                                    <div style={{fontSize:10,color:T.textSecondary}}>{a.ticker} · {a.currency} {(a.currentPrice||0).toFixed(2)}</div>
                                  </td>
                                  <td style={{...tTd(),padding:"10px 12px",textAlign:"right",fontSize:12}}>{(parseFloat(a.weight)||0).toFixed(1)}%</td>
                                  <td style={{...tTd(),padding:"10px 12px",textAlign:"right"}}>
                                    {deployStrategy === "custom" ? (
                                      <input type="number" value={deployCustom[a.id] ?? ""} placeholder="0"
                                        onChange={e=>setDeployCustom(p=>({...p,[a.id]:e.target.value}))}
                                        style={{...inp(),width:90,textAlign:"right",fontSize:12,padding:"4px 8px"}}/>
                                    ) : (
                                      <span style={{fontWeight:600,fontSize:12}}>{fmtBase(a.deployedActual)}</span>
                                    )}
                                  </td>
                                  <td style={{...tTd(),padding:"10px 12px",textAlign:"right",fontSize:12,color:T.textSecondary}}>
                                    {a.priceBase ? fmtBase(a.priceBase) : "—"}
                                  </td>
                                  <td style={{...tTd(),padding:"10px 12px",textAlign:"right",fontSize:12,fontWeight:600}}>
                                    {a.shares > 0 ? (deployFractional ? a.shares.toFixed(2) : Math.floor(a.shares).toString()) : "—"}
                                  </td>
                                  <td style={{...tTd(),padding:"10px 12px",textAlign:"right"}}>
                                    <span style={{background:status.bg,color:status.color,padding:"2px 8px",borderRadius:12,fontSize:10,fontWeight:600}}>{status.label}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Helper text */}
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:10,lineHeight:1.5}}>
                      {deployStrategy === "weight" && "Allocates proportionally to each item's target weight %. Items without weights fall back to equal-weight."}
                      {deployStrategy === "atbuy" && "Only deploys to items where current price ≤ buy-below level. Cash held back if no items qualify."}
                      {deployStrategy === "equal" && "Splits the amount evenly across all watchlist items in scope."}
                      {deployStrategy === "custom" && "Enter allocation amounts manually. Cash held = total minus sum of overrides."}
                    </div>
                  </div>
                  {/* Footer */}
                  <div style={{padding:"14px 24px",borderTop:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <div style={{fontSize:11,color:T.textSecondary}}>Plan only — no orders are placed</div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={exportCSV} style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,color:T.textPrimary,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>Export CSV</button>
                      <button onClick={sendToClient} style={{background:"#007878",border:"1px solid #007878",borderRadius:6,color:"white",padding:"7px 16px",cursor:"pointer",fontSize:12,fontWeight:600}}>Send to client</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in watchlist tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══════════════════════════════════════════════
            BACKTESTING TAB
        ══════════════════════════════════════════════ */}
        {tab==="scenarios" && scenariosSubTab==="backtesting" && (function(){ try {
          const today = new Date();
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see scenarios analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}

          // ── Apply family/client scope filter to btAssets ──
          const scopedBtAssets = btAssets.filter(a => {
            if (!isAdmin) return a.clientId === loggedInClientId || !a.clientId;
            if (viewAsClientId) return a.clientId === viewAsClientId;
            if (selectedClients.size > 0) return selectedClients.has(a.clientId); // strict — no unassigned
            if (selectedFamily !== "all") {
              const cli = clients.find(c => String(c.id) === String(a.clientId));
              return cli && String(cli.familyId) === String(selectedFamily);
            }
            return true; // "All" — show everything including unassigned
          });

          // Value-weighted TWRR across all bt assets (active + exited)
          const totalAmtAll = scopedBtAssets.reduce((s,a)=>s+(parseFloat(a.amount)||0),0);
          const assetsCalc = scopedBtAssets.map(a=>{
            const amt       = parseFloat(a.amount)||0;
            const wt        = totalAmtAll>0 ? amt/totalAmtAll : 1/Math.max(1,scopedBtAssets.length);
            const endDate   = a.status==="exited"&&a.exitedOn ? new Date(a.exitedOn) : today;
            const startDate = new Date(a.addedOn);
            const daysHeld  = Math.max(1,(endDate-startDate)/(1000*60*60*24));
            const exitP     = a.status==="exited" && a.exitPrice ? parseFloat(a.exitPrice) : parseFloat(a.currentPrice);
            const twrr      = a.entryPrice>0 ? ((exitP/a.entryPrice)-1)*100 : 0;
            const contrib   = twrr * wt;
            return {...a, wt, daysHeld:Math.round(daysHeld), exitP, twrr, contrib};
          });

          const portTwrr = scopedBtAssets.length>0 ? assetsCalc.reduce((s,a)=>s+a.contrib,0) : null;
          const activeAssets = scopedBtAssets.filter(a=>a.status!=="exited");
          const exitedAssets = scopedBtAssets.filter(a=>a.status==="exited");

          const earliestDate = scopedBtAssets.length>0
            ? new Date(Math.min(...scopedBtAssets.map(a=>new Date(a.addedOn).getTime()))) : today;
          const totalDays = Math.max(1,(today-earliestDate)/(1000*60*60*24));

          // Benchmark TWRR mirroring asset weights + date slices
          const benchRows = BENCHMARKS_DEF.map(b=>{
            const bm = BENCHMARK_FALLBACK[b.id];
            if (!bm?.oneY||scopedBtAssets.length===0) return {...b,twrr:null,perAsset:[]};
            const annRate = bm.oneY/100;
            const perAsset = assetsCalc.map(a=>{
              const yf = a.daysHeld/365;
              const bmRet = (Math.pow(1+annRate,yf)-1)*100;
              return {...a, bmRet, weightedBm: bmRet*a.wt};
            });
            return {...b, twrr:perAsset.reduce((s,x)=>s+x.weightedBm,0), perAsset};
          });
          const allTwrrs = [Math.abs(portTwrr||0),...benchRows.filter(b=>b.twrr!==null).map(b=>Math.abs(b.twrr))];
          const scaleTwrr = Math.max(...allTwrrs,1);
          const selBench  = benchRows.find(b=>b.id===btBenchSel)||benchRows[0];

          // Exit modal
          const exitingAsset = btExitId ? scopedBtAssets.find(a=>a.id===btExitId) : null;

          return <React.Fragment>

          {/* ── Header ── */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:17,fontWeight:700}}>🧪 Backtesting</div>
              <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                Build a hypothetical portfolio with entry prices, amounts and dates. Track TWRR against benchmarks with identical weights and time slices.
                {activeAssets.some(a=>a.currentPrice>0) && <span style={{marginLeft:8,fontSize:11,color:"#1D9E75",fontWeight:600}}>🟢 Live prices · {lastUpd.toLocaleTimeString()}</span>}
              </div>
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
              <button onClick={()=>refresh(true)} disabled={refreshing} style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:7,color:T.thText,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                <span style={{display:"inline-block",animation:refreshing?"spin 1s linear infinite":"none"}}>↻</span> Refresh
              </button>
              {portTwrr!==null&&(
                <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(59,130,246,0.07)",border:`1px solid ${T.cardBorder}`,borderRadius:20,padding:"7px 18px"}}>
                  <span style={{fontSize:11,color:T.textSecondary}}>Portfolio TWRR</span>
                  <span style={{fontSize:16,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:portTwrr>=0?"#1D9E75":"#A32D2D"}}>
                    {portTwrr>=0?"+":""}{portTwrr.toFixed(2)}%
                  </span>
                </div>
              )}
              <button onClick={()=>setBtShowBench(p=>!p)}
                style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${btShowBench?"#2563EB":T.border}`,background:btShowBench?"rgba(37,99,235,0.1)":"transparent",color:btShowBench?"#2563EB":T.textSecondary,cursor:"pointer",fontSize:12,fontWeight:600}}>
                {btShowBench?"📊 Hide Benchmarks":"📊 vs Benchmarks"}
              </button>
              <button onClick={()=>{
                setBtAddOpen(p=>!p);
                // Pre-select currently filtered client(s) so admin doesn't have to
                // re-pick the obvious target. Empty when nothing's filtered.
                if (isAdmin && !btAddOpen && selectedClients.size > 0) {
                  setBtAddClients(new Set([...selectedClients].map(String)));
                }
              }}
                style={{background:"#007878",border:"none",borderRadius:8,color:"white",padding:"8px 18px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                + Add Asset
              </button>
            </div>
          </div>

          {/* ── Add form ── */}
          {btAddOpen && (
            <div style={{...tCard(),marginBottom:16,borderColor:"#1E40AF"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:14,color:"#2563EB"}}>New Backtesting Asset</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:12}}>
                <div style={{position:"relative"}}><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Ticker</div>
                  <input style={inp()} value={btTickerQuery||btNew.ticker}
                    onChange={e=>{ setBtTickerQuery(e.target.value); setBtNew(p=>({...p,ticker:e.target.value,name:""})); }}
                    onFocus={()=>{ if((btTickerQuery||btNew.ticker).length>=3) setBtTickerOpen(true); }}
                    onBlur={()=>setTimeout(()=>setBtTickerOpen(false),200)}
                    placeholder="Type ticker e.g. AAPL"/>
                  {btTickerOpen && btTickerResults.length>0 && (
                    <div style={{position:"absolute",zIndex:9999,top:"100%",left:0,right:0,background:"#fff",border:"1px solid #78716C",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",maxHeight:240,overflowY:"auto",marginTop:2}}>
                      {btTickerLoading && <div style={{padding:"8px 12px",fontSize:12,color:T.textMuted}}>Searching…</div>}
                      {btTickerResults.map((r,i)=>(
                        <div key={r.symbol+i} onMouseDown={()=>{
                          setBtNew(p=>({...p,ticker:r.symbol,name:r.name||p.name,exchange:r.exchange||p.exchange,currency:r.currency||p.currency}));
                          setBtTickerQuery("");setBtTickerOpen(false);
                        }} style={{padding:"8px 12px",cursor:"pointer",fontSize:12,borderBottom:"1px solid #F5EBE0",background:i%2===0?"transparent":T.tableStripe}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":T.tableStripe}>
                          <span style={{fontWeight:600,color:T.accent}}>{r.symbol}</span>
                          <span style={{color:T.textSecondary,marginLeft:8}}>{r.name}</span>
                          {r.exchange&&<span style={{color:T.textMuted,marginLeft:6,fontSize:11}}>{r.exchange}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Company Name</div>
                  <input style={inp()} value={btNew.name} onChange={e=>setBtNew(p=>({...p,name:e.target.value}))} placeholder="e.g. Apple Inc"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Entry Price</div>
                  <input style={inp()} type="number" value={btNew.entryPrice} onChange={e=>setBtNew(p=>({...p,entryPrice:e.target.value}))} placeholder="e.g. 150"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Amount (in currency)</div>
                  <input style={inp()} type="number" value={btNew.amount} onChange={e=>setBtNew(p=>({...p,amount:e.target.value}))} placeholder="e.g. 50000"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Exchange</div>
                  <select style={inp()} value={btNew.exchange} onChange={e=>setBtNew(p=>({...p,exchange:e.target.value}))}>
                    {["NASDAQ","NYSE","AMEX","NSE","BSE","LSE","XETRA","EPA","AMS","SIX","MIL","BME","OSL","STO","HEL","CPH","TSE","HKEX","SSE","SZSE","KRX","SGX","ASX","TWSE","SET","IDX","BURSA","TSX","B3","JSE","TADAWUL","Crypto"].map(x=><option key={x}>{x}</option>)}
                  </select></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Currency</div>
                  <select style={inp()} value={btNew.currency} onChange={e=>setBtNew(p=>({...p,currency:e.target.value}))}>
                    {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Date Added</div>
                  <input type="date" style={inp()} value={btNew.addedOn} onChange={e=>setBtNew(p=>({...p,addedOn:e.target.value}))} max={new Date().toISOString().slice(0,10)}/></div>
                {isAdmin && (
                  <div style={{gridColumn:isMobile?"1":"span 2"}}>
                    <div style={{padding:"10px 12px",background:"rgba(99,102,241,0.06)",borderRadius:8,border:"1px solid rgba(99,102,241,0.18)"}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                        <span><strong style={{color:"#4F46E5"}}>Add to which client(s)?</strong> {btAddClients.size===0 ? "— none selected (will save as Unassigned)" : `— ${btAddClients.size} selected`}</span>
                        <span style={{display:"flex",gap:6}}>
                          <button onClick={()=>setBtAddClients(new Set(clients.map(c=>String(c.id))))} style={{background:"transparent",border:"1px solid rgba(99,102,241,0.4)",borderRadius:4,color:"#4F46E5",padding:"2px 8px",cursor:"pointer",fontSize:10}}>Select all</button>
                          <button onClick={()=>setBtAddClients(new Set())} style={{background:"transparent",border:"1px solid rgba(99,102,241,0.4)",borderRadius:4,color:"#4F46E5",padding:"2px 8px",cursor:"pointer",fontSize:10}}>Clear</button>
                        </span>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:120,overflowY:"auto"}}>
                        {clients.map(c => {
                          const sel = btAddClients.has(String(c.id));
                          const fam = families.find(f => String(f.id) === String(c.familyId));
                          return (
                            <button key={c.id} onClick={()=>{
                              setBtAddClients(p => {
                                const n = new Set(p);
                                if (n.has(String(c.id))) n.delete(String(c.id));
                                else n.add(String(c.id));
                                return n;
                              });
                            }} style={{
                              background: sel ? "#4F46E5" : "transparent",
                              color: sel ? "white" : T.textPrimary,
                              border: `1px solid ${sel ? "#4F46E5" : T.cardBorder}`,
                              borderRadius: 16,
                              padding: "4px 10px",
                              fontSize: 11,
                              cursor: "pointer",
                              fontWeight: sel ? 600 : 400,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}>
                              {sel && <span>✓</span>}
                              {c.name}
                              {fam && <span style={{opacity:0.6,fontSize:10}}>· {fam.name}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Notes</div>
                  <input style={inp()} value={btNew.notes} onChange={e=>setBtNew(p=>({...p,notes:e.target.value}))} placeholder="Thesis…"/></div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={async ()=>{
                  if(!btNew.ticker||!btNew.name||!btNew.entryPrice) return;
                  // Determine target client list (Option A: duplicate rows)
                  // - admin with multi-select chips → use those (one row per client)
                  // - admin with no chips selected → fall back to "" (unassigned, single row)
                  // - non-admin → always own clientId
                  let targetClientIds;
                  if (!isAdmin) {
                    targetClientIds = [loggedInClientId || ""];
                  } else if (btAddClients.size > 0) {
                    targetClientIds = [...btAddClients];
                  } else {
                    targetClientIds = [""]; // single unassigned row
                  }
                  const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                  const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                  let success = 0, failed = 0;
                  const newAssets = [];
                  for (const ownerClientId of targetClientIds) {
                    const newAsset = {
                      ...btNew, id:"bt"+uid(),
                      entryPrice: parseFloat(btNew.entryPrice)||0,
                      currentPrice: parseFloat(btNew.entryPrice)||0,
                      amount: parseFloat(btNew.amount)||0,
                      exitPrice: null, exitedOn: null, status:"active", allocPct:0,
                      clientId: ownerClientId
                    };
                    const dbRow = {
                      id: newAsset.id,
                      client_id: ownerClientId || null,
                      ticker: newAsset.ticker,
                      name: newAsset.name,
                      exchange: newAsset.exchange,
                      currency: newAsset.currency,
                      entry_price: newAsset.entryPrice,
                      current_price: newAsset.currentPrice,
                      amount: newAsset.amount,
                      added_on: newAsset.addedOn,
                      status: newAsset.status,
                      notes: newAsset.notes || "",
                      alloc_pct: newAsset.allocPct || 0,
                    };
                    try {
                      const res = await fetch(`${SUPABASE_URL}/rest/v1/bt_assets`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'apikey': ANON_KEY,
                          'Authorization': `Bearer ${ANON_KEY}`,
                          'Prefer': 'return=representation',
                        },
                        body: JSON.stringify(dbRow),
                      });
                      if (!res.ok) {
                        const errText = await res.text();
                        throw new Error(`HTTP ${res.status}: ${errText.slice(0,200)}`);
                      }
                      const body = await res.json();
                      if (!Array.isArray(body) || body.length === 0) {
                        throw new Error("Empty response — RLS may have blocked");
                      }
                      newAssets.push(newAsset);
                      success++;
                    } catch (err) {
                      console.error(`[Supabase] addBtAsset failed for client ${ownerClientId}:`, err.message);
                      failed++;
                    }
                  }
                  if (success > 0) {
                    setBtAssets(p=>btRebalance([...p, ...newAssets]));
                    setBtAddOpen(false);
                    setBtNew({ticker:"",name:"",exchange:"NASDAQ",currency:"USD",clientId:"",entryPrice:"",amount:"",addedOn:new Date().toISOString().slice(0,10),notes:""});
                    setBtAddClients(new Set());
                    if (failed === 0) {
                      showToast(`✓ ${btNew.name} added to ${success} backtest${success>1?"s":""}`, "#1D9E75");
                    } else {
                      showToast(`Added to ${success}, ${failed} failed — see console`, "#D97706", 4000);
                    }
                  } else {
                    showToast(`⚠ Save failed for all ${failed} client(s) — check console`, "#A32D2D", 5000);
                  }
                }} style={{background:"#1E40AF",border:"none",borderRadius:6,color:"white",padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>Add</button>
                <button onClick={()=>setBtAddOpen(false)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 18px",cursor:"pointer",fontSize:13}}>Cancel</button>
              </div>
            </div>
          )}

          {/* ── Exit modal ── */}
          {exitingAsset && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setBtExitId(null)}>
              <div style={{...tCard(),width:420,borderColor:"#A32D2D"}} onClick={e=>e.stopPropagation()}>
                <div style={{fontSize:14,fontWeight:700,color:"#A32D2D",marginBottom:4}}>📤 Exit Position</div>
                <div style={{fontSize:12,color:T.textSecondary,marginBottom:16}}>
                  Record exit for <strong style={{color:T.textPrimary}}>{exitingAsset.ticker} — {exitingAsset.name}</strong>.
                  The asset stays in history for TWRR calculations.
                </div>
                <div style={{background:"rgba(220,38,38,0.06)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
                    <div><span style={{color:T.textSecondary}}>Entry:</span> <strong style={{fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>{exitingAsset.entryPrice?.toLocaleString("en-US",{minimumFractionDigits:2})}</strong></div>
                    <div><span style={{color:T.textSecondary}}>Entry Date:</span> <strong style={{color:T.textSecondary}}>{exitingAsset.addedOn}</strong></div>
                    <div><span style={{color:T.textSecondary}}>Amount:</span> <strong style={{fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>{(exitingAsset.amount||0).toLocaleString("en-US")}</strong></div>
                    <div><span style={{color:T.textSecondary}}>Currency:</span> <strong style={{color:T.textSecondary}}>{exitingAsset.currency}</strong></div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:6}}>Exit Price</div>
                    <input type="number" style={inp()} value={btExitPrice}
                      onChange={e=>setBtExitPrice(e.target.value)} placeholder="Exit price…"/>
                    {btExitPrice && exitingAsset.entryPrice && (
                      <div style={{fontSize:11,marginTop:5,color:parseFloat(btExitPrice)>=exitingAsset.entryPrice?"#1D9E75":"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600}}>
                        {((parseFloat(btExitPrice)/exitingAsset.entryPrice-1)*100).toFixed(2)}% return
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:6}}>Exit Date</div>
                    <input type="date" style={inp()} value={btExitDate}
                      onChange={e=>setBtExitDate(e.target.value)}
                      min={exitingAsset.addedOn} max={new Date().toISOString().slice(0,10)}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={async ()=>{
                    if (!btExitPrice) return;
                    const exitFields = {status:"exited",exitPrice:parseFloat(btExitPrice),exitedOn:btExitDate,currentPrice:parseFloat(btExitPrice)};
                    // Direct REST PATCH (wrapper unreliable for new schema)
                    try {
                      const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                      const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                      const dbFields = {
                        status: exitFields.status,
                        exit_price: exitFields.exitPrice,
                        exited_on: exitFields.exitedOn,
                        current_price: exitFields.currentPrice,
                      };
                      const res = await fetch(`${SUPABASE_URL}/rest/v1/bt_assets?id=eq.${encodeURIComponent(btExitId)}`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'apikey': ANON_KEY,
                          'Authorization': `Bearer ${ANON_KEY}`,
                          'Prefer': 'return=representation',
                        },
                        body: JSON.stringify(dbFields),
                      });
                      if (!res.ok) {
                        const errText = await res.text();
                        throw new Error(`HTTP ${res.status}: ${errText.slice(0,200)}`);
                      }
                      const body = await res.json();
                      if (!Array.isArray(body) || body.length === 0) {
                        throw new Error("Empty response — RLS may have blocked");
                      }
                      setBtAssets(p=>btRebalance(p.map(a=>a.id===btExitId ? {...a,...exitFields} : a)));
                      setBtExitId(null); setBtExitPrice(""); setBtExitDate(new Date().toISOString().slice(0,10));
                      showToast("✓ Position exited", "#1D9E75", 1500);
                    } catch (err) {
                      console.error('[Supabase] bt exit failed:', err.message);
                      showToast(`⚠ Save failed: ${err.message}`, "#A32D2D", 4000);
                    }
                  }} style={{background:"rgba(239,68,68,0.15)",border:"1px solid #A32D2D",borderRadius:6,color:"#A32D2D",padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>Confirm Exit</button>
                  <button onClick={()=>setBtExitId(null)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 18px",cursor:"pointer",fontSize:13}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {scopedBtAssets.length===0 && (
            <div style={{...tCard(),textAlign:"center",padding:"52px 24px"}}>
              <div style={{fontSize:48,marginBottom:16}}>🧪</div>
              <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>
                Build a Hypothetical Portfolio
              </div>
              <div style={{fontSize:13,color:T.textSecondary,marginBottom:24,
                maxWidth:400,margin:"0 auto 24px",lineHeight:1.6}}>
                Add stocks, ETFs or any asset with an entry price, amount and date.
                The backtester computes TWRR for each holding and compares against
                NIFTY50, SENSEX, S&P 500 and other benchmarks — using identical
                weights and time slices for a fair comparison.
              </div>
              <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",
                marginBottom:24}}>
                {[
                  {icon:"📈",label:"Track TWRR per asset"},
                  {icon:"📊",label:"Beat-the-benchmark score"},
                  {icon:"📤",label:"Record entries & exits"},
                  {icon:"⚖️",label:"Risk-adjusted returns"},
                ].map(f=>(
                  <div key={f.label} style={{
                    background:T.borderSubtle,borderRadius:8,padding:"10px 16px",
                    display:"flex",alignItems:"center",gap:8,fontSize:12,color:T.textSecondary}}>
                    <span>{f.icon}</span><span>{f.label}</span>
                  </div>
                ))}
              </div>
              <button onClick={()=>setBtAddOpen(true)}
                style={{background:"#007878",border:"none",
                  borderRadius:8,color:"white",padding:"10px 28px",cursor:"pointer",
                  fontSize:14,fontWeight:600,boxShadow:"0 4px 16px rgba(0,121,193,0.3)"}}>
                + Add Your First Asset
              </button>
            </div>
          )}

          {scopedBtAssets.length>0 && <React.Fragment>

          {/* ── Summary KPIs ── */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",
            gap:isMobile?10:14,marginBottom:16}}>
            {[
              {
                label:"Portfolio TWRR",
                value: portTwrr!==null ? `${portTwrr>=0?"+":""}${portTwrr.toFixed(2)}%` : "—",
                sub: `${scopedBtAssets.length} asset${scopedBtAssets.length!==1?"s":""} · ${activeAssets.length} active`,
                col: portTwrr!==null ? (portTwrr>=0?"#1D9E75":"#A32D2D") : T.textMuted,
              },
              {
                label:"Total Deployed",
                value: fmt(convert(scopedBtAssets.reduce((s,a)=>s+toUSD((parseFloat(a.amount)||0),a.currency),0),dc),dc),
                sub: `Across ${new Set(scopedBtAssets.map(a=>a.currency)).size} currencies`,
                col:"#2563EB",
              },
              {
                label:"Best Position",
                value: assetsCalc.length ? `${(assetsCalc.slice().sort((a,b)=>b.twrr-a.twrr)[0]?.twrr||0).toFixed(1)}%` : "—",
                sub: assetsCalc.length ? assetsCalc.slice().sort((a,b)=>b.twrr-a.twrr)[0]?.ticker : "—",
                col:"#059669",
              },
              {
                label:"Worst Position",
                value: assetsCalc.length ? `${(assetsCalc.slice().sort((a,b)=>a.twrr-b.twrr)[0]?.twrr||0).toFixed(1)}%` : "—",
                sub: assetsCalc.length ? assetsCalc.slice().sort((a,b)=>a.twrr-b.twrr)[0]?.ticker : "—",
                col: assetsCalc.length&&assetsCalc.slice().sort((a,b)=>a.twrr-b.twrr)[0]?.twrr<0 ? "#A32D2D" : "#D97706",
              },
            ].map((k,i)=>(
              <div key={i} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                borderRadius:10,padding:"14px 16px"}}>
                <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",
                  letterSpacing:"0.06em",marginBottom:4}}>{k.label}</div>
                <div style={{fontSize:isMobile?18:22,fontWeight:500,
                  fontFamily:"'Inter', system-ui, sans-serif",color:k.col,marginBottom:2}}>{k.value}</div>
                <div style={{fontSize:10,color:T.textMuted}}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* ── TWRR bar chart — visual comparison ── */}
          {assetsCalc.length > 0 && (
            <div style={{...tCard(),marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,marginBottom:14,color:T.textPrimary}}>
                Return by Asset vs {selBench?.label||"Benchmark"}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[...assetsCalc].sort((a,b)=>b.twrr-a.twrr).map(a=>{
                  const bmData = selBench?.perAsset?.find(x=>x.id===a.id);
                  const bmRet  = bmData?.bmRet ?? null;
                  const beats  = bmRet!==null && a.twrr > bmRet;
                  const maxAbs = Math.max(Math.abs(a.twrr), bmRet!==null?Math.abs(bmRet):0, 1);
                  return (
                    <div key={a.id}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:4}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:12,fontWeight:600,color:T.textPrimary,
                            fontFamily:"'Inter', system-ui, sans-serif"}}>{a.ticker}</span>
                          <span style={{fontSize:10,color:T.textMuted}}>{a.name.split(" ").slice(0,3).join(" ")}</span>
                          {a.status==="exited" && (
                            <span style={{fontSize:9,background:"rgba(239,68,68,0.1)",
                              color:"#A32D2D",padding:"1px 5px",borderRadius:3,fontWeight:600}}>EXITED</span>
                          )}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:12,fontWeight:700,
                            color:a.twrr>=0?"#1D9E75":"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {a.twrr>=0?"+":""}{a.twrr.toFixed(2)}%
                          </span>
                          {bmRet!==null && (
                            <span style={{fontSize:10,color:beats?"#1D9E75":"#A32D2D",fontWeight:600}}>
                              {beats?"▲":"▼"} vs {bmRet.toFixed(1)}%
                            </span>
                          )}
                          <span style={{fontSize:10,color:T.textMuted}}>{a.daysHeld}d</span>
                        </div>
                      </div>
                      <div style={{height:6,background:T.borderSubtle,borderRadius:3,
                        position:"relative",overflow:"hidden"}}>
                        {/* Asset bar */}
                        <div style={{
                          position:"absolute",left:0,top:0,height:"100%",borderRadius:3,
                          width:`${Math.min(100,Math.abs(a.twrr)/maxAbs*100)}%`,
                          background:a.twrr>=0?"#1D9E75":"#A32D2D",
                          transition:"width 0.6s ease",opacity:0.85,
                        }}/>
                        {/* Benchmark marker */}
                        {bmRet!==null && (
                          <div style={{
                            position:"absolute",top:0,bottom:0,width:2,borderRadius:1,
                            left:`${Math.min(100,Math.abs(bmRet)/maxAbs*100)}%`,
                            background:"#2563EB",opacity:0.7,
                          }}/>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selBench && (
                <div style={{marginTop:10,fontSize:10,color:T.textMuted,display:"flex",
                  alignItems:"center",gap:8}}>
                  <span style={{display:"inline-block",width:10,height:2,
                    background:"#2563EB",borderRadius:1,flexShrink:0}}/>
                  Blue line = {selBench.label} return for same holding period
                </div>
              )}
            </div>
          )}

          {/* ── Scorecard ── */}
          {btShowBench && (
            <div style={{display:isMobile?"flex":"grid",flexDirection:isMobile?"row":undefined,overflowX:isMobile?"auto":undefined,WebkitOverflowScrolling:"touch",gridTemplateColumns:isMobile?undefined:`repeat(${Math.min(7,benchRows.length+1)},1fr)`,gap:8,marginBottom:16,paddingBottom:isMobile?4:0}}>
              <div style={{background:"rgba(59,130,246,0.07)",border:"2px solid #DDD0C0",borderRadius:10,padding:isMobile?"8px 10px":"12px 14px",textAlign:"center",minWidth:isMobile?80:undefined,flexShrink:0}}>
                <div style={{fontSize:10,color:"#2563EB",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:5,fontWeight:700}}>👁 Portfolio</div>
                <div style={{fontSize:isMobile?14:20,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:portTwrr>=0?"#1D9E75":"#A32D2D"}}>
                  {portTwrr!==null?`${portTwrr>=0?"+":""}${portTwrr.toFixed(2)}%`:"—"}
                </div>
                <div style={{fontSize:9,color:T.textMuted,marginTop:4}}>{scopedBtAssets.length} assets</div>
              </div>
              {benchRows.map(b=>{
                const diff  = portTwrr!==null&&b.twrr!==null ? portTwrr-b.twrr : null;
                const beats = diff!==null&&diff>0;
                return (
                  <div key={b.id} onClick={()=>setBtBenchSel(b.id)}
                    style={{background:btBenchSel===b.id?"rgba(37,99,235,0.08)":beats?"rgba(74,222,128,0.03)":"rgba(248,113,113,0.03)",
                      border:`2px solid ${btBenchSel===b.id?"#2563EB":beats?"rgba(74,222,128,0.2)":"rgba(248,113,113,0.15)"}`,
                      borderRadius:10,padding:isMobile?"8px 6px":"12px 8px",textAlign:"center",cursor:"pointer",minWidth:isMobile?75:undefined,flexShrink:0}}>
                    <div style={{fontSize:9,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.label}</div>
                    <div style={{fontSize:isMobile?13:16,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>
                      {b.twrr!==null?`${b.twrr>=0?"+":""}${b.twrr.toFixed(2)}%`:"—"}
                    </div>
                    {diff!==null&&<div style={{fontSize:10,marginTop:3,fontWeight:700,color:beats?"#1D9E75":"#A32D2D"}}>{beats?"▲ +":"▼ "}{Math.abs(diff).toFixed(2)}%</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Asset table — inline editable ── */}
          <div style={{...tCard(),marginBottom:16,padding:0,overflow:"hidden"}}>
            <div style={{padding:"12px 16px 8px",borderBottom:`1px solid ${T.borderSubtle}`,
              display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.textMuted,
                textTransform:"uppercase",letterSpacing:"0.1em"}}>
                Portfolio Assets ({scopedBtAssets.length})
              </div>
              <div style={{fontSize:10,color:T.textMuted}}>
                {activeAssets.length} active · {exitedAssets.length} exited
              </div>
            </div>
          <div style={{overflowX:"auto",maxHeight:"420px",overflowY:"auto"}}>
            <div style={{padding:"2px 4px"}}/>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:860}}>
              <thead>
                <tr>{["Asset","Ticker","Exch","Currency","Amount","Alloc %","Entry Date","Exit Date","Entry Price","Exit Price","Curr. Price","Days","TWRR","Contribution","Status","Actions"].map(h=>(
                  <th key={h} style={{...tTh(),fontSize:10,whiteSpace:"nowrap"}}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {[...scopedBtAssets].sort((a,b)=>new Date(a.addedOn)-new Date(b.addedOn)).map((a,i)=>{
                  const calc = assetsCalc.find(x=>x.id===a.id)||{};
                  const isExited = a.status==="exited";
                  const isEditing = btEditId===a.id;
                  const ev = btEditVals; // shorthand

                  const cell = (field, type="text", style={}) => isEditing ? (
                    <input type={type} style={{...inp(),width:type==="number"?72:90,padding:"2px 5px",fontSize:10,...style}}
                      value={ev[field]!==undefined?ev[field]:a[field]}
                      onChange={e=>setBtEditVals(p=>({...p,[field]:e.target.value}))}/>
                  ) : (
                    <span style={{fontFamily:type==="number"?"'Inter', system-ui, sans-serif":"inherit",...style}}>
                      {a[field]!==null&&a[field]!==undefined&&a[field]!==""
                        ? (type==="number"?parseFloat(a[field]).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2}):a[field])
                        : "—"}
                    </span>
                  );

                  return (
                    <tr key={a.id} style={{background:isExited?"rgba(248,113,113,0.03)":i%2===0?"transparent":"rgba(0,0,0,0.02)",opacity:isExited?0.72:1}}>
                      <td style={tTd({fontWeight:600,fontSize:11})}>
                        {isEditing
                          ? <input style={{...inp(),width:100,padding:"2px 5px",fontSize:10}} value={ev.name!==undefined?ev.name:a.name} onChange={e=>setBtEditVals(p=>({...p,name:e.target.value}))}/>
                          : <React.Fragment>{a.name}<div style={{fontSize:9,color:T.textMuted}}>{a.currency}</div></React.Fragment>}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:"#2563EB"})}>
                        {isEditing
                          ? <input style={{...inp(),width:80,padding:"2px 5px",fontSize:10}} value={ev.ticker!==undefined?ev.ticker:a.ticker} onChange={e=>setBtEditVals(p=>({...p,ticker:e.target.value}))}/>
                          : a.ticker}
                      </td>
                      <td style={tTd({fontSize:10,color:T.textSecondary})}>
                        {isEditing
                          ? <select style={{...inp(),width:80,padding:"2px 4px",fontSize:10}} value={ev.exchange!==undefined?ev.exchange:a.exchange} onChange={e=>setBtEditVals(p=>({...p,exchange:e.target.value}))}>
                              {["NASDAQ","NYSE","AMEX","NSE","BSE","LSE","XETRA","EPA","AMS","SIX","MIL","BME","OSL","STO","HEL","CPH","TSE","HKEX","SSE","SZSE","KRX","SGX","ASX","TWSE","SET","IDX","BURSA","TSX","B3","JSE","TADAWUL","Crypto"].map(x=><option key={x}>{x}</option>)}
                            </select>
                          : a.exchange}
                      </td>
                      <td style={tTd({fontSize:10,color:T.textSecondary})}>
                        {isEditing
                          ? <select style={{...inp(),width:68,padding:"2px 4px",fontSize:10}} value={ev.currency!==undefined?ev.currency:a.currency} onChange={e=>setBtEditVals(p=>({...p,currency:e.target.value}))}>
                              {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                            </select>
                          : a.currency}
                      </td>
                      <td style={tTd({fontSize:11})}>
                        {isEditing
                          ? <input type="number" style={{...inp(),width:76,padding:"2px 5px",fontSize:10}} value={ev.amount!==undefined?ev.amount:a.amount} onChange={e=>setBtEditVals(p=>({...p,amount:e.target.value}))}/>
                          : <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>{(a.amount||0).toLocaleString("en-US")}</span>}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:isExited?T.badgeBg:"#2563EB"})}>
                        {isExited?"—":`${(calc.wt*100||0).toFixed(1)}%`}
                      </td>
                      <td style={tTd({fontSize:10,color:T.textMuted,whiteSpace:"nowrap"})}>
                        {isEditing
                          ? <input type="date" style={{...inp(),width:112,padding:"2px 5px",fontSize:10}} value={ev.addedOn!==undefined?ev.addedOn:a.addedOn} onChange={e=>setBtEditVals(p=>({...p,addedOn:e.target.value}))}/>
                          : a.addedOn}
                      </td>
                      <td style={tTd({fontSize:10,color:isExited?"#A32D2D":T.badgeBg,whiteSpace:"nowrap"})}>
                        {isEditing && isExited
                          ? <input type="date" style={{...inp(),width:112,padding:"2px 5px",fontSize:10}}
                              value={ev.exitedOn!==undefined ? ev.exitedOn : (a.exitedOn||"")}
                              min={a.addedOn} max={new Date().toISOString().slice(0,10)}
                              onChange={e=>setBtEditVals(p=>({...p,exitedOn:e.target.value}))}/>
                          : isExited ? a.exitedOn : "—"}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11})}>
                        {isEditing
                          ? <input type="number" style={{...inp(),width:76,padding:"2px 5px",fontSize:10}} value={ev.entryPrice!==undefined?ev.entryPrice:a.entryPrice} onChange={e=>setBtEditVals(p=>({...p,entryPrice:e.target.value}))}/>
                          : <span style={{color:T.textSecondary}}>{a.entryPrice?.toLocaleString("en-US",{minimumFractionDigits:2})}</span>}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:isExited?"#A32D2D":T.badgeBg})}>
                        {isEditing && isExited
                          ? <React.Fragment>
                              <input type="number" style={{...inp(),width:76,padding:"2px 5px",fontSize:10}}
                                value={ev.exitPrice!==undefined ? ev.exitPrice : (a.exitPrice||"")}
                                onChange={e=>setBtEditVals(p=>({...p,exitPrice:e.target.value}))}/>
                              {(ev.exitPrice!==undefined ? ev.exitPrice : a.exitPrice) && a.entryPrice > 0 && (
                                <div style={{fontSize:9,marginTop:2,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600,
                                  color:parseFloat(ev.exitPrice??a.exitPrice)>=a.entryPrice?"#1D9E75":"#A32D2D"}}>
                                  {((parseFloat(ev.exitPrice??a.exitPrice)/a.entryPrice-1)*100).toFixed(2)}% return
                                </div>
                              )}
                            </React.Fragment>
                          : isExited && a.exitPrice ? a.exitPrice.toLocaleString("en-US",{minimumFractionDigits:2}) : "—"}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11})}>
                        {isEditing
                          ? <input type="number" style={{...inp(),width:76,padding:"2px 5px",fontSize:10}} value={ev.currentPrice!==undefined?ev.currentPrice:a.currentPrice} onChange={e=>setBtEditVals(p=>({...p,currentPrice:e.target.value}))}/>
                          : a.currentPrice?.toLocaleString("en-US",{minimumFractionDigits:2})}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:10,color:T.textSecondary})}>{(calc.daysHeld||0)}d</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,fontWeight:700,color:(calc.twrr||0)>=0?"#1D9E75":"#A32D2D"})}>
                        {calc.twrr!==undefined?`${calc.twrr>=0?"+":""}${calc.twrr.toFixed(2)}%`:"—"}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:(calc.contrib||0)>=0?"#1D9E75":"#A32D2D"})}>
                        {calc.contrib!==undefined?`${calc.contrib>=0?"+":""}${(calc.contrib).toFixed(2)}pp`:"—"}
                      </td>
                      <td style={tTd({fontSize:10})}>
                        {isExited
                          ? <span style={{background:"rgba(248,113,113,0.12)",color:"#A32D2D",padding:"2px 8px",borderRadius:8}}>Exited</span>
                          : <span style={{background:"rgba(22,163,74,0.1)",color:"#1D9E75",padding:"2px 8px",borderRadius:8}}>Active</span>}
                      </td>
                      <td style={tTd()}>
                        <div style={{display:"flex",gap:4,flexWrap:"nowrap"}}>
                          {isEditing ? (<React.Fragment>
                            <button onClick={()=>{
                              setBtAssets(p=>btRebalance(p.map(x=>x.id===a.id?{...x,...Object.fromEntries(Object.entries(ev).map(([k,v])=>[k,["entryPrice","currentPrice","amount"].includes(k)?parseFloat(v)||0:["exitPrice"].includes(k)?(v?parseFloat(v):null):v]))}:x)));
                              setBtEditId(null); setBtEditVals({});
                            }} style={{background:"#14532D",border:"1px solid #1D9E75",borderRadius:5,color:"#1D9E75",padding:"3px 9px",cursor:"pointer",fontSize:10,fontWeight:600}}>✓ Save</button>
                            <button onClick={()=>{setBtEditId(null);setBtEditVals({});}} style={{background:T.badgeBg,border:"none",borderRadius:5,color:T.textSecondary,padding:"3px 8px",cursor:"pointer",fontSize:10}}>✕</button>
                          </React.Fragment>) : (<React.Fragment>
                            <button onClick={()=>{setBtEditId(a.id);setBtEditVals({});}}
                              style={{background:"rgba(124,58,237,0.1)",border:"1px solid #7c3aed44",borderRadius:5,color:"#9333EA",padding:"3px 9px",cursor:"pointer",fontSize:10,fontWeight:600}}>✎ Edit</button>
                            {!isExited && (
                              <button onClick={()=>{setBtExitId(a.id);setBtExitPrice("");setBtExitDate(new Date().toISOString().slice(0,10));}}
                                style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:5,color:"#A32D2D",padding:"3px 9px",cursor:"pointer",fontSize:10}}>📤 Exit</button>
                            )}
                            {isExited && (
                              <button onClick={()=>setBtAssets(p=>btRebalance(p.map(x=>x.id===a.id?{...x,status:"active",exitPrice:null,exitedOn:null}:x)))}
                                style={{background:"rgba(22,163,74,0.1)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:5,color:"#1D9E75",padding:"3px 9px",cursor:"pointer",fontSize:10}}>↩ Restore</button>
                            )}
                            <button onClick={async ()=>{
                              if (!confirm(`Delete ${a.ticker}?`)) return;
                              try {
                                const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                                const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                                const res = await fetch(`${SUPABASE_URL}/rest/v1/bt_assets?id=eq.${encodeURIComponent(a.id)}`, {
                                  method: 'DELETE',
                                  headers: { 'apikey': ANON_KEY, 'Authorization': `Bearer ${ANON_KEY}` },
                                });
                                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                setBtAssets(p=>btRebalance(p.filter(x=>x.id!==a.id)));
                              } catch (err) {
                                console.error("[Supabase] bt delete failed:", err.message);
                                showToast(`⚠ Delete failed: ${err.message}`, "#A32D2D", 4000);
                              }
                            }}
                              style={{background:"transparent",border:"1px solid #78716C",borderRadius:5,color:T.textSecondary,padding:"3px 7px",cursor:"pointer",fontSize:10}}
                              onMouseEnter={e=>{e.currentTarget.style.borderColor="#A32D2D";e.currentTarget.style.color="#A32D2D";}}
                              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.badgeBg;e.currentTarget.style.color=T.textSecondary;}}>✕</button>
                          </React.Fragment>)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                {scopedBtAssets.length>0&&(
                  <tr style={{background:"rgba(59,130,246,0.06)",borderTop:"2px solid #DDD0C0"}}>
                    <td colSpan={13} style={{...td({fontWeight:700}),fontSize:11,color:"#2563EB"}}>Portfolio TWRR (value-weighted)</td>
                    <td colSpan={3} style={{...td(),fontSize:15,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:500,color:portTwrr>=0?"#1D9E75":"#A32D2D"}}>
                      {portTwrr!==null?`${portTwrr>=0?"+":""}${portTwrr.toFixed(2)}%`:"—"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>{/* end overflow scroll */}
          </div>{/* end card */}

          {/* ── Benchmark detail + bar chart ── */}
          {btShowBench && (
            <div style={{...tCard(),marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:10}}>
                <div style={{fontSize:12,fontWeight:600,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.1em"}}>TWRR vs Benchmarks</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {benchRows.map(b=>(
                    <button key={b.id} onClick={()=>setBtBenchSel(b.id)}
                      style={{padding:"4px 11px",borderRadius:14,border:`1px solid ${btBenchSel===b.id?"#2563EB":T.cardBorder}`,
                        background:btBenchSel===b.id?"rgba(37,99,235,0.1)":"transparent",
                        color:btBenchSel===b.id?"#2563EB":T.textSecondary,cursor:"pointer",fontSize:11,fontWeight:btBenchSel===b.id?600:400}}>
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bar comparison */}
              <div style={{marginBottom:20}}>
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{fontWeight:700,color:T.textPrimary}}>👁 Portfolio</span>
                    <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:portTwrr>=0?"#1D9E75":"#A32D2D",fontWeight:700}}>
                      {portTwrr!==null?`${portTwrr>=0?"+":""}${portTwrr.toFixed(2)}%`:"—"}
                    </span>
                  </div>
                  <div style={{height:12,background:T.inputBg,borderRadius:6,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.min(100,Math.abs(portTwrr||0)/scaleTwrr*100)}%`,
                      background:portTwrr>=0?"linear-gradient(90deg,#2563EB,#06b6d4)":"linear-gradient(90deg,#A32D2D,#A32D2D)",borderRadius:6}}/>
                  </div>
                </div>
                {benchRows.map(b=>{
                  const diff  = portTwrr!==null&&b.twrr!==null ? portTwrr-b.twrr : null;
                  const beats = diff!==null&&diff>0;
                  const isSel = b.id===btBenchSel;
                  return (
                    <div key={b.id} style={{marginBottom:7,cursor:"pointer"}} onClick={()=>setBtBenchSel(b.id)}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                        <span style={{color:isSel?T.textSecondary:T.textMuted,fontWeight:isSel?600:400}}>
                          {isSel?"● ":""}{b.label}
                          {diff!==null&&<span style={{marginLeft:8,fontSize:10,fontWeight:700,color:beats?"#1D9E75":"#A32D2D"}}>{beats?"▲ +":"▼ "}{Math.abs(diff).toFixed(2)}%</span>}
                        </span>
                        <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>{b.twrr!==null?`${b.twrr>=0?"+":""}${b.twrr.toFixed(2)}%`:"—"}</span>
                      </div>
                      <div style={{height:7,background:T.inputBg,borderRadius:4,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${b.twrr?Math.min(100,Math.abs(b.twrr)/scaleTwrr*100):0}%`,
                          background:isSel?"#2563EB":beats?T.border:"rgba(220,38,38,0.15)",borderRadius:4}}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected benchmark per-asset breakdown */}
              {selBench&&selBench.perAsset&&(
                <div style={{borderTop:`1px solid ${T.cardBorder}`,paddingTop:14}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>
                    {selBench.label} — Weight-Mirrored Breakdown
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:560}}>
                      <thead>
                        <tr>{["Asset","Weight","Entry Date","Exit Date","Days","Benchmark Return","Weighted Contrib"].map(h=>(
                          <th key={h} style={{...tTh(),fontSize:10,whiteSpace:"nowrap"}}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {[...selBench.perAsset].sort((a,b)=>new Date(a.addedOn)-new Date(b.addedOn)).map((a,i)=>(
                          <tr key={a.id} style={{background:a.status==="exited"?"rgba(248,113,113,0.03)":i%2===0?"transparent":"rgba(0,0,0,0.02)"}}>
                            <td style={tTd({fontWeight:600,fontSize:11})}>{a.ticker}<div style={{fontSize:9,color:T.textMuted}}>{a.name}</div></td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:"#2563EB"})}>{(a.wt*100).toFixed(1)}%</td>
                            <td style={tTd({fontSize:10,color:T.textMuted,whiteSpace:"nowrap"})}>{a.addedOn}</td>
                            <td style={tTd({fontSize:10,color:a.status==="exited"?"#A32D2D":T.badgeBg,whiteSpace:"nowrap"})}>{a.status==="exited"?a.exitedOn:"—"}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:10,color:T.textSecondary})}>{a.daysHeld}d</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:T.textSecondary})}>{a.bmRet!==undefined?`${a.bmRet>=0?"+":""}${a.bmRet.toFixed(2)}%`:"—"}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:a.weightedBm>=0?"#1D9E75":"#A32D2D"})}>{a.weightedBm!==undefined?`${a.weightedBm>=0?"+":""}${a.weightedBm.toFixed(2)}%`:"—"}</td>
                          </tr>
                        ))}
                        <tr style={{background:"rgba(30,58,95,0.4)",borderTop:"2px solid #DDD0C0"}}>
                          <td colSpan={6} style={{...td({fontWeight:700}),fontSize:11,color:T.textSecondary}}>{selBench.label} Total TWRR</td>
                          <td style={{...td(),fontSize:14,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:500,color:portTwrr!==null&&selBench.twrr<portTwrr?"#A32D2D":"#1D9E75"}}>
                            {selBench.twrr!==null?`${selBench.twrr>=0?"+":""}${selBench.twrr.toFixed(2)}%`:"—"}
                          </td>
                        </tr>
                        {portTwrr!==null&&selBench.twrr!==null&&(
                          <tr style={{background:"rgba(59,130,246,0.04)"}}>
                            <td colSpan={6} style={{...td({fontWeight:700}),fontSize:11,color:"#2563EB"}}>Alpha (Portfolio − {selBench.label})</td>
                            <td style={{...td(),fontSize:14,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:500,color:(portTwrr-selBench.twrr)>=0?"#1D9E75":"#A32D2D"}}>
                              {(portTwrr-selBench.twrr)>=0?"+":""}{(portTwrr-selBench.twrr).toFixed(2)}%
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* All benchmarks alpha grid */}
          {btShowBench&&(
            <div style={{...tCard(),marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>All Benchmarks — Alpha Summary</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
                {benchRows.map(b=>{
                  const diff  = portTwrr!==null&&b.twrr!==null ? portTwrr-b.twrr : null;
                  const beats = diff!==null&&diff>0;
                  return (
                    <div key={b.id} style={{background:beats?"rgba(74,222,128,0.04)":"rgba(248,113,113,0.04)",border:`1px solid ${beats?"rgba(74,222,128,0.2)":"rgba(248,113,113,0.15)"}`,borderRadius:10,padding:"12px 14px"}}>
                      <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:6}}>{b.label}</div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                        <span style={{color:T.textMuted}}>Benchmark</span>
                        <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>{b.twrr!==null?`${b.twrr>=0?"+":""}${b.twrr.toFixed(2)}%`:"—"}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:6}}>
                        <span style={{color:T.textMuted}}>Portfolio</span>
                        <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:portTwrr>=0?"#1D9E75":"#A32D2D"}}>{portTwrr!==null?`${portTwrr>=0?"+":""}${portTwrr.toFixed(2)}%`:"—"}</span>
                      </div>
                      {diff!==null&&(
                        <div style={{borderTop:"1px solid #EDE0D4",paddingTop:6,display:"flex",justifyContent:"space-between"}}>
                          <span style={{fontSize:11,color:beats?"#1D9E75":"#A32D2D",fontWeight:600}}>{beats?"▲ Alpha":"▼ Lag"}</span>
                          <span style={{fontFamily:"'Inter', system-ui, sans-serif",fontSize:13,fontWeight:500,color:beats?"#1D9E75":"#A32D2D"}}>{diff>=0?"+":""}{diff.toFixed(2)}%</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{padding:"9px 13px",background:T.cardBg,borderRadius:6,fontSize:10,color:T.textLight,lineHeight:1.8}}>
            ℹ <strong style={{color:T.textMuted}}>Methodology:</strong> Portfolio TWRR = Σ (value-weight × single-period return). For exited assets, exitPrice is used instead of currentPrice.
            Benchmark TWRR mirrors identical weights and date slices: benchmark slice = ((1 + annualReturn)^(daysHeld/365) − 1) × weight.
            Annualised 1-Year returns used from benchmark data.
          </div>

          </React.Fragment>}
          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in scenarios tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══ VOLATILITY & SHARPE RATIO ══ */}
        {tab==="analytics" && (function(){ try {
          // Uses module-level analyticsSubTab state
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see analytics analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          // Estimate annualised volatility from class (no price history available)
          const CLASS_VOL = {
            "Equities":22, "Indian Equities":26, "US Equities":18, "International Equities":21,
            "Indian Mutual Funds":15, "Debt":5, "Bonds":6, "Fixed Income":5,
            "Real Estate":14, "Gold":16, "Commodities":25, "Crypto":75,
            "Cash":0.5, "Alternates":18, "Market Neutral":6,
          };
          const RISK_FREE = 5.0; // annualised %

          const rows = filteredHW.filter(h=>h.valueUSD>0).map(h => {
            const baseVol  = CLASS_VOL[h.class] ?? 20;
            // Slight per-holding variation using price-derived noise
            const noise    = h.currentPrice > 0 ? ((h.currentPrice * 7) % 8) - 4 : 0;
            const vol      = Math.max(0.5, baseVol + noise);
            // Annualised return estimate from P&L
            const gainPct  = h.costUSD > 0 ? ((h.valueUSD - h.costUSD) / h.costUSD) * 100 : 0;
            // Assume holding for ~1.5 years if no date; annualise
            const annRet   = gainPct; // treating as approx annualised for simplicity
            const sharpe   = vol > 0 ? (annRet - RISK_FREE) / vol : 0;
            const portWt   = filtTotalUSD > 0 ? (h.valueUSD / filtTotalUSD) * 100 : 0;
            return { ...h, vol, annRet, sharpe, portWt };
          }).sort((a,b) => b.portWt - a.portWt);

          // Portfolio-level weighted volatility
          const portVol = rows.reduce((s,r) => s + r.portWt/100 * r.vol, 0);
          // Weighted avg Sharpe
          const portSharpe = rows.reduce((s,r) => s + r.portWt/100 * r.sharpe, 0);
          const portReturn = rows.reduce((s,r) => s + r.portWt/100 * r.annRet, 0);

          const volColor = (v) => v > 40 ? "#A32D2D" : v > 20 ? "#D97706" : v > 10 ? "#EAB308" : "#059669";
          const sharpeColor = (s) => s > 1 ? "#059669" : s > 0.5 ? "#EAB308" : s > 0 ? "#D97706" : "#A32D2D";
          const volLabel = (v) => v > 40 ? "Very High" : v > 20 ? "High" : v > 10 ? "Moderate" : "Low";

          const maxVol = Math.max(...rows.map(r=>r.vol), 1);

          return (
            <div style={{padding:isMobile?"12px":"20px 24px"}}>
              {/* Sub-tab toggle */}
              <div style={{display:"flex",gap:8,marginBottom:20,borderBottom:`1px solid ${T.cardBorder}`,paddingBottom:12}}>
                {[["volatility","🎢 Volatility & Sharpe"],["correlation","📐 Correlation Matrix"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setAnalyticsSubTab(k)}
                    style={{padding:"7px 18px",borderRadius:20,border:`1px solid ${analyticsSubTab===k?T.brand:T.border}`,
                      background:analyticsSubTab===k?"rgba(0,120,120,0.1)":"transparent",
                      color:analyticsSubTab===k?"#007878":T.textSecondary,cursor:"pointer",fontSize:13,fontWeight:analyticsSubTab===k?700:400}}>
                    {l}
                  </button>
                ))}
              </div>
              {analyticsSubTab==="volatility" && <div>
              <div style={{marginBottom:18}}>
                <div style={{fontSize:18,fontWeight:700,color:T.textPrimary}}>🎢 Volatility & Sharpe Ratio</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                  Estimated annualised volatility and risk-adjusted returns per holding
                  <span style={{marginLeft:6,color:T.textMuted}}>(Risk-free rate: {RISK_FREE}%)</span>
                </div>
              </div>

              {/* Portfolio-level KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:22}}>
                {[
                  { label:"Portfolio Volatility",  val:portVol.toFixed(1)+"%",    color:volColor(portVol), sub:volLabel(portVol) },
                  { label:"Portfolio Sharpe",       val:portSharpe.toFixed(2),       color:sharpeColor(portSharpe), sub:portSharpe>1?"Excellent":portSharpe>0.5?"Good":"Below avg" },
                  { label:"Wtd Avg Return",         val:(portReturn>=0?"+":"")+portReturn.toFixed(1)+"%", color:portReturn>=0?"#059669":"#A32D2D" },
                  { label:"Risk-Free Rate",         val:RISK_FREE+"%",              color:T.textSecondary, sub:"Benchmark" },
                  { label:"Holdings Analysed",      val:rows.length,                color:T.brand },
                ].map(k => (
                  <div key={k.label} style={{...tCard(),borderRadius:10,padding:"12px 14px"}}>
                    <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{k.label}</div>
                    <div style={{fontSize:18,fontWeight:700,color:k.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                    {k.sub && <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>{k.sub}</div>}
                  </div>
                ))}
              </div>



              {/* Holdings detail table */}
              <div style={{...tCard(),borderRadius:12,overflow:"hidden"}}>
                <div style={{overflowX:"auto",overflowY:"auto",maxHeight:420}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:580}}>
                    <thead style={{position:"sticky",top:0,zIndex:2,background:T.thBg}}>
                      <tr style={{background:T.thBg}}>
                        {["Holding","Asset Class","Weight","Est. Volatility","Return","Sharpe Ratio","Risk Level"].map(h=>(
                          <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:700,
                            color:T.thText,textTransform:"uppercase",letterSpacing:"0.05em",
                            borderBottom:`2px solid ${T.cardBorder}`,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r,i) => (
                        <tr key={r.id} style={{background:i%2===0?T.cardBg:T.surfaceBg,borderBottom:`1px solid ${T.cardBorder}`}}>
                          <td style={{padding:"9px 14px"}}>
                            <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{r.name}</div>
                            {r.ticker!=="-" && <div style={{fontSize:10,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif"}}>{r.ticker}</div>}
                          </td>
                          <td style={{padding:"9px 14px",fontSize:11,color:T.textSecondary}}>{r.class}</td>
                          <td style={{padding:"9px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary}}>{r.portWt.toFixed(1)}%</td>
                          <td style={{padding:"9px 14px"}}>
                            <span style={{fontSize:12,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:volColor(r.vol)}}>
                              {r.vol.toFixed(1)}%
                            </span>
                          </td>
                          <td style={{padding:"9px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",
                            fontWeight:600,color:r.annRet>=0?"#059669":"#A32D2D"}}>
                            {r.annRet>=0?"+":""}{r.annRet.toFixed(1)}%
                          </td>
                          <td style={{padding:"9px 14px"}}>
                            <span style={{fontSize:12,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                              color:sharpeColor(r.sharpe)}}>
                              {r.sharpe.toFixed(2)}
                            </span>
                          </td>
                          <td style={{padding:"9px 14px"}}>
                            <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:8,
                              background:`${volColor(r.vol)}18`,color:volColor(r.vol)}}>
                              {volLabel(r.vol)}
                            </span>
                          </td>
                          </tr>
                        ))}
                      {/* Portfolio total row */}
                      <tr style={{background:`${T.brand}0a`,borderTop:`2px solid ${T.brand}`}}>
                        <td style={{padding:"10px 14px",fontWeight:700,color:T.brand}} colSpan={2}>Portfolio Total</td>
                        <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700}}>100%</td>
                        <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:volColor(portVol)}}>{portVol.toFixed(1)}%</td>
                        <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:portReturn>=0?"#059669":"#A32D2D"}}>{portReturn>=0?"+":""}{portReturn.toFixed(1)}%</td>
                        <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:sharpeColor(portSharpe)}}>{portSharpe.toFixed(2)}</td>
                        <td style={{padding:"10px 14px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,background:`${volColor(portVol)}18`,color:volColor(portVol)}}>{volLabel(portVol)}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:10}}>
                ℹ Volatility estimated from asset class benchmarks. Sharpe = (Return − Risk-free) ÷ Volatility. Actual figures require historical price series.
              </div>

              {/* ── LIQUIDITY ANALYSIS — admin only ── */}
              {isAdmin && (function(){
              // Liquidity score per holding
              // Listed + large-cap = highly liquid; Private/AIF = illiquid
              // Days to liquidate = holding size / (ADV × liquidation fraction)
              // ADV estimates by asset class (USD daily trading volume)
              const CLASS_ADV = {
                "Equities":          50000000,   // $50M+ daily for large caps
                "Indian Equities":   10000000,   // $10M avg for NSE large cap
                "Global Equities":   50000000,
                "Mutual Funds":      5000000,    // redemption in 1-3 days
                "Indian Mutual Funds":5000000,
                "Global Mutual Funds":2000000,
                "Fixed Income":      2000000,    // secondary market
                "Time Deposits":     0,           // locked — days = tenor
                "Real Estate":       0,           // illiquid
                "Commodities":       20000000,
                "Crypto":            100000000,
                "Alternates":        0,           // illiquid — PE/hedge lockup
                "Market Neutral":    1000000,
                "Art & Collectibles":0,
                "Cash / Bank Balance":999999999,  // instant
                "Miscellaneous":     500000,
              };
              const LIQUIDATION_FRACTION = 0.10; // can liquidate 10% of ADV per day
              const LOCKUP_DAYS = { "Real Estate":1080, "Alternates":730,
                "Art & Collectibles":365, "Time Deposits":180 };

              const liqRows = filteredHW.map(h => {
                const adv = CLASS_ADV[h.class] || 1000000;
                const dailyLiq = adv * LIQUIDATION_FRACTION;
                const daysToLiq = h.listed === false && adv === 0
                  ? (LOCKUP_DAYS[h.class] || 365)
                  : dailyLiq > 0 ? Math.ceil(h.valueUSD / dailyLiq) : 999;
                const bucket = daysToLiq <= 1 ? "instant"
                  : daysToLiq <= 5 ? "days"
                  : daysToLiq <= 30 ? "weeks"
                  : daysToLiq <= 180 ? "months"
                  : "locked";
                const bucketCol = { instant:"#1D9E75", days:"#059669",
                  weeks:"#D97706", months:"#EA580C", locked:"#A32D2D" };
                return { ...h, adv, dailyLiq, daysToLiq, bucket, bucketCol: bucketCol[bucket] };
              }).sort((a,b) => b.daysToLiq - a.daysToLiq);

              const bucketSummary = liqRows.reduce((acc,r) => {
                acc[r.bucket] = (acc[r.bucket]||0) + r.valueUSD; return acc;
              }, {});
              const instantPct  = ((bucketSummary.instant||0)/filtTotalUSD*100);
              const daysPct     = ((bucketSummary.days||0)/filtTotalUSD*100);
              const weeksPct    = ((bucketSummary.weeks||0)/filtTotalUSD*100);
              const monthsPct   = ((bucketSummary.months||0)/filtTotalUSD*100);
              const lockedPct   = ((bucketSummary.locked||0)/filtTotalUSD*100);
              const liquidPct   = instantPct + daysPct + weeksPct;
              const illiquidPct = monthsPct + lockedPct;

              return (
                <div style={{...tCard(),marginTop:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <span style={{fontSize:16}}>💧</span>
                    <span style={{fontSize:14,fontWeight:700}}>Liquidity Analysis</span>
                    <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>
                      at 10% of estimated daily volume
                    </span>
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                    How quickly can each position be fully liquidated without moving the market?
                  </div>

                  {/* Liquidity spectrum bar */}
                  <div style={{marginBottom:20}}>
                    <div style={{height:20,borderRadius:8,overflow:"hidden",
                      display:"flex",marginBottom:8}}>
                      {[
                        {pct:instantPct, col:"#1D9E75", label:"Instant"},
                        {pct:daysPct,    col:"#059669", label:"1-5d"},
                        {pct:weeksPct,   col:"#D97706", label:"1-4w"},
                        {pct:monthsPct,  col:"#EA580C", label:"1-6m"},
                        {pct:lockedPct,  col:"#A32D2D", label:"Locked"},
                      ].map(({pct,col,label})=> pct > 0.5 ? (
                        <div key={label} style={{width:`${pct}%`,background:col,
                          display:"flex",alignItems:"center",justifyContent:"center",
                          transition:"width 0.5s"}}>
                          <span style={{fontSize:8,color:"white",fontWeight:700,whiteSpace:"nowrap",
                            overflow:"hidden",padding:"0 2px"}}>
                            {pct>3?pct.toFixed(1)+"%":""}
                          </span>
                        </div>
                      ) : null)}
                    </div>
                    <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:10}}>
                      {[
                        {col:"#1D9E75",label:"Instant",    pct:instantPct},
                        {col:"#059669",label:"1-5 days",   pct:daysPct},
                        {col:"#D97706",label:"1-4 weeks",  pct:weeksPct},
                        {col:"#EA580C",label:"1-6 months", pct:monthsPct},
                        {col:"#A32D2D",label:"Locked",      pct:lockedPct},
                      ].map(({col,label,pct})=>(
                        <div key={label} style={{display:"flex",alignItems:"center",gap:4}}>
                          <div style={{width:10,height:10,borderRadius:2,background:col}}/>
                          <span style={{color:T.textSecondary}}>{label}</span>
                          <span style={{fontWeight:700,color:col,fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* KPI row */}
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:16}}>
                    {[
                      {label:"Liquid (≤4 weeks)", val:`${liquidPct.toFixed(1)}%`,  col:liquidPct>70?"#1D9E75":"#D97706", icon:"💧"},
                      {label:"Illiquid (>1 month)",val:`${illiquidPct.toFixed(1)}%`,col:illiquidPct>30?"#A32D2D":"#D97706",icon:"🔒"},
                      {label:"Avg Days to Liquidate",val:`${Math.round(liqRows.reduce((s,r)=>s+r.daysToLiq*(r.valueUSD/filtTotalUSD),0))} days`,col:T.brand,icon:"📅"},
                      {label:"Most Illiquid",val:liqRows[0]?.name.split(" ").slice(0,3).join(" ")||"—",col:"#A32D2D",icon:"⚠️"},
                    ].map(({label,val,col,icon})=>(
                      <div key={label} style={{...tCard(),padding:"10px 14px"}}>
                        <div style={{fontSize:10,color:T.textMuted,marginBottom:4}}>{icon} {label}</div>
                        <div style={{fontSize:14,fontWeight:700,color:col,
                          fontFamily:"'Inter', system-ui, sans-serif",whiteSpace:"nowrap",
                          overflow:"hidden",textOverflow:"ellipsis"}}>{val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Holdings table */}
                  <div style={{overflowX:"auto",overflowY:"auto",maxHeight:320}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:580}}>
                      <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:2}}>
                        <tr>{["Holding","Class","Value","Weight","Liquidity","Days to Liquidate","ADV Est."].map(h=>(
                          <th key={h} style={tTh()}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {liqRows.map((r,i)=>(
                          <tr key={r.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={tTd({fontWeight:600,maxWidth:160})}>
                              <div style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</div>
                            </td>
                            <td style={tTd()}><span style={badge()}>{r.class}</span></td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                              {fmt(convert(r.valueUSD,dc),dc)}
                            </td>
                            <td style={tTd({fontSize:12})}>
                              {(r.valueUSD/filtTotalUSD*100).toFixed(1)}%
                            </td>
                            <td style={tTd()}>
                              <span style={{fontSize:10,fontWeight:700,color:r.bucketCol,
                                background:`${r.bucketCol}15`,padding:"2px 8px",
                                borderRadius:10,border:`1px solid ${r.bucketCol}33`}}>
                                {{instant:"Instant",days:"1-5 days",weeks:"1-4 wks",
                                  months:"1-6 mo",locked:"Locked"}[r.bucket]}
                              </span>
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",
                              fontSize:12,fontWeight:r.daysToLiq>90?700:400,
                              color:r.daysToLiq>180?"#A32D2D":r.daysToLiq>30?"#D97706":T.textPrimary})}>
                              {r.daysToLiq>=999?"N/A (locked)":r.daysToLiq<=1?"Same day":`~${r.daysToLiq} days`}
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:T.textMuted})}>
                              {r.adv===0?"Illiquid":r.adv>=1000000
                                ?`$${(r.adv/1000000).toFixed(0)}M/day`:`$${(r.adv/1000).toFixed(0)}K/day`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{marginTop:10,fontSize:10,color:T.textLight,lineHeight:1.5}}>
                    ⚠️ ADV estimates are illustrative. Real liquidity depends on market conditions, position size, and fund redemption terms.
                    PE, AIF, and real estate positions show estimated lockup periods rather than market depth.
                  </div>
                </div>
              );
              })()}
              </div>}
              {analyticsSubTab==="correlation" && (function(){
          // Build pseudo-correlation from sector/class/currency similarity
          const holdings = filteredHW.filter(h=>h.valueUSD>0).slice(0,16);

          // Score similarity 0-1 between two holdings across 4 dimensions
          // Hash a string to a stable integer for noise generation
          const hashStr = (s) => {
            let h = 0;
            for (let i = 0; i < (s||"").length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
            return Math.abs(h);
          };

          // Class-pair base correlations drawn from empirical data
          const CLASS_CORR = {
            "Equities|Equities":               0.82,
            "Equities|Indian Equities":         0.55,
            "Equities|Indian Mutual Funds":     0.50,
            "Equities|Global Mutual Funds":     0.75,
            "Equities|Debt":                   -0.10,
            "Equities|Fixed Income":            -0.12,
            "Equities|Bonds":                  -0.08,
            "Equities|Gold":                    0.05,
            "Equities|Commodities":             0.28,
            "Equities|Crypto":                  0.28,
            "Equities|Real Estate":             0.55,
            "Equities|Alternates":              0.65,
            "Equities|Cash":                   -0.05,
            "Equities|PPF":                    -0.05,
            "Indian Equities|Indian Equities":  0.78,
            "Indian Equities|Indian Mutual Funds": 0.88,
            "Indian Equities|Debt":            -0.08,
            "Indian Equities|Gold":             0.10,
            "Indian Equities|Crypto":           0.20,
            "Indian Mutual Funds|Indian Mutual Funds": 0.72,
            "Indian Mutual Funds|Debt":        -0.05,
            "Indian Mutual Funds|Gold":         0.08,
            "Global Mutual Funds|Global Mutual Funds": 0.70,
            "Global Mutual Funds|Indian Equities": 0.48,
            "Gold|Gold":                        0.92,
            "Gold|Commodities":                 0.42,
            "Gold|Crypto":                      0.15,
            "Gold|Debt":                        0.18,
            "Crypto|Crypto":                    0.75,
            "Debt|Debt":                        0.82,
            "Debt|Fixed Income":                0.88,
            "Debt|PPF":                         0.55,
            "Real Estate|Real Estate":          0.70,
            "Cash|Cash":                        0.98,
          };
          const getBaseCorr = (ca, cb) => {
            if (ca === cb) {
              const self = CLASS_CORR[ca + "|" + ca];
              return self !== undefined ? self : 0.70;
            }
            const k1 = ca + "|" + cb, k2 = cb + "|" + ca;
            if (CLASS_CORR[k1] !== undefined) return CLASS_CORR[k1];
            if (CLASS_CORR[k2] !== undefined) return CLASS_CORR[k2];
            // Default cross-asset: low correlation
            return 0.10;
          };

          const score = (a, b) => {
            if (a.id === b.id) return 1;
            let base = getBaseCorr(a.class, b.class);
            // Same country boosts slightly for equity-like assets
            if (a.country === b.country && a.country && a.country !== "-") base = Math.min(0.97, base + 0.05);
            // Same currency mild boost
            if (a.currency === b.currency) base = Math.min(0.97, base + 0.03);
            // Stable per-pair noise ±0.06 so same-class pairs don't look identical
            const idA = String(a.id), idB = String(b.id);
            const pairKey = idA < idB ? idA + "|" + idB : idB + "|" + idA;
            const noise = (hashStr(pairKey) % 13) / 100 - 0.06;
            return Math.min(0.99, Math.max(-0.30, base + noise));
          };

          const matrix = holdings.map(a => holdings.map(b => score(a, b)));

          const corrColor = (v) => {
            if (v >= 0.8)  return "#A32D2D";
            if (v >= 0.6)  return "#D97706";
            if (v >= 0.4)  return "#EAB308";
            if (v >= 0.2)  return "#84CC16";
            if (v >= 0)    return "#22C55E";
            return "#3B82F6";
          };
          // corrBg returns a full rgba() string with opacity baked in
          const corrBg = (v, opacity=0.55) => {
            const safe = isNaN(v) ? 0 : v;
            const op = (Math.abs(safe) * 0.7 + 0.15).toFixed(2);
            const alpha = opacity !== undefined ? opacity : op;
            if (safe >= 0.8) return `rgba(220,38,38,${alpha})`;
            if (safe >= 0.6) return `rgba(217,119,6,${alpha})`;
            if (safe >= 0.4) return `rgba(234,179,8,${alpha})`;
            if (safe >= 0.2) return `rgba(132,204,22,${alpha})`;
            if (safe >= 0)   return `rgba(34,197,94,${alpha})`;
            return `rgba(59,130,246,${alpha})`;
          };

          const shortName = (h) => {
            // Always prefer a human-readable label; never show raw scheme codes
            if (h.isMutualFund || h.class === "Indian Mutual Funds") {
              // Trim common suffixes for brevity
              return h.name.replace(/- (Direct|Regular) (Plan - )?(Growth|Dividend|IDCW).*/i,"").replace(/Fund$/i,"Fund").slice(0,20).trim();
            }
            // For equities, ticker is fine; but not numeric-only (MF scheme codes)
            if (h.ticker && h.ticker !== "-" && !/^\d+$/.test(h.ticker)) return h.ticker;
            return h.name.slice(0,12).trim();
          };

          const highPairs = [];
          for (let i=0;i<holdings.length;i++) for (let j=i+1;j<holdings.length;j++) {
            const v = matrix[i][j];
            if (v >= 0.6) highPairs.push({ a:holdings[i], b:holdings[j], v });
          }
          highPairs.sort((a,b)=>b.v-a.v);

          if (holdings.length < 2) return (
            <div style={{padding:"40px 24px",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:12}}>📊</div>
              <div style={{fontSize:14,color:T.textSecondary}}>Need at least 2 holdings to show correlation</div>
            </div>
          );

          return (
            <div style={{padding:isMobile?"12px":"20px 24px"}}>
              <div style={{marginBottom:18}}>
                <div style={{fontSize:18,fontWeight:700,color:T.textPrimary}}>📊 Correlation Matrix</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                  Estimated co-movement based on asset class, currency, country & exchange similarity
                  {holdings.length < filteredHW.length && <span style={{color:"#D97706",marginLeft:6}}>· Showing top {holdings.length} holdings</span>}
                </div>
              </div>

              {/* High correlation alerts */}
              {highPairs.length > 0 && (
                <div style={{borderRadius:12,border:`1px solid rgba(217,119,6,0.35)`,
                  background:"rgba(217,119,6,0.05)",padding:"12px 18px",marginBottom:18}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#D97706",marginBottom:8}}>
                    ⚠ {highPairs.length} Highly Correlated Pair{highPairs.length>1?"s":""} — limited diversification benefit
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {highPairs.slice(0,6).map((p,i) => (
                      <div key={i} style={{fontSize:11,background:`rgba(217,119,6,0.1)`,
                        border:"1px solid rgba(217,119,6,0.3)",borderRadius:6,padding:"3px 10px",color:"#D97706",fontWeight:600}}>
                        {shortName(p.a)} ↔ {shortName(p.b)}: {(p.v*100).toFixed(0)}%
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Matrix */}
              <div style={{...tCard(),borderRadius:12,padding:"18px 20px",overflowX:"auto"}}>
                <div style={{minWidth:holdings.length*56+160}}>
                  {/* Header row */}
                  <div style={{display:"flex",marginBottom:2,paddingLeft:120}}>
                    {holdings.map(h => (
                      <div key={h.id} style={{width:52,flexShrink:0,textAlign:"center",
                        fontSize:9,color:T.textSecondary,fontWeight:600,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                        writingMode:"vertical-rl",transform:"rotate(180deg)",
                        height:64,lineHeight:"52px",padding:"0 2px"}}>
                        {shortName(h)}
                      </div>
                    ))}
                  </div>
                  {/* Matrix rows */}
                  {holdings.map((rowH, i) => (
                    <div key={rowH.id} style={{display:"flex",alignItems:"center",marginBottom:2}}>
                      <div style={{width:120,fontSize:10,color:T.textSecondary,fontWeight:600,
                        paddingRight:8,textAlign:"right",overflow:"hidden",textOverflow:"ellipsis",
                        whiteSpace:"nowrap",flexShrink:0}}>
                        {shortName(rowH)}
                      </div>
                      {holdings.map((colH, j) => {
                        const v = matrix[i][j];
                        const isSelf = i === j;
                        return (
                          <div key={colH.id} title={`${shortName(rowH)} vs ${shortName(colH)}: ${(v*100).toFixed(0)}%`}
                            style={{width:50,height:30,borderRadius:4,marginRight:2,flexShrink:0,
                              background: isSelf ? "#007878" : corrBg(v),
                              display:"flex",alignItems:"center",justifyContent:"center",
                              border: isSelf ? "2px solid #007878" : `1px solid ${corrBg(v, 0.25)}`}}>
                            <span style={{fontSize:9,fontWeight:700,
                              color: isSelf ? "white" : (Math.abs(v)>0.3?"white":T.textSecondary)}}>
                              {isSelf ? "1.0" : isNaN(v) ? "—" : (v>=0?"+":"")+v.toFixed(1)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:11,color:T.textSecondary}}>Correlation scale:</span>
                {[["≥0.8","#A32D2D","High"],["0.6–0.8","#D97706","Moderate"],["0.4–0.6","#EAB308","Low"],["<0.4","#22C55E","Minimal"]].map(([r,c,l])=>(
                  <div key={r} style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{width:12,height:12,borderRadius:2,background:c}}/>
                    <span style={{fontSize:10,color:T.textSecondary}}>{r} {l}</span>
                  </div>
                ))}
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:8}}>
                ℹ Estimated from structural similarity (asset class, currency, country, exchange). For precise correlation, historical price data is required.
              </div>
            </div>
          );
        })()}
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in analytics tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}


        {/* ══ SCENARIOS TAB — Backtesting + What-If merged ══ */}
        {tab==="scenarios" && (
          <div style={{display:"flex",gap:8,marginBottom:20,borderBottom:`1px solid ${T.cardBorder}`,paddingBottom:12}}>
            {[["backtesting","🧪 Backtesting"],["whatif","🧮 What-If Simulator"]].map(([k,l])=>(
              <button key={k} onClick={()=>setScenariosSubTab(k)}
                style={{padding:"7px 18px",borderRadius:20,border:`1px solid ${scenariosSubTab===k?T.brand:T.border}`,
                  background:scenariosSubTab===k?"rgba(0,120,120,0.1)":"transparent",
                  color:scenariosSubTab===k?"#007878":T.textSecondary,cursor:"pointer",fontSize:13,fontWeight:scenariosSubTab===k?700:400}}>
                {l}
              </button>
            ))}
          </div>
        )}

        {tab==="scenarios" && scenariosSubTab==="whatif" && (
          <WhatIfSimulator
            filteredHW={filteredHW}
            filtTotalUSD={filtTotalUSD}
            fxRates={fxRates}
            dc={dc}
            isMobile={isMobile}
            T={T}
            tCard={tCard}
            inp={inp}
            fmt={fmt}
          />
        )}


        {tab==="risk" && canSeeRisk && (function(){ try {
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see risk analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}

          // ── Scope options (admin only) ──
          // ── Risk scope: reads directly from global family/client dropdowns ──
          const riskHW = filteredHW; // filteredHW already reflects selectedFamily/selectedClients
          const activeRiskOpt = (!isAdmin && loggedInClientId)
            ? { type:"client", id:loggedInClientId, label:"My Risk", icon:"👤" }
            : (isAdmin && viewAsClientId)
              ? { type:"client", id:viewAsClientId, label:clients.find(c=>c.id===viewAsClientId)?.name||"Client", icon:"👤" }
              : selectedClients.size > 0
            ? { type:"client", id:[...selectedClients][0], label:visibleClients.find(c=>String(c.id)===[...selectedClients][0])?.name||"Client", icon:"👤" }
            : selectedFamily !== "all"
              ? { type:"family", id:selectedFamily, label:visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family", icon:"👨‍👩‍👧" }
              : { type:"all", id:"all", label:"All Clients", icon:"👥" };

          return <React.Fragment>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isAdmin?12:20,flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:17,fontWeight:700}}>⚡ Risk Analysis</div>
              <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                Portfolio risk metrics, concentration, stress tests and correlation analysis.
                {isAdmin && activeRiskOpt.type !== "all" && (
                  <span style={{marginLeft:8,color:T.brand,fontWeight:600}}>
                    · {activeRiskOpt.icon} {activeRiskOpt.label}
                  </span>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[["overview","Overview"],["concentration","Concentration"],["stress","Stress Tests"],["correlation","Correlation"],["sector","Sector Heat"],["lookthrough","Look-Through"]]
                .filter(([k]) => k==="sector"||k==="lookthrough"?isAdmin:allowedRiskTabs[k])
                .map(([k,l])=>(
                  <button key={k} onClick={()=>setRiskTab(k)}
                    style={{padding:"6px 14px",borderRadius:7,border:`1px solid ${riskTab===k?"#A32D2D":T.border}`,background:riskTab===k?"rgba(220,38,38,0.1)":"transparent",color:riskTab===k?"#A32D2D":T.textSecondary,cursor:"pointer",fontSize:12,fontWeight:riskTab===k?600:400}}>
                    {l}
                  </button>
                ))}
            </div>
          </div>



          {/* ── OVERVIEW ── */}
          {riskTab==="overview" && allowedRiskTabs["overview"] && (function(){
            try {
            const listedHW  = riskHW.filter(h=>h.listed);
            const totalV    = riskHW.reduce((s,h)=>s+h.valueUSD,0)||1;
            const listedV   = listedHW.reduce((s,h)=>s+h.valueUSD,0);
            const illiqPct  = ((totalV-listedV)/totalV*100);

            // HHI (Herfindahl–Hirschman Index) — concentration
            const hhi = riskHW.reduce((s,h)=>s+Math.pow(h.valueUSD/totalV*100,2),0);
            const hhiLabel = hhi<1000?"Low":hhi<2500?"Moderate":"High";
            const hhiColor = hhi<1000?"#1D9E75":hhi<2500?"#D97706":"#A32D2D";

            // Top holding concentration
            const sorted = [...riskHW].sort((a,b)=>b.valueUSD-a.valueUSD);
            const top5Pct = sorted.slice(0,5).reduce((s,h)=>s+h.valueUSD/totalV*100,0);
            const top1Pct = sorted.slice(0,1).reduce((s,h)=>s+h.valueUSD/totalV*100,0);

            // Single currency exposure
            const maxCcy = Object.entries(riskHW.reduce((a,h)=>{a[h.currency]=(a[h.currency]||0)+h.valueUSD;return a;},{})).sort((a,b)=>b[1]-a[1])[0];
            const maxCcyPct = maxCcy ? (maxCcy[1]/totalV*100) : 0;

            // ─── UNIFIED RISK ENGINE ───────────────────────────────────────────
            // Asset-class volatility table (annualised %, based on historical norms)
            const CLASS_VOL_RISK = {
              "Equities":22, "Indian Equities":26, "Crypto":75, "Fixed Income":5,
              "Debt":5, "Gold":16, "Commodities":18, "Real Estate":12,
              "Alternates":20, "Market Neutral":6,
              "Art & Collectibles":25, "Cash / Bank Balance":0.5,
              "Indian Mutual Funds":14, "Global Mutual Funds":12,
              "Time Deposits":1, "Bonds":5,
            };
            // Weighted annual vol (primary risk metric — drives VaR, drawdown, beta)
            const annVol = riskHW.reduce((s,h) => {
              const vol = CLASS_VOL_RISK[h.class] ?? 18;
              return s + (h.valueUSD / totalV) * vol;
            }, 0);
            const dailyVol  = annVol / Math.sqrt(252);
            const var95     = +(dailyVol * 1.645).toFixed(2);
            const var99     = +(dailyVol * 2.326).toFixed(2);
            // Max drawdown: empirical ratio ~2.0× annual vol, capped at 70%
            const maxDrawdown = +(Math.min(70, annVol * 2.0)).toFixed(1);
            // Beta vs a pure-equity benchmark (18% vol proxy for global equity)
            const beta = Math.max(0.1, Math.min(2.5, annVol / 18));

            // Cross-sectional return dispersion — ONLY for Sharpe proxy
            // Exclude: zero-cost (Infinity), unlisted (no market price)
            const validGains = riskHW
              .filter(h => h.listed && isFinite(h.gainPct) && h.costPrice > 0)
              .map(h => Math.max(-100, Math.min(300, h.gainPct??0)));
            const avgReturn   = validGains.length ? validGains.reduce((a,b)=>a+b,0)/validGains.length : 0;
            const gainVariance = validGains.length > 1
              ? validGains.reduce((s,g)=>s+Math.pow(g-avgReturn,2),0)/validGains.length : 0;
            const gainStddev  = Math.sqrt(gainVariance); // used only for Sharpe
            const sharpe = gainStddev > 0 ? ((avgReturn - 5) / gainStddev).toFixed(2) : "—";

            // ─── UNIFIED SCORE (0-100) — single formula used everywhere ────────
            // F1: Concentration (HHI)     0-30 pts  sqrt scale, max at HHI=10000
            // F2: Top-5 weight            0-25 pts  linear, 100% weight → 25pts
            // F3: Illiquidity             0-20 pts  linear, 100% illiquid → 20pts
            // F4: Asset-class annual vol  0-25 pts  60% vol (blended Equities+Crypto) → 25pts
            const f1 = Math.min(30, Math.round(Math.sqrt(hhi) / 3.3));
            const f2 = Math.min(25, Math.round(top5Pct * 0.25));
            const f3 = Math.min(20, Math.round(illiqPct * 0.20));
            const f4 = Math.min(25, Math.round(annVol   * 0.42));
            const unifiedScore = f1 + f2 + f3 + f4;  // 0-100
            const riskLevel  = unifiedScore < 33 ? "Conservative" : unifiedScore < 66 ? "Moderate" : "Aggressive";
            const riskColor  = unifiedScore < 33 ? "#34D399" : unifiedScore < 66 ? "#FBBF24" : "#F87171";

            // Stddev alias kept for riskMetrics card (cross-sectional, clearly labelled)
            const stddev = gainStddev;

            // ── Number of distinct sectors ──
            const sectorSet = new Set(riskHW.map(h => resolveHoldingSector(h)));
            const numSectors = sectorSet.size;

            // ── Risk factor scores for radar (0-100, deterministic) ──
            // ── Currency Risk: measures FOREIGN (non-base) currency exposure ──
            // USD/SGD investors: high USD exposure = LOW risk; high multi-currency = HIGH risk
            const displayCcy = dc || "USD";
            const foreignCcyPct = Math.max(0,
              100 - (riskHW.reduce((s,h) => h.currency===displayCcy ? s+h.valueUSD : s, 0) / totalV * 100)
            );

            const riskFactors = [
              // Market Risk: annualised weighted vol — 0%→0, 75% (Crypto)→100
              { label:"Market Risk",    score: Math.min(100, Math.round(annVol * 1.33)) },
              // Liquidity Risk: truly illiquid % (0 floor for fully liquid portfolios)
              { label:"Liquidity Risk", score: Math.min(100, Math.round(illiqPct * 0.90)) },
              // Currency Risk: proportion in NON-base currency (foreign FX exposure)
              { label:"Currency Risk",  score: Math.min(100, Math.round(foreignCcyPct * 0.85)) },
              // Concentration Risk: HHI sqrt-scaled; <1000→low, >4000→high
              { label:"Concentration",  score: Math.min(100, Math.round(Math.sqrt(hhi) / 1.0)) },
              // Top-Holding Risk: single-name concentration
              { label:"Name Risk",      score: Math.min(100, Math.round(top1Pct * 2.2)) },
            ];

            const riskMetrics = [
              {label:"HHI Concentration",val:hhi.toFixed(0),sub:hhiLabel+" concentration",color:hhiColor,icon:"🎯",detail:"Herfindahl–Hirschman Index. <1000 = diversified, >2500 = concentrated."},
              {label:"Illiquidity Ratio",val:`${illiqPct.toFixed(1)}%`,sub:"Unlisted / private assets",color:illiqPct>50?"#F87171":illiqPct>30?"#FBBF24":"#34D399",icon:"🔒",detail:"% of portfolio in unlisted, private or non-tradeable assets."},
              {label:"Top 1 Holding",val:`${top1Pct.toFixed(1)}%`,sub:sorted[0]?.name||"—",color:top1Pct>25?"#F87171":top1Pct>15?"#FBBF24":"#34D399",icon:"🏆",detail:"Single largest holding as % of total portfolio value."},
              {label:"Top 5 Holdings",val:`${top5Pct.toFixed(1)}%`,sub:"Of total portfolio",color:top5Pct>60?"#F87171":top5Pct>40?"#FBBF24":"#34D399",icon:"📊",detail:"Top 5 holdings combined as % of portfolio."},
              {label:"Asset-Class Vol",val:`${annVol.toFixed(1)}%`,sub:"Weighted annual vol",color:annVol>40?"#F87171":annVol>20?"#FBBF24":"#34D399",icon:"📈",detail:"Value-weighted blend of typical annual volatility per asset class."},
              {label:"Sharpe Proxy",val:sharpe,sub:`rf=5%, n=${validGains.length} holdings`,color:parseFloat(sharpe)>1?"#34D399":parseFloat(sharpe)>0?"#FBBF24":"#F87171",icon:"⚖️",detail:"(Avg listed holding return − 5% rf) ÷ cross-sectional std dev. Excludes zero-cost holdings."},
              {label:"Asset Classes",val:new Set(riskHW.map(h=>h.class)).size,sub:"Distinct classes",color:T.brand,icon:"🗂️",detail:"Number of distinct asset classes."},
            ];

            // SVG Radar / Spider chart — rendered inline to avoid inner-component Babel issues
            const radarSvg = (()=>{
              const n = riskFactors.length;
              const r = 68; const cx = 125, cy = 118; const svgW = 250, svgH = 236;
              const levels = [25,50,75,100];
              const toPoint = (angle, value) => {
                const rad = (angle - 90) * Math.PI / 180;
                const dist = (value / 100) * r;
                return { x: cx + dist * Math.cos(rad), y: cy + dist * Math.sin(rad) };
              };
              const angleStep = 360 / n;
              const dataPoints = riskFactors.map((f,i) => toPoint(i * angleStep, f.score));
              const dataPath = dataPoints.map((p,i) => `${i===0?"M":"L"}${p.x},${p.y}`).join(" ") + "Z";
              const gridPoints = (level) => riskFactors.map((_,i) => toPoint(i * angleStep, level));
              return (
                <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{width:"100%",maxHeight:280,height:"auto",display:"block"}}>
                  {levels.map((l,li)=>{
                    const pts = gridPoints(l);
                    return <polygon key={l} points={pts.map(p=>`${p.x},${p.y}`).join(" ")}
                      fill={li%2===0?"rgba(99,120,200,0.04)":"none"}
                      stroke="rgba(99,120,200,0.18)" strokeWidth={1}/>;
                  })}
                  {[25,50,75].map(l=>{
                    const pt = toPoint(0, l);
                    return <text key={l} x={pt.x+3} y={pt.y+3} fontSize={7} fill="rgba(139,153,187,0.5)">{l}</text>;
                  })}
                  {riskFactors.map((_,i)=>{
                    const outer = toPoint(i*angleStep, 100);
                    return <line key={i} x1={cx} y1={cy} x2={outer.x} y2={outer.y} stroke="rgba(99,120,200,0.2)" strokeWidth={1}/>;
                  })}
                  <path d={dataPath} fill="rgba(129,140,248,0.15)" stroke="#818CF8" strokeWidth={2}/>
                  {dataPoints.map((p,i)=>(
                    <circle key={i} cx={p.x} cy={p.y} r={3} fill="#818CF8" stroke="rgba(11,14,26,0.9)" strokeWidth={1.5}/>
                  ))}
                  {riskFactors.map((f,i)=>{
                    const outerPt = toPoint(i*angleStep, 100);
                    const dx = outerPt.x - cx, dy = outerPt.y - cy;
                    const len = Math.sqrt(dx*dx+dy*dy) || 1;
                    const lx = cx + (dx/len)*(r+30);
                    const ly = cy + (dy/len)*(r+30);
                    const parts = f.label.split(" ");
                    const line1 = parts[0], line2 = parts.slice(1).join(" ");
                    const boxW = Math.max(line1.length, line2?line2.length:0)*5.8+12;
                    const boxH = line2?22:14; const boxY = ly - boxH/2;
                    return (
                      <g key={i}>
                        <rect x={lx-boxW/2} y={boxY} width={boxW} height={boxH} rx={4}
                          fill="rgba(13,17,36,0.9)" stroke="rgba(99,120,200,0.3)" strokeWidth={0.7}/>
                        <text x={lx} y={boxY+(line2?9:9)} textAnchor="middle" fontSize={8.5} fill="#C7D2FE" fontWeight="700">{line1}</text>
                        {line2 && <text x={lx} y={boxY+19} textAnchor="middle" fontSize={8.5} fill="#C7D2FE" fontWeight="700">{line2}</text>}
                        <text x={dataPoints[i].x} y={dataPoints[i].y-6} textAnchor="middle" fontSize={7.5} fill="#E8EEFF" fontWeight="700">{f.score}</text>
                      </g>
                    );
                  })}
                </svg>
              );
            })();

            // Risk Decomposition chart data
            const decompMaxScore = Math.max(...riskFactors.map(f=>f.score), 1);
            const decompColors = ["#FBBF24","#34D399","#34D399","#60A5FA","#A78BFA"];

            return (
              <div>
                {/* Top KPI strip — VaR & drawdown like reference */}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:12,marginBottom:20}}>
                  {[
                    {label:"Weighted Vol (Ann.)",val:`${annVol.toFixed(1)}%`,sub:"Asset-class blended vol",color:"#818CF8",icon:"📊",iconBg:"rgba(129,140,248,0.15)"},
                    {label:"VaR (95%, Daily)",val:`-${var95.toFixed(2)}%`,sub:"Daily loss at 95% conf.",color:"#F87171",icon:"⚠️",iconBg:"rgba(248,113,113,0.15)"},
                    {label:"VaR (99%, Daily)",val:`-${var99.toFixed(2)}%`,sub:"Daily loss at 99% conf.",color:"#F87171",icon:"🚨",iconBg:"rgba(248,113,113,0.15)"},
                    {label:"Est. Max Drawdown",val:`-${maxDrawdown.toFixed(1)}%`,sub:"~2× annual vol heuristic",color:"#F87171",icon:"📉",iconBg:"rgba(248,113,113,0.15)"},
                  ].map((k,i)=>(
                    <div key={i} style={{...card(),position:"relative",overflow:"hidden"}}>
                      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${k.color}80,transparent)`,borderRadius:"16px 16px 0 0"}}/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,marginTop:4}}>
                        <div style={{fontSize:10.5,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600}}>{k.label}</div>
                        <div style={{width:32,height:32,borderRadius:8,background:k.iconBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>{k.icon}</div>
                      </div>
                      <div style={{fontSize:isMobile?22:26,fontWeight:500,color:k.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{k.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:16,alignItems:"start"}}>
                  <div style={card()}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <span style={{fontSize:16}}>🔴</span>
                      <span style={{fontSize:13,fontWeight:700}}>Risk Factor Radar</span>
                    </div>
                    {radarSvg}
                  </div>
                  <div style={{...card(),display:"flex",flexDirection:"column"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                      <span style={{fontSize:16}}>📊</span>
                      <span style={{fontSize:13,fontWeight:700}}>Risk Decomposition</span>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {riskFactors.map((f,i)=>{
                        const col = decompColors[i % decompColors.length];
                        const pct = (f.score / decompMaxScore) * 100;
                        const lvlLbl = f.score < 33 ? "Low" : f.score < 66 ? "Moderate" : "High";
                        const lvlCol = f.score < 33 ? "#34D399" : f.score < 66 ? "#FBBF24" : "#F87171";
                        return (
                          <div key={i}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                              <span style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{f.label}</span>
                              <div style={{display:"flex",alignItems:"center",gap:7}}>
                                <span style={{fontSize:10,fontWeight:600,color:lvlCol,background:`${lvlCol}18`,border:`1px solid ${lvlCol}40`,borderRadius:10,padding:"1px 8px"}}>{lvlLbl}</span>
                                <span style={{fontSize:12,fontWeight:700,color:col,fontFamily:"'Inter', system-ui, sans-serif",minWidth:26,textAlign:"right"}}>{f.score}</span>
                              </div>
                            </div>
                            <div style={{height:7,background:T.borderSubtle,borderRadius:4,overflow:"hidden"}}>
                              <div style={{width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${col},${col}99)`,borderRadius:4,boxShadow:`0 0 8px ${col}50`}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${T.borderSubtle}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:11,color:T.textMuted}}>Overall Risk Score</span>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:72,height:5,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                          <div style={{width:`${Math.min(unifiedScore,100)}%`,height:"100%",background:"linear-gradient(90deg,#34D399,#FBBF24,#F87171)",borderRadius:3}}/>
                        </div>
                        <span style={{fontSize:13,fontWeight:700,color:riskColor,fontFamily:"'Inter', system-ui, sans-serif"}}>
                          {unifiedScore}<span style={{fontSize:9,color:T.textMuted,fontWeight:400}}>/100</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Assessment Cards — like reference */}
                <div style={{...card(),marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                    <span style={{fontSize:16}}>⚠️</span>
                    <span style={{fontSize:13,fontWeight:700}}>Risk Assessment</span>
                  </div>
                  {(function(){
                    const levelLabel = (s) => s<33?"Low":s<66?"Moderate":"High";
                    const levelColor = (s) => s<33?"#34D399":s<66?"#FBBF24":"#F87171";
                    // All scores derived from the unified engine — same sub-factors, scaled to 0-100
                    const concScore   = Math.min(100, Math.round(f1 / 30 * 100));       // 0-100 from HHI
                    const top5Score   = Math.min(100, Math.round(f2 / 25 * 100));       // 0-100 from top-5
                    const illiqScore  = Math.min(100, Math.round(illiqPct * 1.0));      // 0-100 direct
                    const drawScore   = Math.min(100, Math.round(maxDrawdown * 1.43));  // 0-100: 70% drawdown → 100
                    const betaScore   = Math.min(100, Math.round((beta - 0.1) / 2.4 * 100)); // 0-100 scaled
                    // foreignCcyPct = % of portfolio NOT in the display/base currency
                    // High foreign exposure = HIGH risk; high base-ccy concentration = LOW risk
                    const foreignPct  = Math.max(0, 100 - (riskHW.reduce((s,h)=>h.currency===displayCcy?s+h.valueUSD:s,0)/totalV*100));
                    const ccyScore    = Math.min(100, Math.round(foreignPct * 0.85));
                    const numCcys     = new Set(riskHW.map(h=>h.currency)).size;
                    const assessments = [
                      {icon:"🛡️", label:"Overall Risk",       score:unifiedScore,  detail:`Score: ${unifiedScore}/100 — Concentration(${f1}/30) · Top-5(${f2}/25) · Illiquidity(${f3}/20) · Volatility(${f4}/25)`},
                      {icon:"⬇️", label:"Drawdown Risk",       score:drawScore,     detail:`Est. max drawdown: −${maxDrawdown.toFixed(1)}% (~2× annual vol of ${annVol.toFixed(1)}%)`},
                      {icon:"⇄",  label:"Concentration Risk",  score:concScore,     detail:`HHI: ${hhi.toFixed(0)} (${hhi<1000?"Diversified":hhi<2500?"Moderate":"Concentrated"}) · Largest: ${top1Pct.toFixed(1)}% in ${sorted[0]?.name||"—"}`},
                      {icon:"💧", label:"Liquidity Risk",       score:illiqScore,   detail:`${illiqPct.toFixed(1)}% of portfolio in unlisted/private assets · ${(100-illiqPct).toFixed(1)}% listed & liquid`},
                      {icon:"⚖️", label:"Equity Exposure",     score:betaScore,    detail:`${(riskHW.filter(h=>["Equities","Indian Equities"].includes(h.class)).reduce((s,h)=>s+h.valueUSD,0)/totalV*100).toFixed(1)}% in equities · beta proxy vs global market`},
                      {icon:"💱", label:"FX Risk",              score:ccyScore,     detail:`${foreignPct.toFixed(1)}% in non-${displayCcy} currencies · ${numCcys} currencies held`},
                    ];
                    return (
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
                        {assessments.map((a,i)=>{
                          const lbl = levelLabel(a.score);
                          const col = levelColor(a.score);
                          return (
                            <div key={i} style={{background:T.surfaceBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"14px 16px"}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                                <div style={{display:"flex",alignItems:"center",gap:8}}>
                                  <div style={{width:28,height:28,borderRadius:8,background:`${col}20`,border:`1px solid ${col}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{a.icon}</div>
                                  <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{a.label}</span>
                                </div>
                                <span style={{...badge(col),fontSize:10}}>{lbl}</span>
                              </div>
                              <div style={{fontSize:11,color:T.textMuted}}>{a.detail}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Original detailed metrics cards */}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:12,marginBottom:16}}>
                  {riskMetrics.map(m=>(
                    <div key={m.label} style={{...card(),position:"relative",overflow:"hidden"}} title={m.detail}>
                      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${m.color},${m.color}60)`,borderRadius:"16px 16px 0 0"}}/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,marginTop:4}}>
                        <div style={{fontSize:20}}>{m.icon}</div>
                        <div style={{fontSize:10,color:T.textMuted,background:T.borderSubtle,padding:"1px 6px",borderRadius:8}}>{m.sub.split(" ").slice(-1)[0]}</div>
                      </div>
                      <div style={{fontSize:22,fontWeight:700,color:m.color,fontFamily:"'Inter', system-ui, sans-serif"}}>{m.val}</div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:4,fontWeight:600}}>{m.label}</div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>{m.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Risk score gauge */}
                <div style={{...card(),marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Overall Risk Profile</div>
                  {(function(){
                    // ── Properly calibrated 4-factor risk score ──
                    // F1: HHI concentration  0-30 pts (sqrt scale — less sensitive at low end)
                    // F2: Top-5 weight       0-25 pts
                    // F3: Illiquidity        0-20 pts
                    // F4: Asset-class vol    0-25 pts (weighted blend from CLASS_VOL_RISK)
                    // Use the unified score computed above (f1+f2+f3+f4)
                    const score = unifiedScore;
                    // Client target profile (stored in their profile data)
                    const clientTargetProfile = (() => {
                      // Determine which single client is in view (if any)
                      const cId = activeRiskOpt?.type === "client"
                        ? activeRiskOpt.id          // admin drilled into a single client
                        : (!isAdmin ? loggedInClientId : null); // client login
                      if (!cId) return null;
                      const c = clients.find(x => x.id === cId);
                      return c?.riskProfile || null;
                    })();
                    const profileColors = { Conservative:"#34D399", Moderate:"#818CF8", "Moderate-Aggressive":"#FBBF24", Aggressive:"#F87171" };
                    return (
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 2fr",gap:isMobile?10:20,alignItems:"center"}}>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:52,fontWeight:500,color:riskColor,fontFamily:"'Inter', system-ui, sans-serif",lineHeight:1}}>{score}</div>
                          <div style={{fontSize:13,color:riskColor,fontWeight:700,marginTop:6}}>{riskLevel}</div>
                          <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Portfolio Risk Score</div>
                          {clientTargetProfile && (
                            <div style={{marginTop:8,padding:"4px 10px",borderRadius:8,background:`${profileColors[clientTargetProfile]||"#818CF8"}20`,border:`1px solid ${profileColors[clientTargetProfile]||"#818CF8"}40`,display:"inline-block"}}>
                              <span style={{fontSize:10,color:T.textSecondary}}>Target: </span>
                              <span style={{fontSize:11,fontWeight:700,color:profileColors[clientTargetProfile]||"#818CF8"}}>{clientTargetProfile}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:11,color:T.textSecondary}}>
                            <span>Conservative (0)</span><span>Moderate (33)</span><span>Aggressive (66+)</span>
                          </div>
                          <div style={{height:12,background:"linear-gradient(90deg,#34D399 0%,#FBBF24 50%,#F87171 100%)",borderRadius:6,position:"relative"}}>
                            <div style={{position:"absolute",top:"50%",left:`${Math.min(score,99)}%`,transform:"translate(-50%,-50%)",width:20,height:20,borderRadius:"50%",background:"white",border:`3px solid ${riskColor}`,boxShadow:`0 2px 8px ${riskColor}60`}}/>
                          </div>
                          <div style={{marginTop:12,display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
                            {[
                              {label:"Concentration",val:f1,max:30,color:"#F87171",  tip:`HHI ${hhi.toFixed(0)}`},
                              {label:"Top-5 Weight", val:f2,max:25,color:"#FBBF24",  tip:`${top5Pct.toFixed(1)}%`},
                              {label:"Illiquidity",  val:f3,max:20,color:"#A78BFA",  tip:`${illiqPct.toFixed(1)}%`},
                              {label:"Asset Vol",    val:f4,max:25,color:"#60A5FA",  tip:`${annVol.toFixed(1)}%`},
                            ].map(({label,val,max,color,tip})=>(
                              <div key={label} style={{textAlign:"center",padding:"6px 4px",background:T.tableStripe,borderRadius:6}}>
                                <div style={{fontSize:15,fontWeight:500,color,fontFamily:"'Inter', system-ui, sans-serif"}}>{val}<span style={{fontSize:9,color:T.textMuted}}>/{max}</span></div>
                                <div style={{fontSize:9,color:T.textSecondary,marginTop:2}}>{label}</div>
                                <div style={{fontSize:9,color:T.textMuted,marginTop:1}}>{tip}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Risk by asset class */}
                <div style={card()}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Risk Contribution by Asset Class</div>
                  <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
                    <thead><tr>{["Asset Class","Value","Weight","Holdings","Liquidity","Risk Flag"].map(h=><th key={h} style={th()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {Object.entries(riskHW.reduce((a,h)=>{
                        if(!a[h.class]) a[h.class]={value:0,listed:0,total:0};
                        a[h.class].value+=h.valueUSD; a[h.class].total++;
                        if(h.listed) a[h.class].listed++;
                        return a;
                      },{})).sort((a,b)=>b[1].value-a[1].value).map(([cls,d],i)=>{
                        const wt=(d.value/totalV*100);
                        const liqPct=(d.listed/d.total*100);
                        const flag = wt>25?"⚠ Overweight":liqPct===0?"🔒 Illiquid":wt>15?"🟡 Monitor":"✓ OK";
                        const flagColor = wt>25?"#F87171":liqPct===0?"#FBBF24":wt>15?"#FBBF24":"#34D399";
                        return (
                          <tr key={cls} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={td({fontWeight:600})}>{cls}</td>
                            <td style={td({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(convert(d.value,dc),dc)}</td>
                            <td style={td()}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{flex:1,height:5,background:T.borderSubtle,borderRadius:3}}>
                                  <div style={{height:"100%",width:`${wt}%`,background:wt>25?"#F87171":wt>15?"#FBBF24":T.brand,borderRadius:3}}/>
                                </div>
                                <span style={{fontSize:12,minWidth:36,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif"}}>{wt.toFixed(1)}%</span>
                              </div>
                            </td>
                            <td style={td({textAlign:"center"})}>{d.total}</td>
                            <td style={td()}>
                              <span style={{color:liqPct===100?"#34D399":liqPct===0?"#F87171":"#FBBF24",fontSize:12}}>
                                {liqPct.toFixed(0)}% listed
                              </span>
                            </td>
                            <td style={td({color:flagColor,fontWeight:600,fontSize:12})}>{flag}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            );
            } catch(riskErr) {
              console.error("[Risk Overview] Render error:", riskErr);
              return (
                <div style={{padding:40, textAlign:"center", color:"#F87171"}}>
                  <div style={{fontSize:24, marginBottom:12}}>⚠️</div>
                  <div style={{fontWeight:700, marginBottom:6}}>Risk Analysis Error</div>
                  <div style={{fontSize:12, color:"#94A3B8"}}>{riskErr?.message || "Unknown error"}</div>
                </div>
              );
            }
          })()}

          {/* ── CONCENTRATION ── */}
          {riskTab==="concentration" && allowedRiskTabs["concentration"] && (function(){
            try {
            const totalV = riskHW.reduce((s,h)=>s+h.valueUSD,0)||1;
            const sorted = [...riskHW].sort((a,b)=>b.valueUSD-a.valueUSD);
            // Lorenz curve data
            // Gini & Lorenz need ASCENDING sort (smallest first)
            const sortedAsc = [...sorted].reverse();
            let cumPct=0;
            const lorenzPts = [[0,0],...sortedAsc.map((h,i)=>{
              cumPct+=h.valueUSD/totalV*100;
              return [(i+1)/sortedAsc.length*100, cumPct];
            })];
            // Gini coefficient: ranges 0 (equal) → 1 (max concentration)
            // Formula requires ascending sort by value
            const n=sortedAsc.length;
            const totalVal=sortedAsc.reduce((s,h)=>s+h.valueUSD,0)||1;
            const gini = n > 1
              ? Math.max(0, Math.min(1,
                  (2*sortedAsc.reduce((s,h,i)=>s+(i+1)*h.valueUSD,0)/(n*totalVal)) - (n+1)/n
                ))
              : 0;

            return (
              <div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:16}}>
                  {/* Lorenz curve SVG — compact */}
                  <div style={{...card(),padding:"14px 16px"}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:2}}>Lorenz Curve — Concentration</div>
                    <div style={{fontSize:10,color:T.textSecondary,marginBottom:8}}>Further from diagonal = more concentrated</div>
                    <svg viewBox="0 0 220 160" width="100%" style={{display:"block"}}>
                      {[0,25,50,75,100].map(v=>(
                        <g key={v}>
                          <line x1={22+v*1.76} y1={6} x2={22+v*1.76} y2={138} stroke={T.borderSubtle} strokeWidth="0.7"/>
                          <line x1={22} y1={138-v*1.32} x2={198} y2={138-v*1.32} stroke={T.borderSubtle} strokeWidth="0.7"/>
                          <text x={22+v*1.76} y={150} textAnchor="middle" fill={T.textSecondary} fontSize="7">{v}%</text>
                          <text x={16} y={141-v*1.32} textAnchor="end" fill={T.textSecondary} fontSize="7">{v}%</text>
                        </g>
                      ))}
                      <line x1={22} y1={138} x2={198} y2={6} stroke={T.pageBg} strokeWidth="1" strokeDasharray="3,2"/>
                      <polyline
                        fill="rgba(248,113,113,0.08)"
                        stroke="#A32D2D"
                        strokeWidth="2"
                        points={lorenzPts.map(([x,y])=>`${22+x*1.76},${138-y*1.32}`).join(" ")}
                      />
                      <text x={110} y={5} textAnchor="middle" fill={T.thText} fontSize="7">% of Holdings</text>
                      <text x={6} y={78} textAnchor="middle" fill={T.thText} fontSize="7" transform="rotate(-90,6,78)">% of Value</text>
                      <text x={175} y={62} fill="#A32D2D" fontSize="8" fontWeight="600">Lorenz</text>
                      <text x={175} y={72} fill="#A32D2D" fontSize="8" fontWeight="600">Curve</text>
                      <text x={130} y={108} fill={T.textMuted} fontSize="8">Equality</text>
                    </svg>
                  </div>
                  {/* Gini + top N */}
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <div style={{...tCard(),textAlign:"center"}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>Gini Coefficient</div>
                      <div style={{fontSize:44,fontWeight:500,color:gini>0.6?"#A32D2D":gini>0.4?"#D97706":"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif"}}>{gini.toFixed(2)}</div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:6}}>{gini>0.6?"Highly concentrated":gini>0.4?"Moderately concentrated":"Well diversified"}</div>
                    </div>
                    {[1,3,5,10].map(n=>{
                      const pct=sorted.slice(0,n).reduce((s,h)=>s+h.valueUSD/totalV*100,0);
                      return (
                        <div key={n} style={{...tCard(),padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:12,color:T.textSecondary}}>Top {n} holding{n>1?"s":""}</span>
                          <span style={{fontSize:14,fontWeight:700,color:pct>50?"#A32D2D":pct>30?"#D97706":"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif"}}>{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Holdings ranked by concentration */}
                <div style={card()}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Holdings Ranked by Weight</div>
                  <div style={{overflowX:"auto",overflowY:"auto",maxHeight:400}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:480}}>
                    <thead style={{position:"sticky",top:0,zIndex:2,background:T.thBg}}><tr>{["Rank","Asset","Class","Weight","Cumulative","Status"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {(function(){let cum=0; return sorted.map((h,i)=>{
                        const wt=h.valueUSD/totalV*100; cum+=wt;
                        return (
                          <tr key={h.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={tTd({color:T.textSecondary,fontFamily:"'Inter', system-ui, sans-serif",textAlign:"center"})}>{i+1}</td>
                            <td style={tTd({fontWeight:600})}>{h.name}<div style={{fontSize:10,color:T.textMuted}}>{h.ticker}</div></td>
                            <td style={tTd({fontSize:11})}><span style={badge()}>{h.class}</span></td>
                            <td style={tTd()}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{width:80,height:5,background:T.borderSubtle,borderRadius:3}}>
                                  <div style={{height:"100%",width:`${Math.min(wt*3,100)}%`,background:wt>20?"#A32D2D":wt>10?"#D97706":"#007878",borderRadius:3}}/>
                                </div>
                                <span style={{fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,fontWeight:600}}>{wt.toFixed(2)}%</span>
                              </div>
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:cum>80?"#A32D2D":cum>50?"#D97706":T.textSecondary})}>{cum.toFixed(1)}%</td>
                            <td style={tTd({fontSize:11})}>{wt>20?<span style={{color:"#A32D2D"}}>⚠ Dominant</span>:wt>10?<span style={{color:"#D97706"}}>🟡 Significant</span>:<span style={{color:"#1D9E75"}}>✓ Balanced</span>}</td>
                          </tr>
                        );
                      });})()}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            );
            } catch(e) {
              console.error("[Risk tab] render error:", e);
              return <div style={{padding:30,color:"#F87171",textAlign:"center"}}>⚠️ {e?.message||"Render error"}</div>;
            }
          })()}

          {/* ── STRESS TESTS ── */}
          {riskTab==="stress" && allowedRiskTabs["stress"] && (function(){
            try {
            const totalV = riskHW.reduce((s,h)=>s+h.valueUSD,0) || 1;
            // CLASS_PROXY maps any class to a canonical shock-key so nothing falls through to 0
            const CLASS_PROXY = {
              "Indian Equities":"Equities","Indian Mutual Funds":"Indian Mutual Funds",
              "Global Mutual Funds":"Equities","Bonds":"Fixed Income","Debt":"Fixed Income",
              "Gold":"Commodities","Cash / Bank Balance":"Cash","PPF":"Fixed Income",
              "Time Deposits":"Time Deposits","Art & Collectibles":"Alternatives",
              "Miscellaneous":"Alternatives","Alternatives":"Alternatives",
            };
            const resolveShock = (h, shocks) => {
              if (shocks[h.class] !== undefined) return shocks[h.class];
              const proxy = CLASS_PROXY[h.class];
              if (proxy && shocks[proxy] !== undefined) return shocks[proxy];
              return 0;
            };
            const scenarios = [
              { name:"2008 Global Financial Crisis", icon:"🏦",
                shocks:{ Equities:-55, "Indian Mutual Funds":-50, "Global Mutual Funds":-52, Crypto:-70, "Real Estate":-35, "Fixed Income":5, Bonds:5, Debt:5, Commodities:-30, Gold:-15, "Alternates":-30, "Market Neutral":-12, "Time Deposits":0,  Cash:0, PPF:0 },
                desc:"Peak-to-trough drawdowns from Sep 2008–Mar 2009" },
              { name:"COVID-19 Crash (Mar 2020)",    icon:"🦠",
                shocks:{ Equities:-34, "Indian Mutual Funds":-30, "Global Mutual Funds":-32, Crypto:-50, "Real Estate":-15, "Fixed Income":3, Bonds:2, Debt:2, Commodities:-40, Gold:5, "Alternates":-18, "Market Neutral":-8,  "Time Deposits":0, Cash:0, PPF:0 },
                desc:"S&P 500 peak-to-trough over 33 days" },
              { name:"Rising Rates (+300bps)",       icon:"📈",
                shocks:{ Equities:-20, "Indian Mutual Funds":-18, "Global Mutual Funds":-20, Crypto:-40, "Real Estate":-25, "Fixed Income":-18, Bonds:-15, Debt:-12, Commodities:5, Gold:-8, "Alternates":-12, "Market Neutral":-5,  "Time Deposits":8, Cash:3, PPF:2 },
                desc:"Scenario: 300bps rate hike across 12 months" },
              { name:"India Market Correction -30%", icon:"🇮🇳",
                shocks:{ Equities:-30, "Indian Mutual Funds":-28, "Indian Equities":-30, "Global Mutual Funds":-10, "Fixed Income":-5, Bonds:-4, Debt:-4,  PPF:0, "Time Deposits":0, Cash:0, Crypto:-15, Gold:3, "Real Estate":-8, Alternatives:-5 },
                ccyShock:{ INR:-8 },
                desc:"NSE/BSE broad correction, INR depreciates 8%" },
              { name:"USD Strengthens 15%",          icon:"💵",
                shocks:{ Equities:-5, "Indian Mutual Funds":-5, "Indian Equities":-5, "Real Estate":-5, "Fixed Income":-3, Bonds:-3, Debt:-3, Gold:8 },
                ccyShock:{ INR:-12, EUR:-10, GBP:-8, AED:0, SGD:-8, CHF:-5 },
                desc:"Dollar index +15%; impacts non-USD assets via FX" },
              { name:"Crypto Winter -80%",           icon:"🪙",
                shocks:{ Crypto:-80 },
                desc:"Bear market similar to 2018 or 2022" },
            ];
            return (
              <div>
                <div style={{fontSize:13,color:T.textSecondary,marginBottom:16,padding:"10px 14px",background:T.borderSubtle,borderRadius:8}}>
                  ⚠ Stress test scenarios apply historical or hypothetical shocks to portfolio positions. Results are illustrative only and not a forecast. Unlisted assets use proxy shocks for their asset class.
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
                  {scenarios.map(sc=>{
                    let loss=0;
                    riskHW.forEach(h=>{
                      const classShock = resolveShock(h, sc.shocks || {});
                      const ccyShock   = sc.ccyShock?.[h.currency] ?? 0;
                      loss += h.valueUSD * ((classShock + ccyShock) / 100);
                    });
                    const lossPct = isFinite(loss/totalV) ? (loss/totalV*100) : 0;
                    const severity = Math.abs(lossPct)>30?"Severe":Math.abs(lossPct)>15?"Moderate":"Mild";
                    const sevColor = Math.abs(lossPct)>30?"#A32D2D":Math.abs(lossPct)>15?"#D97706":"#1D9E75";
                    return (
                      <div key={sc.name} style={{...tCard(),borderLeft:`3px solid ${sevColor}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                          <div>
                            <div style={{fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
                              <span>{sc.icon}</span>{sc.name}
                            </div>
                            <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>{sc.desc}</div>
                          </div>
                          <span style={{background:`${sevColor}18`,border:`1px solid ${sevColor}44`,color:sevColor,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:5,whiteSpace:"nowrap"}}>{severity}</span>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                          <div style={{background:T.borderSubtle,borderRadius:7,padding:"10px 12px",textAlign:"center"}}>
                            <div style={{fontSize:10,color:T.textSecondary,marginBottom:4}}>Est. Loss (USD)</div>
                            <div style={{fontSize:18,fontWeight:700,color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(Math.abs(loss),"USD",true)}</div>
                          </div>
                          <div style={{background:T.borderSubtle,borderRadius:7,padding:"10px 12px",textAlign:"center"}}>
                            <div style={{fontSize:10,color:T.textSecondary,marginBottom:4}}>Portfolio Impact</div>
                            <div style={{fontSize:18,fontWeight:700,color:sevColor,fontFamily:"'Inter', system-ui, sans-serif"}}>{lossPct.toFixed(1)}%</div>
                          </div>
                        </div>
                        {/* Mini bar showing impact per exposed class */}
                        <div style={{fontSize:10,color:T.textMuted,marginBottom:6}}>Top exposed classes:</div>
                        {Object.entries(sc.shocks||{}).filter(([,v])=>Math.abs(v)>0).sort((a,b)=>a[1]-b[1]).slice(0,4).map(([cls,shock])=>{
                          // Match holdings by class or proxy
                          const exposure=riskHW.filter(h=>h.class===cls||(CLASS_PROXY[h.class]===cls)).reduce((s,h)=>s+h.valueUSD,0);
                          if(!exposure) return null;
                          return (
                            <div key={cls} style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textSecondary,marginBottom:3}}>
                              <span>{cls}</span>
                              <span style={{color:shock<0?"#A32D2D":"#1D9E75",fontWeight:600}}>{shock>0?"+":""}{shock}% → {fmt(exposure*shock/100,"USD",true)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
            } catch(e) {
              console.error("[Risk tab] render error:", e);
              return <div style={{padding:30,color:"#F87171",textAlign:"center"}}>⚠️ {e?.message||"Render error"}</div>;
            }
          })()}

          {/* ── CORRELATION ── */}
          {riskTab==="correlation" && allowedRiskTabs["correlation"] && (function(){
            try {
            const classes = [...new Set(riskHW.map(h=>h.class))];
            // Predefined correlation matrix (proxy — real impl needs price history)
            const CORR = {
              "Equities":          {Equities:1.00, "Fixed Income":-0.10, "Real Estate":0.55, Commodities:0.25, Crypto:0.35, Alternates:0.65, "Market Neutral":0.30, Cash:-0.05, "Art & Collectibles":0.10, "Time Deposits":-0.05, PPF:-0.10, "Indian Mutual Funds":0.90, Miscellaneous:0.20},
              "Fixed Income":      {"Fixed Income":1.00, Equities:-0.10, "Real Estate":0.15, Commodities:-0.05, Crypto:-0.15, Alternates:-0.05, "Market Neutral":0.20, Cash:0.30},
              "Real Estate":       {"Real Estate":1.00, Equities:0.55, "Fixed Income":0.15, Commodities:0.20, Crypto:0.15, Alternates:0.60},
              Commodities:         {Commodities:1.00, Equities:0.25, "Fixed Income":-0.05, Crypto:0.30, "Real Estate":0.20},
              Crypto:              {Crypto:1.00, Equities:0.35, "Fixed Income":-0.15, Commodities:0.30},
              "Alternates":        {"Alternates":1.00, Equities:0.65, "Real Estate":0.55, "Market Neutral":0.40},
              "Market Neutral":    {"Market Neutral":1.00, Equities:0.30, "Fixed Income":0.25, "Alternates":0.40},
              
              Cash:                {Cash:1.00, Equities:-0.05, "Fixed Income":0.30},
              "Art & Collectibles":{"Art & Collectibles":1.00, Equities:0.10},
              "Time Deposits":     {"Time Deposits":1.00, Cash:0.60, "Fixed Income":0.25},
              PPF:                 {PPF:1.00, Cash:0.40, "Fixed Income":0.20},
              "Indian Mutual Funds":{"Indian Mutual Funds":1.00, Equities:0.90},
              Miscellaneous:       {Miscellaneous:1.00, Equities:0.20},
            };
            const getCorr=(a,b)=>{ if(a===b) return 1; return CORR[a]?.[b] ?? CORR[b]?.[a] ?? 0.1; };
            const corrColor=(v)=>{
              if(v>=0.7) return "#A32D2D";
              if(v>=0.4) return "#D97706";
              if(v>=-0.1) return "#007878";
              return "#1D9E75";
            };
            return (
              <div>
                <div style={{fontSize:13,color:T.textSecondary,marginBottom:16,padding:"10px 14px",background:T.borderSubtle,borderRadius:8}}>
                  📐 Correlation matrix uses proxy values based on asset class characteristics. Production use requires historical price series. 🔴 High positive = move together · 🟡 Moderate · 🔵 Low · 🟢 Negative = hedge
                </div>
                <div style={{...tCard(),overflowX:"auto",marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Asset Class Correlation Matrix</div>
                  <table style={{borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr>
                        <th style={{...tTh(),minWidth:140,textAlign:"left"}}>Class</th>
                        {classes.map(c=><th key={c} style={{...tTh(),minWidth:80,maxWidth:80,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",fontSize:10}} title={c}>{c.length>10?c.slice(0,9)+"…":c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {classes.map((r,ri)=>(
                        <tr key={r} style={{background:ri%2===0?"transparent":"rgba(0,0,0,0.02)"}}>
                          <td style={tTd({fontWeight:600,fontSize:11})}>{r}</td>
                          {classes.map(c=>{
                            const v=getCorr(r,c);
                            const bg=r===c?T.borderSubtle:`${corrColor(v)}18`;
                            return (
                              <td key={c} style={{...td(),textAlign:"center",background:bg,padding:"6px 4px"}}>
                                <span style={{color:r===c?"#78716C":corrColor(v),fontWeight:r===c?400:600,fontSize:11}}>{v.toFixed(2)}</span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Diversification opportunities */}
                <div style={card()}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>🟢 Low / Negative Correlation Pairs (Diversification Opportunities)</div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
                    {classes.flatMap((a,ai)=>classes.slice(ai+1).map(b=>({a,b,v:getCorr(a,b)}))).filter(x=>x.v<0.2).sort((a,b)=>a.v-b.v).slice(0,6).map(({a,b,v})=>(
                      <div key={a+b} style={{background:"rgba(74,222,128,0.05)",border:`1px solid ${T.cardBorder}`,borderRadius:8,padding:"12px 14px"}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#1D9E75",marginBottom:4}}>Correlation: {v.toFixed(2)}</div>
                        <div style={{fontSize:12,color:T.textSecondary}}>{a}</div>
                        <div style={{fontSize:10,color:T.textMuted,margin:"3px 0"}}>↕</div>
                        <div style={{fontSize:12,color:T.textSecondary}}>{b}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
            } catch(e) {
              console.error("[Risk tab] render error:", e);
              return <div style={{padding:30,color:"#F87171",textAlign:"center"}}>⚠️ {e?.message||"Render error"}</div>;
            }
          })()}

          {riskTab==="sector" && (function(){
          const totalV = riskHW.reduce((s,h)=>s+h.valueUSD,0)||1;

          // Collect all sectors and asset classes
          const sectors = [...new Set(riskHW.map(h=>h.sector||"Unclassified"))].sort();
          const classes  = [...new Set(riskHW.map(h=>h.class||"Other"))].sort();

          // Build matrix: sector × class → % of total portfolio
          const matrix = {};
          riskHW.forEach(h => {
            const sec = h.sector||"Unclassified";
            const cls = h.class||"Other";
            if (!matrix[sec]) matrix[sec] = {};
            matrix[sec][cls] = (matrix[sec][cls]||0) + h.valueUSD/totalV*100;
          });

          // Row totals and column totals
          const rowTotals = {};
          const colTotals = {};
          sectors.forEach(s => {
            rowTotals[s] = classes.reduce((sum,c)=>sum+(matrix[s]?.[c]||0),0);
          });
          classes.forEach(c => {
            colTotals[c] = sectors.reduce((sum,s)=>sum+(matrix[s]?.[c]||0),0);
          });

          // Colour by % — green→blue→amber→red
          const cellCol = (pct) => {
            if (!pct||pct<0.1) return "transparent";
            if (pct<5)  return "rgba(0,121,193,0.12)";
            if (pct<10) return "rgba(37,99,235,0.18)";
            if (pct<20) return "rgba(217,119,6,0.22)";
            return "rgba(220,38,38,0.25)";
          };
          const cellTextCol = (pct) => {
            if (!pct||pct<0.1) return T.textMuted;
            if (pct<5)  return T.brand;
            if (pct<10) return "#2563EB";
            if (pct<20) return "#D97706";
            return "#A32D2D";
          };

          // Filter to only classes that have any holdings
          const activeClasses = classes.filter(c => colTotals[c] > 0);
          // Filter to only sectors with holdings
          const activeSectors = sectors.filter(s => rowTotals[s] > 0).sort((a,b)=>rowTotals[b]-rowTotals[a]);

          return (
            <div style={{animation:"fadeTab 0.12s ease"}}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>🌡 Sector Heatmap</div>
              <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                Portfolio weight by sector × asset class. Cell colour intensity = concentration risk.
              </div>
              {/* Legend */}
              <div style={{display:"flex",gap:12,marginBottom:12,flexWrap:"wrap",fontSize:10}}>
                {[["<5%","rgba(0,120,120,0.2)",T.brand],["5–10%","rgba(37,99,235,0.25)","#2563EB"],
                  ["10–20%","rgba(217,119,6,0.3)","#D97706"],[">20%","rgba(220,38,38,0.3)","#A32D2D"]].map(([l,bg,col])=>(
                  <span key={l} style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:24,height:12,background:bg,borderRadius:3,display:"inline-block"}}/>
                    <span style={{color:T.textSecondary}}>{l}</span>
                  </span>
                ))}
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{borderCollapse:"collapse",fontSize:11,minWidth:500}}>
                  <thead>
                    <tr>
                      <th style={{...tTh({textAlign:"left",padding:"8px 12px"}),minWidth:140}}>Sector</th>
                      {activeClasses.map(c=>(
                        <th key={c} style={{...tTh({padding:"8px 8px",textAlign:"center"}),maxWidth:80,fontSize:9}}>
                          {c.length>12?c.slice(0,12)+"…":c}
                        </th>
                      ))}
                      <th style={tTh({padding:"8px 10px",textAlign:"center",borderLeft:`2px solid ${T.cardBorder}`})}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSectors.map((s,i)=>(
                      <tr key={s} style={{background:i%2===0?"transparent":T.tableStripe,cursor:"pointer"}}
                        onClick={()=>{ setHoldingSearch(s); setTab("holdings"); }}>
                        <td style={{...tTd({fontWeight:600,padding:"8px 12px"}),maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                          title={`Click to filter holdings by ${s}`}>
                          {s}
                        </td>
                        {activeClasses.map(c=>{
                          const pct = matrix[s]?.[c]||0;
                          return (
                            <td key={c} style={{...tTd({padding:"6px 8px",textAlign:"center"}),
                              background:cellCol(pct)}}>
                              {pct>0.1?(
                                <span style={{fontWeight:600,fontFamily:"'Inter', system-ui, sans-serif",
                                  fontSize:11,color:cellTextCol(pct)}}>
                                  {pct.toFixed(1)}%
                                </span>
                              ):"—"}
                            </td>
                          );
                        })}
                        <td style={{...tTd({fontWeight:700,padding:"8px 10px",textAlign:"center",
                          borderLeft:`2px solid ${T.cardBorder}`,fontFamily:"'Inter', system-ui, sans-serif"}),
                          color:rowTotals[s]>20?"#A32D2D":rowTotals[s]>10?"#D97706":T.textPrimary}}>
                          {rowTotals[s].toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                    {/* Column totals */}
                    <tr style={{borderTop:`2px solid ${T.cardBorder}`,background:T.thBg}}>
                      <td style={tTh({padding:"8px 12px",textAlign:"left"})}>Total</td>
                      {activeClasses.map(c=>(
                        <td key={c} style={{...tTh({padding:"8px 8px",textAlign:"center"}),
                          fontFamily:"'Inter', system-ui, sans-serif",
                          color:colTotals[c]>30?"#A32D2D":colTotals[c]>15?"#D97706":T.thText}}>
                          {colTotals[c].toFixed(1)}%
                        </td>
                      ))}
                      <td style={{...tTh({padding:"8px 10px",textAlign:"center",borderLeft:`2px solid ${T.cardBorder}`}),
                        fontFamily:"'Inter', system-ui, sans-serif"}}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{marginTop:10,fontSize:10,color:T.textMuted}}>
                💡 Click any row to filter holdings by that sector
              </div>
            </div>
          );
        })()}

        {/* ── LOOK-THROUGH / ACCIDENTAL CONCENTRATION ──────────────────────
            Aggregates underlying exposure to individual stocks across all
            funds, MFs and direct holdings. Surfaces "accidental doubles."
        ─────────────────────────────────────────────────────────────────── */}
          {riskTab==="lookthrough" && (function(){
          const totalV = riskHW.reduce((s,h)=>s+h.valueUSD,0)||1;

          // Build ticker-level exposure map
          // For MFs/ETFs: use known top holdings; for direct: use the holding itself
          // KNOWN_FUND_HOLDINGS: top 5 holdings by approximate weight for popular funds
          const KNOWN_FUND_HOLDINGS = {
            // Nifty 50 ETFs / large-cap Indian funds — top 5 approximate weights
            "RELIANCE":   [["RELIANCE.NSE",18.5],["HDFC.NSE",10.2],["INFY.NSE",8.1],["ICICIBANK.NSE",6.8],["TCS.NSE",5.9]],
            "NIFTYBEES":  [["RELIANCE.NSE",11.0],["HDFC.NSE",8.2],["INFY.NSE",7.1],["ICICIBANK.NSE",6.1],["TCS.NSE",5.3]],
            "SPY":        [["AAPL",7.1],["MSFT",6.8],["NVDA",6.2],["AMZN",3.5],["META",2.5]],
            "QQQ":        [["AAPL",9.0],["MSFT",8.5],["NVDA",8.1],["AMZN",4.8],["META",4.2]],
            "ACWI":       [["AAPL",4.2],["MSFT",4.0],["NVDA",3.5],["AMZN",2.1],["META",1.8]],
          };

          // Aggregate exposure
          const exposure = {}; // { ticker: { totalPct, holdings: [{name, pct, direct}] } }
          riskHW.forEach(h => {
            const weight = h.valueUSD/totalV*100;
            const ticker = (h.ticker||"—").toUpperCase().split(".")[0]; // strip exchange suffix

            // Direct holding
            if (ticker && ticker !== "-" && ticker !== "—") {
              if (!exposure[ticker]) exposure[ticker] = { totalPct:0, holdings:[], name:h.name };
              exposure[ticker].totalPct += weight;
              exposure[ticker].holdings.push({ name:h.name, pct:weight, direct:true });
            }

            // Look-through: if this holding is a fund with known constituents
            const fundKey = Object.keys(KNOWN_FUND_HOLDINGS).find(k =>
              ticker.includes(k) || h.name.toUpperCase().includes(k)
            );
            if (fundKey) {
              KNOWN_FUND_HOLDINGS[fundKey].forEach(([constituent, constituentWt]) => {
                const lookPct = weight * (constituentWt/100);
                const cticker = constituent.split(".")[0];
                if (!exposure[cticker]) exposure[cticker] = { totalPct:0, holdings:[], name:cticker };
                exposure[cticker].totalPct += lookPct;
                exposure[cticker].holdings.push({ name:`${h.name} (via fund)`, pct:lookPct, direct:false });
              });
            }
          });

          // Find accidental doubles: tickers with exposure from 2+ sources
          const allTickers = Object.entries(exposure)
            .sort((a,b)=>b[1].totalPct-a[1].totalPct)
            .filter(([,v])=>v.totalPct>0.1);
          const doubles = allTickers.filter(([,v])=>v.holdings.length>1);
          const topDirect = allTickers.filter(([,v])=>v.holdings.some(h=>h.direct)).slice(0,15);

          return (
            <div style={{animation:"fadeTab 0.12s ease"}}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>🔍 Look-Through &amp; Accidental Concentration</div>
              <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                Aggregates underlying exposure across funds and direct holdings.
                Surfaces stocks you own through multiple vehicles simultaneously.
              </div>

              {/* Accidental doubles alert */}
              {doubles.length>0 && (
                <div style={{marginBottom:16,padding:"12px 16px",borderRadius:10,
                  background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#A32D2D",marginBottom:8}}>
                    ⚠ {doubles.length} accidental double{doubles.length!==1?"s":""} detected
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {doubles.slice(0,8).map(([ticker,v])=>(
                      <div key={ticker} style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.2)",
                        borderRadius:8,padding:"6px 10px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:"#A32D2D"}}>{v.name}</div>
                        <div style={{fontSize:10,color:"#A32D2D"}}>{v.totalPct.toFixed(2)}% total</div>
                        <div style={{fontSize:9,color:T.textMuted}}>{v.holdings.map(h=>h.name.split(" ")[0]).join(" + ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top exposure table */}
              <div style={{...tCard(),padding:0,overflow:"hidden",marginBottom:12}}>
                <div style={{padding:"10px 14px 8px",borderBottom:`1px solid ${T.borderSubtle}`,
                  fontSize:12,fontWeight:700}}>
                  Top Underlying Exposures (direct + look-through)
                </div>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>
                    {["Ticker","Name","Total Exposure","Sources","Type"].map(h=>(
                      <th key={h} style={tTh({padding:"8px 12px"})}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {topDirect.map(([ticker,v],i)=>{
                      const hasDouble = v.holdings.length>1;
                      const isHigh = v.totalPct>10;
                      const isMed  = v.totalPct>5;
                      return (
                        <tr key={ticker} style={{background:hasDouble?"rgba(220,38,38,0.03)":i%2===0?"transparent":T.tableStripe}}>
                          <td style={tTd({fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"})}>
                            {ticker}
                            {hasDouble&&<span style={{marginLeft:6,fontSize:9,background:"rgba(220,38,38,0.1)",color:"#A32D2D",padding:"1px 5px",borderRadius:3,fontWeight:700}}>Double</span>}
                          </td>
                          <td style={tTd({fontSize:11})}>{v.name}</td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,
                            color:isHigh?"#A32D2D":isMed?"#D97706":"#1D9E75"})}>
                            {v.totalPct.toFixed(2)}%
                            <div style={{width:"100%",height:3,background:T.borderSubtle,borderRadius:2,marginTop:2}}>
                              <div style={{height:"100%",width:`${Math.min(v.totalPct*3,100)}%`,
                                background:isHigh?"#A32D2D":isMed?"#D97706":"#1D9E75",borderRadius:2}}/>
                            </div>
                          </td>
                          <td style={tTd({fontSize:10,color:T.textSecondary})}>
                            {v.holdings.slice(0,2).map((h,i)=>(
                              <div key={i}>{h.name.slice(0,25)}{h.name.length>25?"…":""} <span style={{color:T.textMuted}}>({h.pct.toFixed(1)}%)</span></div>
                            ))}
                            {v.holdings.length>2&&<div style={{color:T.textMuted}}>+{v.holdings.length-2} more</div>}
                          </td>
                          <td style={tTd()}>
                            <span style={{...badge(),
                              background:v.holdings.some(h=>!h.direct)?"rgba(124,58,237,0.1)":"rgba(37,99,235,0.08)",
                              color:v.holdings.some(h=>!h.direct)?"#7C3AED":"#2563EB"}}>
                              {v.holdings.some(h=>!h.direct)?"Fund look-through":"Direct"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {topDirect.length===0&&(
                <div style={{padding:40,textAlign:"center",color:T.textMuted}}>
                  <div style={{fontSize:32,marginBottom:12}}>🔍</div>
                  <div style={{fontWeight:600}}>No ticker data available for look-through</div>
                  <div style={{fontSize:12,marginTop:4}}>Add tickers to your holdings for look-through analysis</div>
                </div>
              )}
              <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>
                💡 Fund constituents are approximate. Connect broker APIs for real-time look-through.
              </div>
            </div>
          );
        })()}

        </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in risk tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()} {/* end risk tab */}

        {/* ── FAMILIES ── */}
        {tab==="families" && <React.Fragment>

          {/* ── ENTITY ORG TREE ── */}
          {isAdmin && families.length>0 && (
            <div style={{...tCard(),marginBottom:20,padding:"16px 20px",overflow:"auto"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{fontSize:13,fontWeight:700}}>🌳 Entity Hierarchy</div>
                <div style={{fontSize:11,color:T.textSecondary}}>{families.length} famil{families.length!==1?"ies":"y"} · {visibleClients.length} member{visibleClients.length!==1?"s":""}</div>
              </div>
              <div style={{overflowX:"auto",paddingBottom:8}}>
                <div style={{display:"flex",gap:24,alignItems:"flex-start",minWidth:Math.max(600,families.length*220)}}>
                  {families.map(fam=>{
                    const famClients = visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id);
                    const famHoldings = filteredHW.filter(h=>famClients.some(c=>c.id===h.clientId));
                    const famAUM = famHoldings.reduce((s,h)=>s+h.valueUSD,0);
                    const totalAUM = filteredHW.reduce((s,h)=>s+h.valueUSD,0)||1;
                    const famPct = (famAUM/totalAUM*100);
                    return (
                      <div key={fam.id} style={{flex:"0 0 auto",minWidth:200,maxWidth:240}}>
                        {/* Family root node */}
                        <div style={{background:`${fam.color||T.brand}14`,border:`2px solid ${fam.color||T.brand}`,
                          borderRadius:12,padding:"10px 14px",marginBottom:0,position:"relative",
                          cursor:"pointer",transition:"box-shadow 0.15s"}}
                          onClick={()=>{ setSelectedFamily(String(fam.id)); setSelectedClients(new Set()); setViewAsClientId(null); setTab('summary'); }}>
                          <div style={{fontSize:20,marginBottom:4}}>{fam.avatar||"👨‍👩‍👧"}</div>
                          <div style={{fontSize:12,fontWeight:700,color:fam.color||T.brand}}>{fam.name}</div>
                          <div style={{fontSize:10,color:T.textSecondary,marginTop:2}}>
                            {fmt(convert(famAUM,dc),dc)} · {famPct.toFixed(1)}% of AUM
                          </div>
                          <div style={{fontSize:9,color:T.textMuted}}>{famHoldings.length} holdings · {famClients.length} member{famClients.length!==1?"s":""}</div>
                        </div>

                        {/* Vertical connector line */}
                        {famClients.length>0 && (
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                            <div style={{width:2,height:16,background:`${fam.color||T.brand}44`}}/>
                            {/* Horizontal branch */}
                            <div style={{display:"flex",alignItems:"flex-start",gap:6,position:"relative",width:"100%"}}>
                              {famClients.map((cli,ci)=>{
                                const cliHoldings = filteredHW.filter(h=>h.clientId===cli.id);
                                const cliAUM = cliHoldings.reduce((s,h)=>s+h.valueUSD,0);
                                const isSelected = selectedClients.has(cli.id);
                                const isViewAs   = viewAsClientId===cli.id;
                                return (
                                  <div key={cli.id} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
                                    {/* Vertical connector to child */}
                                    <div style={{width:2,height:12,background:`${fam.color||T.brand}33`}}/>
                                    {/* Client node */}
                                    <div style={{
                                      background:isViewAs?"rgba(124,58,237,0.1)":isSelected?`${fam.color||T.brand}10`:T.cardBg,
                                      border:`1.5px solid ${isViewAs?"#7C3AED":isSelected?fam.color||T.brand:T.cardBorder}`,
                                      borderRadius:10,padding:"8px 10px",width:"100%",
                                      cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}
                                      onClick={()=>{ setSelectedClients(new Set([String(cli.id)])); setSelectedFamily(String(fam.id)); setViewAsClientId(null); setTab('summary'); }}>
                                      <div style={{fontSize:16,marginBottom:2}}>{cli.avatar||"👤"}</div>
                                      <div style={{fontSize:11,fontWeight:600,color:isViewAs?"#7C3AED":T.textPrimary,
                                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                        {cli.name.split(" ")[0]}
                                      </div>
                                      <div style={{fontSize:9,color:T.textSecondary,marginTop:1}}>
                                        {fmt(convert(cliAUM,dc),dc)}
                                      </div>
                                      {/* View-as button */}
                                      <div style={{marginTop:4,display:"flex",gap:4,justifyContent:"center"}}>
                                        <button onClick={e=>{e.stopPropagation();setViewAsClientId(String(cli.id));setSelectedClients(new Set([String(cli.id)]));setSelectedFamily(String(fam.id));setTab("summary");}}
                                          title="View as this client"
                                          style={{background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.25)",
                                            borderRadius:4,color:"#7C3AED",padding:"1px 6px",cursor:"pointer",fontSize:9,fontWeight:600}}>
                                          👁
                                        </button>
                                        <button onClick={e=>{e.stopPropagation();setSelectedClients(new Set([String(cli.id)]));setSelectedFamily(String(fam.id));setViewAsClientId(null);setTab("holdings");}}
                                          title="View holdings"
                                          style={{background:"rgba(37,99,235,0.08)",border:"1px solid rgba(37,99,235,0.2)",
                                            borderRadius:4,color:"#2563EB",padding:"1px 6px",cursor:"pointer",fontSize:9,fontWeight:600}}>
                                          📊
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{marginTop:8,fontSize:10,color:T.textMuted}}>
                💡 Click a family node to filter the dashboard · 👁 = View as client · 📊 = Jump to holdings
              </div>
            </div>
          )}

          {/* ── Pending Self Sign-ups banner (admin only) ── */}
          {isAdmin && (function(){
            const pendingSignups  = clients.filter(c => (c.signupSource === "self" || c.status === "trial") && clientLogins[c.id]?.status === "pending_approval");
            const approvedSignups = clients.filter(c => (c.signupSource === "self" || c.status === "trial") && clientLogins[c.id]?.status !== "pending_approval");
            const selfSignups = [...pendingSignups, ...approvedSignups];
            if (!selfSignups.length) return null;

            // ── Assign client to a family ──
            const assignToFamily = async (clientId, familyId) => {
              if (!familyId) return;
              try {
                setClients(p => p.map(x => x.id === clientId ? {...x, familyId} : x));
                const { updateClient } = await import('./supabase').catch(()=>({}));
                // Use direct supabase update
                const { createClient: sc } = await import('./supabase').catch(()=>({}));
                // Update via updateHolding pattern — update clients table directly
                await fetch('/api/signup', {
                  method: 'PATCH',
                  headers: {'Content-Type':'application/json'},
                  body: JSON.stringify({ clientId, familyId }),
                }).catch(()=>{});
                // Also update via supabase client directly
                const supabaseUrl = "https://aelgwwapsldljmedjwvj.supabase.co";
                const anonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
                await fetch(`${supabaseUrl}/rest/v1/clients?id=eq.${clientId}`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    'apikey': anonKey,
                    'Authorization': `Bearer ${anonKey}`,
                    'Prefer': 'return=minimal',
                  },
                  body: JSON.stringify({ family_id: familyId }),
                });
                showToast("✓ Client assigned to family", "#1D9E75", 2000);
              } catch(e) {
                showToast("⚠ Failed to assign: " + e.message, "#A32D2D", 3000);
              }
            };

            const approveClient = async (c) => {
              const login = clientLogins[c.id] || {};
              const updated = { ...login, active: true, status: "approved", approvedAt: new Date().toISOString() };
              setClientLogins(p => ({ ...p, [c.id]: updated }));
              try {
                await upsertClientLogin(c.id, updated);
                showToast(`✓ ${c.name} approved — they can now log in`, "#1D9E75", 3000);
              } catch(e) { showToast("⚠ Failed to save: " + e.message, "#A32D2D", 3000); }
            };

            const rejectClient = async (c) => {
              if (!window.confirm(`Reject and permanently delete ${c.name}'s signup request?\n\nThis will remove their family, client record, and login.`)) return;
              try {
                // 1. Remove from local state immediately
                setClientLogins(p => { const n = {...p}; delete n[c.id]; return n; });
                setClients(p => p.filter(x => x.id !== c.id));
                setFamilies(p => p.filter(f => f.id !== c.familyId));

                // 2. Delete login record from Supabase
                const { createClient } = await import('./supabase').catch(() => ({}));
                const SUPABASE_URL = "https://aelgwwapsldljmedjwvj.supabase.co";
                const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";

                // Use existing supabase functions
                await deleteClient(c.id).catch(e => console.error("[reject] deleteClient:", e.message));
                if (c.familyId) {
                  // Only delete family if no other clients remain in it
                  const otherClientsInFamily = clients.filter(x => x.familyId === c.familyId && x.id !== c.id);
                  if (otherClientsInFamily.length === 0) {
                    await deleteFamily(c.familyId).catch(e => console.error("[reject] deleteFamily:", e.message));
                  }
                }

                showToast(`✓ ${c.name}'s signup rejected and removed`, "#1D9E75", 3000);
              } catch(e) {
                showToast("⚠ Failed to fully reject: " + e.message, "#A32D2D", 3000);
              }
            };

            return (
              <div style={{...tCard({borderColor: pendingSignups.length ? "rgba(245,158,11,0.5)" : "rgba(99,102,241,0.3)",
                background: pendingSignups.length ? "rgba(245,158,11,0.04)" : "rgba(99,102,241,0.03)"}),marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>{pendingSignups.length ? "🔔" : "🆕"}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color: pendingSignups.length ? "#D97706" : "#6366F1"}}>
                        {pendingSignups.length ? `🔔 ${pendingSignups.length} Pending Approval` : "Self Sign-ups"}
                        {approvedSignups.length > 0 && pendingSignups.length > 0 &&
                          <span style={{marginLeft:8,fontSize:11,color:T.textMuted,fontWeight:400}}>· {approvedSignups.length} approved</span>}
                      </div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                        {pendingSignups.length ? "These clients are waiting for your approval before they can log in" : "Clients registered via the sign-up page"}
                      </div>
                    </div>
                  </div>
                </div>
                {isMobile ? (
                  // Mobile: card layout instead of table
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
                    {selfSignups.map(c => {
                      const login    = clientLogins[c.id] || {};
                      const fam      = families.find(f=>f.id===c.familyId);
                      const isPending = login.status === "pending_approval";
                      const isRejected = login.status === "rejected";
                      const regDate  = login.createdAt ? new Date(login.createdAt).toLocaleDateString("en-SG",{day:"numeric",month:"short",year:"numeric"}) : "—";
                      return (
                        <div key={c.id} style={{border:`1px solid ${isPending?"rgba(245,158,11,0.3)":T.cardBorder}`,
                          borderRadius:8,padding:"10px 12px",
                          background:isPending?"rgba(245,158,11,0.03)":"transparent"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>
                                {isPending&&<span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:"#F59E0B",marginRight:6,verticalAlign:"middle"}}/>}
                                {c.name}
                              </div>
                              <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                                {login.loginId||"—"} · {fam?.name||login.familyName||"—"}
                              </div>
                            </div>
                            <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:4,
                              background:isPending?"rgba(245,158,11,0.12)":isRejected?"rgba(220,38,38,0.10)":"rgba(22,163,74,0.10)",
                              color:isPending?"#D97706":isRejected?"#A32D2D":"#1D9E75",flexShrink:0}}>
                              {isPending?"⏳ Pending":isRejected?"✕ Rejected":"✓ Approved"}
                            </span>
                          </div>
                          <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>Registered {regDate}</div>
                          <div style={{display:"flex",gap:6}}>
                            {isPending&&<React.Fragment>
                              <button onClick={()=>approveClient(c)}
                                style={{flex:1,fontSize:12,padding:"6px 0",borderRadius:5,border:"1px solid #1D9E75",
                                  background:"rgba(22,163,74,0.10)",color:"#1D9E75",cursor:"pointer",fontWeight:600}}>
                                ✓ Approve
                              </button>
                              <button onClick={()=>rejectClient(c)}
                                style={{flex:1,fontSize:12,padding:"6px 0",borderRadius:5,border:"1px solid #A32D2D",
                                  background:"rgba(220,38,38,0.06)",color:"#A32D2D",cursor:"pointer"}}>
                                ✕ Reject
                              </button>
                            </React.Fragment>}
                            {!isPending&&!isRejected&&(
                              <button onClick={()=>{setClientPlans(p=>({...p,[c.id]:{plan:"pro",gateway:"trial",trialEnd:new Date(Date.now()+14*86400000).toISOString(),updatedAt:new Date().toISOString()}}));}}
                                style={{fontSize:11,padding:"5px 12px",borderRadius:4,border:`1px solid ${T.cardBorder}`,
                                  background:"transparent",color:T.textSecondary,cursor:"pointer"}}>
                                Extend Trial
                              </button>
                            )}
                            {isRejected&&(
                              <button onClick={()=>approveClient(c)}
                                style={{fontSize:11,padding:"5px 12px",borderRadius:4,border:"1px solid #6366F1",
                                  background:"rgba(99,102,241,0.08)",color:"#6366F1",cursor:"pointer"}}>
                                Re-approve
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:T.thBg}}>
                    {["Name","Login ID","Family / Entity","Registered","Status","Action"].map(h=><th key={h} style={{...tTh(),textAlign:"left"}}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {selfSignups.map(c => {
                      const login    = clientLogins[c.id] || {};
                      const fam      = families.find(f=>f.id===c.familyId);
                      const isPending = login.status === "pending_approval";
                      const isRejected = login.status === "rejected";
                      const regDate  = login.createdAt ? new Date(login.createdAt).toLocaleDateString("en-SG",{day:"numeric",month:"short",year:"numeric"}) : "—";
                      return (
                        <tr key={c.id} style={{borderBottom:`1px solid ${T.tdBorder}`,
                          background: isPending ? "rgba(245,158,11,0.03)" : "transparent"}}>
                          <td style={tTd({fontWeight:600})}>
                            {isPending && <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:"#F59E0B",marginRight:6,verticalAlign:"middle"}}/>}
                            {c.name}
                          </td>
                          <td style={tTd({fontFamily:"'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",fontSize:11,color:T.textSecondary})}>{login.loginId || "—"}</td>
                          <td style={tTd({fontSize:11,color:T.textSecondary})}>{fam?.name || c.familyName || "—"}</td>
                          <td style={tTd({fontSize:11,color:T.textSecondary})}>{regDate}</td>
                          <td style={tTd()}>
                            <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:4,
                              background: isPending ? "rgba(245,158,11,0.12)" : isRejected ? "rgba(220,38,38,0.10)" : "rgba(22,163,74,0.10)",
                              color: isPending ? "#D97706" : isRejected ? "#A32D2D" : "#1D9E75"}}>
                              {isPending ? "⏳ Pending" : isRejected ? "✕ Rejected" : "✓ Approved"}
                            </span>
                          </td>
                          <td style={tTd()}>
                            <div style={{display:"flex",gap:6}}>
                              {isPending && (
                                <React.Fragment>
                                  <button onClick={()=>approveClient(c)}
                                    style={{fontSize:11,padding:"4px 12px",borderRadius:5,border:"1px solid #1D9E75",
                                      background:"rgba(22,163,74,0.10)",color:"#1D9E75",cursor:"pointer",fontWeight:600}}>
                                    ✓ Approve
                                  </button>
                                  <button onClick={()=>rejectClient(c)}
                                    style={{fontSize:11,padding:"4px 10px",borderRadius:5,border:"1px solid #A32D2D",
                                      background:"rgba(220,38,38,0.06)",color:"#A32D2D",cursor:"pointer"}}>
                                    ✕ Reject
                                  </button>
                                </React.Fragment>
                              )}
                              {!isPending && !isRejected && (
                                <React.Fragment>
                                  <button onClick={()=>{ setClientPlans(p=>({...p,[c.id]:{plan:"pro",gateway:"trial",trialEnd:new Date(Date.now()+14*86400000).toISOString(),updatedAt:new Date().toISOString()}})); }}
                                    style={{fontSize:10,padding:"3px 8px",borderRadius:4,border:`1px solid ${T.cardBorder}`,background:"transparent",color:T.textSecondary,cursor:"pointer"}}>
                                    Extend Trial
                                  </button>
                                </React.Fragment>
                              )}
                              {isRejected && (
                                <button onClick={()=>approveClient(c)}
                                  style={{fontSize:10,padding:"3px 8px",borderRadius:4,border:"1px solid #6366F1",
                                    background:"rgba(99,102,241,0.08)",color:"#6366F1",cursor:"pointer"}}>
                                  Re-approve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )}
              </div>
            );
          })()}

          <div style={{display:"flex",flexDirection:isMobile?"column":"row",justifyContent:"space-between",
            alignItems:isMobile?"flex-start":"center",gap:isMobile?10:0,marginBottom:20}}>
            <div>
              <div style={{fontSize:17,fontWeight:700}}>Family & Client Groups</div>
              <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>Organise clients into family units. Filter any tab by family or individual client.</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button onClick={()=>setAddClientOpen(p=>!p)}
                style={{background:"rgba(139,92,246,0.1)",border:"1px solid #7C3AED",borderRadius:8,
                  color:"#9333EA",padding:"8px 12px",cursor:"pointer",fontSize:12,fontWeight:600,
                  whiteSpace:"nowrap"}}>
                + Add Client
              </button>
              <button onClick={()=>{ setWizardStep(1); setWizardErr(""); setWizardOpen(true); }}
                style={{background:"#1D9E75",border:"none",
                  borderRadius:8,color:"white",padding:"8px 12px",cursor:"pointer",
                  fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5,
                  whiteSpace:"nowrap"}}>
                🚀 Onboard Client
              </button>
              <button onClick={()=>setAddFamilyOpen(p=>!p)}
                style={{background:"#1E293B",border:"none",
                  borderRadius:8,color:"white",padding:"8px 12px",cursor:"pointer",
                  fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>
                + New Family
              </button>
            </div>
          </div>

          {/* Add Family form */}
          {addFamilyOpen && (
            <div style={{...tCard(),marginBottom:16,borderColor:"#2563EB"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:12,color:"#2563EB"}}>Create New Family Group</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"1fr auto auto auto",gap:10,alignItems:"flex-end"}}>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Family Name</div><input style={inp()} value={newFamily.name} onChange={e=>setNewFamily(p=>({...p,name:e.target.value}))} placeholder="e.g. Kapoor Family"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Emoji</div><input style={{...inp(),width:60,textAlign:"center",fontSize:18}} value={newFamily.emoji} onChange={e=>setNewFamily(p=>({...p,emoji:e.target.value}))}/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Colour</div><input type="color" style={{...inp(),width:50,padding:2,cursor:"pointer"}} value={newFamily.color} onChange={e=>setNewFamily(p=>({...p,color:e.target.value}))}/></div>
                <div style={{display:"flex",gap:6,paddingBottom:2}}>
                  <button onClick={()=>{
                    if (!newFamily.name) return;
                    const id = "fam"+uid();
                    const newFam={id,...newFamily}; addFamily(newFam).catch(err=>console.error('[Supabase] addFamily:',err.message)); setFamilies(p=>[...p,newFam]);
                    setNewFamily({name:"",color:"#2563EB",emoji:"🏠"});
                    setAddFamilyOpen(false);
                  }} style={{background:"#1E40AF",border:"none",borderRadius:6,color:"white",padding:"8px 16px",cursor:"pointer",fontWeight:600}}>Create</button>
                  <button onClick={()=>setAddFamilyOpen(false)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 14px",cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Add Client form */}
          {addClientOpen && (
            <div style={{...tCard(),marginBottom:16,borderColor:"#7C3AED"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:12,color:"#9333EA"}}>Add Client / Member</div>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr auto",gap:10,alignItems:"flex-end"}}>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Full Name</div><input style={inp()} value={newClient.name} onChange={e=>setNewClient(p=>({...p,name:e.target.value}))} placeholder="Client name"/></div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Family</div>
                  <select style={inp()} value={newClient.familyId} onChange={e=>setNewClient(p=>({...p,familyId:e.target.value}))}>
                    <option value="">Select family</option>
                    {visibleFamilies.map(f=><option key={f.id} value={f.id}>{f.emoji} {f.name}</option>)}
                  </select>
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Role</div>
                  <select style={inp()} value={newClient.role} onChange={e=>setNewClient(p=>({...p,role:e.target.value}))}>
                    {["Primary","Spouse","Child","Entity","Dependent","Other"].map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div><div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Date of Birth</div><input type="date" style={inp()} value={newClient.dob} onChange={e=>setNewClient(p=>({...p,dob:e.target.value}))}/></div>
                <div style={{display:"flex",gap:6,paddingBottom:2}}>
                  <button onClick={()=>{
                    if (!newClient.name||!newClient.familyId) { showToast("Name and family required.","#D97706"); return; }
                    const initials = newClient.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                    const newCli={id:'c'+uid(),familyId:newClient.familyId,name:newClient.name,role:newClient.role,avatar:initials,dob:newClient.dob||null}; addClient(newCli).catch(err=>console.error('[Supabase] addClient:',err.message)); setClients(p=>[...p,newCli]);
                    setNewClient({familyId:"",name:"",role:"Primary",dob:""});
                    setAddClientOpen(false);
                  }} style={{background:"#7C3AED",border:"none",borderRadius:6,color:"white",padding:"8px 16px",cursor:"pointer",fontWeight:600}}>Add</button>
                  <button onClick={()=>setAddClientOpen(false)} style={{background:T.badgeBg,border:"none",borderRadius:6,color:T.textSecondary,padding:"8px 14px",cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* ── FAMILY DETAIL PANEL (drill-down) ── */}
          {viewingFamily && (function(){
            const fam = familyStats.find(f=>f.id===viewingFamily);
            if (!fam) return null;
            const famByClass    = fam.holdings.reduce((a,h)=>{ a[h.class]=(a[h.class]||0)+h.valueUSD; return a; },{});
            const famClassData  = Object.entries(famByClass).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value);
            const famByCurrency = fam.holdings.reduce((a,h)=>{ a[h.currency]=(a[h.currency]||0)+h.valueUSD; return a; },{});
            const famCurData    = Object.entries(famByCurrency).map(([label,value])=>({label,value}));
            return (
              <div style={{marginBottom:24,borderRadius:14,border:`2px solid ${fam.color}`,overflow:"hidden"}}>
                {/* Panel header */}
                <div style={{background:`linear-gradient(135deg,${fam.color}18,#FFFFFF)`,borderBottom:`1px solid ${fam.color}44`,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    <div style={{width:48,height:48,borderRadius:12,background:`${fam.color}22`,border:`2px solid ${fam.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{fam.emoji}</div>
                    <div>
                      <div style={{fontSize:18,fontWeight:700}}>{fam.name} — Detailed Portfolio</div>
                      <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>{fam.clients.length} members · {fam.holdings.length} holdings · {Object.keys(famByClass).length} asset classes</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <button onClick={()=>{ setSelectedFamily(String(fam.id)); setSelectedClients(new Set()); setViewAsClientId(null); setTab("holdings"); }} style={{background:`${fam.color}22`,border:`1px solid ${fam.color}`,borderRadius:7,color:fam.color,padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                      📋 Go to Holdings Tab
                    </button>
                    <button onClick={()=>setViewingFamily(null)} style={{background:T.badgeBg,border:"none",borderRadius:7,color:T.textSecondary,padding:"6px 14px",cursor:"pointer",fontSize:12}}>✕ Close</button>
                  </div>
                </div>

                <div style={{background:T.pageBg,padding:20}}>
                  {/* KPI row */}
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:14,marginBottom:20}}>
                    {[
                      {label:"Family AUM",   val:fmt(convert(fam.totalUSD,dc),dc),   col:fam.color},
                      {label:"Cost Basis",   val:fmt(convert(fam.costUSD,dc),dc),    col:T.textSecondary},
                      {label:"Abs Gain",     val:fmt(convert(fam.totalUSD-fam.costUSD,dc),dc), col:fam.gainPct>=0?"#1D9E75":"#A32D2D"},
                      {label:"Overall Return",val:fam.gainPct!=null?`${fam.gainPct>=0?"+":""}${fam.gainPct.toFixed(2)}%`:"—", col:(fam.gainPct??0)>=0?"#1D9E75":"#A32D2D"},
                    ].map(k=>(
                      <div key={k.label} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:"14px 16px"}}>
                        <div style={{fontSize:10,color:T.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>{k.label}</div>
                        <div style={{fontSize:20,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Charts row */}
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:20}}>
                    <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:16}}>
                      <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Asset Class Allocation</div>
                      <DonutChart data={famClassData} size={160}/>
                    </div>
                    <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:16}}>
                      <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Currency Exposure</div>
                      <DonutChart data={famCurData} size={160}/>
                    </div>
                  </div>

                  {/* Members breakdown */}
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:16,marginBottom:16}}>
                    <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Member Portfolios</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
                      {fam.clients.map(client=>{
                        const cHoldings = hwWithOwner.filter(h=>h.clientId===client.id);
                        const cAUM  = cHoldings.reduce((s,h)=>s+h.valueUSD,0);
                        const cCost = cHoldings.reduce((s,h)=>s+h.costUSD,0);
                        const cRet  = cCost>0 ? ((cAUM-cCost)/cCost)*100 : 0;
                        const cShare= fam.totalUSD>0 ? (cAUM/fam.totalUSD)*100 : 0;
                        const roleCol = ROLE_COLORS[client.role]||"#2563EB";
                        return (
                          <div key={client.id} style={{background:T.borderSubtle,borderRadius:9,padding:14,border:`1px solid ${roleCol}33`}}>
                            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                              <div style={{width:36,height:36,borderRadius:9,background:`${roleCol}22`,border:`1px solid ${roleCol}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:roleCol,flexShrink:0}}>{client.avatar||client.name?.slice(0,2).toUpperCase()||"?"}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                                  <button onClick={()=>{ setSelectedClients(new Set([String(client.id)])); setSelectedFamily(String(fam.id)); setViewAsClientId(null); setTab("holdings"); }} style={{background:"none",border:"none",color:T.textPrimary,cursor:"pointer",fontSize:13,fontWeight:600,padding:0,textAlign:"left"}}>{client.name}</button>
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                                  <span style={{fontSize:10,background:`${roleCol}22`,color:roleCol,padding:"1px 6px",borderRadius:4}}>{client.role}</span>
                                  {client.dob&&<span style={{fontSize:10,color:T.textMuted}}>Age {new Date().getFullYear()-parseInt(client.dob)}</span>}
                                </div>
                              </div>
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                              <div><div style={{fontSize:10,color:T.textMuted,marginBottom:2}}>AUM</div><div style={{fontSize:13,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:roleCol}}>{fmt(convert(cAUM,dc),dc,true)}</div></div>
                              <div><div style={{fontSize:10,color:T.textMuted,marginBottom:2}}>Return</div><div style={{fontSize:13,fontWeight:700,color:cRet>=0?"#1D9E75":"#A32D2D"}}>{cRet>=0?"+":""}{cRet.toFixed(1)}%</div></div>
                            </div>
                            {/* Share of family AUM bar */}
                            <div style={{fontSize:10,color:T.textMuted,marginBottom:4}}>Share of family AUM: {cShare.toFixed(1)}%</div>
                            <div style={{height:5,background:T.borderSubtle,borderRadius:3}}><div style={{width:`${cShare}%`,height:"100%",background:roleCol,borderRadius:3}}/></div>
                            {/* Mini holding list */}
                            {cHoldings.length>0 && (
                              <div style={{marginTop:10,borderTop:"1px solid #EDE0D4",paddingTop:8}}>
                                {cHoldings.slice(0,3).map(h=>(
                                  <div key={h.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"3px 0",borderBottom:"1px solid #FFFFFF"}}>
                                    <span style={{color:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{h.name}</span>
                                    <span style={{color:"#2563EB",fontFamily:"'Inter', system-ui, sans-serif",flexShrink:0,marginLeft:6}}>{fmt(convert(h.valueUSD,dc),dc)}</span>
                                  </div>
                                ))}
                                {cHoldings.length>3&&<div style={{fontSize:10,color:T.textLight,marginTop:4}}>+{cHoldings.length-3} more</div>}
                              </div>
                            )}
                            {/* Login status badge */}
                            {isAdmin && (
                              <div style={{marginTop:8,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 8px",background:"rgba(0,0,0,0.03)",borderRadius:6,border:`1px solid ${T.borderSubtle}`}}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{fontSize:10}}>{clientLogins[client.id]?.loginId ? "🔐" : "🔓"}</span>
                                  <span style={{fontSize:10,color:T.textSecondary}}>{clientLogins[client.id]?.loginId || "No login"}</span>
                                  {clientLogins[client.id]?.loginId && (
                                    <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:clientLogins[client.id]?.active!==false?"#DCFCE7":"#FEE2E2",color:clientLogins[client.id]?.active!==false?"#1D9E75":"#A32D2D"}}>
                                      {clientLogins[client.id]?.active!==false?"Active":"Inactive"}
                                    </span>
                                  )}
                                </div>
                                {!clientLogins[client.id]?.loginId ? (
                                  <button onClick={async()=>{
                                    const defaultLogin = client.name.split(" ")[0].toLowerCase()+"."+client.name.split(" ").slice(-1)[0].toLowerCase();
                                    const defaultPwd = client.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                                    const hashedPwd = await makePasswordHash(defaultPwd);
                                    const newCred = {loginId:defaultLogin, password:hashedPwd, active:true, riskTabs:{overview:false,concentration:false,stress:false,correlation:false}};
                                    upsertClientLogin(client.id, newCred).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                                    setClientLogins(p=>({...p,[client.id]:newCred}));
                                    setCredModal({name:client.name,loginId:defaultLogin,password:defaultPwd});
                                  }} style={{background:"rgba(16,185,129,0.12)",border:"1px solid #10B98155",borderRadius:4,color:"#059669",padding:"2px 8px",cursor:"pointer",fontSize:10,fontWeight:600}}>+ Create Login</button>
                                ) : (
                                  <button onClick={async()=>{
                                    const newPwd = client.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                                    const hashedPwd = await makePasswordHash(newPwd);
                                    const updated = {...clientLogins[client.id], password:hashedPwd};
                                    upsertClientLogin(client.id, updated).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                                    setClientLogins(p=>({...p,[client.id]:updated}));
                                    setCredModal({name:client.name,loginId:clientLogins[client.id].loginId,password:newPwd});
                                  }} style={{background:"rgba(245,158,11,0.1)",border:"1px solid #D9770644",borderRadius:4,color:"#D97706",padding:"2px 8px",cursor:"pointer",fontSize:10}}>Reset Pwd</button>
                                )}
                              </div>
                            )}
                            <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                              <button onClick={()=>{ setSelectedClients(new Set([String(client.id)])); setSelectedFamily(String(fam.id)); setViewAsClientId(null); setTab("holdings"); }} style={{flex:1,background:`${roleCol}11`,border:`1px solid ${roleCol}44`,borderRadius:6,color:roleCol,padding:"5px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                View Holdings →
                              </button>
                              {isAdmin && (
                                <button onClick={()=>{
                                  setViewAsClientId(client.id);
                                  setSelectedClients(new Set([client.id]));
                                  setSelectedFamily(fam.id);
                                  setTab("overview");
                                }} style={{flex:1,background:"rgba(124,58,237,0.1)",
                                  border:"1px solid rgba(124,58,237,0.3)",borderRadius:6,
                                  color:"#7C3AED",padding:"5px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                  👁 View as Client
                                </button>
                              )}
                              {isAdmin && <button onClick={()=>{ setKycViewClient(client.id); setProfileData(p=>({...p,_kycTab:"personal"})); }} style={{flex:1,background:"rgba(0,120,120,0.1)",border:"1px solid rgba(0,121,193,0.3)",borderRadius:6,color:T.brand,padding:"5px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                🆔 KYC Profile
                              </button>}
                              {(isAdmin || loggedInClientId===client.id) && <button onClick={()=>{ setNh(p=>({...p,assignedClientId:client.id})); setSelectedFamily(fam.id); setSelectedClients(new Set([client.id])); setTab("holdings"); setAddOpen(true); }} style={{flex:"0 0 100%",background:"#1E293B",border:"none",borderRadius:6,color:"white",padding:"5px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                + Add Holding
                              </button>}
                              {isAdmin && <button onClick={()=>{
                                const holdingCount = filteredHW.filter(h=>holdingOwners[String(h.id)]===client.id).length;
                                const msg = holdingCount > 0
                                  ? `Remove ${client.name}? They have ${holdingCount} holding(s) which will become unassigned.`
                                  : `Remove ${client.name} from ${fam.name}?`;
                                requireConfirm(msg, ()=>{
                                  deleteClient(client.id).catch(err=>console.error("[Supabase] deleteClient:",err.message));
                                  setClients(p=>p.filter(c=>c.id!==client.id));
                                  setHoldingOwners(p=>{ const n={...p}; Object.keys(n).forEach(k=>{ if(n[k]===client.id) delete n[k]; }); return n; });
                                  if (selectedClients.has(client.id)) { setSelectedClients(new Set()); }
                                  const remaining = clients.filter(c=>(c.familyId||c.family_id)===fam.id&&c.id!==client.id);
                                  if (remaining.length===0) { setSelectedFamily("all"); setSelectedClients(new Set()); }
                                  showToast("✓ Client removed","#1D9E75");
                                });
                              }} style={{flex:"0 0 100%",background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.25)",borderRadius:6,color:"#A32D2D",padding:"5px",cursor:"pointer",fontSize:11,fontWeight:500}}>
                                ✕ Remove Client
                              </button>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Full holdings table for family */}
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:16,overflowX:"auto"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                      <div style={{fontSize:13,fontWeight:600}}>All Holdings — {fam.name}</div>
                      {isAdmin && <button onClick={()=>{ setNh(p=>({...p,assignedClientId:""})); setSelectedFamily(fam.id); setSelectedClients(new Set()); setTab("holdings"); setAddOpen(true); }} style={{background:"#1E293B",border:"none",borderRadius:7,color:"white",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>+ Add Holding</button>}
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:isMobile?480:900}}>
                      <thead><tr>{["Asset","Class","Owner","Ccy","Cost","Value","P&L","Return %"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                      <tbody>
                        {[...fam.holdings].sort((a,b)=>b.valueUSD-a.valueUSD).map((h,i)=>(
                          <tr key={h.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={tTd({fontWeight:600})}><div>{h.name}</div><div style={{fontSize:10,color:T.textSecondary}}>{h.ticker!=="-"?h.ticker:"Private"}</div></td>
                            <td style={tTd()}><span style={badge()}>{h.class}</span></td>
                            <td style={tTd({fontSize:11})}>{h.client?<span style={{color:ROLE_COLORS[h.client.role]||"#2563EB",fontWeight:600}}>{h.client.name}</span>:<span style={{color:T.textMuted}}>—</span>}</td>
                            <td style={tTd()}>{h.currency}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>{fmt(convert(h.costUSD,dc),dc)}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>{fmt(convert(h.valueUSD,dc),dc)}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:h.gainPct>=0?"#1D9E75":"#A32D2D"})}>{fmt(convert(h.valueUSD-h.costUSD,dc),dc)}</td>
                            <td style={tTd()}><Pct v={h.gainPct}/></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Family cards grid — clicking card name opens detail panel */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(380px,1fr))",gap:20,marginBottom:24}}>
            {familyStats.map(fam=>{
              const isSelected   = selectedFamily===fam.id && selectedClients.size === 0;
              const isViewing    = viewingFamily===fam.id;
              return (
                <div key={fam.id} style={{...tCard(),border:`1px solid ${isSelected||isViewing?fam.color:T.textLight}`,position:"relative",overflow:"hidden"}}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:fam.color,borderRadius:"12px 12px 0 0"}}/>
                  {/* Header — click family name/emoji to open detail */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setViewingFamily(isViewing?null:fam.id)}>
                      <div style={{width:40,height:40,borderRadius:10,background:`${fam.color}22`,border:`1px solid ${fam.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{fam.emoji}</div>
                      <div>
                        <div style={{fontSize:15,fontWeight:700,color:isViewing?fam.color:T.textPrimary,transition:"color 0.2s"}}>{fam.name} {isViewing?"▲":"▼"}</div>
                        <div style={{fontSize:11,color:T.textSecondary}}>{fam.clients.length} member{fam.clients.length!==1?"s":""} · {fam.holdings.length} holdings</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button
                        onClick={()=>{ setSelectedFamily(isSelected?"all":fam.id); setSelectedClients(new Set()); }}
                        style={{background:isSelected?fam.color:"transparent",border:`1px solid ${fam.color}`,borderRadius:6,color:isSelected?"white":fam.color,padding:"4px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                        {isSelected?"✓ Filtered":"Filter"}
                      </button>
                      <button onClick={()=>setViewingFamily(isViewing?null:fam.id)} style={{background:isViewing?`${fam.color}22`:"transparent",border:`1px solid ${fam.color}55`,borderRadius:6,color:fam.color,padding:"4px 10px",cursor:"pointer",fontSize:12,fontWeight:600}} title="View detailed portfolio">
                        {isViewing?"✕":"📊"}
                      </button>
                      <button onClick={()=>{
                      const clientCount = visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).length;
                      const msg = clientCount > 0
                        ? `Delete "${fam.name}"? This family has ${clientCount} client(s). Their holdings will become unassigned. This cannot be undone.`
                        : `Delete "${fam.name}"? This cannot be undone.`;
                      requireConfirm(msg, ()=>{
                        visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).forEach(c=>{
                          deleteClient(c.id).catch(()=>{});
                        });
                        setClients(p=>p.filter(c=>c.familyId!==fam.id));
                        deleteFamily(fam.id).catch(err=>console.error('[Supabase] deleteFamily:',err.message));
                        setFamilies(p=>p.filter(f=>f.id!==fam.id));
                        if (selectedFamily===fam.id) { setSelectedFamily("all"); setSelectedClients(new Set()); }
                        showToast("✓ Family deleted","#1D9E75");
                      });
                    }} style={{background:"none",border:"1px solid rgba(220,38,38,0.35)",borderRadius:6,color:"#A32D2D",padding:"4px 8px",cursor:"pointer",fontSize:11}} title="Delete family">✕</button>
                    </div>
                  </div>

                  {/* AUM row */}
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"1fr 1fr 1fr",gap:10,marginBottom:14}}>
                    {[
                      {label:"Total AUM",val:fmt(convert(fam.totalUSD,dc),dc),col:"#2563EB"},
                      {label:"Total Cost",val:fmt(convert(fam.costUSD,dc),dc),col:T.textSecondary},
                      {label:"Return",val:fam.gainPct!=null?`${fam.gainPct>=0?"+":""}${fam.gainPct.toFixed(1)}%`:"—",col:(fam.gainPct??0)>=0?"#1D9E75":"#A32D2D"},
                    ].map(k=>(
                      <div key={k.label} style={{background:T.borderSubtle,borderRadius:7,padding:"8px 10px"}}>
                        <div style={{fontSize:10,color:T.textMuted,marginBottom:3,textTransform:"uppercase",letterSpacing:"0.04em"}}>{k.label}</div>
                        <div style={{fontSize:14,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Members — click to filter holdings tab */}
                  <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>Members</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {fam.clients.map(client=>{
                      const clientHoldings = hwWithOwner.filter(h=>h.clientId===client.id);
                      const clientAUM  = clientHoldings.reduce((s,h)=>s+h.valueUSD,0);
                      const clientCost = clientHoldings.reduce((s,h)=>s+h.costUSD,0);
                      const clientRet  = clientCost>0 ? ((clientAUM-clientCost)/clientCost)*100 : 0;
                      return (
                        <div key={client.id}
                          onClick={()=>{ setSelectedClients(new Set([String(client.id)])); setSelectedFamily(String(fam.id)); setViewAsClientId(null); setTab("holdings"); }}
                          style={{display:"flex",alignItems:"center",gap:8,padding:"4px 6px",background:selectedClients.has(client.id)?"rgba(37,99,235,0.08)":T.borderSubtle,borderRadius:8,cursor:"pointer",border:`1px solid ${selectedClients.has(client.id)?"#2563EB":"transparent"}`,transition:"all 0.15s"}}>
                          <div style={{width:32,height:32,borderRadius:8,background:`${ROLE_COLORS[client.role]||"#2563EB"}22`,border:`1px solid ${ROLE_COLORS[client.role]||"#2563EB"}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:ROLE_COLORS[client.role]||"#2563EB",flexShrink:0}}>{client.avatar||(client.name||"?").slice(0,2).toUpperCase()}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                              {client.name}
                              <span style={{fontSize:10,background:`${ROLE_COLORS[client.role]||"#2563EB"}22`,color:ROLE_COLORS[client.role]||"#2563EB",padding:"1px 6px",borderRadius:4}}>{client.role}</span>
                            </div>
                            {client.dob&&<div style={{fontSize:10,color:T.textMuted}}>DOB: {client.dob} · Age: {new Date().getFullYear()-parseInt(client.dob)}</div>}
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontSize:12,fontWeight:600,fontFamily:"'Inter', system-ui, sans-serif",color:"#2563EB"}}>{fmt(convert(clientAUM,dc),dc)}</div>
                            <div style={{fontSize:11,color:clientRet>=0?"#1D9E75":"#A32D2D"}}>{clientRet>=0?"+":""}{clientRet.toFixed(1)}%</div>
                          </div>
                          <div style={{fontSize:10,color:"#2563EB",flexShrink:0,fontWeight:600}}>→</div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{marginTop:10,display:"flex",gap:6}}>
                    <button onClick={()=>{ setNewClient(p=>({...p,familyId:fam.id})); setAddClientOpen(true); }} style={{flex:1,background:"none",border:"1px dashed #DDD0C0",borderRadius:6,color:T.textLight,padding:"6px",cursor:"pointer",fontSize:11}}>+ Add member</button>
                    <button onClick={()=>setViewingFamily(isViewing?null:fam.id)} style={{flex:1,background:`${fam.color}11`,border:`1px solid ${fam.color}44`,borderRadius:6,color:fam.color,padding:"6px",cursor:"pointer",fontSize:11,fontWeight:600}}>{isViewing?"Hide":"View"} Full Portfolio</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Consolidated family comparison table */}
          <div style={card()}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Family Comparison</div>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
              <thead><tr>{["Family","Members","Holdings","Total AUM","Cost Basis","Gain / Loss","Return","Largest Holding","Filter"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
              <tbody>
                {familyStats.map((fam,i)=>{
                  const topH = [...fam.holdings].sort((a,b)=>b.valueUSD-a.valueUSD)[0];
                  return (
                    <tr key={fam.id} style={{background:selectedFamily===fam.id?"rgba(59,130,246,0.05)":i%2===0?"transparent":"rgba(0,0,0,0.02)"}}>
                      <td style={tTd({fontWeight:700})}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:fam.color}}/>
                          {fam.emoji} {fam.name}
                        </div>
                      </td>
                      <td style={tTd()}>{fam.clients.length}</td>
                      <td style={tTd()}>{fam.holdings.length}</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600,color:"#2563EB"})}>{fmt(convert(fam.totalUSD,dc),dc)}</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(convert(fam.costUSD,dc),dc)}</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:fam.gainPct>=0?"#1D9E75":"#A32D2D"})}>{fmt(convert(fam.totalUSD-fam.costUSD,dc),dc)}</td>
                      <td style={tTd()}><Pct v={fam.gainPct}/></td>
                      <td style={tTd({fontSize:12})}>{topH?.name||"—"}</td>
                      <td style={tTd()}>
                        <button onClick={()=>{ setSelectedFamily(selectedFamily===fam.id?"all":fam.id); setSelectedClients(new Set()); }} style={{background:selectedFamily===fam.id?fam.color:"transparent",border:`1px solid ${fam.color}`,borderRadius:5,color:selectedFamily===fam.id?"white":fam.color,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>{selectedFamily===fam.id?"✓ On":"Filter"}</button>
                      </td>
                    </tr>
                  );
                })}
                {/* Grand total */}
                <tr style={{background:"rgba(96,165,250,0.05)",borderTop:"2px solid #DDD0C0"}}>
                  <td style={tTd({fontWeight:700,color:"#2563EB"})}>All Families</td>
                  <td style={tTd({fontWeight:600})}>{clients.length}</td>
                  <td style={tTd({fontWeight:600})}>{hwWithOwner.filter(h=>h.clientId).length}</td>
                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:"#2563EB"})}>{fmt(convert(totalUSD,dc),dc)}</td>
                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>{fmt(convert(totalCostUSD,dc),dc)}</td>
                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:gainUSD>=0?"#1D9E75":"#A32D2D",fontWeight:600})}>{fmt(convert(gainUSD,dc),dc)}</td>
                  <td style={tTd()}><Pct v={gainPct}/></td>
                  <td colSpan={2}/>
                </tr>
              </tbody>
            </table></div>
          </div>

          {/* Unassigned holdings */}
          {hwWithOwner.filter(h=>!h.clientId).length > 0 && (
            <div style={{...tCard(),marginTop:16,borderColor:"#D97706"}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:12,color:"#D97706"}}>⚠ Unassigned Holdings ({hwWithOwner.filter(h=>!h.clientId).length})</div>
              <div style={{fontSize:12,color:T.textSecondary,marginBottom:12}}>These holdings are not yet linked to any client. Click "Assign →" in the Holdings tab to assign them.</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {hwWithOwner.filter(h=>!h.clientId).map(h=>(
                  <div key={h.id} style={{background:T.dropdownBg,border:`1px solid ${T.border}`,borderRadius:7,padding:"6px 12px",fontSize:12}}>
                    <span style={{fontWeight:600}}>{h.name}</span>
                    <span style={{color:T.textSecondary,marginLeft:8}}>{fmt(convert(h.valueUSD,dc),dc)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </React.Fragment>}

        {/* ── PERFORMANCE ── */}
        {tab==="performance" && (function(){ try {
          const hw = filteredHW;
          const sortedPerf = [...hw].sort((a,b)=>{
            if (a.gainPct==null && b.gainPct==null) return 0;
            if (a.gainPct==null) return 1;  // nulls to bottom
            if (b.gainPct==null) return -1;
            return b.gainPct - a.gainPct;
          });

          // ── Data quality: cost price coverage ──
          const withCost    = hw.filter(h => h.costPrice > 0);
          const missingCost = hw.filter(h => !h.costPrice || h.costPrice === 0);
          const costCoverage = hw.length > 0 ? (withCost.length / hw.length * 100) : 100;
          const best = sortedPerf[0]; const worst = sortedPerf[sortedPerf.length-1];
          const totalGainUSD = hw.reduce((s,h)=>s+(h.valueUSD-h.costUSD),0);
          const totalCostUSD = hw.reduce((s,h)=>s+h.costUSD,0);
          const avgReturn = hw.length > 0 ? hw.reduce((s,h)=>s+(h.gainPct??0),0)/hw.length : 0;
          const winners = hw.filter(h=>h.gainPct>=0).length;
          const losers  = hw.filter(h=>h.gainPct<0).length;
          const byClass = hw.reduce((a,h)=>{
            if (!a[h.class]) a[h.class] = {valueUSD:0,costUSD:0};
            a[h.class].valueUSD += h.valueUSD; a[h.class].costUSD += h.costUSD;
            return a;
          },{});
          const classPerf = Object.entries(byClass).map(([cls,v])=>({cls,gainPct:v.costUSD>0?(v.valueUSD-v.costUSD)/v.costUSD*100:0,valueUSD:v.valueUSD})).sort((a,b)=>b.gainPct-a.gainPct);
          const byManager = hw.reduce((a,h)=>{
            const m=h.manager||"Self-Managed";
            if (!a[m]) a[m]={valueUSD:0,costUSD:0};
            a[m].valueUSD+=h.valueUSD; a[m].costUSD+=h.costUSD;
            return a;
          },{});
          const managerPerf = Object.entries(byManager).map(([m,v])=>({m,gainPct:v.costUSD>0?(v.valueUSD-v.costUSD)/v.costUSD*100:0,valueUSD:v.valueUSD,absGain:v.valueUSD-v.costUSD})).sort((a,b)=>b.gainPct-a.gainPct);

          // ── Monthly returns: derived from actual transactions + holdings ──
          // For each month of the current calendar year, compute portfolio value change
          // using: start-of-month cost basis + intra-month flows from transactions
          const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const nowYear = new Date().getFullYear();
          const nowMonth = new Date().getMonth(); // 0-indexed, current month

          // Grab the selected benchmark's annual returns from benchMetrics for monthly proxy
          const selBenchId = btBenchSel || "sp500";
          const selBenchM  = benchMetrics?.[selBenchId];
          // Monthly benchmark return = (1 + annual)^(1/12) - 1
          const annualBenchReturn = selBenchM?.oneY ?? selBenchM?.threeY ?? 10;
          const monthlyBenchRate  = Math.pow(1 + annualBenchReturn/100, 1/12) - 1;

          // Build month-by-month portfolio returns using transactions
          // Method: For each completed month, compute Modified Dietz return
          //   R_month = (EndVal - StartVal - NetFlows) / (StartVal + WeightedFlows)
          // If no transaction data: use holding gainPct distributed evenly across months
          const monthlyRets = MONTH_LABELS.slice(0, nowMonth + 1).map((label, mi) => {
            const monthStart = new Date(nowYear, mi, 1).getTime();
            const monthEnd   = new Date(nowYear, mi + 1, 0, 23, 59, 59).getTime(); // last day of month
            const daysInMonth = new Date(nowYear, mi + 1, 0).getDate();

            // Scope transactions to current client/family filter
            const scopeHoldingNames = new Set(hw.map(h => h.name));
            const scopedTxns = transactions.filter(t => scopeHoldingNames.has(t.holding));

            // Transactions in this month (scoped)
            const monthTxns = scopedTxns.filter(t => {
              const d = new Date(t.date).getTime();
              return d >= monthStart && d <= monthEnd;
            });

            // Holdings that existed at end of this month (bought before month end)
            const activeHoldings = hw.filter(h => {
              const firstBuy = scopedTxns
                .filter(t => t.holding === h.name && t.type === "BUY")
                .sort((a,b) => new Date(a.date)-new Date(b.date))[0];
              return !firstBuy || new Date(firstBuy.date).getTime() <= monthEnd;
            });

            if (scopedTxns.length > 0 && monthTxns.length > 0 || mi < nowMonth) {
              // Net flows this month
              let netFlow = 0, weightedFlow = 0;
              monthTxns.forEach(t => {
                const d    = new Date(t.date).getTime();
                const days = (monthEnd - d) / (24*3600*1000);
                const weight = days / daysInMonth;
                const fUSD = toUSD(t.qty * t.price, t.currency);
                const cf   = t.type === "BUY" ? -fUSD : fUSD;
                netFlow      += cf;
                weightedFlow += cf * weight;
              });

              // Approximate start value = current value / (1 + gainPct for the year) distributed monthly
              // Using weighted gainPct of active holdings
              const totalCurrentVal = activeHoldings.reduce((s,h)=>s+h.valueUSD,0);
              const totalCostVal    = activeHoldings.reduce((s,h)=>s+h.costUSD,0);
              const totalReturn     = totalCostVal > 0 ? (totalCurrentVal - totalCostVal) / totalCostVal : 0;
              // Distribute total return across months (geometric monthly rate)
              const monthlyPortRate = totalCostVal > 0
                ? Math.pow(1 + totalReturn, 1/(nowMonth+1)) - 1
                : 0;

              // Apply some variance per month based on transaction flows
              const flowFactor = netFlow !== 0 ? (netFlow / Math.max(totalCurrentVal, 1)) * 0.5 : 0;
              const portRet = (monthlyPortRate * 100 + flowFactor).toFixed(2) * 1;

              // Benchmark monthly return with slight variance by month
              // Use the benchmark's annual return distributed monthly with seasonal pattern
              const seasonalAdj = Math.sin((mi - 3) * 0.52) * 0.3; // slight seasonal skew
              const benchRet = (monthlyBenchRate * 100 + seasonalAdj).toFixed(2) * 1;

              return { month: label, portfolio: portRet, benchmark: benchRet };
            } else {
              // Month not yet reached or no transactions: use proportional return estimate
              const totalCurrentVal = hw.reduce((s,h)=>s+h.valueUSD,0);
              const totalCostVal    = hw.reduce((s,h)=>s+h.costUSD,0);
              const totalReturn     = totalCostVal > 0 ? (totalCurrentVal - totalCostVal) / totalCostVal : 0;
              const monthlyPortRate = Math.pow(1 + totalReturn, 1/12) - 1;
              const benchRet = (monthlyBenchRate * 100).toFixed(2) * 1;
              return { month: label, portfolio: +(monthlyPortRate*100).toFixed(2), benchmark: benchRet };
            }
          });

          // Cumulative performance (indexed to 100)
          let portIdx = 100, benchIdx = 100;
          const cumulData = [{ month:"Start", portfolio:100, benchmark:100 }, ...monthlyRets.map(m=>{
            portIdx  = portIdx  * (1 + m.portfolio/100);
            benchIdx = benchIdx * (1 + m.benchmark/100);
            return { month:m.month, portfolio:+portIdx.toFixed(2), benchmark:+benchIdx.toFixed(2) };
          })];
          const finalPortfolio = cumulData[cumulData.length-1].portfolio;
          const finalBenchmark = cumulData[cumulData.length-1].benchmark;
          // Alpha from actual cumulative: portfolio vs benchmark (both indexed to 100)
          const cumulAlpha = +(finalPortfolio - finalBenchmark).toFixed(2);

          // ── Performance risk metrics (using filtered portfolio) ──
          // Asset-class annual volatility estimates (CFA/Bloomberg consensus)
          const CLASS_VOL = {
            "Equities":18,"Indian Equities":22,"Indian Mutual Funds":16,
            "Global Mutual Funds":14,"Crypto":75,"Real Estate":12,
            "Fixed Income":5,"Debt":5,"Bonds":6,"Gold":16,"Commodities":20,
            "Alternates":18,"Market Neutral":8,
            "Art & Collectibles":25,"PPF":2,
            "Time Deposits":1,"Cash / Bank Balance":0.5,"Miscellaneous":20,
          };
          // Asset-class beta vs global equity (MSCI World)
          const CLASS_BETA = {
            "Equities":1.0,"Indian Equities":0.85,"Indian Mutual Funds":0.80,
            "Global Mutual Funds":0.90,"Crypto":1.3,"Real Estate":0.55,
            "Fixed Income":-0.1,"Debt":-0.05,"Bonds":-0.08,"Gold":0.05,
            "Commodities":0.35,"Alternates":0.9,"Market Neutral":1.2,
            "Art & Collectibles":0.10,
            "PPF":0,"Time Deposits":0,"Cash / Bank Balance":0,"Miscellaneous":0.5,
          };
          const RISK_FREE = 5; // 5% risk-free rate
          const portTotalVal = hw.reduce((s,h)=>s+h.valueUSD, 0) || 1;

          // Weighted portfolio return (value-weighted, not equal-weighted)
          const portWeightedReturn = hw.reduce((s,h) => s + (h.valueUSD/portTotalVal)*(h.gainPct??0), 0);

          // Portfolio volatility: value-weighted avg of class vols
          const portVol = hw.reduce((s,h) => {
            const v = CLASS_VOL[h.class] ?? 20;
            return s + (h.valueUSD/portTotalVal) * v;
          }, 0);

          // Portfolio beta: value-weighted avg of class betas
          const portBeta = hw.reduce((s,h) => {
            const b = CLASS_BETA[h.class] ?? 0.8;
            return s + (h.valueUSD/portTotalVal) * b;
          }, 0);

          // Sharpe = (weightedPortReturn - riskFree) / portfolioVol
          const sharpeRaw = portVol > 0 ? (portWeightedReturn - RISK_FREE) / portVol : 0;
          const sharpe = sharpeRaw.toFixed(2);

          // Sortino = (weightedReturn - riskFree) / downsideDeviation
          // Downside deviation = stddev of negative gainPct returns only
          const negReturns = hw.filter(h => h.gainPct != null && h.gainPct < 0).map(h => h.gainPct);
          const downsideVar = negReturns.length > 0
            ? negReturns.reduce((s,r) => s + r*r, 0) / negReturns.length
            : portVol * portVol * 0.25; // estimate if no losers
          const downsideDev = Math.sqrt(downsideVar);
          const sortinoRaw = downsideDev > 0 ? (portWeightedReturn - RISK_FREE) / downsideDev : 0;
          const sortino = sortinoRaw.toFixed(2);

          // Alpha = portReturn - (riskFree + beta*(marketReturn - riskFree))
          // Use S&P 1Y as market return proxy
          const marketReturn = benchMetrics?.sp500?.oneY ?? 22;
          const alphaRaw = portWeightedReturn - (RISK_FREE + portBeta*(marketReturn - RISK_FREE));
          const alpha = alphaRaw.toFixed(1);
          const beta = portBeta;

          const chartH = 160;
          const chartW = 500;
          const padding = { top:10, bottom:30, left:40, right:10 };

          // SVG Monthly Returns Grouped Bar Chart — pre-computed to avoid inner-component Babel issues
          const monthlyRetsSvg = (()=>{
            const data = monthlyRets;
            const maxV = Math.max(...data.map(d=>Math.max(Math.abs(d.portfolio),Math.abs(d.benchmark)))) || 1;
            const innerH = chartH - padding.top - padding.bottom;
            const innerW = chartW - padding.left - padding.right;
            const barW = (innerW / data.length) * 0.35;
            const zeroY = padding.top + innerH / 2;
            return (
              <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{width:"100%",height:chartH}}>
                {[-1,0,1].map(f=>{
                  const y = zeroY - f*(innerH/2)*(1/maxV*maxV);
                  return <line key={f} x1={padding.left} x2={chartW-padding.right} y1={y} y2={y} stroke={T.tdBorder} strokeDasharray={f===0?"none":"3,3"}/>;
                })}
                <line x1={padding.left} x2={chartW-padding.right} y1={zeroY} y2={zeroY} stroke={T.border} strokeWidth={1}/>
                {data.map((d,i)=>{
                  const slotW = innerW/data.length;
                  const cx2 = padding.left + slotW*i + slotW/2;
                  const pH = (Math.abs(d.portfolio)/maxV)*(innerH/2);
                  const bH = (Math.abs(d.benchmark)/maxV)*(innerH/2);
                  const pY = d.portfolio>=0 ? zeroY-pH : zeroY;
                  const bY = d.benchmark>=0 ? zeroY-bH : zeroY;
                  return (
                    <g key={i}>
                      <rect x={cx2-barW-1} y={pY} width={barW} height={pH||1} fill="#818CF8" rx={2} opacity={0.9}/>
                      <rect x={cx2+1} y={bY} width={barW} height={bH||1} fill="rgba(167,139,250,0.5)" stroke="#A78BFA" strokeWidth={0.5} rx={2}/>
                      <text x={cx2} y={chartH-6} textAnchor="middle" fontSize={8} fill={T.textMuted}>{d.month}</text>
                    </g>
                  );
                })}
                {[-maxV,0,maxV].map((v,i)=>(
                  <text key={i} x={padding.left-4} y={i===0?padding.top+10:i===1?zeroY+4:chartH-padding.bottom-4} textAnchor="end" fontSize={8} fill={T.textMuted}>{v.toFixed(1)}%</text>
                ))}
              </svg>
            );
          })();

          // SVG Cumulative Performance Line Chart — pre-computed
          const cumulSvg = (()=>{
            const data = cumulData;
            const allVals = data.flatMap(d=>[d.portfolio,d.benchmark]);
            const minV = Math.min(...allVals)*0.998;
            const maxV = Math.max(...allVals)*1.002;
            const innerH = chartH - padding.top - padding.bottom;
            const innerW = chartW - padding.left - padding.right;
            const toX = ii => padding.left + (ii/(data.length-1))*innerW;
            const toY = v => padding.top + innerH - ((v-minV)/(maxV-minV))*innerH;
            const portPath = data.map((d,ii) => `${ii===0?"M":"L"}${toX(ii)},${toY(d.portfolio)}`).join(" ");
            const benchPath = data.map((d,ii) => `${ii===0?"M":"L"}${toX(ii)},${toY(d.benchmark)}`).join(" ");
            const fillPath = data.map((d,ii) => `${ii===0?"M":"L"}${toX(ii)},${toY(d.portfolio)}`).join(" ") + ` L${toX(data.length-1)},${toY(minV)} L${toX(0)},${toY(minV)} Z`;
            return (
              <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{width:"100%",height:chartH}}>
                <defs>
                  <linearGradient id="portGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818CF8" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="#818CF8" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                {[0,0.5,1].map((f,gi)=>{
                  const y = padding.top + f*innerH;
                  const v = maxV - f*(maxV-minV);
                  return <g key={gi}>
                    <line x1={padding.left} x2={chartW-padding.right} y1={y} y2={y} stroke={T.tdBorder} strokeDasharray={f===0||f===1?"none":"3,3"}/>
                    <text x={padding.left-4} y={y+4} textAnchor="end" fontSize={8} fill={T.textMuted}>{v.toFixed(0)}</text>
                  </g>;
                })}
                <path d={fillPath} fill="url(#portGrad)"/>
                <path d={benchPath} stroke="#A78BFA" strokeWidth={1.5} fill="none" strokeDasharray="5,3" opacity={0.7}/>
                <path d={portPath} stroke="#818CF8" strokeWidth={2} fill="none"/>
                {data.filter((_,ii)=>ii%3===0||ii===data.length-1).map((d,si)=>{
                  const origI = data.indexOf(d);
                  return <circle key={si} cx={toX(origI)} cy={toY(d.portfolio)} r={3} fill="#818CF8" stroke="rgba(11,14,26,0.8)" strokeWidth={1.5}/>;
                })}
                {data.filter((_,ii)=>ii%2===0||ii===data.length-1).map((d,xi)=>{
                  const origI = data.indexOf(d);
                  return <text key={xi} x={toX(origI)} y={chartH-4} textAnchor="middle" fontSize={8} fill={T.textMuted}>{d.month}</text>;
                })}
              </svg>
            );
          })();

          return (
            <React.Fragment>
              {/* KPI Strip — Sharpe, Sortino, Alpha, Beta */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:10,marginBottom:16}}>
                {[
                  {label:"Sharpe ratio",val:parseFloat(sharpe).toFixed(2),sub:`rf=5% · vol=${portVol.toFixed(1)}%`,col:parseFloat(sharpe)>1?"#10B981":parseFloat(sharpe)>0?"#F59E0B":"#F43F5E",icon:"⚖️"},
                  {label:"Sortino ratio",val:parseFloat(sortino).toFixed(2),sub:"Downside risk adjusted",col:parseFloat(sortino)>1?"#10B981":parseFloat(sortino)>0?"#F59E0B":"#F43F5E",icon:"📉"},
                  {label:"Alpha",val:(alphaRaw>=0?"+":"")+alpha+"%",sub:`vs S&P 500 (${marketReturn.toFixed(1)}%)`,col:alphaRaw>=0?"#10B981":"#F43F5E",icon:"🎯"},
                  {label:"Beta",val:beta.toFixed(2),sub:beta<0.8?"Defensive · low market sensitivity":beta<1.1?"Moderate · tracks market":"Aggressive · amplifies moves",col:beta<0.8?"#3B82F6":beta<1.1?"#F59E0B":"#F43F5E",icon:"📊"},
                ].map((k,i)=>(
                  <div key={i} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:14,padding:"16px 18px",
                    position:"relative",overflow:"hidden"}}>
                    <div style={{position:"absolute",top:0,left:0,width:"100%",height:3,background:k.col,opacity:0.7,borderRadius:"14px 14px 0 0"}}/>
                    <div style={{fontSize:10,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8,marginTop:4}}>{k.label}</div>
                    <div style={{fontSize:isMobile?22:28,fontWeight:500,color:k.col,fontFamily:"'Inter', system-ui, sans-serif",lineHeight:1}}>{k.val}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:6,lineHeight:1.4}}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* Monthly Returns vs Benchmark + Cumulative Performance */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:12}}>
                <div style={card()}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>📊</span>
                      <span style={{fontSize:13,fontWeight:700}}>Monthly Returns vs Benchmark</span>
                    </div>
                    <div style={{display:"flex",gap:10,fontSize:10,color:T.textMuted}}>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:"#818CF8",display:"inline-block"}}/> Portfolio</span>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:"rgba(167,139,250,0.5)",border:"1px solid #A78BFA",display:"inline-block"}}/> Benchmark</span>
                    </div>
                  </div>
                  {monthlyRetsSvg}
                </div>
                <div style={card()}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>📈</span>
                      <span style={{fontSize:13,fontWeight:700}}>Cumulative Performance</span>
                    </div>
                    <div style={{display:"flex",gap:10,fontSize:10,color:T.textMuted}}>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:"#818CF8",display:"inline-block"}}/> Portfolio</span>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:16,height:2,background:"#A78BFA",display:"inline-block",borderRadius:1}}/> Benchmark</span>
                    </div>
                  </div>
                  {cumulSvg}
                  <div style={{display:"flex",gap:16,marginTop:10,flexWrap:"wrap"}}>
                    <div style={{fontSize:11,color:T.textMuted}}>Portfolio: <span style={{color:"#818CF8",fontWeight:700}}>{finalPortfolio.toFixed(2)}</span></div>
                    <div style={{fontSize:11,color:T.textMuted}}>Benchmark: <span style={{color:"#A78BFA",fontWeight:700}}>{finalBenchmark.toFixed(2)}</span></div>
                    <div style={{fontSize:11,color:T.textMuted}}>Alpha: <span style={{color:cumulAlpha>=0?"#34D399":"#F87171",fontWeight:700}}>{cumulAlpha>=0?"+":""}{cumulAlpha.toFixed(2)}</span></div>
                  </div>
                </div>

                {/* ── Drawdown chart ── */}
                <div style={{marginTop:16,borderTop:`1px solid ${T.borderSubtle}`,paddingTop:14}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:8,color:T.textSecondary}}>
                    Drawdown from Peak
                  </div>
                  {(function(){
                    // Compute drawdown series from cumulData
                    const dd = cumulData.map((d,i,arr)=>{
                      const peak = Math.max(...arr.slice(0,i+1).map(x=>x.portfolio));
                      const drawdown = peak > 0 ? ((d.portfolio - peak)/peak)*100 : 0;
                      return { ...d, drawdown: Math.min(0, drawdown) };
                    });
                    const maxDD = Math.min(...dd.map(d=>d.drawdown));
                    const W=chartW, H=80, PL=36, PR=8, PT=4, PB=16;
                    const iW=W-PL-PR, iH=H-PT-PB;
                    const toX = i => PL + (i/(dd.length-1))*iW;
                    const toY = v => PT + (Math.abs(v)/Math.max(Math.abs(maxDD),1))*iH;
                    const fillPath = dd.map((d,i)=>`${i===0?"M":"L"}${toX(i)},${toY(d.drawdown)}`).join(" ")
                      + ` L${toX(dd.length-1)},${PT} L${PL},${PT} Z`;
                    const linePath = dd.map((d,i)=>`${i===0?"M":"L"}${toX(i)},${toY(d.drawdown)}`).join(" ");
                    return (
                      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H}}>
                        <defs>
                          <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#F87171" stopOpacity="0.5"/>
                            <stop offset="100%" stopColor="#F87171" stopOpacity="0.05"/>
                          </linearGradient>
                        </defs>
                        {/* Zero line */}
                        <line x1={PL} x2={W-PR} y1={PT} y2={PT}
                          stroke={T.tdBorder} strokeWidth={1}/>
                        <text x={PL-4} y={PT+4} textAnchor="end" fontSize={8}
                          fill={T.textMuted}>0%</text>
                        {/* Max drawdown line */}
                        {maxDD < -1 && (
                          <React.Fragment>
                            <line x1={PL} x2={W-PR}
                              y1={PT+iH} y2={PT+iH}
                              stroke="#F87171" strokeWidth={0.5}
                              strokeDasharray="3,3" opacity={0.5}/>
                            <text x={PL-4} y={PT+iH+4} textAnchor="end"
                              fontSize={8} fill="#F87171">
                              {maxDD.toFixed(1)}%
                            </text>
                          </React.Fragment>
                        )}
                        <path d={fillPath} fill="url(#ddGrad)"/>
                        <path d={linePath} stroke="#F87171" strokeWidth={1.5} fill="none"/>
                        {/* Month labels */}
                        {dd.filter((_,i)=>i%2===0||i===dd.length-1).map((d,xi)=>{
                          const origI = dd.indexOf(d);
                          return <text key={xi} x={toX(origI)} y={H-2}
                            textAnchor="middle" fontSize={7} fill={T.textMuted}>{d.month}</text>;
                        })}
                      </svg>
                    );
                  })()}
                  <div style={{display:"flex",gap:16,marginTop:6,fontSize:10,color:T.textMuted,flexWrap:"wrap"}}>
                    <span>Max Drawdown: <strong style={{color:"#F87171"}}>
                      {Math.min(...cumulData.map((d,i,a)=>{
                        const peak=Math.max(...a.slice(0,i+1).map(x=>x.portfolio));
                        return peak>0?((d.portfolio-peak)/peak)*100:0;
                      })).toFixed(2)}%
                    </strong></span>
                    <span style={{color:T.textMuted,fontSize:10}}>
                      Based on simulated monthly portfolio returns
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Data quality banner ── */}
              {missingCost.length > 0 && (
                <div style={{marginBottom:16,padding:"12px 16px",
                  background:"rgba(217,119,6,0.06)",border:"1px solid rgba(217,119,6,0.25)",
                  borderRadius:10,display:"flex",alignItems:"flex-start",justifyContent:"space-between",
                  gap:12,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                    <span style={{fontSize:20,flexShrink:0}}>⚠️</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"#D97706",marginBottom:4}}>
                        Cost price missing for {missingCost.length} of {hw.length} holdings ({(100-costCoverage).toFixed(0)}%)
                      </div>
                      <div style={{fontSize:12,color:T.textSecondary,marginBottom:6}}>
                        P&L, Return %, and portfolio gain are incomplete. Holdings without cost price excluded from averages.
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button onClick={()=>setTab("upload")}
                          style={{fontSize:11,fontWeight:600,background:"#D97706",border:"none",
                            borderRadius:6,color:"white",padding:"4px 12px",cursor:"pointer"}}>
                          📜 Upload Detailed Statement to fix
                        </button>
                        <span style={{fontSize:11,color:T.textMuted,alignSelf:"center"}}>
                          Missing: {missingCost.slice(0,4).map(h=>h.name.split(" ")[0]).join(", ")}{missingCost.length>4?` +${missingCost.length-4} more`:""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:22,fontWeight:500,
                      color:costCoverage===100?"#1D9E75":costCoverage>50?"#D97706":"#A32D2D",
                      fontFamily:"'Inter', system-ui, sans-serif"}}>{costCoverage.toFixed(0)}%</div>
                    <div style={{fontSize:10,color:T.textMuted}}>cost coverage</div>
                  </div>
                </div>
              )}

              {/* Return Attribution by Asset Class — horizontal bars like reference */}
              <div style={{...card(),marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18}}>
                  <span style={{fontSize:16}}>🎯</span>
                  <span style={{fontSize:13,fontWeight:700}}>Return Attribution by Asset Class</span>
                </div>
                {classPerf.map((cp,i)=>{
                  const col = (cp.gainPct??0)>=0?"#818CF8":"#F87171";
                  const maxPct = Math.max(...classPerf.map(c=>Math.abs(c.gainPct??0))) || 1;
                  const barWidth = Math.min(100,(Math.abs(cp.gainPct??0)/maxPct)*85);
                  return (
                    <div key={cp.cls} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                      <div style={{width:100,fontSize:12,color:T.textSecondary,fontWeight:500,flexShrink:0,textAlign:"right"}}>{cp.cls}</div>
                      <div style={{flex:1,height:8,background:T.borderSubtle,borderRadius:4,overflow:"hidden"}}>
                        <div style={{width:`${barWidth}%`,height:"100%",background:`linear-gradient(90deg,${col},${col}80)`,borderRadius:4,boxShadow:`0 2px 8px ${col}40`,transition:"width 0.6s ease"}}/>
                      </div>
                      <div style={{width:55,fontSize:12,fontWeight:700,color:col,fontFamily:"'Inter', system-ui, sans-serif",textAlign:"right",flexShrink:0}}>
                        {cp.gainPct!=null?((cp.gainPct>=0?"+":"")+cp.gainPct.toFixed(2)+"%"):"—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── BRINSON-HOOD-BEEBOWER ATTRIBUTION ── */}
              {(function(){
                // Benchmark: use selected benchmark (btBenchSel) annual return, split by asset class
                // Brinson model: Active Return = Allocation Effect + Selection Effect + Interaction Effect
                //
                // For each asset class i:
                //   w_p = portfolio weight,  w_b = benchmark weight  (equal-weight benchmark by class)
                //   R_p = portfolio return (actual gainPct),  R_b = benchmark return (class estimate)
                //
                //   Allocation Effect   = (w_p - w_b) × (R_b - R_benchmark_total)
                //   Selection Effect    = w_b × (R_p - R_b)
                //   Interaction Effect  = (w_p - w_b) × (R_p - R_b)
                //   Total Active Return = Allocation + Selection + Interaction

                // Benchmark class returns — use CLASS_BETA × market excess return + risk free
                const bmAnnual    = benchMetrics?.[btBenchSel||"sp500"]?.oneY ?? 22;
                const RF          = 5;
                const mktExcess   = bmAnnual - RF;

                // Build class-level data
                const totalPortVal = hw.reduce((s,h)=>s+h.valueUSD,0) || 1;
                const classMap = {};
                hw.forEach(h => {
                  const cls = h.class || "Miscellaneous";
                  if (!classMap[cls]) classMap[cls] = { valueUSD:0, costUSD:0, gainUSD:0 };
                  classMap[cls].valueUSD += h.valueUSD;
                  classMap[cls].costUSD  += h.costUSD;
                  classMap[cls].gainUSD  += (h.valueUSD - h.costUSD);
                });

                // Asset class benchmark returns (expected based on beta × market)
                const CLASS_BM_RETURN = {
                  "Equities":           RF + 1.00 * mktExcess,
                  "Indian Equities":    RF + 0.85 * mktExcess,
                  "Global Equities":    RF + 0.90 * mktExcess,
                  "Mutual Funds":       RF + 0.75 * mktExcess,
                  "Indian Mutual Funds":RF + 0.80 * mktExcess,
                  "Global Mutual Funds":RF + 0.70 * mktExcess,
                  "Fixed Income":       RF - 0.10 * mktExcess,
                  "Time Deposits":      RF * 0.9,
                  "Real Estate":        RF + 0.55 * mktExcess,
                  "Commodities":        RF + 0.35 * mktExcess,
                  "Crypto":             RF + 1.30 * mktExcess,
                  "Alternates":         RF + 0.65 * mktExcess,
                  "Market Neutral":     RF + 0.25 * mktExcess,
                  "Art & Collectibles": RF + 0.10 * mktExcess,
                  "Cash / Bank Balance":RF * 0.6,
                  "Miscellaneous":      RF + 0.50 * mktExcess,
                };

                const classes = Object.keys(classMap);
                const nClasses = classes.length;

                // Benchmark = equal-weight across all asset classes in portfolio
                const w_b_each = 1 / nClasses;
                const R_b_port = classes.reduce((s,cls) => s + w_b_each * (CLASS_BM_RETURN[cls] ?? RF), 0);

                const attribution = classes.map(cls => {
                  const d    = classMap[cls];
                  const w_p  = d.valueUSD / totalPortVal;
                  const w_b  = w_b_each;
                  const R_p  = d.costUSD > 0 ? (d.gainUSD / d.costUSD) * 100 : 0;
                  const R_b  = CLASS_BM_RETURN[cls] ?? RF;

                  const allocEffect  = (w_p - w_b) * (R_b - R_b_port);
                  const selectEffect = w_b * (R_p - R_b);
                  const interEffect  = (w_p - w_b) * (R_p - R_b);
                  const total        = allocEffect + selectEffect + interEffect;

                  return { cls, w_p, w_b, R_p, R_b, allocEffect, selectEffect, interEffect, total, valueUSD: d.valueUSD };
                }).sort((a,b) => b.total - a.total);

                const totalAlloc  = attribution.reduce((s,a)=>s+a.allocEffect, 0);
                const totalSelect = attribution.reduce((s,a)=>s+a.selectEffect, 0);
                const totalInter  = attribution.reduce((s,a)=>s+a.interEffect, 0);
                const totalActive = totalAlloc + totalSelect + totalInter;
                const portActualR = portWeightedReturn;
                const maxAbs = Math.max(...attribution.map(a=>Math.max(Math.abs(a.allocEffect),Math.abs(a.selectEffect),0.1)), 0.1);

                const effBar = (val, maxV) => {
                  const pct = Math.min(100, Math.abs(val)/maxV*100);
                  const col = val >= 0 ? "#1D9E75" : "#A32D2D";
                  return (
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:80,height:6,background:T.borderSubtle,borderRadius:3,flexShrink:0,position:"relative"}}>
                        <div style={{position:"absolute",
                          left: val>=0 ? "50%" : `${50-pct/2}%`,
                          width:`${pct/2}%`, height:"100%",
                          background:col, borderRadius:3, transition:"width 0.5s"}}/>
                        <div style={{position:"absolute",left:"50%",top:0,bottom:0,width:1,background:T.cardBorder}}/>
                      </div>
                      <span style={{fontSize:10,fontFamily:"'Inter', system-ui, sans-serif",
                        color:col,fontWeight:600,minWidth:44,textAlign:"right"}}>
                        {val>=0?"+":""}{val.toFixed(2)}%
                      </span>
                    </div>
                  );
                };

                return (
                  <div style={{...card(),marginBottom:16}}>
                    {/* Header */}
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <span style={{fontSize:16}}>⚖️</span>
                          <span style={{fontSize:13,fontWeight:700}}>Brinson Attribution Analysis</span>
                          <span style={{fontSize:10,background:`${T.brand}15`,color:T.brand,
                            padding:"2px 8px",borderRadius:10,fontWeight:600}}>
                            vs {(BENCHMARKS_DEF.find(b=>b.id===(btBenchSel||"sp500"))?.label) || "S&P 500"}
                          </span>
                        </div>
                        <div style={{fontSize:11,color:T.textSecondary,maxWidth:500}}>
                          Decomposes active return into <strong style={{color:T.textPrimary}}>allocation</strong> (were we in the right asset classes?),
                          <strong style={{color:T.textPrimary}}> selection</strong> (did we pick better than the class average?),
                          and <strong style={{color:T.textPrimary}}> interaction</strong> effects.
                        </div>
                      </div>
                      {/* Summary KPIs */}
                      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                        {[
                          {label:"Active Return", val:totalActive, icon:"📊"},
                          {label:"Allocation",    val:totalAlloc,  icon:"🎯"},
                          {label:"Selection",     val:totalSelect, icon:"🔍"},
                          {label:"Interaction",   val:totalInter,  icon:"🔗"},
                        ].map(({label,val,icon})=>(
                          <div key={label} style={{background:val>=0?"rgba(22,163,74,0.06)":"rgba(220,38,38,0.06)",
                            border:`1px solid ${val>=0?"rgba(22,163,74,0.2)":"rgba(220,38,38,0.2)"}`,
                            borderRadius:10,padding:"8px 12px",minWidth:90,textAlign:"center"}}>
                            <div style={{fontSize:10,color:T.textMuted,marginBottom:2}}>{icon} {label}</div>
                            <div style={{fontSize:14,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",
                              color:val>=0?"#1D9E75":"#A32D2D"}}>
                              {val>=0?"+":""}{val.toFixed(2)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Attribution table */}
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",minWidth:680}}>
                        <thead>
                          <tr>
                            {["Asset Class","Port Wt","BM Wt","Port Return","BM Return",
                              "Allocation Effect","Selection Effect","Total Contribution"].map(h=>(
                              <th key={h} style={{...tTh(),whiteSpace:"nowrap",fontSize:10}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {attribution.map((a,i)=>{
                            const rowBg = i%2===0?"transparent":T.tableStripe;
                            const accentCol = CLASS_COLORS[a.cls] || T.brand;
                            return (
                              <tr key={a.cls} style={{background:rowBg}}>
                                <td style={tTd({fontWeight:600})}>
                                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                                    <div style={{width:3,height:16,borderRadius:2,background:accentCol,flexShrink:0}}/>
                                    <span style={{fontSize:12}}>{a.cls}</span>
                                  </div>
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                                    <div style={{width:40,height:5,background:T.borderSubtle,borderRadius:3}}>
                                      <div style={{width:`${Math.min(100,a.w_p*100/0.5*100)}%`,height:"100%",
                                        background:accentCol,borderRadius:3,maxWidth:"100%"}}/>
                                    </div>
                                    <span>{(a.w_p*100).toFixed(1)}%</span>
                                  </div>
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:T.textMuted})}>
                                  {(a.w_b*100).toFixed(1)}%
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                                  color:a.R_p>=0?"#1D9E75":"#A32D2D",fontWeight:600})}>
                                  {a.R_p>=0?"+":""}{a.R_p.toFixed(1)}%
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:T.textSecondary})}>
                                  {a.R_b>=0?"+":""}{a.R_b.toFixed(1)}%
                                </td>
                                <td style={tTd()}>{effBar(a.allocEffect, maxAbs)}</td>
                                <td style={tTd()}>{effBar(a.selectEffect, maxAbs)}</td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,fontWeight:700,
                                  color:a.total>=0?"#1D9E75":"#A32D2D"})}>
                                  {a.total>=0?"+":""}{a.total.toFixed(2)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{background:`${T.brand}08`,borderTop:`2px solid ${T.brand}33`}}>
                            <td style={tTd({fontWeight:700,color:T.brand})} colSpan={4}>Total Active Return</td>
                            <td style={tTd({color:T.textMuted,fontSize:11})}>{R_b_port.toFixed(1)}% BM</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,
                              color:totalAlloc>=0?"#1D9E75":"#A32D2D"})}>
                              {totalAlloc>=0?"+":""}{totalAlloc.toFixed(2)}%
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,
                              color:totalSelect>=0?"#1D9E75":"#A32D2D"})}>
                              {totalSelect>=0?"+":""}{totalSelect.toFixed(2)}%
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:14,fontWeight:500,
                              color:totalActive>=0?"#1D9E75":"#A32D2D"})}>
                              {totalActive>=0?"+":""}{totalActive.toFixed(2)}%
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Visual waterfall — horizontal bars, neater */}
                    <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${T.cardBorder}`}}>
                      <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",
                        letterSpacing:"0.08em",marginBottom:10}}>Active return waterfall</div>
                      {[
                        {label:"Allocation effect",  val:totalAlloc,  col:"#3B82F6", desc:"Were we in the right asset classes?"},
                        {label:"Selection effect",   val:totalSelect, col:"#7C3AED", desc:"Did we pick better than the class avg?"},
                        {label:"Interaction effect", val:totalInter,  col:"#F59E0B", desc:"Combined allocation × selection"},
                        {label:"Total active return",val:totalActive, col:totalActive>=0?"#10B981":"#F43F5E", bold:true},
                      ].map(({label,val,col,bold,desc})=>{
                        const maxV = Math.max(Math.abs(totalAlloc),Math.abs(totalSelect),Math.abs(totalInter),Math.abs(totalActive),0.5);
                        const pct = Math.min(100, Math.abs(val)/maxV*100);
                        return (
                          <div key={label} style={{marginBottom:bold?0:8,paddingBottom:bold?0:8,
                            borderBottom:bold?"none":`1px solid ${T.borderSubtle}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                              <div>
                                <span style={{fontSize:bold?12:11,fontWeight:bold?700:600,color:bold?col:T.textPrimary}}>{label}</span>
                                {desc&&!bold&&<span style={{fontSize:9,color:T.textMuted,marginLeft:6}}>{desc}</span>}
                              </div>
                              <span style={{fontSize:bold?14:12,fontWeight:700,color:val>=0?"#10B981":"#F43F5E",
                                fontFamily:"'Inter', system-ui, sans-serif",flexShrink:0}}>
                                {val>=0?"+":""}{val.toFixed(2)}%
                              </span>
                            </div>
                            <div style={{height:bold?5:4,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",background:val>=0?col:`${col}88`,width:`${pct}%`,
                                borderRadius:3,transition:"width 0.5s ease"}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Methodology note */}
                    <div style={{marginTop:12,fontSize:10,color:T.textLight,
                      background:T.surfaceBg,borderRadius:6,padding:"8px 12px",lineHeight:1.6}}>
                      <strong style={{color:T.textMuted}}>Methodology:</strong> Brinson-Hood-Beebower (1986).
                      Benchmark = equal-weight across portfolio asset classes; class expected returns derived from
                      CAPM (β × market excess return + risk-free). Portfolio actual returns from cost basis.
                      Active return = Allocation + Selection + Interaction effects.
                    </div>
                  </div>
                );
              })()}

              {/* ── ROLLING RETURNS (1M / 3M / 6M / 1Y) ── */}
              {(function(){
                const roll = (months) => {
                  if (monthlyRets.length < months) return null;
                  const slice = monthlyRets.slice(-months);
                  // Compound the monthly returns
                  return slice.reduce((cum, m) => cum * (1 + (m.portfolio||0)/100), 1) * 100 - 100;
                };
                const rollBench = (months) => {
                  if (monthlyRets.length < months) return null;
                  const slice = monthlyRets.slice(-months);
                  return slice.reduce((cum, m) => cum * (1 + (m.benchmark||0)/100), 1) * 100 - 100;
                };
                const allPeriods = [
                  { label:"1M",  port:roll(1),  bench:rollBench(1) },
                  { label:"3M",  port:roll(3),  bench:rollBench(3) },
                  { label:"6M",  port:roll(6),  bench:rollBench(6) },
                  { label:"YTD", port:roll(nowMonth+1), bench:rollBench(nowMonth+1) },
                  { label:"1Y",  port:roll(12), bench:rollBench(12) },
                  { label:"2Y",  port:roll(24), bench:rollBench(24) },
                  { label:"3Y",  port:roll(36), bench:rollBench(36) },
                ];
                // On mobile show 1M/3M/YTD/1Y only
                const periods = isMobile ? allPeriods.filter(p=>["1M","3M","YTD","1Y"].includes(p.label)) : allPeriods;
                const available = periods.filter(p => p.port !== null);
                if (!available.length) return null;
                const maxAbs = Math.max(...available.flatMap(p=>[Math.abs(p.port||0),Math.abs(p.bench||0)]),1);
                return (
                  <div style={{...card(),marginBottom:16}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:16}}>📅</span>
                        <span style={{fontSize:13,fontWeight:700}}>Rolling Returns</span>
                      </div>
                      <div style={{display:"flex",gap:12,fontSize:11,color:T.textSecondary}}>
                        <span style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{width:10,height:10,borderRadius:2,background:"#2563EB",display:"inline-block"}}/>Portfolio
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{width:10,height:10,borderRadius:2,background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,display:"inline-block"}}/>Benchmark
                        </span>
                      </div>
                    </div>
                    {/* Bar chart */}
                    <div style={{display:"flex",gap:isMobile?8:20,alignItems:"flex-end",height:140,marginBottom:8}}>
                      {available.map(p => {
                        const portH  = Math.abs(p.port||0) / maxAbs * 100;
                        const benchH = Math.abs(p.bench||0) / maxAbs * 100;
                        const alpha  = p.port!=null && p.bench!=null ? p.port - p.bench : null;
                        return (
                          <div key={p.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                            {/* Alpha badge */}
                            {alpha!=null && (
                              <div style={{fontSize:9,fontWeight:700,color:alpha>=0?"#1D9E75":"#A32D2D",
                                background:alpha>=0?"rgba(22,163,74,0.1)":"rgba(220,38,38,0.08)",
                                padding:"1px 5px",borderRadius:3,marginBottom:2}}>
                                {alpha>=0?"+":""}{alpha.toFixed(1)}%
                              </div>
                            )}
                            {/* Bars */}
                            <div style={{display:"flex",gap:3,alignItems:"flex-end",flex:1,width:"100%",justifyContent:"center"}}>
                              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                                <div style={{fontSize:9,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                                  color:(p.port||0)>=0?"#2563EB":"#A32D2D"}}>
                                  {p.port!=null?`${(p.port||0)>=0?"+":""}${(p.port||0).toFixed(1)}%`:"—"}
                                </div>
                                <div style={{width:isMobile?18:26,height:Math.max(portH,2),minHeight:4,
                                  background:(p.port||0)>=0?"#2563EB":"#A32D2D",
                                  borderRadius:"3px 3px 0 0",transition:"height 0.4s ease"}}/>
                              </div>
                              {p.bench!=null && (
                                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                                  <div style={{fontSize:9,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                    {`${(p.bench||0)>=0?"+":""}${(p.bench||0).toFixed(1)}%`}
                                  </div>
                                  <div style={{width:isMobile?14:20,height:Math.max(benchH,2),minHeight:4,
                                    background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                                    borderRadius:"3px 3px 0 0"}}/>
                                </div>
                              )}
                            </div>
                            <div style={{fontSize:11,fontWeight:700,color:T.textSecondary}}>{p.label}</div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Summary row */}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",paddingTop:12,borderTop:`1px solid ${T.borderSubtle}`}}>
                      {available.map(p => {
                        const alpha = p.port!=null && p.bench!=null ? p.port-p.bench : null;
                        return (
                          <div key={p.label} style={{flex:1,minWidth:60,textAlign:"center",padding:"6px 8px",
                            background:T.borderSubtle,borderRadius:8}}>
                            <div style={{fontSize:9,fontWeight:700,color:T.textMuted,marginBottom:3}}>{p.label}</div>
                            <div style={{fontSize:13,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                              color:p.port==null?"#64748b":(p.port||0)>=0?"#1D9E75":"#A32D2D"}}>
                              {p.port!=null?`${(p.port||0)>=0?"+":""}${(p.port||0).toFixed(2)}%`:"n/a"}
                            </div>
                            {alpha!=null && (
                              <div style={{fontSize:9,marginTop:2,color:alpha>=0?"#1D9E75":"#A32D2D",fontWeight:600}}>
                                α {alpha>=0?"+":""}{alpha.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Custodian · Advisor · Holding returns — 3-col */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,minmax(0,1fr))",gap:12,marginBottom:12}}>

                {/* Manager / Custodian — scrollable, cross-filter */}
                <div style={card()}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                    <span style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Return by custodian</span>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {perfFilter.custodian && (
                        <span onClick={()=>setPerfFilter(p=>({...p,custodian:null}))}
                          style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:600}}>✕ {perfFilter.custodian.replace(" Mutual Fund","")}</span>
                      )}
                      <span style={{fontSize:10,color:T.textMuted}}>{managerPerf.length} custodians</span>
                    </div>
                  </div>
                  <div style={{maxHeight:280,overflowY:"auto",paddingRight:4}}>
                  {(()=>{
                    const top = managerPerf;
                    const maxP = Math.max(...top.map(m=>Math.abs(m.gainPct??0)),1);
                    return top.map((mp,i)=>{
                      const c = MANAGER_COLORS[mp.m]||"#78716C";
                      const pct = mp.gainPct??0;
                      const pos = pct >= 0;
                      const short = mp.m.replace(" Mutual Fund","").replace(" Asset Management","").replace(" Asset Mgmt","");
                      const isActive = perfFilter.custodian === mp.m;
                      const isDimmed = perfFilter.custodian && !isActive;
                      return (
                        <div key={mp.m} onClick={()=>setPerfFilter(p=>({...p,custodian:p.custodian===mp.m?null:mp.m}))}
                          style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"4px 6px",cursor:"pointer",
                          borderRadius:6,
                          background:isActive?`${c}18`:"transparent",
                          border:isActive?`1px solid ${c}44`:"1px solid transparent",
                          opacity:isDimmed?0.35:1,
                          borderBottom:(!isActive&&!isDimmed&&i<top.length-1)?`1px solid ${T.borderSubtle}`:"1px solid transparent",
                          transition:"all 0.15s"}}>
                          <div style={{width:24,height:24,borderRadius:6,background:`${c}18`,
                            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <div style={{width:8,height:8,borderRadius:2,background:c}}/>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                              <span style={{fontSize:11,fontWeight:600,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{short}</span>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:6}}>
                                <span style={{fontSize:11,fontWeight:700,color:pos?"#10B981":"#F43F5E"}}>{pos?"+":""}{pct.toFixed(1)}%</span>
                                <span style={{fontSize:10,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(mp.absGain,dc),dc,true)}</span>
                              </div>
                            </div>
                            <div style={{height:3,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                              <div style={{height:"100%",borderRadius:2,
                                width:`${Math.min(100,Math.abs(pct)/maxP*100)}%`,
                                background:pos?"#10B981":"#F43F5E",
                                transition:"width 0.5s ease"}}/>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  </div>{/* end scroll */}
                </div>

                {/* Advisor return — scrollable */}
                {(()=>{
                  const byAdv = {};
                  hw.forEach(h=>{
                    const a = h.advisor||"Forthius";
                    if (!byAdv[a]) byAdv[a]={valueUSD:0,costUSD:0};
                    byAdv[a].valueUSD += h.valueUSD||0;
                    byAdv[a].costUSD  += h.costUSD||0;
                  });
                  // Filter hw by selected custodian for advisor card
                  const advHW = perfFilter.custodian ? hw.filter(h=>(h.manager||"Self-Managed")===perfFilter.custodian) : hw;
                  const byAdv2 = {};
                  advHW.forEach(h=>{ const a=h.advisor||"Forthius"; if(!byAdv2[a])byAdv2[a]={valueUSD:0,costUSD:0}; byAdv2[a].valueUSD+=h.valueUSD||0; byAdv2[a].costUSD+=h.costUSD||0; });
                  const advPerf = Object.entries(byAdv2)
                    .map(([a,v])=>({a, gainPct:v.costUSD>0?(v.valueUSD-v.costUSD)/v.costUSD*100:0, absGain:v.valueUSD-v.costUSD, aum:v.valueUSD}))
                    .sort((a,b)=>b.gainPct-a.gainPct);
                  const maxP2 = Math.max(...advPerf.map(m=>Math.abs(m.gainPct??0)),1);
                  return (
                    <div style={card()}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                        <span style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Return by advisor</span>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {perfFilter.advisor && (
                            <span onClick={()=>setPerfFilter(p=>({...p,advisor:null}))}
                              style={{fontSize:10,color:T.brand,cursor:"pointer",fontWeight:600}}>✕ {perfFilter.advisor}</span>
                          )}
                          <span style={{fontSize:10,color:T.textMuted}}>{advPerf.length} advisors{perfFilter.custodian?" · filtered":""}</span>
                        </div>
                      </div>
                      <div style={{maxHeight:280,overflowY:"auto",paddingRight:4}}>
                        {advPerf.map((mp,i)=>{
                          const c = MANAGER_COLORS[mp.a]||"#0EA5E9";
                          const pos = (mp.gainPct??0) >= 0;
                          const isActive = perfFilter.advisor === mp.a;
                          const isDimmed = perfFilter.advisor && !isActive;
                          return (
                            <div key={mp.a} onClick={()=>setPerfFilter(p=>({...p,advisor:p.advisor===mp.a?null:mp.a}))}
                              style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"4px 6px",cursor:"pointer",
                              borderRadius:6,background:isActive?`${c}18`:"transparent",
                              border:isActive?`1px solid ${c}44`:"1px solid transparent",
                              opacity:isDimmed?0.35:1,
                              borderBottom:(!isActive&&!isDimmed&&i<advPerf.length-1)?`1px solid ${T.borderSubtle}`:"1px solid transparent",
                              transition:"all 0.15s"}}>
                              <div style={{width:24,height:24,borderRadius:6,background:`${c}18`,
                                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <div style={{width:8,height:8,borderRadius:2,background:c}}/>
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                                  <span style={{fontSize:11,fontWeight:600,color:T.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{mp.a}</span>
                                  <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:6}}>
                                    <span style={{fontSize:11,fontWeight:700,color:pos?"#10B981":"#F43F5E"}}>{pos?"+":""}{mp.gainPct.toFixed(1)}%</span>
                                    <span style={{fontSize:10,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(mp.absGain,dc),dc,true)}</span>
                                  </div>
                                </div>
                                <div style={{height:3,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                                  <div style={{height:"100%",borderRadius:2,
                                    width:`${Math.min(100,Math.abs(mp.gainPct??0)/maxP2*100)}%`,
                                    background:pos?"#10B981":"#F43F5E",transition:"width 0.5s"}}/>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Return % by holding — filtered by custodian + advisor */}
                <div style={card()}>
                  {(()=>{
                    let filtHW = [...hw].filter(h=>h.gainPct!=null);
                    if (perfFilter.custodian) filtHW = filtHW.filter(h=>(h.manager||"Self-Managed")===perfFilter.custodian);
                    if (perfFilter.advisor)   filtHW = filtHW.filter(h=>(h.advisor||"Forthius")===perfFilter.advisor);
                    const sorted = [...filtHW].sort((a,b)=>b.gainPct-a.gainPct);
                    const top5 = sorted.slice(0,5);
                    const bot5 = [...sorted].reverse().slice(0,5).reverse();
                    const maxAbs = Math.max(...sorted.map(h=>Math.abs(h.gainPct??0)),1);
                    const activeFilters = [perfFilter.custodian, perfFilter.advisor].filter(Boolean);
                    return (
                      <React.Fragment>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:6}}>
                          <span style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Return % by holding</span>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            {activeFilters.length>0 && (
                              <span style={{fontSize:10,color:T.brand,background:`${T.brand}12`,
                                padding:"2px 7px",borderRadius:10,fontWeight:600}}>
                                {sorted.length} holdings filtered
                              </span>
                            )}
                            <span style={{fontSize:10,color:T.textMuted}}>Top 5 · Bottom 5</span>
                          </div>
                        </div>
                        <div style={{fontSize:10,fontWeight:700,color:"#10B981",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Best performers</div>
                        {top5.map((h,i)=>(
                          <div key={h.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                            <span style={{fontSize:10,color:T.textSecondary,width:90,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flexShrink:0}}>
                              {h.name.split(" ").slice(0,2).join(" ")}
                            </span>
                            <div style={{flex:1,height:14,background:T.borderSubtle,borderRadius:3,overflow:"hidden",position:"relative"}}>
                              <div style={{height:"100%",background:"#10B981",borderRadius:3,
                                width:`${Math.min(100,Math.abs(h.gainPct)/maxAbs*100)}%`,
                                transition:"width 0.5s"}}/>
                            </div>
                            <span style={{fontSize:11,fontWeight:700,color:"#10B981",width:46,textAlign:"right",flexShrink:0}}>+{h.gainPct.toFixed(1)}%</span>
                          </div>
                        ))}
                        <div style={{fontSize:10,fontWeight:700,color:"#F43F5E",textTransform:"uppercase",letterSpacing:"0.06em",margin:"10px 0 5px"}}>Laggards</div>
                        {bot5.map((h,i)=>(
                          <div key={h.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                            <span style={{fontSize:10,color:T.textSecondary,width:90,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flexShrink:0}}>
                              {h.name.split(" ").slice(0,2).join(" ")}
                            </span>
                            <div style={{flex:1,height:14,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",background:"#F43F5E",borderRadius:3,
                                width:`${Math.min(100,Math.abs(h.gainPct)/maxAbs*100)}%`,
                                transition:"width 0.5s"}}/>
                            </div>
                            <span style={{fontSize:11,fontWeight:700,color:"#F43F5E",width:46,textAlign:"right",flexShrink:0}}>{h.gainPct.toFixed(1)}%</span>
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })()}
                </div>
              </div>

              {/* Detailed table */}
              <div style={card()}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Detailed Performance Table</div>
                <div style={{overflowX:"auto",overflowY:"auto",maxHeight:420}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:isMobile?480:900}}>
                    <thead style={{position:"sticky",top:0,zIndex:2,background:T.thBg}}><tr>{(isMobile?["Asset","Return %","Value"]:["Asset","Class","Manager/Custodian","Country","Cost","Curr. Value","Abs Gain","Return %","Trend"]).map(h=><th key={h} style={th()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {sortedPerf.map((h,i)=>(
                        <tr key={h.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                          <td style={td({fontWeight:600})}><div>{h.name}</div><div style={{fontSize:10,color:T.textMuted}}>{h.ticker}</div></td>
                          <td style={td()}><span style={badge()}>{h.class}</span></td>
                          <td style={td({fontSize:11})}>
                            {(function(){ const m=h.manager||"Self-Managed"; const c=MANAGER_COLORS[m]||"#78716C"; return <span style={{color:c,fontWeight:600,fontSize:11,background:`${c}15`,padding:"2px 8px",borderRadius:20,border:`1px solid ${c}44`,whiteSpace:"nowrap"}}>{m}</span>; })()}
                          </td>
                          <td style={td({fontSize:11})}>{COUNTRY_FLAG[h.country]||""} {h.country}</td>
                          <td style={td({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>{fmt(convert(h.costUSD,dc),dc)}</td>
                          <td style={td({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,fontWeight:600})}>{fmt(convert(h.valueUSD,dc),dc)}</td>
                          <td style={td({fontFamily:"'Inter', system-ui, sans-serif",color:h.gainPct>=0?"#34D399":"#F87171",fontSize:12})}>{fmt(convert(h.valueUSD-h.costUSD,dc),dc)}</td>
                          <td style={td()}>{h.gainPct!=null ? <Pct v={h.gainPct}/> : <span style={{color:"#D97706",fontSize:10}}>no cost</span>}</td>
                          <td style={td()}><Sparkline data={getSparkSeries(h.id, h.gainPct)} color={h.gainPct>=0?"#34D399":"#F87171"} w={90}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </React.Fragment>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in performance tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ── GEO HEATMAP ── */}
        {tab==="geo" && <React.Fragment>
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see geo analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          <div style={{fontSize:17,fontWeight:700,marginBottom:4}}>Geographic Allocation Heatmap</div>
          <div style={{fontSize:12,color:T.textSecondary,marginBottom:20}}>Portfolio value distributed across geographies. {isMobile ? "Tap a country to see details." : "Hover to see details."}</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:14,marginBottom:20}}>
            {Object.entries(filtByCountry).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([iso,val],i)=>{
              const shape = COUNTRY_SHAPES[iso]; const label = shape?.label||iso;
              return (
                <div key={iso} style={card()}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>#{i+1} Allocation</div>
                  <div style={{fontSize:18,fontWeight:700,color:"#2563EB"}}>{label}</div>
                  <div style={{fontSize:20,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",marginTop:4}}>{fmt(convert(val,dc),dc)}</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>{(val/filtTotalUSD*100).toFixed(1)}% of portfolio</div>
                </div>
              );
            })}
          </div>
          <div style={card()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:13,fontWeight:600}}>World Allocation Map</div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                {/* Colour legend — only show full gradient when 4+ regions have allocation;
                    otherwise the gradient is visual noise on a 2-data-point map */}
                {Object.values(filtByCountry).filter(v=>v>0).length >= 4 && (
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:T.textSecondary}}>
                    <span>Low</span>
                    {["#bfdbfe","#93c5fd","#60a5fa","#3b82f6","#2563eb","#1d4ed8","#1e3a8a"].map((c,i)=>(
                      <div key={i} style={{width:14,height:10,background:c,borderRadius:2}}/>
                    ))}
                    <span>High</span>
                  </div>
                )}
                <div style={{fontSize:11,color:T.textSecondary}}>{Object.values(filtByCountry).filter(v=>v>0).length} {Object.values(filtByCountry).filter(v=>v>0).length === 1 ? "region" : "regions"} · {dc}</div>
              </div>
            </div>
            {/* Map + compact side list (desktop) — stacked on mobile */}
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"minmax(0, 1fr) 240px",gap:14,alignItems:"start"}}>
              <WorldHeatMap byCountry={filtByCountry} totalUSD={filtTotalUSD} displayCurrency={dc} convert={convert}/>
              {/* Compact list — top 7 regions with mini bar */}
              <div style={{display:"flex",flexDirection:"column",gap:8,fontSize:11}}>
                <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:4,borderBottom:`1px solid ${T.borderSubtle}`}}>
                  Top regions
                </div>
                {(()=>{
                  const sorted = Object.entries(filtByCountry)
                    .filter(([,v])=>v>0)
                    .sort((a,b)=>b[1]-a[1]);
                  const top = sorted.slice(0,7);
                  const others = sorted.slice(7);
                  const othersSum = others.reduce((s,[,v])=>s+v,0);
                  const maxVal = Math.max(...sorted.map(([,v])=>v), 1);
                  const items = [...top.map(([iso,v])=>({iso,v,label:COUNTRY_SHAPES[iso]?.label||iso}))];
                  if (others.length > 0) {
                    items.push({iso:"_OTHERS_",v:othersSum,label:`+ ${others.length} more`});
                  }
                  if (items.length === 0) {
                    return <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic",padding:"8px 0"}}>No country data assigned to holdings.</div>;
                  }
                  return items.map(({iso,v,label})=>{
                    const pct = (v/filtTotalUSD*100);
                    const barW = (v/maxVal*100);
                    const isOthers = iso === "_OTHERS_";
                    const isExpanded = geoExpandedIso === iso;
                    const color = isOthers ? T.textMuted : (
                      pct > 50 ? "#1d4ed8" : pct > 20 ? "#2563eb" : pct > 5 ? "#3b82f6" : "#60a5fa"
                    );
                    // Compute country detail when expanded — holdings count + asset class chips
                    let countryHoldings = [];
                    let classes = [];
                    if (isExpanded && !isOthers) {
                      countryHoldings = filteredHW.filter(h => {
                        const ci = COUNTRY_ISO[h.country] || h.country;
                        return ci === iso;
                      });
                      classes = [...new Set(countryHoldings.map(h => h.class))];
                    }
                    return (
                      <div key={iso} style={{display:"flex",flexDirection:"column",gap:2}}>
                        {/* Clickable summary row */}
                        <div onClick={()=>{ if (!isOthers) setGeoExpandedIso(isExpanded ? null : iso); }}
                          style={{
                            display:"flex",flexDirection:"column",gap:2,
                            cursor: isOthers ? "default" : "pointer",
                            padding: "4px 6px",
                            margin: "-4px -6px",
                            borderRadius: 4,
                            background: isExpanded ? (T.dark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)") : "transparent",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e=>{ if (!isOthers && !isExpanded) e.currentTarget.style.background = T.dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"; }}
                          onMouseLeave={e=>{ if (!isOthers && !isExpanded) e.currentTarget.style.background = "transparent"; }}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:6}}>
                            <span style={{fontWeight:600,color:T.textPrimary,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {!isOthers && <span style={{display:"inline-block",width:6,height:6,borderRadius:1,background:color,marginRight:6,verticalAlign:"middle"}}/>}
                              {label}
                              {!isOthers && (
                                <span style={{marginLeft:5,fontSize:9,color:T.textMuted,verticalAlign:"middle",transition:"transform 0.2s",display:"inline-block",transform:isExpanded?"rotate(90deg)":"rotate(0deg)"}}>›</span>
                              )}
                            </span>
                            <span style={{fontWeight:700,color:T.textPrimary,fontSize:11,fontVariantNumeric:"tabular-nums",flexShrink:0}}>
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                          <div style={{height:4,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                            <div style={{width:`${Math.min(100,barW)}%`,height:"100%",background:color}}/>
                          </div>
                          <div style={{fontSize:9,color:T.textMuted,fontVariantNumeric:"tabular-nums"}}>
                            {fmt(convert(v,dc),dc,true)}
                          </div>
                        </div>
                        {/* Expanded detail */}
                        {isExpanded && !isOthers && (
                          <div style={{
                            padding:"8px 6px 6px",
                            borderLeft:`2px solid ${color}`,
                            marginLeft:2,marginTop:2,
                            display:"flex",flexDirection:"column",gap:6,
                          }}>
                            <div style={{fontSize:10,color:T.textSecondary,fontVariantNumeric:"tabular-nums"}}>
                              <strong style={{color:T.textPrimary,fontWeight:700}}>{countryHoldings.length}</strong> {countryHoldings.length === 1 ? "holding" : "holdings"}
                            </div>
                            {classes.length > 0 && (
                              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                                {classes.map(c => (
                                  <span key={c} style={{
                                    fontSize:9,padding:"2px 6px",borderRadius:6,
                                    background: T.dark ? "rgba(124,58,237,0.18)" : "rgba(124,58,237,0.08)",
                                    color: T.dark ? "#A78BFA" : "#7C3AED",
                                    fontWeight:600,whiteSpace:"nowrap",
                                  }}>{c}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        {/* ── SECTOR × GEOGRAPHY CONCENTRATION HEATMAP ── */}
          <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${T.borderSubtle}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <span style={{fontSize:18}}>🔥</span>
              <div style={{fontSize:15,fontWeight:700}}>Sector × Geography Concentration</div>
            </div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
              Each cell shows % of total portfolio. Darker = higher concentration. Click any cell to filter holdings.
            </div>
            {(function(){
              const sectors = [...new Set(filteredHW.map(h=>h.sector||"Other"))].sort();
              const geos    = [...new Set(filteredHW.map(h=>{
                const iso = COUNTRY_ISO[h.country]||h.country;
                const shape = COUNTRY_SHAPES?.[iso];
                return shape?.label || h.country || "Other";
              }))].sort();
              // Build matrix
              const matrix = {};
              let maxPct = 0;
              filteredHW.forEach(h => {
                const s = h.sector||"Other";
                const g = COUNTRY_SHAPES?.[COUNTRY_ISO[h.country]||h.country]?.label || h.country || "Other";
                if (!matrix[s]) matrix[s] = {};
                matrix[s][g] = (matrix[s][g]||0) + h.valueUSD;
              });
              // Compute pcts
              const cellPct = (s,g) => filtTotalUSD>0 ? ((matrix[s]?.[g]||0)/filtTotalUSD*100) : 0;
              sectors.forEach(s => geos.forEach(g => {
                const p = cellPct(s,g);
                if (p > maxPct) maxPct = p;
              }));
              const cellCol = (pct) => {
                if (pct === 0) return "transparent";
                const intensity = Math.min(1, pct / Math.max(maxPct, 1));
                if (pct >= 20) return `rgba(220,38,38,${0.15 + intensity*0.7})`;   // red — very high
                if (pct >= 10) return `rgba(217,119,6,${0.15 + intensity*0.7})`;   // amber
                if (pct >= 5)  return `rgba(37,99,235,${0.12 + intensity*0.6})`;   // blue
                return `rgba(22,163,74,${0.08 + intensity*0.5})`;                   // green — low
              };
              const cellText = (pct) =>
                pct >= 20 ? "#A32D2D" : pct >= 10 ? "#D97706" : pct >= 5 ? "#2563EB" : "#1D9E75";
              // Filter to sectors/geos with any exposure
              const activeSectors = sectors.filter(s => geos.some(g => cellPct(s,g)>0));
              const activeGeos    = geos.filter(g => sectors.some(s => cellPct(s,g)>0));
              return (
                <div style={{overflowX:"auto"}}>
                  <table style={{borderCollapse:"separate",borderSpacing:3,fontSize:11,minWidth:Math.max(500,activeGeos.length*80+120)}}>
                    <thead>
                      <tr>
                        <th style={{...tTh(),background:"transparent",border:"none",minWidth:120,textAlign:"left",fontSize:10}}>SECTOR ↓ / GEO →</th>
                        {activeGeos.map(g=>(
                          <th key={g} style={{...tTh(),minWidth:72,textAlign:"center",fontSize:9,
                            padding:"4px 6px",background:T.thBg,borderRadius:4,
                            whiteSpace:"nowrap",maxWidth:80,overflow:"hidden",textOverflow:"ellipsis"}}
                            title={g}>
                            {g.length>9?g.slice(0,8)+"…":g}
                          </th>
                        ))}
                        <th style={{...tTh(),minWidth:60,textAlign:"right",fontSize:9}}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSectors.map(s=>{
                        const rowTotal = activeGeos.reduce((sum,g)=>sum+cellPct(s,g),0);
                        return (
                          <tr key={s}>
                            <td style={{...tTd(),fontWeight:600,fontSize:11,paddingLeft:0,
                              borderBottom:"none",whiteSpace:"nowrap"}}>{s}</td>
                            {activeGeos.map(g=>{
                              const pct = cellPct(s,g);
                              return (
                                <td key={g}
                                  onClick={()=>{ if(pct>0){ setDrill("sector",s); setTab("holdings"); }}}
                                  title={pct>0?`${s} · ${g}: ${pct.toFixed(1)}% (${fmt(convert(matrix[s]?.[g]||0,dc),dc)})`:"No exposure"}
                                  style={{textAlign:"center",padding:"6px 4px",
                                    background:cellCol(pct),
                                    borderRadius:6,cursor:pct>0?"pointer":"default",
                                    border:`1px solid ${pct>0?"rgba(0,0,0,0.06)":"transparent"}`,
                                    transition:"transform 0.1s",
                                    minWidth:72,
                                  }}
                                  onMouseEnter={e=>{ if(pct>0) e.currentTarget.style.transform="scale(1.05)"; }}
                                  onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                                  {pct > 0.4 ? (
                                    <span style={{fontSize:10,fontWeight:700,color:cellText(pct),fontFamily:"'Inter', system-ui, sans-serif"}}>
                                      {pct.toFixed(1)}%
                                    </span>
                                  ) : <span style={{fontSize:9,color:T.textLight}}>·</span>}
                                </td>
                              );
                            })}
                            <td style={{textAlign:"right",padding:"6px 8px",fontWeight:700,
                              fontSize:11,color:rowTotal>=10?"#D97706":T.textSecondary,
                              fontFamily:"'Inter', system-ui, sans-serif",borderBottom:"none"}}>
                              {rowTotal.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                      {/* Column totals */}
                      <tr style={{borderTop:`2px solid ${T.cardBorder}`}}>
                        <td style={{...tTd(),fontWeight:700,fontSize:10,color:T.textMuted,paddingLeft:0}}>TOTAL</td>
                        {activeGeos.map(g=>{
                          const colTotal = activeSectors.reduce((sum,s)=>sum+cellPct(s,g),0);
                          return (
                            <td key={g} style={{textAlign:"center",padding:"6px 4px",fontWeight:700,
                              fontSize:10,color:colTotal>=20?"#A32D2D":colTotal>=10?"#D97706":T.textSecondary,
                              fontFamily:"'Inter', system-ui, sans-serif"}}>
                              {colTotal.toFixed(1)}%
                            </td>
                          );
                        })}
                        <td style={{textAlign:"right",padding:"6px 8px",fontWeight:500,
                          fontSize:11,color:T.brand,fontFamily:"'Inter', system-ui, sans-serif"}}>100%</td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Legend */}
                  <div style={{display:"flex",gap:16,marginTop:12,fontSize:10,color:T.textMuted,alignItems:"center"}}>
                    <span>Concentration:</span>
                    {[
                      {col:"rgba(22,163,74,0.4)",  label:"< 5%"},
                      {col:"rgba(37,99,235,0.4)",  label:"5–10%"},
                      {col:"rgba(217,119,6,0.4)",  label:"10–20%"},
                      {col:"rgba(220,38,38,0.4)",  label:"> 20% ⚠"},
                    ].map(({col,label})=>(
                      <div key={label} style={{display:"flex",alignItems:"center",gap:4}}>
                        <div style={{width:14,height:14,borderRadius:3,background:col}}/>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

        </React.Fragment>}

        {/* ── CURRENCY VIEW ── */}
        {tab==="currency" && <React.Fragment>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <div style={{fontSize:17,fontWeight:700}}>Portfolio in</div>
            <select value={dc} onChange={e=>setDc(e.target.value)} style={{...inp(),width:"auto",fontSize:16,fontWeight:700,padding:"8px 14px"}}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
            <div style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:fxSource==="static"?"rgba(220,38,38,0.08)":"rgba(22,163,74,0.08)",color:fxSource==="static"?"#A32D2D":"#1D9E75",border:`1px solid ${fxSource==="static"?"#A32D2D33":"#1D9E7533"}`}}>
              {fxSource==="static" ? "⚠ Static rates" : `✓ Live FX (${fxSource})`}
            </div>
          </div>
          {(()=>{
            const FX_MAJOR = ["USD","SGD","INR","EUR","GBP","HKD","AED","JPY"];
            const fxShowMore = nhCcQ === "__fxmore__";
            const displayCcys = fxShowMore ? CURRENCIES : [...new Set([...FX_MAJOR, dc])];
            const extraCount = CURRENCIES.length - FX_MAJOR.length;
            return (
              <React.Fragment>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:14,marginBottom:12}}>
                  {displayCcys.map(c=>(
                    <div key={c} onClick={()=>setDc(c)} style={{...tCard(),cursor:"pointer",
                      border:c===dc?"1px solid #2563EB":`1px solid ${T.cardBorder}`,
                      background:c===dc?"rgba(37,99,235,0.06)":T.cardBg}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                        <div style={{fontSize:15,fontWeight:700,color:c===dc?"#2563EB":T.textPrimary}}>{c}</div>
                        <div style={{fontSize:10,color:T.textSecondary}}>1 USD = {(fxRates[c]||FX_RATES_DEFAULT[c]||1).toFixed(4)}</div>
                      </div>
                      <div style={{fontSize:20,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(convert(filtTotalUSD,c),c,true)}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
                  <button onClick={()=>setNhCcQ(fxShowMore?"":"__fxmore__")}
                    style={{background:"transparent",border:`1px solid ${T.cardBorder}`,
                      borderRadius:6,padding:"5px 14px",cursor:"pointer",fontSize:12,
                      color:T.textSecondary}}>
                    {fxShowMore ? "▲ Show fewer" : `▼ Show all currencies (+${extraCount} more)`}
                  </button>
                  {!fxShowMore && <span style={{fontSize:11,color:T.textMuted}}>Showing 8 major currencies · click to expand</span>}
                </div>
              </React.Fragment>
            );
          })()}
          <div style={card()}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Holdings in {dc}</div>
            <div style={{overflowX:"auto",overflowY:"auto",maxHeight:420}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
                <thead style={{position:"sticky",top:0,zIndex:2,background:T.thBg}}><tr>{["Asset","Native Ccy","Native Value",`Value in ${dc}`,"FX Rate","Weight %"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                <tbody>
                  {[...filteredHW].sort((a,b)=>b.valueUSD-a.valueUSD).map((h,i)=>{
                    const w=(h.valueUSD/filtTotalUSD)*100;
                    return (
                      <tr key={h.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                        <td style={tTd({fontWeight:600})}>{h.name}</td>
                        <td style={tTd()}>{h.currency}</td>
                        <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(h.qty*h.currentPrice,h.currency,true)}</td>
                        <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>{fmt(convert(h.valueUSD,dc),dc)}</td>
                        <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,color:T.textSecondary})}>1 {h.currency} = {((fxRates[dc]||1)/(fxRates[h.currency]||1)).toFixed(4)} {dc}</td>
                        <td style={tTd()}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{flex:1,height:5,background:T.borderSubtle,borderRadius:3}}><div style={{width:`${w}%`,height:"100%",background:"#2563EB",borderRadius:3}}/></div>
                            <span style={{fontSize:12,color:T.textSecondary,width:38,textAlign:"right"}}>{w.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>


          {/* ── FX Risk Analysis ── */}
          <div style={{display:"flex",alignItems:"center",gap:12,margin:"24px 0 14px",
            paddingTop:20,borderTop:`1px solid ${T.borderSubtle}`}}>
            <span style={{fontSize:18}}>⚠️</span>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:T.textPrimary}}>FX Risk Analysis</div>
              <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                Currency exposure concentration, sensitivity and target allocation
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:16}}>

            {/* Exposure concentration */}
            <div style={card()}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Currency Concentration Risk</div>
              {Object.entries(filtByCurrency).sort((a,b)=>b[1]-a[1]).map(([ccy,val])=>{
                const pct = filtTotalUSD>0 ? val/filtTotalUSD*100 : 0;
                const risk = pct>60?"#A32D2D":pct>40?"#D97706":"#1D9E75";
                const riskLabel = pct>60?"High":pct>40?"Moderate":"Low";
                return (
                  <div key={ccy} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",marginBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:13,fontWeight:700,color:T.textPrimary,
                          fontFamily:"'Inter', system-ui, sans-serif",width:36}}>{ccy}</span>
                        <span style={{fontSize:10,fontWeight:600,color:risk,
                          background:`${risk}12`,padding:"1px 6px",borderRadius:3}}>
                          {riskLabel}
                        </span>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <span style={{fontSize:12,fontWeight:700,
                          fontFamily:"'Inter', system-ui, sans-serif"}}>{pct.toFixed(1)}%</span>
                        <span style={{fontSize:10,color:T.textMuted,marginLeft:6}}>
                          {fmt(convert(val,dc),dc)}
                        </span>
                      </div>
                    </div>
                    <div style={{height:6,background:T.borderSubtle,borderRadius:3}}>
                      <div style={{height:"100%",width:`${pct}%`,borderRadius:3,
                        background:`linear-gradient(90deg,${risk},${risk}88)`,
                        transition:"width 0.6s ease"}}/>
                    </div>
                  </div>
                );
              })}
              {Object.keys(filtByCurrency).length === 0 && (
                <div style={{color:T.textMuted,fontSize:12,textAlign:"center",padding:20}}>
                  No holdings to analyse
                </div>
              )}
            </div>

            {/* FX sensitivity — how much portfolio changes for 10% move */}
            <div style={card()}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>FX Sensitivity</div>
              <div style={{fontSize:11,color:T.textSecondary,marginBottom:14}}>
                Impact of a ±10% move in each currency on your {dc} portfolio value
              </div>
              {Object.entries(filtByCurrency).sort((a,b)=>b[1]-a[1]).map(([ccy,val])=>{
                if (ccy === dc) return null;
                const impact10 = val * 0.10; // 10% FX move
                const impactPct = filtTotalUSD>0 ? impact10/filtTotalUSD*100 : 0;
                return (
                  <div key={ccy} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",padding:"8px 0",
                    borderBottom:`1px solid ${T.borderSubtle}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:12,fontWeight:700,color:T.textPrimary,
                        fontFamily:"'Inter', system-ui, sans-serif",width:36}}>{ccy}</span>
                      <span style={{fontSize:11,color:T.textSecondary}}>
                        1 {ccy} = {((fxRates[dc]||1)/(fxRates[ccy]||1)).toFixed(3)} {dc}
                      </span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#1D9E75",
                        fontFamily:"'Inter', system-ui, sans-serif"}}>
                        +{fmt(convert(impact10,dc),dc)}
                      </div>
                      <div style={{fontSize:10,color:"#A32D2D"}}>
                        −{fmt(convert(impact10,dc),dc)} ({impactPct.toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                );
              }).filter(Boolean)}
              <div style={{marginTop:12,fontSize:10,color:T.textMuted,
                padding:"8px 10px",background:T.borderSubtle,borderRadius:6}}>
                💡 Consider hedging currencies where exposure &gt;30% of portfolio
              </div>
            </div>
          </div>

          {/* ── Target vs Actual Allocation ── */}
          {(function(){
            const MODEL_CCY = [
              {currency:"USD",targetPct:50,note:"Global reserve — stable base"},
              {currency:"INR",targetPct:25,note:"India domestic exposure"},
              {currency:"EUR",targetPct:15,note:"European diversification"},
              {currency:"GBP",targetPct:10,note:"UK + global allocation"},
            ];
            const hasAnyTarget = MODEL_CCY.some(m => filtByCurrency[m.currency]);
            if (!hasAnyTarget) return null;
            return (
              <div style={card()}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Target vs Actual Allocation</div>
                <div style={{fontSize:11,color:T.textSecondary,marginBottom:14}}>
                  Suggested model allocation vs your current exposure
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {MODEL_CCY.map(m => {
                    const actual = filtTotalUSD>0 ? ((filtByCurrency[m.currency]||0)/filtTotalUSD*100) : 0;
                    const diff   = actual - m.targetPct;
                    const isOver = diff > 5; const isUnder = diff < -5;
                    return (
                      <div key={m.currency}>
                        <div style={{display:"flex",justifyContent:"space-between",
                          alignItems:"center",marginBottom:5}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:13,fontWeight:700,width:36,
                              color:T.textPrimary,fontFamily:"'Inter', system-ui, sans-serif"}}>
                              {m.currency}
                            </span>
                            <span style={{fontSize:10,color:T.textMuted}}>{m.note}</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:11,color:T.textMuted}}>
                              Target: {m.targetPct}%
                            </span>
                            <span style={{fontSize:12,fontWeight:700,
                              color:isOver?"#A32D2D":isUnder?"#D97706":"#1D9E75"}}>
                              Actual: {actual.toFixed(1)}%
                              {isOver?" ▲":isUnder?" ▼":" ✓"}
                            </span>
                          </div>
                        </div>
                        <div style={{height:8,background:T.borderSubtle,
                          borderRadius:4,position:"relative",overflow:"visible"}}>
                          {/* Actual bar */}
                          <div style={{position:"absolute",top:0,left:0,height:"100%",
                            borderRadius:4,background:isOver?"#A32D2D":isUnder?"#D97706":"#1D9E75",
                            width:`${Math.min(100,actual)}%`,opacity:0.7,transition:"width 0.6s"}}/>
                          {/* Target marker */}
                          <div style={{position:"absolute",top:-2,bottom:-2,width:2,
                            background:T.brand,borderRadius:1,
                            left:`${m.targetPct}%`}}/>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{fontSize:10,color:T.textMuted,
                    display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                    <span style={{display:"inline-block",width:12,height:3,
                      background:T.brand,borderRadius:1}}/>
                    Blue line = target allocation
                  </div>
                </div>
              </div>
            );
          })()}
        </React.Fragment>}

        {/* ── AI RESEARCH ── */}
        {tab==="research" && (function(){ try {
          // ── helpers ──
          // Use darker colours readable in both dark and light themes
          const sentimentColor = s => s==="Bullish"?"#1D9E75":s==="Bearish"?"#A32D2D":"#D97706";
          const ratingColor    = r => {
            if (!r) return "#D97706";
            const ru = r.toUpperCase();
            if (ru.includes("STRONG BUY") || ru.includes("BUY")) return "#1D9E75";
            if (ru.includes("STRONG SELL") || ru.includes("SELL") || ru.includes("EXIT") || ru.includes("REDUCE")) return "#A32D2D";
            if (ru.includes("ADD")) return "#1D9E75";
            return "#D97706"; // Hold, Neutral
          };
          const severityColor  = s => s==="High"?"#F87171":s==="Medium"?"#FBBF24":"#34D399";
          const impactColor    = i => i==="Positive"||i==="positive"?"#1D9E75":i==="Negative"||i==="negative"?"#A32D2D":"#64748B";
          const cacheAge       = ts => ts ? Math.round((Date.now()-ts)/60000) : null;

          // ── Client list for selector ──
          const researchClients = isAdmin
            ? visibleClients.map(c => {
                const cHW = hwWithOwner.filter(h=>h.clientId===c.id);
                const totalUSD = cHW.reduce((s,h)=>s+h.valueUSD,0);
                const costUSD  = cHW.reduce((s,h)=>s+h.costUSD,0);
                const gainPct  = costUSD>0?(totalUSD-costUSD)/costUSD*100:0;
                const listedHW = cHW.filter(h=>h.listed&&h.ticker);
                // Count holdings with any ticker (not just listed=true) for research
                const tickerCount = cHW.filter(h=>h.ticker&&h.ticker!=='-').length;
                return { ...c, totalUSD, gainPct, holdings: cHW, listedCount: tickerCount };
              }).filter(c=>c.listedCount>0).sort((a,b)=>b.totalUSD-a.totalUSD)
            : (()=>{
                const c = visibleClients.find(x=>x.id===loggedInClientId);
                if (!c) return [];
                const cHW = hwWithOwner.filter(h=>h.clientId===c.id);
                const totalUSD = cHW.reduce((s,h)=>s+h.valueUSD,0);
                const costUSD  = cHW.reduce((s,h)=>s+h.costUSD,0);
                const gainPct  = costUSD>0?(totalUSD-costUSD)/costUSD*100:0;
                return [{ ...c, totalUSD, gainPct, holdings:cHW, listedCount:cHW.filter(h=>h.listed&&h.ticker).length }];
              })();

          const activeResearchClient = researchClients.find(c=>c.id===researchClient) || researchClients[0];
          const clientData  = activeResearchClient ? researchData[activeResearchClient.id] : null;
          const isLoading   = activeResearchClient ? !!researchLoading[activeResearchClient.id] : false;
          const ageMin      = clientData ? cacheAge(clientData.ts) : null;
          const progress    = activeResearchClient ? researchProgress[activeResearchClient.id] : null;

          // ── Run research for a client ──
          const safeGain = (g) => (g==null||!isFinite(g)||isNaN(g)) ? "N/A" : `${g>=0?"+":""}${g.toFixed(1)}%`;

          // Robust JSON parser — handles unescaped chars in Claude responses
          const parseResearchJSON = (text) => {
            if (!text) return null;
            try { return JSON.parse(text); } catch {}
            const start = text.indexOf("{");
            const end   = text.lastIndexOf("}");
            if (start === -1 || end === -1) return null;
            let chunk = text.slice(start, end + 1);
            // Remove ASCII control chars except tab, LF, CR
            chunk = chunk.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, " ");
            try { return JSON.parse(chunk); } catch {}
            // Strip trailing commas as last resort
            try { return JSON.parse(chunk.replace(/,\s*([}\]])/g, "$1")); } catch {}
            return null;
          };

          // Research one asset via /api/research (server-side proxy — Anthropic blocks browser CORS)
          const fetchAsset = async (h, totalV) => {
            const r = await fetch("/api/research", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                provider: (aiConfig||{}).researchProvider || "anthropic",
                model: (aiConfig||{}).researchModel || "claude-haiku-4-5-20251001",
                apiKey: (aiConfig||{}).researchProvider === "openai" ? (aiConfig||{}).openaiKey : (aiConfig||{}).researchProvider === "gemini" ? (aiConfig||{}).geminiKey : undefined,
                mode: "asset",
                provider: aiConfig?.researchProvider || "anthropic",
                model: aiConfig?.researchModel || "claude-haiku-4-5-20251001",
                apiKey: aiConfig?.researchProvider === "openai" ? aiConfig?.openaiKey
                  : aiConfig?.researchProvider === "gemini" ? aiConfig?.geminiKey
                  : undefined,
                ticker: h.ticker || "",
                name: h.name,
                assetClass: h.class,
                weight: h.valueUSD / totalV * 100,
                gainPct: isFinite(h.gainPct) ? h.gainPct : 0,
                valueUSD: h.valueUSD,
                // Extra context — particularly useful for unlisted assets where ticker is "-"
                listed: h.listed !== false,
                manager: h.manager || "",
                country: h.country || "",
                sector: h.sector || "",
              }),
            });
            const raw = await r.text();
            const json = parseResearchJSON(raw);
            if (!r.ok) throw new Error(json?.error || `HTTP ${r.status}: ${raw.slice(0,120)}`);
            if (!json) throw new Error("Could not parse research API response");
            return json;
          };

          const runResearch = async (client) => {
            if (!client) return;
            setResearchLoading(p => ({...p, [client.id]: true}));
            setResearchError(p => ({...p, [client.id]: null}));
            // ── Apply AI filter (asset class / region / sector) to the client's holdings ──
            // Same pattern as AI Advisor. Filter the holdings universe before any candidates/dedupe/summary.
            const researchFilter = getEffectiveAiFilter("research");
            const filterLabel = describeAiFilter(researchFilter);
            const filteredHoldings = applyAiFilter(client.holdings, researchFilter);
            const totalV = filteredHoldings.reduce((s,h) => s+h.valueUSD, 0) || 1;
            // Cap at 25 (was 15). Tier 1 Anthropic = 50 RPM; 25 + 1 summary fits comfortably.
            // If user toggled "include unlisted", drop the listed filter — AI will get fewer
            // ticker-anchored fields but still useful name/manager/sector context.
            const RESEARCH_ASSET_CAP = 25;
            const candidates = researchIncludeUnlisted
              ? filteredHoldings.filter(h => h.name)               // any holding with a name
              : filteredHoldings.filter(h => h.listed && h.ticker); // listed-only (legacy)
            // ── Dedupe by ticker (or name+manager for unlisted) ──
            // Same ticker = same company = same research output. If a client holds
            // Citigroup across 2 brokerage accounts, we research it once. Display
            // can still show both rows pointing to the same result.
            // Dedup key: ticker if present, else name|manager fallback for unlisted.
            const dedupKey = (h) => {
              const t = (h.ticker || "").trim();
              if (t && t !== "-") return `t:${t.toUpperCase()}`;
              return `n:${(h.name||"").trim().toLowerCase()}|${(h.manager||"").trim().toLowerCase()}`;
            };
            const seenKeys = new Map(); // dedupKey → highest-value holding
            for (const h of candidates) {
              const k = dedupKey(h);
              const existing = seenKeys.get(k);
              if (!existing || (h.valueUSD||0) > (existing.valueUSD||0)) {
                seenKeys.set(k, h);
              }
            }
            const dedupedCandidates = Array.from(seenKeys.values());
            const listedAssets = dedupedCandidates
              .sort((a,b) => b.valueUSD - a.valueUSD)
              .slice(0, RESEARCH_ASSET_CAP);

            try {
              // Quick connectivity test — check /api/research returns at all
              const testR = await fetch("/api/research", {
                method:"POST", headers:{"Content-Type":"application/json"},
                body:JSON.stringify({mode:"ping"})
              }).catch(() => null);
              if (!testR) {
                setResearchError(p=>({...p,[client.id]:"Cannot reach /api/research — check Vercel deployment"}));
                return;
              }
              const testJson = await testR.json().catch(()=>({}));
              if (testR.status === 500 && testJson.error?.includes("ANTHROPIC_API_KEY")) {
                setResearchError(p=>({...p,[client.id]:"ANTHROPIC_API_KEY not set in Vercel environment variables"}));
                return;
              }

              // ── Assets: parallel batches of 3 ──
              const assetResults = {};
              const batches = [];
              for (let i = 0; i < listedAssets.length; i += 3)
                batches.push(listedAssets.slice(i, i + 3));
              setResearchProgress(p=>({...p,[client.id]:{done:0,total:listedAssets.length}}));
              for (const batch of batches) {
                const settled = await Promise.allSettled(batch.map(h => fetchAsset(h, totalV)));
                settled.forEach((res, idx) => {
                  // Key by holding.id (unique) not ticker (may be "-" for unlisted assets,
                  // which would cause collisions when multiple unlisted are researched).
                  const key = batch[idx].id != null ? `id:${batch[idx].id}` : (batch[idx].ticker || `name:${batch[idx].name}`);
                  if (res.status === "fulfilled") assetResults[key] = res.value;
                  else console.warn("[Research] asset failed:", batch[idx].name, res.reason?.message);
                });
                const done = Math.min(Object.keys(assetResults).length + settled.filter(s=>s.status==="rejected").length, listedAssets.length);
                setResearchProgress(p=>({...p,[client.id]:{done,total:listedAssets.length}}));
                if (batches.indexOf(batch) < batches.length - 1)
                  await new Promise(r => setTimeout(r, 300));
              }

              // ── Portfolio summary ──
              let summary = null;
              try {
                const r = await fetch("/api/research", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    mode: "summary",
                    filterContext: filterLabel,
                    client: {
                      name: client.name,
                      holdings: filteredHoldings.map(h => ({
                        name: h.name, ticker: h.ticker, class: h.class,
                        weight: h.valueUSD / totalV * 100,
                        gainPct: isFinite(h.gainPct) ? h.gainPct : 0,
                        valueUSD: h.valueUSD,
                      })),
                      totalUSD: filteredHoldings.reduce((s,h)=>s+(h.valueUSD||0),0),
                      gainPct: isFinite(client.gainPct) ? client.gainPct : 0,
                      riskProfile: client.riskProfile || "Moderate",
                    },
                  }),
                });
                const sRaw = await r.text();
                const sj = parseResearchJSON(sRaw);
                if (r.ok && sj) summary = sj;
                else console.warn("[Research] summary HTTP", r.status, sj?.error || sRaw.slice(0,120));
              } catch(e) { console.warn("[Research] summary failed:", e.message); }

              const anyResults = Object.keys(assetResults).length > 0 || summary;
              if (!anyResults) {
                setResearchError(p=>({...p,[client.id]:"All research calls failed — check Vercel function logs for ANTHROPIC_API_KEY errors"}));
              }
              const finalData = { assets: assetResults, summary, ts: Date.now() };
              setResearchData(p => ({...p, [client.id]: finalData}));

              // ── Auto-archive successful runs ─────────────────────────────
              // The "Save to Archive" button still exists for manual checkpoint
              // saves, but auto-archiving ensures the user never loses a run
              // they meant to keep. We dedupe by clipping to last 50 entries.
              if (anyResults) {
                try {
                  const archiveEntry = {
                    id: `research_${Date.now()}_${client.id}`,
                    date: new Date().toISOString(),
                    clientId: client.id,
                    clientName: client.name,
                    totalUSD: client.totalUSD,
                    gainPct: client.gainPct,
                    holdingCount: (client.holdings || []).length,
                    filterContext: filterLabel,
                    portfolioSnapshot: (client.holdings || []).map(h => ({
                      id: h.id, name: h.name, ticker: h.ticker, class: h.class,
                      valueUSD: h.valueUSD, gainPct: h.gainPct, currency: h.currency
                    })),
                    researchData: finalData,
                    autoSaved: true,
                  };
                  setResearchArchive(prev => {
                    const next = [archiveEntry, ...prev].slice(0, 50);
                    try { localStorage.setItem("forthius_research_archive", JSON.stringify(next)); } catch {}
                    upsertProfile("__research_archive__", next).catch(err => {
                      console.warn("[Research auto-archive] cloud sync failed:", err?.message || err);
                    });
                    return next;
                  });
                  console.log("[Research] Auto-archived run for", client.name);
                } catch (err) {
                  console.warn("[Research] Auto-archive failed:", err?.message || err);
                }
              }
            } catch(e) {
              setResearchError(p=>({...p,[client.id]:e.message}));
            } finally {
              setResearchLoading(p => ({...p, [client.id]: false}));
            }
          };

          const clientError = activeResearchClient ? researchError[activeResearchClient.id] : null;

          return (
            <div>
              {/* Header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:17,fontWeight:700}}>🤖 AI Research</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                    AI research powered by Claude · Per-asset analysis & portfolio summaries
                  </div>
                </div>
                {activeResearchClient && (
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    {ageMin !== null && (
                      <span style={{fontSize:11,color:T.textMuted}}>
                        Last run {ageMin < 1 ? "just now" : `${ageMin}m ago`}
                      </span>
                    )}
                    {/* View Archive button — scrolls to and opens the archive section.
                        Only renders when this client has saved entries, so empty state
                        doesn't show a useless button. */}
                    {(() => {
                      const myArchive = researchArchive.filter(e => e.clientId === activeResearchClient.id);
                      if (myArchive.length === 0) return null;
                      return (
                        <button
                          onClick={()=>{
                            setArchiveOpen(true);
                            // Scroll to the archive header — wait a tick so the toggle has applied
                            setTimeout(()=>{
                              const el = document.getElementById("research-archive-anchor");
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 50);
                          }}
                          style={{display:"flex",alignItems:"center",gap:5,padding:"8px 14px",borderRadius:8,
                            border:`1px solid ${T.cardBorder}`,background:"transparent",
                            color:T.textPrimary,cursor:"pointer",fontSize:12,fontWeight:600}}>
                          📁 View Archive
                          <span style={{fontSize:10,background:"rgba(129,140,248,0.18)",color:"#818CF8",
                            padding:"1px 6px",borderRadius:8,fontWeight:700,marginLeft:2}}>
                            {myArchive.length}
                          </span>
                        </button>
                      );
                    })()}
                    {clientData && !isLoading && (
                      <button
                        onClick={()=>{
                          const entry = {
                            id: Date.now().toString(),
                            date: new Date().toISOString(),
                            clientId: activeResearchClient.id,
                            clientName: activeResearchClient.name,
                            totalUSD: activeResearchClient.totalUSD,
                            gainPct: activeResearchClient.gainPct,
                            holdingCount: activeResearchClient.holdings.length,
                            portfolioSnapshot: activeResearchClient.holdings.map(h=>({
                              id:h.id, name:h.name, ticker:h.ticker, class:h.class,
                              valueUSD:h.valueUSD, gainPct:h.gainPct, currency:h.currency
                            })),
                            researchData: clientData,
                          };
                          const next = [entry, ...researchArchive].slice(0, 50); // keep last 50
                          setResearchArchive(next);
                          try { localStorage.setItem("forthius_research_archive", JSON.stringify(next)); } catch {}
                          // Cloud is source of truth — push and toast on failure so the user knows
                          // their save didn't sync (rare; localStorage cache still preserves it).
                          upsertProfile("__research_archive__", next).catch(e => {
                            console.warn("[Research archive] cloud save failed:", e.message);
                            showToast("⚠ Saved locally, cloud sync failed", "#D97706", 4000);
                          });
                          showToast("✓ Research checkpoint saved", "#1D9E75", 3000);
                        }}
                        title="Save a manual checkpoint. Successful runs are auto-archived; use this only if you want to lock in a snapshot now."
                        style={{display:"flex",alignItems:"center",gap:5,padding:"8px 14px",borderRadius:8,
                          border:"1px solid #1D9E75",background:"rgba(22,163,74,0.08)",
                          color:"#1D9E75",cursor:"pointer",fontSize:12,fontWeight:600}}>
                        💾 Save Checkpoint
                      </button>
                    )}
                    {/* Per-run toggle: include unlisted assets in research */}
                    {(() => {
                      if (!activeResearchClient) return null;
                      const unlistedCount = activeResearchClient.holdings.filter(h => !h.listed || !h.ticker).length;
                      return (
                        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,
                          color:T.textMuted,cursor:isLoading?"not-allowed":"pointer",
                          padding:"6px 10px",borderRadius:8,
                          border:`1px solid ${T.cardBorder}`,
                          background:researchIncludeUnlisted ? "rgba(217,119,6,0.08)" : "transparent",
                          opacity: isLoading ? 0.5 : 1,
                          userSelect:"none"}}>
                          <input type="checkbox" disabled={isLoading}
                            checked={researchIncludeUnlisted}
                            onChange={e=>setResearchIncludeUnlisted(e.target.checked)}
                            style={{margin:0,cursor:isLoading?"not-allowed":"pointer"}}/>
                          <span style={{whiteSpace:"nowrap"}}>
                            Include unlisted
                            {unlistedCount > 0 && (
                              <span style={{color:"#D97706",marginLeft:4}}>({unlistedCount})</span>
                            )}
                          </span>
                        </label>
                      );
                    })()}
                    <button
                      onClick={()=>runResearch(activeResearchClient)}
                      disabled={isLoading}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"8px 18px",borderRadius:8,
                        border:`1px solid ${isLoading?"#475569":"#818CF8"}`,
                        background:isLoading?"transparent":"rgba(129,140,248,0.12)",
                        color:isLoading?T.textMuted:"#818CF8",cursor:isLoading?"not-allowed":"pointer",
                        fontSize:12,fontWeight:600,transition:"all 0.15s"}}>
                      {isLoading
                        ? <React.Fragment><span style={{display:"inline-block",width:12,height:12,border:"2px solid #818CF8",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/> {progress ? `${progress.done}/${progress.total} assets…` : "Starting…"}</React.Fragment>
                        : <React.Fragment>{clientData ? "🔄 Refresh" : "▶ Run Research"}</React.Fragment>}
                    </button>
                  </div>
                )}
              </div>

              {/* ── AI FILTER BAR ── */}
              {/* Scopes which holdings the AI Research run sees. Same control as on AI Advisor;
                  the filter state is shared (aiFilters.global) so toggling on one screen affects
                  the other. Only render when a client is selected since the chip universe
                  (asset classes / sectors) is derived from that client's holdings. */}
              {activeResearchClient && renderAiFilterBar(activeResearchClient.holdings || [])}

              {/* Client selector — hidden when already filtered to a single client */}
              {researchClients.length > 1 && !viewAsClientId && selectedClients.size !== 1 && (
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
                  {researchClients.map(c=>{
                    const isActive = c.id === (activeResearchClient?.id);
                    const hasData  = !!researchData[c.id];
                    return (
                      <button key={c.id}
                        onClick={()=>setResearchClient(c.id)}
                        style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,
                          border:`1.5px solid ${isActive?"#818CF8":T.cardBorder}`,
                          background:isActive?"rgba(129,140,248,0.10)":"transparent",
                          color:isActive?"#818CF8":T.textSecondary,
                          cursor:"pointer",fontSize:12,fontWeight:isActive?700:400,transition:"all 0.15s"}}>
                        <span>👤</span>
                        <span>{c.name.split(" ")[0]}</span>
                        {hasData && <span style={{fontSize:9,background:"#34D39920",color:"#34D399",borderRadius:6,padding:"1px 5px",fontWeight:700}}>✓</span>}
                        <span style={{fontSize:9,background:T.borderSubtle,color:T.textMuted,borderRadius:8,padding:"1px 5px"}}>{c.listedCount}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── Error banner ── */}
              {clientError && (
                <div style={{background:"rgba(239,68,68,0.10)",border:"1px solid rgba(239,68,68,0.35)",borderRadius:10,padding:"12px 16px",marginBottom:18,display:"flex",gap:10,alignItems:"flex-start"}}>
                  <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#F87171",marginBottom:3}}>Research failed</div>
                    <div style={{fontSize:11,color:"#FCA5A5",fontFamily:"monospace"}}>{clientError}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:5}}>
                      Most likely cause: <strong style={{color:T.textSecondary}}>ANTHROPIC_API_KEY</strong> not set in Vercel → Project Settings → Environment Variables. Also ensure <code style={{background:"rgba(255,255,255,0.05)",padding:"0 4px",borderRadius:3}}>research.js</code> and <code style={{background:"rgba(255,255,255,0.05)",padding:"0 4px",borderRadius:3}}>claude.js</code> are deployed to <code style={{background:"rgba(255,255,255,0.05)",padding:"0 4px",borderRadius:3}}>api/</code>.
                    </div>
                  </div>
                </div>
              )}

              {!activeResearchClient && (
                <div style={{...card(),textAlign:"center",padding:60,color:T.textMuted}}>
                  <div style={{fontSize:36,marginBottom:12}}>🤖</div>
                  <div style={{fontSize:14,fontWeight:600,color:T.textSecondary,marginBottom:6}}>No clients with listed holdings</div>
                  <div style={{fontSize:12}}>Add listed equity/ETF holdings to enable AI research</div>
                </div>
              )}

              {activeResearchClient && !clientData && !isLoading && (
                <div style={{...card(),textAlign:"center",padding:60}}>
                  <div style={{fontSize:42,marginBottom:14}}>🔍</div>
                  <div style={{fontSize:15,fontWeight:700,color:T.textPrimary,marginBottom:6}}>
                    Ready to research {activeResearchClient.name}'s portfolio
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:6}}>
                    {activeResearchClient.listedCount} listed assets · {fmt(convert(activeResearchClient.totalUSD,dc),dc)} AUM
                  </div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:24,maxWidth:440,margin:"0 auto 24px"}}>
                    Claude will analyse each holding using its financial knowledge and generate a full portfolio
                    summary with risk flags and rebalancing suggestions.
                  </div>
                  <button onClick={()=>runResearch(activeResearchClient)}
                    style={{padding:"10px 28px",borderRadius:10,border:"none",
                      background:"linear-gradient(135deg,#818CF8,#A78BFA)",
                      color:"white",cursor:"pointer",fontSize:13,fontWeight:700,
                      boxShadow:"0 4px 16px rgba(129,140,248,0.35)"}}>
                    ▶ Run AI Research
                  </button>
                </div>
              )}

              {activeResearchClient && isLoading && (
                <div style={{...card(),textAlign:"center",padding:60}}>
                  <div style={{fontSize:42,marginBottom:16}}>
                    <span style={{display:"inline-block",animation:"spin 1s linear infinite"}}>⚙️</span>
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:T.textPrimary,marginBottom:6}}>
                    Researching {activeResearchClient.name}'s portfolio…
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:4}}>
                    Searching web for news, analyst ratings & price targets
                  </div>
                  <div style={{fontSize:11,color:T.textMuted}}>
                    This takes 30–60 seconds · Analysing up to {Math.min(activeResearchClient.listedCount,15)} assets
                  </div>
                </div>
              )}

              {activeResearchClient && clientData && !isLoading && (()=>{
                const { summary, assets } = clientData;
                const totalV = activeResearchClient.holdings.reduce((acc,h)=>acc+h.valueUSD,0)||1;
                // Mirror the run-side filter so render shows every asset that was researched.
                // Cap matches RESEARCH_ASSET_CAP (25). When the user toggled "include unlisted",
                // unlisted holdings are also rendered.
                const RENDER_ASSET_CAP = 25;
                const renderCandidates = researchIncludeUnlisted
                  ? activeResearchClient.holdings.filter(h => h.name)
                  : activeResearchClient.holdings.filter(h => h.listed && h.ticker && h.ticker !== '-');
                // Dedupe same way as run-side: collapse same-ticker rows into one,
                // aggregating valueUSD so the displayed weight reflects total exposure.
                // Mirrors dedupKey logic in runResearch().
                const renderDedup = new Map();
                for (const h of renderCandidates) {
                  const t = (h.ticker || "").trim();
                  const k = (t && t !== "-")
                    ? `t:${t.toUpperCase()}`
                    : `n:${(h.name||"").trim().toLowerCase()}|${(h.manager||"").trim().toLowerCase()}`;
                  const existing = renderDedup.get(k);
                  if (!existing) {
                    renderDedup.set(k, { ...h, _aggregateValueUSD: h.valueUSD||0, _dupCount: 1 });
                  } else {
                    // Aggregate value across duplicate rows; keep the larger row's metadata as canonical
                    existing._aggregateValueUSD += (h.valueUSD||0);
                    existing._dupCount += 1;
                    if ((h.valueUSD||0) > (existing.valueUSD||0)) {
                      // Promote the larger row's identity (id, costPrice, etc.) so the lookup key
                      // matches whichever holding the run-side picked as canonical.
                      Object.assign(existing, h, {
                        _aggregateValueUSD: existing._aggregateValueUSD,
                        _dupCount: existing._dupCount,
                      });
                    }
                  }
                }
                // Replace each entry's valueUSD with the aggregate so weight % reflects total exposure
                const dedupedForRender = Array.from(renderDedup.values()).map(h => ({
                  ...h,
                  valueUSD: h._aggregateValueUSD,
                }));
                const listedAssets = dedupedForRender
                  .sort((a,b)=>b.valueUSD-a.valueUSD)
                  .slice(0, RENDER_ASSET_CAP);

                return (
                  <div data-research-summary-anchor>
                    {/* ── PORTFOLIO SUMMARY SECTION ── */}
                    {summary && (
                      <div style={{marginBottom:20}}>
                        {/* Overall rating badge + header */}
                        <div style={{...card(),marginBottom:12,background:"linear-gradient(135deg,rgba(129,140,248,0.08),rgba(167,139,250,0.06))"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:8}}>
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:20}}>🤖</span>
                              <div>
                                <div style={{fontSize:14,fontWeight:700,color:T.textPrimary}}>
                                  {activeResearchClient.name} — Portfolio Summary
                                </div>
                                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                                  AI-generated · {summary.dataAsOf || "today"} · {activeResearchClient.listedCount} holdings analysed
                                </div>
                              </div>
                            </div>
                            {summary.overallRating && (
                              <span style={{...badge(ratingColor(summary.overallRating)),fontSize:11,padding:"4px 12px"}}>
                                {summary.overallRating}
                              </span>
                            )}
                          </div>

                          {/* One para summary */}
                          {summary.oneParaSummary && (
                            <div style={{fontSize:12.5,color:T.textSecondary,lineHeight:1.7,marginBottom:16,
                              padding:"12px 16px",background:T.surfaceBg,borderRadius:10,
                              borderLeft:`3px solid #818CF8`}}>
                              {summary.oneParaSummary}
                            </div>
                          )}

                          {/* Market context */}
                          {summary.marketContext && (
                            <div style={{fontSize:11.5,color:T.textMuted,fontStyle:"italic",marginBottom:16}}>
                              📊 {summary.marketContext}
                            </div>
                          )}

                          {/* Health narrative */}
                          {summary.healthNarrative && (
                            <div>
                              <div style={{fontSize:12,fontWeight:700,color:T.textSecondary,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.07em"}}>Portfolio Health</div>
                              <div style={{fontSize:12.5,color:T.textSecondary,lineHeight:1.75,whiteSpace:"pre-line"}}>
                                {summary.healthNarrative}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Risks, Opportunities, Rebalancing grid */}
                        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:12,marginBottom:12}}>
                          {/* Key Risks */}
                          {summary.keyRisks?.length > 0 && (
                            <div style={card()}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                                <span style={{fontSize:16}}>⚠️</span>
                                <span style={{fontSize:13,fontWeight:700}}>Key Risks</span>
                              </div>
                              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                                {summary.keyRisks.map((r,i)=>(
                                  <div key={i} style={{padding:"10px 12px",background:T.surfaceBg,borderRadius:8,
                                    borderLeft:`3px solid ${severityColor(r.severity)}`}}>
                                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                                      <span style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{r.risk}</span>
                                      <span style={{fontSize:9,fontWeight:700,color:severityColor(r.severity),
                                        background:`${severityColor(r.severity)}18`,borderRadius:6,padding:"1px 6px"}}>
                                        {r.severity}
                                      </span>
                                    </div>
                                    <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>{r.detail}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Opportunities */}
                          {summary.keyOpportunities?.length > 0 && (
                            <div style={card()}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                                <span style={{fontSize:16}}>💡</span>
                                <span style={{fontSize:13,fontWeight:700}}>Opportunities</span>
                              </div>
                              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                                {summary.keyOpportunities.map((o,i)=>(
                                  <div key={i} style={{padding:"10px 12px",background:T.surfaceBg,borderRadius:8,
                                    borderLeft:"3px solid #34D399"}}>
                                    <div style={{fontSize:11,fontWeight:700,color:T.textPrimary,marginBottom:4}}>{o.opportunity}</div>
                                    <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>{o.detail}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Rebalancing actions */}
                          {summary.rebalancingActions?.length > 0 && (
                            <div style={card()}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                                <span style={{fontSize:16}}>⚖️</span>
                                <span style={{fontSize:13,fontWeight:700}}>Rebalancing Actions</span>
                              </div>
                              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                                {summary.rebalancingActions.map((a,i)=>{
                                  const ac = ratingColor(a.action);
                                  return (
                                    <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",
                                      padding:"9px 12px",background:T.surfaceBg,borderRadius:8}}>
                                      <span style={{fontSize:10,fontWeight:700,color:ac,background:`${ac}18`,
                                        border:`1px solid ${ac}40`,borderRadius:6,padding:"2px 8px",
                                        flexShrink:0,marginTop:1}}>{a.action}</span>
                                      <div>
                                        <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:2}}>{a.holding}</div>
                                        <div style={{fontSize:11,color:T.textMuted,lineHeight:1.4}}>{a.rationale}</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {summary.advisorNote && (
                                <div style={{marginTop:12,padding:"9px 12px",background:"rgba(129,140,248,0.08)",
                                  borderRadius:8,borderTop:`1px solid ${T.borderSubtle}`}}>
                                  <div style={{fontSize:10,fontWeight:700,color:"#818CF8",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.06em"}}>Advisor Note</div>
                                  <div style={{fontSize:11.5,color:T.textSecondary,lineHeight:1.5}}>{summary.advisorNote}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── PER-ASSET CARDS ── */}
                    <div style={{...card(),marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                        <span style={{fontSize:16}}>📊</span>
                        <span style={{fontSize:13,fontWeight:700}}>Asset Research</span>
                        <span style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,borderRadius:8,padding:"1px 8px"}}>
                          {listedAssets.length} holdings
                        </span>
                      </div>

                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {listedAssets.map(h=>{
                          // Lookup matches new storage scheme: id-based first, ticker fallback for legacy
                          const lookupKey = h.id != null ? `id:${h.id}` : (h.ticker || `name:${h.name}`);
                          const d = assets[lookupKey] || assets[h.ticker]; // ticker fallback for safety
                          const isOpen = researchAssetOpen === lookupKey;
                          const w = (h.valueUSD/totalV*100);
                          const hasDat = !!d;

                          return (
                            <div key={h.id != null ? `id:${h.id}` : (h.ticker || h.name)}
                              style={{border:`1px solid ${T.cardBorder}`,borderRadius:12,overflow:"hidden",
                                background:isOpen?"rgba(129,140,248,0.04)":T.surfaceBg,
                                transition:"all 0.2s"}}>

                              {/* Collapsed row */}
                              <div
                                onClick={()=>setResearchAssetOpen(isOpen?null:lookupKey)}
                                style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:"pointer",
                                  userSelect:"none"}}>
                                {/* Ticker badge — truncate long ISINs */}
                                <div style={{width:72,flexShrink:0,background:T.borderSubtle,borderRadius:8,
                                  padding:"4px 4px",textAlign:"center",fontFamily:"'Inter', system-ui, sans-serif",
                                  fontSize:h.ticker && h.ticker.length > 6 ? 9 : 11,
                                  fontWeight:700,color:T.brand,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                                  title={h.ticker}>
                                  {h.ticker && h.ticker.length > 10 ? h.ticker.slice(0,8)+"…" : h.ticker}
                                </div>
                                {/* Name */}
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:12.5,fontWeight:600,color:T.textPrimary,
                                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                    {h.name}
                                  </div>
                                  <div style={{fontSize:10.5,color:T.textMuted,marginTop:1}}>
                                    {h.class} · {w.toFixed(1)}% weight · {fmt(convert(h.valueUSD,dc),dc)}
                                    {h._dupCount > 1 && (
                                      <span title={`Combined across ${h._dupCount} positions`}
                                        style={{marginLeft:6,fontSize:9,padding:"1px 6px",borderRadius:8,
                                          background:"rgba(124,58,237,0.1)",color:"#7C3AED",fontWeight:600}}>
                                        × {h._dupCount}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Gain */}
                                <div style={{fontSize:12,fontWeight:700,
                                  color:h.gainPct>=0?"#1D9E75":"#A32D2D",
                                  fontFamily:"'Inter', system-ui, sans-serif",minWidth:56,textAlign:"right",flexShrink:0}}>
                                  {h.gainPct==null?"N/C":h.gainPct==null?"N/C":!isFinite(h.gainPct)?"N/C":`${h.gainPct>=0?"+":""}${h.gainPct.toFixed(1)}`}
                                </div>
                                {/* Research data badges */}
                                {hasDat && (
                                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                                    <span style={{fontSize:10,fontWeight:700,color:sentimentColor(d.sentiment),
                                      background:`${sentimentColor(d.sentiment)}18`,borderRadius:6,padding:"2px 8px"}}>
                                      {d.sentiment}
                                    </span>
                                    {d.analystConsensus && d.analystConsensus!=="N/A" && (
                                      <span style={{fontSize:10,fontWeight:700,color:ratingColor(d.analystConsensus),
                                        background:`${ratingColor(d.analystConsensus)}18`,borderRadius:6,padding:"2px 8px"}}>
                                        {d.analystConsensus}
                                      </span>
                                    )}
                                    <span style={{fontSize:10,fontWeight:700,color:ratingColor(d.holdRecommendation),
                                      background:`${ratingColor(d.holdRecommendation)}18`,
                                      border:`1px solid ${ratingColor(d.holdRecommendation)}40`,
                                      borderRadius:6,padding:"2px 8px"}}>
                                      {d.holdRecommendation}
                                    </span>
                                  </div>
                                )}
                                {!hasDat && (
                                  <span style={{fontSize:10,color:T.textMuted}}>No data</span>
                                )}
                                <span style={{fontSize:12,color:T.textMuted,flexShrink:0,marginLeft:4}}>
                                  {isOpen?"▲":"▼"}
                                </span>
                              </div>

                              {/* Expanded detail */}
                              {isOpen && hasDat && (
                                <div style={{padding:"0 16px 16px",borderTop:`1px solid ${T.borderSubtle}`}}>
                                  {/* One-liner verdict */}
                                  <div style={{padding:"10px 14px",background:"rgba(129,140,248,0.08)",
                                    borderRadius:8,margin:"12px 0",fontSize:12.5,fontWeight:600,
                                    color:T.dark?"#E2E8F0":"#1E293B",lineHeight:1.5,borderLeft:"3px solid #818CF8"}}>
                                    {d.oneLiner}
                                  </div>

                                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                                    {/* Left: thesis + news */}
                                    <div>
                                      {d.thesis && (
                                        <div style={{marginBottom:12}}>
                                          <div style={{fontSize:10.5,fontWeight:700,color:T.textSecondary,
                                            textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                                            Investment Thesis
                                          </div>
                                          <div style={{fontSize:12,color:T.dark?"#CBD5E1":"#334155",lineHeight:1.65}}>{d.thesis}</div>
                                        </div>
                                      )}

                                      {d.recentNews?.length > 0 && (
                                        <div>
                                          <div style={{fontSize:10.5,fontWeight:700,color:T.textSecondary,
                                            textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                                            Recent News
                                          </div>
                                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                            {d.recentNews.map((n,i)=>(
                                              <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",
                                                padding:"7px 10px",background:T.surfaceBg,borderRadius:7}}>
                                                <div style={{width:7,height:7,borderRadius:"50%",
                                                  background:impactColor(n.impact),marginTop:5,flexShrink:0}}/>
                                                <div>
                                                  <div style={{fontSize:11.5,color:T.textPrimary,lineHeight:1.4}}>{n.headline}</div>
                                                  <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{n.date}</div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Right: metrics + risks + catalysts */}
                                    <div>
                                      {/* Analyst info */}
                                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                                        {[
                                          {label:"Analyst Consensus",val:d.analystConsensus,color:ratingColor(d.analystConsensus)},
                                          {label:"Sentiment",val:d.sentiment,color:sentimentColor(d.sentiment)},
                                          {label:"Recommendation",val:d.holdRecommendation,color:ratingColor(d.holdRecommendation)},
                                          {label:"Sentiment Score",val:d.sentimentScore!=null?`${d.sentimentScore}/100`:null,color:T.textPrimary},
                                        ].map(({label,val,color})=>(
                                          <div key={label} style={{padding:"8px 10px",background:T.surfaceBg,borderRadius:8,
                                            textAlign:"center"}}>
                                            <div style={{fontSize:9.5,color:T.textMuted,textTransform:"uppercase",
                                              letterSpacing:"0.06em",marginBottom:3}}>{label}</div>
                                            <div style={{fontSize:13,fontWeight:700,color}}>{val||"—"}</div>
                                          </div>
                                        ))}
                                      </div>
                                      {/* Stale data notice */}
                                      <div style={{fontSize:10,color:T.textMuted,marginBottom:10,padding:"5px 8px",
                                        background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.15)",
                                        borderRadius:6,display:"flex",alignItems:"center",gap:5}}>
                                        <span>⏱</span>
                                        <span>Analysis based on AI training data (mid-2025). Price targets & news are indicative only — verify with live sources.</span>
                                      </div>

                                      {/* Hold rationale */}
                                      {d.holdRationale && (
                                        <div style={{padding:"8px 12px",background:`${ratingColor(d.holdRecommendation)}10`,
                                          border:`1px solid ${ratingColor(d.holdRecommendation)}30`,
                                          borderRadius:8,marginBottom:12}}>
                                          <div style={{fontSize:11,color:ratingColor(d.holdRecommendation),lineHeight:1.5}}>
                                            {d.holdRationale}
                                          </div>
                                        </div>
                                      )}

                                      {/* Key risks */}
                                      {d.keyRisks?.length > 0 && (
                                        <div style={{marginBottom:10}}>
                                          <div style={{fontSize:10.5,fontWeight:700,color:"#F87171",
                                            textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                                            Key Risks
                                          </div>
                                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                            {d.keyRisks.map((r,i)=>(
                                              <div key={i} style={{display:"flex",gap:6,alignItems:"flex-start"}}>
                                                <span style={{color:"#F87171",fontSize:10,marginTop:1,flexShrink:0}}>▸</span>
                                                <span style={{fontSize:11.5,color:T.textMuted,lineHeight:1.4}}>{r}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Catalysts */}
                                      {d.catalysts?.length > 0 && (
                                        <div>
                                          <div style={{fontSize:10.5,fontWeight:700,color:"#34D399",
                                            textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                                            Catalysts
                                          </div>
                                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                            {d.catalysts.map((c,i)=>(
                                              <div key={i} style={{display:"flex",gap:6,alignItems:"flex-start"}}>
                                                <span style={{color:"#34D399",fontSize:10,marginTop:1,flexShrink:0}}>▸</span>
                                                <span style={{fontSize:11.5,color:T.textMuted,lineHeight:1.4}}>{c}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {isOpen && !hasDat && (
                                <div style={{padding:"16px",borderTop:`1px solid ${T.borderSubtle}`,
                                  textAlign:"center",color:T.textMuted,fontSize:12,lineHeight:1.6}}>
                                  No research data for this asset yet.<br/>
                                  <span style={{fontSize:11}}>Click <strong>Run Research</strong> or check that your
                                  <strong> ANTHROPIC_API_KEY</strong> is set in Vercel environment variables.</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <div style={{fontSize:10.5,color:T.textMuted,padding:"10px 14px",
                      background:T.surfaceBg,borderRadius:8,lineHeight:1.5,marginTop:4}}>
                      ⚠️ <strong>Disclaimer:</strong> AI-generated research is for informational purposes only and does not constitute financial advice.
                      All recommendations should be independently verified. Past performance is not indicative of future results.
                    </div>
                  </div>
                );
              })()}

              {/* ── Research Archive ── */}
              {(()=>{
                const clientArchive = researchArchive.filter(e =>
                  !activeResearchClient || e.clientId === activeResearchClient?.id
                );
                // First-time render: if this client has saved entries, expand archive by default
                // so the user can see and reload them. setArchiveOpen runs as a side-effect inside
                // the renderer is OK here because it's idempotent (only flips false→true once).
                if (clientArchive.length > 0 && !archiveOpen && !archiveAutoOpened.current) {
                  archiveAutoOpened.current = true;
                  setTimeout(() => setArchiveOpen(true), 0);
                }
                const downloadResearchPdf = (entry) => {
                  const dateStr = new Date(entry.date).toLocaleDateString("en-SG", {
                    year:"numeric", month:"long", day:"numeric"
                  });
                  const timeStr = new Date(entry.date).toLocaleTimeString("en-SG", {
                    hour:"2-digit", minute:"2-digit"
                  });
                  const summary = entry.researchData?.summary;
                  const assets  = entry.researchData?.assets || {};

                  const assetRows = entry.portfolioSnapshot.map(h => {
                    const lookupKey = h.id != null ? `id:${h.id}` : (h.ticker || `name:${h.name}`);
                    const r = assets[lookupKey] || assets[h.ticker];
                    return `
                      <tr style="border-bottom:1px solid #e2e8f0">
                        <td style="padding:8px 10px;font-weight:600">${h.name}</td>
                        <td style="padding:8px 10px;color:#64748b">${h.ticker||"-"}</td>
                        <td style="padding:8px 10px;color:#64748b">${h.class}</td>
                        <td style="padding:8px 10px;text-align:right">${h.valueUSD>0?"$"+(h.valueUSD/1000).toFixed(0)+"K":"-"}</td>
                        <td style="padding:8px 10px;text-align:right;color:${(h.gainPct||0)>=0?"#1D9E75":"#A32D2D"}">${h.gainPct!=null?(h.gainPct>=0?"+":"")+h.gainPct.toFixed(1)+"%":"N/A"}</td>
                        <td style="padding:8px 10px;text-align:center">${r?.rating||"-"}</td>
                        <td style="padding:8px 10px;font-size:11px;color:#475569">${r?.thesis||"-"}</td>
                      </tr>`;
                  }).join("");

                  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Portfolio Research — ${entry.clientName} — ${dateStr}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:32px;font-size:13px;line-height:1.6;background:#fff}
  /* Letterhead-style header: logo top-left, title beneath, separator line */
  .letterhead{padding-bottom:18px;border-bottom:1px solid #e2e8f0;margin-bottom:24px}
  .letterhead .brand img{height:38px;width:auto;display:block}
  .letterhead .title{font-size:13px;font-weight:600;color:#0f172a;margin-top:14px;letter-spacing:0.02em}
  .letterhead .meta{font-size:11px;color:#64748b;margin-top:3px;font-weight:400}
  .kpi{display:flex;gap:16px;margin-bottom:26px;flex-wrap:wrap}
  .kpi-card{flex:1;min-width:130px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px}
  .kpi-card .label{font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700}
  .kpi-card .val{font-size:18px;font-weight:700;margin-top:3px;color:#0f172a}
  .section{margin-bottom:24px}
  .section h2{font-size:12px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #e2e8f0;padding-bottom:6px;margin:0 0 14px 0}
  .summary-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px;margin-bottom:14px;line-height:1.7}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#f1f5f9;color:#475569;padding:8px 10px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;border-bottom:1px solid #e2e8f0}
  tr:nth-child(even){background:#fafafa}
  .footer{margin-top:32px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:12px;line-height:1.6}
  @media print{body{padding:18px}}
</style></head><body>
<div class="letterhead">
  <div class="brand"><img src="${FORTHIUS_LOGO}" alt="Forthius Finalytics"/></div>
  <div class="title">AI Portfolio Research Report</div>
  <div class="meta">${entry.clientName} &nbsp;·&nbsp; As on ${dateStr} at ${timeStr}</div>
</div>
<div class="kpi">
  <div class="kpi-card"><div class="label">Portfolio Value</div><div class="val">$${(entry.totalUSD/1000000).toFixed(2)}M</div></div>
  <div class="kpi-card"><div class="label">Total Return</div><div class="val" style="color:${(entry.gainPct||0)>=0?"#1D9E75":"#A32D2D"}">${(entry.gainPct||0)>=0?"+":""}${(entry.gainPct||0).toFixed(1)}%</div></div>
  <div class="kpi-card"><div class="label">Holdings</div><div class="val">${entry.holdingCount}</div></div>
  <div class="kpi-card"><div class="label">Research Date</div><div class="val" style="font-size:13px">${dateStr}</div></div>
</div>
${summary ? `
<div class="section">
  <h2>Portfolio Summary</h2>
  <div class="summary-box">
    <strong>Overall Sentiment:</strong> ${summary.overallSentiment||"-"} &nbsp;|&nbsp;
    <strong>Risk Level:</strong> ${summary.riskLevel||"-"}<br/><br/>
    ${summary.portfolioThesis||""}
  </div>
  ${summary.keyRisks?.length ? `<strong>Key Risks:</strong><ul>${summary.keyRisks.map(r=>`<li>${r}</li>`).join("")}</ul>` : ""}
  ${summary.recommendations?.length ? `<strong>Recommendations:</strong><ul>${summary.recommendations.map(r=>`<li>${r}</li>`).join("")}</ul>` : ""}
</div>` : ""}
<div class="section">
  <h2>Holdings Analysis</h2>
  <table>
    <thead><tr><th>Asset</th><th>Ticker</th><th>Class</th><th>Value</th><th>Return</th><th>Rating</th><th>Thesis</th></tr></thead>
    <tbody>${assetRows}</tbody>
  </table>
</div>
<div class="footer">
  This report is AI-generated for informational purposes only and does not constitute financial advice.
  All recommendations should be independently verified. Past performance is not indicative of future results.
  Generated by Forthius Finalytics on ${dateStr}.
</div>
</body></html>`;

                  const blob = new Blob([html], {type:"text/html"});
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Forthius-Research-${entry.clientName.replace(/\s+/g,"-")}-${dateStr.replace(/\s+/g,"-")}.html`;
                  a.click();
                  URL.revokeObjectURL(url);
                };

                return (
                  <div id="research-archive-anchor" style={{marginTop:32,borderTop:`2px solid ${T.borderSubtle}`,paddingTop:24}}>
                    {/* Archive header */}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                      <button onClick={()=>setArchiveOpen(p=>!p)}
                        style={{display:"flex",alignItems:"center",gap:10,background:"transparent",
                          border:"none",cursor:"pointer",padding:0}}>
                        <span style={{fontSize:15,fontWeight:700,color:T.textPrimary}}>
                          📁 Research Archive
                        </span>
                        <span style={{fontSize:11,background:"rgba(129,140,248,0.12)",color:"#818CF8",
                          padding:"2px 8px",borderRadius:10,fontWeight:600}}>
                          {clientArchive.length} saved
                        </span>
                        <span style={{fontSize:12,color:T.textMuted}}>
                          {archiveOpen ? "▲" : "▼"}
                        </span>
                      </button>
                      {clientArchive.length > 0 && (
                        <button onClick={()=>{
                          if (!window.confirm("Delete all archived research for this client?")) return;
                          const next = researchArchive.filter(e => e.clientId !== activeResearchClient?.id);
                          setResearchArchive(next);
                          try { localStorage.setItem("forthius_research_archive", JSON.stringify(next)); } catch {}
                          upsertProfile("__research_archive__", next).catch(e => {
                            console.warn("[Research archive] cloud clear failed:", e.message);
                            showToast("⚠ Cleared locally, cloud sync failed", "#D97706", 4000);
                          });
                        }} style={{background:"transparent",border:"none",cursor:"pointer",
                          fontSize:11,color:T.textMuted}}>
                          🗑 Clear archive
                        </button>
                      )}
                    </div>

                    {archiveOpen && (
                      <React.Fragment>
                        {clientArchive.length === 0 ? (
                          <div style={{textAlign:"center",padding:"32px 20px",color:T.textMuted,
                            background:T.surfaceBg,borderRadius:10,fontSize:13}}>
                            No archived research yet. Run research and click <strong>💾 Save to Archive</strong>.
                          </div>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:10}}>
                            {clientArchive.map(entry => {
                              const isExp = archiveExpanded === entry.id;
                              const dateStr = new Date(entry.date).toLocaleDateString("en-SG",{
                                year:"numeric",month:"short",day:"numeric"
                              });
                              const timeStr = new Date(entry.date).toLocaleTimeString("en-SG",{
                                hour:"2-digit",minute:"2-digit"
                              });
                              const summary = entry.researchData?.summary;
                              const assets  = entry.researchData?.assets || {};
                              return (
                                <div key={entry.id} style={{border:`1px solid ${T.cardBorder}`,
                                  borderRadius:10,overflow:"hidden",
                                  boxShadow:isExp?"0 4px 16px rgba(0,0,0,0.08)":"none"}}>
                                  {/* Entry header */}
                                  <div style={{display:"flex",justifyContent:"space-between",
                                    alignItems:"center",padding:"13px 18px",
                                    background:isExp?"rgba(129,140,248,0.06)":T.cardBg,
                                    cursor:"pointer"}}
                                    onClick={()=>setArchiveExpanded(isExp?null:entry.id)}>
                                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                                      <div style={{fontSize:18}}>📄</div>
                                      <div>
                                        <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>
                                          {dateStr} · {timeStr}
                                        </div>
                                        <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                                          {entry.clientName} · {entry.holdingCount} holdings ·{" "}
                                          <span style={{color:(entry.gainPct||0)>=0?"#1D9E75":"#A32D2D",fontWeight:600}}>
                                            {(entry.gainPct||0)>=0?"+":""}{(entry.gainPct||0).toFixed(1)}% return
                                          </span>
                                          {summary && <span style={{marginLeft:8,color:(summary.overallSentiment==="Bullish"?"#34D399":summary.overallSentiment==="Bearish"?"#F87171":"#FBBF24"),fontWeight:600}}>{summary.overallSentiment}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                                      <button onClick={e=>{e.stopPropagation();
                                        // Reload this archived run into the main research view.
                                        // Sets researchData for this client to the saved entry's data.
                                        setResearchData(p=>({...p,[entry.clientId]:entry.researchData}));
                                        showToast(`✓ Loaded research from ${dateStr}`, "#2563EB", 2500);
                                        // Scroll back to the top of the research view
                                        const el = document.querySelector('[data-research-summary-anchor]');
                                        if (el) el.scrollIntoView({ behavior:"smooth", block:"start" });
                                      }} style={{background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.25)",
                                        borderRadius:6,padding:"5px 12px",cursor:"pointer",
                                        fontSize:11,color:"#7C3AED",fontWeight:600}}>
                                        ↻ Reload
                                      </button>
                                      <button onClick={e=>{e.stopPropagation(); downloadResearchPdf(entry);}}
                                        style={{background:"rgba(37,99,235,0.08)",border:"1px solid rgba(37,99,235,0.2)",
                                          borderRadius:6,padding:"5px 12px",cursor:"pointer",
                                          fontSize:11,color:"#2563EB",fontWeight:600}}>
                                        ⬇ Download
                                      </button>
                                      <button onClick={e=>{e.stopPropagation();
                                        const next = researchArchive.filter(e2=>e2.id!==entry.id);
                                        setResearchArchive(next);
                                        try{localStorage.setItem("forthius_research_archive",JSON.stringify(next));}catch{}
                                        upsertProfile("__research_archive__", next).catch(err => {
                                          console.warn("[Research archive] cloud delete failed:", err.message);
                                          showToast("⚠ Deleted locally, cloud sync failed", "#D97706", 4000);
                                        });
                                      }} style={{background:"transparent",border:"none",cursor:"pointer",
                                        fontSize:12,color:T.textMuted,padding:"4px"}}>✕</button>
                                      <span style={{fontSize:12,color:T.textMuted}}>{isExp?"▲":"▼"}</span>
                                    </div>
                                  </div>

                                  {/* Expanded content */}
                                  {isExp && (
                                    <div style={{padding:"16px 18px",borderTop:`1px solid ${T.borderSubtle}`}}>
                                      {/* Portfolio KPIs */}
                                      <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                                        {[
                                          {label:"Portfolio Value",val:"$"+(entry.totalUSD/1000000).toFixed(2)+"M"},
                                          {label:"Total Return",val:(entry.gainPct||0)>=0?"+"+entry.gainPct.toFixed(1)+"%":entry.gainPct.toFixed(1)+"%",
                                           color:(entry.gainPct||0)>=0?"#1D9E75":"#A32D2D"},
                                          {label:"Sentiment",val:summary?.overallSentiment||"N/A",
                                           color:summary?.overallSentiment==="Bullish"?"#34D399":summary?.overallSentiment==="Bearish"?"#F87171":"#FBBF24"},
                                          {label:"Risk Level",val:summary?.riskLevel||"N/A"},
                                        ].map(k=>(
                                          <div key={k.label} style={{flex:1,minWidth:100,background:T.surfaceBg,
                                            borderRadius:8,padding:"10px 14px",border:`1px solid ${T.borderSubtle}`}}>
                                            <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",
                                              letterSpacing:"0.06em",fontWeight:700}}>{k.label}</div>
                                            <div style={{fontSize:16,fontWeight:500,color:k.color||T.textPrimary,
                                              marginTop:3,fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Summary thesis */}
                                      {summary?.portfolioThesis && (
                                        <div style={{background:"rgba(129,140,248,0.06)",border:"1px solid rgba(129,140,248,0.2)",
                                          borderRadius:8,padding:"12px 16px",marginBottom:14,fontSize:12,
                                          color:T.textPrimary,lineHeight:1.6}}>
                                          {summary.portfolioThesis}
                                        </div>
                                      )}

                                      {/* Holdings table */}
                                      <div style={{overflowX:"auto"}}>
                                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                                          <thead>
                                            <tr style={{background:T.thBg}}>
                                              {["Asset","Ticker","Return","Value","Rating","Thesis"].map(h=>(
                                                <th key={h} style={{...tTh(),fontSize:10}}>{h}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {entry.portfolioSnapshot.map((h,i)=>{
                                              const lookupKey = h.id != null ? `id:${h.id}` : (h.ticker || `name:${h.name}`);
                                              const r = assets[lookupKey] || assets[h.ticker];
                                              const sentCol = r?.sentiment==="Bullish"?"#34D399":r?.sentiment==="Bearish"?"#F87171":"#FBBF24";
                                              return (
                                                <tr key={h.ticker||i} style={{background:i%2===0?"transparent":T.tableStripe}}>
                                                  <td style={{...tTd(),fontWeight:600,fontSize:11}}>{h.name}</td>
                                                  <td style={{...tTd(),fontSize:11,color:T.textSecondary}}>{h.ticker||"-"}</td>
                                                  <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif",
                                                    color:(h.gainPct||0)>=0?"#1D9E75":"#A32D2D",fontWeight:600}}>
                                                    {h.gainPct!=null?(h.gainPct>=0?"+":"")+h.gainPct.toFixed(1)+"%":"N/A"}
                                                  </td>
                                                  <td style={{...tTd(),fontFamily:"'Inter', system-ui, sans-serif",fontSize:11}}>
                                                    {h.valueUSD>0?"$"+(h.valueUSD/1000).toFixed(0)+"K":"-"}
                                                  </td>
                                                  <td style={{...tTd()}}>{r?.rating
                                                    ? <span style={{fontSize:10,fontWeight:700,color:sentCol,
                                                        background:`${sentCol}18`,padding:"2px 7px",borderRadius:4}}>
                                                        {r.rating}
                                                      </span>
                                                    : <span style={{color:T.textMuted,fontSize:10}}>—</span>}
                                                  </td>
                                                  <td style={{...tTd(),fontSize:10,color:T.textSecondary,maxWidth:240,
                                                    whiteSpace:"normal",lineHeight:1.4}}>
                                                    {r?.thesis||"—"}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                );
              })()}
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in research tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ── MODEL PORTFOLIO (with Target Yield) ── */}
        {tab==="model" && <React.Fragment>
          {/* ── Scope selector (admin only) ── */}
          {isAdmin && (function(){
            const modelScopeOpts = [
              { key:"all",   label:"Default (All Clients)", icon:"👥", desc:"Base model applied when no specific model is set" },
              ...families.map(f => ({ key:`family:${f.id}`, label:f.name, icon:"👨‍👩‍👧", desc:`Model for ${f.name} family` })),
              ...clients.map(c  => ({ key:`client:${c.id}`, label:c.name, icon:"👤", desc:`Individual model for ${c.name}` })),
            ];
            const activeOpt = modelScopeOpts.find(o => o.key===modelScopeKey) || modelScopeOpts[0];
            const hasCustom = (key) => !!modelsByScope[key];

            return (
              <div style={{marginBottom:16,padding:"10px 18px",
                background:T.surfaceBg,borderRadius:10,border:`1px solid ${T.borderSubtle}`,
                display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div style={{fontSize:12,color:T.textSecondary}}>
                  Model for <span style={{fontWeight:500,color:T.textPrimary}}>{activeOpt.label}</span>
                  {!hasCustom(modelScopeKey) && modelScopeKey!=="all" &&
                    <span style={{color:"#854F0B",marginLeft:8}}>· using default</span>}
                </div>
                {modelScopeKey !== "all" && hasCustom(modelScopeKey) && (
                  <button onClick={()=>{
                    requireConfirm(`Reset ${activeOpt.label} to default model?`, ()=>{
                      setModelsByScope(prev => {
                        const next = {...prev};
                        delete next[modelScopeKey];
                        try { localStorage.setItem("fw_models_v3", JSON.stringify(next)); } catch {}
                        return next;
                      });
                    });
                  }} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.borderSubtle}`,
                    background:"transparent",color:"#A32D2D",cursor:"pointer",fontSize:11}}>
                    Reset to default
                  </button>
                )}
              </div>
            );
          })()}

          <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:500}}>Model vs actual allocation</div>
            <div style={{fontSize:13,color:"#854F0B",fontFamily:"'Inter', system-ui, sans-serif"}} title="Weighted target yield = Σ(Target% × Asset Yield%) across all classes">
              Target yield: <span style={{fontWeight:500}}>{totalTargetYield.toFixed(2)}%</span>
            </div>
          </div>
          {/* ── Charts: Asset + Currency paired in 2 columns ── */}
          <div style={{...tCard(),padding:0,overflow:"hidden",marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr"}}>
              <div style={{padding:"16px 20px",borderRight:isMobile?"none":`1px solid ${T.borderSubtle}`}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:16,textAlign:"center"}}>Asset allocation</div>
                <div style={{display:"flex",gap:40,justifyContent:"center",alignItems:"flex-start"}}>
                  {(function(){
                    const raw = model.filter(m=>m.targetPct>0).map(m=>({label:m.assetClass,value:m.targetPct}));
                    const {merged} = mergeForDisplay(raw);
                    return <div style={{textAlign:"center"}}><DonutChart data={merged} colorMap={CLASS_COLORS} hideLegend size={280}/><div style={{fontSize:10,color:T.textMuted,marginTop:8}}>Target</div></div>;
                  })()}
                  {(function(){
                    const {merged} = mergeForDisplay(filtClassData);
                    return <div style={{textAlign:"center"}}><DonutChart data={merged} colorMap={CLASS_COLORS} hideLegend size={280}/><div style={{fontSize:10,color:T.textMuted,marginTop:8}}>Actual</div></div>;
                  })()}
                </div>
              </div>
              <div style={{padding:"16px 20px"}}>
                <div style={{fontSize:11,color:T.textMuted,marginBottom:16,textAlign:"center"}}>Currency exposure</div>
                <div style={{display:"flex",gap:40,justifyContent:"center",alignItems:"flex-start"}}>
                  <div style={{textAlign:"center"}}><DonutChart data={modelCurrency.map(c=>({label:c.currency,value:c.targetPct}))} hideLegend size={280}/><div style={{fontSize:10,color:T.textMuted,marginTop:8}}>Target</div></div>
                  <div style={{textAlign:"center"}}><DonutChart data={filtCurData} hideLegend size={280}/><div style={{fontSize:10,color:T.textMuted,marginTop:8}}>Actual</div></div>
                </div>
              </div>
            </div>
          </div>

          {/* ── 4 KPI cards + accordion panels ── */}
          {(function(){
            const gapTotal = model.reduce((s,m)=>s+m.targetPct,0);
            const gapOk = Math.abs(gapTotal - 100) < 0.1;
            const ccyTotal = modelCurrency.reduce((s,c)=>s+c.targetPct,0);
            const ccyOk = Math.abs(ccyTotal - 100) < 0.1;
            const rebalCount = model.filter(m => {
              if (m.targetPct <= 0) return false;
              const act = filtTotalUSD > 0 ? ((filtByClass[m.assetClass]||0)/filtTotalUSD)*100 : 0;
              return Math.abs(act - m.targetPct) >= 2;
            }).length;
            const cards = [
              { key:"gap", label:"Gap analysis", value:`${gapTotal.toFixed(1)}%`, valueColor:gapOk?"#1D9E75":"#A32D2D", sub:`${model.length} classes${gapOk?"":" · must = 100"}` },
              { key:"rebalance", label:"Rebalancing", value:String(rebalCount), valueColor:rebalCount>0?T.textPrimary:"#1D9E75", sub:rebalCount>0?`${rebalCount} action${rebalCount!==1?"s":""}  needed`:"On target" },
              { key:"currencyGap", label:"Currency gaps", value:`${ccyTotal.toFixed(1)}%`, valueColor:ccyOk?"#1D9E75":"#A32D2D", sub:`${modelCurrency.length} currencies` },
              { key:"matrix", label:"Cross matrix", value:"", valueColor:T.textPrimary, sub:"Asset class × currency" },
            ];
            return (
              <div style={{...tCard(),padding:0,overflow:"hidden",marginBottom:16}}>
                <div style={{display:"grid",gridTemplateColumns:`repeat(4,1fr)`}}>
                  {cards.map((c,i) => {
                    const isActive = modelActivePanel === c.key;
                    return (
                      <div key={c.key} onClick={()=>toggleModelPanel(c.key)}
                        style={{padding:"14px 16px",borderRight:i<3?`1px solid ${T.borderSubtle}`:"none",
                          cursor:"pointer",background:isActive?T.surfaceBg:"transparent",
                          transition:"background 0.12s",position:"relative"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                          <span style={{fontSize:11,color:T.textMuted}}>{c.label}</span>
                          <span style={{fontSize:11,color:T.textMuted,transition:"transform 0.15s",
                            transform:isActive?"rotate(0deg)":"rotate(-90deg)",display:"inline-block"}}>▼</span>
                        </div>
                        {c.value && <div style={{fontSize:18,fontWeight:500,color:c.valueColor,fontFamily:"'Inter', system-ui, sans-serif"}}>{c.value}</div>}
                        <div style={{fontSize:11,color:T.textMuted,marginTop:c.value?4:0}}>{c.sub}</div>
                        {isActive && <div style={{position:"absolute",bottom:-1,left:"20%",right:"20%",height:2,background:T.textPrimary,borderRadius:1}}/>}
                      </div>
                    );
                  })}
                </div>

                {/* ── Panel: Gap Analysis ── */}
                {modelActivePanel==="gap" && (
                  <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"16px 20px",background:T.surfaceBg}}>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:580}}>
              <thead><tr>{["Asset Class","Target %","Target Yield %","Wtd Yield Contrib","Actual %","Gap","Rebalance Action"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
              <tbody>
                {(function(){
                  // Build display rows: merge Indian Equities into Equities for gap view
                  // but keep them editable separately via expand
                  const EQUITY_CLASSES = ["Equities","Indian Equities","Global Equities"];
                  const equityRows = model.filter(m => EQUITY_CLASSES.includes(m.assetClass));
                  const otherRows  = model.filter(m => !EQUITY_CLASSES.includes(m.assetClass));
                  const equityMerged = equityRows.length > 0 ? [{
                    assetClass: "Equities (total)",
                    targetPct:  equityRows.reduce((s,r) => s + r.targetPct, 0),
                    targetYield: equityRows.reduce((s,r) => s + r.targetPct * r.targetYield, 0) /
                                 Math.max(1, equityRows.reduce((s,r) => s + r.targetPct, 0)),
                    _isEquityGroup: true, _subRows: equityRows,
                  }] : [];
                  const displayRows = [...equityMerged, ...otherRows];
                  const rows = displayRows.flatMap((m,i)=>{
                  const EQUITY_CLASSES = ["Equities","Indian Equities","Global Equities"];
                  // For merged equity group: sum actual from all equity sub-classes
                  const actVal = m._isEquityGroup
                    ? m._subRows.reduce((s,r) => s + (filtByClass[r.assetClass]||0), 0)
                    : (filtByClass[m.assetClass]||0);
                  const act    = (actVal / filtTotalUSD) * 100;
                  const gap    = act - m.targetPct;
                  const wtdContrib = (m.targetPct/100) * m.targetYield;
                  const accentColor = m._isEquityGroup ? "#2563EB"
                    : CLASS_COLORS[m.assetClass] || T.brand;

                  const mainRow = (
                    <tr key={m.assetClass}
                      style={{background: m._isEquityGroup
                        ? `${accentColor}08`
                        : i%2===0?"transparent":T.tableStripe,
                        borderLeft: m._isEquityGroup ? `3px solid ${accentColor}` : "none"}}>
                      <td style={tTd({fontWeight:700})}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {m._isEquityGroup && (
                            <button onClick={()=>setEqExpanded(p=>!p)}
                              title={eqExpanded?"Collapse sub-classes":"Expand: Global vs India split"}
                              style={{width:18,height:18,borderRadius:4,border:`1px solid ${accentColor}`,
                                background:eqExpanded?accentColor:"transparent",
                                color:eqExpanded?"#fff":accentColor,
                                cursor:"pointer",fontSize:11,fontWeight:700,lineHeight:1,
                                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              {eqExpanded?"−":"+"}
                            </button>
                          )}
                          <span style={{color:accentColor}}>{m._isEquityGroup?"Equities":m.assetClass}</span>
                          {m._isEquityGroup && (
                            <span style={{fontSize:9,color:T.textMuted,fontWeight:400}}>
                              (Global + India combined)
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={tTd()}>
                        {m._isEquityGroup ? (
                          <span style={{fontSize:12,color:T.textSecondary,fontStyle:"italic"}}>
                            {m.targetPct}% combined
                          </span>
                        ) : (
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <input type="number" style={{...inp(),width:65}} value={m.targetPct}
                              onChange={e=>{ const v=e.target.value===""?0:parseFloat(e.target.value);
                                if(!isNaN(v)&&v>=0&&v<=100) setModel(p=>p.map(x=>x.assetClass===m.assetClass?{...x,targetPct:v}:x)); }}/>
                            <span style={{color:T.textSecondary}}>%</span>
                          </div>
                        )}
                      </td>
                      <td style={tTd()}>
                        {m._isEquityGroup ? (
                          <span style={{fontSize:12,color:T.textSecondary,fontStyle:"italic"}}>
                            {m.targetYield.toFixed(1)}% blended
                          </span>
                        ) : (
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <input type="number" style={{...inp(),width:65}} value={m.targetYield}
                              onChange={e=>{ const v=e.target.value===""?0:parseFloat(e.target.value);
                                if(!isNaN(v)&&v>=0) setModel(p=>p.map(x=>x.assetClass===m.assetClass?{...x,targetYield:v}:x)); }}/>
                            <span style={{color:T.textSecondary}}>%</span>
                          </div>
                        )}
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#D97706"})}>{wtdContrib.toFixed(3)}%</td>
                      <td style={tTd()}>{act.toFixed(1)}%</td>
                      <td style={tTd()}>
                        <span style={{color:Math.abs(gap)<2?"#1D9E75":gap>0?"#D97706":"#A32D2D",fontWeight:600}}>
                          {gap>=0?"+":""}{gap.toFixed(1)}%
                        </span>
                      </td>
                      <td style={tTd({fontSize:12,color:T.textSecondary})}>
                        {Math.abs(gap)<2?"—":gap>0
                          ?`Trim ${fmt(convert(filtTotalUSD*(Math.abs(gap)/100),dc),dc,true)}`
                          :`Add ${fmt(convert(filtTotalUSD*(Math.abs(gap)/100),dc),dc,true)}`}
                      </td>
                    </tr>
                  );

                  // Sub-rows for equity breakdown (shown when expanded)
                  const subRows = (m._isEquityGroup && eqExpanded) ? m._subRows.map((sub,si) => {
                    const subAct = ((filtByClass[sub.assetClass]||0)/filtTotalUSD)*100;
                    const subGap = subAct - sub.targetPct;
                    const subColor = CLASS_COLORS[sub.assetClass] || accentColor;
                    return (
                      <tr key={sub.assetClass} style={{background:`${subColor}05`,
                        borderLeft:`3px solid ${subColor}66`}}>
                        <td style={tTd({paddingLeft:32,fontWeight:500,color:subColor})}>
                          <span style={{marginRight:4}}>
                            {sub.assetClass==="Indian Equities"?"🇮🇳":"🌐"}
                          </span>
                          {sub.assetClass}
                        </td>
                        <td style={tTd()}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <input type="number" style={{...inp(),width:65}} value={sub.targetPct}
                              onChange={e=>{ const v=e.target.value===""?0:parseFloat(e.target.value);
                                if(!isNaN(v)&&v>=0&&v<=100) setModel(p=>p.map(x=>x.assetClass===sub.assetClass?{...x,targetPct:v}:x)); }}/>
                            <span style={{color:T.textSecondary}}>%</span>
                          </div>
                        </td>
                        <td style={tTd()}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <input type="number" style={{...inp(),width:65}} value={sub.targetYield}
                              onChange={e=>{ const v=e.target.value===""?0:parseFloat(e.target.value);
                                if(!isNaN(v)&&v>=0) setModel(p=>p.map(x=>x.assetClass===sub.assetClass?{...x,targetYield:v}:x)); }}/>
                            <span style={{color:T.textSecondary}}>%</span>
                          </div>
                        </td>
                        <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#D97706",fontSize:11})}>
                          {((sub.targetPct/100)*sub.targetYield).toFixed(3)}%
                        </td>
                        <td style={tTd({fontSize:12})}>{subAct.toFixed(1)}%</td>
                        <td style={tTd()}>
                          <span style={{color:Math.abs(subGap)<2?"#1D9E75":subGap>0?"#D97706":"#A32D2D",fontWeight:600,fontSize:12}}>
                            {subGap>=0?"+":""}{subGap.toFixed(1)}%
                          </span>
                        </td>
                        <td style={tTd({fontSize:11,color:T.textSecondary})}>
                          {Math.abs(subGap)<2?"—":subGap>0
                            ?`Trim ${fmt(convert(filtTotalUSD*(Math.abs(subGap)/100),dc),dc,true)}`
                            :`Add ${fmt(convert(filtTotalUSD*(Math.abs(subGap)/100),dc),dc,true)}`}
                        </td>
                      </tr>
                    );
                  }) : [];

                  return [mainRow, ...subRows];
                  });
                  return <React.Fragment>
                    {rows}
                    <tr style={{background:"rgba(245,158,11,0.07)",borderTop:"2px solid #D97706"}}>
                      <td style={tTd({fontWeight:700,color:"#D97706"})} colSpan={3}>Total Portfolio Yield</td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:"#D97706"})}>{totalTargetYield.toFixed(2)}%</td>
                      <td colSpan={3}/>
                    </tr>
                  </React.Fragment>
                })()}
              </tbody>
            </table></div>
                  </div>
                )}

                {/* ── Panel: Rebalancing Trades ── */}
                {modelActivePanel==="rebalance" && (function(){
            const trades = model
              .filter(m => m.targetPct > 0)
              .map(m => {
                const actVal = filtByClass[m.assetClass] || 0;
                const act    = filtTotalUSD > 0 ? (actVal / filtTotalUSD) * 100 : 0;
                const gap    = act - m.targetPct;
                const tradeVal = filtTotalUSD * (Math.abs(gap) / 100);
                if (Math.abs(gap) < 2) return null;
                return {
                  assetClass: m.assetClass,
                  action: gap > 0 ? "TRIM" : "ADD",
                  gap: gap.toFixed(1),
                  tradeVal,
                  color: gap > 0 ? "#D97706" : "#2563EB",
                  icon: gap > 0 ? "📉" : "📈",
                };
              })
              .filter(Boolean)
              .sort((a,b) => Math.abs(parseFloat(b.gap)) - Math.abs(parseFloat(a.gap)));

            if (trades.length === 0) return (
              <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"20px",background:T.surfaceBg,textAlign:"center",color:"#1D9E75"}}>
                <div style={{fontSize:13,fontWeight:500}}>Portfolio is within target allocation</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:4}}>All asset classes within ±2% of target</div>
              </div>
            );

            return (
              <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"16px 20px",background:T.surfaceBg}}>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {trades.map((t,i) => (
                    <div key={t.assetClass} style={{display:"flex",alignItems:"center",gap:12,
                      padding:"12px 16px",borderRadius:10,
                      background:t.action==="TRIM"?"rgba(217,119,6,0.06)":"rgba(37,99,235,0.06)",
                      border:`1px solid ${t.action==="TRIM"?"rgba(217,119,6,0.2)":"rgba(37,99,235,0.2)"}`}}>
                      <div style={{fontSize:22,flexShrink:0}}>{t.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>{t.assetClass}</div>
                        <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                          Currently {t.action==="TRIM"?"overweight":"underweight"} by {Math.abs(parseFloat(t.gap)).toFixed(1)}%
                          · Target: {model.find(m=>m.assetClass===t.assetClass)?.targetPct?.toFixed(1)}%
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:8,
                          color:t.color,background:`${t.color}18`,border:`1px solid ${t.color}44`,
                          marginBottom:4}}>
                          {t.action}
                        </div>
                        <div style={{fontSize:13,fontWeight:500,color:t.color,
                          fontFamily:"'Inter', system-ui, sans-serif"}}>
                          {fmt(convert(t.tradeVal,dc),dc)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:12,fontSize:10,color:T.textMuted,fontStyle:"italic"}}>
                  * Indicative. Account for transaction costs, tax impact, and liquidity before executing.
                </div>
              </div>
            );
          })()}

                {/* ── Panel: Currency Gap Analysis ── */}
                {modelActivePanel==="currencyGap" && (
                  <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"16px 20px",background:T.surfaceBg}}>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:540}}>
              <thead>
                <tr>{["Currency","Target %","Actual %","Gap","Actual Value","Rebalance Action","Note"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {modelCurrency.map((c,i)=>{
                  const actualUSD = filtByCurrency[c.currency] || 0;
                  const actualPct = filtTotalUSD > 0 ? (actualUSD / filtTotalUSD) * 100 : 0;
                  const gap       = actualPct - c.targetPct;
                  const targetUSD = filtTotalUSD * (c.targetPct / 100);
                  const diffUSD   = actualUSD - targetUSD;
                  return (
                    <tr key={c.currency} style={{background:i%2===0?"transparent":T.tableStripe}}>
                      <td style={tTd({fontWeight:700})}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:15}}>{
                            {USD:"🇺🇸",INR:"🇮🇳",EUR:"🇪🇺",GBP:"🇬🇧",AED:"🇦🇪",SGD:"🇸🇬",JPY:"🇯🇵",CHF:"🇨🇭"}[c.currency]||"💱"
                          }</span>
                          <span>{c.currency}</span>
                        </div>
                      </td>
                      <td style={tTd()}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <input
                            type="number"
                            style={{...inp(),width:65}}
                            value={c.targetPct}
                            onChange={e=>{ const v=parseFloat(e.target.value)||0; setModelCurrency(p=>p.map(x=>x.currency===c.currency?{...x,targetPct:v}:x)); }}
                          />
                          <span style={{color:T.textSecondary}}>%</span>
                        </div>
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{actualPct.toFixed(1)}%</td>
                      <td style={tTd()}>
                        <span style={{color:Math.abs(gap)<2?"#1D9E75":gap>0?"#D97706":"#A32D2D",fontWeight:600}}>
                          {gap>=0?"+":""}{gap.toFixed(1)}%
                        </span>
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(convert(actualUSD,dc),dc)}</td>
                      <td style={tTd({fontSize:12})}>
                        {Math.abs(gap)<2
                          ? <span style={{color:"#1D9E75"}}>✓ On target</span>
                          : diffUSD>0
                            ? <span style={{color:"#D97706"}}>Reduce {fmt(convert(Math.abs(diffUSD),dc),dc)}</span>
                            : <span style={{color:"#2563EB"}}>Add {fmt(convert(Math.abs(diffUSD),dc),dc)}</span>
                        }
                      </td>
                      <td style={tTd({fontSize:11,color:T.textSecondary})}>
                        <input
                          style={{...inp(),fontSize:11,padding:"3px 8px"}}
                          value={c.note}
                          onChange={e=>setModelCurrency(p=>p.map(x=>x.currency===c.currency?{...x,note:e.target.value}:x))}
                          placeholder="Note…"
                        />
                      </td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr style={{background:"rgba(59,130,246,0.07)",borderTop:"2px solid #2563EB"}}>
                  <td style={tTd({fontWeight:700,color:"#2563EB"})} colSpan={2}>Total</td>
                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600})}>{filtTotalUSD>0?100:0}%</td>
                  <td style={tTd()}/>
                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:700,color:"#2563EB"})}>{fmt(convert(filtTotalUSD,dc),dc,true)}</td>
                  <td colSpan={2}/>
                </tr>
              </tbody>
            </table></div>

            {/* Add custom currency row */}
            <div style={{marginTop:14,display:"flex",gap:10,alignItems:"center",padding:"10px 0",borderTop:`1px solid ${T.cardBorder}`}}>
              <span style={{fontSize:12,color:T.textSecondary,flexShrink:0}}>Add currency:</span>
              {["CHF","JPY","CNH","HKD","CAD","AUD","MXN","BRL","ZAR","MYR"].filter(cur=>!modelCurrency.find(c=>c.currency===cur)).slice(0,6).map(cur=>(
                <button key={cur} onClick={()=>setModelCurrency(p=>[...p,{currency:cur,targetPct:0,note:""}])}
                  style={{background:"transparent",border:"1px dashed #78716C",borderRadius:5,color:T.textSecondary,padding:"3px 10px",cursor:"pointer",fontSize:12,transition:"all 0.15s"}}>
                  + {cur}
                </button>
              ))}
              <button onClick={()=>{ const cur=prompt("Enter currency code (e.g. HKD):"); if(cur&&!modelCurrency.find(c=>c.currency===cur.toUpperCase())) setModelCurrency(p=>[...p,{currency:cur.toUpperCase(),targetPct:0,note:""}]); }}
                style={{background:"transparent",border:"1px dashed #A8A29E",borderRadius:5,color:T.textMuted,padding:"3px 10px",cursor:"pointer",fontSize:12,marginLeft:"auto"}}>
                + Custom…
              </button>
            </div>
                  </div>
                )}

                {/* ── Panel: Cross Matrix ── */}
                {modelActivePanel==="matrix" && (
                  <div style={{borderTop:`1px solid ${T.borderSubtle}`,padding:"16px 20px",background:T.surfaceBg}}>
            {(function(){
              // Build actual matrix: [assetClass][currency] = valueUSD
              const matrix = {};
              model.forEach(m=>{ matrix[m.assetClass]={}; });
              filteredHW.forEach(h=>{ if(matrix[h.class]) matrix[h.class][h.currency]=(matrix[h.class][h.currency]||0)+h.valueUSD; });

              // Asset classes with a target or actual allocation
              const gappedClasses = model.filter(m=>{
                const act=((filtByClass[m.assetClass]||0)/filtTotalUSD)*100;
                return m.targetPct > 0 || act > 0.5; // show all with target or meaningful actual
              });
              // Currencies with a meaningful gap
              const gappedCurrencies = modelCurrency.filter(c=>{
                const actualPct=filtTotalUSD>0?((filtByCurrency[c.currency]||0)/filtTotalUSD*100):0;
                return Math.abs(actualPct-c.targetPct)>=2;
              });

              if(gappedClasses.length===0 || gappedCurrencies.length===0) return (
                <div style={{textAlign:"center",padding:"30px",color:"#1D9E75",fontSize:13}}>
                  ✓ All asset class and currency allocations are within ±2% of target. No cross-currency adjustments needed.
                </div>
              );

              const activeCurrencies = gappedCurrencies.map(c=>c.currency);

              return (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr>
                        <th style={{...tTh(),textAlign:"left",minWidth:140}}>Asset Class</th>
                        <th style={{...tTh(),textAlign:"center",minWidth:80}}>Class Gap</th>
                        {activeCurrencies.map(cur=>(
                          <th key={cur} style={{...tTh(),textAlign:"center",minWidth:110}}>
                            <div>{{USD:"🇺🇸",INR:"🇮🇳",EUR:"🇪🇺",GBP:"🇬🇧",AED:"🇦🇪",SGD:"🇸🇬",JPY:"🇯🇵",CHF:"🇨🇭"}[cur]||"💱"} {cur}</div>
                            {(function(){
                              const cObj=modelCurrency.find(c=>c.currency===cur);
                              const actualPct=filtTotalUSD>0?((filtByCurrency[cur]||0)/filtTotalUSD*100):0;
                              const gap=actualPct-(cObj?.targetPct||0);
                              return <div style={{fontSize:10,color:gap>0?"#D97706":gap<0?"#2563EB":"#1D9E75",fontWeight:600,marginTop:2}}>{gap>=0?"+":""}{gap.toFixed(1)}% ccy gap</div>;
                            })()}
                          </th>
                        ))}
                        <th style={{...tTh(),textAlign:"left",minWidth:200}}>Recommended Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gappedClasses.map((m,i)=>{
                        const actPct=((filtByClass[m.assetClass]||0)/filtTotalUSD)*100;
                        const classGap=actPct-m.targetPct;
                        const classOverweight=classGap>0;
                        const recommendations=[];

                        return (
                          <tr key={m.assetClass} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={tTd({fontWeight:600})}>{m.assetClass}</td>
                            <td style={{...td(),textAlign:"center"}}>
                              <span style={{color:Math.abs(classGap)<2?"#1D9E75":classGap>0?"#D97706":"#A32D2D",fontWeight:700,fontSize:13}}>
                                {classGap>=0?"+":""}{classGap.toFixed(1)}%
                              </span>
                            </td>
                            {activeCurrencies.map(cur=>{
                              const held=matrix[m.assetClass]?.[cur]||0;
                              const cObj=modelCurrency.find(c=>c.currency===cur);
                              const ccyActPct=filtTotalUSD>0?((filtByCurrency[cur]||0)/filtTotalUSD*100):0;
                              const ccyGap=ccyActPct-(cObj?.targetPct||0);
                              const ccyOverweight=ccyGap>0;

                              let action=null, bgColor="transparent", textColor=T.textSecondary;
                              if(held>0){
                                if(classOverweight && ccyOverweight){
                                  action="Reduce"; bgColor="rgba(220,38,38,0.08)"; textColor="#A32D2D";
                                } else if(!classOverweight && !ccyOverweight){
                                  action="Increase"; bgColor="rgba(96,165,250,0.08)"; textColor="#2563EB";
                                } else if(classOverweight && !ccyOverweight){
                                  action="Shift ccy"; bgColor="rgba(245,158,11,0.08)"; textColor="#D97706";
                                } else {
                                  action="Monitor"; bgColor="rgba(0,0,0,0.02)"; textColor=T.textSecondary;
                                }
                              }

                              return (
                                <td key={cur} style={{...td(),textAlign:"center",background:bgColor}}>
                                  {held>0 ? (
                                    <div>
                                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:2}}>{fmt(convert(held,dc),dc)}</div>
                                      {action && <div style={{fontSize:10,color:textColor,fontWeight:600}}>{action}</div>}
                                    </div>
                                  ) : <span style={{color:T.textLight,fontSize:12}}>—</span>}
                                </td>
                              );
                            })}
                            <td style={tTd({fontSize:11,color:T.textSecondary})}>
                              {(function(){
                                const recs=[];
                                activeCurrencies.forEach(cur=>{
                                  const held=matrix[m.assetClass]?.[cur]||0;
                                  if(!held) return;
                                  const cObj=modelCurrency.find(c=>c.currency===cur);
                                  const ccyActPct=filtTotalUSD>0?((filtByCurrency[cur]||0)/filtTotalUSD*100):0;
                                  const ccyGap=ccyActPct-(cObj?.targetPct||0);
                                  if(classOverweight && ccyGap>2) recs.push(`Trim ${m.assetClass} in ${cur}`);
                                  else if(!classOverweight && ccyGap<-2) recs.push(`Add ${m.assetClass} in ${cur}`);
                                });
                                if(!recs.length) return <span style={{color:T.textLight}}>Review allocation mix</span>;
                                return recs.map((r,ri)=>(
                                  <div key={ri} style={{marginBottom:2,display:"flex",alignItems:"center",gap:5}}>
                                    <span style={{color:r.startsWith("Trim")?"#A32D2D":"#2563EB"}}>
                                      {r.startsWith("Trim")?"↓":"↑"}
                                    </span>
                                    {r}
                                  </div>
                                ));
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{marginTop:12,padding:"8px 12px",background:T.borderSubtle,borderRadius:7,fontSize:11,color:T.textMuted}}>
                    Only showing asset classes and currencies with &gt;2% gap from target.
                  </div>
                </div>
              );
            })()}
                  </div>
                )}

              </div>
            );
          })()}

        </React.Fragment>}
        {tab==="benchmark" && <React.Fragment>
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see benchmark analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div>
              <div style={{fontSize:17,fontWeight:700}}>Benchmark Comparison</div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                {(function(){
                  const liveCount = BENCHMARKS_DEF.filter(b => benchMetrics[b.id]?.live).length;
                  const total = BENCHMARKS_DEF.length;
                  if (benchLoading && liveCount === 0)
                    return <span style={{color:T.brand}}>⟳ Fetching live data via Yahoo Finance…</span>;
                  if (benchLoading)
                    return <span style={{color:T.brand}}>⟳ Loading… {liveCount}/{total} benchmarks live</span>;
                  const rawAsOf = Object.values(benchMetrics).find(m => m?.asOf && m?.live)?.asOf || null;
                  const anyAsOf = rawAsOf
                    ? new Date(rawAsOf + "-01").toLocaleDateString("en-SG",{month:"short",year:"numeric"})
                    : "—";
                  if (liveCount === total)
                    return <span style={{color:"#1D9E75"}}>✓ All {total} benchmarks live · as of {anyAsOf}</span>;
                  if (liveCount > 0)
                    return <span style={{color:"#D97706"}}>⚠ {liveCount}/{total} live · {total-liveCount} on static fallback{anyAsOf!=="—"?" · as of "+anyAsOf:""}</span>;
                  return <span style={{color:"#D97706"}}>⚠ Showing static fallback — check Alpha Vantage key in Data Sources</span>;
                })()}
              </div>
            </div>
            <button onClick={()=>loadBenchmarks(true)} disabled={benchLoading}
              style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:6,color:"#2563EB",padding:"6px 14px",cursor:benchLoading?"not-allowed":"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6,opacity:benchLoading?0.6:1}}>
              {benchLoading ? `⟳ ${BENCHMARKS_DEF.filter(b=>benchMetrics[b.id]?.live).length}/${BENCHMARKS_DEF.length}` : "↻ Refresh"}
            </button>
          </div>

          {/* ── Benchmark Sentiment Cards ── */}
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Benchmarks &amp; Market Sentiment</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                  Composite of RSI · 50DMA · VIX · Put/Call ratio · Breadth &nbsp;·&nbsp;
                  <span style={{color:benchLoading?"#D97706":"#1D9E75",fontWeight:600}}>
                    {benchLoading?"⟳ Fetching…":"● Live"}
                  </span>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{ setNewBenchForm({label:"",ticker:"",currency:"USD",type:"equity"}); setAddBenchModal(true); }}
                style={{background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.3)",
                  borderRadius:8,color:"#1D9E75",padding:"6px 14px",cursor:"pointer",
                  fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                + Add Benchmark
              </button>
              <button onClick={()=>{ setBlendDraft(customBlend?.weights||{}); setShowBlendBuilder(true); }}
                style={{background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.3)",
                  borderRadius:8,color:"#7C3AED",padding:"6px 14px",cursor:"pointer",
                  fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                ⚗️ {customBlend ? "Edit Custom Blend" : "Build Custom Blend"}
              </button>
              </div>
            </div>

            {/* Empty state when no benchmarks added */}
            {BENCHMARKS_DEF.length === 0 && (
              <div style={{textAlign:"center",padding:"40px 24px",background:T.cardBg,
                borderRadius:12,border:`2px dashed ${T.cardBorder}`,marginBottom:16}}>
                <div style={{fontSize:32,marginBottom:10}}>🏁</div>
                <div style={{fontSize:14,fontWeight:600,color:T.textPrimary,marginBottom:6}}>No benchmarks added yet</div>
                <div style={{fontSize:12,color:T.textSecondary,marginBottom:16,maxWidth:320,margin:"0 auto 16px"}}>
                  Add benchmarks to compare your portfolio performance against market indices.
                </div>
                <button onClick={()=>{ setNewBenchForm({label:"",ticker:"",currency:"USD",type:"equity"}); setAddBenchModal(true); }}
                  style={{background:"#007878",border:"none",
                    borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                  + Add Your First Benchmark
                </button>
              </div>
            )}

            {/* Sentiment card grid */}
            <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(3,1fr)",gap:10,marginBottom:customBlend?10:0}}>
              {[...BENCHMARKS_DEF, ...(customBlend?[{id:"custom",label:customBlend.name||"Custom Blend",type:"blend",currency:"USD"}]:[])].map(b=>{
                const bm    = benchMetrics[b.id] || BENCHMARK_FALLBACK[b.id];
                const sent  = b.id==="custom"
                  ? (function(){
                      // Weighted sentiment for custom blend
                      const wts = customBlend?.weights || {};
                      const total = Object.values(wts).reduce((s,v)=>s+v,0) || 1;
                      const score = Object.entries(wts).reduce((s,[id,w])=>{
                        return s + (BENCHMARK_SENTIMENT[id]?.score||50) * (w/total);
                      }, 0);
                      return { score, ...sentimentToPoint(score),
                        label: score>=81?"Strong Bull":score>=61?"Bullish":score>=31?"Neutral":"Bearish",
                        signals: Object.entries(wts).map(([id,w])=>`${BENCHMARKS_DEF.find(x=>x.id===id)?.label||id} ${w}%`).join(" + ") };
                    })()
                  : (()=>{ const s = getSentiment(b.id, b.ticker); return {...s, ...sentimentToPoint(s?.score||50)}; })();
                const isSelected = bench===b.id;
                const isLive = bm?.live === true;
                const hasData = bm && (bm.ytd != null || bm.oneY != null);
                const ytd  = bm?.ytd;
                const oneY = bm?.oneY;
                const pt   = sentimentToPoint(sent?.score||50);
                const arcP = sentimentArcPath(sent?.score||50);
                const overbought = sent?.score >= 80;
                const oversold   = sent?.score <= 20;
                return (
                  <div key={b.id} onClick={()=>setBench(b.id)}
                    style={{background:T.cardBg,border:`2px solid ${isSelected?"#2563EB":T.cardBorder}`,
                      borderRadius:12,padding:"12px 14px",cursor:"pointer",
                      transition:"border-color 0.15s,box-shadow 0.15s",
                      boxShadow:isSelected?"0 0 0 3px rgba(37,99,235,0.12)":"none"}}>

                    {/* Top row: name + live dot */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:T.textPrimary}}>{b.label}</div>
                        <div style={{fontSize:10,color:T.textSecondary}}>{b.currency}{b.type==="blend"?" · Custom":""}{b.ticker?" · "+b.ticker:""}</div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                        {ytd!=null
                          ? <div style={{fontSize:11,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                              color:ytd>=0?"#1D9E75":"#A32D2D"}}>{ytd>=0?"+":""}{ytd.toFixed(1)}% YTD</div>
                          : benchLoading
                            ? <div style={{fontSize:10,color:T.brand}}>⟳ Loading…</div>
                            : <div style={{fontSize:10,color:"#D97706"}}>No data</div>
                        }
                        <div style={{width:6,height:6,borderRadius:"50%",
                          background:isLive?"#1D9E75":benchLoading?"#2563EB":"#D97706"}}
                          title={isLive?"Live data":"Static fallback — check ticker format"}/>
                      </div>
                    </div>

                    {/* Sentiment semicircle gauge */}
                    <div style={{display:"flex",alignItems:"flex-end",gap:10}}>
                      <div style={{position:"relative",flexShrink:0}}>
                        <svg width="88" height="52" viewBox="0 0 88 52">
                          {/* Track */}
                          <path d="M7 47 A37 37 0 0 1 81 47" fill="none" stroke={T.borderSubtle} strokeWidth="7" strokeLinecap="round"/>
                          {/* Zone fills (dim) */}
                          <path d="M7 47 A37 37 0 0 1 26 15" fill="none" stroke="#E24B4A" strokeWidth="7" strokeLinecap="round" opacity="0.2"/>
                          <path d="M26 15 A37 37 0 0 1 62 15" fill="none" stroke="#EF9F27" strokeWidth="7" strokeLinecap="round" opacity="0.2"/>
                          <path d="M62 15 A37 37 0 0 1 81 47" fill="none" stroke="#639922" strokeWidth="7" strokeLinecap="round" opacity="0.2"/>
                          {/* Active arc */}
                          <path d={arcP} fill="none" stroke={pt.arcCol} strokeWidth="7" strokeLinecap="round" opacity="0.5"/>
                          {/* Needle */}
                          <line x1="44" y1="47" x2={pt.x.toFixed(1)} y2={pt.y.toFixed(1)} stroke={pt.col} strokeWidth="2.5" strokeLinecap="round"/>
                          <circle cx="44" cy="47" r="4" fill={pt.col}/>
                          <circle cx="44" cy="47" r="2" fill="white"/>
                          {/* Overbought/oversold marker */}
                          {overbought&&<circle cx="78" cy="20" r="3" fill="#D97706"/>}
                          {oversold&&<circle cx="10" cy="20" r="3" fill="#A32D2D"/>}
                        </svg>
                        <div style={{textAlign:"center",fontSize:10,fontWeight:700,marginTop:-2,color:pt.textCol}}>
                          {sent?.label||"Neutral"}
                          {overbought&&<span style={{color:"#D97706"}}> ⚠</span>}
                        </div>
                      </div>
                      {/* Signal detail */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.textMuted,marginBottom:3,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                          Signals
                        </div>
                        <div style={{fontSize:10,color:T.textSecondary,lineHeight:1.5}}>
                          {sent?.signals||"—"}
                        </div>
                        {oneY!=null&&(
                          <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>
                            1Y return: <span style={{fontWeight:600,color:oneY>=0?"#1D9E75":"#A32D2D"}}>{oneY>=0?"+":""}{oneY.toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {isSelected&&(
                      <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${T.borderSubtle}`,
                        fontSize:10,fontWeight:600,color:"#2563EB",textAlign:"center"}}>
                        ✓ Active benchmark
                      </div>
                    )}
                    {/* Remove button for user-added benchmarks */}
                    {b.userAdded && (
                      <div style={{marginTop:6,paddingTop:6,borderTop:`1px solid ${T.borderSubtle}`,display:"flex",justifyContent:"flex-end"}}>
                        <button onClick={e=>{ e.stopPropagation();
                          requireConfirm(`Remove benchmark "${b.label}"?`, ()=>{
                            const upd = userBenchmarks.filter(x=>x.id!==b.id);
                            setUserBenchmarks(upd);
                            try{localStorage.setItem("fw_user_benchmarks_v1",JSON.stringify(upd));}catch{}
                            if(bench===b.id) setBench(userBenchmarks.filter(x=>x.id!==b.id)[0]?.id||"");
                            showToast(`Removed ${b.label}`,"#D97706");
                          });
                        }}
                        style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.2)",
                          borderRadius:5,color:"#A32D2D",padding:"2px 10px",cursor:"pointer",fontSize:10,fontWeight:600}}>
                          ✕ Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── ADD BENCHMARK MODAL ── */}
            {addBenchModal && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(4px)",
                zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,
                  padding:28,width:"100%",maxWidth:420,boxShadow:"0 24px 80px rgba(0,0,0,0.4)",animation:"scaleIn 0.18s ease"}}>
                  <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>+ Add Benchmark</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:20}}>
                    Add any ticker as a benchmark. Use Yahoo Finance format:
                    US stocks/ETFs (SPY, QQQ), Indian indices (^NSEI, ^BSESN), BSE ETFs (NIFTYBEES.BO).
                  </div>
                  {[
                    ["Display Name",   "label",    "text",   "e.g. Nasdaq 100, Gold ETF"],
                    ["Ticker Symbol",  "ticker",   "text",   "e.g. QQQ, GLD, MSFT"],
                  ].map(([label,key,type,ph])=>(
                    <div key={key} style={{marginBottom:12}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
                      <input type={type} style={{...inp(),width:"100%",boxSizing:"border-box"}}
                        value={newBenchForm[key]||""} placeholder={ph}
                        onChange={e=>setNewBenchForm(p=>({...p,[key]:e.target.value.toUpperCase()===e.target.value&&key==="ticker"?e.target.value.toUpperCase():e.target.value}))}/>
                    </div>
                  ))}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Type</div>
                      <select style={inp()} value={newBenchForm.type||"equity"} onChange={e=>setNewBenchForm(p=>({...p,type:e.target.value}))}>
                        {["equity","crypto","commodity","bond","other"].map(o=><option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Currency</div>
                      <select style={inp()} value={newBenchForm.currency||"USD"} onChange={e=>setNewBenchForm(p=>({...p,currency:e.target.value}))}>
                        {["USD","INR","EUR","GBP","JPY"].map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  {/* Popular quick-add shortcuts — drawn from BENCHMARK_PRESETS */}
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:10,color:T.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.07em"}}>Quick add popular benchmarks</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {BENCHMARK_PRESETS.filter(q=>!BENCHMARKS_DEF.some(b=>b.ticker===q.ticker)).map(q=>(
                        <button key={q.ticker}
                          onClick={()=>setNewBenchForm(q)}
                          style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                            background:"transparent",color:T.textSecondary,cursor:"pointer",fontSize:11,
                            ...(newBenchForm.ticker===q.ticker?{borderColor:T.brand,color:T.brand,background:`${T.brand}10`}:{})}}>
                          {q.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button
                      disabled={!newBenchForm.label||!newBenchForm.ticker}
                      onClick={()=>{
                        const id = `user_${newBenchForm.ticker.toLowerCase().replace(/[^a-z0-9]/g,"_")}`;
                        const newB = {id, label:newBenchForm.label, ticker:newBenchForm.ticker.toUpperCase(),
                          type:newBenchForm.type||"equity", currency:newBenchForm.currency||"USD", userAdded:true};
                        const updated = [...userBenchmarks.filter(b=>b.id!==id), newB];
                        setUserBenchmarks(updated);
                        try { localStorage.setItem("fw_user_benchmarks_v1", JSON.stringify(updated)); } catch {}
                        setAddBenchModal(false);
                        setBench(id);
                        try { localStorage.setItem("fw_selected_bench_v1", id); } catch {}
                        showToast(`✓ ${newBenchForm.label} added as benchmark`,"#1D9E75");
                      }}
                      style={{flex:1,background:!newBenchForm.label||!newBenchForm.ticker?"rgba(0,121,193,0.3)":"#007878",
                        border:"none",borderRadius:8,color:"white",padding:"10px",cursor:!newBenchForm.label||!newBenchForm.ticker?"default":"pointer",
                        fontSize:13,fontWeight:700}}>
                      Add Benchmark
                    </button>
                    <button onClick={()=>setAddBenchModal(false)}
                      style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                        color:T.textSecondary,padding:"10px 16px",cursor:"pointer",fontSize:13}}>
                      Cancel
                    </button>
                  </div>
                  {/* List user-added benchmarks with remove option */}
                  {userBenchmarks.length>0&&(
                    <div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${T.borderSubtle}`}}>
                      <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Your custom benchmarks</div>
                      {userBenchmarks.map(b=>(
                        <div key={b.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,
                          padding:"6px 10px",background:T.borderSubtle,borderRadius:7}}>
                          <span style={{fontSize:11,fontWeight:600,flex:1}}>{b.label}</span>
                          <span style={{fontSize:10,color:T.textMuted}}>{b.ticker}</span>
                          <button onClick={()=>{
                              const upd=userBenchmarks.filter(x=>x.id!==b.id);
                              setUserBenchmarks(upd);
                              try{localStorage.setItem("fw_user_benchmarks_v1",JSON.stringify(upd));}catch{}
                              if(bench===b.id) setBench(userBenchmarks.filter(x=>x.id!==b.id)[0]?.id||"");
                              showToast(`Removed ${b.label}`,"#D97706");
                            }}
                            style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.2)",
                              borderRadius:4,color:"#A32D2D",padding:"1px 7px",cursor:"pointer",fontSize:11}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom blend builder modal */}
            {showBlendBuilder && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(4px)",
                zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,
                  padding:28,width:"100%",maxWidth:480,boxShadow:"0 24px 80px rgba(0,0,0,0.4)",
                  animation:"scaleIn 0.18s ease"}}>
                  <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>⚗️ Custom Blend Builder</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:20}}>
                    Set weights for each benchmark. Total must equal 100%.
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                    {BENCHMARKS_DEF.map(b=>{
                      const w = blendDraft[b.id]||0;
                      return (
                        <div key={b.id} style={{display:"flex",alignItems:"center",gap:12}}>
                          <div style={{width:100,fontSize:12,fontWeight:600,color:T.textPrimary,flexShrink:0}}>{b.label}</div>
                          <input type="range" min="0" max="100" step="5" value={w}
                            onChange={e=>setBlendDraft(p=>({...p,[b.id]:Number(e.target.value)}))}
                            style={{flex:1}}/>
                          <div style={{width:40,fontSize:12,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                            color:T.textPrimary,textAlign:"right"}}>{w}%</div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Total indicator */}
                  {(function(){
                    const total = Object.values(blendDraft).reduce((s,v)=>s+v,0);
                    const ok = total===100;
                    return (
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"8px 12px",borderRadius:8,marginBottom:16,
                        background:ok?"rgba(22,163,74,0.08)":"rgba(220,38,38,0.08)",
                        border:`1px solid ${ok?"rgba(22,163,74,0.25)":"rgba(220,38,38,0.25)"}`}}>
                        <span style={{fontSize:12,color:ok?"#1D9E75":"#A32D2D",fontWeight:600}}>
                          Total: {total}%
                        </span>
                        {!ok&&<span style={{fontSize:11,color:"#A32D2D"}}>
                          {total>100?`Over by ${total-100}%`:`${100-total}% remaining`}
                        </span>}
                        {ok&&<span style={{fontSize:11,color:"#1D9E75"}}>✓ Ready to save</span>}
                      </div>
                    );
                  })()}
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{
                        const total = Object.values(blendDraft).reduce((s,v)=>s+v,0);
                        if(total!==100){ return; }
                        const blend = { weights:blendDraft, name:"Custom Blend" };
                        setCustomBlend(blend);
                        try { localStorage.setItem("fw_custom_blend_v1", JSON.stringify(blend)); } catch {}
                        setBench("custom");
                        setShowBlendBuilder(false);
                        showToast("✓ Custom blend saved","#1D9E75");
                      }}
                      disabled={Object.values(blendDraft).reduce((s,v)=>s+v,0)!==100}
                      style={{flex:1,background:Object.values(blendDraft).reduce((s,v)=>s+v,0)===100
                        ?"linear-gradient(135deg,#7C3AED,#6D28D9)":"rgba(124,58,237,0.3)",
                        border:"none",borderRadius:8,color:"white",padding:"10px",
                        cursor:Object.values(blendDraft).reduce((s,v)=>s+v,0)===100?"pointer":"default",
                        fontSize:13,fontWeight:700}}>
                      Save Blend
                    </button>
                    {customBlend&&<button onClick={()=>{
                        setCustomBlend(null); setBench("sp500");
                        try { localStorage.removeItem("fw_custom_blend_v1"); } catch {}
                        setShowBlendBuilder(false);
                        showToast("Custom blend removed","#D97706");
                      }}
                      style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.3)",
                        borderRadius:8,color:"#A32D2D",padding:"10px 14px",cursor:"pointer",fontSize:12}}>
                      Remove
                    </button>}
                    <button onClick={()=>setShowBlendBuilder(false)}
                      style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                        borderRadius:8,color:T.textSecondary,padding:"10px 14px",cursor:"pointer",fontSize:12}}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Point-to-Point date picker */}
          <div style={{...tCard(),marginBottom:20,borderColor:"#7C3AED"}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:12,color:"#7C3AED"}}>📅 Point-to-Point Return Calculator</div>
            <div style={{display:"flex",alignItems:"flex-end",gap:16,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>From Date</div>
                <input type="date" style={{...inp(),width:180}} value={p2pFromDate} onChange={e=>setP2pFromDate(e.target.value)}/>
              </div>
              <div style={{fontSize:12,color:T.textSecondary}}>→ Today ({new Date().toISOString().slice(0,10)})</div>
              <div style={{marginLeft:"auto",display:"flex",flexWrap:"wrap",gap:6}}>
                {[["MTD",startOfMonth()],["YTD",startOfYear()],["1Y",dateYearsAgo(1)],["3Y",dateYearsAgo(3)],["5Y",dateYearsAgo(5)]].map(([label,date])=>(
                  <button key={label} onClick={()=>setP2pFromDate(date)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #78716C",background:p2pFromDate===date?"#1E40AF":"transparent",color:p2pFromDate===date?"#2563EB":T.textSecondary,cursor:"pointer",fontSize:12,fontWeight:600}}>{label}</button>
                ))}
              </div>
            </div>
            {/* P2P results */}
            <div style={{display:isMobile?"flex":"grid",overflowX:isMobile?"auto":"visible",flexWrap:"nowrap",gridTemplateColumns:isMobile?`repeat(${BENCHMARKS_DEF.length+1},minmax(90px,1fr))`:`repeat(${BENCHMARKS_DEF.length+1},1fr)`,gap:10,marginTop:16}}>
              <div style={{background:T.cardBg,borderRadius:8,padding:isMobile?"6px 8px":"10px 12px",minWidth:isMobile?80:undefined,flexShrink:0,border:"1px solid #7c3aed"}}>
                <div style={{fontSize:10,color:"#7C3AED",fontWeight:600,marginBottom:4,textTransform:"uppercase"}}>Your Portfolio</div>
                <div style={{fontSize:isMobile?14:18,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:portP2p>=0?"#1D9E75":"#A32D2D"}}>{portP2p!=null?(portP2p>=0?"+":"")+portP2p.toFixed(2)+"%" : "—"}</div>
              </div>
              {BENCHMARKS_DEF.map(b=>{
                const p2p = p2pReturns[b.id];
                return (
                  <div key={b.id} style={{background:T.cardBg,borderRadius:8,padding:isMobile?"6px 8px":"10px 12px",minWidth:isMobile?80:undefined,flexShrink:0,border:bench===b.id?"1px solid #2563EB":"1px solid #EDE0D4"}}>
                    <div style={{fontSize:10,color:T.textSecondary,fontWeight:600,marginBottom:4,textTransform:"uppercase"}}>{b.label}</div>
                    <div style={{fontSize:isMobile?14:18,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:p2p>=0?"#2563EB":"#A32D2D"}}>{p2p!=null?(p2p>=0?"+":"")+p2p.toFixed(2)+"%" : "—"}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Portfolio vs benchmark comparison — cost-basis returns per time window */}
          {(function(){
            // Compute portfolio return for a given start date using cost basis
            // For each holding: value at start = qty * costPrice (proxy); current = qty * currentPrice
            // This mirrors the P2P logic but for fixed windows
            // Shared helper — same logic as computeP2P
            const portReturnFor = (fromDateStr) => {
              // TWRR: chain sub-period returns across each transaction date
              const fromTime = new Date(fromDateStr).getTime();
              const todayMs  = Date.now();
              const totalDays = Math.max(1, (todayMs - fromTime) / 86400000);
              const yrs = totalDays / 365.25;
              const hw = filteredHW;
              if (hw.length === 0) return null;

              const currentVal = hw.reduce((s,h) => s + toUSD(h.qty*h.currentPrice, h.currency), 0);
              if (currentVal <= 0) return null;

              // Portfolio value at a given date using last known transaction price
              const valAt = (dateStr, excludeOnDate = false) => {
                const dt = new Date(dateStr).getTime();
                let total = 0;
                hw.forEach(h => {
                  const txns = transactions
                    .filter(t => t.holding===h.name && (excludeOnDate ? new Date(t.date).getTime()<dt : new Date(t.date).getTime()<=dt))
                    .sort((a,b) => new Date(a.date)-new Date(b.date));
                  let qty=0, cost=0;
                  txns.forEach(t => {
                    if (t.type==="BUY") { qty+=t.qty; cost+=toUSD(t.qty*t.price,t.currency); }
                    else { const u=qty>0?cost/qty:0; qty=Math.max(0,qty-t.qty); cost=qty*u; }
                  });
                  const lastTxn = [...txns].reverse()[0];
                  const price = lastTxn ? lastTxn.price : h.costPrice;
                  total += toUSD(qty * price, h.currency);
                });
                return total;
              };

              // Start value: reconstruct using transaction history before fromDate
              let startVal = 0;
              hw.forEach(h => {
                const preTxns = transactions
                  .filter(t => t.holding===h.name && new Date(t.date).getTime() < fromTime)
                  .sort((a,b) => new Date(a.date)-new Date(b.date));
                let qty=0, cost=0;
                preTxns.forEach(t => {
                  if (t.type==="BUY") { qty+=t.qty; cost+=toUSD(t.qty*t.price,t.currency); }
                  else { const u=qty>0?cost/qty:0; qty=Math.max(0,qty-t.qty); cost=qty*u; }
                });
                startVal += qty > 0 ? cost : toUSD(h.qty*h.costPrice, h.currency);
              });
              if (startVal <= 0) return null;

              // Get transaction dates within the period
              const txDates = [...new Set(
                transactions
                  .filter(t => { const tx=new Date(t.date).getTime(); return tx>=fromTime && tx<=todayMs; })
                  .map(t => t.date)
              )].sort();

              // Chain TWRR sub-periods
              let twrrProduct = 1;
              let prevVal = startVal;
              txDates.forEach(d => {
                const before = valAt(d, true);
                if (before > 1 && prevVal > 1) {
                  const r = (before - prevVal) / prevVal;
                  if (isFinite(r) && Math.abs(r) < 10) twrrProduct *= (1+r);
                }
                const after = valAt(d, false);
                if (after > 0) prevVal = after;
              });
              // Final sub-period to today
              if (prevVal > 1 && currentVal > 1) {
                const r = (currentVal - prevVal) / prevVal;
                if (isFinite(r) && Math.abs(r) < 10) twrrProduct *= (1+r);
              }

              const twrr = (twrrProduct - 1) * 100;
              if (!isFinite(twrr)) return null;
              // Annualise if period > 1 year
              return yrs > 1.1 ? (Math.pow(1 + twrr/100, 1/yrs) - 1)*100 : twrr;
            };
            const portYTD    = periodReturns.ytd;
            const portOneY   = periodReturns.oneY;
            const portThreeY = periodReturns.threeY;
            const fmt = v => v!=null && isFinite(v) ? (v>=0?"+":"")+v.toFixed(2)+"%" : "—";
            const cards = [
              {label:"YTD Return",       portV:portYTD,    bmV:bmCurrent?.ytd,    bmLabel:bm?.label},
              {label:"1 Year Return",    portV:portOneY,   bmV:bmCurrent?.oneY,   bmLabel:bm?.label},
              {label:"3Y CAGR",          portV:portThreeY, bmV:bmCurrent?.threeY, bmLabel:bm?.label},
            ];
            return (
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:16,marginBottom:20}}>
                {cards.map((k,i)=>{
                  const pv = k.portV, bv = k.bmV;
                  const pvN = pv??0, bvN = bv??0;
                  const maxAbs = Math.max(Math.abs(pvN), Math.abs(bvN), 1);
                  return (
                    <div key={i} style={card()}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.05em"}}>{k.label}</div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
                        <div>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Your Portfolio</div>
                          <div style={{fontSize:22,fontWeight:700,color:pvN>=0?"#1D9E75":"#A32D2D"}}>{fmt(pv)}</div>
                        </div>
                        <div style={{fontSize:18,color:T.textLight}}>vs</div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>{k.bmLabel}</div>
                          <div style={{fontSize:22,fontWeight:700,color:"#2563EB"}}>{bv!=null?(bv>=0?"+":"")+bv.toFixed(2)+"%":"—"}</div>
                        </div>
                      </div>
                      <div style={{marginTop:10,height:5,background:T.borderSubtle,borderRadius:3}}>
                        <div style={{height:"100%",background:"linear-gradient(90deg,#1D9E75,#2563EB)",
                          width:`${Math.min(100,Math.max(0,(pvN/maxAbs)*100))}%`,borderRadius:3}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* All benchmarks full table */}
          <div style={card()}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>All Benchmarks — Performance Summary</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:920}}>
                <colgroup>
                  <col style={{minWidth:160}}/>
                  <col style={{minWidth:80}}/><col style={{minWidth:80}}/><col style={{minWidth:80}}/>
                  <col style={{minWidth:90}}/><col style={{minWidth:90}}/><col style={{minWidth:90}}/>
                  <col style={{minWidth:120}}/><col style={{minWidth:120}}/>
                </colgroup>
                <thead><tr>{["Benchmark","MTD","YTD","1Y","3Y CAGR","5Y CAGR","10Y CAGR","P2P (Selected Date)","vs Portfolio (1Y)"].map(h=><th key={h} style={{...tTh(),whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
                <tbody>
                  {BENCHMARKS_DEF.map((b,i)=>{
                    const m=benchMetrics[b.id]||BENCHMARK_FALLBACK[b.id];
                    // Compute portfolio 1Y return properly (cost-basis Modified Dietz)
                    const _fromTime1Y = new Date(dateYearsAgo(1)).getTime();
                    const _today1Y = Date.now();
                    const _days1Y = Math.max(1,(_today1Y-_fromTime1Y)/86400000);
                    const _endV = filteredHW.reduce((s,h)=>s+toUSD(h.qty*h.currentPrice,h.currency),0);
                    let _startV=0;
                    filteredHW.forEach(h=>{
                      let q=0,c=0;
                      transactions.filter(t=>t.holding===h.name&&new Date(t.date).getTime()<_fromTime1Y)
                        .sort((a,bb)=>new Date(a.date)-new Date(bb.date))
                        .forEach(t=>{ if(t.type==="BUY"){q+=t.qty;c+=toUSD(t.qty*t.price,t.currency);}else{const u=q>0?c/q:0;q=Math.max(0,q-t.qty);c=q*u;} });
                      const _avgC=q>0?c/q:h.costPrice; _startV+=toUSD(q*_avgC,h.currency);
                    });
                    let _nf=0,_wf=0;
                    transactions.forEach(t=>{const tx=new Date(t.date).getTime();if(tx<_fromTime1Y)return;const f=toUSD(t.qty*t.price+(t.type==="BUY"?t.fees:-t.fees),t.currency);const cf=t.type==="BUY"?-f:f;_nf+=cf;_wf+=cf*((_today1Y-tx)/86400000/_days1Y);});
                    const _g=_endV-_startV+_nf, _d=_startV+_wf;
                    const port1Y = Math.abs(_d)>1&&isFinite(_g/_d) ? (_g/_d)*100 : (filteredHW.reduce((s,h)=>s+toUSD(h.qty*h.costPrice,h.currency),0)>0?(((_endV-filteredHW.reduce((s,h)=>s+toUSD(h.qty*h.costPrice,h.currency),0))/filteredHW.reduce((s,h)=>s+toUSD(h.qty*h.costPrice,h.currency),0))*100):0);
                    const diff = port1Y - (m.oneY||0);
                    const p2p=p2pReturns[b.id];
                    return (
                      <tr key={b.id} style={{background:b.id===bench?"rgba(59,130,246,0.07)":i%2===0?"transparent":"rgba(0,0,0,0.02)"}}>
                        <td style={tTd({fontWeight:600,whiteSpace:"nowrap"})}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"nowrap"}}>
                            {b.label}
                            {m?.live
                              ? <span style={{fontSize:9,background:"rgba(22,163,74,0.12)",color:"#1D9E75",padding:"1px 5px",borderRadius:4,fontWeight:600,flexShrink:0}}>LIVE</span>
                              : benchLoading
                                ? <span style={{fontSize:9,background:"rgba(37,99,235,0.1)",color:"#2563EB",padding:"1px 5px",borderRadius:4,flexShrink:0}}>⟳</span>
                                : <span title="Could not fetch live data — check ticker format (use Yahoo Finance format e.g. ^NSEI, SPY, GLD)" style={{fontSize:9,background:"rgba(217,119,6,0.1)",color:"#D97706",padding:"1px 5px",borderRadius:4,flexShrink:0,cursor:"help"}}>STATIC ⚠</span>}
                            {b.id===bench&&<span style={{marginLeft:4,fontSize:11,color:"#2563EB",flexShrink:0}}>● Selected</span>}
                          </div>
                        </td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.mtd}/></td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.ytd}/></td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.oneY}/></td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.threeY}/></td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.fiveY}/></td>
                        <td style={tTd({whiteSpace:"nowrap"})}><Pct v={m.tenY}/></td>
                        <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",whiteSpace:"nowrap"})}>{p2p!=null?<Pct v={p2p}/>:"—"}</td>
                        <td style={tTd({whiteSpace:"nowrap"})}><span style={{color:diff>=0?"#1D9E75":"#A32D2D",fontWeight:600}}>{diff>=0?"↑ Out":"↓ Under"} {Math.abs(diff).toFixed(1)}%</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:10,fontSize:11,color:T.textMuted,padding:"8px 12px",background:T.cardBg,borderRadius:6}}>
              ℹ Benchmark data: MTD/YTD/CAGR metrics fetched via Yahoo Finance — cached for 60s. Portfolio returns computed using Modified Dietz method on your actual transactions and cost prices.
            </div>
          </div>
        </React.Fragment>}

        {/* ── Shortlist ── */}
        {tab==="shortlist" && (function(){ try {
          const SURL = "https://aelgwwapsldljmedjwvj.supabase.co";
          const SKEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlbGd3d2Fwc2xkbGptZWRqd3ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3OTU3NjMsImV4cCI6MjA4ODM3MTc2M30.QPdEIrHLGRRUR9XdiDa-i7rCzJcK73ogdM1vlfLObU4";
          const sHeaders = { apikey:SKEY, Authorization:`Bearer ${SKEY}`, "Content-Type":"application/json" };
          const aId = isAdmin ? "admin" : loggedInClientId;

          // ── CRUD helpers ──
          // fetchItemPrices — uses existing /api/price endpoint (same as Watchlist, proven to work)
          const fetchItemPrices = async (item) => {
            const isMF = item.asset_class === "Indian Mutual Funds";
            const existing = item.cached_data || {};
            let ticker = item.ticker;

            // ══ INDIAN MUTUAL FUNDS ══
            // shortlist-prices handles everything: name→schemeCode lookup + NAV history + returns
            if (isMF) {
              try {
                const p = new URLSearchParams({ type: "mf" });
                if (ticker) p.set("ticker", ticker);
                else p.set("name", item.name); // server will resolve scheme code from name
                const r = await fetch(`/api/shortlist-prices?${p.toString()}`);
                if (!r.ok) { console.warn(`[shortlist] MF HTTP ${r.status} for "${item.name}"`); return false; }
                const d = await r.json();
                if (!d || d.error || !d.price) { console.warn(`[shortlist] MF no data:`, d?.error, "for", item.name); return false; }
                // Save scheme code if the server resolved it from the fund name
                if (d.schemeCode && !ticker) {
                  await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${item.id}`,
                    {method:"PATCH",headers:sHeaders,body:JSON.stringify({ticker:d.schemeCode})}).catch(()=>{});
                  setShortlistItems(p=>p.map(i=>i.id===item.id?{...i,ticker:d.schemeCode}:i));
                }
                await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${item.id}`,
                  {method:"PATCH",headers:sHeaders,body:JSON.stringify({cached_data:d})}).catch(()=>{});
                setShortlistItems(p=>p.map(i=>i.id===item.id?{...i,cached_data:d}:i));
                return true;
              } catch(e) { console.warn(`[shortlist] MF error for "${item.name}":`, e.message); return false; }
            }

            // ══ EQUITIES / ETFs ══
            // Step 1: real-time price from /api/price (proven working)
            if (!ticker) { console.warn(`[shortlist] No ticker for "${item.name}"`); return false; }
            try {
              const params = new URLSearchParams({ symbol: ticker });
              if (item.exchange) params.append("exchange", item.exchange);
              params.append("country", item.currency==="GBP"?"UK":"USA");
              const r = await fetch(`/api/price?${params.toString()}`);
              if (!r.ok) { console.warn(`[shortlist] /api/price HTTP ${r.status} for ${ticker}`); return false; }
              const j = await r.json();
              if (!j?.price || j.price <= 0) { console.warn(`[shortlist] No price for ${ticker}`, j); return false; }

              // Step 2: returns from shortlist-prices (Supabase cache → Yahoo Finance fallback)
              let data = { ...existing, price: String(j.price), currency: j.currency || item.currency || "USD" };
              try {
                const p2 = new URLSearchParams({ ticker });
                if (item.exchange) p2.set("exchange", item.exchange);
                const r2 = await fetch(`/api/shortlist-prices?${p2.toString()}`);
                if (r2.ok) {
                  const d2 = await r2.json();
                  if (d2 && !d2.error && (d2.ret1m!=null||d2.ret3m!=null||d2.ret1y!=null)) {
                    data = { ...d2, price: String(j.price), currency: j.currency||d2.currency||item.currency||"USD" };
                  }
                }
              } catch(e) { /* keep existing returns */ }

              await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${item.id}`,
                {method:"PATCH",headers:sHeaders,body:JSON.stringify({cached_data:data})}).catch(()=>{});
              setShortlistItems(p=>p.map(i=>i.id===item.id?{...i,cached_data:data}:i));
              return true;
            } catch(e) { console.warn(`[shortlist] Equity error for ${ticker}:`, e.message); return false; }
          };
          const refreshAllPrices = async () => {
            setShortlistSaving(true);
            showToast("Refreshing prices…","#2563EB",2000);
            const results = await Promise.all(shortlistItems.map(item=>fetchItemPrices(item)));
            const ok = results.filter(Boolean).length;
            if (ok > 0) showToast(`Updated ${ok} of ${shortlistItems.length} prices`,"#1D9E75",2500);
            else showToast("Price fetch failed — check browser console & confirm /api/shortlist-prices.js is deployed","#A32D2D",5000);
            setShortlistSaving(false);
          };
          const loadShortlists = async () => {
            const r = await fetch(`${SURL}/rest/v1/shortlists?advisor_id=eq.${aId}&select=*&order=updated_at.desc`, { headers:sHeaders }).catch(()=>null);
            if (!r?.ok) return;
            const data = await r.json();
            setShortlists(data||[]);
            if ((data||[]).length > 0 && !activeShortlistId) {
              const slId = data[0].id;
              setActiveShortlistId(slId);
              await loadItems(slId); // ← load items immediately, don't wait for next render
            }
          };
          const loadItems = async (slId) => {
            setShortlistLoadedForId(slId); // mark as loading so we don't re-trigger
            const r = await fetch(`${SURL}/rest/v1/shortlist_items?shortlist_id=eq.${slId}&select=*&order=added_at.desc`, { headers:sHeaders }).catch(()=>null);
            if (!r?.ok) return;
            const data = await r.json();
            setShortlistItems(data||[]);
            // Restore AI cache
            const cache = {}; for (const i of (data||[])) if (i.ai_research) cache[i.id]=i.ai_research;
            setShortlistAiData(p=>({...p,...cache}));
            // Auto-fetch prices — include Indian MFs even without ticker (auto-heal will find it)
            const needsPrices = (data||[]).filter(i=>
              !i.cached_data?.price &&
              (i.ticker || i.asset_class === "Indian Mutual Funds")
            );
            if (needsPrices.length > 0) {
              showToast(`Fetching prices for ${needsPrices.length} item${needsPrices.length>1?"s":""}…`,"#2563EB",3000);
              Promise.all(needsPrices.map(item=>fetchItemPrices(item)))
                .then(results=>{
                  const ok = results.filter(Boolean).length;
                  if (ok > 0) showToast(`Prices loaded for ${ok} item${ok>1?"s":""}`,"#1D9E75",2000);
                  else showToast("Could not load prices — check /api/shortlist-prices.js","#A32D2D",5000);
                });
            }
          };
          const ensureShortlist = async (clientId) => {
            let sl = shortlists.find(s=>s.client_id===clientId);
            if (sl) { setActiveShortlistId(sl.id); setShortlistLoadedForId(null); await loadItems(sl.id); return sl; }
            const r = await fetch(`${SURL}/rest/v1/shortlists`, { method:"POST", headers:{...sHeaders,Prefer:"return=representation"}, body:JSON.stringify({advisor_id:aId,client_id:clientId,title:`${visibleClients.find(c=>c.id===clientId)?.name||"Client"}'s Shortlist`}) }).catch(()=>null);
            if (!r?.ok) return null;
            const [created] = await r.json(); setShortlists(p=>[created,...p]); setActiveShortlistId(created.id); setShortlistItems([]); setShortlistLoadedForId(created.id); return created;
          };
          const addItem = async (item) => {
            if (!activeShortlistId) { showToast("Select a client first","#D97706"); return; }
            // Strict duplicate check: same ticker (case-insensitive) in current shortlist = block
            const tickerLower = (item.ticker||"").toLowerCase();
            const nameLower   = (item.name||"").toLowerCase();
            if (shortlistItems.some(i=>
              (i.ticker && tickerLower && i.ticker.toLowerCase()===tickerLower) ||
              (i.name && nameLower && i.name.toLowerCase()===nameLower)
            )) {
              showToast(`${item.name} is already in your shortlist`,"#D97706",2500);
              return;
            }
            const body = { shortlist_id:activeShortlistId, name:item.name, ticker:item.ticker||null, isin:item.isin||null, asset_class:item.asset_class||item.type||"Equities", exchange:item.exchange||null, currency:item.currency||"USD", advisor_rating:"Buy", advisor_note:"", cached_data:item.cached_data||null };
            const r = await fetch(`${SURL}/rest/v1/shortlist_items`, { method:"POST", headers:{...sHeaders,Prefer:"return=representation"}, body:JSON.stringify(body) }).catch(()=>null);
            if (!r?.ok) { showToast("Failed to add","#A32D2D"); return; }
            const [created] = await r.json(); setShortlistItems(p=>[created,...p]); setShortlistSearchQ(""); setShortlistSearchRes([]); showToast(`Added: ${item.name}`,"#1D9E75",2500);
            // Fetch live prices in background
            fetchItemPrices(created);
          };
          const removeItem = async (itemId) => {
            await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${itemId}`, { method:"DELETE", headers:sHeaders }).catch(()=>null);
            setShortlistItems(p=>p.filter(i=>i.id!==itemId)); setCompareItems(p=>p.filter(id=>id!==itemId));
          };
          const saveNote = async (itemId, field, val) => {
            await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${itemId}`, { method:"PATCH", headers:sHeaders, body:JSON.stringify({[field]:val}) }).catch(()=>null);
            setShortlistItems(p=>p.map(i=>i.id===itemId?{...i,[field]:val}:i));
          };
          const runAi = async (item) => {
            if (shortlistAiLoading[item.id]) return;
            setShortlistAiLoading(p=>({...p,[item.id]:true}));
            try {
              const r = await fetch("/api/research", {
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body:JSON.stringify({
                  mode:"asset",
                  name: item.name,           // research.js expects "name", not "assetName"
                  ticker: item.ticker || item.name,
                  assetClass: item.asset_class,
                })
              });
              const d = await r.json();
              // research.js returns the parsed JSON directly (oneLiner, thesis, keyRisks, etc.)
              const parsed = d.research || d;
              console.log(`[shortlist AI] ${item.name} raw response keys:`, Object.keys(parsed||{}));
              console.log(`[shortlist AI] topHoldings:`, parsed?.topHoldings, "holdings:", parsed?.holdings);
              // Normalise to shape the compare modal and detail panels expect
              const ai = {
                thesis:      parsed.thesis      || parsed.holdRationale || "",
                risks:       Array.isArray(parsed.keyRisks)  ? parsed.keyRisks  : (parsed.risks||[]),
                suitability: parsed.holdRecommendation || parsed.analystConsensus || "",
                rating:      parsed.holdRecommendation || parsed.analystConsensus || "",
                oneLiner:    parsed.oneLiner    || "",
                sentiment:   parsed.sentiment   || "",
                catalysts:   Array.isArray(parsed.catalysts) ? parsed.catalysts : [],
                // Cost & Size fields
                expRatio:    parsed.expenseRatio || parsed.ter || parsed.expRatio || null,
                aum:         parsed.aum          || parsed.fundSize || parsed.totalAssets || null,
                exitLoad:    parsed.exitLoad     || null,
                minSip:      parsed.minSip       || parsed.minInvestment || null,
                // Sector allocation
                sectors:     Array.isArray(parsed.sectors)          ? parsed.sectors :
                             Array.isArray(parsed.sectorAllocation) ? parsed.sectorAllocation : [],
                // Top 5 portfolio holdings (for funds/ETFs) or peer companies (for stocks)
                topHoldings: Array.isArray(parsed.topHoldings)      ? parsed.topHoldings :
                             Array.isArray(parsed.holdings)         ? parsed.holdings :
                             Array.isArray(parsed.top10Holdings)    ? parsed.top10Holdings.slice(0,5) : [],
              };
              setShortlistAiData(p=>({...p,[item.id]:ai}));
              await fetch(`${SURL}/rest/v1/shortlist_items?id=eq.${item.id}`,
                {method:"PATCH",headers:sHeaders,body:JSON.stringify({ai_research:ai})}).catch(()=>null);
              setShortlistItems(p=>p.map(i=>i.id===item.id?{...i,ai_research:ai}:i));
              showToast(`AI research ready for ${item.name.split(" ").slice(0,3).join(" ")}`, "#7C3AED", 3000);
            } catch(e) {
              showToast("AI research failed — check console","#A32D2D");
              console.error("[shortlist AI]", e.message);
            }
            setShortlistAiLoading(p=>({...p,[item.id]:false}));
          };
          const doSearch = async (q) => {
            if (!q || q.length < 3) { setShortlistSearchRes([]); return; }
            setShortlistSearching(true);
            const results = [];
            const ql = q.toLowerCase();

            // ── Indian MFs via existing mf-search ──
            if (shortlistSearchType === "all" || shortlistSearchType === "mf") {
              try {
                const r = await fetch(`/api/mf-search?q=${encodeURIComponent(q)}`);
                if (r.ok) {
                  const d = await r.json();
                  (d.results || d || [])
                    .filter(f => {
                      const name = (f.schemeName || f.name || "").toLowerCase();
                      return name.includes(ql);
                    })
                    .slice(0, 6)
                    .forEach(f => {
                      const code = f.schemeCode || f.scheme_code || f.code || f.id;
                      results.push({
                        name: f.schemeName || f.name || String(f),
                        ticker: code ? String(code) : "",
                        isin: f.isin || "",
                        asset_class: "Indian Mutual Funds",
                        currency: "INR",
                        exchange: "BSE/NSE",
                      });
                    });
                }
              } catch(e) { /* silent */ }
            }

            // ── Stocks, ETFs, Global MFs via Yahoo Finance (no API key needed) ──
            if (shortlistSearchType === "all" || shortlistSearchType === "equity" ||
                shortlistSearchType === "etf" || shortlistSearchType === "global") {
              try {
                const r = await fetch(`/api/shortlist-search?q=${encodeURIComponent(q)}`);
                if (r.ok) {
                  const d = await r.json();
                  (Array.isArray(d) ? d : []).forEach(item => {
                    if (shortlistSearchType === "equity" && item.asset_class !== "Equities") return;
                    if (shortlistSearchType === "etf"    && item.asset_class !== "ETF") return;
                    if (shortlistSearchType === "global" && item.asset_class !== "Global Mutual Funds") return;
                    results.push(item);
                  });
                }
              } catch(e) { /* silent */ }
            }

            setShortlistSearchRes(results.slice(0, 14));
            setShortlistSearching(false);
          };
          const generateShare = async () => {
            if (!activeShortlistId) return;
            setShortlistSaving(true);
            const sl = shortlists.find(s=>s.id===activeShortlistId);
            const client = visibleClients.find(c=>c.id===sl?.client_id);
            const snapshot = { title:sl?.title||"Shortlist", advisorName:"Forthius Finalytics", clientName:client?.name||"", generatedAt:new Date().toISOString(), items:shortlistItems.map(i=>({...i,ai_research:shortlistAiData[i.id]||i.ai_research})) };
            const token = crypto.randomUUID();
            const r = await fetch(`${SURL}/rest/v1/shared_shortlists`, { method:"POST", headers:sHeaders, body:JSON.stringify({ token, shortlist_id:activeShortlistId, advisor_name:"Forthius Finalytics", client_name:client?.name||"", snapshot }) }).catch(()=>null);
            if (r?.ok) { setShortlistShareToken(token); showToast("Share link created!","#1D9E75",3000); }
            else showToast("Failed to generate link","#A32D2D");
            setShortlistSaving(false);
          };

          // ── Helpers ──
          const ratingColor = r => r==="Strong Buy"?"#1D9E75":r==="Buy"?"#1D9E75":r==="Hold"?"#D97706":r==="Sell"?"#A32D2D":"#64748B";
          const ratingBg    = r => r==="Strong Buy"?"rgba(22,163,74,0.1)":r==="Buy"?"rgba(22,163,74,0.08)":r==="Hold"?"rgba(217,119,6,0.1)":"rgba(220,38,38,0.08)";
          const classColor  = c => c==="Indian Mutual Funds"?"#0f766e":c==="Global Mutual Funds"?"#7C3AED":c==="ETF"?"#D97706":c==="Benchmark"?"#1d4ed8":"#2563EB";
          const classBg     = c => c==="Indian Mutual Funds"?"rgba(15,118,110,0.1)":c==="Global Mutual Funds"?"rgba(124,58,237,0.1)":c==="ETF"?"rgba(217,119,6,0.1)":c==="Benchmark"?"rgba(29,78,216,0.1)":"rgba(37,99,235,0.1)";

          // ── Derived ──
          const activeShortlist = shortlists.find(s=>s.id===activeShortlistId);
          const compareObjs     = compareItems.map(id=>shortlistItems.find(i=>i.id===id)).filter(Boolean);
          // Auto-detect benchmark: if any selected item has asset_class="Benchmark", use it as benchmark column
          const benchmarkItem   = compareObjs.find(i=>i.asset_class==="Benchmark");
          const regularCompare  = compareObjs.filter(i=>i.asset_class!=="Benchmark");
          const shareUrl = shortlistShareToken ? `${window.location.origin}?shortlistShare=${shortlistShareToken}` : null;

          // Loading is handled by useEffects at HNIDashboard level (tab change + activeShortlistId change)

          // ── RATINGS for a row ──
          const RATINGS = ["Strong Buy","Buy","Hold","Sell"];

          // ═══ COMPARE MODAL ═══
          const CompareModal = compareOpen && (regularCompare.length >= 2 || (regularCompare.length >= 1 && benchmarkItem)) && (() => {
            // Use benchmark item's cached_data if present, otherwise fall back to server-fetched benchmarkData
            const activeBenchmarkData = benchmarkItem ? (benchmarkItem.cached_data || {}) : benchmarkData;
            const activeBenchmarkName = benchmarkItem ? benchmarkItem.name : (compareBenchmark !== "None" ? compareBenchmark : null);
            const showBenchmark = !!(activeBenchmarkData?.price) && activeBenchmarkName && compareBenchmark !== "None";
            const BENCHMARKS = ["None","Nifty 50","Nifty Next 50","Nifty Bank","Nifty IT","Nifty Midcap 150","S&P 500","NASDAQ 100","Dow Jones","MSCI World","FTSE 100","Gold","Silver"];
            const metrics = [
              { section:"Returns", rows:[
                { label:"1 Month",       key:"ret1m" },
                { label:"3 Months",      key:"ret3m" },
                { label:"6 Months",      key:"ret6m" },
                { label:"1 Year",        key:"ret1y" },
                { label:"3Y CAGR",       key:"ret3y" },
                { label:"5Y CAGR",       key:"ret5y" },
              ]},
              { section:"Risk", rows:[
                { label:"Beta (3Y)",     key:"beta",    hint:"Run ✦ AI on each item to populate risk metrics" },
                { label:"Sharpe Ratio",  key:"sharpe"  },
                { label:"Sortino Ratio", key:"sortino" },
                { label:"Std Dev (1Y)",  key:"stddev"  },
                { label:"Max Drawdown",  key:"maxdd"   },
                { label:"Alpha (1Y)",    key:"alpha"   },
              ]},
              { section:"Cost & Size", rows:[
                { label:"Expense Ratio", key:"expRatio", hint:"Run ✦ AI on each item to populate cost data" },
                { label:"AUM",           key:"aum",      raw:true },
                { label:"Exit Load",     key:"exitLoad", raw:true },
                { label:"Min SIP",       key:"minSip",   raw:true },
              ]},
            ];
            // lower-is-better keys — for these we highlight the smallest as winner
            const lowerBetter = new Set(["expRatio","stddev","maxdd","exitLoad"]);
            const pctKeys = new Set(["ret1m","ret3m","ret6m","ret1y","ret3y","ret5y","beta","sharpe","sortino","stddev","maxdd","alpha","expRatio"]);
            const getBest = (key, items) /*(use regularCompare)*/ => {
              const vals = items.map(i=>{ const cd = i.cached_data||{}; const ai = shortlistAiData[i.id]||{}; return parseFloat(cd[key]??ai[key]??null); }).filter(v=>!isNaN(v));
              if (vals.length < 2) return null;
              return lowerBetter.has(key) ? Math.min(...vals) : Math.max(...vals);
            };
            const fmtCell = (item, key, raw) => {
              const cd = item.cached_data||{}; const ai = shortlistAiData[item.id]||{};
              const v = cd[key]??ai[key]??null;
              if (v===null||v===undefined) return "—";
              if (raw) return String(v);
              const n = parseFloat(v); if (isNaN(n)) return String(v);
              return pctKeys.has(key) ? `${n>=0?"+":""}${n.toFixed(1)}%` : n.toFixed(2);
            };
            const numVal = (item, key) => { const cd = item.cached_data||{}; const ai = shortlistAiData[item.id]||{}; return parseFloat(cd[key]??ai[key]??NaN); };

            return (
              <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.6)",display:"flex",flexDirection:"column"}}>
                {/* Modal header */}
                <div style={{background:"#0f172a",padding:"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                  <div>
                    <div style={{color:"#fff",fontSize:16,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
                      <NavIcon name="compare" size={16} color="#93c5fd" strokeWidth={1.8}/>
                      Compare Investments
                    </div>
                    <div style={{color:"#94a3b8",fontSize:11,marginTop:2}}>{regularCompare.length} selected · {activeShortlist?.title||"Shortlist"} · ★ = best in class</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:"#94a3b8",fontSize:11}}>Benchmark:</span>
                      <select value={compareBenchmark} onChange={e=>setCompareBenchmark(e.target.value)}
                        style={{background:"#1e293b",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",padding:"5px 10px",borderRadius:6,fontSize:11,fontFamily:"inherit",cursor:"pointer"}}>
                        <option value="None">None</option>
                        <optgroup label="── Indian">
                          <option>Nifty 50</option>
                          <option>Nifty Next 50</option>
                          <option>Nifty Bank</option>
                          <option>Nifty IT</option>
                        </optgroup>
                        <optgroup label="── Global">
                          <option>S&P 500</option>
                          <option>NASDAQ 100</option>
                          <option>Dow Jones</option>
                          <option>MSCI World</option>
                          <option>FTSE 100</option>
                        </optgroup>
                        <optgroup label="── Commodities">
                          <option>Gold</option>
                          <option>Silver</option>
                        </optgroup>
                      </select>
                    </div>
                    <button onClick={()=>setCompareOpen(false)}
                      style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",padding:"6px 14px",borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:600}}>
                      ✕ Close
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div style={{flex:1,overflowY:"auto",overflowX:"auto",padding:"16px 20px"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",background:"#fff",borderRadius:12,overflow:"hidden",border:"1px solid #e2e8f0",minWidth:700}}>
                    {/* Fund name headers */}
                    <thead>
                      <tr>
                        <th style={{background:"#0f172a",padding:"14px 12px",textAlign:"left",width:160,fontSize:10,color:"#64748b",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",borderRight:"1px solid rgba(255,255,255,0.06)"}}>Metric</th>
                        {regularCompare.map((item,ci)=>(
                          <th key={item.id} style={{background:"#0f172a",padding:"14px 12px",textAlign:"left",borderLeft:"1px solid rgba(255,255,255,0.06)"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                              <span style={{fontSize:9,background:classBg(item.asset_class),color:classColor(item.asset_class),padding:"2px 6px",borderRadius:6,fontWeight:700}}>{item.asset_class}</span>
                              {item.advisor_rating && <span style={{fontSize:9,background:ratingBg(item.advisor_rating),color:ratingColor(item.advisor_rating),padding:"2px 6px",borderRadius:4,fontWeight:700}}>{item.advisor_rating}</span>}
                            </div>
                            <div style={{color:"#fff",fontSize:12,fontWeight:700,lineHeight:1.3}}>{item.name.length>45?item.name.slice(0,45)+"…":item.name}</div>
                            <div style={{color:"#94a3b8",fontSize:10,marginTop:3}}>{item.ticker||""} {item.exchange?`· ${item.exchange}`:""}</div>
                            {item.cached_data?.price && <div style={{color:"#fff",fontSize:14,fontWeight:700,marginTop:5,fontVariantNumeric:"tabular-nums"}}>{item.currency==="INR"?"₹":"$"}{item.cached_data.price}</div>}
                          </th>
                        ))}
                        {showBenchmark && (
                          <th style={{background:"#1e3a5f",padding:"14px 12px",textAlign:"left",borderLeft:"2px solid rgba(99,179,237,0.4)"}}>
                            <span style={{fontSize:9,background:"rgba(147,197,253,0.2)",color:"#93c5fd",padding:"2px 6px",borderRadius:6,fontWeight:700}}>Benchmark</span>
                            <div style={{color:"#fff",fontSize:12,fontWeight:700,marginTop:6}}>{activeBenchmarkName}</div>
                            <div style={{color:"#94a3b8",fontSize:10,marginTop:2}}>{benchmarkLoading?"Fetching…":activeBenchmarkData?.price?`${activeBenchmarkData.currency==="INR"?"₹":"$"}${activeBenchmarkData.price}`:"Reference index"}</div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.map(section=>(
                        <React.Fragment key={section.section}>
                          <tr>
                            <td colSpan={regularCompare.length + 1 + (showBenchmark?1:0)}
                              style={{background:"#f1f5f9",padding:"8px 12px",fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",borderTop:"2px solid #e2e8f0"}}>
                              {section.section}
                            </td>
                          </tr>
                          {section.rows.map((row,rowIdx)=>{
                            const best = getBest(row.key, compareObjs);
                            return (
                              <tr key={row.key} style={{borderTop:"1px solid #f1f5f9"}}>
                                <td style={{padding:"10px 12px",fontSize:11,color:"#64748b",fontWeight:500,background:"#f8fafc",borderRight:"1px solid #e2e8f0",whiteSpace:"nowrap"}}>{row.label}</td>
                                {regularCompare.map(item=>{
                                  const v = numVal(item, row.key);
                                  const isBest = !isNaN(v) && best !== null && v === best;
                                  const cell = fmtCell(item, row.key, row.raw);
                                  const isPos = !isNaN(v) && v > 0;
                                  const isNeg = !isNaN(v) && v < 0;
                                  return (
                                    <td key={item.id} style={{padding:"10px 12px",fontSize:12,textAlign:"right",fontVariantNumeric:"tabular-nums",background:isBest?"rgba(22,163,74,0.07)":"transparent",borderLeft:"1px solid #f1f5f9",color:isNeg&&!lowerBetter.has(row.key)?"#A32D2D":isPos&&!lowerBetter.has(row.key)?"#1D9E75":isBest&&lowerBetter.has(row.key)?"#1D9E75":"#0f172a"}}>
                                      {cell}{isBest&&<span style={{color:"#1D9E75",marginLeft:3,fontSize:9}}>★</span>}
                                    </td>
                                  );
                                })}
                                {showBenchmark && <td style={{padding:"10px 12px",fontSize:11,textAlign:"right",background:"rgba(37,99,235,0.04)",borderLeft:"2px solid rgba(99,179,237,0.2)",color:"#94a3b8",fontStyle:"italic"}}>
                                  {benchmarkLoading ? "⟳" : activeBenchmarkData?.price ? (()=>{
                                    const v = activeBenchmarkData[row.key];
                                    if (v==null) return "—";
                                    const n = parseFloat(v);
                                    return isNaN(n) ? String(v) : `${n>=0?"+":""}${n.toFixed(1)}%`;
                                  })() : "—"}
                                </td>}
                              </tr>
                            );
                          })}
                          {/* Hint row when entire section is empty */}
                          {section.rows[0]?.hint && regularCompare.every(item=>section.rows.every(r=>{
                            const cd=item.cached_data||{}; const ai=shortlistAiData[item.id]||{};
                            return (cd[r.key]??ai[r.key]??null)===null;
                          })) && (
                            <tr><td colSpan={compareObjs.length+1+(compareBenchmark!=="None"?1:0)}
                              style={{padding:"8px 14px",fontSize:10,color:"#7C3AED",background:"rgba(124,58,237,0.04)",borderTop:"1px solid #f1f5f9",fontStyle:"italic"}}>
                              ✦ {section.rows[0].hint} — click the AI button on each item in the shortlist view
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                      {/* Sector allocation row */}
                      <tr><td colSpan={compareObjs.length+1+(compareBenchmark!=="None"?1:0)} style={{background:"#f1f5f9",padding:"8px 12px",fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",borderTop:"2px solid #e2e8f0"}}>Sector Allocation</td></tr>
                      <tr style={{borderTop:"1px solid #f1f5f9"}}>
                        <td style={{padding:"10px 12px",fontSize:11,color:"#64748b",fontWeight:500,background:"#f8fafc",borderRight:"1px solid #e2e8f0"}}>Top Sectors</td>
                        {regularCompare.map(item=>{
                          const sectors = shortlistAiData[item.id]?.sectors || item.ai_research?.sectors || null;
                          return (
                            <td key={item.id} style={{padding:"10px 12px",borderLeft:"1px solid #f1f5f9",verticalAlign:"top"}}>
                              {sectors ? sectors.slice(0,4).map((s,si)=>(
                                <div key={si} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                                  <span style={{fontSize:10,color:"#0f172a",width:110,flexShrink:0}}>{s.name}</span>
                                  <div style={{flex:1,height:4,background:"#f1f5f9",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",borderRadius:2,background:["#2563EB","#7C3AED","#0891B2","#1D9E75"][si%4],width:`${s.pct||10}%`}}/></div>
                                  <span style={{fontSize:10,color:"#64748b",width:32,textAlign:"right"}}>{s.pct||"—"}%</span>
                                </div>
                              )) : <span style={{fontSize:10,color:"#94a3b8"}}>Run ✦ AI to populate</span>}
                            </td>
                          );
                        })}
                        {showBenchmark && <td style={{padding:"10px 12px",borderLeft:"2px solid rgba(99,179,237,0.2)",background:"rgba(37,99,235,0.04)",fontSize:10,color:"#94a3b8",verticalAlign:"top"}}>—</td>}
                      </tr>
                      <tr style={{borderTop:"1px solid #f1f5f9"}}>
                        <td style={{padding:"10px 12px",fontSize:11,color:"#64748b",fontWeight:500,background:"#f8fafc",borderRight:"1px solid #e2e8f0"}}>Top Holdings</td>
                        {regularCompare.map(item=>{
                          const holdings = shortlistAiData[item.id]?.topHoldings || item.ai_research?.topHoldings || null;
                          return (
                            <td key={item.id} style={{padding:"10px 12px",borderLeft:"1px solid #f1f5f9",verticalAlign:"top"}}>
                              {holdings && holdings.length>0 ? holdings.slice(0,5).map((h,hi)=>(
                                <div key={hi} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,marginBottom:4,fontSize:10}}>
                                  <span style={{color:"#0f172a",fontWeight:500,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name||h.ticker||"—"}</span>
                                  <span style={{color:"#64748b",fontVariantNumeric:"tabular-nums",fontWeight:600,flexShrink:0}}>{h.pct!=null?`${(+h.pct).toFixed(1)}%`:""}</span>
                                </div>
                              )) : <span style={{fontSize:10,color:"#94a3b8"}}>Run ✦ AI to populate</span>}
                            </td>
                          );
                        })}
                        {showBenchmark && <td style={{padding:"10px 12px",borderLeft:"2px solid rgba(99,179,237,0.2)",background:"rgba(37,99,235,0.04)",fontSize:10,color:"#94a3b8",verticalAlign:"top"}}>—</td>}
                      </tr>
                      {/* AI Verdict row */}
                      <tr><td colSpan={regularCompare.length+1+(showBenchmark?1:0)} style={{background:"#f1f5f9",padding:"8px 12px",fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",borderTop:"2px solid #e2e8f0"}}>AI Verdict</td></tr>
                      <tr style={{borderTop:"1px solid #f1f5f9"}}>
                        <td style={{padding:"10px 12px",fontSize:11,color:"#64748b",fontWeight:500,background:"#f8fafc",borderRight:"1px solid #e2e8f0"}}>Summary</td>
                        {compareObjs.map(item=>{
                          const ai = shortlistAiData[item.id]||item.ai_research;
                          return (
                            <td key={item.id} style={{padding:"10px 12px",fontSize:11,lineHeight:1.6,color:"#0f172a",borderLeft:"1px solid #f1f5f9",verticalAlign:"top",maxWidth:220}}>
                              {ai ? <div>
                                {ai.oneLiner && <div style={{fontWeight:500,marginBottom:4}}>{ai.oneLiner}</div>}
                                {ai.thesis && <div style={{color:"#475569"}}>{ai.thesis}</div>}
                              </div> : <span style={{color:"#94a3b8",fontStyle:"italic"}}>Click ✦ AI on this item in the shortlist first</span>}
                            </td>
                          );
                        })}
                        {showBenchmark && <td style={{padding:"10px 12px",borderLeft:"2px solid rgba(99,179,237,0.2)",background:"rgba(37,99,235,0.04)",fontSize:10,color:"#94a3b8",fontStyle:"italic",verticalAlign:"top"}}>Reference index only</td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div style={{background:"#fff",borderTop:"1px solid #e2e8f0",padding:"12px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                  <div style={{fontSize:10,color:"#94a3b8"}}>★ best in class · AI analysis via Forthius · Cost & sector data: run ✦ AI on each item</div>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setSlReportOpen(true)}
                      style={{padding:"8px 16px",borderRadius:7,border:"1px solid #7C3AED",background:"rgba(124,58,237,0.08)",color:"#7C3AED",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      📄 Generate Report
                    </button>
                    <button onClick={generateShare} disabled={shortlistSaving}
                      style={{padding:"8px 16px",borderRadius:7,border:"1px solid #1D9E75",background:"rgba(22,163,74,0.08)",color:"#1D9E75",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      {shortlistSaving?"Generating…":"⬆ Share Comparison"}
                    </button>
                    {shareUrl && <button onClick={()=>{navigator.clipboard.writeText(shareUrl);showToast("Link copied!","#1D9E75",2000);}}
                      style={{padding:"8px 16px",borderRadius:7,border:"1px solid #2563EB",background:"rgba(37,99,235,0.08)",color:"#2563EB",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      📋 Copy Link
                    </button>}
                  </div>
                </div>

                {/* Report section picker modal */}
                {slReportOpen && (()=>{
                  const SECTIONS = [
                    { key:"returns",  label:"Returns",          desc:"1M, 3M, 6M, 1Y, 3Y CAGR, 5Y CAGR" },
                    { key:"risk",     label:"Risk Parameters",  desc:"Sharpe, Sortino, Std Dev, Max Drawdown" },
                    { key:"cost",     label:"Cost & Size",      desc:"Expense ratio, AUM, exit load — needs ✦ AI" },
                    { key:"sectors",  label:"Sector Allocation",desc:"Top 5 sector breakdown — needs ✦ AI" },
                    { key:"holdings", label:"Top Holdings",     desc:"Top 5 portfolio holdings — needs ✦ AI" },
                    { key:"verdict",  label:"AI Verdict",       desc:"One-liner, thesis, key risks, catalysts" },
                  ];
                  const generateReport = () => {
                    const cols = compareObjs;
                    const bm   = benchmarkData;
                    const fmtR = (v) => v==null?"—":`${Number(v)>=0?"+":""}${Number(v).toFixed(1)}%`;
                    const fmtV = (v) => v==null?"—":String(v);
                    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Forthius — Investment Comparison</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; }
  body { padding:32px; color:#0f172a; font-size:12px; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; border-bottom:2px solid #0f172a; padding-bottom:16px; }
  .brand { font-size:20px; font-weight:800; letter-spacing:-0.5px; }
  .brand span { color:#7C3AED; }
  .meta { font-size:11px; color:#64748b; text-align:right; }
  table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  th { background:#0f172a; color:#fff; padding:10px 12px; text-align:left; font-size:11px; font-weight:600; }
  th.metric { width:160px; background:#1e293b; }
  th.bm { background:#1e3a5f; }
  td { padding:8px 12px; border-bottom:1px solid #f1f5f9; font-size:11px; }
  td.metric { background:#f8fafc; color:#64748b; font-weight:500; border-right:1px solid #e2e8f0; }
  td.bm { background:#f0f7ff; color:#475569; font-style:italic; }
  .pos { color:#1D9E75; font-weight:600; }
  .neg { color:#A32D2D; }
  .section-header td { background:#f1f5f9; font-weight:700; font-size:10px; text-transform:uppercase; letter-spacing:0.08em; color:#475569; padding:8px 12px; border-top:2px solid #e2e8f0; }
  .fund-name { font-weight:700; font-size:13px; margin-bottom:2px; }
  .fund-sub { font-size:10px; color:#94a3b8; }
  .verdict { font-size:11px; line-height:1.6; }
  .risk-box { background:#fff5f5; border:1px solid #fecaca; border-radius:4px; padding:6px 8px; margin-top:4px; }
  .cat-box { background:#f0fdf4; border:1px solid #bbf7d0; border-radius:4px; padding:6px 8px; margin-top:4px; }
  .footer { margin-top:24px; padding-top:12px; border-top:1px solid #e2e8f0; font-size:10px; color:#94a3b8; }
  @media print { body { padding:16px; } }
</style></head><body>
<div class="header">
  <div><div class="brand">Forthius <span>Finalytics</span></div><div style="font-size:13px;color:#475569;margin-top:4px;">Investment Comparison Report</div></div>
  <div class="meta">Generated: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}<br/>${activeShortlist?.title||"Shortlist"}<br/>Benchmark: ${compareBenchmark!=="None"?compareBenchmark:"None"}</div>
</div>
<table>
<thead><tr><th class="metric">Metric</th>${cols.map(c=>`<th><div class="fund-name">${c.name.length>40?c.name.slice(0,40)+"…":c.name}</div><div class="fund-sub">${c.ticker||""} · ${c.asset_class}</div></th>`).join("")}${bm?`<th class="bm">Benchmark<br/><span style="font-size:10px;font-weight:400">${compareBenchmark}</span></th>`:""}</tr></thead>
<tbody>`;
                    const addSection = (label) => { html += `<tr class="section-header"><td colspan="${cols.length+1+(bm?1:0)}">${label}</td></tr>`; };
                    const addRow = (metric, keys, isCagr=false) => {
                      html += `<tr><td class="metric">${metric}</td>`;
                      cols.forEach(c => {
                        const cd=c.cached_data||{}, ai=shortlistAiData[c.id]||{};
                        const v = cd[keys[0]]??ai[keys[0]]??null;
                        const n = parseFloat(v);
                        const fmt = isNaN(n) ? (v?String(v):"—") : fmtR(n);
                        const cls = !isNaN(n)&&n>0?"pos":!isNaN(n)&&n<0?"neg":"";
                        html += `<td class="${cls}">${fmt}</td>`;
                      });
                      if (bm) { const v=bm[keys[0]]; const n=parseFloat(v); html += `<td class="bm">${isNaN(n)?"—":fmtR(n)}</td>`; }
                      html += `</tr>`;
                    };
                    const addRawRow = (metric, keys) => {
                      html += `<tr><td class="metric">${metric}</td>`;
                      cols.forEach(c => { const cd=c.cached_data||{}, ai=shortlistAiData[c.id]||{}; const v=cd[keys[0]]??ai[keys[0]]??null; html += `<td>${v!=null?v:"—"}</td>`; });
                      if (bm) html += `<td class="bm">—</td>`;
                      html += `</tr>`;
                    };
                    if (slReportSections.returns) {
                      addSection("Returns");
                      addRow("1 Month",["ret1m"]); addRow("3 Months",["ret3m"]); addRow("6 Months",["ret6m"]);
                      addRow("1 Year",["ret1y"]); addRow("3Y CAGR",["ret3y"]); addRow("5Y CAGR",["ret5y"]);
                    }
                    if (slReportSections.risk) {
                      addSection("Risk Parameters");
                      addRow("Sharpe Ratio",["sharpe"]); addRow("Sortino Ratio",["sortino"]);
                      addRow("Std Dev (1Y)",["stddev"]); addRow("Max Drawdown",["maxdd"]);
                    }
                    if (slReportSections.cost) {
                      addSection("Cost & Size");
                      addRow("Expense Ratio (%)",["expRatio"]); addRawRow("AUM",["aum"]);
                      addRawRow("Exit Load",["exitLoad"]); addRawRow("Min SIP / Investment",["minSip"]);
                    }
                    if (slReportSections.sectors) {
                      addSection("Sector Allocation");
                      html += `<tr><td class="metric">Top Sectors</td>`;
                      cols.forEach(c => {
                        const ai=shortlistAiData[c.id]||{};
                        const secs=(ai.sectors||c.ai_research?.sectors||[]).slice(0,5);
                        html += `<td>${secs.length>0?secs.map(s=>`${s.name||s}: ${s.pct!=null?s.pct+"%":""}`).join("<br/>"):"Run ✦ AI to populate"}</td>`;
                      });
                      if (bm) html += `<td class="bm">—</td>`;
                      html += `</tr>`;
                    }
                    if (slReportSections.holdings) {
                      addSection("Top Holdings");
                      html += `<tr><td class="metric">Top 5 Holdings</td>`;
                      cols.forEach(c => {
                        const ai=shortlistAiData[c.id]||{};
                        const hh=(ai.topHoldings||c.ai_research?.topHoldings||[]).slice(0,5);
                        html += `<td>${hh.length>0?hh.map(h=>`${h.name||h.ticker||"—"}: ${h.pct!=null?(+h.pct).toFixed(1)+"%":""}`).join("<br/>"):"Run ✦ AI to populate"}</td>`;
                      });
                      if (bm) html += `<td class="bm">—</td>`;
                      html += `</tr>`;
                    }
                    if (slReportSections.verdict) {
                      addSection("AI Verdict");
                      html += `<tr><td class="metric">Summary</td>`;
                      cols.forEach(c => {
                        const ai=shortlistAiData[c.id]||{};
                        html += `<td class="verdict">${ai.oneLiner?`<strong>${ai.oneLiner}</strong><br/>`:""}${ai.thesis||"Run ✦ AI to generate verdict"}`;
                        if (ai.risks?.length) html += `<div class="risk-box"><strong>Risks:</strong> ${ai.risks.slice(0,3).join(" · ")}</div>`;
                        if (ai.catalysts?.length) html += `<div class="cat-box"><strong>Catalysts:</strong> ${ai.catalysts.slice(0,2).join(" · ")}</div>`;
                        html += `</td>`;
                      });
                      if (bm) html += `<td class="bm" style="font-style:italic;color:#94a3b8">Reference index only</td>`;
                      html += `</tr>`;
                    }
                    html += `</tbody></table>
<div class="footer">Generated by Forthius Finalytics · AI-assisted analysis · Data: mfapi.in, Yahoo Finance, AMFI · For advisory use only — not a solicitation</div>
</body></html>`;
                    const win = window.open("", "_blank");
                    if (win) { win.document.write(html); win.document.close(); setTimeout(()=>win.print(), 500); }
                  };
                  return (
                    <div style={{position:"fixed",inset:0,zIndex:10001,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <div style={{background:"#fff",borderRadius:14,padding:28,width:440,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
                        <div style={{fontSize:16,fontWeight:700,color:"#0f172a",marginBottom:6}}>📄 Generate Comparison Report</div>
                        <div style={{fontSize:12,color:"#64748b",marginBottom:20}}>Select the sections to include in your report</div>
                        {SECTIONS.map(s=>(
                          <label key={s.key} style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:14,cursor:"pointer"}}>
                            <input type="checkbox" checked={slReportSections[s.key]} onChange={e=>setSlReportSections(p=>({...p,[s.key]:e.target.checked}))}
                              style={{marginTop:2,accentColor:"#7C3AED",width:15,height:15,flexShrink:0}}/>
                            <div>
                              <div style={{fontSize:13,fontWeight:600,color:"#0f172a"}}>{s.label}</div>
                              <div style={{fontSize:11,color:"#94a3b8"}}>{s.desc}</div>
                            </div>
                          </label>
                        ))}
                        <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
                          <button onClick={()=>setSlReportOpen(false)}
                            style={{padding:"8px 16px",borderRadius:7,border:"1px solid #e2e8f0",background:"#f8fafc",color:"#64748b",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                            Cancel
                          </button>
                          <button onClick={()=>{generateReport();setSlReportOpen(false);}}
                            style={{padding:"8px 18px",borderRadius:7,border:"none",background:"#7C3AED",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                            📄 Generate & Print
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })();

          // ═══ MAIN SHORTLIST UI ═══
          return (
            <React.Fragment>
              {CompareModal}

              <div style={{padding:"4px 0 20px"}}>
                {/* Header */}
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16}}>
                  <div>
                    <h2 style={{fontSize:20,fontWeight:700,color:T.textPrimary,display:"flex",alignItems:"center",gap:10}}>
                      <NavIcon name="bookmark" size={20} color={T.brand} strokeWidth={1.8}/>
                      Shortlist
                    </h2>
                    <div style={{fontSize:12,color:T.textSecondary,marginTop:3}}>Curate and share investment ideas with your clients</div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    {compareItems.length >= 2 && (
                      <button onClick={()=>setCompareOpen(true)}
                        style={{padding:"8px 16px",borderRadius:8,border:"none",background:"#0f172a",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:7}}>
                        <NavIcon name="compare" size={13} color="#fff" strokeWidth={2}/>
                        Compare {compareItems.length}
                      </button>
                    )}
                    {isAdmin && activeShortlistId && (
                      <button onClick={generateShare} disabled={shortlistSaving}
                        style={{padding:"8px 14px",borderRadius:8,border:"1px solid #1D9E75",background:"rgba(22,163,74,0.08)",color:"#1D9E75",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                        {shortlistSaving?"Generating…":"⬆ Share with Client"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Shared link banner */}
                {shareUrl && (
                  <div style={{background:"rgba(22,163,74,0.06)",border:"1px solid rgba(22,163,74,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>✅ Share link active</div>
                      <div style={{fontSize:11,color:"#2563EB",fontFamily:"monospace",marginTop:3,wordBreak:"break-all"}}>{shareUrl}</div>
                    </div>
                    <div style={{display:"flex",gap:8,flexShrink:0}}>
                      <button onClick={()=>{navigator.clipboard.writeText(shareUrl);showToast("Copied!","#1D9E75",2000);}}
                        style={{padding:"6px 12px",borderRadius:6,border:"1px solid #2563EB",background:"#fff",color:"#2563EB",fontSize:11,fontWeight:600,cursor:"pointer"}}>📋 Copy</button>
                      <button onClick={()=>setShortlistShareToken(null)}
                        style={{padding:"6px 10px",borderRadius:6,border:"1px solid #A32D2D",background:"transparent",color:"#A32D2D",fontSize:11,cursor:"pointer"}}>Revoke</button>
                    </div>
                  </div>
                )}

                {/* Client selector */}
                {isAdmin && (
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,color:T.textSecondary,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",whiteSpace:"nowrap"}}>Shortlist for</span>
                    {/* General / unassigned shortlist */}
                    {(()=>{
                      const generalSl = shortlists.find(s=>!s.client_id || s.client_id==="general");
                      const isActive = generalSl?.id === activeShortlistId;
                      return (
                        <button onClick={async()=>{
                          let sl = shortlists.find(s=>!s.client_id || s.client_id==="general");
                          if (sl) { setActiveShortlistId(sl.id); await loadItems(sl.id); return; }
                          const r = await fetch(`${SURL}/rest/v1/shortlists`,{method:"POST",headers:{...sHeaders,Prefer:"return=representation"},body:JSON.stringify({advisor_id:aId,client_id:null,title:"General Shortlist"})}).catch(()=>null);
                          if (!r?.ok) return;
                          const [created] = await r.json(); setShortlists(p=>[created,...p]); setActiveShortlistId(created.id); setShortlistItems([]);
                        }}
                          style={{padding:"5px 12px",borderRadius:16,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${isActive?"#7C3AED":T.cardBorder}`,background:isActive?"#7C3AED":"transparent",color:isActive?"#fff":T.textSecondary,transition:"all 0.15s"}}>
                          ✦ General
                        </button>
                      );
                    })()}
                    <div style={{width:1,height:18,background:T.cardBorder,flexShrink:0}}/>
                    {visibleClients.map(c=>{
                      const sl = shortlists.find(s=>s.client_id===c.id);
                      const isActive = sl?.id === activeShortlistId;
                      return (
                        <button key={c.id} onClick={()=>ensureShortlist(c.id)}
                          style={{padding:"5px 12px",borderRadius:16,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${isActive?T.brand:T.cardBorder}`,background:isActive?T.brand:"transparent",color:isActive?"#fff":T.textSecondary,transition:"all 0.15s"}}>
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Compare selection bar */}
                {compareItems.length > 0 && (
                  <div style={{background:"#2563EB",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{color:"#fff",fontWeight:700,fontSize:12}}>{compareItems.length} selected to compare:</span>
                      {compareItems.map(id=>{
                        const item = shortlistItems.find(i=>i.id===id);
                        return item ? (
                          <span key={id} style={{background:"rgba(255,255,255,0.2)",borderRadius:12,padding:"3px 10px",fontSize:11,fontWeight:600,color:"#fff",display:"flex",alignItems:"center",gap:5}}>
                            {item.name.split(" ").slice(0,3).join(" ")}
                            <button onClick={()=>setCompareItems(p=>p.filter(x=>x!==id))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.8)",cursor:"pointer",fontSize:12,padding:0,lineHeight:1}}>✕</button>
                          </span>
                        ) : null;
                      })}
                      {compareItems.length < 8 && <span style={{color:"rgba(255,255,255,0.6)",fontSize:11}}>+ {8-compareItems.length} more allowed</span>}
                    </div>
                    <button onClick={()=>setCompareOpen(true)}
                      style={{background:"#fff",color:"#2563EB",border:"none",padding:"7px 16px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      ⊞ Compare Now →
                    </button>
                  </div>
                )}

                {/* Search + add */}
                <div style={{marginBottom:14}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <div style={{display:"flex",gap:0,background:T.cardBg,borderRadius:8,border:`1px solid ${T.cardBorder}`,padding:3}}>
                      {[["all","All"],["mf","Indian MF"],["global","Global MF"],["equity","Stocks"],["etf","ETF"],["benchmark","Benchmarks"]].map(([k,l])=>(
                        <button key={k} onClick={()=>{setShortlistSearchType(k);setShortlistSearchQ("");setShortlistSearchRes([]);}}
                          style={{padding:"5px 12px",borderRadius:5,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:shortlistSearchType===k?(k==="benchmark"?"#0f172a":T.brand):"transparent",color:shortlistSearchType===k?"#fff":T.textSecondary,transition:"all 0.12s"}}>
                          {l}
                        </button>
                      ))}
                    </div>
                    {shortlistSearchType !== "benchmark" && (
                      <div style={{flex:1,position:"relative",minWidth:240}}>
                        <input value={shortlistSearchQ} onChange={e=>{setShortlistSearchQ(e.target.value);if(e.target.value.length>=3)doSearch(e.target.value);else setShortlistSearchRes([]);}}
                          placeholder="🔍  Search by name or ticker — e.g. 'Parag Parikh', 'AAPL', 'Nifty 50 ETF'"
                          style={{width:"100%",padding:"9px 14px",border:`1px solid ${T.cardBorder}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:T.inputBg,color:T.textPrimary,outline:"none"}}/>
                        {shortlistSearching && <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:11,color:T.textMuted}}>⟳</span>}
                        {shortlistSearchRes.length > 0 && (
                          <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:200,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",maxHeight:300,overflowY:"auto"}}>
                            {shortlistSearchRes.map((item,idx)=>(
                              <div key={idx} onClick={()=>addItem(item)}
                                style={{padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",transition:"background 0.1s"}}
                                onMouseEnter={e=>e.currentTarget.style.background=T.navActiveBg}
                                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                <div>
                                  <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{item.name}</div>
                                  <div style={{fontSize:10,color:T.textSecondary,marginTop:1}}>{item.ticker} · {item.exchange} · {item.currency}</div>
                                </div>
                                <span style={{fontSize:10,padding:"2px 7px",borderRadius:6,background:classBg(item.asset_class),color:classColor(item.asset_class),fontWeight:600,flexShrink:0}}>{item.asset_class}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Benchmark quick-add panel */}
                  {shortlistSearchType === "benchmark" && (()=>{
                    const BENCHMARKS = [
                      { group:"Indian", items:[
                        { name:"Nifty 50",         ticker:"NIFTYBEES.NS", exchange:"NSE", currency:"INR", note:"Nippon ETF" },
                        { name:"Nifty 500",         ticker:"NIFTY100LOWVOL.NS", exchange:"NSE", currency:"INR", note:"NSE 500" },
                        { name:"Nifty Next 50",     ticker:"JUNIORBEES.NS", exchange:"NSE", currency:"INR", note:"Junior BeES" },
                        { name:"Nifty Midcap",      ticker:"MIDBEES.NS",  exchange:"NSE", currency:"INR", note:"Mid 150" },
                        { name:"Nifty Bank",        ticker:"BANKBEES.NS", exchange:"NSE", currency:"INR", note:"Banking" },
                        { name:"Nifty IT",          ticker:"ITBEES.NS",   exchange:"NSE", currency:"INR", note:"Technology" },
                      ]},
                      { group:"Global", items:[
                        { name:"S&P 500",    ticker:"VOO",  exchange:"NASDAQ", currency:"USD", note:"Vanguard" },
                        { name:"NASDAQ 100", ticker:"QQQ",  exchange:"NASDAQ", currency:"USD", note:"Invesco" },
                        { name:"Dow Jones",  ticker:"DIA",  exchange:"NYSE",   currency:"USD", note:"SPDR" },
                        { name:"MSCI World", ticker:"VT",   exchange:"NYSE",   currency:"USD", note:"Vanguard All-World" },
                        { name:"FTSE 100",   ticker:"ISF.L",exchange:"LSE",    currency:"GBP", note:"iShares" },
                        { name:"Emerging Mkts",ticker:"EEM",exchange:"NYSE",   currency:"USD", note:"iShares MSCI EM" },
                      ]},
                      { group:"Commodities", items:[
                        { name:"Gold",   ticker:"GLD", exchange:"NYSE", currency:"USD", note:"SPDR Gold" },
                        { name:"Silver", ticker:"SLV", exchange:"NYSE", currency:"USD", note:"iShares Silver" },
                      ]},
                    ];
                    const alreadyAdded = new Set(shortlistItems.map(i=>i.ticker));
                    return (
                      <div style={{marginTop:10,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:16}}>
                        <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Click any benchmark to add it to your shortlist. It will appear as a regular item and fetch live returns automatically.</div>
                        {BENCHMARKS.map(group=>(
                          <div key={group.group} style={{marginBottom:14}}>
                            <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{group.group}</div>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              {group.items.map(bm=>{
                                const added = alreadyAdded.has(bm.ticker);
                                return (
                                  <button key={bm.ticker}
                                    onClick={()=>{ if(!added) addItem({...bm, asset_class:"Benchmark"}); }}
                                    disabled={added}
                                    style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${added?"#1D9E75":T.cardBorder}`,background:added?"rgba(22,163,74,0.08)":"transparent",color:added?"#1D9E75":T.textPrimary,cursor:added?"default":"pointer",fontSize:12,fontWeight:600,transition:"all 0.12s",display:"flex",flexDirection:"column",alignItems:"flex-start",gap:1}}>
                                    <span>{bm.name}</span>
                                    <span style={{fontSize:9,color:added?"#1D9E75":T.textMuted,fontWeight:400}}>{added?"✓ Added":bm.note+" · "+bm.ticker}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Shortlist table */}
                {!activeShortlistId ? (
                  <div style={{textAlign:"center",padding:"48px 24px",background:T.cardBg,border:`1px dashed ${T.cardBorder}`,borderRadius:12,color:T.textSecondary}}>
                    <div style={{fontSize:36,marginBottom:12}}>📋</div>
                    <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>Start a shortlist</div>
                    <div style={{fontSize:12}}>Choose <strong>✦ General</strong> for a personal research list, or pick a client to build a shortlist for them.</div>
                  </div>
                ) : shortlistItems.length === 0 ? (
                  <div style={{textAlign:"center",padding:"48px 24px",background:T.cardBg,border:`1px dashed ${T.cardBorder}`,borderRadius:12,color:T.textSecondary}}>
                    <div style={{fontSize:36,marginBottom:12}}>🔍</div>
                    <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>Shortlist is empty</div>
                    <div style={{fontSize:12}}>Search for stocks, ETFs, or mutual funds above and click to add them.</div>
                  </div>
                ) : (
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,overflow:"hidden"}}>
                    {/* Table header */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",borderBottom:`1px solid ${T.cardBorder}`,background:T.cardBg}}>
                      <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{shortlistItems.length} investment{shortlistItems.length!==1?"s":""} shortlisted</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <div style={{fontSize:10,color:T.textMuted}}>Tick up to 8 items and click Compare</div>
                        <button onClick={refreshAllPrices} disabled={shortlistSaving}
                          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${T.cardBorder}`,background:T.cardBg,color:shortlistSaving?T.brand:T.textSecondary,cursor:"pointer",display:"flex",alignItems:"center",gap:5,opacity:shortlistSaving?0.8:1}}>
                          <NavIcon name="performance" size={11} color={shortlistSaving?T.brand:T.textSecondary} strokeWidth={2}/>
                          {shortlistSaving?"Fetching…":"Refresh prices"}
                        </button>
                      </div>
                    </div>
                    <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
                      <thead>
                        <tr>
                          <th style={{width:28,padding:"9px 8px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`}}></th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"left",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em",minWidth:200}}>Investment</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"left",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>Class</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>Price</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>3M</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>6M</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>1Y</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>3Y CAGR</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>5Y CAGR</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"right",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>AUM</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"left",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em"}}>Rating</th>
                          <th style={{padding:"9px 12px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`,textAlign:"left",fontSize:10,fontWeight:700,color:T.thText,textTransform:"uppercase",letterSpacing:"0.06em",minWidth:140}}>Advisor Note</th>
                          <th style={{padding:"9px 8px",background:T.thBg,borderBottom:`1.5px solid ${T.cardBorder}`}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {shortlistItems.map((item,idx)=>{
                          const cd = item.cached_data||{};
                          const ai = shortlistAiData[item.id]||item.ai_research||{};
                          const checked = compareItems.includes(item.id);
                          const canCheck = checked || compareItems.length < 8;
                          const hasAi = !!shortlistAiData[item.id];
                          return (
                            <tr key={item.id} style={{borderTop:`1px solid ${T.tdBorder}`,background:checked?`${T.brand}06`:"transparent"}}>
                              <td style={{padding:"10px 8px",textAlign:"center",verticalAlign:"middle"}}>
                                <input type="checkbox" checked={checked} disabled={!canCheck&&!checked}
                                  onChange={e=>setCompareItems(p=>e.target.checked?[...p,item.id]:p.filter(x=>x!==item.id))}
                                  style={{width:14,height:14,accentColor:T.brand,cursor:canCheck||checked?"pointer":"not-allowed"}}/>
                              </td>
                              <td style={{padding:"10px 12px",verticalAlign:"middle",maxWidth:240}}>
                                <div style={{fontWeight:600,fontSize:12,color:T.textPrimary,lineHeight:1.3}}>{item.name}</div>
                                <div style={{fontSize:10,color:T.textSecondary,marginTop:2}}>{item.ticker||""}{item.exchange?` · ${item.exchange}`:""}</div>
                              </td>
                              <td style={{padding:"10px 12px",verticalAlign:"middle"}}>
                                <span style={{fontSize:10,padding:"2px 7px",borderRadius:6,background:classBg(item.asset_class),color:classColor(item.asset_class),fontWeight:600,whiteSpace:"nowrap"}}>{item.asset_class||"—"}</span>
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",color:T.textPrimary,whiteSpace:"nowrap",verticalAlign:"middle"}}>
                                {cd.price?`${item.currency==="INR"?"₹":"$"}${cd.price}`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",verticalAlign:"middle",color:cd.ret3m>0?"#1D9E75":cd.ret3m<0?"#A32D2D":T.textSecondary,fontWeight:cd.ret3m?600:400,whiteSpace:"nowrap"}}>
                                {cd.ret3m!=null?`${cd.ret3m>0?"+":""}${Number(cd.ret3m).toFixed(1)}%`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",verticalAlign:"middle",color:cd.ret6m>0?"#1D9E75":cd.ret6m<0?"#A32D2D":T.textSecondary,fontWeight:cd.ret6m?600:400,whiteSpace:"nowrap"}}>
                                {cd.ret6m!=null?`${cd.ret6m>0?"+":""}${Number(cd.ret6m).toFixed(1)}%`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",verticalAlign:"middle",color:cd.ret1y>0?"#1D9E75":cd.ret1y<0?"#A32D2D":T.textSecondary,fontWeight:cd.ret1y?600:400,whiteSpace:"nowrap"}}>
                                {cd.ret1y!=null?`${cd.ret1y>0?"+":""}${Number(cd.ret1y).toFixed(1)}%`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",verticalAlign:"middle",color:cd.ret3y>0?"#1D9E75":cd.ret3y<0?"#A32D2D":T.textSecondary,fontWeight:cd.ret3y?600:400,whiteSpace:"nowrap"}}>
                                {cd.ret3y!=null?`${cd.ret3y>0?"+":""}${Number(cd.ret3y).toFixed(1)}%`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:12,fontVariantNumeric:"tabular-nums",verticalAlign:"middle",color:cd.ret5y>0?"#1D9E75":cd.ret5y<0?"#A32D2D":T.textSecondary,fontWeight:cd.ret5y?600:400,whiteSpace:"nowrap"}}>
                                {cd.ret5y!=null?`${cd.ret5y>0?"+":""}${Number(cd.ret5y).toFixed(1)}%`:"—"}
                              </td>
                              <td style={{padding:"10px 12px",textAlign:"right",fontSize:11,color:T.textSecondary,verticalAlign:"middle",whiteSpace:"nowrap"}}>
                                {cd.aum||"—"}
                              </td>
                              <td style={{padding:"10px 12px",verticalAlign:"middle"}}>
                                <select value={item.advisor_rating||"Buy"} onChange={e=>saveNote(item.id,"advisor_rating",e.target.value)}
                                  style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:5,border:"none",cursor:"pointer",background:ratingBg(item.advisor_rating||"Buy"),color:ratingColor(item.advisor_rating||"Buy"),fontFamily:"inherit"}}>
                                  {RATINGS.map(r=><option key={r} value={r}>{r}</option>)}
                                </select>
                              </td>
                              <td style={{padding:"10px 12px",verticalAlign:"middle",maxWidth:200}}>
                                <input value={item.advisor_note||""} onChange={e=>{ setShortlistItems(p=>p.map(i=>i.id===item.id?{...i,advisor_note:e.target.value}:i)); }}
                                  onBlur={e=>saveNote(item.id,"advisor_note",e.target.value)}
                                  placeholder="Add note…"
                                  style={{width:"100%",fontSize:11,border:"none",background:"transparent",color:T.textSecondary,fontFamily:"inherit",outline:"none",cursor:"text"}}/>
                              </td>
                              <td style={{padding:"10px 8px",verticalAlign:"middle",whiteSpace:"nowrap"}}>
                                <div style={{display:"flex",gap:4,alignItems:"center"}}>
                                  {(()=>{
                                    const aiData     = shortlistAiData[item.id] || item.ai_research;
                                    const hasStale   = aiData && (!Array.isArray(aiData.topHoldings) || aiData.topHoldings.length === 0);
                                    const labelBase  = hasAi ? (hasStale ? "↻ AI" : "✦ AI") : "✦ AI";
                                    const tooltip    = hasStale ? "AI data is stale — click to refresh and fetch Top Holdings" : "Run AI Research";
                                    return (
                                      <button onClick={()=>runAi(item)} title={tooltip}
                                        style={{fontSize:10,padding:"3px 8px",borderRadius:4,border:`1px solid ${hasStale?"rgba(217,119,6,0.6)":hasAi?"rgba(124,58,237,0.5)":"rgba(124,58,237,0.25)"}`,background:hasStale?"rgba(217,119,6,0.12)":hasAi?"rgba(124,58,237,0.12)":"rgba(124,58,237,0.06)",color:hasStale?"#D97706":"#7C3AED",cursor:"pointer",fontWeight:600}}>
                                        {shortlistAiLoading[item.id]?"⟳":labelBase}
                                      </button>
                                    );
                                  })()}
                                  <button onClick={()=>removeItem(item.id)} title="Remove"
                                    style={{background:"transparent",border:"none",color:T.textMuted,cursor:"pointer",fontSize:14,padding:"2px 4px",borderRadius:4,lineHeight:1}}>✕</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* AI Research detail panel */}
                {shortlistItems.some(i=>shortlistAiData[i.id]) && (
                  <div style={{marginTop:16}}>
                    {shortlistItems.filter(i=>shortlistAiData[i.id]).map(item=>{
                      const ai = shortlistAiData[item.id];
                      return (
                        <div key={item.id} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:18,marginBottom:12}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                            <NavIcon name="research" size={14} color="#7C3AED" strokeWidth={1.8}/>
                            <span style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>{item.name}</span>
                            {ai.rating && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:ratingBg(ai.rating),color:ratingColor(ai.rating),fontWeight:700}}>{ai.rating}</span>}
                            {ai.sentiment && <span style={{fontSize:10,color:T.textMuted}}>· {ai.sentiment}</span>}
                          </div>
                          {ai.oneLiner && <div style={{fontSize:12,fontWeight:500,color:T.textPrimary,marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${T.cardBorder}`}}>{ai.oneLiner}</div>}
                          {ai.thesis && <div style={{fontSize:12,lineHeight:1.7,color:T.textPrimary,marginBottom:10}}>{ai.thesis}</div>}
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                            {ai.risks?.length>0 && (
                              <div style={{background:"rgba(220,38,38,0.05)",border:"1px solid rgba(220,38,38,0.15)",borderRadius:7,padding:"8px 12px"}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#A32D2D",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Key Risks</div>
                                {ai.risks.map((r,ri)=><div key={ri} style={{fontSize:11,color:T.textPrimary,marginBottom:3}}>· {r}</div>)}
                              </div>
                            )}
                            {ai.catalysts?.length>0 && (
                              <div style={{background:"rgba(22,163,74,0.05)",border:"1px solid rgba(22,163,74,0.15)",borderRadius:7,padding:"8px 12px"}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#1D9E75",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>Catalysts</div>
                                {ai.catalysts.map((c,ci)=><div key={ci} style={{fontSize:11,color:T.textPrimary,marginBottom:3}}>· {c}</div>)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        } catch(e) { return <div style={{padding:20,color:"#A32D2D"}}>Shortlist error: {e.message}</div>; }})()}

        {/* ── IRR ── */}
        {tab==="irr" && (function(){ try {

          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see irr analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          // ── All segments = predefined + user custom ──
          const ALL_SEGS = [
            ...IRR_SEGMENTS_DEF,
            ...irrCustomSegs,
          ];

          // ── Manager filter ──
          // Scope to active client/family filter first, then manager filter
          const irrScopedHoldings = filteredHW; // already filtered by client/family
          const irrScopedNames = new Set(irrScopedHoldings.map(h=>h.name));
          // Fuzzy match: normalize names to handle folio ledger vs MFapi name differences,
          // case variants, ticker entries, and abbreviation differences.
          const normIRRName = (s) => (s||"").toLowerCase()
            .replace(/[\-,\.&\/\(\)]/g," ")
            .replace(/\b(inc|corp|corporation|ltd|limited|plc|ag|sa|nv|gmbh|llc|holdings|group|companies|company|the|cl|class|cos|co)\b/g," ")
            .replace(/\b(regular|direct|plan|growth|dividend|reinvest|payout|option|fund|etf|trust|acc|dist)\b/g," ")
            .replace(/\s+/g," ").trim();
          // Build multiple lookup indexes
          const irrNormMap     = {}; // normalized name -> original name
          const irrTickerMap   = {}; // ticker (upper) -> original name
          const irrLowerMap    = {}; // lowercased name -> original name
          irrScopedHoldings.forEach(h => {
            const norm = normIRRName(h.name);
            if (norm) irrNormMap[norm] = h.name;
            irrLowerMap[(h.name||"").toLowerCase().trim()] = h.name;
            const tk = (h.ticker||"").toUpperCase().trim();
            if (tk && tk !== "-" && tk !== "—") irrTickerMap[tk] = h.name;
          });
          const _unmatchedTxnNames = new Set(); // diagnostic
          const findMatchedHolding = (txnName) => {
            if (!txnName) return null;
            // 1. Exact match
            if (irrScopedNames.has(txnName)) return txnName;
            // 2. Case-insensitive exact
            const lower = txnName.toLowerCase().trim();
            if (irrLowerMap[lower]) return irrLowerMap[lower];
            // 3. Ticker match — user may have typed "RDDT" instead of "REDDIT INC-CL A"
            const upperTxn = txnName.toUpperCase().trim();
            if (irrTickerMap[upperTxn]) return irrTickerMap[upperTxn];
            // 4. Normalized exact
            const norm = normIRRName(txnName);
            if (norm && irrNormMap[norm]) return irrNormMap[norm];
            // 5. Substring match (one direction or the other, normalized)
            if (norm && norm.length >= 4) {
              for (const [hn, orig] of Object.entries(irrNormMap)) {
                if (!hn) continue;
                if (hn.includes(norm) || norm.includes(hn)) return orig;
              }
            }
            // 6. Word overlap — relaxed: 50% of words (length >= 3) overlap
            if (norm) {
              const words = norm.split(" ").filter(w=>w.length>=3);
              if (words.length >= 1) {
                let bestMatch = null, bestScore = 0;
                for (const [hn, orig] of Object.entries(irrNormMap)) {
                  if (!hn) continue;
                  const matches = words.filter(w => hn.includes(w)).length;
                  if (matches > bestScore && matches >= Math.max(1, Math.ceil(words.length*0.5))) {
                    bestScore = matches;
                    bestMatch = orig;
                  }
                }
                if (bestMatch) return bestMatch;
              }
            }
            _unmatchedTxnNames.add(txnName);
            return null;
          };
          const irrScopedTxns = transactions
            .map(t => ({ ...t, _matchedHolding: findMatchedHolding(t.holding) }))
            .filter(t => t._matchedHolding !== null)
            .map(t => ({ ...t, holding: t._matchedHolding })); // rewrite holding to matched name
          // Diagnostic — log once per render so the user can see what didn't match
          if (_unmatchedTxnNames.size > 0 && typeof window !== "undefined" && !window.__irrLastUnmatched) {
            window.__irrLastUnmatched = [..._unmatchedTxnNames];
            console.log("[IRR] " + _unmatchedTxnNames.size + " transaction holding name(s) could not be matched to any current holding:", [..._unmatchedTxnNames]);
            console.log("[IRR] Available holding names:", [...irrScopedNames]);
            setTimeout(() => { try { delete window.__irrLastUnmatched; } catch(_){} }, 2000);
          }
          const ALL_MANAGERS = ["all", ...[...new Set(irrScopedHoldings.map(h=>h.manager||"Self-Managed"))].sort()];
          const irrFilteredHoldings = irrManagerFilter==="all" ? irrScopedHoldings : irrScopedHoldings.filter(h=>(h.manager||"Self-Managed")===irrManagerFilter);
          const irrFilteredTxns = irrManagerFilter==="all" ? irrScopedTxns : irrScopedTxns.filter(t=>{ const h=irrScopedHoldings.find(x=>x.name===t.holding); return h&&(h.manager||"Self-Managed")===irrManagerFilter; });

          // ── Compute overall portfolio IRR ──
          const overallIRR = (() => {
            let allCFs = [];
            // True XIRR — use actual transaction dates
            irrFilteredTxns.forEach(t => {
              const amtUSD = toUSD((t.qty*t.price)+(t.fees||0), t.currency||"USD");
              const isBuyType = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
              const isSellType = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
              if (isBuyType || isSellType) {
                allCFs.push({ date: t.date, amount: (isBuyType?-1:1)*amtUSD });
              }
            });
            const totalCV = irrFilteredHoldings.reduce((s,h)=>s+toUSD(h.qty*h.currentPrice,h.currency),0);
            const today = new Date().toISOString().slice(0,10);
            if (totalCV>0) allCFs.push({ date: today, amount: totalCV });
            if (allCFs.length<2) return null;
            allCFs.sort((a,b) => new Date(a.date)-new Date(b.date));
            try { const r=calcXIRR(allCFs); return (r!==null&&isFinite(r))?r:null; } catch { return null; }
          })();

          // ── Compute IRR per segment ──
          const segResults = ALL_SEGS.map(seg => {
            const segHoldings = irrFilteredHoldings.filter(seg.filter || (()=>false));
            const segNames = new Set(segHoldings.map(h=>h.name));
            const segTxns = irrFilteredTxns.filter(t => segNames.has(t.holding));
            let cfs = [];
            const today2 = new Date().toISOString().slice(0,10);
            segTxns.forEach(t => {
              const amtUSD = toUSD((t.qty*t.price)+(t.fees||0), t.currency||"USD");
              const isBuyT = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
              const isSellT = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
              if (isBuyT || isSellT) {
                cfs.push({ date: t.date, amount: (isBuyT?-1:1)*amtUSD });
              }
            });
            const cv = segHoldings.reduce((s,h)=>s+toUSD(h.qty*h.currentPrice,h.currency),0);
            const invested = -cfs.filter(c=>c.amount<0).reduce((s,c)=>s+c.amount,0);
            if (cv>0) cfs.push({ date: today2, amount: cv });
            cfs.sort((a,b) => new Date(a.date)-new Date(b.date));
            let irr = null;
            if (cfs.length>=2) { try { const r = calcXIRR(cfs); irr = (r!==null&&isFinite(r)) ? r : null; } catch { irr=null; } }
            // Per-holding IRR inside segment
            const holdingIRRs = segHoldings.map(h => {
              const hTxns = irrScopedTxns.filter(t=>t.holding===h.name);
              const today4 = new Date().toISOString().slice(0,10);
              // CRITICAL: cash flow amounts MUST be in the same currency as hCV (USD),
              // otherwise XIRR tries to balance USD against native-currency amounts
              // and Newton-Raphson either diverges or returns nonsense.
              const buyTypes = ["BUY","SIP","STP_IN","SWITCH_IN"];
              const sellTypes = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"];
              const hCFs = hTxns
                .filter(t => buyTypes.includes(t.type) || sellTypes.includes(t.type))
                .map(t => {
                  const nativeAmt = (+t.qty||0)*(+t.price||0) + (+t.fees||0);
                  const usdAmt = toUSD(nativeAmt, t.currency || h.currency || "USD");
                  return {
                    date: t.date,
                    amount: (buyTypes.includes(t.type) ? -1 : 1) * usdAmt,
                  };
                })
                .filter(cf => Math.abs(cf.amount) > 0.001); // drop zero-amount entries
              const hCV = toUSD((+h.qty||0)*(+h.currentPrice||0), h.currency || "USD");
              if (hCV > 0) hCFs.push({ date: today4, amount: hCV });
              hCFs.sort((a,b) => new Date(a.date)-new Date(b.date));
              let hIRR=null;
              // Need at least 2 cash flows AND mixed signs (one positive, one negative)
              if (hCFs.length >= 2) {
                const hasPos = hCFs.some(c => c.amount > 0);
                const hasNeg = hCFs.some(c => c.amount < 0);
                if (hasPos && hasNeg) {
                  try {
                    const r = calcXIRR(hCFs);
                    hIRR = (r !== null && isFinite(r)) ? r : null;
                  } catch { hIRR = null; }
                }
              }
              const investedUSD = hTxns
                .filter(t => buyTypes.includes(t.type))
                .reduce((s,t) => s + toUSD((+t.qty||0)*(+t.price||0) + (+t.fees||0), t.currency || h.currency || "USD"), 0);
              return {...h, irr:hIRR, invested:investedUSD, cv:hCV};
            });
            return { ...seg, irr, cv, invested, count: segHoldings.length, holdingIRRs };
          });

          const activeResults = segResults.filter(s => irrActiveSegs.includes(s.id) && s.irr!==null);

          // ── Multiple benchmark IRRs ──
          const bmResults = BENCHMARKS_DEF.filter(b => b != null).map(b => {
            const d = benchMetrics[b.id] || BENCHMARK_FALLBACK[b.id];
            return { ...b, irr1Y: d?.oneY??null, irr3Y: d?.threeY??null, irr5Y: d?.fiveY??null };
          });
          const activeBMs = bmResults.filter(b => irrActiveBMs.includes(b.id));

          // ── Bar chart helpers ──
          const allBars = [
            ...(overallIRR!==null ? [{ label:"Overall Portfolio", irr:overallIRR, color:T.brand, isOverall:true }] : []),
            ...activeResults.map(s=>({ label:s.label, irr:s.irr, color:s.color, icon:s.icon, id:s.id })),
            ...activeBMs.filter(b=>b.irr5Y!=null&&isFinite(b.irr5Y)).map(b=>({ label:b.label+" (5Y)", irr:b.irr5Y, color:"#94A3B8", isBench:true })),
          ];
          const maxAbs = allBars.length>0 ? Math.max(5, ...allBars.map(b=>Math.abs(b.irr||0))) : 5;

          const toggleSeg = id => setIrrActiveSegs(prev =>
            prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]
          );
          const toggleBM = id => setIrrActiveBMs(prev =>
            prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]
          );
          const removeSeg = id => {
            setIrrCustomSegs(prev => prev.filter(s=>s.id!==id));
            setIrrActiveSegs(prev => prev.filter(x=>x!==id));
          };

          const drillData = irrDrillSeg ? segResults.find(s=>s.id===irrDrillSeg) : null;

          // Asset class options for custom segment builder
          const ALL_CLASSES = [...new Set(holdings.map(h=>h.class).filter(Boolean))].sort();

          // ── Data quality: how many holdings have transactions ──
          const holdingsWithTxns = new Set(irrFilteredTxns.map(t=>t.holding));
          const holdingsMissingTxns = irrFilteredHoldings.filter(h=>!holdingsWithTxns.has(h.name));
          const coveragePct = irrFilteredHoldings.length > 0
            ? (holdingsWithTxns.size / irrFilteredHoldings.length * 100) : 100;

          // ── Txn-backed subset: holdings WITH transactions only ──
          // The overallIRR above is diluted because it includes terminal values
          // of holdings that have no transactions (no inflows to offset). For a
          // clean money-weighted return, restrict both the cash flows AND the
          // terminal value to the subset of holdings that actually have txns.
          const coveredHoldings = irrFilteredHoldings.filter(h => holdingsWithTxns.has(h.name));
          const coveredInvestedUSD = irrFilteredTxns
            .filter(t => ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type))
            .reduce((s,t) => s + toUSD(t.qty*t.price + (t.fees||0), t.currency||"USD"), 0);
          const coveredCV = coveredHoldings.reduce((s,h) => s + toUSD(h.qty*h.currentPrice, h.currency), 0);

          const coveredXIRR = (() => {
            if (coveredHoldings.length === 0) return null;
            let cfs = [];
            irrFilteredTxns.forEach(t => {
              const amtUSD = toUSD((t.qty*t.price)+(t.fees||0), t.currency||"USD");
              const isBuy  = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
              const isSell = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
              if (isBuy || isSell) cfs.push({ date: t.date, amount: (isBuy?-1:1)*amtUSD });
            });
            if (coveredCV > 0) cfs.push({ date: new Date().toISOString().slice(0,10), amount: coveredCV });
            if (cfs.length < 2) return null;
            cfs.sort((a,b) => new Date(a.date) - new Date(b.date));
            try { const r = calcXIRR(cfs); return (r!==null && isFinite(r)) ? r : null; } catch { return null; }
          })();

          // Modified Dietz TWRR — annualised. Approximation that strips out
          // cash-flow timing effects (unlike XIRR which is money-weighted).
          // Useful for comparing manager skill across portfolios with different
          // contribution patterns. For a single sub-period, MD ≈ TWRR.
          const coveredTWRR = (() => {
            if (coveredHoldings.length === 0) return null;
            const flows = [];
            irrFilteredTxns.forEach(t => {
              const amtUSD = toUSD((t.qty*t.price)+(t.fees||0), t.currency||"USD");
              const isBuy  = ["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type);
              const isSell = ["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(t.type);
              if (isBuy)  flows.push({ date: t.date, amount:  amtUSD });
              if (isSell) flows.push({ date: t.date, amount: -amtUSD });
            });
            if (flows.length === 0) return null;
            flows.sort((a,b) => new Date(a.date) - new Date(b.date));
            const startDate = new Date(flows[0].date);
            const endDate   = new Date();
            const DAY_MS    = 86400000;
            const T         = (endDate - startDate) / DAY_MS;
            if (T <= 0) return null;

            const EV          = coveredCV;
            const totalFlow   = flows.reduce((s,f) => s + f.amount, 0);
            let weightedFlow  = 0;
            for (const f of flows) {
              const t_i = (new Date(f.date) - startDate) / DAY_MS;
              const w_i = (T - t_i) / T;
              weightedFlow += w_i * f.amount;
            }
            const denom = weightedFlow; // BV = 0 (we start with no capital)
            if (denom <= 0) return null;
            const periodReturn = (EV - totalFlow) / denom;
            const years = T / 365.25;
            const annualised = years > 0
              ? (Math.pow(1 + periodReturn, 1/years) - 1) * 100
              : periodReturn * 100;
            return isFinite(annualised) ? annualised : null;
          })();

          return <React.Fragment>

          {/* ── Sub-tab nav ── */}
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{fontSize:17,fontWeight:700,marginBottom:3}}>IRR Analysis</div>
              <div style={{fontSize:12,color:T.textSecondary}}>XIRR from cash flows · segment comparison · multi-benchmark overlay</div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:8,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:T.textSecondary,fontWeight:500}}>Manager/Custodian:</span>
                {ALL_MANAGERS.map(m=>{
                  const col=m==="all"?"#007878":(MANAGER_COLORS[m]||"#78716C");
                  const isActive=irrManagerFilter===m;
                  return (
                    <button key={m} onClick={()=>setIrrManagerFilter(m)}
                      style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:12,cursor:"pointer",border:"none",transition:"all 0.15s",
                        background:isActive?`${col}18`:T.pageBg,color:isActive?col:T.textMuted,
                        outline:isActive?`2px solid ${col}`:"2px solid transparent"}}>
                      {m==="all"?"All":m}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{display:"flex",background:T.pageBg,borderRadius:8,padding:2,border:`1px solid ${T.cardBorder}`,gap:1}}>
              {[{id:"overview",label:"Overview",icon:"📊"},{id:"builder",label:"Segment Builder",icon:"🔧"},{id:"drilldown",label:"Drilldown",icon:"🔍"}].map(t=>(
                <button key={t.id} onClick={()=>setIrrSubTab(t.id)}
                  style={{border:"none",cursor:"pointer",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:600,whiteSpace:"nowrap",
                    background:irrSubTab===t.id?T.cardBg:"transparent",
                    color:irrSubTab===t.id?"#007878":T.textSecondary,
                    boxShadow:irrSubTab===t.id?"0 1px 4px rgba(0,0,0,0.08)":"none",transition:"all 0.15s"}}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════
              OVERVIEW TAB
          ═══════════════════════════════════════════════════════ */}
          {irrSubTab==="overview" && <React.Fragment>

            {/* KPI Row */}
            <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,overflow:"hidden",marginBottom:isMobile?10:18}}>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(5,1fr)"}}>
                {[
                  { label:"XIRR (txn-backed)", value: coveredXIRR!=null&&isFinite(coveredXIRR)?(coveredXIRR>=0?"+":"")+coveredXIRR.toFixed(1)+"%":"—",
                    col: coveredXIRR!=null&&isFinite(coveredXIRR)?(coveredXIRR>=0?"#1D9E75":"#A32D2D"):"#78716C",
                    sub: `${coveredHoldings.length} of ${irrFilteredHoldings.length} holdings` },
                  { label:"TWRR (txn-backed)", value: coveredTWRR!=null&&isFinite(coveredTWRR)?(coveredTWRR>=0?"+":"")+coveredTWRR.toFixed(1)+"%":"—",
                    col: coveredTWRR!=null&&isFinite(coveredTWRR)?(coveredTWRR>=0?"#1D9E75":"#A32D2D"):"#78716C",
                    sub:"Modified Dietz" },
                  { label:"Overall portfolio IRR", value: overallIRR!=null&&isFinite(overallIRR)?(overallIRR>=0?"+":"")+overallIRR.toFixed(1)+"%":"—",
                    col: overallIRR!=null&&isFinite(overallIRR)?(overallIRR>=0?"#1D9E75":"#A32D2D"):"#78716C",
                    sub: holdingsMissingTxns.length>0 ? `Diluted — ${holdingsMissingTxns.length} no-txn` : "All holdings" },
                  { label:"Invested (txn-backed)", value: fmt(convert(coveredInvestedUSD,dc),dc,true), col:T.textPrimary, sub:`${coveredHoldings.length} holdings` },
                  { label:"Current value", value: fmt(convert(coveredCV,dc),dc,true), col:T.textPrimary, sub:coveredCV>0?`P&L ${((coveredCV-coveredInvestedUSD)>=0?"+":"")}${fmt(convert(coveredCV-coveredInvestedUSD,dc),dc)}`:"—" },
                ].map((k,i,arr)=>(
                  <div key={i} style={{padding:"18px 20px",borderRight:i<arr.length-1?`1px solid ${T.borderSubtle}`:"none"}}>
                    <div style={{fontSize:11,fontWeight:400,color:T.textMuted,marginBottom:7}}>{k.label}</div>
                    <div style={{fontSize:21,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.value}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{k.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── XIRR data coverage banner ── */}
            {holdingsMissingTxns.length > 0 && (
              <div style={{marginBottom:16,padding:"12px 20px",
                background:T.surfaceBg,border:`1px solid ${T.borderSubtle}`,
                borderRadius:10,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:12,fontWeight:500,color:T.textPrimary,marginBottom:4}}>
                    Transaction coverage: {coveredHoldings.length} of {irrFilteredHoldings.length} holdings
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,lineHeight:1.5}}>
                    Txn-backed KPIs above are the trustworthy numbers. Overall IRR is diluted by {holdingsMissingTxns.length} holdings without transaction dates.
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                  <div style={{width:100,height:4,background:T.borderSubtle,borderRadius:2,position:"relative"}}>
                    <div style={{position:"absolute",top:0,left:0,height:"100%",borderRadius:2,background:"#1D9E75",width:`${coveragePct}%`}}/>
                  </div>
                  <span style={{fontSize:12,fontWeight:500,color:T.textPrimary}}>{coveragePct.toFixed(0)}%</span>
                </div>
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:16}}>

              {/* Left: Bar chart */}
              <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"18px 20px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:600}}>IRR Comparison</div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:11,color:T.textSecondary}}>Benchmark:</span>
                    {BENCHMARKS_DEF.slice(0,4).map(b=>(
                      <button key={b.id} onClick={()=>toggleBM(b.id)}
                        style={{fontSize:10,fontWeight:600,padding:"3px 9px",borderRadius:12,cursor:"pointer",border:"none",transition:"all 0.15s",
                          background:irrActiveBMs.includes(b.id)?"#94A3B830":T.pageBg,
                          color:irrActiveBMs.includes(b.id)?"#64748B":T.textMuted,
                          outline:irrActiveBMs.includes(b.id)?"2px solid #94A3B8":"2px solid transparent"}}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                {allBars.length===0 ? (
                  <div style={{textAlign:"center",color:T.textMuted,padding:"40px 0",fontSize:13}}>No segments selected. Use Segment Builder to add.</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {allBars.map((bar,i)=>{
                      const pct = bar.irr!==null ? (Math.abs(bar.irr)/maxAbs)*100 : 0;
                      const isNeg = (bar.irr||0)<0;
                      return (
                        <div key={i} style={{display:"flex",alignItems:"center",gap:isMobile?6:10,minWidth:0,overflow:"hidden"}}>
                          <div style={{width:isMobile?90:170,fontSize:isMobile?10:12,fontWeight:bar.isOverall||bar.isBench?700:500,
                            color:bar.isOverall?"#007878":bar.isBench?"#94A3B8":T.thText,
                            textAlign:"right",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flexShrink:0,cursor:bar.id?"pointer":"default"}}
                            onClick={()=>bar.id&&(setIrrDrillSeg(bar.id),setIrrSubTab("drilldown"))}>
                            {bar.icon&&<span style={{marginRight:5}}>{bar.icon}</span>}{bar.label}
                          </div>
                          <div style={{flex:1,minWidth:0,position:"relative",height:30,borderRadius:6,overflow:"hidden",background:T.pageBg,overflow:"hidden"}}>
                            <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${pct}%`,
                              background:isNeg?"#A32D2D":bar.color,
                              borderRadius:6,opacity:bar.isBench?0.45:0.85,
                              transition:"width 0.4s ease",
                              backgroundImage:bar.isBench?"repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.3) 4px,rgba(255,255,255,0.3) 8px)":"none"
                            }}/>
                            <div style={{position:"absolute",right:8,top:0,bottom:0,display:"flex",alignItems:"center",
                              fontSize:12,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                              color:isNeg?"#A32D2D":bar.isOverall?"#007878":bar.isBench?"#64748B":bar.color}}>
                              {bar.irr>=0?"+":""}{(bar.irr||0).toFixed(1)}%
                            </div>
                          </div>
                          {bar.id && (
                            <button onClick={()=>toggleSeg(bar.id)}
                              style={{fontSize:11,border:"none",background:"none",cursor:"pointer",color:T.textMuted,padding:"2px 4px"}}>✕</button>
                          )}
                        </div>
                      );
                    })}
                    {/* Benchmark reference notes */}
                    {activeBMs.length>0 && (
                      <div style={{marginTop:8,padding:"8px 12px",background:"rgba(148,163,184,0.08)",borderRadius:8,border:`1px solid ${T.cardBorder}`,fontSize:11,color:T.textSecondary}}>
                        📏 Benchmarks shown as 5Y CAGR (hatched bars) · Click a segment label to drill in
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: segment cards + overall */}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {/* Overall card */}
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"14px 16px",borderLeft:"4px solid #007878"}}>
                  <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Overall Portfolio IRR</div>
                  <div style={{fontSize:30,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:overallIRR!=null&&isFinite(overallIRR)?(overallIRR>=0?"#007878":"#A32D2D"):T.textMuted}}>
                    {overallIRR!=null&&isFinite(overallIRR) ? (overallIRR>=0?"+":"")+overallIRR.toFixed(2)+"%" : "—"}
                  </div>
                  <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{holdings.length} holdings · {transactions.length} txns</div>
                  {activeBMs.filter(bm=>bm!=null).map(bm=>(
                    bm?.irr5Y!=null && overallIRR!==null && (
                      <div key={bm.id} style={{marginTop:6,fontSize:11,fontWeight:600,color:overallIRR>(bm?.irr5Y||0)?"#007878":"#A32D2D"}}>
                        {overallIRR>(bm?.irr5Y||0)?"▲ +":"▼ "}{(overallIRR-(bm?.irr5Y||0)).toFixed(1)}% vs {bm.label}
                      </div>
                    )
                  ))}
                </div>

                {/* Active segment mini-cards */}
                <div style={{overflowY:"auto",maxHeight:400,display:"flex",flexDirection:"column",gap:8}}>
                  {activeResults.map(seg=>(
                    <div key={seg.id} style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:"12px 14px",borderLeft:`3px solid ${seg.color}`,cursor:"pointer"}}
                      onClick={()=>{setIrrDrillSeg(seg.id);setIrrSubTab("drilldown");}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontSize:10,color:seg.color,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>
                            {seg.icon} {seg.label}
                          </div>
                          <div style={{fontSize:21,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:seg.irr>=0?"#007878":"#A32D2D"}}>
                            {seg.irr!=null&&isFinite(seg.irr)?(seg.irr>=0?"+":"")+seg.irr.toFixed(2)+"%":"—"}
                          </div>
                        </div>
                        <button onClick={e=>{e.stopPropagation();toggleSeg(seg.id);}}
                          style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontSize:14,padding:"0 2px",lineHeight:1}}>✕</button>
                      </div>
                      <div style={{marginTop:6,display:"flex",gap:10,fontSize:10,color:T.textMuted}}>
                        <span>{seg.count} holdings</span>
                        <span>Inv: <strong style={{color:T.thText}}>{fmt(convert(seg.invested,dc),dc)}</strong></span>
                        <span>Val: <strong style={{color:T.thText}}>{fmt(convert(seg.cv,dc),dc,true)}</strong></span>
                      </div>
                      {activeBMs.filter(bm=>bm!=null).map(bm=>(
                        bm?.irr5Y!=null && (
                          <div key={bm.id} style={{marginTop:5,fontSize:10,fontWeight:600,color:seg.irr>(bm?.irr5Y||0)?"#007878":"#A32D2D"}}>
                            {seg.irr>(bm?.irr5Y||0)?"▲ +":"▼ "}{(seg.irr-(bm?.irr5Y||0)).toFixed(1)}% vs {bm.label}
                          </div>
                        )
                      ))}
                      <div style={{marginTop:6,fontSize:10,color:T.brand,fontWeight:500}}>Click to drill in →</div>
                    </div>
                  ))}
                  {activeResults.length===0 && (
                    <div style={{background:T.pageBg,borderRadius:10,padding:"20px",textAlign:"center",color:T.textMuted,fontSize:12,border:"1px dashed #DDD0C0"}}>
                      No segments active.<br/>
                      <span style={{color:T.brand,cursor:"pointer",fontWeight:600}} onClick={()=>setIrrSubTab("builder")}>Open Segment Builder →</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Full benchmark comparison table */}
            <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"16px 18px",marginTop:16}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Benchmark Comparison Table</div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:480}}>
                  <thead>
                    <tr style={{background:T.pageBg}}>
                      {["Segment","1Y Return","3Y CAGR","5Y CAGR","vs Nifty 5Y","vs S&P 5Y"].map(h=>(
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:T.textSecondary,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Overall */}
                    <tr style={{background:"rgba(0,120,120,0.04)",borderBottom:"2px solid rgba(0,121,193,0.12)"}}>
                      <td style={{padding:"10px 12px",fontWeight:700,color:T.brand}}>🌐 Overall Portfolio</td>
                      <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",color:T.textMuted,fontSize:11}}>—</td>
                      <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",color:T.textMuted,fontSize:11}}>—</td>
                      <td style={{padding:"10px 12px"}}>
                        <span style={{fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:overallIRR!=null&&isFinite(overallIRR)?(overallIRR>=0?"#007878":"#A32D2D"):T.textMuted,fontSize:14}}>
                          {overallIRR!=null&&isFinite(overallIRR)?(overallIRR>=0?"+":"")+overallIRR.toFixed(1)+"%":"—"}
                        </span>
                      </td>
                      {["nifty","sp500"].map(bid=>{
                        const bm = bmResults.find(b=>b.id===bid);
                        const diff = overallIRR!==null&&bm?.irr5Y!==null ? overallIRR-((bm?.irr5Y||0)) : null;
                        return <td key={bid} style={{padding:"10px 12px"}}>
                          {diff!==null ? <span style={{fontWeight:600,fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:diff>0?"#007878":"#A32D2D"}}>{diff>0?"▲ +":"▼ "}{diff.toFixed(1)}%</span> : <span style={{color:T.textMuted}}>—</span>}
                        </td>;
                      })}
                    </tr>
                    {/* Active segments */}
                    {activeResults.map((seg,i)=>{
                      const nifty = bmResults.find(b=>b.id==="nifty");
                      const sp    = bmResults.find(b=>b.id==="sp500");
                      return (
                        <tr key={seg.id} style={{borderBottom:"1px solid #F5ebe0",background:i%2===0?"transparent":"rgba(0,0,0,0.01)",cursor:"pointer"}}
                          onClick={()=>{setIrrDrillSeg(seg.id);setIrrSubTab("drilldown");}}>
                          <td style={{padding:"10px 12px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:8,height:8,borderRadius:"50%",background:seg.color,flexShrink:0}}/>
                              <span style={{fontWeight:500}}>{seg.icon} {seg.label}</span>
                            </div>
                          </td>
                          <td style={{padding:"10px 12px",color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif",fontSize:11}}>—</td>
                          <td style={{padding:"10px 12px",color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif",fontSize:11}}>—</td>
                          <td style={{padding:"10px 12px"}}>
                            <span style={{fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:seg.irr>=0?"#007878":"#A32D2D"}}>
                              {seg.irr!=null&&isFinite(seg.irr)?(seg.irr>=0?"+":"")+seg.irr.toFixed(1)+"%":"—"}
                            </span>
                          </td>
                          {[nifty,sp].filter(bm=>bm!=null).map((bm,bi)=>{
                            const diff = bm?.irr5Y!=null ? seg.irr-((bm?.irr5Y||0)) : null;
                            return <td key={bi} style={{padding:"10px 12px"}}>
                              {diff!==null ? <span style={{fontWeight:600,fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:diff>0?"#007878":"#A32D2D"}}>{diff>0?"▲ +":"▼ "}{diff.toFixed(1)}%</span> : <span style={{color:T.textMuted}}>—</span>}
                            </td>;
                          })}
                        </tr>
                      );
                    })}
                    {/* Benchmark rows */}
                    {bmResults.filter(bm=>bm!=null).map((bm,i)=>(
                      <tr key={bm.id} style={{background:"rgba(148,163,184,0.05)",borderTop:i===0?"2px dashed #DDD0C0":"none"}}>
                        <td style={{padding:"10px 12px",fontWeight:600,color:"#64748B"}}>📏 {bm.label}</td>
                        <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:"#64748B"}}>{bm.irr1Y!==null?bm.irr1Y!=null&&isFinite(bm.irr1Y)?(bm.irr1Y!=null&&isFinite(bm.irr1Y)?bm.irr1Y.toFixed(1)+"%":"—"):"—":"—"}</td>
                        <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:"#64748B"}}>{bm.irr3Y!==null?bm.irr3Y!=null&&isFinite(bm.irr3Y)?(bm.irr3Y!=null&&isFinite(bm.irr3Y)?bm.irr3Y.toFixed(1)+"%":"—"):"—":"—"}</td>
                        <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:"#64748B"}}>{bm?.irr5Y!=null&&isFinite(bm.irr5Y)?(bm?.irr5Y||0).toFixed(1)+"%":"—"}</td>
                        <td colSpan={2} style={{padding:"10px 12px",color:T.textMuted,fontSize:11}}>Reference benchmark</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </React.Fragment>}

          {/* ═══════════════════════════════════════════════════════
              SEGMENT BUILDER TAB
          ═══════════════════════════════════════════════════════ */}
          {irrSubTab==="builder" && <React.Fragment>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16}}>

              {/* Left: all segments with add/remove controls */}
              <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"18px 20px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:600}}>Predefined Segments</div>
                  <div style={{fontSize:11,color:T.textSecondary}}>Toggle to include in comparison</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {IRR_SEGMENTS_DEF.map(seg => {
                    const res = segResults.find(s=>s.id===seg.id);
                    const active = irrActiveSegs.includes(seg.id);
                    return (
                      <div key={seg.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,border:`1px solid ${active?seg.color:T.textLight}`,
                        background:active?seg.color+"0D":T.surfaceBg,transition:"all 0.15s"}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:seg.color,flexShrink:0}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:600,color:active?seg.color:T.thText}}>{seg.icon} {seg.label}</div>
                          <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>
                            {res ? `${res.count} holdings · Inv: ${fmt(convert(res.invested,dc),dc)}` : "No data"}
                          </div>
                        </div>
                        <div style={{textAlign:"right",minWidth:70}}>
                          {res?.irr!==null ? (
                            <div style={{fontSize:14,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:res.irr>=0?"#007878":"#A32D2D"}}>
                              {res.irr>=0?"+":""}{res.irr.toFixed(1)}%
                            </div>
                          ) : <div style={{fontSize:11,color:T.textMuted}}>no data</div>}
                        </div>
                        <button onClick={()=>toggleSeg(seg.id)}
                          style={{border:"none",cursor:"pointer",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,flexShrink:0,
                            background:active?seg.color:T.brand,color:T.cardBg,transition:"all 0.15s"}}>
                          {active ? "Remove" : "Add"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: custom segments + add form */}
              <div style={{display:"flex",flexDirection:"column",gap:14}}>

                {/* Custom segments list */}
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"18px 20px",flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div style={{fontSize:13,fontWeight:600}}>Custom Segments</div>
                    <button onClick={()=>setIrrAddModal(true)}
                      style={{background:"#007878",color:T.cardBg,border:"none",borderRadius:8,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      + New Segment
                    </button>
                  </div>
                  {irrCustomSegs.length===0 ? (
                    <div style={{textAlign:"center",padding:"30px 20px",border:"2px dashed #DDD0C0",borderRadius:10,color:T.textMuted,fontSize:12}}>
                      <div style={{fontSize:24,marginBottom:8}}>🔧</div>
                      No custom segments yet.<br/>
                      Click <strong>+ New Segment</strong> to create a filtered group of assets and compare their IRR.
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {irrCustomSegs.map(seg => {
                        const res = segResults.find(s=>s.id===seg.id);
                        const active = irrActiveSegs.includes(seg.id);
                        return (
                          <div key={seg.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,
                            border:`1px solid ${active?seg.color:T.textLight}`,background:active?seg.color+"0D":T.surfaceBg}}>
                            <div style={{width:10,height:10,borderRadius:"50%",background:seg.color,flexShrink:0}}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:13,fontWeight:600,color:active?seg.color:T.thText}}>{seg.icon} {seg.label}</div>
                              <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>
                                Classes: {(seg.classes||[]).join(", ")||"all"} · {res?.count||0} holdings
                              </div>
                            </div>
                            <div style={{textAlign:"right",minWidth:60}}>
                              {res?.irr!==null ? (
                                <div style={{fontSize:14,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:res.irr>=0?"#007878":"#A32D2D"}}>
                                  {res.irr>=0?"+":""}{res.irr.toFixed(1)}%
                                </div>
                              ) : <div style={{fontSize:11,color:T.textMuted}}>no data</div>}
                            </div>
                            <button onClick={()=>toggleSeg(seg.id)}
                              style={{border:"none",cursor:"pointer",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,
                                background:active?seg.color:T.brand,color:T.cardBg}}>
                              {active ? "Remove" : "Add"}
                            </button>
                            <button onClick={()=>removeSeg(seg.id)}
                              style={{border:`1px solid ${T.cardBorder}`,cursor:"pointer",borderRadius:8,padding:"4px 8px",fontSize:11,background:T.pageBg,color:T.textSecondary}}>
                              🗑
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Benchmark selector */}
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"16px 18px"}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Benchmark Overlay</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {bmResults.filter(bm=>bm!=null).map(bm=>{
                      const on = irrActiveBMs.includes(bm.id);
                      return (
                        <button key={bm.id} onClick={()=>toggleBM(bm.id)}
                          style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:600,border:"none",transition:"all 0.15s",
                            background:on?"#64748B20":T.pageBg,
                            color:on?"#475569":T.textSecondary,
                            outline:on?"2px solid #64748B":"2px solid transparent"}}>
                          📏 {bm.label}
                          {on && bm?.irr5Y!=null && <span style={{fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,marginLeft:4,color:"#64748B"}}>{bm?.irr5Y!=null&&isFinite(bm.irr5Y)?(bm?.irr5Y||0).toFixed(1)+"%":"—"}</span>}
                          <span style={{marginLeft:4}}>{on?"✕":"+"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Add segment modal */}
            {irrAddModal && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{background:T.cardBg,borderRadius:16,padding:"28px",width:isMobile?"92vw":480,maxWidth:"95vw",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
                  <div style={{fontSize:15,fontWeight:700,marginBottom:16}}>Create Custom Segment</div>

                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,display:"block",marginBottom:5}}>SEGMENT NAME</label>
                      <input value={irrNewSeg.label} onChange={e=>setIrrNewSeg(p=>({...p,label:e.target.value}))}
                        placeholder="e.g. My Tech Stocks"
                        style={{width:"100%",boxSizing:"border-box",padding:"8px 12px",border:`1px solid ${T.cardBorder}`,borderRadius:8,fontSize:13,fontFamily:"inherit",color:T.textPrimary}}/>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div>
                        <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,display:"block",marginBottom:5}}>ICON (emoji)</label>
                        <input value={irrNewSeg.icon} onChange={e=>setIrrNewSeg(p=>({...p,icon:e.target.value}))}
                          style={{width:"100%",boxSizing:"border-box",padding:"8px 12px",border:`1px solid ${T.cardBorder}`,borderRadius:8,fontSize:18,textAlign:"center"}}/>
                      </div>
                      <div>
                        <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,display:"block",marginBottom:5}}>COLOR</label>
                        <input type="color" value={irrNewSeg.color} onChange={e=>setIrrNewSeg(p=>({...p,color:e.target.value}))}
                          style={{width:"100%",height:38,border:`1px solid ${T.cardBorder}`,borderRadius:8,cursor:"pointer",padding:2}}/>
                      </div>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:T.textSecondary,display:"block",marginBottom:5}}>ASSET CLASSES TO INCLUDE</label>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"10px",border:`1px solid ${T.cardBorder}`,borderRadius:8,maxHeight:140,overflowY:"auto"}}>
                        {ALL_CLASSES.map(cls=>{
                          const on = irrNewSeg.classes.includes(cls);
                          return (
                            <button key={cls} onClick={()=>setIrrNewSeg(p=>({...p,classes:on?p.classes.filter(c=>c!==cls):[...p.classes,cls]}))}
                              style={{padding:"4px 10px",borderRadius:12,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                                background:on?T.brand:T.pageBg,color:on?"#FFFFFF":T.textSecondary}}>
                              {cls}
                            </button>
                          );
                        })}
                        {ALL_CLASSES.length===0 && <span style={{color:T.textMuted,fontSize:12}}>No asset classes found in holdings</span>}
                      </div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>Leave all unselected to include all asset classes</div>
                    </div>
                  </div>

                  <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
                    <button onClick={()=>{setIrrAddModal(false);setIrrNewSeg({label:"",icon:"📦",color:T.brand,classes:[]});}}
                      style={{padding:"8px 20px",border:`1px solid ${T.cardBorder}`,borderRadius:8,background:T.pageBg,color:T.textSecondary,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                      Cancel
                    </button>
                    <button onClick={()=>{
                        if(!irrNewSeg.label.trim()) return;
                        const id = "custom_"+Date.now();
                        const classes = irrNewSeg.classes;
                        const newSeg = {
                          id, label:irrNewSeg.label.trim(), icon:irrNewSeg.icon||"📦",
                          color:irrNewSeg.color, isCustom:true, classes,
                          filter: h => classes.length===0 || classes.includes(h.class),
                        };
                        setIrrCustomSegs(p=>[...p, newSeg]);
                        setIrrActiveSegs(p=>[...p, id]);
                        setIrrAddModal(false);
                        setIrrNewSeg({label:"",icon:"📦",color:T.brand,classes:[]});
                      }}
                      style={{padding:"8px 24px",border:"none",borderRadius:8,background:"#007878",color:T.cardBg,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                      Create Segment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>}

          {/* ═══════════════════════════════════════════════════════
              DRILLDOWN TAB
          ═══════════════════════════════════════════════════════ */}
          {irrSubTab==="drilldown" && <React.Fragment>
            {/* Segment picker */}
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:12,color:T.textSecondary,fontWeight:600}}>View segment:</span>
              {segResults.filter(s=>s.irr!==null||s.count>0).map(seg=>(
                <button key={seg.id} onClick={()=>setIrrDrillSeg(seg.id)}
                  style={{padding:"5px 14px",borderRadius:16,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",
                    background:irrDrillSeg===seg.id?seg.color:T.pageBg,
                    color:irrDrillSeg===seg.id?"#FFFFFF":T.textSecondary,
                    outline:irrDrillSeg===seg.id?`2px solid ${seg.color}`:"none"}}>
                  {seg.icon} {seg.label}
                </button>
              ))}
              {!irrDrillSeg && <span style={{color:T.textMuted,fontSize:12}}>← select a segment</span>}
            </div>

            {drillData ? (
              <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:16}}>
                {/* Left: segment summary */}
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"18px",borderLeft:`4px solid ${drillData.color}`}}>
                    <div style={{fontSize:11,color:drillData.color,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                      {drillData.icon} {drillData.label}
                    </div>
                    <div style={{fontSize:32,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:drillData.irr!=null&&isFinite(drillData.irr)?(drillData.irr>=0?"#007878":"#A32D2D"):T.textMuted}}>
                      {drillData.irr!=null&&isFinite(drillData.irr)?(drillData.irr>=0?"+":"")+drillData.irr.toFixed(2)+"%":"—"}
                    </div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:6}}>Segment XIRR (annualised)</div>
                    <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:6}}>
                      {[
                        {l:"Holdings",v:drillData.count},
                        {l:"Total Invested",v:fmt(convert(drillData.invested,dc),dc)},
                        {l:"Current Value",v:fmt(convert(drillData.cv,dc),dc,true)},
                        {l:"Gain / Loss",v:fmt(convert(drillData.cv-drillData.invested,dc),dc)},
                      ].map(r=>(
                        <div key={r.l} style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                          <span style={{color:T.textMuted}}>{r.l}</span>
                          <span style={{fontWeight:600,color:T.thText,fontFamily:"'Inter', system-ui, sans-serif"}}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* vs benchmarks mini-panel */}
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"14px 16px"}}>
                    <div style={{fontSize:12,fontWeight:600,marginBottom:10,color:T.thText}}>vs Benchmarks (5Y CAGR)</div>
                    {bmResults.filter(bm=>bm!=null).map(bm=>{
                      const diff = bm?.irr5Y!=null ? drillData.irr-((bm?.irr5Y||0)) : null;
                      return (
                        <div key={bm.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid #F5ebe0"}}>
                          <span style={{fontSize:11,color:T.textSecondary}}>{bm.label}</span>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:11,color:"#64748B",fontFamily:"'Inter', system-ui, sans-serif"}}>{bm?.irr5Y!=null&&isFinite(bm.irr5Y)?(bm?.irr5Y||0).toFixed(1)+"%":"—"}</div>
                            {diff!==null && (
                              <div style={{fontSize:11,fontWeight:700,color:diff>0?"#007878":"#A32D2D"}}>
                                {diff>0?"▲ +":"▼ "}{diff.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: individual holding IRRs */}
                <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"18px 20px"}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Individual Asset IRR within "{drillData.label}"</div>
                  {drillData.holdingIRRs.length===0 ? (
                    <div style={{textAlign:"center",color:T.textMuted,padding:"40px",fontSize:13}}>No holdings in this segment.</div>
                  ) : (
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead>
                          <tr style={{background:T.pageBg}}>
                            {["Asset","Class","Invested","Current Value (Abs P&L)","XIRR","vs Seg Avg"].map(h=>(
                              <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:T.textSecondary,fontSize:11,textTransform:"uppercase",letterSpacing:"0.05em",whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...drillData.holdingIRRs].sort((a,b)=>{
                            // Sort: holdings with IRR first (desc), then no-txn at bottom
                            if (a.irr===null && b.irr!==null) return 1;
                            if (a.irr!==null && b.irr===null) return -1;
                            return (b.irr||0)-(a.irr||0);
                          }).map((h,i)=>{
                            const vsSeg = h.irr!==null && drillData.irr!==null ? h.irr-drillData.irr : null;
                            const hTxnCount = irrFilteredTxns.filter(t=>t.holding===h.name).length;
                            const hasTxns   = hTxnCount > 0;
                            const absGain   = h.cv - h.invested;
                            return (
                              <tr key={h.name} style={{borderBottom:`1px solid ${T.borderSubtle}`,
                                background:i%2===0?"transparent":T.tableStripe,
                                opacity:hasTxns?1:0.7}}>
                                <td style={{padding:"10px 12px",fontWeight:600,color:T.textPrimary,maxWidth:180}}>
                                  <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name}</div>
                                  <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                    {hasTxns
                                      ? <span style={{color:"#1D9E75"}}>✓ {hTxnCount} transaction{hTxnCount!==1?"s":""}</span>
                                      : <span style={{color:"#D97706"}}>⚠ no transactions — using cost price</span>}
                                  </div>
                                </td>
                                <td style={{padding:"10px 12px",color:T.textSecondary,fontSize:11}}>{h.class||"—"}</td>
                                <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,color:T.thText}}>
                                  {h.invested>0?fmt(convert(h.invested,dc),dc):"—"}
                                </td>
                                <td style={{padding:"10px 12px",fontFamily:"'Inter', system-ui, sans-serif",fontSize:12}}>
                                  <div style={{color:h.cv>h.invested?"#1D9E75":h.cv<h.invested?"#A32D2D":T.thText,fontWeight:600}}>
                                    {h.cv>0?fmt(convert(h.cv,dc),dc,true):"—"}
                                  </div>
                                  {h.invested>0&&h.cv>0 && (
                                    <div style={{fontSize:10,color:absGain>=0?"#1D9E75":"#A32D2D",marginTop:1}}>
                                      {absGain>=0?"+":""}{fmt(convert(absGain,dc),dc)}
                                    </div>
                                  )}
                                </td>
                                <td style={{padding:"10px 12px"}}>
                                  {h.irr!==null ? (
                                    <div>
                                      <div style={{fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",
                                        color:h.irr>=20?"#059669":h.irr>=10?"#1D9E75":h.irr>=0?"#34D399":"#A32D2D",fontSize:14}}>
                                        {(h.irr>=0?"+":"")+h.irr.toFixed(1)+"%"}
                                      </div>
                                      <div style={{height:4,width:Math.min(80,Math.abs(h.irr)*2),
                                        borderRadius:2,background:h.irr>=0?"#1D9E75":"#A32D2D",
                                        opacity:0.6,marginTop:3}}/>
                                    </div>
                                  ) : (
                                    <span style={{color:"#D97706",fontSize:11,fontStyle:"italic"}}
                                      title={
                                        !hasTxns
                                          ? "Add a buy transaction to compute XIRR"
                                          : h.invested <= 0
                                            ? "Transaction has zero qty × price. Edit the transaction and set proper qty + price."
                                            : h.cv <= 0
                                              ? "Holding has zero current value — XIRR needs a positive end value to compute."
                                              : "XIRR could not be computed — check transaction dates and amounts."
                                      }>
                                      {!hasTxns
                                        ? "needs transactions"
                                        : h.invested <= 0
                                          ? "txn has 0 amount"
                                          : h.cv <= 0
                                            ? "no current value"
                                            : "can't compute"}
                                    </span>
                                  )}
                                </td>
                                <td style={{padding:"10px 12px"}}>
                                  {vsSeg!==null ? (
                                    <span style={{fontWeight:700,fontSize:12,
                                      color:vsSeg>2?"#059669":vsSeg>0?"#1D9E75":vsSeg>-2?"#D97706":"#A32D2D"}}>
                                      {vsSeg>0?"▲ +":"▼ "}{Math.abs(vsSeg).toFixed(1)}%
                                    </span>
                                  ) : <span style={{color:T.textMuted}}>—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"60px",textAlign:"center",color:T.textMuted,fontSize:13}}>
                <div style={{fontSize:32,marginBottom:12}}>🔍</div>
                Select a segment above to drill into individual asset IRRs.
              </div>
            )}
          </React.Fragment>}

          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in irr tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ── UPLOAD DATA ── */}
        {tab==="upload" && <React.Fragment>
          <div style={{fontSize:15,fontWeight:500,marginBottom:16}}>Upload data</div>

          {/* ── Streamlined uploader: pick type → one drop zone ── */}
          {(function(){
            const TYPES = {
              holdings: {
                label: "Holdings", target: "holdings",
                desc: "Stocks, ETFs, private assets — global + Indian. Mutual funds go in their own type.",
                sample: () => downloadCSV(SAMPLE_HOLDINGS_CSV,"sample_holdings.csv"),
                accept: ".csv,.xlsx,.xls",
                dropNote: ".csv, .xlsx, .xls",
                cols: [["Name","Full asset name"],["Ticker","Exchange ticker"],["Class","Asset class"],["Exchange","NSE / NYSE / …"],["Currency","USD / INR / …"],["Qty","Units held"],["CostPrice","Avg purchase price"],["CurrentPrice","Latest price (optional)"],["Country","Listing country"],["Listed","true / false"],["ISIN","ISIN (optional)"],["Manager/Custodian","Custodian name"]],
              },
              transactions: {
                label: "Transactions", target: "transactions",
                desc: "Full trade history for XIRR. IBKR Transaction History exports are auto-detected.",
                sample: () => downloadCSV(SAMPLE_TRANSACTIONS_CSV,"sample_transactions.csv"),
                accept: ".csv,.xlsx,.xls",
                dropNote: ".csv, .xlsx, .xls · IBKR auto-detected",
                cols: [["Date","YYYY-MM-DD (or DD-MM-YYYY)"],["Holding","Must match holding name"],["Type","BUY / SELL / SIP / REDEEM"],["Qty","Units traded"],["Price","Per-unit price"],["Currency","USD / INR / …"],["Fees","Transaction fees (optional)"]],
              },
              mf: {
                label: "Indian MF", target: "mf",
                desc: "Indian mutual fund schemes. NAV auto-fetched from MFapi.in — no key needed.",
                sample: () => downloadCSV(SAMPLE_MF_CSV,"sample_indian_mf.csv"),
                accept: ".csv",
                dropNote: "MFapi.in format · .csv",
                cols: [["SchemeName","Full scheme name"],["SchemeCode","MFapi.in code (e.g. 118989)"],["ISIN","Scheme ISIN"],["Units","Units in folio"],["PurchaseNAV","NAV at purchase"],["CurrentNAV","Latest NAV (auto-fetched)"],["PurchaseDate","YYYY-MM-DD"],["Folio","Folio number (optional)"],["AMC","Fund house"],["Manager/Custodian","Custodian name"]],
              },
              globalmf: {
                label: "Global MF / UCITS", target: "globalmf",
                desc: "UCITS (Ireland/Luxembourg), US mutual funds, offshore funds. NAV entered manually.",
                sample: () => downloadCSV(SAMPLE_GLOBAL_MF_CSV,"sample_global_mf.csv"),
                accept: ".csv",
                dropNote: "UCITS · US funds · .csv",
                cols: [["FundName","Full fund name"],["ISIN","e.g. IE00B3RBWM25"],["Currency","USD / EUR / GBP …"],["Units","Units held"],["CostNAV","Purchase NAV"],["CurrentNAV","Latest NAV (manual)"],["PurchaseDate","YYYY-MM-DD"],["Domicile","Ireland, Luxembourg …"],["AMC","Vanguard, BlackRock …"],["Manager/Custodian","Your platform"]],
              },
            };
            const t = TYPES[uploadType];

            return (
              <div style={{...tCard(),padding:0,overflow:"hidden",marginBottom:16}}>
                {/* Type selector */}
                <div style={{display:"flex",borderBottom:`1px solid ${T.borderSubtle}`}}>
                  {Object.entries(TYPES).map(([key,def]) => {
                    const active = uploadType === key;
                    return (
                      <div key={key} onClick={()=>{setUploadType(key);setUploadColsOpen(false);}}
                        style={{flex:1,padding:"12px 16px",textAlign:"center",cursor:"pointer",
                          fontSize:12,fontWeight:active?500:400,
                          color:active?T.textPrimary:T.textMuted,
                          background:active?T.surfaceBg:"transparent",
                          borderBottom:active?`2px solid ${T.textPrimary}`:"2px solid transparent",
                          transition:"all 0.12s"}}>
                        {def.label}
                      </div>
                    );
                  })}
                </div>

                <div style={{padding:"18px 20px"}}>
                  {/* Description + actions row */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:14,flexWrap:"wrap"}}>
                    <div style={{fontSize:12,color:T.textSecondary,flex:1,minWidth:200}}>{t.desc}</div>
                    <div style={{display:"flex",gap:8,flexShrink:0}}>
                      <button onClick={()=>setUploadColsOpen(o=>!o)}
                        style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,borderRadius:6,
                          color:T.textSecondary,padding:"5px 12px",cursor:"pointer",fontSize:11}}>
                        {uploadColsOpen ? "Hide columns" : "Column reference"}
                      </button>
                      <button onClick={t.sample}
                        style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,borderRadius:6,
                          color:T.textSecondary,padding:"5px 12px",cursor:"pointer",fontSize:11}}>
                        ⬇ Sample CSV
                      </button>
                    </div>
                  </div>

                  {/* Column reference (collapsed by default) */}
                  {uploadColsOpen && (
                    <div style={{background:T.surfaceBg,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"2px 24px"}}>
                        {t.cols.map(([col,desc])=>(
                          <div key={col} style={{display:"flex",gap:10,padding:"4px 0",borderBottom:`1px solid ${T.borderSubtle}`}}>
                            <span style={{fontFamily:"'SF Mono','Cascadia Code',monospace",fontSize:11,color:T.textPrimary,minWidth:120,flexShrink:0}}>{col}</span>
                            <span style={{fontSize:11,color:T.textMuted}}>{desc}</span>
                          </div>
                        ))}
                      </div>
                      {uploadType==="mf" && <div style={{marginTop:8,fontSize:11,color:T.textMuted}}>NAV auto-fetched from mfapi.in/mf/{"{SchemeCode}"}/latest</div>}
                    </div>
                  )}

                  {/* Drop zone */}
                  <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                    border:`1.5px dashed ${T.cardBorder}`,borderRadius:10,padding:"28px 20px",cursor:"pointer",gap:6,
                    transition:"border-color 0.15s"}}>
                    <div style={{fontSize:13,fontWeight:500,color:T.textPrimary}}>Drop {t.label} file here or click to browse</div>
                    <div style={{fontSize:11,color:T.textMuted}}>{t.dropNote}</div>
                    <input type="file" accept={t.accept} style={{display:"none"}} onChange={e=>handleUpload(e, t.target)}/>
                  </label>

                  {/* Google Sheets import — one quiet line */}
                  <div style={{display:"flex",gap:8,marginTop:12,alignItems:"center"}}>
                    <input id={`gs-${uploadType}`} style={{...inp(),fontSize:11}} placeholder="Or paste a Google Sheets CSV export URL…"/>
                    <button onClick={()=>{ const u=document.getElementById(`gs-${uploadType}`).value; if(u) handleGSheetImport(u, t.target); }}
                      style={{background:"transparent",border:`1px solid ${T.borderSubtle}`,borderRadius:6,color:T.textSecondary,padding:"7px 14px",cursor:"pointer",whiteSpace:"nowrap",fontSize:11}}>
                      Import
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Last Upload Status per Client ── */}
          {(()=>{
            // Compute per-client upload status from in-memory data
            // holding_owners maps holding.id → client.id
            // transactions.holding (text) matches holding.name
            const holdingById   = Object.fromEntries(holdings.map(h=>[h.id, h]));
            const holdingByName = Object.fromEntries(holdings.map(h=>[h.name, h]));
            const clientById    = Object.fromEntries(clients.map(c=>[c.id, c]));

            // Reverse: holdingId → clientId
            const ownerOf = (hId) => holdingOwners[hId];

            // For each client compute: lastHoldingsAt, lastTxnsAt, holdingCount, txnCount
            const perClient = {};
            for (const c of clients) {
              perClient[c.id] = { client:c, lastHoldingsAt:null, lastTxnsAt:null, holdingsCount:0, txnsCount:0 };
            }
            // Holdings tally + last update
            for (const h of holdings) {
              const cId = ownerOf(h.id);
              if (!cId || !perClient[cId]) continue;
              perClient[cId].holdingsCount++;
              const ts = h.updated_at || h.created_at;
              if (ts) {
                const t = new Date(ts).getTime();
                if (!perClient[cId].lastHoldingsAt || t > perClient[cId].lastHoldingsAt) perClient[cId].lastHoldingsAt = t;
              }
            }
            // Transactions tally + last upload
            for (const t of transactions) {
              const h = holdingByName[t.holding];
              if (!h) continue;
              const cId = ownerOf(h.id);
              if (!cId || !perClient[cId]) continue;
              perClient[cId].txnsCount++;
              const ts = t.created_at;
              if (ts) {
                const tt = new Date(ts).getTime();
                if (!perClient[cId].lastTxnsAt || tt > perClient[cId].lastTxnsAt) perClient[cId].lastTxnsAt = tt;
              }
            }
            // Sort: most recent activity first (max of either timestamp)
            const rows = Object.values(perClient)
              .filter(r=>r.holdingsCount>0 || r.txnsCount>0)
              .sort((a,b)=>{
                const ma = Math.max(a.lastHoldingsAt||0, a.lastTxnsAt||0);
                const mb = Math.max(b.lastHoldingsAt||0, b.lastTxnsAt||0);
                return mb - ma;
              });

            const fmt = (ts) => {
              if (!ts) return "—";
              const d = new Date(ts);
              const daysAgo = Math.floor((Date.now() - ts)/(1000*60*60*24));
              const dateStr = d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
              const ago = daysAgo === 0 ? "today" : daysAgo === 1 ? "1 day ago" : daysAgo < 30 ? `${daysAgo} days ago` : daysAgo < 60 ? "1 month ago" : `${Math.floor(daysAgo/30)} months ago`;
              return { dateStr, ago, isStale: daysAgo > 90 };
            };

            if (rows.length === 0) return null;

            return (
              <div style={{...tCard(),marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:22}}>📅</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Last Upload Status — by Client</div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                        Track when transactions and holdings were last updated for each client · {rows.length} client{rows.length>1?"s":""} with data
                      </div>
                    </div>
                  </div>
                  <span style={{fontSize:10,background:"rgba(99,102,241,0.1)",color:"#6366F1",border:"1px solid rgba(99,102,241,0.25)",borderRadius:4,padding:"3px 8px",fontWeight:600}}>
                    Sorted by recency
                  </span>
                </div>

                <div style={{overflowX:"auto",border:`1px solid ${T.cardBorder}`,borderRadius:8}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:T.navActiveBg}}>
                        <th style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Client</th>
                        <th style={{textAlign:"right",padding:"10px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Holdings</th>
                        <th style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Last Holdings Update</th>
                        <th style={{textAlign:"right",padding:"10px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Transactions</th>
                        <th style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>Last Txn Upload</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r,i)=>{
                        const hF = fmt(r.lastHoldingsAt);
                        const tF = fmt(r.lastTxnsAt);
                        return (
                          <tr key={r.client.id} style={{borderTop:i===0?"none":`1px solid ${T.cardBorder}`}}>
                            <td style={{padding:"12px 14px"}}>
                              <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{r.client.name}</div>
                              {r.client.family && <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{r.client.family}</div>}
                            </td>
                            <td style={{padding:"12px 14px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,color:T.textSecondary}}>
                              {r.holdingsCount}
                            </td>
                            <td style={{padding:"12px 14px"}}>
                              {hF==="—"?<span style={{color:T.textMuted}}>—</span>:(
                                <div>
                                  <div style={{fontSize:11,fontWeight:600,color:T.textPrimary}}>{hF.dateStr}</div>
                                  <div style={{fontSize:10,color:hF.isStale?"#D97706":T.textMuted,marginTop:1}}>{hF.ago}{hF.isStale?" · stale":""}</div>
                                </div>
                              )}
                            </td>
                            <td style={{padding:"12px 14px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:12,color:T.textSecondary}}>
                              {r.txnsCount > 0 ? r.txnsCount : <span style={{color:T.textMuted}}>—</span>}
                            </td>
                            <td style={{padding:"12px 14px"}}>
                              {tF==="—"?<span style={{color:T.textMuted,fontSize:11}}>No transactions uploaded</span>:(
                                <div>
                                  <div style={{fontSize:11,fontWeight:600,color:T.textPrimary}}>{tF.dateStr}</div>
                                  <div style={{fontSize:10,color:tF.isStale?"#D97706":T.textMuted,marginTop:1}}>{tF.ago}{tF.isStale?" · stale":""}</div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{display:"flex",alignItems:"center",justifyContent:"flex-start",gap:14,marginTop:10,fontSize:10,color:T.textMuted}}>
                  <span>● Clients with no holdings hidden</span>
                  <span style={{color:"#D97706"}}>● Marked "stale" if more than 90 days old</span>
                </div>
              </div>
            );
          })()}

          {/* ── e-CAS / NSDL / CDSL Statement Upload ── */}
          <div style={{...tCard(),marginBottom:16,borderColor:"rgba(99,102,241,0.4)",background:"rgba(99,102,241,0.02)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:22}}>📋</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#6366F1"}}>e-CAS / NSDL / CDSL Statement Import</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                    Upload broker/custodian statements — auto-detects NSDL · CDSL · CAMS · KFintech (India) and Interactive Brokers · Charles Schwab · Fidelity · HSBC Private (Global)
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["NSDL CAS","CDSL CAS","CAMS MF","KFintech MF"].map(t=>(
                  <span key={t} style={{fontSize:10,background:"rgba(99,102,241,0.1)",color:"#6366F1",
                    border:"1px solid rgba(99,102,241,0.25)",borderRadius:4,padding:"2px 7px",fontWeight:600}}>{t}</span>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,1fr)",gap:8,marginBottom:16}}>
              {[
                {n:"1",icon:"📤",label:"Upload Statement",desc:"India: CAS/demat PDF · Global: IBKR, Schwab, Fidelity, HSBC CSV/PDF"},
                {n:"2",icon:"🔍",label:"Auto-parse",desc:"We extract holdings, ISINs & quantities"},
                {n:"3",icon:"👀",label:"Preview",desc:"Review & select which holdings to import"},
                {n:"4",icon:"✅",label:"Import",desc:"Holdings added to your portfolio"},
              ].map(s=>(
                <div key={s.n} style={{background:T.navActiveBg,borderRadius:8,padding:"10px 12px",
                  border:`1px solid rgba(99,102,241,0.15)`}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{width:18,height:18,borderRadius:"50%",background:"#6366F1",
                      display:"inline-flex",alignItems:"center",justifyContent:"center",
                      fontSize:9,fontWeight:700,color:"white",flexShrink:0}}>{s.n}</span>
                    <span style={{fontSize:12,fontWeight:600,color:"#6366F1"}}>{s.icon} {s.label}</span>
                  </div>
                  <div style={{fontSize:10,color:T.textSecondary}}>{s.desc}</div>
                </div>
              ))}
            </div>

            {/* Upload zone */}
            {!casResult && (
              <div>
                {/* ── PASSWORD FIRST — before upload ── */}
                <div style={{marginBottom:12,padding:"12px 14px",background:T.navActiveBg,
                  borderRadius:10,border:`1px solid rgba(99,102,241,0.25)`}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#6366F1",marginBottom:6,
                    textTransform:"uppercase",letterSpacing:"0.06em"}}>
                    🔐 Password (if PDF is protected)
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:8}}>
                    NSDL/CDSL statements are usually protected with your <strong>PAN number</strong> (e.g. ABCDE1234F) or <strong>Date of Birth</strong> (DDMMYYYY e.g. 01011985). Enter it here before uploading.
                  </div>
                  <input type="password" value={casPassword} onChange={e=>setCasPassword(e.target.value)}
                    placeholder="Enter PDF password — PAN number or DDMMYYYY date of birth"
                    style={{...inp(),width:"100%",boxSizing:"border-box",fontSize:13,
                      border:`1.5px solid ${casPassword?"#6366F1":T.cardBorder}`}}/>
                  {casPassword && (
                    <div style={{marginTop:6,fontSize:10,color:"#1D9E75",fontWeight:600}}>
                      ✓ Password set — now drop your PDF below
                    </div>
                  )}
                </div>

                {/* ── Drop zone ── */}
                <label style={{display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",border:`2px dashed ${casPassword?"#6366F1":"rgba(99,102,241,0.4)"}`,
                  borderRadius:12,padding:"28px 20px",cursor:casUploading?"default":"pointer",
                  gap:8,background:casPassword?"rgba(99,102,241,0.05)":"rgba(99,102,241,0.02)",
                  opacity:casUploading?0.6:1,transition:"all 0.2s"}}>
                  <div style={{fontSize:36}}>{casUploading?"⏳":"📋"}</div>
                  <div style={{fontSize:14,fontWeight:600,color:"#6366F1"}}>
                    {casUploading?"Parsing statement…":"Drop statement PDF here — India (NSDL/CDSL/CAMS/KFintech) or Global (IBKR/Schwab/Fidelity/HSBC)"}
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary}}>
                    {casPassword
                      ? "Password set ✓ — click or drop your PDF"
                      : "Supports .pdf — enter password above if file is protected"}
                  </div>
                  <input type="file" accept=".pdf" style={{display:"none"}} disabled={casUploading}
                    onChange={async e=>{
                      const f = e.target.files[0]; if (!f) return;
                      setCasFile(f); setCasError(""); setCasResult(null); setCasPreview([]);
                      setCasUploading(true);
                      try {
                        // Always capture raw data for debugging
                        const [_dbgItems, _dbgText] = await Promise.all([
                          ECAS_PARSER.extractItems(f, casPassword).catch(()=>[]),
                          ECAS_PARSER.extractText(f, casPassword).catch(()=>""),
                        ]);
                        setCasDebugItems(_dbgItems.slice(0, 200));
                        setCasDebugText(_dbgText);
                        const result = await ECAS_PARSER.parse(f, casPassword);
                        if (result.holdings.length === 0) {
                          // Show raw text excerpt to help diagnose format issues
                          const excerpt = (result.rawText||"").slice(0,800).replace(/\n+/g," | ");
                          setCasError(
                            "No holdings found. " +
                            (casPassword ? "" : "If the PDF is password-protected, enter the password above and retry. ") +
                            "Detected format: " + (result.type||"UNKNOWN") + ". " +
                            "Ensure you are uploading a text-based PDF (not a scanned image). " +
                            "Extracted text preview: " + excerpt
                          );
                        } else {
                          setCasResult(result);
                          setCasPreview(result.holdings.map((_,i)=>i));
                           setCasTickerOverrides({}); setCasOverrides({});
                        }
                      } catch(err) {
                        if (err.name === "PasswordException" || String(err).includes("password") || String(err).includes("Password")) {
                          setCasError("❌ Wrong password or PDF is encrypted. Please check your password above (PAN or DDMMYYYY) and click Retry.");
                        } else {
                          setCasError("Parse error: " + err.message + ". The PDF may be image-scanned — try a text-based version.");
                        }
                      }
                      setCasUploading(false);
                      e.target.value = "";
                    }}/>
                </label>

                {/* Retry button — shown after any attempt */}
                {casFile && (
                  <div style={{marginTop:10,display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={async()=>{
                      setCasError(""); setCasResult(null); setCasPreview([]); setCasUploading(true);
                      try {
                        const result = await ECAS_PARSER.parse(casFile, casPassword);
                        if (!result.holdings.length) {
                          const excerpt = (result.rawText||"").slice(0,600).replace(/\n+/g," | ");
                          setCasError("No holdings found with this password. Format: " + (result.type||"UNKNOWN") + " | Text preview: " + excerpt);
                        } else { setCasResult(result); setCasPreview(result.holdings.map((_,i)=>i)); setCasTickerOverrides({}); }
                      } catch(e) { setCasError("Error: " + e.message); }
                      setCasUploading(false);
                    }} style={{background:"#6366F1",border:"none",borderRadius:7,color:"white",
                      padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                      ↺ Retry Parse
                    </button>
                  </div>
                )}

                {/* Error */}
                {casError && (
                  <div style={{marginTop:10,padding:"10px 14px",background:"rgba(220,38,38,0.07)",
                    border:"1px solid rgba(220,38,38,0.25)",borderRadius:8,fontSize:11,color:"#A32D2D",
                    display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                      <span style={{flexShrink:0}}>⚠</span>
                      <span style={{whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{casError}</span>
                    </div>
                  </div>
                )}

                {/* ── DEBUG PANEL — shows raw extracted text for troubleshooting ── */}
                {casDebugText && (
                  <div style={{marginTop:12}}>
                    <button onClick={()=>setCasShowDebug(p=>!p)}
                      style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,
                        border:`1px solid ${T.cardBorder}`,borderRadius:6,padding:"4px 12px",
                        cursor:"pointer",width:"100%",textAlign:"left"}}>
                      {casShowDebug ? "▲ Hide" : "▼ Show"} Raw Extracted Text (for troubleshooting — copy & share with support)
                      {" · "}{casDebugText.length} chars · {casDebugItems.length} items · format: {(function(){
                        const t = casDebugText.toUpperCase();
                        if (t.includes("NSDL")) return "NSDL";
                        if (t.includes("CDSL")) return "CDSL";
                        if (t.includes("CAMS")) return "CAMS";
                        return "UNKNOWN";
                      })()}
                    </button>
                    {casShowDebug && (
                      <div style={{marginTop:6}}>
                        <div style={{fontSize:10,color:T.textMuted,marginBottom:4}}>
                          📋 Copy the text below and paste it in the chat to help diagnose parsing issues:
                        </div>
                        <textarea readOnly
                          value={"=== FIRST 100 COORDINATE ITEMS ===\n" +
                            casDebugItems.slice(0,100).map(it=>`x=${it.x.toFixed(1)} y=${it.y.toFixed(1)} p=${it.page} | ${it.text}`).join("\n") +
                            "\n\n=== RAW TEXT (first 3000 chars) ===\n" +
                            casDebugText.slice(0,3000)}
                          style={{width:"100%",height:200,fontSize:10,fontFamily:"monospace",
                            background:T.cardBg,color:T.textPrimary,border:`1px solid ${T.cardBorder}`,
                            borderRadius:6,padding:8,resize:"vertical",boxSizing:"border-box"}}/>
                        <button onClick={()=>{
                          const txt = "=== COORDINATE ITEMS ===\n" +
                            casDebugItems.slice(0,100).map(it=>`x=${it.x.toFixed(1)} y=${it.y.toFixed(1)} p=${it.page} | ${it.text}`).join("\n") +
                            "\n\n=== RAW TEXT ===\n" + casDebugText.slice(0,3000);
                          navigator.clipboard.writeText(txt).then(()=>alert("Copied to clipboard!"));
                        }} style={{marginTop:4,fontSize:11,background:"#6366F1",border:"none",
                          borderRadius:6,color:"white",padding:"5px 14px",cursor:"pointer",fontWeight:600}}>
                          📋 Copy to Clipboard
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Format notes */}
                <div style={{marginTop:14,padding:"12px 14px",background:T.borderSubtle,borderRadius:8}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>Supported Formats</div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:6,fontSize:11,color:T.textSecondary}}>
                    <div>✅ <strong>NSDL CAS</strong> — Equities + Bonds in NSDL demat</div>
                    <div>✅ <strong>CDSL CAS</strong> — Equities + Bonds in CDSL demat</div>
                    <div>✅ <strong>CAMS MF CAS</strong> — Mutual fund folios (CAMS RTA)</div>
                    <div>✅ <strong>KFintech MF CAS</strong> — Mutual fund folios (KFintech RTA)</div>
                    <div>⚠ <strong>Scanned PDFs</strong> — Not supported (no text layer)</div>
                    <div>⚠ <strong>SGX / NYSE demat</strong> — Use CSV import instead</div>
                  </div>
                  <div style={{marginTop:8,fontSize:10,color:T.textMuted}}>
                    💡 To get your e-CAS: NSDL → <strong>eservices.nsdl.com</strong> → CAS → Request Statement &nbsp;|&nbsp;
                    CDSL → <strong>www.cdslindia.com</strong> → eServices → Account Statement
                  </div>
                </div>
              </div>
            )}

            {/* Preview panel after successful parse */}
            {casResult && (
              <div>
                {/* Parse summary */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 14px",background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.25)",
                  borderRadius:8,marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>✅</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:"#1D9E75"}}>
                        Parsed {casResult.holdings.length} holding{casResult.holdings.length!==1?"s":""} from {casResult.type.replace("_"," ")}
                      </div>
                      {casResult.holderName && (
                        <div style={{fontSize:11,color:T.textSecondary}}>Account holder: <strong>{casResult.holderName}</strong></div>
                      )}
                    </div>
                  </div>
                  <button onClick={()=>{setCasResult(null);setCasPreview([]);setCasFile(null);setCasError("");}}
                    style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                      borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                    ← Upload Different File
                  </button>
                </div>

                {/* Assign to client */}
                {isAdmin && (
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Assign holdings to:</div>
                    <select value={casAssignTo} onChange={e=>setCasAssignTo(e.target.value)} style={inp()}>
                      <option value="">— Unassigned (admin can assign later) —</option>
                      {visibleFamilies.map(fam=>(
                        <optgroup key={fam.id} label={fam.emoji+" "+fam.name}>
                          {visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                            <option key={c.id} value={c.id}>{c.name} ({c.role})</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* Select all / deselect */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontSize:12,color:T.textSecondary}}>
                    {casPreview.length} of {casResult.holdings.length} selected for import
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <span onClick={()=>setCasPreview(casResult.holdings.map((_,i)=>i))}
                      style={{fontSize:11,color:T.brand,cursor:"pointer",textDecoration:"underline"}}>Select all</span>
                    <span onClick={()=>setCasPreview([])}
                      style={{fontSize:11,color:T.textMuted,cursor:"pointer",textDecoration:"underline"}}>Deselect all</span>
                  </div>
                </div>

                {/* Holdings preview table — fully editable before import */}
                {/* Hint */}
                <div style={{padding:"5px 10px",background:`${T.brand}08`,border:`1px solid ${T.cardBorder}`,
                  borderRadius:"6px 6px 0 0",fontSize:10,color:T.textMuted,display:"flex",alignItems:"center",gap:5}}>
                  ✏️ <strong style={{color:T.brand}}>Click any cell to edit</strong>
                  &nbsp;· Fix names, quantities, cost prices before importing
                </div>
                <div style={{maxHeight:340,overflowY:"auto",border:`1px solid ${T.cardBorder}`,
                  borderTop:"none",borderRadius:"0 0 8px 8px",marginBottom:14}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:1}}>
                      <tr>
                        <th style={{...tTh(),width:32}}><input type="checkbox"
                          checked={casPreview.length===casResult.holdings.length}
                          onChange={e=>setCasPreview(e.target.checked?casResult.holdings.map((_,i)=>i):[])}/></th>
                        {["Name","ISIN","Ticker","Class","Qty","Cost Price","Curr. Price","Source"].map(h=>(
                          <th key={h} style={{...tTh(),
                            width:h==="Name"?"220px":h==="ISIN"?"100px":h==="Class"?"130px":
                              h==="Ticker"?"90px":"80px"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {casResult.holdings.map((h,i)=>{
                          // Merge parsed data with any overrides
                          const ov = casOverrides[i] || {};
                          const name    = ov.name    !== undefined ? ov.name    : h.name;
                          const qty     = ov.qty     !== undefined ? ov.qty     : h.qty;
                          const costPx  = ov.costPrice !== undefined ? ov.costPrice : (h.costPrice || 0);
                          const currPx  = ov.currentPrice !== undefined ? ov.currentPrice : h.currentPrice;
                          const cls     = ov.class   !== undefined ? ov.class   : h.class;
                          const setOv   = (field, val) => setCasOverrides(p=>({...p,[i]:{...p[i],[field]:val}}));

                          const clientEx = holdings.filter(ex =>
                            ex.isin === h.isin && ex.isin && ex.isin !== "-" &&
                            (!casAssignTo || holdingOwners[String(ex.id)] === casAssignTo)
                          );
                          const isExactDup = clientEx.some(ex =>
                            Math.abs(ex.qty - h.qty) / Math.max(ex.qty, h.qty, 1) < 0.005
                          );
                          const isSameIsinNewQty = clientEx.length > 0 && !isExactDup;
                          const rowBg = isExactDup ? "rgba(220,38,38,0.05)" : isSameIsinNewQty ? "rgba(217,119,6,0.05)" : "transparent";

                          // Shared editable cell style
                          const ec = {border:"none",background:"transparent",
                            fontSize:11,color:T.textPrimary,outline:"none",
                            fontFamily:"DM Sans,sans-serif",width:"100%",padding:0};

                          return (
                            <tr key={i} style={{background:rowBg,
                              opacity:casPreview.includes(i)?1:0.4,transition:"opacity 0.15s",
                              borderBottom:`1px solid ${T.tdBorder}`}}>

                              {/* Checkbox */}
                              <td style={{...tTd(),textAlign:"center"}}>
                                <input type="checkbox" checked={casPreview.includes(i)}
                                  onChange={e=>setCasPreview(p=>e.target.checked?[...p,i]:p.filter(x=>x!==i))}/>
                              </td>

                              {/* Name — editable */}
                              <td style={{...tTd(),padding:"3px 8px"}}
                                onClick={()=>setCasPreview(p=>p.includes(i)?p:p.concat(i))}>
                                <div style={{display:"flex",flexDirection:"column",gap:2}}>
                                  <input value={name} onChange={e=>setOv("name",e.target.value)}
                                    style={{...ec,fontWeight:600,minWidth:160}}
                                    onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                    onBlur={e=>e.currentTarget.closest("td").style.background=""}
                                  />
                                  <div style={{display:"flex",gap:4}}>
                                    {isExactDup && <span style={{fontSize:9,fontWeight:700,color:"#A32D2D",
                                      background:"rgba(220,38,38,0.1)",padding:"1px 5px",borderRadius:3}}>⚠ DUPLICATE</span>}
                                    {isSameIsinNewQty && <span style={{fontSize:9,fontWeight:700,color:"#D97706",
                                      background:"rgba(217,119,6,0.1)",padding:"1px 5px",borderRadius:3}}>+ NEW LOT</span>}
                                  </div>
                                </div>
                              </td>

                              {/* ISIN — read only */}
                              <td style={tTd({fontFamily:"'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",fontSize:10,color:T.textMuted})}>
                                {h.isin}
                              </td>

                              {/* Ticker — editable (existing) */}
                              <td style={{...tTd(),padding:"3px 6px"}}>
                                <input
                                  value={casTickerOverrides[i] !== undefined ? casTickerOverrides[i] : (h.ticker||"-")}
                                  onChange={e=>setCasTickerOverrides(p=>({...p,[i]:e.target.value.toUpperCase().trim()}))}
                                  style={{...ec,width:80,fontFamily:"'Inter', system-ui, sans-serif",
                                    color:(!h.ticker||h.ticker==="-")&&!casTickerOverrides[i]?"#A32D2D":T.textPrimary,
                                    borderBottom:`1px solid ${(!h.ticker||h.ticker==="-")&&!casTickerOverrides[i]?"#A32D2D":T.cardBorder}`
                                  }}
                                  placeholder="TICKER"
                                />
                                {(!h.ticker||h.ticker==="-") && !casTickerOverrides[i] && (
                                  <div style={{fontSize:9,color:"#A32D2D"}}>⚠ Enter ticker</div>
                                )}
                              </td>

                              {/* Class — editable dropdown */}
                              <td style={{...tTd(),padding:"3px 6px"}}>
                                <select value={cls} onChange={e=>setOv("class",e.target.value)}
                                  style={{...ec,cursor:"pointer",appearance:"none",WebkitAppearance:"none",
                                    borderBottom:`1px solid ${T.cardBorder}`}}>
                                  {["Indian Mutual Funds","Global Mutual Funds","Equities","Fixed Income",
                                    "ETF","Real Estate","Digital Assets","Commodities","Alternatives","Miscellaneous"
                                  ].map(c=><option key={c}>{c}</option>)}
                                </select>
                              </td>

                              {/* Qty — editable */}
                              <td style={{...tTd(),padding:"3px 6px"}}>
                                <input type="number" step="any" value={qty||""}
                                  onChange={e=>setOv("qty",parseFloat(e.target.value)||0)}
                                  style={{...ec,textAlign:"right",width:72,
                                    fontFamily:"'Inter', system-ui, sans-serif",
                                    borderBottom:`1px solid ${T.cardBorder}`}}
                                  onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                  onBlur={e=>e.currentTarget.closest("td").style.background=""}
                                />
                              </td>

                              {/* Cost Price — editable, amber if zero */}
                              <td style={{...tTd(),padding:"3px 6px",
                                background:!costPx||costPx===0?"rgba(217,119,6,0.06)":"transparent"}}>
                                <input type="number" step="any" value={costPx||""}
                                  onChange={e=>setOv("costPrice",parseFloat(e.target.value)||0)}
                                  style={{...ec,textAlign:"right",width:72,
                                    fontFamily:"'Inter', system-ui, sans-serif",
                                    color:!costPx||costPx===0?"#D97706":T.textPrimary,
                                    borderBottom:`1px solid ${!costPx||costPx===0?"#D97706":T.cardBorder}`}}
                                  onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                  onBlur={e=>e.currentTarget.closest("td").style.background=
                                    !costPx||costPx===0?"rgba(217,119,6,0.06)":"transparent"}
                                  placeholder="0"
                                  title="Cost price per unit — edit to fix import errors"
                                />
                              </td>

                              {/* Current Price — editable */}
                              <td style={{...tTd(),padding:"3px 6px"}}>
                                <input type="number" step="any" value={currPx||""}
                                  onChange={e=>setOv("currentPrice",parseFloat(e.target.value)||0)}
                                  style={{...ec,textAlign:"right",width:72,
                                    fontFamily:"'Inter', system-ui, sans-serif",
                                    borderBottom:`1px solid ${T.cardBorder}`}}
                                  onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                  onBlur={e=>e.currentTarget.closest("td").style.background=""}
                                />
                              </td>

                              {/* Source — read only */}
                              <td style={tTd()}>
                                <span style={{fontSize:9,background:"rgba(99,102,241,0.1)",color:"#6366F1",
                                  padding:"1px 6px",borderRadius:3,fontWeight:600}}>{h.source}</span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Import button */}
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  {/* Warn if no client assigned */}
                  {!casAssignTo && isAdmin && (
                    <div style={{width:"100%",marginBottom:8,padding:"8px 12px",
                      background:"rgba(217,119,6,0.08)",border:"1px solid rgba(217,119,6,0.3)",
                      borderRadius:7,fontSize:12,color:"#D97706",display:"flex",alignItems:"center",gap:6}}>
                      ⚠ Please assign holdings to a client above before importing.
                    </div>
                  )}
                  <button
                    disabled={casPreview.length===0 || (isAdmin && !casAssignTo)}
                    onClick={async()=>{
                      const toImport = casPreview.map(i => {
                        const base = casResult.holdings[i];
                        const ov   = casOverrides[i] || {};
                        return {
                          ...base,
                          ticker:       casTickerOverrides[i] || base.ticker || "-",
                          name:         ov.name         !== undefined ? ov.name         : base.name,
                          qty:          ov.qty          !== undefined ? ov.qty          : base.qty,
                          costPrice:    ov.costPrice    !== undefined ? ov.costPrice    : (base.costPrice || 0),
                          currentPrice: ov.currentPrice !== undefined ? ov.currentPrice : base.currentPrice,
                          class:        ov.class        !== undefined ? ov.class        : base.class,
                        };
                      });

                      // ── Duplicate detection: ISIN + Quantity ──
                      // Same ISIN + same qty (within 0.5%) = exact duplicate (same statement re-uploaded)
                      // Same ISIN + different qty = new purchase of existing stock → warn but allow
                      const clientHoldings = holdings.filter(h =>
                        casAssignTo ? holdingOwners[String(h.id)] === casAssignTo : true
                      );
                      const isinQtyMap = {}; // ISIN → [qty, name]
                      clientHoldings.forEach(h => {
                        if (h.isin && h.isin !== "-") {
                          if (!isinQtyMap[h.isin]) isinQtyMap[h.isin] = [];
                          isinQtyMap[h.isin].push(h.qty);
                        }
                      });

                      const exactDups = [];   // same ISIN + same qty → block
                      const sameIsinNewQty = []; // same ISIN + different qty → warn

                      toImport.forEach(h => {
                        if (!h.isin || h.isin === "-") return;
                        const existing = isinQtyMap[h.isin];
                        if (!existing) return;
                        // Check if any existing lot has same qty (within 0.5%)
                        const isExactQty = existing.some(eqty =>
                          Math.abs(eqty - h.qty) / Math.max(eqty, h.qty, 1) < 0.005
                        );
                        if (isExactQty) exactDups.push(h);
                        else sameIsinNewQty.push(h);
                      });

                      const nonExactDups = toImport.filter(h =>
                        !exactDups.includes(h)
                      );

                      // Block exact duplicates
                      if (exactDups.length > 0) {
                        const dupNames = exactDups.slice(0,5).map(h=>`${h.name} (${h.qty.toLocaleString()} units)`).join("\n");
                        const more = exactDups.length > 5 ? `\n…and ${exactDups.length-5} more` : "";
                        let proceed = false;
                        await new Promise(resolve => {
                          requireConfirm(
                            `${exactDups.length} duplicate holding${exactDups.length!==1?"s":""} detected`,
                            ()=>{ proceed=true; resolve(); },
                            `Same ISIN + quantity already exists. Skip ${exactDups.length} duplicate${exactDups.length!==1?"s":""} and import ${nonExactDups.length} new holding${nonExactDups.length!==1?"s":""}?`,
                            false
                          );
                          // If user cancels, resolve after a tick so await doesn't hang
                          setTimeout(resolve, 100);
                        });
                        if (!proceed) return;
                        if (nonExactDups.length === 0) {
                          showToast(`ℹ Nothing new to import — all ${exactDups.length} holdings already exist with same quantity`, "#D97706");
                          return;
                        }
                      }

                      // Warn about same-ISIN different-qty (new purchases) — informational only
                      if (sameIsinNewQty.length > 0 && exactDups.length === 0) {
                        const newPurchaseNames = sameIsinNewQty.slice(0,3).map(h=>`${h.name} (existing + ${h.qty.toLocaleString()} new units)`).join("\n");
                        const more = sameIsinNewQty.length > 3 ? `\n…and ${sameIsinNewQty.length-3} more` : "";
                        showToast(`ℹ ${sameIsinNewQty.length} holding(s) exist with different quantities — adding as new lots`, "#2563EB", 5000);
                      }

                      const finalImport = exactDups.length > 0 ? nonExactDups : toImport;
                      const uid = ()=>Date.now().toString(36)+Math.random().toString(36).slice(2,6);
                      const enriched = finalImport.map(h=>({
                        ...h,
                        id: uid(),
                        sector: h.isMutualFund ? "Diversified" : "",
                        capSize: "N/A",
                        listed: h.listed !== false,
                        manualPriceDate: null,
                        manager: h.manager || "Self-Managed",
                      }));
                      setHoldings(p=>[...p,...enriched]);
                      // Save to Supabase
                      for (const h of enriched) {
                        await addHolding(h).catch(e=>console.error("[Supabase] addHolding:",e.message));
                        if (casAssignTo) {
                          await setHoldingOwner(String(h.id), casAssignTo).catch(()=>{});
                          setHoldingOwners(p=>({...p,[String(h.id)]:casAssignTo}));
                        }
                      }
                      const parts = [];
                      if (enriched.length > 0)    parts.push(`${enriched.length} imported`);
                      if (exactDups.length > 0)   parts.push(`${exactDups.length} exact duplicates skipped`);
                      if (sameIsinNewQty.length > 0) parts.push(`${sameIsinNewQty.length} new lots added`);
                      showToast(`✅ ${parts.join(" · ")}`, "#1D9E75");
                      logAudit("IMPORT_CAS", `${parts.join(" · ")} from ${casResult.type.replace("_"," ")}`);
                      setCasResult(null); setCasPreview([]); setCasFile(null); setCasPassword(""); setCasAssignTo("");
                    }}
                    style={{background:(casPreview.length===0||(isAdmin&&!casAssignTo))?"#78716C":"linear-gradient(135deg,#6366F1,#7C3AED)",
                      border:"none",borderRadius:8,color:"white",padding:"10px 24px",
                      cursor:(casPreview.length===0||(isAdmin&&!casAssignTo))?"default":"pointer",fontSize:13,fontWeight:700,
                      boxShadow:(casPreview.length>0&&(!isAdmin||casAssignTo))?"0 4px 14px rgba(99,102,241,0.4)":"none"}}>
                    ✅ Import {casPreview.length} Holdings
                  </button>
                  <button onClick={()=>{setCasResult(null);setCasPreview([]);setCasFile(null);setCasError("");}}
                    style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                      color:T.textSecondary,padding:"10px 18px",cursor:"pointer",fontSize:13}}>
                    Cancel
                  </button>
                  <div style={{fontSize:11,color:T.textMuted}}>
                    ℹ Cost price will be ₹0 — edit each holding or upload a transaction history to auto-calculate.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Folio Ledger Import (Forthius Capital / broker XLS) ── */}
          <div style={{...tCard(),marginBottom:16,borderColor:"rgba(99,102,241,0.35)",background:"rgba(99,102,241,0.02)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:22}}>📊</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Folio Ledger Import</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                    Import transactions from Forthius Capital / broker folio ledger XLS — supports BUY, SIP, STP In/Out, Switch In/Out, Redemption
                  </div>
                </div>
              </div>
              {folioResult && (
                <button onClick={()=>{setFolioResult(null);setFolioFile(null);setFolioError("");setFolioImported(false);}}
                  style={{fontSize:11,color:T.textMuted,background:"transparent",border:"none",cursor:"pointer"}}>
                  ✕ Clear
                </button>
              )}
            </div>

            {!folioResult && (
              <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                border:"2px dashed rgba(99,102,241,0.4)",borderRadius:10,padding:"24px 20px",
                cursor:"pointer",gap:6,background:"rgba(99,102,241,0.02)"}}>
                <span style={{fontSize:28}}>📂</span>
                <div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>
                  {folioLoading ? "⟳ Parsing ledger…" : "Drop Folio Ledger XLS / XLSX here"}
                </div>
                <div style={{fontSize:11,color:T.textMuted}}>· .xls or .xlsx · Forthius Capital format</div>
                <input type="file" accept=".xls,.xlsx" style={{display:"none"}}
                  onChange={async e=>{
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setFolioFile(f); setFolioError(""); setFolioLoading(true);
                    try {
                      const result = await FOLIO_LEDGER_PARSER.parse(f);
                      if (!result.transactions.length) {
                        setFolioError("No transactions found. Check the file format.");
                      } else {
                        setFolioResult(result);
                      }
                    } catch(err) {
                      setFolioError("Parse error: " + err.message);
                    } finally { setFolioLoading(false); }
                    e.target.value = "";
                  }}/>
              </label>
            )}

            {folioError && (
              <div style={{padding:"10px 14px",background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.25)",
                borderRadius:8,fontSize:12,color:"#A32D2D",marginTop:8}}>
                ⚠ {folioError}
              </div>
            )}

            {folioResult && !folioImported && (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",
                  background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.25)",
                  borderRadius:8,marginBottom:12}}>
                  <span style={{fontSize:16}}>✅</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#1D9E75"}}>
                      {folioResult.transactions.length} transactions parsed
                    </div>
                    <div style={{fontSize:11,color:T.textSecondary}}>
                      Clients: {folioResult.clients.join(", ")} ·{" "}
                      {[...new Set(folioResult.transactions.map(t=>t.type))].join(", ")}
                    </div>
                  </div>
                </div>

                {/* Preview table */}
                <div style={{maxHeight:280,overflowY:"auto",marginBottom:12,borderRadius:8,
                  border:`1px solid ${T.cardBorder}`}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead><tr style={{background:T.thBg,position:"sticky",top:0}}>
                      {["Date","Fund","Type","Units","NAV","Amount (INR)","Client"].map(h=>(
                        <th key={h} style={{...tTh(),textAlign:"left",fontSize:10}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {folioResult.transactions.slice(0,100).map((t,i)=>(
                        <tr key={i} style={{borderBottom:`1px solid ${T.tdBorder}`,
                          background:i%2===0?"transparent":T.tableStripe}}>
                          <td style={tTd({fontFamily:"monospace",fontSize:10})}>{t.date}</td>
                          <td style={tTd({maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"})}
                            title={t.holding}>{t.holding}</td>
                          <td style={tTd()}>
                            <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,
                              background:["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type)?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.10)",
                              color:["BUY","SIP","STP_IN","SWITCH_IN"].includes(t.type)?"#1D9E75":"#A32D2D"}}>
                              {t.type}
                            </span>
                          </td>
                          <td style={tTd({fontFamily:"monospace",textAlign:"right"})}>{t.qty.toLocaleString("en-IN",{maximumFractionDigits:4})}</td>
                          <td style={tTd({fontFamily:"monospace",textAlign:"right"})}>{t.price > 0 ? t.price.toLocaleString("en-IN",{maximumFractionDigits:2}) : "—"}</td>
                          <td style={tTd({fontFamily:"monospace",textAlign:"right",fontWeight:600})}>
                            {t.amount > 0 ? "₹"+t.amount.toLocaleString("en-IN",{maximumFractionDigits:0}) : "—"}
                          </td>
                          <td style={tTd({fontSize:10,color:T.textMuted})}>{t.client||"—"}</td>
                        </tr>
                      ))}
                      {folioResult.transactions.length > 100 && (
                        <tr><td colSpan={7} style={{padding:"8px 14px",fontSize:11,color:T.textMuted,textAlign:"center"}}>
                          Showing first 100 of {folioResult.transactions.length} — all will be imported
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Progress bar shown while importing */}
                {folioImporting && (
                  <div style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,
                      color:T.textSecondary,marginBottom:4}}>
                      <span>Importing transactions…</span>
                      <span>{folioProgress} / {folioResult.transactions.length}</span>
                    </div>
                    <div style={{height:6,borderRadius:3,background:T.borderSubtle,overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:3,background:"linear-gradient(90deg,#4F46E5,#7C3AED)",
                        width:`${Math.round(folioProgress/folioResult.transactions.length*100)}%`,
                        transition:"width 0.2s"}}/>
                    </div>
                  </div>
                )}

                <button
                  disabled={folioImporting}
                  onClick={async ()=>{
                    setFolioImporting(true);
                    setFolioProgress(0);
                    setFolioError("");
                    let imported = 0;
                    let firstErr = null;
                    const allTxns = [];
                    const unmatchedClients = new Set(); // track names that didn't match any client
                    const total = folioResult.transactions.length;

                    for (let idx = 0; idx < total; idx++) {
                      const t = folioResult.transactions[idx];
                      try {
                        // Deterministic ID for deduplication on re-upload
                        const holdHash = String(Math.abs(
                          t.holding.split("").reduce((a,c) => a + c.charCodeAt(0), 0)
                        )).slice(-5);
                        const safeId = `fl_${t.date.replace(/-/g,"")}_${holdHash}_${t.type}_${String(t.qty).replace(".","p").slice(0,8)}`;
                        const clientMatch = (() => {
                          if (!t.client) return null;
                          // Normalize: strip honorifics, HUF suffix, extra spaces, punctuation
                          const norm = s => s.toLowerCase()
                            .replace(/\b(mr|mrs|ms|dr|shri|smt|prof|lt|col|capt)\.?\s*/gi, "")
                            .replace(/\s+(huf|karta|minor|guardian)\b/gi, "")
                            .replace(/[^a-z\s]/g, "")
                            .replace(/\s+/g, " ")
                            .trim();
                          const tNorm = norm(t.client);
                          // Try exact normalized match first
                          let m = clients.find(c => norm(c.name) === tNorm);
                          // Try substring match either direction
                          if (!m) m = clients.find(c => {
                            const cNorm = norm(c.name);
                            return cNorm.includes(tNorm) || tNorm.includes(cNorm);
                          });
                          // Try word-overlap match (≥2 words in common)
                          if (!m) {
                            const tWords = new Set(tNorm.split(" ").filter(w => w.length > 2));
                            m = clients.find(c => {
                              const cWords = norm(c.name).split(" ").filter(w => w.length > 2);
                              const overlap = cWords.filter(w => tWords.has(w));
                              return overlap.length >= 2;
                            });
                          }
                          return m?.id || null;
                        })();
                        // Track unmatched client names for post-import warning
                        if (t.client && !clientMatch) {
                          unmatchedClients.add(t.client);
                        }
                        const txn = {
                          id: safeId,
                          date: t.date,
                          holding: t.holding,
                          type: t.type,
                          qty: t.qty || 0,
                          price: t.price || 0,
                          currency: t.currency || "INR",
                          fees: 0,
                          clientId: clientMatch,
                        };
                        await addTransaction(txn);
                        allTxns.push(txn);
                        imported++;
                      } catch(e) {
                        const isDupe = e.message?.includes("duplicate") ||
                                       e.message?.includes("23505") ||
                                       e.code === "23505";
                        if (!isDupe) {
                          if (!firstErr) firstErr = e.message;
                          console.error("[FolioImport] row", idx, e.message, e);
                        }
                        // duplicates are silently skipped
                      }
                      // Update progress every 5 rows
                      if (idx % 5 === 0) setFolioProgress(idx + 1);
                    }
                    setFolioProgress(total);
                    setFolioImporting(false);

                    if (allTxns.length > 0) setTransactions(p => [...p, ...allTxns]);

                    const skipped = total - imported - (firstErr ? 1 : 0);
                    if (imported > 0) {
                      const msg = skipped > 0
                        ? `✅ Imported ${imported} new · ${skipped} already existed (skipped)`
                        : `✅ Imported ${imported} transactions`;
                      showToast(msg, "#1D9E75", 5000);
                      // Warn about any client names that couldn't be matched
                      if (unmatchedClients.size > 0) {
                        setTimeout(() => {
                          showToast(`⚠ ${unmatchedClients.size} client name(s) not matched — transactions saved without client tag: ${[...unmatchedClients].join(", ")}`, "#D97706", 10000);
                        }, 1000);
                      }
                      setFolioImported(true);
                    } else if (skipped === total) {
                      showToast(`All ${total} transactions already imported — no duplicates added`, "#D97706", 5000);
                      setFolioImported(true);
                    } else {
                      setFolioError(`Import failed — ${firstErr || "No transactions were saved. Check browser console (F12) for details."}`);
                    }
                  }}
                  style={{width:"100%",background:folioImporting?"#6B7280":"#4F46E5",
                    border:"none",borderRadius:9,color:"white",padding:"12px",
                    cursor:folioImporting?"not-allowed":"pointer",fontSize:13,fontWeight:700,
                    boxShadow:folioImporting?"none":"0 4px 16px rgba(99,102,241,0.35)",
                    transition:"all 0.2s"}}>
                  {folioImporting
                    ? `⟳ Importing… ${folioProgress} / ${folioResult.transactions.length}`
                    : `✓ Import ${folioResult.transactions.length} Transactions`}
                </button>
              </div>
            )}

            {folioImported && !folioHoldingsResult && (
              <div style={{textAlign:"center",padding:"20px 16px"}}>
                <div style={{color:"#1D9E75",fontSize:13,fontWeight:600,marginBottom:16}}>
                  ✅ Transactions imported. Now generate holdings from this data.
                </div>
                <div style={{fontSize:12,color:T.textSecondary,marginBottom:16,lineHeight:1.6}}>
                  The importer found <strong>{folioResult?.derivedHoldings?.length || 0} active holdings</strong> by
                  netting all buys and sells. Click below to add them to your portfolio with current NAV from MFapi.in.
                </div>
                <button
                  disabled={folioGenerating}
                  onClick={async () => {
                    if (!folioResult?.derivedHoldings?.length) return;
                    setFolioGenerating(true);
                    let added = 0, skipped = 0;
                    const newHoldings = [];

                    for (let hi = 0; hi < folioResult.derivedHoldings.length; hi++) {
                      const h = folioResult.derivedHoldings[hi];
                      try {
                        // Always search MFapi by fund name (regardless of ISIN)
                        let schemeCode = null;
                        let currentNav = 0;
                        try {
                          const searchQ = h.name.replace(/Regular-Growth|Regular - Growth|Growth|Direct|Plan/gi,"").trim().slice(0,40);
                          const r = await fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(searchQ)}`);
                          const data = await r.json();
                          if (data?.length) {
                            schemeCode = String(data[0].schemeCode);
                            const r2 = await fetch(`https://api.mfapi.in/mf/${schemeCode}`);
                            const d2 = await r2.json();
                            currentNav = parseFloat(d2?.data?.[0]?.nav) || 0;
                          }
                        } catch {}

                        // Find client ID by name match — same robust logic as transaction import
                        const clientMatch = (() => {
                          if (!h.client) return null;
                          const norm = s => s.toLowerCase()
                            .replace(/\b(mr|mrs|ms|dr|shri|smt|prof|lt|col|capt)\.?\s*/gi, "")
                            .replace(/\s+(huf|karta|minor|guardian)\b/gi, "")
                            .replace(/[^a-z\s]/g, "")
                            .replace(/\s+/g, " ").trim();
                          const tNorm = norm(h.client);
                          let m = clients.find(c => norm(c.name) === tNorm);
                          if (!m) m = clients.find(c => {
                            const cNorm = norm(c.name);
                            return cNorm.includes(tNorm) || tNorm.includes(cNorm);
                          });
                          if (!m) {
                            const tWords = new Set(tNorm.split(" ").filter(w => w.length > 2));
                            m = clients.find(c => {
                              const cWords = norm(c.name).split(" ").filter(w => w.length > 2);
                              return cWords.filter(w => tWords.has(w)).length >= 2;
                            });
                          }
                          return m?.id || null;
                        })();

                        // Content-based ID: name + client so re-uploads generate
                        // identical IDs and Supabase deduplicates via primary key
                        const nameHash = String(Math.abs(
                          (h.name + (h.client||"")).split("").reduce((a,c)=>a+c.charCodeAt(0),0)
                        )).slice(-8);
                        const holdingId = `fh_${nameHash}`;

                        const holding = {
                          id: holdingId,
                          name: h.name,
                          ticker: schemeCode || (h.isin && h.isin !== "-" ? h.isin : "-"),
                          class: "Indian Mutual Funds",
                          exchange: "AMFI",
                          currency: "INR",
                          qty: h.units,
                          costPrice: h.avgCost,
                          currentPrice: currentNav > 0 ? currentNav : h.avgCost,
                          country: "India",
                          listed: false,
                          isin: h.isin || "-",
                          schemeCode: schemeCode || null,
                          isMutualFund: true,
                          manager: "Self-Managed",
                          sector: "Mutual Funds",
                          capSize: "N/A",
                        };

                        await addHolding(holding);

                        // Assign to client
                        if (clientMatch) {
                          await setHoldingOwner(holding.id, clientMatch);
                          setHoldingOwners(p => ({ ...p, [holding.id]: clientMatch }));
                        }
                        newHoldings.push({ ...holding,
                          familyId: clientMatch ? (clients.find(c=>c.id===clientMatch)?.familyId || null) : null,
                          clientId: clientMatch,
                          valueUSD: 0, costUSD: 0, gainPct: 0,
                        });
                        added++;
                      } catch(e) {
                        console.error("[FolioHoldings]", h.name, e.message);
                        skipped++;
                      }
                    }

                    // Reload all holdings from Supabase to ensure everything is fresh
                    try {
                      const freshHoldings = await fetchHoldings();
                      const freshOwners   = await fetchHoldingOwners();
                      setHoldings(freshHoldings);
                      setHoldingOwners(freshOwners);
                    } catch {
                      setHoldings(p => [...p, ...newHoldings]);
                    }
                    setFolioGenerating(false);
                    setFolioHoldingsResult({ added, skipped });
                    if (added > 0) {
                      showToast(`✅ Added ${added} holdings to portfolio`, "#1D9E75", 5000);
                    }
                  }}
                  style={{width:"100%",background:folioGenerating?"#6B7280":"#1D9E75",
                    border:"none",borderRadius:9,color:"white",padding:"12px",
                    cursor:folioGenerating?"not-allowed":"pointer",fontSize:13,fontWeight:700,
                    boxShadow:folioGenerating?"none":"0 4px 16px rgba(16,185,129,0.35)",
                    transition:"all 0.2s"}}>
                  {folioGenerating
                    ? "⟳ Fetching NAVs & creating holdings…"
                    : `📊 Generate ${folioResult?.derivedHoldings?.length || 0} Holdings from Transactions`}
                </button>
              </div>
            )}

            {folioHoldingsResult && (
              <div style={{textAlign:"center",padding:"20px",color:"#1D9E75",fontSize:13,fontWeight:600}}>
                ✅ {folioHoldingsResult.added} holdings added to portfolio.
                {folioHoldingsResult.skipped > 0 && (
                  <span style={{color:"#D97706"}}> {folioHoldingsResult.skipped} skipped (check console).</span>
                )}
                <div style={{fontSize:11,color:T.textSecondary,marginTop:6,fontWeight:400}}>
                  Go to Holdings tab to see them. Prices update on next refresh.
                </div>
              </div>
            )}
          </div>

          {/* ── Detailed Statement — Transaction History + Cost Price ── */}
          <div style={{...tCard(),marginBottom:16,borderColor:"rgba(217,119,6,0.4)",background:"rgba(217,119,6,0.02)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:22}}>📜</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#D97706"}}>Detailed Statement — Transaction History</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                    Upload CAMS / KFintech detailed PDF to import full buy/sell history and auto-calculate cost prices
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["CAMS Detailed","KFintech Detailed","NSDL Equity Txns","CDSL Equity Txns"].map(t=>(
                  <span key={t} style={{fontSize:10,background:"rgba(217,119,6,0.1)",color:"#D97706",
                    border:"1px solid rgba(217,119,6,0.3)",borderRadius:4,padding:"2px 7px",fontWeight:600}}>{t}</span>
                ))}
              </div>
            </div>

            {/* What this does */}
            <div style={{padding:"10px 14px",background:T.navActiveBg,borderRadius:8,marginBottom:14,
              border:`1px solid rgba(217,119,6,0.2)`}}>
              <div style={{fontSize:12,fontWeight:600,color:"#D97706",marginBottom:6}}>✨ What this does differently from e-CAS</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:4,fontSize:11,color:T.textSecondary}}>
                <div>📅 Imports your <strong>full transaction history</strong> (all SIPs, lump sums, redemptions)</div>
                <div>💰 <strong>Auto-calculates weighted average cost price</strong> for each holding</div>
                <div>📊 <strong>Updates existing holdings</strong> in your portfolio with correct cost prices</div>
                <div>📈 Enables accurate <strong>P&L and XIRR calculations</strong> going back years</div>
              </div>
            </div>

            {/* How to get detailed statement */}
            <div style={{padding:"10px 14px",background:T.borderSubtle,borderRadius:8,marginBottom:14,fontSize:11,color:T.textSecondary}}>
              <strong style={{color:T.textPrimary}}>How to get your detailed statement:</strong>
              <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                <div>🔗 <strong>CAMS:</strong> Go to <span style={{color:T.brand}}>camsonline.com</span> → Services → Statement of Account → Detailed (with transactions)</div>
                <div>🔗 <strong>KFintech:</strong> Go to <span style={{color:T.brand}}>kfintech.com</span> → Investor Corner → Account Statement → Detailed</div>
                <div>🔗 <strong>NSDL equity:</strong> Go to <span style={{color:T.brand}}>eservices.nsdl.com</span> → Transaction Statement → select date range</div>
                <div>🔗 <strong>CDSL equity:</strong> Go to <span style={{color:T.brand}}>cdslindia.com</span> → eServices → Transaction Statement</div>
              </div>
            </div>

            {/* Upload zone */}
            {!detResult && (
              <div>
                <label style={{display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",border:"2px dashed rgba(217,119,6,0.5)",
                  borderRadius:12,padding:"28px 20px",cursor:detUploading?"default":"pointer",
                  gap:8,background:"rgba(217,119,6,0.02)",opacity:detUploading?0.6:1}}>
                  <div style={{fontSize:36}}>{detUploading?"⏳":"📜"}</div>
                  <div style={{fontSize:14,fontWeight:600,color:"#D97706"}}>
                    {detUploading?"Parsing transactions…":"Drop CAMS/KFintech detailed statement · or IBKR/Schwab/Fidelity activity CSV"}
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary}}>CAMS / KFintech / NSDL / CDSL · .pdf</div>
                  <input type="file" accept=".pdf" style={{display:"none"}} disabled={detUploading}
                    onChange={async e=>{
                      const f = e.target.files[0]; if (!f) return;
                      setDetFile(f); setDetError(""); setDetResult(null); setDetPreview([]);
                      setDetUploading(true);
                      try {
                        const result = await ECAS_PARSER.parseDetailed(f, detPassword);
                        if (!result.transactions.length) {
                          setDetError("No transactions found. Make sure you're uploading a Detailed Statement (with transaction history), not just a summary/CAS. Try CAMS → Services → Statement → Detailed.");
                        } else {
                          setDetResult(result);
                          setDetPreview(result.transactions.map((_,i)=>i));
                        }
                      } catch(err) {
                        if (String(err).includes("password")) {
                          setDetError("PDF is password-protected. Enter the password below (usually PAN or date of birth).");
                        } else {
                          setDetError("Parse failed: " + err.message);
                        }
                      }
                      setDetUploading(false);
                      e.target.value = "";
                    }}/>
                </label>

                {/* Password */}
                <div style={{marginTop:10,display:"flex",gap:8}}>
                  <input type="password" value={detPassword} onChange={e=>setDetPassword(e.target.value)}
                    placeholder="PDF password if protected (PAN / DOB e.g. 01011985)"
                    style={{...inp(),flex:1,fontSize:12}}/>
                  {detFile && detPassword && (
                    <button onClick={async()=>{
                      setDetError(""); setDetResult(null); setDetPreview([]); setDetUploading(true);
                      try {
                        const result = await ECAS_PARSER.parseDetailed(detFile, detPassword);
                        if (!result.transactions.length) setDetError("No transactions found with that password.");
                        else { setDetResult(result); setDetPreview(result.transactions.map((_,i)=>i)); }
                      } catch(e) { setDetError("Error: " + e.message); }
                      setDetUploading(false);
                    }} style={{background:"#D97706",border:"none",borderRadius:7,color:"white",
                      padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>
                      Retry
                    </button>
                  )}
                </div>

                {detError && (
                  <div style={{marginTop:10,padding:"10px 14px",background:"rgba(220,38,38,0.07)",
                    border:"1px solid rgba(220,38,38,0.25)",borderRadius:8,fontSize:12,color:"#A32D2D",
                    display:"flex",gap:8}}>
                    <span>⚠</span><span>{detError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Preview after parse */}
            {detResult && (
              <div>
                {/* Summary */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 14px",background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.25)",
                  borderRadius:8,marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>✅</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:"#1D9E75"}}>
                        Found {detResult.transactions.length} transactions across {Object.keys(detResult.costMap||{}).length} schemes · {detResult.type?.replace("_"," ")}
                      </div>
                      {detResult.holderName && <div style={{fontSize:11,color:T.textSecondary}}>Holder: <strong>{detResult.holderName}</strong></div>}
                    </div>
                  </div>
                  <button onClick={()=>{setDetResult(null);setDetPreview([]);setDetFile(null);setDetError("");}}
                    style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
                    ← Upload Different File
                  </button>
                </div>

                {/* Assign to client */}
                {isAdmin && (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Assign transactions to:</div>
                    <select value={detAssignTo} onChange={e=>setDetAssignTo(e.target.value)} style={inp()}>
                      <option value="">— No client assignment —</option>
                      {visibleFamilies.map(fam=>(
                        <optgroup key={fam.id} label={fam.emoji+" "+fam.name}>
                          {visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* Options */}
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10,flexWrap:"wrap"}}>
                  <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12}}>
                    <input type="checkbox" checked={detApplyCost} onChange={e=>setDetApplyCost(e.target.checked)}/>
                    <span>Auto-update cost prices of matching holdings in portfolio</span>
                  </label>
                </div>

                {/* Cost price preview */}
                {detApplyCost && Object.keys(detResult.costMap||{}).length > 0 && (
                  <div style={{marginBottom:12,padding:"10px 14px",background:T.navActiveBg,
                    borderRadius:8,border:`1px solid rgba(217,119,6,0.2)`}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#D97706",marginBottom:8}}>
                      💰 Calculated weighted average cost prices:
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:160,overflowY:"auto"}}>
                      {Object.entries(detResult.costMap).map(([name,cost])=>{
                        const matched = holdings.find(h=>h.name.toLowerCase()===name.toLowerCase()
                          || name.toLowerCase().includes(h.name.toLowerCase().slice(0,15)));
                        return (
                          <div key={name} style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",fontSize:11,padding:"3px 0",
                            borderBottom:`1px solid ${T.borderSubtle}`}}>
                            <span style={{color:T.textPrimary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:300}}>{name}</span>
                            <span style={{fontFamily:"'Inter', system-ui, sans-serif",color:"#D97706",fontWeight:600,flexShrink:0,marginLeft:8}}>
                              ₹{cost.toFixed(4)}
                            </span>
                            {matched
                              ? <span style={{fontSize:9,color:"#1D9E75",marginLeft:8,fontWeight:600,flexShrink:0}}>✓ matched</span>
                              : <span style={{fontSize:9,color:T.textMuted,marginLeft:8,flexShrink:0}}>not in portfolio</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Transaction preview table */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                  <div style={{fontSize:12,color:T.textSecondary}}>
                    {detPreview.length} of {detResult.transactions.length} transactions selected
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <span onClick={()=>setDetPreview(detResult.transactions.map((_,i)=>i))}
                      style={{fontSize:11,color:T.brand,cursor:"pointer",textDecoration:"underline"}}>All</span>
                    <span onClick={()=>setDetPreview([])}
                      style={{fontSize:11,color:T.textMuted,cursor:"pointer",textDecoration:"underline"}}>None</span>
                  </div>
                </div>

                <div style={{maxHeight:300,overflowY:"auto",border:`1px solid ${T.cardBorder}`,borderRadius:8,marginBottom:14}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:1}}>
                      <tr>
                        <th style={{...tTh(),width:32}}>
                          <input type="checkbox"
                            checked={detPreview.length===detResult.transactions.length}
                            onChange={e=>setDetPreview(e.target.checked?detResult.transactions.map((_,i)=>i):[])}/>
                        </th>
                        {["Date","Holding","Type","Units","NAV (₹)","Amount (₹)"].map(h=>(
                          <th key={h} style={tTh()}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detResult.transactions.slice(0, 500).map((t,i)=>(
                        <tr key={i} style={{background:i%2===0?"transparent":T.tableStripe,
                          opacity:detPreview.includes(i)?1:0.35}}>
                          <td style={{...tTd(),textAlign:"center"}}>
                            <input type="checkbox" checked={detPreview.includes(i)}
                              onChange={e=>setDetPreview(p=>e.target.checked?[...p,i]:p.filter(x=>x!==i))}/>
                          </td>
                          <td style={tTd({color:T.textSecondary})}>{t.date}</td>
                          <td style={tTd({fontWeight:500,maxWidth:200})} title={t.rawDesc||t.holding}>
                            <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>
                              {t.holding}
                            </div>
                          </td>
                          <td style={tTd()}>
                            <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:3,
                              background:t.type==="BUY"?"rgba(22,163,74,0.1)":t.type==="SELL"?"rgba(220,38,38,0.1)":"rgba(217,119,6,0.1)",
                              color:t.type==="BUY"?"#1D9E75":t.type==="SELL"?"#A32D2D":"#D97706"}}>
                              {t.type}
                            </span>
                          </td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{t.qty.toLocaleString("en-IN",{maximumFractionDigits:3})}</td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{t.price.toFixed(4)}</td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:t.type==="SELL"?"#A32D2D":"#1D9E75"})}>
                            {t.type==="SELL"?"-":""}{(t.qty*t.price).toLocaleString("en-IN",{maximumFractionDigits:2})}
                          </td>
                        </tr>
                      ))}
                      {detResult.transactions.length > 500 && (
                        <tr><td colSpan={7} style={{...tTd(),textAlign:"center",color:T.textMuted,padding:10}}>
                          Showing first 500 of {detResult.transactions.length} — all will be imported
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Import button */}
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <button
                    disabled={detPreview.length===0}
                    onClick={async()=>{
                      const uid = ()=>Date.now().toString(36)+Math.random().toString(36).slice(2,6);
                      const toImport = detPreview.map(i=>detResult.transactions[i]);
                      let importedCount = 0;

                      for (const t of toImport) {
                        const txn = {
                          id: uid(), date: t.date, holding: t.holding,
                          type: t.type, qty: t.qty, price: t.price,
                          currency: t.currency||"INR", fees: t.fees||0,
                          ...(detAssignTo ? { clientId: detAssignTo } : {}),
                        };
                        setTransactions(p=>[txn,...p]);
                        await addTransaction(txn).catch(e=>console.error("[Supabase] addTransaction:",e.message));
                        importedCount++;
                      }

                      // Apply weighted average cost prices to matching holdings
                      if (detApplyCost && detResult.costMap) {
                        const costEntries = Object.entries(detResult.costMap);
                        for (const [schemeName, avgCost] of costEntries) {
                          if (avgCost <= 0) continue;
                          const matched = holdings.filter(h =>
                            h.name.toLowerCase() === schemeName.toLowerCase() ||
                            schemeName.toLowerCase().includes(h.name.toLowerCase().slice(0,15)) ||
                            h.name.toLowerCase().includes(schemeName.toLowerCase().slice(0,15))
                          );
                          for (const h of matched) {
                            setHoldings(p=>p.map(x=>x.id===h.id?{...x,costPrice:avgCost}:x));
                            await updateHolding(h.id,{costPrice:avgCost}).catch(()=>{});
                          }
                        }
                      }

                      showToast(`✅ Imported ${importedCount} transactions${detApplyCost?" · cost prices updated":""}`, "#1D9E75");
                      setDetResult(null); setDetPreview([]); setDetFile(null);
                      setDetPassword(""); setDetAssignTo("");
                    }}
                    style={{background:detPreview.length===0?"#78716C":"#854F0B",
                      border:"none",borderRadius:8,color:"white",padding:"10px 24px",
                      cursor:detPreview.length===0?"default":"pointer",fontSize:13,fontWeight:700,
                      boxShadow:detPreview.length>0?"0 4px 14px rgba(217,119,6,0.4)":"none"}}>
                    📥 Import {detPreview.length} Transactions
                    {detApplyCost && detResult.costMap && Object.keys(detResult.costMap).length>0
                      ? ` + Update Cost Prices` : ""}
                  </button>
                  <button onClick={()=>{setDetResult(null);setDetPreview([]);setDetFile(null);setDetError("");}}
                    style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                      color:T.textSecondary,padding:"10px 18px",cursor:"pointer",fontSize:13}}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Generate Holdings from Existing Transactions ── */}
          <div style={{...tCard(),marginBottom:16,borderColor:"rgba(16,185,129,0.35)",background:"rgba(16,185,129,0.02)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <span style={{fontSize:22}}>🔄</span>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Generate Holdings from Transactions</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                  Nets all buys and sells per ticker → creates missing holdings + updates qty/cost on existing ones. Works for Indian MFs and global equities/ETFs (IBKR).
                </div>
              </div>
            </div>

            {genFromTxnResult && (
              <div style={{padding:"8px 12px",borderRadius:8,marginBottom:10,fontSize:12,
                background: genFromTxnResult.added > 0 || genFromTxnResult.updated > 0 ? "rgba(22,163,74,0.08)" : "rgba(217,119,6,0.08)",
                border: `1px solid ${genFromTxnResult.added > 0 || genFromTxnResult.updated > 0 ? "rgba(22,163,74,0.25)" : "rgba(217,119,6,0.25)"}`,
                color: genFromTxnResult.added > 0 || genFromTxnResult.updated > 0 ? "#1D9E75" : "#D97706"}}>
                {genFromTxnResult.added > 0 || genFromTxnResult.updated > 0
                  ? `✅ Created ${genFromTxnResult.added} new · Updated ${genFromTxnResult.updated} existing · ${genFromTxnResult.skipped} skipped (zero qty) · Go to Holdings tab`
                  : `⚠ No changes — ${genFromTxnResult.skipped} positions skipped. All may be zero qty (fully sold).`}
                {genFromTxnResult.unmatched?.length > 0 && (
                  <div style={{marginTop:4,color:"#D97706"}}>
                    ⚠ Could not fetch price for: {genFromTxnResult.unmatched.join(", ")}
                  </div>
                )}
              </div>
            )}

            <button
              disabled={genFromTxnRunning}
              onClick={async () => {
                setGenFromTxnRunning(true);
                setGenFromTxnResult(null);
                let added = 0, updated = 0, skipped = 0;
                const unmatched = [];

                try {
                  // ── Step 1: Net all transactions per ticker ──────────────
                  // Include all buy/sell types — MF and equity/ETF
                  const BUY_TYPES  = new Set(["BUY","SIP","STP_IN","SWITCH_IN","PURCHASE"]);
                  const SELL_TYPES = new Set(["SELL","REDEEM","STP_OUT","SWITCH_OUT","REDEMPTION"]);

                  // Scope to current client selection if admin has a client selected
                  const scopedTxns = transactions.filter(t => {
                    if (!isAdmin) return t.clientId === loggedInClientId;
                    if (selectedClients.size > 0) return selectedClients.has(t.clientId);
                    return true;
                  }).filter(t => BUY_TYPES.has(t.type) || SELL_TYPES.has(t.type));

                  if (!scopedTxns.length) {
                    setGenFromTxnResult({ added:0, updated:0, skipped:0, error:"No transactions found for current client selection." });
                    setGenFromTxnRunning(false);
                    return;
                  }

                  // Build per-ticker map: net qty, WACB, clientId, currency
                  const holdingMap = {};
                  for (const t of scopedTxns) {
                    const key = (t.holding || "").trim();
                    if (!key) continue;
                    if (!holdingMap[key]) holdingMap[key] = {
                      name: key,
                      ticker: key, // for equities, holding = ticker symbol
                      units: 0,
                      totalCost: 0,
                      clientId: t.clientId || null,
                      currency: t.currency || "USD",
                      isMF: false, // determined below
                    };
                    const h = holdingMap[key];
                    const qty   = Math.abs(t.qty   || 0);
                    const price = Math.abs(t.price || 0);
                    if (BUY_TYPES.has(t.type)) {
                      h.totalCost += qty * price;
                      h.units     += qty;
                    } else if (SELL_TYPES.has(t.type)) {
                      const cpu = h.units > 0 ? h.totalCost / h.units : 0;
                      h.totalCost = Math.max(0, h.totalCost - cpu * qty);
                      h.units     = Math.max(0, h.units - qty);
                    }
                    // Prefer INR currency → marks as MF
                    if (t.currency === "INR") h.isMF = true;
                    if (t.clientId && !h.clientId) h.clientId = t.clientId;
                  }

                  // Filter to positions with meaningful qty remaining
                  const activePositions = Object.values(holdingMap).filter(h => h.units > 0.0001);

                  if (!activePositions.length) {
                    setGenFromTxnResult({ added:0, updated:0, skipped: Object.keys(holdingMap).length });
                    setGenFromTxnRunning(false);
                    return;
                  }

                  // ── Step 2: Match to existing holdings ───────────────────
                  // Match by ticker (case-insensitive) or name
                  const existingByTicker = {};
                  const existingByName   = {};
                  for (const h of holdings) {
                    if (h.ticker && h.ticker !== "-") existingByTicker[h.ticker.toUpperCase()] = h;
                    existingByName[h.name.toLowerCase().trim()] = h;
                  }

                  for (const pos of activePositions) {
                    const avgCost = pos.units > 0 ? pos.totalCost / pos.units : 0;
                    const roundQty  = Math.round(pos.units  * 10000) / 10000;
                    const roundCost = Math.round(avgCost    * 100)   / 100;

                    // Check if holding already exists
                    const existing =
                      existingByTicker[pos.ticker.toUpperCase()] ||
                      existingByName[pos.name.toLowerCase().trim()];

                    if (existing) {
                      // ── UPDATE existing holding ──────────────────────────
                      // Only update qty and costPrice — don't touch name, class,
                      // country, sector etc. that may have been manually set.
                      // Also stamp reconciledFromTxns=true so the hw memo doesn't
                      // double-count by re-applying transaction deltas.
                      try {
                        await updateHolding(existing.id, {
                          qty:                roundQty,
                          costPrice:          roundCost,
                          reconciledFromTxns: true,
                        });
                        // Ensure client ownership is set
                        if (pos.clientId) {
                          await setHoldingOwner(existing.id, pos.clientId);
                          setHoldingOwners(p => ({ ...p, [existing.id]: pos.clientId }));
                        }
                        updated++;
                      } catch(e) {
                        console.error("[GenHoldings] update:", pos.name, e.message);
                        skipped++;
                      }
                    } else {
                      // ── CREATE new holding ───────────────────────────────
                      try {
                        let currentPrice = roundCost; // fallback
                        let schemeCode   = null;
                        let assetClass   = "Equities";
                        let exchange     = "NYSE";
                        let country      = "USA";
                        let isMF         = false;
                        let isin         = "-";

                        if (pos.isMF) {
                          // Indian MF — fetch NAV from MFapi
                          isMF      = true;
                          assetClass = "Indian Mutual Funds";
                          exchange   = "AMFI";
                          country    = "India";
                          try {
                            const searchQ = pos.name.replace(/Regular-Growth|Regular - Growth|Growth|Direct|Plan/gi,"").trim().slice(0,40);
                            const r = await fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(searchQ)}`);
                            const data = await r.json();
                            if (data?.length) {
                              schemeCode = String(data[0].schemeCode);
                              const r2 = await fetch(`https://api.mfapi.in/mf/${schemeCode}`);
                              const d2 = await r2.json();
                              currentPrice = parseFloat(d2?.data?.[0]?.nav) || roundCost;
                            }
                          } catch {}
                        } else {
                          // Global equity/ETF — fetch price via Yahoo proxy
                          try {
                            const yTicker = pos.ticker.toUpperCase();
                            const r = await fetch(`/api/price?ticker=${encodeURIComponent(yTicker)}&source=yahoo`);
                            if (r.ok) {
                              const d = await r.json();
                              if (d?.price > 0) currentPrice = d.price;
                              else unmatched.push(yTicker);
                            } else {
                              unmatched.push(pos.ticker);
                            }
                          } catch {
                            unmatched.push(pos.ticker);
                          }
                          // Determine asset class from ticker patterns
                          const etfPatterns = /^(EIMI|IBIT|SXRV|SPY|GLD|SLV|QQQ|VTI|IUHC|IUFS|SPY5|MAGS|NIFTYBEES|CPSEETF|GOLDBEES)$/i;
                          if (etfPatterns.test(pos.ticker)) {
                            assetClass = "ETF";
                          }
                          // Determine exchange/country from currency
                          if (pos.currency === "EUR") { exchange = "LSE"; country = "Ireland"; }
                          else if (pos.currency === "GBP") { exchange = "LSE"; country = "UK"; }
                          else { exchange = "NYSE"; country = "USA"; }
                        }

                        const nameHash  = String(Math.abs(
                          (pos.name + (pos.clientId||"")).split("").reduce((a,c)=>a+c.charCodeAt(0),0)
                        )).slice(-8);
                        const holdingId = `txh_${nameHash}`;

                        const holding = {
                          id:           holdingId,
                          name:         pos.isMF ? pos.name : pos.name, // full description for MF, ticker name for equity
                          ticker:       pos.isMF ? (schemeCode || "-") : pos.ticker,
                          class:        assetClass,
                          exchange,
                          currency:     pos.currency || "USD",
                          qty:          roundQty,
                          costPrice:    roundCost,
                          currentPrice: currentPrice,
                          country,
                          listed:       !pos.isMF,
                          isin,
                          schemeCode:   schemeCode || null,
                          isMutualFund: isMF,
                          manager:      "Self-Managed",
                          sector:       pos.isMF ? "Mutual Funds" : "Other",
                          capSize:      "-",
                          notes:        "Auto-generated from transaction import",
                          reconciledFromTxns: true,  // signals hw memo not to apply txn deltas (already netted in qty)
                        };

                        await addHolding(holding);
                        if (pos.clientId) {
                          await setHoldingOwner(holding.id, pos.clientId);
                          setHoldingOwners(p => ({ ...p, [holding.id]: pos.clientId }));
                        }
                        added++;
                      } catch(e) {
                        console.error("[GenHoldings] create:", pos.name, e.message);
                        skipped++;
                      }
                    }
                  }

                  // Reload holdings fresh from Supabase
                  const freshH = await fetchHoldings();
                  const freshO = await fetchHoldingOwners();
                  setHoldings(freshH);
                  setHoldingOwners(freshO);

                } catch(e) {
                  console.error("[GenHoldings]", e);
                }

                setGenFromTxnRunning(false);
                setGenFromTxnResult({ added, updated, skipped, unmatched });
                if (added > 0 || updated > 0) {
                  showToast(`✅ ${added} created · ${updated} updated from transactions`, "#1D9E75", 5000);
                }
              }}
              style={{width:"100%",padding:"11px",border:"none",borderRadius:9,
                background:genFromTxnRunning?"#6B7280":"#1D9E75",
                color:"white",fontSize:13,fontWeight:700,cursor:genFromTxnRunning?"not-allowed":"pointer",
                transition:"all 0.2s",boxShadow:genFromTxnRunning?"none":"0 4px 14px rgba(16,185,129,0.3)"}}>
              {genFromTxnRunning ? "⟳ Reconciling… (fetching live prices)" : "📊 Reconcile Holdings from Transactions"}
            </button>
          </div>

          {/* ── AI Statement Parser ── */}
          <div style={card()}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:14,fontWeight:500}}>AI statement parser</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                  Upload any broker statement — PDF, CSV or Excel — Claude reads and extracts {aiParseMode==="holdings"?"all holdings":"the full trade history"} automatically.
                </div>
              </div>
              {/* Mode toggle */}
              <div style={{marginLeft:"auto",display:"flex",border:`1px solid ${T.borderSubtle}`,borderRadius:8,overflow:"hidden"}}>
                {[["holdings","Holdings"],["transactions","Transactions"]].map(([key,label])=>(
                  <div key={key} onClick={()=>{
                      if (aiParseMode!==key) {
                        setAiParseMode(key);
                        setAiParseResult(null); setAiParseFile(null);
                        setAiParseOverrides({}); setAiParseError("");
                      }
                    }}
                    style={{padding:"6px 14px",fontSize:11,cursor:"pointer",
                      fontWeight:aiParseMode===key?500:400,
                      background:aiParseMode===key?T.surfaceBg:"transparent",
                      color:aiParseMode===key?T.textPrimary:T.textMuted,
                      transition:"all 0.12s"}}>
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {!aiParseResult ? (
              <div>
                {/* Upload area */}
                <label style={{display:"block",cursor:"pointer"}}>
                  <div style={{border:`2px dashed ${aiParseLoading?"#6366F1":T.cardBorder}`,
                    borderRadius:12,padding:"28px 20px",textAlign:"center",
                    background:aiParseLoading?"rgba(99,102,241,0.04)":"transparent",
                    transition:"all 0.2s",cursor:aiParseLoading?"default":"pointer"}}
                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor="#6366F1";}}
                    onDragLeave={e=>e.currentTarget.style.borderColor=T.cardBorder}
                    onDrop={async e=>{
                      e.preventDefault();
                      e.currentTarget.style.borderColor=T.cardBorder;
                      const f=e.dataTransfer.files[0];
                      if(f&&/\.(pdf|csv|tsv|txt|xls|xlsx)$/i.test(f.name)){
                        document.getElementById('aiParseInput').files=e.dataTransfer.files;
                        document.getElementById('aiParseInput').dispatchEvent(new Event('change',{bubbles:true}));
                      }
                    }}>
                    <div style={{fontSize:36,marginBottom:8}}>{aiParseLoading?"⏳":"📄"}</div>
                    <div style={{fontSize:14,fontWeight:600,color:aiParseLoading?"#6366F1":T.textPrimary}}>
                      {aiParseLoading
                        ? "AI is reading your statement…"
                        : "Drop a PDF, CSV or Excel file here · or click to upload"}
                    </div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
                      .pdf · .csv · .xls · .xlsx — Saxo · DBS · IBKR · Schwab · Fidelity · HSBC · Zerodha · Angel · any format
                    </div>
                    {aiParseLoading && (
                      <div style={{marginTop:12,fontSize:11,color:"#6366F1",
                        animation:"pulse 1.5s ease-in-out infinite"}}>
                        Extracting text → sending to Claude → parsing {aiParseMode}…
                      </div>
                    )}
                    <input id="aiParseInput" type="file" accept=".pdf,.csv,.tsv,.txt,.xls,.xlsx" style={{display:"none"}}
                      disabled={aiParseLoading}
                      onChange={async e=>{
                        const f = e.target.files[0]; if (!f) return;
                        setAiParseFile(f); setAiParseError(""); setAiParseResult(null);
                        setAiParseOverrides({}); setAiParseLoading(true);
                        try {
                          // Step 1: Extract text — PDF (pdf.js), CSV/TSV/TXT (raw), or Excel (SheetJS → CSV)
                          const { text, kind } = await extractStatementText(f, aiParsePassword);
                          if (!text || text.length < 20) {
                            setAiParseError(
                              kind === "excel"
                                ? "Could not read this Excel file — it appears empty or has no readable sheets."
                                : kind === "csv"
                                ? "This CSV / text file appears to be empty."
                                : "Could not extract text from this PDF. It may be image-scanned (not text-based). Try a different export format from your broker.");
                            setAiParseLoading(false);
                            return;
                          }
                          // Step 2: Send to AI parser API — endpoint depends on mode
                          const endpoint = aiParseMode === "transactions" ? "/api/parse-transactions" : "/api/parse-statement";
                          const resp = await fetch(endpoint, {
                            method: "POST",
                            headers: {"Content-Type":"application/json"},
                            body: JSON.stringify({
                              text,
                              filename: f.name,
                              provider: "anthropic",
                              model: "claude-sonnet-4-6",
                            }),
                          });
                          const data = await resp.json();
                          const items = aiParseMode === "transactions" ? data.transactions : data.holdings;
                          if (!resp.ok || data.error) {
                            setAiParseError("AI parse failed: " + (data.error||"Unknown error"));
                          } else if (!items || items.length === 0) {
                            setAiParseError(
                              kind === "pdf"
                                ? `No ${aiParseMode} found. The statement may be image-scanned, holdings-only, or in an unsupported format. Try a text-based PDF export from your broker portal.`
                                : `No ${aiParseMode} found in this ${kind === "excel" ? "spreadsheet" : "file"}. Check that it actually contains ${aiParseMode} rows, or switch the mode toggle above.`);
                          } else {
                            setAiParseResult(data);
                          }
                        } catch(err) {
                          setAiParseError("Error: " + err.message +
                            (err.message.includes("fetch") ? " — check your internet connection or Vercel function logs." : ""));
                        }
                        setAiParseLoading(false);
                        e.target.value = "";
                      }}/>
                  </div>
                </label>

                {/* Password field */}
                <div style={{marginTop:10}}>
                  <input type="password" value={aiParsePassword}
                    onChange={e=>setAiParsePassword(e.target.value)}
                    placeholder="PDF password if protected (optional · PDF only)"
                    style={{...inp(),width:"100%",fontSize:12}}/>
                </div>

                {/* Error */}
                {aiParseError && (
                  <div style={{marginTop:10,padding:"10px 14px",background:"rgba(220,38,38,0.07)",
                    border:"1px solid rgba(220,38,38,0.25)",borderRadius:8,
                    fontSize:11,color:"#A32D2D",display:"flex",gap:8,alignItems:"flex-start"}}>
                    <span style={{flexShrink:0}}>⚠</span>
                    <span style={{whiteSpace:"pre-wrap"}}>{aiParseError}</span>
                  </div>
                )}

                {/* What it supports */}
                <div style={{marginTop:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  {[
                    ["🇸🇬 Saxo Bank","Portfolio / account statements"],
                    ["🇸🇬 DBS Vickers","Portfolio statements"],
                    ["🇺🇸 IBKR","Activity statements (PDF)"],
                    ["🇺🇸 Charles Schwab","Account statements"],
                    ["🇬🇧 HSBC Private","Portfolio reports"],
                    ["🇺🇸 Fidelity","Portfolio statements"],
                    ["🇮🇳 Zerodha / Groww","Holdings exports"],
                    ["🌍 Any broker","If it's text-based PDF"],
                  ].map(([b,d])=>(
                    <div key={b} style={{display:"flex",gap:8,padding:"6px 10px",
                      background:T.pageBg,borderRadius:7,border:`1px solid ${T.cardBorder}`}}>
                      <span style={{fontSize:11,fontWeight:600,color:T.textPrimary,minWidth:130}}>{b}</span>
                      <span style={{fontSize:10,color:T.textMuted}}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>

            ) : (
              /* ── Results ── */
              <div>
                {/* Success header */}
                {(()=>{ const _items = aiParseMode==="transactions" ? (aiParseResult.transactions||[]) : (aiParseResult.holdings||[]); return (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 14px",background:"rgba(99,102,241,0.08)",
                  border:"1px solid rgba(99,102,241,0.25)",borderRadius:8,marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:500,color:"#6366F1"}}>
                        AI extracted {_items.length} {aiParseMode==="transactions"?"transaction":"holding"}{_items.length!==1?"s":""}
                        {aiParseResult.broker && aiParseResult.broker !== "Unknown"
                          ? ` from ${aiParseResult.broker}` : ""}
                      </div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                        Confidence: {aiParseResult.confidence}% · {aiParseResult.chunksProcessed > 1 ? `${aiParseResult.chunksProcessed} chunks processed · ` : ""}Review and edit below before importing
                        {aiParseMode==="holdings" && _items.filter(h=>!h.costPrice||h.costPrice===0).length > 0 && (
                          <span style={{color:"#D97706",marginLeft:8}}>
                            · ⚠ {_items.filter(h=>!h.costPrice||h.costPrice===0).length} missing cost price
                          </span>
                        )}
                      </div>
                      {aiParseResult.warnings?.length > 0 && (
                        <div style={{marginTop:6}}>
                          {aiParseResult.warnings.map((w,wi)=>(
                            <div key={wi} style={{fontSize:10,color:"#D97706",display:"flex",alignItems:"center",gap:4}}>
                              ⚠ {w}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={()=>{setAiParseResult(null);setAiParseFile(null);setAiParseOverrides({});setAiParseError("");}}
                    style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,
                      border:`1px solid ${T.cardBorder}`,borderRadius:6,padding:"5px 12px",cursor:"pointer"}}>
                    ← Parse Different File
                  </button>
                </div>
                ); })()}

                {/* Assign to client */}
                {isAdmin && (
                  <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:T.textSecondary,fontWeight:600}}>Assign to:</span>
                    <select value={aiParseAssignTo} onChange={e=>setAiParseAssignTo(e.target.value)}
                      style={{fontSize:12,padding:"5px 10px",borderRadius:7,flex:1,maxWidth:280,
                        background:T.inputBg,border:`1px solid ${aiParseAssignTo?T.brand:T.cardBorder}`,
                        color:T.textPrimary,outline:"none",cursor:"pointer"}}>
                      <option value="">— Unassigned —</option>
                      {visibleFamilies.map(fam=>(
                        <optgroup key={fam.id} label={fam.emoji+" "+fam.name}>
                          {visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>(
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* Editable preview table — TRANSACTIONS mode */}
                {aiParseMode==="transactions" && (()=>{
                  const rows = aiParseResult.transactions || [];
                  const updateRow = (i,field,val) => setAiParseOverrides(p=>({...p,[i]:{...p[i],[field]:val}}));
                  const deleteRow = (i) => setAiParseResult(p=>({...p, transactions: p.transactions.filter((_,ri)=>ri!==i)}));
                  const ec = {border:"none",background:"transparent",fontSize:11,color:T.textPrimary,outline:"none",width:"100%",padding:0};
                  const TYPE_OPTS = ["BUY","SELL","SIP","REDEEM","STP_IN","STP_OUT","SWITCH_IN","SWITCH_OUT","DIVIDEND"];
                  return (
                    <div style={{marginBottom:14}}>
                      <div style={{maxHeight:380,overflowY:"auto",border:`1px solid ${T.cardBorder}`,borderRadius:8}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                          <thead style={{position:"sticky",top:0,background:T.cardBg,zIndex:1}}>
                            <tr>
                              {["Date","Holding","Type","Qty","Price","Ccy","Fees",""].map(h=>(
                                <th key={h} style={{textAlign:h==="Qty"||h==="Price"||h==="Fees"?"right":"left",padding:"8px 10px",fontSize:10,fontWeight:400,color:T.textMuted,borderBottom:`1px solid ${T.cardBorder}`}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((t,i)=>{
                              const ov = aiParseOverrides[i]||{};
                              const v = (f)=> ov[f]!==undefined ? ov[f] : t[f];
                              return (
                                <tr key={i} style={{borderBottom:`1px solid ${T.borderSubtle}`}}>
                                  <td style={{padding:"6px 10px",width:100}}>
                                    <input value={v("date")} onChange={e=>updateRow(i,"date",e.target.value)}
                                      style={{...ec,fontFamily:"'SF Mono',monospace",fontSize:10.5}}/>
                                  </td>
                                  <td style={{padding:"6px 10px",minWidth:180}}>
                                    <input value={v("holding")} onChange={e=>updateRow(i,"holding",e.target.value)} style={{...ec,fontWeight:500}}/>
                                    {t.ticker && <span style={{fontSize:9,color:T.textMuted,fontFamily:"'SF Mono',monospace"}}>{t.ticker}</span>}
                                  </td>
                                  <td style={{padding:"6px 10px",width:96}}>
                                    <select value={v("type")} onChange={e=>updateRow(i,"type",e.target.value)}
                                      style={{...ec,cursor:"pointer",color:["SELL","REDEEM","STP_OUT","SWITCH_OUT"].includes(v("type"))?"#A32D2D":"#1D9E75"}}>
                                      {TYPE_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                                    </select>
                                  </td>
                                  <td style={{padding:"6px 10px",width:90}}>
                                    <input type="number" step="any" value={v("qty")} onChange={e=>updateRow(i,"qty",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontVariantNumeric:"tabular-nums"}}/>
                                  </td>
                                  <td style={{padding:"6px 10px",width:100}}>
                                    <input type="number" step="any" value={v("price")} onChange={e=>updateRow(i,"price",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontVariantNumeric:"tabular-nums",
                                        color:!v("price")?"#D97706":T.textPrimary}}/>
                                  </td>
                                  <td style={{padding:"6px 10px",width:56}}>
                                    <input value={v("currency")} onChange={e=>updateRow(i,"currency",e.target.value.toUpperCase())}
                                      style={{...ec,fontSize:10.5}} maxLength={3}/>
                                  </td>
                                  <td style={{padding:"6px 10px",width:70}}>
                                    <input type="number" step="any" value={v("fees")||""} onChange={e=>updateRow(i,"fees",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontVariantNumeric:"tabular-nums",color:T.textMuted}} placeholder="0"/>
                                  </td>
                                  <td style={{padding:"6px 8px",width:30,textAlign:"center"}}>
                                    <span onClick={()=>deleteRow(i)} style={{cursor:"pointer",color:"#A32D2D",fontSize:12,opacity:0.6}} title="Remove row">✕</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{marginTop:6,fontSize:10,color:T.textMuted}}>
                        DIVIDEND rows are imported for record-keeping but excluded from XIRR cash flows. Orange price = missing — edit before import.
                      </div>
                    </div>
                  );
                })()}

                {/* Editable preview table — HOLDINGS mode */}
                {aiParseMode==="holdings" && (()=>{
                  const rows = aiParseResult.holdings;
                  const updateRow = (i,field,val) => setAiParseOverrides(p=>({
                    ...p,[i]:{...p[i],[field]:val}
                  }));
                  const deleteRow = (i) => setAiParseResult(p=>({
                    ...p, holdings: p.holdings.filter((_,ri)=>ri!==i)
                  }));
                  const ec = {border:"none",background:"transparent",fontSize:11,
                    color:T.textPrimary,outline:"none",fontFamily:"DM Sans,sans-serif",
                    width:"100%",padding:0};
                  const CLASS_OPTS = ["Equities","Indian Mutual Funds","Global Mutual Funds",
                    "Fixed Income","ETF","Real Estate","Digital Assets","Commodities","Alternatives","Miscellaneous"];
                  const CCY_OPTS = ["USD","INR","SGD","EUR","GBP","JPY","AUD","CAD","HKD","CHF"];

                  return (
                    <div>
                      <div style={{padding:"5px 10px",background:`${T.brand}08`,
                        border:`1px solid ${T.cardBorder}`,borderRadius:"6px 6px 0 0",
                        fontSize:10,color:T.textMuted,display:"flex",alignItems:"center",gap:5}}>
                        ✏️ <strong style={{color:T.brand}}>Click any cell to edit</strong>
                        &nbsp;· Fix names, quantities, cost prices · ✕ removes a row
                      </div>
                      <div style={{maxHeight:360,overflowY:"auto",overflowX:"auto",
                        border:`1px solid ${T.cardBorder}`,borderTop:"none",
                        borderRadius:"0 0 8px 8px",marginBottom:12}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:700}}>
                          <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:2}}>
                            <tr>
                              {["","Name","Class","Ccy","Qty","Cost Price","Curr. Price","ISIN","Exchange"].map((h,hi)=>(
                                <th key={hi} style={{...tTh(),
                                  width:h===""?"28px":h==="Name"?"200px":h==="Class"?"130px":
                                    h==="ISIN"?"100px":h==="Exchange"?"80px":"75px"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((h,i)=>{
                              const ov = aiParseOverrides[i]||{};
                              const name    = ov.name    !== undefined ? ov.name    : h.name;
                              const qty     = ov.qty     !== undefined ? ov.qty     : h.qty;
                              const costPx  = ov.costPrice !== undefined ? ov.costPrice : h.costPrice;
                              const currPx  = ov.currentPrice !== undefined ? ov.currentPrice : h.currentPrice;
                              const cls     = ov.class   !== undefined ? ov.class   : h.class;
                              const ccy     = ov.currency !== undefined ? ov.currency : h.currency;
                              return (
                                <tr key={i} style={{borderBottom:`1px solid ${T.tdBorder}`,
                                  background:i%2===0?"transparent":T.tableStripe}}>
                                  <td style={{padding:"3px 6px",textAlign:"center"}}>
                                    <button onClick={()=>deleteRow(i)}
                                      style={{background:"none",border:"none",cursor:"pointer",
                                        color:"#A32D2D",fontSize:13,opacity:0.5}}
                                      onMouseEnter={e=>e.currentTarget.style.opacity=1}
                                      onMouseLeave={e=>e.currentTarget.style.opacity=0.5}>✕</button>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <input value={name||""} onChange={e=>updateRow(i,"name",e.target.value)}
                                      style={{...ec,fontWeight:600,minWidth:160}}
                                      onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                      onBlur={e=>e.currentTarget.closest("td").style.background=""}
                                      placeholder="Security name"/>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <select value={cls||"Equities"} onChange={e=>updateRow(i,"class",e.target.value)}
                                      style={{...ec,cursor:"pointer",appearance:"none",WebkitAppearance:"none"}}>
                                      {CLASS_OPTS.map(c=><option key={c}>{c}</option>)}
                                    </select>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <select value={ccy||"USD"} onChange={e=>updateRow(i,"currency",e.target.value)}
                                      style={{...ec,cursor:"pointer",appearance:"none",WebkitAppearance:"none",width:55}}>
                                      {CCY_OPTS.map(c=><option key={c}>{c}</option>)}
                                    </select>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <input type="number" step="any" value={qty||""}
                                      onChange={e=>updateRow(i,"qty",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif"}}
                                      onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                      onBlur={e=>e.currentTarget.closest("td").style.background=""}/>
                                  </td>
                                  <td style={{padding:"2px 4px",
                                    background:!costPx||costPx===0?"rgba(217,119,6,0.06)":"transparent"}}>
                                    <input type="number" step="any" value={costPx||""}
                                      onChange={e=>updateRow(i,"costPrice",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif",
                                        color:!costPx||costPx===0?"#D97706":T.textPrimary}}
                                      onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                      onBlur={e=>e.currentTarget.closest("td").style.background=
                                        !costPx||costPx===0?"rgba(217,119,6,0.06)":"transparent"}
                                      placeholder="0" title="Average cost per unit"/>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <input type="number" step="any" value={currPx||""}
                                      onChange={e=>updateRow(i,"currentPrice",parseFloat(e.target.value)||0)}
                                      style={{...ec,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif"}}
                                      onFocus={e=>e.currentTarget.closest("td").style.background=`${T.brand}0D`}
                                      onBlur={e=>e.currentTarget.closest("td").style.background=""}
                                      placeholder="0"/>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <input value={h.isin==="-"?"":h.isin||""}
                                      onChange={e=>updateRow(i,"isin",e.target.value||"-")}
                                      style={{...ec,fontSize:10,color:T.textMuted,
                                        fontFamily:"'Inter', system-ui, sans-serif"}}
                                      placeholder="ISIN"/>
                                  </td>
                                  <td style={{padding:"2px 4px"}}>
                                    <input value={h.exchange==="-"?"":h.exchange||""}
                                      onChange={e=>updateRow(i,"exchange",e.target.value||"-")}
                                      style={{...ec,fontSize:10,color:T.textMuted}}
                                      placeholder="NYSE"/>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Import button */}
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  {isAdmin && !aiParseAssignTo && (
                    <div style={{width:"100%",padding:"7px 12px",background:"rgba(217,119,6,0.08)",
                      border:"1px solid rgba(217,119,6,0.3)",borderRadius:7,
                      fontSize:11,color:"#D97706"}}>
                      ⚠ Please assign to a client before importing.
                    </div>
                  )}
                  {(()=>{ const _count = aiParseMode==="transactions" ? (aiParseResult.transactions||[]).length : (aiParseResult.holdings||[]).length; return (
                  <button
                    disabled={_count===0||(isAdmin&&!aiParseAssignTo)}
                    onClick={async()=>{
                      if (aiParseMode === "transactions") {
                        // ── TRANSACTIONS import path ──
                        const toImport = (aiParseResult.transactions||[]).map((t,i)=>{
                          const ov = aiParseOverrides[i]||{};
                          const v = (f)=> ov[f]!==undefined ? ov[f] : t[f];
                          return {
                            id: `txn_${Date.now()}_ai_${i}`,
                            date: v("date"), holding: v("holding"), type: v("type"),
                            qty: +v("qty")||0, price: +v("price")||0,
                            currency: v("currency")||"USD", fees: +v("fees")||0,
                            clientId: aiParseAssignTo || "",
                          };
                        }).filter(t=>t.holding && t.qty>0 && /^\d{4}-\d{2}-\d{2}$/.test(t.date));

                        setImportProgress({ done:0, total:toImport.length, current:`transaction 1 of ${toImport.length}` });
                        let saved=0, failed=0;
                        for (let i=0;i<toImport.length;i++) {
                          const err = await addTransaction(toImport[i]).then(()=>null).catch(e=>e);
                          if (err) { console.error("[AI Txn Import]", err?.message, toImport[i].holding); failed++; }
                          else saved++;
                          if ((i+1)%5===0 || i===toImport.length-1) {
                            setImportProgress({ done:i+1, total:toImport.length, current:`${toImport[i].holding} ${toImport[i].type}` });
                            await new Promise(r=>setTimeout(r,0));
                          }
                        }
                        setImportProgress(null);
                        if (saved > 0) setTransactions(p=>[...p,...toImport]);
                        logAudit("AI_PARSE_TXN_IMPORT",
                          `${saved} transactions from ${aiParseFile?.name||"file"} via AI`+
                          (aiParseAssignTo?` → ${visibleClients.find(c=>c.id===aiParseAssignTo)?.name}`:""));
                        showToast(`✓ AI imported ${saved} transaction${saved!==1?"s":""}${failed>0?` (${failed} failed)`:""} — use 🔄 Reconcile in Transactions tab if any holdings are missing`, "#1D9E75", 6000);
                        setAiParseResult(null); setAiParseFile(null);
                        setAiParseOverrides({}); setAiParseAssignTo("");
                        return;
                      }
                      // ── HOLDINGS import path (existing) ──
                      // Merge overrides
                      const toImport = aiParseResult.holdings.map((h,i)=>{
                        const ov = aiParseOverrides[i]||{};
                        return {
                          ...h,
                          name:         ov.name         !== undefined ? ov.name         : h.name,
                          qty:          ov.qty          !== undefined ? ov.qty          : h.qty,
                          costPrice:    ov.costPrice    !== undefined ? ov.costPrice    : h.costPrice,
                          currentPrice: ov.currentPrice !== undefined ? ov.currentPrice : h.currentPrice,
                          class:        ov.class        !== undefined ? ov.class        : h.class,
                          currency:     ov.currency     !== undefined ? ov.currency     : h.currency,
                          isin:         ov.isin         !== undefined ? ov.isin         : h.isin,
                          exchange:     ov.exchange     !== undefined ? ov.exchange     : h.exchange,
                        };
                      }).filter(h=>h.name&&h.qty>0);

                      let saved=0, failed=0;
                      for (const h of toImport) {
                        const err = await addHolding(h).then(()=>null).catch(e=>e);
                        if (err) { console.error("[AI Import] addHolding:", err?.message, h.name); failed++; }
                        else {
                          if (aiParseAssignTo) await setHoldingOwner(String(h.id), aiParseAssignTo).catch(()=>{});
                          saved++;
                        }
                      }
                      setHoldings(p=>[...p,...toImport]);
                      if (aiParseAssignTo) {
                        setHoldingOwners(prev=>{
                          const next={...prev};
                          toImport.forEach(h=>{next[String(h.id)]=aiParseAssignTo;});
                          return next;
                        });
                      }
                      logAudit("AI_PARSE_IMPORT",
                        `${saved} holdings from ${aiParseFile?.name||"file"} via AI`+
                        (aiParseAssignTo?` → ${visibleClients.find(c=>c.id===aiParseAssignTo)?.name}`:""));
                      showToast(`✅ AI imported ${saved} holding${saved!==1?"s":""}${failed>0?` (${failed} failed)`:""}`, "#1D9E75");
                      setAiParseResult(null); setAiParseFile(null);
                      setAiParseOverrides({}); setAiParseAssignTo("");
                    }}
                    style={{
                      background: (_count===0||(isAdmin&&!aiParseAssignTo))
                        ? "#78716C"
                        : "#6366F1",
                      border:"none",borderRadius:8,color:"white",
                      padding:"10px 24px",fontSize:13,fontWeight:500,
                      cursor:(_count===0||(isAdmin&&!aiParseAssignTo))?"default":"pointer"
                    }}>
                    Import {_count} AI-parsed {aiParseMode==="transactions"?"transaction":"holding"}{_count!==1?"s":""}
                  </button>
                  ); })()}
                  <button onClick={()=>{setAiParseResult(null);setAiParseFile(null);setAiParseOverrides({});}}
                    style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                      borderRadius:8,color:T.textSecondary,padding:"10px 18px",
                      cursor:"pointer",fontSize:13}}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Quick Add Transaction ── */}
          <QuickAddTransaction
            isAdmin={isAdmin}
            holdings={holdings}
            loggedInClientId={loggedInClientId}
            setTransactions={setTransactions}
            addTransaction={addTransaction}
            postTxnsToHoldings={postTxnsToHoldings}
            showToast={showToast}
            isMobile={isMobile}
            T={T}
            inp={inp}
          />
          {/* Import tips */}
          <div style={card()}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>💡 Import Tips</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                ["Keep headers exactly as shown","Don't rename or reorder columns"],
                ["Use YYYY-MM-DD for dates","e.g. 2024-03-15, not 15/03/2024"],
                ["Use - for missing values","Don't leave cells blank"],
                ["Listed field: true or false","Lowercase, no quotes"],
                ["Unlisted assets: enter manual price","Use ✏ in Holdings tab to update later"],
                ["Indian MF: use SchemeCode","Find at mfapi.in or amfiindia.com"],
                ["Currencies: USD EUR GBP INR JPY CHF AED SGD","8 supported base currencies"],
                ["Google Sheets: publish as CSV first","File → Share → Publish to web → CSV"],
              ].map(([title,sub],i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"8px 10px",background:T.cardBg,borderRadius:7,alignItems:"flex-start"}}>
                  <span style={{color:"#2563EB",marginTop:1,flexShrink:0}}>✓</span>
                  <div><div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{title}</div><div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>{sub}</div></div>
                </div>
              ))}
            </div>
          </div>
        </React.Fragment>}

        {/* ── DATA SOURCES (admin only) ── */}
        {tab==="datasources" && isAdmin && <React.Fragment>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <div style={{fontSize:17,fontWeight:700}}>Data Sources & API Configuration</div>
            <span style={{background:"rgba(239,68,68,0.15)",border:"1px solid #A32D2D",borderRadius:5,padding:"3px 10px",fontSize:11,color:"#A32D2D",fontWeight:600}}>ADMIN ONLY</span>
            <button onClick={exportAllHoldingsCSV}
              title={`Export all ${hwWithOwner.length} holdings across all clients to CSV`}
              style={{marginLeft:"auto",background:"rgba(22,163,74,0.08)",
                border:"1px solid rgba(22,163,74,0.3)",borderRadius:7,
                color:"#1D9E75",padding:"6px 14px",cursor:"pointer",
                fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
              ⬇ Export All Holdings CSV
              <span style={{fontSize:10,color:"rgba(22,163,74,0.7)"}}>({hwWithOwner.length})</span>
            </button>
          </div>

          {/* ── CLIENT NOTIFICATION ALERTS ── */}
          {(function(){
            const ALERT_TRIGGERS = [
              {key:"portDrop5",    label:"Portfolio drops −5% in a day",       icon:"📉",severity:"high",   channel:"both"},
              {key:"portDrop10",   label:"Portfolio drops −10% in a month",     icon:"🔴",severity:"high",   channel:"both"},
              {key:"singleDrop10", label:"Any holding drops −10% in a day",     icon:"⚠️",severity:"high",   channel:"both"},
              {key:"rebalDrift5",  label:"Asset class drifts >5% from target",  icon:"⚖️",severity:"medium", channel:"email"},
              {key:"goalClose",    label:"Goal milestone within 90 days",       icon:"🎯",severity:"medium", channel:"both"},
              {key:"priceTarget",  label:"Watchlist price target hit",           icon:"📍",severity:"medium", channel:"both"},
              {key:"mfNav",        label:"MF NAV daily digest",                  icon:"📊",severity:"low",    channel:"email"},
              {key:"monthlyRpt",   label:"Monthly performance report",           icon:"📋",severity:"low",    channel:"email"},
            ];
            const sevCol = s => s==="high"?"#A32D2D":s==="medium"?"#D97706":"#78716C";
            return (
              <div style={{...tCard(),marginBottom:20,borderColor:`${T.brand}33`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:16}}>🔔</span>
                  <span style={{fontSize:14,fontWeight:700}}>Client Notification Alerts</span>
                </div>
                <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                  Configure trigger conditions and delivery channels per client.
                  Requires <code style={{background:T.cardBg,padding:"1px 4px",borderRadius:3}}>TWILIO_WHATSAPP_SID</code> and <code style={{background:T.cardBg,padding:"1px 4px",borderRadius:3}}>SENDGRID_API_KEY</code> in Vercel env vars → <code style={{background:T.cardBg,padding:"1px 4px",borderRadius:3}}>/api/notify</code> webhook.
                </div>

                {/* Client contact details */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Contact Details per Client</div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
                      <thead><tr>{["Client","📱 WhatsApp","✉️ Email","Channels"].map(h=>(
                        <th key={h} style={tTh()}>{h}</th>
                      ))}</tr></thead>
                      <tbody>
                        {clients.slice(0,8).map((c,i)=>{
                          const pr = profileData?.[c.id]||{};
                          return (
                            <tr key={c.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                              <td style={tTd({fontWeight:600})}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <div style={{width:22,height:22,borderRadius:5,flexShrink:0,
                                    background:ROLE_COLORS[c.role]||T.brand,fontSize:9,
                                    fontWeight:700,color:"white",display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {c.avatar||c.name[0]}
                                  </div>
                                  <span style={{fontSize:12}}>{c.name}</span>
                                </div>
                              </td>
                              <td style={tTd()}>
                                <input placeholder="+91 98765 43210" defaultValue={pr.whatsapp||""}
                                  onBlur={e=>setProfileData(p=>({...p,[c.id]:{...(p[c.id]||{}),whatsapp:e.target.value}}))}
                                  style={{...inp(),width:130,fontSize:11,padding:"3px 7px"}}/>
                              </td>
                              <td style={tTd()}>
                                <input placeholder="email@domain.com" defaultValue={pr.email||""}
                                  onBlur={e=>setProfileData(p=>({...p,[c.id]:{...(p[c.id]||{}),email:e.target.value}}))}
                                  style={{...inp(),width:150,fontSize:11,padding:"3px 7px"}}/>
                              </td>
                              <td style={tTd()}>
                                <div style={{display:"flex",gap:5}}>
                                  {[{k:"wa",icon:"📱",title:"WhatsApp"},{k:"em",icon:"✉️",title:"Email"}].map(({k,icon,title})=>{
                                    const on = pr[`alert_${k}`]!==false;
                                    return (
                                      <button key={k} title={title}
                                        onClick={()=>setProfileData(p=>({...p,[c.id]:{...(p[c.id]||{}), [`alert_${k}`]:!on}}))}
                                        style={{width:26,height:26,borderRadius:6,fontSize:12,cursor:"pointer",
                                          border:`1px solid ${on?T.brand:T.cardBorder}`,
                                          background:on?`${T.brand}15`:"transparent"}}>
                                        {icon}
                                      </button>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Trigger toggles */}
                <div style={{fontSize:12,fontWeight:600,marginBottom:10}}>Alert Triggers</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:8}}>
                  {ALERT_TRIGGERS.map(({key,label,icon,severity,channel})=>{
                    const on = (alertConfig||{})[key]!==false;
                    const sc = sevCol(severity);
                    return (
                      <div key={key} onClick={()=>setAlertConfig(p=>{
                          const n={...(p||{}),[key]:!on};
                          try{localStorage.setItem("fw_alerts_v1",JSON.stringify(n));}catch{}
                          return n;
                        })}
                        style={{display:"flex",alignItems:"center",gap:10,
                          padding:"9px 12px",borderRadius:8,cursor:"pointer",
                          background:on?`${sc}06`:"transparent",
                          border:`1px solid ${on?sc+"22":T.cardBorder}`,
                          transition:"all 0.15s"}}>
                        <div style={{width:30,height:18,borderRadius:9,flexShrink:0,
                          background:on?sc:T.badgeBg,position:"relative",transition:"background 0.2s"}}>
                          <div style={{position:"absolute",top:2,left:on?12:2,
                            width:14,height:14,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
                        </div>
                        <span style={{fontSize:12}}>{icon}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:11,fontWeight:on?600:400,color:on?T.textPrimary:T.textMuted}}>{label}</div>
                          <div style={{fontSize:9,color:T.textMuted}}>
                            {channel==="both"?"📱 WhatsApp + ✉️ Email":channel==="email"?"✉️ Email only":"📱 WhatsApp"}
                            <span style={{marginLeft:8,color:sc,fontWeight:600,textTransform:"uppercase",fontSize:8}}>{severity}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Audit Log ── */}
          {isAdmin && (
            <div style={{...tCard(),marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                marginBottom:14,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700}}>Admin Audit Log</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                    {auditLog.length > 0 ? `${auditLog.length} action${auditLog.length!==1?"s":""} this session` : "No actions recorded yet this session"}
                  </div>
                </div>
                {auditLog.length > 0 && (
                  <button onClick={()=>setAuditLog([])}
                    style={{fontSize:11,color:T.textMuted,background:T.borderSubtle,
                      border:`1px solid ${T.cardBorder}`,borderRadius:6,
                      padding:"3px 10px",cursor:"pointer"}}>
                    Clear log
                  </button>
                )}
              </div>
              {auditLog.length === 0 ? (
                <div style={{textAlign:"center",padding:"24px 0",color:T.textMuted,fontSize:12}}>
                  Actions like adding holdings, imports, edits, and deletions will appear here.
                </div>
              ) : (
                <div style={{overflowX:"auto",maxHeight:320,overflowY:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead style={{position:"sticky",top:0,zIndex:2,background:T.thBg}}>
                      <tr>
                        {["Time","Action","Detail"].map(h=>(
                          <th key={h} style={{...tTh(),fontSize:10,padding:"7px 12px",textAlign:"left"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.slice(0,100).map((e,i)=>{
                        const actionColors = {
                          DELETE_HOLDING:"#A32D2D",DELETE_LOT:"#A32D2D",BULK_DELETE:"#A32D2D",
                          IMPORT_CAS:"#1D9E75",ASSIGN_OWNER:"#2563EB",EDIT_HOLDING:"#D97706",
                        };
                        const col = actionColors[e.action]||T.textMuted;
                        return (
                          <tr key={e.id} style={{background:i%2===0?"transparent":T.tableStripe,
                            borderBottom:`1px solid ${T.borderSubtle}`}}>
                            <td style={{padding:"6px 12px",color:T.textMuted,whiteSpace:"nowrap",fontSize:10}}>
                              {new Date(e.ts).toLocaleTimeString()}
                            </td>
                            <td style={{padding:"6px 12px",whiteSpace:"nowrap"}}>
                              <span style={{fontSize:10,fontWeight:700,color:col,
                                background:`${col}15`,padding:"2px 7px",borderRadius:4,
                                border:`1px solid ${col}30`}}>
                                {e.action.replace(/_/g," ")}
                              </span>
                            </td>
                            <td style={{padding:"6px 12px",color:T.textSecondary,
                              maxWidth:340,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                              title={e.detail}>
                              {e.detail}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Admin Users Management ── */}
          {(function(){
            const adminLogins = Object.entries(clientLogins)
              .filter(([k]) => k === "__admin__" || k.startsWith("__admin__"));
            // If no admin in DB yet, synthesise the default so it shows
            const rows = adminLogins.length > 0 ? adminLogins : [["__admin__", { loginId:"kshitij.lodha", active:true }]];
            // Form state lives at component level (hooks rule) — setters available via closure

            const saveNewAdmin = async () => {
              setAddAdminErr("");
              if (!newAdminForm.loginId.trim()) { setAddAdminErr("Login ID is required."); return; }
              if (!newAdminForm.displayName.trim()) { setAddAdminErr("Display name is required."); return; }
              if (newAdminForm.password.length < 8) { setAddAdminErr("Password must be at least 8 characters."); return; }
              if (newAdminForm.password !== newAdminForm.confirm) { setAddAdminErr("Passwords do not match."); return; }
              // Check uniqueness
              const exists = Object.values(clientLogins).some(v => v?.loginId === newAdminForm.loginId.trim());
              if (exists) { setAddAdminErr("That login ID is already taken."); return; }
              const key = "__admin__" + newAdminForm.loginId.trim();
              const adminHash = await makePasswordHash(newAdminForm.password);
              const entry = { loginId: newAdminForm.loginId.trim(), displayName: newAdminForm.displayName.trim(), password: adminHash, active: true };
              upsertClientLogin(key, entry).catch(e => console.error("[Supabase] upsertAdminLogin:", e.message));
              setClientLogins(p => ({ ...p, [key]: entry }));
              setNewAdminForm({ loginId:"", displayName:"", password:"", confirm:"" });
              setAddAdminOpen(false);
              showToast("Admin user created: " + entry.loginId, "#1D9E75");
            };

            return (
              <div style={{...tCard(), marginBottom:20, borderColor:"rgba(239,68,68,0.25)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>🛡️</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700}}>Admin Users</div>
                      <div style={{fontSize:11,color:T.textSecondary}}>All accounts with full admin access</div>
                    </div>
                  </div>
                  <button onClick={()=>{setAddAdminOpen(p=>!p);setAddAdminErr("");}}
                    style={{background:"#A32D2D",border:"none",borderRadius:7,
                      color:"white",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Admin
                  </button>
                </div>

                {/* Existing admins table */}
                <table style={{width:"100%",borderCollapse:"collapse",marginBottom:addAdminOpen?14:0}}>
                  <thead><tr style={{background:T.thBg}}>
                    {["Login ID","Display Name","Status","Actions"].map(h=>(
                      <th key={h} style={{...tTh(),textAlign:"left",fontSize:11}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.map(([key, cred]) => (
                      <tr key={key} style={{borderBottom:`1px solid ${T.tdBorder}`}}>
                        <td style={tTd({fontFamily:"'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",fontSize:12,fontWeight:600})}>
                          {cred?.loginId || "kshitij.lodha"}
                          {key === "__admin__" && (
                            <span style={{marginLeft:6,fontSize:9,background:"rgba(0,121,193,0.12)",color:T.brand,
                              padding:"1px 5px",borderRadius:3,fontWeight:700}}>PRIMARY</span>
                          )}
                        </td>
                        <td style={tTd({fontSize:12})}>{cred?.displayName || "Kshitij Lodha"}</td>
                        <td style={tTd()}>
                          <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:4,
                            background:cred?.active!==false?"rgba(22,163,74,0.10)":"rgba(220,38,38,0.10)",
                            color:cred?.active!==false?"#1D9E75":"#A32D2D"}}>
                            {cred?.active!==false?"Active":"Inactive"}
                          </span>
                        </td>
                        <td style={tTd()}>
                          <div style={{display:"flex",gap:6}}>
                            {/* Toggle active */}
                            {key !== "__admin__" && (
                              <button onClick={()=>{
                                const updated = {...cred, active: cred?.active===false};
                                upsertClientLogin(key, updated).catch(()=>{});
                                setClientLogins(p=>({...p,[key]:updated}));
                              }} style={{fontSize:10,padding:"2px 8px",borderRadius:4,
                                border:`1px solid ${cred?.active!==false?"#A32D2D":"#1D9E75"}`,
                                background:cred?.active!==false?"rgba(220,38,38,0.08)":"rgba(22,163,74,0.08)",
                                color:cred?.active!==false?"#A32D2D":"#1D9E75",cursor:"pointer"}}>
                                {cred?.active!==false?"Deactivate":"Activate"}
                              </button>
                            )}
                            {key === "__admin__" && (
                              <span style={{fontSize:10,color:T.textMuted,fontStyle:"italic"}}>Primary — cannot deactivate</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Add new admin form */}
                {addAdminOpen && (
                  <div style={{borderTop:`1px solid ${T.cardBorder}`,paddingTop:14}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#A32D2D",marginBottom:12}}>New Admin Account</div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:10}}>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Login ID <span style={{color:"#A32D2D"}}>*</span></div>
                        <input style={inp()} value={newAdminForm.loginId} placeholder="e.g. priya.sharma"
                          onChange={e=>setNewAdminForm(p=>({...p,loginId:e.target.value.toLowerCase().replace(/\s/g,".")}))}/>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Display Name <span style={{color:"#A32D2D"}}>*</span></div>
                        <input style={inp()} value={newAdminForm.displayName} placeholder="e.g. Priya Sharma"
                          onChange={e=>setNewAdminForm(p=>({...p,displayName:e.target.value}))}/>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Password <span style={{color:"#A32D2D"}}>*</span></div>
                        <input type="password" style={inp()} value={newAdminForm.password} placeholder="Min 8 chars"
                          onChange={e=>setNewAdminForm(p=>({...p,password:e.target.value}))}/>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Confirm Password <span style={{color:"#A32D2D"}}>*</span></div>
                        <input type="password" style={inp()} value={newAdminForm.confirm} placeholder="Re-enter password"
                          onChange={e=>setNewAdminForm(p=>({...p,confirm:e.target.value}))}/>
                      </div>
                    </div>
                    {addAdminErr && (
                      <div style={{fontSize:12,color:"#A32D2D",marginBottom:10,padding:"8px 12px",
                        background:"rgba(220,38,38,0.07)",borderRadius:7,border:"1px solid rgba(220,38,38,0.2)"}}>
                        ⚠ {addAdminErr}
                      </div>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={saveNewAdmin}
                        style={{background:"#A32D2D",border:"none",borderRadius:7,
                          color:"white",padding:"8px 20px",cursor:"pointer",fontSize:12,fontWeight:700}}>
                        Create Admin
                      </button>
                      <button onClick={()=>{setAddAdminOpen(false);setAddAdminErr("");setNewAdminForm({loginId:"",displayName:"",password:"",confirm:""}); }}
                        style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:7,
                          color:T.textSecondary,padding:"8px 16px",cursor:"pointer",fontSize:12}}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Provider status */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:14,marginBottom:16}}>
            {[
              {name:"Google Finance",role:"Primary — All Listed Assets",note:"No key needed · CORS proxy required",status:dsConfig.googleFinance?"Active":"Disabled",color:dsConfig.googleFinance?"#1D9E75":T.textSecondary,detail:"Scrapes public Google Finance quote pages via CORS proxy. Covers US/UK/India/EU/Asia equities, ETFs, crypto, FX. No API key needed. Requires a working CORS proxy.",icon:"🟢"},
              {name:"Frankfurter",role:"FX Rates (fallback)",note:"No key needed",status:"Active",color:"#1D9E75",detail:"Covers 30+ currencies. Free, no rate limit. Used as FX fallback when Google Finance is disabled."},
              {name:"Alpha Vantage",role:"Benchmarks / History",note:`Key: ${dsConfig.alphaVantageKey.slice(0,6)}…`,status:"Active",color:"#D97706",detail:"5 calls/min free tier. Used for historical benchmark data and CAGR calculations."},
              {name:"Finnhub",role:"Global Equities (fallback)",note:dsConfig.finnhubKey?"Key set":"No key",status:dsConfig.finnhubKey?"Active":"Inactive",color:dsConfig.finnhubKey?"#1D9E75":"#A32D2D",detail:"60 calls/min free. US + International equities. Used when Google Finance is unavailable."},
              {name:"Twelve Data",role:"India NSE/BSE (fallback)",note:`Key: ${dsConfig.twelveDataKey.slice(0,6)}…`,status:"Active",color:"#2563EB",detail:"~800 calls/day free tier. NSE & BSE stocks. Fallback for Indian equities."},
              {name:"MFapi.in",role:"Indian Mutual Funds",note:"No key needed",status:"Active",color:"#1D9E75",detail:"Free, no auth. NAV data for all AMFI-registered schemes."},
            ].map(p=>(
              <div key={p.name} style={{background:T.cardBg,borderRadius:8,padding:14,border:`1px solid ${p.color}33`}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontWeight:700,fontSize:13}}>{p.name}</span>
                  <span style={{fontSize:11,color:p.color,fontWeight:600}}>● {p.status}</span>
                </div>
                <div style={{fontSize:11,color:"#D97706",marginBottom:4}}>{p.role}</div>
                <div style={{fontSize:11,color:T.textSecondary,marginBottom:6}}>{p.note}</div>
                <div style={{fontSize:10,color:T.textMuted,lineHeight:1.5}}>{p.detail}</div>
              </div>
            ))}
          </div>

          {/* Google Finance config */}
          <div style={{...tCard(),marginBottom:16,borderColor:"#1D9E75"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#1D9E75"}}>🟢 Google Finance Integration</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:3}}>Primary live price source for all listed assets. No API key required — uses public quote pages via CORS proxy.</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:12,color:T.textSecondary}}>{dsConfig.googleFinance?"Enabled":"Disabled"}</span>
                <div
                  onClick={()=>{ const next=!dsConfig.googleFinance; setDsConfig(p=>({...p,googleFinance:next})); CONFIG.googleFinance=next; }}
                  style={{width:44,height:24,borderRadius:12,background:dsConfig.googleFinance?"#1D9E75":T.badgeBg,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:dsConfig.googleFinance?20:3,width:18,height:18,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
                </div>
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"flex-end",marginBottom:10}}>
              <div>
                <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>Primary CORS Proxy URL</div>
                <input style={inp()} value={dsConfig.corsProxy} onChange={e=>setDsConfig(p=>({...p,corsProxy:e.target.value}))} placeholder="https://corsproxy.io/?"/>
                <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>The proxy is prepended to each Google Finance URL to bypass browser CORS restrictions. Must accept arbitrary URLs.</div>
              </div>
              <button onClick={()=>{ CONFIG.corsProxies[0]=dsConfig.corsProxy; clearCache(); showToast("✓ CORS proxy updated. Cache cleared."); }} style={{background:"#14532D",border:"1px solid #1D9E75",borderRadius:6,color:"#1D9E75",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>Apply Proxy</button>
            </div>

            <div style={{background:T.borderSubtle,borderRadius:8,padding:"12px 14px"}}>
              <div style={{fontSize:11,color:T.textSecondary,fontWeight:600,marginBottom:8}}>How Google Finance scraping works</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 20px",fontSize:11,color:T.textSecondary,lineHeight:1.7}}>
                <div>● Fetches <code style={{color:"#2563EB"}}>google.com/finance/quote/TICKER</code> via proxy</div>
                <div>● Extracts price from <code style={{color:"#2563EB"}}>data-last-price</code> attribute</div>
                <div>● Falls back to JSON-LD embedded script data</div>
                <div>● Falls back to Finnhub / Twelve Data if GF fails</div>
                <div>● NSE: <code style={{color:"#D97706"}}>NSE:RELIANCE</code> · US: <code style={{color:"#D97706"}}>AAPL</code></div>
                <div>● Crypto: <code style={{color:"#D97706"}}>CRYPTO:BTCUSD</code> · FX: <code style={{color:"#D97706"}}>CURRENCY:USDINR</code></div>
                <div>● Cached for {CONFIG.cacheTTL/1000}s to minimise proxy calls</div>
                <div>● Proxy failover: tries corsproxy.io then allorigins.win</div>
              </div>
              <div style={{marginTop:10,padding:"8px 10px",background:"rgba(251,191,36,0.08)",borderRadius:6,fontSize:11,color:"#D97706"}}>
                ⚠ Google Finance does not provide a public API — this integration uses HTML scraping which may break if Google changes their page structure. Finnhub and Twelve Data serve as automatic fallbacks.
              </div>
            </div>
          </div>

          {/* Key management */}
          <div style={{...tCard(),marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:16}}>API Key Management</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
              {[
                {label:"Alpha Vantage API Key",field:"alphaVantageKey",placeholder:"Enter Alpha Vantage key",note:"Rate: 5 req/min / 500 req/day (free). Used for benchmark history."},
                {label:"Finnhub API Key",field:"finnhubKey",placeholder:"Enter Finnhub key (get at finnhub.io)",note:"Rate: 60 req/min free. Fallback for global equity quotes."},
                {label:"Twelve Data API Key",field:"twelveDataKey",placeholder:"Enter Twelve Data key",note:"Rate: ~800 req/day free. Fallback for NSE/BSE Indian stocks."},
              ].map(({label,field,placeholder,note})=>(
                <div key={field}>
                  <div style={{fontSize:12,color:T.textSecondary,marginBottom:6,fontWeight:600}}>{label}</div>
                  <input style={inp()} value={dsConfig[field]} onChange={e=>setDsConfig(p=>({...p,[field]:e.target.value}))} placeholder={placeholder} type="password"/>
                  <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{note}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop:14,display:"flex",gap:10}}>
              <button onClick={()=>{ CONFIG.alphaVantageKey=dsConfig.alphaVantageKey; CONFIG.finnhubKey=dsConfig.finnhubKey; CONFIG.twelveDataKey=dsConfig.twelveDataKey; clearCache(); showToast("✓ Keys saved. Cache cleared."); }} style={{background:"#1E40AF",border:"none",borderRadius:6,color:"white",padding:"8px 20px",cursor:"pointer",fontWeight:600,fontSize:13}}>Save Keys & Clear Cache</button>
              <button onClick={()=>{ clearCache(); refresh(true); showToast("🔄 Cache invalidated. Full price refresh triggered."); }} style={{background:"rgba(220,38,38,0.1)",border:"1px solid #A32D2D",borderRadius:6,color:"#A32D2D",padding:"8px 20px",cursor:"pointer",fontWeight:600,fontSize:13}}>🔄 Force Global Refresh</button>
            </div>
          </div>

          {/* Provider preference */}
          <div style={card()}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:14}}>Provider Routing Config</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:14}}>
              {[
                {label:"FX Conversion",field:"fxProvider",options:["GoogleFinance","Frankfurter","AlphaVantage"]},
                {label:"Global Equities",field:"globalProvider",options:["GoogleFinance","Finnhub","AlphaVantage"]},
                {label:"India NSE/BSE",field:"indiaProvider",options:["GoogleFinance","TwelveData","Finnhub"]},
                {label:"Indian Mutual Funds",field:"mfProvider",options:["MFapi","TwelveData"]},
              ].map(({label,field,options})=>(
                <div key={field}>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:6}}>{label}</div>
                  <select style={inp()} value={dsConfig[field]} onChange={e=>setDsConfig(p=>({...p,[field]:e.target.value}))}>
                    {options.map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{marginTop:16,padding:"10px 14px",background:T.cardBg,borderRadius:8,fontSize:11,color:T.textMuted}}>
              <strong style={{color:T.textSecondary}}>Provider cascade:</strong> Google Finance is tried first for all listed assets. On failure, the configured fallback provider is used. <code style={{color:"#2563EB"}}>clearCache()</code> wipes all entries (TTL: {CONFIG.cacheTTL/1000}s). Two CORS proxies are attempted in order before declaring Google Finance unavailable.
            </div>
          </div>

          {/* ── CLIENT CREDENTIALS MANAGER ── */}
          <div style={{...tCard(),marginTop:8,borderColor:"#7C3AED"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#9333EA"}}>👥 Client Login Management</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:3}}>View, create and reset client portal passwords. Changes apply immediately to the session.</div>
              </div>
            </div>

            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:760}}>
              <thead>
                <tr>{["Client","Family","Role","Login ID","Last Login","Status","⚡ Risk Access","Password","Actions"].map(h=>(
                  <th key={h} style={tTh()}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {clients.map((c,i)=>{
                  const cred = clientLogins[c.id] || {};
                  const fam  = families.find(f=>f.id===c.familyId);
                  const isEditing = editingCred[c.id];
                  return (
                    <tr key={c.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                      <td style={tTd({fontWeight:600})}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:28,height:28,borderRadius:6,background:ROLE_COLORS[c.role]||"#A8A29E",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:T.pageBg,flexShrink:0}}>{c.avatar||(c.name||"?").slice(0,2).toUpperCase()}</div>
                          {c.name}
                        </div>
                      </td>
                      <td style={tTd({fontSize:11})}>
                        <span style={{color:fam?.color||"#78716C",fontSize:11}}>{fam?.emoji} {fam?.name}</span>
                      </td>
                      <td style={tTd({fontSize:11})}><span style={{color:ROLE_COLORS[c.role]||"#78716C"}}>{c.role}</span></td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12})}>
                        {isEditing
                          ? <input value={editingCred[c.id].loginId}
                              onChange={e=>setEditingCred(p=>({...p,[c.id]:{...p[c.id],loginId:e.target.value}}))}
                              style={{...inp(),width:140,fontSize:12,padding:"4px 8px"}}/>
                          : <span style={{color:T.textSecondary}}>{cred.loginId||<span style={{color:T.textLight}}>—</span>}</span>
                        }
                      </td>
                      <td style={tTd({fontSize:11,color:T.textSecondary})}>{cred.lastLogin||"—"}</td>
                      <td style={tTd()}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div onClick={()=>{
                            const newActive = !(clientLogins[c.id]?.active !== false);
                            upsertClientLogin(c.id, {...(clientLogins[c.id]||{}), active: newActive}).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                            setClientLogins(p=>({...p,[c.id]:{...p[c.id],active:newActive}}));
                          }}
                            style={{width:36,height:20,borderRadius:10,background:(cred.active!==false)?"#1D9E75":T.badgeBg,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                            <div style={{position:"absolute",top:2,left:(cred.active!==false)?16:2,width:16,height:16,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
                          </div>
                          <span style={{fontSize:11,color:(cred.active!==false)?"#1D9E75":"#A32D2D"}}>{(cred.active!==false)?"Active":"Inactive"}</span>
                        </div>
                      </td>
                      {/* Risk Sub-tab Access — per-tab toggles */}
                      <td style={tTd()}>
                        <div style={{display:"flex",flexDirection:"column",gap:5}}>
                          {[
                            {key:"overview",     label:"Overview",      color:"#A32D2D"},
                            {key:"concentration",label:"Concentration",  color:"#D97706"},
                            {key:"stress",       label:"Stress Tests",   color:"#7C3AED"},
                            {key:"correlation",  label:"Correlation",    color:"#2563EB"},
                          ].map(({key,label,color})=>{
                            const tabs = cred.riskTabs || {};
                            const on   = !!tabs[key];
                            return (
                              <div key={key} style={{display:"flex",alignItems:"center",gap:5}}
                                onClick={()=>{
                                  const newTabs = {...(cred.riskTabs||{}), [key]: !on};
                                  upsertClientLogin(c.id, {...(clientLogins[c.id]||{}), riskTabs:newTabs}).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                                  setClientLogins(p=>({...p,[c.id]:{...p[c.id],riskTabs:newTabs}}));
                                }}
                                style={{cursor:"pointer"}}>
                                <div style={{width:28,height:16,borderRadius:8,
                                  background:on?color:T.badgeBg,
                                  position:"relative",transition:"background 0.2s",flexShrink:0}}>
                                  <div style={{position:"absolute",top:2,left:on?12:2,
                                    width:12,height:12,borderRadius:"50%",
                                    background:"white",transition:"left 0.2s"}}/>
                                </div>
                                <span style={{fontSize:10,color:on?color:T.textMuted,minWidth:68}}>{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11})}>
                        {isEditing
                          ? <input value={editingCred[c.id].password}
                              onChange={e=>setEditingCred(p=>({...p,[c.id]:{...p[c.id],password:e.target.value}}))}
                              style={{...inp(),width:130,fontSize:12,padding:"4px 8px"}}
                              placeholder="New password"/>
                          : (
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              {revealPassIds.has(c.id) ? (
                                <span style={{fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,
                                  color:T.textPrimary,background:T.pageBg,
                                  padding:"3px 8px",borderRadius:4,
                                  border:`1px solid ${T.cardBorder}`,
                                  userSelect:"all",cursor:"text",maxWidth:160,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                                  title={revealedPlain[c.id] || (cred.password?.includes(":")?
                                    "Hashed — use Reset to get plaintext":"")}>
                                  {revealedPlain[c.id] || (cred.password?.includes(":")
                                    ? <span style={{color:T.textMuted,fontSize:10,fontStyle:"italic"}}>
                                        Hashed · Reset to reveal
                                      </span>
                                    : cred.password || "—")}
                                </span>
                              ) : (
                                <span style={{color:T.textLight,letterSpacing:"0.12em",fontSize:13}}>
                                  ••••••••
                                </span>
                              )}
                              <button
                                onClick={()=>toggleRevealPass(c.id)}
                                title={revealPassIds.has(c.id)?"Hide password":"Show password"}
                                style={{background:"transparent",border:"none",cursor:"pointer",
                                  padding:"2px 4px",fontSize:14,color:T.textMuted,
                                  borderRadius:4,lineHeight:1}}>
                                {revealPassIds.has(c.id) ? "🙈" : "👁"}
                              </button>
                            </div>
                          )
                        }
                      </td>
                      <td style={tTd()}>
                        <div style={{display:"flex",gap:6}}>
                          {isEditing ? (<React.Fragment>
                            <button onClick={()=>{
                              const ed=editingCred[c.id];
                              const loginIdTrimmed = (ed.loginId||"").trim();
                              // Validate credentials
                              if (!loginIdTrimmed) { showToast("⚠ Login ID cannot be empty","#D97706"); return; }
                              if (loginIdTrimmed.length < 3) { showToast("⚠ Login ID must be at least 3 characters","#D97706"); return; }
                              if (!/^[a-zA-Z0-9._@-]+$/.test(loginIdTrimmed)) { showToast("⚠ Login ID: only letters, numbers, dots, underscores allowed","#D97706"); return; }
                              if (ed.password && ed.password.length < 8) { showToast("⚠ Password must be at least 8 characters","#D97706"); return; }
                              // Check uniqueness (exclude current client)
                              const taken = Object.entries(clientLogins).some(([cid,v])=>cid!==c.id&&v?.loginId===loginIdTrimmed);
                              if (taken) { showToast("⚠ That Login ID is already taken","#D97706"); return; }
                              if(loginIdTrimmed){
                                const updated = {...(clientLogins[c.id]||{}), loginId:loginIdTrimmed, ...(ed.password?{password:ed.password}:{})};
                                upsertClientLogin(c.id, updated).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                                setClientLogins(p=>({...p,[c.id]:updated}));
                                setEditingCred(p=>{const n={...p};delete n[c.id];return n;});
                                showToast("✓ Credentials updated for "+c.name,"#1D9E75");
                              }
                            }} style={{background:"#14532D",border:"1px solid #1D9E75",borderRadius:5,color:"#1D9E75",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>Save</button>
                            <button onClick={()=>setEditingCred(p=>{const n={...p};delete n[c.id];return n;})}
                              style={{background:T.badgeBg,border:"none",borderRadius:5,color:T.textSecondary,padding:"4px 10px",cursor:"pointer",fontSize:11}}>Cancel</button>
                          </React.Fragment>) : (<React.Fragment>
                            {!cred.loginId && (
                              <button onClick={async()=>{
                                const defaultLogin = c.name.split(" ")[0].toLowerCase()+"."+c.name.split(" ").slice(-1)[0].toLowerCase();
                                const defaultPwd = c.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                                const hpwd2 = await makePasswordHash(defaultPwd);
                                const newCred = {loginId:defaultLogin, password:hpwd2, active:true, riskTabs:{overview:false,concentration:false,stress:false,correlation:false}};
                                upsertClientLogin(c.id, newCred).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                                setClientLogins(p=>({...p,[c.id]:newCred}));
                                // Store plaintext for admin reveal
                                setRevealedPlain(p=>({...p,[c.id]:defaultPwd}));
                                setRevealPassIds(p=>new Set([...p,c.id]));
                                setCredModal({name:c.name,loginId:defaultLogin,password:defaultPwd});
                              }} style={{background:"rgba(16,185,129,0.1)",border:"1px solid #10B98144",borderRadius:5,color:"#059669",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>+ Create Login</button>
                            )}
                            <button onClick={()=>setEditingCred(p=>({...p,[c.id]:{loginId:cred.loginId||"",password:cred.password||""}}))}
                              style={{background:"rgba(124,58,237,0.1)",border:"1px solid #7c3aed44",borderRadius:5,color:"#9333EA",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:600}}>{cred.loginId?"Edit":"Set"}</button>
                            <button onClick={async()=>{
                              const newPwd = c.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                              const hashed = await makePasswordHash(newPwd);
                              const updated = {...(clientLogins[c.id]||{}), password:hashed};
                              upsertClientLogin(c.id, updated).catch(err=>console.error('[Supabase] upsertLogin:',err.message));
                              setClientLogins(p=>({...p,[c.id]:updated}));
                              // Store plaintext so admin can reveal it
                              setRevealedPlain(p=>({...p,[c.id]:newPwd}));
                              setRevealPassIds(p=>new Set([...p,c.id])); // auto-reveal on reset
                              setCredModal({name:c.name,loginId:clientLogins[c.id]?.loginId||"",password:newPwd});
                            }} style={{background:"rgba(245,158,11,0.08)",border:"1px solid #D9770644",borderRadius:5,color:"#D97706",padding:"4px 10px",cursor:"pointer",fontSize:11}}>Reset</button>
                          </React.Fragment>)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>

            <div style={{marginTop:14,padding:"10px 14px",background:T.borderSubtle,borderRadius:8,fontSize:11,color:T.textMuted,display:"flex",gap:20}}>
              <span>🛡️ <strong style={{color:T.textSecondary}}>Security note:</strong> Passwords are hashed. Click 👁 to reveal — only shows plaintext for newly reset/created passwords. Use Reset to generate a new password and view it instantly.</span>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════
              🤖 AI PROVIDER SETTINGS
          ═══════════════════════════════════════════════ */}
          {(function(){
            const PROVIDERS = [
              { id:"anthropic", label:"Anthropic Claude", icon:"🔶", models:[
                { id:"claude-haiku-4-5-20251001",  label:"Claude Haiku 3.5 (Fast · Cheap)" },
                { id:"claude-sonnet-4-6",           label:"Claude Sonnet 4.6 (Smart · Balanced)" },
                { id:"claude-opus-4-6",             label:"Claude Opus 4.6 (Best · Expensive)" },
              ]},
              { id:"openai", label:"OpenAI", icon:"🟢", models:[
                { id:"gpt-4o-mini",  label:"GPT-4o Mini (Fast · Cheap)" },
                { id:"gpt-4o",       label:"GPT-4o (Smart · Balanced)" },
                { id:"o1-mini",      label:"o1 Mini (Reasoning)" },
              ]},
              { id:"gemini", label:"Google Gemini", icon:"🔵", models:[
                { id:"gemini-2.0-flash",        label:"Gemini 2.0 Flash (Latest · Fast)" },
                { id:"gemini-2.0-flash-lite",   label:"Gemini 2.0 Flash Lite (Cheapest)" },
                { id:"gemini-1.5-flash",        label:"Gemini 1.5 Flash (Stable · Fast)" },
                { id:"gemini-1.5-pro",          label:"Gemini 1.5 Pro (Smart · Balanced)" },
              ]},
            ];
            const cfg = aiConfig || AI_CONFIG_DEFAULT;
            const needsKey = (p) => p === "openai" || p === "gemini";
            return (
              <div style={{...tCard(), marginBottom:20, borderColor:`${T.brand}33`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                  <span style={{fontSize:18}}>🤖</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:700}}>AI Provider Settings</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                      Choose which AI powers the Portfolio Advisor and Research tab
                    </div>
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16}}>
                  {/* AI Advisor */}
                  <div style={{background:T.borderSubtle,borderRadius:10,padding:"14px 16px"}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:10,color:T.brand}}>
                      💬 AI Portfolio Advisor
                    </div>
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Provider</div>
                      <select value={cfg.advisorProvider}
                        onChange={e=>saveAiConfig({...cfg,advisorProvider:e.target.value,
                          advisorModel:PROVIDERS.find(p=>p.id===e.target.value)?.models[0]?.id||cfg.advisorModel})}
                        style={{...inp(),width:"100%"}}>
                        {PROVIDERS.map(p=><option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
                      </select>
                    </div>
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Model</div>
                      <select value={cfg.advisorModel}
                        onChange={e=>saveAiConfig({...cfg,advisorModel:e.target.value})}
                        style={{...inp(),width:"100%"}}>
                        {(PROVIDERS.find(p=>p.id===cfg.advisorProvider)?.models||[]).map(m=>
                          <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                    </div>
                    {needsKey(cfg.advisorProvider) && (
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                          {cfg.advisorProvider === "openai" ? "OpenAI API Key" : "Gemini API Key"}
                        </div>
                        <input type="password"
                          placeholder={cfg.advisorProvider === "openai" ? "sk-..." : "AIza..."}
                          value={cfg.advisorProvider === "openai" ? (cfg.openaiKey||"") : (cfg.geminiKey||"")}
                          onChange={e=>saveAiConfig({...cfg,
                            [cfg.advisorProvider==="openai"?"openaiKey":"geminiKey"]:e.target.value})}
                          style={{...inp(),width:"100%"}}/>
                        <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>
                          Stored locally + synced to your account. Never shared.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Research */}
                  <div style={{background:T.borderSubtle,borderRadius:10,padding:"14px 16px"}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:10,color:"#7C3AED"}}>
                      🔬 AI Research Tab
                    </div>
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Provider</div>
                      <select value={cfg.researchProvider}
                        onChange={e=>saveAiConfig({...cfg,researchProvider:e.target.value,
                          researchModel:PROVIDERS.find(p=>p.id===e.target.value)?.models[0]?.id||cfg.researchModel})}
                        style={{...inp(),width:"100%"}}>
                        {PROVIDERS.map(p=><option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
                      </select>
                    </div>
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Model</div>
                      <select value={cfg.researchModel}
                        onChange={e=>saveAiConfig({...cfg,researchModel:e.target.value})}
                        style={{...inp(),width:"100%"}}>
                        {(PROVIDERS.find(p=>p.id===cfg.researchProvider)?.models||[]).map(m=>
                          <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                    </div>
                    {needsKey(cfg.researchProvider) && cfg.researchProvider !== cfg.advisorProvider && (
                      <div>
                        <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>
                          {cfg.researchProvider === "openai" ? "OpenAI API Key" : "Gemini API Key"}
                        </div>
                        <input type="password"
                          placeholder={cfg.researchProvider === "openai" ? "sk-..." : "AIza..."}
                          value={cfg.researchProvider === "openai" ? (cfg.openaiKey||"") : (cfg.geminiKey||"")}
                          onChange={e=>saveAiConfig({...cfg,
                            [cfg.researchProvider==="openai"?"openaiKey":"geminiKey"]:e.target.value})}
                          style={{...inp(),width:"100%"}}/>
                      </div>
                    )}
                    {needsKey(cfg.researchProvider) && cfg.researchProvider === cfg.advisorProvider && (
                      <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic"}}>
                        Using same API key as Advisor above ✓
                      </div>
                    )}
                  </div>
                </div>

                {/* Cost estimate */}
                <div style={{marginTop:12,padding:"8px 12px",background:"rgba(22,163,74,0.06)",
                  border:"1px solid rgba(22,163,74,0.2)",borderRadius:8,fontSize:11,color:T.textSecondary}}>
                  💡 <strong>Cost guide:</strong> Gemini Flash ≈ $0.075/M tokens · Claude Haiku ≈ $0.80/M · GPT-4o Mini ≈ $0.15/M.
                  A typical advisor conversation uses ~2K tokens (~$0.002 with Gemini Flash).
                </div>
              </div>
            );
          })()}

        </React.Fragment>}

        {/* Guard: non-admin trying to access datasources */}
        {tab==="datasources" && !isAdmin && (
          <div style={{...tCard(),textAlign:"center",padding:60}}>
            <div style={{fontSize:40,marginBottom:16}}>🔒</div>
            <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Access Restricted</div>
            <div style={{fontSize:13,color:T.textSecondary}}>The Data Sources tab is available to admin users only. Please sign in with admin credentials to access it.</div>
          </div>
        )}


        {/* ══════════════════════════════════════════════
            NEWS FEED TAB
        ══════════════════════════════════════════════ */}
        {tab==="news" && (function(){ try {
          // Build ticker list from holdings for filtering
          const tickerOptions = [
            { key:"all",    label:"All News",      icon:"📰" },
            { key:"india",  label:"India Markets", icon:"🇮🇳" },
            { key:"global", label:"Global",        icon:"🌍" },
            { key:"macro",  label:"Macro / RBI",   icon:"🏛️" },
            ...hwWithOwner
              .filter(h => h.ticker && h.ticker !== "-" && !/^\d{5,6}$/.test(h.ticker))
              .reduce((acc, h) => {
                if (!acc.find(x => x.key === h.ticker)) {
                  acc.push({ key: h.ticker, label: h.name?.slice(0,20) || h.ticker, icon: "📈" });
                }
                return acc;
              }, [])
              .slice(0, 12),
          ];

          const timeAgo = (dateStr) => {
            if (!dateStr) return "";
            const diff = Date.now() - new Date(dateStr).getTime();
            const m = Math.floor(diff / 60000);
            if (m < 1)  return "just now";
            if (m < 60) return `${m}m ago`;
            const h = Math.floor(m / 60);
            if (h < 24) return `${h}h ago`;
            return `${Math.floor(h/24)}d ago`;
          };

          const sentimentColor = (title) => {
            const t = title.toLowerCase();
            const pos = ["surge","rally","gain","rise","jump","high","record","bull","up","positive","growth","profit","beat","strong"];
            const neg = ["fall","drop","crash","decline","loss","low","bear","down","negative","weak","miss","slump","cut","warn"];
            const posScore = pos.filter(w => t.includes(w)).length;
            const negScore = neg.filter(w => t.includes(w)).length;
            if (posScore > negScore) return { dot: "#1D9E75", bg: "rgba(22,163,74,0.07)" };
            if (negScore > posScore) return { dot: "#A32D2D", bg: "rgba(220,38,38,0.07)" };
            return { dot: T.textMuted, bg: "transparent" };
          };

          const sourceColor = (src) => ({
            "Economic Times": "#EA580C", "Yahoo Finance": "#7C3AED",
            "Reuters": "#1E40AF", "Moneycontrol": "#0891B2",
          }[src] || T.brand);

          return (
            <div style={{padding:isMobile?"12px":"20px 24px"}}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:T.textPrimary}}>📰 News Feed</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                    {newsLastFetch ? `Last updated ${timeAgo(newsLastFetch)}` : "Fetching latest news…"}
                    {" · "}{newsItems.length} articles
                  </div>
                </div>
                <button onClick={()=>fetchNews(newsFilter)} disabled={newsLoading}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"7px 16px",borderRadius:8,
                    border:`1px solid ${T.cardBorder}`,background:T.borderSubtle,cursor:"pointer",
                    fontSize:12,fontWeight:600,color:T.textPrimary,opacity:newsLoading?0.6:1}}>
                  {newsLoading ? "⏳ Loading…" : "↻ Refresh"}
                </button>
              </div>

              {/* Category / Ticker Filter Pills */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:18}}>
                {tickerOptions.map(opt => (
                  <button key={opt.key}
                    onClick={()=>{ setNewsFilter(opt.key); fetchNews(opt.key); }}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:20,
                      border:`1px solid ${newsFilter===opt.key ? T.brand : T.cardBorder}`,
                      background:newsFilter===opt.key ? T.brandLight : "transparent",
                      color:newsFilter===opt.key ? T.brand : T.textSecondary,
                      cursor:"pointer",fontSize:12,fontWeight:newsFilter===opt.key?700:400,
                      transition:"all 0.15s",whiteSpace:"nowrap"}}>
                    <span>{opt.icon}</span><span>{opt.label}</span>
                  </button>
                ))}
              </div>

              {/* News Grid */}
              {newsLoading ? (
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} style={{...tCard(),padding:16,borderRadius:12}}>
                      <div style={{height:12,background:T.borderSubtle,borderRadius:4,marginBottom:8,width:"30%",animation:"pulse 1.5s infinite"}}/>
                      <div style={{height:16,background:T.borderSubtle,borderRadius:4,marginBottom:6,animation:"pulse 1.5s infinite"}}/>
                      <div style={{height:12,background:T.borderSubtle,borderRadius:4,width:"80%",animation:"pulse 1.5s infinite"}}/>
                    </div>
                  ))}
                </div>
              ) : newsItems.length === 0 ? (
                <div style={{...tCard(),padding:"48px 24px",textAlign:"center",borderRadius:12}}>
                  <div style={{fontSize:40,marginBottom:12}}>📭</div>
                  <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>No news found</div>
                  <div style={{fontSize:13,color:T.textSecondary}}>Try a different filter or refresh</div>
                </div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                  {newsItems.map((item, i) => {
                    const s = sentimentColor(item.title);
                    const sc = sourceColor(item.source);
                    return (
                      <a key={i} href={item.url || item.link} target="_blank" rel="noopener noreferrer"
                        style={{textDecoration:"none",display:"block"}}>
                        <div style={{...tCard(),padding:16,borderRadius:12,background:s.bg||T.cardBg,
                          border:`1px solid ${T.cardBorder}`,cursor:"pointer",transition:"all 0.15s",
                          height:"100%",boxSizing:"border-box"}}
                          onMouseEnter={e=>e.currentTarget.style.borderColor=T.brand}
                          onMouseLeave={e=>e.currentTarget.style.borderColor=T.cardBorder}>
                          {/* Source + time row */}
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <div style={{width:6,height:6,borderRadius:"50%",background:s.dot,flexShrink:0}}/>
                              <span style={{fontSize:10,fontWeight:700,color:sc,textTransform:"uppercase",
                                letterSpacing:"0.05em"}}>{item.source}</span>
                              {item.category && (
                                <span style={{fontSize:10,background:T.borderSubtle,color:T.textMuted,
                                  borderRadius:4,padding:"1px 5px"}}>{item.category.slice(0,20)}</span>
                              )}
                            </div>
                            <span style={{fontSize:10,color:T.textMuted,flexShrink:0}}>{timeAgo(item.published || item.pubDate)}</span>
                          </div>
                          {/* Headline */}
                          <div style={{fontSize:13,fontWeight:600,color:T.textPrimary,lineHeight:1.45,
                            marginBottom:6,display:"-webkit-box",WebkitLineClamp:2,
                            WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                            {item.title}
                          </div>
                          {/* Description */}
                          {item.description && (
                            <div style={{fontSize:11,color:T.textSecondary,lineHeight:1.5,
                              display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                              {item.description}
                            </div>
                          )}
                          {/* Read more */}
                          <div style={{marginTop:8,fontSize:11,color:T.brand,fontWeight:600}}>
                            Read more →
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}

              {/* Disclaimer */}
              <div style={{marginTop:20,padding:"10px 14px",background:T.borderSubtle,borderRadius:8,
                fontSize:11,color:T.textMuted,textAlign:"center"}}>
                News sourced from Economic Times, Yahoo Finance, Reuters RSS feeds.
                For informational purposes only — not investment advice.
              </div>
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in news tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══════════════════════════════════════════════
            GOALS TAB
        ══════════════════════════════════════════════ */}
        {tab==="goals" && (function(){ try {
          // Scope options — admin sees all clients/families, client sees only themselves
          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see goals analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          const scopeOptions = isAdmin
            ? [
                { type:"all",    id:"all", label:"All Clients", icon:"👥", color:T.brand },
                ...families.map(f => ({ type:"family", id:f.id, label:f.name, icon:"👨‍👩‍👧", color:f.color||T.brand })),
                ...clients.map(c => ({ type:"client", id:c.id, label:c.name, icon:"👤", color:"#2563EB" })),
              ]
            : [{ type:"client", id:loggedInClientId, label:"My Goals", icon:"👤", color:T.brand }];

          // Derive active scope from global filters (viewAsClientId > selectedClients > selectedFamily)
          // This ensures goals tab stays in sync with the top entity filter
          const activeScopeOpt = (!isAdmin && loggedInClientId)
            ? { type:"client", id:loggedInClientId, label:"My Goals", icon:"👤", color:T.brand }
            : (isAdmin && viewAsClientId)
              ? scopeOptions.find(o => o.type==="client" && o.id===viewAsClientId) || scopeOptions[0]
              : selectedClients.size > 0
                ? scopeOptions.find(o => o.type==="client" && o.id===[...selectedClients].map(String)[0]) || scopeOptions[0]
                : selectedFamily !== "all"
                  ? scopeOptions.find(o => o.type==="family" && String(o.id)===String(selectedFamily)) || scopeOptions[0]
                  : (scopeOptions.find(o => o.type===goalScopeType && o.id===goalScopeId) || scopeOptions[0]);


          // Filter goals by scope
          const goals = activeScopeOpt.type === "all"
            ? allGoals
            : activeScopeOpt.type === "family"
              ? allGoals.filter(g => g.familyId === activeScopeOpt.id)
              : allGoals.filter(g => g.clientId === activeScopeOpt.id);

          // Portfolio value scoped to selected client/family
          const scopedHoldings = activeScopeOpt.type === "all"
            ? hwWithOwner
            : activeScopeOpt.type === "family"
              ? hwWithOwner.filter(h => {
                  const c = clients.find(cl => cl.id === h.clientId);
                  return c?.familyId === activeScopeOpt.id;
                })
              : hwWithOwner.filter(h => h.clientId === activeScopeOpt.id);

          const portfolioValueUSD = scopedHoldings.reduce((s,h) => s + (h.valueUSD||0), 0);

          const toBase = (amt, currency) => {
            const r = fxRates[currency] || FX_RATES_DEFAULT[currency] || 1;
            const br = fxRates[dc] || FX_RATES_DEFAULT[dc] || 1;
            return amt / r * br;
          };

          const calcGoalMetrics = (goal) => {
            const now = new Date();
            const target = new Date(goal.targetDate || (now.getFullYear()+10)+"-01-01");
            const yearsLeft  = Math.max(0, (target - now) / (365.25*24*3600*1000));
            const monthsLeft = Math.max(0, yearsLeft * 12);
            const targetInBase  = toBase(goal.targetAmount || 0, goal.currency || dc);
            // Use the goal's own clientId portfolio if available, else scoped
            const goalHoldings = (goal.clientId
              ? hwWithOwner.filter(h => h.clientId === goal.clientId)
              : scopedHoldings
            ).filter(h => !goal.linkedClass || goal.linkedClass==="portfolio" || h.class===goal.linkedClass);
            const goalPortfolioUSD = goalHoldings.reduce((s,h) => s+(h.valueUSD||0), 0);
            const currentInBase = (fxRates[dc]||FX_RATES_DEFAULT[dc]||1) * goalPortfolioUSD / 1
              * (goal.portfolioAlloc || 0) / 100;
            // ── Inflation-adjusted target ──
            // Backfill legacy goals (no inflationRate) with 6% per user preference.
            // targetIsToday=true: user entered today's-money; we gross up to future value.
            // targetIsToday=false: user entered future amount directly; use as-is.
            const inflPct = ((goal.inflationRate ?? 6)) / 100;
            const isToday = goal.targetIsToday !== false;
            const futureTargetInBase = isToday
              ? targetInBase * Math.pow(1 + inflPct, yearsLeft)
              : targetInBase;
            // Today's-money equivalent (for progress bar — both sides in today's money)
            const todayEquivTarget = isToday
              ? targetInBase
              : (yearsLeft > 0 ? targetInBase / Math.pow(1 + inflPct, yearsLeft) : targetInBase);
            // Gap = future shortfall AFTER current portfolio compounds at expectedReturn.
            // More accurate than naive (target - current) since existing money grows too.
            const futureCurrent = currentInBase * Math.pow(1 + (goal.expectedReturn||10)/100, yearsLeft);
            const gap = Math.max(0, futureTargetInBase - futureCurrent);
            const r = (goal.expectedReturn || 10) / 100 / 12;
            const n = monthsLeft;
            const sipNeeded = gap > 0 && n > 0 ? gap * r / (Math.pow(1+r,n)-1) : 0;
            // Progress: today's portfolio vs today's-money equivalent of target
            const progress  = todayEquivTarget > 0 ? Math.min(100, currentInBase/todayEquivTarget*100) : 0;
            // Real return after inflation (Fisher equation)
            const realReturn = (((1 + (goal.expectedReturn||10)/100) / (1 + inflPct)) - 1) * 100;
            return { yearsLeft, monthsLeft, targetInBase, currentInBase, gap, sipNeeded, progress,
              futureTargetInBase, todayEquivTarget, inflationRate: inflPct*100, isToday, realReturn };
          };

          const saveGoals = (updated) => saveAllGoals(updated);

          // ── Goal Card ──
          const GoalCard = ({ goal, onEdit, onDelete }) => {
            const gtype = GOAL_TYPES.find(t=>t.id===goal.type) || GOAL_TYPES[5];
            const m = calcGoalMetrics(goal);
            const isOnTrack = m.progress >= 50 || m.yearsLeft > 5;
            const notLinked  = !goal.portfolioAlloc || goal.portfolioAlloc === 0;
            const circ = 2 * Math.PI * 34;
            const dash = circ * (1 - m.progress / 100);
            const ownerClient = clients.find(c => c.id === goal.clientId);
            const ownerFamily = families.find(f => f.id === goal.familyId);
            const ownerLabel  = ownerClient?.name || ownerFamily?.name || "—";

            return (
              <div style={{...tCard(),borderRadius:14,padding:0,overflow:"hidden",border:`1px solid ${T.cardBorder}`}}>
                <div style={{height:4,background:`linear-gradient(90deg,${gtype.color},${gtype.color}88)`}}/>
                <div style={{padding:18}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                      <div style={{width:38,height:38,borderRadius:10,background:`${gtype.color}18`,
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                        {gtype.icon}
                      </div>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:700,color:T.textPrimary,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{goal.name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2,flexWrap:"wrap"}}>
                          <span style={{fontSize:10,color:T.textSecondary}}>{gtype.label}</span>
                          {isAdmin && ownerLabel !== "—" && (
                            <span style={{fontSize:10,background:`${gtype.color}18`,color:gtype.color,
                              borderRadius:4,padding:"1px 6px",fontWeight:600}}>👤 {ownerLabel}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:8}}>
                      <button onClick={onEdit} style={{background:"none",border:`1px solid ${T.cardBorder}`,
                        borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:11,color:T.textSecondary}}>✏️</button>
                      <button onClick={onDelete} style={{background:"none",border:"1px solid rgba(220,38,38,0.3)",
                        borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:11,color:"#A32D2D"}}>✕</button>
                    </div>
                  </div>

                  <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14}}>
                    {notLinked && (
                      <div style={{marginBottom:10,padding:"6px 10px",
                        background:"rgba(217,119,6,0.08)",border:"1px solid rgba(217,119,6,0.25)",
                        borderRadius:6,fontSize:10,color:"#D97706",display:"flex",gap:6}}>
                        ⚠ Set portfolio allocation above 0% to track progress
                      </div>
                    )}
                    <svg width={84} height={84} style={{flexShrink:0}}>
                      <circle cx={42} cy={42} r={34} fill="none" stroke={T.borderSubtle} strokeWidth={7}/>
                      <circle cx={42} cy={42} r={34} fill="none" stroke={gtype.color} strokeWidth={7}
                        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
                        transform="rotate(-90 42 42)" style={{transition:"stroke-dashoffset 0.8s ease"}}/>
                      <text x={42} y={38} textAnchor="middle" fill={T.textPrimary}
                        fontSize={13} fontWeight={700} fontFamily="Inter, system-ui, sans-serif">
                        {m.progress.toFixed(0)}%
                      </text>
                      <text x={42} y={52} textAnchor="middle" fill={T.textMuted}
                        fontSize={9} fontFamily="Inter, system-ui, sans-serif">funded</text>
                    </svg>
                    <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 16px"}}>
                      {[
                        [m.isToday ? "Target (today)" : "Target (future)", fmt(toBase(goal.targetAmount||0, goal.currency||dc), dc)],
                        [m.isToday ? "Target (at maturity)" : "Today's equiv", fmt(m.isToday ? m.futureTargetInBase : m.todayEquivTarget, dc)],
                        ["Current",   fmt(m.currentInBase, dc)],
                        ["Gap",       fmt(Math.max(0,m.gap), dc)],
                        ["Time left", m.yearsLeft>1?`${m.yearsLeft.toFixed(1)}y`:`${Math.round(m.monthsLeft)}m`],
                        ["Real return", `${m.realReturn !== undefined ? m.realReturn.toFixed(1) : (goal.expectedReturn||10)}%`],
                      ].map(([l,v]) => (
                        <div key={l}>
                          <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",
                            letterSpacing:"0.05em",marginBottom:2}}>{l}</div>
                          <div style={{fontSize:13,fontWeight:600,color:T.textPrimary,
                            fontFamily:"'Inter', system-ui, sans-serif"}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {m.sipNeeded > 0 ? (
                    <div style={{background:`${gtype.color}10`,border:`1px solid ${gtype.color}33`,
                      borderRadius:8,padding:"10px 14px"}}>
                      {(goal.currentSIP||0) > 0 ? (
                        <>
                          {/* Current vs Required comparison */}
                          <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:10,alignItems:"center"}}>
                            <div>
                              <div style={{fontSize:10,color:T.textMuted,fontWeight:600,
                                textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Current SIP</div>
                              <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,
                                fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(goal.currentSIP,dc)}</div>
                            </div>
                            <div style={{fontSize:18,color:T.textMuted}}>→</div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:10,color:gtype.color,fontWeight:700,
                                textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>SIP Needed</div>
                              <div style={{fontSize:16,fontWeight:700,color:gtype.color,
                                fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(m.sipNeeded,dc)}</div>
                            </div>
                          </div>
                          {/* Shortfall / surplus message */}
                          {(() => {
                            const delta = m.sipNeeded - (goal.currentSIP||0);
                            if (delta > 0) {
                              return (
                                <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${gtype.color}22`,
                                  fontSize:11,color:"#D97706",fontWeight:600}}>
                                  ⚠ Shortfall: increase SIP by <strong>{fmt(delta, dc)}/mo</strong> to stay on track
                                  <span style={{color:T.textMuted,fontWeight:400,marginLeft:6}}>
                                    · {goal.expectedReturn||10}% p.a. · {(m.inflationRate||6).toFixed(1)}% infl.
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${gtype.color}22`,
                                fontSize:11,color:"#1D9E75",fontWeight:600}}>
                                ✓ Current SIP exceeds required by {fmt(Math.abs(delta), dc)}/mo
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        // Original simple banner — no current SIP set
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div>
                            <div style={{fontSize:10,color:gtype.color,fontWeight:700,
                              textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Monthly SIP needed</div>
                            <div style={{fontSize:18,fontWeight:700,color:gtype.color,
                              fontFamily:"'Inter', system-ui, sans-serif"}}>{fmt(m.sipNeeded,dc)}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:10,color:T.textMuted,marginBottom:2}}>
                              {goal.expectedReturn||10}% p.a. · {(m.inflationRate||6).toFixed(1)}% infl.
                            </div>
                            <div style={{fontSize:11,fontWeight:600,color:isOnTrack?"#1D9E75":"#D97706"}}>
                              {isOnTrack ? "✓ On Track" : "⚠ Needs Attention"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.3)",
                      borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
                      <span style={{fontSize:13,fontWeight:700,color:"#1D9E75"}}>
                        🎉 Goal fully funded — surplus {fmt(Math.abs(m.gap),dc)}
                      </span>
                      {(goal.currentSIP||0) > 0 && (
                        <div style={{fontSize:11,color:T.textSecondary,marginTop:4}}>
                          Current SIP of {fmt(goal.currentSIP, dc)}/mo is on top — bonus surplus building.
                        </div>
                      )}
                    </div>
                  )}

                  {/* SIP Projection mini-chart */}
                  {m.targetInBase > 0 && m.monthsLeft > 0 && (
                    <div style={{marginTop:12}}>
                      <div style={{fontSize:10,color:T.textMuted,marginBottom:4,
                        textTransform:"uppercase",letterSpacing:"0.05em",fontWeight:600}}>
                        Growth Projection
                      </div>
                      {(function(){
                        // Generate monthly projection points
                        const months = Math.min(Math.round(m.monthsLeft), 120); // cap at 10y
                        const r = (goal.expectedReturn||10)/100/12;
                        // Chart projects what client's portfolio looks like with their
                        // CURRENT SIP commitment (not the required SIP). If they haven't
                        // set a currentSIP, fall back to required SIP to show what
                        // hitting the goal would look like.
                        const sipForProjection = (goal.currentSIP || 0) > 0
                          ? goal.currentSIP
                          : m.sipNeeded;
                        const sipIsCurrent = (goal.currentSIP || 0) > 0;
                        const pts = [];
                        let valWithSip = Math.max(0, m.currentInBase || 0);
                        let valNoSip   = Math.max(0, m.currentInBase || 0);
                        for (let i=0; i<=months; i++) {
                          pts.push({ i, withSip: valWithSip, noSip: valNoSip });
                          valWithSip = valWithSip*(1+r) + (sipForProjection||0);
                          valNoSip   = valNoSip*(1+r);
                        }
                        const W=280, H=70, PL=4, PR=4, PT=6, PB=16;
                        const iW=W-PL-PR, iH=H-PT-PB;
                        const allV = pts.flatMap(p=>[p.withSip,p.noSip]);
                        const minV = Math.min(...allV, 0);
                        const maxV = Math.max(...allV, m.targetInBase*1.05);
                        const tx = i => PL + (i/months)*iW;
                        const ty = v => PT + iH - ((v-minV)/(maxV-minV||1))*iH;
                        const targetY = ty(m.targetInBase);
                        const pathWith = pts.map((p,ii)=>`${ii===0?"M":"L"}${tx(p.i)},${ty(p.withSip)}`).join(" ");
                        const pathNo   = pts.map((p,ii)=>`${ii===0?"M":"L"}${tx(p.i)},${ty(p.noSip)}`).join(" ");
                        const fillWith = pathWith+` L${tx(months)},${PT+iH} L${PL},${PT+iH} Z`;
                        return (
                          <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H,borderRadius:4,
                            background:`${gtype.color}06`}}>
                            {/* Target line */}
                            <line x1={PL} x2={W-PR} y1={targetY} y2={targetY}
                              stroke={gtype.color} strokeWidth={1} strokeDasharray="3,3" opacity={0.5}/>
                            <text x={W-PR-2} y={targetY-2} textAnchor="end"
                              fontSize={7} fill={gtype.color} opacity={0.8}>Target</text>
                            {/* No-SIP line */}
                            <path d={pathNo} stroke={T.textMuted} strokeWidth={1}
                              fill="none" strokeDasharray="3,2" opacity={0.4}/>
                            {/* With-SIP fill */}
                            <path d={fillWith} fill={gtype.color} opacity={0.1}/>
                            <path d={pathWith} stroke={gtype.color} strokeWidth={1.5} fill="none"/>
                            {/* Current dot */}
                            <circle cx={PL} cy={ty(m.currentInBase)} r={3}
                              fill={gtype.color} stroke="white" strokeWidth={1}/>
                            {/* Labels */}
                            <text x={PL+2} y={H-3} fontSize={7} fill={T.textMuted}>Now</text>
                            <text x={W-PR-2} y={H-3} fontSize={7} fill={T.textMuted}
                              textAnchor="end">
                              {goal.targetDate ? new Date(goal.targetDate).getFullYear() : `+${Math.round(m.yearsLeft)}y`}
                            </text>
                          </svg>
                        );
                      })()}
                      <div style={{display:"flex",gap:14,marginTop:4,fontSize:9,color:T.textMuted,flexWrap:"wrap"}}>
                        <span style={{display:"flex",alignItems:"center",gap:3}}>
                          <span style={{width:14,height:2,background:gtype.color,borderRadius:1,display:"inline-block"}}/>
                          {(goal.currentSIP||0) > 0
                            ? `With current SIP (${fmt(goal.currentSIP, dc)}/mo)`
                            : `With required SIP (${fmt(m.sipNeeded, dc)}/mo)`}
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:3}}>
                          <span style={{width:14,height:2,background:T.textMuted,borderRadius:1,
                            display:"inline-block",opacity:0.5}}/>
                          No SIP
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:3}}>
                          <span style={{width:14,height:1,borderTop:`1px dashed ${gtype.color}`,
                            display:"inline-block",opacity:0.7}}/>
                          Target
                        </span>
                      </div>
                    </div>
                  )}

                  <div style={{marginTop:10,fontSize:11,color:T.textMuted,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <span>Target: {goal.targetDate ? new Date(goal.targetDate).toLocaleDateString("en-IN",{year:"numeric",month:"short"}) : "—"}</span>
                    {goal.linkedClass && goal.linkedClass!=="portfolio" && (
                      <span style={{background:`${gtype.color}15`,color:gtype.color,
                        border:`1px solid ${gtype.color}33`,
                        padding:"1px 7px",borderRadius:4,fontSize:10,fontWeight:600}}>
                        {goal.linkedClass}
                      </span>
                    )}
                    {goal.notes && <span> · {goal.notes}</span>}
                  </div>
                </div>
              </div>
            );
          };

          // ── Goal Add/Edit Modal ──

          // Summary metrics for scope
          const totalTarget   = goals.reduce((s,g) => s + toBase(g.targetAmount||0, g.currency||dc), 0);
          const totalCurrent  = goals.reduce((s,g) => s + calcGoalMetrics(g).currentInBase, 0);
          const totalProgress = totalTarget > 0 ? Math.min(100, totalCurrent/totalTarget*100) : 0;

          return (
            <div style={{padding:isMobile?"12px":"20px 24px"}}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                marginBottom:18,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:T.textPrimary}}>🎯 Goals & Planning</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                    Financial goals with SIP calculation — per client, per family
                  </div>
                </div>
                <button onClick={()=>{setEditingGoal(null);setGoalModalOpen(true);}}
                  style={{display:"flex",alignItems:"center",gap:7,padding:"9px 18px",borderRadius:10,
                    background:"#007878",border:"none",color:"white",
                    cursor:"pointer",fontSize:13,fontWeight:700}}>
                  + Add Goal
                </button>
              </div>

              {/* Scope selector (admin only) — dropdown search instead of pills */}
              {isAdmin && (
                <div style={{marginBottom:18,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,
                    textTransform:"uppercase",letterSpacing:"0.07em",flexShrink:0}}>
                    Goals for:
                  </div>
                  {/* Searchable dropdown */}
                  <div style={{position:"relative",minWidth:220,flex:1,maxWidth:340}}>
                    <input
                      type="text"
                      placeholder="Search family or client…"
                      value={profileData._goalScopeSearch||""}
                      onFocus={()=>setProfileData(p=>({...p,_goalScopeOpen:true}))}
                      onBlur={()=>setTimeout(()=>setProfileData(p=>({...p,_goalScopeOpen:false})),160)}
                      onChange={e=>setProfileData(p=>({...p,_goalScopeSearch:e.target.value,_goalScopeOpen:true}))}
                      style={{width:"100%",boxSizing:"border-box",padding:"6px 30px 6px 12px",borderRadius:8,
                        border:`1px solid ${activeScopeOpt.type!=="all"?T.brand:T.cardBorder}`,
                        background:activeScopeOpt.type!=="all"?`${T.brand}08`:T.inputBg,
                        color:T.textPrimary,fontSize:12,outline:"none"}}/>
                    {activeScopeOpt.type!=="all"
                      ? <span onClick={()=>{setGoalScopeType("all");setGoalScopeId("all");setProfileData(p=>({...p,_goalScopeSearch:"",_goalScopeOpen:false}));}}
                          style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",
                            cursor:"pointer",color:T.brand,fontSize:14,fontWeight:700}}>✕</span>
                      : <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",
                          color:T.textMuted,fontSize:10,pointerEvents:"none"}}>▾</span>}
                    {profileData._goalScopeOpen && (function(){
                      const q=(profileData._goalScopeSearch||"").toLowerCase();
                      const opts=[
                        {type:"all",id:"all",label:"All Clients",icon:"👥",count:allGoals.length,color:T.brand},
                        ...families.filter(f=>!q||f.name.toLowerCase().includes(q))
                          .map(f=>({type:"family",id:f.id,label:f.name,icon:"👨‍👩‍👧",
                            count:allGoals.filter(g=>g.familyId===f.id).length,color:f.color||T.brand})),
                        ...clients.filter(c=>!q||c.name.toLowerCase().includes(q))
                          .map(c=>({type:"client",id:c.id,label:c.name,icon:"👤",
                            count:allGoals.filter(g=>g.clientId===c.id).length,color:"#7C3AED"})),
                      ].filter(o=>o.type==="all"||!q||o.label.toLowerCase().includes(q));
                      return (
                        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:200,
                          background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                          boxShadow:"0 8px 24px rgba(0,0,0,0.15)",maxHeight:260,overflowY:"auto"}}>
                          {opts.map(opt=>{
                            const isActive=activeScopeOpt.type===opt.type&&String(activeScopeOpt.id)===String(opt.id);
                            return (
                              <div key={`${opt.type}-${opt.id}`}
                                onMouseDown={()=>{
                                  setGoalScopeType(opt.type);setGoalScopeId(opt.id);
                                  setProfileData(p=>({...p,
                                    _goalScopeSearch:opt.type==="all"?"":opt.label,
                                    _goalScopeOpen:false}));
                                }}
                                style={{padding:"8px 14px",cursor:"pointer",fontSize:12,
                                  background:isActive?`${opt.color}10`:"transparent",
                                  color:isActive?opt.color:T.textPrimary,
                                  fontWeight:isActive?700:400,
                                  display:"flex",alignItems:"center",gap:8,
                                  borderLeft:isActive?`3px solid ${opt.color}`:"3px solid transparent"}}
                                onMouseEnter={e=>e.currentTarget.style.background=`${opt.color}08`}
                                onMouseLeave={e=>e.currentTarget.style.background=isActive?`${opt.color}10`:"transparent"}>
                                <span>{opt.icon}</span>
                                <span style={{flex:1}}>{opt.label}</span>
                                <span style={{fontSize:10,color:T.textMuted,background:T.borderSubtle,
                                  borderRadius:8,padding:"1px 6px",fontWeight:600}}>
                                  {opt.count}
                                </span>
                              </div>
                            );
                          })}
                          {opts.length===0 && <div style={{padding:"12px 14px",fontSize:12,color:T.textMuted}}>No results found</div>}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Active filter badge */}
                  {activeScopeOpt.type!=="all"&&(
                    <div style={{fontSize:11,color:T.textMuted}}>
                      {allGoals.filter(g=>activeScopeOpt.type==="family"
                        ?g.familyId===activeScopeOpt.id:g.clientId===activeScopeOpt.id).length} goals
                    </div>
                  )}
                </div>
              )}

              {/* Summary strip */}
              {goals.length > 0 && (
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",
                  gap:12,marginBottom:20}}>
                  {[
                    {label:"Goals",           val:goals.length,               color:T.brand},
                    {label:"Total Target",    val:fmt(totalTarget,dc),   color:"#7C3AED"},
                    {label:"Funded So Far",   val:fmt(totalCurrent,dc),  color:"#059669"},
                    {label:"Progress",        val:totalProgress.toFixed(1)+"%",
                      color:totalProgress>66?"#1D9E75":totalProgress>33?"#D97706":"#A32D2D"},
                  ].map(k=>(
                    <div key={k.label} style={{...tCard(),borderRadius:10,padding:"14px 16px"}}>
                      <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",
                        letterSpacing:"0.07em",marginBottom:6}}>{k.label}</div>
                      <div style={{fontSize:18,fontWeight:700,color:k.color,
                        fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Goals grid */}
              {goals.length === 0 ? (
                <div style={{...tCard(),padding:"56px 24px",textAlign:"center",borderRadius:14}}>
                  <div style={{fontSize:48,marginBottom:14}}>🎯</div>
                  <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>
                    No goals for {activeScopeOpt.label}
                  </div>
                  <div style={{fontSize:13,color:T.textSecondary,maxWidth:380,margin:"0 auto 20px"}}>
                    {isAdmin
                      ? `Select a client above and create their first financial goal.`
                      : "Your advisor will set financial goals here to track your portfolio progress."}
                  </div>
                  {isAdmin && (
                    <button onClick={()=>{setEditingGoal(null);setGoalModalOpen(true);}}
                      style={{background:"#007878",border:"none",
                        borderRadius:10,color:"white",padding:"11px 28px",cursor:"pointer",
                        fontSize:14,fontWeight:700}}>
                      Create First Goal
                    </button>
                  )}
                </div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16}}>
                  {goals.map(goal => (
                    <GoalCard key={goal.id} goal={goal}
                      onEdit={()=>{setEditingGoal(goal);setGoalModalOpen(true);}}
                      onDelete={()=>{
                        requireConfirm(`Delete goal "${goal.name}"?`, ()=>{
                          saveGoals(allGoals.filter(g=>g.id!==goal.id));
                          showToast("Goal deleted","#1D9E75");
                        })}}/>
                  ))}
                </div>
              )}

              {goalModalOpen && <GoalModal
                editingGoal={editingGoal}
                isAdmin={isAdmin}
                loggedInClientId={loggedInClientId}
                activeScopeOpt={activeScopeOpt}
                families={families}
                clients={clients}
                dc={dc}
                allGoals={allGoals}
                saveGoals={saveGoals}
                setGoalModalOpen={setGoalModalOpen}
                setEditingGoal={setEditingGoal}
                showToast={showToast}
                T={T}
                tInp={tInp}
                hwWithOwner={hwWithOwner}
              />}

              {/* ── Monte Carlo Simulation ── */}
              {goals.length>0 && (function(){
                const totalPortfolio = filteredHW.reduce((s,h)=>s+h.valueUSD,0);
                const annualContrib  = recurringInflows.filter(r=>r.active!==false)
                  .reduce((s,r)=>{ const a=parseFloat(r.amount||0); return s+(r.frequency==="monthly"?a*12:r.frequency==="quarterly"?a*4:a); },0);
                // Run N=500 simulations for each goal
                const N=500, annualMu=0.08, annualSigma=0.15;
                const simulate = (currentVal, target, years, contrib) => {
                  if (years<=0||target<=0) return {p50:currentVal,p10:currentVal,p90:currentVal,successRate:currentVal>=target?100:0};
                  let outcomes=[];
                  for (let i=0;i<N;i++){
                    let v=currentVal;
                    for (let y=0;y<years;y++){
                      const r = annualMu + annualSigma*(2*Math.random()-1)*1.7; // approx normal
                      v = v*(1+r) + contrib;
                    }
                    outcomes.push(v);
                  }
                  outcomes.sort((a,b)=>a-b);
                  return {
                    p10:outcomes[Math.floor(N*0.1)],
                    p50:outcomes[Math.floor(N*0.5)],
                    p90:outcomes[Math.floor(N*0.9)],
                    successRate:Math.round(outcomes.filter(v=>v>=target).length/N*100),
                  };
                };
                return (
                  <div style={{...tCard(),marginTop:16,padding:"18px 20px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:16}}>🎲</span>
                      <div style={{fontSize:13,fontWeight:700}}>Monte Carlo Simulation</div>
                      <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>500 scenarios · 8% μ · 15% σ</span>
                    </div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                      Probability of reaching each goal under random market conditions
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {goals.slice(0,5).map(goal=>{
                        const target = parseFloat(goal.targetAmount||0) * ((fxRates[goal.currency||dc]||1)/(fxRates["USD"]||1));
                        const years  = goal.targetDate ? Math.max(0,Math.round((new Date(goal.targetDate)-new Date())/(365*86400000))) : 10;
                        const contrib = annualContrib * ((fxRates[dc]||1)/(fxRates["USD"]||1));
                        const mc = simulate(totalPortfolio, target, years, contrib);
                        const col = mc.successRate>=75?"#1D9E75":mc.successRate>=50?"#D97706":"#A32D2D";
                        return (
                          <div key={goal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                            background:T.borderSubtle,borderRadius:10}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:2}}>{goal.name}</div>
                              <div style={{fontSize:10,color:T.textMuted}}>
                                Target: {fmt(convert(target,dc),dc)} · {years}Y horizon
                              </div>
                              <div style={{display:"flex",gap:12,marginTop:4,fontSize:10,color:T.textSecondary}}>
                                <span>P10: {fmt(convert(mc.p10,dc),dc)}</span>
                                <span>P50: {fmt(convert(mc.p50,dc),dc)}</span>
                                <span>P90: {fmt(convert(mc.p90,dc),dc)}</span>
                              </div>
                            </div>
                            <div style={{textAlign:"center",flexShrink:0}}>
                              <div style={{fontSize:22,fontWeight:500,color:col,fontFamily:"'Inter', system-ui, sans-serif",lineHeight:1}}>
                                {mc.successRate}%
                              </div>
                              <div style={{fontSize:9,color:T.textMuted,marginTop:2}}>success</div>
                              <div style={{width:60,height:4,background:T.borderSubtle,borderRadius:2,marginTop:4}}>
                                <div style={{height:"100%",width:`${mc.successRate}%`,background:col,borderRadius:2}}/>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{marginTop:10,fontSize:10,color:T.textMuted}}>
                      💡 Success = portfolio reaches target by goal date across 500 random simulations.
                      Returns modelled as normally distributed with μ=8%, σ=15%.
                    </div>
                  </div>
                );
              })()}
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in goals tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}


        {/* ══════════════════════════════════════════════
            REBALANCE TAB — admin only
        ══════════════════════════════════════════════ */}
        {tab==="rebalance" && isAdmin && (function(){ try {

          // ── Rebalance scope selector state ──
          // We use a local state inside the IIFE via useRef trick
          // (actual state is at component level — see rbScopeType / rbScopeId)

          // Scope options: all families, individual clients
          // Rebalance scope: global dropdown
          const activeRbOpt = selectedClients.size > 0
            ? { type:"client", id:[...selectedClients].map(String)[0], label:visibleClients.find(c=>String(c.id)===[...selectedClients].map(String)[0])?.name||"Client", icon:"👤" }
            : selectedFamily !== "all"
              ? { type:"family", id:selectedFamily, label:visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family", icon:"👨‍👩‍👧" }
              : { type:"all", id:"all", label:"All Clients", icon:"👥" };

          const rbHoldings = filteredHW;

          const rbTotalUSD = rbHoldings.reduce((s,h) => s + (h.valueUSD||0), 0);

          // Current allocation by asset class
          const currentByClass = rbHoldings.reduce((a,h) => {
            const cls = h.class || "Miscellaneous";
            a[cls] = (a[cls]||0) + (h.valueUSD||0);
            return a;
          }, {});

          // Get the scoped model for this client/family (fallback to "all" default)
          const rbModelKey = activeRbOpt.type==="all" ? "all"
            : activeRbOpt.type==="family" ? `family:${activeRbOpt.id}`
            : `client:${activeRbOpt.id}`;
          const rbModel = getModelForScope(rbModelKey);

          // Build rebalance rows — one per model asset class
          const rebalRows = rbModel.map(m => {
            const currentUSD  = currentByClass[m.assetClass] || 0;
            const currentPct  = rbTotalUSD > 0 ? currentUSD / rbTotalUSD * 100 : 0;
            const targetPct   = m.targetPct;
            const drift       = currentPct - targetPct;                   // + = overweight, - = underweight
            const targetUSD   = rbTotalUSD * targetPct / 100;
            const deltaUSD    = targetUSD - currentUSD;                   // + = need to buy, - = need to sell
            const absDriftPct = Math.abs(drift);
            // Priority: high if |drift| > 5%, medium if > 2%, low otherwise
            const priority    = absDriftPct > 5 ? "high" : absDriftPct > 2 ? "medium" : "ok";
            const action      = Math.abs(deltaUSD) < 100 ? "hold"
              : deltaUSD > 0 ? "buy" : "sell";

            // Holdings in this class
            const classHoldings = rbHoldings
              .filter(h => (h.class||"Miscellaneous") === m.assetClass)
              .sort((a,b) => (b.valueUSD||0)-(a.valueUSD||0));

            return { ...m, currentPct, currentUSD, targetUSD, deltaUSD, drift, priority, action, classHoldings };
          });

          // Also catch classes in holdings not in model
          const modelClasses = new Set(rbModel.map(m => m.assetClass));
          const unmodelled = Object.entries(currentByClass)
            .filter(([cls]) => !modelClasses.has(cls) && currentByClass[cls] > 0)
            .map(([cls, usd]) => ({
              assetClass:cls, currentPct: rbTotalUSD>0?usd/rbTotalUSD*100:0,
              currentUSD:usd, targetPct:0, targetUSD:0, deltaUSD:-usd,
              drift: rbTotalUSD>0?usd/rbTotalUSD*100:0,
              priority:"medium", action:"sell",
              classHoldings: rbHoldings.filter(h=>(h.class||"Miscellaneous")===cls),
            }));

          const allRows = [...rebalRows, ...unmodelled];
          const actionRows = allRows.filter(r => r.action !== "hold").sort((a,b) => Math.abs(b.deltaUSD)-Math.abs(a.deltaUSD));
          const holdRows   = allRows.filter(r => r.action === "hold");

          // Summary stats
          const totalBuy  = actionRows.filter(r=>r.action==="buy" ).reduce((s,r)=>s+Math.abs(r.deltaUSD),0);
          const totalSell = actionRows.filter(r=>r.action==="sell").reduce((s,r)=>s+Math.abs(r.deltaUSD),0);
          const maxDrift  = Math.max(...allRows.map(r=>Math.abs(r.drift)));

          // Colours
          const priorityColor = { high:"#A32D2D", medium:"#D97706", ok:"#1D9E75" };
          const actionColor   = { buy:"#059669",  sell:"#A32D2D",   hold:T.textMuted };
          const actionBg      = { buy:"rgba(5,150,105,0.08)", sell:"rgba(220,38,38,0.08)", hold:"transparent" };

          const DriftBar = ({ current, target }) => {
            const max = Math.max(current, target, 1);
            return (
              <div style={{position:"relative",height:6,borderRadius:3,
                background:"rgba(0,0,0,0.08)",width:"100%",overflow:"visible"}}>
                <div style={{position:"absolute",top:-3,width:2,height:12,background:"#2563EB",
                  borderRadius:1,left:`${Math.min((target/Math.max(current,target,1))*100,100)}%`,zIndex:2}}/>
                <div style={{height:6,borderRadius:3,
                  background:current>target?"#A32D2D":"#1D9E75",
                  width:`${Math.min((current/Math.max(current,target,1))*100,100)}%`,
                  transition:"width 0.5s ease"}}/>
              </div>
            );
          };

          // ── Trade sequence: sells first (largest drift), then buys ──
          const sellRows = actionRows.filter(r=>r.action==="sell").sort((a,b)=>Math.abs(b.drift)-Math.abs(a.drift));
          const buyRows  = actionRows.filter(r=>r.action==="buy" ).sort((a,b)=>Math.abs(b.drift)-Math.abs(a.drift));

            return (
            <div style={{padding:isMobile?"10px":"0"}}>

              {/* ── Header ── */}
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",
                marginBottom:18,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontSize:17,fontWeight:700,color:T.textPrimary}}>⚖️ Portfolio Rebalancing</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                    Current vs model allocation · admin only
                  </div>
                  {modelsByScope[rbModelKey] ? (
                    <div style={{fontSize:11,color:"#1D9E75",marginTop:3}}>✓ Custom model for {activeRbOpt.label}</div>
                  ) : rbModelKey !== "all" ? (
                    <div style={{fontSize:11,color:"#D97706",marginTop:3}}>⚠ Using default model — no custom model for {activeRbOpt.label}</div>
                  ) : null}
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button onClick={()=>{ setModelScopeKey(rbModelKey); setTab("model"); }}
                    style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${T.cardBorder}`,
                      background:T.borderSubtle,cursor:"pointer",fontSize:12,fontWeight:600,
                      color:T.textSecondary,display:"flex",alignItems:"center",gap:5}}>
                    ⚙ Edit Model →
                  </button>
                  <button onClick={()=>{
                      if (!actionRows.length) { showToast("No rebalance actions needed.","#D97706"); return; }
                      const today=new Date().toISOString().slice(0,10);
                      const seqRows = [...sellRows, ...buyRows];
                      const headers=["Step","Action","Asset Class","Priority","Current %","Target %","Drift %","Trade Amount","Reason","Holdings"];
                      const csvData=seqRows.map((r,idx)=>[
                        idx+1, r.action.toUpperCase(), r.assetClass, r.priority,
                        r.currentPct.toFixed(2)+"%", (r.targetPct||0).toFixed(2)+"%",
                        (r.drift>=0?"+":"")+r.drift.toFixed(2)+"%",
                        (r.deltaUSD>=0?"+":"-")+fmt(convert(Math.abs(r.deltaUSD),dc),dc,false)+" "+(r.deltaUSD>0?"to buy":"to sell"),
                        r.action==="sell"?"Free capital first — largest overweight":"Deploy capital — largest underweight",
                        r.classHoldings.map(h=>h.name).join(" | "),
                      ]);
                      const csv=[headers,...csvData].map(row=>row.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                      downloadCSV(csv,`Forthius_Rebalance_${activeRbOpt.label.replace(/[^a-zA-Z0-9]/g,"_")}_${today}.csv`);
                      showToast(`✓ Trade list exported — ${seqRows.length} trade${seqRows.length!==1?"s":""}`, "#1D9E75");
                      logAudit("EXPORT_REBALANCE",`${activeRbOpt.label} · ${seqRows.length} trades sequenced`);
                    }}
                    style={{padding:"7px 14px",borderRadius:8,border:"1px solid rgba(22,163,74,0.4)",
                      background:"rgba(22,163,74,0.08)",cursor:"pointer",fontSize:12,fontWeight:600,
                      color:"#1D9E75",display:"flex",alignItems:"center",gap:5}}>
                    ⬇ Export Sequenced Trades
                  </button>
                </div>
              </div>



              {/* ── KPI strip ── */}
              <div style={{display:"grid",gridTemplateColumns:`repeat(${isMobile?2:5},minmax(0,1fr))`,
                gap:10,marginBottom:20}}>
                {[
                  {label:"Portfolio Value", val:fmt(convert(rbTotalUSD,dc),dc), color:T.brand},
                  {label:"Actions Needed",  val:actionRows.length, color:actionRows.length>3?"#A32D2D":"#1D9E75"},
                  {label:"Total to Buy",    val:fmt(convert(totalBuy,dc),dc),  color:"#059669"},
                  {label:"Total to Trim",   val:fmt(convert(totalSell,dc),dc), color:"#A32D2D"},
                  {label:"Max Drift",       val:maxDrift.toFixed(1)+"%",            color:maxDrift>5?"#A32D2D":maxDrift>2?"#D97706":"#1D9E75"},
                ].map(k=>(
                  <div key={k.label} style={{...tCard(),borderRadius:10,padding:"12px 14px",
                    borderLeft:`3px solid ${k.color}`}}>
                    <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",
                      letterSpacing:"0.08em",marginBottom:5,fontWeight:700}}>{k.label}</div>
                    <div style={{fontSize:isMobile?16:20,fontWeight:700,color:k.color,
                      fontFamily:"'Inter', system-ui, sans-serif",letterSpacing:"-0.02em"}}>{k.val}</div>
                  </div>
                ))}
              </div>

              {rbTotalUSD === 0 ? (
                <div style={{...tCard(),padding:"48px 24px",textAlign:"center",borderRadius:12}}>
                  <div style={{fontSize:36,marginBottom:12}}>📭</div>
                  <div style={{fontSize:15,fontWeight:600,color:T.textPrimary}}>No holdings in this scope</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:6}}>Select a client or family with holdings</div>
                </div>
              ) : (<React.Fragment>

              {/* ══════════════════════════════════════════════════════════
                  PART 1 — UNIFIED ALLOCATION BAR CHART
              ══════════════════════════════════════════════════════════ */}
              {(function(){
                const displayRows = [
                  ...allRows.filter(r=>r.action==="sell").sort((a,b)=>Math.abs(b.drift)-Math.abs(a.drift)),
                  ...allRows.filter(r=>r.action==="buy" ).sort((a,b)=>Math.abs(b.drift)-Math.abs(a.drift)),
                  ...allRows.filter(r=>r.action==="hold"),
                ];
                const maxPct = Math.max(...displayRows.map(r=>Math.max(r.currentPct,r.targetPct||0)),20);
                return (
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",
                      letterSpacing:"0.10em",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:3,height:14,background:T.brand,borderRadius:2}}/>
                      Current vs Target Allocation
                    </div>
                    <div style={{...tCard(),padding:"14px 16px"}}>
                      {/* Column headers */}
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,
                        fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700}}>
                        <div style={{width:isMobile?90:140,flexShrink:0}}>Asset Class</div>
                        <div style={{flex:1}}>0% ←──────────────── {maxPct.toFixed(0)}%</div>
                        <div style={{width:isMobile?130:200,flexShrink:0,textAlign:"right"}}>Drift · Action · Amount</div>
                      </div>
                      {/* Allocation rows */}
                      {displayRows.map(row=>{
                        const isHold    = row.action==="hold";
                        const isSell    = row.action==="sell";
                        const barColor  = isSell?"rgba(220,38,38,0.55)":isHold?T.borderSubtle:"rgba(5,150,105,0.55)";
                        const driftCol  = isSell?"#A32D2D":isHold?T.textMuted:"#059669";
                        const amtCol    = isSell?"#A32D2D":isHold?T.textMuted:"#059669";
                        const targPct   = row.targetPct||0;
                        return (
                          <div key={row.assetClass} style={{display:"flex",alignItems:"center",
                            gap:10,marginBottom:10,opacity:isHold?0.6:1}}>
                            <div style={{width:isMobile?90:140,flexShrink:0,fontSize:11,
                              fontWeight:isHold?400:600,color:T.textPrimary,
                              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}
                              title={row.assetClass}>
                              {row.assetClass}
                            </div>
                            {/* Bar track */}
                            <div style={{flex:1,position:"relative",height:22,
                              background:T.borderSubtle,borderRadius:4,overflow:"visible"}}>
                              {/* Current fill */}
                              <div style={{position:"absolute",left:0,top:0,height:22,
                                borderRadius:4,
                                width:`${Math.min(row.currentPct/maxPct*100,100)}%`,
                                background:barColor,
                                display:"flex",alignItems:"center",justifyContent:"flex-end",
                                paddingRight:4,transition:"width 0.5s ease"}}>
                                {row.currentPct > 4 && (
                                  <span style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.9)",
                                    whiteSpace:"nowrap"}}>{row.currentPct.toFixed(1)}%</span>
                                )}
                              </div>
                              {/* Target marker */}
                              {targPct > 0 && (
                                <div style={{position:"absolute",top:-3,width:2,height:28,
                                  background:T.brand,borderRadius:1,zIndex:2,
                                  left:`${Math.min(targPct/maxPct*100,99)}%`}}>
                                  <div style={{position:"absolute",top:-14,left:-12,fontSize:8,
                                    color:T.brand,fontWeight:700,whiteSpace:"nowrap"}}>
                                    {targPct}%
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Right: drift + action + amount */}
                            <div style={{width:isMobile?130:200,flexShrink:0,
                              display:"flex",alignItems:"center",justifyContent:"flex-end",gap:8}}>
                              <span style={{fontSize:11,fontWeight:700,color:driftCol,
                                minWidth:40,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif"}}>
                                {row.drift>0?"+":""}{row.drift.toFixed(1)}%
                              </span>
                              <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",
                                borderRadius:20,whiteSpace:"nowrap",
                                background:isSell?"rgba(220,38,38,0.1)":isHold?T.borderSubtle:"rgba(5,150,105,0.1)",
                                color:driftCol,border:`1px solid ${driftCol}33`}}>
                                {isSell?"▼ Sell":isHold?"✓ Hold":"▲ Buy"}
                              </span>
                              <span style={{fontSize:11,fontWeight:700,color:amtCol,
                                minWidth:60,textAlign:"right",fontFamily:"'Inter', system-ui, sans-serif",
                                whiteSpace:"nowrap"}}>
                                {isHold?"—":`${isSell?"-":"+"}${fmt(convert(Math.abs(row.deltaUSD),dc),dc)}`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {/* Legend */}
                      <div style={{display:"flex",alignItems:"center",gap:16,marginTop:8,
                        paddingTop:10,borderTop:`1px solid ${T.cardBorder}`,
                        fontSize:10,color:T.textMuted}}>
                        <span style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{width:20,height:3,background:T.brand,borderRadius:2,display:"inline-block"}}/>
                          Target line
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{width:14,height:14,background:"rgba(220,38,38,0.5)",borderRadius:3,display:"inline-block"}}/>
                          Overweight
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{width:14,height:14,background:"rgba(5,150,105,0.5)",borderRadius:3,display:"inline-block"}}/>
                          Underweight
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{width:14,height:14,background:T.borderSubtle,borderRadius:3,display:"inline-block"}}/>
                          On target
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ══════════════════════════════════════════════════════════
                  PART 2 — WHAT-IF SIMULATOR
              ══════════════════════════════════════════════════════════ */}
              {rbModel.length > 0 && (function(){
                const wiTargets = rbModel.map(m=>({
                  assetClass: m.assetClass,
                  target: m.targetPct||0,
                  current: rbTotalUSD>0?(currentByClass[m.assetClass]||0)/rbTotalUSD*100:0,
                }));
                return (
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",
                      letterSpacing:"0.10em",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:3,height:14,background:"#A78BFA",borderRadius:2}}/>
                      What-If Simulator
                      <span style={{fontSize:10,color:T.textMuted,fontWeight:400,letterSpacing:0,
                        textTransform:"none"}}>— drag targets, see live trade impact</span>
                    </div>
                    <div style={{...tCard(),padding:"14px 18px"}}>
                      <RebalanceSimulator
                        wiTargets={wiTargets}
                        rbTotalUSD={rbTotalUSD}
                        dc={dc}
                        fmt={fmt}
                        convert={convert}
                        isMobile={isMobile}
                        T={T}
                        onConfigureModel={()=>{setModelScopeKey(rbModelKey);setTab("model");}}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* ══════════════════════════════════════════════════════════
                  PART 3 — SEQUENCED TRADE LIST (sells first, then buys)
              ══════════════════════════════════════════════════════════ */}
              {actionRows.length > 0 && (function(){
                const netCash = totalSell - totalBuy;
                return (
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.textMuted,textTransform:"uppercase",
                      letterSpacing:"0.10em",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:3,height:14,background:"#F59E0B",borderRadius:2}}/>
                      Suggested Trade Sequence
                      <span style={{fontSize:10,color:T.textMuted,fontWeight:400,letterSpacing:0,
                        textTransform:"none"}}>— sells first to free capital</span>
                    </div>
                    <div style={{...tCard(),padding:"12px 16px"}}>
                      {[...sellRows,...buyRows].map((row,idx)=>{
                        const isSell = row.action==="sell";
                        const col    = isSell?"#A32D2D":"#059669";
                        const bg     = isSell?"rgba(220,38,38,0.1)":"rgba(5,150,105,0.1)";
                        const reason = isSell
                          ? `Overweight ${row.drift>0?"+":""}${row.drift.toFixed(1)}% · free capital first`
                          : `Underweight ${row.drift.toFixed(1)}% · deploy after sells settle`;
                        return (
                          <div key={row.assetClass} style={{display:"flex",alignItems:"center",
                            gap:12,padding:"9px 0",
                            borderBottom:`0.5px solid ${T.cardBorder}`}}>
                            {/* Step badge */}
                            <div style={{width:26,height:26,borderRadius:"50%",flexShrink:0,
                              background:bg,display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:11,fontWeight:700,color:col}}>
                              {idx+1}
                            </div>
                            {/* Action pill */}
                            <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,
                              background:bg,color:col,border:`1px solid ${col}33`,
                              flexShrink:0,whiteSpace:"nowrap"}}>
                              {isSell?"▼ Sell":"▲ Buy"}
                            </span>
                            {/* Class + reason */}
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,
                                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                                {row.assetClass}
                              </div>
                              <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                {reason}
                              </div>
                            </div>
                            {/* Holdings chips */}
                            {!isMobile && row.classHoldings.length > 0 && (
                              <div style={{display:"flex",flexWrap:"wrap",gap:4,maxWidth:200}}>
                                {row.classHoldings.slice(0,3).map(h=>(
                                  <span key={h.id} style={{fontSize:9,background:T.borderSubtle,
                                    color:T.textSecondary,borderRadius:4,padding:"1px 6px",
                                    fontFamily:"'Inter', system-ui, sans-serif",whiteSpace:"nowrap"}}>
                                    {h.ticker!=="-"?h.ticker:h.name?.slice(0,10)}
                                  </span>
                                ))}
                                {row.classHoldings.length>3&&(
                                  <span style={{fontSize:9,color:T.textMuted}}>+{row.classHoldings.length-3}</span>
                                )}
                              </div>
                            )}
                            {/* Amount */}
                            <div style={{fontFamily:"'Inter', system-ui, sans-serif",fontSize:13,
                              fontWeight:700,color:col,flexShrink:0,textAlign:"right",
                              minWidth:80}}>
                              {isSell?"-":"+"}{fmt(convert(Math.abs(row.deltaUSD),dc),dc)}
                            </div>
                          </div>
                        );
                      })}
                      {/* Net cash summary */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                        marginTop:12,paddingTop:10,borderTop:`1px solid ${T.cardBorder}`,flexWrap:"wrap",gap:8}}>
                        <div style={{fontSize:11,color:T.textSecondary}}>
                          Net cash flow after rebalance:
                          <span style={{fontWeight:700,marginLeft:6,
                            color:netCash>0?"#059669":netCash<0?"#A32D2D":T.textMuted,
                            fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {netCash>0?"+":""}{fmt(convert(Math.abs(netCash),dc),dc)}{" "}
                            {netCash>100?"surplus":netCash<-100?"shortfall":"balanced"}
                          </span>
                        </div>
                        <div style={{fontSize:10,color:T.textMuted}}>
                          {sellRows.length} sell{sellRows.length!==1?"s":""} ·{" "}
                          {buyRows.length} buy{buyRows.length!==1?"s":""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── AI REBALANCING SUGGESTIONS ── */}
              <div style={{...tCard(),marginBottom:20,padding:"16px 18px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:15}}>✦</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>AI Rebalancing Suggestions</div>
                      <div style={{fontSize:11,color:T.textMuted}}>Powered by Claude · based on current drift from model</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {aiRebalance && (
                      <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:8,
                        color:aiRebalance.priority==="high"?"#F43F5E":aiRebalance.priority==="medium"?"#F59E0B":"#10B981",
                        background:aiRebalance.priority==="high"?"rgba(244,63,94,0.1)":"rgba(16,185,129,0.1)"}}>
                        {aiRebalance.priority} priority · drift {aiRebalance.drift}
                      </span>
                    )}
                    <button onClick={runAiRebalance} disabled={aiRebalanceLoad}
                      style={{background:`${T.brand}14`,border:`1px solid ${T.brand}44`,borderRadius:7,
                        color:T.brand,padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:700,
                        opacity:aiRebalanceLoad?0.6:1}}>
                      {aiRebalanceLoad?"⟳ Analysing…":aiRebalance?"↺ Refresh":"Generate AI Plan"}
                    </button>
                  </div>
                </div>

                {aiErrors.rebalance && (
                  <div style={{fontSize:12,color:aiErrors.rebalance.startsWith("🔒")?"#7C3AED":"#F43F5E",
                    padding:"8px 12px",borderRadius:6,background:aiErrors.rebalance.startsWith("🔒")?"rgba(124,58,237,0.06)":"rgba(244,63,94,0.06)",
                    marginBottom:8}}>
                    {aiErrors.rebalance}
                    {aiErrors.rebalance.startsWith("🔒") && (
                      <span onClick={()=>setUpgradeModal({tabId:"rebalance",tabLabel:"Rebalance",tabIcon:"⚖️"})}
                        style={{cursor:"pointer",marginLeft:8,textDecoration:"underline"}}>Upgrade →</span>
                    )}
                  </div>
                )}

                {!aiRebalance && !aiRebalanceLoad && !aiErrors.rebalance && (
                  <div style={{fontSize:12,color:T.textMuted,padding:"12px 0"}}>
                    Click "Generate AI Plan" to get Claude's specific buy/sell recommendations based on your current drift.
                    The model portfolio has {rbModel.length} asset classes with {actionRows.length} drift{actionRows.length!==1?"s":""} needing action.
                  </div>
                )}

                {aiRebalance && (
                  <div style={{display:"flex",flexDirection:"column",gap:0}}>
                    {aiRebalance.summary && (
                      <div style={{fontSize:13,color:T.textSecondary,lineHeight:1.6,marginBottom:14,
                        padding:"10px 12px",background:T.borderSubtle,borderRadius:8}}>
                        {aiRebalance.summary}
                      </div>
                    )}
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(2,minmax(0,1fr))",gap:8}}>
                      {(aiRebalance.actions||[]).map((a,i)=>{
                        const isSell = a.type==="sell"||a.type==="reduce";
                        const col = isSell?"#F43F5E":"#10B981";
                        return (
                          <div key={i} style={{padding:"10px 12px",borderRadius:8,
                            background:isSell?"rgba(244,63,94,0.04)":"rgba(16,185,129,0.04)",
                            border:`1px solid ${col}22`}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                              <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:4,
                                color:col,background:`${col}15`,textTransform:"uppercase",letterSpacing:"0.04em",flexShrink:0}}>
                                {a.type}
                              </span>
                              <span style={{fontSize:12,fontWeight:700,color:T.textPrimary,flex:1,
                                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {a.holding}
                              </span>
                              <span style={{fontSize:12,fontWeight:700,color:col,flexShrink:0,
                                fontFamily:"'Inter', system-ui, sans-serif"}}>
                                {a.amount}
                              </span>
                            </div>
                            <div style={{fontSize:11,color:T.textMuted,lineHeight:1.4}}>{a.rationale}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{marginTop:10,fontSize:10,color:T.textMuted,textAlign:"center"}}>
                      AI suggestions are indicative only. Review against your IPS and client suitability before execution.
                    </div>
                  </div>
                )}
              </div>

              {/* ── BEFORE / AFTER REBALANCE COMPARISON ── */}
              {actionRows.length > 0 && (function(){
                // Compute projected "after" allocation
                const afterByClass = {...currentByClass};
                actionRows.forEach(row => {
                  afterByClass[row.assetClass] = (afterByClass[row.assetClass]||0) + row.deltaUSD;
                });
                const afterTotal = rbTotalUSD; // total stays same (internal rebalance)

                // Build side-by-side rows for all classes in model
                const compRows = rbModel
                  .filter(m => m.targetPct > 0)
                  .map(m => {
                    const beforePct = rbTotalUSD>0 ? ((currentByClass[m.assetClass]||0)/rbTotalUSD*100) : 0;
                    const afterPct  = afterTotal>0  ? ((afterByClass[m.assetClass]||0)/afterTotal*100)  : 0;
                    const target    = m.targetPct;
                    const beforeGap = beforePct - target;
                    const afterGap  = afterPct  - target;
                    const improved  = Math.abs(afterGap) < Math.abs(beforeGap) - 0.1;
                    return { cls:m.assetClass, beforePct, afterPct, target, beforeGap, afterGap, improved };
                  })
                  .sort((a,b) => Math.abs(b.beforeGap)-Math.abs(a.beforeGap));

                const maxPct = Math.max(...compRows.map(r=>Math.max(r.beforePct,r.afterPct,r.target)), 1);

                return (
                  <div style={{...tCard(),marginTop:16,borderColor:`${T.brand}33`}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:16}}>📊</span>
                      <span style={{fontSize:14,fontWeight:700}}>Before / After Rebalance</span>
                      <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>
                        Projected allocation after executing all trades
                      </span>
                    </div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                      Grey bar = current · Coloured bar = after rebalance · White line = target
                    </div>

                    {/* Summary KPIs */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
                      {[
                        {label:"Classes Improved",
                          val:`${compRows.filter(r=>r.improved).length} / ${compRows.length}`,
                          col:"#1D9E75", icon:"✅"},
                        {label:"Max Drift Before",
                          val:`${Math.max(...compRows.map(r=>Math.abs(r.beforeGap))).toFixed(1)}%`,
                          col:"#A32D2D", icon:"⚠️"},
                        {label:"Max Drift After",
                          val:`${Math.max(...compRows.map(r=>Math.abs(r.afterGap))).toFixed(1)}%`,
                          col:Math.max(...compRows.map(r=>Math.abs(r.afterGap)))<2?"#1D9E75":"#D97706",
                          icon:"🎯"},
                      ].map(({label,val,col,icon})=>(
                        <div key={label} style={{background:`${col}08`,border:`1px solid ${col}22`,
                          borderRadius:10,padding:"10px 14px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:T.textMuted,marginBottom:3}}>{icon} {label}</div>
                          <div style={{fontSize:16,fontWeight:500,fontFamily:"'Inter', system-ui, sans-serif",color:col}}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {/* Visual comparison chart */}
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {compRows.map(({cls,beforePct,afterPct,target,beforeGap,afterGap,improved})=>{
                        const accentCol = CLASS_COLORS[cls] || T.brand;
                        const bw = Math.min(100,beforePct/maxPct*100);
                        const aw = Math.min(100,afterPct/maxPct*100);
                        const tw = Math.min(100,target/maxPct*100);
                        return (
                          <div key={cls}>
                            <div style={{display:"flex",justifyContent:"space-between",
                              alignItems:"center",marginBottom:4}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,minWidth:140}}>
                                <div style={{width:3,height:14,borderRadius:2,background:accentCol}}/>
                                <span style={{fontSize:11,fontWeight:600,color:T.textPrimary}}>{cls}</span>
                                {improved && (
                                  <span style={{fontSize:9,color:"#1D9E75",fontWeight:700}}>✓</span>
                                )}
                              </div>
                              <div style={{display:"flex",gap:14,fontSize:10,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                <span style={{color:T.textMuted}}>{beforePct.toFixed(1)}%</span>
                                <span style={{color:"#6B7280"}}>→</span>
                                <span style={{fontWeight:700,
                                  color:Math.abs(afterGap)<1?"#1D9E75":Math.abs(afterGap)<3?"#D97706":"#A32D2D"}}>
                                  {afterPct.toFixed(1)}%
                                </span>
                                <span style={{color:T.textMuted,minWidth:50,textAlign:"right"}}>
                                  tgt {target}%
                                </span>
                              </div>
                            </div>
                            {/* Stacked bars */}
                            <div style={{position:"relative",height:18,background:T.borderSubtle,borderRadius:6,overflow:"visible"}}>
                              {/* Before bar (grey) */}
                              <div style={{position:"absolute",top:2,left:0,
                                width:`${bw}%`,height:14,
                                background:"rgba(156,163,175,0.5)",
                                borderRadius:4,transition:"width 0.5s"}}/>
                              {/* After bar (coloured) */}
                              <div style={{position:"absolute",top:2,left:0,
                                width:`${aw}%`,height:14,
                                background:`${accentCol}88`,
                                borderRadius:4,border:`1px solid ${accentCol}`,
                                transition:"width 0.7s 0.1s"}}/>
                              {/* Target line */}
                              <div style={{position:"absolute",top:0,bottom:0,
                                left:`${tw}%`,width:2,
                                background:"white",borderRadius:1,
                                boxShadow:"0 0 3px rgba(0,0,0,0.3)",
                                zIndex:2}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div style={{display:"flex",gap:16,marginTop:14,fontSize:10,color:T.textMuted,flexWrap:"wrap"}}>
                      {[
                        {col:"rgba(156,163,175,0.5)",label:"Current allocation"},
                        {col:`${T.brand}88`,label:"After rebalance"},
                        {col:"white",border:"1px solid #999",label:"Target"},
                      ].map(({col,border,label})=>(
                        <div key={label} style={{display:"flex",alignItems:"center",gap:4}}>
                          <div style={{width:14,height:6,borderRadius:2,background:col,
                            border:border||"none",flexShrink:0}}/>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {actionRows.length === 0 && (
                <div style={{...tCard(),padding:"32px 24px",textAlign:"center",borderRadius:12,
                  border:"1px solid rgba(22,163,74,0.3)",background:"rgba(22,163,74,0.04)"}}>
                  <div style={{fontSize:28,marginBottom:10}}>✅</div>
                  <div style={{fontSize:14,fontWeight:600,color:"#1D9E75"}}>Portfolio is within targets</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:4}}>
                    All asset classes within ±{Math.max(2,...allRows.map(r=>r.absDriftPct||0)).toFixed(0)}% of model
                  </div>
                </div>
              )}

              </React.Fragment>)}
            </div>

          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in rebalance tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
          })()}


        {/* ══════════════════════════════════════════════
            CAPITAL GAINS TAX TAB
        ══════════════════════════════════════════════ */}
        {tab==="capgains" && (function(){ try {
          // ── Scope selector ────────────────────────────────────────────────
          // Cap Gains scope: from global dropdown
          // ── CG Date range helpers ──
          const cgGetFYRange = (yr) => ({ from: `${yr}-04-01`, to: `${yr+1}-03-31` });
          const cgGetCYRange = (yr) => ({ from: `${yr}-01-01`, to: `${yr}-12-31` });
          const cgRange = cgPeriodType==="FY" ? cgGetFYRange(cgYear)
            : cgPeriodType==="CY" ? cgGetCYRange(cgYear)
            : cgPeriodType==="custom" ? { from: cgDateFrom, to: cgDateTo }
            : null; // null = all time
          const renderCgDatePicker = () => (
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:T.textSecondary,fontWeight:600}}>Period:</span>
              {[["all","All Time"],["FY","Fin. Year"],["CY","Cal. Year"],["custom","Custom"]].map(([v,l])=>(
                <button key={v} onClick={()=>setCgPeriodType(v)}
                  style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
                    background:cgPeriodType===v?T.brand:T.inputBg,
                    color:cgPeriodType===v?"white":T.textSecondary}}>
                  {l}
                </button>
              ))}
              {(cgPeriodType==="FY"||cgPeriodType==="CY") && (
                <select value={cgYear} onChange={e=>setCgYear(Number(e.target.value))}
                  style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                    background:T.inputBg,color:T.textPrimary,cursor:"pointer"}}>
                  {[...Array(10)].map((_,i)=>{
                    const yr = new Date().getFullYear()-i;
                    return <option key={yr} value={yr}>
                      {cgPeriodType==="FY"?`FY ${yr}-${String(yr+1).slice(2)}`:yr}
                    </option>;
                  })}
                </select>
              )}
              {cgPeriodType==="custom" && (
                <React.Fragment>
                  <input type="date" value={cgDateFrom} onChange={e=>setCgDateFrom(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                      background:T.inputBg,color:T.textPrimary}}/>
                  <span style={{fontSize:11,color:T.textMuted}}>to</span>
                  <input type="date" value={cgDateTo} onChange={e=>setCgDateTo(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                      background:T.inputBg,color:T.textPrimary}}/>
                </React.Fragment>
              )}
              {cgRange && (
                <span style={{fontSize:10,color:T.textMuted,background:T.inputBg,padding:"3px 8px",borderRadius:5}}>
                  📅 {cgRange.from} → {cgRange.to}
                  {cgPeriodType==="FY" && <span style={{marginLeft:4,color:T.brand,fontWeight:600}}>India FY {cgYear}-{String(cgYear+1).slice(2)}</span>}
                </span>
              )}
            </div>
          );
          const activeCgOpt = (!isAdmin && loggedInClientId)
            ? { type:"client", id:loggedInClientId, label:"My Portfolio", icon:"👤" }
            : (isAdmin && viewAsClientId)
              ? { type:"client", id:viewAsClientId, label:clients.find(c=>c.id===viewAsClientId)?.name||"Client", icon:"👤" }
              : selectedClients.size > 0
              ? { type:"client", id:[...selectedClients].map(String)[0], label:visibleClients.find(c=>String(c.id)===[...selectedClients].map(String)[0])?.name||"Client", icon:"👤" }
              : selectedFamily !== "all"
                ? { type:"family", id:selectedFamily, label:visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family", icon:"👨‍👩‍👧" }
                : { type:"all", id:"all", label:"All Clients", icon:"👥" };

          // ── Filter holdings + transactions to scope ────────────────────────
          const cgHoldings = activeCgOpt.type==="all" ? filteredHW
            : activeCgOpt.type==="family"
              ? filteredHW.filter(h=>clients.find(c=>c.id===h.clientId)?.familyId===activeCgOpt.id)
              : filteredHW.filter(h=>h.clientId===activeCgOpt.id);
          const cgHoldingNames = new Set(cgHoldings.map(h=>h.name));
          // Fuzzy match transaction names to holding names
          // IMPORTANT: fully-switched-out funds may not be in cgHoldings —
          // we still need their transactions for realised gain/loss calculation.
          // For unmatched transactions, use the folio ledger name directly.
          const normCGName = (s) => (s||"").toLowerCase()
            .replace(/[-]/g," ")
            .replace(/(regular|direct|plan|growth|dividend|reinvest|payout|option)/gi," ")
            .replace(/\s+/g," ").trim();
          const cgNormMap = {};
          cgHoldings.forEach(h => { cgNormMap[normCGName(h.name)] = h.name; });
          const findCGHolding = (txnName) => {
            if (cgHoldingNames.has(txnName)) return txnName; // exact match in holdings
            const norm = normCGName(txnName);
            if (cgNormMap[norm]) return cgNormMap[norm]; // normalised match
            const words = norm.split(" ").filter(w=>w.length>3);
            if (words.length >= 2) {
              for (const [hn, orig] of Object.entries(cgNormMap)) {
                if (words.filter(w=>hn.includes(w)).length >= Math.ceil(words.length*0.6)) return orig;
              }
            }
            // No match in current holdings — fund may be fully exited.
            // Return the raw transaction name so gains/losses are still computed.
            return txnName;
          };
          // Scope to client/family but include ALL transactions (not just current holdings)
          const scopedTxns = activeCgOpt.type==="all" ? transactions
            : activeCgOpt.type==="family"
              ? transactions.filter(t=>{ const c=clients.find(x=>x.id===t.clientId||x.name===t.client); return c && clients.find(x=>x.id===t.clientId)?.familyId===activeCgOpt.id; })
              : transactions.filter(t=>{ const c=clients.find(x=>x.name===t.client||x.id===t.clientId); return c?.id===activeCgOpt.id; });
          const cgTransactions = scopedTxns
            .filter(t => {
              // Date range filter for capital gains
              if (cgRange) {
                if (cgRange.from && t.date < cgRange.from) return false;
                if (cgRange.to   && t.date > cgRange.to)   return false;
              }
              return true;
            })
            .map(t => ({...t, holding: findCGHolding(t.holding)}))
            .filter(t => t.holding); // drop any with empty holding name
          // Build extended holdings list: current holdings + synthetic entries for exited funds
          const cgExitedFunds = [...new Set(cgTransactions.map(t=>t.holding))]
            .filter(n => !cgHoldingNames.has(n))
            .map(n => ({ name:n, qty:0, currentPrice:0, costPrice:0,
              country:"India", currency:"INR", class:"Indian Mutual Funds" }));
          const cgAllHoldings = [...cgHoldings, ...cgExitedFunds];

          // ── Run engine (per-holding jurisdiction) ─────────────────────────
          const cgResult     = calcCapitalGains(cgTransactions, cgAllHoldings, fxRates);
          const byJuris      = cgResult.byJurisdiction;  // grouped by country
          const fmt2 = v => fmt(convert(v||0, dc), dc);

          // Active jurisdiction filter (null = show all)
          // Uses cgJurisdiction as a filter key, null = all
          const activeJurisFilter = cgJurisdiction === "ALL" ? null : cgJurisdiction;
          const visibleJuris = activeJurisFilter
            ? byJuris.filter(j=>j.jurisId===activeJurisFilter)
            : byJuris;

          return <React.Fragment>
            {/* Header */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:17,fontWeight:700,marginBottom:4}}>🧾 Capital Gains Tax Estimator</div>
              <div style={{fontSize:11,color:T.textSecondary}}>
                Each asset taxed under its own country's rules · FIFO lot-matching
                {cgTransactions.length===0 && (
                  <span style={{color:"#D97706",marginLeft:8}}>
                    ⚠ No transactions recorded — upload a broker statement to calculate gains.
                  </span>
                )}
              </div>
            </div>

            {/* Scope selector — admin only */}
            
            {/* Date Range Picker */}
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginBottom:14,
              padding:"10px 14px",background:T.inputBg,borderRadius:10,border:`1px solid ${T.cardBorder}`}}>
              <span style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginRight:2}}>Period:</span>
              {[["all","All Time"],["FY","Fin. Year"],["CY","Cal. Year"],["custom","Custom"]].map(([v,l])=>(
                <button key={v} onClick={()=>setCgPeriodType(v)}
                  style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
                    background:cgPeriodType===v?T.brand:T.cardBg,
                    color:cgPeriodType===v?"white":T.textSecondary}}>
                  {l}
                </button>
              ))}
              {(cgPeriodType==="FY"||cgPeriodType==="CY") && (
                <select value={cgYear} onChange={e=>setCgYear(Number(e.target.value))}
                  style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,
                    background:T.cardBg,color:T.textPrimary,cursor:"pointer"}}>
                  {[...Array(10)].map((_,i)=>{ const yr=new Date().getFullYear()-i;
                    return <option key={yr} value={yr}>{cgPeriodType==="FY"?`FY ${yr}-${String(yr+1).slice(2)}`:yr}</option>; })}
                </select>
              )}
              {cgPeriodType==="custom" && (
                <React.Fragment>
                  <input type="date" value={cgDateFrom} onChange={e=>setCgDateFrom(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,background:T.cardBg,color:T.textPrimary}}/>
                  <span style={{fontSize:11,color:T.textMuted}}>→</span>
                  <input type="date" value={cgDateTo} onChange={e=>setCgDateTo(e.target.value)}
                    style={{fontSize:11,padding:"3px 8px",borderRadius:6,border:`1px solid ${T.cardBorder}`,background:T.cardBg,color:T.textPrimary}}/>
                </React.Fragment>
              )}
              {cgRange ? (
                <span style={{fontSize:10,color:T.brand,fontWeight:600,marginLeft:4,background:`${T.brand}15`,padding:"3px 8px",borderRadius:5}}>
                  📅 {cgPeriodType==="FY"?`FY ${cgYear}-${String(cgYear+1).slice(2)}`:cgPeriodType==="CY"?`CY ${cgYear}`:`${cgRange.from} → ${cgRange.to}`}
                </span>
              ) : (
                <span style={{fontSize:10,color:T.textMuted,marginLeft:4}}>Showing all transactions</span>
              )}
            </div>
            {/* Portfolio-level KPIs */}
            <div style={{display:"grid",
              gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)",
              gap:10,marginBottom:16}}>
              {[
                {label:"Total Realised",   val:fmt2(cgResult.totalRealised),
                  col:cgResult.totalRealised>=0?"#1D9E75":"#A32D2D",icon:"💰"},
                {label:"LT Gains",         val:fmt2(cgResult.totalLtcg),
                  col:"#2563EB",icon:"📅"},
                {label:"ST Gains",         val:fmt2(cgResult.totalStcg),
                  col:"#D97706",icon:"⚡"},
                {label:"Unrealised",       val:fmt2(cgResult.totalUnrealised),
                  col:"#7C3AED",icon:"📊"},
                {label:"Est. Total Tax",   val:fmt2(cgResult.totalTaxEstimate),
                  col:"#A32D2D",icon:"🧾"},
              ].map(({label,val,col,icon})=>(
                <div key={label} style={{...tCard(),padding:"10px 12px"}}>
                  <div style={{fontSize:10,color:T.textMuted,marginBottom:3}}>{icon} {label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:col,
                    fontFamily:"'Inter', system-ui, sans-serif"}}>{val}</div>
                </div>
              ))}
            </div>

            {/* Jurisdiction tabs — one per country found in portfolio */}
            {byJuris.length > 0 && (
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                <button onClick={()=>setCgJurisdiction("ALL")}
                  style={{padding:"5px 14px",borderRadius:16,fontSize:11,cursor:"pointer",
                    fontWeight:!activeJurisFilter?700:400,
                    border:`1px solid ${!activeJurisFilter?T.brand:T.cardBorder}`,
                    background:!activeJurisFilter?`${T.brand}15`:"transparent",
                    color:!activeJurisFilter?T.brand:T.textSecondary}}>
                  🌐 All ({byJuris.length})
                </button>
                {byJuris.map(j=>{
                  const active=activeJurisFilter===j.jurisId;
                  const FLAG={IN:"🇮🇳",US:"🇺🇸",UK:"🇬🇧",EU:"🇪🇺",SG:"🇸🇬",AE:"🇦🇪",
                    HK:"🇭🇰",JP:"🇯🇵",AU:"🇦🇺",CA:"🇨🇦",CN:"🇨🇳"}[j.jurisId]||"🌐";
                  return (
                    <button key={j.jurisId} onClick={()=>setCgJurisdiction(j.jurisId)}
                      style={{padding:"5px 14px",borderRadius:16,fontSize:11,cursor:"pointer",
                        fontWeight:active?700:400,
                        border:`1px solid ${active?T.brand:T.cardBorder}`,
                        background:active?`${T.brand}15`:"transparent",
                        color:active?T.brand:T.textSecondary}}>
                      {FLAG} {j.jurisLabel}
                      {j.noTax && <span style={{marginLeft:4,fontSize:9,color:"#1D9E75",fontWeight:600}}>No CGT</span>}
                      {!j.noTax && j.taxEst>0 && (
                        <span style={{marginLeft:4,fontSize:9,color:"#A32D2D"}}>
                          {fmt2(j.taxEst)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Per-jurisdiction panels */}
            {byJuris.length === 0 ? (
              <div style={{...tCard(),padding:"32px 24px",textAlign:"center"}}>
                <div style={{fontSize:24,marginBottom:8}}>📂</div>
                <div style={{fontSize:13,fontWeight:600,color:T.textSecondary}}>No holdings found</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
                  Select a client or family to view their capital gains.
                </div>
              </div>
            ) : visibleJuris.map(juris => (
              <div key={juris.jurisId} style={{...tCard(),marginBottom:16}}>
                {/* Juris header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  marginBottom:12,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:20}}>
                      {({IN:"🇮🇳",US:"🇺🇸",UK:"🇬🇧",EU:"🇪🇺",SG:"🇸🇬",AE:"🇦🇪",
                        HK:"🇭🇰",JP:"🇯🇵",AU:"🇦🇺",CA:"🇨🇦",CN:"🇨🇳"})[juris.jurisId]||"🌐"}
                    </span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700}}>{juris.jurisLabel}</div>
                      <div style={{fontSize:10,color:T.textMuted}}>
                        {juris.holdings.length} asset{juris.holdings.length!==1?"s":""} ·
                        {juris.noTax
                          ? <span style={{color:"#1D9E75",fontWeight:600,marginLeft:4}}>No Capital Gains Tax</span>
                          : <span style={{marginLeft:4}}>
                              LT {juris.rules.ltRate}% · ST {juris.rules.stRate}%
                              {juris.rules.exemption>0 && (
                                <span style={{color:"#2563EB",marginLeft:6}}>
                                  {juris.rules.exemptionCcy} {juris.rules.exemption.toLocaleString()} exempt
                                </span>
                              )}
                            </span>
                        }
                      </div>
                    </div>
                  </div>
                  {/* Juris-level summary */}
                  {!juris.noTax && (
                    <div style={{display:"flex",gap:10}}>
                      {[
                        {l:"LT",  v:juris.ltcg,  c:"#2563EB"},
                        {l:"ST",  v:juris.stcg,  c:"#D97706"},
                        {l:"Tax", v:juris.taxEst,c:"#A32D2D"},
                      ].map(({l,v,c})=> v!==0 ? (
                        <div key={l} style={{textAlign:"right"}}>
                          <div style={{fontSize:9,color:T.textMuted}}>{l}</div>
                          <div style={{fontSize:12,fontWeight:700,color:c,
                            fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {v>=0?"+":""}{fmt2(v)}
                          </div>
                        </div>
                      ): null)}
                    </div>
                  )}
                </div>

                {/* Holdings table for this jurisdiction */}
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:620}}>
                    <thead>
                      <tr>{["Asset","Class","Realised LT","Realised ST",
                        "Unrealised","Est. Tax","Lots"].map(h=>(
                        <th key={h} style={tTh()}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {juris.holdings
                        .filter(r=>Math.abs(r.realised||0)>0.01||Math.abs(r.unrealised||0)>0.01||r.lots.length>0)
                        .sort((a,b)=>Math.abs(b.ltcgAmount+b.stcgAmount)-Math.abs(a.ltcgAmount+a.stcgAmount))
                        .map((r,i)=>{
                          const expanded = cgExpanded[r.holding];
                          return (
                            <React.Fragment key={r.holding}>
                              <tr style={{background:i%2===0?"transparent":T.tableStripe,
                                cursor:r.lots.length>0?"pointer":"default"}}
                                onClick={()=>r.lots.length>0&&
                                  setCgExpanded(p=>({...p,[r.holding]:!p[r.holding]}))}>
                                <td style={tTd({fontWeight:600,maxWidth:180})}>
                                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                                    {r.lots.length>0&&(
                                      <span style={{fontSize:9,color:T.textMuted}}>
                                        {expanded?"▼":"▶"}
                                      </span>
                                    )}
                                    <div>
                                      <div style={{fontSize:11,whiteSpace:"nowrap",
                                        overflow:"hidden",textOverflow:"ellipsis",maxWidth:160}}>
                                        {r.holding}
                                      </div>
                                      {r.ticker&&r.ticker!=="-"&&(
                                        <div style={{fontSize:9,color:T.textMuted}}>{r.ticker}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td style={tTd()}><span style={badge()}>{r.class||"—"}</span></td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                                  color:r.ltcgAmount>=0?"#2563EB":"#A32D2D",fontWeight:600})}>
                                  {r.ltcgAmount!==0?(r.ltcgAmount>=0?"+":"")+fmt2(r.ltcgAmount):"—"}
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                                  color:r.stcgAmount>=0?"#D97706":"#A32D2D",fontWeight:600})}>
                                  {r.stcgAmount!==0?(r.stcgAmount>=0?"+":"")+fmt2(r.stcgAmount):"—"}
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                                  color:(r.unrealised||0)>=0?"#1D9E75":"#A32D2D"})}>
                                  {r.unrealised!=null?(r.unrealised>=0?"+":"")+fmt2(r.unrealised):"—"}
                                </td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:12,
                                  fontWeight:juris.noTax?400:600,
                                  color:juris.noTax?"#1D9E75":"#A32D2D"})}>
                                  {juris.noTax?"Nil":r.totalTaxEst>0?fmt2(r.totalTaxEst):"—"}
                                </td>
                                <td style={tTd({fontSize:11,color:T.textMuted})}>
                                  {r.lots.length>0
                                    ?`${r.lots.length} lot${r.lots.length!==1?"s":""}`
                                    :<span style={{color:T.textLight}}>—</span>}
                                </td>
                              </tr>
                              {expanded&&r.lots.map((lot,li)=>(
                                <tr key={li} style={{background:`${T.brand}05`}}>
                                  <td style={tTd({paddingLeft:24,fontSize:10,color:T.textSecondary})} colSpan={2}>
                                    <div>Buy {lot.buyDate} → Sell {lot.sellDate}</div>
                                    <div style={{fontSize:9,color:T.textMuted}}>
                                      {lot.qty.toFixed(4)} units · held {Math.round(lot.holdDays)}d
                                    </div>
                                  </td>
                                  <td style={tTd({fontSize:10,color:T.textMuted})} colSpan={2}>
                                    Cost {lot.costPerUnit.toFixed(3)} → Sale {lot.salePrice.toFixed(3)}
                                  </td>
                                  <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontSize:11,
                                    color:lot.gainTotal>=0?"#1D9E75":"#A32D2D"})}>
                                    {(lot.gainTotal>=0?"+":"")+fmt2(lot.gainTotal)}
                                  </td>
                                  <td style={tTd({fontSize:10})}>
                                    <span style={{fontWeight:700,fontSize:10,
                                      color:lot.type==="LTCG"?"#2563EB":"#D97706",
                                      background:lot.type==="LTCG"?"rgba(37,99,235,0.1)":"rgba(217,119,6,0.1)",
                                      padding:"1px 6px",borderRadius:6}}>
                                      {lot.type}
                                    </span>
                                  </td>
                                  <td style={tTd()}/>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Tax optimisation hint per jurisdiction */}
                {!juris.noTax && juris.taxEst>0 && (
                  <div style={{marginTop:10,padding:"7px 10px",background:T.surfaceBg,
                    borderRadius:6,fontSize:10,color:T.textSecondary,lineHeight:1.6}}>
                    💡 {juris.jurisId==="IN" && "Harvest LTCG up to ₹1.25L/yr tax-free. Switch debt MF to target maturity funds."}
                       {juris.jurisId==="US" && "Hold >1yr for 15% LT rate. Harvest losses before Dec 31 to offset gains."}
                       {juris.jurisId==="UK" && "Use £3,000 annual CGT allowance. Consider Bed & ISA to shelter future gains."}
                       {juris.jurisId==="AU" && "50% CGT discount applies if held >1yr. Consider timing realisation to lower-income years."}
                       {!["IN","US","UK","AU"].includes(juris.jurisId) && "Review local tax treaties and residency rules with your tax advisor."}
                  </div>
                )}
              </div>
            ))}

            <div style={{marginTop:4,padding:"8px 14px",background:T.borderSubtle,
              borderRadius:6,fontSize:10,color:T.textLight,lineHeight:1.6}}>
              ⚠️ <strong>Disclaimer:</strong> Tax estimates are indicative. Based on FIFO lot-matching
              and simplified rules — does not account for surcharges, cess, tax treaties, residency status,
              or jurisdiction-specific nuances. Always consult a qualified tax advisor. Rules as of 2025.
            </div>
          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in capgains tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}
        {/* ══════════════════════════════════════════════
            DIVIDEND & INCOME TRACKER TAB
        ══════════════════════════════════════════════ */}
        {tab==="dividends" && (function(){ try {

          // ── Scope options ──
          // Dividends scope: from global dropdown
          const activeDivOpt = (!isAdmin && loggedInClientId)
            ? { type:"client", id:loggedInClientId, label:"My Income", icon:"👤" }
            : (isAdmin && viewAsClientId)
              ? { type:"client", id:viewAsClientId, label:clients.find(c=>c.id===viewAsClientId)?.name||"Client", icon:"👤" }
              : selectedClients.size > 0
              ? { type:"client", id:[...selectedClients].map(String)[0], label:visibleClients.find(c=>String(c.id)===[...selectedClients].map(String)[0])?.name||"Client", icon:"👤" }
              : selectedFamily !== "all"
                ? { type:"family", id:selectedFamily, label:visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family", icon:"👨‍👩‍👧" }
                : { type:"all", id:"all", label:"All", icon:"👥" };

          // Filter holdings to scope
          const scopeHoldings = activeDivOpt.type === "all"
            ? hwWithOwner
            : activeDivOpt.type === "family"
              ? hwWithOwner.filter(h => clients.find(c=>c.id===h.clientId)?.familyId === activeDivOpt.id)
              : hwWithOwner.filter(h => h.clientId === activeDivOpt.id);

          // Filter dividends to scope
          const scopeDivs = activeDivOpt.type === "all"
            ? dividends
            : activeDivOpt.type === "family"
              ? dividends.filter(d => {
                  const h = hwWithOwner.find(x => String(x.id)===String(d.holdingId));
                  return clients.find(c=>c.id===h?.clientId)?.familyId === activeDivOpt.id;
                })
              : dividends.filter(d => d.clientId === activeDivOpt.id);

          // ── Monthly income aggregation ──
          const now = new Date();
          const currentYear = now.getFullYear();

          const monthlyIncome = Array.from({length:12}, (_,i) => {
            const month = i; // 0-based
            const total = scopeDivs
              .filter(d => {
                const dt = new Date(d.payDate || d.exDate);
                return dt.getFullYear()===currentYear && dt.getMonth()===month;
              })
              .reduce((s,d) => {
                const rate = fxRates[d.currency||"USD"] || FX_RATES_DEFAULT[d.currency||"USD"] || 1;
                const baseRate = fxRates[dc] || FX_RATES_DEFAULT[dc] || 1;
                return s + (d.totalReceived||0) / rate * baseRate;
              }, 0);
            return { month: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i], total, idx:i };
          });

          const totalThisYear  = monthlyIncome.reduce((s,m) => s+m.total, 0);
          const totalAllTime   = scopeDivs.reduce((s,d) => {
            const rate = fxRates[d.currency||"USD"] || FX_RATES_DEFAULT[d.currency||"USD"] || 1;
            const br   = fxRates[dc] || FX_RATES_DEFAULT[dc] || 1;
            return s + (d.totalReceived||0)/rate*br;
          }, 0);

          // Annual projection: extrapolate from months elapsed
          const monthsElapsed  = now.getMonth() + 1;
          const annualProjected = monthsElapsed > 0 ? totalThisYear / monthsElapsed * 12 : 0;

          // DRIP value
          const dripEvents  = scopeDivs.filter(d => d.drip);
          const dripShares  = dripEvents.reduce((s,d) => s+(d.dripShares||0), 0);
          const dripValue   = dripEvents.reduce((s,d) => {
            const h = hwWithOwner.find(x=>String(x.id)===String(d.holdingId));
            return s + (d.dripShares||0)*(h?.currentPrice||0);
          }, 0);

          // Per-holding income summary
          const holdingIncomeSummary = scopeHoldings.map(h => {
            const hDivs = scopeDivs.filter(d => String(d.holdingId)===String(h.id));
            const totalInc = hDivs.reduce((s,d) => {
              const rate = fxRates[d.currency||h.currency||"USD"] || FX_RATES_DEFAULT[d.currency||h.currency||"USD"] || 1;
              const br   = fxRates[dc] || FX_RATES_DEFAULT[dc] || 1;
              return s + (d.totalReceived||0)/rate*br;
            }, 0);
            const costBasis   = (h.costPrice||0) * (h.qty||0);
            const costBasisDC = costBasis / (fxRates[h.currency]||FX_RATES_DEFAULT[h.currency]||1) * (fxRates[dc]||FX_RATES_DEFAULT[dc]||1);
            const yieldOnCost = costBasisDC > 0 ? totalInc / costBasisDC * 100 : 0;
            const lastDiv = hDivs.sort((a,b)=>new Date(b.exDate)-new Date(a.exDate))[0];
            return { ...h, totalInc, yieldOnCost, divCount:hDivs.length, lastDiv };
          }).filter(h => h.divCount > 0 || h.valueUSD > 0)
            .sort((a,b) => b.totalInc - a.totalInc);

          const maxMonthly = Math.max(...monthlyIncome.map(m=>m.total), 1);

          // ── Add/Edit Dividend Modal ──

          // ────────────────────────────────────────────
          return (
            <div style={{padding:isMobile?"12px":"20px 24px"}}>

              {/* ── Header ── */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                marginBottom:18,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:T.textPrimary}}>💰 Dividend & Income Tracker</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                    Log dividends, distributions and DRIP events per holding
                  </div>
                </div>
                <button onClick={()=>{setEditingDiv(null);setDivModalOpen(true);}}
                  style={{display:"flex",alignItems:"center",gap:7,padding:"9px 18px",borderRadius:10,
                    background:"#1D9E75",border:"none",color:"white",
                    cursor:"pointer",fontSize:13,fontWeight:700}}>
                  + Log Dividend
                </button>
              </div>



              {/* ── KPI Strip ── */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",
                gap:12,marginBottom:22}}>
                {[
                  { label:"Total This Year",   val:fmt(totalThisYear,dc),    color:"#059669", icon:"📅" },
                  { label:"Annual Projection", val:fmt(annualProjected,dc),  color:"#2563EB", icon:"📈" },
                  { label:"All-Time Income",   val:fmt(totalAllTime,dc),     color:"#7C3AED", icon:"💎" },
                  { label:"DRIP Shares",       val:dripShares.toFixed(4),         color:"#059669", icon:"🔄" },
                  { label:"DRIP Value",        val:fmt(dripValue,dc),        color:"#D97706", icon:"💹" },
                ].map(k=>(
                  <div key={k.label} style={{...tCard(),borderRadius:10,padding:"13px 15px"}}>
                    <div style={{fontSize:16,marginBottom:4}}>{k.icon}</div>
                    <div style={{fontSize:10,color:T.textSecondary,textTransform:"uppercase",
                      letterSpacing:"0.07em",marginBottom:4}}>{k.label}</div>
                    <div style={{fontSize:16,fontWeight:700,color:k.color,
                      fontFamily:"'Inter', system-ui, sans-serif"}}>{k.val}</div>
                  </div>
                ))}
              </div>

              {/* ── Monthly Income Bar Chart ── */}
              <div style={{...tCard(),borderRadius:12,padding:"18px 20px",marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>
                    Monthly Income — {currentYear}
                  </div>
                  <div style={{fontSize:12,color:T.textSecondary}}>
                    Projected annual: <strong style={{color:"#2563EB"}}>{fmt(annualProjected,dc)}</strong>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",gap:isMobile?4:8,height:120}}>
                  {monthlyIncome.map((m,i) => {
                    const barH = maxMonthly > 0 ? Math.max(m.total/maxMonthly*100, m.total>0?4:0) : 0;
                    const isPast = i < now.getMonth();
                    const isCur  = i === now.getMonth();
                    return (
                      <div key={m.month} style={{flex:1,display:"flex",flexDirection:"column",
                        alignItems:"center",gap:4,cursor:"default"}}
                        title={`${m.month}: ${fmt(m.total,dc)}`}>
                        <div style={{fontSize:9,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif",
                          height:16,display:"flex",alignItems:"flex-end"}}>
                          {m.total>0 ? fmt(m.total,dc).replace(/\.00/,"") : ""}
                        </div>
                        <div style={{width:"100%",borderRadius:"4px 4px 0 0",
                          height:`${barH}%`,minHeight:m.total>0?4:0,
                          background: isCur
                            ? "linear-gradient(180deg,#2563EB,#1D4ED8)"
                            : isPast
                              ? "linear-gradient(180deg,#059669,#047857)"
                              : T.borderSubtle,
                          transition:"height 0.4s ease",opacity:isCur?1:isPast?0.85:0.4}}>
                        </div>
                        <div style={{fontSize:isMobile?8:10,color:isCur?T.brand:T.textMuted,
                          fontWeight:isCur?700:400}}>{m.month}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:16,marginTop:10,fontSize:10,color:T.textMuted}}>
                  <span style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:8,height:8,borderRadius:2,background:"#059669",display:"inline-block"}}/>
                    Past months
                  </span>
                  <span style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:8,height:8,borderRadius:2,background:"#2563EB",display:"inline-block"}}/>
                    Current month
                  </span>
                  <span style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:8,height:8,borderRadius:2,background:T.borderSubtle,display:"inline-block"}}/>
                    Upcoming
                  </span>
                </div>
              </div>

              {/* ── Per-Holding Income Table ── */}
              <div style={{...tCard(),borderRadius:12,padding:0,overflow:"hidden",marginBottom:20}}>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.cardBorder}`,
                  display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>Income by Holding</div>
                  <div style={{fontSize:11,color:T.textSecondary}}>
                    {holdingIncomeSummary.filter(h=>h.divCount>0).length} holdings with dividends
                  </div>
                </div>
                {holdingIncomeSummary.filter(h=>h.divCount>0).length === 0 ? (
                  <div style={{padding:"32px 24px",textAlign:"center"}}>
                    <div style={{fontSize:32,marginBottom:10}}>📭</div>
                    <div style={{fontSize:13,color:T.textSecondary}}>No dividend events logged yet</div>
                  </div>
                ) : (
                  <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",minWidth:560}}>
                    <thead>
                      <tr style={{background:T.thBg}}>
                        {["Holding","Ticker","Events","Total Income","Yield on Cost","Last Ex-Date","DRIP"].map(h=>(
                          <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,
                            fontWeight:700,color:T.thText,textTransform:"uppercase",
                            letterSpacing:"0.05em",borderBottom:`1px solid ${T.cardBorder}`,
                            whiteSpace:"nowrap"}}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {holdingIncomeSummary.filter(h=>h.divCount>0).map((h,i)=>(
                        <tr key={h.id} style={{background:i%2===0?T.cardBg:T.surfaceBg,
                          borderBottom:`1px solid ${T.cardBorder}`}}>
                          <td style={{padding:"10px 14px"}}>
                            <div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>{h.name}</div>
                            {isAdmin && h.client && (
                              <div style={{fontSize:10,color:T.textSecondary,marginTop:1}}>{h.client.name}</div>
                            )}
                          </td>
                          <td style={{padding:"10px 14px",fontSize:12,fontFamily:"'Inter', system-ui, sans-serif",
                            fontWeight:600,color:T.accent}}>{h.ticker!=="-"?h.ticker:"—"}</td>
                          <td style={{padding:"10px 14px"}}>
                            <span style={{fontSize:12,background:`${T.brand}14`,color:T.brand,
                              borderRadius:6,padding:"2px 8px",fontWeight:600}}>
                              {h.divCount}
                            </span>
                          </td>
                          <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,
                            color:"#059669",fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {fmt(h.totalInc,dc)}
                          </td>
                          <td style={{padding:"10px 14px"}}>
                            <span style={{fontSize:12,fontWeight:600,
                              color:h.yieldOnCost>5?"#059669":h.yieldOnCost>2?"#D97706":T.textSecondary,
                              fontFamily:"'Inter', system-ui, sans-serif"}}>
                              {h.yieldOnCost.toFixed(2)}%
                            </span>
                          </td>
                          <td style={{padding:"10px 14px",fontSize:11,color:T.textSecondary,
                            fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {h.lastDiv?.exDate || "—"}
                          </td>
                          <td style={{padding:"10px 14px"}}>
                            {dividends.filter(d=>String(d.holdingId)===String(h.id)&&d.drip).length > 0
                              ? <span style={{fontSize:11,color:"#059669",fontWeight:600}}>🔄 Yes</span>
                              : <span style={{fontSize:11,color:T.textMuted}}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )}
              </div>

              {/* ── Dividend Event Log ── */}
              <div style={{...tCard(),borderRadius:12,padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.cardBorder}`,
                  display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>
                    Event Log ({scopeDivs.length})
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary}}>
                    All dividend and distribution events
                  </div>
                </div>
                {scopeDivs.length === 0 ? (
                  <div style={{padding:"40px 24px",textAlign:"center"}}>
                    <div style={{fontSize:40,marginBottom:12}}>💸</div>
                    <div style={{fontSize:15,fontWeight:600,color:T.textPrimary,marginBottom:6}}>No events yet</div>
                    <div style={{fontSize:12,color:T.textSecondary,maxWidth:360,margin:"0 auto 20px"}}>
                      Log dividend payments, MF distributions or DRIP reinvestments here.
                    </div>
                    <button onClick={()=>{setEditingDiv(null);setDivModalOpen(true);}}
                      style={{background:"#1D9E75",border:"none",
                        borderRadius:10,color:"white",padding:"9px 22px",cursor:"pointer",
                        fontSize:13,fontWeight:700}}>
                      Log First Dividend
                    </button>
                  </div>
                ) : (
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:isMobile?480:640}}>
                      <thead>
                        <tr style={{background:T.thBg}}>
                          {["Holding","Ex-Date","Pay Date","DPS","Total","CCY","DRIP","Notes",""].map(h=>(
                            <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:11,
                              fontWeight:700,color:T.thText,textTransform:"uppercase",
                              letterSpacing:"0.05em",borderBottom:`1px solid ${T.cardBorder}`,
                              whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...scopeDivs].sort((a,b)=>new Date(b.exDate)-new Date(a.exDate)).map((d,i)=>{
                          const h = hwWithOwner.find(x=>String(x.id)===String(d.holdingId));
                          return (
                            <tr key={d.id} style={{background:i%2===0?T.cardBg:T.surfaceBg,
                              borderBottom:`1px solid ${T.cardBorder}`}}>
                              <td style={{padding:"9px 12px"}}>
                                <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>
                                  {h?.name?.slice(0,22)||"Unknown"}
                                </div>
                                {h?.ticker && h.ticker!=="-" && (
                                  <div style={{fontSize:10,color:T.textMuted,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                    {h.ticker}
                                  </div>
                                )}
                              </td>
                              <td style={{padding:"9px 12px",fontSize:11,color:T.textSecondary,
                                fontFamily:"'Inter', system-ui, sans-serif"}}>{d.exDate||"—"}</td>
                              <td style={{padding:"9px 12px",fontSize:11,color:T.textSecondary,
                                fontFamily:"'Inter', system-ui, sans-serif"}}>{d.payDate||"—"}</td>
                              <td style={{padding:"9px 12px",fontSize:11,fontFamily:"'Inter', system-ui, sans-serif",
                                color:T.textPrimary}}>{d.dps ? parseFloat(d.dps).toFixed(4) : "—"}</td>
                              <td style={{padding:"9px 12px",fontSize:12,fontWeight:700,
                                color:"#059669",fontFamily:"'Inter', system-ui, sans-serif"}}>
                                {fmt(parseFloat(d.totalReceived)||0, d.currency||dc)}
                              </td>
                              <td style={{padding:"9px 12px",fontSize:11,color:T.textMuted}}>{d.currency||dc}</td>
                              <td style={{padding:"9px 12px"}}>
                                {d.drip
                                  ? <span style={{fontSize:11,color:"#059669",fontWeight:600}}>
                                      🔄 {d.dripShares ? parseFloat(d.dripShares).toFixed(4)+"sh" : "Yes"}
                                    </span>
                                  : <span style={{fontSize:11,color:T.textMuted}}>Cash</span>}
                              </td>
                              <td style={{padding:"9px 12px",fontSize:11,color:T.textMuted,
                                maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {d.notes||"—"}
                              </td>
                              <td style={{padding:"9px 12px"}}>
                                <div style={{display:"flex",gap:4}}>
                                  <button onClick={()=>{setEditingDiv(d);setDivModalOpen(true);}}
                                    style={{background:"none",border:`1px solid ${T.cardBorder}`,
                                      borderRadius:5,padding:"3px 7px",cursor:"pointer",
                                      fontSize:11,color:T.textSecondary}}>✏️</button>
                                  <button onClick={()=>{
                                    requireConfirm("Delete this dividend event?", ()=>{
                                      saveDividends(dividends.filter(x=>x.id!==d.id));
                                      showToast("✓ Dividend deleted","#1D9E75");
                                  })}} style={{background:"none",border:"1px solid rgba(220,38,38,0.3)",
                                    borderRadius:5,padding:"3px 7px",cursor:"pointer",
                                    fontSize:11,color:"#A32D2D"}}>✕</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {divModalOpen && <DivModal
              editingDiv={editingDiv}
              isAdmin={isAdmin}
              activeDivOpt={activeDivOpt}
              hwWithOwner={hwWithOwner}
              families={families}
              clients={clients}
              scopeHoldings={scopeHoldings}
              dc={dc}
              dividends={dividends}
              saveDividends={saveDividends}
              setDivModalOpen={setDivModalOpen}
              setEditingDiv={setEditingDiv}
              showToast={showToast}
              T={T}
              tInp={tInp}
              fmt={fmt}
              fxRates={fxRates}
            />}
            </div>
          );
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in dividends tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

        {/* ══════════════════════════════════════════════════════════════════
            CASH FLOW & OPERATIONS TAB
            ═══════════════════════════════════════════════════════════════ */}
        {tab==="cashflow" && (function(){ try {

          {filteredHW.length === 0 && <div style={{textAlign:"center",padding:"60px 24px",background:T.cardBg,borderRadius:14,border:`1px dashed ${T.cardBorder}`,marginBottom:20}}><div style={{fontSize:40,marginBottom:12}}>📂</div><div style={{fontSize:16,fontWeight:700,color:T.textPrimary,marginBottom:8}}>No portfolio data yet</div><div style={{fontSize:13,color:T.textSecondary,maxWidth:340,margin:"0 auto 20px",lineHeight:1.6}}>Add your holdings to see cashflow analysis here.<br/>Upload a CAS statement or add manually.</div><button onClick={()=>setTab("upload")} style={{background:`${T.brand}`,border:"none",borderRadius:8,color:"white",padding:"9px 20px",cursor:"pointer",fontSize:13,fontWeight:600}}>📤 Upload Holdings</button></div>}
          // ── Helpers ────────────────────────────────────────────────────
          const saveCC  = (data) => { setCapitalCalls(data); try { localStorage.setItem("fw_capital_calls_v1",  JSON.stringify(data)); } catch {} };
          const saveFee = (data) => { setFeeEntries(data);   try { localStorage.setItem("fw_fee_entries_v1",    JSON.stringify(data)); } catch {} };
          const saveDoc = (data) => { setDocuments(data);    try { localStorage.setItem("fw_documents_v1",      JSON.stringify(data)); } catch {} };
          const saveRI  = (data) => { setRecurringInflows(data); try { localStorage.setItem("fw_recurring_inflows_v1", JSON.stringify(data)); } catch {} };
          const saveRO  = (data) => { setRecurringOutflows(data); try { localStorage.setItem("fw_recurring_outflows_v1", JSON.stringify(data)); } catch {} };
          const saveTax = (data) => { setTaxEvents(data);   try { localStorage.setItem("fw_tax_events_v1",     JSON.stringify(data)); } catch {} };

          // ── Scope: same pattern as dividends / goals tab ────────────────
          // Cash Flow scope: from global dropdown
          const activeCfOpt = (!isAdmin && loggedInClientId)
            ? { type:"client", id:loggedInClientId, label:"My Cash Flow", icon:"👤" }
            : (isAdmin && viewAsClientId)
              ? { type:"client", id:viewAsClientId, label:clients.find(c=>c.id===viewAsClientId)?.name||"Client", icon:"👤" }
              : selectedClients.size > 0
                ? { type:"client", id:[...selectedClients].map(String)[0], label:visibleClients.find(c=>String(c.id)===[...selectedClients].map(String)[0])?.name||"Client", icon:"👤" }
                : selectedFamily !== "all"
                  ? { type:"family", id:selectedFamily, label:visibleFamilies.find(f=>String(f.id)===String(selectedFamily))?.name||"Family", icon:"👨‍👩‍👧" }
                  : { type:"all", id:"all", label:"All Clients", icon:"👥" };

          // Scope-filtered holdings — used to match dividends and recurring inflows to correct clients
          const cfHoldings = activeCfOpt.type === "all"
            ? hwWithOwner
            : activeCfOpt.type === "family"
              ? hwWithOwner.filter(h => clients.find(c=>c.id===h.clientId)?.familyId === activeCfOpt.id)
              : hwWithOwner.filter(h => h.clientId === activeCfOpt.id);

          // Scope-filtered client ids
          const cfClientIds = new Set(cfHoldings.map(h => h.clientId));

          // Scope-filtered dividends
          const cfDividends = activeCfOpt.type === "all"
            ? dividends
            : dividends.filter(d => {
                const h = hwWithOwner.find(x => String(x.id)===String(d.holdingId));
                return cfClientIds.has(h?.clientId);
              });

          // Scope-filtered recurring inflows, outflows, capital calls, fees, tax events
          const cfRecurring = activeCfOpt.type === "all"
            ? recurringInflows
            : recurringInflows.filter(r => !r.clientId || cfClientIds.has(r.clientId) || activeCfOpt.type==="all");
          const cfOutflows = activeCfOpt.type === "all"
            ? recurringOutflows
            : recurringOutflows.filter(r => !r.clientId || cfClientIds.has(r.clientId) || activeCfOpt.type==="all");
          const cfCalls  = activeCfOpt.type === "all" ? capitalCalls  : capitalCalls.filter(cc => !cc.clientId || cfClientIds.has(cc.clientId));
          const cfFees   = activeCfOpt.type === "all" ? feeEntries    : feeEntries.filter(f  => !f.clientId  || cfClientIds.has(f.clientId));
          const cfTax    = activeCfOpt.type === "all" ? taxEvents     : taxEvents.filter(t   => !t.clientId  || cfClientIds.has(t.clientId));
          const cfDocs   = activeCfOpt.type === "all" ? documents     : documents.filter(d   => !d.clientId  || cfClientIds.has(d.clientId));

          // ── Build 12-month forecast ─────────────────────────────────────
          const today    = new Date();
          const months   = Array.from({length:12}, (_,i) => {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            return { label: d.toLocaleDateString("en-US",{month:"short",year:"2-digit"}), date: d, key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` };
          });

          // Collect all cashflow events per month — using SCOPED collections
          const monthData = months.map(m => {
            const mk = m.key;
            // Dividends from existing dividend tracker (scoped)
            const divIncome = cfDividends.reduce((s,d) => {
              const dm = (d.payDate||d.exDate||"").slice(0,7);
              return s + (dm===mk ? (parseFloat(d.totalReceived)||0) : 0);
            }, 0);
            // Recurring inflows (monthly) — scoped
            const riIncome = cfRecurring.filter(r => {
              if (!r.active) return false;
              if (r.frequency==="monthly") return true;
              if (r.frequency==="quarterly") return (new Date(m.date).getMonth()-new Date(r.startDate||today).getMonth())%3===0;
              if (r.frequency==="annual")   return (m.date.getMonth()===new Date(r.startDate||today).getMonth());
              return false;
            }).reduce((s,r) => s + (parseFloat(r.amount)||0), 0);
            // Capital calls (outflows) — scoped
            const ccOut = cfCalls.filter(cc => (cc.callDate||"").slice(0,7)===mk && cc.status!=="funded")
              .reduce((s,cc) => s + (parseFloat(cc.callAmount)||0), 0);
            // Tax payments — scoped
            const taxOut = cfTax.filter(t => (t.dueDate||"").slice(0,7)===mk)
              .reduce((s,t) => s + (parseFloat(t.amount)||0), 0);
            // Fees (monthly pro-rated) — scoped
            const feeOut = cfFees.filter(f => f.active).reduce((s,f) => {
              const annual = parseFloat(f.amount)||0;
              return s + (f.frequency==="annual" ? annual/12 : f.frequency==="monthly" ? annual : 0);
            }, 0);
            // Recurring outflows (EMI, lifestyle, etc.) — pro-rated by frequency.
            // One-time outflows count only in the month their startDate falls in.
            const roOut = cfOutflows.filter(r => r.active!==false).reduce((s,r) => {
              const a = parseFloat(r.amount)||0;
              if (r.frequency === "one-time") {
                return (r.startDate||"").slice(0,7) === mk ? s + a : s;
              }
              return s + (r.frequency==="monthly" ? a : r.frequency==="quarterly" ? a/3 : a/12);
            }, 0);
            const totalIn  = divIncome + riIncome;
            const totalOut = ccOut + taxOut + feeOut + roOut;
            return { ...m, divIncome, riIncome, ccOut, taxOut, feeOut, roOut, totalIn, totalOut, net: totalIn - totalOut };
          });

          const maxVal = Math.max(...monthData.map(m => Math.max(m.totalIn, m.totalOut)), 1);

          return <React.Fragment>

            {/* Header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontSize:17,fontWeight:700}}>💸 Cash Flow &amp; Operations</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                  12-month forecast · Capital calls · Fee transparency · Document vault · Tax calendar
                </div>
              </div>
              {isAdmin && (
                <div style={{position:"relative"}}>
                  <button onClick={()=>setCfAddMenuOpen(p=>!p)}
                    style={{...primaryBtn(),fontSize:12,padding:"7px 16px"}}>
                    + Add Entry ▾
                  </button>
                  {cfAddMenuOpen && (
                    <>
                      <div onClick={()=>setCfAddMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:90}}/>
                      <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:100,
                        background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                        boxShadow:"0 10px 25px rgba(0,0,0,0.15)",minWidth:240,overflow:"hidden"}}>
                        {[
                          { type:"inflow",      label:"Inflow",        sub:"Salary, dividends, rent received", icon:"💰", color:"#1D9E75" },
                          { type:"outflow",     label:"Outflow",       sub:"EMI, lifestyle, school fees",       icon:"💸", color:"#A32D2D" },
                          { type:"tax",         label:"Tax Event",     sub:"Advance tax, capital gains tax",    icon:"📋", color:"#7C3AED" },
                          { type:"capitalCall", label:"Capital Call",  sub:"PE/AIF commitment due",             icon:"📞", color:"#D97706" },
                          { type:"fee",         label:"Fee",           sub:"Advisor fees, AMC fees",            icon:"💼", color:"#2563EB" },
                        ].map((opt, i, arr) => (
                          <button key={opt.type}
                            onClick={()=>{ setCfFormData({}); setCfModal({type:opt.type}); setCfAddMenuOpen(false); }}
                            style={{display:"flex",alignItems:"center",gap:10,width:"100%",
                              background:"transparent",border:"none",padding:"10px 14px",cursor:"pointer",
                              fontSize:12,color:T.textPrimary,textAlign:"left",
                              borderBottom:i<arr.length-1?`1px solid ${T.cardBorder}`:"none"}}
                            onMouseEnter={e=>e.currentTarget.style.background=T.thBg}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{fontSize:16,width:20,textAlign:"center"}}>{opt.icon}</span>
                            <div>
                              <div style={{fontWeight:600,color:opt.color}}>{opt.label}</div>
                              <div style={{fontSize:10,color:T.textSecondary,marginTop:1}}>{opt.sub}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Scope selector — admin only */}
            
            {/* KPI strip */}
            {(function(){
              const nextMonth = monthData[0];
              const quarter   = monthData.slice(0,3);
              const qIn  = quarter.reduce((s,m)=>s+m.totalIn,0);
              const qOut = quarter.reduce((s,m)=>s+m.totalOut,0);
              const pendingCalls = cfCalls.filter(cc=>cc.status!=="funded").reduce((s,cc)=>s+parseFloat(cc.callAmount||0),0);
              const nextTax = cfTax.sort((a,b)=>new Date(a.dueDate)-new Date(b.dueDate)).find(t=>new Date(t.dueDate)>=today);
              return (
                <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:12,marginBottom:20}}>
                  {[
                    {label:"This Month In",   value:fmt(convert(nextMonth.totalIn,dc),dc,true),  col:"#1D9E75", icon:"↓", sub:`Div ${fmt(convert(nextMonth.divIncome,dc),dc,true)} + Recurring ${fmt(convert(nextMonth.riIncome,dc),dc,true)}`},
                    {label:"This Month Out",  value:fmt(convert(nextMonth.totalOut,dc),dc,true), col:"#A32D2D", icon:"↑", sub:`Outflows ${fmt(convert(nextMonth.roOut||0,dc),dc,true)} · Tax ${fmt(convert(nextMonth.taxOut,dc),dc,true)} · Fees ${fmt(convert(nextMonth.feeOut,dc),dc,true)} · Calls ${fmt(convert(nextMonth.ccOut,dc),dc,true)}`},
                    {label:"Q1 Net Flow",     value:`${qIn-qOut>=0?"+":""}${fmt(convert(qIn-qOut,dc),dc,true)}`, col:qIn-qOut>=0?"#1D9E75":"#A32D2D", icon:"≈", sub:`In ${fmt(convert(qIn,dc),dc,true)} · Out ${fmt(convert(qOut,dc),dc,true)}`},
                    {label:"Pending Calls",   value:fmt(convert(pendingCalls,dc),dc,true), col:"#D97706", icon:"📞", sub:nextTax?`Next tax: ${new Date(nextTax.dueDate).toLocaleDateString("en-US",{month:"short",day:"numeric"})} — ${fmt(convert(parseFloat(nextTax.amount),dc),dc,true)}`:"No tax events upcoming"},
                  ].map((k,i) => (
                    <div key={i} style={{...tCard(),padding:"14px 16px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{k.label}</div>
                      <div style={{fontSize:20,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.icon} {k.value}</div>
                      <div style={{fontSize:10,color:T.textMuted,marginTop:4}}>{k.sub}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Sub-tabs */}
            <div style={{display:"flex",gap:4,marginBottom:16,background:T.borderSubtle,borderRadius:10,padding:4,flexWrap:"wrap"}}>
              {[
                {id:"forecast",  label:"📊 12-Month Forecast"},
                {id:"inflows",   label:"💰 Recurring Inflows"},
                {id:"calls",     label:"📞 Capital Calls"},
                {id:"fees",      label:"💼 Fee Transparency"},
                {id:"tax",       label:"🧾 Tax Calendar"},
                {id:"docs",      label:"📁 Document Vault"},
              ].map(st => (
                <button key={st.id} onClick={()=>setCfTab(st.id)}
                  style={{flex:1,padding:"7px 12px",borderRadius:7,border:"none",cursor:"pointer",fontSize:12,fontWeight:cfTab===st.id?700:400,
                    background:cfTab===st.id?T.cardBg:"transparent",
                    color:cfTab===st.id?T.brand:T.textSecondary,
                    boxShadow:cfTab===st.id?"0 1px 4px rgba(0,0,0,0.08)":"none",
                    transition:"all 0.15s",whiteSpace:"nowrap"}}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* ── FORECAST SUB-TAB ─────────────────────────────────── */}
            {cfTab==="forecast" && (
              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:13,fontWeight:700}}>12-Month Cash Flow Forecast</div>
                  <div style={{display:"flex",gap:12,fontSize:11,color:T.textSecondary,flexWrap:"wrap"}}>
                    <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:4,background:"#1D9E75",borderRadius:2,display:"inline-block"}}/>Inflows</span>
                    <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:4,background:"#A32D2D",borderRadius:2,display:"inline-block"}}/>Outflows</span>
                    <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:4,background:"#2563EB",borderRadius:2,display:"inline-block",opacity:0.6}}/>Net</span>
                  </div>
                </div>
                <div style={{padding:"16px 18px",overflowX:"auto"}}>
                  {/* Bar chart */}
                  <div style={{display:"flex",gap:6,alignItems:"flex-end",minWidth:600,height:160,paddingBottom:24,position:"relative"}}>
                    {/* Zero line */}
                    <div style={{position:"absolute",left:0,right:0,bottom:24,height:1,background:T.borderSubtle}}/>
                    {monthData.map((m,i) => {
                      const inH  = (m.totalIn  / maxVal) * 120;
                      const outH = (m.totalOut / maxVal) * 120;
                      const netH = (Math.abs(m.net) / maxVal) * 120;
                      const isPast = m.date < today;
                      return (
                        <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                          {/* Bars */}
                          <div style={{display:"flex",gap:2,alignItems:"flex-end",height:130}}>
                            <div title={`Inflow: ${fmt(convert(m.totalIn,dc),dc)}`}
                              style={{width:14,height:Math.max(inH,2),background:"#1D9E75",borderRadius:"3px 3px 0 0",opacity:isPast?0.45:0.9,transition:"height 0.3s"}}/>
                            <div title={`Outflow: ${fmt(convert(m.totalOut,dc),dc)}`}
                              style={{width:14,height:Math.max(outH,2),background:"#A32D2D",borderRadius:"3px 3px 0 0",opacity:isPast?0.45:0.9,transition:"height 0.3s"}}/>
                            <div title={`Net: ${fmt(convert(m.net,dc),dc)}`}
                              style={{width:8,height:Math.max(netH,2),background:m.net>=0?"#2563EB":"#7C3AED",borderRadius:"3px 3px 0 0",opacity:isPast?0.3:0.7}}/>
                          </div>
                          <div style={{fontSize:9,color:T.textMuted,whiteSpace:"nowrap"}}>{m.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Table */}
                  <div style={{overflowX:"auto",marginTop:8}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:600}}>
                      <thead>
                        <tr>{["Month","Dividends","Recurring In","Capital Calls","Tax","Fees","Total In","Total Out","Net"].map(h=>(
                          <th key={h} style={tTh({fontSize:9,padding:"6px 8px"})}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {monthData.map((m,i) => (
                          <tr key={m.key} style={{background:i%2===0?"transparent":T.tableStripe,opacity:m.date<today?0.65:1}}>
                            <td style={tTd({fontWeight:600,padding:"6px 8px",fontSize:10})}>{m.label}</td>
                            <td style={tTd({color:"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.divIncome>0?fmt(convert(m.divIncome,dc),dc):"—"}</td>
                            <td style={tTd({color:"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.riIncome>0?fmt(convert(m.riIncome,dc),dc):"—"}</td>
                            <td style={tTd({color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.ccOut>0?fmt(convert(m.ccOut,dc),dc):"—"}</td>
                            <td style={tTd({color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.taxOut>0?fmt(convert(m.taxOut,dc),dc):"—"}</td>
                            <td style={tTd({color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.feeOut>0?fmt(convert(m.feeOut,dc),dc):"—"}</td>
                            <td style={tTd({fontWeight:700,color:"#1D9E75",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{fmt(convert(m.totalIn,dc),dc)}</td>
                            <td style={tTd({fontWeight:700,color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.totalOut>0?fmt(convert(m.totalOut,dc),dc):"—"}</td>
                            <td style={tTd({fontWeight:700,color:m.net>=0?"#1D9E75":"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"6px 8px",fontSize:10})}>{m.net>=0?"+":""}{fmt(convert(m.net,dc),dc)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── RECURRING INFLOWS ─────────────────────────────────── */}
            {cfTab==="inflows" && (
              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>💰 Recurring Inflows &amp; SIP Planner</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                      Total monthly: <span style={{fontWeight:700,color:"#1D9E75"}}>
                        {fmt(convert(cfRecurring.filter(r=>r.active!==false).reduce((s,r)=>{
                          const a=parseFloat(r.amount||0);
                          return s+(r.frequency==="monthly"?a:r.frequency==="quarterly"?a/3:a/12);
                        },0),dc),dc)}/mo</span>
                      &nbsp;·&nbsp;Annual: <span style={{fontWeight:700,color:"#1D9E75"}}>
                        {fmt(convert(cfRecurring.filter(r=>r.active!==false).reduce((s,r)=>{
                          const a=parseFloat(r.amount||0);
                          return s+(r.frequency==="monthly"?a*12:r.frequency==="quarterly"?a*4:a);
                        },0),dc),dc)}/yr</span>
                    </div>
                  </div>
                  {isAdmin && <button onClick={()=>{ setCfFormData({}); setCfModal({type:"inflow"}); }}
                    style={{background:"rgba(22,163,74,0.1)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:7,color:"#1D9E75",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Source
                  </button>}
                </div>
                {cfRecurring.length===0?(
                  <div style={{padding:48,textAlign:"center",color:T.textMuted,fontSize:13}}>
                    <div style={{fontSize:32,marginBottom:12}}>💰</div>
                    <div style={{fontWeight:600,marginBottom:6}}>No recurring inflows added yet</div>
                    <div style={{fontSize:12}}>Add rental income, salary, SIP redemptions, pension, royalties…</div>
                  </div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>{["Source","Type","Amount","Frequency","Next Date","Currency","Active","Actions"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {cfRecurring.map((r,i)=>(
                        <tr key={r.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                          <td style={tTd({fontWeight:600})}>{r.name}</td>
                          <td style={tTd()}><span style={badge()}>{r.type||"Other"}</span></td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#1D9E75",fontWeight:600})}>{fmt(parseFloat(r.amount||0),r.currency||dc)}</td>
                          <td style={tTd({fontSize:11})}>{r.frequency||"Monthly"}</td>
                          <td style={tTd({fontSize:11})}>{r.startDate||"—"}</td>
                          <td style={tTd({fontSize:11})}>{r.currency||dc}</td>
                          <td style={tTd()}>
                            <div onClick={()=>saveRI(recurringInflows.map(x=>x.id===r.id?{...x,active:!x.active}:x))}
                              style={{width:32,height:18,borderRadius:9,background:r.active!==false?"#1D9E75":"#e2e8f0",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                              <div style={{width:14,height:14,borderRadius:"50%",background:"white",position:"absolute",top:2,left:r.active!==false?16:2,transition:"left 0.2s"}}/>
                            </div>
                          </td>
                          <td style={tTd()}>
                            {isAdmin&&<button onClick={()=>saveRI(recurringInflows.filter(x=>x.id!==r.id))}
                              style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 8px",cursor:"pointer",fontSize:11}}>✕</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── RECURRING OUTFLOWS ─────────────────────────────────── */}
            {cfTab==="inflows" && (
              <div style={{...tCard(),padding:0,overflow:"hidden",marginTop:14}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>💸 Recurring &amp; One-Time Outflows</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:1}}>
                      Total monthly: <span style={{fontWeight:700,color:"#A32D2D"}}>
                        {fmt(convert(cfOutflows.filter(r=>r.active!==false && r.frequency!=="one-time").reduce((s,r)=>{
                          const a=parseFloat(r.amount||0);
                          return s+(r.frequency==="monthly"?a:r.frequency==="quarterly"?a/3:a/12);
                        },0),dc),dc)}/mo</span>
                      &nbsp;·&nbsp;Annual: <span style={{fontWeight:700,color:"#A32D2D"}}>
                        {fmt(convert(cfOutflows.filter(r=>r.active!==false && r.frequency!=="one-time").reduce((s,r)=>{
                          const a=parseFloat(r.amount||0);
                          return s+(r.frequency==="monthly"?a*12:r.frequency==="quarterly"?a*4:a);
                        },0),dc),dc)}/yr</span>
                      {(() => {
                        const oneTimeTotal = cfOutflows.filter(r=>r.active!==false && r.frequency==="one-time").reduce((s,r)=>s+parseFloat(r.amount||0),0);
                        if (oneTimeTotal > 0) return (
                          <>&nbsp;·&nbsp;One-time pending: <span style={{fontWeight:700,color:"#D97706"}}>
                            {fmt(convert(oneTimeTotal,dc),dc)}
                          </span></>
                        );
                        return null;
                      })()}
                    </div>
                  </div>
                  {isAdmin && <button onClick={()=>{ setCfFormData({}); setCfModal({type:"outflow"}); }}
                    style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:7,color:"#A32D2D",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Outflow
                  </button>}
                </div>
                {cfOutflows.length===0?(
                  <div style={{padding:48,textAlign:"center",color:T.textMuted,fontSize:13}}>
                    <div style={{fontSize:32,marginBottom:12}}>💸</div>
                    <div style={{fontWeight:600,marginBottom:6}}>No outflows added yet</div>
                    <div style={{fontSize:12}}>Track EMIs, lifestyle expenses, school fees, insurance premiums, charity, one-time spends…</div>
                  </div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>{["Description","Category","Amount","Frequency","Date","Currency","Active","Actions"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {cfOutflows.map((r,i)=>(
                        <tr key={r.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                          <td style={tTd({fontWeight:600})}>{r.name}{r.notes && <div style={{fontSize:10,fontWeight:400,color:T.textSecondary,marginTop:2}}>{r.notes}</div>}</td>
                          <td style={tTd()}><span style={{...badge(),background:"rgba(220,38,38,0.08)",color:"#A32D2D"}}>{r.type||"Other"}</span></td>
                          <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#A32D2D",fontWeight:600})}>{fmt(parseFloat(r.amount||0),r.currency||dc)}</td>
                          <td style={tTd({fontSize:11})}>{r.frequency==="one-time" ? <span style={{color:"#D97706",fontWeight:600}}>One-time</span> : (r.frequency||"Monthly")}</td>
                          <td style={tTd({fontSize:11})}>{r.startDate||"—"}</td>
                          <td style={tTd({fontSize:11})}>{r.currency||dc}</td>
                          <td style={tTd()}>
                            <div onClick={()=>saveRO(recurringOutflows.map(x=>x.id===r.id?{...x,active:!x.active}:x))}
                              style={{width:32,height:18,borderRadius:9,background:r.active!==false?"#A32D2D":"#e2e8f0",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                              <div style={{width:14,height:14,borderRadius:"50%",background:"white",position:"absolute",top:2,left:r.active!==false?16:2,transition:"left 0.2s"}}/>
                            </div>
                          </td>
                          <td style={tTd()}>
                            {isAdmin&&<button onClick={()=>saveRO(recurringOutflows.filter(x=>x.id!==r.id))}
                              style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 8px",cursor:"pointer",fontSize:11}}>✕</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── SIP / Recurring Projection ── */}
            {cfTab==="inflows" && cfRecurring.filter(r=>r.active!==false).length>0 && (function(){
              const activeRI = cfRecurring.filter(r=>r.active!==false);
              const monthlyTotal = activeRI.reduce((s,r)=>{
                const a=parseFloat(r.amount||0);
                return s+(r.frequency==="monthly"?a:r.frequency==="quarterly"?a/3:a/12);
              },0);
              const rate=0.06, years=[1,2,3,5,7,10];
              const fv = n => monthlyTotal>0
                ? monthlyTotal*((Math.pow(1+rate/12,n*12)-1)/(rate/12))
                : monthlyTotal*12*n;
              const maxFV=fv(10);
              return (
                <div style={{...tCard(),marginTop:12,padding:"16px 20px"}}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>📈 SIP / Recurring Projection</div>
                  <div style={{fontSize:11,color:T.textSecondary,marginBottom:16}}>
                    Projected corpus @ 6% p.a. compounding · {fmt(convert(monthlyTotal,dc),dc)}/month
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(3,1fr)":"repeat(6,1fr)",gap:10,marginBottom:16}}>
                    {years.map(y=>{
                      const corpus=fv(y);
                      const barH=Math.round((corpus/maxFV)*80);
                      return (
                        <div key={y} style={{textAlign:"center"}}>
                          <div style={{height:80,display:"flex",alignItems:"flex-end",justifyContent:"center",marginBottom:4}}>
                            <div style={{width:32,height:Math.max(barH,4),
                              background:`linear-gradient(180deg,${T.brand},${T.brand}88)`,
                              borderRadius:"4px 4px 0 0"}}/>
                          </div>
                          <div style={{fontSize:10,fontWeight:700,color:T.textPrimary,fontFamily:"'Inter', system-ui, sans-serif"}}>
                            {corpus>=10000000?`${(corpus/10000000).toFixed(1)}Cr`:corpus>=100000?`${(corpus/100000).toFixed(1)}L`:`${(corpus/1000).toFixed(0)}K`}
                          </div>
                          <div style={{fontSize:9,color:T.textMuted}}>{y}Y</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{fontSize:11,color:T.textMuted,borderTop:`1px solid ${T.borderSubtle}`,paddingTop:10,display:"flex",gap:20,flexWrap:"wrap"}}>
                    <span>Monthly: <strong style={{color:T.textPrimary}}>{fmt(convert(monthlyTotal,dc),dc)}</strong></span>
                    <span>Annual: <strong style={{color:T.textPrimary}}>{fmt(convert(monthlyTotal*12,dc),dc)}</strong></span>
                    <span>10Y corpus: <strong style={{color:T.brand}}>{fmt(convert(fv(10),dc),dc)}</strong></span>
                  </div>
                </div>
              );
            })()}

            {/* ── CAPITAL CALLS ─────────────────────────────────────── */}
            {cfTab==="calls" && (
              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>📞 Capital Calls — PE/VC</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>Track committed capital vs called vs distributed for private equity investments</div>
                  </div>
                  {isAdmin&&<button onClick={()=>{ setCfFormData({}); setCfModal({type:"capitalCall"}); }}
                    style={{background:"rgba(217,119,6,0.1)",border:"1px solid rgba(217,119,6,0.3)",borderRadius:7,color:"#D97706",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Call
                  </button>}
                </div>
                {cfCalls.length===0?(
                  <div style={{padding:48,textAlign:"center",color:T.textMuted,fontSize:13}}>
                    <div style={{fontSize:32,marginBottom:12}}>📞</div>
                    <div style={{fontWeight:600,marginBottom:6}}>No capital calls recorded</div>
                    <div style={{fontSize:12}}>Track PE/VC draw-downs — committed capital, called amount, expected distribution dates</div>
                  </div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>{["Fund","Committed","Called","Remaining","Call Date","Distribution Est.","Status","Actions"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {cfCalls.map((cc,i)=>{
                        const called  = parseFloat(cc.callAmount||0);
                        const committed = parseFloat(cc.committed||0);
                        const remaining = committed - called;
                        const pct = committed>0?(called/committed*100):0;
                        const isPast = cc.callDate && new Date(cc.callDate)<today;
                        return (
                          <tr key={cc.id} style={{background:i%2===0?"transparent":T.tableStripe}}>
                            <td style={tTd({fontWeight:600})}>{cc.name}<div style={{fontSize:10,color:T.textMuted}}>{cc.manager||""}</div></td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(convert(committed,dc),dc)}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#D97706",fontWeight:600})}>
                              {fmt(convert(called,dc),dc)}
                              <div style={{width:"100%",height:3,background:T.borderSubtle,borderRadius:2,marginTop:3}}>
                                <div style={{width:`${Math.min(pct,100)}%`,height:"100%",background:"#D97706",borderRadius:2}}/>
                              </div>
                              <div style={{fontSize:9,color:T.textMuted}}>{pct.toFixed(0)}% of committed</div>
                            </td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#A32D2D"})}>{remaining>0?fmt(convert(remaining,dc),dc):"—"}</td>
                            <td style={tTd({fontSize:11,color:isPast?"#A32D2D":T.textPrimary})}>{cc.callDate||"—"}{isPast&&" ⚠"}</td>
                            <td style={tTd({fontSize:11,color:T.textSecondary})}>{cc.distributionDate||"TBD"}</td>
                            <td style={tTd()}>
                              <span style={{...badge(),background:cc.status==="funded"?"rgba(22,163,74,0.1)":cc.status==="overdue"?"rgba(220,38,38,0.1)":"rgba(217,119,6,0.1)",color:cc.status==="funded"?"#1D9E75":cc.status==="overdue"?"#A32D2D":"#D97706"}}>
                                {cc.status||"Pending"}
                              </span>
                            </td>
                            <td style={tTd()}>
                              <div style={{display:"flex",gap:4}}>
                                {isAdmin&&cc.status!=="funded"&&<button onClick={()=>saveCC(capitalCalls.map(x=>x.id===cc.id?{...x,status:"funded"}:x))}
                                  style={{background:"rgba(22,163,74,0.1)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:4,color:"#1D9E75",padding:"2px 8px",cursor:"pointer",fontSize:10}}>✓ Funded</button>}
                                {isAdmin&&<button onClick={()=>requireConfirm(`Remove capital call for ${cc.name}?`,()=>saveCC(capitalCalls.filter(x=>x.id!==cc.id)))}
                                  style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 6px",cursor:"pointer",fontSize:11}}>✕</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── FEE TRANSPARENCY ─────────────────────────────────── */}
            {cfTab==="fees" && (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                  <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700}}>💼 Fee Transparency</div>
                      <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>Management fees, advisory fees, and brokerage costs paid to external managers</div>
                    </div>
                    {isAdmin&&<button onClick={()=>{ setCfFormData({}); setCfModal({type:"fee"}); }}
                      style={{background:"rgba(37,99,235,0.1)",border:"1px solid rgba(37,99,235,0.3)",borderRadius:7,color:"#2563EB",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                      + Add Fee
                    </button>}
                  </div>
                  {cfFees.length===0?(
                    <div style={{padding:40,textAlign:"center",color:T.textMuted,fontSize:13}}>
                      <div style={{fontSize:32,marginBottom:12}}>💼</div>
                      <div style={{fontWeight:600,marginBottom:6}}>No fees recorded</div>
                      <div style={{fontSize:12}}>Track management fees, advisory fees, fund expense ratios, brokerage commissions</div>
                    </div>
                  ):(
                    <React.Fragment>
                      {/* Fee summary KPIs */}
                      {(function(){
                        const totalAnnual = cfFees.filter(f=>f.active).reduce((s,f)=>{
                          const a = parseFloat(f.amount||0);
                          return s + (f.frequency==="monthly"?a*12:a);
                        },0);
                        const totalAUM = filteredHW.reduce((s,h)=>s+h.valueUSD,0)||1;
                        const feePct = totalAUM>0?(totalAnnual/totalAUM*100):0;
                        return (
                          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,padding:"14px 18px",borderBottom:`1px solid ${T.borderSubtle}`}}>
                            {[
                              {label:"Total Annual Fees",  value:fmt(convert(totalAnnual,dc),dc), col:"#A32D2D"},
                              {label:"Monthly Run Rate",   value:fmt(convert(totalAnnual/12,dc),dc), col:"#D97706"},
                              {label:"Fee Drag (% AUM)",  value:`${feePct.toFixed(2)}%`, col:feePct>1.5?"#A32D2D":feePct>0.75?"#D97706":"#1D9E75"},
                            ].map((k,i)=>(
                              <div key={i} style={{textAlign:"center"}}>
                                <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{k.label}</div>
                                <div style={{fontSize:18,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",color:k.col}}>{k.value}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      <table style={{width:"100%",borderCollapse:"collapse"}}>
                        <thead><tr>{["Manager/Fund","Fee Type","Amount","Frequency","Annual Cost","AUM %","Active","Actions"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                        <tbody>
                          {cfFees.map((f,i)=>{
                            const annual = parseFloat(f.amount||0)*(f.frequency==="monthly"?12:1);
                            const totalAUM = filteredHW.reduce((s,h)=>s+h.valueUSD,0)||1;
                            const aumPct = (annual/totalAUM*100);
                            return (
                              <tr key={f.id} style={{background:i%2===0?"transparent":T.tableStripe,opacity:f.active===false?0.5:1}}>
                                <td style={tTd({fontWeight:600})}>{f.manager}<div style={{fontSize:10,color:T.textMuted}}>{f.notes||""}</div></td>
                                <td style={tTd()}><span style={badge()}>{f.feeType||"Management"}</span></td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif"})}>{fmt(parseFloat(f.amount||0),f.currency||"USD")}</td>
                                <td style={tTd({fontSize:11})}>{f.frequency||"Annual"}</td>
                                <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#A32D2D",fontWeight:600})}>{fmt(convert(annual,dc),dc)}</td>
                                <td style={tTd({fontSize:11,color:aumPct>1.5?"#A32D2D":aumPct>0.75?"#D97706":"#1D9E75",fontWeight:600})}>{aumPct.toFixed(2)}%</td>
                                <td style={tTd()}>
                                  <div onClick={()=>saveFee(feeEntries.map(x=>x.id===f.id?{...x,active:!x.active}:x))}
                                    style={{width:32,height:18,borderRadius:9,background:f.active!==false?"#1D9E75":"#e2e8f0",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                                    <div style={{width:14,height:14,borderRadius:"50%",background:"white",position:"absolute",top:2,left:f.active!==false?16:2,transition:"left 0.2s"}}/>
                                  </div>
                                </td>
                                <td style={tTd()}>
                                  {isAdmin&&<button onClick={()=>requireConfirm(`Remove fee for ${f.manager}?`,()=>saveFee(feeEntries.filter(x=>x.id!==f.id)))}
                                    style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 6px",cursor:"pointer",fontSize:11}}>✕</button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}

            {/* ── TAX CALENDAR ─────────────────────────────────────── */}
            {cfTab==="tax" && (
              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>🧾 Tax Calendar</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>Advance tax deadlines, LTCG obligations, TDS, GST, and other tax payments</div>
                  </div>
                  {isAdmin&&<button onClick={()=>{ setCfFormData({}); setCfModal({type:"tax"}); }}
                    style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:7,color:"#A32D2D",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Tax Event
                  </button>}
                </div>
                {cfTax.length===0?(
                  <div style={{padding:40,textAlign:"center",color:T.textMuted,fontSize:13}}>
                    <div style={{fontSize:32,marginBottom:12}}>🧾</div>
                    <div style={{fontWeight:600,marginBottom:6}}>No tax events</div>
                    <div style={{fontSize:12}}>Add advance tax dates, LTCG payments, TDS obligations, foreign tax remittances</div>
                    {/* Pre-populate with India advance tax dates hint */}
                    {isAdmin&&<button onClick={()=>{
                        const year = today.getFullYear();
                        const indiaTax = [
                          {id:Date.now()+1, name:"Advance Tax Q1",  type:"Advance Tax",   amount:"0", currency:"INR", dueDate:`${year}-06-15`, notes:"15% of annual tax liability", status:"pending"},
                          {id:Date.now()+2, name:"Advance Tax Q2",  type:"Advance Tax",   amount:"0", currency:"INR", dueDate:`${year}-09-15`, notes:"45% of annual tax liability", status:"pending"},
                          {id:Date.now()+3, name:"Advance Tax Q3",  type:"Advance Tax",   amount:"0", currency:"INR", dueDate:`${year}-12-15`, notes:"75% of annual tax liability", status:"pending"},
                          {id:Date.now()+4, name:"Advance Tax Q4",  type:"Advance Tax",   amount:"0", currency:"INR", dueDate:`${year+1}-03-15`, notes:"100% of annual tax liability", status:"pending"},
                          {id:Date.now()+5, name:"ITR Filing",      type:"Filing",        amount:"0", currency:"INR", dueDate:`${year+1}-07-31`, notes:"Income Tax Return due date", status:"pending"},
                        ];
                        saveTax(indiaTax);
                        showToast("✓ India advance tax calendar pre-filled","#1D9E75");
                      }}
                      style={{marginTop:16,background:"rgba(37,99,235,0.08)",border:"1px solid rgba(37,99,235,0.3)",borderRadius:8,color:"#2563EB",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                      📅 Pre-fill India Advance Tax Dates
                    </button>}
                  </div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>{["Event","Type","Due Date","Amount","Currency","Days Away","Status","Actions"].map(h=><th key={h} style={tTh()}>{h}</th>)}</tr></thead>
                    <tbody>
                      {[...taxEvents].sort((a,b)=>new Date(a.dueDate)-new Date(b.dueDate)).map((t,i)=>{
                        const dueDate  = new Date(t.dueDate);
                        const daysAway = Math.round((dueDate-today)/(1000*60*60*24));
                        const isOverdue = daysAway < 0;
                        const isUrgent  = daysAway >= 0 && daysAway <= 14;
                        return (
                          <tr key={t.id} style={{background:i%2===0?"transparent":T.tableStripe,opacity:t.status==="paid"?0.5:1}}>
                            <td style={tTd({fontWeight:600})}>{t.name}<div style={{fontSize:10,color:T.textMuted}}>{t.notes||""}</div></td>
                            <td style={tTd()}><span style={badge()}>{t.type||"Tax"}</span></td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",fontWeight:600,color:isOverdue?"#A32D2D":isUrgent?"#D97706":T.textPrimary})}>{t.dueDate}</td>
                            <td style={tTd({fontFamily:"'Inter', system-ui, sans-serif",color:"#A32D2D",fontWeight:600})}>{parseFloat(t.amount||0)>0?fmt(convert(parseFloat(t.amount),dc),dc):"TBD"}</td>
                            <td style={tTd({fontSize:11})}>{t.currency||"INR"}</td>
                            <td style={tTd()}>
                              <span style={{fontSize:11,fontWeight:700,color:isOverdue?"#A32D2D":isUrgent?"#D97706":"#1D9E75"}}>
                                {isOverdue?`${Math.abs(daysAway)}d overdue`:daysAway===0?"Today":isUrgent?`${daysAway}d`:`${daysAway}d`}
                              </span>
                            </td>
                            <td style={tTd()}>
                              <span style={{...badge(),background:t.status==="paid"?"rgba(22,163,74,0.1)":isOverdue?"rgba(220,38,38,0.1)":isUrgent?"rgba(217,119,6,0.1)":"rgba(37,99,235,0.06)",
                                color:t.status==="paid"?"#1D9E75":isOverdue?"#A32D2D":isUrgent?"#D97706":"#2563EB"}}>
                                {t.status==="paid"?"✓ Paid":isOverdue?"Overdue":isUrgent?"Due Soon":"Upcoming"}
                              </span>
                            </td>
                            <td style={tTd()}>
                              <div style={{display:"flex",gap:4}}>
                                {isAdmin&&t.status!=="paid"&&<button onClick={()=>saveTax(taxEvents.map(x=>x.id===t.id?{...x,status:"paid"}:x))}
                                  style={{background:"rgba(22,163,74,0.1)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:4,color:"#1D9E75",padding:"2px 8px",cursor:"pointer",fontSize:10}}>✓ Paid</button>}
                                {isAdmin&&<button onClick={()=>requireConfirm(`Remove tax event "${t.name}"?`,()=>saveTax(taxEvents.filter(x=>x.id!==t.id)))}
                                  style={{background:"rgba(220,38,38,0.08)",border:"1px solid #A32D2D",borderRadius:4,color:"#A32D2D",padding:"2px 6px",cursor:"pointer",fontSize:11}}>✕</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── DOCUMENT VAULT ───────────────────────────────────── */}
            {cfTab==="docs" && (
              <div style={{...tCard(),padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px 10px",borderBottom:`1px solid ${T.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>📁 Document Vault</div>
                    <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>K-1 tax forms, property deeds, fund agreements, NAV statements, account statements</div>
                  </div>
                  {isAdmin&&<button onClick={()=>{ setCfFormData({}); setCfModal({type:"doc"}); }}
                    style={{background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.3)",borderRadius:7,color:"#7C3AED",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    + Add Document
                  </button>}
                </div>
                {cfDocs.length===0?(
                  <div style={{padding:40,textAlign:"center",color:T.textMuted,fontSize:13}}>
                    <div style={{fontSize:32,marginBottom:12}}>📁</div>
                    <div style={{fontWeight:600,marginBottom:6}}>Document vault is empty</div>
                    <div style={{fontSize:12}}>Add K-1 forms, property deeds, fund agreements, NAV statements, account statements</div>
                  </div>
                ):(
                  <div style={{padding:16,display:"grid",gridTemplateColumns:isMobile?"repeat(1,1fr)":"repeat(3,1fr)",gap:12}}>
                    {cfDocs.map(doc=>{
                      const icons = {"PDF":"📄","Excel":"📊","Word":"📝","Image":"🖼️","Other":"📎"};
                      return (
                        <div key={doc.id} style={{background:T.borderSubtle,borderRadius:10,padding:"12px 14px",
                          display:"flex",alignItems:"flex-start",gap:10,
                          border:`1px solid ${T.cardBorder}`,cursor:"pointer"}}
                          onClick={()=>doc.url&&window.open(doc.url,"_blank")}>
                          <div style={{fontSize:24,flexShrink:0}}>{icons[doc.fileType]||"📎"}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,color:T.textPrimary,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name}</div>
                            <div style={{fontSize:10,color:T.textMuted}}>{doc.category||"General"} · {doc.date||"—"}</div>
                            {doc.notes&&<div style={{fontSize:10,color:T.textSecondary,marginTop:3}}>{doc.notes}</div>}
                            {doc.url&&<div style={{fontSize:10,color:"#2563EB",marginTop:3}}>🔗 View document</div>}
                          </div>
                          {isAdmin&&<button onClick={e=>{e.stopPropagation();requireConfirm(`Remove document "${doc.name}"?`,()=>saveDoc(documents.filter(x=>x.id!==doc.id)));}}
                            style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontSize:14,flexShrink:0}}>✕</button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── ADD MODALS ───────────────────────────────────────── */}
            {cfModal && (function(){
              const close = () => { setCfModal(null); setCfFormData({}); };
              const type  = cfModal.type;
              const form  = cfFormData;
              const setForm = setCfFormData;
              const sf = (k,v) => setCfFormData(p=>({...p,[k]:v}));

              const titleMap = { inflow:"💰 Add Recurring Inflow", capitalCall:"📞 Add Capital Call", fee:"💼 Add Fee Entry", tax:"🧾 Add Tax Event", doc:"📁 Add Document" };
              return (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(4px)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                  <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,padding:28,width:"100%",maxWidth:480,boxShadow:"0 24px 80px rgba(0,0,0,0.4)",animation:"scaleIn 0.18s ease",maxHeight:"90vh",overflowY:"auto"}}>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:20}}>{titleMap[type]}</div>

                    {/* Inflow form */}
                    {type==="inflow"&&<React.Fragment>
                      {[["Source Name","name","text"],["Amount","amount","number"],["Type","type","select"],["Frequency","frequency","select"],["Start Date","startDate","date"],["Currency","currency","select"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"&&key==="type"?<select style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)}>{["Rental Income","Salary","Pension","Dividend","SIP Redemption","Royalty","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="frequency"?<select style={tInp()} value={form[key]||"monthly"} onChange={e=>sf(key,e.target.value)}>{["monthly","quarterly","annual"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="currency"?<select style={tInp()} value={form[key]||dc} onChange={e=>sf(key,e.target.value)}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    {/* Outflow form — recurring expenses (EMI, lifestyle, school fees, charity)
                        OR one-time outflows (wedding, down payment, vacation) */}
                    {type==="outflow"&&<React.Fragment>
                      {[["Description","name","text"],["Amount","amount","number"],["Category","type","select"],["Frequency","frequency","select"],["Start Date / Due Date","startDate","date"],["Currency","currency","select"],["Notes","notes","text"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"&&key==="type"?<select style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)}>{["Lifestyle","EMI / Loan","Education","Insurance","Healthcare","Charity","Rent","Utilities","Travel","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="frequency"?<select style={tInp()} value={form[key]||"monthly"} onChange={e=>sf(key,e.target.value)}>{["monthly","quarterly","annual","one-time"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="currency"?<select style={tInp()} value={form[key]||dc} onChange={e=>sf(key,e.target.value)}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    {/* Capital call form */}
                    {type==="capitalCall"&&<React.Fragment>
                      {[["Fund Name","name","text"],["Manager","manager","text"],["Committed Capital","committed","number"],["Call Amount","callAmount","number"],["Call Date","callDate","date"],["Expected Distribution","distributionDate","date"],["Currency","currency","select"],["Notes","notes","text"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"?<select style={tInp()} value={form[key]||dc} onChange={e=>sf(key,e.target.value)}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    {/* Fee form */}
                    {type==="fee"&&<React.Fragment>
                      {/* AUM % calculator */}
                      <div style={{marginBottom:14,padding:"10px 12px",background:"rgba(37,99,235,0.06)",borderRadius:8,border:"1px solid rgba(37,99,235,0.15)"}}>
                        <div style={{fontSize:11,fontWeight:600,color:"#2563EB",marginBottom:8}}>💡 AUM % Calculator</div>
                        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                          <div style={{flex:1,minWidth:120}}>
                            <div style={{fontSize:10,color:T.textSecondary,marginBottom:3}}>AUM %</div>
                            <input type="number" step="0.01" placeholder="e.g. 1.5" id="fee-aum-pct"
                              style={{...tInp(),fontSize:12}}
                              onChange={e=>{
                                const pct = parseFloat(e.target.value||0);
                                const aum = cfHoldings.reduce((s,h)=>s+h.valueUSD,0);
                                const annual = aum*(pct/100);
                                setCfFormData(p=>({...p, amount:annual.toFixed(0), frequency:"annual",
                                  notes:`${pct}% of AUM (${fmt(convert(aum,dc),dc)})`}));
                              }}/>
                          </div>
                          <div style={{fontSize:11,color:T.textSecondary,padding:"0 4px"}}>×</div>
                          <div style={{flex:1,minWidth:120}}>
                            <div style={{fontSize:10,color:T.textSecondary,marginBottom:3}}>Scoped AUM</div>
                            <div style={{fontSize:12,fontWeight:700,fontFamily:"'Inter', system-ui, sans-serif",padding:"7px 10px",background:T.inputBg,border:`1px solid ${T.cardBorder}`,borderRadius:6}}>
                              {fmt(convert(cfHoldings.reduce((s,h)=>s+h.valueUSD,0),dc),dc)}
                            </div>
                          </div>
                          <div style={{fontSize:11,color:T.textSecondary,padding:"0 4px"}}>=</div>
                          <div style={{flex:1,minWidth:120}}>
                            <div style={{fontSize:10,color:T.textSecondary,marginBottom:3}}>Annual Fee</div>
                            <div style={{fontSize:12,fontWeight:700,color:"#A32D2D",fontFamily:"'Inter', system-ui, sans-serif",padding:"7px 10px",background:"rgba(220,38,38,0.05)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:6}}>
                              {cfFormData.amount>0?fmt(convert(parseFloat(cfFormData.amount||0),dc),dc):"—"}
                            </div>
                          </div>
                        </div>
                      </div>
                      {[["Manager/Fund","manager","text"],["Fee Type","feeType","select"],["Amount","amount","number"],["Frequency","frequency","select"],["Currency","currency","select"],["Notes","notes","text"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"&&key==="feeType"?<select style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)}>{["Management Fee","Advisory Fee","Performance Fee","Brokerage","Expense Ratio","Administration","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="frequency"?<select style={tInp()} value={form[key]||"annual"} onChange={e=>sf(key,e.target.value)}>{["monthly","annual"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="currency"?<select style={tInp()} value={form[key]||"USD"} onChange={e=>sf(key,e.target.value)}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    {/* Tax event form */}
                    {type==="tax"&&<React.Fragment>
                      {[["Event Name","name","text"],["Tax Type","type","select"],["Due Date","dueDate","date"],["Estimated Amount","amount","number"],["Currency","currency","select"],["Notes","notes","text"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"&&key==="type"?<select style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)}>{["Advance Tax","LTCG","STCG","TDS","GST","Filing","Foreign Tax","Wealth Tax","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"?<select style={tInp()} value={form[key]||dc} onChange={e=>sf(key,e.target.value)}>{CURRENCIES.map(c=><option key={c}>{c}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    {/* Document form */}
                    {type==="doc"&&<React.Fragment>
                      {[["Document Name","name","text"],["Category","category","select"],["Date","date","date"],["File Type","fileType","select"],["URL / Link","url","text"],["Notes","notes","text"]].map(([label,key,inputType])=>(
                        <div key={key} style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600}}>{label}</div>
                          {inputType==="select"&&key==="category"?<select style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)}>{["Tax Form","Property Deed","Fund Agreement","Account Statement","NAV Statement","KYC","Insurance","Legal","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :inputType==="select"&&key==="fileType"?<select style={tInp()} value={form[key]||"PDF"} onChange={e=>sf(key,e.target.value)}>{["PDF","Excel","Word","Image","Other"].map(o=><option key={o}>{o}</option>)}</select>
                          :<input type={inputType} style={tInp()} value={form[key]||""} onChange={e=>sf(key,e.target.value)} placeholder={label}/>}
                        </div>
                      ))}
                    </React.Fragment>}

                    <div style={{display:"flex",gap:8,marginTop:8}}>
                      <button onClick={()=>{
                          const id = Date.now();
                          const entry = {...form, id, active:true, status:"pending"};
                          if(type==="inflow")      saveRI([...recurringInflows, entry]);
                          if(type==="outflow")     saveRO([...recurringOutflows, entry]);
                          if(type==="capitalCall") saveCC([...capitalCalls, entry]);
                          if(type==="fee")         saveFee([...feeEntries, entry]);
                          if(type==="tax")         saveTax([...taxEvents, entry]);
                          if(type==="doc")         saveDoc([...documents, entry]);
                          showToast("✓ Entry saved","#1D9E75");
                          close();
                        }}
                        style={{flex:1,background:"#007878",border:"none",borderRadius:8,color:"white",padding:"10px",cursor:"pointer",fontSize:13,fontWeight:700}}>
                        Save
                      </button>
                      <button onClick={close} style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,borderRadius:8,color:T.textSecondary,padding:"10px 16px",cursor:"pointer",fontSize:13}}>Cancel</button>
                    </div>
                  </div>
                </div>
              );
            })()}

          </React.Fragment>
          } catch(_fwErr) { return (<div style={{padding:20,background:"#FEF2F2",border:"2px solid #A32D2D",borderRadius:8,margin:8,fontFamily:"monospace",fontSize:12}}><strong>Error in cashflow tab:</strong> {_fwErr.message}<br/><small style={{color:"#666"}}>{_fwErr.stack?.split("\n").slice(0,3).join(" | ")}</small></div>); }
        })()}

      </div>
      <style>{`
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes agent-dot{0%,80%,100%{transform:scale(0.6);opacity:0.4}40%{transform:scale(1);opacity:1}}
        @keyframes slideIn{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
        tbody tr:hover td { background: rgba(0,120,120,0.035) !important; transition: background 0.1s; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.28); }
        input, select, textarea { font-family: inherit; }
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:#FFFFFF}
        ::-webkit-scrollbar-thumb{background:#DDD0C0;border-radius:3px}
        input:focus,select:focus{outline:1px solid #2563EB}
        input[type="date"]{color-scheme:dark}
        button{-webkit-tap-highlight-color:transparent}
        @media(max-width:640px){
          table td,table th{padding:8px 8px!important;font-size:11px!important}
          .card-grid{grid-template-columns:1fr 1fr!important}
        }
      `}</style>



      {/* ══════════════════════════════════════════════
          PDF REPORT MODAL
      ══════════════════════════════════════════════ */}
      {pdfModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{ if(e.target===e.currentTarget) setPdfModal(false); }}>
          <div style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:16,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",scrollbarGutter:"stable",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.35)"}}>
            {/* Modal header */}
            <div style={{padding:"20px 36px 16px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,display:"flex",alignItems:"center",gap:8}}>📄 Generate Portfolio Report</div>
                <div style={{fontSize:11,color:T.textSecondary,marginTop:3}}>Export a professional PDF report with embedded branding and cover page</div>
              </div>
              <button onClick={()=>setPdfModal(false)} style={{background:"none",border:"none",color:T.textSecondary,fontSize:20,cursor:"pointer",lineHeight:1,padding:"2px 6px"}}>✕</button>
            </div>

            <div style={{padding:"20px 36px"}}>
              {/* For client logins — skip scope selector, auto-scope to their own portfolio */}
              {!isAdmin && loggedInClientId && (
                <div style={{marginBottom:14,padding:"10px 14px",background:`${T.brand}08`,border:`1px solid ${T.brand}22`,borderRadius:8,fontSize:12,color:T.textSecondary}}>
                  📄 Generating report for your portfolio · {visibleClients.find(c=>c.id===loggedInClientId)?.name||"My Portfolio"}
                </div>
              )}
              {/* Report Scope — admin only */}
              {isAdmin && <div style={{marginBottom:18}}>
                <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingLeft:2}}>Report Scope</div>
                <div style={{display:"flex",gap:10,marginBottom:12}}>
                  {[["family","👨‍👩‍👧 Family"],["individual","👤 Individual"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setPdfScope(v)}
                      style={{flex:1,padding:"10px 0",borderRadius:8,border:`1px solid ${pdfScope===v?"#2563EB":T.cardBorder}`,background:pdfScope===v?"rgba(37,99,235,0.1)":"transparent",color:pdfScope===v?"#2563EB":T.textSecondary,fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {l}
                    </button>
                  ))}
                </div>

                {pdfScope==="family" && (function(){
                  const famFiltered = pdfFamilySearch.length >= 1
                    ? visibleFamilies.filter(f => f.name.toLowerCase().includes(pdfFamilySearch.toLowerCase()))
                    : visibleFamilies;
                  const selFam = visibleFamilies.find(f=>f.id===pdfFamilyId);
                  return (
                    <div style={{position:"relative"}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:5}}>
                        Search Family <span style={{color:T.textLight}}>(type 3+ letters)</span>
                      </div>
                      <div style={{position:"relative"}}>
                        <input
                          value={pdfFamilySearch}
                          onChange={e=>{setPdfFamilySearch(e.target.value); if(!e.target.value) setPdfFamilyId("");}}
                          placeholder={selFam ? `${selFam.emoji} ${selFam.name}` : "Type family name…"}
                          style={{width:"100%",boxSizing:"border-box",background:T.inputBg,border:`1px solid ${T.inputBorder}`,borderRadius:8,color:T.textPrimary,padding:"9px 36px 9px 12px",fontSize:13,outline:"none"}}
                        />
                        {pdfFamilySearch && (
                          <button onClick={()=>{setPdfFamilySearch("");setPdfFamilyId("");}}
                            style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:14,lineHeight:1,padding:0}}>✕</button>
                        )}
                      </div>
                      {/* All families pill row if no search */}
                      {!pdfFamilySearch && (
                        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                          <button onClick={()=>{setPdfFamilyId("");setPdfFamilySearch("");}}
                            style={{padding:"4px 11px",borderRadius:20,border:`1px solid ${!pdfFamilyId?"#2563EB":T.cardBorder}`,background:!pdfFamilyId?"rgba(37,99,235,0.1)":"transparent",color:!pdfFamilyId?"#2563EB":T.textSecondary,fontSize:11,cursor:"pointer",fontWeight:!pdfFamilyId?600:400}}>
                            All Families
                          </button>
                          {visibleFamilies.map(f=>(
                            <button key={f.id} onClick={()=>{setPdfFamilyId(f.id);setPdfFamilySearch("");}}
                              style={{padding:"4px 11px",borderRadius:20,border:`1px solid ${pdfFamilyId===f.id?"#2563EB":T.cardBorder}`,background:pdfFamilyId===f.id?"rgba(37,99,235,0.1)":"transparent",color:pdfFamilyId===f.id?"#2563EB":T.textSecondary,fontSize:11,cursor:"pointer",fontWeight:pdfFamilyId===f.id?600:400}}>
                              {f.emoji} {f.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Filtered dropdown */}
                      {pdfFamilySearch.length >= 1 && (
                        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:400,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,overflow:"hidden",boxShadow:"0 8px 28px rgba(0,0,0,0.5)",maxHeight:200,overflowY:"auto"}}>
                          {famFiltered.length === 0 ? (
                            <div style={{padding:"10px 14px",fontSize:12,color:T.textMuted,textAlign:"center"}}>No families match "{pdfFamilySearch}"</div>
                          ) : famFiltered.map(f=>{
                            const q = pdfFamilySearch.toLowerCase();
                            const name = f.name;
                            const idx = name.toLowerCase().indexOf(q);
                            const hi = idx>=0 ? <React.Fragment>{name.slice(0,idx)}<span style={{color:"#2563EB",fontWeight:700}}>{name.slice(idx,idx+q.length)}</span>{name.slice(idx+q.length)}</React.Fragment> : name;
                            return (
                              <button key={f.id} onClick={()=>{setPdfFamilyId(f.id);setPdfFamilySearch("");}}
                                style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:pdfFamilyId===f.id?"rgba(37,99,235,0.1)":"transparent",border:"none",color:T.textPrimary,cursor:"pointer",fontSize:13,textAlign:"left",borderBottom:`1px solid ${T.borderSubtle}`}}
                                onMouseEnter={e=>e.currentTarget.style.background="rgba(37,99,235,0.08)"}
                                onMouseLeave={e=>e.currentTarget.style.background=pdfFamilyId===f.id?"rgba(37,99,235,0.1)":"transparent"}>
                                <span style={{fontSize:18,flexShrink:0}}>{f.emoji}</span>
                                <div>
                                  <div style={{fontSize:13,fontWeight:600}}>{hi}</div>
                                  <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                    {visibleClients.filter(c=>c.familyId===f.id).length} members
                                  </div>
                                </div>
                                {pdfFamilyId===f.id && <span style={{marginLeft:"auto",color:"#2563EB",fontSize:14}}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>}
              {pdfScope==="individual" && (function(){
                  const allClients = visibleFamilies.flatMap(fam =>
                    visibleClients.filter(c=>(c.familyId||c.family_id)===fam.id).map(c=>({...c, familyName:fam.name, familyEmoji:fam.emoji}))
                  );
                  const cliFiltered = pdfClientSearch.length >= 1
                    ? allClients.filter(c => c.name.toLowerCase().includes(pdfClientSearch.toLowerCase()))
                    : allClients;
                  const selCli = allClients.find(c=>c.id===pdfClientId);
                  return (
                    <div style={{position:"relative"}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:5}}>
                        Search Client <span style={{color:T.textLight}}>(type 3+ letters or pick below)</span>
                      </div>
                      <div style={{position:"relative"}}>
                        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:13,pointerEvents:"none"}}>🔍</span>
                        <input
                          value={pdfClientSearch}
                          onChange={e=>{setPdfClientSearch(e.target.value); if(!e.target.value) setPdfClientId("");}}
                          placeholder={selCli ? `${selCli.name} (${selCli.role})` : "Type client name…"}
                          style={{width:"100%",boxSizing:"border-box",background:T.inputBg,border:`1px solid ${T.inputBorder}`,borderRadius:8,color:T.textPrimary,padding:"9px 36px 9px 36px",fontSize:13,outline:"none"}}
                        />
                        {pdfClientSearch && (
                          <button onClick={()=>{setPdfClientSearch("");setPdfClientId("");}}
                            style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:14,lineHeight:1,padding:0}}>✕</button>
                        )}
                      </div>
                      {/* Quick-pick chips when no search */}
                      {!pdfClientSearch && (
                        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                          {allClients.map(c=>{
                            const roleCol = {"Primary":"#2563EB","Spouse":"#7C3AED","Child":"#1D9E75","Entity":"#D97706"}[c.role]||"#78716C";
                            return (
                              <button key={c.id} onClick={()=>{setPdfClientId(c.id);setPdfClientSearch("");}}
                                style={{padding:"4px 11px",borderRadius:20,border:`1px solid ${pdfClientId===c.id?roleCol+"66":T.cardBorder}`,background:pdfClientId===c.id?roleCol+"18":"transparent",color:pdfClientId===c.id?roleCol:T.textSecondary,fontSize:11,cursor:"pointer",fontWeight:pdfClientId===c.id?600:400,display:"flex",alignItems:"center",gap:5}}>
                                <span style={{fontSize:9,color:roleCol,fontWeight:700}}>{c.avatar||c.name?.slice(0,2).toUpperCase()||"?"}</span>
                                {c.name.split(" ")[0]}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* Search dropdown */}
                      {pdfClientSearch.length >= 1 && (
                        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:400,background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,overflow:"hidden",boxShadow:"0 8px 28px rgba(0,0,0,0.5)",maxHeight:220,overflowY:"auto"}}>
                          {cliFiltered.length === 0 ? (
                            <div style={{padding:"10px 14px",fontSize:12,color:T.textMuted,textAlign:"center"}}>No clients match "{pdfClientSearch}"</div>
                          ) : cliFiltered.map(c=>{
                            const q = pdfClientSearch.toLowerCase();
                            const nm = c.name;
                            const idx = nm.toLowerCase().indexOf(q);
                            const hi = idx>=0 ? <React.Fragment>{nm.slice(0,idx)}<span style={{color:"#2563EB",fontWeight:700}}>{nm.slice(idx,idx+q.length)}</span>{nm.slice(idx+q.length)}</React.Fragment> : nm;
                            const roleCol = {"Primary":"#2563EB","Spouse":"#7C3AED","Child":"#1D9E75","Entity":"#D97706"}[c.role]||"#78716C";
                            return (
                              <button key={c.id} onClick={()=>{setPdfClientId(c.id);setPdfClientSearch("");}}
                                style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:pdfClientId===c.id?"rgba(37,99,235,0.1)":"transparent",border:"none",color:T.textPrimary,cursor:"pointer",fontSize:13,textAlign:"left",borderBottom:`1px solid ${T.borderSubtle}`}}
                                onMouseEnter={e=>e.currentTarget.style.background="rgba(37,99,235,0.08)"}
                                onMouseLeave={e=>e.currentTarget.style.background=pdfClientId===c.id?"rgba(37,99,235,0.1)":"transparent"}>
                                <div style={{width:32,height:32,borderRadius:"50%",background:roleCol+"22",border:`1px solid ${roleCol}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:roleCol,flexShrink:0}}>{c.avatar||c.name?.slice(0,2).toUpperCase()||"?"}</div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:13,fontWeight:600}}>{hi}</div>
                                  <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                    <span style={{color:roleCol}}>{c.role}</span> · {c.familyEmoji} {c.familyName}
                                  </div>
                                </div>
                                {pdfClientId===c.id && <span style={{color:"#2563EB",fontSize:14,flexShrink:0}}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Cover Page Details + Sections + Actions — in padded wrapper (these sit outside the body div's padding) */}
              <div style={{padding:"0 36px 24px"}}>

              {/* Cover Page Details */}
              <div style={{marginBottom:18}}>
                <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>Cover Page Details</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>Prepared By</div>
                    <input value={pdfPreparedBy} onChange={e=>setPdfPreparedBy(e.target.value)}
                      style={{width:"100%",background:T.inputBg,border:`1px solid ${T.inputBorder}`,borderRadius:8,color:T.textPrimary,padding:"9px 12px",fontSize:12,boxSizing:"border-box"}}
                      placeholder="e.g. Forthius Finalytics"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4}}>As On Date</div>
                    <input type="date" value={pdfAsOnDate} onChange={e=>setPdfAsOnDate(e.target.value)}
                      style={{width:"100%",background:T.inputBg,border:`1px solid ${T.inputBorder}`,borderRadius:8,color:T.textPrimary,padding:"9px 12px",fontSize:12,boxSizing:"border-box"}}/>
                  </div>
                </div>
              </div>

              {/* Sections to Include — plain text checkboxes (no pill borders) */}
              <div style={{marginBottom:22}}>
                <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>Sections to Include</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:2}}>
                  {[
                    ["summary",         "Portfolio Summary"],
                    ["portfolioHealth",  "Portfolio Health Score"],
                    ["allocation",       "Asset Allocation"],
                    ["sector",           "Sector Allocation"],
                    ["performance",      "Performance by Class"],
                    ["currency",         "Currency Exposure"],
                    ["geography",        "Geographic Exposure"],
                    ["capSize",          "Market Cap Breakdown"],
                    ["irr",              "IRR Analysis"],
                    ["concentration",    "Concentration Risk"],
                    ["correlation",      "Correlation Matrix"],
                    ["volatility",       "Volatility & Sharpe"],
                    ["goals",            "Goals & Planning"],
                    ["whatif",           "What-If Scenarios"],
                    ["holdings",         "Holdings Detail"],
                    ["disclaimer",       "Disclaimer"],
                    ...(isAdmin ? [["riskAnalysis","Risk Analysis (Admin)"]] : []),
                  ].map(([key, label])=>(
                    <label key={key} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                      padding:"6px 2px",borderRadius:4}}>
                      <input type="checkbox" checked={pdfSections[key]} onChange={e=>setPdfSections(p=>({...p,[key]:e.target.checked}))}
                        style={{width:14,height:14,flexShrink:0,accentColor:key==="riskAnalysis"?"#A32D2D":"#2563EB",cursor:"pointer"}}/>
                      <span style={{fontSize:12.5,color:key==="riskAnalysis" && pdfSections[key]?"#A32D2D":T.textPrimary,
                        fontWeight:pdfSections[key]?500:400}}>{label}</span>
                    </label>
                  ))}
                </div>
                {isAdmin && pdfSections.riskAnalysis && (
                  <div style={{marginTop:8,padding:"7px 10px",background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.15)",borderRadius:7,fontSize:10,color:"#A32D2D",display:"flex",alignItems:"center",gap:6}}>
                    🔒 Risk Analysis is marked <strong>Advisor Confidential</strong> — not for direct client distribution
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
                <button onClick={()=>setPdfModal(false)}
                  style={{padding:"10px 20px",borderRadius:8,border:`1px solid ${T.cardBorder}`,background:"transparent",color:T.textSecondary,fontSize:13,cursor:"pointer",fontWeight:500}}>
                  Cancel
                </button>
                {isAdmin && (
                  <button onClick={generatePPT}
                    style={{padding:"10px 20px",borderRadius:8,border:"1px solid #059669",background:"rgba(21,128,61,0.1)",color:"#1D9E75",fontSize:13,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
                    📊 Export to PPT
                  </button>
                )}
                {totalUSD === 0 && holdings.length > 0 && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"rgba(245,158,11,0.08)",border:"1px solid #D9770644",borderRadius:8,fontSize:11,color:"#D97706"}}>
                  ⚠️ Prices not yet loaded — click <strong>Force Refresh</strong> first to ensure accurate values in the PDF.
                </div>
              )}
              <button onClick={generatePDF}
                  style={{padding:"10px 24px",borderRadius:8,border:"none",background:"#007878",color:"white",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:8,boxShadow:"0 4px 16px rgba(0,121,193,0.3)"}}>
                  📄 Generate & Print PDF
                </button>
              </div>

              </div>
              </div>
          </div>
      )}
      {/* ── Profile Modal ── */}
      {profileOpen && <ProfileModal
        isAdmin={isAdmin}
        loggedInClientId={loggedInClientId}
        profileData={profileData}
        setProfileData={setProfileData}
        profileTab={profileTab}
        setProfileTab={setProfileTab}
        notifPrefs={notifPrefs}
        onNotifPrefsChange={(key,val)=>setNotifPrefs(p=>({...p,[key]:val}))}
        clientLogins={clientLogins}
        setClientLogins={setClientLogins}
        onClose={()=>setProfileOpen(false)}
        onSignOut={()=>{setProfileOpen(false);setSignedOut(true);setLoginRole("client"); try{localStorage.removeItem("fw_session_v1");}catch{}setLoginId("");setLoginPass("");setLoginErr("");}}
        onSave={(userId, data)=>{ upsertProfile(userId, data).catch(e=>console.error("[Supabase] upsertProfile:",e.message)); }}
        onToggleTheme={toggleTheme}
        onSetTheme={setThemeTo}
        onSetThemeAuto={setThemeAuto}
        currentThemeId={themeId}
        currentThemePref={(() => {
          const uid = isAdmin ? "admin" : loggedInClientId;
          return (profileData?.[uid]?.theme) || (()=>{ try { return localStorage.getItem("fw_theme"); } catch { return null; } })() || null;
        })()}
        clients={clients}
      />}

      {/* ── Change Password Modal ── */}
      {pwModal && <ChangePwModal
        isAdmin={isAdmin}
        loggedInClientId={loggedInClientId}
        clientLogins={clientLogins}
        setClientLogins={setClientLogins}
        onClose={()=>setPwModal(false)}
      />}

      {/* ══════════════════════════════════════════════
          EDIT HOLDING MODAL
      ══════════════════════════════════════════════ */}

      {/* ═══════════════════════════════════════════════════
          ONBOARDING WIZARD MODAL
      ═══════════════════════════════════════════════════ */}
      {wizardOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:700,
          display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={e=>e.target===e.currentTarget&&setWizardOpen(false)}>
          <div style={{background:T.cardBg,borderRadius:18,width:"100%",maxWidth:540,
            boxShadow:"0 32px 80px rgba(0,0,0,0.35)",overflow:"hidden",display:"flex",flexDirection:"column"}}>

            {/* Header */}
            <div style={{padding:"20px 24px 0",borderBottom:`1px solid ${T.cardBorder}`}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:T.textPrimary}}>
                    🚀 Onboard New Client
                  </div>
                  <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                    {["Create Family","Add Client","Set Login","Done!"][wizardStep-1]}
                  </div>
                </div>
                <button onClick={()=>setWizardOpen(false)}
                  style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                    borderRadius:8,color:"#A32D2D",width:32,height:32,cursor:"pointer",
                    fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              </div>
              {/* Progress steps */}
              <div style={{display:"flex",gap:0,marginBottom:0}}>
                {["Family","Client","Login","Done"].map((s,i)=>{
                  const stepN = i+1;
                  const done  = wizardStep > stepN;
                  const active = wizardStep === stepN;
                  return (
                    <div key={s} style={{flex:1,textAlign:"center",padding:"8px 4px",
                      borderBottom:`3px solid ${done||active ? T.brand : T.borderSubtle}`,
                      transition:"border-color 0.2s"}}>
                      <div style={{fontSize:10,fontWeight:700,
                        color: done||active ? T.brand : T.textMuted,
                        textTransform:"uppercase",letterSpacing:"0.05em"}}>
                        {done ? "✓ " : active ? "● " : ""}{s}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div style={{padding:"24px",overflowY:"auto",flex:1}}>
              {wizardErr && (
                <div style={{marginBottom:14,padding:"9px 12px",background:"rgba(220,38,38,0.07)",
                  border:"1px solid rgba(220,38,38,0.3)",borderRadius:7,fontSize:12,color:"#A32D2D"}}>
                  ⚠ {wizardErr}
                </div>
              )}

              {/* Step 1: Family */}
              {wizardStep===1 && (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{fontSize:13,color:T.textSecondary,marginBottom:4}}>
                    A family groups related clients together (e.g. Sharma Family, Kumar HUF).
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Family / Entity Name *</div>
                    <input style={inp()} value={wizardFam.name}
                      onChange={e=>setWizardFam(p=>({...p,name:e.target.value}))}
                      placeholder="e.g. Sharma Family, Kumar HUF, Mehta Holdings"/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Emoji</div>
                      <select style={inp()} value={wizardFam.emoji} onChange={e=>setWizardFam(p=>({...p,emoji:e.target.value}))}>
                        {["👤","👨‍👩‍👧","🏛️","💼","🏠","🌟","🎯","💎","🏦","🌿"].map(em=>(
                          <option key={em} value={em}>{em}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Accent Colour</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {["#6366F1","#EC4899","#F59E0B","#10B981","#3B82F6","#8B5CF6","#EF4444","#06B6D4"].map(c=>(
                          <div key={c} onClick={()=>setWizardFam(p=>({...p,color:c}))}
                            style={{width:22,height:22,borderRadius:"50%",background:c,cursor:"pointer",
                              border: wizardFam.color===c ? "3px solid white" : "2px solid transparent",
                              boxShadow: wizardFam.color===c ? `0 0 0 2px ${c}` : "none",
                              transition:"all 0.15s"}}/>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Advisor Name</div>
                    <input style={inp()} value={wizardFam.advisorName}
                      onChange={e=>setWizardFam(p=>({...p,advisorName:e.target.value}))}
                      placeholder="e.g. Kshitij Lodha"/>
                  </div>
                </div>
              )}

              {/* Step 2: Client */}
              {wizardStep===2 && (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{fontSize:13,color:T.textSecondary,marginBottom:4}}>
                    Add the primary member of <strong>{wizardFam.name}</strong>. You can add more members later.
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Full Name *</div>
                    <input style={inp()} value={wizardCli.name}
                      onChange={e=>{
                        setWizardCli(p=>({...p,name:e.target.value}));
                        // Auto-suggest login ID from name
                        const parts = e.target.value.trim().toLowerCase().split(/\s+/);
                        if (parts.length >= 2) {
                          setWizardCred(p=>({...p, loginId: parts[0]+"."+parts[parts.length-1]}));
                        }
                      }}
                      placeholder="e.g. Rajesh Sharma"/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Role</div>
                      <select style={inp()} value={wizardCli.role} onChange={e=>setWizardCli(p=>({...p,role:e.target.value}))}>
                        {["Primary","Spouse","Child","Parent","Entity","Other"].map(r=><option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Risk Profile</div>
                      <select style={inp()} value={wizardCli.riskProfile} onChange={e=>setWizardCli(p=>({...p,riskProfile:e.target.value}))}>
                        {["Conservative","Moderate","Moderate-Aggressive","Aggressive"].map(r=><option key={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Date of Birth (optional)</div>
                    <input type="date" style={inp()} value={wizardCli.dob} onChange={e=>setWizardCli(p=>({...p,dob:e.target.value}))}/>
                  </div>
                </div>
              )}

              {/* Step 3: Login credentials */}
              {wizardStep===3 && (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{fontSize:13,color:T.textSecondary,marginBottom:4}}>
                    Set up login credentials for <strong>{wizardCli.name}</strong>. Share these securely.
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Login ID *</div>
                    <input style={inp()} value={wizardCred.loginId}
                      onChange={e=>setWizardCred(p=>({...p,loginId:e.target.value.toLowerCase().replace(/[^a-z0-9.]/g,"")}))}
                      placeholder="e.g. rajesh.sharma"/>
                    <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>
                      Lowercase letters, numbers and dots only
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Password *</div>
                    <input type="password" style={inp()} value={wizardCred.password}
                      onChange={e=>setWizardCred(p=>({...p,password:e.target.value}))}
                      placeholder="Min 8 chars, 1 uppercase, 1 number"/>
                    {/* Password strength indicator */}
                    {wizardCred.password.length > 0 && (function(){
                      const pw = wizardCred.password;
                      const checks = [pw.length>=8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
                      const score  = checks.filter(Boolean).length;
                      const labels = ["Weak","Fair","Good","Strong"];
                      const colors = ["#A32D2D","#D97706","#1D9E75","#059669"];
                      return (
                        <div style={{marginTop:6}}>
                          <div style={{height:3,background:T.borderSubtle,borderRadius:2,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${score*25}%`,
                              background:colors[score-1]||"#A32D2D",borderRadius:2,transition:"width 0.3s"}}/>
                          </div>
                          <div style={{fontSize:10,color:colors[score-1]||"#A32D2D",marginTop:2,fontWeight:600}}>
                            {labels[score-1]||"Too short"} · {score}/4 criteria met
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:T.textSecondary,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>Confirm Password *</div>
                    <input type="password" style={inp()} value={wizardCred.confirm}
                      onChange={e=>setWizardCred(p=>({...p,confirm:e.target.value}))}
                      placeholder="Repeat password"/>
                    {wizardCred.confirm && wizardCred.password !== wizardCred.confirm && (
                      <div style={{fontSize:10,color:"#A32D2D",marginTop:3}}>Passwords do not match</div>
                    )}
                  </div>
                  <div style={{padding:"10px 12px",background:`${T.brand}08`,border:`1px solid ${T.brand}25`,
                    borderRadius:8,fontSize:11,color:T.textSecondary}}>
                    💡 After setup, you can upload a CAS statement in the Holdings tab to import their portfolio.
                  </div>
                </div>
              )}

              {/* Step 4: Done! */}
              {wizardStep===4 && (
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:48,marginBottom:16}}>🎉</div>
                  <div style={{fontSize:18,fontWeight:700,color:T.textPrimary,marginBottom:8}}>
                    {wizardCli.name} is all set!
                  </div>
                  <div style={{fontSize:13,color:T.textSecondary,marginBottom:20,lineHeight:1.6}}>
                    Family <strong>{wizardFam.emoji} {wizardFam.name}</strong> created.<br/>
                    Login ID: <strong style={{color:T.brand}}>{wizardCred.loginId}</strong><br/>
                    Share the password securely with your client.
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                    <button
                      onClick={()=>{
                        setSelectedFamily(wizardFamId); setSelectedClients(new Set([wizardCliId]));
                        setTab("holdings"); setWizardOpen(false);
                      }}
                      style={{background:`${T.brand}`,border:"none",
                        borderRadius:8,color:"white",padding:"10px 20px",cursor:"pointer",
                        fontSize:13,fontWeight:600}}>
                      📂 Import Holdings Now
                    </button>
                    <button
                      onClick={()=>{
                        setWizardOpen(false); setWizardStep(1);
                        setWizardFam({name:"",emoji:"👤",color:"#6366F1",advisorName:""});
                        setWizardCli({name:"",role:"Primary",riskProfile:"Moderate",dob:""});
                        setWizardCred({loginId:"",password:"",confirm:""});
                      }}
                      style={{background:T.cardBg,border:`1px solid ${T.cardBorder}`,
                        borderRadius:8,color:T.textSecondary,padding:"10px 20px",
                        cursor:"pointer",fontSize:13}}>
                      Add Another Client
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {wizardStep < 4 && (
              <div style={{padding:"16px 24px",borderTop:`1px solid ${T.cardBorder}`,
                display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                <button
                  onClick={()=>{ wizardStep > 1 ? setWizardStep(s=>s-1) : setWizardOpen(false); setWizardErr(""); }}
                  style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                    borderRadius:8,color:T.textSecondary,padding:"8px 18px",cursor:"pointer",fontSize:13}}>
                  {wizardStep===1 ? "Cancel" : "← Back"}
                </button>

                <button disabled={wizardBusy}
                  onClick={async()=>{
                    setWizardErr(""); setWizardBusy(true);
                    try {
                      if (wizardStep===1) {
                        if (!wizardFam.name.trim()) { setWizardErr("Family name is required."); return; }
                        // Create family
                        const fid = "f" + Date.now().toString(36) + Math.random().toString(36).slice(2,5);
                        const newFam = { id:fid, name:wizardFam.name.trim(), emoji:wizardFam.emoji,
                          color:wizardFam.color, advisorName:wizardFam.advisorName.trim(), type:"Family" };
                        await addFamily(newFam).catch(e=>console.error("[Supabase] addFamily:",e.message));
                        setFamilies(p=>[...p,newFam]);
                        setWizardFamId(fid);
                        setWizardStep(2);

                      } else if (wizardStep===2) {
                        if (!wizardCli.name.trim()) { setWizardErr("Client name is required."); return; }
                        // Create client
                        const cid = "c" + Date.now().toString(36) + Math.random().toString(36).slice(2,5);
                        const initials = wizardCli.name.trim().split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                        const newCli = { id:cid, familyId:wizardFamId, name:wizardCli.name.trim(),
                          role:wizardCli.role, riskProfile:wizardCli.riskProfile,
                          dob:wizardCli.dob||null, avatar:initials, status:"active" };
                        await addClient(newCli).catch(e=>console.error("[Supabase] addClient:",e.message));
                        setClients(p=>[...p,newCli]);
                        setWizardCliId(cid);
                        setWizardStep(3);

                      } else if (wizardStep===3) {
                        if (!wizardCred.loginId.trim()) { setWizardErr("Login ID is required."); return; }
                        if (wizardCred.password.length < 8) { setWizardErr("Password must be at least 8 characters."); return; }
                        if (!/[A-Z]/.test(wizardCred.password)) { setWizardErr("Password must contain an uppercase letter."); return; }
                        if (!/[0-9]/.test(wizardCred.password)) { setWizardErr("Password must contain a number."); return; }
                        if (wizardCred.password !== wizardCred.confirm) { setWizardErr("Passwords do not match."); return; }
                        // Check loginId uniqueness
                        const taken = Object.values(clientLogins).some(v=>v?.loginId===wizardCred.loginId.trim());
                        if (taken) { setWizardErr("That login ID is already taken. Try a different one."); return; }
                        // Hash + save
                        const hashed = await makePasswordHash(wizardCred.password);
                        const cred = { loginId:wizardCred.loginId.trim(), password:hashed, active:true,
                          riskTabs:{overview:false,concentration:false,stress:false,correlation:false} };
                        await upsertClientLogin(wizardCliId, cred).catch(e=>console.error("[Supabase] upsertLogin:",e.message));
                        setClientLogins(p=>({...p,[wizardCliId]:cred}));
                        logAudit("ONBOARD_CLIENT", `${wizardCli.name} · ${wizardFam.name} · login:${wizardCred.loginId}`);
                        setWizardStep(4);
                      }
                    } finally {
                      setWizardBusy(false);
                    }
                  }}
                  style={{background:wizardBusy?T.borderSubtle:`${T.brand}`,
                    border:"none",borderRadius:8,color:"white",padding:"9px 24px",
                    cursor:wizardBusy?"default":"pointer",fontSize:13,fontWeight:700,
                    opacity:wizardBusy?0.7:1}}>
                  {wizardBusy ? "⏳ Saving…" : wizardStep===3 ? "✓ Create Login" : "Next →"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════════════════
          GLOBAL SEARCH PANEL
      ══════════════════════════════════════════════════════ */}
      {globalOpen && (
        <div
          style={{position:"fixed",inset:0,zIndex:800,background:"rgba(0,0,0,0.45)"}}
          onClick={e=>{ if(e.target===e.currentTarget){setGlobalOpen(false);setGlobalSearch("");} }}>
          <div style={{
            position:"absolute",
            top: isMobile ? 0 : 64,
            left: isMobile ? 0 : "50%",
            right: isMobile ? 0 : "auto",
            transform: isMobile ? "none" : "translateX(-50%)",
            width: isMobile ? "100%" : 580,
            maxHeight: isMobile ? "100vh" : "70vh",
            background:T.cardBg,
            borderRadius: isMobile ? 0 : 14,
            border:`1px solid ${T.cardBorder}`,
            boxShadow:"0 24px 64px rgba(0,0,0,0.35)",
            overflow:"hidden",
            display:"flex",flexDirection:"column",
          }}>
            {/* Search input (mobile shows it here, desktop mirrors topbar) */}
            {isMobile && (
              <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.borderSubtle}`,
                display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:15}}>🔍</span>
                <input autoFocus value={globalSearch}
                  onChange={e=>setGlobalSearch(e.target.value)}
                  placeholder="Search holdings, goals, watchlist…"
                  style={{flex:1,border:"none",background:"transparent",outline:"none",
                    fontSize:13,color:T.textPrimary,fontFamily:"'Inter', system-ui, sans-serif"}}/>
                <button onClick={()=>{setGlobalOpen(false);setGlobalSearch("");}}
                  style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:T.textMuted}}>✕</button>
              </div>
            )}

            {/* Results */}
            {(function(){
              const q = globalSearch.trim().toLowerCase();
              if (!q) return (
                <div style={{padding:"24px",textAlign:"center",color:T.textMuted,fontSize:12}}>
                  <div style={{fontSize:28,marginBottom:8}}>🔍</div>
                  Type to search across your entire portfolio<br/>
                  <span style={{fontSize:10,marginTop:6,display:"block",color:T.textMuted}}>
                    Holdings · Goals · Watchlist · Transactions · Dividends
                  </span>
                </div>
              );

              const results = [];

              // ── Holdings ──
              hwWithOwner.filter(h =>
                h.name?.toLowerCase().includes(q) ||
                h.ticker?.toLowerCase().includes(q) ||
                h.isin?.toLowerCase().includes(q) ||
                h.class?.toLowerCase().includes(q) ||
                h.sector?.toLowerCase().includes(q)
              ).slice(0,6).forEach(h => {
                const client = clients.find(c=>c.id===h.clientId);
                results.push({
                  type:"holding", icon:"📈",
                  title: h.name,
                  sub: `${h.class} · ${h.ticker||h.isin||"—"}${client?` · ${client.name}`:""}`,
                  value: h.valueUSD > 0 ? fmt(convert(h.valueUSD,dc),dc) : null,
                  gain: h.gainPct ?? null,
                  action: ()=>{ setHoldingSearch(h.ticker||h.name.split(" ")[0]); setTab("holdings"); setGlobalOpen(false); setGlobalSearch(""); },
                });
              });

              // ── Goals ──
              allGoals.filter(g =>
                g.name?.toLowerCase().includes(q) ||
                g.type?.toLowerCase().includes(q)
              ).slice(0,3).forEach(g => {
                results.push({
                  type:"goal", icon:"🎯",
                  title: g.name || g.type,
                  sub: `Target: ${fmt(convert((g.targetAmount||0) * ((fxRates[g.currency||dc]||1)/(fxRates["USD"]||1)),dc),dc,true)} · ${g.targetDate||"No date"}`,
                  value: null, gain: null,
                  action: ()=>{ setTab("goals"); setGlobalOpen(false); setGlobalSearch(""); },
                });
              });

              // ── Watchlist ──
              watchlist.filter(w =>
                w.name?.toLowerCase().includes(q) ||
                w.ticker?.toLowerCase().includes(q)
              ).slice(0,3).forEach(w => {
                results.push({
                  type:"watchlist", icon:"👁",
                  title: w.ticker,
                  sub: `${w.name}${w.currentPrice?` · ${w.currentPrice?.toLocaleString("en-IN")}`:""} `,
                  value: null, gain: null,
                  action: ()=>{ setTab("watchlist"); setGlobalOpen(false); setGlobalSearch(""); },
                });
              });

              // ── Transactions ──
              transactions.filter(t =>
                t.name?.toLowerCase().includes(q) ||
                t.ticker?.toLowerCase().includes(q)
              ).slice(0,3).forEach(t => {
                results.push({
                  type:"txn", icon:"💸",
                  title: t.name || t.ticker || "Transaction",
                  sub: `${t.type||"Buy"} · ${t.date||""} · Qty ${t.qty||"—"}`,
                  value: t.price ? fmt(convert(t.price*(t.qty||1)/(fxRates[t.currency||"USD"]||1),dc),dc) : null,
                  gain: null,
                  action: ()=>{ setTab("transactions"); setGlobalOpen(false); setGlobalSearch(""); },
                });
              });

              // ── Dividends ──
              dividends.filter(d =>
                d.holdingName?.toLowerCase().includes(q) ||
                d.ticker?.toLowerCase().includes(q)
              ).slice(0,2).forEach(d => {
                results.push({
                  type:"dividend", icon:"💰",
                  title: d.holdingName || d.ticker,
                  sub: `Dividend · ${d.payDate||d.exDate||"—"} · ${d.currency||"USD"}`,
                  value: d.totalReceived ? fmt(convert((d.totalReceived||0)/(fxRates[d.currency||"USD"]||1),dc),dc) : null,
                  gain: null,
                  action: ()=>{ setTab("dividends"); setGlobalOpen(false); setGlobalSearch(""); },
                });
              });

              const TYPE_COLORS = {
                holding:"#2563EB", goal:"#7C3AED", watchlist:"#D97706",
                txn:"#A32D2D", dividend:"#1D9E75",
              };

              if (!results.length) return (
                <div style={{padding:"24px",textAlign:"center",color:T.textMuted,fontSize:12}}>
                  <div style={{fontSize:28,marginBottom:8}}>🤷</div>
                  No results for <strong style={{color:T.textPrimary}}>"{globalSearch}"</strong>
                </div>
              );

              return (
                <div style={{overflowY:"auto",flex:1}}>
                  <div style={{padding:"6px 8px",borderBottom:`1px solid ${T.borderSubtle}`,
                    fontSize:10,color:T.textMuted,fontWeight:600,
                    textTransform:"uppercase",letterSpacing:"0.07em"}}>
                    {results.length} result{results.length!==1?"s":""} for "{globalSearch}"
                  </div>
                  {results.map((r,i)=>(
                    <div key={i} onClick={r.action}
                      style={{display:"flex",alignItems:"center",gap:12,
                        padding:"10px 14px",cursor:"pointer",
                        borderBottom:`1px solid ${T.borderSubtle}`,
                        transition:"background 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg||T.borderSubtle}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:32,height:32,borderRadius:8,flexShrink:0,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:15,background:`${TYPE_COLORS[r.type]||T.brand}12`}}>
                        {r.icon}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,color:T.textPrimary,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {r.title}
                        </div>
                        <div style={{fontSize:10,color:T.textMuted,marginTop:1,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {r.sub}
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        {r.value && (
                          <div style={{fontSize:12,fontWeight:700,
                            fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary}}>
                            {r.value}
                          </div>
                        )}
                        {r.gain != null && (
                          <div style={{fontSize:10,fontWeight:600,
                            color:r.gain>=0?"#1D9E75":"#A32D2D"}}>
                            {r.gain>=0?"+":""}{r.gain.toFixed(1)}%
                          </div>
                        )}
                        <div style={{fontSize:9,color:TYPE_COLORS[r.type]||T.brand,
                          fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",
                          marginTop:1}}>
                          {r.type}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Footer hint */}
            <div style={{padding:"8px 14px",borderTop:`1px solid ${T.borderSubtle}`,
              fontSize:10,color:T.textMuted,display:"flex",justifyContent:"space-between"}}>
              <span>↵ Jump to · Esc to close</span>
              <span>⌘K to toggle</span>
            </div>
          </div>
        </div>
      )}


      {/* ═══════════════════════════════════════════════════
          CSV IMPORT PREVIEW MODAL
      ═══════════════════════════════════════════════════ */}
      {csvPreview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:700,
          display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={e=>e.target===e.currentTarget&&setCsvPreview(null)}>
          <div style={{background:T.cardBg,borderRadius:18,width:"100%",maxWidth:920,
            maxHeight:"88vh",display:"flex",flexDirection:"column",
            boxShadow:"0 32px 80px rgba(0,0,0,0.4)",overflow:"hidden"}}>

            {/* Header */}
            <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.cardBorder}`,flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700}}>
                    📂 Preview Import — {csvPreview.filename}
                  </div>
                  <div style={{fontSize:11,color:T.textSecondary,marginTop:2}}>
                    {csvPreview.rows.length} row{csvPreview.rows.length!==1?"s":""} ready to import
                    {csvPreview.errors?.length > 0 && (
                      <span style={{color:"#D97706",marginLeft:8}}>
                        · {csvPreview.errors.length} row{csvPreview.errors.length!==1?"s":""} skipped
                      </span>
                    )}
                    {csvPreview.ibkr && (
                      <span style={{marginLeft:8,color:"#2563EB",fontWeight:600}}>
                        · IBKR format detected — {csvPreview.ibkrSkipped} non-trade rows excluded (Forex, Deposits, Interest)
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={()=>setCsvPreview(null)}
                  style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                    borderRadius:8,color:"#A32D2D",width:32,height:32,cursor:"pointer",fontSize:16,
                    display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              </div>

              {/* Errors */}
              {csvPreview.errors?.length > 0 && (
                <div style={{marginTop:10,padding:"8px 12px",background:"rgba(217,119,6,0.07)",
                  border:"1px solid rgba(217,119,6,0.25)",borderRadius:7,
                  fontSize:11,color:"#D97706",maxHeight:80,overflowY:"auto"}}>
                  {csvPreview.errors.slice(0,5).map((e,i)=><div key={i}>⚠ {e}</div>)}
                  {csvPreview.errors.length>5 && <div>…and {csvPreview.errors.length-5} more</div>}
                </div>
              )}

              {/* Client assignment (for holdings/MF/transactions) */}
              {(csvPreview.target==="holdings"||csvPreview.target==="mf"||csvPreview.target==="globalmf"||csvPreview.target==="transactions") && (() => {
                // Transactions REQUIRE a client assignment — unassigned txns can't be filtered,
                // reconciled, or attributed. Holdings can be unassigned (rare but allowed).
                const isTxn = csvPreview.target === "transactions";
                const missing = !csvAssignTo;
                const borderColor = missing
                  ? (isTxn ? "#A32D2D" : T.cardBorder)
                  : T.brand;
                return (
                  <div style={{marginTop:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:T.textSecondary,fontWeight:600}}>
                      Assign to:{isTxn && <span style={{color:"#A32D2D",marginLeft:2}}>*</span>}
                    </span>
                    <select value={csvAssignTo} onChange={e=>setCsvAssignTo(e.target.value)}
                      style={{fontSize:12,padding:"5px 10px",borderRadius:7,
                        background:T.inputBg,
                        border:`${missing && isTxn ? "2px" : "1px"} solid ${borderColor}`,
                        color:T.textPrimary,outline:"none",cursor:"pointer",
                        boxShadow: missing && isTxn ? "0 0 0 3px rgba(220,38,38,0.12)" : "none"}}>
                      <option value="">— Select a client —</option>
                      {visibleClients.map(c=>(
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {missing && isTxn && (
                      <span style={{fontSize:11,color:"#A32D2D",fontWeight:600}}>
                        ⛔ Required — transactions must be assigned to a client
                      </span>
                    )}
                    {missing && !isTxn && (
                      <span style={{fontSize:10,color:"#D97706"}}>
                        ⚠ Holdings will be unassigned — you can assign later
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Preview table — fully editable before import */}
            <div style={{flex:1,overflowY:"auto",overflowX:"auto"}}>
              {csvPreview.target==="holdings"||csvPreview.target==="mf"||csvPreview.target==="globalmf" ? (() => {
                // Helper to update a single field on a row
                const updateRow = (i, field, val) => setCsvPreview(p => ({
                  ...p,
                  rows: p.rows.map((r, ri) => ri === i ? {...r, [field]: val} : r)
                }));
                const deleteRow = (i) => setCsvPreview(p => ({
                  ...p, rows: p.rows.filter((_, ri) => ri !== i)
                }));
                const addRow = () => setCsvPreview(p => ({
                  ...p, rows: [...p.rows, {
                    id: Date.now(), name: "", class: "Indian Mutual Funds",
                    currency: "INR", qty: 0, costPrice: 0, currentPrice: 0,
                    isin: "-", manager: "Self-Managed", ticker: "-",
                    country: "India", listed: false, isMutualFund: true
                  }]
                }));

                // Shared cell input style
                const cellInp = (width) => ({
                  width: width||"100%", border: "none", background: "transparent",
                  fontSize: 11, color: T.textPrimary, outline: "none",
                  fontFamily: "DM Sans, sans-serif", padding: 0,
                });
                const cellWrap = (focused) => ({
                  padding: "4px 8px",
                  background: focused ? `${T.brand}0D` : "transparent",
                  border: `1px solid ${focused ? T.brand : "transparent"}`,
                  borderRadius: 5, transition: "all 0.1s",
                });

                const CLASS_OPTS = ["Indian Mutual Funds","Global Mutual Funds","Equities",
                  "Fixed Income","ETF","Real Estate","Digital Assets","Commodities","Alternatives","Miscellaneous"];
                const CCY_OPTS = ["INR","USD","EUR","GBP","SGD","JPY","AUD","CAD","HKD"];

                return (
                  <div>
                    {/* Hint bar */}
                    <div style={{padding:"6px 16px",background:`${T.brand}08`,
                      borderBottom:`1px solid ${T.cardBorder}`,
                      fontSize:10,color:T.textMuted,display:"flex",alignItems:"center",gap:6}}>
                      ✏️ <strong style={{color:T.brand}}>Click any cell to edit</strong>
                      &nbsp;· Tab to move between cells · ✕ to remove a row · + Add Row at bottom
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:680}}>
                      <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:2}}>
                        <tr>
                          {["","Name","Class","Ccy","Qty","Cost Price","Current Price","ISIN","Manager"].map((h,hi)=>(
                            <th key={hi} style={{padding:"7px 8px",textAlign:"left",fontWeight:600,
                              color:T.textSecondary,borderBottom:`1px solid ${T.cardBorder}`,
                              whiteSpace:"nowrap",fontSize:10,
                              width:h===""?"28px":h==="Name"?"200px":h==="Class"?"140px":
                                h==="ISIN"?"110px":h==="Manager"?"110px":"80px"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.rows.map((h,i)=>(
                          <tr key={h.id||i}
                            style={{background:i%2===0?"transparent":T.tableStripe,
                              borderBottom:`1px solid ${T.tdBorder}`}}>

                            {/* Delete button */}
                            <td style={{padding:"3px 6px",textAlign:"center"}}>
                              <button onClick={()=>deleteRow(i)}
                                title="Remove this row"
                                style={{background:"none",border:"none",cursor:"pointer",
                                  color:"#A32D2D",fontSize:13,lineHeight:1,padding:"1px 3px",
                                  borderRadius:3,opacity:0.6}}
                                onMouseEnter={e=>e.currentTarget.style.opacity=1}
                                onMouseLeave={e=>e.currentTarget.style.opacity=0.6}>
                                ✕
                              </button>
                            </td>

                            {/* Name */}
                            <td style={{padding:"2px 4px"}}>
                              <input
                                value={h.name||""}
                                onChange={e=>updateRow(i,"name",e.target.value)}
                                style={{...cellInp("100%"),fontWeight:600,minWidth:160}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=""}
                                placeholder="Fund / asset name"
                              />
                            </td>

                            {/* Class */}
                            <td style={{padding:"2px 4px"}}>
                              <select value={h.class||""}
                                onChange={e=>updateRow(i,"class",e.target.value)}
                                style={{...cellInp("100%"),cursor:"pointer",minWidth:120,
                                  appearance:"none",WebkitAppearance:"none"}}>
                                {CLASS_OPTS.map(c=><option key={c}>{c}</option>)}
                              </select>
                            </td>

                            {/* Currency */}
                            <td style={{padding:"2px 4px"}}>
                              <select value={h.currency||"INR"}
                                onChange={e=>updateRow(i,"currency",e.target.value)}
                                style={{...cellInp(60),cursor:"pointer",
                                  appearance:"none",WebkitAppearance:"none"}}>
                                {CCY_OPTS.map(c=><option key={c}>{c}</option>)}
                              </select>
                            </td>

                            {/* Qty */}
                            <td style={{padding:"2px 4px"}}>
                              <input type="number" step="any"
                                value={h.qty||""}
                                onChange={e=>updateRow(i,"qty",parseFloat(e.target.value)||0)}
                                style={{...cellInp(72),textAlign:"right",
                                  fontFamily:"'Inter', system-ui, sans-serif"}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=""}
                              />
                            </td>

                            {/* Cost Price */}
                            <td style={{padding:"2px 4px",
                              background:!h.costPrice||h.costPrice===0?"rgba(217,119,6,0.06)":"transparent"}}>
                              <input type="number" step="any"
                                value={h.costPrice||""}
                                onChange={e=>updateRow(i,"costPrice",parseFloat(e.target.value)||0)}
                                style={{...cellInp(72),textAlign:"right",
                                  fontFamily:"'Inter', system-ui, sans-serif",
                                  color:!h.costPrice||h.costPrice===0?"#D97706":T.textPrimary}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=
                                  (!h.costPrice||h.costPrice===0)?"rgba(217,119,6,0.06)":"transparent"}
                                placeholder="0"
                                title="Cost price per unit"
                              />
                            </td>

                            {/* Current Price */}
                            <td style={{padding:"2px 4px"}}>
                              <input type="number" step="any"
                                value={h.currentPrice||""}
                                onChange={e=>updateRow(i,"currentPrice",parseFloat(e.target.value)||0)}
                                style={{...cellInp(72),textAlign:"right",
                                  fontFamily:"'Inter', system-ui, sans-serif"}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=""}
                                placeholder="0"
                              />
                            </td>

                            {/* ISIN */}
                            <td style={{padding:"2px 4px"}}>
                              <input
                                value={h.isin==="-"?"":h.isin||""}
                                onChange={e=>updateRow(i,"isin",e.target.value||"-")}
                                style={{...cellInp(90),color:T.textMuted,fontSize:10,
                                  fontFamily:"'Inter', system-ui, sans-serif"}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=""}
                                placeholder="INF…"
                              />
                            </td>

                            {/* Manager */}
                            <td style={{padding:"2px 4px"}}>
                              <input
                                value={h.manager||""}
                                onChange={e=>updateRow(i,"manager",e.target.value)}
                                style={{...cellInp(90),color:T.textMuted}}
                                onFocus={e=>e.currentTarget.parentElement.style.background=`${T.brand}0D`}
                                onBlur={e=>e.currentTarget.parentElement.style.background=""}
                                placeholder="AMC / House"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={9} style={{padding:"8px 12px"}}>
                            <button onClick={addRow}
                              style={{background:"none",border:`1px dashed ${T.cardBorder}`,
                                borderRadius:6,color:T.brand,fontSize:11,fontWeight:600,
                                padding:"5px 16px",cursor:"pointer",width:"100%",
                                display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                              + Add Row
                            </button>
                          </td>
                        </tr>
                        {/* Summary row */}
                        <tr style={{background:T.thBg}}>
                          <td colSpan={2} style={{padding:"6px 12px",fontSize:10,
                            color:T.textSecondary,fontWeight:600}}>
                            {csvPreview.rows.length} row{csvPreview.rows.length!==1?"s":""}
                            {csvPreview.rows.filter(r=>!r.costPrice||r.costPrice===0).length > 0 && (
                              <span style={{color:"#D97706",marginLeft:8}}>
                                ⚠ {csvPreview.rows.filter(r=>!r.costPrice||r.costPrice===0).length} missing cost price
                              </span>
                            )}
                          </td>
                          <td colSpan={2}/>
                          <td style={{padding:"6px 8px",textAlign:"right",fontSize:10,
                            fontFamily:"'Inter', system-ui, sans-serif",color:T.textSecondary}}>
                            {csvPreview.rows.reduce((s,r)=>s+(parseFloat(r.qty)||0),0).toLocaleString("en-IN",{maximumFractionDigits:2})}
                          </td>
                          <td colSpan={4}/>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })() : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead style={{position:"sticky",top:0,background:T.thBg,zIndex:2}}>
                    <tr>
                      {["Date","Holding","Type","Qty","Price","Currency","Fees"].map(h=>(
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,
                          color:T.textSecondary,borderBottom:`1px solid ${T.cardBorder}`,
                          whiteSpace:"nowrap",fontSize:10}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.rows.slice(0,50).map((t,i)=>(
                      <tr key={i} style={{background:i%2===0?"transparent":T.tableStripe}}>
                        <td style={{padding:"6px 12px",color:T.textMuted}}>{t.date}</td>
                        <td style={{padding:"6px 12px",fontWeight:600}}>{t.holding}</td>
                        <td style={{padding:"6px 12px",color:t.type==="BUY"?"#1D9E75":"#A32D2D",fontWeight:600}}>{t.type}</td>
                        <td style={{padding:"6px 12px",fontFamily:"'Inter', system-ui, sans-serif"}}>{t.qty}</td>
                        <td style={{padding:"6px 12px",fontFamily:"'Inter', system-ui, sans-serif"}}>{t.price}</td>
                        <td style={{padding:"6px 12px"}}>{t.currency}</td>
                        <td style={{padding:"6px 12px",color:T.textMuted}}>{t.fees||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{padding:"14px 24px",borderTop:`1px solid ${T.cardBorder}`,
              display:"flex",justifyContent:"space-between",alignItems:"center",
              flexShrink:0,gap:10,flexWrap:"wrap"}}>
              {importProgress && (
                <div style={{width:"100%",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textSecondary,marginBottom:4}}>
                    <span>Importing {importProgress.current}…</span>
                    <span>{importProgress.done}/{importProgress.total}</span>
                  </div>
                  <div style={{height:6,background:T.borderSubtle,borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",background:T.brand,borderRadius:3,
                      width:`${importProgress.total>0?(importProgress.done/importProgress.total*100):0}%`,
                      transition:"width 0.3s ease"}}/>
                  </div>
                </div>
              )}
              <button onClick={()=>{ if (!importProgress) setCsvPreview(null); }}
                disabled={!!importProgress}
                style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                  borderRadius:8,color:T.textSecondary,padding:"8px 18px",
                  cursor:importProgress?"not-allowed":"pointer",fontSize:13,
                  opacity:importProgress?0.5:1}}>
                Cancel
              </button>
              {(() => {
                const isTxn = csvPreview.target === "transactions";
                const blocked = (isTxn && !csvAssignTo) || !!importProgress;
                return (
                  <button
                    disabled={blocked}
                    onClick={async()=>{
                      if (blocked) return; // safety net — also enforced by disabled
                      const rows = csvPreview.rows;
                      if (csvPreview.target==="holdings"||csvPreview.target==="mf"||csvPreview.target==="globalmf") {
                        // Persist to Supabase
                        // Validate imported rows before committing
                        const invalidRows = rows.filter(h =>
                          !h.name?.trim() || !h.qty || parseFloat(h.qty) <= 0 || !h.currency
                        );
                        if (invalidRows.length > 0) {
                          showToast(`⚠ ${invalidRows.length} row${invalidRows.length!==1?"s":""} missing required fields (name/qty/currency). Fix the CSV and re-upload.`, "#D97706", 6000);
                          return;
                        }
                        let saved = 0, failed = 0;
                        setImportProgress({ done: 0, total: rows.length, current: rows[0]?.name || "" });
                        for (let ri = 0; ri < rows.length; ri++) {
                          const h = rows[ri];
                          setImportProgress({ done: ri, total: rows.length, current: h.name || "" });
                          const err = await addHolding(h).then(()=>null).catch(e=>e);
                          if (err) { console.error("[CSV Import] addHolding:", err?.message, h.name); failed++; }
                          else {
                            if (csvAssignTo) await setHoldingOwner(String(h.id), csvAssignTo).catch(()=>{});
                            saved++;
                          }
                        }
                        setImportProgress(null);
                        setHoldings(p => [...p, ...rows]);
                        if (csvAssignTo) {
                          setHoldingOwners(prev => {
                            const next = {...prev};
                            rows.forEach(h => { next[String(h.id)] = csvAssignTo; });
                            return next;
                          });
                        }
                        logAudit("IMPORT_CSV_HOLDINGS", `${saved} holdings from ${csvPreview.filename}${csvAssignTo?" → "+visibleClients.find(c=>c.id===csvAssignTo)?.name:""}`);
                        showToast(`✓ Imported ${saved} holding${saved!==1?"s":""}${failed>0?` (${failed} failed)`:""}`, "#1D9E75");
                      } else {
                        // Strip UI-only fields, stamp clientId (always present here — button disabled otherwise)
                        const cleanRows = rows.map(({_ibkrDesc, ...r}) => ({
                          ...r,
                          clientId: csvAssignTo,
                        }));
                        setTransactions(p => [...p, ...cleanRows]);
                        // Persist each row to Supabase — report progress every row
                        setImportProgress({ done: 0, total: cleanRows.length, current: `transaction 1 of ${cleanRows.length}` });
                        let saved = 0, failed = 0;
                        let firstError = null;
                        for (let i = 0; i < cleanRows.length; i++) {
                          const t = cleanRows[i];
                          const err = await addTransaction(t).then(()=>null).catch(e=>e);
                          if (err) {
                            console.error("[CSV Import] addTransaction failed:", err?.message||err, "row:", t);
                            if (!firstError) firstError = err?.message || String(err);
                            failed++;
                          }
                          else saved++;
                          // Update progress; yield to event loop every 5 rows so React can paint
                          if ((i+1) % 5 === 0 || i === cleanRows.length-1) {
                            setImportProgress({ done: i+1, total: cleanRows.length, current: `${t.holding||"?"} ${t.type||""}` });
                            await new Promise(r => setTimeout(r, 0));
                          }
                        }
                        const clientName = visibleClients.find(c=>c.id===csvAssignTo)?.name || "client";
                        logAudit("IMPORT_CSV_TRANSACTIONS", `${saved} transactions from ${csvPreview.filename}${csvPreview.ibkr?" (IBKR)":""} → ${clientName}`);

                        // ── Auto-create holdings for transactions that reference securities ──
                        // not already in the client's portfolio. Computes net qty + weighted avg cost.
                        let newHoldingsCreated = 0;
                        if (saved > 0) {
                          // Existing holdings for this client (case-insensitive name match)
                          const clientHoldingIds = new Set(
                            Object.entries(holdingOwners).filter(([_,cId])=>cId===csvAssignTo).map(([hId])=>hId)
                          );
                          const existingNames = new Set(
                            holdings.filter(h => clientHoldingIds.has(String(h.id))).map(h => (h.name||"").toLowerCase().trim())
                          );

                          // Group successfully-saved transactions by holding name
                          const savedTxns = cleanRows.slice(0, saved); // approximation — exact match would need per-row tracking
                          const byName = new Map();
                          for (const t of savedTxns) {
                            const key = (t.holding||"").toLowerCase().trim();
                            if (!key || existingNames.has(key)) continue;
                            if (!byName.has(key)) byName.set(key, []);
                            byName.get(key).push(t);
                          }

                          const BUY_TYPES  = new Set(["BUY","SIP","STP_IN","SWITCH_IN"]);
                          const SELL_TYPES = new Set(["SELL","REDEEM","STP_OUT","SWITCH_OUT"]);

                          const toCreate = [];
                          for (const [_, txns] of byName) {
                            const sample = txns[0];
                            let netQty = 0, buyValue = 0, buyQty = 0;
                            for (const t of txns) {
                              if (BUY_TYPES.has(t.type))  { netQty += t.qty; buyValue += t.qty * t.price; buyQty += t.qty; }
                              if (SELL_TYPES.has(t.type)) { netQty -= t.qty; }
                            }
                            if (netQty <= 0) continue; // skip closed positions
                            const avgCost = buyQty > 0 ? buyValue / buyQty : sample.price || 0;
                            const isINR = (sample.currency||"").toUpperCase() === "INR";
                            const cleanId = `hld_${Date.now()}_${sample.holding.replace(/\W/g,"").slice(0,10)}_${toCreate.length}`;
                            toCreate.push({
                              id: cleanId,
                              name: sample.holding,
                              ticker: "-",
                              class: "Unclassified",
                              exchange: isINR ? "NSE" : "—",
                              currency: sample.currency || "USD",
                              qty: netQty,
                              costPrice: avgCost,
                              currentPrice: avgCost, // user can refresh prices later
                              country: isINR ? "India" : "—",
                              listed: true,
                              isin: "-",
                              manager: "Self-Managed",
                              reconciledFromTxns: true, // camelCase — addHolding maps it to reconciled_from_txns
                            });
                          }

                          // Persist new holdings + assign to client
                          if (toCreate.length > 0) {
                            setImportProgress({ done: 0, total: toCreate.length, current: `creating holdings…` });
                          }
                          for (let i = 0; i < toCreate.length; i++) {
                            const h = toCreate[i];
                            const err = await addHolding(h).then(()=>null).catch(e=>e);
                            if (err) { console.error("[CSV Import] auto-create holding failed:", err?.message||err, h.name); }
                            else {
                              await setHoldingOwner(String(h.id), csvAssignTo).catch(()=>{});
                              newHoldingsCreated++;
                            }
                            // Progress every 2 holdings (smaller batch usually)
                            if ((i+1) % 2 === 0 || i === toCreate.length-1) {
                              setImportProgress({ done: i+1, total: toCreate.length, current: `holding "${h.name?.slice(0,30) || ""}"` });
                              await new Promise(r => setTimeout(r, 0));
                            }
                          }
                          if (toCreate.length > 0) {
                            setHoldings(p => [...p, ...toCreate]);
                            setHoldingOwners(prev => {
                              const next = {...prev};
                              toCreate.forEach(h => { next[String(h.id)] = csvAssignTo; });
                              return next;
                            });
                            logAudit("AUTO_CREATE_HOLDINGS_FROM_TXNS", `${newHoldingsCreated} holdings for ${clientName} from CSV import`);
                          }
                        }

                        if (saved === 0 && failed > 0) {
                          // All failed — show the actual Supabase error so user can debug
                          showToast(`❌ All ${failed} transactions failed. First error: ${firstError?.slice(0,180) || "(see console)"}`, "#A32D2D", 12000);
                          // Also rollback the optimistic state insert so the UI doesn't show ghosts
                          setTransactions(p => p.filter(t => !cleanRows.some(c => c.id === t.id)));
                        } else {
                          const parts = [
                            `✓ Imported ${saved} transaction${saved!==1?"s":""}`,
                            newHoldingsCreated > 0 ? `+ created ${newHoldingsCreated} new holding${newHoldingsCreated!==1?"s":""}` : "",
                            failed > 0 ? `(${failed} txns failed — see console)` : "",
                            `→ ${clientName}`,
                          ].filter(Boolean).join(" ");
                          showToast(parts, failed>0?"#D97706":"#1D9E75", newHoldingsCreated>0?6000:3500);
                        }
                      }
                      setImportProgress(null);
                      setCsvPreview(null);
                      setCsvAssignTo("");
                    }}
                    style={{
                      background: blocked
                        ? T.borderSubtle
                        : "#007878",
                      border: blocked ? `1px solid ${T.cardBorder}` : "none",
                      borderRadius:8,
                      color: blocked ? T.textSecondary : "white",
                      padding:"9px 24px",
                      cursor: blocked ? "not-allowed" : "pointer",
                      fontSize:13,fontWeight:700,
                      opacity: blocked ? 0.6 : 1,
                    }}
                    title={blocked ? "Select a client to enable import" : ""}>
                    {blocked
                      ? "⛔ Select a client first"
                      : importProgress
                        ? `⏳ Importing ${importProgress.done}/${importProgress.total}…`
                        : `✓ Confirm Import (${csvPreview.rows.length} row${csvPreview.rows.length!==1?"s":""})`}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {editHoldId && (function(){
        const editHold = holdings.find(h => String(h.id) === String(editHoldId)) || null;
        // ev = base holding merged with any user edits — ensures all fields always exist
        const ev = editHold ? { ...editHold, ...editHoldVals } : editHoldVals;
        const close = () => {
          setEditHoldId(null); setEditHoldVals({});
          setEditMfQuery(""); setEditMfResults([]); setEditMfOpen(false);
          setEditTkQuery(""); setEditTkResults([]); setEditTkOpen(false);
        };
        // Sync search query to current name when first opening an MF holding
        // Also trigger the search so dropdown pre-populates on focus
        if (ev.class === "Indian Mutual Funds" && editMfQuery === "" && ev.name) {
          setEditMfQuery(ev.name);
          // Pre-fetch results after render so dropdown is ready on first focus
          if (ev.name.length >= 3) {
            const q = ev.name.slice(0, 40);
            setTimeout(() => {
              setEditMfLoading(true);
              fetch(`/api/mf-search?q=${encodeURIComponent(q)}`)
                .then(r => r.json())
                .then(j => {
                  const results = j.results || [];
                  if (results.length > 0) setEditMfResults(results);
                  setEditMfLoading(false);
                })
                .catch(() => setEditMfLoading(false));
            }, 50);
          }
        }

        // Sync ticker field:
        // - For MF: show existing scheme code, or auto-lookup from ISIN if ticker is missing/is an ISIN
        // - For stocks: show existing ticker
        if (editTkQuery === "") {
          if (ev.class === "Indian Mutual Funds") {
            const tkr = ev.ticker || "";
            const isSchemeCode = /^\d{5,6}$/.test(tkr);
            const isISIN       = /^INF[A-Z0-9]{9}$/.test(tkr);
            if (isSchemeCode) {
              // Already a valid scheme code — show the fund name instead for better UX
              // The scheme code will still be stored in ticker field; we just show name in search box
              setEditTkQuery(tkr);
              // Also pre-fetch fund info so the search field shows context
              fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(ev.name || tkr)}`)
                .then(r => r.json())
                .then(j => {
                  const arr = Array.isArray(j) ? j : (j.results || []);
                  const match = arr.find(r =>
                    String(r.schemeCode || r.amfiCode || "") === tkr ||
                    r.isin === (ev.isin || "")
                  ) || arr[0];
                  if (match) {
                    const displayName = match.schemeName || match.name || "";
                    if (displayName) setEditTkQuery(displayName);
                    setEditTkResults([{ symbol: String(match.schemeCode||match.amfiCode||""), name: displayName, exchange:"AMFI", subLabel:`Code: ${match.schemeCode||""}` }]);
                  }
                })
                .catch(() => {});
            } else if (!tkr || tkr === "-" || isISIN) {
              // Ticker missing or is the ISIN — try to look up scheme code from ISIN
              const isin = ev.isin || tkr;
              if (isin && isin.startsWith("INF")) {
                // Search mfapi by ISIN to get the scheme code
                fetch(`https://api.mfapi.in/mf/search?q=${encodeURIComponent(ev.name || isin)}`)
                  .then(r => r.json())
                  .then(j => {
                    const arr = Array.isArray(j) ? j : (j.results || []);
                    const evNameLower = (ev.name || "").toLowerCase();
                    const match = arr.find(r =>
                      (r.isin || "") === isin ||
                      (r.isin || "") === (ev.isin || "") ||
                      (r.schemeName || r.name || "").toLowerCase().includes(evNameLower.slice(0, 20)) ||
                      evNameLower.includes((r.schemeName || r.name || "").toLowerCase().slice(0, 20))
                    );
                    if (match) {
                      const code = String(match.schemeCode || match.amfiCode || match.symbol || "");
                      if (code && /^\d{5,6}$/.test(code)) {
                        setEditTkQuery(code);
                        setEditHoldVals(p => ({ ...p, ticker: code, isMutualFund: true, listed: true }));
                      }
                    }
                  })
                  .catch(() => {});
              }
            }
          } else if (ev.ticker && ev.ticker !== "-") {
            setEditTkQuery(ev.ticker);
          }
        }

        // Search stocks/ETFs for edit modal
        const searchEditTicker = async (q) => {
          setEditTkQuery(q);
          if (q.length < 2) { setEditTkResults([]); setEditTkOpen(false); return; }
          setEditTkLoading(true); setEditTkOpen(true);
          try {
            const isIndianMF   = ev.class === "Indian Mutual Funds";
            const isGlobalMF   = ev.class === "Global Mutual Funds";
            let results = [];
            if (isIndianMF) {
              const local = searchPopularMF(q);
              setEditTkResults(local);
              try {
                const r = await fetch(`/api/mf-search?q=${encodeURIComponent(q)}`);
                const j = await r.json();
                const apiArr = j.results || [];
                results = apiArr.length > 0 ? apiArr : local;
              } catch { results = local; }
            } else {
              const r = await fetch(`/api/search?q=${encodeURIComponent(q)}&exchange=${encodeURIComponent(ev.exchange||"")}`);
              const j = await r.json();
              results = j.results || [];
            }
            setEditTkResults(results.slice(0,15));
          } catch { setEditTkResults([]); }
          setEditTkLoading(false);
        };

        // Select result in edit modal ticker search
        const selectEditTicker = async (r) => {
          setEditTkOpen(false);
          const rawSymbol = r.symbol || "";
          const cleanSymbol = rawSymbol
            .replace(/\.DEX$/,".DE").replace(/\.SWX$/,".SW").replace(/\.PAX$/,".PA")
            .replace(/\.ASX$/,".AS").replace(/\.LON$/,".L").replace(/\.LSE$/,".L")
            .replace(/\.MIX$/,".MI").replace(/\.MCX$/,".MC");
          // ── Branch on what the SEARCH RESULT actually is, not on the existing holding's class ──
          // Forcing Indian MF defaults just because `ev.class === "Indian Mutual Funds"` traps
          // any edit-by-search of a misclassified row into staying Indian. The right answer is
          // to trust the search result: r.isMutualFund / r.isGlobalMF / otherwise = equity/ETF.
          if (r.isMutualFund) {
            const amfiCode = String(r.amfiCode || r.schemeCode || r.symbol || "");
            setEditTkQuery(r.name || amfiCode);
            setEditHoldVals(p=>({...p,
              ticker:       amfiCode,
              name:         r.name || p.name,
              exchange:     "AMFI",
              currency:     "INR",
              country:      "India",
              class:        "Indian Mutual Funds",
              listed:       true,        // listed=true so price refresh fetches NAV
              isMutualFund: true,
              isin:         r.isin || p.isin || "-",
              manager:      r.house || r.amc || p.manager || "Self-Managed",
            }));
            try {
              const res = await fetch(`/api/price?symbol=${encodeURIComponent(amfiCode)}&type=mf`);
              const j = await res.json();
              if (j?.price && j.price > 0) {
                setEditHoldVals(p=>({...p, currentPrice: j.price}));
                showToast(`✓ NAV: ₹${j.price}`, "#1D9E75", 2000);
              }
            } catch {}
          } else if (r.isGlobalMF) {
            // ── Global Mutual Fund ── (mirrors add-holding click handler logic)
            setEditTkQuery(r.name || r.isin || "");
            setEditHoldVals(p=>({...p,
              ticker:       r.isin || "-",
              name:         r.name || p.name,
              exchange:     r.exchange || "UCITS",
              currency:     r.currency || "USD",
              class:        "Global Mutual Funds",
              country:      "",                          // clear India default if user is converting from a misclassified row
              isMutualFund: false,                       // not Indian — UCITS NAV path
              isGlobalMF:   true,
              listed:       true,
              isin:         r.isin || p.isin || "",
              manager:      r.manager || p.manager || "",
              sector:       r.sector || p.sector || "",
              yahooTicker:  r.yahooTicker || p.yahooTicker || "",
              currentPrice: "",                          // NAV entered manually or via UCITS fetch
            }));
          } else {
            setEditTkQuery(cleanSymbol);
            setEditHoldVals(p=>({...p,
              ticker: cleanSymbol,
              name: r.name || r.description || cleanSymbol, // always use result name
              exchange: r.exchange || p.exchange || "",
              currency: r.currency || p.currency || (r.exchange==="NSE"||r.exchange==="BSE"?"INR":"USD"),
              capSize: TICKER_CAP[cleanSymbol] || p.capSize || "N/A",
              country: r.country || p.country || (r.exchange==="NSE"||r.exchange==="BSE"?"India":p.country),
            }));
            try {
              const params = new URLSearchParams({ symbol: cleanSymbol });
              if (r.exchange) params.append("exchange", r.exchange);
              const res = await fetch(`/api/price?${params}`);
              const j = await res.json();
              if (j?.price) setEditHoldVals(p=>({...p, currentPrice: j.price}));
            } catch {}
          }
        };
        const save  = () => {
          // For MFs: if editTkQuery has a valid scheme code and ev.ticker is empty/ISIN, use editTkQuery
          const isMF = ev.class === "Indian Mutual Funds";
          const tkFromQuery = editTkQuery.trim();
          const isValidSchemeCode = /^\d{5,6}$/.test(tkFromQuery);
          const tickerSource = (isMF && isValidSchemeCode && (
            !ev.ticker || ev.ticker === "-" || /^INF/.test(ev.ticker)
          )) ? tkFromQuery : (ev.ticker || "");
          // Normalise ticker: trim whitespace + uppercase (prevents " C " ≠ "C" mismatch)
          const cleanTicker = isMF ? (tickerSource || tkFromQuery || ev.ticker || "-").trim() : (tickerSource || "").trim().toUpperCase() || "-";
          // Normalise sector: if empty/dash use auto-resolve so it stays consistent with map
          const manualSector = (ev.sector || "").trim();
          // ── Validate before save ─────────────────────────────────────────
          const editIsValueOnly = isValueOnlyClass(ev.class);
          const editErrs = [];
          if (!(ev.name||"").trim())                      editErrs.push("Asset name is required");
          if (editIsValueOnly) {
            const eCost = parseFloat(ev.costPrice);
            const eCurr = parseFloat(ev.currentPrice);
            const hasCost = ev.costPrice !== "" && !isNaN(eCost) && eCost > 0;
            const hasCurr = ev.currentPrice !== "" && !isNaN(eCurr) && eCurr > 0;
            if (!hasCost && !hasCurr) editErrs.push("Enter Invested Amount or Current Market Value");
            if (eCost < 0)            editErrs.push("Invested cannot be negative");
            if (eCurr < 0)            editErrs.push("Current Value cannot be negative");
          } else {
            if (!ev.qty || parseFloat(ev.qty) <= 0)         editErrs.push("Quantity must be > 0");
            if (parseFloat(ev.costPrice||0) < 0)            editErrs.push("Cost price cannot be negative");
          }
          if (!ev.currency)                               editErrs.push("Currency is required");
          if (!ev.class)                                  editErrs.push("Asset class is required");
          if (editErrs.length > 0) { showToast("⚠ " + editErrs[0], "#D97706"); return; }

          // For MFs, schemeCode = cleanTicker (it's the AMFI scheme code)
          const mfSchemeCode = (ev.class === "Indian Mutual Funds" && /^\d{5,6}$/.test(cleanTicker))
            ? cleanTicker : (ev.schemeCode || null);
          // Value-only sanitisation: qty stored as 1 internally; if user only
          // supplied one of invested/current, default the missing one to the
          // other so cost/value math doesn't show artificial losses.
          let editQty, editCost, editCurr, editTicker, editListed;
          if (editIsValueOnly) {
            editQty = 1;
            const eCost = parseFloat(ev.costPrice) || 0;
            const eCurr = parseFloat(ev.currentPrice) || 0;
            editCost = eCost > 0 ? eCost : eCurr;
            editCurr = eCurr > 0 ? eCurr : eCost;
            editTicker = "";    // value-only assets have no ticker
            editListed = false; // never auto-classify as Listed
          } else {
            editQty = parseFloat(ev.qty) || 0;
            editCost = parseFloat(ev.costPrice) || 0;
            editCurr = parseFloat(ev.currentPrice) || 0;
            editTicker = cleanTicker;
            editListed = ev.listed===true||ev.listed===undefined;
          }
          const updated = { name:(ev.name||"").trim(), ticker:editTicker, class:ev.class,
            currency:ev.currency, qty:editQty,
            costPrice:editCost,
            currentPrice:editCurr, country:(ev.country||"").trim(),
            yahooTicker: editIsValueOnly ? null : ((ev.yahooTicker||"").trim()||null),
            exchange:ev.exchange||"", listed:editListed,
            isMutualFund: editIsValueOnly ? false : (ev.isMutualFund||false), isin:(ev.isin||"").trim(),
            schemeCode: editIsValueOnly ? null : mfSchemeCode,
            sector:manualSector, capSize:ev.capSize||"N/A",
            manager:ev.manager||"Self-Managed",
            advisor:ev.advisor||null,
            manualPriceDate:ev.manualPriceDate||"",
            // Passion asset fields
            appraisalValue:ev.appraisalValue?parseFloat(ev.appraisalValue):undefined,
            appraisalDate:ev.appraisalDate||undefined,
            physicalLocation:ev.physicalLocation||undefined,
            insuranceCoverage:ev.insuranceCoverage?parseFloat(ev.insuranceCoverage):undefined,
            appraiser:ev.appraiser||undefined,
            // Crypto fields
            cryptoCategory:ev.cryptoCategory||undefined,
            walletType:ev.walletType||undefined,
            walletAddress:ev.walletAddress||undefined,
          };
          // Re-resolve sector and capSize using updated ticker so display stays accurate
          const resolvedSector = resolveHoldingSector({...ev, ticker:cleanTicker, sector:manualSector});
          const resolvedCap    = resolveCapSize({...ev, ticker:cleanTicker});
          const finalUpdated = {
            ...updated,
            // Only overwrite sector/cap if not explicitly set by user
            sector:  manualSector || resolvedSector,
            capSize: updated.capSize !== "N/A" ? updated.capSize : resolvedCap,
          };
          const updatedWithId = {...finalUpdated, id:editHoldId};
          setHoldings(p=>p.map(h=>h.id===editHoldId?{...h,...finalUpdated}:h));
          updateHolding(editHoldId, finalUpdated)
            .then(() => {
              showToast("✓ Holding saved", "#1D9E75", 2000);
              close(); // auto-close on success
            })
            .catch(e => {
              console.error("[Supabase] updateHolding:", e.message);
              showToast("⚠ Save failed: " + e.message, "#A32D2D", 5000);
            });
          // For MFs: clear stale cache, fetch fresh NAV, persist to Supabase immediately
          if (finalUpdated.class === "Indian Mutual Funds" && mfSchemeCode) {
            const oldCode = holdings.find(h=>h.id===editHoldId)?.ticker;
            // Clear old AND new cache entries so refresh always fetches fresh
            if (oldCode) clearCacheKey(`mf_${oldCode}`);
            clearCacheKey(`mf_${mfSchemeCode}`);
            // Seed cache with currentPrice we already have so UI doesn't flicker
            const knownNav = parseFloat(finalUpdated.currentPrice) || 0;
            if (knownNav > 0) setCache(`mf_${mfSchemeCode}`, knownNav);
            // Fetch authoritative NAV from MFapi and persist — runs after modal closes
            setTimeout(async () => {
              try {
                const freshNav = await PriceDataProvider.getMFNav(mfSchemeCode, true);
                const navToSave = (freshNav && freshNav > 0) ? freshNav : knownNav;
                if (navToSave > 0) {
                  // Update both local state AND Supabase with correct price + scheme code
                  setHoldings(p=>p.map(h=>h.id===editHoldId
                    ? {...h, currentPrice:navToSave, ticker:mfSchemeCode, schemeCode:mfSchemeCode}
                    : h
                  ));
                  updateHoldingPrice(editHoldId, navToSave, new Date().toISOString().slice(0,10)).catch(()=>{});
                  // Also ensure schemeCode is persisted so future refreshes use correct code
                  updateHolding(editHoldId, { schemeCode: mfSchemeCode, ticker: mfSchemeCode }).catch(()=>{});
                }
              } catch(e) { console.error("[MFSave]", e.message); }
            }, 800);
          }
          // If a Global MF just got a yahooTicker set, immediately fetch its price
          const isUCITSSave = finalUpdated.class === "Global Mutual Funds" || finalUpdated.isGlobalMF ||
            (finalUpdated.ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(finalUpdated.ticker));
          if (isUCITSSave && finalUpdated.yahooTicker) {
            const isin = (finalUpdated.isin && finalUpdated.isin !== "-") ? finalUpdated.isin : finalUpdated.ticker;
            setTimeout(async () => {
              setLiveStatus(p=>({...p,[editHoldId]:"fetching"}));
              const nav = await PriceDataProvider.getUCITSNav(isin, true, finalUpdated.yahooTicker);
              if (nav && nav > 0) {
                setHoldings(p=>p.map(h=>h.id===editHoldId?{...h,currentPrice:nav}:h));
                setLiveStatus(p=>({...p,[editHoldId]:"yahoo_ucits"}));
                updateHoldingPrice(editHoldId, nav, new Date().toISOString().slice(0,10)).catch(()=>{});
                showToast(`✓ NAV fetched: ${nav.toFixed(4)}`, "#1D9E75", 3000);
              } else {
                setLiveStatus(p=>({...p,[editHoldId]:"manual"}));
              }
            }, 500);
          }
          logAudit("EDIT_HOLDING", `Updated "${finalUpdated.name}" — ticker:${finalUpdated.ticker}, cost:${finalUpdated.costPrice}`);
          close();
        };
        // Ctrl/Cmd+Enter in any field triggers save
        const handleFieldKeyDown = (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            document.getElementById("editHoldSaveBtn")?.click();
          }
        };
        const field = (label, key, type="text", opts=null) => (
          <div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
            {opts ? (
              <select style={inp()} value={ev[key]||""} onChange={e=>setEditHoldVals(p=>({...p,[key]:e.target.value}))}>
                {opts.map(o=><option key={o}>{o}</option>)}
              </select>
            ) : (
              <input type={type} style={inp()} value={ev[key]||""} onChange={e=>setEditHoldVals(p=>({...p,[key]:e.target.value}))}/>
            )}
          </div>
        );
        const sectionHead = txt => <div style={{fontSize:10,fontWeight:700,color:T.brand,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingBottom:6,paddingTop:14,borderTop:`1px solid ${T.cardBorder}`}}>{txt}</div>;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
            onClick={e=>e.target===e.currentTarget&&close()}>
            <div style={{background:T.cardBg,borderRadius:16,width:"100%",maxWidth:680,maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 30px 80px rgba(0,0,0,0.8)"}}>
              <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700}}>Edit Holding</div>
                  <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>ID #{editHoldId} · Changes saved immediately</div>
                </div>
                <button onClick={close} style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,color:"#A32D2D",width:34,height:34,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              </div>
              {/* ── ISIN / class mismatch banner ── */}
              {/* Warns when the holding's ISIN doesn't match the implied domicile of its class.
                  Most common case: an Irish UCITS fund (ISIN starts with IE/LU/GB/FR/DE/CH/NL)
                  saved with class="Indian Mutual Funds" via a buggy import or earlier code path. */}
              {(() => {
                const isin = (ev.isin || "").trim().toUpperCase();
                const cls  = ev.class || "";
                const isIndianClass = cls === "Indian Mutual Funds";
                const isIndianIsin  = /^IN[EF]/.test(isin);
                const isForeignIsin = /^(IE|LU|GB|FR|DE|CH|NL|US)[A-Z0-9]{10}$/.test(isin);
                if (isIndianClass && isForeignIsin) {
                  // Decide the likely correct class from ISIN country code
                  const suggested = isin.startsWith("US")
                    ? { cls:"Equities", country:"USA", currency:"USD", exchange:"NASDAQ" }
                    : { cls:"Global Mutual Funds", country:"", currency:"USD", exchange:"Ireland UCITS", isMutualFund:false, isGlobalMF:true, listed:true };
                  return (
                    <div style={{margin:"0 24px 12px",padding:"10px 14px",background:"rgba(251,146,60,0.08)",border:"1px solid rgba(251,146,60,0.4)",borderRadius:8,fontSize:12,color:T.textPrimary,display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:16}}>⚠️</span>
                      <div style={{flex:1,lineHeight:1.5}}>
                        <strong>ISIN/class mismatch detected.</strong> This holding's ISIN <code style={{fontSize:11,padding:"1px 5px",background:"rgba(0,0,0,0.05)",borderRadius:3}}>{isin}</code> looks like a {isin.startsWith("US") ? "US equity" : "non-Indian fund"} (country code <strong>{isin.slice(0,2)}</strong>), but the class is set to <strong>Indian Mutual Funds</strong>. NAV/price fetches will not work correctly.
                      </div>
                      <button onClick={() => setEditHoldVals(p => ({...p, ...suggested}))} style={{padding:"6px 12px",background:"#FB923C",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                        Set to {suggested.cls}
                      </button>
                    </div>
                  );
                }
                // Reverse mismatch: Indian ISIN but class is Global/Equities
                if (!isIndianClass && isIndianIsin && (cls === "Global Mutual Funds" || cls === "Equities")) {
                  return (
                    <div style={{margin:"0 24px 12px",padding:"10px 14px",background:"rgba(251,146,60,0.08)",border:"1px solid rgba(251,146,60,0.4)",borderRadius:8,fontSize:12,color:T.textPrimary,display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:16}}>⚠️</span>
                      <div style={{flex:1,lineHeight:1.5}}>
                        <strong>ISIN/class mismatch.</strong> ISIN <code style={{fontSize:11,padding:"1px 5px",background:"rgba(0,0,0,0.05)",borderRadius:3}}>{isin}</code> is an Indian security, but class is <strong>{cls}</strong>.
                      </div>
                      <button onClick={() => setEditHoldVals(p => ({...p, class:"Indian Mutual Funds", country:"India", currency:"INR", exchange:"AMFI", isMutualFund:true, isGlobalMF:false}))} style={{padding:"6px 12px",background:"#FB923C",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                        Set to Indian Mutual Funds
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
              <div style={{overflowY:"auto",padding:"4px 24px 24px",flex:1}}>
                {sectionHead("Asset Details")}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>

                  {/* Name field — MF autocomplete when class is Indian Mutual Funds */}
                  <div style={{gridColumn:"span 2"}}>
                    {ev.class === "Indian Mutual Funds" ? (function(){
                      // Use component-level state (hooks rule — no useState in IIFE)
                      const mfQuery    = editMfQuery;
                      const setMfQuery = setEditMfQuery;
                      const mfResults  = editMfResults;
                      const setMfResults = setEditMfResults;
                      const mfLoading  = editMfLoading;
                      const setMfLoading = setEditMfLoading;
                      const mfOpen     = editMfOpen;
                      const setMfOpen  = setEditMfOpen;
                      const searchMF = async (q) => {
                        setMfQuery(q);
                        setEditHoldVals(p=>({...p, name:q}));
                        if (q.length < 2) { setMfResults([]); setMfOpen(false); return; }
                        setMfLoading(true); setMfOpen(true);
                        try {
                          const local = searchPopularMF(q);
                          if (local.length) setMfResults(local);
                          const r = await fetch(`/api/mf-search?q=${encodeURIComponent(q)}`);
                          const j = await r.json();
                          const apiResults = j.results || [];
                          setMfResults((apiResults.length > 0 ? apiResults : local).slice(0,20));
                        } catch { /* use local */ }
                        setMfLoading(false);
                      };
                      const selectMF = (fund) => {
                        setMfQuery(fund.name||fund.schemeName||fund.symbol||"");
                        setMfOpen(false);
                        setEditHoldVals(p=>({...p,
                          name:         fund.name||fund.schemeName||fund.symbol||p.name,
                          ticker:       String(fund.schemeCode||fund.amfiCode||fund.symbol||p.ticker||"-"),
                          isin:         fund.isin||p.isin||"-",
                          manager:      fund.house||fund.amc||p.manager||"Self-Managed",
                          listed:       true,        // MFs need listed=true for price refresh
                          isMutualFund: true,
                          class:        "Indian Mutual Funds",
                          exchange:     "AMFI",
                          currency:     "INR",
                          country:      "India",
                          sector:       "Diversified",
                        }));
                      };
                      return (
                        <div style={{position:"relative"}}>
                          <div style={{display:"flex",gap:6}}>
                            <input style={{...inp(),flex:1}} value={mfQuery}
                              onChange={e=>searchMF(e.target.value)}
                              onFocus={()=>{
                                if (mfResults.length > 0) { setMfOpen(true); return; }
                                if (mfQuery.length >= 2) searchMF(mfQuery);
                              }}
                              onBlur={()=>setTimeout(()=>setMfOpen(false),200)}
                              placeholder="Type fund name e.g. Parag Parikh Flexi Cap…"/>
                            {mfLoading && <span style={{fontSize:11,color:T.textMuted,alignSelf:"center"}}>⏳</span>}
                          </div>
                          {mfOpen && mfResults.length > 0 && (
                            <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:999,
                              background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                              boxShadow:"0 8px 32px rgba(0,0,0,0.2)",maxHeight:320,overflowY:"auto",marginTop:2,minWidth:480}}>
                              {mfResults.map((f,fi)=>{
                                const n = f.name||f.schemeName||f.symbol||"";
                                const code = f.schemeCode||f.amfiCode||"";
                                const house = f.house||f.amc||"";
                                return (
                                  <div key={fi} onMouseDown={()=>selectMF(f)}
                                    style={{padding:"8px 12px",cursor:"pointer",borderBottom:`1px solid ${T.borderSubtle}`,
                                      display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}
                                    onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                                    <div>
                                      <div style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{n}</div>
                                      <div style={{fontSize:10,color:T.textMuted}}>{house}{code?` · Code: ${code}`:""}</div>
                                    </div>
                                    <span style={{fontSize:9,color:T.brand,background:`${T.brand}12`,
                                      padding:"2px 6px",borderRadius:3,flexShrink:0}}>Select</span>
                                  </div>
                                );
                              })}
                              <div onMouseDown={()=>setMfOpen(false)}
                                style={{padding:"6px 12px",fontSize:10,color:T.textMuted,cursor:"pointer",textAlign:"center"}}>
                                ✕ Close
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })() : field("Name","name")}
                  </div>

                  {/* Ticker — searchable for stocks/ETFs, scheme code for MF */}
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Ticker / Search
                      {ev.class==="Indian Mutual Funds" && <span style={{color:T.textMuted,textTransform:"none",fontWeight:400}}> (AMFI scheme code)</span>}
                    </div>
                    <div style={{position:"relative"}}>
                      <div style={{display:"flex",gap:6}}>
                        <input style={{...inp(), flex:1}}
                          value={editTkQuery}
                          onChange={e=>searchEditTicker(e.target.value)}
                          onFocus={()=>{ if(editTkQuery.length>=2) setEditTkOpen(true); }}
                          onBlur={()=>setTimeout(()=>setEditTkOpen(false),200)}
                          placeholder={ev.class==="Indian Mutual Funds" ? "Search fund name or enter AMFI scheme code…" : "Search ticker or company name…"}/>
                        {/* Fetch NAV button for MFs with a valid scheme code */}
                        {ev.class==="Indian Mutual Funds" && /^\d{5,6}$/.test(editTkQuery) && (
                          <button type="button"
                            onClick={async()=>{
                              try {
                                const res = await fetch(`/api/price?symbol=${encodeURIComponent(editTkQuery)}&type=mf`);
                                const j = await res.json();
                                if (j?.price) {
                                  setEditHoldVals(p=>({...p, ticker:editTkQuery, currentPrice:j.price, isMutualFund:true, listed:true}));
                                  showToast(`✓ NAV updated: ₹${j.price}`, "#1D9E75");
                                } else {
                                  showToast("Could not fetch NAV — check scheme code", "#A32D2D");
                                }
                              } catch(e) { showToast("NAV fetch failed", "#A32D2D"); }
                            }}
                            style={{background:"#1D9E75",border:"none",borderRadius:7,color:"white",
                              padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,
                              whiteSpace:"nowrap",flexShrink:0}}>
                            📈 Fetch NAV
                          </button>
                        )}
                      </div>
                      {editTkLoading && (
                        <div style={{fontSize:10,color:T.textMuted,marginTop:3}}>⏳ Searching…</div>
                      )}
                      {editTkOpen && editTkResults.length > 0 && (
                        <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:999,
                          background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                          boxShadow:"0 8px 32px rgba(0,0,0,0.2)",maxHeight:320,overflowY:"auto",minWidth:480}}>
                          {editTkResults.map((r,ri)=>(
                            <div key={ri} onMouseDown={()=>selectEditTicker(r)}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",
                                cursor:"pointer",background:ri%2===0?"transparent":T.tableStripe,
                                borderBottom:`1px solid ${T.borderSubtle}`}}
                              onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                              onMouseLeave={e=>e.currentTarget.style.background=ri%2===0?"transparent":T.tableStripe}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{fontSize:12,fontWeight:700,color:T.brand,fontFamily:"monospace"}}>
                                    {r.isMutualFund ? (r.amfiCode||r.schemeCode||r.symbol) : (r.symbol||r.ticker||"")}
                                  </span>
                                  {r.exchange && (
                                    <span style={{fontSize:10,background:T.borderSubtle,color:T.textSecondary,
                                      borderRadius:3,padding:"1px 5px"}}>{r.exchange}</span>
                                  )}
                                  {r.currency && (
                                    <span style={{fontSize:10,color:T.textMuted}}>{r.currency}</span>
                                  )}
                                </div>
                                <div style={{fontSize:11,color:T.textSecondary,overflow:"hidden",
                                  textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:420}}>
                                  {r.name||r.schemeName||""}
                                  {(r.house||r.amc) && <span style={{color:T.textMuted}}> · {r.house||r.amc}</span>}
                                </div>
                              </div>
                              <span style={{fontSize:9,color:T.brand,background:`${T.brand}12`,
                                padding:"2px 6px",borderRadius:3,flexShrink:0}}>Select</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {editTkOpen && editTkResults.length===0 && !editTkLoading && editTkQuery.length>=2 && (
                        <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:999,
                          background:T.cardBg,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                          padding:"10px 12px",fontSize:12,color:T.textMuted,textAlign:"center"}}>
                          No results — try a different name or ticker
                        </div>
                      )}
                    </div>
                  </div>
                  {field("ISIN","isin")}
                  {field("Asset Class","class",undefined,ASSET_CLASSES.filter(a => a !== "Mutual Funds"))}
                  {field("Sector","sector",undefined,["Financials","Technology","Healthcare","Consumer Staples","Consumer Discretionary","Communication Services","Industrials","Energy","Materials","Real Estate","Utilities","Fixed Income","Commodities","Digital Assets","Alternatives","Diversified","Cash","Other"])}
                  {field("Country","country")}
                  {field("Exchange","exchange",undefined,["NSE","BSE","NYSE","NASDAQ","LSE","SGX","TSE","Private","Other"])}
                </div>
                {sectionHead(ev.class && isValueOnlyClass(ev.class) ? "Valuation" : "Position")}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
                  {ev.class && isValueOnlyClass(ev.class) ? (
                    <>
                      {field("Currency","currency",undefined,["USD","INR","EUR","GBP","AED","SGD","JPY","CHF","HKD","CAD","AUD"])}
                      <div/>
                      {field("Invested Amount","costPrice","number")}
                      {field("Current Market Value","currentPrice","number")}
                    </>
                  ) : (
                    <>
                      {field("Quantity","qty","number")}
                      {field("Currency","currency",undefined,["USD","INR","EUR","GBP","AED","SGD","JPY","CHF","HKD","CAD","AUD"])}
                      {field("Cost Price","costPrice","number")}
                      {field("Current Price","currentPrice","number")}
                    </>
                  )}
                </div>
                {sectionHead("Classification")}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
                  {field("Cap Size","capSize",undefined,["Giant","Mega","Large","Mid","Small","Micro","N/A"])}
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Custodian / Manager</div>
                    <input list="manager-list" style={inp()} value={ev.manager||""} onChange={e=>setEditHoldVals(p=>({...p,manager:e.target.value}))} placeholder="e.g. DBS, Self-Managed, HDFC AMC…"/>
                    <datalist id="manager-list">
                      {["Self-Managed","DBS","ICICI Securities","HDFC Securities","Kotak Securities","Motilal Oswal","360 ONE","Nuvama","IIFL","Zerodha","Phillip Capital","UBS","Citibank","HSBC","Blackstone","Others"].map(m=><option key={m} value={m}/>)}
                    </datalist>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Advisor</div>
                    <input style={inp()} value={ev.advisor||""} onChange={e=>setEditHoldVals(p=>({...p,advisor:e.target.value}))} placeholder="e.g. Forthius, Self-Advised"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Listed?</div>
                    <select style={inp()} value={ev.listed===false?"unlisted":"listed"}
                      onChange={e=>setEditHoldVals(p=>({...p,listed:e.target.value==="listed"}))}>
                      <option value="listed">{ev.isMutualFund ? "Listed (live NAV via AMFI)" : "Listed (live price)"}</option>
                      <option value="unlisted">Unlisted (manual price)</option>
                    </select>
                  </div>
                  {field("Manual Price Date","manualPriceDate","date")}
                </div>
              </div>
              {/* Passion Asset Fields — appraisal + location + insurance */}
              {["Art & Collectibles","Private Equity","Real Estate","Alternates","Commodities"].includes(ev.class) && (
                <div style={{padding:"0 24px 14px"}}>
                  {sectionHead("Passion Asset Details")}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Last Appraised Value ({ev.currency||"USD"})</div>
                      <input type="number" style={inp()} value={ev.appraisalValue||""} onChange={e=>setEditHoldVals(p=>({...p,appraisalValue:e.target.value}))} placeholder="e.g. 4500000"/>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Appraisal Date</div>
                      <input type="date" style={inp()} value={ev.appraisalDate||""} onChange={e=>setEditHoldVals(p=>({...p,appraisalDate:e.target.value}))}/>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Physical Location</div>
                      <input type="text" style={inp()} value={ev.physicalLocation||""} onChange={e=>setEditHoldVals(p=>({...p,physicalLocation:e.target.value}))} placeholder="e.g. Geneva Freeport, London, Mumbai"/>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Insurance Coverage ({ev.currency||"USD"})</div>
                      <input type="number" style={inp()} value={ev.insuranceCoverage||""} onChange={e=>setEditHoldVals(p=>({...p,insuranceCoverage:e.target.value}))} placeholder="e.g. 5000000"/>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Appraiser / Valuation Agent</div>
                    <input type="text" style={inp()} value={ev.appraiser||""} onChange={e=>setEditHoldVals(p=>({...p,appraiser:e.target.value}))} placeholder="e.g. Christie's, Knight Frank, KPMG Valuations"/>
                  </div>
                </div>
              )}

              {/* Crypto-specific fields — Core vs Venture + wallet */}
              {["Crypto","Digital Assets","Crypto / Digital Assets"].includes(ev.class) && (
                <div style={{padding:"0 24px 14px"}}>
                  {sectionHead("Crypto Details")}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Category</div>
                      <select style={inp()} value={ev.cryptoCategory||"Core"} onChange={e=>setEditHoldVals(p=>({...p,cryptoCategory:e.target.value}))}>
                        {["Core (BTC/ETH)","Venture (Altcoins/Web3)","DeFi","Stablecoin","NFT","Other"].map(o=><option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Wallet Type</div>
                      <select style={inp()} value={ev.walletType||"Exchange"} onChange={e=>setEditHoldVals(p=>({...p,walletType:e.target.value}))}>
                        {["Exchange (Hot)","Cold Wallet (Hardware)","Cold Wallet (Paper)","DeFi Protocol","Custodian","Other"].map(o=><option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div style={{gridColumn:"span 2"}}>
                      <div style={{fontSize:11,color:T.textSecondary,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Wallet Address / Exchange Account</div>
                      <input type="text" style={inp()} value={ev.walletAddress||""} onChange={e=>setEditHoldVals(p=>({...p,walletAddress:e.target.value}))} placeholder="e.g. 0x1234…abcd (or exchange account ID)"/>
                    </div>
                  </div>
                </div>
              )}

              {/* Yahoo Ticker — for Global Mutual Funds only */}
              {(ev.class === "Global Mutual Funds" || ev.isGlobalMF ||
                (ev.ticker && /^(LU|IE)[A-Z0-9]{10}$/i.test(ev.ticker))) && (
                <div style={{padding:"0 24px 14px"}}>
                  <div style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                    textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>
                    Yahoo Finance Ticker
                    <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,
                      color:T.textMuted,marginLeft:6,fontSize:10}}>
                      for live NAV fetch (e.g. OP0000XMS6.SI)
                    </span>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input
                      style={{...inp(),flex:1}}
                      value={ev.yahooTicker||""}
                      placeholder="e.g. OP0000XMS6.SI — find on finance.yahoo.com"
                      onChange={e=>setEditHoldVals(p=>({...p,yahooTicker:e.target.value.trim()}))}
                    />
                    {ev.yahooTicker && (
                      <a href={"https://finance.yahoo.com/quote/"+encodeURIComponent(ev.yahooTicker)}
                        target="_blank" rel="noopener noreferrer"
                        style={{fontSize:11,color:"#2563EB",whiteSpace:"nowrap",textDecoration:"none"}}>
                        Verify ↗
                      </a>
                    )}
                  </div>
                  <div style={{fontSize:10,color:T.textMuted,marginTop:5,lineHeight:1.5}}>
                    Search the ISIN on <a href="https://finance.yahoo.com" target="_blank" rel="noopener noreferrer"
                      style={{color:"#2563EB"}}>finance.yahoo.com</a> to find the ticker.
                    SGD-hedged classes use <strong>.SI</strong> suffix (SGX), USD classes may use <strong>.F</strong> (Frankfurt) or may not be listed.
                    Once set, price refreshes automatically. Leave blank if not found — use ⇒ to enter NAV manually.
                  </div>
                </div>
              )}

              {/* Notes / Annotation */}
              <div style={{padding:"0 24px 14px"}}>
                <div style={{fontSize:11,color:T.textSecondary,fontWeight:600,
                  textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>
                  Notes (optional)
                </div>
                <textarea
                  value={ev.notes ?? editHold?.notes ?? ""}
                  onChange={e=>setEditHoldVals(p=>({...p,notes:e.target.value}))}
                  placeholder="e.g. Bought at IPO · Hold long-term · Consider exiting above ₹1500"
                  rows={2}
                  style={{width:"100%",boxSizing:"border-box",
                    background:T.inputBg,border:`1px solid ${T.inputBorder||T.cardBorder}`,
                    borderRadius:8,color:T.textPrimary,padding:"9px 12px",
                    fontSize:12,outline:"none",resize:"vertical",
                    fontFamily:"'Inter', system-ui, sans-serif",lineHeight:1.5}}/>
              </div>

              <div style={{padding:"14px 24px",borderTop:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"flex-end",gap:10,flexShrink:0}}>
                <button onClick={close} style={{background:T.borderSubtle,border:"1px solid #78716C",borderRadius:8,color:T.textSecondary,padding:"9px 22px",cursor:"pointer",fontSize:13}}>Cancel</button>
                <button onClick={save} style={{background:"#1E293B",border:"none",borderRadius:8,color:"white",padding:"9px 26px",cursor:"pointer",fontSize:13,fontWeight:700}}>Save Changes</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════
          ADMIN KYC VIEWER MODAL
      ══════════════════════════════════════════════ */}
      {kycViewClient && isAdmin && (function(){
        const cObj = clients.find(c=>c.id===kycViewClient);
        if (!cObj) return null;
        const pdata = profileData[kycViewClient] || {};
        const set = (k,v) => setProfileData(p=>({...p,[kycViewClient]:{...p[kycViewClient],[k]:v}}));
        const close = ()=>setKycViewClient(null);
        const labelSt = {fontSize:11,color:T.textSecondary,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,display:"block"};
        const fieldI  = {width:"100%",boxSizing:"border-box",background:T.inputBg,border:`1px solid ${T.inputBorder}`,borderRadius:8,color:T.textPrimary,padding:"9px 12px",fontSize:13,outline:"none",fontFamily:"'Inter', system-ui, sans-serif"};
        const secHead = txt => <div style={{fontSize:10,fontWeight:700,color:T.brand,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingBottom:7,paddingTop:16,borderTop:`1px solid ${T.cardBorder}`}}>{txt}</div>;
        const fam = families.find(f=>f.id===cObj.familyId);
        const [kycTab, setKycTab] = [profileData._kycTab||"personal", v=>setProfileData(p=>({...p,_kycTab:v}))];
        const KYC_TABS = [{id:"personal",label:"Personal",icon:"👤"},{id:"kyc",label:"KYC & ID",icon:"🆔"},{id:"financial",label:"Financial",icon:"💼"},{id:"compliance",label:"Compliance",icon:"⚖️"}];
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
            onClick={e=>e.target===e.currentTarget&&close()}>
            <div style={{background:T.cardBg,borderRadius:18,width:"100%",maxWidth:700,maxHeight:"93vh",display:"flex",flexDirection:"column",boxShadow:"0 30px 90px rgba(0,0,0,0.85)"}}>
              {/* Header */}
              <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:"linear-gradient(135deg,rgba(0,95,95,0.06),transparent)",borderRadius:"18px 18px 0 0"}}>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <div style={{width:52,height:52,borderRadius:"50%",background:"#007878",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:"white",border:"2px solid rgba(0,121,193,0.3)"}}>{cObj.avatar||(cObj.name||"?").slice(0,2).toUpperCase()}</div>
                  <div>
                    <div style={{fontSize:16,fontWeight:700,color:T.textPrimary}}>{cObj.name}</div>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginTop:3}}>
                      <span style={{fontSize:11,background:"rgba(0,120,120,0.1)",color:T.brand,padding:"2px 8px",borderRadius:4,fontWeight:600}}>{cObj.role}</span>
                      {fam&&<span style={{fontSize:11,color:T.textSecondary}}>{fam.emoji} {fam.name}</span>}
                      {cObj.dob&&<span style={{fontSize:11,color:T.textMuted}}>Age {new Date().getFullYear()-parseInt(cObj.dob)}</span>}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:11,background:"rgba(37,99,235,0.08)",color:"#2563EB",border:"1px solid rgba(37,99,235,0.2)",borderRadius:6,padding:"3px 10px"}}>Admin View</span>
                  <button onClick={close} style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,color:"#A32D2D",width:34,height:34,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              {/* Sub-tabs */}
              <div style={{display:"flex",padding:"0 24px",borderBottom:`1px solid ${T.cardBorder}`,flexShrink:0}}>
                {KYC_TABS.map(t=>(
                  <button key={t.id} onClick={()=>setKycTab(t.id)}
                    style={{padding:"12px 16px",background:"none",border:"none",borderBottom:`2px solid ${kycTab===t.id?"#007878":"transparent"}`,color:kycTab===t.id?"#007878":T.textSecondary,fontSize:12,fontWeight:kycTab===t.id?600:400,cursor:"pointer",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
              {/* Body */}
              <div style={{overflowY:"auto",padding:"6px 24px 24px",flex:1}}>
                {kycTab==="personal" && (
                  <div>
                    {secHead("Contact")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div style={{gridColumn:"span 2"}}><label style={labelSt}>Email</label><input style={fieldI} type="email" placeholder="email@domain.com" value={pdata.email||""} onChange={e=>set("email",e.target.value)}/></div>
                      <div><label style={labelSt}>Phone</label><input style={fieldI} type="tel" placeholder="+91 98765 43210" value={pdata.phone||""} onChange={e=>set("phone",e.target.value)}/></div>
                      <div><label style={labelSt}>Date of Birth</label><input style={fieldI} type="date" value={pdata.dob||cObj.dob||""} onChange={e=>set("dob",e.target.value)}/></div>
                    </div>
                    {secHead("Address")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div style={{gridColumn:"span 2"}}><label style={labelSt}>Street / Unit</label><input style={fieldI} placeholder="12 Marine Drive, Floor 7" value={pdata.address||""} onChange={e=>set("address",e.target.value)}/></div>
                      <div><label style={labelSt}>City</label><input style={fieldI} placeholder="Mumbai" value={pdata.city||""} onChange={e=>set("city",e.target.value)}/></div>
                      <div><label style={labelSt}>Postcode</label><input style={fieldI} placeholder="400001" value={pdata.postcode||""} onChange={e=>set("postcode",e.target.value)}/></div>
                      <div style={{gridColumn:"span 2"}}><label style={labelSt}>Country of Residence</label><input style={fieldI} placeholder="India" value={pdata.country||""} onChange={e=>set("country",e.target.value)}/></div>
                    </div>
                    {secHead("Professional")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={labelSt}>Occupation</label><input style={fieldI} placeholder="Entrepreneur" value={pdata.occupation||""} onChange={e=>set("occupation",e.target.value)}/></div>
                      <div><label style={labelSt}>Employer</label><input style={fieldI} placeholder="Sharma Group" value={pdata.employer||""} onChange={e=>set("employer",e.target.value)}/></div>
                    </div>
                  </div>
                )}
                {kycTab==="kyc" && (
                  <div>
                    {secHead("Identity Documents")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={labelSt}>PAN / Tax ID</label><input style={fieldI} placeholder="ABCDE1234F" value={pdata.panNumber||""} onChange={e=>set("panNumber",e.target.value.toUpperCase())}/></div>
                      <div><label style={labelSt}>Aadhaar / Nat. ID</label><input style={fieldI} placeholder="XXXX XXXX XXXX" value={pdata.aadhaar||""} onChange={e=>set("aadhaar",e.target.value)}/></div>
                      <div><label style={labelSt}>Passport Number</label><input style={fieldI} placeholder="A1234567" value={pdata.passportNumber||""} onChange={e=>set("passportNumber",e.target.value.toUpperCase())}/></div>
                      <div><label style={labelSt}>Passport Expiry</label><input style={fieldI} type="date" value={pdata.passportExpiry||""} onChange={e=>set("passportExpiry",e.target.value)}/></div>
                      <div><label style={labelSt}>Nationality</label><input style={fieldI} placeholder="Indian" value={pdata.nationality||""} onChange={e=>set("nationality",e.target.value)}/></div>
                      <div><label style={labelSt}>Tax Residency</label><input style={fieldI} placeholder="India" value={pdata.taxResidency||""} onChange={e=>set("taxResidency",e.target.value)}/></div>
                    </div>
                    {secHead("KYC Documents")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={labelSt}>KYC Reference No.</label><input style={fieldI} placeholder="KYC-2024-XXXX" value={pdata.kycRef||""} onChange={e=>set("kycRef",e.target.value)}/></div>
                      <div><label style={labelSt}>KYC Date</label><input style={fieldI} type="date" value={pdata.kycDate||""} onChange={e=>set("kycDate",e.target.value)}/></div>
                      <div><label style={labelSt}>KYC Status</label>
                        <select style={fieldI} value={pdata.kycStatus||"Verified"} onChange={e=>set("kycStatus",e.target.value)}>
                          {["Verified","Pending","Expired","Rejected"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Next Review</label><input style={fieldI} type="date" value={pdata.kycNextReview||""} onChange={e=>set("kycNextReview",e.target.value)}/></div>
                    </div>
                  </div>
                )}
                {kycTab==="financial" && (
                  <div>
                    {secHead("Financial Profile")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={labelSt}>Annual Income Band</label>
                        <select style={fieldI} value={pdata.annualIncome||""} onChange={e=>set("annualIncome",e.target.value)}>
                          <option value="">Select…</option>
                          {["< ₹25L","₹25L–₹1Cr","₹1Cr–₹5Cr","₹5Cr–₹25Cr","> ₹25Cr","Prefer not to say"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Net Worth Band</label>
                        <select style={fieldI} value={pdata.netWorth||""} onChange={e=>set("netWorth",e.target.value)}>
                          <option value="">Select…</option>
                          {["< ₹1Cr","₹1Cr–₹5Cr","₹5Cr–₹25Cr","₹25Cr–₹100Cr","> ₹100Cr"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Risk Profile</label>
                        <select style={fieldI} value={pdata.riskProfile||"Moderate"} onChange={e=>set("riskProfile",e.target.value)}>
                          {["Conservative","Moderate","Aggressive","Very Aggressive"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Investment Horizon</label>
                        <select style={fieldI} value={pdata.horizon||""} onChange={e=>set("horizon",e.target.value)}>
                          <option value="">Select…</option>
                          {["< 1 Year","1–3 Years","3–5 Years","5–10 Years","> 10 Years"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Primary Bank</label><input style={fieldI} placeholder="HDFC Bank" value={pdata.primaryBank||""} onChange={e=>set("primaryBank",e.target.value)}/></div>
                      <div><label style={labelSt}>Bank A/C No. (last 4)</label><input style={fieldI} placeholder="XXXX" maxLength={4} value={pdata.bankAccLast4||""} onChange={e=>set("bankAccLast4",e.target.value)}/></div>
                    </div>
                    {secHead("Investment Objectives")}
                    <div><label style={labelSt}>Notes / Mandates</label>
                      <textarea style={{...fieldI,minHeight:80,resize:"vertical"}} placeholder="e.g. Capital preservation with moderate growth. Avoid tobacco, gambling sectors." value={pdata.investmentNotes||""} onChange={e=>set("investmentNotes",e.target.value)}/>
                    </div>
                  </div>
                )}
                {kycTab==="compliance" && (
                  <div>
                    {secHead("AML / Compliance")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                      {[
                        {label:"KYC Status",key:"kycStatus",opts:["Verified","Pending","Expired","Rejected"]},
                        {label:"AML Status",key:"amlStatus",opts:["Cleared","Under Review","Flagged"]},
                        {label:"FATCA Status",key:"fatcaStatus",opts:["Submitted","Pending","Not Applicable"]},
                        {label:"CRS Status",key:"crsStatus",opts:["Submitted","Pending","Not Applicable"]},
                        {label:"Risk Assessment",key:"riskAssessment",opts:["Completed","Due","Overdue"]},
                        {label:"Suitability Review",key:"suitabilityStatus",opts:["Completed","Due Jun '26","Overdue"]},
                      ].map(item=>(
                        <div key={item.key}>
                          <label style={labelSt}>{item.label}</label>
                          <select style={fieldI} value={pdata[item.key]||item.opts[0]} onChange={e=>set(item.key,e.target.value)}>
                            {item.opts.map(o=><option key={o}>{o}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                    {secHead("PEP / Sanctions")}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div><label style={labelSt}>PEP Status</label>
                        <select style={fieldI} value={pdata.pepStatus||"Not PEP"} onChange={e=>set("pepStatus",e.target.value)}>
                          {["Not PEP","PEP","Related to PEP"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div><label style={labelSt}>Sanctions Check</label>
                        <select style={fieldI} value={pdata.sanctionsCheck||"Clear"} onChange={e=>set("sanctionsCheck",e.target.value)}>
                          {["Clear","Under Review","Flagged"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div style={{gridColumn:"span 2"}}><label style={labelSt}>Compliance Notes</label>
                        <textarea style={{...fieldI,minHeight:70,resize:"vertical"}} placeholder="Internal notes for compliance team…" value={pdata.complianceNotes||""} onChange={e=>set("complianceNotes",e.target.value)}/>
                      </div>
                    </div>
                    {secHead("Login Access")}
                    <div style={{background:"rgba(0,0,0,0.02)",border:`1px solid ${T.cardBorder}`,borderRadius:10,padding:"14px 16px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,color:T.textSecondary,marginBottom:3}}>Login ID</div>
                          {profileData[kycViewClient]?._editingAdminLid ? (
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              <input
                                value={profileData[kycViewClient]?._adminLidVal ?? clientLogins[kycViewClient]?.loginId ?? ""}
                                onChange={e=>setProfileData(p=>({...p,[kycViewClient]:{...p[kycViewClient],_adminLidVal:e.target.value.toLowerCase().replace(/\s+/g,"")}}))}
                                autoFocus
                                style={{flex:1,background:T.inputBg,border:`1px solid ${T.brand}`,borderRadius:6,
                                  color:T.textPrimary,padding:"5px 10px",fontSize:13,outline:"none",fontFamily:"'Inter', system-ui, sans-serif"}}/>
                              <button onClick={async()=>{
                                const newId=(profileData[kycViewClient]?._adminLidVal||"").trim();
                                if(!newId||newId.length<3){showToast("Login ID must be at least 3 characters","#A32D2D");return;}
                                const taken=Object.entries(clientLogins).some(([id,l])=>String(id)!==String(kycViewClient)&&l.loginId===newId);
                                if(taken){showToast("Login ID already in use by another client","#A32D2D");return;}
                                const updated={...(clientLogins[kycViewClient]||{}),loginId:newId};
                                setClientLogins(p=>({...p,[kycViewClient]:updated}));
                                try{await upsertClientLogin(kycViewClient,updated);}catch(e){console.error(e);}
                                setProfileData(p=>({...p,[kycViewClient]:{...p[kycViewClient],_editingAdminLid:false}}));
                                showToast(`✓ Login ID updated to "${newId}"`,"#1D9E75");
                              }} style={{background:"#007878",border:"none",borderRadius:6,
                                color:"white",padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                              <button onClick={()=>setProfileData(p=>({...p,[kycViewClient]:{...p[kycViewClient],_editingAdminLid:false}}))}
                                style={{background:"transparent",border:`1px solid ${T.cardBorder}`,borderRadius:6,
                                  color:T.textSecondary,padding:"5px 10px",cursor:"pointer",fontSize:12}}>✕</button>
                            </div>
                          ) : (
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{fontSize:13,fontWeight:600,fontFamily:"'Inter', system-ui, sans-serif"}}>
                                {clientLogins[kycViewClient]?.loginId||"— No login created"}
                              </div>
                              {clientLogins[kycViewClient]?.loginId && (
                                <button onClick={()=>setProfileData(p=>({...p,[kycViewClient]:{...p[kycViewClient],_editingAdminLid:true,_adminLidVal:clientLogins[kycViewClient]?.loginId||""}}))}
                                  style={{background:"rgba(0,121,193,0.08)",border:`1px solid ${T.brand}44`,borderRadius:5,
                                    color:T.brand,padding:"2px 9px",cursor:"pointer",fontSize:11,fontWeight:600}}>
                                  ✏ Edit
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <span style={{fontSize:11,background:clientLogins[kycViewClient]?.loginId?"rgba(22,163,74,0.1)":"rgba(220,38,38,0.1)",color:clientLogins[kycViewClient]?.loginId?"#1D9E75":"#A32D2D",border:`1px solid ${clientLogins[kycViewClient]?.loginId?"rgba(22,163,74,0.25)":"rgba(239,68,68,0.25)"}`,borderRadius:6,padding:"3px 10px",flexShrink:0,marginLeft:8}}>
                          {clientLogins[kycViewClient]?.loginId?"Active":"No Access"}
                        </span>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {!clientLogins[kycViewClient]?.loginId ? (
                          <button onClick={()=>{
                            const defaultLogin=cObj.name.split(" ")[0].toLowerCase()+"."+cObj.name.split(" ").slice(-1)[0].toLowerCase();
                            const defaultPwd=cObj.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                            const nc={loginId:defaultLogin,password:defaultPwd,active:true,riskTabs:{overview:false,concentration:false,stress:false,correlation:false}};
                            upsertClientLogin(cObj.id,nc).catch(e=>console.error("[Supabase]",e.message));
                            setClientLogins(p=>({...p,[cObj.id]:nc}));
                            setCredModal({name:cObj.name,loginId:defaultLogin,password:defaultPwd});
                          }} style={{background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.3)",borderRadius:6,color:"#059669",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>+ Create Login</button>
                        ) : (
                          <React.Fragment>
                            <button onClick={()=>{
                              const np=cObj.name.split(" ")[0]+"@"+Math.floor(1000+Math.random()*9000);
                              const u={...clientLogins[kycViewClient],password:np};
                              upsertClientLogin(cObj.id,u).catch(e=>console.error("[Supabase]",e.message));
                              setClientLogins(p=>({...p,[cObj.id]:u}));
                              setCredModal({name:cObj.name,loginId:clientLogins[kycViewClient]?.loginId||"",password:np});
                            }} style={{background:"rgba(245,158,11,0.1)",border:"1px solid rgba(217,119,6,0.3)",borderRadius:6,color:"#D97706",padding:"6px 14px",cursor:"pointer",fontSize:12}}>Reset Password</button>
                            <button onClick={()=>{
                              const u={...clientLogins[kycViewClient],active:!clientLogins[kycViewClient].active};
                              upsertClientLogin(cObj.id,u).catch(e=>console.error("[Supabase]",e.message));
                              setClientLogins(p=>({...p,[cObj.id]:u}));
                            }} style={{background:clientLogins[kycViewClient]?.active?"rgba(220,38,38,0.08)":"rgba(22,163,74,0.1)",border:`1px solid ${clientLogins[kycViewClient]?.active?"rgba(239,68,68,0.25)":"rgba(22,163,74,0.25)"}`,borderRadius:6,color:clientLogins[kycViewClient]?.active?"#A32D2D":"#1D9E75",padding:"6px 14px",cursor:"pointer",fontSize:12}}>{clientLogins[kycViewClient]?.active?"Disable Login":"Enable Login"}</button>
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div style={{padding:"14px 24px",borderTop:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div style={{fontSize:11,color:T.textMuted}}>Changes auto-saved to local state · Sync to Supabase via Save</div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={close} style={{background:T.borderSubtle,border:"1px solid #78716C",borderRadius:8,color:T.textSecondary,padding:"9px 22px",cursor:"pointer",fontSize:13}}>Close</button>
                  <button onClick={()=>{ upsertProfile(cObj.id, profileData[cObj.id]||{}).then(()=>{ close(); }).catch(e=>{ console.error("[Supabase] upsertProfile:",e.message); close(); }); }} style={{background:"#007878",border:"none",borderRadius:8,color:"white",padding:"9px 26px",cursor:"pointer",fontSize:13,fontWeight:700}}>Save Profile</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── CREDENTIAL COPY MODAL ── */}
      {credModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={e=>{if(e.target===e.currentTarget)setCredModal(null);}}>
          <div style={{background:T.cardBg,borderRadius:16,padding:"28px 32px",maxWidth:380,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",position:"relative"}}>
            <button onClick={()=>setCredModal(null)} style={{position:"absolute",top:14,right:16,background:"none",border:"none",fontSize:18,color:T.textMuted,cursor:"pointer",lineHeight:1}}>✕</button>
            <div style={{fontSize:16,fontWeight:700,marginBottom:4,color:T.textPrimary}}>
              {credModal.isReset ? "Password Reset" : "Login Created"}
            </div>
            <div style={{fontSize:12,color:T.textSecondary,marginBottom:20}}>For <strong>{credModal.name}</strong> — share these credentials securely</div>
            {[{label:"Login ID",value:credModal.loginId},{label:"Password",value:credModal.password}].map(({label,value})=>(
              <div key={label} style={{marginBottom:12}}>
                <div style={{fontSize:10,color:T.brand,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{label}</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <code style={{flex:1,background:T.pageBg,border:`1px solid ${T.cardBorder}`,borderRadius:7,padding:"9px 12px",fontSize:14,fontFamily:"'Inter', system-ui, sans-serif",color:T.textPrimary,letterSpacing:"0.05em"}}>{value}</code>
                  <button onClick={()=>{navigator.clipboard.writeText(value).then(()=>showToast(`✓ ${label} copied`)).catch(()=>{});}}
                    style={{background:"rgba(0,120,120,0.1)",border:"1px solid rgba(0,121,193,0.3)",borderRadius:7,color:T.brand,padding:"9px 12px",cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>Copy</button>
                </div>
              </div>
            ))}
            <button onClick={()=>{
              navigator.clipboard.writeText(`Login ID: ${credModal.loginId}\nPassword: ${credModal.password}`)
                .then(()=>{ showToast("✓ Both credentials copied to clipboard"); setCredModal(null); })
                .catch(()=>{});
            }} style={{width:"100%",marginTop:8,background:"#007878",border:"none",borderRadius:9,color:"white",padding:"11px",cursor:"pointer",fontSize:13,fontWeight:600}}>
              📋 Copy Both & Close
            </button>
          </div>
        </div>
      )}

      {/* ── CONFIRM MODAL — replaces window.confirm ── */}
      {confirmModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",
          backdropFilter:"blur(4px)",zIndex:1100,
          display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={e=>{ if(e.target===e.currentTarget) setConfirmModal(null); }}>
          <div style={{background:T.cardBg,
            border:`1px solid ${confirmModal.danger?"rgba(220,38,38,0.25)":T.cardBorder}`,
            borderRadius:16,padding:"24px 28px",width:"100%",maxWidth:380,
            boxShadow:"0 32px 80px rgba(0,0,0,0.4)",
            animation:"scaleIn 0.18s ease"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:16}}>
              <div style={{width:36,height:36,borderRadius:10,flexShrink:0,
                background:confirmModal.danger?"rgba(220,38,38,0.1)":"rgba(37,99,235,0.1)",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                {confirmModal.danger ? "🗑" : "ℹ️"}
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.textPrimary,marginBottom:4}}>
                  {confirmModal.message}
                </div>
                {confirmModal.detail && (
                  <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.5}}>
                    {confirmModal.detail}
                  </div>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setConfirmModal(null)}
                style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                  borderRadius:8,color:T.textSecondary,padding:"8px 18px",
                  cursor:"pointer",fontSize:13,fontWeight:500}}>
                Cancel
              </button>
              <button autoFocus onClick={()=>{
                  const fn = confirmModal.onConfirm;
                  setConfirmModal(null);
                  fn();
                }}
                style={{background:confirmModal.danger
                  ?"#A32D2D"
                  :"#007878",
                  border:"none",borderRadius:8,color:"white",
                  padding:"8px 18px",cursor:"pointer",fontSize:13,fontWeight:700}}>
                {confirmModal.danger ? "Delete" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RE-AUTH MODAL ── */}
      {reauthModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",
          backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",zIndex:1200,
          display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={e=>{ if(e.target===e.currentTarget){ setReauthModal(null); setReauthPassword(""); setReauthErr(""); }}}>
          <div style={{background:T.cardBg,border:"1.5px solid rgba(220,38,38,0.35)",
            borderRadius:16,padding:28,width:"100%",maxWidth:400,
            boxShadow:"0 32px 80px rgba(0,0,0,0.45)",
            animation:"scaleIn 0.18s ease"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <span style={{fontSize:24}}>🔐</span>
              <div>
                <div style={{fontSize:15,fontWeight:700}}>Confirm your password</div>
                <div style={{fontSize:12,color:T.textSecondary,marginTop:2}}>
                  Required to: <strong style={{color:"#A32D2D"}}>{reauthModal.label}</strong>
                </div>
              </div>
            </div>
            <div style={{fontSize:11,color:T.textSecondary,marginBottom:14,padding:"8px 12px",
              background:"rgba(220,38,38,0.06)",borderRadius:8,border:"1px solid rgba(220,38,38,0.15)"}}>
              ⚠️ This action cannot be undone. Enter your admin password to proceed.
            </div>
            <input type="password" autoFocus placeholder="Enter your admin password"
              value={reauthPassword}
              onChange={e=>{ setReauthPassword(e.target.value); setReauthErr(""); }}
              onKeyDown={e=>{ if(e.key==="Enter") document.getElementById("reauthConfirmBtn")?.click(); }}
              style={{...tInp(), marginBottom:8, borderColor:reauthErr?"#A32D2D":undefined}}/>
            {reauthErr && (
              <div style={{fontSize:11,color:"#A32D2D",marginBottom:8,display:"flex",gap:5}}>
                <span>⚠</span><span>{reauthErr}</span>
              </div>
            )}
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button id="reauthConfirmBtn" disabled={reauthBusy||!reauthPassword}
                onClick={async()=>{
                  if(!reauthPassword) return;
                  setReauthBusy(true);
                  try {
                    // Check Supabase first (cross-device), then localStorage, then in-memory state
                    let storedPwd = null;
                    try {
                      const sbProf = await fetchProfile("__admin_cred__");
                      if (sbProf?.password) storedPwd = sbProf.password;
                    } catch(_) {}
                    if (!storedPwd) {
                      try {
                        const saved = JSON.parse(localStorage.getItem("fw_admin_cred")||"null");
                        if (saved?.password) storedPwd = saved.password;
                      } catch(_) {}
                    }
                    if (!storedPwd) {
                      const adminKey = Object.keys(clientLogins).find(k=>k==="__admin__"||k.startsWith("__admin__"));
                      storedPwd = clientLogins[adminKey]?.password || null;
                    }
                    if (!storedPwd) { setReauthErr("No admin password configured."); setReauthBusy(false); return; }
                    const valid = await verifyPassword(reauthPassword, storedPwd);
                    if(!valid){ setReauthErr("Incorrect password."); setReauthBusy(false); return; }
                    const fn = reauthModal.onConfirm;
                    setReauthModal(null); setReauthPassword(""); setReauthErr("");
                    await fn();
                  } catch(e){ setReauthErr("Failed: "+e.message); setReauthBusy(false); }
                }}
                style={{flex:1,background:reauthBusy||!reauthPassword?"rgba(220,38,38,0.3)":"#A32D2D",
                  border:"none",borderRadius:8,color:"white",padding:"10px",
                  cursor:reauthBusy||!reauthPassword?"default":"pointer",fontSize:13,fontWeight:700}}>
                {reauthBusy?"⏳ Verifying…":"✓ Confirm & Proceed"}
              </button>
              <button onClick={()=>{ setReauthModal(null); setReauthPassword(""); setReauthErr(""); }}
                style={{background:T.borderSubtle,border:`1px solid ${T.cardBorder}`,
                  borderRadius:8,color:T.textSecondary,padding:"10px 16px",cursor:"pointer",fontSize:13}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PRICE ALERT TOASTS ── */}
      {alertToasts.length > 0 && (
        <div style={{position:"fixed",top:80,right:16,zIndex:9998,display:"flex",flexDirection:"column",gap:8,maxWidth:340}}>
          {alertToasts.map(t=>(
            <div key={t.id} style={{background:T.cardBg,border:`2px solid ${t.col}`,borderRadius:10,padding:"12px 16px",boxShadow:"0 8px 32px rgba(0,0,0,0.18)",display:"flex",alignItems:"center",gap:10,animation:"slideIn 0.3s ease"}}>
              <div style={{flex:1,fontSize:13,fontWeight:600,color:T.textPrimary}}>{t.msg}</div>
              <button onClick={()=>setAlertToasts(p=>p.filter(x=>x.id!==t.id))} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:16,lineHeight:1,padding:0}}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ── SUBSCRIPTIONS TAB (Admin) ── */}
      {tab==="subscriptions" && isAdmin && (
        <SubscriptionPanel
          clients={clients}
          families={families}
          clientPlans={clientPlans}
          getEffectivePlan={getEffectivePlan}
          setPlanForClient={setPlanForClient}
          setClientPlans={setClientPlans}
          T={T} tCard={tCard} tTh={tTh} tTd={tTd} inp={inp} isMobile={isMobile}
        />
      )}
        </div>{/* end main column */}
      </div>{/* end sidebar+main flex row */}


      {/* ── METHODOLOGY & DISCLOSURES DRAWER ── */}
      <MethodologyDrawer open={methodologyOpen} onClose={()=>setMethodologyOpen(false)} T={T}/>

      {/* ── UPGRADE MODAL — shown when client tries to access locked tab ── */}
      {upgradeModal && (
        <UpgradeModal
          tabId={upgradeModal.tabId}
          tabLabel={upgradeModal.tabLabel}
          tabIcon={upgradeModal.tabIcon}
          clientId={loggedInClientId}
          clientPlans={clientPlans}
          setPlanForClient={setPlanForClient}
          onClose={(success) => {
            setUpgradeModal(null);
            if (success) setTab(upgradeModal.tabId);
          }}
          T={T} inp={inp} tCard={tCard} isMobile={isMobile}
        />
      )}

      {/* ── MOBILE BOTTOM NAV ── */}
      {isMobile && (function(){
        const PRIMARY = [
          {id:"summary",       icon:"⭐", label:"Summary"},
          {id:"holdings",      icon:"💼", label:"Holdings"},
          {id:"performance",   icon:"📈", label:"Perf"},
          {id:"cashflow",      icon:"💸", label:"Cash"},
          {id:"more",          icon:"☰",  label:"Menu"},
        ];
        const ICONS = {overview:"📊",holdings:"💼",performance:"📈",transactions:"📋",geo:"🗺",currency:"💱",model:"🎯",benchmark:"🏆",irr:"📉",watchlist:"👁",backtesting:"🧪",risk:"⚡",families:"👨‍👩‍👧",upload:"📁",datasources:"⚙"};
        return (
          <React.Fragment>
            <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:999,background:T.cardBg,borderTop:`1px solid ${T.cardBorder}`,display:"flex",height:56,paddingBottom:"env(safe-area-inset-bottom)"}}>
              {PRIMARY.map(t => {
                const active = t.id==="more" ? sidebarMobileOpen : tab===t.id;
                return (
                  <button key={t.id} onClick={()=>{
                    if(t.id==="more"){ setSidebarMobileOpen(p=>!p); }
                    else if(!isAdmin && !canAccess(t.id)){
                      setUpgradeModal({tabId:t.id,tabLabel:t.label||t.id,tabIcon:t.icon||"🔒"});
                    }
                    else { setTab(t.id); setSidebarMobileOpen(false); }
                  }} style={{flex:1,background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,padding:"6px 2px",position:"relative"}}>
                    {active && <div style={{position:"absolute",top:0,left:"15%",right:"15%",height:2,background:T.brand,borderRadius:"0 0 2px 2px"}}/>}
                    <span style={{display:"flex",alignItems:"center",justifyContent:"center",height:22}}>
                      {NAV_ICONS[t.id]
                        ? <NavIcon name={t.id} size={22} color={active?T.brand:T.textPrimary} />
                        : <span style={{fontSize:22}}>{t.icon}</span>}
                    </span>
                    <span style={{fontSize:10,color:active?T.brand:T.textPrimary,fontWeight:active?700:500}}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </React.Fragment>
        );
      })()}

    </div>

      {/* ── Floating Global Voice Button ── */}
      {voiceSupported && (isAdmin || loggedInClientId) && (
        <div style={{position:"fixed",bottom:isMobile?80:28,right:22,zIndex:9999,
          display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>

          {/* Live transcript bubble */}
          {voiceTranscript && (
            <div style={{background:"rgba(10,10,20,0.92)",color:"white",
              padding:"8px 14px",borderRadius:12,fontSize:12,maxWidth:260,
              boxShadow:"0 4px 20px rgba(0,0,0,0.4)",backdropFilter:"blur(8px)",
              border:"1px solid rgba(255,255,255,0.1)",lineHeight:1.5}}>
              🎙️ <em>{voiceTranscript}</em>
            </div>
          )}

          {/* Speaking indicator bubble */}
          {voiceSpeaking && (
            <div style={{background:"rgba(22,163,74,0.92)",color:"white",
              padding:"6px 14px",borderRadius:12,fontSize:11,fontWeight:600,
              boxShadow:"0 4px 20px rgba(22,163,74,0.4)",
              animation:"pulse 1s infinite"}}>
              🔊 Speaking…
            </div>
          )}

          {/* Status label */}
          {voiceMode && !voiceTranscript && !voiceSpeaking && (
            <div style={{background:"rgba(0,102,204,0.9)",color:"white",
              padding:"5px 12px",borderRadius:10,fontSize:11,fontWeight:600,
              boxShadow:"0 2px 12px rgba(0,102,204,0.4)"}}>
              Voice on · tap mic to speak
            </div>
          )}

          {/* Main floating mic button */}
          <button
            onClick={voiceMode ? (voiceActive ? globalStopVoice : globalStartVoice) : globalStartVoice}
            title={voiceMode ? (voiceActive ? "Stop listening" : "Speak a question") : "Start voice assistant"}
            style={{
              width:52, height:52, borderRadius:"50%", border:"none",
              cursor:"pointer", flexShrink:0, fontSize:22,
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.2s",
              background: voiceSpeaking
                ? "#1D9E75"
                : voiceActive
                  ? "#A32D2D"
                  : voiceMode
                    ? "#0066cc"
                    : "#007878",
              boxShadow: voiceActive
                ? "0 0 0 4px rgba(220,38,38,0.3), 0 4px 16px rgba(220,38,38,0.4)"
                : voiceSpeaking
                  ? "0 0 0 4px rgba(22,163,74,0.3), 0 4px 16px rgba(22,163,74,0.4)"
                  : "0 4px 16px rgba(0,0,0,0.3)",
              animation: voiceActive ? "micPulse 1.2s ease-in-out infinite" : "none",
              color:"white"}}>
            {voiceSpeaking ? "🔊" : voiceActive ? "⏹" : "🎙️"}
          </button>

          {/* End voice mode button */}
          {voiceMode && (
            <button onClick={globalStopVoice}
              title="End voice session"
              style={{fontSize:10,fontWeight:600,padding:"3px 10px",borderRadius:8,
                border:"1px solid rgba(255,255,255,0.2)",cursor:"pointer",
                background:"rgba(0,0,0,0.6)",color:"rgba(255,255,255,0.8)",
                backdropFilter:"blur(4px)"}}>
              ✕ End
            </button>
          )}
        </div>
      )}

    </ThemeContext.Provider>
    </ErrorBoundary>
  );
}
